import React, {
  forwardRef,
  useContext,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { Collapse } from "react-bootstrap";
import ErrorField from "../../../Components/ErrorField";
import {
  DiscontinuationReason,
  HTTPResponse,
  PageDesignConstants,
  RRMMCohortTreatment,
  SideMenuItems,
  QuestionType,
  ToastMessageType,
  ToastMessages,
  COHORT_TREATMENT_GENERIC_NAME_MAP,
} from "../../../Helpers/Enums";
import SideBarMenu from "../../../Components/SideBarMenu";
import dayjs from "dayjs";
import {
  AllowNumericWithDecimal,
  AllowOnlyNonZeroNumeric,
  CheckDateValidation,
  CheckDateValidationQ36,
  DateInRange,
  GetLocalStorageData,
  GetLoggedInUserID,
  GetOrdinal,
  IsAdmin,
  IsBodyWeightInvalid,
  IsDateAfter,
  IsDateLessThanThirtyDays,
  IsLessOrEqual,
  isStringEmpty,
  IsValidDateValue,
  isValidNumberRange,
  ValidateQ26LastAdministrationMatch,
  IsDateValid,
  buildNextLotStartDatesLookup,
  buildQ40PeriodEndDateLookup,
  ValidateQ402BDate,
  ValidateQ403BDate,
  formatDateMinus7Days,
  formatDatePlus14Days,
  CalculateQ402CPeriodicRowCount,
  GetQ402CTargetDate,
  ValidateQ402CDate,
  ValidateQ361AdmissionDate,
  ValidateQ36DateSequence,
  getEarliestDate,
  CollapsibleSectionHeader,
  useCollapsibleGroups,
} from "../../../Helpers/Utilities";
import PatientDetails from "../../../Components/PatientInformation";
import SaveAndNavigate from "../../../Components/SaveAndNavigate";

import {
  CommonError_Msgs,
  ELCharacteristics_MSGS,
  hyperlinkTextQ36Elrexfio,
  hyperlinkTextQ36Linvoseltamab,
  hyperlinkTextLineOfTherapy,
  getFirstSubsWarningMessage,
  getDose2WarningMessage,
  getFirstTreatmentWarningMessage,
} from "../../../Helpers/HelperText";
import { AppContext } from "../../../Contexts/AppContextProvider";
import { AdditionalTreatmentService } from "../../../WebApiServices/AdditionalTreatment.service";

import InfoPopUp from "../../../Components/InfoPopUp";
import DatePickerComponent from "../../../Components/DatePickerComponent";

// Q38.1.A / Q38.2.A: top-level discontinuation reasons retired from the
// dose-frequency/interruption reason checklists.
const RetiredDiscontinuationReasons = [
  DiscontinuationReason.PatientDeath,
  "End of treatment plan",
];

// Q38.1.A / Q38.2.A: "Adverse event" sub-reason (bispecifictreatmentdiscontinuationreason_ae)
// retired from the nested AE sub-reason checklist.
const RetiredAdverseEventSubReasons = ["Weight loss"];

// Filters retired top-level reasons (and, within the "Adverse event" reason,
// its nested aeSubReasons) out of a reasonList loaded from the backend.
// EL's reason mechanism is id-based (bispecifictreatmentdiscontinuationreasonid),
// not positional, so it's safe to filter the source list directly (unlike
// TTCharacteristics.jsx, which needed a render-time-only visual filter due
// to a hardcoded discontinuationReasons[0] "Unknown" position dependency).
const FilterRetiredReasons = (reasonList) => {
  if (!Array.isArray(reasonList)) return reasonList;
  return reasonList
    .filter((r) => !RetiredDiscontinuationReasons.includes(r.reasonName))
    .map((r) =>
      Array.isArray(r.aeSubReasons)
        ? {
            ...r,
            aeSubReasons: r.aeSubReasons.filter(
              (ae) => !RetiredAdverseEventSubReasons.includes(ae.reasonName),
            ),
          }
        : r,
    );
};

const IsDateInRangeInclusive = (date, minDate, maxDate) => {
  const d = dayjs(date);
  return !d.isBefore(minDate, "day") && !d.isAfter(maxDate, "day");
};

// Q36-Q40.3.B render once per LOT cohort (Line_CohortID, e.g. "2L Elrexfio"), the same
// way TTCharacteristics.jsx does. ELLotSection holds the existing per-drug
// state/handlers/markup for ONE Elrexfio or Linvoseltamab LOT cohort; the
// ELCharacteristics page below renders one section per LOT and merges their saves.
//   lot = { cohortTreatmentId, lineOfTherapy (null when the cohort has no received
//           lines), isFirstLot, label }
// Rows returned by the API that carry no `lineOfTherapy` yet (legacy / pre-LOT data)
// are shown under the cohort's first LOT only; rows that carry one go to that LOT.
// Response row lists that hold per-cohort Q36-Q40.3.B answers (everything else in the
// response - Q26 data, next-LOT dates, dates - is shared lookup data).
const LotScopedRowLists = [
  "elrexfioData",
  "linvoseltamabData",
  "elrexfioHospitalData",
  "linvoseltamabHospitalData",
  "eLcharElrexfioModelList",
  "eLcharLinvoseltamabModelList",
  "steroidsmedicationModelList",
  "granulocytemedicationModelList",
  "q38ElrexfioData",
  "q38LinvoseltamabData",
  "q381AElrexfioData",
  "q381ALinvoseltamabData",
  "q382AElrexfioData",
  "q382ALinvoseltamabData",
  "elrexfioTocilizumabData",
  "linvoseltamabTocilizumabData",
  "q401aElrData",
  "q401aLinvoData",
  "q401cElrData",
  "q401cLinvoData",
  "q402aElrData",
  "q402aLinvoData",
  "q402cElrData",
  "q402cLinvoData",
  "q403aElrData",
  "q403aLinvoData",
];

// Q38.1.A / Q38.2.A: a LOT with no rows yet gets a blank row built from the shared
// reason checklists the API sends once at the top level (like TTCharacteristics.jsx).
const BlankLotRowBuilders = {
  q381AElrexfioData: (response, lot) => ({
    teclistamatalquetamabdosefrequencyinfobridgeid: 0,
    cohorttreatmentid: lot.cohortTreatmentId,
    lineOfTherapy: lot.lineOfTherapy,
    bispecifictreatmentdiscontinuationreasonUnk: false,
    reasonList: response.discontinuationReasons ?? [],
    neurologicAEReasonList: response.neurologicAEReasonList ?? [],
  }),
  q381ALinvoseltamabData: (response, lot) =>
    BlankLotRowBuilders.q381AElrexfioData(response, lot),
  q382AElrexfioData: (response, lot) => ({
    teclistamatalquetamabdosedelaybridgeid: 0,
    cohorttreatmentid: lot.cohortTreatmentId,
    lineOfTherapy: lot.lineOfTherapy,
    bispecifictreatmentdiscontinuationreasonUnk: false,
    reasonList: response.discontinuationReasonsQ382A ?? [],
    neurologicAEReasonList: response.neurologicAEReasonList ?? [],
  }),
  q382ALinvoseltamabData: (response, lot) =>
    BlankLotRowBuilders.q382AElrexfioData(response, lot),
};

const ScopeResponseToLot = (response, lot) => {
  const scoped = { ...response };
  LotScopedRowLists.forEach((key) => {
    const rows = scoped[key];
    if (!Array.isArray(rows)) return;
    let lotRows = rows.filter((row) =>
      row?.lineOfTherapy != null
        ? row.lineOfTherapy === lot.lineOfTherapy
        : lot.isFirstLot,
    );
    if (lotRows.length === 0 && BlankLotRowBuilders[key]) {
      lotRows = [BlankLotRowBuilders[key](response, lot)];
    }
    scoped[key] = lotRows;
  });
  // Q26 rows (number of administrations, LOT dates) are keyed by lotNumber: this
  // section only sees its own LOT of its own cohort.
  if (Array.isArray(scoped.bispecificQ26Data) && lot.lineOfTherapy != null) {
    scoped.bispecificQ26Data = scoped.bispecificQ26Data.filter(
      (x) =>
        x?.cohortTreatmentId !== lot.cohortTreatmentId ||
        Number(x?.lotNumber) === Number(lot.lineOfTherapy),
    );
  }
  return scoped;
};

const ELLotSection = forwardRef(({ lot }, ref) => {
  const storedData = GetLocalStorageData("CohortTreatment");
  const pid = GetLocalStorageData("patientId") ?? 0;
  const { isCollapsed: isLotCollapsed, toggleGroup: toggleLotBlock } =
    useCollapsibleGroups();

  const Q401cWarningUnkMsg = (
    <>
      WARNING: dose frequency has been listed as unknown for at least one
      reported change. Please attempt to infer the dose frequency both before
      and after the change based on information within the patient chart. Only
      use the <i>unknown</i> option if there is truly no information reported on
      the dose frequency (note that directionality of change is required).
    </>
  );

  const [isNewEcrfA, setIsNewEcrfA] = useState(false);
  const [isNewEcrfB, setIsNewEcrfB] = useState(false);
  const [isNewEcrfC, setIsNewEcrfC] = useState(false);

  const [cohortNextLotDatesList, setCohortNextLotDatesList] = useState(
    new Map(),
  );
  const [q40PeriodEndDateList, setQ40PeriodEndDateList] = useState(new Map());

  const [ndmmAvailable, setNdmmAvailable] = useState(false);

  const [elrexDuplicateDates, setElrexDuplicateDates] = useState(false);
  const [linvoDuplicateDates, setLinvoDuplicateDates] = useState(false);

  const [isElrexvayliSelected, setElrexvayliSelected] = useState(false);
  const [isLinvoveySelected, setLinvoveySelected] = useState(false);

  const [indexDateRRMM_elrHard, setIndexDateRRMM_elrHard] = useState(null);
  const [indexDateRRMM_linvoHard, setIndexDateRRMM_linvoHard] = useState(null);

  const [elrNumberofAdministrator, setElrNumberofAdministrator] =
    useState(null);
  const [linvoNumberofAdministrator, setLinvoNumberofAdministrator] =
    useState(null);

  const [setupElrDose3_WarningHard, setSetupElrDose3_WarningHard] =
    useState("");
  const [setupLinvoDose3_WarningHard, setSetupLinvoDose3_WarningHard] =
    useState("");

  const [setupElrDose1st_WarningHard, setSetupElrDose1st_WarningHard] =
    useState("");
  const [setupLinvoDose1st_WarningHard, setSetupLinvoDose1st_WarningHard] =
    useState("");

  const [setupElrDose1stSubs_WarningHard, setSetupElrDose1stSubs_WarningHard] =
    useState("");
  const [
    setupLinvoDose1stSubs_WarningHard,
    setSetupLinvoDose1stSubs_WarningHard,
  ] = useState("");

  const [setupElr_WarningHard, setSetupElr_WarningHard] = useState(false);
  const [setupLinvo_WarningHard, setSetupLinvo_WarningHard] = useState(false);

  const [setupElrQ26_WarningHard, setSetupElrQ26_WarningHard] = useState(false);
  const [setupLinvoQ26_WarningHard, setSetupLinvoQ26_WarningHard] =
    useState(false);

  // const [
  //   elrNumberofAdministratorMatching,
  //   setElrNumberofAdministratorMatching,
  // ] = useState(false);
  // const [
  //   linvoNumberofAdministratorMatching,
  //   setLinvoNumberofAdministratorMatching,
  // ] = useState(false);

  const [isElrLastAdminDateValid, setIsElrLastAdminDateValid] = useState(true);
  const [isLinvoLastAdminDateValid, setIsLinvoLastAdminDateValid] =
    useState(true);

  const [elrvayliDateRRMM, setElrvayliDateRRMM] = useState(
    "[Bispecifics/CAR-T end of follow up]",
  );
  const [linvoveyDateRRMM, setLinvoveyDateRRMM] = useState(
    "[Bispecifics/CAR-T end of follow up]",
  );

  const [Q39ElrexConditionMet, setQ39ElrexConditionMet] = useState(false);
  const [Q39LinvoConditionMet, setQ39LinvoConditionMet] = useState(false);

  // Q36 Warnings
  const [elrexfioDuplicatePhaseWarning, setElrexfioDuplicatePhaseWarning] =
    useState(null);
  const [linvoDuplicatePhaseWarning, setLinvoDuplicatePhaseWarning] =
    useState(null);
  const [elrexfioMissingStepUpWarning, setElrexfioMissingStepUpWarning] =
    useState(false);
  const [linvoMissingStepUpWarning, setLinvoMissingStepUpWarning] =
    useState(false);

  // Q36.1 - Warning for missing hospitalization when inpatient setting exists
  const [elrInpatientHospMissing, setElrInpatientHospMissing] = useState(false);
  const [linvoInpatientHospMissing, setLinvoInpatientHospMissing] =
    useState(false);

  const [rrmmAvailable, setRRMMAvailable] = useState(false);

  const [elrexIndexDate, setElrexIndexDate] = useState("");
  const [linvoIndexDate, setLinvoIndexDate] = useState("");
  const [elrexInitiationDate, setElrexInitiationDate] = useState("");
  const [linvoInitiationDate, setLinvoInitiationDate] = useState("");
  const [endOfFollowUpDate, setEndOfFollowUpDate] = useState("");

  const [elrexfioLOTStartDate, setElrexfioLOTStartDate] = useState("");
  const [linvoseltamabLOTStartDate, setLinvoseltamabLOTStartDate] =
    useState("");
  const [patientDeathDate, setPatientDeathDate] = useState(null);

  const [doseStrength_Elr, setDoseStrength_Elr] = useState(null);
  const [doseStrength_Linvo, setDoseStrength_Linvo] = useState(null);

  const [showHideHyperlinkModalQ36Elrex, setShowHideHyperlinkModalQ36Elrex] =
    useState(false);
  const [
    showHideHyperlinkModalQ36Linvoseltamab,
    setShowHideHyperlinkModalQ36Linvoseltamab,
  ] = useState(false);

  const Q26AllRecords = {
    bispecificQ26ModelId: 0,
    cohortTreatmentId: 0,
    lotNumber: null,

    firstAdministrationDt: null,
    lastAdministrationDt: null,
  };

  const Q331AAllRecords = {
    bispecificQ26ModelId: 0,
    cohorttreatmentid: 0,
    lotNumber: null,

    firstAdministrationDt: null,
    lastAdministrationDt: null,
  };

  const NdmmAllRecords = {
    ndmmModelId: 0,
    cohorttreatmentid: 0,
    lotNumber: null,

    startLotDt: null,
    endLotDt: null,
  };

  const [bispecificQ26Data, setBispecificQ26Data] = useState([Q26AllRecords]);
  const [bispecificQ331AData, setBispecificQ331AData] = useState([
    Q331AAllRecords,
  ]);

  const [bispecificQ31Q32Data, setBispecificQ31Q32Data] = useState([
    Q26AllRecords,
  ]);

  const [ndmmLotData, setNdmmLotData] = useState([NdmmAllRecords]);

  const [showLineOfTherapyModal, setShowLineOfTherapyModal] = useState(false);

  const [elrexLOTStartDate, setElrexLOTStartDate] = useState("");
  const [linvoLOTStartDate, setLinvoLOTStartDate] = useState("");

  const [q40Elr_WarningHard, setQ40Elr_WarningHard] = useState(false);
  const [q40Lin_WarningHard, setQ40Lin_WarningHard] = useState(false);

  const { ShowToast, ToggleLoader, HandleSessionTimeout } =
    useContext(AppContext);

  const [showTopErrMsg, setShowTopErrMsg] = useState(false);

  let cohortTreatmentModelListt = [];
  if (storedData) {
    cohortTreatmentModelListt = JSON.parse(storedData);
  }
  const TTIsPresent = cohortTreatmentModelListt.some(
    (treatment) =>
      (treatment.cohortTreatmentID === 9 ||
        treatment.cohortTreatmentID === 10) &&
      treatment.isEligible === true,
  );

  const ElrexvayliRow = {
    elrexlinvodosagebridgeid: 0,
    cohorttreatmentid: isElrexvayliSelected ? RRMMCohortTreatment.ELREXFIO : 0, // GetLocalStorageData("elrexvayliID") || 0

    //ELREXFIO
    dose_administration_elr: null,
    dosing_phase_elr: null,
    siteofcare_elr: null,
    elr_dose_strength_rbl: null,
    elr_dose_strength_mg: "",
    elr_dose_strength_mg_kg: "",
    elr_dose_strength_mgm: "",
    elr_dose_weekly_strength_mg_kgHard: false,
    elr_dose_strength_mg_kgHard: false,
    elr_dose_weekly_strength_mg_kgRange: false,
    elr_dose_strength_mg_kgRange: false,

    elr_dose_freq_rbl: null,
    elr_dose_freq_week: "",
    elr_dose_freq_onceweek: "",
    elr_dose_freq_hard_error: false,
    elr_dose_weekly_freq_hard_error: false,
    elr_dose_weekly_freq_RangeError: false,

    dose_administration_elr_dt: "",
    dt_admission_elr_dt: "",
    dt_discharge_elr_dt: "",

    dosing_phase_elrHard_othEmpty: false,
    dose_administration_elrHard: false,
    dosing_phase_elrHard: false,
    siteofcare_elrHard: false,
    dt_admission_elrHard: false,
    dt_discharge_elrHard: false,

    dose_administration_elrHard_dt: false,
    Invalid_dose_administration_elr_dt: false,
    dt_admission_elrHard_dt: false,
    dt_discharge_elrHard_dt: false,
    dt_admission_elrHard_dt_check: false,
    dt_discharge_elrHard_dt_check: false,
    // dt_elr_greater_prevCheck: false,
    dt_elr_date_out_of_bound: false,
    dose_administration_elr_dt_before_eof: false,

    Q36ElrexConditionMet: false,

    //new
    dosing_phase_oth_txt_elr: "",
    // true only for rows appended via the "Add row" button (AddElrexvayliRow),
    // never for row 0 (auto-populated on load) or rows seeded from server data.
    isManuallyAdded: false,
  };

  const LinvoveyRow = {
    elrexlinvodosagebridgeid: 0,
    cohorttreatmentid: isLinvoveySelected
      ? RRMMCohortTreatment.Linvoseltamab
      : 0, //GetLocalStorageData("linvoveyID") || 0

    //LINVOSELTAMAB

    dose_administration_linvo: null,
    dosing_phase_linvo: null,
    siteofcare_linvo: null,
    //dt_admission_linvo: null,
    // dt_discharge_linvo: null,
    linvo_dose_strength_rbl: null,
    linvo_dose_strength_mg: "",
    linvo_dose_strength_mg_kg: "",
    linvo_dose_strength_mgm: "",
    linvo_dose_strength_mg_kgHard: false,
    linvo_dose_strength_mg_kgRange: false,
    linvo_dose_weekly_strength_mg_kgHard: false,

    linvo_dose_freq_rbl: null,
    linvo_dose_freq_week: "",
    linvo_dose_freq_onceweek: "",
    linvo_dose_freq_hard_error: false,
    linvo_dose_weekly_freq_hard_error: false,
    linvo_dose_weekly_freq_RangeError: false,

    dose_administration_linvo_dt: "",
    Invalid_dose_administration_linvo_dt: false,
    dt_admission_linvo_dt: "",
    dt_discharge_linvo_dt: "",

    dose_administration_linvoHard: false,
    dosing_phase_linvoHard: false,
    siteofcare_linvoHard: false,
    dt_admission_linvoHard: false,
    dt_discharge_linvoHard: false,

    dose_administration_linvoHard_dt: false,
    dt_admission_linvoHard_dt: false,
    dt_discharge_linvoHard_dt: false,

    dt_admission_linvoHard_dt_check: false,
    dt_discharge_linvoHard_dt_check: false,

    // dt_linvo_greater_prevCheck: false,
    dt_linvo_date_out_of_bound: false,

    //Q36LinvoConditionMet: false,
    dosing_phase_oth_txt_linvo: "",
    dosing_phase_linvoHard_othEmpty: false,

    duplicateDateCheck: false,
    sequentialLOTError: false,
    // true only for rows appended via the "Add row" button (AddLinvoveyRow),
    // never for row 0 (auto-populated on load) or rows seeded from server data.
    isManuallyAdded: false,
  };
  //36.1
  const HospitalizationRow = {
    elrexlinvohospitalizationbridgeid: 0,
    elrexlinvodosagebridgeid: 0,
    cohorttreatmentid: 0,
    //B
    dt_admission: null,
    dt_admission_dt: "",
    dt_admission_Hard: false,
    dt_admission_Hard_dt: false,
    dt_admission_out_of_bound: false,
    dt_admission_before_eof: false,
    Invalid_admission_Hard_dt: false,

    //C
    dt_discharge: null,
    dt_discharge_dt: "",
    dt_discharge_Hard: false,
    dt_discharge_Hard_dt: false,
    dt_discharge_Hard_dt_check: false,
    dt_discharge_out_of_bound: false,
    dateRangeThirty: false,
    Invalid_dt_discharge_Hard: false,
    dt_discharge_before_eof: false,
    //D
    hospReason1: false,
    hospReason2: false,
    hospReason3: false,
    hospReason4: false,
    hospReason5: false,
    hospReason6: false,
    hospReason7: false,
    hosp_reason_oth_txt: "",
    hospReasonUnk: false,
    hospReasonEmptyHard: false,
    hospReasonTxtEmpty: false,
  };

  const Q37ElrexRow = {
    elrexlinvocharacteristicsid: 0,
    cohorttreatmentid: isElrexvayliSelected ? RRMMCohortTreatment.ELREXFIO : 0, //GetLocalStorageData("elrexvayliID") || 0
    // Q37 retired
    // Q37ElrexvayliRd: null,

    // q37ElrexvayliEmpty: false,

    // q37_Elr_Dose_week_frquencyRd: null,
    // q37_Elr_Dose_week_frquency_txt: "",
    // q37_Elr_Dose_week_frquencyRd_Empty: false,
    // q37_Elr_Dose_week_frquency_txt_Empty: false,
    // q37_Elr_Dose_week_freq_range: false,
    // q37SubsequentDoseConflict: false,

    // q37ElrDosageRd: null, // 1 = 76 mg, 2 = Other, 99 = Unknown
    // q37ElrDosageOtherTxt: "",
    // q37ElrDosageRdEmpty: false,
    // q37ElrDosageOtherTxtEmpty: false,

    //40
    Q40dosingfreqRd: null,
    Q40IgILevelRd: null,
    Q40init_dt_Rd: null,
    Q40init_dt: "",
    primaryProf: false,
    secondaryProf: false,
    otherReson: false,
    InvalidQ40init_dt: false,
    init_dt_before_eof: false,
    init_dt_full_chain_invalid: false,

    dosing_freq_oth_txt: "",
    // Q37 retired
    // showDateRedFreqElr: false,

    //40 validations state
    Q40DosingFreqEmptyHard: false,
    Q40DosingFreqTxtEmptyHard: false,
    Q40IgGEmptyHard: false,
    Q40Init_dtHard: false,
    Q40Init_RdHard: false,
    Q40ReasonEmptyHard: false,

    //40.C
    ivigInitRd_elrC: null,
    ivigInitDt_elrC: "",
    ivigInitRdHard_elrC: false,
    ivigInitDtHard_elrC: false,
    ivigInitDtInvalid_elrC: false,
    ivigInitDtBeforeEof_elrC: false,
    ivigInitDtFullChainInvalid_elrC: false,

    ivigDosingType_elrC: null,
    ivigDosingTypeEmptyHard_elrC: false,

    ivigDosingFreqRd_elrC: null,
    ivigDosingFreqTimesTxt_elrC: "",
    ivigDosingFreqWeeksTxt_elrC: "",
    ivigDosingFreqOtherTxt_elrC: "",
    ivigDosingFreqEmptyHard_elrC: false,
    ivigDosingFreqTxtEmptyHard_elrC: false,
    ivigDosingFreqRangeInvalid_elrC: false,

    ivigReasonPrimary_elrC: false,
    ivigReasonSecondary_elrC: false,
    ivigReasonOther_elrC: false,
    ivigReasonOtherTxt_elrC: "",
    ivigReasonUnk_elrC: false,
    ivigReasonEmptyHard_elrC: false,
    ivigReasonOtherTxtEmptyHard_elrC: false,

    ivigDoseStrengthRd_elrC: null,
    ivigDoseStrengthGTxt_elrC: "",
    ivigDoseStrengthMgKgTxt_elrC: "",
    ivigDoseStrengthOtherTxt_elrC: "",
    ivigDoseStrengthEmptyHard_elrC: false,
    ivigDoseStrengthRangeInvalid_elrC: false,
    ivigDoseStrengthMgKgRangeInvalid_elrC: false,
    ivigDoseStrengthOtherTxtEmptyHard_elrC: false,

    // [H] Body weight ±1 month of initiation date (1 = kg, 2 = lbs, 99 = Unknown)
    ivigBodyWeightRd_elrC: null,
    ivigBodyWeightKgTxt_elrC: "",
    ivigBodyWeightLbsTxt_elrC: "",
    ivigBodyWeightEmptyHard_elrC: false,
    ivigBodyWeightRangeInvalid_elrC: false,

    ivigLastIgg_elrC: null,
    ivigLastIggEmptyHard_elrC: false,

    //39
    tocilizumab_elr: false,
    Granulocyte: false,
    noneoftheabove_elr: null,
    SteroidsElr: false,

    //39 - 1.1
    // Q39.1 retired
    // cytokin_elr: false,
    // secondary_prophylactic_crs_elr: false,
    // crs_elr: false,
    // neurotoxicity_elr: false,
    // secondary_neurotoxicity_elr: false,
    // tmt_neurotoxicity_elr: false,
    // tmt_elr_unknown: false,
    // Q39elrReasonHard: false,

    // New Phase 39.2
    steroid_pre_tmt_elr: false,
    cytokin_elr_steroids: false,
    secondary_prophylactic_elr_steroids: false,
    crs_elr_steroids: false,
    neurotoxicity_elr_steroids: false,
    secondary_neurotoxicity_elr_steroids: false,
    tmt_neurotoxicity_elr_steroids: false,
    tmt_steroids_elr_unknown: false,

    //ELREX New column
    betamethasone_elr: false,
    betamethasone_elrNum: "",

    betamethasone_elr_txtEmpty: false,

    dexamethasone_elr: false,
    dexamethasone_elrNum: "",

    dexamethasone_elr_txtEmpty: false,

    hydrocortisone_elr: false,
    hydrocortisone_elrNum: "",

    hydrocortisone_elr_txtEmpty: false,

    methylprednisolone: false,
    methylprednisoloneNum: "",
    methylprednisolone_txtEmpty: false,

    prednisolone_elr: false,
    prednisolone_elrNum: "",
    prednisolone_elr_txtEmpty: false,

    prednisone_elr: false,
    prednisone_elrNum: "",

    prednisone_elr_txtEmpty: false,

    triamcinolone_elr: false,
    triamcinolone_elrNum: "",
    triamcinolone_elr_txtEmpty: false,

    dose_name_elr_unknown: false,

    name_dose_steroidsHardEmpty: false,
    steroids_elr_txtEmpty: false,

    //39.3
    prophylactic_use_elr: false,
    secondary_prophylactic_use_elr: false,
    oth_use_elr: false,
    unknown_elr: null,

    Q39ElrEmptyHard: false,
    Q39elrGranduloEmptyHard: false,

    Q39elrSteroidsEmptyHard: false,

    //39.C
    tocilizumab_elrC: false,
    steroids_elrC: false,
    granulocyte_elrC: false,
    noneoftheabove_elrC: null,
    Q39CEmptyHard: false,

    //39.1.C - Tocilizumab administration (single instance, not a repeater)
    tociliAdmsDateRD_elrC: null, // 1 = date known, 99 = unknown
    tociliAdmsDate_elrC: "",
    tociliAdmsDateInvalid_elrC: false,
    tociliAdmsDateRangeInvalid_elrC: false,
    tociliAdmsDateEmpty_elrC: false,

    tociliDoseRd_elrC: null, // 1 = dose entered, 2 = Other, 99 = Unknown
    tociliDoseMgkg_elrC: "",
    tociliDoseOther_elrC: "",
    tociliDoseEmptyHard_elrC: false,
    tociliDoseMgkgEmptyHard_elrC: false,
    tociliDoseOtherEmptyHard_elrC: false,

    tociliReasonPretratment_elrC: false,
    tociliReasonPrimarycrs_elrC: false,
    tociliReasonSecondarycrs_elrC: false,
    tociliReasonSupportivecrs_elrC: false,
    tociliReasonPrimaryneuro_elrC: false,
    tociliReasonSecondaryneuro_elrC: false,
    tociliReasonSupportiveneuro_elrC: false,
    tociliReasonUnk_elrC: false,
    Q391CReasonEmptyHard: false,

    //39.2.C
    steroidsmedicationidElrC: 0,
    betamethasone_elrC: false,
    betamethasone_elrCNum: "",
    dexamethasone_elrC: false,
    dexamethasone_elrCNum: "",
    hydrocortisone_elrC: false,
    hydrocortisone_elrCNum: "",
    methylprednisolone_elrC: false,
    methylprednisolone_elrCNum: "",
    prednisolone_elrC: false,
    prednisolone_elrCNum: "",
    prednisone_elrC: false,
    prednisone_elrCNum: "",
    triamcinolone_elrC: false,
    triamcinolone_elrCNum: "",
    dose_name_elrC_unknown: false,
    name_dose_steroidsHardEmpty_elrC: false,
    steroids_elrC_txtEmpty: false,
    steroidsDoseRangeInvalid_elrC: false,
    steroidReasonPretratment_elrC: false,
    steroidReasonPrimarycrs_elrC: false,
    steroidReasonSecondarycrs_elrC: false,
    steroidReasonSupportivecrs_elrC: false,
    steroidReasonPrimaryneuro_elrC: false,
    steroidReasonSecondaryneuro_elrC: false,
    steroidReasonSupportiveneuro_elrC: false,
    steroidReasonUnk_elrC: false,
    Q392CReasonEmptyHard: false,

    //39.3.C
    granulocytemedicationidElrC: 0,
    prophylactic_use_elrC: false,
    secondary_prophylactic_use_elrC: false,
    oth_use_elrC: false,
    unknown_elrC: null,
    Q393CReasonEmptyHard: false,
  };

  // Q39.1.A
  const q391AElrRow = {
    cohorttreatmentid: RRMMCohortTreatment.ELREXFIO,
    tocilizumabmedicationid: 0,
    patientid: 0,
    //checkbox
    primaryProphylacticCrs_elr: false,
    secondaryProphylacticCrs_elr: false,
    supportiveCareCrs_elr: false,
    primaryProphylacticNeurologic_elr: false,
    secondaryProphylacticNeurologic_elr: false,
    supportiveCareNeurologic_elr: false,
    medicationReasonUnk_elr: false,
    q39ElrReasonEmpty: false,

    //date
    administrationDate_elr: null,
    administrationDateRD_elr: null,
    q39ElrInvalidAdministrationDate: false,
    q39ElrAdministrationDateEmpty: false,
    q39ElrAdministrationRDEmpty: false,
  };

  const q391ALinvoRow = {
    cohorttreatmentid: RRMMCohortTreatment.Linvoseltamab,
    tocilizumabmedicationid: 0,
    patientid: 0,
    //checkbox
    primaryProphylacticCrs_linvo: false,
    secondaryProphylacticCrs_linvo: false,
    supportiveCareCrs_linvo: false,
    primaryProphylacticNeurologic_linvo: false,
    secondaryProphylacticNeurologic_linvo: false,
    supportiveCareNeurologic_linvo: false,
    medicationReasonUnk_linvo: false,
    q39LinvoReasonEmpty: false,

    //date
    administrationDate_linvo: null,
    administrationDateRD_linvo: null,
    q39LinvoInvalidAdministrationDate: false,
    q39LinvoAdministrationDateEmpty: false,
    q39LinvoAdministrationRDEmpty: false,
  };

  const Q37LinvoRow = {
    elrexlinvocharacteristicsid: 0,
    cohorttreatmentid: isLinvoveySelected
      ? RRMMCohortTreatment.Linvoseltamab
      : 0, //GetLocalStorageData("linvoveyID") || 0
    // Q37 retired
    // Q37LinvoveyRd: null,

    // q37LinvoveyEmpty: false,

    // showDateRedFreqLinvo: false,

    //New Column Q37
    // q37_Linvo_Dose_week_frquencyRd: null,
    // q37_Linvo_Dose_week_frquency_txt: "",
    // q37_Linvo_Dosage_Rd: null,
    // q37_Linvo_Dosage_OtherTxt: "",

    // q37_Linvo_Dose_week_frquencyRd_Empty: false,
    // q37_Linvo_Dose_week_frquency_txt_Empty: false,
    // q37_Linvo_Dose_week_freq_range: false,

    // q37LinvoSubsequentDoseConflict: false,

    // q37_Linvo_Dosage_Rd_Empty: false,
    // q37_Linvo_Dosage_OtherTxt_Empty: false,

    //linvoseltamab new column
    betamethasone_linvo: false,
    betamethasone_linvoNum: "",

    dexamethasone_linvo: false,
    dexamethasone_linvoNum: "",

    hydrocortisone_linvo: false,
    hydrocortisone_linvoNum: "",

    methylprednisolone_linvo: false,
    methylprednisolone_linvoNum: "",

    prednisolone_linvo: false,
    prednisolone_linvoNum: "",

    prednisone_linvo: false,
    prednisone_linvoNum: "",

    triamcinolone_linvo: false,
    triamcinolone_linvoNum: "",

    dose_name_linvo_unknown: false,

    name_dose_steroids_linvoHardEmpty: false,
    steroids_linvo_txtEmpty: false,

    //39
    tocilizumab_linvo: false,
    Steroids: false,
    granulocyte_linvo: false,
    noneoftheabove_linvo: null,

    //39.1
    // Q39.1 retired
    // cytokin_linvo: false,
    // secondary_prophylactic_crs_linvo: false,
    // crs_linvo: false,
    // neurotoxicity_linvo: false,
    // secondary_neurotoxicity_linvo: false,
    // tmt_neurotoxicity_linvo: false,
    // tmt_linvoseltamab_unknown: false,

    //39.2
    steroid_pre_tmt_linvo: false,
    cytokin_linvo_steroids: false,
    secondary_prophylactic_linvo_steroids: false,

    crs_linvo_steroids: false,
    neurotoxicity_linvo_steroids: false,
    secondary_neurotoxicity_linvo_steroids: false,
    tmt_neurotoxicity_linvo_steroids: false,
    tmt_linvo_unknown: false,

    //Q39.3
    prophylactic_use_linvo: false,
    secondary_prophylactic_use_linvo: false,
    oth_use_linvo: false,
    unknown_linvo: null,

    Q39LinvoEmptyHard: false,
    // Q39.1 retired
    // Q39linvoReasonHard: false,
    Q39linvoSteroidsEmptyHard: false,
    Q39linvoGranduloEmptyHard: false,

    //39.C
    tocilizumab_linvoC: false,
    steroids_linvoC: false,
    granulocyte_linvoC: false,
    noneoftheabove_linvoC: null,
    Q39CEmptyHard: false,

    //39.1.C - Tocilizumab administration (single instance, not a repeater)
    tociliAdmsDateRD_linvoC: null, // 1 = date known, 99 = unknown
    tociliAdmsDate_linvoC: "",
    tociliAdmsDateInvalid_linvoC: false,
    tociliAdmsDateRangeInvalid_linvoC: false,
    tociliAdmsDateEmpty_linvoC: false,

    tociliDoseRd_linvoC: null, // 1 = dose entered, 2 = Other, 99 = Unknown
    tociliDoseMgkg_linvoC: "",
    tociliDoseOther_linvoC: "",
    tociliDoseEmptyHard_linvoC: false,
    tociliDoseMgkgEmptyHard_linvoC: false,
    tociliDoseOtherEmptyHard_linvoC: false,

    tociliReasonPretratment_linvoC: false,
    tociliReasonPrimarycrs_linvoC: false,
    tociliReasonSecondarycrs_linvoC: false,
    tociliReasonSupportivecrs_linvoC: false,
    tociliReasonPrimaryneuro_linvoC: false,
    tociliReasonSecondaryneuro_linvoC: false,
    tociliReasonSupportiveneuro_linvoC: false,
    tociliReasonUnk_linvoC: false,
    Q391CReasonEmptyHard: false,

    //39.2.C
    steroidsmedicationidLinvoC: 0,
    betamethasone_linvoC: false,
    betamethasone_linvoCNum: "",
    dexamethasone_linvoC: false,
    dexamethasone_linvoCNum: "",
    hydrocortisone_linvoC: false,
    hydrocortisone_linvoCNum: "",
    methylprednisolone_linvoC: false,
    methylprednisolone_linvoCNum: "",
    prednisolone_linvoC: false,
    prednisolone_linvoCNum: "",
    prednisone_linvoC: false,
    prednisone_linvoCNum: "",
    triamcinolone_linvoC: false,
    triamcinolone_linvoCNum: "",
    dose_name_linvoC_unknown: false,
    name_dose_steroidsHardEmpty_linvoC: false,
    steroids_linvoC_txtEmpty: false,
    steroidsDoseRangeInvalid_linvoC: false,
    steroidReasonPretratment_linvoC: false,
    steroidReasonPrimarycrs_linvoC: false,
    steroidReasonSecondarycrs_linvoC: false,
    steroidReasonSupportivecrs_linvoC: false,
    steroidReasonPrimaryneuro_linvoC: false,
    steroidReasonSecondaryneuro_linvoC: false,
    steroidReasonSupportiveneuro_linvoC: false,
    steroidReasonUnk_linvoC: false,
    Q392CReasonEmptyHard: false,

    //39.3.C
    granulocytemedicationidLinvoC: 0,
    prophylactic_use_linvoC: false,
    secondary_prophylactic_use_linvoC: false,
    oth_use_linvoC: false,
    unknown_linvoC: null,
    Q393CReasonEmptyHard: false,

    //40
    q40DosingFreqRd_linvo: null,
    q40IgILevelRd_linvo: null,
    q40Init_Dt_Rd_linvo: null,
    q40Init_Dt_linvo: "",
    primaryProf_linvo: false,
    secondaryProf_linvo: false,
    otherReson_linvo: false,
    invalidQ40Init_dt_linvo: false,
    init_dt_before_eof_linvo: false,
    init_dt_full_chain_invalid_linvo: false,

    dosing_Freq_Oth_Txt_linvo: "",

    //40 validations state
    Q40LinvoDosingFreqEmptyHard: false,
    Q40LinvoDosingFreqTxtEmptyHard: false,
    Q40LinvoIgGEmptyHard: false,
    Q40LinvoInit_dtHard: false,
    Q40LinvoInit_RdHard: false,
    Q40LinvoReasonEmptyHard: false,

    //40.C
    ivigInitRd_linvoC: null,
    ivigInitDt_linvoC: "",
    ivigInitRdHard_linvoC: false,
    ivigInitDtHard_linvoC: false,
    ivigInitDtInvalid_linvoC: false,
    ivigInitDtBeforeEof_linvoC: false,
    ivigInitDtFullChainInvalid_linvoC: false,

    ivigDosingType_linvoC: null,
    ivigDosingTypeEmptyHard_linvoC: false,

    ivigDosingFreqRd_linvoC: null,
    ivigDosingFreqTimesTxt_linvoC: "",
    ivigDosingFreqWeeksTxt_linvoC: "",
    ivigDosingFreqOtherTxt_linvoC: "",
    ivigDosingFreqEmptyHard_linvoC: false,
    ivigDosingFreqTxtEmptyHard_linvoC: false,
    ivigDosingFreqRangeInvalid_linvoC: false,

    ivigReasonPrimary_linvoC: false,
    ivigReasonSecondary_linvoC: false,
    ivigReasonOther_linvoC: false,
    ivigReasonOtherTxt_linvoC: "",
    ivigReasonUnk_linvoC: false,
    ivigReasonEmptyHard_linvoC: false,
    ivigReasonOtherTxtEmptyHard_linvoC: false,

    ivigDoseStrengthRd_linvoC: null,
    ivigDoseStrengthGTxt_linvoC: "",
    ivigDoseStrengthMgKgTxt_linvoC: "",
    ivigDoseStrengthOtherTxt_linvoC: "",
    ivigDoseStrengthEmptyHard_linvoC: false,
    ivigDoseStrengthRangeInvalid_linvoC: false,
    ivigDoseStrengthMgKgRangeInvalid_linvoC: false,
    ivigDoseStrengthOtherTxtEmptyHard_linvoC: false,

    // [H] Body weight ±1 month of initiation date (1 = kg, 2 = lbs, 99 = Unknown)
    ivigBodyWeightRd_linvoC: null,
    ivigBodyWeightKgTxt_linvoC: "",
    ivigBodyWeightLbsTxt_linvoC: "",
    ivigBodyWeightEmptyHard_linvoC: false,
    ivigBodyWeightRangeInvalid_linvoC: false,

    ivigLastIgg_linvoC: null,
    ivigLastIggEmptyHard_linvoC: false,
  };

  // Q38 retired
  /*
  const Q38ElrexRow = {
    elrexlinvodosefrequencybridgeid: 0,
    cohorttreatmentid: isElrexvayliSelected ? RRMMCohortTreatment.ELREXFIO : 0, //GetLocalStorageData("elrexvayliID") || 0

    Q38ElrexvayliRd: null,
    dt_dosefreq_elr_dt: "",
    Invalid_dt_dosefreq_elr_dt: false,

    Q38ElrexEmptyHard: false,
    Q38DateEmpty: false,
    Q38ElrexDateInvalid: false,

    q38_Elr_Dose_week_frquencyRd: null,
    q38_Elr_Dose_week_frquency_txt: "",

    q38_Elr_Dose_week_frquencyRd_Empty: false,
    q38_Elr_Dose_week_frquency_txt_Empty: false,
    q38ElrDoseFrequencyConflict: false,
  };

  const Q38LinvoRow = {
    elrexlinvodosefrequencybridgeid: 0,
    cohorttreatmentid: isLinvoveySelected
      ? RRMMCohortTreatment.Linvoseltamab
      : 0, //GetLocalStorageData("linvoveyID") || 0

    Q38LinvoveyRd: null,
    dt_dosefreq_linvo_dt: "",

    InvalidQ38Linvoveydate: false,

    Q39LinvoDateInvalid: false,

    Q38LinvoEmptyHard: false,
    Q38LinvoDateEmpty: false,
    Q38LinvoDateInvalid: false,

    q38_Linvo_Dose_week_frquencyRd: null,
    q38_Linvo_Dose_week_frquency_txt: "",
    q38_Linvo_Dosage_Rd: null,
    q38_Linvo_Dosage_OtherTxt: "",

    q38_Linvo_Dose_week_frquencyRd_Empty: false,
    q38_Linvo_Dose_week_frquency_txt_Empty: false,
    q38_Linvo_Dosage_Rd_Empty: false,
    q38_Linvo_Dosage_OtherTxt_Empty: false,
    q38LinvoDoseFrequencyConflict: false,
  };
  */
  const Q38ElrexRow = {};
  const Q38LinvoRow = {};

  // Q38.1.A

  const Q381AElrexRow = {
    teclistamatalquetamabdosefrequencyinfobridgeid: 0,
    cohorttreatmentid: isElrexvayliSelected ? RRMMCohortTreatment.ELREXFIO : 0,

    // Column B - Type of change
    changeType: null, // 1=No changes, 2=Frequency, 3=Strength, 99=Unknown
    changeTypeDoseFrequency: null, // 1=Increase, 2=Decrease
    changeTypeDoseStrength: null, // 1=Increase, 2=Decrease

    // Column C - Reasons (multi-select arrays)
    selectedReasons: [], // Array of bispecifictreatmentdiscontinuationreasonid
    selectedAEReasons: [], // Array of bispecifictreatmentdiscontinuationreasonaeid
    reasonOtherText: "", // For "Other" reason (ID=14)
    reasonUnknown: false,
    neurologicAEReasonList: [],
    // Column D - Pattern BEFORE change
    patterBeforeChangeDoseFrequency: null, // 1=times/week, 2=every X weeks, 99=Unknown
    patterBeforeChangeDoseFrequencyTimesPerWeek: "",

    patterBeforeChangeDoseFrequencyWeek: "", // Integer 0-10
    patterBeforeChangeDoseStrength: null, // 1=mg, 2=mg/kg, 3=mg/m², 99=Unknown

    patterBeforeChangeDoseStrengthMg: "", // For radio value 1
    patterBeforeChangeDoseStrengthMgKg: "", // For radio value 2
    patterBeforeChangeDoseStrengthMgM2: "", // For radio value 3

    // AUTO-009 - true when column D was auto-populated from Q36 (read-only)
    patternBeforeAutoPopulated: false,

    // Column E - Pattern AFTER change
    patterAfterChangeDoseFrequency: null,
    patterAfterChangeDoseFrequencyWeek: "",
    patterAfterChangeDoseFrequencyTimesPerWeek: "",
    patterAfterChangeDoseStrength: null,

    patterAfterChangeDoseStrengthMg: "", // For radio value 1
    patterAfterChangeDoseStrengthMgKg: "", // For radio value 2
    patterAfterChangeDoseStrengthMgM2: "", // For radio value 3

    // Column F - Date of change
    dateChangeRd: null, // 1=Date provided, 99=Unknown
    dateChange: "",

    // ========================================
    // VALIDATION FLAGS - HARD ERRORS
    // ========================================
    // Column B validations
    changeTypeEmpty: false,
    changeTypeDoseFrequencyEmpty: false,
    changeTypeDoseStrengthEmpty: false,

    // Column C validations
    reasonEmpty: false,
    deathDtWarning: false,
    reasonOtherTextEmpty: false,
    aeSubReasonEmpty: false,
    neuroReasonEmpty: false,
    neuroOtherTextEmpty: false,

    // Column D validations (Pattern BEFORE)
    patternBeforeFrequencyEmpty: false,
    patternBeforeFrequencyRangeInvalid: false,

    patternBeforeStrengthEmpty: false,
    patternAfterFrequencyRangeInvalid: false,

    // Column E validations (Pattern AFTER)
    patternAfterFrequencyEmpty: false,
    patternAfterStrengthEmpty: false,
    patternAfterStrengthRangeInvalid: false,

    // Column F validations
    dateChangeRdEmpty: false,
    dateChangeEmpty: false,
    invalidDateChange: false,
    dateChangeOutOfBound: false,
    dateChangeBeforeEOF: false,

    // ========================================
    // CONFLICT VALIDATIONS (HARD ERRORS)
    // ========================================
    frequencyIncreaseConflict: false,
    frequencyDecreaseConflict: false,
    strengthIncreaseConflict: false,
    strengthDecreaseConflict: false,

    // ========================================
    // WARNING FLAGS
    // ========================================
    patternBeforeUnknownWarning: false,
    patternAfterUnknownWarning: false,
  };

  const [Q381AElrexDetails, setQ381AElrexDetails] = useState([Q381AElrexRow]);
  const Q381ALinvoRow = {
    teclistamatalquetamabdosefrequencyinfobridgeid: 0,
    cohorttreatmentid: isLinvoveySelected
      ? RRMMCohortTreatment.Linvoseltamab
      : 0,

    // Column B - Type of change
    changeType: null, // 1=No changes, 2=Frequency change, 3=Strength change, 99=Unknown
    changeTypeDoseFrequency: null, // 1=Increase, 2=Decrease (only if changeType === 2)
    changeTypeDoseStrength: null, // 1=Increase, 2=Decrease (only if changeType === 3)

    // Column C - Reasons for change (multi-select)
    selectedReasons: [],
    selectedAEReasons: [],
    neurologicAEReasonList: [],
    reasonOtherText: "",
    reasonUnknown: false,
    reasonList: [],
    // Column D - Pattern BEFORE change
    patterBeforeChangeDoseFrequency: null,
    patterBeforeChangeDoseFrequencyTimesPerWeek: "",

    patterBeforeChangeDoseFrequencyWeek: "",
    patterBeforeChangeDoseStrength: null,

    patterBeforeChangeDoseStrengthMg: "",
    patterBeforeChangeDoseStrengthMgKg: "",
    patterBeforeChangeDoseStrengthMgM2: "",

    // AUTO-009 - true when column D was auto-populated from Q36 (read-only)
    patternBeforeAutoPopulated: false,

    // Column E - Pattern AFTER change
    patterAfterChangeDoseFrequency: null,
    patterAfterChangeDoseFrequencyTimesPerWeek: "",
    patterAfterChangeDoseFrequencyWeek: "",
    patterAfterChangeDoseStrength: null,

    patterAfterChangeDoseStrengthMg: "",
    patterAfterChangeDoseStrengthMgKg: "",
    patterAfterChangeDoseStrengthMgM2: "",

    // Column F - Date of change
    dateChangeRd: null, // 1=Date provided, 99=Unknown
    dateChange: "", // DateTime

    // Validation flags
    changeTypeEmpty: false,
    changeTypeDoseFrequencyEmpty: false,
    changeTypeDoseStrengthEmpty: false,
    reasonEmpty: false,
    deathDtWarning: false,
    reasonOtherTextEmpty: false,
    patternBeforeFrequencyEmpty: false,
    patternBeforeFrequencyRangeInvalid: false,
    patternBeforeStrengthEmpty: false,
    patternAfterFrequencyEmpty: false,
    patternAfterFrequencyRangeInvalid: false,
    patternAfterStrengthEmpty: false,
    patternAfterStrengthRangeInvalid: false,
    dateChangeEmpty: false,
    dateChangeRdEmpty: false,
    invalidDateChange: false,
    dateChangeOutOfBound: false,
    dateChangeBeforeEOF: false,
    aeSubReasonEmpty: false,
    neuroReasonEmpty: false,
    neuroOtherTextEmpty: false,

    // Conflict validations
    frequencyIncreaseConflict: false,
    frequencyDecreaseConflict: false,
    strengthIncreaseConflict: false,
    strengthDecreaseConflict: false,

    // Warning flags
    patternBeforeUnknownWarning: false,
    patternAfterUnknownWarning: false,
  };

  const [Q381ALinvoDetails, setQ381ALinvoDetails] = useState([Q381ALinvoRow]);

  // Q38.2.A
  const Q382AElrexRow = {
    teclistamatalquetamabdosefrequencyinfobridgeid: 0,
    cohorttreatmentid: isElrexvayliSelected ? RRMMCohortTreatment.ELREXFIO : 0,

    // Column B - Type of change
    changeType: null, // 1=No changes, 2=dose interruption, 99=Unknown

    // Column C - Reasons (multi-select arrays)
    selectedReasons: [], // Array of bispecifictreatmentdiscontinuationreasonid
    selectedAEReasons: [], // Array of bispecifictreatmentdiscontinuationreasonaeid
    reasonOtherText: "", // For "Other" reason (ID=14)
    reasonUnknown: false,
    neurologicAEReasonList: [],

    // Column D - Pattern BEFORE change
    startDateChangeRd: null, // 1=Date provided, 99=Unknown
    startDateChange: "",

    // Column E - Date of change
    endDateChangeRd: null, // 1=Date provided, 99=Unknown
    endDateChange: "",

    // ========================================
    // VALIDATION FLAGS - HARD ERRORS
    // ========================================
    // Column B validations
    changeTypeEmpty: false,

    // Column C validations
    reasonEmpty: false,
    deathDtWarning: false,
    reasonOtherTextEmpty: false,
    aeSubReasonEmpty: false,
    neuroReasonEmpty: false,
    neuroOtherTextEmpty: false,

    // Column D validations
    startDateChangeRdEmpty: false,
    startDateChangeEmpty: false,
    invalidStartDateChange: false,
    startDateChangeOutOfBound: false,

    // Column E validations
    endDateChangeRdEmpty: false,
    endDateChangeEmpty: false,
    invalidEndDateChange: false,
    endDateChangeOutOfBound: false,

    // ========================================
    // WARNING FLAGS
    // ========================================
    interruptionStartDateUnknownWarning: false,
    interruptionEndDateUnknownWarning: false,
  };

  const [Q382AElrexDetails, setQ382AElrexDetails] = useState([Q382AElrexRow]);
  const Q382ALinvoRow = {
    teclistamatalquetamabdosefrequencyinfobridgeid: 0,
    cohorttreatmentid: isLinvoveySelected
      ? RRMMCohortTreatment.Linvoseltamab
      : 0,

    // Column B - Type of change
    changeType: null, // 1=No changes, 2=Frequency change, 3=Strength change, 99=Unknown

    // Column C - Reasons for change (multi-select)
    selectedReasons: [],
    selectedAEReasons: [],
    neurologicAEReasonList: [],
    reasonOtherText: "",
    reasonUnknown: false,
    reasonList: [],

    // Column D - start date change
    startDateChangeRd: null, // 1=Date provided, 99=Unknown
    startDateChange: "",

    // Column E - end Date of change
    endDateChangeRd: null, // 1=Date provided, 99=Unknown
    endDateChange: "", // DateTime

    // Validation flags
    changeTypeEmpty: false,

    reasonEmpty: false,
    deathDtWarning: false,
    reasonOtherTextEmpty: false,
    aeSubReasonEmpty: false,
    neuroReasonEmpty: false,
    neuroOtherTextEmpty: false,

    // Column D validations
    startDateChangeRdEmpty: false,
    startDateChangeEmpty: false,
    invalidStartDateChange: false,
    startDateChangeOutOfBound: false,

    // Column E validations
    endDateChangeRdEmpty: false,
    endDateChangeEmpty: false,
    invalidEndDateChange: false,
    endDateChangeOutOfBound: false,

    // Warning flags
    interruptionStartDateUnknownWarning: false,
    interruptionEndDateUnknownWarning: false,
  };

  const [Q382ALinvoDetails, setQ382ALinvoDetails] = useState([Q382ALinvoRow]);

  const Q401aElrRow = {
    tectalivigreceivedbridgeid: 0,
    cohorttreatmentid: isElrexvayliSelected ? RRMMCohortTreatment.ELREXFIO : 0, //GetLocalStorageData("techvayliID") || 0

    Q401ainit_dt_Rd_Elr: null,
    Q401ainit_Elr_dt: "",
    Q401aInit_Elr_RdHard: false,
    Q401aInit_Elr_dtHard: false,
    Q401aInvalidQ401ainit_Elr_dt: false,
    Q401aInit_Elr_dt_before_eof: false,

    Q401aIntendedDosing_Elr: null,
    Q401aIntendedDosing_Elr_WeekValue: "",
    Q401aIntendedDosing_Elr_MonthValue: "",
    Q401aIntendedDosing_Elr_OnceWeekValue: "",
    Q401aIntendedDosing_Elr_OnceMonthValue: "",
    Q401aIntendedDosing_Elr_OtherValue: "",
    Q401aIntendedDosingEmpty_Elr: false,
    Q401aIntendedDosingRangeInvalid_Elr: false,

    Q401aPrimaryProf_Elr: false,
    Q401aSecProf_Elr: false,
    Q401aOtherProf_Elr: false,
    Q401aUnknownProf_Elr: null,
    Q401aReasonIvigEmptyHard_Elr: false,

    Q401aDosingType_Elr: null,
    Q401aDosingType_ElrEmptyHard: false,

    Q401aDosage_Elr: null,
    Q401aDosage_Elr_GValue: "",
    Q401aDosage_Elr_MgValue: "",
    Q401aDosageFrequencyEmpty_Elr: false,
    Q401aDosageRangeInvalid_Elr: false,

    Q401aWeight_Elr: null,
    Q401aWeight_Value_KgElr: "",
    Q401aWeight_Value_LbsElr: "",
    Q401aWeightEmpty_Elr: false,
    Q401aWeightRangeInvalid_Elr: false,

    Q401aWeight_Asses_Elr: null,
    Q401aWeight_Asses_Elr_dt: "",
    Q401aWeight_AssesEmptyHard_Elr: false,
    Q401aWeight_AssesDtEmptyHard_Elr: false,
    Q401aWeight_AssesDtInvalid_Elr: false,
    Q401aWeight_Asses_dt_before_eof_Elr: false,
  };

  const Q401aLinvoRow = {
    tectalivigreceivedbridgeid: 0,
    cohorttreatmentid: isLinvoveySelected
      ? RRMMCohortTreatment.Linvoseltamab
      : 0, //GetLocalStorageData("talveyID") || 0

    Q401ainit_dt_Rd_Lin: null,
    Q401ainit_Lin_dt: "",
    Q401aInit_Lin_RdHard: false,
    Q401aInit_Lin_dtHard: false,
    Q401aInvalidQ401ainit_Lin_dt: false,
    Q401aInit_Lin_dt_before_eof: false,

    Q401aIntendedDosing_Lin: null,
    Q401aIntendedDosing_Lin_WeekValue: "",
    Q401aIntendedDosing_Lin_MonthValue: "",
    Q401aIntendedDosing_Lin_OnceWeekValue: "",
    Q401aIntendedDosing_Lin_OnceMonthValue: "",
    Q401aIntendedDosing_Lin_OtherValue: "",
    Q401aIntendedDosingEmpty_Lin: false,
    Q401aIntendedDosingRangeInvalid_Lin: false,

    Q401aPrimaryProf_Lin: false,
    Q401aSecProf_Lin: false,
    Q401aOtherProf_Lin: false,
    Q401aUnknownProf_Lin: null,
    Q401aReasonIvigEmptyHard_Lin: false,

    Q401aDosingType_Lin: null,
    Q401aDosingType_LinEmptyHard: false,

    Q401aDosage_Lin: null,
    Q401aDosage_Lin_GValue: "",
    Q401aDosage_Lin_MgValue: "",
    Q401aDosageFrequencyEmpty_Lin: false,
    Q401aDosageRangeInvalid_Lin: false,

    Q401aWeight_Lin: null,
    Q401aWeight_Value_KgLin: "",
    Q401aWeight_Value_LbsLin: "",
    Q401aWeightEmpty_Lin: false,
    Q401aWeightRangeInvalid_Lin: false,

    Q401aWeight_Asses_Lin: null,
    Q401aWeight_Asses_Lin_dt: "",
    Q401aWeight_AssesEmptyHard_Lin: false,
    Q401aWeight_AssesDtEmptyHard_Lin: false,
    Q401aWeight_AssesDtInvalid_Lin: false,
    Q401aWeight_Asses_dt_before_eof_Lin: false,
  };

  const Q402aElrRow = {
    tectalivigiggreceivedbridgeid: 0,
    cohorttreatmentid: isElrexvayliSelected ? RRMMCohortTreatment.ELREXFIO : 0, //GetLocalStorageData("techvayliID") || 0

    Q402aIgg_assessment_Elr: null,
    Q402aIgg_assessment_Elr_dt: "",
    Q402aIgg_assessment_Elr_RdHard: false,
    Q402aIgg_assessment_Elr_dtHard: false,
    Q402aIgg_assessment_Elr_dtInvalid: false,
    Q402aIgg_assessment_Elr_dt_before_eof: false,
    Q402aIgg_level_Elr: null,
    Q402aIgg_level_Elr_MgValue: "",
    Q402aIgg_level_Elr_GValue: "",
    Q402aIgg_level_Elr_Empty: false,
    Q402aIgg_level_Elr_RangeInvalid: false,
  };

  const Q402aLinvoRow = {
    tectalivigiggreceivedbridgeid: 0,
    cohorttreatmentid: isLinvoveySelected
      ? RRMMCohortTreatment.Linvoseltamab
      : 0,

    Q402aIgg_assessment_Lin: null,
    Q402aIgg_assessment_Lin_dt: "",
    Q402aIgg_assessment_Lin_RdHard: false,
    Q402aIgg_assessment_Lin_dtHard: false,
    Q402aIgg_assessment_Lin_dtInvalid: false,
    Q402aIgg_assessment_Lin_dt_before_eof: false,
    Q402aIgg_level_Lin: null,
    Q402aIgg_level_Lin_MgValue: "",
    Q402aIgg_level_Lin_GValue: "",
    Q402aIgg_level_Lin_Empty: false,
    Q402aIgg_level_Lin_RangeInvalid: false,
  };

  const Q403aElrRow = {
    tectalivigiggneverreceivedbridgeid: 0,
    cohorttreatmentid: isElrexvayliSelected ? RRMMCohortTreatment.ELREXFIO : 0, //GetLocalStorageData("elrexvayliID") || 0

    Q403aIgg_assessment_Elr: null,
    Q403aIgg_assessment_Elr_dt: "",
    Q403aIgg_assessment_Elr_RdHard: false,
    Q403aIgg_assessment_Elr_dtHard: false,
    Q403aIgg_assessment_Elr_dtInvalid: false,
    Q403aIgg_assessment_Elr_dt_before_eof: false,
    Q403aIgg_level_Elr: null,
    Q403aIgg_level_Elr_MgValue: "",
    Q403aIgg_level_Elr_GValue: "",
    Q403aIgg_level_Elr_Empty: false,
    Q403aIgg_level_Elr_RangeInvalid: false,
  };

  const Q403aLinvoRow = {
    tectalivigiggneverreceivedbridgeid: 0,
    cohorttreatmentid: isLinvoveySelected
      ? RRMMCohortTreatment.Linvoseltamab
      : 0,

    Q403aIgg_assessment_Lin: null,
    Q403aIgg_assessment_Lin_dt: "",
    Q403aIgg_assessment_Lin_RdHard: false,
    Q403aIgg_assessment_Lin_dtHard: false,
    Q403aIgg_assessment_Lin_dtInvalid: false,
    Q403aIgg_assessment_Lin_dt_before_eof: false,
    Q403aIgg_level_Lin: null,
    Q403aIgg_level_Lin_MgValue: "",
    Q403aIgg_level_Lin_GValue: "",
    Q403aIgg_level_Lin_Empty: false,
    Q403aIgg_level_Lin_RangeInvalid: false,
  };

  // Q40.1.C - IVIG dose frequency change (eCRF New Elements C)
  const Q401cElrRow = {
    ivigdosefreqchangebridgeid: 0,
    cohorttreatmentid: isElrexvayliSelected ? RRMMCohortTreatment.ELREXFIO : 0,

    // Column B - Type of dose frequency change
    Q401cChangeType_Elr: null, // 1=No changes occurred, 2=Frequency increase, 3=Frequency decrease, 99=Unknown if any changes occurred
    Q401cChangeTypeEmptyHard_Elr: false,

    // Column C - Dose frequency before change
    Q401cFreqBeforeRd_Elr: null, // 1=times per week, 2=once every N weeks, 3=Other, 99=Unknown
    Q401cFreqBeforeTimesTxt_Elr: "",
    Q401cFreqBeforeWeeksTxt_Elr: "",
    Q401cFreqBeforeOtherTxt_Elr: "",
    Q401cFreqBeforeEmptyHard_Elr: false,
    Q401cFreqBeforeTxtEmptyHard_Elr: false,
    Q401cFreqBeforeRangeInvalid_Elr: false,
    Q401cFreqBeforeAutoPopulated_Elr: false,

    // Column D - Dose frequency after change
    Q401cFreqAfterRd_Elr: null,
    Q401cFreqAfterTimesTxt_Elr: "",
    Q401cFreqAfterWeeksTxt_Elr: "",
    Q401cFreqAfterOtherTxt_Elr: "",
    Q401cFreqAfterEmptyHard_Elr: false,
    Q401cFreqAfterTxtEmptyHard_Elr: false,
    Q401cFreqAfterRangeInvalid_Elr: false,

    // Column E - Date of change
    Q401cDateChangeRd_Elr: null, // 1=Date provided, 99=Unknown
    Q401cDateChange_Elr: "",
    Q401cDateChangeRdHard_Elr: false,
    Q401cDateChangeDtHard_Elr: false,
    Q401cDateChangeInvalid_Elr: false,
    Q401cDateChangeOutOfBound_Elr: false,

    // Conflict / warning flags
    Q401cFrequencyIncreaseConflict_Elr: false,
    Q401cFrequencyDecreaseConflict_Elr: false,
    Q401cWarningUnk_Elr: false,
  };

  const Q401cLinvoRow = {
    ivigdosefreqchangebridgeid: 0,
    cohorttreatmentid: isLinvoveySelected
      ? RRMMCohortTreatment.Linvoseltamab
      : 0,

    // Column B - Type of dose frequency change
    Q401cChangeType_Lin: null,
    Q401cChangeTypeEmptyHard_Lin: false,

    // Column C - Dose frequency before change
    Q401cFreqBeforeRd_Lin: null,
    Q401cFreqBeforeTimesTxt_Lin: "",
    Q401cFreqBeforeWeeksTxt_Lin: "",
    Q401cFreqBeforeOtherTxt_Lin: "",
    Q401cFreqBeforeEmptyHard_Lin: false,
    Q401cFreqBeforeTxtEmptyHard_Lin: false,
    Q401cFreqBeforeRangeInvalid_Lin: false,
    Q401cFreqBeforeAutoPopulated_Lin: false,

    // Column D - Dose frequency after change
    Q401cFreqAfterRd_Lin: null,
    Q401cFreqAfterTimesTxt_Lin: "",
    Q401cFreqAfterWeeksTxt_Lin: "",
    Q401cFreqAfterOtherTxt_Lin: "",
    Q401cFreqAfterEmptyHard_Lin: false,
    Q401cFreqAfterTxtEmptyHard_Lin: false,
    Q401cFreqAfterRangeInvalid_Lin: false,

    // Column E - Date of change
    Q401cDateChangeRd_Lin: null,
    Q401cDateChange_Lin: "",
    Q401cDateChangeRdHard_Lin: false,
    Q401cDateChangeDtHard_Lin: false,
    Q401cDateChangeInvalid_Lin: false,
    Q401cDateChangeOutOfBound_Lin: false,

    // Conflict / warning flags
    Q401cFrequencyIncreaseConflict_Lin: false,
    Q401cFrequencyDecreaseConflict_Lin: false,
    Q401cWarningUnk_Lin: false,
  };

  const stateResolver = {
    "381A": {
      [RRMMCohortTreatment.ELREXFIO]: [Q381AElrexDetails, setQ381AElrexDetails],
      [RRMMCohortTreatment.Linvoseltamab]: [
        Q381ALinvoDetails,
        setQ381ALinvoDetails,
      ],
    },
    "382A": {
      [RRMMCohortTreatment.ELREXFIO]: [Q382AElrexDetails, setQ382AElrexDetails],
      [RRMMCohortTreatment.Linvoseltamab]: [
        Q382ALinvoDetails,
        setQ382ALinvoDetails,
      ],
    },
  };

  const [Q37ElrexDetails, setQ37ElrexDetails] = useState([Q37ElrexRow]);
  const [Q37LinvoDetails, setQ37LinvoDetails] = useState([Q37LinvoRow]);

  const [Q38ElrexDetails, setQ38ElrexDetails] = useState([Q38ElrexRow]);
  const [Q38LinvoDetails, setQ38LinvoDetails] = useState([Q38LinvoRow]);

  const [elrexvayliDetails, setElrexvayliDetails] = useState([ElrexvayliRow]);
  const [linvoveyDetails, setLinvoveyDetails] = useState([LinvoveyRow]);

  // AUTO-009 -------------------------------------------------------------
  // Q38.1.A column [D] ("Pattern before change") is auto-populated, for the
  // first dose frequency / dose strength change row only, from the Q36 row
  // reporting the 1st subsequent treatment dose. Auto-populated values are
  // read-only; every later row stays user-editable.
  //
  // Mirrors the implementation in TTCharacteristics.jsx. The dosing-phase code
  // marking the 1st subsequent treatment dose differs per drug (Linvoseltamab
  // carries an extra step-up dose), and this file names column [D]'s frequency
  // text boxes differently from the TT file, so both are passed in by caller.
  const GetQ36FirstSubsequentDoseRow = (
    q36Rows,
    dosingPhaseField,
    subsequentDoseCode,
    dateField,
  ) => {
    const matching = (q36Rows ?? []).filter(
      (r) => r?.[dosingPhaseField] === subsequentDoseCode,
    );
    if (matching.length === 0) return null;

    // Seed from the earliest administration; rows without a usable date keep
    // their entered order behind those that have one (sort is stable).
    const sorted = [...matching].sort((a, b) => {
      const aDate = a?.[dateField];
      const bDate = b?.[dateField];
      const aValid = IsValidDateValue(aDate);
      const bValid = IsValidDateValue(bDate);
      if (aValid && bValid) return new Date(aDate) - new Date(bDate);
      if (aValid) return -1;
      if (bValid) return 1;
      return 0;
    });
    return sorted[0];
  };

  const BuildQ38PatternBeforeFromQ36 = (q36Row, prefix, fieldNames) => {
    if (!q36Row) return null;

    const freqRadio = q36Row[`${prefix}_dose_freq_rbl`] ?? null;
    const strengthRadio = q36Row[`${prefix}_dose_strength_rbl`] ?? null;

    return {
      patterBeforeChangeDoseFrequency: freqRadio,
      [fieldNames.perWeek]:
        freqRadio === 1 ? (q36Row[`${prefix}_dose_freq_week`] ?? "") : "",
      [fieldNames.everyWeek]:
        freqRadio === 2 ? (q36Row[`${prefix}_dose_freq_onceweek`] ?? "") : "",

      patterBeforeChangeDoseStrength: strengthRadio,
      patterBeforeChangeDoseStrengthMg:
        strengthRadio === 1 ? (q36Row[`${prefix}_dose_strength_mg`] ?? "") : "",
      patterBeforeChangeDoseStrengthMgKg:
        strengthRadio === 2
          ? (q36Row[`${prefix}_dose_strength_mg_kg`] ?? "")
          : "",
      patterBeforeChangeDoseStrengthMgM2:
        strengthRadio === 3
          ? (q36Row[`${prefix}_dose_strength_mgm`] ?? "")
          : "",
    };
  };

  const ApplyQ38PatternBeforeAutoPopulate = (
    q381Rows,
    q36Rows,
    { dosingPhaseField, subsequentDoseCode, dateField, prefix, fieldNames },
  ) => {
    if (!q381Rows || q381Rows.length === 0) return q381Rows;

    const q36Row = GetQ36FirstSubsequentDoseRow(
      q36Rows,
      dosingPhaseField,
      subsequentDoseCode,
      dateField,
    );
    const pattern = BuildQ38PatternBeforeFromQ36(q36Row, prefix, fieldNames);

    // Clear any previous auto-populate before re-deriving, so a row that is no
    // longer first stops being locked.
    const rows = q381Rows.map((r) => ({
      ...r,
      patternBeforeAutoPopulated: false,
    }));
    if (!pattern) return rows;

    const firstIndexByChangeType = (changeType) => {
      const candidates = rows
        .map((r, index) => ({ r, index }))
        .filter(({ r }) => r.changeType === changeType);
      if (candidates.length === 0) return -1;

      const sorted = [...candidates].sort((a, b) => {
        const aValid =
          a.r.dateChangeRd === 1 && IsValidDateValue(a.r.dateChange);
        const bValid =
          b.r.dateChangeRd === 1 && IsValidDateValue(b.r.dateChange);
        return aValid && bValid
          ? new Date(a.r.dateChange) - new Date(b.r.dateChange)
          : 0;
      });
      return sorted[0].index;
    };

    // Seed a row's Column D from Q36 only while it is genuinely blank. Once
    // it carries a value - whether from a prior auto-populate or the user
    // typing/selecting directly - leave it alone on later runs of this
    // effect (e.g. triggered by an unrelated Q36 state update during
    // Validate) so that value isn't wiped out.
    const freqIndex = firstIndexByChangeType(2);
    if (freqIndex >= 0) {
      const hasValue = q381Rows[freqIndex]?.patterBeforeChangeDoseFrequency != null;
      rows[freqIndex] = hasValue
        ? { ...rows[freqIndex], patternBeforeAutoPopulated: true }
        : {
            ...rows[freqIndex],
            patterBeforeChangeDoseFrequency:
              pattern.patterBeforeChangeDoseFrequency,
            [fieldNames.perWeek]: pattern[fieldNames.perWeek],
            [fieldNames.everyWeek]: pattern[fieldNames.everyWeek],
            patternBeforeAutoPopulated: true,
            patternBeforeFrequencyEmpty: false,
            patternBeforeFrequencyRangeInvalid: false,
          };
    }

    const strengthIndex = firstIndexByChangeType(3);
    if (strengthIndex >= 0) {
      const hasValue =
        q381Rows[strengthIndex]?.patterBeforeChangeDoseStrength != null;
      rows[strengthIndex] = hasValue
        ? { ...rows[strengthIndex], patternBeforeAutoPopulated: true }
        : {
            ...rows[strengthIndex],
            patterBeforeChangeDoseStrength:
              pattern.patterBeforeChangeDoseStrength,
            patterBeforeChangeDoseStrengthMg:
              pattern.patterBeforeChangeDoseStrengthMg,
            patterBeforeChangeDoseStrengthMgKg:
              pattern.patterBeforeChangeDoseStrengthMgKg,
            patterBeforeChangeDoseStrengthMgM2:
              pattern.patterBeforeChangeDoseStrengthMgM2,
            patternBeforeAutoPopulated: true,
            patternBeforeStrengthEmpty: false,
            patternBeforeStrengthRangeInvalid: false,
          };
    }

    return rows;
  };

  // AUTO-009 hard validation - see TTCharacteristics.jsx for the reasoning.
  const ValidateQ38PatternBeforeMatchesQ36 = (
    firstFreqRow,
    firstStrengthRow,
    q36Rows,
    { dosingPhaseField, subsequentDoseCode, prefix, fieldNames },
    getFrequencyValue,
    getStrengthValue,
  ) => {
    const q36Matches = (q36Rows ?? []).filter(
      (r) => r?.[dosingPhaseField] === subsequentDoseCode,
    );
    if (q36Matches.length < 2) return false;

    if (firstFreqRow) {
      const before = getFrequencyValue(firstFreqRow, "Before");
      if (before) {
        const anyMatch = q36Matches.some((q36Row) => {
          const unit = q36Row[`${prefix}_dose_freq_rbl`];
          if (unit !== before.unit) return false;
          const txt =
            unit === 1
              ? q36Row[`${prefix}_dose_freq_week`]
              : q36Row[`${prefix}_dose_freq_onceweek`];
          return String(txt ?? "") === before.value;
        });
        if (!anyMatch) return true;
      }
    }

    if (firstStrengthRow) {
      const before = getStrengthValue(firstStrengthRow, "Before");
      if (before) {
        const anyMatch = q36Matches.some((q36Row) => {
          const unit = q36Row[`${prefix}_dose_strength_rbl`];
          if (unit !== before.unit) return false;
          const txt =
            unit === 1
              ? q36Row[`${prefix}_dose_strength_mg`]
              : unit === 2
                ? q36Row[`${prefix}_dose_strength_mg_kg`]
                : q36Row[`${prefix}_dose_strength_mgm`];
          return String(txt ?? "") === before.value;
        });
        if (!anyMatch) return true;
      }
    }

    return false;
  };

  const Q381A_ELREX_AUTO_CONFIG = {
    dosingPhaseField: "dosing_phase_elr",
    subsequentDoseCode: 4,
    dateField: "dose_administration_elr_dt",
    prefix: "elr",
    fieldNames: {
      perWeek: "patterBeforeChangeDoseFrequencyTimesPerWeek",
      everyWeek: "patterBeforeChangeDoseFrequencyWeek",
    },
  };

  const Q381A_LINVO_AUTO_CONFIG = {
    dosingPhaseField: "dosing_phase_linvo",
    subsequentDoseCode: 5,
    dateField: "dose_administration_linvo_dt",
    prefix: "linvo",
    fieldNames: {
      perWeek: "patterBeforeChangeDoseFrequencyTimesPerWeek",
      everyWeek: "patterBeforeChangeDoseFrequencyWeek",
    },
  };

  // Q36.1 column [B] (date of admission) upper bound - the date of the 1st
  // Subsequent Treatment Dose reported in Q36 (same row AUTO-009 uses to
  // seed Q38.1.A column [D]). Falls back to null when Q36 hasn't reported
  // that dose yet, so callers can fall back to the existing RRMM/EOF bound.
  const GetQ36FirstSubsequentDoseDate = (q36Rows, config) => {
    const row = GetQ36FirstSubsequentDoseRow(
      q36Rows,
      config.dosingPhaseField,
      config.subsequentDoseCode,
      config.dateField,
    );
    const date = row?.[config.dateField];
    return IsValidDateValue(date) ? date : null;
  };

  // Q36.1 column [B] lower bound - the question text itself says admission
  // dates must fall "between the date of Step-up Dose 1 and the date of the
  // 1st Subsequent Treatment Dose reported in Q36", so the lower bound is
  // Step-up Dose 1's own date (dosing phase code 1, same for every drug),
  // not the Bispecifics/CAR-T initiation date. Falls back to the initiation
  // date if Step-up Dose 1 hasn't been entered yet.
  const GetQ36StepUpDose1Date = (q36Rows, config) => {
    const row = (q36Rows ?? []).find((r) => r?.[config.dosingPhaseField] === 1);
    const date = row?.[config.dateField];
    return IsValidDateValue(date) ? date : null;
  };

  // Q36.1 column [B] full chain -------------------------------------------
  // Bispecifics/CAR-T initiation date <= [B] <= 1st subsequent treatment
  // dose in Q36 <= last administration date in column [D] of Q26 (if known)
  // <= end date of LOT in Q26.2 <= start date of subsequent LOT if reported
  // in Q26/Q26.1/Q26.2/Q27/Q31 <= end of follow-up.
  //
  // The Q26/Q26.2/next-LOT-start portion is the same current-LOT boundary
  // Q36's own ValidateForDateCondition already resolves; this re-derives it
  // for whichever LOT is current for the cohort so it can be re-checked
  // directly against [B] rather than relying only on it holding transitively
  // through the 1st subsequent dose.
  const GetCurrentLotNumber = (cohortType) => {
    // this section is one LOT cohort (Line_CohortID): its LOT is the current LOT
    if (lot.lineOfTherapy != null && lot.cohortTreatmentId === cohortType)
      return Number(lot.lineOfTherapy);
    const lots = (bispecificQ26Data ?? [])
      .filter(
        (x) =>
          x?.cohortTreatmentId === cohortType && !isNaN(Number(x.lotNumber)),
      )
      .map((x) => Number(x.lotNumber));
    return lots.length > 0 ? Math.max(...lots) : null;
  };

  const GetQ361ChainBoundaryDates = (cohortType) => {
    const currentLot = GetCurrentLotNumber(cohortType);

    const q26Row = (bispecificQ26Data ?? []).find(
      (x) =>
        x?.cohortTreatmentId === cohortType &&
        Number(x.lotNumber) === currentLot,
    );
    const q26LastAdminDt =
      q26Row && IsValidDateValue(q26Row.lastAdministrationDt)
        ? q26Row.lastAdministrationDt
        : null;

    // bispecificQ331AData is Q26.2 (Noncohortbispecifictreatmenthighestlotbridge):
    // lastAdministrationDt here holds Q26.2's LOT end date.
    const q262Row = (bispecificQ331AData ?? []).find(
      (x) =>
        x?.cohortTreatmentId === cohortType &&
        Number(x.lotNumber) === currentLot,
    );
    const q262EndDate =
      q262Row && IsValidDateValue(q262Row.lastAdministrationDt)
        ? q262Row.lastAdministrationDt
        : null;

    // Already the cascade across Q26/Q26.1/Q26.2/Q27/Q31 for the next LOT's
    // start date (CommonService.ResolveQ40SubsequentLotStartDate).
    const nextLotStartDate =
      cohortNextLotDatesList?.get(cohortType)?.get(QuestionType.Q40) ?? null;

    return { q26LastAdminDt, q262EndDate, nextLotStartDate };
  };

  // Filters out unknown dates and checks the rest are in non-decreasing
  // order - a generalisation of CheckDateValidationQ36 to an arbitrary
  // number of chain points.
  const IsQ361DateChainValid = (dates) => {
    const valid = (dates ?? []).filter((d) => d && IsValidDateValue(d));
    for (let i = 1; i < valid.length; i++) {
      if (!IsLessOrEqual(valid[i - 1], valid[i])) return false;
    }
    return true;
  };

  const [Q401aElrDetails, setQ401aElrDetails] = useState([Q401aElrRow]);
  const [Q401aLinvoDetails, setQ401aLinvoDetails] = useState([Q401aLinvoRow]);

  const [Q402aElrDetails, setQ402aElrDetails] = useState([Q402aElrRow]);
  const [Q402aLinvoDetails, setQ402aLinvoDetails] = useState([Q402aLinvoRow]);

  const [Q403aElrDetails, setQ403aElrDetails] = useState([Q403aElrRow]);
  const [Q403aLinvoDetails, setQ403aLinvoDetails] = useState([Q403aLinvoRow]);

  const [Q401cElrDetails, setQ401cElrDetails] = useState([Q401cElrRow]);
  const [Q401cLinvoDetails, setQ401cLinvoDetails] = useState([Q401cLinvoRow]);
  const [q401cElr_WarningHard, setQ401cElr_WarningHard] = useState(false);
  const [q401cLinvo_WarningHard, setQ401cLinvo_WarningHard] = useState(false);

  // Q40.2.C - IgG level at target dates/windows (eCRF New Elements C).
  // rowType: 1 = baseline (closest to start of bispecific treatment),
  // 2 = highest IgG level after treatment initiation,
  // 3 = periodic (auto-generated every 91 days; not addable/deletable)
  const Q402cElrRow = {
    igglevelinformationid: 0,
    rowType: 1,
    Q402cAssessment_Elr: null, // 1 = No IgG assessment available, 2 = date entered, 99 = Date unknown
    Q402cAssessment_Elr_dt: "",
    Q402cAssessment_Elr_RdHard: false,
    Q402cAssessment_Elr_dtHard: false,
    Q402cAssessment_Elr_dtInvalid: false,
    Q402cAssessment_Elr_dtOutOfRange: false,
    Q402cRow1NotEarliestHard: false, // row 0 (baseline) only
    Q402cLevelBucket_Elr: null, // rows 1 & 3: 1 = <400 mg/dL, 2 = 400-500 mg/dL, 3 = >500 mg/dL, 99 = Unknown
    Q402cLevelBucket_Elr_Empty: false,
    Q402cLevelValue_Elr: null, // row 2 only: 1 = mg/dL, 2 = g/L, 99 = Unknown
    Q402cLevelValue_Elr_MgValue: "",
    Q402cLevelValue_Elr_GValue: "",
    Q402cLevelValue_Elr_Empty: false,
    Q402cLevelValue_Elr_RangeInvalid: false,
  };

  const Q402cLinvoRow = {
    igglevelinformationid: 0,
    rowType: 1,
    Q402cAssessment_Lin: null,
    Q402cAssessment_Lin_dt: "",
    Q402cAssessment_Lin_RdHard: false,
    Q402cAssessment_Lin_dtHard: false,
    Q402cAssessment_Lin_dtInvalid: false,
    Q402cAssessment_Lin_dtOutOfRange: false,
    Q402cRow1NotEarliestHard: false,
    Q402cLevelBucket_Lin: null,
    Q402cLevelBucket_Lin_Empty: false,
    Q402cLevelValue_Lin: null,
    Q402cLevelValue_Lin_MgValue: "",
    Q402cLevelValue_Lin_GValue: "",
    Q402cLevelValue_Lin_Empty: false,
    Q402cLevelValue_Lin_RangeInvalid: false,
  };

  const [Q402cElrDetails, setQ402cElrDetails] = useState([
    { ...Q402cElrRow, rowType: 1 },
    { ...Q402cElrRow, rowType: 2 },
  ]);
  const [Q402cLinvoDetails, setQ402cLinvoDetails] = useState([
    { ...Q402cLinvoRow, rowType: 1 },
    { ...Q402cLinvoRow, rowType: 2 },
  ]);
  const [q402cElrDatesWarning, setQ402cElrDatesWarning] = useState(false);
  const [q402cLinvoDatesWarning, setQ402cLinvoDatesWarning] = useState(false);
  const [q402cElrThresholdWarning, setQ402cElrThresholdWarning] =
    useState(false);
  const [q402cLinvoThresholdWarning, setQ402cLinvoThresholdWarning] =
    useState(false);

  // Number of periodic ("Row 3+") assessments to auto-generate, based on the
  // Bispecifics/CAR-T index date and the Q40 period end date
  // (MIN(start of subsequent LOT, end of follow-up)) already computed
  // server-side and looked up via q40PeriodEndDateList.
  const q402cElrPeriodicRowCount = CalculateQ402CPeriodicRowCount(
    elrexIndexDate,
    q40PeriodEndDateList.get(RRMMCohortTreatment.ELREXFIO) || endOfFollowUpDate,
  );
  const q402cLinvoPeriodicRowCount = CalculateQ402CPeriodicRowCount(
    linvoIndexDate,
    q40PeriodEndDateList.get(RRMMCohortTreatment.Linvoseltamab) || endOfFollowUpDate,
  );

  useEffect(() => {
    if (!isNewEcrfC || !isElrexvayliSelected) return;
    setQ402cElrDetails((prev) => {
      const baseline = { ...(prev[0] || Q402cElrRow), rowType: 1 };
      const highest = { ...(prev[1] || Q402cElrRow), rowType: 2 };
      const existingPeriodic = prev.slice(2);
      const periodic = [];
      for (let i = 0; i < q402cElrPeriodicRowCount; i++) {
        periodic.push({
          ...(existingPeriodic[i] || Q402cElrRow),
          rowType: 3,
        });
      }
      return [baseline, highest, ...periodic];
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q402cElrPeriodicRowCount, isNewEcrfC, isElrexvayliSelected]);

  useEffect(() => {
    if (!isNewEcrfC || !isLinvoveySelected) return;
    setQ402cLinvoDetails((prev) => {
      const baseline = { ...(prev[0] || Q402cLinvoRow), rowType: 1 };
      const highest = { ...(prev[1] || Q402cLinvoRow), rowType: 2 };
      const existingPeriodic = prev.slice(2);
      const periodic = [];
      for (let i = 0; i < q402cLinvoPeriodicRowCount; i++) {
        periodic.push({
          ...(existingPeriodic[i] || Q402cLinvoRow),
          rowType: 3,
        });
      }
      return [baseline, highest, ...periodic];
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q402cLinvoPeriodicRowCount, isNewEcrfC, isLinvoveySelected]);
  const [q381AElrex_WarningHard, setQ381AElrex_WarningHard] = useState(false);
  const [q381ALinvo_WarningHard, setQ381ALinvo_WarningHard] = useState(false);
  // AUTO-009 - column [D] no longer matches any of Q36's 1st subsequent
  // treatment dose rows (only reachable when Q36 reports more than one).
  const [q381AElrexQ36Mismatch, setQ381AElrexQ36Mismatch] = useState(false);
  const [q381ALinvoQ36Mismatch, setQ381ALinvoQ36Mismatch] = useState(false);

  // const [hardErrList, setHardErrList] = useState(InitialHardErrList);
  //36.1
  const [elrHospDetails, setElrHospDetails] = useState([HospitalizationRow]);
  const [linvoHospDetails, setLinvoHospDetails] = useState([
    HospitalizationRow,
  ]);

  const [dt_elr_2L_3L_date_conditions, setDt_elr_2L_3L_date_conditions] =
    useState(false);
  const [dt_lino_2L_3L_date_conditions, setDt_lino_2L_3L_date_conditions] =
    useState(false);

  //39.1.A
  const [q391AElrTocilzumabList, setQ391AElrTocilzumabList] = useState([
    q391AElrRow,
  ]);
  const [q391ALinvoTocilzumabList, setQ391ALinvoTocilzumabList] = useState([
    q391ALinvoRow,
  ]);
  const [
    q391AElrAdministrationDateOutOfRange,
    setQ391AElrAdministrationDateOutOfRange,
  ] = useState(false);

  const [
    q391ALinvoAdministrationDateOutOfRange,
    setQ391ALinvoAdministrationDateOutOfRange,
  ] = useState(false);

  // ADD/DELETE ELREXFIO ROWS
  const DeleteElrexvayliRow = (id) => {
    const newArray = [...elrexvayliDetails];
    newArray.splice(id, 1);
    setElrexvayliDetails(newArray);
  };
  const AddElrexvayliRow = () => {
    setElrexvayliDetails((prev) => {
      if (prev.length >= 550) {
        return prev;
      }

      return [
        ...prev,
        {
          ...ElrexvayliRow,
          isManuallyAdded: true,
        },
      ];
    });
  };
  //36.1 Tec
  const DeleteElrHospRow = (id) => {
    const newArray = [...elrHospDetails];
    newArray.splice(id, 1);
    setElrHospDetails(newArray);
  };
  const AddElrHospRow = () => {
    setElrHospDetails((prev) => [
      ...prev,
      {
        ...HospitalizationRow,
        cohorttreatmentid: isElrexvayliSelected
          ? RRMMCohortTreatment.ELREXFIO
          : 0, //GetLocalStorageData("elrexvayliID") || 0
      },
    ]);
  };

  // ADD/DELETE LINVOSELTAMAB ROWS
  const DeleteLinvoveyRow = (id) => {
    const newArray = [...linvoveyDetails];
    newArray.splice(id, 1);
    setLinvoveyDetails(newArray);
  };

  const AddLinvoveyRow = () => {
    setLinvoveyDetails((prev) => {
      if (prev.length >= 550) {
        return prev;
      }

      return [
        ...prev,
        {
          ...LinvoveyRow,
          isManuallyAdded: true,
        },
      ];
    });
  };

  //36.1 Linvo
  const DeleteLinvoHospRow = (id) => {
    const newArray = [...linvoHospDetails];
    newArray.splice(id, 1);
    setLinvoHospDetails(newArray);
  };
  const AddLinvoHospRow = () => {
    setLinvoHospDetails((prev) => [
      ...prev,
      {
        ...HospitalizationRow,
        cohorttreatmentid: isLinvoveySelected
          ? RRMMCohortTreatment.Linvoseltamab
          : 0, //GetLocalStorageData("linvoveyID") || 0
      },
    ]);
  };

  // Q38

  // ADD/DELETE ELREXFIO ROWS
  const DeleteQ38ElrexvayliRow = (id) => {
    const newArray = [...Q38ElrexDetails];
    newArray.splice(id, 1);
    setQ38ElrexDetails(newArray);
  };
  const AddQ38ElrexvayliRow = () => {
    setQ38ElrexDetails((prev) => [
      ...prev,
      {
        ...Q38ElrexRow,
      },
    ]);
  };

  // ADD/DELETE LINVOSELTAMAB ROWS
  const DeleteQ38LinvoveyRow = (id) => {
    const newArray = [...Q38LinvoDetails];
    newArray.splice(id, 1);
    setQ38LinvoDetails(newArray);
  };
  const AddQ38LinvoveyRow = () => {
    setQ38LinvoDetails((prev) => [
      ...prev,
      {
        ...Q38LinvoRow,
      },
    ]);
  };

  // ========================================
  // Q38.1.A ELREXFIO - ADD/DELETE ROWS
  // ========================================
  const DeleteQ381AElrexRow = (id) => {
    const newArray = [...Q381AElrexDetails];
    newArray.splice(id, 1);
    setQ381AElrexDetails(newArray);
  };

  // Q38.1.A ELREXFIO - ADD ROW
  const AddQ381AElrexRow = () => {
    setQ381AElrexDetails((prev) => {
      const templateReasonList =
        prev.length > 0 && prev[0].reasonList?.length > 0
          ? prev[0].reasonList.map((reason) => ({
              ...reason,
              isSelected: false,
              reasonText: "",
              aeSubReasons:
                reason.aeSubReasons?.map((ae) => ({
                  ...ae,
                  isSelected: false,
                })) || [],
            }))
          : [];

      return [
        ...prev,
        {
          ...Q381AElrexRow,
          cohorttreatmentid: isElrexvayliSelected
            ? RRMMCohortTreatment.ELREXFIO
            : 0,
          reasonList: templateReasonList,
          neurologicAEReasonList:
            prev.length > 0 && prev[0].neurologicAEReasonList
              ? resetNeurologicReasons(prev[0].neurologicAEReasonList)
              : [],
        },
      ];
    });
  };

  // Q38.1.A LINVOSELTAMAB - ADD ROW
  const AddQ381ALinvoRow = () => {
    setQ381ALinvoDetails((prev) => {
      const templateReasonList =
        prev.length > 0 && prev[0].reasonList?.length > 0
          ? prev[0].reasonList.map((reason) => ({
              ...reason,
              isSelected: false,
              reasonText: "",
              aeSubReasons:
                reason.aeSubReasons?.map((ae) => ({
                  ...ae,
                  isSelected: false,
                })) || [],
            }))
          : [];

      return [
        ...prev,
        {
          ...Q381ALinvoRow,
          cohorttreatmentid: isLinvoveySelected
            ? RRMMCohortTreatment.Linvoseltamab
            : 0,
          reasonList: templateReasonList,
          neurologicAEReasonList:
            prev.length > 0 && prev[0].neurologicAEReasonList
              ? resetNeurologicReasons(prev[0].neurologicAEReasonList)
              : [],
        },
      ];
    });
  };

  // ========================================
  // Q38.1.A LINVOSELTAMAB - ADD/DELETE ROWS
  // ========================================
  const DeleteQ381ALinvoRow = (id) => {
    const newArray = [...Q381ALinvoDetails];
    newArray.splice(id, 1);
    setQ381ALinvoDetails(newArray);
  };

  const DeleteQ391ARow = (id, cohortId) => {
    if (!id || !cohortId) return;
    if (cohortId === RRMMCohortTreatment.ELREXFIO) {
      const newArray = [...q391AElrTocilzumabList];
      newArray.splice(id, 1);
      setQ391AElrTocilzumabList(newArray);
    } else if (cohortId === RRMMCohortTreatment.Linvoseltamab) {
      const newArray = [...q391ALinvoTocilzumabList];
      newArray.splice(id, 1);
      setQ391ALinvoTocilzumabList(newArray);
    }
  };

  const AddQ391ARow = (cohortId) => {
    if (!cohortId) return;

    const baseRow =
      cohortId === RRMMCohortTreatment.ELREXFIO
        ? q391AElrRow
        : cohortId === RRMMCohortTreatment.Linvoseltamab
          ? q391ALinvoRow
          : null;

    if (!baseRow) return;

    const newRow = {
      ...baseRow,
      patientid: pid,
    };

    cohortId === RRMMCohortTreatment.ELREXFIO
      ? setQ391AElrTocilzumabList((prev) => [...prev, newRow])
      : setQ391ALinvoTocilzumabList((prev) => [...prev, newRow]);
  };

  const AddQ401aElrRow = () => {
    setQ401aElrDetails((prev) => [
      ...prev,
      {
        ...Q401aElrRow,
      },
    ]);
  };

  const AddQ401aLinvoRow = () => {
    setQ401aLinvoDetails((prev) => [
      ...prev,
      {
        ...Q401aLinvoRow,
      },
    ]);
  };

  const DeleteQ401aElrRow = (id) => {
    const newArray = [...Q401aElrDetails];
    newArray.splice(id, 1);
    setQ401aElrDetails(newArray);
  };

  const DeleteQ401aLinvoRow = (id) => {
    const newArray = [...Q401aLinvoDetails];
    newArray.splice(id, 1);
    setQ401aLinvoDetails(newArray);
  };

  // Q40.1.C
  const AddQ401cElrRow = () => {
    setQ401cElrDetails((prev) => [...prev, { ...Q401cElrRow }]);
  };

  const AddQ401cLinvoRow = () => {
    setQ401cLinvoDetails((prev) => [...prev, { ...Q401cLinvoRow }]);
  };

  const DeleteQ401cElrRow = (id) => {
    const newArray = [...Q401cElrDetails];
    newArray.splice(id, 1);
    setQ401cElrDetails(newArray);
  };

  const DeleteQ401cLinvoRow = (id) => {
    const newArray = [...Q401cLinvoDetails];
    newArray.splice(id, 1);
    setQ401cLinvoDetails(newArray);
  };

  const resetNeurologicReasons = (neuroList) =>
    neuroList?.map((x) => ({
      ...x,
      isSelected: false,
      reasonUnk: false,
      reasonText: "",
    })) ?? [];

  // Builds this LOT cohort's slice of the Save payload (the former SavePage body).
  // Every row is tagged with LineOfTherapy (Line_CohortID's LOT), like the Disease
  // History LOT rows, so the backend can key Q36-Q40.3.B rows by cohort + LOT.
  const BuildLotPayload = (validate) => {
    {
      {
        const ElrexfioData = elrexvayliDetails.map((test) => ({
          Elranatamablinvoseltamabdosagebridgeid: test.elrexlinvodosagebridgeid,
          Cohorttreatmentid: test.cohorttreatmentid,
          DoseAdministrationDate:
            test.dose_administration_elr_dt != "Invalid Date" &&
            test.dose_administration_elr_dt
              ? test.dose_administration_elr_dt
              : "",
          DoseAdministrationDateUnk:
            test.dose_administration_elr === 99 ? true : null,
          DoseAdminidatrationDateNotReceived:
            test.dose_administration_elr === 2 ? true : null,
          DosePhase: test.dosing_phase_elr,
          SiteOfCare: test.siteofcare_elr,
          DosingPhaseOthTxt: test.dosing_phase_oth_txt_elr,
          DoseStrengthmg: test.elr_dose_strength_mg,
          DoseStrengthmgkg: test.elr_dose_strength_mg_kg,
          DoseStrengthmgm: test.elr_dose_strength_mgm,
          DoseStrengthUnk: test.elr_dose_strength_rbl === 99 ? 99 : null,

          DoseFreqDosingPhase: test.elr_dose_freq_rbl,
          DoseFreqDosingWeeks:
            test.elr_dose_freq_rbl === 1
              ? test.elr_dose_freq_week
              : test.elr_dose_freq_rbl === 2
                ? test.elr_dose_freq_onceweek
                : "",

          InpatientAdmissionDate: test.dt_admission_elr_dt
            ? test.dt_admission_elr_dt
            : "",
          // InpatientAdmissionDateUnk:
          //   test.dt_admission_elr === 99 ? true : null,

          InpatientDischargeDate: test.dt_discharge_elr_dt
            ? test.dt_discharge_elr_dt
            : "",
          // InpatientDischargeUnk: test.dt_discharge_elr === 99 ? true : null,
          // PatientStillHospilinvoized: test.dt_discharge_elr,
        }));
        //36.1 Elran
        const ElrexfioHospitalizationData = elrHospDetails.map((test) => ({
          Teclistamatalquetamabhospitalizationbridgeid:
            test.elrexlinvohospitalizationbridgeid,

          Cohorttreatmentid: test.cohorttreatmentid,
          // Teclistamatalquetamabdosagebridgeid: test.elrexlinvodosagebridgeid,

          HospitalizationAdmissionDate:
            test.dt_admission_dt && test.dt_admission_dt != "Invalid Date"
              ? test.dt_admission_dt
              : "",
          HospitalizationAdmissionDateUnk: test.dt_admission === 99,
          NoHospitalizationDosage: test.dt_admission === 2,
          HospitalizationDischargeDate:
            test.dt_discharge_dt && test.dt_discharge_dt != "Invalid Date"
              ? test.dt_discharge_dt
              : "",
          HospitalizationDischargeUnk: test.dt_discharge === 99,
          PatientStillHospitalized: test.dt_discharge === 2,
          HospitalizationReasonAdministration: test.hospReason1,
          HospitalizationReasonMonitorAe: test.hospReason2,
          HospitalizationReasonCrs: test.hospReason3,
          HospitalizationReasonIcans: test.hospReason4,
          HospitalizationReasonOtherae: test.hospReason5,
          HospitalizationReasonOtnernonae: test.hospReason6,
          HospitalizationReasonAdministrationDaratumumab: test.hospReason7,
          HospitalizationReasonUnknown: test.hospReasonUnk,
          HospitalizationReasonOthernonaeTxt: test.hosp_reason_oth_txt,
        }));
        const LinvoseltamabData = linvoveyDetails.map((test) => ({
          Teclistamatalquetamabdosagebridgeid:
            test.elranatamablinvoseltamabdosagebridgeid,
          Cohorttreatmentid: test.cohorttreatmentid,

          DoseAdministrationDate:
            test.dose_administration_linvo_dt &&
            test.dose_administration_linvo_dt != "Invalid Date"
              ? test.dose_administration_linvo_dt
              : "",
          DoseAdministrationDateUnk:
            test.dose_administration_linvo === 99 ? true : null,
          DoseAdminidatrationDateNotReceived:
            test.dose_administration_linvo === 2 ? true : null,
          DosePhase: test.dosing_phase_linvo,
          SiteOfCare: test.siteofcare_linvo,
          DosingPhaseOthTxt: test.dosing_phase_oth_txt_linvo,
          DoseStrengthmg: test.linvo_dose_strength_mg,
          DoseStrengthmgkg: test.linvo_dose_strength_mg_kg,
          DoseStrengthmgm: test.linvo_dose_strength_mgm,
          DoseStrengthUnk: test.linvo_dose_strength_rbl === 99 ? 99 : null,
          InpatientAdmissionDate: test.dt_admission_linvo_dt
            ? test.dt_admission_linvo_dt
            : "",

          DoseFreqDosingPhase: test.linvo_dose_freq_rbl,
          DoseFreqDosingWeeks:
            test.linvo_dose_freq_rbl === 1
              ? test.linvo_dose_freq_week
              : test.linvo_dose_freq_rbl === 2
                ? test.linvo_dose_freq_onceweek
                : "",

          InpatientDischargeDate: test.dt_discharge_linvo_dt
            ? test.dt_discharge_linvo_dt
            : "",
        }));
        //36.1 Linvo
        const LinvoseltamabHospitalizationData = linvoHospDetails.map(
          (test) => ({
            Teclistamatalquetamabhospitalizationbridgeid:
              test.elrexlinvohospitalizationbridgeid,
            Cohorttreatmentid: test.cohorttreatmentid,

            HospitalizationAdmissionDate:
              test.dt_admission_dt && test.dt_admission_dt != " Invalid Date"
                ? test.dt_admission_dt
                : "",
            HospitalizationAdmissionDateUnk: test.dt_admission === 99,
            NoHospitalizationDosage: test.dt_admission === 2,

            HospitalizationDischargeDate:
              test.dt_discharge_dt && test.dt_discharge_dt != "Invalid Date"
                ? test.dt_discharge_dt
                : "",
            HospitalizationDischargeUnk: test.dt_discharge === 99,
            PatientStillHospitalized: test.dt_discharge === 2,

            HospitalizationReasonAdministration: test.hospReason1,
            HospitalizationReasonMonitorAe: test.hospReason2,
            HospitalizationReasonCrs: test.hospReason3,
            HospitalizationReasonIcans: test.hospReason4,
            HospitalizationReasonOtherae: test.hospReason5,
            HospitalizationReasonOtnernonae: test.hospReason6,
            HospitalizationReasonAdministrationDaratumumab: test.hospReason7,
            HospitalizationReasonUnknown: test.hospReasonUnk,
            HospitalizationReasonOthernonaeTxt: test.hosp_reason_oth_txt,
          }),
        );

        //Q37 and Q40 Payload
        const Q37ElrDetail = Q37ElrexDetails.map((test) => ({
          Teclistamabcharacteristicsid: test.elrexlinvocharacteristicsid,

          Cohorttreatmentid: isElrexvayliSelected
            ? RRMMCohortTreatment.ELREXFIO
            : 0, //test.cohorttreatmentid,

          // Q37 retired
          // Dosefrequencyreduced: test.Q37ElrexvayliRd,

          // TreatmentInitialDoseFrequency: test.q37_Elr_Dose_week_frquencyRd,
          // TreatmentInitialDoseFrequencyText:
          //   test.q37_Elr_Dose_week_frquency_txt,
          // TreatmentInitialDosage: test.q37ElrDosageRd,
          // TreatmentInitialDosageOthtext: test.q37ElrDosageOtherTxt,

          //40

          IvigDosageFrequency: test.Q40dosingfreqRd,
          LastIgglevel: test.Q40IgILevelRd,
          IvigInitiationDateUnk: test.Q40init_dt_Rd === 99 ? true : null,
          IvigInitiationDate:
            test.Q40init_dt && test.Q40init_dt !== "Invalid Date"
              ? test.Q40init_dt
              : "",
          MedicationReasonPrimaryProphylactic: test.primaryProf,
          MedicationReasonSecondaryProphylactic: test.secondaryProf,
          MedicationReasonOth: test.otherReson,

          IvigDosageFrequencyOthTxt: test.dosing_freq_oth_txt,

          //40.C
          IvigInitiationDateRdC: test.ivigInitRd_elrC,
          IvigInitiationDateC:
            test.ivigInitDt_elrC && test.ivigInitDt_elrC != "Invalid Date"
              ? test.ivigInitDt_elrC
              : "",
          IvigDosingTypeC: test.ivigDosingType_elrC,
          IvigDosageFrequencyRdC: test.ivigDosingFreqRd_elrC,
          IvigDosageFrequencyTimesTxtC: test.ivigDosingFreqTimesTxt_elrC,
          IvigDosageFrequencyWeeksTxtC: test.ivigDosingFreqWeeksTxt_elrC,
          IvigDosageFrequencyOthTxtC: test.ivigDosingFreqOtherTxt_elrC,
          IvigReasonPrimaryC: test.ivigReasonPrimary_elrC,
          IvigReasonSecondaryC: test.ivigReasonSecondary_elrC,
          IvigReasonOthC: test.ivigReasonOther_elrC,
          IvigReasonOthTxtC: test.ivigReasonOtherTxt_elrC,
          IvigReasonUnkC: test.ivigReasonUnk_elrC,
          IvigDoseStrengthRdC: test.ivigDoseStrengthRd_elrC,
          IvigDoseStrengthGTxtC: test.ivigDoseStrengthGTxt_elrC,
          IvigDoseStrengthMgKgTxtC: test.ivigDoseStrengthMgKgTxt_elrC,
          IvigDoseStrengthOthTxtC: test.ivigDoseStrengthOtherTxt_elrC,
          IvigBodyWeightRdC: test.ivigBodyWeightRd_elrC,
          IvigBodyWeightKgTxtC: test.ivigBodyWeightKgTxt_elrC,
          IvigBodyWeightLbsTxtC: test.ivigBodyWeightLbsTxt_elrC,
          LastIgglevelBeforeFirstIvigC: test.ivigLastIgg_elrC,

          //39

          MedicationTocilizumab: test.tocilizumab_elr,
          MedicationGcsf: test.Granulocyte,
          MedicationNone: test.noneoftheabove_elr,
          MedicationSteroids: test.SteroidsElr,

          //39.C
          MedicationTocilizumabC: test.tocilizumab_elrC,
          MedicationSteroidsC: test.steroids_elrC,
          MedicationGcsfC: test.granulocyte_elrC,
          MedicationNoneC: test.noneoftheabove_elrC === 99,
          TociliAdmsDateC:
            test.tociliAdmsDateRD_elrC === 1 &&
            test.tociliAdmsDate_elrC &&
            test.tociliAdmsDate_elrC !== "Invalid Date"
              ? test.tociliAdmsDate_elrC
              : "",
          TociliAdmsDateUnkC: test.tociliAdmsDateRD_elrC === 99,
          TociliDoseC: test.tociliDoseRd_elrC,
          TociliDoseMgkgUnitC: test.tociliDoseMgkg_elrC
            ? parseFloat(test.tociliDoseMgkg_elrC)
            : null,
          TociliDoseOtherC: test.tociliDoseOther_elrC,
          TociliReasonPretratmentC: test.tociliReasonPretratment_elrC,
          TociliReasonPrimarycrsC: test.tociliReasonPrimarycrs_elrC,
          TociliReasonSecondarycrsC: test.tociliReasonSecondarycrs_elrC,
          TociliReasonSupportivecrsC: test.tociliReasonSupportivecrs_elrC,
          TociliReasonPrimaryneuroC: test.tociliReasonPrimaryneuro_elrC,
          TociliReasonSecondaryneuroC: test.tociliReasonSecondaryneuro_elrC,
          TociliReasonSupportiveneuroC: test.tociliReasonSupportiveneuro_elrC,
          TociliReasonUnkC: test.tociliReasonUnk_elrC,

          //39 - 1.1
          // Q39.1 retired
          // DosageusageReasonProphylacticCrs: test.cytokin_elr,
          // DosageusageReasonSecondaryProphylacticCrs:
          //   test.secondary_prophylactic_crs_elr,
          // DosagusageReasonTreatmentsupportiveCrs: test.crs_elr,
          // DosagusageReasonProphylacticneurotoxicity: test.neurotoxicity_elr,
          // DosagusageReasonSecondaryProphylacticNeurotoxicity:
          //   test.secondary_neurotoxicity_elr,
          // DosagusageReasonTreatmentsupportiveNeurotoxicity:
          //   test.tmt_neurotoxicity_elr,
          // DosagusageReasonTreatmentUnknown: test.tmt_elr_unknown,
          //39 - 1.3
          GcsfUsageReasonProphylactic: test.prophylactic_use_elr,
          GcsfUsageReasonSecondaryProphylactic:
            test.secondary_prophylactic_use_elr,
          GcsfUsageReasonOth: test.oth_use_elr,
          GcsfUsageReasonUnk: test.unknown_elr === 99 ? true : null,

          //39.2
          SteroidUsageReasonTreatmentRecommendation: test.steroid_pre_tmt_elr,
          SteroidsUsageReasonProphylacticCrs: test.cytokin_elr_steroids,
          SteroidsUsageReasonSecondaryProphylacticCrs:
            test.secondary_prophylactic_elr_steroids,
          SteroidsUsageReasonTreatmentsupportiveCrs: test.crs_elr_steroids,
          SteroidsUsageReasonProphylacticneurotoxicity:
            test.neurotoxicity_elr_steroids,
          SteroidsUsageReasonSecondaryProphylacticNeurologic:
            test.secondary_neurotoxicity_elr_steroids,
          SteroidsUsageReasonTreatmentsupportiveNeurotoxicity:
            test.tmt_neurotoxicity_elr_steroids,

          SteroidUsageReasonUnknown: test.tmt_steroids_elr_unknown,

          SteroidNameBetamethasone: test.betamethasone_elr,
          SteroidDoseBetamethasone: test.betamethasone_elrNum,

          SteroidNameDexamethasone: test.dexamethasone_elr,
          SteroidDoseDexamethasone: test.dexamethasone_elrNum,

          SteroidNameHydrocortisone: test.hydrocortisone_elr,
          SteroidDoseHydrocortisone: test.hydrocortisone_elrNum,

          SteroidNameMethylprednisolone: test.methylprednisolone,
          SteroidDoseMethylprednisolone: test.methylprednisoloneNum,

          SteroidNamePrednisolone: test.prednisolone_elr,
          SteroidDosePrednisolone: test.prednisolone_elrNum,

          SteroidNamePrednisone: test.prednisone_elr,
          SteroidDosePrednisone: test.prednisone_elrNum,

          SteroidNameTriamcinolone: test.triamcinolone_elr,
          SteroidDoseTriamcinolone: test.triamcinolone_elrNum,

          SteroidDoseNameUnknown: test.dose_name_elr_unknown,
        }));

        const Q37LinvoDetail = Q37LinvoDetails.map((test) => ({
          Teclistamabcharacteristicsid: test.teclistamabcharacteristicsid,

          Cohorttreatmentid: isLinvoveySelected
            ? RRMMCohortTreatment.Linvoseltamab
            : 0, //test.cohorttreatmentid,

          // Q37 retired
          // Dosefrequencyreduced: test.Q37LinvoveyRd,

          // TreatmentInitialDoseFrequency: test.q37_Linvo_Dose_week_frquencyRd,

          // TreatmentInitialDoseFrequencyText:
          //   test.q37_Linvo_Dose_week_frquency_txt,
          // TreatmentInitialDosage: test.q37_Linvo_Dosage_Rd,
          // TreatmentInitialDosageOthtext: test.q37_Linvo_Dosage_OtherTxt,

          //39

          MedicationTocilizumab: test.tocilizumab_linvo,
          MedicationSteroids: test.Steroids,
          MedicationNone: test.noneoftheabove_linvo,
          MedicationGcsf: test.granulocyte_linvo,

          //39.C
          MedicationTocilizumabC: test.tocilizumab_linvoC,
          MedicationSteroidsC: test.steroids_linvoC,
          MedicationGcsfC: test.granulocyte_linvoC,
          MedicationNoneC: test.noneoftheabove_linvoC === 99,
          TociliAdmsDateC:
            test.tociliAdmsDateRD_linvoC === 1 &&
            test.tociliAdmsDate_linvoC &&
            test.tociliAdmsDate_linvoC !== "Invalid Date"
              ? test.tociliAdmsDate_linvoC
              : "",
          TociliAdmsDateUnkC: test.tociliAdmsDateRD_linvoC === 99,
          TociliDoseC: test.tociliDoseRd_linvoC,
          TociliDoseMgkgUnitC: test.tociliDoseMgkg_linvoC
            ? parseFloat(test.tociliDoseMgkg_linvoC)
            : null,
          TociliDoseOtherC: test.tociliDoseOther_linvoC,
          TociliReasonPretratmentC: test.tociliReasonPretratment_linvoC,
          TociliReasonPrimarycrsC: test.tociliReasonPrimarycrs_linvoC,
          TociliReasonSecondarycrsC: test.tociliReasonSecondarycrs_linvoC,
          TociliReasonSupportivecrsC: test.tociliReasonSupportivecrs_linvoC,
          TociliReasonPrimaryneuroC: test.tociliReasonPrimaryneuro_linvoC,
          TociliReasonSecondaryneuroC: test.tociliReasonSecondaryneuro_linvoC,
          TociliReasonSupportiveneuroC: test.tociliReasonSupportiveneuro_linvoC,
          TociliReasonUnkC: test.tociliReasonUnk_linvoC,

          //39.1
          // Q39.1 retired
          // DosageusageReasonProphylacticCrs: test.cytokin_linvo,
          // DosageusageReasonSecondaryProphylacticCrs:
          //   test.secondary_prophylactic_crs_linvo,
          // DosagusageReasonTreatmentsupportiveCrs: test.crs_linvo,
          // DosagusageReasonProphylacticneurotoxicity: test.neurotoxicity_linvo,
          // DosagusageReasonSecondaryProphylacticNeurotoxicity:
          //   test.secondary_neurotoxicity_linvo,
          // DosagusageReasonTreatmentsupportiveNeurotoxicity:
          //   test.tmt_neurotoxicity_linvo,
          // DosagusageReasonTreatmentUnknown: test.tmt_linvoseltamab_unknown,
          IvigInitiationDateUnk:
            test.administrationDateRD_linvo === 99 ? true : null,
          IvigInitiationDate:
            test.q40Init_Dt_linvo && test.q40Init_Dt_linvo !== "Invalid Date"
              ? test.q40Init_Dt_linvo
              : "",

          //39 - 1.3
          GcsfUsageReasonProphylactic: test.prophylactic_use_linvo,
          GcsfUsageReasonSecondaryProphylactic:
            test.secondary_prophylactic_use_linvo,
          GcsfUsageReasonOth: test.oth_use_linvo,
          GcsfUsageReasonUnk: test.unknown_linvo === 99 ? true : null,

          // //1.2
          SteroidUsageReasonTreatmentRecommendation: test.steroid_pre_tmt_linvo,
          SteroidsUsageReasonProphylacticCrs: test.cytokin_linvo_steroids,
          SteroidsUsageReasonSecondaryProphylacticCrs:
            test.secondary_prophylactic_linvo_steroids,
          SteroidsUsageReasonTreatmentsupportiveCrs: test.crs_linvo_steroids,
          SteroidsUsageReasonProphylacticneurotoxicity:
            test.neurotoxicity_linvo_steroids,
          SteroidsUsageReasonSecondaryProphylacticNeurologic:
            test.secondary_neurotoxicity_linvo_steroids,
          SteroidsUsageReasonTreatmentsupportiveNeurotoxicity:
            test.tmt_neurotoxicity_linvo_steroids,
          SteroidUsageReasonUnknown: test.tmt_linvo_unknown,

          SteroidNameBetamethasone: test.betamethasone_linvo,
          SteroidDoseBetamethasone: test.betamethasone_linvoNum,

          SteroidNameDexamethasone: test.dexamethasone_linvo,
          SteroidDoseDexamethasone: test.dexamethasone_linvoNum,

          SteroidNameHydrocortisone: test.hydrocortisone_linvo,
          SteroidDoseHydrocortisone: test.hydrocortisone_linvoNum,

          SteroidNameMethylprednisolone: test.methylprednisolone_linvo,
          SteroidDoseMethylprednisolone: test.methylprednisolone_linvoNum,

          SteroidNamePrednisolone: test.prednisolone_linvo,
          SteroidDosePrednisolone: test.prednisolone_linvoNum,

          SteroidNamePrednisone: test.prednisone_linvo,
          SteroidDosePrednisone: test.prednisone_linvoNum,

          SteroidNameTriamcinolone: test.triamcinolone_linvo,
          SteroidDoseTriamcinolone: test.triamcinolone_linvoNum,

          SteroidDoseNameUnknown: test.dose_name_linvo_unknown,

          //40

          IvigDosageFrequency: test.q40DosingFreqRd_linvo,
          LastIgglevel: test.q40IgILevelRd_linvo,
          MedicationReasonPrimaryProphylactic: test.primaryProf_linvo,
          MedicationReasonSecondaryProphylactic: test.secondaryProf_linvo,
          MedicationReasonOth: test.otherReson_linvo,
          IvigDosageFrequencyOthTxt: test.dosing_Freq_Oth_Txt_linvo,

          //40.C
          IvigInitiationDateRdC: test.ivigInitRd_linvoC,
          IvigInitiationDateC:
            test.ivigInitDt_linvoC && test.ivigInitDt_linvoC != "Invalid Date"
              ? test.ivigInitDt_linvoC
              : "",
          IvigDosingTypeC: test.ivigDosingType_linvoC,
          IvigDosageFrequencyRdC: test.ivigDosingFreqRd_linvoC,
          IvigDosageFrequencyTimesTxtC: test.ivigDosingFreqTimesTxt_linvoC,
          IvigDosageFrequencyWeeksTxtC: test.ivigDosingFreqWeeksTxt_linvoC,
          IvigDosageFrequencyOthTxtC: test.ivigDosingFreqOtherTxt_linvoC,
          IvigReasonPrimaryC: test.ivigReasonPrimary_linvoC,
          IvigReasonSecondaryC: test.ivigReasonSecondary_linvoC,
          IvigReasonOthC: test.ivigReasonOther_linvoC,
          IvigReasonOthTxtC: test.ivigReasonOtherTxt_linvoC,
          IvigReasonUnkC: test.ivigReasonUnk_linvoC,
          IvigDoseStrengthRdC: test.ivigDoseStrengthRd_linvoC,
          IvigDoseStrengthGTxtC: test.ivigDoseStrengthGTxt_linvoC,
          IvigDoseStrengthMgKgTxtC: test.ivigDoseStrengthMgKgTxt_linvoC,
          IvigDoseStrengthOthTxtC: test.ivigDoseStrengthOtherTxt_linvoC,
          IvigBodyWeightRdC: test.ivigBodyWeightRd_linvoC,
          IvigBodyWeightKgTxtC: test.ivigBodyWeightKgTxt_linvoC,
          IvigBodyWeightLbsTxtC: test.ivigBodyWeightLbsTxt_linvoC,
          LastIgglevelBeforeFirstIvigC: test.ivigLastIgg_linvoC,
        }));

        // Q38Elr payload
        const q38ElrexfioData = Q38ElrexDetails.map((test) => ({
          Elranatamablinvoseltamabdosefrequencybridgeid:
            test.elrexlinvodosefrequencybridgeid,
          Cohorttreatmentid: test.cohorttreatmentid,

          DosefreqReductionDateUnk: test.Q38ElrexvayliRd,
          DosefreqReductionDate:
            test.dt_dosefreq_elr_dt != "Invalid Date" && test.dt_dosefreq_elr_dt
              ? test.dt_dosefreq_elr_dt
              : "",

          TreatmentInitialDoseFrequency: test.q38_Elr_Dose_week_frquencyRd,

          TreatmentInitialDoseFrequencyText:
            test.q38_Elr_Dose_week_frquency_txt,
        }));

        // Q38linvoseltamabData
        const q38LinvoseltamabData = Q38LinvoDetails.map((test) => ({
          Elranatamablinvoseltamabdosefrequencybridgeid:
            test.elrexlinvodosefrequencybridgeid,
          Cohorttreatmentid: test.cohorttreatmentid,

          DosefreqReductionDateUnk: test.Q38LinvoveyRd,
          DosefreqReductionDate:
            test.dt_dosefreq_linvo_dt != "Invalid Date" &&
            test.dt_dosefreq_linvo_dt
              ? test.dt_dosefreq_linvo_dt
              : "",

          TreatmentInitialDoseFrequency: test.q38_Linvo_Dose_week_frquencyRd,
          TreatmentInitialDoseFrequencyText:
            test.q38_Linvo_Dose_week_frquency_txt,
          TreatmentInitialDosageOthtext: test.q38_Linvo_Dosage_OtherTxt,
          TreatmentInitialDosage: test.q38_Linvo_Dosage_Rd,
        }));

        // Q38.1.A ELREXFIO Payload
        const Q381AElrexfioData = Q381AElrexDetails.map((test) => ({
          Teclistamatalquetamabdosefrequencyinfobridgeid:
            test.teclistamatalquetamabdosefrequencyinfobridgeid || 0,
          Cohorttreatmentid: test.cohorttreatmentid || 11,
          Patientid: pid,

          // Column B - Type of change
          ChangeType: test.changeType,
          ChangeTypeDoseFrequency: test.changeTypeDoseFrequency,
          ChangeTypeDoseStrength: test.changeTypeDoseStrength,

          // Column C - Reasons
          SelectedReasons: test.selectedReasons || [],
          SelectedAEReasons: test.selectedAEReasons || [],
          NeurologicAEReasonList: test.neurologicAEReasonList || [],
          ReasonOtherText: test.reasonOtherText || "",
          BispecifictreatmentdiscontinuationreasonUnk:
            test.reasonUnknown || false,

          // Column D - Pattern BEFORE
          PatternBeforeChangeDoseFrequency:
            test.patterBeforeChangeDoseFrequency,
          PatternBeforeChangeDoseFrequencyWeek:
            test.patterBeforeChangeDoseFrequency === 1
              ? Number(test.patterBeforeChangeDoseFrequencyTimesPerWeek) || null
              : test.patterBeforeChangeDoseFrequency === 2
                ? Number(test.patterBeforeChangeDoseFrequencyWeek) || null
                : null,

          PatternBeforeChangeDoseStrength: test.patterBeforeChangeDoseStrength,
          PatternBeforeChangeDoseStrengthUnit:
            test.patterBeforeChangeDoseStrength == 1
              ? test.patterBeforeChangeDoseStrengthMg
              : test.patterBeforeChangeDoseStrength == 2
                ? test.patterBeforeChangeDoseStrengthMgKg
                : test.patterBeforeChangeDoseStrength == 3
                  ? test.patterBeforeChangeDoseStrengthMgM2
                  : null,

          // Column E - Pattern AFTER
          PatternAfterChangeDoseFrequency: test.patterAfterChangeDoseFrequency,
          PatternAfterChangeDoseFrequencyWeek:
            test.patterAfterChangeDoseFrequency === 1
              ? Number(test.patterAfterChangeDoseFrequencyTimesPerWeek) || null
              : test.patterAfterChangeDoseFrequency === 2
                ? Number(test.patterAfterChangeDoseFrequencyWeek) || null
                : null,
          PatternAfterChangeDoseStrength: test.patterAfterChangeDoseStrength,
          PatternAfterChangeDoseStrengthUnit:
            test.patterAfterChangeDoseStrength == 1
              ? test.patterAfterChangeDoseStrengthMg
              : test.patterAfterChangeDoseStrength == 2
                ? test.patterAfterChangeDoseStrengthMgKg
                : test.patterAfterChangeDoseStrength == 3
                  ? test.patterAfterChangeDoseStrengthMgM2
                  : null,

          // Column F - Date of change
          DateChange:
            test.dateChange && test.dateChange !== "Invalid Date"
              ? test.dateChange
              : "",
          DateChangeUnk: test.dateChangeRd === 99,
        }));

        // Q38.1.A LINVOSELTAMAB Payload
        const Q381ALinvoData = Q381ALinvoDetails.map((test) => ({
          Teclistamatalquetamabdosefrequencyinfobridgeid:
            test.teclistamatalquetamabdosefrequencyinfobridgeid || 0,
          Cohorttreatmentid: test.cohorttreatmentid || 12,
          Patientid: pid,

          // Column B
          ChangeType: test.changeType,
          ChangeTypeDoseFrequency: test.changeTypeDoseFrequency,
          ChangeTypeDoseStrength: test.changeTypeDoseStrength,

          // Column C
          SelectedReasons: test.selectedReasons || [],
          SelectedAEReasons: test.selectedAEReasons || [],
          NeurologicAEReasonList: test.neurologicAEReasonList || [],
          ReasonOtherText: test.reasonOtherText || "",
          BispecifictreatmentdiscontinuationreasonUnk:
            test.reasonUnknown || false,

          // Column D
          PatternBeforeChangeDoseFrequency:
            test.patterBeforeChangeDoseFrequency,
          PatternBeforeChangeDoseFrequencyWeek:
            test.patterBeforeChangeDoseFrequency === 1
              ? Number(test.patterBeforeChangeDoseFrequencyTimesPerWeek) || null
              : test.patterBeforeChangeDoseFrequency === 2
                ? Number(test.patterBeforeChangeDoseFrequencyWeek) || null
                : null,

          PatternBeforeChangeDoseStrength: test.patterBeforeChangeDoseStrength,
          PatternBeforeChangeDoseStrengthUnit:
            test.patterBeforeChangeDoseStrength == 1
              ? test.patterBeforeChangeDoseStrengthMg
              : test.patterBeforeChangeDoseStrength == 2
                ? test.patterBeforeChangeDoseStrengthMgKg
                : test.patterBeforeChangeDoseStrength == 3
                  ? test.patterBeforeChangeDoseStrengthMgM2
                  : null,

          // Column E
          PatternAfterChangeDoseFrequency: test.patterAfterChangeDoseFrequency,
          PatternAfterChangeDoseFrequencyWeek:
            test.patterAfterChangeDoseFrequency === 1
              ? Number(test.patterAfterChangeDoseFrequencyTimesPerWeek) || null
              : test.patterAfterChangeDoseFrequency === 2
                ? Number(test.patterAfterChangeDoseFrequencyWeek) || null
                : null,
          PatternAfterChangeDoseStrength: test.patterAfterChangeDoseStrength,
          PatternAfterChangeDoseStrengthUnit:
            test.patterAfterChangeDoseStrength == 1
              ? test.patterAfterChangeDoseStrengthMg
              : test.patterAfterChangeDoseStrength == 2
                ? test.patterAfterChangeDoseStrengthMgKg
                : test.patterAfterChangeDoseStrength == 3
                  ? test.patterAfterChangeDoseStrengthMgM2
                  : null,

          // Column F
          DateChange:
            test.dateChange && test.dateChange !== "Invalid Date"
              ? test.dateChange
              : "",
          DateChangeUnk: test.dateChangeRd === 99,
        }));

        //  Q38.2.A
        const Q382AElrexfioData = Q382AElrexDetails.map((row) => ({
          Teclistamatalquetamabdosedelaybridgeid:
            row.teclistamatalquetamabdosefrequencyinfobridgeid, // UI id
          Cohorttreatmentid: row.cohorttreatmentid,
          InterruptType: row.changeType,
          SelectedReasons: row.selectedReasons || [],
          SelectedAEReasons: row.selectedAEReasons || [],
          NeurologicAEReasonList: row.neurologicAEReasonList || [],
          ReasonOtherText: row.reasonOtherText,
          BispecifictreatmentdiscontinuationreasonUnk:
            row.reasonUnknown === true,
          InterruptStartDate:
            row.startDateChangeRd === 1 ? row.startDateChange : null,
          InterruptStartDateUnk: row.startDateChangeRd === 99,
          InterruptEndDate:
            row.endDateChangeRd === 2 && row.endDateChange !== "Invalid Date"
              ? row.endDateChange
              : null,
          InterruptEndOngoingUnk: row.endDateChangeRd,
        }));

        // Q38.2.A LINVOSELTAMAB Payload
        const Q382ALinvoseltamabData = Q382ALinvoDetails.map((test) => ({
          Teclistamatalquetamabdosedelaybridgeid:
            test.teclistamatalquetamabdosefrequencyinfobridgeid ?? 0, // UI id used as key
          Cohorttreatmentid: test.cohorttreatmentid ?? 12,
          Patientid: pid,

          // Column B - Type of change
          InterruptType: test.changeType,

          // Column C - Reasons
          SelectedReasons: test.selectedReasons ?? [],
          SelectedAEReasons: test.selectedAEReasons ?? [],
          NeurologicAEReasonList: test.neurologicAEReasonList || [],
          ReasonOtherText: test.reasonOtherText,
          BispecifictreatmentdiscontinuationreasonUnk:
            test.reasonUnknown === true,

          // Column D - Start date of interruption/delay
          InterruptStartDate:
            test.startDateChangeRd === 1 &&
            test.startDateChange !== "Invalid Date"
              ? test.startDateChange
              : null,
          InterruptStartDateUnk: test.startDateChangeRd === 99,

          // Column E - End date / ongoing / unknown
          InterruptEndDate:
            test.endDateChangeRd === 2 && test.endDateChange !== "Invalid Date"
              ? test.endDateChange
              : null,
          //
          InterruptEndOngoingUnk: test.endDateChangeRd,
        }));

        //39.1.A

        const elrexfioQ391AData = isElrexvayliSelected
          ? q391AElrTocilzumabList.map((itm) => ({
              Tocilizumabmedicationid: itm.tocilizumabmedicationid ?? 0,
              Cohorttreatmentid: itm.cohorttreatmentid,
              Patientid: itm.patientid,
              TocilizumabAdministrationDate:
                itm.administrationDate_elr &&
                itm.administrationDate_elr !== "Invalid Date"
                  ? itm.administrationDate_elr
                  : "",
              TocilizumabAdministrationDateUnk:
                itm.administrationDateRD_elr === 99,
              PrimaryProphylacticCrs: itm.primaryProphylacticCrs_elr,
              SecondaryProphylacticCrs: itm.secondaryProphylacticCrs_elr,
              SupportiveCareCrs: itm.supportiveCareCrs_elr,
              PrimaryProphylacticNeurologic:
                itm.primaryProphylacticNeurologic_elr,
              SecondaryProphylacticNeurologic:
                itm.secondaryProphylacticNeurologic_elr,
              SupportiveCareNeurologic: itm.supportiveCareNeurologic_elr,
              MedicationReasonUnk: itm.medicationReasonUnk_elr,
            }))
          : [];

        const linvoQ391AData = isLinvoveySelected
          ? q391ALinvoTocilzumabList.map((itm) => ({
              Tocilizumabmedicationid: itm.tocilizumabmedicationid ?? 0,
              Cohorttreatmentid: itm.cohorttreatmentid,
              Patientid: itm.patientid,
              TocilizumabAdministrationDate:
                itm.administrationDate_linvo &&
                itm.administrationDate_linvo !== "Invalid Date"
                  ? itm.administrationDate_linvo
                  : "",
              TocilizumabAdministrationDateUnk:
                itm.administrationDateRD_linvo === 99,
              PrimaryProphylacticCrs: itm.primaryProphylacticCrs_linvo,
              SecondaryProphylacticCrs: itm.secondaryProphylacticCrs_linvo,
              SupportiveCareCrs: itm.supportiveCareCrs_linvo,
              PrimaryProphylacticNeurologic:
                itm.primaryProphylacticNeurologic_linvo,
              SecondaryProphylacticNeurologic:
                itm.secondaryProphylacticNeurologic_linvo,
              SupportiveCareNeurologic: itm.supportiveCareNeurologic_linvo,
              MedicationReasonUnk: itm.medicationReasonUnk_linvo,
            }))
          : [];

        const q40DosingFrequencyElr = Q37ElrexDetails?.[0]?.Q40dosingfreqRd;
        const shouldShowQ401BAnd3BElr =
          q40DosingFrequencyElr != null && q40DosingFrequencyElr !== 1;

        const q40DosingFrequencyLinvo =
          Q37LinvoDetails?.[0]?.Q40dosingfreqRd ||
          Q37LinvoDetails?.[0]?.q40DosingFreqRd_linvo;
        const shouldShowQ401BAnd3BLinvo =
          q40DosingFrequencyLinvo != null && q40DosingFrequencyLinvo !== 1;

        //Q40.1.B
        const q401aElrData =
          !isNewEcrfC &&
          isNewEcrfB &&
          shouldShowQ401BAnd3BElr &&
          Q401aElrDetails?.length > 0
            ? Q401aElrDetails.map((test) => ({
                Tectalivigreceivedbridgeid: test.tectalivigreceivedbridgeid,
                Cohorttreatmentid: RRMMCohortTreatment.ELREXFIO,

                Ivig_date_selection: test.Q401ainit_dt_Rd_Elr,
                Ivig_date:
                  test.Q401ainit_Elr_dt !== "Invalid Date" &&
                  test.Q401ainit_Elr_dt
                    ? test.Q401ainit_Elr_dt
                    : "",

                Intended_dosing_frequency: test.Q401aIntendedDosing_Elr,
                Intended_dosing_frequency_value:
                  test.Q401aIntendedDosing_Elr === 1
                    ? test.Q401aIntendedDosing_Elr_WeekValue
                    : test.Q401aIntendedDosing_Elr === 2
                      ? test.Q401aIntendedDosing_Elr_MonthValue
                      : test.Q401aIntendedDosing_Elr === 3
                        ? test.Q401aIntendedDosing_Elr_OnceWeekValue
                        : test.Q401aIntendedDosing_Elr === 4
                          ? test.Q401aIntendedDosing_Elr_OnceMonthValue
                          : test.Q401aIntendedDosing_Elr_OtherValue,
                Primary_reason: test.Q401aPrimaryProf_Elr,
                Secondary_reason: test.Q401aSecProf_Elr,
                Other_reason: test.Q401aOtherProf_Elr,
                Unknown_reason: test.Q401aUnknownProf_Elr,
                Dosing_type: test.Q401aDosingType_Elr,
                Dosage: test.Q401aDosage_Elr,
                Dosage_value:
                  test.Q401aDosage_Elr === 1
                    ? test.Q401aDosage_Elr_GValue
                    : test.Q401aDosage_Elr === 2
                      ? test.Q401aDosage_Elr_MgValue
                      : "",
                Body_weight: test.Q401aWeight_Elr,
                Body_weight_value:
                  test.Q401aWeight_Elr === 1
                    ? test.Q401aWeight_Value_KgElr
                    : test.Q401aWeight_Elr === 2
                      ? test.Q401aWeight_Value_LbsElr
                      : "",
                Weight_date_selection: test.Q401aWeight_Asses_Elr,
                Weight_date:
                  test.Q401aWeight_Asses_Elr_dt != "Invalid Date" &&
                  test.Q401aWeight_Asses_Elr_dt
                    ? test.Q401aWeight_Asses_Elr_dt
                    : "",
              }))
            : [];

        const q401aLinvoData =
          !isNewEcrfC &&
          isNewEcrfB &&
          shouldShowQ401BAnd3BLinvo &&
          Q401aLinvoDetails?.length > 0
            ? Q401aLinvoDetails.map((test) => ({
                Tectalivigreceivedbridgeid: test.tectalivigreceivedbridgeid,
                Cohorttreatmentid: RRMMCohortTreatment.Linvoseltamab,

                Ivig_date_selection: test.Q401ainit_dt_Rd_Lin,
                Ivig_date:
                  test.Q401ainit_Lin_dt != "Invalid Date" &&
                  test.Q401ainit_Lin_dt
                    ? test.Q401ainit_Lin_dt
                    : "",

                Intended_dosing_frequency: test.Q401aIntendedDosing_Lin,
                Intended_dosing_frequency_value:
                  test.Q401aIntendedDosing_Lin === 1
                    ? test.Q401aIntendedDosing_Lin_WeekValue
                    : test.Q401aIntendedDosing_Lin === 2
                      ? test.Q401aIntendedDosing_Lin_MonthValue
                      : test.Q401aIntendedDosing_Lin === 3
                        ? test.Q401aIntendedDosing_Lin_OnceWeekValue
                        : test.Q401aIntendedDosing_Lin === 4
                          ? test.Q401aIntendedDosing_Lin_OnceMonthValue
                          : test.Q401aIntendedDosing_Lin_OtherValue,
                Primary_reason: test.Q401aPrimaryProf_Lin,
                Secondary_reason: test.Q401aSecProf_Lin,
                Other_reason: test.Q401aOtherProf_Lin,
                Unknown_reason: test.Q401aUnknownProf_Lin,
                Dosing_type: test.Q401aDosingType_Lin,
                Dosage: test.Q401aDosage_Lin,
                Dosage_value:
                  test.Q401aDosage_Lin === 1
                    ? test.Q401aDosage_Lin_GValue
                    : test.Q401aDosage_Lin === 2
                      ? test.Q401aDosage_Lin_MgValue
                      : "",
                Body_weight: test.Q401aWeight_Lin,
                Body_weight_value:
                  test.Q401aWeight_Lin === 1
                    ? test.Q401aWeight_Value_KgLin
                    : test.Q401aWeight_Lin === 2
                      ? test.Q401aWeight_Value_LbsLin
                      : "",
                Weight_date_selection: test.Q401aWeight_Asses_Lin,
                Weight_date:
                  test.Q401aWeight_Asses_Lin_dt != "Invalid Date" &&
                  test.Q401aWeight_Asses_Lin_dt
                    ? test.Q401aWeight_Asses_Lin_dt
                    : "",
              }))
            : [];

        //Q40.1.C - maps onto the ivigdosefrequencyinformation table (ELIvigDoseFreqChangeModel).
        // DoseFrequencyBeforeChange/AfterChange = 1 (times/week) or 2 (once every N weeks) share
        // a single "Weeks" text field server-side parses into int; = 3 (Other) uses its own
        // free-text field instead. DateChangeUnk is a plain bool, not a 3-way selection.
        const q401cElrData =
          isNewEcrfC &&
          Q37ElrexDetails?.[0]?.ivigInitRd_elrC != null &&
          Q37ElrexDetails?.[0]?.ivigInitRd_elrC !== 1 &&
          Q401cElrDetails?.length > 0
            ? Q401cElrDetails.map((test) => ({
                Ivigdosefrequencyinformationid: test.ivigdosefreqchangebridgeid,
                Cohorttreatmentid: RRMMCohortTreatment.ELREXFIO,

                DoseFrequencyChange: test.Q401cChangeType_Elr,

                DoseFrequencyBeforeChange: test.Q401cFreqBeforeRd_Elr,
                DoseFrequencyBeforeChangeWeeks:
                  test.Q401cFreqBeforeRd_Elr === 1
                    ? test.Q401cFreqBeforeTimesTxt_Elr
                    : test.Q401cFreqBeforeRd_Elr === 2
                      ? test.Q401cFreqBeforeWeeksTxt_Elr
                      : "",
                DoseFrequencyBeforeChangeOthertxt:
                  test.Q401cFreqBeforeRd_Elr === 3
                    ? test.Q401cFreqBeforeOtherTxt_Elr
                    : "",

                DoseFrequencyAfterChange: test.Q401cFreqAfterRd_Elr,
                DoseFrequencyAfterChangeWeeks:
                  test.Q401cFreqAfterRd_Elr === 1
                    ? test.Q401cFreqAfterTimesTxt_Elr
                    : test.Q401cFreqAfterRd_Elr === 2
                      ? test.Q401cFreqAfterWeeksTxt_Elr
                      : "",
                DoseFrequencyAfterChangeOthertxt:
                  test.Q401cFreqAfterRd_Elr === 3
                    ? test.Q401cFreqAfterOtherTxt_Elr
                    : "",

                DateChange:
                  test.Q401cDateChangeRd_Elr === 1 &&
                  test.Q401cDateChange_Elr !== "Invalid Date" &&
                  test.Q401cDateChange_Elr
                    ? test.Q401cDateChange_Elr
                    : "",
                DateChangeUnk: test.Q401cDateChangeRd_Elr === 99,
              }))
            : [];

        const q401cLinvoData =
          isNewEcrfC &&
          Q37LinvoDetails?.[0]?.ivigInitRd_linvoC != null &&
          Q37LinvoDetails?.[0]?.ivigInitRd_linvoC !== 1 &&
          Q401cLinvoDetails?.length > 0
            ? Q401cLinvoDetails.map((test) => ({
                Ivigdosefrequencyinformationid: test.ivigdosefreqchangebridgeid,
                Cohorttreatmentid: RRMMCohortTreatment.Linvoseltamab,

                DoseFrequencyChange: test.Q401cChangeType_Lin,

                DoseFrequencyBeforeChange: test.Q401cFreqBeforeRd_Lin,
                DoseFrequencyBeforeChangeWeeks:
                  test.Q401cFreqBeforeRd_Lin === 1
                    ? test.Q401cFreqBeforeTimesTxt_Lin
                    : test.Q401cFreqBeforeRd_Lin === 2
                      ? test.Q401cFreqBeforeWeeksTxt_Lin
                      : "",
                DoseFrequencyBeforeChangeOthertxt:
                  test.Q401cFreqBeforeRd_Lin === 3
                    ? test.Q401cFreqBeforeOtherTxt_Lin
                    : "",

                DoseFrequencyAfterChange: test.Q401cFreqAfterRd_Lin,
                DoseFrequencyAfterChangeWeeks:
                  test.Q401cFreqAfterRd_Lin === 1
                    ? test.Q401cFreqAfterTimesTxt_Lin
                    : test.Q401cFreqAfterRd_Lin === 2
                      ? test.Q401cFreqAfterWeeksTxt_Lin
                      : "",
                DoseFrequencyAfterChangeOthertxt:
                  test.Q401cFreqAfterRd_Lin === 3
                    ? test.Q401cFreqAfterOtherTxt_Lin
                    : "",

                DateChange:
                  test.Q401cDateChangeRd_Lin === 1 &&
                  test.Q401cDateChange_Lin !== "Invalid Date" &&
                  test.Q401cDateChange_Lin
                    ? test.Q401cDateChange_Lin
                    : "",
                DateChangeUnk: test.Q401cDateChangeRd_Lin === 99,
              }))
            : [];

        //Q40.2.C
        const q402cElrData =
          isNewEcrfC && isElrexvayliSelected && Q402cElrDetails?.length > 0
            ? Q402cElrDetails.map((test) => ({
                Igglevelinformationid: test.igglevelinformationid,
                Cohorttreatmentid: RRMMCohortTreatment.ELREXFIO,
                RowType: test.rowType,

                Igg_assessment_selection: test.Q402cAssessment_Elr,
                Igg_assessment_date:
                  test.Q402cAssessment_Elr_dt !== "Invalid Date" &&
                  test.Q402cAssessment_Elr_dt
                    ? test.Q402cAssessment_Elr_dt
                    : "",

                Igg_level_bucket_selection: test.Q402cLevelBucket_Elr,

                Igg_level_selection: test.Q402cLevelValue_Elr,
                Igg_level_value:
                  test.Q402cLevelValue_Elr === 1
                    ? test.Q402cLevelValue_Elr_MgValue
                    : test.Q402cLevelValue_Elr_GValue,
              }))
            : [];

        const q402cLinvoData =
          isNewEcrfC && isLinvoveySelected && Q402cLinvoDetails?.length > 0
            ? Q402cLinvoDetails.map((test) => ({
                Igglevelinformationid: test.igglevelinformationid,
                Cohorttreatmentid: RRMMCohortTreatment.Linvoseltamab,
                RowType: test.rowType,

                Igg_assessment_selection: test.Q402cAssessment_Lin,
                Igg_assessment_date:
                  test.Q402cAssessment_Lin_dt !== "Invalid Date" &&
                  test.Q402cAssessment_Lin_dt
                    ? test.Q402cAssessment_Lin_dt
                    : "",

                Igg_level_bucket_selection: test.Q402cLevelBucket_Lin,

                Igg_level_selection: test.Q402cLevelValue_Lin,
                Igg_level_value:
                  test.Q402cLevelValue_Lin === 1
                    ? test.Q402cLevelValue_Lin_MgValue
                    : test.Q402cLevelValue_Lin_GValue,
              }))
            : [];

        //Q40.2.B
        const q402aElrData =
          !isNewEcrfC && isNewEcrfB && Q402aElrDetails?.length > 0
            ? Q402aElrDetails.map((test) => ({
                Tectaliggdetailbridgeid: test.tectaliggdetailbridgeid,
                Cohorttreatmentid: RRMMCohortTreatment.ELREXFIO,

                Igg_assessment_selection: test.Q402aIgg_assessment_Elr,
                Igg_assessment_date:
                  test.Q402aIgg_assessment_Elr_dt != "Invalid Date" &&
                  test.Q402aIgg_assessment_Elr_dt
                    ? test.Q402aIgg_assessment_Elr_dt
                    : "",

                Igg_level_selection: test.Q402aIgg_level_Elr,
                Igg_level_value:
                  test.Q402aIgg_level_Elr === 1
                    ? test.Q402aIgg_level_Elr_MgValue
                    : test.Q402aIgg_level_Elr_GValue,
              }))
            : [];

        const q402aLinvoData =
          !isNewEcrfC && isNewEcrfB && Q402aLinvoDetails.length > 0
            ? Q402aLinvoDetails.map((test) => ({
                Tectaliggdetailbridgeid: test.tectaliggdetailbridgeid,
                Cohorttreatmentid: RRMMCohortTreatment.Linvoseltamab,

                Igg_assessment_selection: test.Q402aIgg_assessment_Lin,
                Igg_assessment_date:
                  test.Q402aIgg_assessment_Lin_dt != "Invalid Date" &&
                  test.Q402aIgg_assessment_Lin_dt
                    ? test.Q402aIgg_assessment_Lin_dt
                    : "",

                Igg_level_selection: test.Q402aIgg_level_Lin,
                Igg_level_value:
                  test.Q402aIgg_level_Lin === 1
                    ? test.Q402aIgg_level_Lin_MgValue
                    : test.Q402aIgg_level_Lin_GValue,
              }))
            : [];

        //Q40.3.B
        const q403aElrData =
          !isNewEcrfC &&
          isNewEcrfB &&
          shouldShowQ401BAnd3BElr &&
          Q403aElrDetails?.length > 0
            ? Q403aElrDetails.map((test) => ({
                Tectaliggdetailbridgeid: test.tectaliggdetailbridgeid,
                Cohorttreatmentid: RRMMCohortTreatment.ELREXFIO,

                Igg_assessment_selection: test.Q403aIgg_assessment_Elr,
                Igg_assessment_date:
                  test.Q403aIgg_assessment_Elr_dt != "Invalid Date" &&
                  test.Q403aIgg_assessment_Elr_dt
                    ? test.Q403aIgg_assessment_Elr_dt
                    : "",

                Igg_level_selection: test.Q403aIgg_level_Elr,
                Igg_level_value:
                  test.Q403aIgg_level_Elr === 1
                    ? test.Q403aIgg_level_Elr_MgValue
                    : test.Q403aIgg_level_Elr_GValue,
              }))
            : [];

        const q403aLinvoData =
          !isNewEcrfC &&
          isNewEcrfB &&
          shouldShowQ401BAnd3BLinvo &&
          Q403aLinvoDetails?.length > 0
            ? Q403aLinvoDetails.map((test) => ({
                Tectaliggdetailbridgeid: test.tectaliggdetailbridgeid,
                Cohorttreatmentid: RRMMCohortTreatment.Linvoseltamab,

                Igg_assessment_selection: test.Q403aIgg_assessment_Lin,
                Igg_assessment_date:
                  test.Q403aIgg_assessment_Lin_dt != "Invalid Date" &&
                  test.Q403aIgg_assessment_Lin_dt
                    ? test.Q403aIgg_assessment_Lin_dt
                    : "",

                Igg_level_selection: test.Q403aIgg_level_Lin,
                Igg_level_value:
                  test.Q403aIgg_level_Lin === 1
                    ? test.Q403aIgg_level_Lin_MgValue
                    : test.Q403aIgg_level_Lin_GValue,
              }))
            : [];

        //39.2.C - Steroidsmedication (separate table, one row per bispecific cohort)
        const SteroidsmedicationList = [];
        if (isElrexvayliSelected && Q37ElrexDetails?.[0]) {
          const elrRow = Q37ElrexDetails[0];
          SteroidsmedicationList.push({
            Steroidsmedicationid: elrRow.steroidsmedicationidElrC || 0,
            Cohorttreatmentid: RRMMCohortTreatment.ELREXFIO,
            SteroidNameBetamethasone: elrRow.betamethasone_elrC,
            SteroidDoseBetamethasone: elrRow.betamethasone_elrCNum
              ? parseInt(elrRow.betamethasone_elrCNum, 10)
              : null,
            SteroidNameDexamethasone: elrRow.dexamethasone_elrC,
            SteroidDoseDexamethasone: elrRow.dexamethasone_elrCNum
              ? parseInt(elrRow.dexamethasone_elrCNum, 10)
              : null,
            SteroidNameHydrocortisone: elrRow.hydrocortisone_elrC,
            SteroidDoseHydrocortisone: elrRow.hydrocortisone_elrCNum
              ? parseInt(elrRow.hydrocortisone_elrCNum, 10)
              : null,
            SteroidNameMethylprednisolone: elrRow.methylprednisolone_elrC,
            SteroidDoseMethylprednisolone: elrRow.methylprednisolone_elrCNum
              ? parseInt(elrRow.methylprednisolone_elrCNum, 10)
              : null,
            SteroidNamePrednisolone: elrRow.prednisolone_elrC,
            SteroidDosePrednisolone: elrRow.prednisolone_elrCNum
              ? parseInt(elrRow.prednisolone_elrCNum, 10)
              : null,
            SteroidNamePrednisone: elrRow.prednisone_elrC,
            SteroidDosePrednisone: elrRow.prednisone_elrCNum
              ? parseInt(elrRow.prednisone_elrCNum, 10)
              : null,
            SteroidNameTriamcinolone: elrRow.triamcinolone_elrC,
            SteroidDoseTriamcinolone: elrRow.triamcinolone_elrCNum
              ? parseInt(elrRow.triamcinolone_elrCNum, 10)
              : null,
            SteroidDoseNameUnknown: elrRow.dose_name_elrC_unknown,
            SteroidReasonTreatmentRecommendation:
              elrRow.steroidReasonPretratment_elrC,
            SteroidReasonPrimaryCrs: elrRow.steroidReasonPrimarycrs_elrC,
            SteroidReasonSecondaryCrs: elrRow.steroidReasonSecondarycrs_elrC,
            SteroidReasonSupportiveCrs: elrRow.steroidReasonSupportivecrs_elrC,
            SteroidReasonPrimaryNeuro: elrRow.steroidReasonPrimaryneuro_elrC,
            SteroidReasonSecondaryNeuro:
              elrRow.steroidReasonSecondaryneuro_elrC,
            SteroidReasonSupportiveNeuro:
              elrRow.steroidReasonSupportiveneuro_elrC,
            SteroidReasonUnk: elrRow.steroidReasonUnk_elrC,
          });
        }
        if (isLinvoveySelected && Q37LinvoDetails?.[0]) {
          const linvoRow = Q37LinvoDetails[0];
          SteroidsmedicationList.push({
            Steroidsmedicationid: linvoRow.steroidsmedicationidLinvoC || 0,
            Cohorttreatmentid: RRMMCohortTreatment.Linvoseltamab,
            SteroidNameBetamethasone: linvoRow.betamethasone_linvoC,
            SteroidDoseBetamethasone: linvoRow.betamethasone_linvoCNum
              ? parseInt(linvoRow.betamethasone_linvoCNum, 10)
              : null,
            SteroidNameDexamethasone: linvoRow.dexamethasone_linvoC,
            SteroidDoseDexamethasone: linvoRow.dexamethasone_linvoCNum
              ? parseInt(linvoRow.dexamethasone_linvoCNum, 10)
              : null,
            SteroidNameHydrocortisone: linvoRow.hydrocortisone_linvoC,
            SteroidDoseHydrocortisone: linvoRow.hydrocortisone_linvoCNum
              ? parseInt(linvoRow.hydrocortisone_linvoCNum, 10)
              : null,
            SteroidNameMethylprednisolone: linvoRow.methylprednisolone_linvoC,
            SteroidDoseMethylprednisolone: linvoRow.methylprednisolone_linvoCNum
              ? parseInt(linvoRow.methylprednisolone_linvoCNum, 10)
              : null,
            SteroidNamePrednisolone: linvoRow.prednisolone_linvoC,
            SteroidDosePrednisolone: linvoRow.prednisolone_linvoCNum
              ? parseInt(linvoRow.prednisolone_linvoCNum, 10)
              : null,
            SteroidNamePrednisone: linvoRow.prednisone_linvoC,
            SteroidDosePrednisone: linvoRow.prednisone_linvoCNum
              ? parseInt(linvoRow.prednisone_linvoCNum, 10)
              : null,
            SteroidNameTriamcinolone: linvoRow.triamcinolone_linvoC,
            SteroidDoseTriamcinolone: linvoRow.triamcinolone_linvoCNum
              ? parseInt(linvoRow.triamcinolone_linvoCNum, 10)
              : null,
            SteroidDoseNameUnknown: linvoRow.dose_name_linvoC_unknown,
            SteroidReasonTreatmentRecommendation:
              linvoRow.steroidReasonPretratment_linvoC,
            SteroidReasonPrimaryCrs: linvoRow.steroidReasonPrimarycrs_linvoC,
            SteroidReasonSecondaryCrs:
              linvoRow.steroidReasonSecondarycrs_linvoC,
            SteroidReasonSupportiveCrs:
              linvoRow.steroidReasonSupportivecrs_linvoC,
            SteroidReasonPrimaryNeuro:
              linvoRow.steroidReasonPrimaryneuro_linvoC,
            SteroidReasonSecondaryNeuro:
              linvoRow.steroidReasonSecondaryneuro_linvoC,
            SteroidReasonSupportiveNeuro:
              linvoRow.steroidReasonSupportiveneuro_linvoC,
            SteroidReasonUnk: linvoRow.steroidReasonUnk_linvoC,
          });
        }

        //39.3.C - Granulocytemedication (separate table, one row per bispecific cohort)
        const GranulocytemedicationList = [];
        if (isElrexvayliSelected && Q37ElrexDetails?.[0]) {
          const elrRow = Q37ElrexDetails[0];
          GranulocytemedicationList.push({
            Granulocytemedicationid: elrRow.granulocytemedicationidElrC || 0,
            Cohorttreatmentid: RRMMCohortTreatment.ELREXFIO,
            PrimaryUse: elrRow.prophylactic_use_elrC,
            SecondaryUse: elrRow.secondary_prophylactic_use_elrC,
            OtherUse: elrRow.oth_use_elrC,
            MedicationReasonUnk: elrRow.unknown_elrC === 99 ? true : null,
          });
        }
        if (isLinvoveySelected && Q37LinvoDetails?.[0]) {
          const linvoRow = Q37LinvoDetails[0];
          GranulocytemedicationList.push({
            Granulocytemedicationid:
              linvoRow.granulocytemedicationidLinvoC || 0,
            Cohorttreatmentid: RRMMCohortTreatment.Linvoseltamab,
            PrimaryUse: linvoRow.prophylactic_use_linvoC,
            SecondaryUse: linvoRow.secondary_prophylactic_use_linvoC,
            OtherUse: linvoRow.oth_use_linvoC,
            MedicationReasonUnk: linvoRow.unknown_linvoC === 99 ? true : null,
          });
        }

        const ELCharModel = {
          Patientid: pid,
          UserId: GetLoggedInUserID(),
          isValidated: validate,
          IsAdmin: IsAdmin(),
          IsNewEcrfA: isNewEcrfA,
          //36
          ElrexfioData: ElrexfioData,
          LinvoseltamabData: LinvoseltamabData,
          //36.1
          ElrexfioHospitalData: ElrexfioHospitalizationData,
          LinvoseltamabHospitalData: LinvoseltamabHospitalizationData,

          //37
          ELcharElrexfioModelList: Q37ElrDetail,
          ELcharLinvoseltamabModelList: Q37LinvoDetail,

          //39.2.C
          SteroidsmedicationModelList: SteroidsmedicationList,

          //39.3.C
          GranulocytemedicationModelList: GranulocytemedicationList,

          //38
          Q38ElrexfioData: q38ElrexfioData,
          Q38LinvoseltamabData: q38LinvoseltamabData,

          //38.1.A
          Q381AElrexfioData: Q381AElrexfioData,
          Q381ALinvoseltamabData: Q381ALinvoData,

          Q382AElrexfioData: Q382AElrexfioData,
          Q382ALinvoseltamabData: Q382ALinvoseltamabData,

          //39.1.A
          ElrexfioTocilizumabData: elrexfioQ391AData,
          LinvoseltamabTocilizumabData: linvoQ391AData,

          //Q40.1.B
          Q401aElrData: isElrexvayliSelected ? q401aElrData : [],
          Q401aLinvoData: isLinvoveySelected ? q401aLinvoData : [],

          //Q40.1.C
          Q401cElrData: isElrexvayliSelected ? q401cElrData : [],
          Q401cLinvoData: isLinvoveySelected ? q401cLinvoData : [],

          //Q40.2.C
          Q402cElrData: isElrexvayliSelected ? q402cElrData : [],
          Q402cLinvoData: isLinvoveySelected ? q402cLinvoData : [],

          //Q40.2.B
          Q402aElrData: isElrexvayliSelected ? q402aElrData : [],
          Q402aLinvoData: isLinvoveySelected ? q402aLinvoData : [],

          //Q40.3.B
          Q403aElrData: isElrexvayliSelected ? q403aElrData : [],
          Q403aLinvoData: isLinvoveySelected ? q403aLinvoData : [],
        };

        Object.values(ELCharModel).forEach((rows) => {
          if (Array.isArray(rows))
            rows.forEach((row) => {
              if (row && typeof row === "object")
                row.LineOfTherapy = lot.lineOfTherapy ?? null;
            });
        });
        return ELCharModel;
      }
    }
  };

  // validate() runs the full existing form validation for this LOT section.
  useImperativeHandle(ref, () => ({
    validate: () => {
      const isValid = ValidateForm();
      setShowTopErrMsg(!isValid);
      return isValid;
    },
    buildPayload: (validate) => {
      setShowTopErrMsg(false);
      return BuildLotPayload(validate);
    },
    reload: () => LoadData(),
    lot,
  }));

  const LoadData = async () => {
    try {
      if (pid > 0) {
        const response =
          await AdditionalTreatmentService.LoadELCharacteristicData(pid);
        if (response?.status === HTTPResponse.OK) {
          const data = ScopeResponseToLot(response.data, lot);

          const nextLotLookup = buildNextLotStartDatesLookup(
            data.cohortNextLotDatesList ?? [],
          );
          setCohortNextLotDatesList(nextLotLookup);

          setQ40PeriodEndDateList(
            buildQ40PeriodEndDateLookup(data.q40PeriodEndDateList ?? []),
          );

          const isNewEcrfAChecked = data.isNewEcrfA ?? false;

          setIsNewEcrfA(isNewEcrfAChecked);
          setIsNewEcrfB(data.isNewEcrfB ?? false);
          setIsNewEcrfC(data.isNewEcrfC ?? false);
          setElrvayliDateRRMM(
            data.rrmmElrexfioDate !== null
              ? data.rrmmElrexfioDate
              : data.rrmmElrexfioDate === null && data.endofFollowUpDate != null
                ? data.endofFollowUpDate
                : "[Bispecifics/CAR-T end of follow up]",
          );

          setLinvoveyDateRRMM(
            data.rrmmLinvoseltamabDate !== null
              ? data.rrmmLinvoseltamabDate
              : data.rrmmLinvoseltamabDate === null &&
                  data.endofFollowUpDate != null
                ? data.endofFollowUpDate
                : "[Bispecifics/CAR-T end of follow up]",
          );

          setElrexLOTStartDate(data.elrexfioLOTStartDate ?? "");
          setLinvoLOTStartDate(data.linvoseltamabLOTStartDate ?? "");

          setPatientDeathDate(data.patientDeathDate);
          //setPatientDeathDate(null);
          setElrNumberofAdministrator(data.elrNoOfAdministration);
          setLinvoNumberofAdministrator(data.linvoNoOfAdministration);

          setEndOfFollowUpDate(data.endofFollowUpDate);
          setIndexDateRRMM_elrHard(false);
          setIndexDateRRMM_linvoHard(false);
          setElrexfioLOTStartDate(data.elrexfioLOTStartDate ?? "");
          setLinvoseltamabLOTStartDate(data.linvoseltamabLOTStartDate ?? "");

          setBispecificQ26Data(data.bispecificQ26Data);
          setBispecificQ331AData(data.bispecificQ331AData);
          setBispecificQ31Q32Data(data.bispecificQ31Q32Data);
          setNdmmLotData(data.ndmmLotData);

          // setElrNumberofAdministratorMatching(false);
          // setLinvoNumberofAdministratorMatching(false);

          setIsElrLastAdminDateValid(true);
          setIsLinvoLastAdminDateValid(true);

          let cohortTreatmentModelListt = [];
          if (storedData) {
            const parsedData = JSON.parse(storedData);
            cohortTreatmentModelListt = parsedData.filter(
              (item) => item.ndmm === false && item.isEligible,
            );
          }

          let modelList = [];
          if (storedData) {
            const parseData = JSON.parse(storedData);
            modelList = parseData.filter(
              (item) => item.ndmm === true && item.isEligible,
            );
          }
          if (modelList.length > 0) {
            setNdmmAvailable(true);
          }
          if (cohortTreatmentModelListt.length > 0) {
            const elrexInitiationDate =
              cohortTreatmentModelListt?.find(
                (x) => x.cohortTreatmentID === RRMMCohortTreatment.ELREXFIO,
              )?.cohortIndexDate ?? null;

            const linvoInitiationDate =
              cohortTreatmentModelListt?.find(
                (x) =>
                  x.cohortTreatmentID === RRMMCohortTreatment.Linvoseltamab,
              )?.cohortIndexDate ?? null;

            setElrexInitiationDate(elrexInitiationDate);
            setLinvoInitiationDate(linvoInitiationDate);

            const isAbeOrCar = cohortTreatmentModelListt.some(
              (a) =>
                a.cohortTreatmentID === RRMMCohortTreatment.ABECMA ||
                a.cohortTreatmentID === RRMMCohortTreatment.CARVYKTI,
            );
            if (isAbeOrCar) {
              setRRMMAvailable(true);
            }
          }

          const elrCohort = cohortTreatmentModelListt.find(
            (item) => item.cohortTreatmentID === RRMMCohortTreatment.ELREXFIO,
          );
          let isTACSelected = false;

          if (
            elrCohort &&
            lot.cohortTreatmentId === RRMMCohortTreatment.ELREXFIO
          ) {
            isTACSelected = true;
            setElrexvayliSelected(true);
            setElrexIndexDate(data.elrexfioIndexDate);
            setElrexInitiationDate(data.elrexfioInitiationDate);
            setDoseStrength_Elr(data.elrexStrength);
          }

          const linvoCohort = cohortTreatmentModelListt.find(
            (item) =>
              item.cohortTreatmentID === RRMMCohortTreatment.Linvoseltamab,
          );
          let isLINVOSelected = false;
          if (
            linvoCohort &&
            lot.cohortTreatmentId === RRMMCohortTreatment.Linvoseltamab
          ) {
            isLINVOSelected = true;
            setLinvoveySelected(true);

            setLinvoIndexDate(data.linvoseltamabIndexDate);
            setLinvoInitiationDate(data.linvoseltamabInitiationDate);
            setDoseStrength_Linvo(data.linvoStrength);
          }

          const cohortTreatmentMap = new Map();
          let elrexfioData = [];

          if (data.elrexfioData && data.elrexfioData.length > 0) {
            elrexfioData = data.elrexfioData.map((item) => {
              return {
                cohorttreatmentid: item.cohorttreatmentid,

                elrexlinvodosagebridgeid:
                  item.elranatamablinvoseltamabdosagebridgeid,

                dose_administration_elr_dt: item.doseAdministrationDate,
                dose_administration_elr:
                  item.doseAdminidatrationDateNotReceived === true
                    ? 2
                    : item.doseAdministrationDateUnk === true
                      ? 99
                      : item.doseAdministrationDate
                        ? 1
                        : null,

                dosing_phase_elr: item.dosePhase,
                siteofcare_elr: item.siteOfCare,

                elr_dose_strength_rbl: item.doseStrengthmg
                  ? 1
                  : item.doseStrengthmgkg
                    ? 2
                    : item.doseStrengthmgm
                      ? 3
                      : item.doseStrengthUnk
                        ? 99
                        : null,
                elr_dose_strength_mg: item.doseStrengthmg
                  ? item.doseStrengthmg
                  : "",
                elr_dose_strength_mg_kg: item.doseStrengthmgkg
                  ? item.doseStrengthmgkg
                  : "",
                elr_dose_strength_mgm: item.doseStrengthmgm
                  ? item.doseStrengthmgm
                  : "",

                elr_dose_freq_rbl: item.doseFreqDosingPhase,
                elr_dose_freq_week:
                  item.doseFreqDosingPhase === 1
                    ? item.doseFreqDosingWeeks
                    : "",
                elr_dose_freq_onceweek:
                  item.doseFreqDosingPhase === 2
                    ? item.doseFreqDosingWeeks
                    : "",

                dosing_phase_oth_txt_elr: item.dosingPhaseOthTxt,

                dt_admission_elr_dt: item.inpatientAdmissionDate,

                dosing_phase_oth_txt_linvo: item.dosingPhaseOthTxt,

                dt_discharge_elr_dt: item.inpatientDischargeDate,

                Q36ElrexConditionMet:
                  (item.dosePhase === 1 ||
                    item.dosePhase === 2 ||
                    item.dosePhase === 3) &&
                  (item.siteOfCare === 1 || item.siteOfCare === 3)
                    ? true
                    : false,

                dose_administration_elrHard: false,
                dosing_phase_elrHard: false,
                dosing_phase_elrHard_othEmpty: false,
                siteofcare_elrHard: false,
                elr_dose_strength_mg_kgHard: false,
                elr_dose_weekly_strength_mg_kgHard: false,
                dt_admission_elrHard: false,
                dt_discharge_elrHard: false,
                elr_dose_strength_mg_kgRange: false,
                elr_dose_freq_hard_error: false,
                elr_dose_weekly_freq_hard_error: false,
                elr_dose_weekly_freq_RangeError: false,

                dose_administration_elrHard_dt: false,
                Invalid_dose_administration_elr_dt: false,
                dt_admission_elrHard_dt: false,
                dt_discharge_elrHard_dt: false,
                dt_admission_elrHard_dt_check: false,
                dt_discharge_elrHard_dt_check: false,
                // dt_elr_greater_prevCheck: false,
                dt_elr_date_out_of_bound: false,
                dose_administration_elr_dt_before_eof: false,
              };
            });

            // Positions 0-3 (Step-up doses 1-2, 1st treatment dose, 1st
            // subsequent treatment dose) have a position-implied dosing
            // phase that the UI always displays as selected, so the
            // underlying value must be kept in sync or it is left null
            // whenever it was never explicitly saved, causing a false
            // "please choose one of the provided options" error.
            elrexfioData.forEach((row, index) => {
              if (index < 4) {
                row.dosing_phase_elr = index + 1;
              }
            });

            setElrexvayliDetails(elrexfioData);
            setDt_elr_2L_3L_date_conditions(false);

            //Duplicate dose warning
            const setupstec = elrexfioData
              .map((r) => r.dosing_phase_elr)
              .filter((v) => v != null && v !== 4);
            const hasDuplicateSetupstTec =
              new Set(setupstec).size !== setupstec.length;
            setSetupElr_WarningHard(hasDuplicateSetupstTec);

            //Warning dependent on Q26
            const tecdata = elrexfioData.filter(
              (v) =>
                v.dosing_phase_elr > 1 &&
                v.dosing_phase_elr < 3 &&
                v.dose_administration_elr == 2,
            );
            const numOfAdmission = data.bispecificQ26Data.filter(
              (b) => b.cohortTreatmentId === RRMMCohortTreatment.ELREXFIO,
            );
            const showQ26DependentWarning =
              tecdata.length > 0 && numOfAdmission[0]?.numberOfAdmission >= 4;
            setSetupElrQ26_WarningHard(showQ26DependentWarning);

            //New Warnings
            const setups2 = elrexfioData.filter(
              (v) => v.dosing_phase_elr === 2,
            );
            const setups1stTreatment = elrexfioData.filter(
              (v) => v.dosing_phase_elr === 3,
            );
            const setups1stSubsTreatment = elrexfioData.filter(
              (v) => v.dosing_phase_elr === 4,
            );

            // Step-up Dose 2: Patient did not receive dose
            const setup2NotReceived = setups2.some(
              (v) => v.dose_administration_elr === 3,
            );
            // Step-up Dose 3: Patient did not receive dose
            const setup1stNotReceived = setups1stTreatment.some(
              (v) => v.dose_administration_elr === 3,
            );

            // Step-up Dose 1st: dose received if date is reported OR Date Unknown
            const setup1stReceived = setups1stTreatment.some(
              (v) =>
                v.dose_administration_elr_dt ||
                v.dose_administration_elr === 99,
            );

            // Step-up Dose 1st subs: dose received if date is reported OR Date Unknown
            const setup1stSubsReceived = setups1stSubsTreatment.some(
              (v) =>
                v.dose_administration_elr_dt ||
                v.dose_administration_elr === 99,
            );

            //If 1st treatment dose reported with date or date unknown, then “Patient did not receive dose” cannot be reported in Step-up Doses 2 or 3
            // Show warning when Dose 3 was received,
            // but Dose 2 was reported as "Patient did not receive dose"
            const showDose1stTreatmentWarning =
              setup1stReceived && setup2NotReceived;
            setSetupElrDose1st_WarningHard(
 showDose1stTreatmentWarning ? getFirstTreatmentWarningMessage({ step2: setup2NotReceived }) : "",
 );

            //If 1st treatment dose reported with date or date unknown, then “Patient did not receive dose” cannot be reported in Step-up Doses 2 or 3
            // Show warning when Dose 3 was received,
            // but Dose 2 was reported as "Patient did not receive dose"
            const showDose1stSubsTreatmentWarning =
              setup1stSubsReceived &&
              (setup2NotReceived || setup1stNotReceived);
            setSetupElrDose1stSubs_WarningHard(
 showDose1stSubsTreatmentWarning
 ? getFirstSubsWarningMessage({ step2: setup2NotReceived, step3: false, first: setup1stNotReceived })
 : "",
 );
          } else {
            const cohortTreatmentid = isTACSelected
              ? RRMMCohortTreatment.ELREXFIO //GetLocalStorageData("elrexvayliID")
              : 0;

            // Update ElrexvayliRow with cohorttreatmentid only
            ElrexvayliRow.cohorttreatmentid = cohortTreatmentid || 0;

            // Retain the remaining fields as is
            ElrexvayliRow.elranatamablinvoseltamabdosagebridgeid =
              ElrexvayliRow.elranatamablinvoseltamabdosagebridgeid || 0;
            ElrexvayliRow.dose_administration_elr =
              ElrexvayliRow.dose_administration_elr || null;
            ElrexvayliRow.dosing_phase_elr =
              ElrexvayliRow.dosing_phase_elr || null;
            ElrexvayliRow.siteofcare_elr = ElrexvayliRow.siteofcare_elr || null;

            ElrexvayliRow.elr_dose_strength_mg_kg =
              ElrexvayliRow.elr_dose_strength_mg_kg || null;
            ElrexvayliRow.elr_dose_strength_mg =
              ElrexvayliRow.elr_dose_strength_mg || null;
            ElrexvayliRow.elr_dose_strength_mgm =
              ElrexvayliRow.elr_dose_strength_mgm || null;
            // ElrexvayliRow.dt_discharge_elr =
            //   ElrexvayliRow.dt_discharge_elr || null;
            ElrexvayliRow.dose_administration_elr_dt =
              ElrexvayliRow.dose_administration_elr_dt || "";
            ElrexvayliRow.dt_admission_elr_dt =
              ElrexvayliRow.dt_admission_elr_dt || "";
            ElrexvayliRow.dt_discharge_elr_dt =
              ElrexvayliRow.dt_discharge_elr_dt || "";

            ElrexvayliRow.dose_administration_elrHard =
              ElrexvayliRow.dose_administration_elrHard || false;
            ElrexvayliRow.dosing_phase_elrHard =
              ElrexvayliRow.dosing_phase_elrHard || false;
            ElrexvayliRow.siteofcare_elrHard =
              ElrexvayliRow.siteofcare_elrHard || false;
            ElrexvayliRow.dt_admission_elrHard =
              ElrexvayliRow.dt_admission_elrHard || false;
            ElrexvayliRow.dt_discharge_elrHard =
              ElrexvayliRow.dt_discharge_elrHard || false;

            ElrexvayliRow.dosing_phase_elrHard_othEmpty = false;
            ElrexvayliRow.elr_dose_strength_mg_kgHard = false;
            ElrexvayliRow.elr_dose_weekly_strength_mg_kgHard = false;
            ElrexvayliRow.elr_dose_strength_mg_kgRange = false;
            ElrexvayliRow.elr_dose_freq_hard_error = false;
            ElrexvayliRow.elr_dose_weekly_freq_hard_error = false;
            ElrexvayliRow.elr_dose_weekly_freq_RangeError = false;
          }
          if (elrexfioData.length < 4) {
            while (elrexfioData.length < 4) {
              elrexfioData.push({
                ...ElrexvayliRow,
              });
            }

            // Assign values based on actual row index
            elrexfioData.forEach((row, index) => {
              row.dosing_phase_elr = index + 1;

              if (index === 0) {
                row.dose_administration_Elr_dt = elrexIndexDate;

                row.elr_dose_strength_rbl = doseStrength_Elr?.includes("/kg")
                  ? 2
                  : doseStrength_Elr?.includes("mg/m")
                    ? 3
                    : doseStrength_Elr?.includes("mg")
                      ? 1
                      : null;

                row.elr_dose_strength_mgm = doseStrength_Elr?.includes("mg/m")
                  ? doseStrength_Elr?.split(" ")[0]
                  : "";

                row.elr_dose_strength_mg_kg = doseStrength_Elr?.includes("/kg")
                  ? doseStrength_Elr?.split(" ")[0]
                  : "";

                row.elr_dose_strength_mg = doseStrength_Elr?.includes("mg")
                  ? doseStrength_Elr?.split(" ")[0]
                  : "";

                row.cohorttreatmentid = RRMMCohortTreatment.ELREXFIO;
              }
            });

            setElrexvayliDetails(elrexfioData);
          }

          //36.1 elrex start
          if (
            data.elrexfioHospitalData &&
            data.elrexfioHospitalData.length > 0
          ) {
            const elrHospData = data.elrexfioHospitalData.map((item) => {
              return {
                ...HospitalizationRow,
                elrexlinvohospitalizationbridgeid:
                  item.teclistamatalquetamabhospitalizationbridgeid,
                cohorttreatmentid: item.cohorttreatmentid,
                elrexlinvodosagebridgeid:
                  item.teclistamatalquetamabdosagebridgeid,
                //B
                dt_admission: item.hospitalizationAdmissionDateUnk
                  ? 99
                  : item.noHospitalizationDosage
                    ? 2
                    : item.hospitalizationAdmissionDate
                      ? 1
                      : null,
                dt_admission_dt: item.hospitalizationAdmissionDate,
                //C
                dt_discharge: item.hospitalizationDischargeUnk
                  ? 99
                  : item.patientStillHospitalized
                    ? 2
                    : item.hospitalizationDischargeDate
                      ? 1
                      : null,
                dt_discharge_dt: item.hospitalizationDischargeDate,
                //D
                hospReason1: item.hospitalizationReasonAdministration,
                hospReason2: item.hospitalizationReasonMonitorAe,
                hospReason3: item.hospitalizationReasonCrs,
                hospReason4: item.hospitalizationReasonIcans,
                hospReason5: item.hospitalizationReasonOtherae,
                hospReason6: item.hospitalizationReasonOtnernonae,
                hospReason7:
                  item.hospitalizationReasonAdministrationDaratumumab,
                hosp_reason_oth_txt: item.hospitalizationReasonOthernonaeTxt,
                hospReasonUnk: item.hospitalizationReasonUnknown,
              };
            });

            if (elrexfioData?.length > 0 && elrHospData?.length > 0) {
              updateQ361HospitalizationWarning(
                RRMMCohortTreatment.ELREXFIO,
                elrHospData,
                elrexfioData,
              );
            }

            setElrHospDetails(elrHospData);
          } else {
            const cohorttreatmentid = isTACSelected
              ? RRMMCohortTreatment.ELREXFIO //GetLocalStorageData("elrexvayliID")
              : 0;

            setElrHospDetails([
              {
                ...HospitalizationRow,
                cohorttreatmentid,
              },
            ]);
          }
          //end
          let linvoseltamabData = [];
          if (data.linvoseltamabData && data.linvoseltamabData.length > 0) {
            linvoseltamabData = data.linvoseltamabData.map((item) => {
              return {
                cohorttreatmentid: item.cohorttreatmentid,

                elrexlinvodosagebridgeid:
                  item.elranatamablinvoseltamabdosagebridgeid,

                dose_administration_linvo_dt: item.doseAdministrationDate,
                dose_administration_linvo:
                  item.doseAdminidatrationDateNotReceived === true
                    ? 2
                    : item.doseAdministrationDateUnk === true
                      ? 99
                      : item.doseAdministrationDate
                        ? 1
                        : null,

                dosing_phase_linvo: item.dosePhase,
                siteofcare_linvo: item.siteOfCare,
                dosing_phase_oth_txt_linvo: item.dosingPhaseOthTxt,
                linvo_dose_strength_rbl: item.doseStrengthmg
                  ? 1
                  : item.doseStrengthmgkg
                    ? 2
                    : item.doseStrengthmgm
                      ? 3
                      : item.doseStrengthUnk
                        ? 99
                        : null,
                linvo_dose_strength_mg: item.doseStrengthmg
                  ? item.doseStrengthmg
                  : "",
                linvo_dose_strength_mg_kg: item.doseStrengthmgkg
                  ? item.doseStrengthmgkg
                  : "",
                linvo_dose_strength_mgm: item.doseStrengthmgm
                  ? item.doseStrengthmgm
                  : "",

                linvo_dose_freq_rbl: item.doseFreqDosingPhase,
                linvo_dose_freq_week:
                  item.doseFreqDosingPhase === 1
                    ? item.doseFreqDosingWeeks
                    : "",
                linvo_dose_freq_onceweek:
                  item.doseFreqDosingPhase === 2
                    ? item.doseFreqDosingWeeks
                    : "",

                dt_admission_linvo_dt: item.inpatientAdmissionDate,

                dt_discharge_linvo_dt: item.inpatientDischargeDate,

                dose_administration_linvoHard: false,
                Invalid_dose_administration_linvo_dt: false,
                dosing_phase_linvoHard: false,
                siteofcare_linvoHard: false,
                dt_admission_linvoHard: false,
                dt_discharge_linvoHard: false,
                dosing_phase_linvoHard_othEmpty: false,
                linvo_dose_strength_mg_kgHard: false,
                linvo_dose_strength_mg_kgRange: false,
                linvo_dose_weekly_strength_mg_kgHard: false,
                linvo_dose_freq_hard_error: false,
                linvo_dose_weekly_freq_hard_error: false,
                linvo_dose_weekly_freq_RangeError: false,

                dose_administration_linvoHard_dt: false,
                dt_admission_linvoHard_dt: false,
                dt_discharge_linvoHard_dt: false,

                dt_admission_linvoHard_dt_check: false,
                dt_discharge_linvoHard_dt_check: false,

                // dt_linvo_greater_prevCheck: false,
                dt_linvo_date_out_of_bound: false,
              };
            });

            // Positions 0-4 (Step-up doses 1-3, 1st treatment dose, 1st
            // subsequent treatment dose) have a position-implied dosing
            // phase that the UI always displays as selected, so the
            // underlying value must be kept in sync or it is left null
            // whenever it was never explicitly saved, causing a false
            // "please choose one of the provided options" error.
            linvoseltamabData.forEach((row, index) => {
              if (index < 5) {
                row.dosing_phase_linvo = index + 1;
              }
            });

            setLinvoveyDetails(linvoseltamabData);
            setDt_lino_2L_3L_date_conditions(false);

            //Duplicate dose warning
            const setupstal = linvoseltamabData
              .map((r) => r.dosing_phase_linvo)
              .filter((v) => v != null && v !== 5);
            const hasDuplicateSetupsTal =
              new Set(setupstal).size !== setupstal.length;
            setSetupLinvo_WarningHard(hasDuplicateSetupsTal);

            //Warning dependent on Q26
            const taldata = linvoseltamabData.filter(
              (v) =>
                v.dosing_phase_linvo > 1 &&
                v.dosing_phase_linvo < 4 &&
                v.dose_administration_linvo == 2,
            );
            const numOfAdmission = data.bispecificQ26Data.filter(
              (b) => b.cohortTreatmentId === RRMMCohortTreatment.TALVEY,
            );
            const showQ26DependentWarning =
              taldata.length > 0 && numOfAdmission[0]?.numberOfAdmission >= 4;
            setSetupLinvoQ26_WarningHard(showQ26DependentWarning);

            //New Warnings
            const setups2 = linvoseltamabData.filter(
              (v) => v.dosing_phase_linvo === 2,
            );
            const setups3 = linvoseltamabData.filter(
              (v) => v.dosing_phase_linvo === 3,
            );
            const setups1stTreatment = linvoseltamabData.filter(
              (v) => v.dosing_phase_linvo === 4,
            );
            const setups1stSubsTreatment = linvoseltamabData.filter(
              (v) => v.dosing_phase_linvo === 5,
            );

            // Step-up Dose 2: Patient did not receive dose
            const setup2NotReceived = setups2.some(
              (v) => v.dose_administration_linvo === 2,
            );
            // Step-up Dose 3: Patient did not receive dose
            const setup3NotReceived = setups3.some(
              (v) => v.dose_administration_linvo === 2,
            );
            // Step-up Dose 3: Patient did not receive dose
            const setup1stNotReceived = setups1stTreatment.some(
              (v) => v.dose_administration_linvo === 2,
            );

            // Step-up Dose 3: dose received if date is reported OR Date Unknown
            const setup3Received = setups3.some(
              (v) =>
                v.dose_administration_linvo_dt ||
                v.dose_administration_linvo === 99,
            );
            // Step-up Dose 1st: dose received if date is reported OR Date Unknown
            const setup1stReceived = setups1stTreatment.some(
              (v) =>
                v.dose_administration_linvo_dt ||
                v.dose_administration_linvo === 99,
            );

            // Step-up Dose 1st subs: dose received if date is reported OR Date Unknown
            const setup1stSubsReceived = setups1stSubsTreatment.some(
              (v) =>
                v.dose_administration_linvo_dt ||
                v.dose_administration_linvo === 99,
            );

            // Case 1: If Step-up Dose 3 reported with date or date unknown, then “Patient did not receive dose” cannot be reported in Step-up Dose 2
            // Show warning when Dose 3 was received,
            // but Dose 2 was reported as "Patient did not receive dose"
            const showDose3Warning = setup3Received && setup2NotReceived;
            setSetupLinvoDose3_WarningHard(
 showDose3Warning ? getDose2WarningMessage({ step2: setup2NotReceived }) : "",
 );

            // Case 2: If 1st treatment dose reported with date or date unknown, then “Patient did not receive dose” cannot be reported in Step-up Doses 2 or 3
            // Show warning when Dose 3 was received,
            // but Dose 2 was reported as "Patient did not receive dose"
            const showDose1stTreatmentWarning =
              setup1stReceived && setup2NotReceived;
            setSetupLinvoDose1st_WarningHard(
 showDose1stTreatmentWarning ? getFirstTreatmentWarningMessage({ step2: setup2NotReceived }) : "",
 );

            // Case 3: If 1st treatment dose reported with date or date unknown, then “Patient did not receive dose” cannot be reported in Step-up Doses 2 or 3
            // Show warning when Dose 3 was received,
            // but Dose 2 was reported as "Patient did not receive dose"
            const showDose1stSubsTreatmentWarning =
              setup1stSubsReceived &&
              (setup2NotReceived || setup3NotReceived || setup1stNotReceived);
            setSetupLinvoDose1stSubs_WarningHard(
 showDose1stSubsTreatmentWarning
 ? getFirstSubsWarningMessage({ step2: setup2NotReceived, step3: setup3NotReceived, first: setup1stNotReceived })
 : "",
 );
          } else {
            const cohortTreatmentid = isLINVOSelected
              ? RRMMCohortTreatment.Linvoseltamab //GetLocalStorageData("linvoveyID")
              : 0;

            // Update ElrexvayliRow with cohorttreatmentid only
            LinvoveyRow.cohorttreatmentid = cohortTreatmentid || 0;

            // Retain the remaining fields as is
            LinvoveyRow.elranatamablinvoseltamabdosagebridgeid =
              LinvoveyRow.elranatamablinvoseltamabdosagebridgeid || 0;
            LinvoveyRow.dose_administration_linvo =
              LinvoveyRow.dose_administration_linvo || null;
            LinvoveyRow.dosing_phase_linvo =
              LinvoveyRow.dosing_phase_linvo || null;
            LinvoveyRow.siteofcare_linvo = LinvoveyRow.siteofcare_linvo || null;
            LinvoveyRow.dose_administration_linvo_dt =
              LinvoveyRow.dose_administration_linvo_dt || "";
            LinvoveyRow.dt_admission_linvo_dt =
              LinvoveyRow.dt_admission_linvo_dt || "";
            LinvoveyRow.dt_discharge_linvo_dt =
              LinvoveyRow.dt_discharge_linvo_dt || "";
            LinvoveyRow.dose_administration_linvoHard =
              LinvoveyRow.dose_administration_linvoHard || false;
            LinvoveyRow.dosing_phase_linvoHard =
              LinvoveyRow.dosing_phase_linvoHard || false;
            LinvoveyRow.siteofcare_linvoHard =
              LinvoveyRow.siteofcare_linvoHard || false;
            LinvoveyRow.dt_admission_linvoHard =
              LinvoveyRow.dt_admission_linvoHard || false;
            LinvoveyRow.dt_discharge_linvoHard =
              LinvoveyRow.dt_discharge_linvoHard || false;
            LinvoveyRow.Invalid_dose_administration_linvo_dt =
              LinvoveyRow.Invalid_dose_administration_linvo_dt || false;

            LinvoveyRow.dosing_phase_linvoHard_othEmpty = false;
            LinvoveyRow.linvo_dose_strength_mg_kg =
              LinvoveyRow.linvo_dose_strength_mg_kg || null;
            LinvoveyRow.linvo_dose_strength_mg =
              LinvoveyRow.linvo_dose_strength_mg || null;
            LinvoveyRow.linvo_dose_strength_mgm =
              LinvoveyRow.linvo_dose_strength_mgm || null;
            LinvoveyRow.linvo_dose_strength_mg_kgHard = false;
            LinvoveyRow.linvo_dose_strength_mg_kgRange = false;
            LinvoveyRow.linvo_dose_weekly_strength_mg_kgHard = false;
            LinvoveyRow.linvo_dose_freq_hard_error = false;
            LinvoveyRow.linvo_dose_weekly_freq_hard_error = false;
            LinvoveyRow.linvo_dose_weekly_freq_RangeError = false;

            //  //Q38
          }

          if (linvoseltamabData.length < 5) {
            // Always show at least 5 rows
            while (linvoseltamabData.length < 5) {
              linvoseltamabData.push({
                ...LinvoveyRow,
              });
            }

            // Assign values based on actual row index
            linvoseltamabData.forEach((row, index) => {
              row.dosing_phase_linvo = index + 1;

              if (index === 0) {
                row.dose_administration_linvo_dt = linvoIndexDate;

                row.linvo_dose_strength_rbl = doseStrength_Linvo?.includes(
                  "/kg",
                )
                  ? 2
                  : doseStrength_Linvo?.includes("mg/m")
                    ? 3
                    : doseStrength_Linvo?.includes("mg")
                      ? 1
                      : null;

                row.linvo_dose_strength_mgm = doseStrength_Linvo?.includes(
                  "mg/m",
                )
                  ? doseStrength_Linvo?.split(" ")[0]
                  : "";

                row.linvo_dose_strength_mg_kg = doseStrength_Linvo?.includes(
                  "/kg",
                )
                  ? doseStrength_Linvo?.split(" ")[0]
                  : "";

                row.linvo_dose_strength_mg = doseStrength_Linvo?.includes("mg")
                  ? doseStrength_Linvo?.split(" ")[0]
                  : "";

                row.cohorttreatmentid = RRMMCohortTreatment.Linvoseltamab;
              }
            });

            setLinvoveyDetails(linvoseltamabData);
          }

          //36.1 Linvo start
          if (
            data.linvoseltamabHospitalData &&
            data.linvoseltamabHospitalData.length > 0
          ) {
            const linvoHospData = data.linvoseltamabHospitalData.map((item) => {
              return {
                ...HospitalizationRow,
                elrexlinvohospitalizationbridgeid:
                  item.teclistamatalquetamabhospitalizationbridgeid,
                cohorttreatmentid: item.cohorttreatmentid,
                elrexlinvodosagebridgeid:
                  item.teclistamatalquetamabdosagebridgeid,
                //B
                dt_admission: item.hospitalizationAdmissionDateUnk
                  ? 99
                  : item.noHospitalizationDosage
                    ? 2
                    : item.hospitalizationAdmissionDate
                      ? 1
                      : null,
                dt_admission_dt: item.hospitalizationAdmissionDate,
                //C
                dt_discharge: item.hospitalizationDischargeUnk
                  ? 99
                  : item.patientStillHospitalized
                    ? 2
                    : item.hospitalizationDischargeDate
                      ? 1
                      : null,
                dt_discharge_dt: item.hospitalizationDischargeDate,
                //D
                hospReason1: item.hospitalizationReasonAdministration,
                hospReason2: item.hospitalizationReasonMonitorAe,
                hospReason3: item.hospitalizationReasonCrs,
                hospReason4: item.hospitalizationReasonIcans,
                hospReason5: item.hospitalizationReasonOtherae,
                hospReason6: item.hospitalizationReasonOtnernonae,
                hospReason7:
                  item.hospitalizationReasonAdministrationDaratumumab,
                hosp_reason_oth_txt: item.hospitalizationReasonOthernonaeTxt,
                hospReasonUnk: item.hospitalizationReasonUnknown,
              };
            });

            if (linvoseltamabData?.length > 0 && linvoHospData?.length > 0) {
              updateQ361HospitalizationWarning(
                RRMMCohortTreatment.Linvoseltamab,
                linvoHospData,
                linvoseltamabData,
              );
            }

            setLinvoHospDetails(linvoHospData);
          } else {
            const cohorttreatmentid = isLINVOSelected
              ? RRMMCohortTreatment.Linvoseltamab //GetLocalStorageData("linvoveyID")
              : 0;
            setLinvoHospDetails([
              {
                ...HospitalizationRow,
                cohorttreatmentid,
              },
            ]);
          }
          //end

          //37 LoadData -Elrexfio

          if (
            data.eLcharElrexfioModelList &&
            data.eLcharElrexfioModelList.length > 0
          ) {
            const Q37ElrexDetails = data.eLcharElrexfioModelList.map((item) => {
              const cohortTreatment =
                cohortTreatmentMap.get(item.cohorttreatmentid) || {};

              return {
                cohorttreatmentid: item.cohorttreatmentid,

                elrexlinvocharacteristicsid: item.teclistamabcharacteristicsid,

                // Q37 retired
                // Q37ElrexvayliRd: item.dosefrequencyreduced,

                // q37_Elr_Dose_week_frquency_txt:
                //   item.treatmentInitialDoseFrequencyText,
                // q37_Elr_Dose_week_frquencyRd:
                //   item.treatmentInitialDoseFrequency,

                // q37ElrDosageRd: item.treatmentInitialDosage,
                // q37ElrDosageOtherTxt: item.treatmentInitialDosageOthtext,
                // q37ElrDosageRdEmpty: false,
                // q37ElrDosageOtherTxtEmpty: false,

                // showDateRedFreqElr:
                //   item.dosefrequencyreduced === 1 ? true : false,

                Q40dosingfreqRd: item.ivigDosageFrequency,
                dosing_freq_oth_txt: item.ivigDosageFrequencyOthTxt,

                Q40IgILevelRd: item.lastIgglevel,

                Q40init_dt: item.ivigInitiationDate,
                Q40init_dt_Rd:
                  item.ivigInitiationDateUnk === true
                    ? 99
                    : item.ivigInitiationDate
                      ? 1
                      : null,
                primaryProf: item.medicationReasonPrimaryProphylactic,
                secondaryProf: item.medicationReasonSecondaryProphylactic,
                otherReson: item.medicationReasonOth,

                //40.C
                ivigInitRd_elrC: item.ivigInitiationDateRdC ?? null,
                ivigInitDt_elrC: item.ivigInitiationDateC ?? "",
                ivigDosingType_elrC: item.ivigDosingTypeC ?? null,
                ivigDosingFreqRd_elrC: item.ivigDosageFrequencyRdC ?? null,
                ivigDosingFreqTimesTxt_elrC:
                  item.ivigDosageFrequencyTimesTxtC ?? "",
                ivigDosingFreqWeeksTxt_elrC:
                  item.ivigDosageFrequencyWeeksTxtC ?? "",
                ivigDosingFreqOtherTxt_elrC:
                  item.ivigDosageFrequencyOthTxtC ?? "",
                ivigReasonPrimary_elrC: item.ivigReasonPrimaryC ?? false,
                ivigReasonSecondary_elrC: item.ivigReasonSecondaryC ?? false,
                ivigReasonOther_elrC: item.ivigReasonOthC ?? false,
                ivigReasonOtherTxt_elrC: item.ivigReasonOthTxtC ?? "",
                ivigReasonUnk_elrC: item.ivigReasonUnkC ?? false,
                ivigDoseStrengthRd_elrC: item.ivigDoseStrengthRdC ?? null,
                ivigDoseStrengthGTxt_elrC:
                  item.ivigDoseStrengthGTxtC != null
                    ? String(item.ivigDoseStrengthGTxtC)
                    : "",
                ivigDoseStrengthMgKgTxt_elrC:
                  item.ivigDoseStrengthMgKgTxtC != null
                    ? String(item.ivigDoseStrengthMgKgTxtC)
                    : "",
                ivigDoseStrengthOtherTxt_elrC:
                  item.ivigDoseStrengthOthTxtC ?? "",
                ivigBodyWeightRd_elrC: item.ivigBodyWeightRdC ?? null,
                ivigBodyWeightKgTxt_elrC:
                  item.ivigBodyWeightKgTxtC != null
                    ? String(item.ivigBodyWeightKgTxtC)
                    : "",
                ivigBodyWeightLbsTxt_elrC:
                  item.ivigBodyWeightLbsTxtC != null
                    ? String(item.ivigBodyWeightLbsTxtC)
                    : "",
                ivigBodyWeightEmptyHard_elrC: false,
                ivigBodyWeightRangeInvalid_elrC: false,
                ivigLastIgg_elrC: item.lastIgglevelBeforeFirstIvigC ?? null,
                ivigInitRdHard_elrC: false,
                ivigInitDtHard_elrC: false,
                ivigInitDtInvalid_elrC: false,
                ivigInitDtBeforeEof_elrC: false,
                ivigInitDtFullChainInvalid_elrC: false,
                ivigDosingTypeEmptyHard_elrC: false,
                ivigDosingFreqEmptyHard_elrC: false,
                ivigDosingFreqTxtEmptyHard_elrC: false,
                ivigDosingFreqRangeInvalid_elrC: false,
                ivigReasonEmptyHard_elrC: false,
                ivigReasonOtherTxtEmptyHard_elrC: false,
                ivigDoseStrengthEmptyHard_elrC: false,
                ivigDoseStrengthRangeInvalid_elrC: false,
                ivigDoseStrengthMgKgRangeInvalid_elrC: false,
                ivigDoseStrengthOtherTxtEmptyHard_elrC: false,
                ivigLastIggEmptyHard_elrC: false,

                //39

                tocilizumab_elr: item.medicationTocilizumab,
                Granulocyte: item.medicationGcsf,
                noneoftheabove_elr: item.medicationNone === true ? 99 : null,
                SteroidsElr: item.medicationSteroids,

                //39.C
                tocilizumab_elrC: item.medicationTocilizumabC,
                steroids_elrC: item.medicationSteroidsC,
                granulocyte_elrC: item.medicationGcsfC,
                noneoftheabove_elrC: item.medicationNoneC === true ? 99 : null,
                tociliAdmsDateRD_elrC: item.tociliAdmsDateUnkC
                  ? 99
                  : item.tociliAdmsDateC
                    ? 1
                    : null,
                tociliAdmsDate_elrC: item.tociliAdmsDateC ?? "",
                tociliDoseRd_elrC: item.tociliDoseC ?? null,
                tociliDoseMgkg_elrC:
                  item.tociliDoseMgkgUnitC != null
                    ? String(item.tociliDoseMgkgUnitC)
                    : "",
                tociliDoseOther_elrC: item.tociliDoseOtherC ?? "",
                tociliReasonPretratment_elrC: item.tociliReasonPretratmentC,
                tociliReasonPrimarycrs_elrC: item.tociliReasonPrimarycrsC,
                tociliReasonSecondarycrs_elrC: item.tociliReasonSecondarycrsC,
                tociliReasonSupportivecrs_elrC: item.tociliReasonSupportivecrsC,
                tociliReasonPrimaryneuro_elrC: item.tociliReasonPrimaryneuroC,
                tociliReasonSecondaryneuro_elrC:
                  item.tociliReasonSecondaryneuroC,
                tociliReasonSupportiveneuro_elrC:
                  item.tociliReasonSupportiveneuroC,
                tociliReasonUnk_elrC: item.tociliReasonUnkC,

                //39 - 1.1
                // Q39.1 retired
                // cytokin_elr: item.dosageusageReasonProphylacticCrs,
                // secondary_prophylactic_crs_elr:
                //   item.dosageusageReasonSecondaryProphylacticCrs,
                // crs_elr: item.dosagusageReasonTreatmentsupportiveCrs,
                // neurotoxicity_elr:
                //   item.dosagusageReasonProphylacticneurotoxicity,
                // secondary_neurotoxicity_elr:
                //   item.dosagusageReasonSecondaryProphylacticNeurotoxicity,
                // tmt_neurotoxicity_elr:
                //   item.dosagusageReasonTreatmentsupportiveNeurotoxicity,
                // tmt_elr_unknown: item.dosagusageReasonTreatmentUnknown,
                // Q39elrReasonHard: false,

                //39 - 1.3
                prophylactic_use_elr: item.gcsfUsageReasonProphylactic,
                secondary_prophylactic_use_elr:
                  item.gcsfUsageReasonSecondaryProphylactic,
                oth_use_elr: item.gcsfUsageReasonOth,
                unknown_elr: item.gcsfUsageReasonUnk === true ? 99 : null,

                // Steroids Usage Reasons 39.2
                steroid_pre_tmt_elr:
                  item.steroidUsageReasonTreatmentRecommendation,

                cytokin_elr_steroids: item.steroidsUsageReasonProphylacticCrs,
                secondary_prophylactic_elr_steroids:
                  item.steroidsUsageReasonSecondaryProphylacticCrs,
                crs_elr_steroids:
                  item.steroidsUsageReasonTreatmentsupportiveCrs,
                neurotoxicity_elr_steroids:
                  item.steroidsUsageReasonProphylacticneurotoxicity,
                secondary_neurotoxicity_elr_steroids:
                  item.steroidsUsageReasonSecondaryProphylacticNeurologic,
                tmt_neurotoxicity_elr_steroids:
                  item.steroidsUsageReasonTreatmentsupportiveNeurotoxicity,
                tmt_steroids_elr_unknown: item.steroidUsageReasonUnknown,

                betamethasone_elr: item.steroidNameBetamethasone,
                betamethasone_elrNum: item.steroidDoseBetamethasone,

                dexamethasone_elr: item.steroidNameDexamethasone,
                dexamethasone_elrNum: item.steroidDoseDexamethasone,

                hydrocortisone_elr: item.steroidNameHydrocortisone,
                hydrocortisone_elrNum: item.steroidDoseHydrocortisone,

                methylprednisolone: item.steroidNameMethylprednisolone,
                methylprednisoloneNum: item.steroidDoseMethylprednisolone,

                prednisolone_elr: item.steroidNamePrednisolone,
                prednisolone_elrNum: item.steroidDosePrednisolone,

                prednisone_elr: item.steroidNamePrednisone,
                prednisone_elrNum: item.steroidDosePrednisone,

                triamcinolone_elr: item.steroidNameTriamcinolone,
                triamcinolone_elrNum: item.steroidDoseTriamcinolone,

                dose_name_elr_unknown: item.steroidDoseNameUnknown,

                name_dose_steroidsHardEmpty: false,

                steroids_elr_txtEmpty: false,
                Q39ElrEmptyHard: false,

                //39.C
                Q39CEmptyHard: false,
                Q391CReasonEmptyHard: false,
                tociliAdmsDateEmpty_elrC: false,
                tociliAdmsDateInvalid_elrC: false,
                tociliAdmsDateRangeInvalid_elrC: false,
                tociliDoseEmptyHard_elrC: false,
                tociliDoseMgkgEmptyHard_elrC: false,
                tociliDoseOtherEmptyHard_elrC: false,

                // Q37 retired
                // q37ElrexvayliEmpty: false,
                Q40DosingFreqEmptyHard: false,
                Q40DosingFreqTxtEmptyHard: false,
                Q40IgGEmptyHard: false,
                Q40Init_dtHard: false,
                InvalidQ40init_dt: false,
                init_dt_before_eof: false,
                init_dt_full_chain_invalid: false,
                Q40Init_RdHard: false,
                Q40ReasonEmptyHard: false,
                // q37_Elr_Dose_week_frquency_txt_Empty: false,
                // q37_Elr_Dose_week_freq_range: false,
                // q37_Elr_Dose_week_frquencyRd_Empty: false,
                // q37SubsequentDoseConflict: false,
                Q39elrGranduloEmptyHard: false,

                Q39elrSteroidsEmptyHard: false,

                //39.2.C (merged in below from steroidsmedicationModelList)
                steroidsmedicationidElrC: 0,
                betamethasone_elrC: false,
                betamethasone_elrCNum: "",
                dexamethasone_elrC: false,
                dexamethasone_elrCNum: "",
                hydrocortisone_elrC: false,
                hydrocortisone_elrCNum: "",
                methylprednisolone_elrC: false,
                methylprednisolone_elrCNum: "",
                prednisolone_elrC: false,
                prednisolone_elrCNum: "",
                prednisone_elrC: false,
                prednisone_elrCNum: "",
                triamcinolone_elrC: false,
                triamcinolone_elrCNum: "",
                dose_name_elrC_unknown: false,
                name_dose_steroidsHardEmpty_elrC: false,
                steroids_elrC_txtEmpty: false,
                steroidsDoseRangeInvalid_elrC: false,
                steroidReasonPretratment_elrC: false,
                steroidReasonPrimarycrs_elrC: false,
                steroidReasonSecondarycrs_elrC: false,
                steroidReasonSupportivecrs_elrC: false,
                steroidReasonPrimaryneuro_elrC: false,
                steroidReasonSecondaryneuro_elrC: false,
                steroidReasonSupportiveneuro_elrC: false,
                steroidReasonUnk_elrC: false,
                Q392CReasonEmptyHard: false,

                //39.3.C (merged in below from granulocytemedicationModelList)
                granulocytemedicationidElrC: 0,
                prophylactic_use_elrC: false,
                secondary_prophylactic_use_elrC: false,
                oth_use_elrC: false,
                unknown_elrC: null,
                Q393CReasonEmptyHard: false,
              };
            });

            //39.3.C - merge in Granulocytemedication row for Elrexfio (separate table)
            const elrGranuloRow = data.granulocytemedicationModelList?.find(
              (g) => g.cohorttreatmentid === RRMMCohortTreatment.ELREXFIO,
            );
            if (elrGranuloRow && Q37ElrexDetails[0]) {
              Q37ElrexDetails[0] = {
                ...Q37ElrexDetails[0],
                granulocytemedicationidElrC:
                  elrGranuloRow.granulocytemedicationid,
                prophylactic_use_elrC: elrGranuloRow.primaryUse,
                secondary_prophylactic_use_elrC: elrGranuloRow.secondaryUse,
                oth_use_elrC: elrGranuloRow.otherUse,
                unknown_elrC:
                  elrGranuloRow.medicationReasonUnk === true ? 99 : null,
              };
            }

            //39.2.C - merge in Steroidsmedication row for Elrexfio (separate table)
            const elrSteroidsRow = data.steroidsmedicationModelList?.find(
              (s) => s.cohorttreatmentid === RRMMCohortTreatment.ELREXFIO,
            );
            if (elrSteroidsRow && Q37ElrexDetails[0]) {
              Q37ElrexDetails[0] = {
                ...Q37ElrexDetails[0],
                steroidsmedicationidElrC: elrSteroidsRow.steroidsmedicationid,
                betamethasone_elrC: elrSteroidsRow.steroidNameBetamethasone,
                betamethasone_elrCNum:
                  elrSteroidsRow.steroidDoseBetamethasone != null
                    ? String(elrSteroidsRow.steroidDoseBetamethasone)
                    : "",
                dexamethasone_elrC: elrSteroidsRow.steroidNameDexamethasone,
                dexamethasone_elrCNum:
                  elrSteroidsRow.steroidDoseDexamethasone != null
                    ? String(elrSteroidsRow.steroidDoseDexamethasone)
                    : "",
                hydrocortisone_elrC: elrSteroidsRow.steroidNameHydrocortisone,
                hydrocortisone_elrCNum:
                  elrSteroidsRow.steroidDoseHydrocortisone != null
                    ? String(elrSteroidsRow.steroidDoseHydrocortisone)
                    : "",
                methylprednisolone_elrC:
                  elrSteroidsRow.steroidNameMethylprednisolone,
                methylprednisolone_elrCNum:
                  elrSteroidsRow.steroidDoseMethylprednisolone != null
                    ? String(elrSteroidsRow.steroidDoseMethylprednisolone)
                    : "",
                prednisolone_elrC: elrSteroidsRow.steroidNamePrednisolone,
                prednisolone_elrCNum:
                  elrSteroidsRow.steroidDosePrednisolone != null
                    ? String(elrSteroidsRow.steroidDosePrednisolone)
                    : "",
                prednisone_elrC: elrSteroidsRow.steroidNamePrednisone,
                prednisone_elrCNum:
                  elrSteroidsRow.steroidDosePrednisone != null
                    ? String(elrSteroidsRow.steroidDosePrednisone)
                    : "",
                triamcinolone_elrC: elrSteroidsRow.steroidNameTriamcinolone,
                triamcinolone_elrCNum:
                  elrSteroidsRow.steroidDoseTriamcinolone != null
                    ? String(elrSteroidsRow.steroidDoseTriamcinolone)
                    : "",
                dose_name_elrC_unknown: elrSteroidsRow.steroidDoseNameUnknown,
                steroidReasonPretratment_elrC:
                  elrSteroidsRow.steroidReasonTreatmentRecommendation,
                steroidReasonPrimarycrs_elrC:
                  elrSteroidsRow.steroidReasonPrimaryCrs,
                steroidReasonSecondarycrs_elrC:
                  elrSteroidsRow.steroidReasonSecondaryCrs,
                steroidReasonSupportivecrs_elrC:
                  elrSteroidsRow.steroidReasonSupportiveCrs,
                steroidReasonPrimaryneuro_elrC:
                  elrSteroidsRow.steroidReasonPrimaryNeuro,
                steroidReasonSecondaryneuro_elrC:
                  elrSteroidsRow.steroidReasonSecondaryNeuro,
                steroidReasonSupportiveneuro_elrC:
                  elrSteroidsRow.steroidReasonSupportiveNeuro,
                steroidReasonUnk_elrC: elrSteroidsRow.steroidReasonUnk,
              };
            }

            setQ37ElrexDetails(Q37ElrexDetails);
          }

          //37 LoadData -Linvoseltamab

          if (
            data.eLcharLinvoseltamabModelList &&
            data.eLcharLinvoseltamabModelList.length > 0
          ) {
            const Q37LinvoDetails = data.eLcharLinvoseltamabModelList.map(
              (item) => {
                return {
                  cohorttreatmentid: item.cohorttreatmentid,
                  elrexlinvocharacteristicsid:
                    item.teclistamabcharacteristicsid,

                  // Q37 retired
                  // Q37LinvoveyRd: item.dosefrequencyreduced,

                  // showDateRedFreqLinvo:
                  //   item.dosefrequencyreduced === 1 ? true : false,

                  //37 Linvoseltamab
                  // q37_Linvo_Dose_week_frquency_txt:
                  //   item.treatmentInitialDoseFrequencyText,
                  // q37_Linvo_Dose_week_frquencyRd:
                  //   item.treatmentInitialDoseFrequency,
                  // q37_Linvo_Dosage_Rd: item.treatmentInitialDosage,
                  // q37_Linvo_Dosage_OtherTxt: item.treatmentInitialDosageOthtext,

                  //39 Linvoseltamab data

                  // Medications
                  tocilizumab_linvo: item.medicationTocilizumab,
                  Steroids: item.medicationSteroids,
                  granulocyte_linvo: item.medicationGcsf ?? false,
                  noneoftheabove_linvo:
                    item.medicationNone === true ? 99 : null,

                  //39.C
                  tocilizumab_linvoC: item.medicationTocilizumabC,
                  steroids_linvoC: item.medicationSteroidsC,
                  granulocyte_linvoC: item.medicationGcsfC,
                  noneoftheabove_linvoC:
                    item.medicationNoneC === true ? 99 : null,
                  tociliAdmsDateRD_linvoC: item.tociliAdmsDateUnkC
                    ? 99
                    : item.tociliAdmsDateC
                      ? 1
                      : null,
                  tociliAdmsDate_linvoC: item.tociliAdmsDateC ?? "",
                  tociliDoseRd_linvoC: item.tociliDoseC ?? null,
                  tociliDoseMgkg_linvoC:
                    item.tociliDoseMgkgUnitC != null
                      ? String(item.tociliDoseMgkgUnitC)
                      : "",
                  tociliDoseOther_linvoC: item.tociliDoseOtherC ?? "",
                  tociliReasonPretratment_linvoC: item.tociliReasonPretratmentC,
                  tociliReasonPrimarycrs_linvoC: item.tociliReasonPrimarycrsC,
                  tociliReasonSecondarycrs_linvoC:
                    item.tociliReasonSecondarycrsC,
                  tociliReasonSupportivecrs_linvoC:
                    item.tociliReasonSupportivecrsC,
                  tociliReasonPrimaryneuro_linvoC:
                    item.tociliReasonPrimaryneuroC,
                  tociliReasonSecondaryneuro_linvoC:
                    item.tociliReasonSecondaryneuroC,
                  tociliReasonSupportiveneuro_linvoC:
                    item.tociliReasonSupportiveneuroC,
                  tociliReasonUnk_linvoC: item.tociliReasonUnkC,

                  // Dosage Usage Reasons -- 39.1
                  // Q39.1 retired
                  // cytokin_linvo: item.dosageusageReasonProphylacticCrs,
                  // secondary_prophylactic_crs_linvo:
                  //   item.dosageusageReasonSecondaryProphylacticCrs,
                  // crs_linvo: item.dosagusageReasonTreatmentsupportiveCrs,
                  // neurotoxicity_linvo:
                  //   item.dosagusageReasonProphylacticneurotoxicity,
                  // secondary_neurotoxicity_linvo:
                  //   item.dosagusageReasonSecondaryProphylacticNeurotoxicity,
                  // tmt_neurotoxicity_linvo:
                  //   item.dosagusageReasonTreatmentsupportiveNeurotoxicity,
                  // tmt_linvoseltamab_unknown:
                  //   item.dosagusageReasonTreatmentUnknown,

                  //39 - 1.3
                  prophylactic_use_linvo: item.gcsfUsageReasonProphylactic,
                  secondary_prophylactic_use_linvo:
                    item.gcsfUsageReasonSecondaryProphylactic,
                  oth_use_linvo: item.gcsfUsageReasonOth,
                  unknown_linvo: item.gcsfUsageReasonUnk === true ? 99 : null,

                  // Steroids Usage Reasons 39.2
                  cytokin_linvo_steroids:
                    item.steroidsUsageReasonProphylacticCrs,
                  secondary_prophylactic_linvo_steroids:
                    item.steroidsUsageReasonSecondaryProphylacticCrs,
                  crs_linvo_steroids:
                    item.steroidsUsageReasonTreatmentsupportiveCrs,
                  neurotoxicity_linvo_steroids:
                    item.steroidsUsageReasonProphylacticneurotoxicity,
                  secondary_neurotoxicity_linvo_steroids:
                    item.steroidsUsageReasonSecondaryProphylacticNeurologic,
                  tmt_neurotoxicity_linvo_steroids:
                    item.steroidsUsageReasonTreatmentsupportiveNeurotoxicity,
                  tmt_linvo_unknown: item.steroidUsageReasonUnknown,
                  steroid_pre_tmt_linvo:
                    item.steroidUsageReasonTreatmentRecommendation,

                  betamethasone_linvo: item.steroidNameBetamethasone,
                  betamethasone_linvoNum: item.steroidDoseBetamethasone,

                  dexamethasone_linvo: item.steroidNameDexamethasone,
                  dexamethasone_linvoNum: item.steroidDoseDexamethasone,

                  hydrocortisone_linvo: item.steroidNameHydrocortisone,
                  hydrocortisone_linvoNum: item.steroidDoseHydrocortisone,

                  methylprednisolone_linvo: item.steroidNameMethylprednisolone,
                  methylprednisolone_linvoNum:
                    item.steroidDoseMethylprednisolone,

                  prednisolone_linvo: item.steroidNamePrednisolone,
                  prednisolone_linvoNum: item.steroidDosePrednisolone,

                  prednisone_linvo: item.steroidNamePrednisone,
                  prednisone_linvoNum: item.steroidDosePrednisone,

                  triamcinolone_linvo: item.steroidNameTriamcinolone,
                  triamcinolone_linvoNum: item.steroidDoseTriamcinolone,

                  dose_name_linvo_unknown: item.steroidDoseNameUnknown,

                  name_dose_steroids_linvoHardEmpty: false,
                  steroids_linvo_txtEmpty: false,

                  // Q37 retired
                  // q37LinvoveyEmpty: false,
                  Q39LinvoEmptyHard: false,
                  // Q39.1 retired
                  // Q39linvoReasonHard: false,
                  Q39linvoSteroidsEmptyHard: false,

                  //39.C
                  Q39CEmptyHard: false,
                  Q391CReasonEmptyHard: false,
                  tociliAdmsDateEmpty_linvoC: false,
                  tociliAdmsDateInvalid_linvoC: false,
                  tociliAdmsDateRangeInvalid_linvoC: false,
                  tociliDoseEmptyHard_linvoC: false,
                  tociliDoseMgkgEmptyHard_linvoC: false,
                  tociliDoseOtherEmptyHard_linvoC: false,
                  // q37_Linvo_Dose_week_frquencyRd_Empty: false,
                  // q37_Linvo_Dose_week_frquency_txt_Empty: false,
                  // q37_Linvo_Dose_week_freq_range: false,
                  // q37LinvoSubsequentDoseConflict: false,

                  // q37_Linvo_Dosage_Rd_Empty: false,
                  // q37_Linvo_Dosage_OtherTxt_Empty: false,
                  Q39linvoGranduloEmptyHard: false,

                  //Q40
                  q40DosingFreqRd_linvo: item.ivigDosageFrequency,
                  dosing_Freq_Oth_Txt_linvo: item.ivigDosageFrequencyOthTxt,
                  q40IgILevelRd_linvo: item.lastIgglevel,
                  q40Init_Dt_linvo: item.ivigInitiationDate,
                  q40Init_Dt_Rd_linvo:
                    item.ivigInitiationDateUnk === true
                      ? 99
                      : item.ivigInitiationDate
                        ? 1
                        : null,
                  primaryProf_linvo: item.medicationReasonPrimaryProphylactic,
                  secondaryProf_linvo:
                    item.medicationReasonSecondaryProphylactic,
                  otherReson_linvo: item.medicationReasonOth,
                  Q40LinvoDosingFreqEmptyHard: false,
                  Q40LinvoDosingFreqTxtEmptyHard: false,
                  Q40LinvoIgGEmptyHard: false,
                  Q40LinvoInit_dtHard: false,
                  invalidQ40Init_dt_linvo: false,
                  init_dt_before_eof_linvo: false,
                  init_dt_full_chain_invalid_linvo: false,
                  Q40LinvoInit_RdHard: false,
                  Q40LinvoReasonEmptyHard: false,

                  //40.C
                  ivigInitRd_linvoC: item.ivigInitiationDateRdC ?? null,
                  ivigInitDt_linvoC: item.ivigInitiationDateC ?? "",
                  ivigDosingType_linvoC: item.ivigDosingTypeC ?? null,
                  ivigDosingFreqRd_linvoC: item.ivigDosageFrequencyRdC ?? null,
                  ivigDosingFreqTimesTxt_linvoC:
                    item.ivigDosageFrequencyTimesTxtC ?? "",
                  ivigDosingFreqWeeksTxt_linvoC:
                    item.ivigDosageFrequencyWeeksTxtC ?? "",
                  ivigDosingFreqOtherTxt_linvoC:
                    item.ivigDosageFrequencyOthTxtC ?? "",
                  ivigReasonPrimary_linvoC: item.ivigReasonPrimaryC ?? false,
                  ivigReasonSecondary_linvoC:
                    item.ivigReasonSecondaryC ?? false,
                  ivigReasonOther_linvoC: item.ivigReasonOthC ?? false,
                  ivigReasonOtherTxt_linvoC: item.ivigReasonOthTxtC ?? "",
                  ivigReasonUnk_linvoC: item.ivigReasonUnkC ?? false,
                  ivigDoseStrengthRd_linvoC: item.ivigDoseStrengthRdC ?? null,
                  ivigDoseStrengthGTxt_linvoC:
                    item.ivigDoseStrengthGTxtC != null
                      ? String(item.ivigDoseStrengthGTxtC)
                      : "",
                  ivigDoseStrengthMgKgTxt_linvoC:
                    item.ivigDoseStrengthMgKgTxtC != null
                      ? String(item.ivigDoseStrengthMgKgTxtC)
                      : "",
                  ivigDoseStrengthOtherTxt_linvoC:
                    item.ivigDoseStrengthOthTxtC ?? "",
                  ivigBodyWeightRd_linvoC: item.ivigBodyWeightRdC ?? null,
                  ivigBodyWeightKgTxt_linvoC:
                    item.ivigBodyWeightKgTxtC != null
                      ? String(item.ivigBodyWeightKgTxtC)
                      : "",
                  ivigBodyWeightLbsTxt_linvoC:
                    item.ivigBodyWeightLbsTxtC != null
                      ? String(item.ivigBodyWeightLbsTxtC)
                      : "",
                  ivigBodyWeightEmptyHard_linvoC: false,
                  ivigBodyWeightRangeInvalid_linvoC: false,
                  ivigLastIgg_linvoC: item.lastIgglevelBeforeFirstIvigC ?? null,
                  ivigInitRdHard_linvoC: false,
                  ivigInitDtHard_linvoC: false,
                  ivigInitDtInvalid_linvoC: false,
                  ivigInitDtBeforeEof_linvoC: false,
                  ivigInitDtFullChainInvalid_linvoC: false,
                  ivigDosingTypeEmptyHard_linvoC: false,
                  ivigDosingFreqEmptyHard_linvoC: false,
                  ivigDosingFreqTxtEmptyHard_linvoC: false,
                  ivigDosingFreqRangeInvalid_linvoC: false,
                  ivigReasonEmptyHard_linvoC: false,
                  ivigReasonOtherTxtEmptyHard_linvoC: false,
                  ivigDoseStrengthEmptyHard_linvoC: false,
                  ivigDoseStrengthRangeInvalid_linvoC: false,
                  ivigDoseStrengthMgKgRangeInvalid_linvoC: false,
                  ivigDoseStrengthOtherTxtEmptyHard_linvoC: false,
                  ivigLastIggEmptyHard_linvoC: false,

                  //39.2.C (merged in below from steroidsmedicationModelList)
                  steroidsmedicationidLinvoC: 0,
                  betamethasone_linvoC: false,
                  betamethasone_linvoCNum: "",
                  dexamethasone_linvoC: false,
                  dexamethasone_linvoCNum: "",
                  hydrocortisone_linvoC: false,
                  hydrocortisone_linvoCNum: "",
                  methylprednisolone_linvoC: false,
                  methylprednisolone_linvoCNum: "",
                  prednisolone_linvoC: false,
                  prednisolone_linvoCNum: "",
                  prednisone_linvoC: false,
                  prednisone_linvoCNum: "",
                  triamcinolone_linvoC: false,
                  triamcinolone_linvoCNum: "",
                  dose_name_linvoC_unknown: false,
                  name_dose_steroidsHardEmpty_linvoC: false,
                  steroids_linvoC_txtEmpty: false,
                  steroidsDoseRangeInvalid_linvoC: false,
                  steroidReasonPretratment_linvoC: false,
                  steroidReasonPrimarycrs_linvoC: false,
                  steroidReasonSecondarycrs_linvoC: false,
                  steroidReasonSupportivecrs_linvoC: false,
                  steroidReasonPrimaryneuro_linvoC: false,
                  steroidReasonSecondaryneuro_linvoC: false,
                  steroidReasonSupportiveneuro_linvoC: false,
                  steroidReasonUnk_linvoC: false,
                  Q392CReasonEmptyHard: false,

                  //39.3.C (merged in below from granulocytemedicationModelList)
                  granulocytemedicationidLinvoC: 0,
                  prophylactic_use_linvoC: false,
                  secondary_prophylactic_use_linvoC: false,
                  oth_use_linvoC: false,
                  unknown_linvoC: null,
                  Q393CReasonEmptyHard: false,
                };
              },
            );

            //39.3.C - merge in Granulocytemedication row for Linvoseltamab (separate table)
            const linvoGranuloRow = data.granulocytemedicationModelList?.find(
              (g) => g.cohorttreatmentid === RRMMCohortTreatment.Linvoseltamab,
            );
            if (linvoGranuloRow && Q37LinvoDetails[0]) {
              Q37LinvoDetails[0] = {
                ...Q37LinvoDetails[0],
                granulocytemedicationidLinvoC:
                  linvoGranuloRow.granulocytemedicationid,
                prophylactic_use_linvoC: linvoGranuloRow.primaryUse,
                secondary_prophylactic_use_linvoC: linvoGranuloRow.secondaryUse,
                oth_use_linvoC: linvoGranuloRow.otherUse,
                unknown_linvoC:
                  linvoGranuloRow.medicationReasonUnk === true ? 99 : null,
              };
            }

            //39.2.C - merge in Steroidsmedication row for Linvoseltamab (separate table)
            const linvoSteroidsRow = data.steroidsmedicationModelList?.find(
              (s) => s.cohorttreatmentid === RRMMCohortTreatment.Linvoseltamab,
            );
            if (linvoSteroidsRow && Q37LinvoDetails[0]) {
              Q37LinvoDetails[0] = {
                ...Q37LinvoDetails[0],
                steroidsmedicationidLinvoC:
                  linvoSteroidsRow.steroidsmedicationid,
                betamethasone_linvoC: linvoSteroidsRow.steroidNameBetamethasone,
                betamethasone_linvoCNum:
                  linvoSteroidsRow.steroidDoseBetamethasone != null
                    ? String(linvoSteroidsRow.steroidDoseBetamethasone)
                    : "",
                dexamethasone_linvoC: linvoSteroidsRow.steroidNameDexamethasone,
                dexamethasone_linvoCNum:
                  linvoSteroidsRow.steroidDoseDexamethasone != null
                    ? String(linvoSteroidsRow.steroidDoseDexamethasone)
                    : "",
                hydrocortisone_linvoC:
                  linvoSteroidsRow.steroidNameHydrocortisone,
                hydrocortisone_linvoCNum:
                  linvoSteroidsRow.steroidDoseHydrocortisone != null
                    ? String(linvoSteroidsRow.steroidDoseHydrocortisone)
                    : "",
                methylprednisolone_linvoC:
                  linvoSteroidsRow.steroidNameMethylprednisolone,
                methylprednisolone_linvoCNum:
                  linvoSteroidsRow.steroidDoseMethylprednisolone != null
                    ? String(linvoSteroidsRow.steroidDoseMethylprednisolone)
                    : "",
                prednisolone_linvoC: linvoSteroidsRow.steroidNamePrednisolone,
                prednisolone_linvoCNum:
                  linvoSteroidsRow.steroidDosePrednisolone != null
                    ? String(linvoSteroidsRow.steroidDosePrednisolone)
                    : "",
                prednisone_linvoC: linvoSteroidsRow.steroidNamePrednisone,
                prednisone_linvoCNum:
                  linvoSteroidsRow.steroidDosePrednisone != null
                    ? String(linvoSteroidsRow.steroidDosePrednisone)
                    : "",
                triamcinolone_linvoC: linvoSteroidsRow.steroidNameTriamcinolone,
                triamcinolone_linvoCNum:
                  linvoSteroidsRow.steroidDoseTriamcinolone != null
                    ? String(linvoSteroidsRow.steroidDoseTriamcinolone)
                    : "",
                dose_name_linvoC_unknown:
                  linvoSteroidsRow.steroidDoseNameUnknown,
                steroidReasonPretratment_linvoC:
                  linvoSteroidsRow.steroidReasonTreatmentRecommendation,
                steroidReasonPrimarycrs_linvoC:
                  linvoSteroidsRow.steroidReasonPrimaryCrs,
                steroidReasonSecondarycrs_linvoC:
                  linvoSteroidsRow.steroidReasonSecondaryCrs,
                steroidReasonSupportivecrs_linvoC:
                  linvoSteroidsRow.steroidReasonSupportiveCrs,
                steroidReasonPrimaryneuro_linvoC:
                  linvoSteroidsRow.steroidReasonPrimaryNeuro,
                steroidReasonSecondaryneuro_linvoC:
                  linvoSteroidsRow.steroidReasonSecondaryNeuro,
                steroidReasonSupportiveneuro_linvoC:
                  linvoSteroidsRow.steroidReasonSupportiveNeuro,
                steroidReasonUnk_linvoC: linvoSteroidsRow.steroidReasonUnk,
              };
            }

            setQ37LinvoDetails(Q37LinvoDetails);
          }

          // Q38
          // Process LinvoseltamabData
          if (
            data.q38LinvoseltamabData &&
            data.q38LinvoseltamabData.length > 0
          ) {
            const Q38LinvoDetails = data.q38LinvoseltamabData.map((item) => {
              const cohortTreatment =
                cohortTreatmentMap.get(item.cohorttreatmentid) || {};

              return {
                cohorttreatmentid: item.cohorttreatmentid,

                elrexlinvodosefrequencybridgeid:
                  item.teclistamatalquetamabdosefrequencybridgeid,

                Q38LinvoveyRd: item.dosefreqReductionDate
                  ? 1
                  : item.dosefreqReductionDateUnk === true
                    ? 99
                    : null,
                dt_dosefreq_linvo_dt: item.dosefreqReductionDate,

                Q38LinvoEmptyHard: false,
                Q38LinvoDateEmpty: false,
                Q38LinvoDateInvalid: false,
                InvalidQ38Linvoveydate: false,
                q38_Linvo_Dose_week_frquencyRd_Empty: false,
                q38_Linvo_Dose_week_frquency_txt_Empty: false,
                q38LinvoDoseFrequencyConflict: false,

                q38_Linvo_Dosage_Rd_Empty: false,
                q38_Linvo_Dosage_OtherTxt_Empty: false,

                q38_Linvo_Dose_week_frquency_txt:
                  item.treatmentInitialDoseFrequencyText,

                q38_Linvo_Dose_week_frquencyRd:
                  item.treatmentInitialDoseFrequency,

                q38_Linvo_Dosage_OtherTxt: item.treatmentInitialDosageOthtext,
                q38_Linvo_Dosage_Rd: item.treatmentInitialDosage,
              };
            });

            setQ38LinvoDetails(Q38LinvoDetails);
          } else {
            const cohortTreatmentid = isLINVOSelected
              ? RRMMCohortTreatment.Linvoseltamab //GetLocalStorageData("linvoveyID")
              : 0;

            // Update ElrexvayliRow with cohorttreatmentid only
            Q38LinvoRow.cohorttreatmentid = cohortTreatmentid || 0;

            // Retain the remaining fields as is
            Q38LinvoRow.elrexlinvodosefrequencybridgeid =
              Q38LinvoRow.teclistamatalquetamabdosefrequencybridgeid || 0;

            Q38LinvoRow.Q38LinvoveyRd = Q38LinvoRow.Q38LinvoveyRd || null;
            Q38LinvoRow.dt_dosefreq_linvo_dt =
              Q38LinvoRow.dt_dosefreq_linvo_dt || "";
          }

          //38
          // Process Elrexfio Data
          if (data.q38ElrexfioData && data.q38ElrexfioData.length > 0) {
            const Q38ElrexDetails = data.q38ElrexfioData.map((item) => {
              const cohortTreatment =
                cohortTreatmentMap.get(item.cohorttreatmentid) || {};

              return {
                cohorttreatmentid: item.cohorttreatmentid,

                elrexlinvodosagebridgeid:
                  item.teclistamatalquetamabdosefrequencybridgeid,

                Q38ElrexvayliRd: item.dosefreqReductionDate
                  ? 1
                  : item.dosefreqReductionDateUnk === true
                    ? 99
                    : null,
                dt_dosefreq_elr_dt: item.dosefreqReductionDate,

                Q38ElrexEmptyHard: false,
                Q38DateEmpty: false,
                Q38ElrexDateInvalid: false,
                Invalid_dt_dosefreq_elr_dt: false,

                q38_Elr_Dose_week_frquencyRd_Empty: false,
                q38_Elr_Dose_week_frquency_txt_Empty: false,
                q38ElrDoseFrequencyConflict: false,

                q38_Elr_Dose_week_frquency_txt:
                  item.treatmentInitialDoseFrequencyText,
                q38_Elr_Dose_week_frquencyRd:
                  item.treatmentInitialDoseFrequency,
              };
            });

            setQ38ElrexDetails(Q38ElrexDetails);
          } else {
            const cohortTreatmentid = isTACSelected
              ? RRMMCohortTreatment.ELREXFIO //GetLocalStorageData("elrexvayliID")
              : 0;

            // Update ElrexvayliRow with cohorttreatmentid only
            Q38ElrexRow.cohorttreatmentid = cohortTreatmentid || 0;

            // Retain the remaining fields as is
            Q38ElrexRow.elrexlinvodosefrequencybridgeid =
              Q38ElrexRow.teclistamatalquetamabdosefrequencybridgeid || 0;

            Q38ElrexRow.Q38ElrexvayliRd = Q38ElrexRow.Q38ElrexvayliRd || null;
            Q38ElrexRow.dt_dosefreq_elr_dt =
              Q38ElrexRow.dt_dosefreq_elr_dt || "";
          }

          // Q38.1.A ELREXFIO - Load data
          if (data.q381AElrexfioData && data.q381AElrexfioData.length > 0) {
            const mappedElrex381A = data.q381AElrexfioData.map((item) => {
              const filteredReasonList381AElrex = FilterRetiredReasons(
                item.reasonList || [],
              );
              return {
                teclistamatalquetamabdosefrequencybridgeid:
                  item.teclistamatalquetamabdosefrequencyinfobridgeid,
                cohorttreatmentid: item.cohorttreatmentid,

                // Column B
                changeType: item.changeType,
                changeTypeDoseFrequency: item.changeTypeDoseFrequency,
                changeTypeDoseStrength: item.changeTypeDoseStrength,

                reasonUnknown: item.bispecifictreatmentdiscontinuationreasonUnk,

                // ✅ Column C - Direct from backend (singular)
                selectedReasons:
                  filteredReasonList381AElrex
                    ?.filter((r) => r.isSelected)
                    .map((r) => r.bispecifictreatmentdiscontinuationreasonid) ||
                  [],

                selectedAEReasons:
                  filteredReasonList381AElrex
                    ?.find(
                      (r) => r.bispecifictreatmentdiscontinuationreasonid === 2,
                    )
                    ?.aeSubReasons?.filter((ae) => ae.isSelected)
                    .map(
                      (ae) => ae.bispecifictreatmentdiscontinuationreasonaeid,
                    ) || [],
                neurologicAEReasonList: item?.neurologicAEReasonList ?? [],

                reasonOtherText: item.reasonOtherText || "",
                // reasonUnknown:
                //   item.reasonList?.some(
                //     (r) =>
                //       r.bispecifictreatmentdiscontinuationreasonid === 99 &&
                //       r.isSelected
                //   ) || false,

                reasonList: filteredReasonList381AElrex,

                // ✅ Column D - Pattern BEFORE (split single unit field)
                patterBeforeChangeDoseFrequency:
                  item.patternBeforeChangeDoseFrequency,

                // For frequency: split based on radio selection
                patterBeforeChangeDoseFrequencyTimesPerWeek:
                  item.patternBeforeChangeDoseFrequency === 1
                    ? item.patternBeforeChangeDoseFrequencyWeek?.toString() ||
                      ""
                    : "",
                patterBeforeChangeDoseFrequencyWeek:
                  item.patternBeforeChangeDoseFrequency === 2
                    ? item.patternBeforeChangeDoseFrequencyWeek?.toString() ||
                      ""
                    : "",

                // For strength: split single unit based on radio selection
                patterBeforeChangeDoseStrength:
                  item.patternBeforeChangeDoseStrength,
                patterBeforeChangeDoseStrengthMg:
                  item.patternBeforeChangeDoseStrength === 1
                    ? item.patternBeforeChangeDoseStrengthUnit?.toString() || ""
                    : "",
                patterBeforeChangeDoseStrengthMgKg:
                  item.patternBeforeChangeDoseStrength === 2
                    ? item.patternBeforeChangeDoseStrengthUnit?.toString() || ""
                    : "",
                patterBeforeChangeDoseStrengthMgM2:
                  item.patternBeforeChangeDoseStrength === 3
                    ? item.patternBeforeChangeDoseStrengthUnit?.toString() || ""
                    : "",

                // ✅ Column E - Pattern AFTER (split single unit field)
                patterAfterChangeDoseFrequency:
                  item.patternAfterChangeDoseFrequency,

                // For frequency: split based on radio selection
                patterAfterChangeDoseFrequencyTimesPerWeek:
                  item.patternAfterChangeDoseFrequency === 1
                    ? item.patternAfterChangeDoseFrequencyWeek?.toString() || ""
                    : "",
                patterAfterChangeDoseFrequencyWeek:
                  item.patternAfterChangeDoseFrequency === 2
                    ? item.patternAfterChangeDoseFrequencyWeek?.toString() || ""
                    : "",

                // For strength: split single unit based on radio selection
                patterAfterChangeDoseStrength:
                  item.patternAfterChangeDoseStrength,
                patterAfterChangeDoseStrengthMg:
                  item.patternAfterChangeDoseStrength === 1
                    ? item.patternAfterChangeDoseStrengthUnit?.toString() || ""
                    : "",
                patterAfterChangeDoseStrengthMgKg:
                  item.patternAfterChangeDoseStrength === 2
                    ? item.patternAfterChangeDoseStrengthUnit?.toString() || ""
                    : "",
                patterAfterChangeDoseStrengthMgM2:
                  item.patternAfterChangeDoseStrength === 3
                    ? item.patternAfterChangeDoseStrengthUnit?.toString() || ""
                    : "",

                // Column F
                dateChangeRd:
                  item.dateChangeUnk === true ? 99 : item.dateChange ? 1 : null,
                dateChange: item.dateChange || "",

                // Validation flags
                changeTypeEmpty: false,
                changeTypeDoseFrequencyEmpty: false,
                changeTypeDoseStrengthEmpty: false,
                reasonEmpty: false,
                deathDtWarning: false,
                reasonOtherTextEmpty: false,
                patternBeforeFrequencyEmpty: false,
                patternBeforeFrequencyRangeInvalid: false,
                patternBeforeStrengthEmpty: false,
                patternAfterFrequencyEmpty: false,
                patternAfterFrequencyRangeInvalid: false,
                patternAfterStrengthEmpty: false,
                patternAfterStrengthRangeInvalid: false,
                dateChangeRdEmpty: false,
                dateChangeEmpty: false,
                invalidDateChange: false,
                dateChangeOutOfBound: false,
                dateChangeBeforeEOF: false,
                frequencyIncreaseConflict: false,
                frequencyDecreaseConflict: false,
                strengthIncreaseConflict: false,
                strengthDecreaseConflict: false,
                patternBeforeUnknownWarning:
                  item.patternBeforeChangeDoseStrength === 99 ||
                  item.patternBeforeChangeDoseFrequency === 99,
                patternAfterUnknownWarning:
                  item.patternAfterChangeDoseStrength === 99 ||
                  item.patternAfterChangeDoseFrequency === 99,
                aeSubReasonEmpty: false,
                neuroReasonEmpty: false,
                neuroOtherTextEmpty: false,
              };
            });

            setQ381AElrexDetails(mappedElrex381A);
          } else {
            const staticReasonList = FilterRetiredReasons(
              data.q381AElrexfioData?.[0]?.reasonList || [],
            );
            // ✅ For new row, fetch template reasonList from backend
            setQ381AElrexDetails([
              {
                ...Q381AElrexRow,
                cohorttreatmentid: isElrexvayliSelected
                  ? RRMMCohortTreatment.ELREXFIO
                  : 0,
                reasonList: staticReasonList,
              },
            ]);
          }

          // Q38.1.A LINVOSELTAMAB - Load data
          if (
            data.q381ALinvoseltamabData &&
            data.q381ALinvoseltamabData.length > 0
          ) {
            const mappedLinvo381A = data.q381ALinvoseltamabData.map((item) => {
              const filteredReasonList381ALinvo = FilterRetiredReasons(
                item.reasonList || [],
              );
              return {
                teclistamatalquetamabdosefrequencybridgeid:
                  item.teclistamatalquetamabdosefrequencyinfobridgeid,
                cohorttreatmentid: item.cohorttreatmentid,

                // Column B
                changeType: item.changeType,
                changeTypeDoseFrequency: item.changeTypeDoseFrequency,
                changeTypeDoseStrength: item.changeTypeDoseStrength,
                reasonUnknown: item.bispecifictreatmentdiscontinuationreasonUnk,

                // ✅ Column C - Direct from backend (singular)
                selectedReasons:
                  filteredReasonList381ALinvo
                    ?.filter((r) => r.isSelected)
                    .map((r) => r.bispecifictreatmentdiscontinuationreasonid) ||
                  [],

                selectedAEReasons:
                  filteredReasonList381ALinvo
                    ?.find(
                      (r) => r.bispecifictreatmentdiscontinuationreasonid === 2,
                    )
                    ?.aeSubReasons?.filter((ae) => ae.isSelected)
                    .map(
                      (ae) => ae.bispecifictreatmentdiscontinuationreasonaeid,
                    ) || [],
                neurologicAEReasonList: item?.neurologicAEReasonList ?? [],

                reasonOtherText: item.reasonOtherText || "",

                reasonList: filteredReasonList381ALinvo,

                // ✅ Column D - Pattern BEFORE (split single unit field)
                patterBeforeChangeDoseFrequency:
                  item.patternBeforeChangeDoseFrequency,

                patterBeforeChangeDoseFrequencyTimesPerWeek:
                  item.patternBeforeChangeDoseFrequency === 1
                    ? item.patternBeforeChangeDoseFrequencyWeek?.toString() ||
                      ""
                    : "",
                patterBeforeChangeDoseFrequencyWeek:
                  item.patternBeforeChangeDoseFrequency === 2
                    ? item.patternBeforeChangeDoseFrequencyWeek?.toString() ||
                      ""
                    : "",

                patterBeforeChangeDoseStrength:
                  item.patternBeforeChangeDoseStrength,
                patterBeforeChangeDoseStrengthMg:
                  item.patternBeforeChangeDoseStrength === 1
                    ? item.patternBeforeChangeDoseStrengthUnit?.toString() || ""
                    : "",
                patterBeforeChangeDoseStrengthMgKg:
                  item.patternBeforeChangeDoseStrength === 2
                    ? item.patternBeforeChangeDoseStrengthUnit?.toString() || ""
                    : "",
                patterBeforeChangeDoseStrengthMgM2:
                  item.patternBeforeChangeDoseStrength === 3
                    ? item.patternBeforeChangeDoseStrengthUnit?.toString() || ""
                    : "",

                // ✅ Column E - Pattern AFTER (split single unit field)
                patterAfterChangeDoseFrequency:
                  item.patternAfterChangeDoseFrequency,

                patterAfterChangeDoseFrequencyTimesPerWeek:
                  item.patternAfterChangeDoseFrequency === 1
                    ? item.patternAfterChangeDoseFrequencyWeek?.toString() || ""
                    : "",
                patterAfterChangeDoseFrequencyWeek:
                  item.patternAfterChangeDoseFrequency === 2
                    ? item.patternAfterChangeDoseFrequencyWeek?.toString() || ""
                    : "",

                patterAfterChangeDoseStrength:
                  item.patternAfterChangeDoseStrength,
                patterAfterChangeDoseStrengthMg:
                  item.patternAfterChangeDoseStrength === 1
                    ? item.patternAfterChangeDoseStrengthUnit?.toString() || ""
                    : "",
                patterAfterChangeDoseStrengthMgKg:
                  item.patternAfterChangeDoseStrength === 2
                    ? item.patternAfterChangeDoseStrengthUnit?.toString() || ""
                    : "",
                patterAfterChangeDoseStrengthMgM2:
                  item.patternAfterChangeDoseStrength === 3
                    ? item.patternAfterChangeDoseStrengthUnit?.toString() || ""
                    : "",

                // Column F
                dateChangeRd:
                  item.dateChangeUnk === true ? 99 : item.dateChange ? 1 : null,
                dateChange: item.dateChange || "",

                // Validation flags (all false initially)
                changeTypeEmpty: false,
                changeTypeDoseFrequencyEmpty: false,
                changeTypeDoseStrengthEmpty: false,
                reasonEmpty: false,
                deathDtWarning: false,
                reasonOtherTextEmpty: false,
                patternBeforeFrequencyEmpty: false,
                patternBeforeFrequencyRangeInvalid: false,
                patternBeforeStrengthEmpty: false,
                patternBeforeFrequencyRangeInvalid: false,
                patternAfterFrequencyEmpty: false,
                patternAfterFrequencyRangeInvalid: false,
                patternAfterStrengthEmpty: false,
                patternAfterStrengthRangeInvalid: false,
                dateChangeRdEmpty: false,
                dateChangeEmpty: false,
                invalidDateChange: false,
                dateChangeOutOfBound: false,
                dateChangeBeforeEOF: false,
                frequencyIncreaseConflict: false,
                frequencyDecreaseConflict: false,
                strengthIncreaseConflict: false,
                strengthDecreaseConflict: false,
                patternBeforeUnknownWarning:
                  item.patternBeforeChangeDoseStrength === 99 ||
                  item.patternBeforeChangeDoseFrequency === 99,
                patternAfterUnknownWarning:
                  item.patternAfterChangeDoseStrength === 99 ||
                  item.patternAfterChangeDoseFrequency === 99,
                aeSubReasonEmpty: false,
                neuroReasonEmpty: false,
                neuroOtherTextEmpty: false,
              };
            });

            setQ381ALinvoDetails(mappedLinvo381A);
          } else {
            const staticReasonList = FilterRetiredReasons(
              data.q381ALinvoseltamabData?.[0]?.reasonList || [],
            );

            // For new row, fetch template reasonList from backend
            setQ381ALinvoDetails([
              {
                ...Q381ALinvoRow,
                cohorttreatmentid: isLinvoveySelected
                  ? RRMMCohortTreatment.Linvoseltamab
                  : 0,
                reasonList: staticReasonList,
              },
            ]);
          }

          //           // Q38.2.A ELREXFIO - Load data
          if (data.q382AElrexfioData && data.q382AElrexfioData.length > 0) {
            const mappedElrex382A = data.q382AElrexfioData.map((item) => {
              const filteredReasonList382AElrex = FilterRetiredReasons(
                item.reasonList || [],
              );
              return {
                teclistamatalquetamabdosefrequencyinfobridgeid:
                  item.teclistamatalquetamabdosedelaybridgeid,
                cohorttreatmentid: item.cohorttreatmentid,

                changeType: item.interruptType,

                // ✅ Add reasonList here
                reasonList: filteredReasonList382AElrex,

                selectedReasons:
                  filteredReasonList382AElrex
                    ?.filter((r) => r.isSelected)
                    .map((r) => r.bispecifictreatmentdiscontinuationreasonid) ||
                  [],
                selectedAEReasons:
                  filteredReasonList382AElrex
                    ?.find(
                      (r) => r.bispecifictreatmentdiscontinuationreasonid === 2,
                    )
                    ?.aeSubReasons?.filter((ae) => ae.isSelected)
                    .map(
                      (ae) => ae.bispecifictreatmentdiscontinuationreasonaeid,
                    ) || [],
                neurologicAEReasonList: item?.neurologicAEReasonList ?? [],
                reasonOtherText: item.reasonOtherText || "",
                reasonUnknown: item.bispecifictreatmentdiscontinuationreasonUnk,

                startDateChangeRd: item.interruptStartDateUnk
                  ? 99
                  : item.interruptStartDate
                    ? 1
                    : null,
                startDateChange: item.interruptStartDate || "",

                endDateChangeRd:
                  item.interruptEndOngoingUnk === 99
                    ? 99
                    : item.interruptEndOngoingUnk === 1
                      ? 1
                      : item.interruptEndDate
                        ? 2
                        : null,
                endDateChange: item.interruptEndDate || "",

                // All validation flags false
                changeTypeEmpty: false,
                reasonEmpty: false,
                deathDtWarning: false,
                reasonOtherTextEmpty: false,
                aeSubReasonEmpty: false,
                neuroReasonEmpty: false,
                neuroOtherTextEmpty: false,
                startDateChangeRdEmpty: false,
                startDateChangeEmpty: false,
                invalidStartDateChange: false,
                startDateChangeOutOfBound: false,
                endDateChangeRdEmpty: false,
                endDateChangeEmpty: false,
                invalidEndDateChange: false,
                endDateChangeOutOfBound: false,
                interruptionStartDateUnknownWarning: item.interruptStartDateUnk,
                interruptionEndDateUnknownWarning:
                  item.interruptEndOngoingUnk === 99,
              };
            });

            setQ382AElrexDetails(mappedElrex382A);
          } else {
            const staticReasonList = FilterRetiredReasons(
              data.q382AElrexfioData?.[0]?.reasonList || [],
            );
            setQ382AElrexDetails([
              {
                ...Q382AElrexRow,
                cohorttreatmentid: isElrexvayliSelected
                  ? RRMMCohortTreatment.ELREXFIO
                  : 0,
                reasonList: staticReasonList,
              },
            ]);
          }

          // Q38.2.A LINVOSELTAMAB - Load data
          if (
            data.q382ALinvoseltamabData &&
            data.q382ALinvoseltamabData.length > 0
          ) {
            const mappedLinvo382A = data.q382ALinvoseltamabData.map((item) => {
              const filteredReasonList382ALinvo = FilterRetiredReasons(
                item.reasonList || [],
              );
              return {
                teclistamatalquetamabdosefrequencyinfobridgeid:
                  item.teclistamatalquetamabdosedelaybridgeid,
                cohorttreatmentid: item.cohorttreatmentid,

                // Column B
                changeType: item.interruptType,

                // Column C - Reasons
                selectedReasons:
                  filteredReasonList382ALinvo
                    ?.filter((r) => r.isSelected)
                    .map((r) => r.bispecifictreatmentdiscontinuationreasonid) ||
                  [],
                selectedAEReasons:
                  filteredReasonList382ALinvo
                    ?.find(
                      (r) => r.bispecifictreatmentdiscontinuationreasonid === 2,
                    )
                    ?.aeSubReasons?.filter((ae) => ae.isSelected)
                    .map(
                      (ae) => ae.bispecifictreatmentdiscontinuationreasonaeid,
                    ) || [],
                neurologicAEReasonList: item?.neurologicAEReasonList ?? [],
                reasonOtherText: item.reasonOtherText || "",
                reasonUnknown: item.bispecifictreatmentdiscontinuationreasonUnk,
                reasonList: filteredReasonList382ALinvo,

                // Column D - Start date
                startDateChangeRd: item.interruptStartDateUnk
                  ? 99
                  : item.interruptStartDate
                    ? 1
                    : null,
                startDateChange: item.interruptStartDate || "",

                // Column E - End date
                endDateChangeRd:
                  item.interruptEndOngoingUnk === 99
                    ? 99
                    : item.interruptEndOngoingUnk === 1
                      ? 1
                      : item.interruptEndDate
                        ? 2
                        : null,
                endDateChange: item.interruptEndDate || "",

                // Validation flags
                changeTypeEmpty: false,
                reasonEmpty: false,
                deathDtWarning: false,
                reasonOtherTextEmpty: false,
                aeSubReasonEmpty: false,
                neuroReasonEmpty: false,
                neuroOtherTextEmpty: false,
                startDateChangeRdEmpty: false,
                startDateChangeEmpty: false,
                invalidStartDateChange: false,
                startDateChangeOutOfBound: false,
                endDateChangeRdEmpty: false,
                endDateChangeEmpty: false,
                invalidEndDateChange: false,
                endDateChangeOutOfBound: false,
                interruptionStartDateUnknownWarning: item.interruptStartDateUnk,
                interruptionEndDateUnknownWarning:
                  item.interruptEndOngoingUnk === 99,
              };
            });

            setQ382ALinvoDetails(mappedLinvo382A);
          } else {
            const staticReasonList = FilterRetiredReasons(
              data.q382ALinvoseltamabData?.[0]?.reasonList,
            );
            setQ382ALinvoDetails([
              {
                ...Q382ALinvoRow,
                cohorttreatmentid: isLinvoveySelected
                  ? RRMMCohortTreatment.Linvoseltamab
                  : 0,
                reasonList: staticReasonList,
              },
            ]);
          }

          //Q40.1.B
          // Process ELR Data
          if (data.q401aElrData && data.q401aElrData.length > 0) {
            const q401aElrDetails = data.q401aElrData.map((item) => {
              return {
                cohorttreatmentid: item.cohorttreatmentid,
                tectalivigreceivedbridgeid: item.tectalivigreceivedbridgeid,

                Q401ainit_dt_Rd_Elr: item.ivig_date_selection,
                Q401ainit_Elr_dt: item.ivig_date,
                Q401aInit_Elr_RdHard: false,
                Q401aInit_Elr_dtHard: false,
                Q401aInvalidQ401ainit_Elr_dt: false,
                Q401aInit_Elr_dt_before_eof: false,

                Q401aIntendedDosing_Elr: item.intended_dosing_frequency,
                Q401aIntendedDosing_Elr_WeekValue:
                  item.intended_dosing_frequency === 1
                    ? item.intended_dosing_frequency_value
                    : null,
                Q401aIntendedDosing_Elr_MonthValue:
                  item.intended_dosing_frequency === 2
                    ? item.intended_dosing_frequency_value
                    : null,
                Q401aIntendedDosing_Elr_OnceWeekValue:
                  item.intended_dosing_frequency === 3
                    ? item.intended_dosing_frequency_value
                    : null,
                Q401aIntendedDosing_Elr_OnceMonthValue:
                  item.intended_dosing_frequency === 4
                    ? item.intended_dosing_frequency_value
                    : null,
                Q401aIntendedDosing_Elr_OtherValue:
                  item.intended_dosing_frequency === 5
                    ? item.intended_dosing_frequency_value
                    : null,
                Q401aIntendedDosingEmpty_Elr: false,
                Q401aIntendedDosingRangeInvalid_Elr: false,

                Q401aPrimaryProf_Elr: item.primary_reason,
                Q401aSecProf_Elr: item.secondary_reason,
                Q401aOtherProf_Elr: item.other_reason,
                Q401aUnknownProf_Elr: item.unknown_reason,
                Q401aReasonIvigEmptyHard_Elr: false,

                Q401aDosingType_Elr: item.dosing_type,
                Q401aDosingType_ElrEmptyHard: false,

                Q401aDosage_Elr: item.dosage,
                Q401aDosage_Elr_GValue:
                  item.dosage === 1 ? item.dosage_value : null,
                Q401aDosage_Elr_MgValue:
                  item.dosage === 2 ? item.dosage_value : null,
                Q401aDosageFrequencyEmpty_Elr: false,
                Q401aDosageRangeInvalid_Elr: false,

                Q401aWeight_Elr: item.body_weight,
                Q401aWeight_Value_KgElr:
                  item.body_weight === 1 ? item.body_weight_value : null,
                Q401aWeight_Value_LbsElr:
                  item.body_weight === 2 ? item.body_weight_value : null,
                Q401aWeightEmpty_Elr: false,
                Q401aWeightRangeInvalid_Elr: false,

                Q401aWeight_Asses_Elr: item.weight_date_selection,
                Q401aWeight_Asses_Elr_dt: item.weight_date,
                Q401aWeight_AssesEmptyHard_Elr: false,
                Q401aWeight_AssesDtEmptyHard_Elr: false,
                Q401aWeight_AssesDtInvalid_Elr: false,
                Q401aWeight_Asses_dt_before_eof_Elr: false,
              };
            });

            setQ401aElrDetails(q401aElrDetails);
          } else {
            const cohortTreatmentid = isTACSelected
              ? RRMMCohortTreatment.ELREXFIO //GetLocalStorageData("techvayliID")
              : 0;

            // Update ELRRow with cohorttreatmentid only
            Q401aElrDetails[0].cohorttreatmentid = cohortTreatmentid || 0;

            // Retain the remaining fields as is
            Q401aElrDetails[0].tectalivigreceivedbridgeid =
              Q401aElrDetails[0].tectalivigreceivedbridgeid || 0;

            Q401aElrDetails[0].Q401ainit_dt_Rd_Elr =
              Q401aElrDetails[0].Q401ainit_dt_Rd_Elr || null;
            Q401aElrDetails[0].Q401ainit_Elr_dt =
              Q401aElrDetails[0].Q401ainit_Elr_dt || "";
          }

          //Q40.1.B
          // Process LINVO Data
          if (data.q401aLinvoData && data.q401aLinvoData.length > 0) {
            const q401aLinvoDetails = data.q401aLinvoData.map((item) => {
              const cohortTreatment =
                cohortTreatmentMap.get(item.cohorttreatmentid) || {};

              return {
                cohorttreatmentid: item.cohorttreatmentid,
                tectalivigreceivedbridgeid: item.tectalivigreceivedbridgeid,

                Q401ainit_dt_Rd_Lin: item.ivig_date_selection,
                Q401ainit_Lin_dt: item.ivig_date,
                Q401aInit_Lin_RdHard: false,
                Q401aInit_Lin_dtHard: false,
                Q401aInvalidQ401ainit_Lin_dt: false,
                Q401aInit_Lin_dt_before_eof: false,

                Q401aIntendedDosing_Lin: item.intended_dosing_frequency,
                Q401aIntendedDosing_Lin_WeekValue:
                  item.intended_dosing_frequency === 1
                    ? item.intended_dosing_frequency_value
                    : null,
                Q401aIntendedDosing_Lin_MonthValue:
                  item.intended_dosing_frequency === 2
                    ? item.intended_dosing_frequency_value
                    : null,
                Q401aIntendedDosing_Lin_OnceWeekValue:
                  item.intended_dosing_frequency === 3
                    ? item.intended_dosing_frequency_value
                    : null,
                Q401aIntendedDosing_Lin_OnceMonthValue:
                  item.intended_dosing_frequency === 4
                    ? item.intended_dosing_frequency_value
                    : null,
                Q401aIntendedDosing_Lin_OtherValue:
                  item.intended_dosing_frequency === 5
                    ? item.intended_dosing_frequency_value
                    : null,
                Q401aIntendedDosingEmpty_Lin: false,
                Q401aIntendedDosingRangeInvalid_Lin: false,

                Q401aPrimaryProf_Lin: item.primary_reason,
                Q401aSecProf_Lin: item.secondary_reason,
                Q401aOtherProf_Lin: item.other_reason,
                Q401aUnknownProf_Lin: item.unknown_reason,
                Q401aReasonIvigEmptyHard_Lin: false,

                Q401aDosingType_Lin: item.dosing_type,
                Q401aDosingType_LinEmptyHard: false,

                Q401aDosage_Lin: item.dosage,
                Q401aDosage_Lin_GValue:
                  item.dosage === 1 ? item.dosage_value : null,
                Q401aDosage_Lin_MgValue:
                  item.dosage === 2 ? item.dosage_value : null,
                Q401aDosageFrequencyEmpty_Lin: false,
                Q401aDosageRangeInvalid_Lin: false,

                Q401aWeight_Lin: item.body_weight,
                Q401aWeight_Value_KgLin:
                  item.body_weight === 1 ? item.body_weight_value : null,
                Q401aWeight_Value_LbsLin:
                  item.body_weight === 2 ? item.body_weight_value : null,
                Q401aWeightEmpty_Lin: false,
                Q401aWeightRangeInvalid_Lin: false,

                Q401aWeight_Asses_Lin: item.weight_date_selection,
                Q401aWeight_Asses_Lin_dt: item.weight_date,
                Q401aWeight_AssesEmptyHard_Lin: false,
                Q401aWeight_AssesDtEmptyHard_Lin: false,
                Q401aWeight_AssesDtInvalid_Lin: false,
                Q401aWeight_Asses_dt_before_eof_Lin: false,
              };
            });

            setQ401aLinvoDetails(q401aLinvoDetails);
          } else {
            const cohortTreatmentid = isLinvoveySelected
              ? RRMMCohortTreatment.Linvoseltamab //GetLocalStorageData("techvayliID")
              : 0;

            // Update TechvayliRow with cohorttreatmentid only
            Q401aLinvoDetails.cohorttreatmentid = cohortTreatmentid || 0;

            // Retain the remaining fields as is
            Q401aLinvoDetails.tectalivigreceivedbridgeid =
              Q401aLinvoDetails.tectalivigreceivedbridgeid || 0;

            Q401aLinvoDetails.Q401ainit_dt_Rd_Lin =
              Q401aLinvoDetails.Q401ainit_dt_Rd_Lin || null;
            Q401aLinvoDetails.Q401ainit_Lin_dt =
              Q401aLinvoDetails.Q401ainit_Lin_dt || "";
          }

          // Warning for Q40.1.B
          const priEnabled = Q37ElrexDetails[0]?.primaryProf;
          const secEnabled = Q37ElrexDetails[0]?.secondaryProf;
          const otherEnabled = Q37ElrexDetails[0]?.otherReson;

          // Check individually
          const priMatch =
            !priEnabled ||
            Q401aElrDetails?.some((x) => x.Q401aPrimaryProf_Elr === true);
          const secMatch =
            !secEnabled ||
            Q401aElrDetails?.some((x) => x.Q401aSecProf_Elr === true);
          const otherMatch =
            !otherEnabled ||
            Q401aElrDetails?.some((x) => x.Q401aOtherProf_Elr === true);

          // Final condition
          const isMatch = priMatch && secMatch && otherMatch;

          if (!isMatch) setQ40Elr_WarningHard(true);
          else setQ40Elr_WarningHard(false);

          // Warning for Q40.1.B
          const priTalEnabled = Q37LinvoDetails[0]?.primaryProf_linvo;
          const secTalEnabled = Q37LinvoDetails[0]?.secondaryProf_linvo;
          const otherTalEnabled = Q37LinvoDetails[0]?.otherReson_linvo;

          // Check individually
          const priTalMatch =
            !priTalEnabled ||
            Q401aLinvoDetails?.some((x) => x.Q401aPrimaryProf_Lin === true);
          const secTalMatch =
            !secTalEnabled ||
            Q401aLinvoDetails?.some((x) => x.Q401aSecProf_Lin === true);
          const otherTalMatch =
            !otherTalEnabled ||
            Q401aLinvoDetails?.some((x) => x.Q401aOtherProf_Lin === true);

          // Final condition
          const isTalMatch = priTalMatch && secTalMatch && otherTalMatch;

          if (!isTalMatch) setQ40Lin_WarningHard(true);
          else setQ40Lin_WarningHard(false);

          //Q40.1.C - backend returns ELIvigDoseFreqChangeModel fields (PascalCase C# properties
          // serialized with only the first letter lowercased, e.g. DoseFrequencyChange -> doseFrequencyChange).
          // DoseFrequencyBeforeChange/AfterChange = 1 or 2 share a single "Weeks" numeric column;
          // = 3 (Other) uses its own free-text column instead.
          // Process Elr Data
          if (data.q401cElrData && data.q401cElrData.length > 0) {
            const q401cElrDetails = data.q401cElrData.map((item) => {
              return {
                cohorttreatmentid: item.cohorttreatmentid,
                ivigdosefreqchangebridgeid: item.ivigdosefrequencyinformationid,

                Q401cChangeType_Elr: item.doseFrequencyChange,
                Q401cChangeTypeEmptyHard_Elr: false,

                Q401cFreqBeforeRd_Elr: item.doseFrequencyBeforeChange,
                Q401cFreqBeforeTimesTxt_Elr:
                  item.doseFrequencyBeforeChange === 1
                    ? (item.doseFrequencyBeforeChangeWeeks ?? "")
                    : "",
                Q401cFreqBeforeWeeksTxt_Elr:
                  item.doseFrequencyBeforeChange === 2
                    ? (item.doseFrequencyBeforeChangeWeeks ?? "")
                    : "",
                Q401cFreqBeforeOtherTxt_Elr:
                  item.doseFrequencyBeforeChangeOthertxt ?? "",
                Q401cFreqBeforeEmptyHard_Elr: false,
                Q401cFreqBeforeTxtEmptyHard_Elr: false,
                Q401cFreqBeforeRangeInvalid_Elr: false,
                Q401cFreqBeforeAutoPopulated_Elr: false,

                Q401cFreqAfterRd_Elr: item.doseFrequencyAfterChange,
                Q401cFreqAfterTimesTxt_Elr:
                  item.doseFrequencyAfterChange === 1
                    ? (item.doseFrequencyAfterChangeWeeks ?? "")
                    : "",
                Q401cFreqAfterWeeksTxt_Elr:
                  item.doseFrequencyAfterChange === 2
                    ? (item.doseFrequencyAfterChangeWeeks ?? "")
                    : "",
                Q401cFreqAfterOtherTxt_Elr:
                  item.doseFrequencyAfterChangeOthertxt ?? "",
                Q401cFreqAfterEmptyHard_Elr: false,
                Q401cFreqAfterTxtEmptyHard_Elr: false,
                Q401cFreqAfterRangeInvalid_Elr: false,

                Q401cDateChangeRd_Elr: item.dateChangeUnk
                  ? 99
                  : item.dateChange
                    ? 1
                    : null,
                Q401cDateChange_Elr: item.dateChange ?? "",
                Q401cDateChangeRdHard_Elr: false,
                Q401cDateChangeDtHard_Elr: false,
                Q401cDateChangeInvalid_Elr: false,
                Q401cDateChangeOutOfBound_Elr: false,

                Q401cFrequencyIncreaseConflict_Elr: false,
                Q401cFrequencyDecreaseConflict_Elr: false,
                Q401cWarningUnk_Elr: false,
              };
            });

            setQ401cElrDetails(q401cElrDetails);
          } else {
            const cohortTreatmentid = isElrexvayliSelected
              ? RRMMCohortTreatment.ELREXFIO
              : 0;

            Q401cElrDetails[0].cohorttreatmentid = cohortTreatmentid || 0;
            Q401cElrDetails[0].ivigdosefreqchangebridgeid =
              Q401cElrDetails[0].ivigdosefreqchangebridgeid || 0;
          }

          //Q40.1.C
          // Process Linvo Data
          if (data.q401cLinvoData && data.q401cLinvoData.length > 0) {
            const q401cLinvoDetails = data.q401cLinvoData.map((item) => {
              return {
                cohorttreatmentid: item.cohorttreatmentid,
                ivigdosefreqchangebridgeid: item.ivigdosefrequencyinformationid,

                Q401cChangeType_Lin: item.doseFrequencyChange,
                Q401cChangeTypeEmptyHard_Lin: false,

                Q401cFreqBeforeRd_Lin: item.doseFrequencyBeforeChange,
                Q401cFreqBeforeTimesTxt_Lin:
                  item.doseFrequencyBeforeChange === 1
                    ? (item.doseFrequencyBeforeChangeWeeks ?? "")
                    : "",
                Q401cFreqBeforeWeeksTxt_Lin:
                  item.doseFrequencyBeforeChange === 2
                    ? (item.doseFrequencyBeforeChangeWeeks ?? "")
                    : "",
                Q401cFreqBeforeOtherTxt_Lin:
                  item.doseFrequencyBeforeChangeOthertxt ?? "",
                Q401cFreqBeforeEmptyHard_Lin: false,
                Q401cFreqBeforeTxtEmptyHard_Lin: false,
                Q401cFreqBeforeRangeInvalid_Lin: false,
                Q401cFreqBeforeAutoPopulated_Lin: false,

                Q401cFreqAfterRd_Lin: item.doseFrequencyAfterChange,
                Q401cFreqAfterTimesTxt_Lin:
                  item.doseFrequencyAfterChange === 1
                    ? (item.doseFrequencyAfterChangeWeeks ?? "")
                    : "",
                Q401cFreqAfterWeeksTxt_Lin:
                  item.doseFrequencyAfterChange === 2
                    ? (item.doseFrequencyAfterChangeWeeks ?? "")
                    : "",
                Q401cFreqAfterOtherTxt_Lin:
                  item.doseFrequencyAfterChangeOthertxt ?? "",
                Q401cFreqAfterEmptyHard_Lin: false,
                Q401cFreqAfterTxtEmptyHard_Lin: false,
                Q401cFreqAfterRangeInvalid_Lin: false,

                Q401cDateChangeRd_Lin: item.dateChangeUnk
                  ? 99
                  : item.dateChange
                    ? 1
                    : null,
                Q401cDateChange_Lin: item.dateChange ?? "",
                Q401cDateChangeRdHard_Lin: false,
                Q401cDateChangeDtHard_Lin: false,
                Q401cDateChangeInvalid_Lin: false,
                Q401cDateChangeOutOfBound_Lin: false,

                Q401cFrequencyIncreaseConflict_Lin: false,
                Q401cFrequencyDecreaseConflict_Lin: false,
                Q401cWarningUnk_Lin: false,
              };
            });

            setQ401cLinvoDetails(q401cLinvoDetails);
          } else {
            const cohortTreatmentid = isLinvoveySelected
              ? RRMMCohortTreatment.Linvoseltamab
              : 0;

            Q401cLinvoDetails[0].cohorttreatmentid = cohortTreatmentid || 0;
            Q401cLinvoDetails[0].ivigdosefreqchangebridgeid =
              Q401cLinvoDetails[0].ivigdosefreqchangebridgeid || 0;
          }

          //Q40.2.C
          // Process Elr Data - if the server has no saved rows yet, leave the
          // default 2-row array in place so the auto-generation effect
          // populates it from elrexIndexDate/q40PeriodEndDateList.
          if (data.q402cElrData && data.q402cElrData.length > 0) {
            const q402cElrDetails = data.q402cElrData.map((item) => ({
              cohorttreatmentid: item.cohorttreatmentid,
              igglevelinformationid: item.igglevelinformationid,
              rowType: item.rowType,

              Q402cAssessment_Elr: item.igg_assessment_selection,
              Q402cAssessment_Elr_dt: item.igg_assessment_date,
              Q402cAssessment_Elr_RdHard: false,
              Q402cAssessment_Elr_dtHard: false,
              Q402cAssessment_Elr_dtInvalid: false,
              Q402cAssessment_Elr_dtOutOfRange: false,
              Q402cRow1NotEarliestHard: false,

              Q402cLevelBucket_Elr: item.igg_level_bucket_selection,
              Q402cLevelBucket_Elr_Empty: false,

              Q402cLevelValue_Elr: item.igg_level_selection,
              Q402cLevelValue_Elr_MgValue:
                item.igg_level_selection === 1 ? item.igg_level_value : "",
              Q402cLevelValue_Elr_GValue:
                item.igg_level_selection === 2 ? item.igg_level_value : "",
              Q402cLevelValue_Elr_Empty: false,
              Q402cLevelValue_Elr_RangeInvalid: false,
            }));

            setQ402cElrDetails(q402cElrDetails);
          }

          //Q40.2.C
          // Process Linvo Data
          if (data.q402cLinvoData && data.q402cLinvoData.length > 0) {
            const q402cLinvoDetails = data.q402cLinvoData.map((item) => ({
              cohorttreatmentid: item.cohorttreatmentid,
              igglevelinformationid: item.igglevelinformationid,
              rowType: item.rowType,

              Q402cAssessment_Lin: item.igg_assessment_selection,
              Q402cAssessment_Lin_dt: item.igg_assessment_date,
              Q402cAssessment_Lin_RdHard: false,
              Q402cAssessment_Lin_dtHard: false,
              Q402cAssessment_Lin_dtInvalid: false,
              Q402cAssessment_Lin_dtOutOfRange: false,
              Q402cRow1NotEarliestHard: false,

              Q402cLevelBucket_Lin: item.igg_level_bucket_selection,
              Q402cLevelBucket_Lin_Empty: false,

              Q402cLevelValue_Lin: item.igg_level_selection,
              Q402cLevelValue_Lin_MgValue:
                item.igg_level_selection === 1 ? item.igg_level_value : "",
              Q402cLevelValue_Lin_GValue:
                item.igg_level_selection === 2 ? item.igg_level_value : "",
              Q402cLevelValue_Lin_Empty: false,
              Q402cLevelValue_Lin_RangeInvalid: false,
            }));

            setQ402cLinvoDetails(q402cLinvoDetails);
          }

          //Q40.2.B
          // Process Elr Data
          if (data.q402aElrData && data.q402aElrData.length > 0) {
            const q402aElrDetails = data.q402aElrData.map((item) => {
              return {
                cohorttreatmentid: item.cohorttreatmentid,
                tectaliggdetailbridgeid: item.tectaliggdetailbridgeid,

                Q402aIgg_assessment_Elr: item.igg_assessment_selection,
                Q402aIgg_assessment_Elr_dt: item.igg_assessment_date,
                Q402aIgg_assessment_Elr_RdHard: false,
                Q402aIgg_assessment_Elr_dtHard: false,
                Q402aIgg_assessment_Elr_dtInvalid: false,
                Q402aIgg_assessment_Elr_dt_before_eof: false,
                Q402aIgg_level_Elr: item.igg_level_selection,
                Q402aIgg_level_Elr_MgValue:
                  item.igg_level_selection === 1 ? item.igg_level_value : null,
                Q402aIgg_level_Elr_GValue:
                  item.igg_level_selection === 2 ? item.igg_level_value : null,
                Q402aIgg_level_Elr_Empty: false,
                Q402aIgg_level_Elr_RangeInvalid: false,
              };
            });

            setQ402aElrDetails(q402aElrDetails);
          } else {
            const cohortTreatmentid = isElrexvayliSelected
              ? RRMMCohortTreatment.ELREXFIO //GetLocalStorageData("techvayliID")
              : 0;

            // Update TechvayliRow with cohorttreatmentid only
            Q402aElrDetails[0].cohorttreatmentid = cohortTreatmentid || 0;

            // Retain the remaining fields as is
            Q402aElrDetails[0].tectaliggdetailbridgeid =
              Q402aElrDetails[0].tectaliggdetailbridgeid || 0;

            Q402aElrDetails[0].Q402aIgg_assessment_Elr =
              Q402aElrDetails[0].Q402aIgg_assessment_Elr || null;
            Q402aElrDetails[0].Q402aIgg_assessment_Elr_dt =
              Q402aElrDetails[0].Q402aIgg_assessment_Elr_dt || "";
          }

          //Q40.2.B
          // Process Linvo Data
          if (data.q402aLinvoData && data.q402aLinvoData.length > 0) {
            const q402aLinvoDetails = data.q402aLinvoData.map((item) => {
              return {
                cohorttreatmentid: item.cohorttreatmentid,
                tectaliggdetailbridgeid: item.tectaliggdetailbridgeid,

                Q402aIgg_assessment_Lin: item.igg_assessment_selection,
                Q402aIgg_assessment_Lin_dt: item.igg_assessment_date,
                Q402aIgg_assessment_Lin_RdHard: false,
                Q402aIgg_assessment_Lin_dtHard: false,
                Q402aIgg_assessment_Lin_dtInvalid: false,
                Q402aIgg_assessment_Lin_dt_before_eof: false,
                Q402aIgg_level_Lin: item.igg_level_selection,
                Q402aIgg_level_Lin_MgValue:
                  item.igg_level_selection === 1 ? item.igg_level_value : null,
                Q402aIgg_level_Lin_GValue:
                  item.igg_level_selection === 2 ? item.igg_level_value : null,
                Q402aIgg_level_Lin_Empty: false,
                Q402aIgg_level_Lin_RangeInvalid: false,
              };
            });

            setQ402aLinvoDetails(q402aLinvoDetails);
          } else {
            const cohortTreatmentid = isLinvoveySelected
              ? RRMMCohortTreatment.Linvoseltamab //GetLocalStorageData("techvayliID")
              : 0;

            // Update TechvayliRow with cohorttreatmentid only
            Q402aLinvoDetails[0].cohorttreatmentid = cohortTreatmentid || 0;

            // Retain the remaining fields as is
            Q402aLinvoDetails[0].tectaliggdetailbridgeid =
              Q402aLinvoDetails[0].tectaliggdetailbridgeid || 0;

            Q402aLinvoDetails[0].Q402aIgg_assessment_Lin =
              Q402aLinvoDetails[0].Q402aIgg_assessment_Lin || null;
            Q402aLinvoDetails[0].Q402aIgg_assessment_Lin_dt =
              Q402aLinvoDetails[0].Q402aIgg_assessment_Lin_dt || "";
          }

          //Q40.3.B
          // Process Elr Data
          if (data.q403aElrData && data.q403aElrData.length > 0) {
            const q403aElrDetails = data.q403aElrData.map((item) => {
              return {
                cohorttreatmentid: item.cohorttreatmentid,
                tectaliggdetailbridgeid: item.tectaliggdetailbridgeid,

                Q403aIgg_assessment_Elr: item.igg_assessment_selection,
                Q403aIgg_assessment_Elr_dt: item.igg_assessment_date,
                Q403aIgg_assessment_Elr_RdHard: false,
                Q403aIgg_assessment_Elr_dtHard: false,
                Q403aIgg_assessment_Elr_dtInvalid: false,
                Q403aIgg_assessment_Elr_dt_before_eof: false,
                Q403aIgg_level_Elr: item.igg_level_selection,
                Q403aIgg_level_Elr_MgValue:
                  item.igg_level_selection === 1 ? item.igg_level_value : null,
                Q403aIgg_level_Elr_GValue:
                  item.igg_level_selection === 2 ? item.igg_level_value : null,
                Q403aIgg_level_Elr_Empty: false,
                Q403aIgg_level_Elr_RangeInvalid: false,
              };
            });

            setQ403aElrDetails(q403aElrDetails);
          } else {
            const cohortTreatmentid = isElrexvayliSelected
              ? RRMMCohortTreatment.ELREXFIO //GetLocalStorageData("techvayliID")
              : 0;

            // Update TechvayliRow with cohorttreatmentid only
            Q403aElrDetails[0].cohorttreatmentid = cohortTreatmentid || 0;

            // Retain the remaining fields as is
            Q403aElrDetails[0].tectaliggdetailbridgeid =
              Q403aElrDetails[0].tectaliggdetailbridgeid || 0;

            Q403aElrDetails[0].Q403aIgg_assessment_Elr =
              Q403aElrDetails[0].Q403aIgg_assessment_Elr || null;
            Q403aElrDetails[0].Q403aIgg_assessment_Elr_dt =
              Q403aElrDetails[0].Q403aIgg_assessment_Elr_dt || "";
          }

          //Q40.3.B
          // Process Linvo Data
          if (data.q403aLinvoData && data.q403aLinvoData.length > 0) {
            const q403aLinvoDetails = data.q403aLinvoData.map((item) => {
              return {
                cohorttreatmentid: item.cohorttreatmentid,
                tectaliggdetailbridgeid: item.tectaliggdetailbridgeid,

                Q403aIgg_assessment_Lin: item.igg_assessment_selection,
                Q403aIgg_assessment_Lin_dt: item.igg_assessment_date,
                Q403aIgg_assessment_Lin_RdHard: false,
                Q403aIgg_assessment_Lin_dtHard: false,
                Q403aIgg_assessment_Lin_dtInvalid: false,
                Q403aIgg_assessment_Lin_dt_before_eof: false,
                Q403aIgg_level_Lin: item.igg_level_selection,
                Q403aIgg_level_Lin_MgValue:
                  item.igg_level_selection === 1 ? item.igg_level_value : null,
                Q403aIgg_level_Lin_GValue:
                  item.igg_level_selection === 2 ? item.igg_level_value : null,
                Q403aIgg_level_Lin_Empty: false,
                Q403aIgg_level_Lin_RangeInvalid: false,
              };
            });

            setQ403aLinvoDetails(q403aLinvoDetails);
          } else {
            const cohortTreatmentid = isLinvoveySelected
              ? RRMMCohortTreatment.Linvoseltamab //GetLocalStorageData("techvayliID")
              : 0;

            // Update TechvayliRow with cohorttreatmentid only
            Q403aLinvoDetails[0].cohorttreatmentid = cohortTreatmentid || 0;

            // Retain the remaining fields as is
            Q403aLinvoDetails[0].tectaliggdetailbridgeid =
              Q403aLinvoDetails[0].tectaliggdetailbridgeid || 0;

            Q403aLinvoDetails[0].Q403aIgg_assessment_Lin =
              Q403aLinvoDetails[0].Q403aIgg_assessment_Lin || null;
            Q403aLinvoDetails[0].Q403aIgg_assessment_Lin_dt =
              Q403aLinvoDetails[0].Q403aIgg_assessment_Lin_dt || "";
          }

          //Q39.1.A
          // Elrex
          const elrSource = data?.elrexfioTocilizumabData ?? [];

          const elrTocilData =
            elrSource.length > 0
              ? elrSource.map((item) => ({
                  ...q391AElrRow,
                  tocilizumabmedicationid: item.tocilizumabmedicationid,
                  cohorttreatmentid: item.cohorttreatmentid,
                  patientid: item.patientid,

                  primaryProphylacticCrs_elr: item.primaryProphylacticCrs,
                  secondaryProphylacticCrs_elr: item.secondaryProphylacticCrs,
                  supportiveCareCrs_elr: item.supportiveCareCrs,
                  primaryProphylacticNeurologic_elr:
                    item.primaryProphylacticNeurologic,
                  secondaryProphylacticNeurologic_elr:
                    item.secondaryProphylacticNeurologic,
                  supportiveCareNeurologic_elr: item.supportiveCareNeurologic,
                  medicationReasonUnk_elr: item.medicationReasonUnk,
                  q39ElrReasonEmpty: false,

                  // 39.1.A
                  administrationDate_elr: item.tocilizumabAdministrationDate,
                  administrationDateRD_elr:
                    item.tocilizumabAdministrationDateUnk
                      ? 99
                      : item.tocilizumabAdministrationDate
                        ? 1
                        : null,

                  q39ElrInvalidAdministrationDate: false,
                  q39ElrAdministrationDateEmpty: false,
                  q39ElrAdministrationRDEmpty: false,
                }))
              : isNewEcrfAChecked
                ? [
                    {
                      ...q391AElrRow,
                      patientid: data?.patientid,
                    },
                  ]
                : [];

          setQ391AElrTocilzumabList(elrTocilData);
          setQ391AElrAdministrationDateOutOfRange(false);

          // Linvo
          const linvoSource = data?.linvoseltamabTocilizumabData ?? [];

          const linvoTocilData =
            linvoSource.length > 0
              ? linvoSource.map((item) => ({
                  ...q391ALinvoRow,
                  tocilizumabmedicationid: item.tocilizumabmedicationid,
                  cohorttreatmentid: item.cohorttreatmentid,
                  patientid: item.patientid,

                  primaryProphylacticCrs_linvo: item.primaryProphylacticCrs,
                  secondaryProphylacticCrs_linvo: item.secondaryProphylacticCrs,
                  supportiveCareCrs_linvo: item.supportiveCareCrs,
                  primaryProphylacticNeurologic_linvo:
                    item.primaryProphylacticNeurologic,
                  secondaryProphylacticNeurologic_linvo:
                    item.secondaryProphylacticNeurologic,
                  supportiveCareNeurologic_linvo: item.supportiveCareNeurologic,

                  medicationReasonUnk_linvo: item.medicationReasonUnk,
                  q39LinvoReasonEmpty: false,

                  // 39.1.A
                  administrationDate_linvo: item.tocilizumabAdministrationDate,
                  administrationDateRD_linvo:
                    item.tocilizumabAdministrationDateUnk
                      ? 99
                      : item.tocilizumabAdministrationDate
                        ? 1
                        : null,

                  q39LinvoInvalidAdministrationDate: false,
                  q39LinvoAdministrationDateEmpty: false,
                  q39LinvoAdministrationRDEmpty: false,
                }))
              : isNewEcrfAChecked
                ? [
                    {
                      ...q391ALinvoRow,
                      patientid: data?.patientid,
                    },
                  ]
                : [];
          setQ391ALinvoAdministrationDateOutOfRange(false);
          setQ391ALinvoTocilzumabList(linvoTocilData);
        } else if (response?.status === HTTPResponse.Unauthorized) {
          HandleSessionTimeout();
        }
      }
    } catch (error) {
      ShowToast(CommonError_Msgs.LOAD_ERR, ToastMessageType.Failed);
    }
  };

  useEffect(() => {
    async function FetchData() {
      if (!isNaN(pid) && Number(pid) !== 0) {
        ToggleLoader(true);
        await LoadData();
        ToggleLoader(false);
      }
    }
    FetchData();
  }, []);

  // AUTO-009 - keep Q38.1.A column [D] in step with Q36. Runs on load and
  // whenever the Q36 rows change, so editing the 1st subsequent treatment dose
  // re-seeds the auto-populated row rather than leaving a stale value.
  useEffect(() => {
    setQ381AElrexDetails((prev) =>
      ApplyQ38PatternBeforeAutoPopulate(
        prev,
        elrexvayliDetails,
        Q381A_ELREX_AUTO_CONFIG,
      ),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [elrexvayliDetails]);

  useEffect(() => {
    setQ381ALinvoDetails((prev) =>
      ApplyQ38PatternBeforeAutoPopulate(
        prev,
        linvoveyDetails,
        Q381A_LINVO_AUTO_CONFIG,
      ),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linvoveyDetails]);

  const handleQ391AList = (setStateFn, rowTemplate, pid, isSelected) => {
    setStateFn((prev) => {
      if (!isSelected) return [];

      if (prev && prev.length > 0) return prev;

      return rowTemplate ? [{ ...rowTemplate, patientid: pid }] : [];
    });
  };

  const HandleRepeaterFieldChange = (e) => {
    const { name, id, value } = e.target;
    const value_int = Number(value);
    const index = Number(id);

    switch (name) {
      // Elrexfio

      case "dose_administration_elr" + id: {
        let updatedArray = [...elrexvayliDetails];

        updatedArray[id].dose_administration_elr = value_int;
        updatedArray[id].dose_administration_elr_dt = "";

        updatedArray[id].dose_administration_elrHard = false;

        updatedArray[id].dose_administration_elrHard_dt = false;
        updatedArray[id].Invalid_dose_administration_elr_dt = false;
        // updatedArray[id].dt_elr_greater_prevCheck = false;
        updatedArray[id].dt_elr_date_out_of_bound = false;
        updatedArray[id].dose_administration_elr_dt_before_eof = false;

        if (updatedArray[id].dose_administration_elr === 2) {
          updatedArray[id].elr_dose_strength_mg = "";
          updatedArray[id].elr_dose_strength_mg_kg = "";
          updatedArray[id].elr_dose_strength_mgm = "";
          updatedArray[id].elr_dose_strength_rbl = null;
          updatedArray[id].elr_dose_strength_mg_kgHard = false;
          updatedArray[id].elr_dose_weekly_strength_mg_kgHard = false;
          updatedArray[id].elr_dose_strength_mg_kgRange = false;

          updatedArray[id].siteofcare_elr = null;
          updatedArray[id].siteofcare_elrHard = false;
        }

        setIndexDateRRMM_elrHard(false);
        setElrexvayliDetails(updatedArray);
        setDt_elr_2L_3L_date_conditions(false);
        setIsElrLastAdminDateValid(true);
        break;
      }

      case "dosing_phase_elr" + id: {
        let updatedArray = [...elrexvayliDetails];

        updatedArray[id].dosing_phase_elr = value_int;

        updatedArray[id].dosing_phase_elrHard = false;
        updatedArray[id].dosing_phase_oth_txt_elr = "";
        updatedArray[id].dosing_phase_elrHard_othEmpty = false;

        if (
          (value_int === 1 || value_int === 2 || value_int === 3) &&
          (updatedArray[id].siteofcare_elr === 1 ||
            updatedArray[id].siteofcare_elr === 3)
        ) {
          updatedArray[id].Q36ElrexConditionMet = true;
        } else {
          updatedArray[id].Q36ElrexConditionMet = false;
        }

        // Q37 retired
        /*
        let updatedArrayQ37 = [...Q37ElrexDetails];
        if (elrexvayliDetails.some((item) => item.dosing_phase_elr != 4)) {
          updatedArrayQ37[0].q37_Elr_Dose_week_frquencyRd = null;
          updatedArrayQ37[0].q37_Elr_Dose_week_frquency_txt = "";
          updatedArrayQ37[0].q37_Elr_Dose_week_frquencyRd_Empty = false;
          updatedArrayQ37[0].q37_Elr_Dose_week_frquency_txt_Empty = false;
          updatedArrayQ37[0].q37_Elr_Dose_week_freq_range = false;
        }
        setQ37ElrexDetails(updatedArrayQ37);
        */

        //Duplicate dose warning
        const setups = elrexvayliDetails
          .map((r) => r.dosing_phase_elr)
          .filter((v) => v != null && v !== 4);
        const hasDuplicateSetups = new Set(setups).size !== setups.length;
        setSetupElr_WarningHard(hasDuplicateSetups);

        //Warning dependent on Q26
        const tecdata = elrexvayliDetails.filter(
          (v) =>
            v.dosing_phase_elr > 1 &&
            v.dosing_phase_elr < 3 &&
            v.dose_administration_elr == 2,
        );
        const numOfAdmission = bispecificQ26Data.filter(
          (b) => b.cohortTreatmentId === RRMMCohortTreatment.ELREXFIO,
        );
        const showQ26DependentWarning =
          tecdata.length > 0 && numOfAdmission[0]?.numberOfAdmission >= 4;
        setSetupElrQ26_WarningHard(showQ26DependentWarning);

        setElrexvayliDetails(updatedArray);
        setElrInpatientHospMissing(false);

        const duplicates = CheckDuplicatePhasesRealtime(
          updatedArray,
          "elrexfio",
        );
        setElrexfioDuplicatePhaseWarning(
          duplicates.length > 0 ? duplicates : null,
        );

        const missingStepUp = CheckMissingStepUpDosesRealtime(
          updatedArray,
          "elrexfio",
        );
        setElrexfioMissingStepUpWarning(missingStepUp);

        break;
      }

      case "dosing_phase_oth_txt_elr" + id: {
        let updatedArray = [...elrexvayliDetails];

        updatedArray[id].dosing_phase_oth_txt_elr = value;
        updatedArray[id].dosing_phase_elr = 5;

        updatedArray[id].dosing_phase_elrHard = false;
        updatedArray[id].dosing_phase_elrHard_othEmpty = false;

        setElrexvayliDetails(updatedArray);
        break;
      }

      case "elr_dose_strength_mg_kg" + id: {
        let updatedArray = [...elrexvayliDetails];

        updatedArray[id].elr_dose_strength_mg = "";
        updatedArray[id].elr_dose_strength_mg_kg = value;
        updatedArray[id].elr_dose_strength_mgm = "";
        updatedArray[id].elr_dose_strength_rbl = 2;
        updatedArray[id].elr_dose_strength_mg_kgHard = false;
        updatedArray[id].elr_dose_weekly_strength_mg_kgHard = false;
        updatedArray[id].elr_dose_strength_mg_kgRange = false;

        setElrexvayliDetails(updatedArray);
        break;
      }
      case "elr_dose_strength_mg" + id: {
        let updatedArray = [...elrexvayliDetails];
        updatedArray[id].elr_dose_strength_mg = value;
        updatedArray[id].elr_dose_strength_mg_kg = "";
        updatedArray[id].elr_dose_strength_mgm = "";
        updatedArray[id].elr_dose_strength_rbl = 1;
        updatedArray[id].elr_dose_strength_mg_kgHard = false;
        updatedArray[id].elr_dose_weekly_strength_mg_kgHard = false;
        updatedArray[id].elr_dose_strength_mg_kgRange = false;
        setElrexvayliDetails(updatedArray);
        break;
      }
      case "elr_dose_strength_mgm" + id: {
        let updatedArray = [...elrexvayliDetails];
        updatedArray[id].elr_dose_strength_mg = "";
        updatedArray[id].elr_dose_strength_mg_kg = "";
        updatedArray[id].elr_dose_strength_mgm = value;
        updatedArray[id].elr_dose_strength_rbl = 3;
        updatedArray[id].elr_dose_strength_mg_kgHard = false;
        updatedArray[id].elr_dose_weekly_strength_mg_kgHard = false;
        updatedArray[id].elr_dose_strength_mg_kgRange = false;
        setElrexvayliDetails(updatedArray);
        break;
      }
      case "elr_dose_strength_rbl" + id: {
        let updatedArray = [...elrexvayliDetails];
        updatedArray[id].elr_dose_strength_rbl = Number(value);
        updatedArray[id].elr_dose_strength_mg = "";
        updatedArray[id].elr_dose_strength_mg_kg = "";
        updatedArray[id].elr_dose_strength_mgm = "";
        updatedArray[id].elr_dose_strength_mg_kgHard = false;
        updatedArray[id].elr_dose_weekly_strength_mg_kgHard = false;
        updatedArray[id].elr_dose_strength_mg_kgRange = false;
        setElrexvayliDetails(updatedArray);
        break;
      }
      case "elr_dose_freq_rbl" + id: {
        let updatedArray = [...elrexvayliDetails];
        updatedArray[id].elr_dose_freq_rbl = Number(value);
        updatedArray[id].elr_dose_freq_week = "";
        updatedArray[id].elr_dose_freq_onceweek = "";
        updatedArray[id].elr_dose_freq_hard_error = false;
        updatedArray[id].elr_dose_weekly_freq_hard_error = false;
        updatedArray[id].elr_dose_weekly_freq_RangeError = false;
        setElrexvayliDetails(updatedArray);
        break;
      }
      case "elr_dose_freq_week" + id: {
        let updatedArray = [...elrexvayliDetails];
        updatedArray[id].elr_dose_freq_rbl = 1;
        updatedArray[id].elr_dose_freq_week = value;
        updatedArray[id].elr_dose_freq_onceweek = "";
        updatedArray[id].elr_dose_freq_hard_error = false;
        updatedArray[id].elr_dose_weekly_freq_hard_error = false;
        updatedArray[id].elr_dose_weekly_freq_RangeError = false;
        setElrexvayliDetails(updatedArray);
        break;
      }
      case "elr_dose_freq_onceweek" + id: {
        let updatedArray = [...elrexvayliDetails];
        updatedArray[id].elr_dose_freq_rbl = 2;
        updatedArray[id].elr_dose_freq_week = "";
        updatedArray[id].elr_dose_freq_onceweek = value;
        updatedArray[id].elr_dose_freq_hard_error = false;
        updatedArray[id].elr_dose_weekly_freq_hard_error = false;
        updatedArray[id].elr_dose_weekly_freq_RangeError = false;
        setElrexvayliDetails(updatedArray);
        break;
      }

      case "siteofcare_elr" + id: {
        let updatedArray = [...elrexvayliDetails];

        updatedArray[id].siteofcare_elr = value_int;

        updatedArray[id].siteofcare_elrHard = false;

        if (value_int === 2) {
          updatedArray[id].dt_admission_elr_dt = null;
          updatedArray[id].dt_discharge_elr_dt = null;

          updatedArray[id].dt_discharge_elrHard = false;
          updatedArray[id].dt_discharge_elrHard_dt = false;
          updatedArray[id].dt_discharge_elrHard_dt_check = false;
          updatedArray[id].dt_admission_elrHard = false;
          updatedArray[id].dt_admission_elrHard_dt = false;
          updatedArray[id].dt_admission_elrHard_dt_check = false;
        }
        setElrInpatientHospMissing(false);
        setElrexvayliDetails(updatedArray);
        break;
      }

      //36.1 start
      //B
      case "dt_admission_elr" + id: {
        let updatedArray = [...elrHospDetails];

        updatedArray[id].dt_admission = value_int;
        updatedArray[id].dt_admission_dt = "";

        updatedArray[id].dt_admission_Hard = false;
        updatedArray[id].dt_admission_Hard_dt = false;
        updatedArray[id].Invalid_admission_Hard_dt = false;
        updatedArray[id].dt_admission_out_of_bound = false;
        updatedArray[id].dt_admission_before_eof = false;

        //Delete rows if "Patient had no hospilinvoizations during the step-up dosing phase" is selected
        //and Reset columns C n D of current row
        if (value_int === 2) {
          updatedArray[id].dt_discharge = null;
          updatedArray[id].dt_discharge_dt = "";
          updatedArray[id].dt_discharge_Hard = false;
          updatedArray[id].dt_discharge_Hard_dt = false;
          updatedArray[id].dt_discharge_Hard_dt_check = false;
          updatedArray[id].dt_discharge_out_of_bound = false;
          updatedArray[id].Invalid_dt_discharge_Hard = false;
          updatedArray[id].dt_discharge_before_eof = false;
          updatedArray[id].dateRangeThirty = false;

          updatedArray[id].hospReason1 = false;
          updatedArray[id].hospReason2 = false;
          updatedArray[id].hospReason3 = false;
          updatedArray[id].hospReason4 = false;
          updatedArray[id].hospReason5 = false;
          updatedArray[id].hospReason6 = false;
          updatedArray[id].hospReason7 = false;
          updatedArray[id].hosp_reason_oth_txt = "";
          updatedArray[id].hospReasonUnk = false;
          updatedArray[id].hospReasonEmptyHard = false;
          updatedArray[id].hospReasonTxtEmpty = false;

          const deleteStartIndex = Number(id) + 1;
          if (deleteStartIndex >= 0 && deleteStartIndex < updatedArray.length) {
            updatedArray.splice(
              deleteStartIndex,
              updatedArray.length - deleteStartIndex,
            );
          }
        }
        setElrHospDetails(updatedArray);
        setElrInpatientHospMissing(false);
        break;
      }
      //C
      case "dt_discharge_elr" + id: {
        let updatedArray = [...elrHospDetails];

        updatedArray[id].dt_discharge = value_int;
        updatedArray[id].dt_discharge_dt = "";
        updatedArray[id].Invalid_dt_discharge_Hard = false;
        updatedArray[id].dt_discharge_before_eof = false;
        updatedArray[id].dt_discharge_Hard = false;
        updatedArray[id].dt_discharge_Hard_dt = false;
        updatedArray[id].dt_discharge_Hard_dt_check = false;
        updatedArray[id].dt_discharge_out_of_bound = false;
        updatedArray[id].dateRangeThirty = false;

        setElrHospDetails(updatedArray);
        break;
      }
      //D
      case "hospReason1_elr" + id:
      case "hospReason2_elr" + id:
      case "hospReason3_elr" + id:
      case "hospReason4_elr" + id:
      case "hospReason5_elr" + id:
      case "hospReason7_elr" + id: {
        let updatedArray = [...elrHospDetails];

        const ctrlId = name.split("_")[0];

        updatedArray[id][ctrlId] = !updatedArray[id][ctrlId];

        updatedArray[id].hospReasonUnk = false;

        updatedArray[id].hospReasonEmptyHard = false;
        updatedArray[id].hospReasonTxtEmpty = false;
        setElrHospDetails(updatedArray);
        break;
      }
      case "hospReason6_elr" + id: {
        let updatedArray = [...elrHospDetails];

        updatedArray[id].hospReason6 = !updatedArray[id].hospReason6;
        updatedArray[id].hosp_reason_oth_txt = "";
        updatedArray[id].hospReasonUnk = false;

        updatedArray[id].hospReasonEmptyHard = false;
        updatedArray[id].hospReasonTxtEmpty = false;
        setElrHospDetails(updatedArray);
        break;
      }
      case "hosp_reason_oth_txt_elr" + id: {
        let updatedArray = [...elrHospDetails];

        updatedArray[id].hosp_reason_oth_txt = value;
        updatedArray[id].hospReason6 = true;
        updatedArray[id].hospReasonUnk = false;

        updatedArray[id].hospReasonEmptyHard = false;
        updatedArray[id].hospReasonTxtEmpty = false;
        setElrHospDetails(updatedArray);
        break;
      }
      case "hospReasonUnk_elr" + id: {
        let updatedArray = [...elrHospDetails];

        updatedArray[id].hospReasonUnk = true;
        updatedArray[id].hospReason1 = false;
        updatedArray[id].hospReason2 = false;
        updatedArray[id].hospReason3 = false;
        updatedArray[id].hospReason4 = false;
        updatedArray[id].hospReason5 = false;
        updatedArray[id].hospReason6 = false;
        updatedArray[id].hospReason7 = false;
        updatedArray[id].hosp_reason_oth_txt = "";

        updatedArray[id].hospReasonEmptyHard = false;
        updatedArray[id].hospReasonTxtEmpty = false;
        setElrHospDetails(updatedArray);
        break;
      }

      // Q37 retired
      /*
      case "q37ElrDosageRd" + id: {
        let updatedArray = [...Q37ElrexDetails];
        updatedArray[id].q37ElrDosageRd = value_int;
        updatedArray[id].q37ElrDosageRdEmpty = false;

        // Clear other text if not "Other" option
        if (value_int !== 2) {
          updatedArray[id].q37ElrDosageOtherTxt = "";
          updatedArray[id].q37ElrDosageOtherTxtEmpty = false;
        }

        setQ37ElrexDetails(updatedArray);
        break;
      }

      //  ELREXFIO Dosage Other Text
      case "q37ElrDosageOtherTxt" + id: {
        let updatedArray = [...Q37ElrexDetails];
        updatedArray[id].q37ElrDosageOtherTxt = value;
        updatedArray[id].q37ElrDosageRd = 2;

        updatedArray[id].q37ElrDosageOtherTxtEmpty = false;
        setQ37ElrexDetails(updatedArray);
        break;
      }
      */
      //end

      //LINVOSELTAMAB
      case "dose_administration_linvo" + id: {
        let updatedArray = [...linvoveyDetails];

        updatedArray[id].dose_administration_linvo = value_int;
        updatedArray[id].dose_administration_linvo_dt = "";

        updatedArray[id].dose_administration_linvoHard = false;
        updatedArray[id].Invalid_dose_administration_linvo_dt = false;

        updatedArray[id].dose_administration_linvoHard_dt = false;

        updatedArray[id].dt_linvo_date_out_of_bound = false;

        if (updatedArray[id].dose_administration_linvo === 2) {
          updatedArray[id].linvo_dose_strength_mg = "";
          updatedArray[id].linvo_dose_strength_mg_kg = "";
          updatedArray[id].linvo_dose_strength_mgm = "";
          updatedArray[id].linvo_dose_strength_rbl = null;
          updatedArray[id].linvo_dose_strength_mg_kgHard = false;
          updatedArray[id].linvo_dose_strength_mg_kgRange = false;
          updatedArray[id].linvo_dose_weekly_strength_mg_kgHard = false;

          updatedArray[id].siteofcare_linvo = null;
          updatedArray[id].siteofcare_linvoHard = false;
        }

        setIndexDateRRMM_linvoHard(false);
        setLinvoveyDetails(updatedArray);
        setDt_lino_2L_3L_date_conditions(false);
        setIsLinvoLastAdminDateValid(true);
        break;
      }

      case "dosing_phase_linvo" + id: {
        let updatedArray = [...linvoveyDetails];

        updatedArray[id].dosing_phase_linvo = value_int;
        updatedArray[id].dosing_phase_linvoHard_othEmpty = false;

        updatedArray[id].dosing_phase_linvoHard = false;
        updatedArray[id].dosing_phase_oth_txt_linvo = "";

        // Q37 retired
        /*
        let updatedArrayQ37 = [...Q37LinvoDetails];
        if (linvoveyDetails.some((item) => item.dosing_phase_linvo != 5)) {
          updatedArrayQ37[0].q37_Linvo_Dose_week_frquencyRd = null;
          updatedArrayQ37[0].q37_Linvo_Dose_week_frquency_txt = "";
          updatedArrayQ37[0].q37_Linvo_Dose_week_frquencyRd_Empty = false;
          updatedArrayQ37[0].q37_Linvo_Dose_week_frquency_txt_Empty = false;
          updatedArrayQ37[0].q37_Linvo_Dose_week_freq_range = false;
        }

        setQ37LinvoDetails(updatedArrayQ37);
        */
        setLinvoveyDetails(updatedArray);
        setLinvoInpatientHospMissing(false);
        const duplicatesLinvo = CheckDuplicatePhasesRealtime(
          updatedArray,
          "linvo",
        );
        setLinvoDuplicatePhaseWarning(
          duplicatesLinvo.length > 0 ? duplicatesLinvo : null,
        );

        const missingStepUpLinvo = CheckMissingStepUpDosesRealtime(
          updatedArray,
          "linvo",
        );
        setLinvoMissingStepUpWarning(missingStepUpLinvo);

        //Duplicate dose warning
        const setups = linvoveyDetails
          .map((r) => r.dosing_phase_linvo)
          .filter((v) => v != null && v !== 5);
        const hasDuplicateSetups = new Set(setups).size !== setups.length;
        setSetupLinvo_WarningHard(hasDuplicateSetups);

        //Warning dependent on Q26
        const taldata = linvoveyDetails.filter(
          (v) =>
            v.dosing_phase_linvo > 1 &&
            v.dosing_phase_linvo < 4 &&
            v.dose_administration_linvo == 2,
        );
        const numOfAdmission = bispecificQ26Data.filter(
          (b) => b.cohortTreatmentId === RRMMCohortTreatment.Linvoseltamab,
        );
        const showQ26DependentWarning =
          taldata.length > 0 && numOfAdmission[0]?.numberOfAdmission >= 4;
        setSetupLinvoQ26_WarningHard(showQ26DependentWarning);

        break;
      }

      case "dosing_phase_oth_txt_linvo" + id: {
        let updatedArray = [...linvoveyDetails];

        updatedArray[id].dosing_phase_oth_txt_linvo = value;
        updatedArray[id].dosing_phase_linvo = 6;
        updatedArray[id].dosing_phase_linvoHard = false;
        updatedArray[id].dosing_phase_linvoHard_othEmpty = false;

        setLinvoveyDetails(updatedArray);
        break;
      }

      case "linvo_dose_strength_mg_kg" + id: {
        let updatedArray = [...linvoveyDetails];

        updatedArray[id].linvo_dose_strength_mg = "";
        updatedArray[id].linvo_dose_strength_mg_kg = value;
        updatedArray[id].linvo_dose_strength_mgm = "";
        updatedArray[id].linvo_dose_strength_rbl = 2;
        updatedArray[id].linvo_dose_strength_mg_kgHard = false;
        updatedArray[id].linvo_dose_strength_mg_kgRange = false;
        updatedArray[id].linvo_dose_weekly_strength_mg_kgHard = false;

        setLinvoveyDetails(updatedArray);
        break;
      }
      case "linvo_dose_strength_mg" + id: {
        let updatedArray = [...linvoveyDetails];
        updatedArray[id].linvo_dose_strength_mg = value;
        updatedArray[id].linvo_dose_strength_mg_kg = "";
        updatedArray[id].linvo_dose_strength_mgm = "";
        updatedArray[id].linvo_dose_strength_rbl = 1;
        updatedArray[id].linvo_dose_strength_mg_kgHard = false;
        updatedArray[id].linvo_dose_strength_mg_kgRange = false;
        updatedArray[id].linvo_dose_weekly_strength_mg_kgHard = false;
        setLinvoveyDetails(updatedArray);
        break;
      }
      case "linvo_dose_strength_mgm" + id: {
        let updatedArray = [...linvoveyDetails];
        updatedArray[id].linvo_dose_strength_mg = "";
        updatedArray[id].linvo_dose_strength_mg_kg = "";
        updatedArray[id].linvo_dose_strength_mgm = value;
        updatedArray[id].linvo_dose_strength_rbl = 3;
        updatedArray[id].linvo_dose_strength_mg_kgHard = false;
        updatedArray[id].linvo_dose_strength_mg_kgRange = false;
        updatedArray[id].linvo_dose_weekly_strength_mg_kgHard = false;
        setLinvoveyDetails(updatedArray);
        break;
      }
      case "linvo_dose_strength_rbl" + id: {
        let updatedArray = [...linvoveyDetails];
        updatedArray[id].linvo_dose_strength_rbl = Number(value);
        updatedArray[id].linvo_dose_strength_mg = "";
        updatedArray[id].linvo_dose_strength_mg_kg = "";
        updatedArray[id].linvo_dose_strength_mgm = "";
        updatedArray[id].linvo_dose_strength_mg_kgHard = false;
        updatedArray[id].linvo_dose_strength_mg_kgRange = false;
        updatedArray[id].linvo_dose_weekly_strength_mg_kgHard = false;
        setLinvoveyDetails(updatedArray);
        break;
      }
      case "linvo_dose_freq_rbl" + id: {
        let updatedArray = [...linvoveyDetails];
        updatedArray[id].linvo_dose_freq_rbl = Number(value);
        updatedArray[id].linvo_dose_freq_week = "";
        updatedArray[id].linvo_dose_freq_onceweek = "";
        updatedArray[id].linvo_dose_freq_hard_error = false;
        updatedArray[id].linvo_dose_weekly_freq_hard_error = false;
        updatedArray[id].linvo_dose_weekly_freq_RangeError = false;

        setLinvoveyDetails(updatedArray);
        break;
      }
      case "linvo_dose_freq_week" + id: {
        let updatedArray = [...linvoveyDetails];
        updatedArray[id].linvo_dose_freq_rbl = 1;
        updatedArray[id].linvo_dose_freq_week = value;
        updatedArray[id].linvo_dose_freq_onceweek = "";
        updatedArray[id].linvo_dose_freq_hard_error = false;
        updatedArray[id].linvo_dose_weekly_freq_hard_error = false;
        updatedArray[id].linvo_dose_weekly_freq_RangeError = false;
        setLinvoveyDetails(updatedArray);
        break;
      }
      case "linvo_dose_freq_onceweek" + id: {
        let updatedArray = [...linvoveyDetails];
        updatedArray[id].linvo_dose_freq_rbl = 2;
        updatedArray[id].linvo_dose_freq_week = "";
        updatedArray[id].linvo_dose_freq_onceweek = value;
        updatedArray[id].linvo_dose_freq_hard_error = false;
        updatedArray[id].linvo_dose_weekly_freq_hard_error = false;
        updatedArray[id].linvo_dose_weekly_freq_RangeError = false;
        setLinvoveyDetails(updatedArray);
        break;
      }

      case "siteofcare_linvo" + id: {
        let updatedArray = [...linvoveyDetails];

        updatedArray[id].siteofcare_linvo = value_int;

        updatedArray[id].siteofcare_linvoHard = false;

        if (value_int === 2) {
          updatedArray[id].dt_admission_linvo_dt = null;
          updatedArray[id].dt_discharge_linvo_dt = null;

          updatedArray[id].dt_discharge_linvoHard = false;
          updatedArray[id].dt_discharge_linvoHard_dt = false;
          updatedArray[id].dt_discharge_linvoHard_dt_check = false;
          updatedArray[id].dt_admission_linvoHard = false;
          updatedArray[id].dt_admission_linvoHard_dt = false;
          updatedArray[id].dt_admission_linvoHard_dt_check = false;
        }
        setLinvoInpatientHospMissing(false);
        setLinvoveyDetails(updatedArray);
        break;
      }

      //36.1 start
      //B
      case "dt_admission_linvo" + id: {
        let updatedArray = [...linvoHospDetails];

        updatedArray[id].dt_admission = value_int;
        updatedArray[id].dt_admission_dt = "";

        updatedArray[id].dt_admission_Hard = false;
        updatedArray[id].Invalid_admission_Hard_dt = false;
        updatedArray[id].dt_admission_Hard_dt = false;
        updatedArray[id].dt_admission_out_of_bound = false;
        updatedArray[id].dt_admission_before_eof = false;

        //Delete rows if "Patient had no hospilinvoizations during the step-up dosing phase" is selected
        //and Reset columns C n D of current row
        if (value_int === 2) {
          updatedArray[id].dt_discharge = null;
          updatedArray[id].dt_discharge_dt = "";
          updatedArray[id].dt_discharge_Hard = false;
          updatedArray[id].dt_discharge_Hard_dt = false;
          updatedArray[id].dt_discharge_Hard_dt_check = false;
          updatedArray[id].dt_discharge_out_of_bound = false;
          updatedArray[id].Invalid_dt_discharge_Hard = false;
          updatedArray[id].dt_discharge_before_eof = false;
          updatedArray[id].dateRangeThirty = false;

          updatedArray[id].hospReason1 = false;
          updatedArray[id].hospReason2 = false;
          updatedArray[id].hospReason3 = false;
          updatedArray[id].hospReason4 = false;
          updatedArray[id].hospReason5 = false;
          updatedArray[id].hospReason6 = false;
          updatedArray[id].hospReason7 = false;
          updatedArray[id].hosp_reason_oth_txt = "";
          updatedArray[id].hospReasonUnk = false;
          updatedArray[id].hospReasonEmptyHard = false;
          updatedArray[id].hospReasonTxtEmpty = false;

          const deleteStartIndex = Number(id) + 1;
          if (deleteStartIndex >= 0 && deleteStartIndex < updatedArray.length) {
            updatedArray.splice(
              deleteStartIndex,
              updatedArray.length - deleteStartIndex,
            );
          }
        }
        setLinvoInpatientHospMissing(false);
        setLinvoHospDetails(updatedArray);
        break;
      }
      //C
      case "dt_discharge_linvo" + id: {
        let updatedArray = [...linvoHospDetails];

        updatedArray[id].dt_discharge = value_int;
        updatedArray[id].dt_discharge_dt = "";

        updatedArray[id].dt_discharge_Hard = false;
        updatedArray[id].dt_discharge_Hard_dt = false;
        updatedArray[id].dt_discharge_Hard_dt_check = false;
        updatedArray[id].dt_discharge_out_of_bound = false;
        updatedArray[id].Invalid_dt_discharge_Hard = false;
        updatedArray[id].dt_discharge_before_eof = false;
        updatedArray[id].dateRangeThirty = false;

        setLinvoHospDetails(updatedArray);

        break;
      }
      case "hospReason1_linvo" + id:
      case "hospReason2_linvo" + id:
      case "hospReason3_linvo" + id:
      case "hospReason4_linvo" + id:
      case "hospReason5_linvo" + id:
      case "hospReason7_linvo" + id: {
        let updatedArray = [...linvoHospDetails];

        const ctrlId = name.split("_")[0];

        updatedArray[id][ctrlId] = !updatedArray[id][ctrlId];
        updatedArray[id].hospReasonUnk = false;

        updatedArray[id].hospReasonEmptyHard = false;
        updatedArray[id].hospReasonTxtEmpty = false;
        setLinvoHospDetails(updatedArray);
        break;
      }
      case "hospReason6_linvo" + id: {
        let updatedArray = [...linvoHospDetails];

        updatedArray[id].hospReason6 = !updatedArray[id].hospReason6;
        updatedArray[id].hosp_reason_oth_txt = "";
        updatedArray[id].hospReasonUnk = false;

        updatedArray[id].hospReasonEmptyHard = false;
        updatedArray[id].hospReasonTxtEmpty = false;
        setLinvoHospDetails(updatedArray);
        break;
      }
      case "hosp_reason_oth_txt_linvo" + id: {
        let updatedArray = [...linvoHospDetails];

        updatedArray[id].hosp_reason_oth_txt = value;
        updatedArray[id].hospReason6 = true;
        updatedArray[id].hospReasonUnk = false;

        updatedArray[id].hospReasonEmptyHard = false;
        updatedArray[id].hospReasonTxtEmpty = false;
        setLinvoHospDetails(updatedArray);
        break;
      }
      case "hospReasonUnk_linvo" + id: {
        let updatedArray = [...linvoHospDetails];

        updatedArray[id].hospReasonUnk = true;
        updatedArray[id].hospReason1 = false;
        updatedArray[id].hospReason2 = false;
        updatedArray[id].hospReason3 = false;
        updatedArray[id].hospReason4 = false;
        updatedArray[id].hospReason5 = false;
        updatedArray[id].hospReason6 = false;
        updatedArray[id].hospReason7 = false;
        updatedArray[id].hosp_reason_oth_txt = "";

        updatedArray[id].hospReasonEmptyHard = false;
        updatedArray[id].hospReasonTxtEmpty = false;
        setLinvoHospDetails(updatedArray);
        break;
      }
      //end
      // Q38 retired - all Q38 field-change handler cases commented out below
      /*
      //Q38 Elr radio
      case "Q38ElrexvayliRd" + id: {
        let updatedArray = [...Q38ElrexDetails];

        updatedArray[id].Q38ElrexvayliRd = value_int;
        updatedArray[id].dt_dosefreq_elr_dt = "";

        updatedArray[id].Q38ElrexEmptyHard = false;
        updatedArray[id].Invalid_dt_dosefreq_elr_dt = false;

        updatedArray[id].Q38DateEmpty = false;
        updatedArray[id].Q38ElrexDateInvalid = false;
        setQ38ElrexDetails(updatedArray);
        break;
      }

      //Q38Linvoseltamab
      case "Q38LinvoveyRd" + id: {
        let updatedArray = [...Q38LinvoDetails];

        updatedArray[id].Q38LinvoveyRd = value_int;
        updatedArray[id].dt_dosefreq_linvo_dt = "";

        // updatedArray[id].dt_discharge_linvoHard = false;
        updatedArray[id].Q38LinvoEmptyHard = false;
        updatedArray[id].Q38LinvoDateEmpty = false;
        updatedArray[id].Q38LinvoDateInvalid = false;
        updatedArray[id].InvalidQ38Linvoveydate = false;
        setQ38LinvoDetails(updatedArray);
        break;
      }

      //Q38 Elr for frequency dosage
      case "q38_Elr_Dose_week_frquencyRd" + id: {
        let updatedArray = [...Q38ElrexDetails];

        updatedArray[id].q38_Elr_Dose_week_frquencyRd = Number(value);
        updatedArray[id].q38_Elr_Dose_week_frquencyRd_Empty = false;
        updatedArray[id].q38_Elr_Dose_week_frquency_txt_Empty = false;
        updatedArray[id].q38_Elr_Dose_week_frquency_txt = "";
        updatedArray[id].q38ElrDoseFrequencyConflict = false;
        setQ38ElrexDetails(updatedArray);
        break;
      }

      case "q38_Elr_Dose_week_frquency_txt" + id: {
        let updatedArray = [...Q38ElrexDetails];

        const reqValue = value;
        updatedArray[id].q38_Elr_Dose_week_frquency_txt = reqValue;
        updatedArray[id].q38_Elr_Dose_week_frquencyRd = 1;
        updatedArray[id].q38_Elr_Dose_week_frquencyRd_Empty = false;
        updatedArray[id].q38_Elr_Dose_week_frquency_txt_Empty = false;
        updatedArray[id].q38ElrDoseFrequencyConflict = false;

        setQ38ElrexDetails(updatedArray);
        break;
      }

      case "q38_Linvo_Dose_week_frquencyRd" + id: {
        let updatedArray = [...Q38LinvoDetails];

        updatedArray[id].q38_Linvo_Dose_week_frquencyRd = Number(value);
        updatedArray[id].q38_Linvo_Dose_week_frquencyRd_Empty = false;
        updatedArray[id].q38_Linvo_Dose_week_frquency_txt_Empty = false;
        updatedArray[id].q38_Linvo_Dose_week_frquency_txt = "";
        updatedArray[id].q38LinvoDoseFrequencyConflict = false;
        setQ38LinvoDetails(updatedArray);
        break;
      }

      case "q38_Linvo_Dose_week_frquency_txt" + id: {
        let updatedArray = [...Q38LinvoDetails];

        const reqValue = value;
        updatedArray[id].q38_Linvo_Dose_week_frquency_txt = reqValue;
        updatedArray[id].q38_Linvo_Dose_week_frquencyRd = 1;
        updatedArray[id].q38_Linvo_Dose_week_frquencyRd_Empty = false;
        updatedArray[id].q38_Linvo_Dose_week_frquency_txt_Empty = false;
        updatedArray[id].q38LinvoDoseFrequencyConflict = false;

        setQ38LinvoDetails(updatedArray);
        break;
      }

      case "q38_Linvo_Dosage_Rd" + id: {
        let updatedArray = [...Q38LinvoDetails];

        updatedArray[id].q38_Linvo_Dosage_Rd = Number(value);
        updatedArray[id].q38_Linvo_Dosage_OtherTxt = "";
        updatedArray[id].q38_Linvo_Dosage_Rd_Empty = false;
        updatedArray[id].q38_Linvo_Dosage_OtherTxt_Empty = false;

        setQ38LinvoDetails(updatedArray);
        break;
      }

      case "q38_Linvo_Dosage_OtherTxt" + id: {
        let updatedArray = [...Q38LinvoDetails];

        const reqValue = value;
        updatedArray[id].q38_Linvo_Dosage_Rd = 3;
        updatedArray[id].q38_Linvo_Dosage_OtherTxt = reqValue;
        updatedArray[id].q38_Linvo_Dosage_Rd_Empty = false;
        updatedArray[id].q38_Linvo_Dosage_OtherTxt_Empty = false;

        setQ38LinvoDetails(updatedArray);
        break;
      }
      */

      // ========================================
      // Q38.1.A ELREXFIO HANDLERS
      // ========================================

      // Column B: Type of change (Main Radio)
      case "changeTypeElr" + id: {
        let updatedArray = [...Q381AElrexDetails];
        updatedArray[id].changeType = Number(value);
        updatedArray[id].changeTypeEmpty = false;

        // Reset sub-radio fields
        updatedArray[id].changeTypeDoseFrequency = null;
        updatedArray[id].changeTypeDoseStrength = null;
        updatedArray[id].changeTypeDoseFrequencyEmpty = false;
        updatedArray[id].changeTypeDoseStrengthEmpty = false;

        // Reset conflict flags
        updatedArray[id].frequencyIncreaseConflict = false;
        updatedArray[id].frequencyDecreaseConflict = false;
        updatedArray[id].strengthIncreaseConflict = false;
        updatedArray[id].strengthDecreaseConflict = false;
        updatedArray[id].patternBeforeUnknownWarning = false;
        updatedArray[id].patternAfterUnknownWarning = false;

        if (
          updatedArray[id].reasonList &&
          Array.isArray(updatedArray[id].reasonList)
        ) {
          updatedArray[id].reasonList = updatedArray[id].reasonList.map(
            (reason) => ({
              ...reason,
              isSelected: false,
              reasonText: "",
              aeSubReasons:
                reason.aeSubReasons?.map((ae) => ({
                  ...ae,
                  isSelected: false,
                })) || [],
            }),
          );
        }

        // Reset columns C-F if radio change selected

        // Column C
        updatedArray[id].selectedReasons = [];
        updatedArray[id].selectedAEReasons = [];
        updatedArray[id].reasonOtherText = "";
        updatedArray[id].reasonEmpty = false;
        updatedArray[id].deathDtWarning = false;
        updatedArray[id].reasonOtherTextEmpty = false;

        updatedArray[id].neurologicAEReasonList = resetNeurologicReasons(
          updatedArray[id].neurologicAEReasonList,
        );

        updatedArray[id].aeSubReasonEmpty = false;
        updatedArray[id].neuroReasonEmpty = false;
        updatedArray[id].neuroOtherTextEmpty = false;

        // Column D
        updatedArray[id].patterBeforeChangeDoseFrequency = null;
        updatedArray[id].patterBeforeChangeDoseFrequencyWeek = "";
        updatedArray[id].patterBeforeChangeDoseStrength = null;

        updatedArray[id].patterBeforeChangeDoseStrengthMg = "";
        updatedArray[id].patterBeforeChangeDoseStrengthMgKg = "";
        updatedArray[id].patterBeforeChangeDoseStrengthMgM2 = "";
        updatedArray[id].patternBeforeFrequencyEmpty = false;
        updatedArray[id].patternBeforeFrequencyRangeInvalid = false;
        updatedArray[id].patternBeforeStrengthEmpty = false;
        updatedArray[id].patternBeforeFrequencyRangeInvalid = false;

        // Column E
        updatedArray[id].patterAfterChangeDoseFrequency = null;
        updatedArray[id].patterAfterChangeDoseFrequencyWeek = "";
        updatedArray[id].patterAfterChangeDoseStrength = null;

        updatedArray[id].patterAfterChangeDoseStrengthMg = ""; // For radio value 1
        updatedArray[id].patterAfterChangeDoseStrengthMgKg = ""; // For radio value 2
        updatedArray[id].patterAfterChangeDoseStrengthMgM2 = ""; // For radio value 3
        updatedArray[id].patternAfterFrequencyEmpty = false;
        updatedArray[id].patternAfterFrequencyRangeInvalid = false;
        updatedArray[id].patternAfterStrengthEmpty = false;
        updatedArray[id].patternAfterStrengthRangeInvalid = false;

        // Column F
        updatedArray[id].dateChangeRd = null;
        updatedArray[id].dateChange = "";
        updatedArray[id].dateChangeRdEmpty = false;
        updatedArray[id].dateChangeEmpty = false;
        updatedArray[id].invalidDateChange = false;
        updatedArray[id].dateChangeOutOfBound = false;
        updatedArray[id].dateChangeBeforeEOF = false;

        if (Number(id) === 0 && (Number(value) === 1 || Number(value) === 99)) {
          updatedArray = [updatedArray[0]];
        }

        setQ381AElrexDetails(updatedArray);
        break;
      }

      // Column B: Frequency sub-radio (Increase/Decrease)
      case "changeTypeDoseFrequencyElr" + id: {
        let updatedArray = [...Q381AElrexDetails];
        updatedArray[id].changeTypeDoseFrequency = Number(value);
        updatedArray[id].changeTypeDoseFrequencyEmpty = false;
        updatedArray[id].frequencyIncreaseConflict = false;
        updatedArray[id].frequencyDecreaseConflict = false;
        setQ381AElrexDetails(updatedArray);
        break;
      }

      // Column B: Strength sub-radio (Increase/Decrease)
      case "changeTypeDoseStrengthElr" + id: {
        let updatedArray = [...Q381AElrexDetails];
        updatedArray[id].changeTypeDoseStrength = Number(value);
        updatedArray[id].changeTypeDoseStrengthEmpty = false;
        updatedArray[id].strengthIncreaseConflict = false;
        updatedArray[id].strengthDecreaseConflict = false;
        setQ381AElrexDetails(updatedArray);
        break;
      }

      // ✅ CORRECTED - Column C Reason Checkboxes Handler
      case "selectedReasonsElr":
        {
          let updatedArray = [...Q381AElrexDetails];
          const reasonId = Number(value);

          // Update reasonList - toggle isSelected
          updatedArray[id].reasonList = updatedArray[id].reasonList.map(
            (reason) => {
              if (
                reason.bispecifictreatmentdiscontinuationreasonid === reasonId
              ) {
                return {
                  ...reason,
                  isSelected: !reason.isSelected,
                };
              }
              return reason;
            },
          );

          // ✅ SYNC to selectedReasons array
          if (updatedArray[id].selectedReasons.includes(reasonId)) {
            updatedArray[id].selectedReasons = updatedArray[
              id
            ].selectedReasons.filter((r) => r !== reasonId);
          } else {
            updatedArray[id].selectedReasons.push(reasonId);
          }

          updatedArray[id].reasonEmpty = false;
          updatedArray[id].deathDtWarning = false;

          // Clear Other text if unchecking Other option (ID=14)
          if (
            reasonId === 14 &&
            !updatedArray[id].selectedReasons.includes(14)
          ) {
            updatedArray[id].reasonOtherText = "";
            updatedArray[id].reasonOtherTextEmpty = false;
          }

          setQ381AElrexDetails(updatedArray);
        }
        break;

      // ✅ CORRECTED - Column C AE Reason Checkboxes Handler
      case "selectedAEReasonsElr":
        {
          let updatedArray = [...Q381AElrexDetails];
          const aeReasonId = Number(value);
          const parentReasonId = 2; // Adverse event parent ID

          // Update reasonList - find parent reason and toggle AE sub-reason
          updatedArray[id].reasonList = updatedArray[id].reasonList.map(
            (reason) => {
              if (
                reason.bispecifictreatmentdiscontinuationreasonid ===
                parentReasonId
              ) {
                return {
                  ...reason,
                  aeSubReasons: reason.aeSubReasons?.map((ae) => {
                    if (
                      ae.bispecifictreatmentdiscontinuationreasonaeid ===
                      aeReasonId
                    ) {
                      return {
                        ...ae,
                        isSelected: !ae.isSelected,
                      };
                    }
                    return ae;
                  }),
                };
              }
              return reason;
            },
          );

          // ✅ SYNC to selectedAEReasons array
          if (updatedArray[id].selectedAEReasons.includes(aeReasonId)) {
            updatedArray[id].selectedAEReasons = updatedArray[
              id
            ].selectedAEReasons.filter((r) => r !== aeReasonId);
          } else {
            updatedArray[id].selectedAEReasons.push(aeReasonId);
          }

          setQ381AElrexDetails(updatedArray);
        }
        break;

      // ✅ CORRECTED - Column C Reason Other Text Handler
      case "reasonOtherTextElr" + id: {
        let updatedArray = [...Q381AElrexDetails];
        updatedArray[id].reasonOtherText = value;
        updatedArray[id].reasonOtherTextEmpty = false;

        // Auto-check "Other" option (ID14) if text is entered
        if (value && !updatedArray[id].selectedReasons.includes(14)) {
          updatedArray[id].selectedReasons.push(14);

          // Also update reasonList
          updatedArray[id].reasonList = updatedArray[id].reasonList.map(
            (reason) => {
              if (reason.bispecifictreatmentdiscontinuationreasonid === 14) {
                return { ...reason, isSelected: true, reasonText: value };
              }
              return reason;
            },
          );
        }

        // ✅ ADD THIS: Uncheck "Unknown" when typing in Other text
        if (value) {
          updatedArray[id].reasonUnknown = false;
        }

        setQ381AElrexDetails(updatedArray);
        break;
      }
      // ✅ NEW: Column C - Reason Unknown Radio (ELREXFIO)
      case "reasonUnknownElr" + id: {
        let updatedArray = [...Q381AElrexDetails];
        const isChecked = value === "true" || value === true;

        updatedArray[id].reasonUnknown = isChecked;

        if (isChecked) {
          //  Clear ALL checkboxes when Unknown is selected
          updatedArray[id].reasonList = updatedArray[id].reasonList.map(
            (reason) => ({
              ...reason,
              isSelected: false,
              reasonText: "",
              // Also clear nested AE sub-reasons
              aeSubReasons: reason.aeSubReasons?.map((ae) => ({
                ...ae,
                isSelected: false,
              })),
            }),
          );

          //  Clear selected arrays
          updatedArray[id].selectedReasons = [];
          updatedArray[id].selectedAEReasons = [];
          updatedArray[id].reasonOtherText = "";
          updatedArray[id].neurologicAEReasonList = resetNeurologicReasons(
            updatedArray[id].neurologicAEReasonList,
          );

          updatedArray[id].aeSubReasonEmpty = false;
          updatedArray[id].neuroReasonEmpty = false;
          updatedArray[id].neuroOtherTextEmpty = false;

          //  Clear "Other" text
          const otherReason = updatedArray[id].reasonList.find(
            (r) => r.bispecifictreatmentdiscontinuationreasonid === 14,
          );
          if (otherReason) {
            otherReason.reasonOtherText = null;
          }
        }

        updatedArray[id].reasonEmpty = false;
        updatedArray[id].deathDtWarning = false;
        setQ381AElrexDetails(updatedArray);
        break;
      }
      // ✅ Column D: Pattern BEFORE - Frequency Radio
      case "patterBeforeChangeDoseFrequencyElr" + id: {
        let updatedArray = [...Q381AElrexDetails];
        const int_value = Number(value);

        updatedArray[id].patterBeforeChangeDoseFrequency = int_value;

        // ✅ Clear BOTH frequency text fields when radio changes manually
        updatedArray[id].patterBeforeChangeDoseFrequencyTimesPerWeek = "";
        updatedArray[id].patterBeforeChangeDoseFrequencyWeek = "";

        updatedArray[id].patternBeforeFrequencyEmpty = false;
        updatedArray[id].patternBeforeFrequencyRangeInvalid = false;
        updatedArray[id].frequencyIncreaseConflict = false;
        updatedArray[id].frequencyDecreaseConflict = false;
        updatedArray[id].patternBeforeUnknownWarning = int_value === 99;
        setQ381AElrexDetails(updatedArray);
        break;
      }

      // ✅ Column D: Pattern BEFORE - Times per week input (NEW)
      case "patterBeforeChangeDoseFrequencyTimesPerWeekElr" + id: {
        let updatedArray = [...Q381AElrexDetails];
        updatedArray[id].patterBeforeChangeDoseFrequencyTimesPerWeek = value;

        // ✅ Auto-select radio option 1 when user types
        if (value && value.trim() !== "") {
          updatedArray[id].patterBeforeChangeDoseFrequency = 1;
          updatedArray[id].patterBeforeChangeDoseFrequencyWeek = ""; // Clear other field
        }

        updatedArray[id].patternBeforeFrequencyEmpty = false;
        updatedArray[id].patternBeforeFrequencyRangeInvalid = false;
        updatedArray[id].frequencyIncreaseConflict = false;
        updatedArray[id].frequencyDecreaseConflict = false;
        updatedArray[id].patternBeforeUnknownWarning = false;
        setQ381AElrexDetails(updatedArray);
        break;
      }

      // ✅ Column D: Pattern BEFORE - Every X weeks input
      case "patterBeforeChangeDoseFrequencyWeekElr" + id: {
        let updatedArray = [...Q381AElrexDetails];
        updatedArray[id].patterBeforeChangeDoseFrequencyWeek = value;

        // ✅ Auto-select radio option 2 when user types
        if (value && value.trim() !== "") {
          updatedArray[id].patterBeforeChangeDoseFrequency = 2;
          updatedArray[id].patterBeforeChangeDoseFrequencyTimesPerWeek = ""; // Clear other field
        }

        updatedArray[id].patternBeforeFrequencyEmpty = false;
        updatedArray[id].patternBeforeFrequencyRangeInvalid = false;
        updatedArray[id].frequencyIncreaseConflict = false;
        updatedArray[id].frequencyDecreaseConflict = false;
        updatedArray[id].patternBeforeUnknownWarning = false;
        setQ381AElrexDetails(updatedArray);
        break;
      }

      // Column D: Pattern BEFORE - Strength Type (Radio)
      case "patterBeforeChangeDoseStrengthElr" + id: {
        let updatedArray = [...Q381AElrexDetails];
        const int_value = Number(value);

        updatedArray[id].patterBeforeChangeDoseStrength = int_value;

        // ✅ Clear ALL text fields when radio changes
        updatedArray[id].patterBeforeChangeDoseStrengthMg = "";
        updatedArray[id].patterBeforeChangeDoseStrengthMgKg = "";
        updatedArray[id].patterBeforeChangeDoseStrengthMgM2 = "";

        updatedArray[id].patternBeforeStrengthEmpty = false;
        updatedArray[id].patternBeforeFrequencyRangeInvalid = false;
        updatedArray[id].patternBeforeStre = false;
        updatedArray[id].strengthIncreaseConflict = false;
        updatedArray[id].strengthDecreaseConflict = false;
        updatedArray[id].patternBeforeUnknownWarning = int_value === 99;
        setQ381AElrexDetails(updatedArray);
        break;
      }

      // ✅ Column D: Pattern BEFORE - mg text input
      case "patterBeforeChangeDoseStrengthMgElr" + id: {
        let updatedArray = [...Q381AElrexDetails];
        updatedArray[id].patterBeforeChangeDoseStrengthMg = value;

        // ✅ Auto-select radio and clear others
        if (value && value.trim() !== "") {
          updatedArray[id].patterBeforeChangeDoseStrength = 1;
          updatedArray[id].patterBeforeChangeDoseStrengthMgKg = "";
          updatedArray[id].patterBeforeChangeDoseStrengthMgM2 = "";
        }

        updatedArray[id].patternBeforeStrengthEmpty = false;
        updatedArray[id].patternBeforeFrequencyRangeInvalid = false;
        updatedArray[id].patternBeforeUnknownWarning = false;
        updatedArray[id].strengthIncreaseConflict = false;
        updatedArray[id].strengthDecreaseConflict = false;
        setQ381AElrexDetails(updatedArray);
        break;
      }

      // ✅ Column D: Pattern BEFORE - mg/kg text input
      case "patterBeforeChangeDoseStrengthMgKgElr" + id: {
        let updatedArray = [...Q381AElrexDetails];
        updatedArray[id].patterBeforeChangeDoseStrengthMgKg = value;

        if (value && value.trim() !== "") {
          updatedArray[id].patterBeforeChangeDoseStrength = 2;
          updatedArray[id].patterBeforeChangeDoseStrengthMg = "";
          updatedArray[id].patterBeforeChangeDoseStrengthMgM2 = "";
        }

        updatedArray[id].patternBeforeStrengthEmpty = false;
        updatedArray[id].patternBeforeFrequencyRangeInvalid = false;
        updatedArray[id].patternBeforeUnknownWarning = false;
        updatedArray[id].strengthIncreaseConflict = false;
        updatedArray[id].strengthDecreaseConflict = false;
        setQ381AElrexDetails(updatedArray);
        break;
      }

      // ✅ Column D: Pattern BEFORE - mg/m² text input
      case "patterBeforeChangeDoseStrengthMgM2Elr" + id: {
        let updatedArray = [...Q381AElrexDetails];
        updatedArray[id].patterBeforeChangeDoseStrengthMgM2 = value;

        if (value && value.trim() !== "") {
          updatedArray[id].patterBeforeChangeDoseStrength = 3;
          updatedArray[id].patterBeforeChangeDoseStrengthMg = "";
          updatedArray[id].patterBeforeChangeDoseStrengthMgKg = "";
        }

        updatedArray[id].patternBeforeStrengthEmpty = false;
        updatedArray[id].patternBeforeFrequencyRangeInvalid = false;
        updatedArray[id].patternBeforeUnknownWarning = false;
        updatedArray[id].strengthIncreaseConflict = false;
        updatedArray[id].strengthDecreaseConflict = false;
        setQ381AElrexDetails(updatedArray);
        break;
      }

      // ✅ Column E: Pattern AFTER - Frequency Radio
      case "patterAfterChangeDoseFrequencyElr" + id: {
        let updatedArray = [...Q381AElrexDetails];
        const int_value = Number(value);

        updatedArray[id].patterAfterChangeDoseFrequency = int_value;

        // ✅ Clear BOTH frequency text fields
        updatedArray[id].patterAfterChangeDoseFrequencyTimesPerWeek = "";
        updatedArray[id].patterAfterChangeDoseFrequencyWeek = "";

        updatedArray[id].patternAfterFrequencyEmpty = false;
        updatedArray[id].patternAfterFrequencyRangeInvalid = false;
        updatedArray[id].frequencyIncreaseConflict = false;
        updatedArray[id].frequencyDecreaseConflict = false;
        updatedArray[id].patternAfterUnknownWarning = int_value === 99;
        setQ381AElrexDetails(updatedArray);
        break;
      }

      // ✅ Column E: Pattern AFTER - Times per week input (NEW)
      case "patterAfterChangeDoseFrequencyTimesPerWeekElr" + id: {
        let updatedArray = [...Q381AElrexDetails];
        updatedArray[id].patterAfterChangeDoseFrequencyTimesPerWeek = value;

        if (value && value.trim() !== "") {
          updatedArray[id].patterAfterChangeDoseFrequency = 1;
          updatedArray[id].patterAfterChangeDoseFrequencyWeek = "";
        }

        updatedArray[id].patternAfterFrequencyEmpty = false;
        updatedArray[id].patternAfterFrequencyRangeInvalid = false;
        updatedArray[id].frequencyIncreaseConflict = false;
        updatedArray[id].frequencyDecreaseConflict = false;
        updatedArray[id].patternAfterUnknownWarning = false;
        setQ381AElrexDetails(updatedArray);
        break;
      }

      // ✅ Column E: Pattern AFTER - Every X weeks input
      case "patterAfterChangeDoseFrequencyWeekElr" + id: {
        let updatedArray = [...Q381AElrexDetails];
        updatedArray[id].patterAfterChangeDoseFrequencyWeek = value;

        if (value && value.trim() !== "") {
          updatedArray[id].patterAfterChangeDoseFrequency = 2;
          updatedArray[id].patterAfterChangeDoseFrequencyTimesPerWeek = "";
        }

        updatedArray[id].patternAfterFrequencyEmpty = false;
        updatedArray[id].patternAfterFrequencyRangeInvalid = false;
        updatedArray[id].frequencyIncreaseConflict = false;
        updatedArray[id].frequencyDecreaseConflict = false;
        updatedArray[id].patternAfterUnknownWarning = false;
        setQ381AElrexDetails(updatedArray);
        break;
      }

      // Column E: Pattern AFTER - Strength Type (Radio)
      case "patterAfterChangeDoseStrengthElr" + id: {
        let updatedArray = [...Q381AElrexDetails];
        const int_value = Number(value);

        updatedArray[id].patterAfterChangeDoseStrength = int_value;

        // ✅ Clear ALL text fields when radio changes
        updatedArray[id].patterAfterChangeDoseStrengthMg = "";
        updatedArray[id].patterAfterChangeDoseStrengthMgKg = "";
        updatedArray[id].patterAfterChangeDoseStrengthMgM2 = "";

        updatedArray[id].patternAfterStrengthEmpty = false;
        updatedArray[id].patternAfterStrengthRangeInvalid = false;
        updatedArray[id].strengthIncreaseConflict = false;
        updatedArray[id].strengthDecreaseConflict = false;
        updatedArray[id].patternAfterUnknownWarning = int_value === 99;
        setQ381AElrexDetails(updatedArray);
        break;
      }

      // ✅ Column E: Pattern AFTER - mg text input
      case "patterAfterChangeDoseStrengthMgElr" + id: {
        let updatedArray = [...Q381AElrexDetails];
        updatedArray[id].patterAfterChangeDoseStrengthMg = value;

        if (value && value.trim() !== "") {
          updatedArray[id].patterAfterChangeDoseStrength = 1;
          updatedArray[id].patterAfterChangeDoseStrengthMgKg = "";
          updatedArray[id].patterAfterChangeDoseStrengthMgM2 = "";
        }

        updatedArray[id].patternAfterStrengthEmpty = false;
        updatedArray[id].patternAfterStrengthRangeInvalid = false;
        updatedArray[id].strengthIncreaseConflict = false;
        updatedArray[id].strengthDecreaseConflict = false;
        updatedArray[id].patternAfterUnknownWarning = false;
        setQ381AElrexDetails(updatedArray);
        break;
      }

      // ✅ Column E: Pattern AFTER - mg/kg text input
      case "patterAfterChangeDoseStrengthMgKgElr" + id: {
        let updatedArray = [...Q381AElrexDetails];
        updatedArray[id].patterAfterChangeDoseStrengthMgKg = value;

        if (value && value.trim() !== "") {
          updatedArray[id].patterAfterChangeDoseStrength = 2;
          updatedArray[id].patterAfterChangeDoseStrengthMg = "";
          updatedArray[id].patterAfterChangeDoseStrengthMgM2 = "";
        }

        updatedArray[id].patternAfterStrengthEmpty = false;
        updatedArray[id].patternAfterStrengthRangeInvalid = false;
        updatedArray[id].strengthIncreaseConflict = false;
        updatedArray[id].strengthDecreaseConflict = false;
        updatedArray[id].patternAfterUnknownWarning = false;
        setQ381AElrexDetails(updatedArray);
        break;
      }

      // ✅ Column E: Pattern AFTER - mg/m² text input
      case "patterAfterChangeDoseStrengthMgM2Elr" + id: {
        let updatedArray = [...Q381AElrexDetails];
        updatedArray[id].patterAfterChangeDoseStrengthMgM2 = value;

        if (value && value.trim() !== "") {
          updatedArray[id].patterAfterChangeDoseStrength = 3;
          updatedArray[id].patterAfterChangeDoseStrengthMg = "";
          updatedArray[id].patterAfterChangeDoseStrengthMgKg = "";
        }

        updatedArray[id].patternAfterStrengthEmpty = false;
        updatedArray[id].patternAfterStrengthRangeInvalid = false;
        updatedArray[id].strengthIncreaseConflict = false;
        updatedArray[id].strengthDecreaseConflict = false;
        updatedArray[id].patternAfterUnknownWarning = false;
        setQ381AElrexDetails(updatedArray);
        break;
      }

      // Column F: Date Radio (Date provided / Unknown)
      case "dateChangeRdElr" + id: {
        let updatedArray = [...Q381AElrexDetails];
        updatedArray[id].dateChangeRd = Number(value);
        updatedArray[id].dateChangeRdEmpty = false;
        updatedArray[id].dateChangeEmpty = false;
        updatedArray[id].invalidDateChange = false;
        updatedArray[id].dateChangeOutOfBound = false;
        updatedArray[id].dateChangeBeforeEOF = false;

        if (Number(value) === 99) {
          updatedArray[id].dateChange = "";
        }

        setQ381AElrexDetails(updatedArray);
        break;
      }

      // ============================================
      // Q38.1.A LINVOSELTAMAB HANDLERS
      // ============================================

      // Column B: Type of change (Main Radio)
      case "changeTypeLinvo" + id: {
        let updatedArray = [...Q381ALinvoDetails];
        updatedArray[id].changeType = Number(value);
        updatedArray[id].changeTypeEmpty = false;

        // Reset sub-radio fields
        updatedArray[id].changeTypeDoseFrequency = null;
        updatedArray[id].changeTypeDoseStrength = null;
        updatedArray[id].changeTypeDoseFrequencyEmpty = false;
        updatedArray[id].changeTypeDoseStrengthEmpty = false;

        // Reset conflict flags
        updatedArray[id].frequencyIncreaseConflict = false;
        updatedArray[id].frequencyDecreaseConflict = false;
        updatedArray[id].strengthIncreaseConflict = false;
        updatedArray[id].strengthDecreaseConflict = false;
        updatedArray[id].patternBeforeUnknownWarning = false;
        updatedArray[id].patternAfterUnknownWarning = false;

        if (
          updatedArray[id].reasonList &&
          Array.isArray(updatedArray[id].reasonList)
        ) {
          updatedArray[id].reasonList = updatedArray[id].reasonList.map(
            (reason) => ({
              ...reason,
              isSelected: false,
              reasonText: "",
              aeSubReasons:
                reason.aeSubReasons?.map((ae) => ({
                  ...ae,
                  isSelected: false,
                })) || [],
            }),
          );
        }

        // Reset columns C-F if "No changes" or "Unknown" selected
        //  if (Number(value) === 1 || Number(value) === 99) {
        // Column C
        updatedArray[id].selectedReasons = [];
        updatedArray[id].selectedAEReasons = [];
        updatedArray[id].reasonOtherText = "";
        updatedArray[id].reasonEmpty = false;
        updatedArray[id].deathDtWarning = false;
        updatedArray[id].reasonOtherTextEmpty = false;
        updatedArray[id].neurologicAEReasonList = resetNeurologicReasons(
          updatedArray[id].neurologicAEReasonList,
        );

        updatedArray[id].aeSubReasonEmpty = false;
        updatedArray[id].neuroReasonEmpty = false;
        updatedArray[id].neuroOtherTextEmpty = false;
        // Column D
        updatedArray[id].patterBeforeChangeDoseFrequency = null;
        updatedArray[id].patterBeforeChangeDoseFrequencyTimesPerWeek = "";
        updatedArray[id].patterBeforeChangeDoseFrequencyWeek = "";
        updatedArray[id].patterBeforeChangeDoseStrength = null;
        updatedArray[id].patterBeforeChangeDoseStrengthMg = "";
        updatedArray[id].patterBeforeChangeDoseStrengthMgKg = "";
        updatedArray[id].patterBeforeChangeDoseStrengthMgM2 = "";
        updatedArray[id].patternBeforeFrequencyEmpty = false;
        updatedArray[id].patternBeforeFrequencyRangeInvalid = false;
        updatedArray[id].patternBeforeStrengthEmpty = false;
        updatedArray[id].patternBeforeFrequencyRangeInvalid = false;

        // Column E
        updatedArray[id].patterAfterChangeDoseFrequency = null;
        updatedArray[id].patterAfterChangeDoseFrequencyTimesPerWeek = "";
        updatedArray[id].patterAfterChangeDoseFrequencyWeek = "";
        updatedArray[id].patterAfterChangeDoseStrength = null;
        updatedArray[id].patterAfterChangeDoseStrengthMg = "";
        updatedArray[id].patterAfterChangeDoseStrengthMgKg = "";
        updatedArray[id].patterAfterChangeDoseStrengthMgM2 = "";
        updatedArray[id].patternAfterFrequencyEmpty = false;
        updatedArray[id].patternAfterFrequencyRangeInvalid = false;
        updatedArray[id].patternAfterStrengthEmpty = false;
        updatedArray[id].patternAfterStrengthRangeInvalid = false;

        // Column F
        updatedArray[id].dateChangeRd = null;
        updatedArray[id].dateChange = "";
        updatedArray[id].dateChangeRdEmpty = false;
        updatedArray[id].dateChangeEmpty = false;
        updatedArray[id].invalidDateChange = false;
        updatedArray[id].dateChangeOutOfBound = false;
        updatedArray[id].dateChangeBeforeEOF = false;

        if (Number(id) === 0 && (Number(value) === 1 || Number(value) === 99)) {
          updatedArray = [updatedArray[0]]; // Keep only row 0
        }
        //  }

        setQ381ALinvoDetails(updatedArray);
        break;
      }

      // Column B: Frequency sub-radio (Increase/Decrease)
      case "changeTypeDoseFrequencyLinvo" + id: {
        let updatedArray = [...Q381ALinvoDetails];
        updatedArray[id].changeTypeDoseFrequency = Number(value);
        updatedArray[id].changeTypeDoseFrequencyEmpty = false;
        updatedArray[id].frequencyIncreaseConflict = false;
        updatedArray[id].frequencyDecreaseConflict = false;
        setQ381ALinvoDetails(updatedArray);
        break;
      }

      // Column B: Strength sub-radio (Increase/Decrease)
      case "changeTypeDoseStrengthLinvo" + id: {
        let updatedArray = [...Q381ALinvoDetails];
        updatedArray[id].changeTypeDoseStrength = Number(value);
        updatedArray[id].changeTypeDoseStrengthEmpty = false;
        updatedArray[id].strengthIncreaseConflict = false;
        updatedArray[id].strengthDecreaseConflict = false;
        setQ381ALinvoDetails(updatedArray);
        break;
      }

      // ✅ CORRECTED - Linvoseltamab Reason Checkboxes
      case "selectedReasonsLinvo":
        {
          let updatedArray = [...Q381ALinvoDetails];
          const reasonId = Number(value);

          // Update reasonList
          updatedArray[id].reasonList = updatedArray[id].reasonList.map(
            (reason) => {
              if (
                reason.bispecifictreatmentdiscontinuationreasonid === reasonId
              ) {
                return {
                  ...reason,
                  isSelected: !reason.isSelected,
                };
              }
              return reason;
            },
          );

          // ✅ SYNC to selectedReasons array
          if (updatedArray[id].selectedReasons.includes(reasonId)) {
            updatedArray[id].selectedReasons = updatedArray[
              id
            ].selectedReasons.filter((r) => r !== reasonId);
          } else {
            updatedArray[id].selectedReasons.push(reasonId);
          }

          updatedArray[id].reasonEmpty = false;
          updatedArray[id].deathDtWarning = false;

          if (
            reasonId === 14 &&
            !updatedArray[id].selectedReasons.includes(14)
          ) {
            updatedArray[id].reasonOtherText = "";
            updatedArray[id].reasonOtherTextEmpty = false;
          }

          setQ381ALinvoDetails(updatedArray);
        }
        break;

      //  NEW: Column C - Reason Unknown Radio (LINVOSELTAMAB)
      case "reasonUnknownLinvo" + id: {
        let updatedArray = [...Q381ALinvoDetails];
        const isChecked = value === "true" || value === true;

        updatedArray[id].reasonUnknown = isChecked;
        updatedArray[id].reasonOtherText = "";
        if (isChecked) {
          //  Clear ALL checkboxes when Unknown is selected
          updatedArray[id].reasonList = updatedArray[id].reasonList.map(
            (reason) => ({
              ...reason,
              isSelected: false,
              reasonText: "",
              // Also clear nested AE sub-reasons
              aeSubReasons: reason.aeSubReasons?.map((ae) => ({
                ...ae,
                isSelected: false,
              })),
            }),
          );

          //  Clear selected arrays
          updatedArray[id].selectedReasons = [];
          updatedArray[id].selectedAEReasons = [];
          updatedArray[id].neurologicAEReasonList = resetNeurologicReasons(
            updatedArray[id].neurologicAEReasonList,
          );

          updatedArray[id].aeSubReasonEmpty = false;
          updatedArray[id].neuroReasonEmpty = false;
          updatedArray[id].neuroOtherTextEmpty = false;
          //  Clear "Other" text
          const otherReason = updatedArray[id].reasonList.find(
            (r) => r.bispecifictreatmentdiscontinuationreasonid === 14,
          );
          if (otherReason) {
            otherReason.reasonOtherText = null;
          }
        }

        updatedArray[id].reasonEmpty = false;
        updatedArray[id].deathDtWarning = false;
        setQ381ALinvoDetails(updatedArray);
        break;
      }
      // ✅ Column D: Pattern BEFORE - Frequency Radio
      case "patterBeforeChangeDoseFrequencyLinvo" + id: {
        let updatedArray = [...Q381ALinvoDetails];
        const int_value = Number(value);
        updatedArray[id].patterBeforeChangeDoseFrequency = int_value;

        // ✅ Clear BOTH frequency text fields when radio changes manually
        updatedArray[id].patterBeforeChangeDoseFrequencyWeek = "";
        updatedArray[id].patterBeforeChangeDoseFrequencyTimesPerWeek = "";

        updatedArray[id].patternBeforeFrequencyEmpty = false;
        updatedArray[id].patternBeforeFrequencyRangeInvalid = false;
        updatedArray[id].frequencyIncreaseConflict = false;
        updatedArray[id].frequencyDecreaseConflict = false;
        updatedArray[id].patternBeforeUnknownWarning = int_value === 99;
        setQ381ALinvoDetails(updatedArray);
        break;
      }

      // ✅ Column D: Pattern BEFORE - Times per week input (NEW)
      case "patterBeforeChangeDoseFrequencyTimesPerWeekLinvo" + id: {
        let updatedArray = [...Q381ALinvoDetails];
        updatedArray[id].patterBeforeChangeDoseFrequencyTimesPerWeek = value;

        // ✅ Auto-select radio option 1 when user types
        if (value && value.trim() !== "") {
          updatedArray[id].patterBeforeChangeDoseFrequency = 1;
          updatedArray[id].patterBeforeChangeDoseFrequencyWeek = ""; // Clear other field
        }

        updatedArray[id].patternBeforeFrequencyEmpty = false;
        updatedArray[id].patternBeforeFrequencyRangeInvalid = false;
        updatedArray[id].frequencyIncreaseConflict = false;
        updatedArray[id].frequencyDecreaseConflict = false;
        updatedArray[id].patternBeforeUnknownWarning = false;
        setQ381ALinvoDetails(updatedArray);
        break;
      }

      // ✅ Column D: Pattern BEFORE - Every X weeks input
      case "patterBeforeChangeDoseFrequencyWeekLinvo" + id: {
        let updatedArray = [...Q381ALinvoDetails];
        updatedArray[id].patterBeforeChangeDoseFrequencyWeek = value;

        // ✅ Auto-select radio option 2 when user types
        if (value && value.trim() !== "") {
          updatedArray[id].patterBeforeChangeDoseFrequency = 2;
          updatedArray[id].patterBeforeChangeDoseFrequencyTimesPerWeek = ""; // Clear other field
        }

        updatedArray[id].patternBeforeFrequencyEmpty = false;
        updatedArray[id].patternBeforeFrequencyRangeInvalid = false;
        updatedArray[id].frequencyIncreaseConflict = false;
        updatedArray[id].frequencyDecreaseConflict = false;
        updatedArray[id].patternBeforeUnknownWarning = false;
        setQ381ALinvoDetails(updatedArray);
        break;
      }

      // Column D: Pattern BEFORE - Strength Type (Radio)
      case "patterBeforeChangeDoseStrengthLinvo" + id: {
        let updatedArray = [...Q381ALinvoDetails];
        const int_value = Number(value);
        updatedArray[id].patterBeforeChangeDoseStrength = int_value;

        // ✅ Clear ALL text fields
        updatedArray[id].patterBeforeChangeDoseStrengthMg = "";
        updatedArray[id].patterBeforeChangeDoseStrengthMgKg = "";
        updatedArray[id].patterBeforeChangeDoseStrengthMgM2 = "";

        updatedArray[id].patternBeforeStrengthEmpty = false;
        updatedArray[id].patternBeforeFrequencyRangeInvalid = false;
        updatedArray[id].strengthIncreaseConflict = false;
        updatedArray[id].strengthDecreaseConflict = false;
        updatedArray[id].patternBeforeUnknownWarning = int_value === 99;
        setQ381ALinvoDetails(updatedArray);
        break;
      }

      // ✅ Column D: Pattern BEFORE - mg text input
      case "patterBeforeChangeDoseStrengthMgLinvo" + id: {
        let updatedArray = [...Q381ALinvoDetails];
        updatedArray[id].patterBeforeChangeDoseStrengthMg = value;

        // ✅ Auto-select radio and clear others
        if (value && value.trim() !== "") {
          updatedArray[id].patterBeforeChangeDoseStrength = 1;
          updatedArray[id].patterBeforeChangeDoseStrengthMgKg = "";
          updatedArray[id].patterBeforeChangeDoseStrengthMgM2 = "";
        }

        updatedArray[id].patternBeforeStrengthEmpty = false;
        updatedArray[id].patternBeforeFrequencyRangeInvalid = false;
        updatedArray[id].patternBeforeUnknownWarning = false;
        updatedArray[id].strengthIncreaseConflict = false;
        updatedArray[id].strengthDecreaseConflict = false;
        setQ381ALinvoDetails(updatedArray);
        break;
      }

      // ✅ Column D: Pattern BEFORE - mg/kg text input
      case "patterBeforeChangeDoseStrengthMgKgLinvo" + id: {
        let updatedArray = [...Q381ALinvoDetails];
        updatedArray[id].patterBeforeChangeDoseStrengthMgKg = value;

        if (value && value.trim() !== "") {
          updatedArray[id].patterBeforeChangeDoseStrength = 2;
          updatedArray[id].patterBeforeChangeDoseStrengthMg = "";
          updatedArray[id].patterBeforeChangeDoseStrengthMgM2 = "";
        }

        updatedArray[id].patternBeforeStrengthEmpty = false;
        updatedArray[id].patternBeforeFrequencyRangeInvalid = false;
        updatedArray[id].patternBeforeUnknownWarning = false;
        updatedArray[id].strengthIncreaseConflict = false;
        updatedArray[id].strengthDecreaseConflict = false;
        setQ381ALinvoDetails(updatedArray);
        break;
      }

      // ✅ Column D: Pattern BEFORE - mg/m² text input
      case "patterBeforeChangeDoseStrengthMgM2Linvo" + id: {
        let updatedArray = [...Q381ALinvoDetails];
        updatedArray[id].patterBeforeChangeDoseStrengthMgM2 = value;

        if (value && value.trim() !== "") {
          updatedArray[id].patterBeforeChangeDoseStrength = 3;
          updatedArray[id].patterBeforeChangeDoseStrengthMg = "";
          updatedArray[id].patterBeforeChangeDoseStrengthMgKg = "";
        }

        updatedArray[id].patternBeforeStrengthEmpty = false;
        updatedArray[id].patternBeforeFrequencyRangeInvalid = false;
        updatedArray[id].patternBeforeUnknownWarning = false;
        updatedArray[id].strengthIncreaseConflict = false;
        updatedArray[id].strengthDecreaseConflict = false;
        setQ381ALinvoDetails(updatedArray);
        break;
      }

      // ✅ Column E: Pattern AFTER - Frequency Radio
      case "patterAfterChangeDoseFrequencyLinvo" + id: {
        let updatedArray = [...Q381ALinvoDetails];
        const int_value = Number(value);
        updatedArray[id].patterAfterChangeDoseFrequency = int_value;

        // ✅ Clear BOTH frequency text fields
        updatedArray[id].patterAfterChangeDoseFrequencyTimesPerWeek = "";
        updatedArray[id].patterAfterChangeDoseFrequencyWeek = "";

        updatedArray[id].patternAfterFrequencyEmpty = false;
        updatedArray[id].patternAfterFrequencyRangeInvalid = false;
        updatedArray[id].frequencyIncreaseConflict = false;
        updatedArray[id].frequencyDecreaseConflict = false;
        updatedArray[id].patternAfterUnknownWarning = int_value === 99;
        setQ381ALinvoDetails(updatedArray);
        break;
      }

      // ✅ Column E: Pattern AFTER - Times per week input (NEW)
      case "patterAfterChangeDoseFrequencyTimesPerWeekLinvo" + id: {
        let updatedArray = [...Q381ALinvoDetails];
        updatedArray[id].patterAfterChangeDoseFrequencyTimesPerWeek = value;

        if (value && value.trim() !== "") {
          updatedArray[id].patterAfterChangeDoseFrequency = 1;
          updatedArray[id].patterAfterChangeDoseFrequencyWeek = "";
        }

        updatedArray[id].patternAfterFrequencyEmpty = false;
        updatedArray[id].patternAfterFrequencyRangeInvalid = false;
        updatedArray[id].frequencyIncreaseConflict = false;
        updatedArray[id].frequencyDecreaseConflict = false;
        updatedArray[id].patternAfterUnknownWarning = false;
        setQ381ALinvoDetails(updatedArray);
        break;
      }

      // ✅ Column E: Pattern AFTER - Every X weeks input
      case "patterAfterChangeDoseFrequencyWeekLinvo" + id: {
        let updatedArray = [...Q381ALinvoDetails];
        updatedArray[id].patterAfterChangeDoseFrequencyWeek = value;

        if (value && value.trim() !== "") {
          updatedArray[id].patterAfterChangeDoseFrequency = 2;
          updatedArray[id].patterAfterChangeDoseFrequencyTimesPerWeek = "";
        }

        updatedArray[id].patternAfterFrequencyEmpty = false;
        updatedArray[id].patternAfterFrequencyRangeInvalid = false;
        updatedArray[id].frequencyIncreaseConflict = false;
        updatedArray[id].frequencyDecreaseConflict = false;
        updatedArray[id].patternAfterUnknownWarning = false;
        setQ381ALinvoDetails(updatedArray);
        break;
      }

      // Column E: Pattern AFTER - Strength Type (Radio)
      case "patterAfterChangeDoseStrengthLinvo" + id: {
        let updatedArray = [...Q381ALinvoDetails];
        const int_value = Number(value);
        updatedArray[id].patterAfterChangeDoseStrength = int_value;

        // ✅ Clear ALL text fields
        updatedArray[id].patterAfterChangeDoseStrengthMg = "";
        updatedArray[id].patterAfterChangeDoseStrengthMgKg = "";
        updatedArray[id].patterAfterChangeDoseStrengthMgM2 = "";

        updatedArray[id].patternAfterStrengthEmpty = false;
        updatedArray[id].patternAfterStrengthRangeInvalid = false;
        updatedArray[id].strengthIncreaseConflict = false;
        updatedArray[id].strengthDecreaseConflict = false;
        updatedArray[id].patternAfterUnknownWarning = int_value === 99;
        setQ381ALinvoDetails(updatedArray);
        break;
      }

      // ✅ Column E: Pattern AFTER - mg text input
      case "patterAfterChangeDoseStrengthMgLinvo" + id: {
        let updatedArray = [...Q381ALinvoDetails];
        updatedArray[id].patterAfterChangeDoseStrengthMg = value;

        if (value && value.trim() !== "") {
          updatedArray[id].patterAfterChangeDoseStrength = 1;
          updatedArray[id].patterAfterChangeDoseStrengthMgKg = "";
          updatedArray[id].patterAfterChangeDoseStrengthMgM2 = "";
        }

        updatedArray[id].patternAfterStrengthEmpty = false;
        updatedArray[id].patternAfterUnknownWarning = false;
        updatedArray[id].patternAfterStrengthRangeInvalid = false;
        updatedArray[id].strengthIncreaseConflict = false;
        updatedArray[id].strengthDecreaseConflict = false;
        setQ381ALinvoDetails(updatedArray);
        break;
      }

      // ✅ Column E: Pattern AFTER - mg/kg text input
      case "patterAfterChangeDoseStrengthMgKgLinvo" + id: {
        let updatedArray = [...Q381ALinvoDetails];
        updatedArray[id].patterAfterChangeDoseStrengthMgKg = value;

        if (value && value.trim() !== "") {
          updatedArray[id].patterAfterChangeDoseStrength = 2;
          updatedArray[id].patterAfterChangeDoseStrengthMg = "";
          updatedArray[id].patterAfterChangeDoseStrengthMgM2 = "";
        }

        updatedArray[id].patternAfterStrengthEmpty = false;
        updatedArray[id].patternAfterUnknownWarning = false;
        updatedArray[id].patternAfterStrengthRangeInvalid = false;
        updatedArray[id].strengthIncreaseConflict = false;
        updatedArray[id].strengthDecreaseConflict = false;
        setQ381ALinvoDetails(updatedArray);
        break;
      }

      // ✅ Column E: Pattern AFTER - mg/m² text input
      case "patterAfterChangeDoseStrengthMgM2Linvo" + id: {
        let updatedArray = [...Q381ALinvoDetails];
        updatedArray[id].patterAfterChangeDoseStrengthMgM2 = value;

        if (value && value.trim() !== "") {
          updatedArray[id].patterAfterChangeDoseStrength = 3;
          updatedArray[id].patterAfterChangeDoseStrengthMg = "";
          updatedArray[id].patterAfterChangeDoseStrengthMgKg = "";
        }

        updatedArray[id].patternAfterStrengthEmpty = false;
        updatedArray[id].patternAfterUnknownWarning = false;
        updatedArray[id].patternAfterStrengthRangeInvalid = false;
        updatedArray[id].strengthIncreaseConflict = false;
        updatedArray[id].strengthDecreaseConflict = false;
        setQ381ALinvoDetails(updatedArray);
        break;
      }

      // Column F: Date Radio (Date provided / Unknown)
      case "dateChangeRdLinvo" + id: {
        let updatedArray = [...Q381ALinvoDetails];
        updatedArray[id].dateChangeRd = Number(value);
        updatedArray[id].dateChangeRdEmpty = false;
        updatedArray[id].dateChangeEmpty = false;
        updatedArray[id].invalidDateChange = false;
        updatedArray[id].dateChangeOutOfBound = false;
        updatedArray[id].dateChangeBeforeEOF = false;

        if (Number(value) === 99) {
          updatedArray[id].dateChange = "";
        }

        setQ381ALinvoDetails(updatedArray);
        break;
      }

      //39.1.A
      case "primaryProphylacticCrs_elr" + id: {
        let updatedArray = [...q391AElrTocilzumabList];
        updatedArray[id].primaryProphylacticCrs_elr =
          !updatedArray[id].primaryProphylacticCrs_elr;

        updatedArray[id].medicationReasonUnk_elr = false;

        updatedArray[id].q39ElrReasonEmpty = false;
        setQ391AElrTocilzumabList(updatedArray);
        break;
      }

      case "secondaryProphylacticCrs_elr" + id: {
        let updatedArray = [...q391AElrTocilzumabList];
        updatedArray[id].secondaryProphylacticCrs_elr =
          !updatedArray[id].secondaryProphylacticCrs_elr;
        updatedArray[id].medicationReasonUnk_elr = false;
        updatedArray[id].q39ElrReasonEmpty = false;
        setQ391AElrTocilzumabList(updatedArray);
        break;
      }

      case "supportiveCareCrs_elr" + id: {
        let updatedArray = [...q391AElrTocilzumabList];
        updatedArray[id].supportiveCareCrs_elr =
          !updatedArray[id].supportiveCareCrs_elr;
        updatedArray[id].q39ElrReasonEmpty = false;
        updatedArray[id].medicationReasonUnk_elr = false;

        setQ391AElrTocilzumabList(updatedArray);
        break;
      }

      case "primaryProphylacticNeurologic_elr" + id: {
        let updatedArray = [...q391AElrTocilzumabList];
        updatedArray[id].primaryProphylacticNeurologic_elr =
          !updatedArray[id].primaryProphylacticNeurologic_elr;
        updatedArray[id].q39ElrReasonEmpty = false;
        updatedArray[id].medicationReasonUnk_elr = false;

        setQ391AElrTocilzumabList(updatedArray);
        break;
      }

      case "secondaryProphylacticNeurologic_elr" + id: {
        let updatedArray = [...q391AElrTocilzumabList];
        updatedArray[id].secondaryProphylacticNeurologic_elr =
          !updatedArray[id].secondaryProphylacticNeurologic_elr;
        updatedArray[id].q39ElrReasonEmpty = false;
        updatedArray[id].medicationReasonUnk_elr = false;
        setQ391AElrTocilzumabList(updatedArray);
        break;
      }

      case "supportiveCareNeurologic_elr" + id: {
        let updatedArray = [...q391AElrTocilzumabList];
        updatedArray[id].supportiveCareNeurologic_elr =
          !updatedArray[id].supportiveCareNeurologic_elr;
        updatedArray[id].q39ElrReasonEmpty = false;
        updatedArray[id].medicationReasonUnk_elr = false;
        setQ391AElrTocilzumabList(updatedArray);
        break;
      }

      case "medicationReasonUnk_elr" + id: {
        let updatedArray = [...q391AElrTocilzumabList];
        updatedArray[id].medicationReasonUnk_elr =
          !updatedArray[id].medicationReasonUnk_elr;
        updatedArray[id].q39ElrReasonEmpty = false;

        updatedArray[id].supportiveCareCrs_elr = false;
        updatedArray[id].primaryProphylacticCrs_elr = false;
        updatedArray[id].secondaryProphylacticNeurologic_elr = false;
        updatedArray[id].secondaryProphylacticCrs_elr = false;
        updatedArray[id].supportiveCareNeurologic_elr = false;
        updatedArray[id].primaryProphylacticNeurologic_elr = false;
        setQ391AElrTocilzumabList(updatedArray);
        break;
      }

      //39.1.A -- linvo

      case "primaryProphylacticCrs_linvo" + id: {
        let updatedArray = [...q391ALinvoTocilzumabList];
        updatedArray[id].primaryProphylacticCrs_linvo =
          !updatedArray[id].primaryProphylacticCrs_linvo;

        updatedArray[id].q39LinvoReasonEmpty = false;
        updatedArray[id].medicationReasonUnk_linvo = false;

        setQ391ALinvoTocilzumabList(updatedArray);
        break;
      }

      case "secondaryProphylacticCrs_linvo" + id: {
        let updatedArray = [...q391ALinvoTocilzumabList];
        updatedArray[id].secondaryProphylacticCrs_linvo =
          !updatedArray[id].secondaryProphylacticCrs_linvo;

        updatedArray[id].q39LinvoReasonEmpty = false;
        updatedArray[id].medicationReasonUnk_linvo = false;

        setQ391ALinvoTocilzumabList(updatedArray);
        break;
      }

      case "supportiveCareCrs_linvo" + id: {
        let updatedArray = [...q391ALinvoTocilzumabList];
        updatedArray[id].supportiveCareCrs_linvo =
          !updatedArray[id].supportiveCareCrs_linvo;

        updatedArray[id].medicationReasonUnk_linvo = false;

        updatedArray[id].q39LinvoReasonEmpty = false;
        setQ391ALinvoTocilzumabList(updatedArray);
        break;
      }

      case "primaryProphylacticNeurologic_linvo" + id: {
        let updatedArray = [...q391ALinvoTocilzumabList];
        updatedArray[id].primaryProphylacticNeurologic_linvo =
          !updatedArray[id].primaryProphylacticNeurologic_linvo;
        updatedArray[id].q39LinvoReasonEmpty = false;
        updatedArray[id].medicationReasonUnk_linvo = false;

        setQ391ALinvoTocilzumabList(updatedArray);
        break;
      }

      case "secondaryProphylacticNeurologic_linvo" + id: {
        let updatedArray = [...q391ALinvoTocilzumabList];
        updatedArray[id].secondaryProphylacticNeurologic_linvo =
          !updatedArray[id].secondaryProphylacticNeurologic_linvo;
        updatedArray[id].q39LinvoReasonEmpty = false;
        updatedArray[id].medicationReasonUnk_linvo = false;

        setQ391ALinvoTocilzumabList(updatedArray);
        break;
      }

      case "supportiveCareNeurologic_linvo" + id: {
        let updatedArray = [...q391ALinvoTocilzumabList];
        updatedArray[id].supportiveCareNeurologic_linvo =
          !updatedArray[id].supportiveCareNeurologic_linvo;
        updatedArray[id].q39LinvoReasonEmpty = false;
        updatedArray[id].medicationReasonUnk_linvo = false;
        setQ391ALinvoTocilzumabList(updatedArray);
        break;
      }

      case "medicationReasonUnk_linvo" + id: {
        let updatedArray = [...q391ALinvoTocilzumabList];
        updatedArray[id].medicationReasonUnk_linvo =
          !updatedArray[id].medicationReasonUnk_linvo;
        updatedArray[id].q39LinvoReasonEmpty = false;

        updatedArray[id].supportiveCareNeurologic_linvo = false;
        updatedArray[id].primaryProphylacticCrs_linvo = false;
        updatedArray[id].secondaryProphylacticCrs_linvo = false;
        updatedArray[id].secondaryProphylacticNeurologic_linvo = false;

        updatedArray[id].primaryProphylacticNeurologic_linvo = false;
        updatedArray[id].supportiveCareCrs_linvo = false;
        setQ391ALinvoTocilzumabList(updatedArray);
        break;
      }

      case "administrationDateRD_elr" + id: {
        let updatedArray = [...q391AElrTocilzumabList];

        updatedArray[id].administrationDateRD_elr = value_int;
        updatedArray[id].administrationDate_elr = "";

        updatedArray[id].q39ElrAdministrationDateEmpty = false;
        updatedArray[id].q39ElrAdministrationRDEmpty = false;
        updatedArray[id].q39ElrInvalidAdministrationDate = false;
        setQ391AElrAdministrationDateOutOfRange(false);

        setQ391AElrTocilzumabList(updatedArray);
        break;
      }

      case "administrationDateRD_linvo" + id: {
        let updatedArray = [...q391ALinvoTocilzumabList];

        updatedArray[id].administrationDateRD_linvo = value_int;
        updatedArray[id].administrationDate_linvo = "";

        updatedArray[id].q39LinvoInvalidAdministrationDate = false;
        updatedArray[id].q39LinvoAdministrationDateEmpty = false;
        updatedArray[id].q39LinvoAdministrationRDEmpty = false;
        setQ391ALinvoAdministrationDateOutOfRange(false);
        setQ391ALinvoTocilzumabList(updatedArray);
        break;
      }

      default:
        break;
    }

    //
    let newCountElr = null;
    elrexvayliDetails.filter((item) => {
      if (
        item.dosing_phase_elr === 1 ||
        item.dosing_phase_elr === 2 ||
        item.dosing_phase_elr === 3
      ) {
        newCountElr = 1;
      }

      if (newCountElr > 0) {
        setQ39ElrexConditionMet(true);
      } else {
        setQ39ElrexConditionMet(false);
      }
    });

    //Linvoseltamab
    let newCountLinvo = null;
    linvoveyDetails.filter((item) => {
      if (
        item.dosing_phase_linvo === 1 ||
        item.dosing_phase_linvo === 2 ||
        item.dosing_phase_linvo === 3 ||
        item.dosing_phase_linvo === 4
      ) {
        newCountLinvo = 1;
      }

      if (newCountLinvo > 0) {
        setQ39LinvoConditionMet(true);
      } else {
        setQ39LinvoConditionMet(false);
      }
    });
  };

  const HandleRepeaterDateChange = (name, date) => {
    const lastIndex = name.lastIndexOf("-");
    const id = name.slice(lastIndex + 1);

    switch (name) {
      //ELREXFIO
      case "dose_administration_elr_dt-" + id: {
        let updatedArray = [...elrexvayliDetails];

        updatedArray[id].dose_administration_elr_dt = date;
        updatedArray[id].dose_administration_elr = 1;
        updatedArray[id].dose_administration_elrHard = false;

        updatedArray[id].dose_administration_elrHard_dt = false;
        updatedArray[id].Invalid_dose_administration_elr_dt = false;
        // updatedArray[id].dt_elr_greater_prevCheck = false;
        updatedArray[id].dt_elr_date_out_of_bound = false;
        updatedArray[id].dose_administration_elr_dt_before_eof = false;
        setIndexDateRRMM_elrHard(false);
        setIsElrLastAdminDateValid(true);
        setElrexDuplicateDates(false);
        setElrexvayliDetails(updatedArray);
        setDt_elr_2L_3L_date_conditions(false);
        break;
      }

      //36.1 start
      //B
      case "dt_admission_dt_elr-" + id: {
        let updatedArray = [...elrHospDetails];
        updatedArray[id].dt_admission_dt = date;
        updatedArray[id].dt_admission = 1;
        updatedArray[id].dt_admission_Hard = false;
        updatedArray[id].dt_admission_Hard_dt = false;
        updatedArray[id].Invalid_admission_Hard_dt = false;
        updatedArray[id].dt_admission_out_of_bound = false;

        const check = IsDateLessThanThirtyDays(
          updatedArray[id].dt_admission_dt,
          updatedArray[id].dt_discharge_dt,
        );
        if (!check) {
          updatedArray[id].dateRangeThirty = true;
        } else {
          updatedArray[id].dateRangeThirty = false;
        }
        setElrHospDetails(updatedArray);
        break;
      }
      //C
      case "dt_discharge_dt_elr-" + id: {
        let updatedArray = [...elrHospDetails];
        updatedArray[id].dt_discharge_dt = date;
        updatedArray[id].dt_discharge = 1;
        updatedArray[id].dt_discharge_Hard = false;
        updatedArray[id].dt_discharge_Hard_dt = false;
        updatedArray[id].dt_discharge_Hard_dt_check = false;
        updatedArray[id].dt_discharge_out_of_bound = false;
        updatedArray[id].Invalid_dt_discharge_Hard = false;
        updatedArray[id].dt_discharge_before_eof = false;

        const check = IsDateLessThanThirtyDays(
          updatedArray[id].dt_admission_dt,
          updatedArray[id].dt_discharge_dt,
        );
        if (!check) {
          updatedArray[id].dateRangeThirty = true;
        } else {
          updatedArray[id].dateRangeThirty = false;
        }
        setElrHospDetails(updatedArray);
        break;
      }
      //end
      //LINVOSELTAMAB
      case "dose_administration_linvo_dt-" + id: {
        let updatedArray = [...linvoveyDetails];

        updatedArray[id].dose_administration_linvo_dt = date;
        updatedArray[id].dose_administration_linvo = 1;
        updatedArray[id].dose_administration_linvoHard = false;
        updatedArray[id].Invalid_dose_administration_linvo_dt = false;
        // updatedArray[id].dt_admission_linvoHard_dt = false;
        // updatedArray[id].dt_admission_linvoHard_dt_check = false;

        // updatedArray[id].dt_linvo_greater_prevCheck = false;
        updatedArray[id].dt_linvo_date_out_of_bound = false;

        updatedArray[id].dt_admission_linvoHard_dt = false;
        setIndexDateRRMM_linvoHard(false);
        setIsLinvoLastAdminDateValid(true);
        setLinvoDuplicateDates(false);
        setLinvoveyDetails(updatedArray);
        setDt_lino_2L_3L_date_conditions(false);
        break;
      }

      //36.1 start
      //B
      case "dt_admission_dt_linvo-" + id: {
        let updatedArray = [...linvoHospDetails];

        updatedArray[id].dt_admission_dt = date;
        updatedArray[id].dt_admission = 1;
        updatedArray[id].dt_admission_Hard = false;
        updatedArray[id].Invalid_admission_Hard_dt = false;
        updatedArray[id].dt_admission_Hard_dt = false;
        updatedArray[id].dt_admission_out_of_bound = false;

        const check = IsDateLessThanThirtyDays(
          updatedArray[id].dt_admission_dt,
          updatedArray[id].dt_discharge_dt,
        );
        if (!check) {
          updatedArray[id].dateRangeThirty = true;
        } else {
          updatedArray[id].dateRangeThirty = false;
        }
        setLinvoHospDetails(updatedArray);

        break;
      }
      //C
      case "dt_discharge_dt_linvo-" + id: {
        let updatedArray = [...linvoHospDetails];

        updatedArray[id].dt_discharge_dt = date;
        updatedArray[id].dt_discharge = 1;

        updatedArray[id].dt_discharge_Hard = false;
        updatedArray[id].dt_discharge_Hard_dt = false;
        updatedArray[id].dt_discharge_Hard_dt_check = false;
        updatedArray[id].dt_discharge_out_of_bound = false;
        updatedArray[id].Invalid_dt_discharge_Hard = false;
        updatedArray[id].dt_discharge_before_eof = false;

        const check = IsDateLessThanThirtyDays(
          updatedArray[id].dt_admission_dt,
          updatedArray[id].dt_discharge_dt,
        );
        if (!check) {
          updatedArray[id].dateRangeThirty = true;
        } else {
          updatedArray[id].dateRangeThirty = false;
        }
        setLinvoHospDetails(updatedArray);

        break;
      }
      //end

      // Q38 retired
      /*
      //Q38 Elr date change
      case "dt_dosefreq_elr_dt-" + id: {
        let updatedArray = [...Q38ElrexDetails];

        updatedArray[id].dt_dosefreq_elr_dt = date;
        updatedArray[id].Q38ElrexvayliRd = 1;

        updatedArray[id].Q38ElrexEmptyHard = false;
        updatedArray[id].Invalid_dt_dosefreq_elr_dt = false;
        updatedArray[id].Q38DateEmpty = false;
        updatedArray[id].Q38ElrexDateInvalid = false;
        setQ38ElrexDetails(updatedArray);
        break;
      }

      //Q38 Linvoseltamab date change
      case "dt_dosefreq_linvo_dt-" + id: {
        let updatedArray = [...Q38LinvoDetails];

        updatedArray[id].dt_dosefreq_linvo_dt = date;
        updatedArray[id].Q38LinvoveyRd = 1;
        //  updatedArray[id].dt_discharge_linvoHard = false;
        updatedArray[id].Q38LinvoEmptyHard = false;
        updatedArray[id].Q38LinvoDateEmpty = false;
        updatedArray[id].Q38LinvoDateInvalid = false;
        updatedArray[id].InvalidQ38Linvoveydate = false;
        setQ38LinvoDetails(updatedArray);
        break;
      }
      */

      //40
      case "Q40init_dt-" + id: {
        let updatedArray = [...Q37ElrexDetails];

        updatedArray[id].Q40init_dt = date;
        updatedArray[id].Q40init_dt_Rd = 1;
        updatedArray[id].InvalidQ40init_dt = false;
        updatedArray[id].Q40Init_dtHard = false;
        updatedArray[id].Q40Init_RdHard = false;
        updatedArray[id].init_dt_before_eof = false;
        updatedArray[id].init_dt_full_chain_invalid = false;

        setQ37ElrexDetails(updatedArray);
        break;
      }

      case "q40Init_Dt_linvo-" + id: {
        let updatedArray = [...Q37LinvoDetails];

        updatedArray[id].q40Init_Dt_linvo = date;
        updatedArray[id].q40Init_Dt_Rd_linvo = 1;
        updatedArray[id].invalidQ40Init_dt_linvo = false;
        updatedArray[id].Q40LinvoInit_dtHard = false;
        updatedArray[id].Q40LinvoInit_RdHard = false;
        updatedArray[id].init_dt_before_eof_linvo = false;
        updatedArray[id].init_dt_full_chain_invalid_linvo = false;

        setQ37LinvoDetails(updatedArray);
        break;
      }

      //40.C
      case "ivigInitDt_elrC-" + id: {
        let updatedArray = [...Q37ElrexDetails];

        updatedArray[id].ivigInitDt_elrC = date;
        updatedArray[id].ivigInitRd_elrC = 2;
        updatedArray[id].ivigInitDtHard_elrC = false;
        updatedArray[id].ivigInitDtInvalid_elrC = false;
        updatedArray[id].ivigInitRdHard_elrC = false;
        updatedArray[id].ivigInitDtBeforeEof_elrC = false;
        updatedArray[id].ivigInitDtFullChainInvalid_elrC = false;

        setQ37ElrexDetails(updatedArray);
        break;
      }

      case "ivigInitDt_linvoC-" + id: {
        let updatedArray = [...Q37LinvoDetails];

        updatedArray[id].ivigInitDt_linvoC = date;
        updatedArray[id].ivigInitRd_linvoC = 2;
        updatedArray[id].ivigInitDtHard_linvoC = false;
        updatedArray[id].ivigInitDtInvalid_linvoC = false;
        updatedArray[id].ivigInitRdHard_linvoC = false;
        updatedArray[id].ivigInitDtBeforeEof_linvoC = false;
        updatedArray[id].ivigInitDtFullChainInvalid_linvoC = false;

        setQ37LinvoDetails(updatedArray);
        break;
      }

      //Q39.1.A
      case "administrationDate_elr-" + id: {
        let updatedArray = [...q391AElrTocilzumabList];

        updatedArray[id].administrationDate_elr = date;
        updatedArray[id].administrationDateRD_elr = 1;
        updatedArray[id].q39ElrInvalidAdministrationDate = false;
        updatedArray[id].q39ElrAdministrationDateEmpty = false;
        updatedArray[id].q39ElrAdministrationRDEmpty = false;
        setQ391AElrAdministrationDateOutOfRange(false);

        setQ391AElrTocilzumabList(updatedArray);
        break;
      }

      case "administrationDate_linvo-" + id: {
        let updatedArray = [...q391ALinvoTocilzumabList];

        updatedArray[id].administrationDate_linvo = date;
        updatedArray[id].administrationDateRD_linvo = 1;
        updatedArray[id].q39LinvoInvalidAdministrationDate = false;
        updatedArray[id].q39LinvoAdministrationDateEmpty = false;
        updatedArray[id].q39LinvoAdministrationRDEmpty = false;

        setQ391ALinvoAdministrationDateOutOfRange(false);
        setQ391ALinvoTocilzumabList(updatedArray);
        break;
      }

      //39.1.C
      case "tociliAdmsDate_elrC-" + id: {
        let updatedArray = [...Q37ElrexDetails];
        updatedArray[id].tociliAdmsDate_elrC = date;
        updatedArray[id].tociliAdmsDateRD_elrC = 1;
        updatedArray[id].tociliAdmsDateInvalid_elrC = false;
        updatedArray[id].tociliAdmsDateRangeInvalid_elrC = false;
        updatedArray[id].tociliAdmsDateEmpty_elrC = false;
        setQ37ElrexDetails(updatedArray);
        break;
      }

      case "tociliAdmsDate_linvoC-" + id: {
        let updatedArray = [...Q37LinvoDetails];
        updatedArray[id].tociliAdmsDate_linvoC = date;
        updatedArray[id].tociliAdmsDateRD_linvoC = 1;
        updatedArray[id].tociliAdmsDateInvalid_linvoC = false;
        updatedArray[id].tociliAdmsDateRangeInvalid_linvoC = false;
        updatedArray[id].tociliAdmsDateEmpty_linvoC = false;
        setQ37LinvoDetails(updatedArray);
        break;
      }

      // Q38.1.A ELREXFIO - Date of change
      case "dateChangeElr-" + id: {
        let updatedArray = [...Q381AElrexDetails];
        updatedArray[id].dateChange = date;
        updatedArray[id].dateChangeRd = 1;
        updatedArray[id].dateChangeRdEmpty = false;
        updatedArray[id].dateChangeEmpty = false;
        updatedArray[id].invalidDateChange = false;
        updatedArray[id].dateChangeOutOfBound = false;
        updatedArray[id].dateChangeBeforeEOF = false;
        setQ381AElrexDetails(updatedArray);
        break;
      }

      // Q38.1.A LINVOSELTAMAB - Date of change
      case "dateChangeLinvo-" + id: {
        let updatedArray = [...Q381ALinvoDetails];
        updatedArray[id].dateChange = date;
        updatedArray[id].dateChangeRd = 1;
        updatedArray[id].dateChangeRdEmpty = false;
        updatedArray[id].dateChangeEmpty = false;
        updatedArray[id].invalidDateChange = false;
        updatedArray[id].dateChangeOutOfBound = false;
        updatedArray[id].dateChangeBeforeEOF = false;
        setQ381ALinvoDetails(updatedArray);
        break;
      }

      // Q38.2.A ELREXFIO - Start date
      case `startDateChangeElr-${id}`: {
        let updatedArrayElr382Start = [...Q382AElrexDetails];
        updatedArrayElr382Start[id].startDateChange = date;
        updatedArrayElr382Start[id].startDateChangeRd = 1;
        updatedArrayElr382Start[id].startDateChangeRdEmpty = false;
        updatedArrayElr382Start[id].startDateChangeEmpty = false;
        updatedArrayElr382Start[id].invalidStartDateChange = false;
        updatedArrayElr382Start[id].startDateChangeOutOfBound = false;
        updatedArrayElr382Start[id].interruptionStartDateUnknownWarning = false;

        setQ382AElrexDetails(updatedArrayElr382Start);
        break;
      }
      // Q38.2.A ELREXFIO - End date
      case `endDateChangeElr-${id}`: {
        let updatedArrayElr382End = [...Q382AElrexDetails];
        updatedArrayElr382End[id].endDateChange = date;
        updatedArrayElr382End[id].endDateChangeRd = 2;
        updatedArrayElr382End[id].endDateChangeRdEmpty = false;
        updatedArrayElr382End[id].endDateChangeEmpty = false;
        updatedArrayElr382End[id].invalidEndDateChange = false;
        updatedArrayElr382End[id].endDateChangeOutOfBound = false;
        updatedArrayElr382End[id].interruptionEndDateUnknownWarning = false;

        setQ382AElrexDetails(updatedArrayElr382End);
        break;
      }
      // Q38.2.A LINVOSELTAMAB - Start date
      case `startDateChangeLinvo-${id}`: {
        let updatedArrayLinvo382Start = [...Q382ALinvoDetails];
        updatedArrayLinvo382Start[id].startDateChange = date;
        updatedArrayLinvo382Start[id].startDateChangeRd = 1; //
        updatedArrayLinvo382Start[id].startDateChangeRdEmpty = false;
        updatedArrayLinvo382Start[id].startDateChangeEmpty = false;
        updatedArrayLinvo382Start[id].invalidStartDateChange = false;
        updatedArrayLinvo382Start[id].startDateChangeOutOfBound = false;
        updatedArrayLinvo382Start[id].interruptionStartDateUnknownWarning =
          false;

        setQ382ALinvoDetails(updatedArrayLinvo382Start);
        break;
      }
      // Q38.2.A LINVOSELTAMAB - End date
      case `endDateChangeLinvo-${id}`: {
        let updatedArrayLinvo382End = [...Q382ALinvoDetails];
        updatedArrayLinvo382End[id].endDateChange = date;
        updatedArrayLinvo382End[id].endDateChangeRd = 2;
        updatedArrayLinvo382End[id].endDateChangeRdEmpty = false;
        updatedArrayLinvo382End[id].endDateChangeEmpty = false;
        updatedArrayLinvo382End[id].invalidEndDateChange = false;
        updatedArrayLinvo382End[id].endDateChangeOutOfBound = false;
        updatedArrayLinvo382End[id].interruptionEndDateUnknownWarning = false;

        setQ382ALinvoDetails(updatedArrayLinvo382End);
        break;
      }

      default:
        break;
    }
  };

  const HandleQ381ARepeaterFieldChange = (
    e,
    agentName,
    reasonName,
    parentIdx,
    itemIdx = null,
    aeIdx = null,
  ) => {
    const { name, value, type } = e.target;
    let updatedArray = [...Q381AElrexDetails];

    // Handle primary reason checkbox toggle
    if (name.startsWith("reasonElr") && itemIdx !== null) {
      const reasonId =
        updatedArray[parentIdx].reasonList[itemIdx]
          .bispecifictreatmentdiscontinuationreasonid;

      // Toggle isSelected
      updatedArray[parentIdx].reasonList[itemIdx].isSelected =
        !updatedArray[parentIdx].reasonList[itemIdx].isSelected;

      //  ADD THIS: SYNC to selectedReasons array
      if (updatedArray[parentIdx].reasonList[itemIdx].isSelected) {
        // Add to selectedReasons if not already there
        if (!updatedArray[parentIdx].selectedReasons.includes(reasonId)) {
          updatedArray[parentIdx].selectedReasons.push(reasonId);
        }
      } else {
        // Remove from selectedReasons
        updatedArray[parentIdx].selectedReasons = updatedArray[
          parentIdx
        ].selectedReasons.filter((r) => r !== reasonId);
      }

      updatedArray[parentIdx].reasonEmpty = false;
      updatedArray[parentIdx].reasonOtherTextEmpty = false;
      updatedArray[parentIdx].deathDtWarning = false;

      if (updatedArray[parentIdx].reasonList[itemIdx].isSelected) {
        updatedArray[parentIdx].reasonUnknown = false;
      }

      // Clear AE sub-reasons when Adverse Event is unchecked
      const reasonItem = updatedArray[parentIdx].reasonList[itemIdx];

      if (reasonId === 2) {
        // Clear AE sub-reasons
        reasonItem.aeSubReasons?.forEach((ae) => {
          ae.isSelected = false;
        });
        // Clear neurologic reasons
        reasonItem.neurologicAEReasonList = resetNeurologicReasons(
          reasonItem.neurologicAEReasonList,
        );

        //  ADD THIS: Clear selectedAEReasons array
        updatedArray[parentIdx].selectedAEReasons = [];
        updatedArray[parentIdx].aeSubReasonEmpty = false;

        // Clear neuro validations
        updatedArray[parentIdx].neuroReasonEmpty = false;
        updatedArray[parentIdx].neuroOtherTextEmpty = false;
      }

      // Clear "Other" text when unchecked
      if (
        updatedArray[parentIdx].reasonList[itemIdx].reasonName?.includes(
          "Other",
        ) &&
        !updatedArray[parentIdx].reasonList[itemIdx].isSelected
      ) {
        updatedArray[parentIdx].reasonList[itemIdx].reasonText = "";
        //  ADD THIS: Clear reasonOtherText
        updatedArray[parentIdx].reasonOtherText = "";
      }
    }

    // Handle AE sub-reason toggle
    else if (
      (name.startsWith("aeReasonElr") || name.startsWith("aeUnknownElr")) &&
      itemIdx !== null &&
      aeIdx !== null
    ) {
      const aeSubReasons =
        updatedArray[parentIdx].reasonList[itemIdx].aeSubReasons;
      const clickedAE = aeSubReasons[aeIdx];
      const aeReasonId = clickedAE.bispecifictreatmentdiscontinuationreasonaeid;
      const isClickedUnknown = clickedAE.reasonName
        .toLowerCase()
        .includes("unknown");

      if (type === "radio" && isClickedUnknown) {
        // Radio clicked - AE type unknown selected
        aeSubReasons.forEach((ae, idx) => {
          ae.isSelected = idx === aeIdx;
        });
        // ✅ ADD THIS: Clear selectedAEReasons and add only unknown
        updatedArray[parentIdx].selectedAEReasons = [aeReasonId];

        // Clear neurologic reasons
        updatedArray[parentIdx].reasonList[itemIdx].neurologicAEReasonList =
          resetNeurologicReasons(
            updatedArray[parentIdx].reasonList[itemIdx].neurologicAEReasonList,
          );

        // Clear neuro validations
        updatedArray[parentIdx].neuroReasonEmpty = false;
        updatedArray[parentIdx].neuroOtherTextEmpty = false;
      } else if (type === "checkbox") {
        // Checkbox clicked - Regular AE selected
        const newCheckedState = !aeSubReasons[aeIdx].isSelected;
        aeSubReasons[aeIdx].isSelected = newCheckedState;

        // ✅ ADD THIS: SYNC to selectedAEReasons array
        if (newCheckedState) {
          if (!updatedArray[parentIdx].selectedAEReasons.includes(aeReasonId)) {
            updatedArray[parentIdx].selectedAEReasons.push(aeReasonId);
          }
          // Uncheck AE type unknown
          aeSubReasons.forEach((ae) => {
            if (ae.reasonName.toLowerCase().includes("unknown")) {
              ae.isSelected = false;
              // Remove unknown from selectedAEReasons
              updatedArray[parentIdx].selectedAEReasons = updatedArray[
                parentIdx
              ].selectedAEReasons.filter(
                (id) => id !== ae.bispecifictreatmentdiscontinuationreasonaeid,
              );
            }
          });
        } else {
          // Remove from selectedAEReasons
          updatedArray[parentIdx].selectedAEReasons = updatedArray[
            parentIdx
          ].selectedAEReasons.filter((id) => id !== aeReasonId);
        }
        const isNeuroSelected =
          clickedAE?.reasonName === DiscontinuationReason.NeurologicalEvent;

        if (isNeuroSelected) {
          updatedArray[parentIdx].neurologicAEReasonList =
            resetNeurologicReasons(
              updatedArray[parentIdx].neurologicAEReasonList,
            );
          updatedArray[parentIdx].neuroReasonEmpty = false;
          updatedArray[parentIdx].neuroOtherTextEmpty = false;
        }
      }

      // Keep parent "Adverse Event" checked
      updatedArray[parentIdx].reasonList[itemIdx].isSelected = true;
      // ✅ ADD THIS: Ensure parent reason ID (2) is in selectedReasons
      if (!updatedArray[parentIdx].selectedReasons.includes(2)) {
        updatedArray[parentIdx].selectedReasons.push(2);
      }
      updatedArray[parentIdx].reasonEmpty = false;
      updatedArray[parentIdx].deathDtWarning = false;
      updatedArray[parentIdx].aeSubReasonEmpty = false;
    }

    // Handle "Other" reason text
    else if (name.startsWith("reasonTextElr") && itemIdx !== null) {
      const newText = value.length <= 300 ? value : value.substring(0, 300);
      updatedArray[parentIdx].reasonList[itemIdx].reasonText = newText;
      updatedArray[parentIdx].reasonList[itemIdx].isSelected = true;

      // ✅ ADD THIS: Sync to reasonOtherText and selectedReasons
      updatedArray[parentIdx].reasonOtherText = newText;
      const otherReasonId =
        updatedArray[parentIdx].reasonList[itemIdx]
          .bispecifictreatmentdiscontinuationreasonid;
      if (!updatedArray[parentIdx].selectedReasons.includes(otherReasonId)) {
        updatedArray[parentIdx].selectedReasons.push(otherReasonId);
      }

      updatedArray[parentIdx].reasonOtherTextEmpty = false;
      updatedArray[parentIdx].reasonEmpty = false;
      updatedArray[parentIdx].deathDtWarning = false;
    }

    setQ381AElrexDetails(updatedArray);
  };

  const HandleQ381ALinvoRepeaterFieldChange = (
    e,
    agentName,
    reasonName,
    parentIdx,
    itemIdx = null,
    aeIdx = null,
  ) => {
    const { name, value, type } = e.target;
    let updatedArray = [...Q381ALinvoDetails];

    // ✅ Handle primary reason checkbox toggle
    if (name.startsWith("reasonLinvo_") && itemIdx !== null) {
      const reasonId =
        updatedArray[parentIdx].reasonList[itemIdx]
          .bispecifictreatmentdiscontinuationreasonid;

      // Toggle isSelected
      updatedArray[parentIdx].reasonList[itemIdx].isSelected =
        !updatedArray[parentIdx].reasonList[itemIdx].isSelected;

      // ✅ SYNC to selectedReasons array
      if (updatedArray[parentIdx].reasonList[itemIdx].isSelected) {
        // Add to selectedReasons if not already there
        if (!updatedArray[parentIdx].selectedReasons.includes(reasonId)) {
          updatedArray[parentIdx].selectedReasons.push(reasonId);
        }
      } else {
        // Remove from selectedReasons
        updatedArray[parentIdx].selectedReasons = updatedArray[
          parentIdx
        ].selectedReasons.filter((r) => r !== reasonId);
      }

      updatedArray[parentIdx].reasonEmpty = false;
      updatedArray[parentIdx].deathDtWarning = false;
      updatedArray[parentIdx].reasonOtherTextEmpty = false;

      if (updatedArray[parentIdx].reasonList[itemIdx].isSelected) {
        updatedArray[parentIdx].reasonUnknown = false;
      }

      // Clear AE sub-reasons when Adverse Event is unchecked
      const reasonItem = updatedArray[parentIdx].reasonList[itemIdx];

      if (reasonId === 2) {
        updatedArray[parentIdx].reasonList[itemIdx].aeSubReasons?.forEach(
          (ae) => {
            ae.isSelected = false;
          },
        );
        // ✅ Clear selectedAEReasons array
        updatedArray[parentIdx].selectedAEReasons = [];
        updatedArray[parentIdx].aeSubReasonEmpty = false;
        // Clear neurologic reasons
        reasonItem.neurologicAEReasonList = resetNeurologicReasons(
          reasonItem.neurologicAEReasonList,
        );
        updatedArray[parentIdx].neuroReasonEmpty = false;
        updatedArray[parentIdx].neuroOtherTextEmpty = false;
      }

      // Clear "Other" text when unchecked
      if (
        updatedArray[parentIdx].reasonList[itemIdx].reasonName?.includes(
          "Other",
        ) &&
        !updatedArray[parentIdx].reasonList[itemIdx].isSelected
      ) {
        updatedArray[parentIdx].reasonList[itemIdx].reasonText = "";
        // ✅ Clear reasonOtherText
        updatedArray[parentIdx].reasonOtherText = "";
      }
    }

    // ✅ Handle AE sub-reason toggle (checkbox OR radio for "unknown")
    else if (
      (name.startsWith("aeReasonLinvo_") ||
        name.startsWith("aeUnknownLinvo_")) &&
      itemIdx !== null &&
      aeIdx !== null
    ) {
      const aeSubReasons =
        updatedArray[parentIdx].reasonList[itemIdx].aeSubReasons;
      const clickedAE = aeSubReasons[aeIdx];
      const aeReasonId = clickedAE.bispecifictreatmentdiscontinuationreasonaeid;
      const clickedAEName =
        clickedAE.reasonName ||
        clickedAE.bispecifictreatmentdiscontinuationreasonae ||
        "";
      const isClickedUnknown = clickedAEName.toLowerCase().includes("unknown");

      if (type === "radio" && isClickedUnknown) {
        // ✅ Radio clicked - "AE type unknown" selected
        // Uncheck ALL other AE checkboxes, only select "unknown"
        aeSubReasons.forEach((ae, idx) => {
          ae.isSelected = idx === aeIdx;
        });
        // ✅ Clear selectedAEReasons and add only unknown
        updatedArray[parentIdx].selectedAEReasons = [aeReasonId];
        // Clear neurologic reasons
        updatedArray[parentIdx].reasonList[itemIdx].neurologicAEReasonList =
          resetNeurologicReasons(
            updatedArray[parentIdx].reasonList[itemIdx].neurologicAEReasonList,
          );

        // Clear neuro validations
        updatedArray[parentIdx].neuroReasonEmpty = false;
        updatedArray[parentIdx].neuroOtherTextEmpty = false;
      } else if (type === "checkbox") {
        // ✅ Checkbox clicked - Regular AE selected
        const newCheckedState = !aeSubReasons[aeIdx].isSelected;
        aeSubReasons[aeIdx].isSelected = newCheckedState;

        // ✅ SYNC to selectedAEReasons array
        if (newCheckedState) {
          if (!updatedArray[parentIdx].selectedAEReasons.includes(aeReasonId)) {
            updatedArray[parentIdx].selectedAEReasons.push(aeReasonId);
          }
          // If any checkbox is now checked, uncheck "AE type unknown"
          aeSubReasons.forEach((ae) => {
            const aeName =
              ae.reasonName ||
              ae.bispecifictreatmentdiscontinuationreasonae ||
              "";
            if (aeName.toLowerCase().includes("unknown")) {
              ae.isSelected = false;
              // ✅ Remove unknown from selectedAEReasons
              updatedArray[parentIdx].selectedAEReasons = updatedArray[
                parentIdx
              ].selectedAEReasons.filter(
                (id) => id !== ae.bispecifictreatmentdiscontinuationreasonaeid,
              );
            }
          });
        } else {
          // ✅ Remove from selectedAEReasons
          updatedArray[parentIdx].selectedAEReasons = updatedArray[
            parentIdx
          ].selectedAEReasons.filter((id) => id !== aeReasonId);
        }
        const isNeuroSelected =
          clickedAE?.reasonName === DiscontinuationReason.NeurologicalEvent;

        if (isNeuroSelected) {
          updatedArray[parentIdx].neurologicAEReasonList =
            resetNeurologicReasons(
              updatedArray[parentIdx].neurologicAEReasonList,
            );
          updatedArray[parentIdx].neuroReasonEmpty = false;
          updatedArray[parentIdx].neuroOtherTextEmpty = false;
        }
      }

      // Keep parent "Adverse Event" checked
      updatedArray[parentIdx].reasonList[itemIdx].isSelected = true;
      // ✅ Ensure parent reason ID (2) is in selectedReasons
      if (!updatedArray[parentIdx].selectedReasons.includes(2)) {
        updatedArray[parentIdx].selectedReasons.push(2);
      }
      updatedArray[parentIdx].reasonEmpty = false;
      updatedArray[parentIdx].aeSubReasonEmpty = false;
      updatedArray[parentIdx].deathDtWarning = false;
    }

    // ✅ Handle "Other" reason text
    else if (name.startsWith("reasonTextLinvo_") && itemIdx !== null) {
      const newText = value.length <= 300 ? value : value.substring(0, 300);
      updatedArray[parentIdx].reasonList[itemIdx].reasonText = newText;
      updatedArray[parentIdx].reasonList[itemIdx].isSelected = true;

      // ✅ SYNC to reasonOtherText and selectedReasons
      updatedArray[parentIdx].reasonOtherText = newText;
      const otherReasonId =
        updatedArray[parentIdx].reasonList[itemIdx]
          .bispecifictreatmentdiscontinuationreasonid;
      if (!updatedArray[parentIdx].selectedReasons.includes(otherReasonId)) {
        updatedArray[parentIdx].selectedReasons.push(otherReasonId);
      }

      updatedArray[parentIdx].reasonOtherTextEmpty = false;
      updatedArray[parentIdx].reasonEmpty = false;
      updatedArray[parentIdx].deathDtWarning = false;
    }

    setQ381ALinvoDetails(updatedArray);
  };

  const HandleQ382ARepeaterChange = (
    e,
    agentName,
    reasonName,
    parentIdx,
    itemIdx = null,
    aeIdx = null,
  ) => {
    const { name, value, type, checked } = e.target;

    // Select the appropriate state array based on agentName
    let updatedArray =
      agentName === "Elrexfio" || agentName === "Elrex"
        ? [...Q382AElrexDetails]
        : [...Q382ALinvoDetails];

    // Handle primary reason checkbox toggle
    if (
      name.startsWith(
        `reason${
          agentName === "Elrexfio" || agentName === "Elrex" ? "Elr" : "Linvo"
        }`,
      ) &&
      itemIdx !== null
    ) {
      const reasonId =
        updatedArray[parentIdx].reasonList[itemIdx]
          .bispecifictreatmentdiscontinuationreasonid;

      // Toggle isSelected
      updatedArray[parentIdx].reasonList[itemIdx].isSelected =
        !updatedArray[parentIdx].reasonList[itemIdx].isSelected;

      // SYNC to selectedReasons array
      if (updatedArray[parentIdx].reasonList[itemIdx].isSelected) {
        if (!updatedArray[parentIdx].selectedReasons.includes(reasonId)) {
          updatedArray[parentIdx].selectedReasons.push(reasonId);
        }
      } else {
        updatedArray[parentIdx].selectedReasons = updatedArray[
          parentIdx
        ].selectedReasons.filter((r) => r !== reasonId);
      }

      // Clear validation flags
      updatedArray[parentIdx].reasonEmpty = false;
      updatedArray[parentIdx].reasonOtherTextEmpty = false;
      updatedArray[parentIdx].deathDtWarning = false;

      // Clear reasonUnknown if selecting something
      if (updatedArray[parentIdx].reasonList[itemIdx].isSelected) {
        updatedArray[parentIdx].reasonUnknown = false;
      }

      // Clear AE sub-reasons when Adverse Event is unchecked
      const reasonItem = updatedArray[parentIdx].reasonList[itemIdx];

      if (reasonId === 2) {
        updatedArray[parentIdx].reasonList[itemIdx].aeSubReasons?.forEach(
          (ae) => {
            ae.isSelected = false;
          },
        );
        updatedArray[parentIdx].selectedAEReasons = [];
        // Clear neurologic reasons
        reasonItem.neurologicAEReasonList = resetNeurologicReasons(
          reasonItem.neurologicAEReasonList,
        );
        updatedArray[parentIdx].aeSubReasonEmpty = false;
        updatedArray[parentIdx].neuroReasonEmpty = false;
        updatedArray[parentIdx].neuroOtherTextEmpty = false;
      }

      // Clear Other text when unchecked
      if (
        updatedArray[parentIdx].reasonList[itemIdx].reasonName?.includes(
          "Other",
        ) &&
        !updatedArray[parentIdx].reasonList[itemIdx].isSelected
      ) {
        updatedArray[parentIdx].reasonList[itemIdx].reasonText = "";
        updatedArray[parentIdx].reasonOtherText = "";
      }
    }

    // Handle AE sub-reason toggle (checkbox OR radio for unknown)
    else if (
      (name.startsWith(
        `aeReason${
          agentName === "Elrexfio" || agentName === "Elrex" ? "Elr" : "Linvo"
        }`,
      ) ||
        name.startsWith(
          `aeUnknown${
            agentName === "Elrexfio" || agentName === "Elrex" ? "Elr" : "Linvo"
          }`,
        )) &&
      itemIdx !== null &&
      aeIdx !== null
    ) {
      const aeSubReasons =
        updatedArray[parentIdx].reasonList[itemIdx].aeSubReasons;
      const clickedAE = aeSubReasons[aeIdx];
      const aeReasonId = clickedAE.bispecifictreatmentdiscontinuationreasonaeid;
      const clickedAEName =
        clickedAE.reasonName ||
        clickedAE.bispecifictreatmentdiscontinuationreasonae;
      const isClickedUnknown = clickedAEName.toLowerCase().includes("unknown");

      if (type === "radio" && isClickedUnknown) {
        // Radio clicked - "AE type unknown" selected
        // Uncheck ALL other AE checkboxes, only select unknown
        aeSubReasons.forEach((ae, idx) => {
          ae.isSelected = idx === aeIdx;
        });
        // Clear selectedAEReasons and add only unknown
        updatedArray[parentIdx].selectedAEReasons = [aeReasonId];
        // Clear neurologic reasons
        updatedArray[parentIdx].reasonList[itemIdx].neurologicAEReasonList =
          resetNeurologicReasons(
            updatedArray[parentIdx].reasonList[itemIdx].neurologicAEReasonList,
          );

        // Clear neuro validations
        updatedArray[parentIdx].neuroReasonEmpty = false;
        updatedArray[parentIdx].neuroOtherTextEmpty = false;
      } else if (type === "checkbox") {
        // Checkbox clicked - Regular AE selected
        const newCheckedState = !aeSubReasons[aeIdx].isSelected;
        aeSubReasons[aeIdx].isSelected = newCheckedState;

        // SYNC to selectedAEReasons array
        if (newCheckedState) {
          if (!updatedArray[parentIdx].selectedAEReasons.includes(aeReasonId)) {
            updatedArray[parentIdx].selectedAEReasons.push(aeReasonId);
          }
        } else {
          updatedArray[parentIdx].selectedAEReasons = updatedArray[
            parentIdx
          ].selectedAEReasons.filter((r) => r !== aeReasonId);
        }

        // If any regular AE checkbox is selected, uncheck the "Unknown" radio
        const unknownAE = aeSubReasons.find((ae) =>
          (ae.reasonName || ae.bispecifictreatmentdiscontinuationreasonae)
            .toLowerCase()
            .includes("unknown"),
        );
        if (unknownAE && newCheckedState) {
          unknownAE.isSelected = false;
          // Remove unknown from selectedAEReasons
          updatedArray[parentIdx].selectedAEReasons = updatedArray[
            parentIdx
          ].selectedAEReasons.filter(
            (r) => r !== unknownAE.bispecifictreatmentdiscontinuationreasonaeid,
          );
        }
        const isNeuroSelected =
          clickedAE?.reasonName === DiscontinuationReason.NeurologicalEvent;

        if (isNeuroSelected) {
          updatedArray[parentIdx].neurologicAEReasonList =
            resetNeurologicReasons(
              updatedArray[parentIdx].neurologicAEReasonList,
            );
          updatedArray[parentIdx].neuroReasonEmpty = false;
          updatedArray[parentIdx].neuroOtherTextEmpty = false;
        }
      }

      updatedArray[parentIdx].aeSubReasonEmpty = false;

      // Ensure parent "Adverse Event" (ID=2) is selected if newSelectedAEReasons.length > 0
      if (
        updatedArray[parentIdx].selectedAEReasons.length > 0 &&
        !updatedArray[parentIdx].selectedReasons.includes(2)
      ) {
        updatedArray[parentIdx].selectedReasons.push(2);
      }

      updatedArray[parentIdx].reasonEmpty =
        updatedArray[parentIdx].selectedReasons.length === 0;
      updatedArray[parentIdx].deathDtWarning = false;
    }

    // Column C - Other reason text
    if (name.startsWith("reasonText") || name.includes("reasonOtherText")) {
      const otherReasonId = 14; // "Other" reason ID
      updatedArray[parentIdx].reasonOtherText = value.substring(0, 300);

      const otherReasonIndex = updatedArray[parentIdx].reasonList?.findIndex(
        (r) => r.bispecifictreatmentdiscontinuationreasonid === otherReasonId,
      );

      if (otherReasonIndex !== -1 && otherReasonIndex !== undefined) {
        updatedArray[parentIdx].reasonList[otherReasonIndex].isSelected = true;
        updatedArray[parentIdx].reasonList[otherReasonIndex].reasonText =
          value.substring(0, 300);
      }

      // Ensure "Other" reason is selected
      if (!updatedArray[parentIdx].selectedReasons.includes(otherReasonId)) {
        updatedArray[parentIdx].selectedReasons.push(otherReasonId);
      }

      updatedArray[parentIdx].reasonOtherTextEmpty = false;
      updatedArray[parentIdx].reasonEmpty = false;
      updatedArray[parentIdx].deathDtWarning = false;
    }

    // Set the updated array (Elrexfio or Linvo)
    if (agentName === "Elrexfio" || agentName === "Elrex") {
      setQ382AElrexDetails(updatedArray);
    } else {
      setQ382ALinvoDetails(updatedArray);
    }
  };

  const HandleQ382RepeaterFieldChange = (e, agentName, rowIndex) => {
    const { name, value, type, checked } = e.target;
    const index = Number(rowIndex);
    const valueInt = Number(value);

    let updatedArray =
      agentName === "Elrexfio"
        ? [...Q382AElrexDetails]
        : [...Q382ALinvoDetails];

    // ========================================
    // COLUMN B - TYPE OF CHANGE RADIO BUTTONS
    // ========================================

    // ✅ Q38.2.A ELREXFIO - Column B Type of change radio
    if (name === `changeTypeElr382A${index}`) {
      updatedArray[index].changeType = valueInt;
      updatedArray[index].changeTypeEmpty = false;

      // Reset columns C-E if "No changes" (1) or "Unknown" (99) selected
      //  if (valueInt === 1 || valueInt === 99) {
      // Column C - Reasons
      updatedArray[index].selectedReasons = [];
      updatedArray[index].selectedAEReasons = [];
      updatedArray[index].reasonOtherText = "";
      updatedArray[index].reasonEmpty = false;
      updatedArray[index].deathDtWarning = false;
      updatedArray[index].reasonOtherTextEmpty = false;
      updatedArray[index].aeSubReasonEmpty = false;
      updatedArray[index].neuroReasonEmpty = false;
      updatedArray[index].neuroOtherTextEmpty = false;
      updatedArray[index].reasonUnknown = false;
      updatedArray[index].neurologicAEReasonList = resetNeurologicReasons(
        updatedArray[index].neurologicAEReasonList,
      );

      if (Number(index) === 0 && (valueInt === 1 || valueInt === 99)) {
        updatedArray = [updatedArray[0]]; // Keep only row 0
      }

      // Reset reasonList
      if (
        updatedArray[index].reasonList &&
        Array.isArray(updatedArray[index].reasonList)
      ) {
        updatedArray[index].reasonList = updatedArray[index].reasonList.map(
          (reason) => ({
            ...reason,
            isSelected: false,
            reasonText: "",
            aeSubReasons: reason.aeSubReasons?.map((ae) => ({
              ...ae,
              isSelected: false,
            })),
          }),
        );
      }

      // Column D - Start date
      updatedArray[index].startDateChangeRd = null;
      updatedArray[index].startDateChange = "";
      updatedArray[index].startDateChangeRdEmpty = false;
      updatedArray[index].startDateChangeEmpty = false;
      updatedArray[index].invalidStartDateChange = false;
      updatedArray[index].startDateChangeOutOfBound = false;

      // Column E - End date
      updatedArray[index].endDateChangeRd = null;
      updatedArray[index].endDateChange = "";
      updatedArray[index].endDateChangeRdEmpty = false;
      updatedArray[index].endDateChangeEmpty = false;
      updatedArray[index].invalidEndDateChange = false;
      updatedArray[index].endDateChangeOutOfBound = false;
      updatedArray[index].endDateChangeBeforeEOF = false;
      updatedArray[index].dateRangeConflict = false;
      // }

      // Clear pattern unknown warning
      updatedArray[index].patternAfterUnknownWarning = false;
      updatedArray[index].patternBeforeUnknownWarning = false;

      updatedArray[index].interruptionStartDateUnknownWarning = false;
      updatedArray[index].interruptionEndDateUnknownWarning = false;
    }

    // ✅ Q38.2.A LINVOSELTAMAB - Column B Type of change radio
    if (name === `changeTypeLinvo382A${index}`) {
      updatedArray[index].changeType = valueInt;
      updatedArray[index].changeTypeEmpty = false;

      // Reset columns C-E if "No changes" (1) or "Unknown" (99) selected
      //  if (valueInt === 1 || valueInt === 99) {
      // Column C - Reasons
      updatedArray[index].selectedReasons = [];
      updatedArray[index].selectedAEReasons = [];
      updatedArray[index].reasonOtherText = "";
      updatedArray[index].reasonEmpty = false;
      updatedArray[index].deathDtWarning = false;
      updatedArray[index].reasonOtherTextEmpty = false;
      updatedArray[index].aeSubReasonEmpty = false;
      updatedArray[index].neuroReasonEmpty = false;
      updatedArray[index].neuroOtherTextEmpty = false;
      updatedArray[index].reasonUnknown = false;
      updatedArray[index].neurologicAEReasonList = resetNeurologicReasons(
        updatedArray[index].neurologicAEReasonList,
      );

      if (Number(index) === 0 && (valueInt === 1 || valueInt === 99)) {
        updatedArray = [updatedArray[0]]; // Keep only row 0
      }

      // Reset reasonList
      if (
        updatedArray[index].reasonList &&
        Array.isArray(updatedArray[index].reasonList)
      ) {
        updatedArray[index].reasonList = updatedArray[index].reasonList.map(
          (reason) => ({
            ...reason,
            isSelected: false,
            reasonText: "",
            aeSubReasons: reason.aeSubReasons?.map((ae) => ({
              ...ae,
              isSelected: false,
            })),
          }),
        );
      }

      // Column D - Start date
      updatedArray[index].startDateChangeRd = null;
      updatedArray[index].startDateChange = "";
      updatedArray[index].startDateChangeRdEmpty = false;
      updatedArray[index].startDateChangeEmpty = false;
      updatedArray[index].invalidStartDateChange = false;
      updatedArray[index].startDateChangeOutOfBound = false;
      updatedArray[index].startDateChangeBeforeEOF = false;

      // Column E - End date
      updatedArray[index].endDateChangeRd = null;
      updatedArray[index].endDateChange = "";
      updatedArray[index].endDateChangeRdEmpty = false;
      updatedArray[index].endDateChangeEmpty = false;
      updatedArray[index].invalidEndDateChange = false;
      updatedArray[index].endDateChangeOutOfBound = false;
      updatedArray[index].endDateChangeBeforeEOF = false;
      updatedArray[index].dateRangeConflict = false;
      //   }

      // Clear pattern unknown warning
      updatedArray[index].patternAfterUnknownWarning = false;
      updatedArray[index].patternBeforeUnknownWarning = false;

      updatedArray[index].interruptionStartDateUnknownWarning = false;
      updatedArray[index].interruptionEndDateUnknownWarning = false;
    }

    // ========================================
    // COLUMN C - REASONS FOR CHANGE
    // ========================================

    // ✅ Column C - Reason "Unknown" radio (ELREXFIO)
    if (name === `reasonUnknownElr382A${index}`) {
      updatedArray[index].reasonUnknown = !updatedArray[index].reasonUnknown;

      if (updatedArray[index].reasonUnknown) {
        // Clear all other reasons
        updatedArray[index].selectedReasons = [];
        updatedArray[index].selectedAEReasons = [];
        updatedArray[index].reasonOtherText = "";

        // Uncheck all reasonList items
        if (
          updatedArray[index].reasonList &&
          Array.isArray(updatedArray[index].reasonList)
        ) {
          updatedArray[index].reasonList = updatedArray[index].reasonList.map(
            (reason) => ({
              ...reason,
              isSelected: false,
              reasonText: "",
              aeSubReasons: reason.aeSubReasons?.map((ae) => ({
                ...ae,
                isSelected: false,
              })),
            }),
          );
        }
      }
      updatedArray[index].neurologicAEReasonList = resetNeurologicReasons(
        updatedArray[index].neurologicAEReasonList,
      );

      updatedArray[index].reasonEmpty = false;
      updatedArray[index].deathDtWarning = false;
      updatedArray[index].reasonOtherTextEmpty = false;
      updatedArray[index].aeSubReasonEmpty = false;
      updatedArray[index].neuroReasonEmpty = false;
      updatedArray[index].neuroOtherTextEmpty = false;
    }

    // ✅ Column C - Reason "Unknown" radio (LINVOSELTAMAB)
    if (name === `reasonUnknownLinvo382A${index}`) {
      updatedArray[index].reasonUnknown = !updatedArray[index].reasonUnknown;

      if (updatedArray[index].reasonUnknown) {
        // Clear all other reasons
        updatedArray[index].selectedReasons = [];
        updatedArray[index].selectedAEReasons = [];
        updatedArray[index].reasonOtherText = "";

        // Uncheck all reasonList items
        if (
          updatedArray[index].reasonList &&
          Array.isArray(updatedArray[index].reasonList)
        ) {
          updatedArray[index].reasonList = updatedArray[index].reasonList.map(
            (reason) => ({
              ...reason,
              isSelected: false,
              reasonText: "",
              aeSubReasons: reason.aeSubReasons?.map((ae) => ({
                ...ae,
                isSelected: false,
              })),
            }),
          );
        }
      }
      updatedArray[index].neurologicAEReasonList = resetNeurologicReasons(
        updatedArray[index].neurologicAEReasonList,
      );
      updatedArray[index].reasonEmpty = false;
      updatedArray[index].deathDtWarning = false;
      updatedArray[index].reasonOtherTextEmpty = false;
      updatedArray[index].aeSubReasonEmpty = false;
      updatedArray[index].neuroReasonEmpty = false;
      updatedArray[index].neuroOtherTextEmpty = false;
    }

    // ========================================
    // COLUMN D - START DATE RADIO BUTTONS
    // ========================================

    // ✅ Column D - Start date radio buttons (ELREXFIO)
    if (name === `startDateChangeRdElr${index}`) {
      updatedArray[index].startDateChangeRd = valueInt;
      updatedArray[index].startDateChangeRdEmpty = false;
      updatedArray[index].interruptionStartDateUnknownWarning = valueInt === 99;
      // Clear date if "Unknown" (99) selected
      if (valueInt === 99) {
        updatedArray[index].startDateChange = "";
        updatedArray[index].startDateChangeEmpty = false;
        updatedArray[index].invalidStartDateChange = false;
        updatedArray[index].startDateChangeOutOfBound = false;
        updatedArray[index].startDateChangeBeforeEOF = false;
      }
    }

    // ✅ Column D - Start date radio buttons (LINVOSELTAMAB)
    if (name === `startDateChangeRdLinvo${index}`) {
      updatedArray[index].startDateChangeRd = valueInt;
      updatedArray[index].startDateChangeRdEmpty = false;
      updatedArray[index].interruptionStartDateUnknownWarning = valueInt === 99;

      // Clear date if "Unknown" (99) selected
      if (valueInt === 99) {
        updatedArray[index].startDateChange = "";
        updatedArray[index].startDateChangeEmpty = false;
        updatedArray[index].invalidStartDateChange = false;
        updatedArray[index].startDateChangeOutOfBound = false;
        updatedArray[index].startDateChangeBeforeEOF = false;
      }
    }

    // ========================================
    // COLUMN E - END DATE RADIO BUTTONS
    // ========================================

    // ✅ Column E - End date radio buttons (ELREXFIO)
    if (name === `endDateChangeRdElr${index}`) {
      updatedArray[index].endDateChangeRd = valueInt;
      updatedArray[index].endDateChangeRdEmpty = false;
      updatedArray[index].interruptionEndDateUnknownWarning = valueInt === 99;

      // Clear date if "Ongoing" (1) or "Unknown" (99) selected
      if (valueInt === 1 || valueInt === 99) {
        updatedArray[index].endDateChange = "";
        updatedArray[index].endDateChangeEmpty = false;
        updatedArray[index].invalidEndDateChange = false;
        updatedArray[index].endDateChangeOutOfBound = false;
        updatedArray[index].endDateChangeBeforeEOF = false;
        updatedArray[index].dateRangeConflict = false;
      }
    }

    // ✅ Column E - End date radio buttons (LINVOSELTAMAB)
    if (name === `endDateChangeRdLinvo${index}`) {
      updatedArray[index].endDateChangeRd = valueInt;
      updatedArray[index].endDateChangeRdEmpty = false;
      updatedArray[index].interruptionEndDateUnknownWarning = valueInt === 99;
      // Clear date if "Ongoing" (1) or "Unknown" (99) selected
      if (valueInt === 1 || valueInt === 99) {
        updatedArray[index].endDateChange = "";
        updatedArray[index].endDateChangeEmpty = false;
        updatedArray[index].invalidEndDateChange = false;
        updatedArray[index].endDateChangeOutOfBound = false;
        updatedArray[index].endDateChangeBeforeEOF = false;
        updatedArray[index].dateRangeConflict = false;
      }
    }

    // ========================================
    // UPDATE STATE BASED ON AGENT NAME
    // ========================================

    if (agentName === "Elrexfio") {
      setQ382AElrexDetails(updatedArray);
    } else {
      setQ382ALinvoDetails(updatedArray);
    }
  };

  const HandleNeuroRepeaterFieldChange = (
    e,
    questionKey,
    cohortId,
    aeNeuroId,
    rowIndex,
  ) => {
    if (isNaN(rowIndex)) return;
    const resolver = stateResolver[questionKey]?.[cohortId];
    if (!resolver) return;

    const [detailsArray, setState] = resolver;

    const { type, checked, value, tagName } = e.target;

    const updatedArray = [...detailsArray];
    const row = { ...updatedArray[rowIndex] };

    row.neurologicAEReasonList =
      row.neurologicAEReasonList?.map((item, index) => {
        if (index !== aeNeuroId) return item;

        // Checkbox change
        if (type === "checkbox") {
          return {
            ...item,
            isSelected: checked,
            reasonText: checked ? item.reasonText : "",
          };
        }

        // Textarea change
        if (tagName === "TEXTAREA") {
          return {
            ...item,
            isSelected: true,
            reasonText: value,
          };
        }

        return item;
      }) ?? [];

    row.aeSubReasonEmpty = false;
    row.neuroReasonEmpty = false;
    row.neuroOtherTextEmpty = false;

    updatedArray[rowIndex] = row;
    setState(updatedArray);
  };

  const HandleQ401aRepeaterDateChange = (name, date) => {
    const lastIndex = name.lastIndexOf("-");
    const id = name.slice(lastIndex + 1);
    switch (name) {
      case "Q401ainit_Elr_dt-" + id: {
        let updatedArray = [...Q401aElrDetails];

        updatedArray[id].Q401ainit_dt_Rd_Elr = 1;
        updatedArray[id].Q401ainit_Elr_dt = date;
        updatedArray[id].Q401aInit_Elr_RdHard = false;
        updatedArray[id].Q401aInit_Elr_dtHard = false;
        updatedArray[id].Q401aInvalidQ401ainit_Elr_dt = false;
        updatedArray[id].Q401aInit_Elr_dt_before_eof = false;

        showElrQ40Date91();
        showElrQ40Date();
        setQ401aElrDetails(updatedArray);
        break;
      }
      case "Q401ainit_Lin_dt-" + id: {
        let updatedArray = [...Q401aLinvoDetails];

        updatedArray[id].Q401ainit_dt_Rd_Lin = 1;
        updatedArray[id].Q401ainit_Lin_dt = date;
        updatedArray[id].Q401aInit_Lin_RdHard = false;
        updatedArray[id].Q401aInit_Lin_dtHard = false;
        updatedArray[id].Q401aInvalidQ401ainit_Lin_dt = false;
        updatedArray[id].Q401aInit_Lin_dt_before_eof = false;

        showLinQ40Date91();
        showLinQ40Date();
        setQ401aLinvoDetails(updatedArray);
        break;
      }
      case "Q401aWeight_Asses_Elr_dt-" + id: {
        let updatedArray = [...Q401aElrDetails];

        updatedArray[id].Q401aWeight_Asses_Elr = 1;
        updatedArray[id].Q401aWeight_Asses_Elr_dt = date;
        updatedArray[id].Q401aWeight_AssesEmptyHard_Elr = false;
        updatedArray[id].Q401aWeight_AssesDtEmptyHard_Elr = false;
        updatedArray[id].Q401aWeight_AssesDtInvalid_Elr = false;
        updatedArray[id].Q401aWeight_Asses_dt_before_eof_Elr = false;

        setQ401aElrDetails(updatedArray);
        break;
      }
      case "Q401aWeight_Asses_Lin_dt-" + id: {
        let updatedArray = [...Q401aLinvoDetails];

        updatedArray[id].Q401aWeight_Asses_Lin = 1;
        updatedArray[id].Q401aWeight_Asses_Lin_dt = date;
        updatedArray[id].Q401aWeight_AssesEmptyHard_Lin = false;
        updatedArray[id].Q401aWeight_AssesDtEmptyHard_Lin = false;
        updatedArray[id].Q401aWeight_AssesDtInvalid_Lin = false;
        updatedArray[id].Q401aWeight_Asses_dt_before_eof_Lin = false;

        setQ401aLinvoDetails(updatedArray);
        break;
      }

      default:
        break;
    }
  };

  const HandleQ401aFieldChange = (e) => {
    const { name, id, value } = e.target;

    switch (name) {
      //Tec
      case "Q401ainit_dt_Rd_Elr" + id: {
        let updatedArray = [...Q401aElrDetails];

        updatedArray[id].Q401ainit_dt_Rd_Elr = Number(value);
        updatedArray[id].Q401ainit_Elr_dt =
          Number(value) === 1 ? updatedArray[id].Q401ainit_Elr_dt : "";
        updatedArray[id].Q401aInit_Elr_RdHard = false;
        updatedArray[id].Q401aInit_Elr_dtHard = false;
        updatedArray[id].Q401aInvalidQ401ainit_Elr_dt = false;
        updatedArray[id].Q401aInit_Elr_dt_before_eof = false;

        setQ401aElrDetails(updatedArray);
        break;
      }

      case "Q401aIntendedDosing_Elr" + id: {
        let updatedArray = [...Q401aElrDetails];

        updatedArray[id].Q401aIntendedDosing_Elr = Number(value);
        updatedArray[id].Q401aIntendedDosing_Elr_WeekValue = "";
        updatedArray[id].Q401aIntendedDosing_Elr_MonthValue = "";
        updatedArray[id].Q401aIntendedDosing_Elr_OnceWeekValue = "";
        updatedArray[id].Q401aIntendedDosing_Elr_OnceMonthValue = "";
        updatedArray[id].Q401aIntendedDosing_Elr_OtherValue = "";
        updatedArray[id].Q401aIntendedDosingEmpty_Elr = false;
        updatedArray[id].Q401aIntendedDosingRangeInvalid_Elr = false;

        setQ401aElrDetails(updatedArray);
        break;
      }

      case "Q401aIntendedDosing_Elr_WeekValue" + id: {
        let updatedArray = [...Q401aElrDetails];

        updatedArray[id].Q401aIntendedDosing_Elr = 1;
        updatedArray[id].Q401aIntendedDosing_Elr_WeekValue = value;
        updatedArray[id].Q401aIntendedDosing_Elr_MonthValue = "";
        updatedArray[id].Q401aIntendedDosing_Elr_OnceWeekValue = "";
        updatedArray[id].Q401aIntendedDosing_Elr_OnceMonthValue = "";
        updatedArray[id].Q401aIntendedDosing_Elr_OtherValue = "";
        updatedArray[id].Q401aIntendedDosingEmpty_Elr = false;
        updatedArray[id].Q401aIntendedDosingRangeInvalid_Elr = false;

        setQ401aElrDetails(updatedArray);
        break;
      }

      case "Q401aIntendedDosing_Elr_MonthValue" + id: {
        let updatedArray = [...Q401aElrDetails];

        updatedArray[id].Q401aIntendedDosing_Elr = 2;
        updatedArray[id].Q401aIntendedDosing_Elr_MonthValue = value;
        updatedArray[id].Q401aIntendedDosing_Elr_WeekValue = "";
        updatedArray[id].Q401aIntendedDosing_Elr_OnceWeekValue = "";
        updatedArray[id].Q401aIntendedDosing_Elr_OnceMonthValue = "";
        updatedArray[id].Q401aIntendedDosing_Elr_OtherValue = "";
        updatedArray[id].Q401aIntendedDosingEmpty_Elr = false;
        updatedArray[id].Q401aIntendedDosingRangeInvalid_Elr = false;

        setQ401aElrDetails(updatedArray);
        break;
      }

      case "Q401aIntendedDosing_Elr_OnceWeekValue" + id: {
        let updatedArray = [...Q401aElrDetails];

        updatedArray[id].Q401aIntendedDosing_Elr = 3;
        updatedArray[id].Q401aIntendedDosing_Elr_OnceWeekValue = value;
        updatedArray[id].Q401aIntendedDosing_Elr_MonthValue = "";
        updatedArray[id].Q401aIntendedDosing_Elr_WeekValue = "";
        updatedArray[id].Q401aIntendedDosing_Elr_OnceMonthValue = "";
        updatedArray[id].Q401aIntendedDosing_Elr_OtherValue = "";
        updatedArray[id].Q401aIntendedDosingEmpty_Elr = false;
        updatedArray[id].Q401aIntendedDosingRangeInvalid_Elr = false;

        setQ401aElrDetails(updatedArray);
        break;
      }

      case "Q401aIntendedDosing_Elr_OnceMonthValue" + id: {
        let updatedArray = [...Q401aElrDetails];

        updatedArray[id].Q401aIntendedDosing_Elr = 4;
        updatedArray[id].Q401aIntendedDosing_Elr_OnceMonthValue = value;
        updatedArray[id].Q401aIntendedDosing_Elr_WeekValue = "";
        updatedArray[id].Q401aIntendedDosing_Elr_OnceWeekValue = "";
        updatedArray[id].Q401aIntendedDosing_Elr_OtherValue = "";
        updatedArray[id].Q401aIntendedDosing_Elr_MonthValue = "";
        updatedArray[id].Q401aIntendedDosingEmpty_Elr = false;
        updatedArray[id].Q401aIntendedDosingRangeInvalid_Elr = false;

        setQ401aElrDetails(updatedArray);
        break;
      }

      case "Q401aIntendedDosing_Elr_OtherValue" + id: {
        let updatedArray = [...Q401aElrDetails];

        updatedArray[id].Q401aIntendedDosing_Elr = 5;
        updatedArray[id].Q401aIntendedDosing_Elr_OtherValue = value;
        updatedArray[id].Q401aIntendedDosing_Elr_MonthValue = "";
        updatedArray[id].Q401aIntendedDosing_Elr_OnceWeekValue = "";
        updatedArray[id].Q401aIntendedDosing_Elr_OnceMonthValue = "";
        updatedArray[id].Q401aIntendedDosing_Elr_WeekValue = "";
        updatedArray[id].Q401aIntendedDosingEmpty_Elr = false;
        updatedArray[id].Q401aIntendedDosingRangeInvalid_Elr = false;

        setQ401aElrDetails(updatedArray);
        break;
      }

      case "Q401aPrimaryProf_Elr" + id: {
        let updatedArray = [...Q401aElrDetails];

        updatedArray[id].Q401aPrimaryProf_Elr =
          !updatedArray[id].Q401aPrimaryProf_Elr;
        updatedArray[id].Q401aUnknownProf_Elr = null;
        updatedArray[id].Q401aReasonIvigEmptyHard_Elr = false;

        setQ401aElrDetails(updatedArray);
        break;
      }

      case "Q401aSecProf_Elr" + id: {
        let updatedArray = [...Q401aElrDetails];

        updatedArray[id].Q401aSecProf_Elr = !updatedArray[id].Q401aSecProf_Elr;
        updatedArray[id].Q401aUnknownProf_Elr = null;
        updatedArray[id].Q401aReasonIvigEmptyHard_Elr = false;

        setQ401aElrDetails(updatedArray);
        break;
      }

      case "Q401aOtherProf_Elr" + id: {
        let updatedArray = [...Q401aElrDetails];

        updatedArray[id].Q401aOtherProf_Elr =
          !updatedArray[id].Q401aOtherProf_Elr;
        updatedArray[id].Q401aUnknownProf_Elr = null;
        updatedArray[id].Q401aReasonIvigEmptyHard_Elr = false;

        setQ401aElrDetails(updatedArray);
        break;
      }

      case "Q401aUnknownProf_Elr" + id: {
        let updatedArray = [...Q401aElrDetails];

        updatedArray[id].Q401aUnknownProf_Elr = Number(value);
        updatedArray[id].Q401aPrimaryProf_Elr = false;
        updatedArray[id].Q401aSecProf_Elr = false;
        updatedArray[id].Q401aOtherProf_Elr = false;
        updatedArray[id].Q401aReasonIvigEmptyHard_Elr = false;

        setQ401aElrDetails(updatedArray);
        break;
      }

      case "Q401aDosingType_Elr" + id: {
        let updatedArray = [...Q401aElrDetails];

        updatedArray[id].Q401aDosingType_Elr = Number(value);
        updatedArray[id].Q401aDosingType_ElrEmptyHard = false;

        updatedArray[id].Q401aDosage_Elr = null;
        updatedArray[id].Q401aDosage_Elr_GValue = "";
        updatedArray[id].Q401aDosage_Elr_MgValue = "";
        updatedArray[id].Q401aDosageFrequencyEmpty_Elr = false;
        updatedArray[id].Q401aDosageRangeInvalid_Elr = false;

        updatedArray[id].Q401aWeight_Elr = null;
        updatedArray[id].Q401aWeight_Value_KgElr = "";
        updatedArray[id].Q401aWeight_Value_LbsElr = "";
        updatedArray[id].Q401aWeightEmpty_Elr = false;
        updatedArray[id].Q401aWeightRangeInvalid_Elr = false;

        updatedArray[id].Q401aWeight_Asses_Elr = null;
        updatedArray[id].Q401aWeight_Asses_Elr_dt = "";
        updatedArray[id].Q401aWeight_AssesEmptyHard_Elr = false;
        updatedArray[id].Q401aWeight_AssesDtEmptyHard_Elr = false;
        updatedArray[id].Q401aWeight_AssesDtInvalid_Elr = false;
        updatedArray[id].Q401aWeight_Asses_dt_before_eof_Elr = false;

        setQ401aElrDetails(updatedArray);
        break;
      }

      case "Q401aDosage_Elr" + id: {
        let updatedArray = [...Q401aElrDetails];

        updatedArray[id].Q401aDosage_Elr = Number(value);
        updatedArray[id].Q401aDosage_Elr_GValue = "";
        updatedArray[id].Q401aDosage_Elr_MgValue = "";
        updatedArray[id].Q401aDosageFrequencyEmpty_Elr = false;
        updatedArray[id].Q401aDosageRangeInvalid_Elr = false;

        setQ401aElrDetails(updatedArray);
        break;
      }

      case "Q401aDosage_Elr_GValue" + id: {
        let updatedArray = [...Q401aElrDetails];

        updatedArray[id].Q401aDosage_Elr = 1;
        updatedArray[id].Q401aDosage_Elr_GValue = value;
        updatedArray[id].Q401aDosage_Elr_MgValue = "";
        updatedArray[id].Q401aDosageFrequencyEmpty_Elr = false;
        updatedArray[id].Q401aDosageRangeInvalid_Elr = false;

        setQ401aElrDetails(updatedArray);
        break;
      }

      case "Q401aDosage_Elr_MgValue" + id: {
        let updatedArray = [...Q401aElrDetails];

        updatedArray[id].Q401aDosage_Elr = 2;
        updatedArray[id].Q401aDosage_Elr_MgValue = value;
        updatedArray[id].Q401aDosage_Elr_GValue = "";
        updatedArray[id].Q401aDosageFrequencyEmpty_Elr = false;
        updatedArray[id].Q401aDosageRangeInvalid_Elr = false;

        setQ401aElrDetails(updatedArray);
        break;
      }

      case "Q401aWeight_Elr" + id: {
        let updatedArray = [...Q401aElrDetails];

        updatedArray[id].Q401aWeight_Elr = Number(value);
        updatedArray[id].Q401aWeight_Value_KgElr = "";
        updatedArray[id].Q401aWeight_Value_LbsElr = "";
        updatedArray[id].Q401aWeightEmpty_Elr = false;
        updatedArray[id].Q401aWeightRangeInvalid_Elr = false;

        setQ401aElrDetails(updatedArray);
        break;
      }

      case "Q401aWeight_Value_KgElr" + id: {
        let updatedArray = [...Q401aElrDetails];

        updatedArray[id].Q401aWeight_Elr = 1;
        updatedArray[id].Q401aWeight_Value_KgElr = value;
        updatedArray[id].Q401aWeight_Value_LbsElr = "";
        updatedArray[id].Q401aWeightEmpty_Elr = false;
        updatedArray[id].Q401aWeightRangeInvalid_Elr = false;

        setQ401aElrDetails(updatedArray);
        break;
      }

      case "Q401aWeight_Value_LbsElr" + id: {
        let updatedArray = [...Q401aElrDetails];

        updatedArray[id].Q401aWeight_Elr = 2;
        updatedArray[id].Q401aWeight_Value_KgElr = "";
        updatedArray[id].Q401aWeight_Value_LbsElr = value;
        updatedArray[id].Q401aWeightEmpty_Elr = false;
        updatedArray[id].Q401aWeightRangeInvalid_Elr = false;

        setQ401aElrDetails(updatedArray);
        break;
      }
      case "Q401aWeight_Asses_Elr" + id: {
        let updatedArray = [...Q401aElrDetails];

        updatedArray[id].Q401aWeight_Asses_Elr = Number(value);
        updatedArray[id].Q401aWeight_Asses_Elr_dt =
          Number(value) === 1 ? updatedArray[id].Q401aWeight_Asses_Elr_dt : "";
        updatedArray[id].Q401aWeight_AssesEmptyHard_Elr = false;
        updatedArray[id].Q401aWeight_AssesDtEmptyHard_Elr = false;
        updatedArray[id].Q401aWeight_AssesDtInvalid_Elr = false;
        updatedArray[id].Q401aWeight_Asses_dt_before_eof_Elr = false;

        setQ401aElrDetails(updatedArray);
        break;
      }

      //Linvo
      case "Q401ainit_dt_Rd_Lin" + id: {
        let updatedArray = [...Q401aLinvoDetails];

        updatedArray[id].Q401ainit_dt_Rd_Lin = Number(value);
        updatedArray[id].Q401ainit_Lin_dt =
          Number(value) === 1 ? updatedArray[id].Q401ainit_Lin_dt : "";
        updatedArray[id].Q401aInit_Lin_RdHard = false;
        updatedArray[id].Q401aInit_Lin_dtHard = false;
        updatedArray[id].Q401aInvalidQ401ainit_Lin_dt = false;
        updatedArray[id].Q401aInit_Lin_dt_before_eof = false;

        setQ401aLinvoDetails(updatedArray);
        break;
      }

      case "Q401aIntendedDosing_Lin" + id: {
        let updatedArray = [...Q401aLinvoDetails];

        updatedArray[id].Q401aIntendedDosing_Lin = Number(value);
        updatedArray[id].Q401aIntendedDosing_Lin_WeekValue = "";
        updatedArray[id].Q401aIntendedDosing_Lin_MonthValue = "";
        updatedArray[id].Q401aIntendedDosing_Lin_OnceWeekValue = "";
        updatedArray[id].Q401aIntendedDosing_Lin_OnceMonthValue = "";
        updatedArray[id].Q401aIntendedDosing_Lin_OtherValue = "";
        updatedArray[id].Q401aIntendedDosingEmpty_Lin = false;
        updatedArray[id].Q401aIntendedDosingRangeInvalid_Lin = false;

        setQ401aLinvoDetails(updatedArray);
        break;
      }

      case "Q401aIntendedDosing_Lin_WeekValue" + id: {
        let updatedArray = [...Q401aLinvoDetails];

        updatedArray[id].Q401aIntendedDosing_Lin = 1;
        updatedArray[id].Q401aIntendedDosing_Lin_WeekValue = value;
        updatedArray[id].Q401aIntendedDosing_Lin_MonthValue = "";
        updatedArray[id].Q401aIntendedDosing_Lin_OnceWeekValue = "";
        updatedArray[id].Q401aIntendedDosing_Lin_OnceMonthValue = "";
        updatedArray[id].Q401aIntendedDosing_Lin_OtherValue = "";
        updatedArray[id].Q401aIntendedDosingEmpty_Lin = false;
        updatedArray[id].Q401aIntendedDosingRangeInvalid_Lin = false;

        setQ401aLinvoDetails(updatedArray);
        break;
      }

      case "Q401aIntendedDosing_Lin_MonthValue" + id: {
        let updatedArray = [...Q401aLinvoDetails];

        updatedArray[id].Q401aIntendedDosing_Lin = 2;
        updatedArray[id].Q401aIntendedDosing_Lin_WeekValue = "";
        updatedArray[id].Q401aIntendedDosing_Lin_MonthValue = value;
        updatedArray[id].Q401aIntendedDosing_Lin_OnceWeekValue = "";
        updatedArray[id].Q401aIntendedDosing_Lin_OnceMonthValue = "";
        updatedArray[id].Q401aIntendedDosing_Lin_OtherValue = "";
        updatedArray[id].Q401aIntendedDosingEmpty_Lin = false;
        updatedArray[id].Q401aIntendedDosingRangeInvalid_Lin = false;

        setQ401aLinvoDetails(updatedArray);
        break;
      }

      case "Q401aIntendedDosing_Lin_OnceWeekValue" + id: {
        let updatedArray = [...Q401aLinvoDetails];

        updatedArray[id].Q401aIntendedDosing_Lin = 3;
        updatedArray[id].Q401aIntendedDosing_Lin_WeekValue = "";
        updatedArray[id].Q401aIntendedDosing_Lin_MonthValue = "";
        updatedArray[id].Q401aIntendedDosing_Lin_OnceWeekValue = value;
        updatedArray[id].Q401aIntendedDosing_Lin_OnceMonthValue = "";
        updatedArray[id].Q401aIntendedDosing_Lin_OtherValue = "";
        updatedArray[id].Q401aIntendedDosingEmpty_Lin = false;
        updatedArray[id].Q401aIntendedDosingRangeInvalid_Lin = false;

        setQ401aLinvoDetails(updatedArray);
        break;
      }

      case "Q401aIntendedDosing_Lin_OnceMonthValue" + id: {
        let updatedArray = [...Q401aLinvoDetails];

        updatedArray[id].Q401aIntendedDosing_Lin = 4;
        updatedArray[id].Q401aIntendedDosing_Lin_WeekValue = "";
        updatedArray[id].Q401aIntendedDosing_Lin_MonthValue = "";
        updatedArray[id].Q401aIntendedDosing_Lin_OnceWeekValue = "";
        updatedArray[id].Q401aIntendedDosing_Lin_OnceMonthValue = value;
        updatedArray[id].Q401aIntendedDosing_Lin_OtherValue = "";
        updatedArray[id].Q401aIntendedDosingEmpty_Lin = false;
        updatedArray[id].Q401aIntendedDosingRangeInvalid_Lin = false;

        setQ401aLinvoDetails(updatedArray);
        break;
      }

      case "Q401aIntendedDosing_Lin_OtherValue" + id: {
        let updatedArray = [...Q401aLinvoDetails];

        updatedArray[id].Q401aIntendedDosing_Lin = 5;
        updatedArray[id].Q401aIntendedDosing_Lin_WeekValue = "";
        updatedArray[id].Q401aIntendedDosing_Lin_MonthValue = "";
        updatedArray[id].Q401aIntendedDosing_Lin_OnceWeekValue = "";
        updatedArray[id].Q401aIntendedDosing_Lin_OnceMonthValue = "";
        updatedArray[id].Q401aIntendedDosing_Lin_OtherValue = value;
        updatedArray[id].Q401aIntendedDosingEmpty_Lin = false;
        updatedArray[id].Q401aIntendedDosingRangeInvalid_Lin = false;

        setQ401aLinvoDetails(updatedArray);
        break;
      }

      case "Q401aPrimaryProf_Lin" + id: {
        let updatedArray = [...Q401aLinvoDetails];

        updatedArray[id].Q401aPrimaryProf_Lin =
          !updatedArray[id].Q401aPrimaryProf_Lin;
        updatedArray[id].Q401aUnknownProf_Lin = null;
        updatedArray[id].Q401aReasonIvigEmptyHard_Lin = false;

        setQ401aLinvoDetails(updatedArray);
        break;
      }

      case "Q401aSecProf_Lin" + id: {
        let updatedArray = [...Q401aLinvoDetails];

        updatedArray[id].Q401aSecProf_Lin = !updatedArray[id].Q401aSecProf_Lin;
        updatedArray[id].Q401aUnknownProf_Lin = null;
        updatedArray[id].Q401aReasonIvigEmptyHard_Lin = false;

        setQ401aLinvoDetails(updatedArray);
        break;
      }

      case "Q401aOtherProf_Lin" + id: {
        let updatedArray = [...Q401aLinvoDetails];

        updatedArray[id].Q401aOtherProf_Lin =
          !updatedArray[id].Q401aOtherProf_Lin;
        updatedArray[id].Q401aUnknownProf_Lin = null;
        updatedArray[id].Q401aReasonIvigEmptyHard_Lin = false;

        setQ401aLinvoDetails(updatedArray);
        break;
      }

      case "Q401aUnknownProf_Lin" + id: {
        let updatedArray = [...Q401aLinvoDetails];

        updatedArray[id].Q401aUnknownProf_Lin = Number(value);
        updatedArray[id].Q401aPrimaryProf_Lin = false;
        updatedArray[id].Q401aSecProf_Lin = false;
        updatedArray[id].Q401aOtherProf_Lin = false;
        updatedArray[id].Q401aReasonIvigEmptyHard_Lin = false;

        setQ401aLinvoDetails(updatedArray);
        break;
      }

      case "Q401aDosingType_Lin" + id: {
        let updatedArray = [...Q401aLinvoDetails];

        updatedArray[id].Q401aDosingType_Lin = Number(value);
        updatedArray[id].Q401aDosingType_LinEmptyHard = false;

        updatedArray[id].Q401aDosage_Lin = null;
        updatedArray[id].Q401aDosage_Lin_GValue = "";
        updatedArray[id].Q401aDosage_Lin_MgValue = "";
        updatedArray[id].Q401aDosageFrequencyEmpty_Lin = false;
        updatedArray[id].Q401aDosageRangeInvalid_Lin = false;

        updatedArray[id].Q401aWeight_Lin = null;
        updatedArray[id].Q401aWeight_Value_KgLin = "";
        updatedArray[id].Q401aWeight_Value_LbsLin = "";
        updatedArray[id].Q401aWeightEmpty_Lin = false;
        updatedArray[id].Q401aWeightRangeInvalid_Lin = false;

        updatedArray[id].Q401aWeight_Asses_Lin = null;
        updatedArray[id].Q401aWeight_Asses_Lin_dt = "";
        updatedArray[id].Q401aWeight_AssesEmptyHard_Lin = false;
        updatedArray[id].Q401aWeight_AssesDtEmptyHard_Lin = false;
        updatedArray[id].Q401aWeight_AssesDtInvalid_Lin = false;
        updatedArray[id].Q401aWeight_Asses_dt_before_eof_Lin = false;

        setQ401aLinvoDetails(updatedArray);
        break;
      }

      case "Q401aDosage_Lin" + id: {
        let updatedArray = [...Q401aLinvoDetails];

        updatedArray[id].Q401aDosage_Lin = Number(value);
        updatedArray[id].Q401aDosage_Lin_GValue = "";
        updatedArray[id].Q401aDosage_Lin_MgValue = "";
        updatedArray[id].Q401aDosageFrequencyEmpty_Lin = false;
        updatedArray[id].Q401aDosageRangeInvalid_Lin = false;

        setQ401aLinvoDetails(updatedArray);
        break;
      }

      case "Q401aDosage_Lin_GValue" + id: {
        let updatedArray = [...Q401aLinvoDetails];

        updatedArray[id].Q401aDosage_Lin = 1;
        updatedArray[id].Q401aDosage_Lin_GValue = value;
        updatedArray[id].Q401aDosage_Lin_MgValue = "";
        updatedArray[id].Q401aDosageFrequencyEmpty_Lin = false;
        updatedArray[id].Q401aDosageRangeInvalid_Lin = false;

        setQ401aLinvoDetails(updatedArray);
        break;
      }

      case "Q401aDosage_Lin_MgValue" + id: {
        let updatedArray = [...Q401aLinvoDetails];

        updatedArray[id].Q401aDosage_Lin = 2;
        updatedArray[id].Q401aDosage_Lin_GValue = "";
        updatedArray[id].Q401aDosage_Lin_MgValue = value;
        updatedArray[id].Q401aDosageFrequencyEmpty_Lin = false;
        updatedArray[id].Q401aDosageRangeInvalid_Lin = false;

        setQ401aLinvoDetails(updatedArray);
        break;
      }

      case "Q401aWeight_Lin" + id: {
        let updatedArray = [...Q401aLinvoDetails];

        updatedArray[id].Q401aWeight_Lin = Number(value);
        updatedArray[id].Q401aWeight_Value_KgLin = "";
        updatedArray[id].Q401aWeight_Value_LbsLin = "";
        updatedArray[id].Q401aWeightEmpty_Lin = false;
        updatedArray[id].Q401aWeightRangeInvalid_Lin = false;

        setQ401aLinvoDetails(updatedArray);
        break;
      }

      case "Q401aWeight_Value_KgLin" + id: {
        let updatedArray = [...Q401aLinvoDetails];

        updatedArray[id].Q401aWeight_Lin = 1;
        updatedArray[id].Q401aWeight_Value_KgLin = value;
        updatedArray[id].Q401aWeight_Value_LbsLin = "";
        updatedArray[id].Q401aWeightEmpty_Lin = false;
        updatedArray[id].Q401aWeightRangeInvalid_Lin = false;

        setQ401aLinvoDetails(updatedArray);
        break;
      }

      case "Q401aWeight_Value_LbsLin" + id: {
        let updatedArray = [...Q401aLinvoDetails];

        updatedArray[id].Q401aWeight_Lin = 2;
        updatedArray[id].Q401aWeight_Value_LbsLin = value;
        updatedArray[id].Q401aWeight_Value_KgLin = "";
        updatedArray[id].Q401aWeightEmpty_Lin = false;
        updatedArray[id].Q401aWeightRangeInvalid_Lin = false;

        setQ401aLinvoDetails(updatedArray);
        break;
      }

      case "Q401aWeight_Asses_Lin" + id: {
        let updatedArray = [...Q401aLinvoDetails];

        updatedArray[id].Q401aWeight_Asses_Lin = Number(value);
        updatedArray[id].Q401aWeight_Asses_Lin_dt =
          Number(value) === 1 ? updatedArray[id].Q401aWeight_Asses_Lin_dt : "";
        updatedArray[id].Q401aWeight_AssesEmptyHard_Lin = false;
        updatedArray[id].Q401aWeight_AssesDtEmptyHard_Lin = false;
        updatedArray[id].Q401aWeight_AssesDtInvalid_Lin = false;
        updatedArray[id].Q401aWeight_Asses_dt_before_eof_Lin = false;

        setQ401aLinvoDetails(updatedArray);
        break;
      }

      default:
        break;
    }
    //
  };

  const HandleQ401cRepeaterDateChange = (name, date) => {
    const lastIndex = name.lastIndexOf("-");
    const id = name.slice(lastIndex + 1);
    switch (name) {
      case "Q401cDateChange_Elr-" + id: {
        let updatedArray = [...Q401cElrDetails];

        updatedArray[id].Q401cDateChangeRd_Elr = 1;
        updatedArray[id].Q401cDateChange_Elr = date;
        updatedArray[id].Q401cDateChangeRdHard_Elr = false;
        updatedArray[id].Q401cDateChangeDtHard_Elr = false;
        updatedArray[id].Q401cDateChangeInvalid_Elr = false;
        updatedArray[id].Q401cDateChangeOutOfBound_Elr = false;

        setQ401cElrDetails(updatedArray);
        break;
      }
      case "Q401cDateChange_Lin-" + id: {
        let updatedArray = [...Q401cLinvoDetails];

        updatedArray[id].Q401cDateChangeRd_Lin = 1;
        updatedArray[id].Q401cDateChange_Lin = date;
        updatedArray[id].Q401cDateChangeRdHard_Lin = false;
        updatedArray[id].Q401cDateChangeDtHard_Lin = false;
        updatedArray[id].Q401cDateChangeInvalid_Lin = false;
        updatedArray[id].Q401cDateChangeOutOfBound_Lin = false;

        setQ401cLinvoDetails(updatedArray);
        break;
      }
      default:
        break;
    }
  };

  const HandleQ401cFieldChange = (e) => {
    const { name, id, value } = e.target;

    switch (name) {
      //Elrexfio
      case "Q401cChangeType_Elr" + id: {
        let updatedArray = [...Q401cElrDetails];
        const numericValue = Number(value);

        updatedArray[id].Q401cChangeType_Elr = numericValue;
        updatedArray[id].Q401cChangeTypeEmptyHard_Elr = false;

        updatedArray[id].Q401cFreqBeforeRd_Elr = null;
        updatedArray[id].Q401cFreqBeforeTimesTxt_Elr = "";
        updatedArray[id].Q401cFreqBeforeWeeksTxt_Elr = "";
        updatedArray[id].Q401cFreqBeforeOtherTxt_Elr = "";
        updatedArray[id].Q401cFreqBeforeEmptyHard_Elr = false;
        updatedArray[id].Q401cFreqBeforeTxtEmptyHard_Elr = false;
        updatedArray[id].Q401cFreqBeforeRangeInvalid_Elr = false;
        updatedArray[id].Q401cFreqBeforeAutoPopulated_Elr = false;

        updatedArray[id].Q401cFreqAfterRd_Elr = null;
        updatedArray[id].Q401cFreqAfterTimesTxt_Elr = "";
        updatedArray[id].Q401cFreqAfterWeeksTxt_Elr = "";
        updatedArray[id].Q401cFreqAfterOtherTxt_Elr = "";
        updatedArray[id].Q401cFreqAfterEmptyHard_Elr = false;
        updatedArray[id].Q401cFreqAfterTxtEmptyHard_Elr = false;
        updatedArray[id].Q401cFreqAfterRangeInvalid_Elr = false;

        updatedArray[id].Q401cDateChangeRd_Elr = null;
        updatedArray[id].Q401cDateChange_Elr = "";
        updatedArray[id].Q401cDateChangeRdHard_Elr = false;
        updatedArray[id].Q401cDateChangeDtHard_Elr = false;
        updatedArray[id].Q401cDateChangeInvalid_Elr = false;
        updatedArray[id].Q401cDateChangeOutOfBound_Elr = false;

        updatedArray[id].Q401cFrequencyIncreaseConflict_Elr = false;
        updatedArray[id].Q401cFrequencyDecreaseConflict_Elr = false;
        updatedArray[id].Q401cWarningUnk_Elr = false;

        // Auto-populate "before" for the earliest row from Q40.C's intended dosing frequency
        if (Number(id) === 0 && (numericValue === 2 || numericValue === 3)) {
          const q40cRow = Q37ElrexDetails?.[0] || {};
          updatedArray[id].Q401cFreqBeforeRd_Elr =
            q40cRow.ivigDosingFreqRd_elrC ?? null;
          updatedArray[id].Q401cFreqBeforeTimesTxt_Elr =
            q40cRow.ivigDosingFreqTimesTxt_elrC ?? "";
          updatedArray[id].Q401cFreqBeforeWeeksTxt_Elr =
            q40cRow.ivigDosingFreqWeeksTxt_elrC ?? "";
          updatedArray[id].Q401cFreqBeforeOtherTxt_Elr =
            q40cRow.ivigDosingFreqOtherTxt_elrC ?? "";
          updatedArray[id].Q401cFreqBeforeAutoPopulated_Elr = true;
        }

        // Collapse back to a single row when "No change" or "Unknown" is (re)selected on row 0
        if (Number(id) === 0 && (numericValue === 1 || numericValue === 99)) {
          updatedArray = [updatedArray[0]];
        }

        setQ401cElrDetails(updatedArray);
        break;
      }

      case "Q401cFreqBeforeRd_Elr" + id: {
        let updatedArray = [...Q401cElrDetails];
        updatedArray[id].Q401cFreqBeforeRd_Elr = Number(value);
        updatedArray[id].Q401cFreqBeforeTimesTxt_Elr = "";
        updatedArray[id].Q401cFreqBeforeWeeksTxt_Elr = "";
        updatedArray[id].Q401cFreqBeforeOtherTxt_Elr = "";
        updatedArray[id].Q401cFreqBeforeEmptyHard_Elr = false;
        updatedArray[id].Q401cFreqBeforeTxtEmptyHard_Elr = false;
        updatedArray[id].Q401cFreqBeforeRangeInvalid_Elr = false;
        updatedArray[id].Q401cFrequencyIncreaseConflict_Elr = false;
        updatedArray[id].Q401cFrequencyDecreaseConflict_Elr = false;
        setQ401cElrDetails(updatedArray);
        break;
      }
      case "Q401cFreqBeforeTimesTxt_Elr" + id: {
        let updatedArray = [...Q401cElrDetails];
        updatedArray[id].Q401cFreqBeforeTimesTxt_Elr = value;
        updatedArray[id].Q401cFreqBeforeWeeksTxt_Elr = "";
        updatedArray[id].Q401cFreqBeforeOtherTxt_Elr = "";
        updatedArray[id].Q401cFreqBeforeRd_Elr = 1;
        updatedArray[id].Q401cFreqBeforeEmptyHard_Elr = false;
        updatedArray[id].Q401cFreqBeforeTxtEmptyHard_Elr = false;
        updatedArray[id].Q401cFreqBeforeRangeInvalid_Elr = false;
        updatedArray[id].Q401cFrequencyIncreaseConflict_Elr = false;
        updatedArray[id].Q401cFrequencyDecreaseConflict_Elr = false;
        setQ401cElrDetails(updatedArray);
        break;
      }
      case "Q401cFreqBeforeWeeksTxt_Elr" + id: {
        let updatedArray = [...Q401cElrDetails];
        updatedArray[id].Q401cFreqBeforeWeeksTxt_Elr = value;
        updatedArray[id].Q401cFreqBeforeTimesTxt_Elr = "";
        updatedArray[id].Q401cFreqBeforeOtherTxt_Elr = "";
        updatedArray[id].Q401cFreqBeforeRd_Elr = 2;
        updatedArray[id].Q401cFreqBeforeEmptyHard_Elr = false;
        updatedArray[id].Q401cFreqBeforeTxtEmptyHard_Elr = false;
        updatedArray[id].Q401cFreqBeforeRangeInvalid_Elr = false;
        updatedArray[id].Q401cFrequencyIncreaseConflict_Elr = false;
        updatedArray[id].Q401cFrequencyDecreaseConflict_Elr = false;
        setQ401cElrDetails(updatedArray);
        break;
      }
      case "Q401cFreqBeforeOtherTxt_Elr" + id: {
        let updatedArray = [...Q401cElrDetails];
        updatedArray[id].Q401cFreqBeforeOtherTxt_Elr = value;
        updatedArray[id].Q401cFreqBeforeTimesTxt_Elr = "";
        updatedArray[id].Q401cFreqBeforeWeeksTxt_Elr = "";
        updatedArray[id].Q401cFreqBeforeRd_Elr = 3;
        updatedArray[id].Q401cFreqBeforeEmptyHard_Elr = false;
        updatedArray[id].Q401cFreqBeforeTxtEmptyHard_Elr = false;
        setQ401cElrDetails(updatedArray);
        break;
      }

      case "Q401cFreqAfterRd_Elr" + id: {
        let updatedArray = [...Q401cElrDetails];
        updatedArray[id].Q401cFreqAfterRd_Elr = Number(value);
        updatedArray[id].Q401cFreqAfterTimesTxt_Elr = "";
        updatedArray[id].Q401cFreqAfterWeeksTxt_Elr = "";
        updatedArray[id].Q401cFreqAfterOtherTxt_Elr = "";
        updatedArray[id].Q401cFreqAfterEmptyHard_Elr = false;
        updatedArray[id].Q401cFreqAfterTxtEmptyHard_Elr = false;
        updatedArray[id].Q401cFreqAfterRangeInvalid_Elr = false;
        updatedArray[id].Q401cFrequencyIncreaseConflict_Elr = false;
        updatedArray[id].Q401cFrequencyDecreaseConflict_Elr = false;
        setQ401cElrDetails(updatedArray);
        break;
      }
      case "Q401cFreqAfterTimesTxt_Elr" + id: {
        let updatedArray = [...Q401cElrDetails];
        updatedArray[id].Q401cFreqAfterTimesTxt_Elr = value;
        updatedArray[id].Q401cFreqAfterWeeksTxt_Elr = "";
        updatedArray[id].Q401cFreqAfterOtherTxt_Elr = "";
        updatedArray[id].Q401cFreqAfterRd_Elr = 1;
        updatedArray[id].Q401cFreqAfterEmptyHard_Elr = false;
        updatedArray[id].Q401cFreqAfterTxtEmptyHard_Elr = false;
        updatedArray[id].Q401cFreqAfterRangeInvalid_Elr = false;
        updatedArray[id].Q401cFrequencyIncreaseConflict_Elr = false;
        updatedArray[id].Q401cFrequencyDecreaseConflict_Elr = false;
        setQ401cElrDetails(updatedArray);
        break;
      }
      case "Q401cFreqAfterWeeksTxt_Elr" + id: {
        let updatedArray = [...Q401cElrDetails];
        updatedArray[id].Q401cFreqAfterWeeksTxt_Elr = value;
        updatedArray[id].Q401cFreqAfterTimesTxt_Elr = "";
        updatedArray[id].Q401cFreqAfterOtherTxt_Elr = "";
        updatedArray[id].Q401cFreqAfterRd_Elr = 2;
        updatedArray[id].Q401cFreqAfterEmptyHard_Elr = false;
        updatedArray[id].Q401cFreqAfterTxtEmptyHard_Elr = false;
        updatedArray[id].Q401cFreqAfterRangeInvalid_Elr = false;
        updatedArray[id].Q401cFrequencyIncreaseConflict_Elr = false;
        updatedArray[id].Q401cFrequencyDecreaseConflict_Elr = false;
        setQ401cElrDetails(updatedArray);
        break;
      }
      case "Q401cFreqAfterOtherTxt_Elr" + id: {
        let updatedArray = [...Q401cElrDetails];
        updatedArray[id].Q401cFreqAfterOtherTxt_Elr = value;
        updatedArray[id].Q401cFreqAfterTimesTxt_Elr = "";
        updatedArray[id].Q401cFreqAfterWeeksTxt_Elr = "";
        updatedArray[id].Q401cFreqAfterRd_Elr = 3;
        updatedArray[id].Q401cFreqAfterEmptyHard_Elr = false;
        updatedArray[id].Q401cFreqAfterTxtEmptyHard_Elr = false;
        setQ401cElrDetails(updatedArray);
        break;
      }

      case "Q401cDateChangeRd_Elr" + id: {
        let updatedArray = [...Q401cElrDetails];
        updatedArray[id].Q401cDateChangeRd_Elr = Number(value);
        updatedArray[id].Q401cDateChange_Elr =
          Number(value) === 1 ? updatedArray[id].Q401cDateChange_Elr : "";
        updatedArray[id].Q401cDateChangeRdHard_Elr = false;
        updatedArray[id].Q401cDateChangeDtHard_Elr = false;
        updatedArray[id].Q401cDateChangeInvalid_Elr = false;
        updatedArray[id].Q401cDateChangeOutOfBound_Elr = false;
        setQ401cElrDetails(updatedArray);
        break;
      }

      //Linvoseltamab
      case "Q401cChangeType_Lin" + id: {
        let updatedArray = [...Q401cLinvoDetails];
        const numericValue = Number(value);

        updatedArray[id].Q401cChangeType_Lin = numericValue;
        updatedArray[id].Q401cChangeTypeEmptyHard_Lin = false;

        updatedArray[id].Q401cFreqBeforeRd_Lin = null;
        updatedArray[id].Q401cFreqBeforeTimesTxt_Lin = "";
        updatedArray[id].Q401cFreqBeforeWeeksTxt_Lin = "";
        updatedArray[id].Q401cFreqBeforeOtherTxt_Lin = "";
        updatedArray[id].Q401cFreqBeforeEmptyHard_Lin = false;
        updatedArray[id].Q401cFreqBeforeTxtEmptyHard_Lin = false;
        updatedArray[id].Q401cFreqBeforeRangeInvalid_Lin = false;
        updatedArray[id].Q401cFreqBeforeAutoPopulated_Lin = false;

        updatedArray[id].Q401cFreqAfterRd_Lin = null;
        updatedArray[id].Q401cFreqAfterTimesTxt_Lin = "";
        updatedArray[id].Q401cFreqAfterWeeksTxt_Lin = "";
        updatedArray[id].Q401cFreqAfterOtherTxt_Lin = "";
        updatedArray[id].Q401cFreqAfterEmptyHard_Lin = false;
        updatedArray[id].Q401cFreqAfterTxtEmptyHard_Lin = false;
        updatedArray[id].Q401cFreqAfterRangeInvalid_Lin = false;

        updatedArray[id].Q401cDateChangeRd_Lin = null;
        updatedArray[id].Q401cDateChange_Lin = "";
        updatedArray[id].Q401cDateChangeRdHard_Lin = false;
        updatedArray[id].Q401cDateChangeDtHard_Lin = false;
        updatedArray[id].Q401cDateChangeInvalid_Lin = false;
        updatedArray[id].Q401cDateChangeOutOfBound_Lin = false;

        updatedArray[id].Q401cFrequencyIncreaseConflict_Lin = false;
        updatedArray[id].Q401cFrequencyDecreaseConflict_Lin = false;
        updatedArray[id].Q401cWarningUnk_Lin = false;

        if (Number(id) === 0 && (numericValue === 2 || numericValue === 3)) {
          const q40cRow = Q37LinvoDetails?.[0] || {};
          updatedArray[id].Q401cFreqBeforeRd_Lin =
            q40cRow.ivigDosingFreqRd_linvoC ?? null;
          updatedArray[id].Q401cFreqBeforeTimesTxt_Lin =
            q40cRow.ivigDosingFreqTimesTxt_linvoC ?? "";
          updatedArray[id].Q401cFreqBeforeWeeksTxt_Lin =
            q40cRow.ivigDosingFreqWeeksTxt_linvoC ?? "";
          updatedArray[id].Q401cFreqBeforeOtherTxt_Lin =
            q40cRow.ivigDosingFreqOtherTxt_linvoC ?? "";
          updatedArray[id].Q401cFreqBeforeAutoPopulated_Lin = true;
        }

        if (Number(id) === 0 && (numericValue === 1 || numericValue === 99)) {
          updatedArray = [updatedArray[0]];
        }

        setQ401cLinvoDetails(updatedArray);
        break;
      }

      case "Q401cFreqBeforeRd_Lin" + id: {
        let updatedArray = [...Q401cLinvoDetails];
        updatedArray[id].Q401cFreqBeforeRd_Lin = Number(value);
        updatedArray[id].Q401cFreqBeforeTimesTxt_Lin = "";
        updatedArray[id].Q401cFreqBeforeWeeksTxt_Lin = "";
        updatedArray[id].Q401cFreqBeforeOtherTxt_Lin = "";
        updatedArray[id].Q401cFreqBeforeEmptyHard_Lin = false;
        updatedArray[id].Q401cFreqBeforeTxtEmptyHard_Lin = false;
        updatedArray[id].Q401cFreqBeforeRangeInvalid_Lin = false;
        updatedArray[id].Q401cFrequencyIncreaseConflict_Lin = false;
        updatedArray[id].Q401cFrequencyDecreaseConflict_Lin = false;
        setQ401cLinvoDetails(updatedArray);
        break;
      }
      case "Q401cFreqBeforeTimesTxt_Lin" + id: {
        let updatedArray = [...Q401cLinvoDetails];
        updatedArray[id].Q401cFreqBeforeTimesTxt_Lin = value;
        updatedArray[id].Q401cFreqBeforeWeeksTxt_Lin = "";
        updatedArray[id].Q401cFreqBeforeOtherTxt_Lin = "";
        updatedArray[id].Q401cFreqBeforeRd_Lin = 1;
        updatedArray[id].Q401cFreqBeforeEmptyHard_Lin = false;
        updatedArray[id].Q401cFreqBeforeTxtEmptyHard_Lin = false;
        updatedArray[id].Q401cFreqBeforeRangeInvalid_Lin = false;
        updatedArray[id].Q401cFrequencyIncreaseConflict_Lin = false;
        updatedArray[id].Q401cFrequencyDecreaseConflict_Lin = false;
        setQ401cLinvoDetails(updatedArray);
        break;
      }
      case "Q401cFreqBeforeWeeksTxt_Lin" + id: {
        let updatedArray = [...Q401cLinvoDetails];
        updatedArray[id].Q401cFreqBeforeWeeksTxt_Lin = value;
        updatedArray[id].Q401cFreqBeforeTimesTxt_Lin = "";
        updatedArray[id].Q401cFreqBeforeOtherTxt_Lin = "";
        updatedArray[id].Q401cFreqBeforeRd_Lin = 2;
        updatedArray[id].Q401cFreqBeforeEmptyHard_Lin = false;
        updatedArray[id].Q401cFreqBeforeTxtEmptyHard_Lin = false;
        updatedArray[id].Q401cFreqBeforeRangeInvalid_Lin = false;
        updatedArray[id].Q401cFrequencyIncreaseConflict_Lin = false;
        updatedArray[id].Q401cFrequencyDecreaseConflict_Lin = false;
        setQ401cLinvoDetails(updatedArray);
        break;
      }
      case "Q401cFreqBeforeOtherTxt_Lin" + id: {
        let updatedArray = [...Q401cLinvoDetails];
        updatedArray[id].Q401cFreqBeforeOtherTxt_Lin = value;
        updatedArray[id].Q401cFreqBeforeTimesTxt_Lin = "";
        updatedArray[id].Q401cFreqBeforeWeeksTxt_Lin = "";
        updatedArray[id].Q401cFreqBeforeRd_Lin = 3;
        updatedArray[id].Q401cFreqBeforeEmptyHard_Lin = false;
        updatedArray[id].Q401cFreqBeforeTxtEmptyHard_Lin = false;
        setQ401cLinvoDetails(updatedArray);
        break;
      }

      case "Q401cFreqAfterRd_Lin" + id: {
        let updatedArray = [...Q401cLinvoDetails];
        updatedArray[id].Q401cFreqAfterRd_Lin = Number(value);
        updatedArray[id].Q401cFreqAfterTimesTxt_Lin = "";
        updatedArray[id].Q401cFreqAfterWeeksTxt_Lin = "";
        updatedArray[id].Q401cFreqAfterOtherTxt_Lin = "";
        updatedArray[id].Q401cFreqAfterEmptyHard_Lin = false;
        updatedArray[id].Q401cFreqAfterTxtEmptyHard_Lin = false;
        updatedArray[id].Q401cFreqAfterRangeInvalid_Lin = false;
        updatedArray[id].Q401cFrequencyIncreaseConflict_Lin = false;
        updatedArray[id].Q401cFrequencyDecreaseConflict_Lin = false;
        setQ401cLinvoDetails(updatedArray);
        break;
      }
      case "Q401cFreqAfterTimesTxt_Lin" + id: {
        let updatedArray = [...Q401cLinvoDetails];
        updatedArray[id].Q401cFreqAfterTimesTxt_Lin = value;
        updatedArray[id].Q401cFreqAfterWeeksTxt_Lin = "";
        updatedArray[id].Q401cFreqAfterOtherTxt_Lin = "";
        updatedArray[id].Q401cFreqAfterRd_Lin = 1;
        updatedArray[id].Q401cFreqAfterEmptyHard_Lin = false;
        updatedArray[id].Q401cFreqAfterTxtEmptyHard_Lin = false;
        updatedArray[id].Q401cFreqAfterRangeInvalid_Lin = false;
        updatedArray[id].Q401cFrequencyIncreaseConflict_Lin = false;
        updatedArray[id].Q401cFrequencyDecreaseConflict_Lin = false;
        setQ401cLinvoDetails(updatedArray);
        break;
      }
      case "Q401cFreqAfterWeeksTxt_Lin" + id: {
        let updatedArray = [...Q401cLinvoDetails];
        updatedArray[id].Q401cFreqAfterWeeksTxt_Lin = value;
        updatedArray[id].Q401cFreqAfterTimesTxt_Lin = "";
        updatedArray[id].Q401cFreqAfterOtherTxt_Lin = "";
        updatedArray[id].Q401cFreqAfterRd_Lin = 2;
        updatedArray[id].Q401cFreqAfterEmptyHard_Lin = false;
        updatedArray[id].Q401cFreqAfterTxtEmptyHard_Lin = false;
        updatedArray[id].Q401cFreqAfterRangeInvalid_Lin = false;
        updatedArray[id].Q401cFrequencyIncreaseConflict_Lin = false;
        updatedArray[id].Q401cFrequencyDecreaseConflict_Lin = false;
        setQ401cLinvoDetails(updatedArray);
        break;
      }
      case "Q401cFreqAfterOtherTxt_Lin" + id: {
        let updatedArray = [...Q401cLinvoDetails];
        updatedArray[id].Q401cFreqAfterOtherTxt_Lin = value;
        updatedArray[id].Q401cFreqAfterTimesTxt_Lin = "";
        updatedArray[id].Q401cFreqAfterWeeksTxt_Lin = "";
        updatedArray[id].Q401cFreqAfterRd_Lin = 3;
        updatedArray[id].Q401cFreqAfterEmptyHard_Lin = false;
        updatedArray[id].Q401cFreqAfterTxtEmptyHard_Lin = false;
        setQ401cLinvoDetails(updatedArray);
        break;
      }

      case "Q401cDateChangeRd_Lin" + id: {
        let updatedArray = [...Q401cLinvoDetails];
        updatedArray[id].Q401cDateChangeRd_Lin = Number(value);
        updatedArray[id].Q401cDateChange_Lin =
          Number(value) === 1 ? updatedArray[id].Q401cDateChange_Lin : "";
        updatedArray[id].Q401cDateChangeRdHard_Lin = false;
        updatedArray[id].Q401cDateChangeDtHard_Lin = false;
        updatedArray[id].Q401cDateChangeInvalid_Lin = false;
        updatedArray[id].Q401cDateChangeOutOfBound_Lin = false;
        setQ401cLinvoDetails(updatedArray);
        break;
      }

      default:
        break;
    }
  };

  const HandleQ402aRepeaterDateChange = (name, date) => {
    const lastIndex = name.lastIndexOf("-");
    const id = name.slice(lastIndex + 1);
    switch (name) {
      case "Q402aIgg_assessment_Elr_dt-" + id: {
        let updatedArray = [...Q402aElrDetails];

        updatedArray[id].Q402aIgg_assessment_Elr = 2;
        updatedArray[id].Q402aIgg_assessment_Elr_dt = date;
        updatedArray[id].Q402aIgg_assessment_Elr_RdHard = false;
        updatedArray[id].Q402aIgg_assessment_Elr_dtHard = false;
        updatedArray[id].Q402aIgg_assessment_Elr_dtInvalid = false;
        updatedArray[id].Q402aIgg_assessment_Elr_dt_before_eof = false;

        setQ402aElrDetails(updatedArray);
        break;
      }
      case "Q402aIgg_assessment_Lin_dt-" + id: {
        let updatedArray = [...Q402aLinvoDetails];

        updatedArray[id].Q402aIgg_assessment_Lin = 2;
        updatedArray[id].Q402aIgg_assessment_Lin_dt = date;
        updatedArray[id].Q402aIgg_assessment_Lin_RdHard = false;
        updatedArray[id].Q402aIgg_assessment_Lin_dtHard = false;
        updatedArray[id].Q402aIgg_assessment_Lin_dtInvalid = false;
        updatedArray[id].Q402aIgg_assessment_Lin_dt_before_eof = false;

        setQ402aLinvoDetails(updatedArray);
        break;
      }
      default:
        break;
    }
  };

  const HandleQ402aFieldChange = (e) => {
    const { name, id, value } = e.target;

    switch (name) {
      //Tec
      case "Q402aIgg_assessment_Elr" + id: {
        let updatedArray = [...Q402aElrDetails];

        updatedArray[id].Q402aIgg_assessment_Elr = Number(value);
        updatedArray[id].Q402aIgg_assessment_Elr_dt =
          Number(value) === 2
            ? updatedArray[id].Q402aIgg_assessment_Elr_dt
            : "";
        updatedArray[id].Q402aIgg_assessment_Elr_RdHard = false;
        updatedArray[id].Q402aIgg_assessment_Elr_dtHard = false;
        updatedArray[id].Q402aIgg_assessment_Elr_dtInvalid = false;
        updatedArray[id].Q402aIgg_assessment_Elr_dt_before_eof = false;
        updatedArray[id].Q402aIgg_level_Elr_MgValue = "";
        updatedArray[id].Q402aIgg_level_Elr_GValue = "";
        updatedArray[id].Q402aIgg_level_Elr = null;

        if (Number(value) === 1) {
          updatedArray[id].Q402aIgg_level_Elr = null;
          updatedArray[id].Q402aIgg_level_Elr_MgValue = "";
          updatedArray[id].Q402aIgg_level_Elr_GValue = "";
          updatedArray[id].Q402aIgg_level_Elr_Empty = false;
          updatedArray[id].Q402aIgg_level_Elr_RangeInvalid = false;
        }

        setQ402aElrDetails(updatedArray);
        break;
      }

      case "Q402aIgg_level_Elr" + id: {
        let updatedArray = [...Q402aElrDetails];

        updatedArray[id].Q402aIgg_level_Elr = Number(value);
        updatedArray[id].Q402aIgg_level_Elr_MgValue = "";
        updatedArray[id].Q402aIgg_level_Elr_GValue = "";
        updatedArray[id].Q402aIgg_level_Elr_Empty = false;
        updatedArray[id].Q402aIgg_level_Elr_RangeInvalid = false;

        setQ402aElrDetails(updatedArray);
        break;
      }

      case "Q402aIgg_level_Elr_MgValue" + id: {
        let updatedArray = [...Q402aElrDetails];

        updatedArray[id].Q402aIgg_level_Elr = 1;
        updatedArray[id].Q402aIgg_level_Elr_MgValue = value;
        updatedArray[id].Q402aIgg_level_Elr_GValue = "";
        updatedArray[id].Q402aIgg_level_Elr_Empty = false;
        updatedArray[id].Q402aIgg_level_Elr_RangeInvalid = false;

        setQ402aElrDetails(updatedArray);
        break;
      }

      case "Q402aIgg_level_Elr_GValue" + id: {
        let updatedArray = [...Q402aElrDetails];

        updatedArray[id].Q402aIgg_level_Elr = 2;
        updatedArray[id].Q402aIgg_level_Elr_GValue = value;
        updatedArray[id].Q402aIgg_level_Elr_MgValue = "";
        updatedArray[id].Q402aIgg_level_Elr_Empty = false;
        updatedArray[id].Q402aIgg_level_Elr_RangeInvalid = false;

        setQ402aElrDetails(updatedArray);
        break;
      }

      //Linvo
      case "Q402aIgg_assessment_Lin" + id: {
        let updatedArray = [...Q402aLinvoDetails];

        updatedArray[id].Q402aIgg_assessment_Lin = Number(value);
        updatedArray[id].Q402aIgg_assessment_Lin_dt =
          Number(value) === 2
            ? updatedArray[id].Q402aIgg_assessment_Lin_dt
            : "";
        updatedArray[id].Q402aIgg_assessment_Lin_RdHard = false;
        updatedArray[id].Q402aIgg_assessment_Lin_dtHard = false;
        updatedArray[id].Q402aIgg_assessment_Lin_dtInvalid = false;
        updatedArray[id].Q402aIgg_assessment_Lin_dt_before_eof = false;

        if (Number(value) === 1) {
          updatedArray[id].Q402aIgg_level_Lin = null;
          updatedArray[id].Q402aIgg_level_Lin_MgValue = "";
          updatedArray[id].Q402aIgg_level_Lin_GValue = "";
          updatedArray[id].Q402aIgg_level_Lin_Empty = false;
          updatedArray[id].Q402aIgg_level_Lin_RangeInvalid = false;
        }

        setQ402aLinvoDetails(updatedArray);
        break;
      }

      case "Q402aIgg_level_Lin" + id: {
        let updatedArray = [...Q402aLinvoDetails];

        updatedArray[id].Q402aIgg_level_Lin = Number(value);
        updatedArray[id].Q402aIgg_level_Lin_MgValue = "";
        updatedArray[id].Q402aIgg_level_Lin_GValue = "";
        updatedArray[id].Q402aIgg_level_Lin_Empty = false;
        updatedArray[id].Q402aIgg_level_Lin_RangeInvalid = false;

        setQ402aLinvoDetails(updatedArray);
        break;
      }

      case "Q402aIgg_level_Lin_MgValue" + id: {
        let updatedArray = [...Q402aLinvoDetails];

        updatedArray[id].Q402aIgg_level_Lin = 1;
        updatedArray[id].Q402aIgg_level_Lin_MgValue = value;
        updatedArray[id].Q402aIgg_level_Lin_GValue = "";
        updatedArray[id].Q402aIgg_level_Lin_Empty = false;
        updatedArray[id].Q402aIgg_level_Lin_RangeInvalid = false;

        setQ402aLinvoDetails(updatedArray);
        break;
      }

      case "Q402aIgg_level_Lin_GValue" + id: {
        let updatedArray = [...Q402aLinvoDetails];

        updatedArray[id].Q402aIgg_level_Lin = 2;
        updatedArray[id].Q402aIgg_level_Lin_GValue = value;
        updatedArray[id].Q402aIgg_level_Lin_MgValue = "";
        updatedArray[id].Q402aIgg_level_Lin_Empty = false;
        updatedArray[id].Q402aIgg_level_Lin_RangeInvalid = false;

        setQ402aLinvoDetails(updatedArray);
        break;
      }
      default:
        break;
    }
    //
  };

  // Q40.2.C - IgG level at target dates/windows
  const HandleQ402cRepeaterDateChange = (name, date) => {
    const lastIndex = name.lastIndexOf("-");
    const id = name.slice(lastIndex + 1);
    switch (name) {
      case "Q402cAssessment_Elr_dt-" + id: {
        let updatedArray = [...Q402cElrDetails];

        updatedArray[id].Q402cAssessment_Elr = 2;
        updatedArray[id].Q402cAssessment_Elr_dt = date;
        updatedArray[id].Q402cAssessment_Elr_RdHard = false;
        updatedArray[id].Q402cAssessment_Elr_dtHard = false;
        updatedArray[id].Q402cAssessment_Elr_dtInvalid = false;
        updatedArray[id].Q402cAssessment_Elr_dtOutOfRange = false;

        setQ402cElrDetails(updatedArray);
        break;
      }
      case "Q402cAssessment_Lin_dt-" + id: {
        let updatedArray = [...Q402cLinvoDetails];

        updatedArray[id].Q402cAssessment_Lin = 2;
        updatedArray[id].Q402cAssessment_Lin_dt = date;
        updatedArray[id].Q402cAssessment_Lin_RdHard = false;
        updatedArray[id].Q402cAssessment_Lin_dtHard = false;
        updatedArray[id].Q402cAssessment_Lin_dtInvalid = false;
        updatedArray[id].Q402cAssessment_Lin_dtOutOfRange = false;

        setQ402cLinvoDetails(updatedArray);
        break;
      }
      default:
        break;
    }
  };

  const HandleQ402cFieldChange = (e) => {
    const { name, id, value } = e.target;

    switch (name) {
      //Elrexfio
      case "Q402cAssessment_Elr" + id: {
        let updatedArray = [...Q402cElrDetails];

        updatedArray[id].Q402cAssessment_Elr = Number(value);
        updatedArray[id].Q402cAssessment_Elr_dt =
          Number(value) === 2 ? updatedArray[id].Q402cAssessment_Elr_dt : "";
        updatedArray[id].Q402cAssessment_Elr_RdHard = false;
        updatedArray[id].Q402cAssessment_Elr_dtHard = false;
        updatedArray[id].Q402cAssessment_Elr_dtInvalid = false;
        updatedArray[id].Q402cAssessment_Elr_dtOutOfRange = false;

        if (Number(value) === 1) {
          updatedArray[id].Q402cLevelBucket_Elr = null;
          updatedArray[id].Q402cLevelBucket_Elr_Empty = false;
          updatedArray[id].Q402cLevelValue_Elr = null;
          updatedArray[id].Q402cLevelValue_Elr_MgValue = "";
          updatedArray[id].Q402cLevelValue_Elr_GValue = "";
          updatedArray[id].Q402cLevelValue_Elr_Empty = false;
          updatedArray[id].Q402cLevelValue_Elr_RangeInvalid = false;
        }

        setQ402cElrDetails(updatedArray);
        break;
      }

      case "Q402cLevelBucket_Elr" + id: {
        let updatedArray = [...Q402cElrDetails];

        updatedArray[id].Q402cLevelBucket_Elr = Number(value);
        updatedArray[id].Q402cLevelBucket_Elr_Empty = false;

        setQ402cElrDetails(updatedArray);
        break;
      }

      case "Q402cLevelValue_Elr" + id: {
        let updatedArray = [...Q402cElrDetails];

        updatedArray[id].Q402cLevelValue_Elr = Number(value);
        updatedArray[id].Q402cLevelValue_Elr_MgValue = "";
        updatedArray[id].Q402cLevelValue_Elr_GValue = "";
        updatedArray[id].Q402cLevelValue_Elr_Empty = false;
        updatedArray[id].Q402cLevelValue_Elr_RangeInvalid = false;

        setQ402cElrDetails(updatedArray);
        break;
      }

      case "Q402cLevelValue_Elr_MgValue" + id: {
        let updatedArray = [...Q402cElrDetails];

        updatedArray[id].Q402cLevelValue_Elr = 1;
        updatedArray[id].Q402cLevelValue_Elr_MgValue = value;
        updatedArray[id].Q402cLevelValue_Elr_GValue = "";
        updatedArray[id].Q402cLevelValue_Elr_Empty = false;
        updatedArray[id].Q402cLevelValue_Elr_RangeInvalid = false;

        setQ402cElrDetails(updatedArray);
        break;
      }

      case "Q402cLevelValue_Elr_GValue" + id: {
        let updatedArray = [...Q402cElrDetails];

        updatedArray[id].Q402cLevelValue_Elr = 2;
        updatedArray[id].Q402cLevelValue_Elr_GValue = value;
        updatedArray[id].Q402cLevelValue_Elr_MgValue = "";
        updatedArray[id].Q402cLevelValue_Elr_Empty = false;
        updatedArray[id].Q402cLevelValue_Elr_RangeInvalid = false;

        setQ402cElrDetails(updatedArray);
        break;
      }

      //Linvoseltamab
      case "Q402cAssessment_Lin" + id: {
        let updatedArray = [...Q402cLinvoDetails];

        updatedArray[id].Q402cAssessment_Lin = Number(value);
        updatedArray[id].Q402cAssessment_Lin_dt =
          Number(value) === 2 ? updatedArray[id].Q402cAssessment_Lin_dt : "";
        updatedArray[id].Q402cAssessment_Lin_RdHard = false;
        updatedArray[id].Q402cAssessment_Lin_dtHard = false;
        updatedArray[id].Q402cAssessment_Lin_dtInvalid = false;
        updatedArray[id].Q402cAssessment_Lin_dtOutOfRange = false;

        if (Number(value) === 1) {
          updatedArray[id].Q402cLevelBucket_Lin = null;
          updatedArray[id].Q402cLevelBucket_Lin_Empty = false;
          updatedArray[id].Q402cLevelValue_Lin = null;
          updatedArray[id].Q402cLevelValue_Lin_MgValue = "";
          updatedArray[id].Q402cLevelValue_Lin_GValue = "";
          updatedArray[id].Q402cLevelValue_Lin_Empty = false;
          updatedArray[id].Q402cLevelValue_Lin_RangeInvalid = false;
        }

        setQ402cLinvoDetails(updatedArray);
        break;
      }

      case "Q402cLevelBucket_Lin" + id: {
        let updatedArray = [...Q402cLinvoDetails];

        updatedArray[id].Q402cLevelBucket_Lin = Number(value);
        updatedArray[id].Q402cLevelBucket_Lin_Empty = false;

        setQ402cLinvoDetails(updatedArray);
        break;
      }

      case "Q402cLevelValue_Lin" + id: {
        let updatedArray = [...Q402cLinvoDetails];

        updatedArray[id].Q402cLevelValue_Lin = Number(value);
        updatedArray[id].Q402cLevelValue_Lin_MgValue = "";
        updatedArray[id].Q402cLevelValue_Lin_GValue = "";
        updatedArray[id].Q402cLevelValue_Lin_Empty = false;
        updatedArray[id].Q402cLevelValue_Lin_RangeInvalid = false;

        setQ402cLinvoDetails(updatedArray);
        break;
      }

      case "Q402cLevelValue_Lin_MgValue" + id: {
        let updatedArray = [...Q402cLinvoDetails];

        updatedArray[id].Q402cLevelValue_Lin = 1;
        updatedArray[id].Q402cLevelValue_Lin_MgValue = value;
        updatedArray[id].Q402cLevelValue_Lin_GValue = "";
        updatedArray[id].Q402cLevelValue_Lin_Empty = false;
        updatedArray[id].Q402cLevelValue_Lin_RangeInvalid = false;

        setQ402cLinvoDetails(updatedArray);
        break;
      }

      case "Q402cLevelValue_Lin_GValue" + id: {
        let updatedArray = [...Q402cLinvoDetails];

        updatedArray[id].Q402cLevelValue_Lin = 2;
        updatedArray[id].Q402cLevelValue_Lin_GValue = value;
        updatedArray[id].Q402cLevelValue_Lin_MgValue = "";
        updatedArray[id].Q402cLevelValue_Lin_Empty = false;
        updatedArray[id].Q402cLevelValue_Lin_RangeInvalid = false;

        setQ402cLinvoDetails(updatedArray);
        break;
      }

      default:
        break;
    }
  };

  const HandleQ403aRepeaterDateChange = (name, date) => {
    const lastIndex = name.lastIndexOf("-");
    const id = name.slice(lastIndex + 1);
    switch (name) {
      case "Q403aIgg_assessment_Elr_dt-" + id: {
        let updatedArray = [...Q403aElrDetails];

        updatedArray[id].Q403aIgg_assessment_Elr = 2;
        updatedArray[id].Q403aIgg_assessment_Elr_dt = date;
        updatedArray[id].Q403aIgg_assessment_Elr_RdHard = false;
        updatedArray[id].Q403aIgg_assessment_Elr_dtHard = false;
        updatedArray[id].Q403aIgg_assessment_Elr_dtInvalid = false;
        updatedArray[id].Q403aIgg_assessment_Elr_dt_before_eof = false;

        setQ403aElrDetails(updatedArray);
        break;
      }
      case "Q403aIgg_assessment_Lin_dt-" + id: {
        let updatedArray = [...Q403aLinvoDetails];

        updatedArray[id].Q403aIgg_assessment_Lin = 2;
        updatedArray[id].Q403aIgg_assessment_Lin_dt = date;
        updatedArray[id].Q403aIgg_assessment_Lin_RdHard = false;
        updatedArray[id].Q403aIgg_assessment_Lin_dtHard = false;
        updatedArray[id].Q403aIgg_assessment_Lin_dtInvalid = false;
        updatedArray[id].Q403aIgg_assessment_Lin_dt_before_eof = false;

        setQ403aLinvoDetails(updatedArray);
        break;
      }
      default:
        break;
    }
  };

  const HandleQ403aFieldChange = (e) => {
    const { name, id, value } = e.target;

    switch (name) {
      //Tec
      case "Q403aIgg_assessment_Elr" + id: {
        let updatedArray = [...Q403aElrDetails];

        updatedArray[id].Q403aIgg_assessment_Elr = Number(value);
        updatedArray[id].Q403aIgg_assessment_Elr_dt =
          Number(value) === 2
            ? updatedArray[id].Q403aIgg_assessment_Elr_dt
            : "";
        updatedArray[id].Q403aIgg_assessment_Elr_RdHard = false;
        updatedArray[id].Q403aIgg_assessment_Elr_dtHard = false;
        updatedArray[id].Q403aIgg_assessment_Elr_dtInvalid = false;
        updatedArray[id].Q403aIgg_assessment_Elr_dt_before_eof = false;
        updatedArray[id].Q403aIgg_level_Elr_MgValue = "";
        updatedArray[id].Q403aIgg_level_Elr_GValue = "";
        updatedArray[id].Q403aIgg_level_Elr = null;

        if (Number(value) === 1) {
          updatedArray[id].Q403aIgg_level_Elr = null;
          updatedArray[id].Q403aIgg_level_Elr_MgValue = "";
          updatedArray[id].Q403aIgg_level_Elr_GValue = "";
          updatedArray[id].Q403aIgg_level_Elr_Empty = false;
          updatedArray[id].Q403aIgg_level_Elr_RangeInvalid = false;
        }

        setQ403aElrDetails(updatedArray);
        break;
      }

      case "Q403aIgg_level_Elr" + id: {
        let updatedArray = [...Q403aElrDetails];

        updatedArray[id].Q403aIgg_level_Elr = Number(value);
        updatedArray[id].Q403aIgg_level_Elr_MgValue = "";
        updatedArray[id].Q403aIgg_level_Elr_GValue = "";
        updatedArray[id].Q403aIgg_level_Elr_Empty = false;
        updatedArray[id].Q403aIgg_level_Elr_RangeInvalid = false;

        setQ403aElrDetails(updatedArray);
        break;
      }

      case "Q403aIgg_level_Elr_MgValue" + id: {
        let updatedArray = [...Q403aElrDetails];

        updatedArray[id].Q403aIgg_level_Elr = 1;
        updatedArray[id].Q403aIgg_level_Elr_MgValue = value;
        updatedArray[id].Q403aIgg_level_Elr_GValue = "";
        updatedArray[id].Q403aIgg_level_Elr_Empty = false;
        updatedArray[id].Q403aIgg_level_Elr_RangeInvalid = false;

        setQ403aElrDetails(updatedArray);
        break;
      }

      case "Q403aIgg_level_Elr_GValue" + id: {
        let updatedArray = [...Q403aElrDetails];

        updatedArray[id].Q403aIgg_level_Elr = 2;
        updatedArray[id].Q403aIgg_level_Elr_GValue = value;
        updatedArray[id].Q403aIgg_level_Elr_MgValue = "";
        updatedArray[id].Q403aIgg_level_Elr_Empty = false;
        updatedArray[id].Q403aIgg_level_Elr_RangeInvalid = false;

        setQ403aElrDetails(updatedArray);
        break;
      }

      //Linvo
      case "Q403aIgg_assessment_Lin" + id: {
        let updatedArray = [...Q403aLinvoDetails];

        updatedArray[id].Q403aIgg_assessment_Lin = Number(value);
        updatedArray[id].Q403aIgg_assessment_Lin_dt =
          Number(value) === 2
            ? updatedArray[id].Q403aIgg_assessment_Lin_dt
            : "";
        updatedArray[id].Q403aIgg_assessment_Lin_RdHard = false;
        updatedArray[id].Q403aIgg_assessment_Lin_dtHard = false;
        updatedArray[id].Q403aIgg_assessment_Lin_dtInvalid = false;
        updatedArray[id].Q403aIgg_assessment_Lin_dt_before_eof = false;

        if (Number(value) === 1) {
          updatedArray[id].Q403aIgg_level_Lin = null;
          updatedArray[id].Q403aIgg_level_Lin_MgValue = "";
          updatedArray[id].Q403aIgg_level_Lin_GValue = "";
          updatedArray[id].Q403aIgg_level_Lin_Empty = false;
          updatedArray[id].Q403aIgg_level_Lin_RangeInvalid = false;
        }

        setQ403aLinvoDetails(updatedArray);
        break;
      }

      case "Q403aIgg_level_Lin" + id: {
        let updatedArray = [...Q403aLinvoDetails];

        updatedArray[id].Q403aIgg_level_Lin = Number(value);
        updatedArray[id].Q403aIgg_level_Lin_MgValue = "";
        updatedArray[id].Q403aIgg_level_Lin_GValue = "";
        updatedArray[id].Q403aIgg_level_Lin_Empty = false;
        updatedArray[id].Q403aIgg_level_Lin_RangeInvalid = false;

        setQ403aLinvoDetails(updatedArray);
        break;
      }

      case "Q403aIgg_level_Lin_MgValue" + id: {
        let updatedArray = [...Q403aLinvoDetails];

        updatedArray[id].Q403aIgg_level_Lin = 1;
        updatedArray[id].Q403aIgg_level_Lin_MgValue = value;
        updatedArray[id].Q403aIgg_level_Lin_GValue = "";
        updatedArray[id].Q403aIgg_level_Lin_Empty = false;
        updatedArray[id].Q403aIgg_level_Lin_RangeInvalid = false;

        setQ403aLinvoDetails(updatedArray);
        break;
      }

      case "Q403aIgg_level_Lin_GValue" + id: {
        let updatedArray = [...Q403aLinvoDetails];

        updatedArray[id].Q403aIgg_level_Lin = 2;
        updatedArray[id].Q403aIgg_level_Lin_GValue = value;
        updatedArray[id].Q403aIgg_level_Lin_MgValue = "";
        updatedArray[id].Q403aIgg_level_Lin_Empty = false;
        updatedArray[id].Q403aIgg_level_Lin_RangeInvalid = false;

        setQ403aLinvoDetails(updatedArray);
        break;
      }
      default:
        break;
    }
    //
  };

  const AddQ382AElrexRow = () => {
    setQ382AElrexDetails((prev) => {
      // Get template reasonList from first row if exists
      const templateReasonList =
        prev.length > 0 && prev[0].reasonList?.length > 0
          ? prev[0].reasonList.map((reason) => ({
              ...reason,
              isSelected: false,
              reasonText: "",
              aeSubReasons: reason.aeSubReasons?.map((ae) => ({
                ...ae,
                isSelected: false,
              })),
            }))
          : [];

      return [
        ...prev,
        {
          ...Q382AElrexRow,
          cohorttreatmentid: isElrexvayliSelected
            ? RRMMCohortTreatment.ELREXFIO
            : 0,
          reasonList: templateReasonList,
          neurologicAEReasonList:
            prev.length > 0 && prev[0].neurologicAEReasonList
              ? resetNeurologicReasons(prev[0].neurologicAEReasonList)
              : [],
        },
      ];
    });
  };

  const AddQ382ALinvoRow = () => {
    setQ382ALinvoDetails((prev) => {
      // Get template reasonList from first row if exists
      const templateReasonList =
        prev.length > 0 && prev[0].reasonList?.length > 0
          ? prev[0].reasonList.map((reason) => ({
              ...reason,
              isSelected: false,
              reasonText: "",
              aeSubReasons: reason.aeSubReasons?.map((ae) => ({
                ...ae,
                isSelected: false,
              })),
            }))
          : [];

      return [
        ...prev,
        {
          ...Q382ALinvoRow,
          cohorttreatmentid: isLinvoveySelected
            ? RRMMCohortTreatment.Linvoseltamab
            : 0,
          reasonList: templateReasonList,
          neurologicAEReasonList:
            prev.length > 0 && prev[0].neurologicAEReasonList
              ? resetNeurologicReasons(prev[0].neurologicAEReasonList)
              : [],
        },
      ];
    });
  };

  // Delete ELREXFIO Row
  const DeleteQ382AElrexRow = (id) => {
    const newArray = [...Q382AElrexDetails];
    newArray.splice(id, 1);
    setQ382AElrexDetails(newArray);
  };

  // Delete LINVOSELTAMAB Row
  const DeleteQ382ALinvoRow = (id) => {
    const newArray = [...Q382ALinvoDetails];
    newArray.splice(id, 1);
    setQ382ALinvoDetails(newArray);
  };

  function isValidNumber(value) {
    // Convert value to a number
    const number = parseFloat(value);

    // Regular expression to match numbers with up to two decimal places
    const decimalRegex = /^\d{1,2}(\.\d{1,2})?$|^50(\.0{1,2})?$/;

    // Validate that the number is within the range and matches the decimal format
    return (
      !isNaN(number) && number >= 0 && number <= 50 && decimalRegex.test(value)
    );
  }

  const HandleFieldChange = (e) => {
    const { name, id, value, checked } = e.target;

    switch (name) {
      // Q37 retired - all Q37 field-change handler cases commented out below
      /*
      case "Q37ElrexvayliRd" + id: {
        let updatedArray = [...Q37ElrexDetails];
        // let updatedElrArray = [...Q38ElrexDetails]

        updatedArray[id].Q37ElrexvayliRd = Number(value);
        updatedArray[id].q37ElrexvayliEmpty = false;

        updatedArray[id].showDateRedFreqElr =
          Number(value) === 1 ? true : false;

        // setQ38ElrexDetails(updatedElrArray);

        setQ38ElrexDetails([
          {
            ...Q38ElrexRow,
          },
        ]);
        setQ37ElrexDetails(updatedArray);
        break;
      }

      case "Q37LinvoveyRd" + id: {
        let updatedArray = [...Q37LinvoDetails];

        updatedArray[id].Q37LinvoveyRd = Number(value);
        updatedArray[id].q37LinvoveyEmpty = false;

        updatedArray[id].showDateRedFreqLinvo =
          Number(value) === 1 ? true : false;
        //  updatedArray[id].dose_administration_elrHard = false;

        setQ38LinvoDetails([
          {
            ...Q38LinvoRow,
          },
        ]);

        setQ37LinvoDetails(updatedArray);
        break;
      }

      case "q37_Elr_Dose_week_frquencyRd" + id: {
        let updatedArray = [...Q37ElrexDetails];

        updatedArray[id].q37_Elr_Dose_week_frquencyRd = Number(value);
        updatedArray[id].q37_Elr_Dose_week_frquency_txt = "";
        updatedArray[id].q37_Elr_Dose_week_frquencyRd_Empty = false;
        updatedArray[id].q37_Elr_Dose_week_frquency_txt_Empty = false;
        updatedArray[id].q37_Elr_Dose_week_freq_range = false;
        updatedArray[id].q37SubsequentDoseConflict = false;

        if (Number(value) === 1) {
          updatedArray[id].Q37ElrexvayliRd = null;
          updatedArray[id].q37ElrexvayliEmpty = false;
          updatedArray[id].showDateRedFreqElr = false;

          updatedArray[id].q37ElrDosageRd = null;
          updatedArray[id].q37ElrDosageOtherTxt = "";
          updatedArray[id].q37ElrDosageRdEmpty = false;
          updatedArray[id].q37ElrDosageOtherTxtEmpty = false;
          setQ38ElrexDetails([
            {
              ...Q38ElrexRow,
            },
          ]);
        }
        setQ37ElrexDetails(updatedArray);
        break;
      }

      case "q37_Elr_Dose_week_frquency_txt" + id: {
        let updatedArray = [...Q37ElrexDetails];

        const reqValue = value;
        updatedArray[id].q37_Elr_Dose_week_frquency_txt = reqValue;
        updatedArray[id].q37_Elr_Dose_week_frquencyRd = 2;
        updatedArray[id].q37_Elr_Dose_week_frquencyRd_Empty = false;
        updatedArray[id].q37_Elr_Dose_week_frquency_txt_Empty = false;
        updatedArray[id].q37_Elr_Dose_week_freq_range = false;
        updatedArray[id].q37SubsequentDoseConflict = false;

        setQ37ElrexDetails(updatedArray);
        break;
      }

      case "q37_Linvo_Dose_week_frquencyRd" + id: {
        let updatedArray = [...Q37LinvoDetails];

        updatedArray[id].q37_Linvo_Dose_week_frquencyRd = Number(value);
        updatedArray[id].q37_Linvo_Dose_week_frquency_txt = "";
        updatedArray[id].q37_Linvo_Dose_week_frquencyRd_Empty = false;
        updatedArray[id].q37_Linvo_Dose_week_frquency_txt_Empty = false;
        updatedArray[id].q37_Linvo_Dose_week_freq_range = false;
        updatedArray[id].q37LinvoSubsequentDoseConflict = false;

        if (Number(value) === 1) {
          updatedArray[id].Q37LinvoveyRd = null;
          updatedArray[id].q37LinvoveyEmpty = false;
          updatedArray[id].showDateRedFreqLinvo = false;
          setQ38LinvoDetails([
            {
              ...Q38LinvoRow,
            },
          ]);
        }

        if (Number(value) == 1) {
          updatedArray[id].q37_Linvo_Dosage_Rd = null;
          updatedArray[id].q37_Linvo_Dosage_OtherTxt = "";
          updatedArray[id].q37_Linvo_Dosage_Rd_Empty = false;
          updatedArray[id].q37_Linvo_Dosage_OtherTxt_Empty = false;
        }
        setQ37LinvoDetails(updatedArray);
        break;
      }

      case "q37_Linvo_Dose_week_frquency_txt" + id: {
        let updatedArray = [...Q37LinvoDetails];

        const reqValue = value;

        updatedArray[id].q37_Linvo_Dose_week_frquency_txt = reqValue;
        updatedArray[id].q37_Linvo_Dose_week_frquencyRd = 2;
        updatedArray[id].q37_Linvo_Dose_week_frquencyRd_Empty = false;
        updatedArray[id].q37_Linvo_Dose_week_frquency_txt_Empty = false;
        updatedArray[id].q37_Linvo_Dose_week_freq_range = false;
        updatedArray[id].q37LinvoSubsequentDoseConflict = false;

        setQ37LinvoDetails(updatedArray);
        break;
      }

      case "q37_Linvo_Dosage_Rd" + id: {
        let updatedArray = [...Q37LinvoDetails];

        updatedArray[id].q37_Linvo_Dosage_Rd = Number(value);
        updatedArray[id].q37_Linvo_Dosage_OtherTxt = "";
        updatedArray[id].q37_Linvo_Dosage_Rd_Empty = false;
        updatedArray[id].q37_Linvo_Dosage_OtherTxt_Empty = false;

        setQ37LinvoDetails(updatedArray);
        break;
      }

      case "q37_Linvo_Dosage_OtherTxt" + id: {
        let updatedArray = [...Q37LinvoDetails];

        const reqValue = value;
        updatedArray[id].q37_Linvo_Dosage_Rd = 2;
        updatedArray[id].q37_Linvo_Dosage_OtherTxt = reqValue;
        updatedArray[id].q37_Linvo_Dosage_Rd_Empty = false;
        updatedArray[id].q37_Linvo_Dosage_OtherTxt_Empty = false;

        setQ37LinvoDetails(updatedArray);
        break;
      }
      */

      //40

      case "Q40dosingfreqRd" + id: {
        let updatedArray = [...Q37ElrexDetails];
        updatedArray[id].Q40dosingfreqRd = Number(value);
        updatedArray[id].dosing_freq_oth_txt = "";

        updatedArray[id].Q40DosingFreqEmptyHard = false;
        updatedArray[id].Q40DosingFreqTxtEmptyHard = false;
        //Reset col C,D,E if 1
        if (Number(value) === 1) {
          updatedArray[id].Q40IgILevelRd = null;
          updatedArray[id].Q40IgGEmptyHard = false;

          updatedArray[id].Q40init_dt_Rd = null;
          updatedArray[id].Q40Init_RdHard = false;
          updatedArray[id].Q40init_dt = "";
          updatedArray[id].Q40Init_dtHard = false;
          updatedArray[id].InvalidQ40init_dt = false;
          updatedArray[id].init_dt_before_eof = false;
          updatedArray[id].init_dt_full_chain_invalid = false;

          updatedArray[id].primaryProf = false;
          updatedArray[id].secondaryProf = false;
          updatedArray[id].otherReson = false;
          updatedArray[id].Q40ReasonEmptyHard = false;

          // Cascade to Q40.C: "Patient never received IVIG during treatment"
          updatedArray[id].ivigInitRd_elrC = 1;
          updatedArray[id].ivigInitRdHard_elrC = false;
          updatedArray[id].ivigInitDtHard_elrC = false;
          updatedArray[id].ivigInitDtInvalid_elrC = false;
          updatedArray[id].ivigInitDtBeforeEof_elrC = false;
          updatedArray[id].ivigInitDtFullChainInvalid_elrC = false;
          updatedArray[id].ivigInitDt_elrC = "";

          updatedArray[id].ivigDosingType_elrC = null;
          updatedArray[id].ivigDosingTypeEmptyHard_elrC = false;

          updatedArray[id].ivigDosingFreqRd_elrC = null;
          updatedArray[id].ivigDosingFreqTimesTxt_elrC = "";
          updatedArray[id].ivigDosingFreqWeeksTxt_elrC = "";
          updatedArray[id].ivigDosingFreqOtherTxt_elrC = "";
          updatedArray[id].ivigDosingFreqEmptyHard_elrC = false;
          updatedArray[id].ivigDosingFreqTxtEmptyHard_elrC = false;
          updatedArray[id].ivigDosingFreqRangeInvalid_elrC = false;

          updatedArray[id].ivigReasonPrimary_elrC = false;
          updatedArray[id].ivigReasonSecondary_elrC = false;
          updatedArray[id].ivigReasonOther_elrC = false;
          updatedArray[id].ivigReasonOtherTxt_elrC = "";
          updatedArray[id].ivigReasonUnk_elrC = false;
          updatedArray[id].ivigReasonEmptyHard_elrC = false;
          updatedArray[id].ivigReasonOtherTxtEmptyHard_elrC = false;

          updatedArray[id].ivigDoseStrengthRd_elrC = null;
          updatedArray[id].ivigDoseStrengthGTxt_elrC = "";
          updatedArray[id].ivigDoseStrengthMgKgTxt_elrC = "";
          updatedArray[id].ivigDoseStrengthOtherTxt_elrC = "";
          updatedArray[id].ivigDoseStrengthEmptyHard_elrC = false;
          updatedArray[id].ivigDoseStrengthRangeInvalid_elrC = false;
          updatedArray[id].ivigDoseStrengthMgKgRangeInvalid_elrC = false;
          updatedArray[id].ivigDoseStrengthOtherTxtEmptyHard_elrC = false;

          updatedArray[id].ivigLastIgg_elrC = null;
          updatedArray[id].ivigLastIggEmptyHard_elrC = false;

          updatedArray[id].ivigBodyWeightRd_elrC = null;
          updatedArray[id].ivigBodyWeightKgTxt_elrC = "";
          updatedArray[id].ivigBodyWeightLbsTxt_elrC = "";
          updatedArray[id].ivigBodyWeightEmptyHard_elrC = false;
          updatedArray[id].ivigBodyWeightRangeInvalid_elrC = false;

          setQ401aElrDetails([{ ...Q401aElrRow }]);
          setQ403aElrDetails([{ ...Q403aElrRow }]);
        }
        setQ37ElrexDetails(updatedArray);
        break;
      }

      case "Q40IgILevelRd" + id: {
        let updatedArray = [...Q37ElrexDetails];

        updatedArray[id].Q40IgILevelRd = Number(value);

        updatedArray[id].Q40IgGEmptyHard = false;
        setQ37ElrexDetails(updatedArray);
        break;
      }

      case "Q40init_dt_Rd" + id: {
        let updatedArray = [...Q37ElrexDetails];

        updatedArray[id].Q40init_dt_Rd = Number(value);

        updatedArray[id].Q40Init_RdHard = false;
        updatedArray[id].Q40Init_dtHard = false;
        updatedArray[id].InvalidQ40init_dt = false;
        updatedArray[id].init_dt_before_eof = false;
        updatedArray[id].init_dt_full_chain_invalid = false;
        updatedArray[id].Q40init_dt = "";
        setQ37ElrexDetails(updatedArray);
        break;
      }

      //39Elr

      case "tocilizumab_elr" + id: {
        let updatedArray = [...Q37ElrexDetails];
        const isSelected = !updatedArray[id].tocilizumab_elr;
        updatedArray[id].tocilizumab_elr = isSelected;

        updatedArray[id].noneoftheabove_elr = null;

        updatedArray[id].Q39elrReasonHard = false;
        updatedArray[id].crs_elr = false;
        updatedArray[id].neurotoxicity_elr = false;
        updatedArray[id].tmt_neurotoxicity_elr = false;
        updatedArray[id].tmt_elr_unknown = false;
        updatedArray[id].cytokin_elr = false;
        updatedArray[id].secondary_prophylactic_crs_elr = false;
        updatedArray[id].secondary_neurotoxicity_elr = false;
        updatedArray[id].Q39ElrEmptyHard = false;

        //39.1.A
        if (isNewEcrfA) {
          handleQ391AList(
            setQ391AElrTocilzumabList,
            q391AElrRow,
            pid,
            isSelected,
          );
        }

        setQ37ElrexDetails(updatedArray);
        break;
      }

      case "Granulocyte" + id: {
        let updatedArray = [...Q37ElrexDetails];
        updatedArray[id].noneoftheabove_elr = null;
        updatedArray[id].Granulocyte = !updatedArray[id].Granulocyte;

        updatedArray[id].Q39ElrEmptyHard = false;
        updatedArray[id].prophylactic_use_elr = false;
        updatedArray[id].secondary_prophylactic_use_elr = false;
        updatedArray[id].oth_use_elr = false;
        updatedArray[id].unknown_elr = null;

        setQ37ElrexDetails(updatedArray);
        break;
      }

      case "SteroidsElr" + id: {
        let updatedArray = [...Q37ElrexDetails];

        updatedArray[id].SteroidsElr = !updatedArray[id].SteroidsElr;
        updatedArray[id].noneoftheabove_elr = null;

        updatedArray[id].steroid_pre_tmt_elr = false;
        updatedArray[id].cytokin_elr_steroids = false;
        updatedArray[id].secondary_prophylactic_elr_steroids = false;
        updatedArray[id].secondary_neurotoxicity_elr_steroids = false;
        updatedArray[id].crs_elr_steroids = false;
        updatedArray[id].neurotoxicity_elr_steroids = false;
        updatedArray[id].tmt_neurotoxicity_elr_steroids = false;
        updatedArray[id].tmt_steroids_elr_unknown = false;

        updatedArray[id].Q39elrSteroidsEmptyHard = false;
        updatedArray[id].Q39ElrEmptyHard = false;

        updatedArray[id].dose_name_elr_unknown = false;
        updatedArray[id].betamethasone_elr = false;
        updatedArray[id].betamethasone_elrNum = "";

        updatedArray[id].dexamethasone_elr = false;
        updatedArray[id].dexamethasone_elrNum = "";

        updatedArray[id].hydrocortisone_elr = false;
        updatedArray[id].hydrocortisone_elrNum = "";

        updatedArray[id].methylprednisolone = false;
        updatedArray[id].methylprednisoloneNum = "";

        updatedArray[id].prednisolone_elr = false;
        updatedArray[id].prednisolone_elrNum = "";

        updatedArray[id].prednisone_elr = false;
        updatedArray[id].prednisone_elrNum = "";

        updatedArray[id].triamcinolone_elr = false;
        updatedArray[id].triamcinolone_elrNum = "";

        setQ37ElrexDetails(updatedArray);
        break;
      }

      case "noneoftheabove_elr" + id: {
        let updatedArray = [...Q37ElrexDetails];

        const isSelected = Number(value);
        updatedArray[id].noneoftheabove_elr = isSelected;
        updatedArray[id].tocilizumab_elr = false;
        updatedArray[id].Granulocyte = false;

        updatedArray[id].Q39elrReasonHard = false;
        updatedArray[id].Q39elrGranduloEmptyHard = false;
        updatedArray[id].Q39ElrEmptyHard = false;
        updatedArray[id].SteroidsElr = false;

        // Q40 is independent of the Q39 answer and stays visible/required
        // regardless of "None of the above" - do not clear its fields here.

        //39.1.A
        if (isNewEcrfA) {
          handleQ391AList(
            setQ391AElrTocilzumabList,
            q391AElrRow,
            pid,
            isSelected,
          );
        }

        setQ37ElrexDetails(updatedArray);
        break;
      }

      //39.C Elrexfio
      case "tocilizumab_elrC" + id: {
        let updatedArray = [...Q37ElrexDetails];
        const isSelected = !updatedArray[id].tocilizumab_elrC;
        updatedArray[id].tocilizumab_elrC = isSelected;
        updatedArray[id].noneoftheabove_elrC = null;
        updatedArray[id].Q39CEmptyHard = false;
        if (!isSelected) {
          updatedArray[id] = {
            ...updatedArray[id],
            tociliAdmsDateRD_elrC: null,
            tociliAdmsDate_elrC: "",
            tociliAdmsDateInvalid_elrC: false,
            tociliAdmsDateRangeInvalid_elrC: false,
            tociliAdmsDateEmpty_elrC: false,
            tociliDoseRd_elrC: null,
            tociliDoseMgkg_elrC: "",
            tociliDoseOther_elrC: "",
            tociliDoseEmptyHard_elrC: false,
            tociliDoseMgkgEmptyHard_elrC: false,
            tociliDoseOtherEmptyHard_elrC: false,
            tociliReasonPretratment_elrC: false,
            tociliReasonPrimarycrs_elrC: false,
            tociliReasonSecondarycrs_elrC: false,
            tociliReasonSupportivecrs_elrC: false,
            tociliReasonPrimaryneuro_elrC: false,
            tociliReasonSecondaryneuro_elrC: false,
            tociliReasonSupportiveneuro_elrC: false,
            tociliReasonUnk_elrC: false,
            Q391CReasonEmptyHard: false,
          };
        }
        setQ37ElrexDetails(updatedArray);
        break;
      }

      case "steroids_elrC" + id: {
        let updatedArray = [...Q37ElrexDetails];
        const isSteroidsSelected = !updatedArray[id].steroids_elrC;
        updatedArray[id].steroids_elrC = isSteroidsSelected;
        updatedArray[id].noneoftheabove_elrC = null;
        updatedArray[id].Q39CEmptyHard = false;
        if (!isSteroidsSelected) {
          updatedArray[id] = {
            ...updatedArray[id],
            betamethasone_elrC: false,
            betamethasone_elrCNum: "",
            dexamethasone_elrC: false,
            dexamethasone_elrCNum: "",
            hydrocortisone_elrC: false,
            hydrocortisone_elrCNum: "",
            methylprednisolone_elrC: false,
            methylprednisolone_elrCNum: "",
            prednisolone_elrC: false,
            prednisolone_elrCNum: "",
            prednisone_elrC: false,
            prednisone_elrCNum: "",
            triamcinolone_elrC: false,
            triamcinolone_elrCNum: "",
            dose_name_elrC_unknown: false,
            name_dose_steroidsHardEmpty_elrC: false,
            steroids_elrC_txtEmpty: false,
            steroidsDoseRangeInvalid_elrC: false,
            steroidReasonPretratment_elrC: false,
            steroidReasonPrimarycrs_elrC: false,
            steroidReasonSecondarycrs_elrC: false,
            steroidReasonSupportivecrs_elrC: false,
            steroidReasonPrimaryneuro_elrC: false,
            steroidReasonSecondaryneuro_elrC: false,
            steroidReasonSupportiveneuro_elrC: false,
            steroidReasonUnk_elrC: false,
            Q392CReasonEmptyHard: false,
          };
        }
        setQ37ElrexDetails(updatedArray);
        break;
      }

      case "granulocyte_elrC" + id: {
        let updatedArray = [...Q37ElrexDetails];
        const isGranuloSelected = !updatedArray[id].granulocyte_elrC;
        updatedArray[id].granulocyte_elrC = isGranuloSelected;
        updatedArray[id].noneoftheabove_elrC = null;
        updatedArray[id].Q39CEmptyHard = false;
        if (!isGranuloSelected) {
          updatedArray[id] = {
            ...updatedArray[id],
            prophylactic_use_elrC: false,
            secondary_prophylactic_use_elrC: false,
            oth_use_elrC: false,
            unknown_elrC: null,
            Q393CReasonEmptyHard: false,
          };
        }
        setQ37ElrexDetails(updatedArray);
        break;
      }

      case "noneoftheabove_elrC" + id: {
        let updatedArray = [...Q37ElrexDetails];
        updatedArray[id] = {
          ...updatedArray[id],
          noneoftheabove_elrC: Number(value),
          tocilizumab_elrC: false,
          steroids_elrC: false,
          granulocyte_elrC: false,
          Q39CEmptyHard: false,
          tociliAdmsDateRD_elrC: null,
          tociliAdmsDate_elrC: "",
          tociliAdmsDateInvalid_elrC: false,
          tociliAdmsDateRangeInvalid_elrC: false,
          tociliAdmsDateEmpty_elrC: false,
          tociliDoseRd_elrC: null,
          tociliDoseMgkg_elrC: "",
          tociliDoseOther_elrC: "",
          tociliDoseEmptyHard_elrC: false,
          tociliDoseMgkgEmptyHard_elrC: false,
          tociliDoseOtherEmptyHard_elrC: false,
          tociliReasonPretratment_elrC: false,
          tociliReasonPrimarycrs_elrC: false,
          tociliReasonSecondarycrs_elrC: false,
          tociliReasonSupportivecrs_elrC: false,
          tociliReasonPrimaryneuro_elrC: false,
          tociliReasonSecondaryneuro_elrC: false,
          tociliReasonSupportiveneuro_elrC: false,
          tociliReasonUnk_elrC: false,
          Q391CReasonEmptyHard: false,
          betamethasone_elrC: false,
          betamethasone_elrCNum: "",
          dexamethasone_elrC: false,
          dexamethasone_elrCNum: "",
          hydrocortisone_elrC: false,
          hydrocortisone_elrCNum: "",
          methylprednisolone_elrC: false,
          methylprednisolone_elrCNum: "",
          prednisolone_elrC: false,
          prednisolone_elrCNum: "",
          prednisone_elrC: false,
          prednisone_elrCNum: "",
          triamcinolone_elrC: false,
          triamcinolone_elrCNum: "",
          dose_name_elrC_unknown: false,
          name_dose_steroidsHardEmpty_elrC: false,
          steroids_elrC_txtEmpty: false,
          steroidsDoseRangeInvalid_elrC: false,
          steroidReasonPretratment_elrC: false,
          steroidReasonPrimarycrs_elrC: false,
          steroidReasonSecondarycrs_elrC: false,
          steroidReasonSupportivecrs_elrC: false,
          steroidReasonPrimaryneuro_elrC: false,
          steroidReasonSecondaryneuro_elrC: false,
          steroidReasonSupportiveneuro_elrC: false,
          steroidReasonUnk_elrC: false,
          Q392CReasonEmptyHard: false,
          prophylactic_use_elrC: false,
          secondary_prophylactic_use_elrC: false,
          oth_use_elrC: false,
          unknown_elrC: null,
          Q393CReasonEmptyHard: false,

          // Q40/Q40.C is independent of the Q39.C answer and stays
          // visible/required regardless of "None of the above" - do not
          // clear its fields here.
        };
        setQ37ElrexDetails(updatedArray);
        break;
      }

      case "tociliAdmsDateRD_elrC" + id: {
        let updatedArray = [...Q37ElrexDetails];
        updatedArray[id].tociliAdmsDateRD_elrC = Number(value);
        updatedArray[id].tociliAdmsDateEmpty_elrC = false;
        updatedArray[id].tociliAdmsDateInvalid_elrC = false;
        updatedArray[id].tociliAdmsDateRangeInvalid_elrC = false;
        if (Number(value) === 99) {
          updatedArray[id].tociliAdmsDate_elrC = "";
        }
        setQ37ElrexDetails(updatedArray);
        break;
      }

      case "tociliDoseRd_elrC" + id: {
        let updatedArray = [...Q37ElrexDetails];
        const rd = Number(value);
        updatedArray[id].tociliDoseRd_elrC = rd;
        updatedArray[id].tociliDoseEmptyHard_elrC = false;
        updatedArray[id].tociliDoseMgkgEmptyHard_elrC = false;
        updatedArray[id].tociliDoseOtherEmptyHard_elrC = false;
        if (rd !== 1) updatedArray[id].tociliDoseMgkg_elrC = "";
        if (rd !== 2) updatedArray[id].tociliDoseOther_elrC = "";
        setQ37ElrexDetails(updatedArray);
        break;
      }

      case "tociliDoseMgkg_elrC" + id: {
        let updatedArray = [...Q37ElrexDetails];
        updatedArray[id].tociliDoseMgkg_elrC = value;
        updatedArray[id].tociliDoseRd_elrC = 1;
        updatedArray[id].tociliDoseOther_elrC = "";
        updatedArray[id].tociliDoseEmptyHard_elrC = false;
        updatedArray[id].tociliDoseMgkgEmptyHard_elrC = false;
        updatedArray[id].tociliDoseOtherEmptyHard_elrC = false;
        setQ37ElrexDetails(updatedArray);
        break;
      }

      case "tociliDoseOther_elrC" + id: {
        let updatedArray = [...Q37ElrexDetails];
        updatedArray[id].tociliDoseOther_elrC = value;
        updatedArray[id].tociliDoseRd_elrC = 2;
        updatedArray[id].tociliDoseMgkg_elrC = "";
        updatedArray[id].tociliDoseEmptyHard_elrC = false;
        updatedArray[id].tociliDoseMgkgEmptyHard_elrC = false;
        updatedArray[id].tociliDoseOtherEmptyHard_elrC = false;
        setQ37ElrexDetails(updatedArray);
        break;
      }

      case "tociliDoseOtherToggle_elrC" + id: {
        let updatedArray = [...Q37ElrexDetails];
        updatedArray[id].tociliDoseRd_elrC = checked ? 2 : null;
        updatedArray[id].tociliDoseEmptyHard_elrC = false;
        updatedArray[id].tociliDoseMgkgEmptyHard_elrC = false;
        updatedArray[id].tociliDoseOtherEmptyHard_elrC = false;
        if (checked) {
          updatedArray[id].tociliDoseMgkg_elrC = "";
        } else {
          updatedArray[id].tociliDoseOther_elrC = "";
        }
        setQ37ElrexDetails(updatedArray);
        break;
      }

      case "tociliReasonPretratment_elrC" + id: {
        let updatedArray = [...Q37ElrexDetails];
        updatedArray[id].tociliReasonPretratment_elrC =
          !updatedArray[id].tociliReasonPretratment_elrC;
        updatedArray[id].tociliReasonUnk_elrC = false;
        updatedArray[id].Q391CReasonEmptyHard = false;
        setQ37ElrexDetails(updatedArray);
        break;
      }

      case "tociliReasonPrimarycrs_elrC" + id: {
        let updatedArray = [...Q37ElrexDetails];
        updatedArray[id].tociliReasonPrimarycrs_elrC =
          !updatedArray[id].tociliReasonPrimarycrs_elrC;
        updatedArray[id].tociliReasonUnk_elrC = false;
        updatedArray[id].Q391CReasonEmptyHard = false;
        setQ37ElrexDetails(updatedArray);
        break;
      }

      case "tociliReasonSecondarycrs_elrC" + id: {
        let updatedArray = [...Q37ElrexDetails];
        updatedArray[id].tociliReasonSecondarycrs_elrC =
          !updatedArray[id].tociliReasonSecondarycrs_elrC;
        updatedArray[id].tociliReasonUnk_elrC = false;
        updatedArray[id].Q391CReasonEmptyHard = false;
        setQ37ElrexDetails(updatedArray);
        break;
      }

      case "tociliReasonSupportivecrs_elrC" + id: {
        let updatedArray = [...Q37ElrexDetails];
        updatedArray[id].tociliReasonSupportivecrs_elrC =
          !updatedArray[id].tociliReasonSupportivecrs_elrC;
        updatedArray[id].tociliReasonUnk_elrC = false;
        updatedArray[id].Q391CReasonEmptyHard = false;
        setQ37ElrexDetails(updatedArray);
        break;
      }

      case "tociliReasonPrimaryneuro_elrC" + id: {
        let updatedArray = [...Q37ElrexDetails];
        updatedArray[id].tociliReasonPrimaryneuro_elrC =
          !updatedArray[id].tociliReasonPrimaryneuro_elrC;
        updatedArray[id].tociliReasonUnk_elrC = false;
        updatedArray[id].Q391CReasonEmptyHard = false;
        setQ37ElrexDetails(updatedArray);
        break;
      }

      case "tociliReasonSecondaryneuro_elrC" + id: {
        let updatedArray = [...Q37ElrexDetails];
        updatedArray[id].tociliReasonSecondaryneuro_elrC =
          !updatedArray[id].tociliReasonSecondaryneuro_elrC;
        updatedArray[id].tociliReasonUnk_elrC = false;
        updatedArray[id].Q391CReasonEmptyHard = false;
        setQ37ElrexDetails(updatedArray);
        break;
      }

      case "tociliReasonSupportiveneuro_elrC" + id: {
        let updatedArray = [...Q37ElrexDetails];
        updatedArray[id].tociliReasonSupportiveneuro_elrC =
          !updatedArray[id].tociliReasonSupportiveneuro_elrC;
        updatedArray[id].tociliReasonUnk_elrC = false;
        updatedArray[id].Q391CReasonEmptyHard = false;
        setQ37ElrexDetails(updatedArray);
        break;
      }

      case "tociliReasonUnk_elrC" + id: {
        let updatedArray = [...Q37ElrexDetails];
        updatedArray[id] = {
          ...updatedArray[id],
          tociliReasonUnk_elrC: !updatedArray[id].tociliReasonUnk_elrC,
          tociliReasonPretratment_elrC: false,
          tociliReasonPrimarycrs_elrC: false,
          tociliReasonSecondarycrs_elrC: false,
          tociliReasonSupportivecrs_elrC: false,
          tociliReasonPrimaryneuro_elrC: false,
          tociliReasonSecondaryneuro_elrC: false,
          tociliReasonSupportiveneuro_elrC: false,
          Q391CReasonEmptyHard: false,
        };
        setQ37ElrexDetails(updatedArray);
        break;
      }

      //39.2.C Elrexfio
      case "betamethasone_elrC" + id: {
        let updatedArray = [...Q37ElrexDetails];
        updatedArray[id].betamethasone_elrC =
          !updatedArray[id].betamethasone_elrC;
        updatedArray[id].betamethasone_elrCNum = "";
        updatedArray[id].name_dose_steroidsHardEmpty_elrC = false;
        updatedArray[id].steroids_elrC_txtEmpty = false;
        updatedArray[id].steroidsDoseRangeInvalid_elrC = false;
        updatedArray[id].dose_name_elrC_unknown = false;
        setQ37ElrexDetails(updatedArray);
        break;
      }

      case "betamethasone_elrCNum" + id: {
        let updatedArray = [...Q37ElrexDetails];
        updatedArray[id].betamethasone_elrCNum = value;
        updatedArray[id].betamethasone_elrC = true;
        updatedArray[id].name_dose_steroidsHardEmpty_elrC = false;
        updatedArray[id].steroids_elrC_txtEmpty = false;
        updatedArray[id].steroidsDoseRangeInvalid_elrC = false;
        updatedArray[id].dose_name_elrC_unknown = false;
        setQ37ElrexDetails(updatedArray);
        break;
      }

      case "dexamethasone_elrC" + id: {
        let updatedArray = [...Q37ElrexDetails];
        updatedArray[id].dexamethasone_elrC =
          !updatedArray[id].dexamethasone_elrC;
        updatedArray[id].dexamethasone_elrCNum = "";
        updatedArray[id].name_dose_steroidsHardEmpty_elrC = false;
        updatedArray[id].steroids_elrC_txtEmpty = false;
        updatedArray[id].steroidsDoseRangeInvalid_elrC = false;
        updatedArray[id].dose_name_elrC_unknown = false;
        setQ37ElrexDetails(updatedArray);
        break;
      }

      case "dexamethasone_elrCNum" + id: {
        let updatedArray = [...Q37ElrexDetails];
        updatedArray[id].dexamethasone_elrCNum = value;
        updatedArray[id].dexamethasone_elrC = true;
        updatedArray[id].name_dose_steroidsHardEmpty_elrC = false;
        updatedArray[id].steroids_elrC_txtEmpty = false;
        updatedArray[id].steroidsDoseRangeInvalid_elrC = false;
        updatedArray[id].dose_name_elrC_unknown = false;
        setQ37ElrexDetails(updatedArray);
        break;
      }

      case "hydrocortisone_elrC" + id: {
        let updatedArray = [...Q37ElrexDetails];
        updatedArray[id].hydrocortisone_elrC =
          !updatedArray[id].hydrocortisone_elrC;
        updatedArray[id].hydrocortisone_elrCNum = "";
        updatedArray[id].name_dose_steroidsHardEmpty_elrC = false;
        updatedArray[id].steroids_elrC_txtEmpty = false;
        updatedArray[id].steroidsDoseRangeInvalid_elrC = false;
        updatedArray[id].dose_name_elrC_unknown = false;
        setQ37ElrexDetails(updatedArray);
        break;
      }

      case "hydrocortisone_elrCNum" + id: {
        let updatedArray = [...Q37ElrexDetails];
        updatedArray[id].hydrocortisone_elrCNum = value;
        updatedArray[id].hydrocortisone_elrC = true;
        updatedArray[id].name_dose_steroidsHardEmpty_elrC = false;
        updatedArray[id].steroids_elrC_txtEmpty = false;
        updatedArray[id].steroidsDoseRangeInvalid_elrC = false;
        updatedArray[id].dose_name_elrC_unknown = false;
        setQ37ElrexDetails(updatedArray);
        break;
      }

      case "methylprednisolone_elrC" + id: {
        let updatedArray = [...Q37ElrexDetails];
        updatedArray[id].methylprednisolone_elrC =
          !updatedArray[id].methylprednisolone_elrC;
        updatedArray[id].methylprednisolone_elrCNum = "";
        updatedArray[id].name_dose_steroidsHardEmpty_elrC = false;
        updatedArray[id].steroids_elrC_txtEmpty = false;
        updatedArray[id].steroidsDoseRangeInvalid_elrC = false;
        updatedArray[id].dose_name_elrC_unknown = false;
        setQ37ElrexDetails(updatedArray);
        break;
      }

      case "methylprednisolone_elrCNum" + id: {
        let updatedArray = [...Q37ElrexDetails];
        updatedArray[id].methylprednisolone_elrCNum = value;
        updatedArray[id].methylprednisolone_elrC = true;
        updatedArray[id].name_dose_steroidsHardEmpty_elrC = false;
        updatedArray[id].steroids_elrC_txtEmpty = false;
        updatedArray[id].steroidsDoseRangeInvalid_elrC = false;
        updatedArray[id].dose_name_elrC_unknown = false;
        setQ37ElrexDetails(updatedArray);
        break;
      }

      case "prednisolone_elrC" + id: {
        let updatedArray = [...Q37ElrexDetails];
        updatedArray[id].prednisolone_elrC =
          !updatedArray[id].prednisolone_elrC;
        updatedArray[id].prednisolone_elrCNum = "";
        updatedArray[id].name_dose_steroidsHardEmpty_elrC = false;
        updatedArray[id].steroids_elrC_txtEmpty = false;
        updatedArray[id].steroidsDoseRangeInvalid_elrC = false;
        updatedArray[id].dose_name_elrC_unknown = false;
        setQ37ElrexDetails(updatedArray);
        break;
      }

      case "prednisolone_elrCNum" + id: {
        let updatedArray = [...Q37ElrexDetails];
        updatedArray[id].prednisolone_elrCNum = value;
        updatedArray[id].prednisolone_elrC = true;
        updatedArray[id].name_dose_steroidsHardEmpty_elrC = false;
        updatedArray[id].steroids_elrC_txtEmpty = false;
        updatedArray[id].steroidsDoseRangeInvalid_elrC = false;
        updatedArray[id].dose_name_elrC_unknown = false;
        setQ37ElrexDetails(updatedArray);
        break;
      }

      case "prednisone_elrC" + id: {
        let updatedArray = [...Q37ElrexDetails];
        updatedArray[id].prednisone_elrC = !updatedArray[id].prednisone_elrC;
        updatedArray[id].prednisone_elrCNum = "";
        updatedArray[id].name_dose_steroidsHardEmpty_elrC = false;
        updatedArray[id].steroids_elrC_txtEmpty = false;
        updatedArray[id].steroidsDoseRangeInvalid_elrC = false;
        updatedArray[id].dose_name_elrC_unknown = false;
        setQ37ElrexDetails(updatedArray);
        break;
      }

      case "prednisone_elrCNum" + id: {
        let updatedArray = [...Q37ElrexDetails];
        updatedArray[id].prednisone_elrCNum = value;
        updatedArray[id].prednisone_elrC = true;
        updatedArray[id].name_dose_steroidsHardEmpty_elrC = false;
        updatedArray[id].steroids_elrC_txtEmpty = false;
        updatedArray[id].steroidsDoseRangeInvalid_elrC = false;
        updatedArray[id].dose_name_elrC_unknown = false;
        setQ37ElrexDetails(updatedArray);
        break;
      }

      case "triamcinolone_elrC" + id: {
        let updatedArray = [...Q37ElrexDetails];
        updatedArray[id].triamcinolone_elrC =
          !updatedArray[id].triamcinolone_elrC;
        updatedArray[id].triamcinolone_elrCNum = "";
        updatedArray[id].name_dose_steroidsHardEmpty_elrC = false;
        updatedArray[id].steroids_elrC_txtEmpty = false;
        updatedArray[id].steroidsDoseRangeInvalid_elrC = false;
        updatedArray[id].dose_name_elrC_unknown = false;
        setQ37ElrexDetails(updatedArray);
        break;
      }

      case "triamcinolone_elrCNum" + id: {
        let updatedArray = [...Q37ElrexDetails];
        updatedArray[id].triamcinolone_elrCNum = value;
        updatedArray[id].triamcinolone_elrC = true;
        updatedArray[id].name_dose_steroidsHardEmpty_elrC = false;
        updatedArray[id].steroids_elrC_txtEmpty = false;
        updatedArray[id].steroidsDoseRangeInvalid_elrC = false;
        updatedArray[id].dose_name_elrC_unknown = false;
        setQ37ElrexDetails(updatedArray);
        break;
      }

      case "dose_name_elrC_unknown" + id: {
        let updatedArray = [...Q37ElrexDetails];
        updatedArray[id] = {
          ...updatedArray[id],
          dose_name_elrC_unknown: !updatedArray[id].dose_name_elrC_unknown,
          name_dose_steroidsHardEmpty_elrC: false,
          steroids_elrC_txtEmpty: false,
          steroidsDoseRangeInvalid_elrC: false,
          betamethasone_elrC: false,
          betamethasone_elrCNum: "",
          dexamethasone_elrC: false,
          dexamethasone_elrCNum: "",
          hydrocortisone_elrC: false,
          hydrocortisone_elrCNum: "",
          methylprednisolone_elrC: false,
          methylprednisolone_elrCNum: "",
          prednisolone_elrC: false,
          prednisolone_elrCNum: "",
          prednisone_elrC: false,
          prednisone_elrCNum: "",
          triamcinolone_elrC: false,
          triamcinolone_elrCNum: "",
        };
        setQ37ElrexDetails(updatedArray);
        break;
      }

      case "steroidReasonPretratment_elrC" + id: {
        let updatedArray = [...Q37ElrexDetails];
        updatedArray[id].steroidReasonPretratment_elrC =
          !updatedArray[id].steroidReasonPretratment_elrC;
        updatedArray[id].steroidReasonUnk_elrC = false;
        updatedArray[id].Q392CReasonEmptyHard = false;
        setQ37ElrexDetails(updatedArray);
        break;
      }

      case "steroidReasonPrimarycrs_elrC" + id: {
        let updatedArray = [...Q37ElrexDetails];
        updatedArray[id].steroidReasonPrimarycrs_elrC =
          !updatedArray[id].steroidReasonPrimarycrs_elrC;
        updatedArray[id].steroidReasonUnk_elrC = false;
        updatedArray[id].Q392CReasonEmptyHard = false;
        setQ37ElrexDetails(updatedArray);
        break;
      }

      case "steroidReasonSecondarycrs_elrC" + id: {
        let updatedArray = [...Q37ElrexDetails];
        updatedArray[id].steroidReasonSecondarycrs_elrC =
          !updatedArray[id].steroidReasonSecondarycrs_elrC;
        updatedArray[id].steroidReasonUnk_elrC = false;
        updatedArray[id].Q392CReasonEmptyHard = false;
        setQ37ElrexDetails(updatedArray);
        break;
      }

      case "steroidReasonSupportivecrs_elrC" + id: {
        let updatedArray = [...Q37ElrexDetails];
        updatedArray[id].steroidReasonSupportivecrs_elrC =
          !updatedArray[id].steroidReasonSupportivecrs_elrC;
        updatedArray[id].steroidReasonUnk_elrC = false;
        updatedArray[id].Q392CReasonEmptyHard = false;
        setQ37ElrexDetails(updatedArray);
        break;
      }

      case "steroidReasonPrimaryneuro_elrC" + id: {
        let updatedArray = [...Q37ElrexDetails];
        updatedArray[id].steroidReasonPrimaryneuro_elrC =
          !updatedArray[id].steroidReasonPrimaryneuro_elrC;
        updatedArray[id].steroidReasonUnk_elrC = false;
        updatedArray[id].Q392CReasonEmptyHard = false;
        setQ37ElrexDetails(updatedArray);
        break;
      }

      case "steroidReasonSecondaryneuro_elrC" + id: {
        let updatedArray = [...Q37ElrexDetails];
        updatedArray[id].steroidReasonSecondaryneuro_elrC =
          !updatedArray[id].steroidReasonSecondaryneuro_elrC;
        updatedArray[id].steroidReasonUnk_elrC = false;
        updatedArray[id].Q392CReasonEmptyHard = false;
        setQ37ElrexDetails(updatedArray);
        break;
      }

      case "steroidReasonSupportiveneuro_elrC" + id: {
        let updatedArray = [...Q37ElrexDetails];
        updatedArray[id].steroidReasonSupportiveneuro_elrC =
          !updatedArray[id].steroidReasonSupportiveneuro_elrC;
        updatedArray[id].steroidReasonUnk_elrC = false;
        updatedArray[id].Q392CReasonEmptyHard = false;
        setQ37ElrexDetails(updatedArray);
        break;
      }

      case "steroidReasonUnk_elrC" + id: {
        let updatedArray = [...Q37ElrexDetails];
        updatedArray[id] = {
          ...updatedArray[id],
          steroidReasonUnk_elrC: !updatedArray[id].steroidReasonUnk_elrC,
          steroidReasonPretratment_elrC: false,
          steroidReasonPrimarycrs_elrC: false,
          steroidReasonSecondarycrs_elrC: false,
          steroidReasonSupportivecrs_elrC: false,
          steroidReasonPrimaryneuro_elrC: false,
          steroidReasonSecondaryneuro_elrC: false,
          steroidReasonSupportiveneuro_elrC: false,
          Q392CReasonEmptyHard: false,
        };
        setQ37ElrexDetails(updatedArray);
        break;
      }

      //39Linvoseltamab

      case "tocilizumab_linvo" + id: {
        let updatedArray = [...Q37LinvoDetails];

        const isSelected = !updatedArray[id].tocilizumab_linvo;
        updatedArray[id].tocilizumab_linvo = isSelected;

        updatedArray[id].noneoftheabove_linvo = null;
        updatedArray[id].cytokin_linvo = false;
        updatedArray[id].secondary_prophylactic_crs_linvo = false;
        updatedArray[id].crs_linvo = false;
        updatedArray[id].neurotoxicity_linvo = false;
        updatedArray[id].secondary_neurotoxicity_linvo = false;
        updatedArray[id].tmt_neurotoxicity_linvo = false;
        updatedArray[id].tmt_linvoseltamab_unknown = false;

        updatedArray[id].Q39LinvoEmptyHard = false;

        updatedArray[id].Q39linvoReasonHard = false;

        //39.1.A
        if (isNewEcrfA) {
          handleQ391AList(
            setQ391ALinvoTocilzumabList,
            q391ALinvoRow,
            pid,
            isSelected,
          );
        }

        setQ37LinvoDetails(updatedArray);
        break;
      }
      case "granulocyte_linvo" + id: {
        const updatedArray = [...Q37LinvoDetails];

        updatedArray[id] = {
          ...updatedArray[id],
          noneoftheabove_linvo: false,
          granulocyte_linvo: !Boolean(updatedArray[id].granulocyte_linvo),
          Q39LinvoEmptyHard: false,
          prophylactic_use_linvo: false,
          secondary_prophylactic_use_linvo: false,
          oth_use_linvo: false,
          unknown_linvo: null,
        };

        setQ37LinvoDetails(updatedArray);
        break;
      }

      case "Steroids" + id: {
        let updatedArray = [...Q37LinvoDetails];

        updatedArray[id].Steroids = !updatedArray[id].Steroids;
        updatedArray[id].noneoftheabove_linvo = null;

        updatedArray[id].cytokin_linvo_steroids = false;
        updatedArray[id].secondary_prophylactic_linvo_steroids = false;
        updatedArray[id].secondary_neurotoxicity_linvo_steroids = false;

        updatedArray[id].crs_linvo_steroids = false;
        updatedArray[id].neurotoxicity_linvo_steroids = false;
        updatedArray[id].tmt_neurotoxicity_linvo_steroids = false;
        updatedArray[id].tmt_linvo_unknown = false;
        updatedArray[id].steroid_pre_tmt_linvo = false;

        updatedArray[id].dose_name_linvo_unknown = false;
        updatedArray[id].betamethasone_linvo = false;
        updatedArray[id].betamethasone_linvoNum = "";

        updatedArray[id].dexamethasone_linvo = false;
        updatedArray[id].dexamethasone_linvoNum = "";

        updatedArray[id].hydrocortisone_linvo = false;
        updatedArray[id].hydrocortisone_linvoNum = "";

        updatedArray[id].methylprednisolone_linvo = false;
        updatedArray[id].methylprednisolone_linvoNum = "";

        updatedArray[id].prednisolone_linvo = false;
        updatedArray[id].prednisolone_linvoNum = "";

        updatedArray[id].prednisone_linvo = false;
        updatedArray[id].prednisone_linvoNum = "";

        updatedArray[id].triamcinolone_linvo = false;
        updatedArray[id].triamcinolone_linvoNum = "";

        updatedArray[id].Q39linvoSteroidsEmptyHard = false;
        updatedArray[id].Q39LinvoEmptyHard = false;

        setQ37LinvoDetails(updatedArray);
        break;
      }

      case "noneoftheabove_linvo" + id: {
        let updatedArray = [...Q37LinvoDetails];

        const isSelected = Number(value);
        updatedArray[id].noneoftheabove_linvo = isSelected;

        updatedArray[id].Steroids = false;
        updatedArray[id].tocilizumab_linvo = false;
        updatedArray[id].Q39LinvoEmptyHard = false;

        updatedArray[id].Q39linvoReasonHard = false;

        updatedArray[id].Q39linvoSteroidsEmptyHard = false;
        updatedArray[id].Q39linvoGranduloEmptyHard = false;
        updatedArray[id].granulocyte_linvo = null;

        // Q40 is independent of the Q39 answer and stays visible/required
        // regardless of "None of the above" - do not clear its fields here.

        //39.1.A
        if (isNewEcrfA) {
          handleQ391AList(
            setQ391ALinvoTocilzumabList,
            q391ALinvoRow,
            pid,
            isSelected,
          );
        }

        setQ37LinvoDetails(updatedArray);
        break;
      }

      //39.C Linvoseltamab
      case "tocilizumab_linvoC" + id: {
        let updatedArray = [...Q37LinvoDetails];
        const isSelected = !updatedArray[id].tocilizumab_linvoC;
        updatedArray[id].tocilizumab_linvoC = isSelected;
        updatedArray[id].noneoftheabove_linvoC = null;
        updatedArray[id].Q39CEmptyHard = false;
        if (!isSelected) {
          updatedArray[id] = {
            ...updatedArray[id],
            tociliAdmsDateRD_linvoC: null,
            tociliAdmsDate_linvoC: "",
            tociliAdmsDateInvalid_linvoC: false,
            tociliAdmsDateRangeInvalid_linvoC: false,
            tociliAdmsDateEmpty_linvoC: false,
            tociliDoseRd_linvoC: null,
            tociliDoseMgkg_linvoC: "",
            tociliDoseOther_linvoC: "",
            tociliDoseEmptyHard_linvoC: false,
            tociliDoseMgkgEmptyHard_linvoC: false,
            tociliDoseOtherEmptyHard_linvoC: false,
            tociliReasonPretratment_linvoC: false,
            tociliReasonPrimarycrs_linvoC: false,
            tociliReasonSecondarycrs_linvoC: false,
            tociliReasonSupportivecrs_linvoC: false,
            tociliReasonPrimaryneuro_linvoC: false,
            tociliReasonSecondaryneuro_linvoC: false,
            tociliReasonSupportiveneuro_linvoC: false,
            tociliReasonUnk_linvoC: false,
            Q391CReasonEmptyHard: false,
          };
        }
        setQ37LinvoDetails(updatedArray);
        break;
      }

      case "steroids_linvoC" + id: {
        let updatedArray = [...Q37LinvoDetails];
        const isSteroidsSelected = !updatedArray[id].steroids_linvoC;
        updatedArray[id].steroids_linvoC = isSteroidsSelected;
        updatedArray[id].noneoftheabove_linvoC = null;
        updatedArray[id].Q39CEmptyHard = false;
        if (!isSteroidsSelected) {
          updatedArray[id] = {
            ...updatedArray[id],
            betamethasone_linvoC: false,
            betamethasone_linvoCNum: "",
            dexamethasone_linvoC: false,
            dexamethasone_linvoCNum: "",
            hydrocortisone_linvoC: false,
            hydrocortisone_linvoCNum: "",
            methylprednisolone_linvoC: false,
            methylprednisolone_linvoCNum: "",
            prednisolone_linvoC: false,
            prednisolone_linvoCNum: "",
            prednisone_linvoC: false,
            prednisone_linvoCNum: "",
            triamcinolone_linvoC: false,
            triamcinolone_linvoCNum: "",
            dose_name_linvoC_unknown: false,
            name_dose_steroidsHardEmpty_linvoC: false,
            steroids_linvoC_txtEmpty: false,
            steroidsDoseRangeInvalid_linvoC: false,
            steroidReasonPretratment_linvoC: false,
            steroidReasonPrimarycrs_linvoC: false,
            steroidReasonSecondarycrs_linvoC: false,
            steroidReasonSupportivecrs_linvoC: false,
            steroidReasonPrimaryneuro_linvoC: false,
            steroidReasonSecondaryneuro_linvoC: false,
            steroidReasonSupportiveneuro_linvoC: false,
            steroidReasonUnk_linvoC: false,
            Q392CReasonEmptyHard: false,
          };
        }
        setQ37LinvoDetails(updatedArray);
        break;
      }

      case "granulocyte_linvoC" + id: {
        let updatedArray = [...Q37LinvoDetails];
        const isGranuloSelected = !updatedArray[id].granulocyte_linvoC;
        updatedArray[id].granulocyte_linvoC = isGranuloSelected;
        updatedArray[id].noneoftheabove_linvoC = null;
        updatedArray[id].Q39CEmptyHard = false;
        if (!isGranuloSelected) {
          updatedArray[id] = {
            ...updatedArray[id],
            prophylactic_use_linvoC: false,
            secondary_prophylactic_use_linvoC: false,
            oth_use_linvoC: false,
            unknown_linvoC: null,
            Q393CReasonEmptyHard: false,
          };
        }
        setQ37LinvoDetails(updatedArray);
        break;
      }

      case "noneoftheabove_linvoC" + id: {
        let updatedArray = [...Q37LinvoDetails];
        updatedArray[id] = {
          ...updatedArray[id],
          noneoftheabove_linvoC: Number(value),
          tocilizumab_linvoC: false,
          steroids_linvoC: false,
          granulocyte_linvoC: false,
          Q39CEmptyHard: false,
          tociliAdmsDateRD_linvoC: null,
          tociliAdmsDate_linvoC: "",
          tociliAdmsDateInvalid_linvoC: false,
          tociliAdmsDateRangeInvalid_linvoC: false,
          tociliAdmsDateEmpty_linvoC: false,
          tociliDoseRd_linvoC: null,
          tociliDoseMgkg_linvoC: "",
          tociliDoseOther_linvoC: "",
          tociliDoseEmptyHard_linvoC: false,
          tociliDoseMgkgEmptyHard_linvoC: false,
          tociliDoseOtherEmptyHard_linvoC: false,
          tociliReasonPretratment_linvoC: false,
          tociliReasonPrimarycrs_linvoC: false,
          tociliReasonSecondarycrs_linvoC: false,
          tociliReasonSupportivecrs_linvoC: false,
          tociliReasonPrimaryneuro_linvoC: false,
          tociliReasonSecondaryneuro_linvoC: false,
          tociliReasonSupportiveneuro_linvoC: false,
          tociliReasonUnk_linvoC: false,
          Q391CReasonEmptyHard: false,
          betamethasone_linvoC: false,
          betamethasone_linvoCNum: "",
          dexamethasone_linvoC: false,
          dexamethasone_linvoCNum: "",
          hydrocortisone_linvoC: false,
          hydrocortisone_linvoCNum: "",
          methylprednisolone_linvoC: false,
          methylprednisolone_linvoCNum: "",
          prednisolone_linvoC: false,
          prednisolone_linvoCNum: "",
          prednisone_linvoC: false,
          prednisone_linvoCNum: "",
          triamcinolone_linvoC: false,
          triamcinolone_linvoCNum: "",
          dose_name_linvoC_unknown: false,
          name_dose_steroidsHardEmpty_linvoC: false,
          steroids_linvoC_txtEmpty: false,
          steroidsDoseRangeInvalid_linvoC: false,
          steroidReasonPretratment_linvoC: false,
          steroidReasonPrimarycrs_linvoC: false,
          steroidReasonSecondarycrs_linvoC: false,
          steroidReasonSupportivecrs_linvoC: false,
          steroidReasonPrimaryneuro_linvoC: false,
          steroidReasonSecondaryneuro_linvoC: false,
          steroidReasonSupportiveneuro_linvoC: false,
          steroidReasonUnk_linvoC: false,
          Q392CReasonEmptyHard: false,
          prophylactic_use_linvoC: false,
          secondary_prophylactic_use_linvoC: false,
          oth_use_linvoC: false,
          unknown_linvoC: null,
          Q393CReasonEmptyHard: false,

          // Q40/Q40.C is independent of the Q39.C answer and stays
          // visible/required regardless of "None of the above" - do not
          // clear its fields here.
        };
        setQ37LinvoDetails(updatedArray);
        break;
      }

      case "tociliAdmsDateRD_linvoC" + id: {
        let updatedArray = [...Q37LinvoDetails];
        updatedArray[id].tociliAdmsDateRD_linvoC = Number(value);
        updatedArray[id].tociliAdmsDateEmpty_linvoC = false;
        updatedArray[id].tociliAdmsDateInvalid_linvoC = false;
        updatedArray[id].tociliAdmsDateRangeInvalid_linvoC = false;
        if (Number(value) === 99) {
          updatedArray[id].tociliAdmsDate_linvoC = "";
        }
        setQ37LinvoDetails(updatedArray);
        break;
      }

      case "tociliDoseRd_linvoC" + id: {
        let updatedArray = [...Q37LinvoDetails];
        const rd = Number(value);
        updatedArray[id].tociliDoseRd_linvoC = rd;
        updatedArray[id].tociliDoseEmptyHard_linvoC = false;
        updatedArray[id].tociliDoseMgkgEmptyHard_linvoC = false;
        updatedArray[id].tociliDoseOtherEmptyHard_linvoC = false;
        if (rd !== 1) updatedArray[id].tociliDoseMgkg_linvoC = "";
        if (rd !== 2) updatedArray[id].tociliDoseOther_linvoC = "";
        setQ37LinvoDetails(updatedArray);
        break;
      }

      case "tociliDoseMgkg_linvoC" + id: {
        let updatedArray = [...Q37LinvoDetails];
        updatedArray[id].tociliDoseMgkg_linvoC = value;
        updatedArray[id].tociliDoseRd_linvoC = 1;
        updatedArray[id].tociliDoseOther_linvoC = "";
        updatedArray[id].tociliDoseEmptyHard_linvoC = false;
        updatedArray[id].tociliDoseMgkgEmptyHard_linvoC = false;
        updatedArray[id].tociliDoseOtherEmptyHard_linvoC = false;
        setQ37LinvoDetails(updatedArray);
        break;
      }

      case "tociliDoseOther_linvoC" + id: {
        let updatedArray = [...Q37LinvoDetails];
        updatedArray[id].tociliDoseOther_linvoC = value;
        updatedArray[id].tociliDoseRd_linvoC = 2;
        updatedArray[id].tociliDoseMgkg_linvoC = "";
        updatedArray[id].tociliDoseEmptyHard_linvoC = false;
        updatedArray[id].tociliDoseMgkgEmptyHard_linvoC = false;
        updatedArray[id].tociliDoseOtherEmptyHard_linvoC = false;
        setQ37LinvoDetails(updatedArray);
        break;
      }

      case "tociliDoseOtherToggle_linvoC" + id: {
        let updatedArray = [...Q37LinvoDetails];
        updatedArray[id].tociliDoseRd_linvoC = checked ? 2 : null;
        updatedArray[id].tociliDoseEmptyHard_linvoC = false;
        updatedArray[id].tociliDoseMgkgEmptyHard_linvoC = false;
        updatedArray[id].tociliDoseOtherEmptyHard_linvoC = false;
        if (checked) {
          updatedArray[id].tociliDoseMgkg_linvoC = "";
        } else {
          updatedArray[id].tociliDoseOther_linvoC = "";
        }
        setQ37LinvoDetails(updatedArray);
        break;
      }

      case "tociliReasonPretratment_linvoC" + id: {
        let updatedArray = [...Q37LinvoDetails];
        updatedArray[id].tociliReasonPretratment_linvoC =
          !updatedArray[id].tociliReasonPretratment_linvoC;
        updatedArray[id].tociliReasonUnk_linvoC = false;
        updatedArray[id].Q391CReasonEmptyHard = false;
        setQ37LinvoDetails(updatedArray);
        break;
      }

      case "tociliReasonPrimarycrs_linvoC" + id: {
        let updatedArray = [...Q37LinvoDetails];
        updatedArray[id].tociliReasonPrimarycrs_linvoC =
          !updatedArray[id].tociliReasonPrimarycrs_linvoC;
        updatedArray[id].tociliReasonUnk_linvoC = false;
        updatedArray[id].Q391CReasonEmptyHard = false;
        setQ37LinvoDetails(updatedArray);
        break;
      }

      case "tociliReasonSecondarycrs_linvoC" + id: {
        let updatedArray = [...Q37LinvoDetails];
        updatedArray[id].tociliReasonSecondarycrs_linvoC =
          !updatedArray[id].tociliReasonSecondarycrs_linvoC;
        updatedArray[id].tociliReasonUnk_linvoC = false;
        updatedArray[id].Q391CReasonEmptyHard = false;
        setQ37LinvoDetails(updatedArray);
        break;
      }

      case "tociliReasonSupportivecrs_linvoC" + id: {
        let updatedArray = [...Q37LinvoDetails];
        updatedArray[id].tociliReasonSupportivecrs_linvoC =
          !updatedArray[id].tociliReasonSupportivecrs_linvoC;
        updatedArray[id].tociliReasonUnk_linvoC = false;
        updatedArray[id].Q391CReasonEmptyHard = false;
        setQ37LinvoDetails(updatedArray);
        break;
      }

      case "tociliReasonPrimaryneuro_linvoC" + id: {
        let updatedArray = [...Q37LinvoDetails];
        updatedArray[id].tociliReasonPrimaryneuro_linvoC =
          !updatedArray[id].tociliReasonPrimaryneuro_linvoC;
        updatedArray[id].tociliReasonUnk_linvoC = false;
        updatedArray[id].Q391CReasonEmptyHard = false;
        setQ37LinvoDetails(updatedArray);
        break;
      }

      case "tociliReasonSecondaryneuro_linvoC" + id: {
        let updatedArray = [...Q37LinvoDetails];
        updatedArray[id].tociliReasonSecondaryneuro_linvoC =
          !updatedArray[id].tociliReasonSecondaryneuro_linvoC;
        updatedArray[id].tociliReasonUnk_linvoC = false;
        updatedArray[id].Q391CReasonEmptyHard = false;
        setQ37LinvoDetails(updatedArray);
        break;
      }

      case "tociliReasonSupportiveneuro_linvoC" + id: {
        let updatedArray = [...Q37LinvoDetails];
        updatedArray[id].tociliReasonSupportiveneuro_linvoC =
          !updatedArray[id].tociliReasonSupportiveneuro_linvoC;
        updatedArray[id].tociliReasonUnk_linvoC = false;
        updatedArray[id].Q391CReasonEmptyHard = false;
        setQ37LinvoDetails(updatedArray);
        break;
      }

      case "tociliReasonUnk_linvoC" + id: {
        let updatedArray = [...Q37LinvoDetails];
        updatedArray[id] = {
          ...updatedArray[id],
          tociliReasonUnk_linvoC: !updatedArray[id].tociliReasonUnk_linvoC,
          tociliReasonPretratment_linvoC: false,
          tociliReasonPrimarycrs_linvoC: false,
          tociliReasonSecondarycrs_linvoC: false,
          tociliReasonSupportivecrs_linvoC: false,
          tociliReasonPrimaryneuro_linvoC: false,
          tociliReasonSecondaryneuro_linvoC: false,
          tociliReasonSupportiveneuro_linvoC: false,
          Q391CReasonEmptyHard: false,
        };
        setQ37LinvoDetails(updatedArray);
        break;
      }

      //39.2.C Linvoseltamab
      case "betamethasone_linvoC" + id: {
        let updatedArray = [...Q37LinvoDetails];
        updatedArray[id].betamethasone_linvoC =
          !updatedArray[id].betamethasone_linvoC;
        updatedArray[id].betamethasone_linvoCNum = "";
        updatedArray[id].name_dose_steroidsHardEmpty_linvoC = false;
        updatedArray[id].steroids_linvoC_txtEmpty = false;
        updatedArray[id].steroidsDoseRangeInvalid_linvoC = false;
        updatedArray[id].dose_name_linvoC_unknown = false;
        setQ37LinvoDetails(updatedArray);
        break;
      }

      case "betamethasone_linvoCNum" + id: {
        let updatedArray = [...Q37LinvoDetails];
        updatedArray[id].betamethasone_linvoCNum = value;
        updatedArray[id].betamethasone_linvoC = true;
        updatedArray[id].name_dose_steroidsHardEmpty_linvoC = false;
        updatedArray[id].steroids_linvoC_txtEmpty = false;
        updatedArray[id].steroidsDoseRangeInvalid_linvoC = false;
        updatedArray[id].dose_name_linvoC_unknown = false;
        setQ37LinvoDetails(updatedArray);
        break;
      }

      case "dexamethasone_linvoC" + id: {
        let updatedArray = [...Q37LinvoDetails];
        updatedArray[id].dexamethasone_linvoC =
          !updatedArray[id].dexamethasone_linvoC;
        updatedArray[id].dexamethasone_linvoCNum = "";
        updatedArray[id].name_dose_steroidsHardEmpty_linvoC = false;
        updatedArray[id].steroids_linvoC_txtEmpty = false;
        updatedArray[id].steroidsDoseRangeInvalid_linvoC = false;
        updatedArray[id].dose_name_linvoC_unknown = false;
        setQ37LinvoDetails(updatedArray);
        break;
      }

      case "dexamethasone_linvoCNum" + id: {
        let updatedArray = [...Q37LinvoDetails];
        updatedArray[id].dexamethasone_linvoCNum = value;
        updatedArray[id].dexamethasone_linvoC = true;
        updatedArray[id].name_dose_steroidsHardEmpty_linvoC = false;
        updatedArray[id].steroids_linvoC_txtEmpty = false;
        updatedArray[id].steroidsDoseRangeInvalid_linvoC = false;
        updatedArray[id].dose_name_linvoC_unknown = false;
        setQ37LinvoDetails(updatedArray);
        break;
      }

      case "hydrocortisone_linvoC" + id: {
        let updatedArray = [...Q37LinvoDetails];
        updatedArray[id].hydrocortisone_linvoC =
          !updatedArray[id].hydrocortisone_linvoC;
        updatedArray[id].hydrocortisone_linvoCNum = "";
        updatedArray[id].name_dose_steroidsHardEmpty_linvoC = false;
        updatedArray[id].steroids_linvoC_txtEmpty = false;
        updatedArray[id].steroidsDoseRangeInvalid_linvoC = false;
        updatedArray[id].dose_name_linvoC_unknown = false;
        setQ37LinvoDetails(updatedArray);
        break;
      }

      case "hydrocortisone_linvoCNum" + id: {
        let updatedArray = [...Q37LinvoDetails];
        updatedArray[id].hydrocortisone_linvoCNum = value;
        updatedArray[id].hydrocortisone_linvoC = true;
        updatedArray[id].name_dose_steroidsHardEmpty_linvoC = false;
        updatedArray[id].steroids_linvoC_txtEmpty = false;
        updatedArray[id].steroidsDoseRangeInvalid_linvoC = false;
        updatedArray[id].dose_name_linvoC_unknown = false;
        setQ37LinvoDetails(updatedArray);
        break;
      }

      case "methylprednisolone_linvoC" + id: {
        let updatedArray = [...Q37LinvoDetails];
        updatedArray[id].methylprednisolone_linvoC =
          !updatedArray[id].methylprednisolone_linvoC;
        updatedArray[id].methylprednisolone_linvoCNum = "";
        updatedArray[id].name_dose_steroidsHardEmpty_linvoC = false;
        updatedArray[id].steroids_linvoC_txtEmpty = false;
        updatedArray[id].steroidsDoseRangeInvalid_linvoC = false;
        updatedArray[id].dose_name_linvoC_unknown = false;
        setQ37LinvoDetails(updatedArray);
        break;
      }

      case "methylprednisolone_linvoCNum" + id: {
        let updatedArray = [...Q37LinvoDetails];
        updatedArray[id].methylprednisolone_linvoCNum = value;
        updatedArray[id].methylprednisolone_linvoC = true;
        updatedArray[id].name_dose_steroidsHardEmpty_linvoC = false;
        updatedArray[id].steroids_linvoC_txtEmpty = false;
        updatedArray[id].steroidsDoseRangeInvalid_linvoC = false;
        updatedArray[id].dose_name_linvoC_unknown = false;
        setQ37LinvoDetails(updatedArray);
        break;
      }

      case "prednisolone_linvoC" + id: {
        let updatedArray = [...Q37LinvoDetails];
        updatedArray[id].prednisolone_linvoC =
          !updatedArray[id].prednisolone_linvoC;
        updatedArray[id].prednisolone_linvoCNum = "";
        updatedArray[id].name_dose_steroidsHardEmpty_linvoC = false;
        updatedArray[id].steroids_linvoC_txtEmpty = false;
        updatedArray[id].steroidsDoseRangeInvalid_linvoC = false;
        updatedArray[id].dose_name_linvoC_unknown = false;
        setQ37LinvoDetails(updatedArray);
        break;
      }

      case "prednisolone_linvoCNum" + id: {
        let updatedArray = [...Q37LinvoDetails];
        updatedArray[id].prednisolone_linvoCNum = value;
        updatedArray[id].prednisolone_linvoC = true;
        updatedArray[id].name_dose_steroidsHardEmpty_linvoC = false;
        updatedArray[id].steroids_linvoC_txtEmpty = false;
        updatedArray[id].steroidsDoseRangeInvalid_linvoC = false;
        updatedArray[id].dose_name_linvoC_unknown = false;
        setQ37LinvoDetails(updatedArray);
        break;
      }

      case "prednisone_linvoC" + id: {
        let updatedArray = [...Q37LinvoDetails];
        updatedArray[id].prednisone_linvoC =
          !updatedArray[id].prednisone_linvoC;
        updatedArray[id].prednisone_linvoCNum = "";
        updatedArray[id].name_dose_steroidsHardEmpty_linvoC = false;
        updatedArray[id].steroids_linvoC_txtEmpty = false;
        updatedArray[id].steroidsDoseRangeInvalid_linvoC = false;
        updatedArray[id].dose_name_linvoC_unknown = false;
        setQ37LinvoDetails(updatedArray);
        break;
      }

      case "prednisone_linvoCNum" + id: {
        let updatedArray = [...Q37LinvoDetails];
        updatedArray[id].prednisone_linvoCNum = value;
        updatedArray[id].prednisone_linvoC = true;
        updatedArray[id].name_dose_steroidsHardEmpty_linvoC = false;
        updatedArray[id].steroids_linvoC_txtEmpty = false;
        updatedArray[id].steroidsDoseRangeInvalid_linvoC = false;
        updatedArray[id].dose_name_linvoC_unknown = false;
        setQ37LinvoDetails(updatedArray);
        break;
      }

      case "triamcinolone_linvoC" + id: {
        let updatedArray = [...Q37LinvoDetails];
        updatedArray[id].triamcinolone_linvoC =
          !updatedArray[id].triamcinolone_linvoC;
        updatedArray[id].triamcinolone_linvoCNum = "";
        updatedArray[id].name_dose_steroidsHardEmpty_linvoC = false;
        updatedArray[id].steroids_linvoC_txtEmpty = false;
        updatedArray[id].steroidsDoseRangeInvalid_linvoC = false;
        updatedArray[id].dose_name_linvoC_unknown = false;
        setQ37LinvoDetails(updatedArray);
        break;
      }

      case "triamcinolone_linvoCNum" + id: {
        let updatedArray = [...Q37LinvoDetails];
        updatedArray[id].triamcinolone_linvoCNum = value;
        updatedArray[id].triamcinolone_linvoC = true;
        updatedArray[id].name_dose_steroidsHardEmpty_linvoC = false;
        updatedArray[id].steroids_linvoC_txtEmpty = false;
        updatedArray[id].steroidsDoseRangeInvalid_linvoC = false;
        updatedArray[id].dose_name_linvoC_unknown = false;
        setQ37LinvoDetails(updatedArray);
        break;
      }

      case "dose_name_linvoC_unknown" + id: {
        let updatedArray = [...Q37LinvoDetails];
        updatedArray[id] = {
          ...updatedArray[id],
          dose_name_linvoC_unknown: !updatedArray[id].dose_name_linvoC_unknown,
          name_dose_steroidsHardEmpty_linvoC: false,
          steroids_linvoC_txtEmpty: false,
          steroidsDoseRangeInvalid_linvoC: false,
          betamethasone_linvoC: false,
          betamethasone_linvoCNum: "",
          dexamethasone_linvoC: false,
          dexamethasone_linvoCNum: "",
          hydrocortisone_linvoC: false,
          hydrocortisone_linvoCNum: "",
          methylprednisolone_linvoC: false,
          methylprednisolone_linvoCNum: "",
          prednisolone_linvoC: false,
          prednisolone_linvoCNum: "",
          prednisone_linvoC: false,
          prednisone_linvoCNum: "",
          triamcinolone_linvoC: false,
          triamcinolone_linvoCNum: "",
        };
        setQ37LinvoDetails(updatedArray);
        break;
      }

      case "steroidReasonPretratment_linvoC" + id: {
        let updatedArray = [...Q37LinvoDetails];
        updatedArray[id].steroidReasonPretratment_linvoC =
          !updatedArray[id].steroidReasonPretratment_linvoC;
        updatedArray[id].steroidReasonUnk_linvoC = false;
        updatedArray[id].Q392CReasonEmptyHard = false;
        setQ37LinvoDetails(updatedArray);
        break;
      }

      case "steroidReasonPrimarycrs_linvoC" + id: {
        let updatedArray = [...Q37LinvoDetails];
        updatedArray[id].steroidReasonPrimarycrs_linvoC =
          !updatedArray[id].steroidReasonPrimarycrs_linvoC;
        updatedArray[id].steroidReasonUnk_linvoC = false;
        updatedArray[id].Q392CReasonEmptyHard = false;
        setQ37LinvoDetails(updatedArray);
        break;
      }

      case "steroidReasonSecondarycrs_linvoC" + id: {
        let updatedArray = [...Q37LinvoDetails];
        updatedArray[id].steroidReasonSecondarycrs_linvoC =
          !updatedArray[id].steroidReasonSecondarycrs_linvoC;
        updatedArray[id].steroidReasonUnk_linvoC = false;
        updatedArray[id].Q392CReasonEmptyHard = false;
        setQ37LinvoDetails(updatedArray);
        break;
      }

      case "steroidReasonSupportivecrs_linvoC" + id: {
        let updatedArray = [...Q37LinvoDetails];
        updatedArray[id].steroidReasonSupportivecrs_linvoC =
          !updatedArray[id].steroidReasonSupportivecrs_linvoC;
        updatedArray[id].steroidReasonUnk_linvoC = false;
        updatedArray[id].Q392CReasonEmptyHard = false;
        setQ37LinvoDetails(updatedArray);
        break;
      }

      case "steroidReasonPrimaryneuro_linvoC" + id: {
        let updatedArray = [...Q37LinvoDetails];
        updatedArray[id].steroidReasonPrimaryneuro_linvoC =
          !updatedArray[id].steroidReasonPrimaryneuro_linvoC;
        updatedArray[id].steroidReasonUnk_linvoC = false;
        updatedArray[id].Q392CReasonEmptyHard = false;
        setQ37LinvoDetails(updatedArray);
        break;
      }

      case "steroidReasonSecondaryneuro_linvoC" + id: {
        let updatedArray = [...Q37LinvoDetails];
        updatedArray[id].steroidReasonSecondaryneuro_linvoC =
          !updatedArray[id].steroidReasonSecondaryneuro_linvoC;
        updatedArray[id].steroidReasonUnk_linvoC = false;
        updatedArray[id].Q392CReasonEmptyHard = false;
        setQ37LinvoDetails(updatedArray);
        break;
      }

      case "steroidReasonSupportiveneuro_linvoC" + id: {
        let updatedArray = [...Q37LinvoDetails];
        updatedArray[id].steroidReasonSupportiveneuro_linvoC =
          !updatedArray[id].steroidReasonSupportiveneuro_linvoC;
        updatedArray[id].steroidReasonUnk_linvoC = false;
        updatedArray[id].Q392CReasonEmptyHard = false;
        setQ37LinvoDetails(updatedArray);
        break;
      }

      case "steroidReasonUnk_linvoC" + id: {
        let updatedArray = [...Q37LinvoDetails];
        updatedArray[id] = {
          ...updatedArray[id],
          steroidReasonUnk_linvoC: !updatedArray[id].steroidReasonUnk_linvoC,
          steroidReasonPretratment_linvoC: false,
          steroidReasonPrimarycrs_linvoC: false,
          steroidReasonSecondarycrs_linvoC: false,
          steroidReasonSupportivecrs_linvoC: false,
          steroidReasonPrimaryneuro_linvoC: false,
          steroidReasonSecondaryneuro_linvoC: false,
          steroidReasonSupportiveneuro_linvoC: false,
          Q392CReasonEmptyHard: false,
        };
        setQ37LinvoDetails(updatedArray);
        break;
      }

      case "dosing_freq_oth_txt" + id: {
        let updatedArray = [...Q37ElrexDetails];
        updatedArray[id].Q40dosingfreqRd = 5;
        updatedArray[id].dosing_freq_oth_txt = value;

        updatedArray[id].Q40DosingFreqEmptyHard = false;
        updatedArray[id].Q40DosingFreqTxtEmptyHard = false;
        setQ37ElrexDetails(updatedArray);
        break;
      }

      //checkbox - primary prof
      case "primaryProf" + id: {
        let updatedArray = [...Q37ElrexDetails];
        updatedArray[id].primaryProf = !updatedArray[id].primaryProf;

        updatedArray[id].Q40ReasonEmptyHard = false;
        setQ37ElrexDetails(updatedArray);
        break;
      }

      case "secondaryProf" + id: {
        let updatedArray = [...Q37ElrexDetails];
        updatedArray[id].secondaryProf = !updatedArray[id].secondaryProf;
        updatedArray[id].Q40ReasonEmptyHard = false;
        setQ37ElrexDetails(updatedArray);
        break;
      }

      case "otherReson" + id: {
        let updatedArray = [...Q37ElrexDetails];
        updatedArray[id].otherReson = !updatedArray[id].otherReson;
        updatedArray[id].Q40ReasonEmptyHard = false;
        setQ37ElrexDetails(updatedArray);
        break;
      }

      //40.C Elrexfio
      case "ivigInitRd_elrC" + id: {
        let updatedArray = [...Q37ElrexDetails];
        updatedArray[id].ivigInitRd_elrC = Number(value);
        updatedArray[id].ivigInitRdHard_elrC = false;
        updatedArray[id].ivigInitDtHard_elrC = false;
        updatedArray[id].ivigInitDtInvalid_elrC = false;
        updatedArray[id].ivigInitDtBeforeEof_elrC = false;
        updatedArray[id].ivigInitDtFullChainInvalid_elrC = false;
        updatedArray[id].ivigInitDt_elrC = "";

        if (Number(value) === 1) {
          updatedArray[id].ivigDosingType_elrC = null;
          updatedArray[id].ivigDosingTypeEmptyHard_elrC = false;

          updatedArray[id].ivigDosingFreqRd_elrC = null;
          updatedArray[id].ivigDosingFreqTimesTxt_elrC = "";
          updatedArray[id].ivigDosingFreqWeeksTxt_elrC = "";
          updatedArray[id].ivigDosingFreqOtherTxt_elrC = "";
          updatedArray[id].ivigDosingFreqEmptyHard_elrC = false;
          updatedArray[id].ivigDosingFreqTxtEmptyHard_elrC = false;
          updatedArray[id].ivigDosingFreqRangeInvalid_elrC = false;

          updatedArray[id].ivigReasonPrimary_elrC = false;
          updatedArray[id].ivigReasonSecondary_elrC = false;
          updatedArray[id].ivigReasonOther_elrC = false;
          updatedArray[id].ivigReasonOtherTxt_elrC = "";
          updatedArray[id].ivigReasonUnk_elrC = false;
          updatedArray[id].ivigReasonEmptyHard_elrC = false;
          updatedArray[id].ivigReasonOtherTxtEmptyHard_elrC = false;

          updatedArray[id].ivigDoseStrengthRd_elrC = null;
          updatedArray[id].ivigDoseStrengthGTxt_elrC = "";
          updatedArray[id].ivigDoseStrengthMgKgTxt_elrC = "";
          updatedArray[id].ivigDoseStrengthOtherTxt_elrC = "";
          updatedArray[id].ivigDoseStrengthEmptyHard_elrC = false;
          updatedArray[id].ivigDoseStrengthRangeInvalid_elrC = false;
          updatedArray[id].ivigDoseStrengthMgKgRangeInvalid_elrC = false;
          updatedArray[id].ivigDoseStrengthOtherTxtEmptyHard_elrC = false;

          updatedArray[id].ivigLastIgg_elrC = null;
          updatedArray[id].ivigLastIggEmptyHard_elrC = false;

          updatedArray[id].ivigBodyWeightRd_elrC = null;
          updatedArray[id].ivigBodyWeightKgTxt_elrC = "";
          updatedArray[id].ivigBodyWeightLbsTxt_elrC = "";
          updatedArray[id].ivigBodyWeightEmptyHard_elrC = false;
          updatedArray[id].ivigBodyWeightRangeInvalid_elrC = false;
        }
        setQ37ElrexDetails(updatedArray);
        break;
      }

      case "ivigDosingType_elrC" + id: {
        let updatedArray = [...Q37ElrexDetails];
        updatedArray[id].ivigDosingType_elrC = Number(value);
        updatedArray[id].ivigDosingTypeEmptyHard_elrC = false;

        // Column [H] is only shown for Weight-based dosage; clear it otherwise
        if (Number(value) !== 2) {
          updatedArray[id].ivigBodyWeightRd_elrC = null;
          updatedArray[id].ivigBodyWeightKgTxt_elrC = "";
          updatedArray[id].ivigBodyWeightLbsTxt_elrC = "";
        }
        updatedArray[id].ivigBodyWeightEmptyHard_elrC = false;
        updatedArray[id].ivigBodyWeightRangeInvalid_elrC = false;

        updatedArray[id].ivigDoseStrengthRd_elrC = null;
        updatedArray[id].ivigDoseStrengthGTxt_elrC = "";
        updatedArray[id].ivigDoseStrengthMgKgTxt_elrC = "";
        updatedArray[id].ivigDoseStrengthOtherTxt_elrC = "";
        updatedArray[id].ivigDoseStrengthEmptyHard_elrC = false;
        updatedArray[id].ivigDoseStrengthRangeInvalid_elrC = false;
        updatedArray[id].ivigDoseStrengthMgKgRangeInvalid_elrC = false;
        updatedArray[id].ivigDoseStrengthOtherTxtEmptyHard_elrC = false;

        setQ37ElrexDetails(updatedArray);
        break;
      }

      case "ivigDosingFreqRd_elrC" + id: {
        let updatedArray = [...Q37ElrexDetails];
        updatedArray[id].ivigDosingFreqRd_elrC = Number(value);
        updatedArray[id].ivigDosingFreqTimesTxt_elrC = "";
        updatedArray[id].ivigDosingFreqWeeksTxt_elrC = "";
        updatedArray[id].ivigDosingFreqOtherTxt_elrC = "";
        updatedArray[id].ivigDosingFreqEmptyHard_elrC = false;
        updatedArray[id].ivigDosingFreqTxtEmptyHard_elrC = false;
        updatedArray[id].ivigDosingFreqRangeInvalid_elrC = false;
        setQ37ElrexDetails(updatedArray);
        break;
      }

      case "ivigDosingFreqTimesTxt_elrC" + id: {
        let updatedArray = [...Q37ElrexDetails];
        updatedArray[id].ivigDosingFreqTimesTxt_elrC = value;
        updatedArray[id].ivigDosingFreqWeeksTxt_elrC = "";
        updatedArray[id].ivigDosingFreqOtherTxt_elrC = "";
        updatedArray[id].ivigDosingFreqRd_elrC = 1;
        updatedArray[id].ivigDosingFreqEmptyHard_elrC = false;
        updatedArray[id].ivigDosingFreqTxtEmptyHard_elrC = false;
        updatedArray[id].ivigDosingFreqRangeInvalid_elrC = false;
        setQ37ElrexDetails(updatedArray);
        break;
      }

      case "ivigDosingFreqWeeksTxt_elrC" + id: {
        let updatedArray = [...Q37ElrexDetails];
        updatedArray[id].ivigDosingFreqWeeksTxt_elrC = value;
        updatedArray[id].ivigDosingFreqTimesTxt_elrC = "";
        updatedArray[id].ivigDosingFreqOtherTxt_elrC = "";
        updatedArray[id].ivigDosingFreqRd_elrC = 2;
        updatedArray[id].ivigDosingFreqEmptyHard_elrC = false;
        updatedArray[id].ivigDosingFreqTxtEmptyHard_elrC = false;
        updatedArray[id].ivigDosingFreqRangeInvalid_elrC = false;
        setQ37ElrexDetails(updatedArray);
        break;
      }

      case "ivigDosingFreqOtherTxt_elrC" + id: {
        let updatedArray = [...Q37ElrexDetails];
        updatedArray[id].ivigDosingFreqOtherTxt_elrC = value;
        updatedArray[id].ivigDosingFreqTimesTxt_elrC = "";
        updatedArray[id].ivigDosingFreqWeeksTxt_elrC = "";
        updatedArray[id].ivigDosingFreqRd_elrC = 3;
        updatedArray[id].ivigDosingFreqEmptyHard_elrC = false;
        updatedArray[id].ivigDosingFreqTxtEmptyHard_elrC = false;
        setQ37ElrexDetails(updatedArray);
        break;
      }

      case "ivigReasonPrimary_elrC" + id: {
        let updatedArray = [...Q37ElrexDetails];
        updatedArray[id].ivigReasonPrimary_elrC =
          !updatedArray[id].ivigReasonPrimary_elrC;
        if (updatedArray[id].ivigReasonPrimary_elrC) {
          updatedArray[id].ivigReasonUnk_elrC = false;
        }
        updatedArray[id].ivigReasonEmptyHard_elrC = false;
        setQ37ElrexDetails(updatedArray);
        break;
      }

      case "ivigReasonSecondary_elrC" + id: {
        let updatedArray = [...Q37ElrexDetails];
        updatedArray[id].ivigReasonSecondary_elrC =
          !updatedArray[id].ivigReasonSecondary_elrC;
        if (updatedArray[id].ivigReasonSecondary_elrC) {
          updatedArray[id].ivigReasonUnk_elrC = false;
        }
        updatedArray[id].ivigReasonEmptyHard_elrC = false;
        setQ37ElrexDetails(updatedArray);
        break;
      }

      case "ivigReasonOther_elrC" + id: {
        let updatedArray = [...Q37ElrexDetails];
        updatedArray[id].ivigReasonOther_elrC =
          !updatedArray[id].ivigReasonOther_elrC;
        if (updatedArray[id].ivigReasonOther_elrC) {
          updatedArray[id].ivigReasonUnk_elrC = false;
        } else {
          updatedArray[id].ivigReasonOtherTxt_elrC = "";
        }
        updatedArray[id].ivigReasonEmptyHard_elrC = false;
        updatedArray[id].ivigReasonOtherTxtEmptyHard_elrC = false;
        setQ37ElrexDetails(updatedArray);
        break;
      }

      case "ivigReasonOtherTxt_elrC" + id: {
        let updatedArray = [...Q37ElrexDetails];
        updatedArray[id].ivigReasonOtherTxt_elrC = value;
        updatedArray[id].ivigReasonOther_elrC = true;
        updatedArray[id].ivigReasonUnk_elrC = false;
        updatedArray[id].ivigReasonEmptyHard_elrC = false;
        updatedArray[id].ivigReasonOtherTxtEmptyHard_elrC = false;
        setQ37ElrexDetails(updatedArray);
        break;
      }

      case "ivigReasonUnk_elrC" + id: {
        let updatedArray = [...Q37ElrexDetails];
        updatedArray[id].ivigReasonUnk_elrC =
          !updatedArray[id].ivigReasonUnk_elrC;
        if (updatedArray[id].ivigReasonUnk_elrC) {
          updatedArray[id].ivigReasonPrimary_elrC = false;
          updatedArray[id].ivigReasonSecondary_elrC = false;
          updatedArray[id].ivigReasonOther_elrC = false;
          updatedArray[id].ivigReasonOtherTxt_elrC = "";
          updatedArray[id].ivigReasonOtherTxtEmptyHard_elrC = false;
        }
        updatedArray[id].ivigReasonEmptyHard_elrC = false;
        setQ37ElrexDetails(updatedArray);
        break;
      }

      case "ivigDoseStrengthRd_elrC" + id: {
        let updatedArray = [...Q37ElrexDetails];
        updatedArray[id].ivigDoseStrengthRd_elrC = Number(value);
        updatedArray[id].ivigDoseStrengthGTxt_elrC = "";
        updatedArray[id].ivigDoseStrengthMgKgTxt_elrC = "";
        updatedArray[id].ivigDoseStrengthOtherTxt_elrC = "";
        updatedArray[id].ivigDoseStrengthEmptyHard_elrC = false;
        updatedArray[id].ivigDoseStrengthRangeInvalid_elrC = false;
        updatedArray[id].ivigDoseStrengthMgKgRangeInvalid_elrC = false;
        updatedArray[id].ivigDoseStrengthOtherTxtEmptyHard_elrC = false;
        setQ37ElrexDetails(updatedArray);
        break;
      }

      case "ivigDoseStrengthGTxt_elrC" + id: {
        let updatedArray = [...Q37ElrexDetails];
        updatedArray[id].ivigDoseStrengthGTxt_elrC = value;
        updatedArray[id].ivigDoseStrengthMgKgTxt_elrC = "";
        updatedArray[id].ivigDoseStrengthOtherTxt_elrC = "";
        updatedArray[id].ivigDoseStrengthRd_elrC = 1;
        updatedArray[id].ivigDoseStrengthEmptyHard_elrC = false;
        updatedArray[id].ivigDoseStrengthRangeInvalid_elrC = false;
        updatedArray[id].ivigDoseStrengthMgKgRangeInvalid_elrC = false;
        updatedArray[id].ivigDoseStrengthOtherTxtEmptyHard_elrC = false;
        setQ37ElrexDetails(updatedArray);
        break;
      }

      case "ivigDoseStrengthMgKgTxt_elrC" + id: {
        let updatedArray = [...Q37ElrexDetails];
        updatedArray[id].ivigDoseStrengthMgKgTxt_elrC = value;
        updatedArray[id].ivigDoseStrengthGTxt_elrC = "";
        updatedArray[id].ivigDoseStrengthOtherTxt_elrC = "";
        updatedArray[id].ivigDoseStrengthRd_elrC = 3;
        updatedArray[id].ivigDoseStrengthEmptyHard_elrC = false;
        updatedArray[id].ivigDoseStrengthRangeInvalid_elrC = false;
        updatedArray[id].ivigDoseStrengthMgKgRangeInvalid_elrC = false;
        updatedArray[id].ivigDoseStrengthOtherTxtEmptyHard_elrC = false;
        setQ37ElrexDetails(updatedArray);
        break;
      }

      case "ivigDoseStrengthOtherTxt_elrC" + id: {
        let updatedArray = [...Q37ElrexDetails];
        updatedArray[id].ivigDoseStrengthOtherTxt_elrC = value;
        updatedArray[id].ivigDoseStrengthGTxt_elrC = "";
        updatedArray[id].ivigDoseStrengthMgKgTxt_elrC = "";
        updatedArray[id].ivigDoseStrengthRd_elrC = 2;
        updatedArray[id].ivigDoseStrengthEmptyHard_elrC = false;
        updatedArray[id].ivigDoseStrengthRangeInvalid_elrC = false;
        updatedArray[id].ivigDoseStrengthMgKgRangeInvalid_elrC = false;
        updatedArray[id].ivigDoseStrengthOtherTxtEmptyHard_elrC = false;
        setQ37ElrexDetails(updatedArray);
        break;
      }

      case "ivigBodyWeightRd_elrC" + id: {
        let updatedArray = [...Q37ElrexDetails];
        updatedArray[id].ivigBodyWeightRd_elrC = Number(value);
        updatedArray[id].ivigBodyWeightKgTxt_elrC = "";
        updatedArray[id].ivigBodyWeightLbsTxt_elrC = "";
        updatedArray[id].ivigBodyWeightEmptyHard_elrC = false;
        updatedArray[id].ivigBodyWeightRangeInvalid_elrC = false;
        setQ37ElrexDetails(updatedArray);
        break;
      }

      case "ivigBodyWeightKgTxt_elrC" + id: {
        let updatedArray = [...Q37ElrexDetails];
        updatedArray[id].ivigBodyWeightKgTxt_elrC = value;
        updatedArray[id].ivigBodyWeightLbsTxt_elrC = "";
        updatedArray[id].ivigBodyWeightRd_elrC = 1;
        updatedArray[id].ivigBodyWeightEmptyHard_elrC = false;
        updatedArray[id].ivigBodyWeightRangeInvalid_elrC = false;
        setQ37ElrexDetails(updatedArray);
        break;
      }

      case "ivigBodyWeightLbsTxt_elrC" + id: {
        let updatedArray = [...Q37ElrexDetails];
        updatedArray[id].ivigBodyWeightLbsTxt_elrC = value;
        updatedArray[id].ivigBodyWeightKgTxt_elrC = "";
        updatedArray[id].ivigBodyWeightRd_elrC = 2;
        updatedArray[id].ivigBodyWeightEmptyHard_elrC = false;
        updatedArray[id].ivigBodyWeightRangeInvalid_elrC = false;
        setQ37ElrexDetails(updatedArray);
        break;
      }

      case "ivigLastIgg_elrC" + id: {
        let updatedArray = [...Q37ElrexDetails];
        updatedArray[id].ivigLastIgg_elrC = Number(value);
        updatedArray[id].ivigLastIggEmptyHard_elrC = false;
        setQ37ElrexDetails(updatedArray);
        break;
      }

      //40 - Linvo

      case "q40DosingFreqRd_linvo" + id: {
        let updatedArray = [...Q37LinvoDetails];
        updatedArray[id].q40DosingFreqRd_linvo = Number(value);
        updatedArray[id].dosing_Freq_Oth_Txt_linvo = "";

        updatedArray[id].Q40LinvoDosingFreqEmptyHard = false;
        updatedArray[id].Q40LinvoDosingFreqTxtEmptyHard = false;
        if (Number(value) === 1) {
          updatedArray[id].q40IgILevelRd_linvo = null;
          updatedArray[id].Q40LinvoIgGEmptyHard = false;

          updatedArray[id].q40Init_Dt_Rd_linvo = null;
          updatedArray[id].Q40LinvoInit_RdHard = false;
          updatedArray[id].q40Init_Dt_linvo = "";
          updatedArray[id].Q40LinvoInit_dtHard = false;
          updatedArray[id].invalidQ40Init_dt_linvo = false;
          updatedArray[id].init_dt_before_eof_linvo = false;
          updatedArray[id].init_dt_full_chain_invalid_linvo = false;

          updatedArray[id].primaryProf_linvo = false;
          updatedArray[id].secondaryProf_linvo = false;
          updatedArray[id].otherReson_linvo = false;
          updatedArray[id].Q40LinvoReasonEmptyHard = false;

          // Cascade to Q40.C: "Patient never received IVIG during treatment"
          updatedArray[id].ivigInitRd_linvoC = 1;
          updatedArray[id].ivigInitRdHard_linvoC = false;
          updatedArray[id].ivigInitDtHard_linvoC = false;
          updatedArray[id].ivigInitDtInvalid_linvoC = false;
          updatedArray[id].ivigInitDtBeforeEof_linvoC = false;
          updatedArray[id].ivigInitDtFullChainInvalid_linvoC = false;
          updatedArray[id].ivigInitDt_linvoC = "";

          updatedArray[id].ivigDosingType_linvoC = null;
          updatedArray[id].ivigDosingTypeEmptyHard_linvoC = false;

          updatedArray[id].ivigDosingFreqRd_linvoC = null;
          updatedArray[id].ivigDosingFreqTimesTxt_linvoC = "";
          updatedArray[id].ivigDosingFreqWeeksTxt_linvoC = "";
          updatedArray[id].ivigDosingFreqOtherTxt_linvoC = "";
          updatedArray[id].ivigDosingFreqEmptyHard_linvoC = false;
          updatedArray[id].ivigDosingFreqTxtEmptyHard_linvoC = false;
          updatedArray[id].ivigDosingFreqRangeInvalid_linvoC = false;

          updatedArray[id].ivigReasonPrimary_linvoC = false;
          updatedArray[id].ivigReasonSecondary_linvoC = false;
          updatedArray[id].ivigReasonOther_linvoC = false;
          updatedArray[id].ivigReasonOtherTxt_linvoC = "";
          updatedArray[id].ivigReasonUnk_linvoC = false;
          updatedArray[id].ivigReasonEmptyHard_linvoC = false;
          updatedArray[id].ivigReasonOtherTxtEmptyHard_linvoC = false;

          updatedArray[id].ivigDoseStrengthRd_linvoC = null;
          updatedArray[id].ivigDoseStrengthGTxt_linvoC = "";
          updatedArray[id].ivigDoseStrengthMgKgTxt_linvoC = "";
          updatedArray[id].ivigDoseStrengthOtherTxt_linvoC = "";
          updatedArray[id].ivigDoseStrengthEmptyHard_linvoC = false;
          updatedArray[id].ivigDoseStrengthRangeInvalid_linvoC = false;
          updatedArray[id].ivigDoseStrengthMgKgRangeInvalid_linvoC = false;
          updatedArray[id].ivigDoseStrengthOtherTxtEmptyHard_linvoC = false;

          updatedArray[id].ivigLastIgg_linvoC = null;
          updatedArray[id].ivigLastIggEmptyHard_linvoC = false;

          updatedArray[id].ivigBodyWeightRd_linvoC = null;
          updatedArray[id].ivigBodyWeightKgTxt_linvoC = "";
          updatedArray[id].ivigBodyWeightLbsTxt_linvoC = "";
          updatedArray[id].ivigBodyWeightEmptyHard_linvoC = false;
          updatedArray[id].ivigBodyWeightRangeInvalid_linvoC = false;

          setQ401aLinvoDetails([{ ...Q401aLinvoRow }]);
          setQ403aLinvoDetails([{ ...Q403aLinvoRow }]);
        }
        setQ37LinvoDetails(updatedArray);
        break;
      }

      case "q40IgILevelRd_linvo" + id: {
        let updatedArray = [...Q37LinvoDetails];

        updatedArray[id].q40IgILevelRd_linvo = Number(value);
        updatedArray[id].Q40LinvoIgGEmptyHard = false;
        setQ37LinvoDetails(updatedArray);
        break;
      }

      case "q40Init_Dt_Rd_linvo" + id: {
        let updatedArray = [...Q37LinvoDetails];
        updatedArray[id].q40Init_Dt_Rd_linvo = Number(value);
        updatedArray[id].Q40LinvoInit_RdHard = false;
        updatedArray[id].Q40LinvoInit_dtHard = false;
        updatedArray[id].invalidQ40Init_dt_linvo = false;
        updatedArray[id].init_dt_before_eof_linvo = false;
        updatedArray[id].init_dt_full_chain_invalid_linvo = false;
        updatedArray[id].q40Init_Dt_linvo = "";
        setQ37LinvoDetails(updatedArray);
        break;
      }

      case "dosing_Freq_Oth_Txt_linvo" + id: {
        let updatedArray = [...Q37LinvoDetails];
        updatedArray[id].q40DosingFreqRd_linvo = 5;
        updatedArray[id].dosing_Freq_Oth_Txt_linvo = value;

        updatedArray[id].Q40LinvoDosingFreqEmptyHard = false;
        updatedArray[id].Q40LinvoDosingFreqTxtEmptyHard = false;
        setQ37LinvoDetails(updatedArray);
        break;
      }

      case "primaryProf_linvo" + id: {
        let updatedArray = [...Q37LinvoDetails];
        updatedArray[id].primaryProf_linvo =
          !updatedArray[id].primaryProf_linvo;

        updatedArray[id].Q40LinvoReasonEmptyHard = false;
        setQ37LinvoDetails(updatedArray);
        break;
      }

      case "secondaryProf_linvo" + id: {
        let updatedArray = [...Q37LinvoDetails];
        updatedArray[id].secondaryProf_linvo =
          !updatedArray[id].secondaryProf_linvo;
        updatedArray[id].Q40LinvoReasonEmptyHard = false;
        setQ37LinvoDetails(updatedArray);
        break;
      }

      case "otherReson_linvo" + id: {
        let updatedArray = [...Q37LinvoDetails];
        updatedArray[id].otherReson_linvo = !updatedArray[id].otherReson_linvo;
        updatedArray[id].Q40LinvoReasonEmptyHard = false;
        setQ37LinvoDetails(updatedArray);
        break;
      }

      //40.C Linvoseltamab
      case "ivigInitRd_linvoC" + id: {
        let updatedArray = [...Q37LinvoDetails];
        updatedArray[id].ivigInitRd_linvoC = Number(value);
        updatedArray[id].ivigInitRdHard_linvoC = false;
        updatedArray[id].ivigInitDtHard_linvoC = false;
        updatedArray[id].ivigInitDtInvalid_linvoC = false;
        updatedArray[id].ivigInitDtBeforeEof_linvoC = false;
        updatedArray[id].ivigInitDtFullChainInvalid_linvoC = false;
        updatedArray[id].ivigInitDt_linvoC = "";

        if (Number(value) === 1) {
          updatedArray[id].ivigDosingType_linvoC = null;
          updatedArray[id].ivigDosingTypeEmptyHard_linvoC = false;

          updatedArray[id].ivigDosingFreqRd_linvoC = null;
          updatedArray[id].ivigDosingFreqTimesTxt_linvoC = "";
          updatedArray[id].ivigDosingFreqWeeksTxt_linvoC = "";
          updatedArray[id].ivigDosingFreqOtherTxt_linvoC = "";
          updatedArray[id].ivigDosingFreqEmptyHard_linvoC = false;
          updatedArray[id].ivigDosingFreqTxtEmptyHard_linvoC = false;
          updatedArray[id].ivigDosingFreqRangeInvalid_linvoC = false;

          updatedArray[id].ivigReasonPrimary_linvoC = false;
          updatedArray[id].ivigReasonSecondary_linvoC = false;
          updatedArray[id].ivigReasonOther_linvoC = false;
          updatedArray[id].ivigReasonOtherTxt_linvoC = "";
          updatedArray[id].ivigReasonUnk_linvoC = false;
          updatedArray[id].ivigReasonEmptyHard_linvoC = false;
          updatedArray[id].ivigReasonOtherTxtEmptyHard_linvoC = false;

          updatedArray[id].ivigDoseStrengthRd_linvoC = null;
          updatedArray[id].ivigDoseStrengthGTxt_linvoC = "";
          updatedArray[id].ivigDoseStrengthMgKgTxt_linvoC = "";
          updatedArray[id].ivigDoseStrengthOtherTxt_linvoC = "";
          updatedArray[id].ivigDoseStrengthEmptyHard_linvoC = false;
          updatedArray[id].ivigDoseStrengthRangeInvalid_linvoC = false;
          updatedArray[id].ivigDoseStrengthMgKgRangeInvalid_linvoC = false;
          updatedArray[id].ivigDoseStrengthOtherTxtEmptyHard_linvoC = false;

          updatedArray[id].ivigLastIgg_linvoC = null;
          updatedArray[id].ivigLastIggEmptyHard_linvoC = false;

          updatedArray[id].ivigBodyWeightRd_linvoC = null;
          updatedArray[id].ivigBodyWeightKgTxt_linvoC = "";
          updatedArray[id].ivigBodyWeightLbsTxt_linvoC = "";
          updatedArray[id].ivigBodyWeightEmptyHard_linvoC = false;
          updatedArray[id].ivigBodyWeightRangeInvalid_linvoC = false;
        }
        setQ37LinvoDetails(updatedArray);
        break;
      }

      case "ivigDosingType_linvoC" + id: {
        let updatedArray = [...Q37LinvoDetails];
        updatedArray[id].ivigDosingType_linvoC = Number(value);
        updatedArray[id].ivigDosingTypeEmptyHard_linvoC = false;

        // Column [H] is only shown for Weight-based dosage; clear it otherwise
        if (Number(value) !== 2) {
          updatedArray[id].ivigBodyWeightRd_linvoC = null;
          updatedArray[id].ivigBodyWeightKgTxt_linvoC = "";
          updatedArray[id].ivigBodyWeightLbsTxt_linvoC = "";
        }
        updatedArray[id].ivigBodyWeightEmptyHard_linvoC = false;
        updatedArray[id].ivigBodyWeightRangeInvalid_linvoC = false;

        updatedArray[id].ivigDoseStrengthRd_linvoC = null;
        updatedArray[id].ivigDoseStrengthGTxt_linvoC = "";
        updatedArray[id].ivigDoseStrengthMgKgTxt_linvoC = "";
        updatedArray[id].ivigDoseStrengthOtherTxt_linvoC = "";
        updatedArray[id].ivigDoseStrengthEmptyHard_linvoC = false;
        updatedArray[id].ivigDoseStrengthRangeInvalid_linvoC = false;
        updatedArray[id].ivigDoseStrengthMgKgRangeInvalid_linvoC = false;
        updatedArray[id].ivigDoseStrengthOtherTxtEmptyHard_linvoC = false;

        setQ37LinvoDetails(updatedArray);
        break;
      }

      case "ivigDosingFreqRd_linvoC" + id: {
        let updatedArray = [...Q37LinvoDetails];
        updatedArray[id].ivigDosingFreqRd_linvoC = Number(value);
        updatedArray[id].ivigDosingFreqTimesTxt_linvoC = "";
        updatedArray[id].ivigDosingFreqWeeksTxt_linvoC = "";
        updatedArray[id].ivigDosingFreqOtherTxt_linvoC = "";
        updatedArray[id].ivigDosingFreqEmptyHard_linvoC = false;
        updatedArray[id].ivigDosingFreqTxtEmptyHard_linvoC = false;
        updatedArray[id].ivigDosingFreqRangeInvalid_linvoC = false;
        setQ37LinvoDetails(updatedArray);
        break;
      }

      case "ivigDosingFreqTimesTxt_linvoC" + id: {
        let updatedArray = [...Q37LinvoDetails];
        updatedArray[id].ivigDosingFreqTimesTxt_linvoC = value;
        updatedArray[id].ivigDosingFreqWeeksTxt_linvoC = "";
        updatedArray[id].ivigDosingFreqOtherTxt_linvoC = "";
        updatedArray[id].ivigDosingFreqRd_linvoC = 1;
        updatedArray[id].ivigDosingFreqEmptyHard_linvoC = false;
        updatedArray[id].ivigDosingFreqTxtEmptyHard_linvoC = false;
        updatedArray[id].ivigDosingFreqRangeInvalid_linvoC = false;
        setQ37LinvoDetails(updatedArray);
        break;
      }

      case "ivigDosingFreqWeeksTxt_linvoC" + id: {
        let updatedArray = [...Q37LinvoDetails];
        updatedArray[id].ivigDosingFreqWeeksTxt_linvoC = value;
        updatedArray[id].ivigDosingFreqTimesTxt_linvoC = "";
        updatedArray[id].ivigDosingFreqOtherTxt_linvoC = "";
        updatedArray[id].ivigDosingFreqRd_linvoC = 2;
        updatedArray[id].ivigDosingFreqEmptyHard_linvoC = false;
        updatedArray[id].ivigDosingFreqTxtEmptyHard_linvoC = false;
        updatedArray[id].ivigDosingFreqRangeInvalid_linvoC = false;
        setQ37LinvoDetails(updatedArray);
        break;
      }

      case "ivigDosingFreqOtherTxt_linvoC" + id: {
        let updatedArray = [...Q37LinvoDetails];
        updatedArray[id].ivigDosingFreqOtherTxt_linvoC = value;
        updatedArray[id].ivigDosingFreqTimesTxt_linvoC = "";
        updatedArray[id].ivigDosingFreqWeeksTxt_linvoC = "";
        updatedArray[id].ivigDosingFreqRd_linvoC = 3;
        updatedArray[id].ivigDosingFreqEmptyHard_linvoC = false;
        updatedArray[id].ivigDosingFreqTxtEmptyHard_linvoC = false;
        setQ37LinvoDetails(updatedArray);
        break;
      }

      case "ivigReasonPrimary_linvoC" + id: {
        let updatedArray = [...Q37LinvoDetails];
        updatedArray[id].ivigReasonPrimary_linvoC =
          !updatedArray[id].ivigReasonPrimary_linvoC;
        if (updatedArray[id].ivigReasonPrimary_linvoC) {
          updatedArray[id].ivigReasonUnk_linvoC = false;
        }
        updatedArray[id].ivigReasonEmptyHard_linvoC = false;
        setQ37LinvoDetails(updatedArray);
        break;
      }

      case "ivigReasonSecondary_linvoC" + id: {
        let updatedArray = [...Q37LinvoDetails];
        updatedArray[id].ivigReasonSecondary_linvoC =
          !updatedArray[id].ivigReasonSecondary_linvoC;
        if (updatedArray[id].ivigReasonSecondary_linvoC) {
          updatedArray[id].ivigReasonUnk_linvoC = false;
        }
        updatedArray[id].ivigReasonEmptyHard_linvoC = false;
        setQ37LinvoDetails(updatedArray);
        break;
      }

      case "ivigReasonOther_linvoC" + id: {
        let updatedArray = [...Q37LinvoDetails];
        updatedArray[id].ivigReasonOther_linvoC =
          !updatedArray[id].ivigReasonOther_linvoC;
        if (updatedArray[id].ivigReasonOther_linvoC) {
          updatedArray[id].ivigReasonUnk_linvoC = false;
        } else {
          updatedArray[id].ivigReasonOtherTxt_linvoC = "";
        }
        updatedArray[id].ivigReasonEmptyHard_linvoC = false;
        updatedArray[id].ivigReasonOtherTxtEmptyHard_linvoC = false;
        setQ37LinvoDetails(updatedArray);
        break;
      }

      case "ivigReasonOtherTxt_linvoC" + id: {
        let updatedArray = [...Q37LinvoDetails];
        updatedArray[id].ivigReasonOtherTxt_linvoC = value;
        updatedArray[id].ivigReasonOther_linvoC = true;
        updatedArray[id].ivigReasonUnk_linvoC = false;
        updatedArray[id].ivigReasonEmptyHard_linvoC = false;
        updatedArray[id].ivigReasonOtherTxtEmptyHard_linvoC = false;
        setQ37LinvoDetails(updatedArray);
        break;
      }

      case "ivigReasonUnk_linvoC" + id: {
        let updatedArray = [...Q37LinvoDetails];
        updatedArray[id].ivigReasonUnk_linvoC =
          !updatedArray[id].ivigReasonUnk_linvoC;
        if (updatedArray[id].ivigReasonUnk_linvoC) {
          updatedArray[id].ivigReasonPrimary_linvoC = false;
          updatedArray[id].ivigReasonSecondary_linvoC = false;
          updatedArray[id].ivigReasonOther_linvoC = false;
          updatedArray[id].ivigReasonOtherTxt_linvoC = "";
          updatedArray[id].ivigReasonOtherTxtEmptyHard_linvoC = false;
        }
        updatedArray[id].ivigReasonEmptyHard_linvoC = false;
        setQ37LinvoDetails(updatedArray);
        break;
      }

      case "ivigDoseStrengthRd_linvoC" + id: {
        let updatedArray = [...Q37LinvoDetails];
        updatedArray[id].ivigDoseStrengthRd_linvoC = Number(value);
        updatedArray[id].ivigDoseStrengthGTxt_linvoC = "";
        updatedArray[id].ivigDoseStrengthMgKgTxt_linvoC = "";
        updatedArray[id].ivigDoseStrengthOtherTxt_linvoC = "";
        updatedArray[id].ivigDoseStrengthEmptyHard_linvoC = false;
        updatedArray[id].ivigDoseStrengthRangeInvalid_linvoC = false;
        updatedArray[id].ivigDoseStrengthMgKgRangeInvalid_linvoC = false;
        updatedArray[id].ivigDoseStrengthOtherTxtEmptyHard_linvoC = false;
        setQ37LinvoDetails(updatedArray);
        break;
      }

      case "ivigDoseStrengthGTxt_linvoC" + id: {
        let updatedArray = [...Q37LinvoDetails];
        updatedArray[id].ivigDoseStrengthGTxt_linvoC = value;
        updatedArray[id].ivigDoseStrengthMgKgTxt_linvoC = "";
        updatedArray[id].ivigDoseStrengthOtherTxt_linvoC = "";
        updatedArray[id].ivigDoseStrengthRd_linvoC = 1;
        updatedArray[id].ivigDoseStrengthEmptyHard_linvoC = false;
        updatedArray[id].ivigDoseStrengthRangeInvalid_linvoC = false;
        updatedArray[id].ivigDoseStrengthMgKgRangeInvalid_linvoC = false;
        updatedArray[id].ivigDoseStrengthOtherTxtEmptyHard_linvoC = false;
        setQ37LinvoDetails(updatedArray);
        break;
      }

      case "ivigDoseStrengthMgKgTxt_linvoC" + id: {
        let updatedArray = [...Q37LinvoDetails];
        updatedArray[id].ivigDoseStrengthMgKgTxt_linvoC = value;
        updatedArray[id].ivigDoseStrengthGTxt_linvoC = "";
        updatedArray[id].ivigDoseStrengthOtherTxt_linvoC = "";
        updatedArray[id].ivigDoseStrengthRd_linvoC = 3;
        updatedArray[id].ivigDoseStrengthEmptyHard_linvoC = false;
        updatedArray[id].ivigDoseStrengthRangeInvalid_linvoC = false;
        updatedArray[id].ivigDoseStrengthMgKgRangeInvalid_linvoC = false;
        updatedArray[id].ivigDoseStrengthOtherTxtEmptyHard_linvoC = false;
        setQ37LinvoDetails(updatedArray);
        break;
      }

      case "ivigDoseStrengthOtherTxt_linvoC" + id: {
        let updatedArray = [...Q37LinvoDetails];
        updatedArray[id].ivigDoseStrengthOtherTxt_linvoC = value;
        updatedArray[id].ivigDoseStrengthGTxt_linvoC = "";
        updatedArray[id].ivigDoseStrengthMgKgTxt_linvoC = "";
        updatedArray[id].ivigDoseStrengthRd_linvoC = 2;
        updatedArray[id].ivigDoseStrengthEmptyHard_linvoC = false;
        updatedArray[id].ivigDoseStrengthRangeInvalid_linvoC = false;
        updatedArray[id].ivigDoseStrengthMgKgRangeInvalid_linvoC = false;
        updatedArray[id].ivigDoseStrengthOtherTxtEmptyHard_linvoC = false;
        setQ37LinvoDetails(updatedArray);
        break;
      }

      case "ivigBodyWeightRd_linvoC" + id: {
        let updatedArray = [...Q37LinvoDetails];
        updatedArray[id].ivigBodyWeightRd_linvoC = Number(value);
        updatedArray[id].ivigBodyWeightKgTxt_linvoC = "";
        updatedArray[id].ivigBodyWeightLbsTxt_linvoC = "";
        updatedArray[id].ivigBodyWeightEmptyHard_linvoC = false;
        updatedArray[id].ivigBodyWeightRangeInvalid_linvoC = false;
        setQ37LinvoDetails(updatedArray);
        break;
      }

      case "ivigBodyWeightKgTxt_linvoC" + id: {
        let updatedArray = [...Q37LinvoDetails];
        updatedArray[id].ivigBodyWeightKgTxt_linvoC = value;
        updatedArray[id].ivigBodyWeightLbsTxt_linvoC = "";
        updatedArray[id].ivigBodyWeightRd_linvoC = 1;
        updatedArray[id].ivigBodyWeightEmptyHard_linvoC = false;
        updatedArray[id].ivigBodyWeightRangeInvalid_linvoC = false;
        setQ37LinvoDetails(updatedArray);
        break;
      }

      case "ivigBodyWeightLbsTxt_linvoC" + id: {
        let updatedArray = [...Q37LinvoDetails];
        updatedArray[id].ivigBodyWeightLbsTxt_linvoC = value;
        updatedArray[id].ivigBodyWeightKgTxt_linvoC = "";
        updatedArray[id].ivigBodyWeightRd_linvoC = 2;
        updatedArray[id].ivigBodyWeightEmptyHard_linvoC = false;
        updatedArray[id].ivigBodyWeightRangeInvalid_linvoC = false;
        setQ37LinvoDetails(updatedArray);
        break;
      }

      case "ivigLastIgg_linvoC" + id: {
        let updatedArray = [...Q37LinvoDetails];
        updatedArray[id].ivigLastIgg_linvoC = Number(value);
        updatedArray[id].ivigLastIggEmptyHard_linvoC = false;
        setQ37LinvoDetails(updatedArray);
        break;
      }

      //39- 1.1
      case "cytokin_elr" + id: {
        let updatedArray = [...Q37ElrexDetails];
        updatedArray[id].cytokin_elr = !updatedArray[id].cytokin_elr;

        updatedArray[id].tmt_elr_unknown = false;

        updatedArray[id].Q39elrReasonHard = false;
        setQ37ElrexDetails(updatedArray);
        break;
      }

      case "secondary_prophylactic_crs_elr" + id: {
        let updatedArray = [...Q37ElrexDetails];
        updatedArray[id].secondary_prophylactic_crs_elr =
          !updatedArray[id].secondary_prophylactic_crs_elr;
        updatedArray[id].tmt_elr_unknown = false;
        updatedArray[id].Q39elrReasonHard = false;
        setQ37ElrexDetails(updatedArray);
        break;
      }

      case "crs_elr" + id: {
        let updatedArray = [...Q37ElrexDetails];
        updatedArray[id].crs_elr = !updatedArray[id].crs_elr;
        updatedArray[id].Q39elrReasonHard = false;
        updatedArray[id].tmt_elr_unknown = false;

        setQ37ElrexDetails(updatedArray);
        break;
      }

      case "neurotoxicity_elr" + id: {
        let updatedArray = [...Q37ElrexDetails];
        updatedArray[id].neurotoxicity_elr =
          !updatedArray[id].neurotoxicity_elr;
        updatedArray[id].Q39elrReasonHard = false;
        updatedArray[id].tmt_elr_unknown = false;

        setQ37ElrexDetails(updatedArray);
        break;
      }

      case "secondary_neurotoxicity_elr" + id: {
        let updatedArray = [...Q37ElrexDetails];
        updatedArray[id].secondary_neurotoxicity_elr =
          !updatedArray[id].secondary_neurotoxicity_elr;
        updatedArray[id].Q39elrReasonHard = false;
        updatedArray[id].tmt_elr_unknown = false;
        setQ37ElrexDetails(updatedArray);
        break;
      }

      case "tmt_neurotoxicity_elr" + id: {
        let updatedArray = [...Q37ElrexDetails];
        updatedArray[id].tmt_neurotoxicity_elr =
          !updatedArray[id].tmt_neurotoxicity_elr;
        updatedArray[id].Q39elrReasonHard = false;
        updatedArray[id].tmt_elr_unknown = false;
        setQ37ElrexDetails(updatedArray);
        break;
      }

      case "tmt_elr_unknown" + id: {
        let updatedArray = [...Q37ElrexDetails];
        updatedArray[id].tmt_elr_unknown = !updatedArray[id].tmt_elr_unknown;
        updatedArray[id].Q39elrReasonHard = false;

        updatedArray[id].crs_elr = false;
        updatedArray[id].cytokin_elr = false;
        updatedArray[id].secondary_neurotoxicity_elr = false;
        updatedArray[id].secondary_prophylactic_crs_elr = false;
        updatedArray[id].tmt_neurotoxicity_elr = false;
        updatedArray[id].neurotoxicity_elr = false;
        setQ37ElrexDetails(updatedArray);
        break;
      }

      //39.3

      case "prophylactic_use_elr" + id: {
        let updatedArray = [...Q37ElrexDetails];
        updatedArray[id].prophylactic_use_elr =
          !updatedArray[id].prophylactic_use_elr;

        updatedArray[id].Q39elrGranduloEmptyHard = false;
        updatedArray[id].unknown_elr = null;
        setQ37ElrexDetails(updatedArray);
        break;
      }

      case "secondary_prophylactic_use_elr" + id: {
        let updatedArray = [...Q37ElrexDetails];
        updatedArray[id].secondary_prophylactic_use_elr =
          !updatedArray[id].secondary_prophylactic_use_elr;

        updatedArray[id].Q39elrGranduloEmptyHard = false;
        updatedArray[id].unknown_elr = null;
        setQ37ElrexDetails(updatedArray);
        break;
      }

      case "oth_use_elr" + id: {
        let updatedArray = [...Q37ElrexDetails];
        updatedArray[id].oth_use_elr = !updatedArray[id].oth_use_elr;

        updatedArray[id].Q39elrGranduloEmptyHard = false;
        updatedArray[id].unknown_elr = null;
        setQ37ElrexDetails(updatedArray);
        break;
      }

      case "unknown_elr" + id: {
        let updatedArray = [...Q37ElrexDetails];
        updatedArray[id].unknown_elr = Number(value);
        updatedArray[id].oth_use_elr = false;
        updatedArray[id].prophylactic_use_elr = false;
        updatedArray[id].secondary_prophylactic_use_elr = false;
        updatedArray[id].Q39elrGranduloEmptyHard = false;
        setQ37ElrexDetails(updatedArray);
        break;
      }

      //39.3.C Elrexfio
      case "prophylactic_use_elrC" + id: {
        let updatedArray = [...Q37ElrexDetails];
        updatedArray[id].prophylactic_use_elrC =
          !updatedArray[id].prophylactic_use_elrC;

        updatedArray[id].Q393CReasonEmptyHard = false;
        updatedArray[id].unknown_elrC = null;
        setQ37ElrexDetails(updatedArray);
        break;
      }

      case "secondary_prophylactic_use_elrC" + id: {
        let updatedArray = [...Q37ElrexDetails];
        updatedArray[id].secondary_prophylactic_use_elrC =
          !updatedArray[id].secondary_prophylactic_use_elrC;

        updatedArray[id].Q393CReasonEmptyHard = false;
        updatedArray[id].unknown_elrC = null;
        setQ37ElrexDetails(updatedArray);
        break;
      }

      case "oth_use_elrC" + id: {
        let updatedArray = [...Q37ElrexDetails];
        updatedArray[id].oth_use_elrC = !updatedArray[id].oth_use_elrC;

        updatedArray[id].Q393CReasonEmptyHard = false;
        updatedArray[id].unknown_elrC = null;
        setQ37ElrexDetails(updatedArray);
        break;
      }

      case "unknown_elrC" + id: {
        let updatedArray = [...Q37ElrexDetails];
        updatedArray[id].unknown_elrC = Number(value);
        updatedArray[id].oth_use_elrC = false;
        updatedArray[id].prophylactic_use_elrC = false;
        updatedArray[id].secondary_prophylactic_use_elrC = false;
        updatedArray[id].Q393CReasonEmptyHard = false;
        setQ37ElrexDetails(updatedArray);
        break;
      }

      // Q39.1 retired - all Q39.1 field-change handler cases commented out below
      /*
      //39.1

      case "cytokin_linvo" + id: {
        let updatedArray = [...Q37LinvoDetails];
        updatedArray[id].cytokin_linvo = !updatedArray[id].cytokin_linvo;

        updatedArray[id].Q39linvoReasonHard = false;
        updatedArray[id].tmt_linvoseltamab_unknown = false;

        setQ37LinvoDetails(updatedArray);
        break;
      }

      case "secondary_prophylactic_crs_linvo" + id: {
        let updatedArray = [...Q37LinvoDetails];
        updatedArray[id].secondary_prophylactic_crs_linvo =
          !updatedArray[id].secondary_prophylactic_crs_linvo;

        updatedArray[id].Q39linvoReasonHard = false;
        updatedArray[id].tmt_linvoseltamab_unknown = false;

        setQ37LinvoDetails(updatedArray);
        break;
      }

      case "crs_linvo" + id: {
        let updatedArray = [...Q37LinvoDetails];
        updatedArray[id].crs_linvo = !updatedArray[id].crs_linvo;

        updatedArray[id].tmt_linvoseltamab_unknown = false;

        updatedArray[id].Q39linvoReasonHard = false;
        setQ37LinvoDetails(updatedArray);
        break;
      }

      case "neurotoxicity_linvo" + id: {
        let updatedArray = [...Q37LinvoDetails];
        updatedArray[id].neurotoxicity_linvo =
          !updatedArray[id].neurotoxicity_linvo;
        updatedArray[id].Q39linvoReasonHard = false;
        updatedArray[id].tmt_linvoseltamab_unknown = false;

        setQ37LinvoDetails(updatedArray);
        break;
      }

      case "secondary_neurotoxicity_linvo" + id: {
        let updatedArray = [...Q37LinvoDetails];
        updatedArray[id].secondary_neurotoxicity_linvo =
          !updatedArray[id].secondary_neurotoxicity_linvo;
        updatedArray[id].Q39linvoReasonHard = false;
        updatedArray[id].tmt_linvoseltamab_unknown = false;

        setQ37LinvoDetails(updatedArray);
        break;
      }

      case "tmt_neurotoxicity_linvo" + id: {
        let updatedArray = [...Q37LinvoDetails];
        updatedArray[id].tmt_neurotoxicity_linvo =
          !updatedArray[id].tmt_neurotoxicity_linvo;
        updatedArray[id].Q39linvoReasonHard = false;
        updatedArray[id].tmt_linvoseltamab_unknown = false;
        setQ37LinvoDetails(updatedArray);
        break;
      }

      case "tmt_linvoseltamab_unknown" + id: {
        let updatedArray = [...Q37LinvoDetails];
        updatedArray[id].tmt_linvoseltamab_unknown =
          !updatedArray[id].tmt_linvoseltamab_unknown;
        updatedArray[id].Q39linvoReasonHard = false;

        updatedArray[id].tmt_neurotoxicity_linvo = false;
        updatedArray[id].cytokin_linvo = false;
        updatedArray[id].secondary_prophylactic_crs_linvo = false;
        updatedArray[id].secondary_neurotoxicity_linvo = false;

        updatedArray[id].neurotoxicity_linvo = false;
        updatedArray[id].crs_linvo = false;
        setQ37LinvoDetails(updatedArray);
        break;
      }
      */

      //Q39.3
      case "prophylactic_use_linvo" + id: {
        const updatedArray = [...Q37LinvoDetails];

        updatedArray[id] = {
          ...updatedArray[id],
          prophylactic_use_linvo: !Boolean(
            updatedArray[id].prophylactic_use_linvo,
          ),
          Q39linvoGranduloEmptyHard: false,
          unknown_linvo: null,
        };

        setQ37LinvoDetails(updatedArray);
        break;
      }

      case "secondary_prophylactic_use_linvo" + id: {
        const updatedArray = [...Q37LinvoDetails];

        updatedArray[id] = {
          ...updatedArray[id],
          secondary_prophylactic_use_linvo: !Boolean(
            updatedArray[id].secondary_prophylactic_use_linvo,
          ),
          Q39linvoGranduloEmptyHard: false,
          unknown_linvo: null,
        };

        setQ37LinvoDetails(updatedArray);
        break;
      }

      case "oth_use_linvo" + id: {
        const updatedArray = [...Q37LinvoDetails];

        updatedArray[id] = {
          ...updatedArray[id],
          oth_use_linvo: !Boolean(updatedArray[id].oth_use_linvo),
          Q39linvoGranduloEmptyHard: false,
          unknown_linvo: null,
        };

        setQ37LinvoDetails(updatedArray);
        break;
      }

      case "unknown_linvo" + id: {
        const updatedArray = [...Q37LinvoDetails];

        updatedArray[id] = {
          ...updatedArray[id],
          unknown_linvo: Number(value),
          oth_use_linvo: false,
          prophylactic_use_linvo: false,
          secondary_prophylactic_use_linvo: false,
          Q39linvoGranduloEmptyHard: false,
        };

        setQ37LinvoDetails(updatedArray);
        break;
      }

      //39.3.C Linvoseltamab
      case "prophylactic_use_linvoC" + id: {
        const updatedArray = [...Q37LinvoDetails];

        updatedArray[id] = {
          ...updatedArray[id],
          prophylactic_use_linvoC: !Boolean(
            updatedArray[id].prophylactic_use_linvoC,
          ),
          Q393CReasonEmptyHard: false,
          unknown_linvoC: null,
        };

        setQ37LinvoDetails(updatedArray);
        break;
      }

      case "secondary_prophylactic_use_linvoC" + id: {
        const updatedArray = [...Q37LinvoDetails];

        updatedArray[id] = {
          ...updatedArray[id],
          secondary_prophylactic_use_linvoC: !Boolean(
            updatedArray[id].secondary_prophylactic_use_linvoC,
          ),
          Q393CReasonEmptyHard: false,
          unknown_linvoC: null,
        };

        setQ37LinvoDetails(updatedArray);
        break;
      }

      case "oth_use_linvoC" + id: {
        const updatedArray = [...Q37LinvoDetails];

        updatedArray[id] = {
          ...updatedArray[id],
          oth_use_linvoC: !Boolean(updatedArray[id].oth_use_linvoC),
          Q393CReasonEmptyHard: false,
          unknown_linvoC: null,
        };

        setQ37LinvoDetails(updatedArray);
        break;
      }

      case "unknown_linvoC" + id: {
        const updatedArray = [...Q37LinvoDetails];

        updatedArray[id] = {
          ...updatedArray[id],
          unknown_linvoC: Number(value),
          oth_use_linvoC: false,
          prophylactic_use_linvoC: false,
          secondary_prophylactic_use_linvoC: false,
          Q393CReasonEmptyHard: false,
        };

        setQ37LinvoDetails(updatedArray);
        break;
      }

      //39.2 Linvoseltamab
      case "cytokin_linvo_steroids" + id: {
        let updatedArray = [...Q37LinvoDetails];
        updatedArray[id].cytokin_linvo_steroids =
          !updatedArray[id].cytokin_linvo_steroids;

        updatedArray[id].Q39linvoSteroidsEmptyHard = false;
        updatedArray[id].tmt_linvo_unknown = false;
        setQ37LinvoDetails(updatedArray);
        break;
      }

      case "secondary_prophylactic_linvo_steroids" + id: {
        let updatedArray = [...Q37LinvoDetails];
        updatedArray[id].secondary_prophylactic_linvo_steroids =
          !updatedArray[id].secondary_prophylactic_linvo_steroids;

        updatedArray[id].Q39linvoSteroidsEmptyHard = false;
        updatedArray[id].tmt_linvo_unknown = false;
        setQ37LinvoDetails(updatedArray);
        break;
      }

      case "crs_linvo_steroids" + id: {
        let updatedArray = [...Q37LinvoDetails];
        updatedArray[id].crs_linvo_steroids =
          !updatedArray[id].crs_linvo_steroids;
        updatedArray[id].Q39linvoSteroidsEmptyHard = false;
        updatedArray[id].tmt_linvo_unknown = false;
        setQ37LinvoDetails(updatedArray);
        break;
      }

      case "neurotoxicity_linvo_steroids" + id: {
        let updatedArray = [...Q37LinvoDetails];
        updatedArray[id].neurotoxicity_linvo_steroids =
          !updatedArray[id].neurotoxicity_linvo_steroids;
        updatedArray[id].Q39linvoSteroidsEmptyHard = false;
        updatedArray[id].tmt_linvo_unknown = false;
        setQ37LinvoDetails(updatedArray);
        break;
      }

      case "secondary_neurotoxicity_linvo_steroids" + id: {
        let updatedArray = [...Q37LinvoDetails];
        updatedArray[id].secondary_neurotoxicity_linvo_steroids =
          !updatedArray[id].secondary_neurotoxicity_linvo_steroids;
        updatedArray[id].Q39linvoSteroidsEmptyHard = false;
        updatedArray[id].tmt_linvo_unknown = false;
        setQ37LinvoDetails(updatedArray);
        break;
      }

      case "tmt_neurotoxicity_linvo_steroids" + id: {
        let updatedArray = [...Q37LinvoDetails];
        updatedArray[id].tmt_neurotoxicity_linvo_steroids =
          !updatedArray[id].tmt_neurotoxicity_linvo_steroids;
        updatedArray[id].Q39linvoSteroidsEmptyHard = false;
        updatedArray[id].tmt_linvo_unknown = false;
        setQ37LinvoDetails(updatedArray);
        break;
      }

      case "tmt_linvo_unknown" + id: {
        let updatedArray = [...Q37LinvoDetails];
        updatedArray[id].tmt_linvo_unknown =
          !updatedArray[id].tmt_linvo_unknown;

        updatedArray[id].cytokin_linvo_steroids = false;
        updatedArray[id].secondary_prophylactic_linvo_steroids = false;
        updatedArray[id].secondary_neurotoxicity_linvo_steroids = false;

        updatedArray[id].crs_linvo_steroids = false;

        updatedArray[id].neurotoxicity_linvo_steroids = false;

        updatedArray[id].tmt_neurotoxicity_linvo_steroids = false;
        updatedArray[id].steroid_pre_tmt_linvo = false;

        updatedArray[id].Q39linvoSteroidsEmptyHard = false;
        setQ37LinvoDetails(updatedArray);
        break;
      }

      case "steroid_pre_tmt_linvo" + id: {
        let updatedArray = [...Q37LinvoDetails];
        updatedArray[id].steroid_pre_tmt_linvo =
          !updatedArray[id].steroid_pre_tmt_linvo;

        updatedArray[id].Q39linvoSteroidsEmptyHard = false;
        updatedArray[id].tmt_linvo_unknown = false;
        setQ37LinvoDetails(updatedArray);
        break;
      }

      case "betamethasone_linvo" + id: {
        let updatedArray = [...Q37LinvoDetails];

        updatedArray[id].betamethasone_linvo =
          !updatedArray[id].betamethasone_linvo;

        updatedArray[id].betamethasone_linvoNum = "";
        updatedArray[id].name_dose_steroids_linvoHardEmpty = false;
        updatedArray[id].dose_name_linvo_unknown = false;

        updatedArray[id].steroids_linvo_txtEmpty = false;
        setQ37LinvoDetails(updatedArray);
        break;
      }

      case "betamethasone_linvoNum" + id: {
        let updatedArray = [...Q37LinvoDetails];

        updatedArray[id].betamethasone_linvoNum = value;

        updatedArray[id].betamethasone_linvo = true;

        updatedArray[id].name_dose_steroids_linvoHardEmpty = false;
        updatedArray[id].steroids_linvo_txtEmpty = false;
        updatedArray[id].dose_name_linvo_unknown = false;

        setQ37LinvoDetails(updatedArray);
        break;
      }

      case "dexamethasone_linvo" + id: {
        let updatedArray = [...Q37LinvoDetails];

        updatedArray[id].dexamethasone_linvo =
          !updatedArray[id].dexamethasone_linvo;

        updatedArray[id].dexamethasone_linvoNum = "";
        updatedArray[id].name_dose_steroids_linvoHardEmpty = false;
        updatedArray[id].steroids_linvo_txtEmpty = false;

        updatedArray[id].dose_name_linvo_unknown = false;

        setQ37LinvoDetails(updatedArray);
        break;
      }

      case "dexamethasone_linvoNum" + id: {
        let updatedArray = [...Q37LinvoDetails];

        updatedArray[id].dexamethasone_linvoNum = value;

        updatedArray[id].dexamethasone_linvo = true;

        updatedArray[id].name_dose_steroids_linvoHardEmpty = false;
        updatedArray[id].steroids_linvo_txtEmpty = false;
        updatedArray[id].dose_name_linvo_unknown = false;

        setQ37LinvoDetails(updatedArray);
        break;
      }

      case "hydrocortisone_linvo" + id: {
        let updatedArray = [...Q37LinvoDetails];

        updatedArray[id].hydrocortisone_linvo =
          !updatedArray[id].hydrocortisone_linvo;

        updatedArray[id].hydrocortisone_linvoNum = "";

        updatedArray[id].name_dose_steroids_linvoHardEmpty = false;
        updatedArray[id].steroids_linvo_txtEmpty = false;
        updatedArray[id].dose_name_linvo_unknown = false;

        setQ37LinvoDetails(updatedArray);
        break;
      }

      case "hydrocortisone_linvoNum" + id: {
        let updatedArray = [...Q37LinvoDetails];

        updatedArray[id].hydrocortisone_linvoNum = value;

        updatedArray[id].hydrocortisone_linvo = true;

        updatedArray[id].name_dose_steroids_linvoHardEmpty = false;
        updatedArray[id].steroids_linvo_txtEmpty = false;
        updatedArray[id].dose_name_linvo_unknown = false;
        setQ37LinvoDetails(updatedArray);
        break;
      }

      //

      case "methylprednisolone_linvo" + id: {
        let updatedArray = [...Q37LinvoDetails];

        updatedArray[id].methylprednisolone_linvo =
          !updatedArray[id].methylprednisolone_linvo;

        updatedArray[id].methylprednisolone_linvoNum = "";

        updatedArray[id].name_dose_steroids_linvoHardEmpty = false;
        updatedArray[id].steroids_linvo_txtEmpty = false;
        updatedArray[id].dose_name_linvo_unknown = false;
        setQ37LinvoDetails(updatedArray);
        break;
      }

      case "methylprednisolone_linvoNum" + id: {
        let updatedArray = [...Q37LinvoDetails];

        updatedArray[id].methylprednisolone_linvoNum = value;

        updatedArray[id].methylprednisolone_linvo = true;

        updatedArray[id].name_dose_steroids_linvoHardEmpty = false;
        updatedArray[id].steroids_linvo_txtEmpty = false;
        updatedArray[id].dose_name_linvo_unknown = false;

        setQ37LinvoDetails(updatedArray);
        break;
      }

      case "prednisolone_linvo" + id: {
        let updatedArray = [...Q37LinvoDetails];

        updatedArray[id].prednisolone_linvo =
          !updatedArray[id].prednisolone_linvo;

        updatedArray[id].prednisolone_linvoNum = "";

        updatedArray[id].name_dose_steroids_linvoHardEmpty = false;
        updatedArray[id].steroids_linvo_txtEmpty = false;
        updatedArray[id].dose_name_linvo_unknown = false;

        setQ37LinvoDetails(updatedArray);
        break;
      }

      case "prednisolone_linvoNum" + id: {
        let updatedArray = [...Q37LinvoDetails];

        updatedArray[id].prednisolone_linvoNum = value;

        updatedArray[id].prednisolone_linvo = true;

        updatedArray[id].name_dose_steroids_linvoHardEmpty = false;
        updatedArray[id].steroids_linvo_txtEmpty = false;
        updatedArray[id].dose_name_linvo_unknown = false;

        setQ37LinvoDetails(updatedArray);
        break;
      }

      case "prednisone_linvo" + id: {
        let updatedArray = [...Q37LinvoDetails];

        updatedArray[id].prednisone_linvo = !updatedArray[id].prednisone_linvo;

        updatedArray[id].prednisone_linvoNum = "";

        updatedArray[id].name_dose_steroids_linvoHardEmpty = false;
        updatedArray[id].steroids_linvo_txtEmpty = false;
        updatedArray[id].dose_name_linvo_unknown = false;

        setQ37LinvoDetails(updatedArray);
        break;
      }

      case "prednisone_linvoNum" + id: {
        let updatedArray = [...Q37LinvoDetails];

        updatedArray[id].prednisone_linvoNum = value;

        updatedArray[id].prednisone_linvo = true;

        updatedArray[id].name_dose_steroids_linvoHardEmpty = false;
        updatedArray[id].steroids_linvo_txtEmpty = false;
        updatedArray[id].dose_name_linvo_unknown = false;

        setQ37LinvoDetails(updatedArray);
        break;
      }

      case "triamcinolone_linvo" + id: {
        let updatedArray = [...Q37LinvoDetails];

        updatedArray[id].triamcinolone_linvo =
          !updatedArray[id].triamcinolone_linvo;

        updatedArray[id].triamcinolone_linvoNum = "";

        updatedArray[id].name_dose_steroids_linvoHardEmpty = false;
        updatedArray[id].steroids_linvo_txtEmpty = false;
        updatedArray[id].dose_name_linvo_unknown = false;

        setQ37LinvoDetails(updatedArray);
        break;
      }

      case "triamcinolone_linvoNum" + id: {
        let updatedArray = [...Q37LinvoDetails];

        updatedArray[id].triamcinolone_linvoNum = value;

        updatedArray[id].triamcinolone_linvo = true;

        updatedArray[id].name_dose_steroids_linvoHardEmpty = false;
        updatedArray[id].steroids_linvo_txtEmpty = false;
        updatedArray[id].dose_name_linvo_unknown = false;

        setQ37LinvoDetails(updatedArray);
        break;
      }

      case "dose_name_linvo_unknown" + id: {
        let updatedArray = [...Q37LinvoDetails];
        updatedArray[id].dose_name_linvo_unknown =
          !updatedArray[id].dose_name_linvo_unknown;

        updatedArray[id].name_dose_steroids_linvoHardEmpty = false;

        updatedArray[id].steroids_linvo_txtEmpty = false;

        updatedArray[id].betamethasone_linvo = false;
        updatedArray[id].betamethasone_linvoNum = "";

        updatedArray[id].dexamethasone_linvo = false;
        updatedArray[id].dexamethasone_linvoNum = "";

        updatedArray[id].hydrocortisone_linvo = false;
        updatedArray[id].hydrocortisone_linvoNum = "";

        updatedArray[id].methylprednisolone_linvo = false;
        updatedArray[id].methylprednisolone_linvoNum = "";

        updatedArray[id].prednisolone_linvo = false;
        updatedArray[id].prednisolone_linvoNum = "";

        updatedArray[id].prednisone_linvo = false;
        updatedArray[id].prednisone_linvoNum = "";

        updatedArray[id].triamcinolone_linvo = false;
        updatedArray[id].triamcinolone_linvoNum = "";

        setQ37LinvoDetails(updatedArray);
        break;
      }

      // New phase
      case "steroid_pre_tmt_elr" + id: {
        let updatedArray = [...Q37ElrexDetails];
        updatedArray[id].steroid_pre_tmt_elr =
          !updatedArray[id].steroid_pre_tmt_elr;

        updatedArray[id].Q39elrSteroidsEmptyHard = false;
        updatedArray[id].tmt_steroids_elr_unknown = false;
        setQ37ElrexDetails(updatedArray);
        break;
      }

      //New elr column handlechange

      case "betamethasone_elr" + id: {
        let updatedArray = [...Q37ElrexDetails];

        updatedArray[id].betamethasone_elr =
          !updatedArray[id].betamethasone_elr;

        updatedArray[id].betamethasone_elrNum = "";
        updatedArray[id].name_dose_steroidsHardEmpty = false;
        updatedArray[id].dose_name_elr_unknown = false;
        updatedArray[id].betamethasone_elr_txtEmpty = false;
        updatedArray[id].steroids_elr_txtEmpty = false;
        setQ37ElrexDetails(updatedArray);
        break;
      }

      case "betamethasone_elrNum" + id: {
        let updatedArray = [...Q37ElrexDetails];

        updatedArray[id].betamethasone_elrNum = value;

        updatedArray[id].betamethasone_elr = true;

        updatedArray[id].name_dose_steroidsHardEmpty = false;
        updatedArray[id].steroids_elr_txtEmpty = false;
        updatedArray[id].dose_name_elr_unknown = false;
        updatedArray[id].betamethasone_elr_txtEmpty = false;

        setQ37ElrexDetails(updatedArray);
        break;
      }

      case "dexamethasone_elr" + id: {
        let updatedArray = [...Q37ElrexDetails];

        updatedArray[id].dexamethasone_elr =
          !updatedArray[id].dexamethasone_elr;

        updatedArray[id].dexamethasone_elrNum = "";
        updatedArray[id].name_dose_steroidsHardEmpty = false;
        updatedArray[id].steroids_elr_txtEmpty = false;
        updatedArray[id].steroids_elr_txtEmpty = false;
        updatedArray[id].dose_name_elr_unknown = false;
        updatedArray[id].dexamethasone_elr_txtEmpty = false;

        setQ37ElrexDetails(updatedArray);
        break;
      }

      case "dexamethasone_elrNum" + id: {
        let updatedArray = [...Q37ElrexDetails];

        updatedArray[id].dexamethasone_elrNum = value;

        updatedArray[id].dexamethasone_elr = true;

        updatedArray[id].name_dose_steroidsHardEmpty = false;
        updatedArray[id].steroids_elr_txtEmpty = false;
        updatedArray[id].dose_name_elr_unknown = false;
        updatedArray[id].dexamethasone_elr_txtEmpty = false;
        setQ37ElrexDetails(updatedArray);
        break;
      }

      case "hydrocortisone_elr" + id: {
        let updatedArray = [...Q37ElrexDetails];

        updatedArray[id].hydrocortisone_elr =
          !updatedArray[id].hydrocortisone_elr;

        updatedArray[id].hydrocortisone_elrNum = "";

        updatedArray[id].name_dose_steroidsHardEmpty = false;
        updatedArray[id].steroids_elr_txtEmpty = false;
        updatedArray[id].dose_name_elr_unknown = false;
        updatedArray[id].hydrocortisone_elr_txtEmpty = false;
        setQ37ElrexDetails(updatedArray);
        break;
      }

      case "hydrocortisone_elrNum" + id: {
        let updatedArray = [...Q37ElrexDetails];

        updatedArray[id].hydrocortisone_elrNum = value;

        updatedArray[id].hydrocortisone_elr = true;

        updatedArray[id].name_dose_steroidsHardEmpty = false;
        updatedArray[id].steroids_elr_txtEmpty = false;
        updatedArray[id].dose_name_elr_unknown = false;
        updatedArray[id].hydrocortisone_elr_txtEmpty = false;
        setQ37ElrexDetails(updatedArray);
        break;
      }

      //

      case "methylprednisolone" + id: {
        let updatedArray = [...Q37ElrexDetails];

        updatedArray[id].methylprednisolone =
          !updatedArray[id].methylprednisolone;

        updatedArray[id].methylprednisoloneNum = "";

        updatedArray[id].name_dose_steroidsHardEmpty = false;
        updatedArray[id].steroids_elr_txtEmpty = false;
        updatedArray[id].dose_name_elr_unknown = false;
        updatedArray[id].methylprednisolone_txtEmpty = false;
        setQ37ElrexDetails(updatedArray);
        break;
      }

      case "methylprednisoloneNum" + id: {
        let updatedArray = [...Q37ElrexDetails];

        updatedArray[id].methylprednisoloneNum = value;

        updatedArray[id].methylprednisolone = true;

        updatedArray[id].name_dose_steroidsHardEmpty = false;
        updatedArray[id].steroids_elr_txtEmpty = false;
        updatedArray[id].dose_name_elr_unknown = false;
        updatedArray[id].methylprednisolone_txtEmpty = false;
        setQ37ElrexDetails(updatedArray);
        break;
      }

      case "prednisolone_elr" + id: {
        let updatedArray = [...Q37ElrexDetails];

        updatedArray[id].prednisolone_elr = !updatedArray[id].prednisolone_elr;

        updatedArray[id].prednisolone_elrNum = "";

        updatedArray[id].name_dose_steroidsHardEmpty = false;
        updatedArray[id].steroids_elr_txtEmpty = false;
        updatedArray[id].dose_name_elr_unknown = false;
        updatedArray[id].prednisolone_elr_txtEmpty = false;
        setQ37ElrexDetails(updatedArray);
        break;
      }

      case "prednisolone_elrNum" + id: {
        let updatedArray = [...Q37ElrexDetails];

        updatedArray[id].prednisolone_elrNum = value;

        updatedArray[id].prednisolone_elr = true;

        updatedArray[id].name_dose_steroidsHardEmpty = false;
        updatedArray[id].steroids_elr_txtEmpty = false;
        updatedArray[id].dose_name_elr_unknown = false;
        updatedArray[id].prednisolone_elr_txtEmpty = false;
        setQ37ElrexDetails(updatedArray);
        break;
      }

      case "prednisone_elr" + id: {
        let updatedArray = [...Q37ElrexDetails];

        updatedArray[id].prednisone_elr = !updatedArray[id].prednisone_elr;

        updatedArray[id].prednisone_elrNum = "";

        updatedArray[id].name_dose_steroidsHardEmpty = false;
        updatedArray[id].steroids_elr_txtEmpty = false;
        updatedArray[id].dose_name_elr_unknown = false;
        updatedArray[id].prednisone_elr_txtEmpty = false;
        setQ37ElrexDetails(updatedArray);
        break;
      }

      case "prednisone_elrNum" + id: {
        let updatedArray = [...Q37ElrexDetails];

        updatedArray[id].prednisone_elrNum = value;

        updatedArray[id].prednisone_elr = true;

        updatedArray[id].name_dose_steroidsHardEmpty = false;
        updatedArray[id].steroids_elr_txtEmpty = false;
        updatedArray[id].dose_name_elr_unknown = false;
        updatedArray[id].prednisone_elr_txtEmpty = false;
        setQ37ElrexDetails(updatedArray);
        break;
      }

      case "triamcinolone_elr" + id: {
        let updatedArray = [...Q37ElrexDetails];

        updatedArray[id].triamcinolone_elr =
          !updatedArray[id].triamcinolone_elr;

        updatedArray[id].triamcinolone_elrNum = "";

        updatedArray[id].name_dose_steroidsHardEmpty = false;
        updatedArray[id].steroids_elr_txtEmpty = false;
        updatedArray[id].dose_name_elr_unknown = false;
        updatedArray[id].triamcinolone_elr_txtEmpty = false;
        setQ37ElrexDetails(updatedArray);
        break;
      }

      case "triamcinolone_elrNum" + id: {
        let updatedArray = [...Q37ElrexDetails];

        updatedArray[id].triamcinolone_elrNum = value;

        updatedArray[id].triamcinolone_elr = true;

        updatedArray[id].name_dose_steroidsHardEmpty = false;
        updatedArray[id].steroids_elr_txtEmpty = false;
        updatedArray[id].dose_name_elr_unknown = false;
        updatedArray[id].triamcinolone_elr_txtEmpty = false;
        setQ37ElrexDetails(updatedArray);
        break;
      }

      case "dose_name_elr_unknown" + id: {
        let updatedArray = [...Q37ElrexDetails];
        updatedArray[id].dose_name_elr_unknown =
          !updatedArray[id].dose_name_elr_unknown;

        updatedArray[id].name_dose_steroidsHardEmpty = false;
        updatedArray[id].steroids_elr_txtEmpty = false;
        updatedArray[id].betamethasone_elr_txtEmpty = false;
        updatedArray[id].dexamethasone_elr_txtEmpty = false;
        updatedArray[id].hydrocortisone_elr_txtEmpty = false;
        updatedArray[id].methylprednisolone_txtEmpty = false;
        updatedArray[id].prednisolone_elr_txtEmpty = false;
        updatedArray[id].prednisone_elr_txtEmpty = false;
        updatedArray[id].triamcinolone_elr_txtEmpty = false;

        updatedArray[id].betamethasone_elr = false;
        updatedArray[id].betamethasone_elrNum = "";

        updatedArray[id].dexamethasone_elr = false;
        updatedArray[id].dexamethasone_elrNum = "";

        updatedArray[id].hydrocortisone_elr = false;
        updatedArray[id].hydrocortisone_elrNum = "";

        updatedArray[id].methylprednisolone = false;
        updatedArray[id].methylprednisoloneNum = "";

        updatedArray[id].prednisolone_elr = false;
        updatedArray[id].prednisolone_elrNum = "";

        updatedArray[id].prednisone_elr = false;
        updatedArray[id].prednisone_elrNum = "";

        updatedArray[id].triamcinolone_elr = false;
        updatedArray[id].triamcinolone_elrNum = "";

        setQ37ElrexDetails(updatedArray);
        break;
      }

      case "cytokin_elr_steroids" + id: {
        let updatedArray = [...Q37ElrexDetails];
        updatedArray[id].cytokin_elr_steroids =
          !updatedArray[id].cytokin_elr_steroids;

        updatedArray[id].Q39elrSteroidsEmptyHard = false;
        updatedArray[id].tmt_steroids_elr_unknown = false;
        setQ37ElrexDetails(updatedArray);
        break;
      }

      case "secondary_prophylactic_elr_steroids" + id: {
        let updatedArray = [...Q37ElrexDetails];
        updatedArray[id].secondary_prophylactic_elr_steroids =
          !updatedArray[id].secondary_prophylactic_elr_steroids;

        updatedArray[id].Q39elrSteroidsEmptyHard = false;
        updatedArray[id].tmt_steroids_elr_unknown = false;
        setQ37ElrexDetails(updatedArray);
        break;
      }

      case "crs_elr_steroids" + id: {
        let updatedArray = [...Q37ElrexDetails];
        updatedArray[id].crs_elr_steroids = !updatedArray[id].crs_elr_steroids;
        updatedArray[id].Q39elrSteroidsEmptyHard = false;
        updatedArray[id].tmt_steroids_elr_unknown = false;
        setQ37ElrexDetails(updatedArray);
        break;
      }

      case "neurotoxicity_elr_steroids" + id: {
        let updatedArray = [...Q37ElrexDetails];
        updatedArray[id].neurotoxicity_elr_steroids =
          !updatedArray[id].neurotoxicity_elr_steroids;
        updatedArray[id].Q39elrSteroidsEmptyHard = false;
        updatedArray[id].tmt_steroids_elr_unknown = false;
        setQ37ElrexDetails(updatedArray);
        break;
      }

      case "secondary_neurotoxicity_elr_steroids" + id: {
        let updatedArray = [...Q37ElrexDetails];
        updatedArray[id].secondary_neurotoxicity_elr_steroids =
          !updatedArray[id].secondary_neurotoxicity_elr_steroids;
        updatedArray[id].Q39elrSteroidsEmptyHard = false;
        updatedArray[id].tmt_steroids_elr_unknown = false;
        setQ37ElrexDetails(updatedArray);
        break;
      }

      case "tmt_neurotoxicity_elr_steroids" + id: {
        let updatedArray = [...Q37ElrexDetails];
        updatedArray[id].tmt_neurotoxicity_elr_steroids =
          !updatedArray[id].tmt_neurotoxicity_elr_steroids;
        updatedArray[id].Q39elrSteroidsEmptyHard = false;
        updatedArray[id].tmt_steroids_elr_unknown = false;
        setQ37ElrexDetails(updatedArray);
        break;
      }

      case "tmt_steroids_elr_unknown" + id: {
        let updatedArray = [...Q37ElrexDetails];
        updatedArray[id].tmt_steroids_elr_unknown =
          !updatedArray[id].tmt_steroids_elr_unknown;

        updatedArray[id].cytokin_elr_steroids = false;
        updatedArray[id].secondary_neurotoxicity_elr_steroids = false;
        updatedArray[id].secondary_prophylactic_elr_steroids = false;

        updatedArray[id].steroid_pre_tmt_elr = false;
        updatedArray[id].crs_elr_steroids = false;

        updatedArray[id].neurotoxicity_elr_steroids = false;

        updatedArray[id].tmt_neurotoxicity_elr_steroids = false;

        updatedArray[id].Q39elrSteroidsEmptyHard = false;
        setQ37ElrexDetails(updatedArray);
        break;
      }

      default:
        break;
    }
  };

  const updateQ361HospitalizationWarning = (
    cohortType,
    hospDetails,
    treatmentDetails,
  ) => {
    if (
      !cohortType ||
      !hospDetails?.length === 0 ||
      !treatmentDetails?.length === 0
    )
      return false;

    // Check if hospitalizations exist (excluding "no hospitalizations" entries)
    const hasHospitalizations = hospDetails.some(
      (v) => v.dt_admission !== null && v.dt_admission !== 2,
    );

    // Check if any dose has Inpatient site of care (siteofcareelr === 1) in step-up phases
    const hasInpatientDoses = treatmentDetails.some((v) => {
      if (cohortType === RRMMCohortTreatment.ELREXFIO) {
        return v.dosing_phase_elr !== 4 && v.siteofcare_elr === 1;
      }

      if (cohortType === RRMMCohortTreatment.Linvoseltamab) {
        return v.dosing_phase_linvo !== 5 && v.siteofcare_linvo === 1;
      }

      return false;
    });

    const showWarning = hasInpatientDoses && !hasHospitalizations;

    if (cohortType === RRMMCohortTreatment.ELREXFIO) {
      setElrInpatientHospMissing(showWarning);
    } else if (cohortType === RRMMCohortTreatment.Linvoseltamab) {
      setLinvoInpatientHospMissing(showWarning);
    }
    return showWarning;
  };

  const ValidateForm = () => {
    let isValid = true;
    const elrNextLotStartDate =
      cohortNextLotDatesList
        ?.get(RRMMCohortTreatment.ELREXFIO)
        ?.get(QuestionType.Q40) ?? "";

    const linvoNextLotStartDate =
      cohortNextLotDatesList
        ?.get(RRMMCohortTreatment.Linvoseltamab)
        ?.get(QuestionType.Q40) ?? "";


    let count = 0;
    let count_linvo = 0;
    // if (
    //   isElrexvayliSelected &&
    //   elrexvayliDetails &&
    //   elrexvayliDetails.length > 0
    // ) {
    //   if (
    //     elrexvayliDetails.length != elrNumberofAdministrator &&
    //     elrNumberofAdministrator != 0
    //   ) {
    //     setElrNumberofAdministratorMatching(true);
    //   } else {
    //     setElrNumberofAdministratorMatching(false);
    //   }
    // }
    if (bispecificQ26Data?.length > 0) {
      const isLinvoValid = ValidateQ26LastAdministrationMatch({
        isSelected: isLinvoveySelected,
        details: linvoveyDetails,
        cohortId: RRMMCohortTreatment.Linvoseltamab,
        dateField: "dose_administration_linvo_dt",
        q26Data: bispecificQ26Data,
      });

      setIsLinvoLastAdminDateValid(isLinvoValid);

      const isElrValid = ValidateQ26LastAdministrationMatch({
        isSelected: isElrexvayliSelected,
        details: elrexvayliDetails,
        cohortId: RRMMCohortTreatment.ELREXFIO,
        dateField: "dose_administration_elr_dt",
        q26Data: bispecificQ26Data,
      });

      setIsElrLastAdminDateValid(isElrValid);

      if (!isLinvoValid || !isElrValid) isValid = false;
    }

    // if (isLinvoveySelected && linvoveyDetails && linvoveyDetails.length > 0) {
    //   if (
    //     linvoveyDetails.length != linvoNumberofAdministrator &&
    //     linvoNumberofAdministrator != 0
    //   ) {
    //     setLinvoNumberofAdministratorMatching(true);
    //   } else {
    //     setLinvoNumberofAdministratorMatching(false);
    //   }
    // }

    const Q26ElrexData =
      bispecificQ26Data?.find(
        (x) => x.cohortTreatmentId === RRMMCohortTreatment.ELREXFIO,
      ) ?? null;
    const Q262ElrexEndDate =
      bispecificQ331AData?.find((x) => x.lotNumber === Q26ElrexData?.lotNumber)
        ?.lastAdministrationDt ?? null;

    const Q26LinvoData =
      bispecificQ26Data?.find(
        (x) => x.cohortTreatmentId === RRMMCohortTreatment.Linvoseltamab,
      ) ?? null;
    const Q262LinvoEndDate =
      bispecificQ331AData?.find((x) => x.lotNumber === Q26LinvoData?.lotNumber)
        ?.lastAdministrationDt ?? null;

    //36 Elrexfio
    if (
      isElrexvayliSelected &&
      elrexvayliDetails &&
      elrexvayliDetails.length > 0
    ) {
      const updatedArray = elrexvayliDetails.map((item, index) => {
        // Row 0 ("Step-up dose 1") shows an auto-populated date with no
        // input control, so it can never have this field answered.
        let dose_administration_elrHard = false;
        if (index !== 0 && item.dose_administration_elr == null) {
          dose_administration_elrHard = true;
        }

        let dose_administration_elrHard_dt = false;
        let dt_elr_date_out_of_bound = false;
        let Invalid_dose_administration_elr_dt = false;
        let dose_administration_elr_dt_before_eof = false;
        if (item.dose_administration_elr === 1) {
          if (isStringEmpty(item.dose_administration_elr_dt)) {
            dose_administration_elrHard_dt = true;
          } else if (!IsDateValid(item.dose_administration_elr_dt)) {
            Invalid_dose_administration_elr_dt = true;
          } else if (
            !ValidateQ36DateSequence(
              elrexIndexDate,
              elrexInitiationDate,
              item.dose_administration_elr_dt,
              Q26ElrexData?.lastAdministrationDt,
              Q262ElrexEndDate,
              elrNextLotStartDate,
              endOfFollowUpDate,
            )
          ) {
            dt_elr_date_out_of_bound = true;
          } else if (
            !isStringEmpty(endOfFollowUpDate) &&
            IsDateValid(endOfFollowUpDate) &&
            !IsLessOrEqual(item.dose_administration_elr_dt, endOfFollowUpDate)
          ) {
            dose_administration_elr_dt_before_eof = true;
          }
        }

        // Q36: ELREXFIO Duplicate Date Check
        if (isElrexvayliSelected && elrexvayliDetails.length > 0) {
          let hasDuplicateElr = false;

          const updatedElrDetails = elrexvayliDetails.map((item, index) => {
            let duplicateDateCheck = false;

            if (item.dose_administration_elr_dt) {
              const duplicateCount = elrexvayliDetails.filter(
                (detail, idx) =>
                  idx !== index &&
                  detail.dose_administration_elr_dt ===
                    item.dose_administration_elr_dt,
              ).length;

              if (duplicateCount > 0) {
                duplicateDateCheck = true;
                hasDuplicateElr = true;
              }
            }

            return { ...item, duplicateDateCheck };
          });

          if (hasDuplicateElr) {
            setElrexvayliDetails(updatedElrDetails);
            setElrexDuplicateDates(true);
            isValid = false;
          } else {
            setElrexDuplicateDates(false);
          }
        }

        let dosing_phase_elrHard = false;
        if (item.dosing_phase_elr == null) {
          dosing_phase_elrHard = true;
        }

        let siteofcare_elrHard = false;
        if (item.siteofcare_elr == null) {
          siteofcare_elrHard = true;
        }

        let dosing_phase_elrHard_othEmpty = false;
        if (
          item.dosing_phase_elr === 5 &&
          item.dosing_phase_oth_txt_elr == ""
        ) {
          dosing_phase_elrHard_othEmpty = true;
        }

        // Row 0 ("Step-up dose 1") shows an auto-populated dose strength
        // with no input control, so it can never have this field answered.
        let elr_dose_strength_mg_kgHard = false;
        if (
          index !== 0 &&
          (item.elr_dose_strength_rbl == null ||
            (item.elr_dose_strength_mg === "" &&
              item.elr_dose_strength_mg_kg === "" &&
              item.elr_dose_strength_mgm === "" &&
              item.elr_dose_strength_rbl !== 99))
        ) {
          elr_dose_strength_mg_kgHard = true;
        }

        let elr_dose_strength_mg_kgRange = false;
        if (
          !elr_dose_strength_mg_kgHard &&
          item.elr_dose_strength_rbl !== 99 &&
          ((item.elr_dose_strength_mg_kg != "" &&
            !isValidNumberRange(item.elr_dose_strength_mg_kg)) ||
            (item.elr_dose_strength_mg != "" &&
              !isValidNumberRange(item.elr_dose_strength_mg)) ||
            (item.elr_dose_strength_mgm != "" &&
              !isValidNumberRange(item.elr_dose_strength_mgm)))
        ) {
          elr_dose_strength_mg_kgRange = true;
        }

        // "Initial dose strength immediately following step-up dosing
        // phase" (1st subsequent treatment dose row) requires a strictly
        // positive value (>0.00), unlike the >=0.00 allowed elsewhere.
        if (
          !elr_dose_strength_mg_kgHard &&
          item.dosing_phase_elr === 4 &&
          item.elr_dose_strength_rbl !== 99 &&
          ((item.elr_dose_strength_mg_kg != "" &&
            parseFloat(item.elr_dose_strength_mg_kg) === 0) ||
            (item.elr_dose_strength_mg != "" &&
              parseFloat(item.elr_dose_strength_mg) === 0) ||
            (item.elr_dose_strength_mgm != "" &&
              parseFloat(item.elr_dose_strength_mgm) === 0))
        ) {
          elr_dose_strength_mg_kgRange = true;
        }

        let elr_dose_weekly_strength_mg_kgHard = false;
        let elr_dose_weekly_freq_hard_error = false;
        if (item.dosing_phase_elr === 4) {
          if (
            item.elr_dose_freq_rbl == null &&
            (item.elr_dose_freq_week === "" ||
              item.elr_dose_freq_week === null) &&
            (item.elr_dose_freq_onceweek === "" ||
              item.elr_dose_freq_onceweek === null) &&
            item.elr_dose_freq_rbl !== 99
          ) {
            elr_dose_weekly_freq_hard_error = true;
          }

          if (
            (item.elr_dose_freq_rbl == 1 &&
              (item.elr_dose_freq_week === "" ||
                item.elr_dose_freq_week === null)) ||
            (item.elr_dose_freq_rbl == 2 &&
              (item.elr_dose_freq_onceweek === "" ||
                item.elr_dose_freq_onceweek === null))
          ) {
            elr_dose_weekly_strength_mg_kgHard = true;
          }
        }

        // "Initial dose frequency immediately following step-up dosing
        // phase" allows the same >0.00 and <=4000 (up to 2 decimals) range
        // as the dose strength field above.
        let elr_dose_weekly_freq_RangeError = false;
        if (
          !elr_dose_weekly_strength_mg_kgHard &&
          item.dosing_phase_elr === 4 &&
          item.elr_dose_freq_rbl !== 99 &&
          ((item.elr_dose_freq_week !== "" &&
            (!isValidNumberRange(item.elr_dose_freq_week) ||
              parseFloat(item.elr_dose_freq_week) === 0)) ||
            (item.elr_dose_freq_onceweek !== "" &&
              (!isValidNumberRange(item.elr_dose_freq_onceweek) ||
                parseFloat(item.elr_dose_freq_onceweek) === 0)))
        ) {
          elr_dose_weekly_freq_RangeError = true;
        }

        if (!isStringEmpty(item.dose_administration_elr_dt) && elrexIndexDate) {
          if (elrexIndexDate === item.dose_administration_elr_dt) {
            count++;
          }
        } else if (
          isStringEmpty(item.dose_administration_elr) ||
          item.dose_administration_elr === 99
        ) {
          count++;
        }

        if (
          dose_administration_elrHard ||
          dose_administration_elrHard_dt ||
          dosing_phase_elrHard ||
          siteofcare_elrHard ||
          dosing_phase_elrHard_othEmpty ||
          elr_dose_strength_mg_kgHard ||
          elr_dose_strength_mg_kgRange ||
          dt_elr_date_out_of_bound ||
          Invalid_dose_administration_elr_dt ||
          dose_administration_elr_dt_before_eof ||
          elr_dose_weekly_strength_mg_kgHard ||
          elr_dose_weekly_freq_hard_error ||
          elr_dose_weekly_freq_RangeError
        ) {
          isValid = false;
        }

        return {
          ...item,
          dose_administration_elrHard,
          dose_administration_elrHard_dt,
          Invalid_dose_administration_elr_dt,
          dosing_phase_elrHard,
          siteofcare_elrHard,
          dosing_phase_elrHard_othEmpty,
          elr_dose_strength_mg_kgHard,
          elr_dose_strength_mg_kgRange,
          dt_elr_date_out_of_bound,
          dose_administration_elr_dt_before_eof,
          elr_dose_weekly_strength_mg_kgHard,
          elr_dose_weekly_freq_hard_error,
          elr_dose_weekly_freq_RangeError,
        };
      });

      if (count === 0) {
        setIndexDateRRMM_elrHard(true);
        isValid = false;
      } else {
        setIndexDateRRMM_elrHard(false);
      }
      setElrexvayliDetails(updatedArray);

      //New Warnings
      const setups2 = elrexvayliDetails.filter((v) => v.dosing_phase_elr === 2);
      const setups1stTreatment = elrexvayliDetails.filter(
        (v) => v.dosing_phase_elr === 3,
      );
      const setups1stSubsTreatment = elrexvayliDetails.filter(
        (v) => v.dosing_phase_elr === 4,
      );

      // Step-up Dose 2: Patient did not receive dose
      const setup2NotReceived = setups2.some(
        (v) => v.dose_administration_elr === 2,
      );
      // Step-up Dose 3: Patient did not receive dose
      const setup1stNotReceived = setups1stTreatment.some(
        (v) => v.dose_administration_elr === 2,
      );

      // Step-up Dose 1st: dose received if date is reported OR Date Unknown
      const setup1stReceived = setups1stTreatment.some(
        (v) => v.dose_administration_elr_dt || v.dose_administration_elr === 99,
      );

      // Step-up Dose 1st subs: dose received if date is reported OR Date Unknown
      const setup1stSubsReceived = setups1stSubsTreatment.some(
        (v) => v.dose_administration_elr_dt || v.dose_administration_elr === 99,
      );

      //If 1st treatment dose reported with date or date unknown, then “Patient did not receive dose” cannot be reported in Step-up Doses 2 or 3
      // Show warning when Dose 3 was received,
      // but Dose 2 was reported as "Patient did not receive dose"
      const showDose1stTreatmentWarning = setup1stReceived && setup2NotReceived;
      setSetupElrDose1st_WarningHard(
 showDose1stTreatmentWarning ? getFirstTreatmentWarningMessage({ step2: setup2NotReceived }) : "",
 );

      //If 1st treatment dose reported with date or date unknown, then “Patient did not receive dose” cannot be reported in Step-up Doses 2 or 3
      // Show warning when Dose 3 was received,
      // but Dose 2 was reported as "Patient did not receive dose"
      const showDose1stSubsTreatmentWarning =
        setup1stSubsReceived && (setup2NotReceived || setup1stNotReceived);
      setSetupElrDose1stSubs_WarningHard(
 showDose1stSubsTreatmentWarning
 ? getFirstSubsWarningMessage({ step2: setup2NotReceived, step3: false, first: setup1stNotReceived })
 : "",
 );

      const isDateValid = ValidateForDateCondition(elrexvayliDetails);
      if (!isDateValid) {
        setDt_elr_2L_3L_date_conditions(true);
        isValid = false;
      }
    }

    //36.1 Elrexfio
    if (isElrexvayliSelected && elrHospDetails && elrHospDetails.length > 0) {
      // Q36.1 column [B] chain: Step-up Dose 1 (Q36) <= [B] <= 1st subsequent
      // treatment dose reported in Q36 <= last administration in column [D]
      // of Q26 (if known) <= end date of LOT in Q26.2 <= start date of
      // subsequent LOT (Q26/Q26.1/Q26.2/Q27/Q31) <= end of follow-up.
      const elrFirstSubQ36Date = GetQ36FirstSubsequentDoseDate(
        elrexvayliDetails,
        Q381A_ELREX_AUTO_CONFIG,
      );
      const elrStepUpDose1Date = GetQ36StepUpDose1Date(
        elrexvayliDetails,
        Q381A_ELREX_AUTO_CONFIG,
      );
      const elrQ361LowerBound =
        elrStepUpDose1Date ||
        (IsValidDateValue(elrexInitiationDate)
          ? elrexInitiationDate
          : elrexIndexDate);
      const elrQ361UpperBound = elrFirstSubQ36Date || elrvayliDateRRMM;
      const elrQ361ChainBoundaries = GetQ361ChainBoundaryDates(
        RRMMCohortTreatment.ELREXFIO,
      );

      const updatedArray = elrHospDetails.map((item, index) => {
        let dt_admission_Hard = false;
        let dt_admission_Hard_dt = false;
        let Invalid_admission_Hard_dt = false;
        let Invalid_dt_discharge_Hard = false;
        let dt_admission_out_of_bound = false;

        let dt_discharge_Hard = false;
        let dt_discharge_Hard_dt = false;
        let dt_discharge_Hard_dt_check = false;
        let dt_discharge_out_of_bound = false;
        let dt_discharge_before_eof = false;
        let dt_admission_before_eof = false;

        let hospReasonEmptyHard = false;
        let hospReasonTxtEmpty = false;

        dt_admission_Hard = item.dt_admission == null;

        if (item.dt_admission === 1) {
          const earliestQ36ElrDate = elrexvayliDetails
            .filter(
              (r) =>
                r.dosing_phase_elr === 4 &&
                !isStringEmpty(r.dose_administration_elr_dt) &&
                IsDateValid(r.dose_administration_elr_dt),
            )
            .map((r) => r.dose_administration_elr_dt)
            .reduce(
              (earliest, curr) =>
                !earliest || new Date(curr) < new Date(earliest)
                  ? curr
                  : earliest,
              null,
            );

          if (isStringEmpty(item.dt_admission_dt)) {
            dt_admission_Hard_dt = true;
          } else if (!IsDateValid(item.dt_admission_dt)) {
            Invalid_admission_Hard_dt = true;
          } else if (
            !ValidateQ361AdmissionDate(
              elrexInitiationDate,
              earliestQ36ElrDate,
              Q26ElrexData?.lastAdministrationDt,
              Q262ElrexEndDate,
              elrNextLotStartDate,
              endOfFollowUpDate,
              item.dt_admission_dt,
            )
          ) {
            Invalid_admission_Hard_dt = true;
          } else {
            const elrQ361ChainValid = IsQ361DateChainValid([
              elrQ361LowerBound,
              item.dt_admission_dt,
              elrFirstSubQ36Date,
              elrQ361ChainBoundaries.q26LastAdminDt,
              elrQ361ChainBoundaries.q262EndDate,
              elrQ361ChainBoundaries.nextLotStartDate,
              endOfFollowUpDate,
            ]);
            if (
              !elrQ361ChainValid ||
              (elrQ361LowerBound !== "" &&
                IsDateAfter(elrQ361LowerBound, item.dt_admission_dt)) ||
              (elrQ361UpperBound !== "" &&
                IsDateAfter(item.dt_admission_dt, elrQ361UpperBound))
            ) {
              dt_admission_out_of_bound = true;
            } else {
              if (
                endOfFollowUpDate !== null &&
                endOfFollowUpDate !== "" &&
                endOfFollowUpDate !== "Invalid Date"
              ) {
                const EOFDateAfter = IsDateAfter(
                  item.dt_admission_dt,
                  endOfFollowUpDate,
                );
                if (EOFDateAfter) {
                  dt_admission_before_eof = true;
                }
              }
            }
          }
        }

        if (item.dt_admission != null && item.dt_admission !== 2) {
          dt_discharge_Hard = item.dt_discharge == null;

          if (item.dt_discharge === 1) {
            if (item.dt_discharge_dt === "") {
              dt_discharge_Hard_dt = true;
            } else if (item.dt_discharge_dt === "Invalid Date") {
              Invalid_dt_discharge_Hard = true;
            } else if (
              item.dt_discharge_dt !== "Invalid Date" &&
              !DateInRange(item.dt_discharge_dt, dayjs("01-01-1920"), dayjs())
            ) {
              Invalid_dt_discharge_Hard = true;
            } else {
              if (
                (elrexIndexDate !== "" &&
                  IsDateAfter(elrexIndexDate, item.dt_discharge_dt)) ||
                (elrvayliDateRRMM !== "" &&
                  IsDateAfter(item.dt_discharge_dt, elrvayliDateRRMM))
              ) {
                dt_discharge_out_of_bound = true;
              } else {
                if (
                  endOfFollowUpDate !== null &&
                  endOfFollowUpDate !== "" &&
                  endOfFollowUpDate !== "Invalid Date"
                ) {
                  const EOFDateAfter = IsDateAfter(
                    item.dt_discharge_dt,
                    endOfFollowUpDate,
                  );
                  if (EOFDateAfter) {
                    dt_discharge_before_eof = true;
                  }
                }
              }
            }
          }
          if (
            item.dt_admission_dt &&
            DateInRange(item.dt_discharge_dt, dayjs("01-01-1920"), dayjs()) &&
            IsDateAfter(item.dt_admission_dt, item.dt_discharge_dt) // item.dt_admission_dt > item.dt_discharge_dt
          ) {
            dt_discharge_Hard_dt_check = true;
          }
          hospReasonEmptyHard =
            !item.hospReason1 &&
            !item.hospReason2 &&
            !item.hospReason3 &&
            !item.hospReason4 &&
            !item.hospReason5 &&
            !item.hospReason6 &&
            !item.hospReason7 &&
            !item.hospReasonUnk;
          hospReasonTxtEmpty =
            item.hospReason6 && isStringEmpty(item.hosp_reason_oth_txt);
        }

        if (
          dt_admission_Hard ||
          dt_admission_Hard_dt ||
          dt_admission_out_of_bound ||
          dt_discharge_Hard ||
          dt_discharge_Hard_dt ||
          dt_discharge_Hard_dt_check ||
          dt_discharge_out_of_bound ||
          hospReasonEmptyHard ||
          hospReasonTxtEmpty ||
          Invalid_admission_Hard_dt ||
          Invalid_dt_discharge_Hard ||
          dt_discharge_before_eof ||
          dt_admission_before_eof
        ) {
          isValid = false;
        }

        return {
          ...item,
          dt_admission_Hard,
          dt_admission_Hard_dt,
          dt_admission_out_of_bound,
          dt_discharge_Hard,
          dt_discharge_Hard_dt,
          dt_discharge_Hard_dt_check,
          dt_discharge_out_of_bound,
          hospReasonEmptyHard,
          hospReasonTxtEmpty,
          Invalid_admission_Hard_dt,
          Invalid_dt_discharge_Hard,
          dt_discharge_before_eof,
          dt_admission_before_eof,
        };
      });

      setElrHospDetails(updatedArray);
    }
    //end
    //36 Linvoseltamab
    if (isLinvoveySelected && linvoveyDetails && linvoveyDetails.length > 0) {
      //

      const updatedArray = linvoveyDetails.map((item, index) => {
        // Row 0 ("Step-up dose 1") shows an auto-populated date with no
        // input control, so it can never have this field answered.
        let dose_administration_linvoHard = false;
        if (index !== 0 && item.dose_administration_linvo == null) {
          dose_administration_linvoHard = true;
        }

        let Invalid_dose_administration_linvo_dt = false;

        let dose_administration_linvoHard_dt = false;
        let dt_linvo_date_out_of_bound = false;
        if (item.dose_administration_linvo === 1) {
          if (isStringEmpty(item.dose_administration_linvo_dt)) {
            dose_administration_linvoHard_dt = true;
          } else if (!IsDateValid(item.dose_administration_linvo_dt)) {
            Invalid_dose_administration_linvo_dt = true;
          } else if (
            !ValidateQ36DateSequence(
              linvoIndexDate,
              linvoInitiationDate,
              item.dose_administration_linvo_dt,
              Q26LinvoData?.lastAdministrationDt,
              Q262LinvoEndDate,
              linvoLOTStartDate,
              endOfFollowUpDate,
            )
          ) {
            dt_linvo_date_out_of_bound = true;
          }
        }

        // Q36: ELREXFIO Duplicate Date Check
        if (isLinvoveySelected && linvoveyDetails.length > 0) {
          let hasDuplicateLin = false;

          const updatedElrDetails = linvoveyDetails.map((item, index) => {
            let duplicateDateCheck = false;

            if (item.dose_administration_linvo_dt) {
              const duplicateCount = linvoveyDetails.filter(
                (detail, idx) =>
                  idx !== index &&
                  detail.dose_administration_linvo_dt ===
                    item.dose_administration_linvo_dt,
              ).length;

              if (duplicateCount > 0) {
                duplicateDateCheck = true;
                hasDuplicateLin = true;
              }
            }

            return { ...item, duplicateDateCheck };
          });

          if (hasDuplicateLin) {
            setLinvoveyDetails(updatedElrDetails);
            setLinvoDuplicateDates(true);
            isValid = false;
          } else {
            setLinvoDuplicateDates(false);
          }
        }

        let dosing_phase_linvoHard = false;
        if (item.dosing_phase_linvo == null) {
          dosing_phase_linvoHard = true;
        }

        let siteofcare_linvoHard = false;
        if (item.siteofcare_linvo == null) {
          siteofcare_linvoHard = true;
        }

        let dosing_phase_linvoHard_othEmpty = false;
        if (
          item.dosing_phase_linvo === 6 &&
          item.dosing_phase_oth_txt_linvo == ""
        ) {
          dosing_phase_linvoHard_othEmpty = true;
        }

        // Row 0 ("Step-up dose 1") shows an auto-populated dose strength
        // with no input control, so it can never have this field answered.
        let linvo_dose_strength_mg_kgHard = false;
        if (
          index !== 0 &&
          (item.linvo_dose_strength_rbl === null ||
            (item.linvo_dose_strength_mg === "" &&
              item.linvo_dose_strength_mg_kg === "" &&
              item.linvo_dose_strength_mgm === "" &&
              item.linvo_dose_strength_rbl !== 99))
        ) {
          linvo_dose_strength_mg_kgHard = true;
        }

        let linvo_dose_strength_mg_kgRange = false;
        if (
          !linvo_dose_strength_mg_kgHard &&
          item.linvo_dose_strength_rbl !== 99 &&
          ((item.linvo_dose_strength_mg_kg != "" &&
            !isValidNumberRange(item.linvo_dose_strength_mg_kg)) ||
            (item.linvo_dose_strength_mg != "" &&
              !isValidNumberRange(item.linvo_dose_strength_mg)) ||
            (item.linvo_dose_strength_mgm != "" &&
              !isValidNumberRange(item.linvo_dose_strength_mgm)))
        ) {
          linvo_dose_strength_mg_kgRange = true;
        }

        // "Initial dose strength immediately following step-up dosing
        // phase" (1st subsequent treatment dose row) requires a strictly
        // positive value (>0.00), unlike the >=0.00 allowed elsewhere.
        if (
          !linvo_dose_strength_mg_kgHard &&
          item.dosing_phase_linvo === 5 &&
          item.linvo_dose_strength_rbl !== 99 &&
          ((item.linvo_dose_strength_mg_kg != "" &&
            parseFloat(item.linvo_dose_strength_mg_kg) === 0) ||
            (item.linvo_dose_strength_mg != "" &&
              parseFloat(item.linvo_dose_strength_mg) === 0) ||
            (item.linvo_dose_strength_mgm != "" &&
              parseFloat(item.linvo_dose_strength_mgm) === 0))
        ) {
          linvo_dose_strength_mg_kgRange = true;
        }

        let linvo_dose_weekly_strength_mg_kgHard = false;
        let linvo_dose_weekly_freq_hard_error = false;
        if (item.dosing_phase_linvo === 5) {
          if (
            item.linvo_dose_freq_rbl == null &&
            (item.linvo_dose_freq_week === "" ||
              item.linvo_dose_freq_week === null) &&
            (item.linvo_dose_freq_onceweek === "" ||
              item.linvo_dose_freq_onceweek === null) &&
            item.linvo_dose_freq_rbl !== 99
          ) {
            linvo_dose_weekly_freq_hard_error = true;
          }

          if (
            (item.linvo_dose_freq_rbl == 1 &&
              (item.linvo_dose_freq_week === "" ||
                item.linvo_dose_freq_week === null)) ||
            (item.linvo_dose_freq_rbl == 2 &&
              (item.linvo_dose_freq_onceweek === "" ||
                item.linvo_dose_freq_onceweek === null))
          ) {
            linvo_dose_weekly_strength_mg_kgHard = true;
          }
        }

        // "Initial dose frequency immediately following step-up dosing
        // phase" allows the same >0.00 and <=4000 (up to 2 decimals) range
        // as the dose strength field above.
        let linvo_dose_weekly_freq_RangeError = false;
        if (
          !linvo_dose_weekly_strength_mg_kgHard &&
          item.dosing_phase_linvo === 5 &&
          item.linvo_dose_freq_rbl !== 99 &&
          ((item.linvo_dose_freq_week !== "" &&
            (!isValidNumberRange(item.linvo_dose_freq_week) ||
              parseFloat(item.linvo_dose_freq_week) === 0)) ||
            (item.linvo_dose_freq_onceweek !== "" &&
              (!isValidNumberRange(item.linvo_dose_freq_onceweek) ||
                parseFloat(item.linvo_dose_freq_onceweek) === 0)))
        ) {
          linvo_dose_weekly_freq_RangeError = true;
        }

        if (
          !isStringEmpty(item.dose_administration_linvo_dt) &&
          linvoInitiationDate
        ) {
          if (linvoInitiationDate === item.dose_administration_linvo_dt) {
            count_linvo++;
          }
        } else if (
          isStringEmpty(item.dose_administration_linvo) ||
          item.dose_administration_linvo === 99
        ) {
          count_linvo++;
        }

        if (
          dose_administration_linvoHard ||
          dose_administration_linvoHard_dt ||
          dosing_phase_linvoHard ||
          siteofcare_linvoHard ||
          dosing_phase_linvoHard_othEmpty ||
          linvo_dose_strength_mg_kgHard ||
          linvo_dose_strength_mg_kgRange ||
          dt_linvo_date_out_of_bound ||
          Invalid_dose_administration_linvo_dt ||
          linvo_dose_weekly_freq_hard_error ||
          linvo_dose_weekly_strength_mg_kgHard ||
          linvo_dose_weekly_freq_RangeError
        ) {
          isValid = false;
        }

        return {
          ...item,
          dose_administration_linvoHard,
          dose_administration_linvoHard_dt,
          dosing_phase_linvoHard,
          siteofcare_linvoHard,
          dosing_phase_linvoHard_othEmpty,
          linvo_dose_strength_mg_kgHard,
          linvo_dose_strength_mg_kgRange,
          dt_linvo_date_out_of_bound,
          Invalid_dose_administration_linvo_dt,
          linvo_dose_weekly_freq_hard_error,
          linvo_dose_weekly_strength_mg_kgHard,
          linvo_dose_weekly_freq_RangeError,
        };
      });

      if (count_linvo === 0) {
        setIndexDateRRMM_linvoHard(true);
        isValid = false;
      } else {
        setIndexDateRRMM_linvoHard(false);
      }
      setLinvoveyDetails(updatedArray);

      //New Warnings
      const setups2 = linvoveyDetails.filter((v) => v.dosing_phase_linvo === 2);
      const setups3 = linvoveyDetails.filter((v) => v.dosing_phase_linvo === 3);
      const setups1stTreatment = linvoveyDetails.filter(
        (v) => v.dosing_phase_linvo === 4,
      );
      const setups1stSubsTreatment = linvoveyDetails.filter(
        (v) => v.dosing_phase_linvo === 5,
      );

      // Step-up Dose 2: Patient did not receive dose
      const setup2NotReceived = setups2.some(
        (v) => v.dose_administration_linvo === 2,
      );
      // Step-up Dose 3: Patient did not receive dose
      const setup3NotReceived = setups3.some(
        (v) => v.dose_administration_linvo === 2,
      );
      // Step-up Dose 3: Patient did not receive dose
      const setup1stNotReceived = setups1stTreatment.some(
        (v) => v.dose_administration_linvo === 2,
      );

      // Step-up Dose 3: dose received if date is reported OR Date Unknown
      const setup3Received = setups3.some(
        (v) =>
          v.dose_administration_linvo_dt || v.dose_administration_linvo === 99,
      );
      // Step-up Dose 1st: dose received if date is reported OR Date Unknown
      const setup1stReceived = setups1stTreatment.some(
        (v) =>
          v.dose_administration_linvo_dt || v.dose_administration_linvo === 99,
      );

      // Step-up Dose 1st Subs: dose received if date is reported OR Date Unknown
      const setup1stSubsReceived = setups1stSubsTreatment.some(
        (v) =>
          v.dose_administration_linvo_dt || v.dose_administration_linvo === 99,
      );

      // Case 1: If Step-up Dose 3 reported with date or date unknown, then “Patient did not receive dose” cannot be reported in Step-up Dose 2
      // Show warning when Dose 3 was received,
      // but Dose 2 was reported as "Patient did not receive dose"

      const showDose3Warning = setup3Received && setup2NotReceived;
      setSetupLinvoDose3_WarningHard(
 showDose3Warning ? getDose2WarningMessage({ step2: setup2NotReceived }) : "",
 );

      // Case 2: If 1st treatment dose reported with date or date unknown, then “Patient did not receive dose” cannot be reported in Step-up Doses 2 or 3
      // Show warning when Dose 3 was received,
      // but Dose 2 was reported as "Patient did not receive dose"
      const showDose1stTreatmentWarning =
        setup1stReceived && setup2NotReceived;

      setSetupLinvoDose1st_WarningHard(
 showDose1stTreatmentWarning ? getFirstTreatmentWarningMessage({ step2: setup2NotReceived }) : "",
 );

      // Case 3: If 1st treatment dose reported with date or date unknown, then “Patient did not receive dose” cannot be reported in Step-up Doses 2 or 3
      // Show warning when Dose 3 was received,
      // but Dose 2 was reported as "Patient did not receive dose"

      const showDose1stSubsTreatmentWarning =
        setup1stSubsReceived &&
        (setup2NotReceived || setup3NotReceived || setup1stNotReceived);
      setSetupLinvoDose1stSubs_WarningHard(
 showDose1stSubsTreatmentWarning
 ? getFirstSubsWarningMessage({ step2: setup2NotReceived, step3: setup3NotReceived, first: setup1stNotReceived })
 : "",
 );

      const isDateValid = ValidateForDateCondition(linvoveyDetails);
      if (!isDateValid) {
        setDt_lino_2L_3L_date_conditions(true);
        isValid = false;
      }
    }

    //36.1 Linvoseltamab
    if (isLinvoveySelected && linvoHospDetails && linvoHospDetails.length > 0) {
      // Q36.1 column [B] chain: Step-up Dose 1 (Q36) <= [B] <= 1st subsequent
      // treatment dose reported in Q36 <= last administration in column [D]
      // of Q26 (if known) <= end date of LOT in Q26.2 <= start date of
      // subsequent LOT (Q26/Q26.1/Q26.2/Q27/Q31) <= end of follow-up.
      const linvoFirstSubQ36Date = GetQ36FirstSubsequentDoseDate(
        linvoveyDetails,
        Q381A_LINVO_AUTO_CONFIG,
      );
      const linvoStepUpDose1Date = GetQ36StepUpDose1Date(
        linvoveyDetails,
        Q381A_LINVO_AUTO_CONFIG,
      );
      const linvoQ361LowerBound =
        linvoStepUpDose1Date ||
        (IsValidDateValue(linvoInitiationDate)
          ? linvoInitiationDate
          : linvoIndexDate);
      const linvoQ361UpperBound = linvoFirstSubQ36Date || linvoveyDateRRMM;
      const linvoQ361ChainBoundaries = GetQ361ChainBoundaryDates(
        RRMMCohortTreatment.Linvoseltamab,
      );

      const updatedArray = linvoHospDetails.map((item, index) => {
        let dt_admission_Hard = false;
        let dt_admission_Hard_dt = false;

        let dt_discharge_Hard = false;
        let dt_discharge_Hard_dt = false;
        let dt_discharge_Hard_dt_check = false;
        let dt_discharge_out_of_bound = false;
        let dt_admission_out_of_bound = false;
        let Invalid_admission_Hard_dt = false;
        let dt_discharge_before_eof = false;
        let dt_admission_before_eof = false;
        let hospReasonEmptyHard = false;
        let hospReasonTxtEmpty = false;

        dt_admission_Hard = item.dt_admission == null;
        if (item.dt_admission === 1) {
          const earliestQ36LinvoDate = linvoveyDetails
            .filter(
              (r) =>
                r.dosing_phase_linvo === 5 &&
                !isStringEmpty(r.dose_administration_linvo_dt) &&
                IsDateValid(r.dose_administration_linvo_dt),
            )
            .map((r) => r.dose_administration_linvo_dt)
            .reduce(
              (earliest, curr) =>
                !earliest || new Date(curr) < new Date(earliest)
                  ? curr
                  : earliest,
              null,
            );

          if (isStringEmpty(item.dt_admission_dt)) {
            dt_admission_Hard_dt = true;
          } else if (!IsDateValid(item.dt_admission_dt)) {
            Invalid_admission_Hard_dt = true;
          } else if (
            item.dt_admission_dt !== "Invalid Date" &&
            !DateInRange(item.dt_admission_dt, dayjs("01-01-1920"), dayjs())
          ) {
            Invalid_admission_Hard_dt = true;
          } else {
            const linvoQ361ChainValid = IsQ361DateChainValid([
              linvoQ361LowerBound,
              item.dt_admission_dt,
              linvoFirstSubQ36Date,
              linvoQ361ChainBoundaries.q26LastAdminDt,
              linvoQ361ChainBoundaries.q262EndDate,
              linvoQ361ChainBoundaries.nextLotStartDate,
              endOfFollowUpDate,
            ]);
            if (
              !linvoQ361ChainValid ||
              (linvoQ361LowerBound !== "" &&
                IsDateAfter(linvoQ361LowerBound, item.dt_admission_dt)) ||
              (linvoQ361UpperBound !== "" &&
                IsDateAfter(item.dt_admission_dt, linvoQ361UpperBound))
            ) {
              dt_admission_out_of_bound = true;
            } else {
              if (
                endOfFollowUpDate !== null &&
                endOfFollowUpDate !== "" &&
                endOfFollowUpDate !== "Invalid Date"
              ) {
                const EOFDateAfter = IsDateAfter(
                  item.dt_admission_dt,
                  endOfFollowUpDate,
                );
                if (EOFDateAfter) {
                  dt_admission_before_eof = true;
                }
              }
            }
          }
        }

        let Invalid_dt_discharge_Hard = false;
        if (item.dt_admission != null && item.dt_admission !== 2) {
          dt_discharge_Hard = item.dt_discharge == null;

          if (item.dt_discharge === 1) {
            if (item.dt_discharge_dt === "") {
              dt_discharge_Hard_dt = true;
            } else if (item.dt_discharge_dt === "Invalid Date") {
              Invalid_dt_discharge_Hard = true;
            } else if (
              item.dt_discharge_dt !== "Invalid Date" &&
              !DateInRange(item.dt_discharge_dt, dayjs("01-01-1920"), dayjs())
            ) {
              Invalid_dt_discharge_Hard = true;
            } else {
              if (
                (linvoIndexDate !== "" &&
                  IsDateAfter(linvoIndexDate, item.dt_discharge_dt)) ||
                (linvoveyDateRRMM !== "" &&
                  IsDateAfter(item.dt_discharge_dt, linvoveyDateRRMM))
              ) {
                dt_discharge_out_of_bound = true;
              } else {
                if (
                  endOfFollowUpDate !== null &&
                  endOfFollowUpDate !== "" &&
                  endOfFollowUpDate !== "Invalid Date"
                ) {
                  const EOFDateAfter = IsDateAfter(
                    item.dt_discharge_dt,
                    endOfFollowUpDate,
                  );
                  if (EOFDateAfter) {
                    dt_discharge_before_eof = true;
                  }
                }
              }
            }
          }

          if (
            item.dt_admission_dt &&
            item.dt_discharge_dt &&
            DateInRange(item.dt_discharge_dt, dayjs("01-01-1920"), dayjs()) &&
            IsDateAfter(item.dt_admission_dt, item.dt_discharge_dt) //item.dt_admission_dt > item.dt_discharge_dt
          ) {
            dt_discharge_Hard_dt_check = true;
          }
          hospReasonEmptyHard =
            !item.hospReason1 &&
            !item.hospReason2 &&
            !item.hospReason3 &&
            !item.hospReason4 &&
            !item.hospReason5 &&
            !item.hospReason6 &&
            !item.hospReason7 &&
            !item.hospReasonUnk;
          hospReasonTxtEmpty =
            item.hospReason6 && isStringEmpty(item.hosp_reason_oth_txt);
        }

        if (
          dt_admission_Hard ||
          dt_admission_Hard_dt ||
          dt_admission_out_of_bound ||
          dt_discharge_Hard ||
          dt_discharge_Hard_dt ||
          dt_discharge_Hard_dt_check ||
          dt_discharge_out_of_bound ||
          hospReasonEmptyHard ||
          hospReasonTxtEmpty ||
          Invalid_admission_Hard_dt ||
          Invalid_dt_discharge_Hard ||
          dt_discharge_before_eof ||
          dt_admission_before_eof
        ) {
          isValid = false;
        }

        return {
          ...item,
          dt_admission_Hard,
          dt_admission_Hard_dt,
          dt_admission_out_of_bound,
          dt_discharge_Hard,
          dt_discharge_Hard_dt,
          dt_discharge_Hard_dt_check,
          dt_discharge_out_of_bound,
          hospReasonEmptyHard,
          hospReasonTxtEmpty,
          Invalid_admission_Hard_dt,
          Invalid_dt_discharge_Hard,
          dt_discharge_before_eof,
          dt_admission_before_eof,
        };
      });

      setLinvoHospDetails(updatedArray);
    }
    //end

    if (
      isElrexvayliSelected &&
      elrexvayliDetails &&
      elrexvayliDetails.length > 0
    ) {
      const isElrInpatientWarning = updateQ361HospitalizationWarning(
        RRMMCohortTreatment.ELREXFIO,
        elrHospDetails,
        elrexvayliDetails,
      );
      if (isElrInpatientWarning) {
        isValid = false;
      }
    }

    // Same validation for Linvoseltamab
    if (isLinvoveySelected && linvoveyDetails && linvoveyDetails.length > 0) {
      const isLinvoInpatientWarning = updateQ361HospitalizationWarning(
        RRMMCohortTreatment.Linvoseltamab,
        linvoHospDetails,
        linvoveyDetails,
      );
      if (isLinvoInpatientWarning) {
        isValid = false;
      }
    }

    if (isElrexvayliSelected && Q37ElrexDetails && Q37ElrexDetails.length > 0) {
      const updatedArray = Q37ElrexDetails.map((item) => {
        let Q40DosingFreqEmptyHard = false;

        let Q40IgGEmptyHard = false;
        let Q40ReasonEmptyHard = false;
        let Q40Init_RdHard = false;
        let Q40Init_dtHard = false;
        let InvalidQ40init_dt = false;
        let init_dt_before_eof = false;
        let init_dt_full_chain_invalid = false;

        let Q40DosingFreqTxtEmptyHard = false;

        // Q40/Q40.C is never skipped based on the Q39/Q39.C answer - it is
        // always displayed (and therefore always validated) for a selected
        // cohort.
        const skipQ40Elr = false;

        if (!isNewEcrfC && !skipQ40Elr) {
          if (item.Q40dosingfreqRd == null) {
            Q40DosingFreqEmptyHard = true;
          }

          if (item.Q40dosingfreqRd === 5 && item.dosing_freq_oth_txt === "") {
            Q40DosingFreqTxtEmptyHard = true;
          }

          if (item.Q40dosingfreqRd !== 1) {
            if (item.Q40IgILevelRd === null) {
              Q40IgGEmptyHard = true;
            }

            if (
              item.primaryProf === false &&
              item.secondaryProf === false &&
              item.otherReson === false
            ) {
              Q40ReasonEmptyHard = true;
            }

            if (item.Q40init_dt_Rd === null) {
              Q40Init_RdHard = true;
            }

            if (!Q40Init_RdHard && item.Q40init_dt_Rd === 1) {
              if (isStringEmpty(item.Q40init_dt)) {
                Q40Init_dtHard = true;
              } else if (!IsDateValid(item.Q40init_dt)) {
                InvalidQ40init_dt = true;
              } else {
                if (
                  !ValidateIndexDAndLOTDateChain(
                    item.Q40init_dt,
                    RRMMCohortTreatment.ELREXFIO,
                  )
                ) {
                  init_dt_before_eof = true;
                }
                if (
                  !ValidateQ40FullDateChain(
                    item.Q40init_dt,
                    RRMMCohortTreatment.ELREXFIO,
                  )
                ) {
                  init_dt_full_chain_invalid = true;
                }
              }
            }
          }
        }

        //40.C - Elrexfio
        let ivigInitRdHard_elrC = false;
        let ivigInitDtHard_elrC = false;
        let ivigInitDtInvalid_elrC = false;
        let ivigInitDtBeforeEof_elrC = false;
        let ivigInitDtFullChainInvalid_elrC = false;
        let ivigDosingTypeEmptyHard_elrC = false;
        let ivigDosingFreqEmptyHard_elrC = false;
        let ivigDosingFreqTxtEmptyHard_elrC = false;
        let ivigDosingFreqRangeInvalid_elrC = false;
        let ivigReasonEmptyHard_elrC = false;
        let ivigReasonOtherTxtEmptyHard_elrC = false;
        let ivigDoseStrengthEmptyHard_elrC = false;
        let ivigDoseStrengthRangeInvalid_elrC = false;
        let ivigDoseStrengthMgKgRangeInvalid_elrC = false;
        let ivigDoseStrengthOtherTxtEmptyHard_elrC = false;
        let ivigBodyWeightEmptyHard_elrC = false;
        let ivigBodyWeightRangeInvalid_elrC = false;
        let ivigLastIggEmptyHard_elrC = false;

        if (isNewEcrfC && !skipQ40Elr) {
          if (item.ivigInitRd_elrC === null) {
            ivigInitRdHard_elrC = true;
          }

          if (item.ivigInitRd_elrC === 2) {
            if (isStringEmpty(item.ivigInitDt_elrC)) {
              ivigInitDtHard_elrC = true;
            } else if (!IsDateValid(item.ivigInitDt_elrC)) {
              ivigInitDtInvalid_elrC = true;
            } else {
              if (
                !ValidateQ40CInitDtDateChain(
                  item.ivigInitDt_elrC,
                  RRMMCohortTreatment.ELREXFIO,
                )
              ) {
                ivigInitDtBeforeEof_elrC = true;
              }
              if (
                !ValidateQ40FullDateChain(
                  item.ivigInitDt_elrC,
                  RRMMCohortTreatment.ELREXFIO,
                )
              ) {
                ivigInitDtFullChainInvalid_elrC = true;
              }
            }
          }

          if (item.ivigInitRd_elrC !== 1) {
            if (item.ivigDosingType_elrC === null) {
              ivigDosingTypeEmptyHard_elrC = true;
            }

            if (item.ivigDosingFreqRd_elrC === null) {
              ivigDosingFreqEmptyHard_elrC = true;
            } else if (item.ivigDosingFreqRd_elrC === 3) {
              if (isStringEmpty(item.ivigDosingFreqOtherTxt_elrC)) {
                ivigDosingFreqTxtEmptyHard_elrC = true;
              }
            } else if (item.ivigDosingFreqRd_elrC !== 99) {
              const freqTxt =
                item.ivigDosingFreqRd_elrC === 1
                  ? item.ivigDosingFreqTimesTxt_elrC
                  : item.ivigDosingFreqWeeksTxt_elrC;
              if (isStringEmpty(freqTxt)) {
                ivigDosingFreqTxtEmptyHard_elrC = true;
              } else {
                const freqNum = parseInt(freqTxt, 10);
                if (
                  isNaN(freqNum) ||
                  freqNum <= 0 ||
                  freqNum > 10 ||
                  !Number.isInteger(Number(freqTxt))
                ) {
                  ivigDosingFreqRangeInvalid_elrC = true;
                }
              }
            }

            if (
              item.ivigReasonPrimary_elrC === false &&
              item.ivigReasonSecondary_elrC === false &&
              item.ivigReasonOther_elrC === false &&
              item.ivigReasonUnk_elrC === false
            ) {
              ivigReasonEmptyHard_elrC = true;
            }
            if (
              item.ivigReasonOther_elrC === true &&
              isStringEmpty(item.ivigReasonOtherTxt_elrC)
            ) {
              ivigReasonOtherTxtEmptyHard_elrC = true;
            }

            // "g" is only offered for Flat dosage and "mg/kg" only for
            // Weight-based dosage, so a stale selection counts as unanswered.
            if (
              item.ivigDoseStrengthRd_elrC === null ||
              (item.ivigDoseStrengthRd_elrC === 1 &&
                item.ivigDosingType_elrC !== 1) ||
              (item.ivigDoseStrengthRd_elrC === 3 &&
                item.ivigDosingType_elrC !== 2)
            ) {
              ivigDoseStrengthEmptyHard_elrC = true;
            } else if (item.ivigDoseStrengthRd_elrC === 1) {
              if (isStringEmpty(item.ivigDoseStrengthGTxt_elrC)) {
                ivigDoseStrengthEmptyHard_elrC = true;
              } else {
                const gNum = parseFloat(item.ivigDoseStrengthGTxt_elrC);
                if (isNaN(gNum) || gNum <= 0 || gNum >= 200) {
                  ivigDoseStrengthRangeInvalid_elrC = true;
                }
              }
            } else if (item.ivigDoseStrengthRd_elrC === 3) {
              if (isStringEmpty(item.ivigDoseStrengthMgKgTxt_elrC)) {
                ivigDoseStrengthEmptyHard_elrC = true;
              } else {
                const mgKgNum = parseFloat(item.ivigDoseStrengthMgKgTxt_elrC);
                if (isNaN(mgKgNum) || mgKgNum <= 0 || mgKgNum >= 2000) {
                  ivigDoseStrengthMgKgRangeInvalid_elrC = true;
                }
              }
            } else if (item.ivigDoseStrengthRd_elrC === 2) {
              if (isStringEmpty(item.ivigDoseStrengthOtherTxt_elrC)) {
                ivigDoseStrengthOtherTxtEmptyHard_elrC = true;
              }
            }

            // [H] Body weight is only required for Weight-based dosage
            if (item.ivigDosingType_elrC === 2) {
              if (item.ivigBodyWeightRd_elrC === null) {
                ivigBodyWeightEmptyHard_elrC = true;
              } else if (item.ivigBodyWeightRd_elrC === 1) {
                if (isStringEmpty(item.ivigBodyWeightKgTxt_elrC)) {
                  ivigBodyWeightEmptyHard_elrC = true;
                } else if (
                  IsBodyWeightInvalid(item.ivigBodyWeightKgTxt_elrC, 1, 630)
                ) {
                  ivigBodyWeightRangeInvalid_elrC = true;
                }
              } else if (item.ivigBodyWeightRd_elrC === 2) {
                if (isStringEmpty(item.ivigBodyWeightLbsTxt_elrC)) {
                  ivigBodyWeightEmptyHard_elrC = true;
                } else if (
                  IsBodyWeightInvalid(item.ivigBodyWeightLbsTxt_elrC, 1, 1400)
                ) {
                  ivigBodyWeightRangeInvalid_elrC = true;
                }
              }
            }

            if (item.ivigLastIgg_elrC === null) {
              ivigLastIggEmptyHard_elrC = true;
            }
          }
        }

        // Q37 retired
        /*
        //37Elr
        let q37ElrexvayliEmpty = false;
        if (
          isNewEcrfA === false &&
          item.q37_Elr_Dose_week_frquencyRd !== 1 &&
          item.Q37ElrexvayliRd === null
        ) {
          q37ElrexvayliEmpty = true;
        }

        let q37_Elr_Dose_week_frquencyRd_Empty = false;
        if (isNewEcrfA === false && item.q37_Elr_Dose_week_frquencyRd == null) {
          q37_Elr_Dose_week_frquencyRd_Empty = true;
        }

        let q37_Elr_Dose_week_frquency_txt_Empty = false;
        let q37_Elr_Dose_week_freq_range = false;
        if (
          isNewEcrfA === false &&
          item.q37_Elr_Dose_week_frquencyRd === 2 &&
          (item.q37_Elr_Dose_week_frquency_txt === "" ||
            item.q37_Elr_Dose_week_frquency_txt === null)
        ) {
          q37_Elr_Dose_week_frquency_txt_Empty = true;
        }

        //invalid Number
        if (
          isNewEcrfA === false &&
          item.q37_Elr_Dose_week_frquencyRd == 2 &&
          (item.q37_Elr_Dose_week_frquency_txt !== "" ||
            item.q37_Elr_Dose_week_frquency_txt !== null)
        ) {
          const checkValue = isValidNumber(item.q37_Elr_Dose_week_frquency_txt);
          if (
            item.q37_Elr_Dose_week_frquency_txt &&
            item.q37_Elr_Dose_week_frquencyRd_Empty == false &&
            !checkValue
          ) {
            q37_Elr_Dose_week_freq_range = true;
          }
        }

        let q37SubsequentDoseConflict = false;
        // Check if any dose in Q36 has "Subsequent treatment doses" (dosingphaseelr === 4)
        const hasSubsequentDoses = elrexvayliDetails.some(
          (item) => item.dosing_phase_elr === 4,
        );

        // Check if Q37 answer is "Patient has not completed the step-up dosing phase" (q37ElrDoseweekfrquencyRd === 1)
        const q37NotCompleted = item.q37_Elr_Dose_week_frquencyRd === 1;

        if (hasSubsequentDoses && q37NotCompleted && isNewEcrfA === false) {
          isValid = false;
          q37SubsequentDoseConflict = true;
        }

        let q37ElrDosageRdEmpty = false;
        if (
          isNewEcrfA === false &&
          item.q37_Elr_Dose_week_frquencyRd !== 1 &&
          item.q37ElrDosageRd == null
        ) {
          q37ElrDosageRdEmpty = true;
        }

        let q37ElrDosageOtherTxtEmpty = false;
        if (
          isNewEcrfA === false &&
          item.q37_Elr_Dose_week_frquencyRd !== 1 &&
          item.q37ElrDosageRd === 2 &&
          (!item.q37ElrDosageOtherTxt || item.q37ElrDosageOtherTxt == null)
        ) {
          q37ElrDosageOtherTxtEmpty = true;
        }
        */
        let q37ElrexvayliEmpty = false;
        let q37_Elr_Dose_week_frquencyRd_Empty = false;
        let q37_Elr_Dose_week_frquency_txt_Empty = false;
        let q37_Elr_Dose_week_freq_range = false;
        let q37SubsequentDoseConflict = false;
        let q37ElrDosageRdEmpty = false;
        let q37ElrDosageOtherTxtEmpty = false;

        let Q39ElrEmptyHard = false;
        let Q39elrReasonHard = false;
        let Q39elrGranduloEmptyHard = false;
        let Q39elrSteroidsEmptyHard = false;

        let name_dose_steroidsHardEmpty = false;
        let steroids_elr_txtEmpty = false;

        if (!isNewEcrfC) {
          if (
            item.tocilizumab_elr === false &&
            item.Granulocyte === false &&
            item.SteroidsElr === false &&
            item.noneoftheabove_elr !== 99
          ) {
            Q39ElrEmptyHard = true;
          }

          // Q39.1 retired
          /*
          if (item.tocilizumab_elr === true && !isNewEcrfA) {
            if (
              item.cytokin_elr === false &&
              item.secondary_prophylactic_crs_elr === false &&
              item.crs_elr === false &&
              item.neurotoxicity_elr === false &&
              item.tmt_neurotoxicity_elr === false &&
              item.secondary_neurotoxicity_elr === false &&
              item.tmt_elr_unknown === false
            ) {
              Q39elrReasonHard = true;
            }
          }
          */

          if (item.Granulocyte === true) {
            if (
              item.prophylactic_use_elr === false &&
              item.secondary_prophylactic_use_elr === false &&
              item.oth_use_elr === false &&
              item.unknown_elr === null
            ) {
              Q39elrGranduloEmptyHard = true;
            }
          }

          if (item.SteroidsElr === true) {
            if (
              item.cytokin_elr_steroids === false &&
              item.secondary_prophylactic_elr_steroids === false &&
              item.crs_elr_steroids === false &&
              item.neurotoxicity_elr_steroids === false &&
              item.secondary_neurotoxicity_elr_steroids === false &&
              item.tmt_neurotoxicity_elr_steroids === false &&
              item.tmt_steroids_elr_unknown === false &&
              item.steroid_pre_tmt_elr === false
            ) {
              Q39elrSteroidsEmptyHard = true;
            }

            if (
              item.betamethasone_elr === false &&
              item.dexamethasone_elr === false &&
              item.hydrocortisone_elr === false &&
              item.methylprednisolone === false &&
              item.prednisolone_elr === false &&
              item.prednisone_elr === false &&
              item.triamcinolone_elr === false &&
              item.dose_name_elr_unknown === false
            ) {
              name_dose_steroidsHardEmpty = true;
            }

            if (
              (item.betamethasone_elr === true &&
                (!item.betamethasone_elrNum === "" ||
                  item.betamethasone_elrNum === "")) ||
              (item.dexamethasone_elr === true &&
                (!item.dexamethasone_elrNum === "" ||
                  item.dexamethasone_elrNum === "")) ||
              (item.hydrocortisone_elr === true &&
                (!item.hydrocortisone_elrNum ||
                  item.hydrocortisone_elrNum === "")) ||
              (item.methylprednisolone === true &&
                (!item.methylprednisoloneNum ||
                  item.methylprednisoloneNum === "")) ||
              (item.prednisolone_elr === true &&
                (!item.prednisolone_elrNum ||
                  item.prednisolone_elrNum === "")) ||
              (item.prednisone_elr === true &&
                (!item.prednisone_elrNum || item.prednisone_elrNum === "")) ||
              (item.triamcinolone_elr === true &&
                (!item.triamcinolone_elrNum ||
                  item.triamcinolone_elrNum === ""))
            ) {
              steroids_elr_txtEmpty = true;
            }
          }
        }

        //39.1.A Elrexfio
        if (
          isNewEcrfA &&
          !isNewEcrfC &&
          item.tocilizumab_elr === true &&
          q391AElrTocilzumabList?.length > 0
        ) {
          let elrIsValid = true;

          const updatedArray = q391AElrTocilzumabList.map((elrRow) => {
            const hasAnyReason =
              elrRow.primaryProphylacticCrs_elr === true ||
              elrRow.secondaryProphylacticCrs_elr === true ||
              elrRow.supportiveCareCrs_elr === true ||
              elrRow.primaryProphylacticNeurologic_elr === true ||
              elrRow.secondaryProphylacticNeurologic_elr === true ||
              elrRow.supportiveCareNeurologic_elr === true ||
              elrRow.medicationReasonUnk_elr === true;

            const q39ElrReasonEmpty = !hasAnyReason;

            const q39ElrAdministrationRDEmpty =
              !elrRow.administrationDateRD_elr;

            let q39ElrAdministrationDateEmpty = false;
            let q39ElrInvalidAdministrationDate = false;

            if (elrRow.administrationDateRD_elr === 1) {
              if (!elrRow.administrationDate_elr) {
                q39ElrAdministrationDateEmpty = true;
              } else if (
                elrRow.administrationDate_elr === "Invalid Date" ||
                !DateInRange(
                  elrRow.administrationDate_elr,
                  dayjs("01-01-1920"),
                  dayjs(),
                )
              ) {
                q39ElrInvalidAdministrationDate = true;
              } else {
              }
            }

            // ---- Aggregate validity ----
            if (
              q39ElrReasonEmpty ||
              q39ElrAdministrationRDEmpty ||
              q39ElrAdministrationDateEmpty ||
              q39ElrInvalidAdministrationDate
            ) {
              elrIsValid = false;
            }

            return {
              ...elrRow,
              q39ElrReasonEmpty,
              q39ElrAdministrationRDEmpty,
              q39ElrAdministrationDateEmpty,
              q39ElrInvalidAdministrationDate,
            };
          });

          const adminDates = q391AElrTocilzumabList
            .filter(
              (row) =>
                row.administrationDateRD_elr === 1 &&
                row.administrationDate_elr &&
                IsValidDateValue(row.administrationDate_elr),
            )
            .map((row) => row.administrationDate_elr);
          const earliestAdminDate =
            adminDates.length > 0
              ? adminDates.reduce((min, d) => (IsLessOrEqual(d, min) ? d : min))
              : null;

          const latestAdminDate =
            adminDates.length > 0
              ? adminDates.reduce((max, d) => (IsLessOrEqual(max, d) ? d : max))
              : null;

          // Bispecific/CAR-T index date ≤ initiation date ≤ earliest 1st
          // subsequent treatment dose in Q36 ≤ administration date ≤ last
          // administration in column [D] of Q26 (if known) ≤ end date of LOT
          // in Q26.2 ≤ start date of subsequent LOT if reported in
          // Q26/Q26.1/Q26.2/Q27/Q31 ≤ end of follow-up.
          const elrFirstSubQ36DateForQ39 = GetQ36FirstSubsequentDoseDate(
            elrexvayliDetails,
            Q381A_ELREX_AUTO_CONFIG,
          );
          const q391ElrChainBounds = GetQ361ChainBoundaryDates(
            RRMMCohortTreatment.ELREXFIO,
          );

          const elrDateIsValid = IsQ361DateChainValid([
            elrexIndexDate,
            elrexInitiationDate,
            elrFirstSubQ36DateForQ39,
            earliestAdminDate,
            latestAdminDate,
            q391ElrChainBounds.q26LastAdminDt,
            q391ElrChainBounds.q262EndDate,
            q391ElrChainBounds.nextLotStartDate,
            endOfFollowUpDate,
          ]);

          setQ391AElrAdministrationDateOutOfRange(!elrDateIsValid);

          if (!elrIsValid || !elrDateIsValid) {
            isValid = false;
          }

          setQ391AElrTocilzumabList(updatedArray);
        }

        //39.C Elrexfio
        let Q39CEmptyHard = false;
        let Q391CReasonEmptyHard = false;
        let tociliAdmsDateEmpty_elrC = false;
        let tociliAdmsDateInvalid_elrC = false;
        let tociliAdmsDateRangeInvalid_elrC = false;
        let tociliDoseEmptyHard_elrC = false;
        let tociliDoseOtherEmptyHard_elrC = false;
        let tociliDoseMgkgEmptyHard_elrC = false;

        //39.2.C Elrexfio
        let name_dose_steroidsHardEmpty_elrC = false;
        let steroids_elrC_txtEmpty = false;
        let steroidsDoseRangeInvalid_elrC = false;
        let Q392CReasonEmptyHard = false;

        //39.3.C Elrexfio
        let Q393CReasonEmptyHard = false;

        if (isNewEcrfC) {
          if (
            item.tocilizumab_elrC === false &&
            item.steroids_elrC === false &&
            item.granulocyte_elrC === false &&
            item.noneoftheabove_elrC !== 99
          ) {
            Q39CEmptyHard = true;
          }

          if (item.tocilizumab_elrC === true) {
            if (
              item.tociliReasonPretratment_elrC === false &&
              item.tociliReasonPrimarycrs_elrC === false &&
              item.tociliReasonSecondarycrs_elrC === false &&
              item.tociliReasonSupportivecrs_elrC === false &&
              item.tociliReasonPrimaryneuro_elrC === false &&
              item.tociliReasonSecondaryneuro_elrC === false &&
              item.tociliReasonSupportiveneuro_elrC === false &&
              item.tociliReasonUnk_elrC === false
            ) {
              Q391CReasonEmptyHard = true;
            }

            if (item.tociliAdmsDateRD_elrC == null) {
              tociliAdmsDateEmpty_elrC = true;
            } else if (item.tociliAdmsDateRD_elrC === 1) {
              if (!item.tociliAdmsDate_elrC) {
                tociliAdmsDateEmpty_elrC = true;
              } else if (item.tociliAdmsDate_elrC === "Invalid Date") {
                tociliAdmsDateInvalid_elrC = true;
              } else if (
                !IsDateInRangeInclusive(
                  item.tociliAdmsDate_elrC,
                  elrexInitiationDate
                    ? dayjs(elrexInitiationDate).subtract(7, "day")
                    : dayjs("01-01-1920"),
                  elrexInitiationDate
                    ? dayjs(elrexInitiationDate).add(14, "day")
                    : dayjs(),
                )
              ) {
                tociliAdmsDateRangeInvalid_elrC = true;
              }
            }

            if (item.tociliDoseRd_elrC == null) {
              tociliDoseEmptyHard_elrC = true;
            } else if (
              item.tociliDoseRd_elrC === 1 &&
              (!item.tociliDoseMgkg_elrC || item.tociliDoseMgkg_elrC === "")
            ) {
              tociliDoseEmptyHard_elrC = true;
            } else if (
              item.tociliDoseRd_elrC === 1 &&
              item.tociliDoseMgkg_elrC &&
              /^\d+(\.\d{0,2})?$/.test(item.tociliDoseMgkg_elrC) &&
              (parseFloat(item.tociliDoseMgkg_elrC) <= 0 ||
                parseFloat(item.tociliDoseMgkg_elrC) > 15)
            ) {
              tociliDoseMgkgEmptyHard_elrC = true;
            } else if (
              item.tociliDoseRd_elrC === 2 &&
              (!item.tociliDoseOther_elrC || item.tociliDoseOther_elrC === "")
            ) {
              tociliDoseOtherEmptyHard_elrC = true;
            }
          }

          if (item.steroids_elrC === true) {
            if (
              item.betamethasone_elrC === false &&
              item.dexamethasone_elrC === false &&
              item.hydrocortisone_elrC === false &&
              item.methylprednisolone_elrC === false &&
              item.prednisolone_elrC === false &&
              item.prednisone_elrC === false &&
              item.triamcinolone_elrC === false &&
              item.dose_name_elrC_unknown === false
            ) {
              name_dose_steroidsHardEmpty_elrC = true;
            }

            if (
              (item.betamethasone_elrC === true &&
                (!item.betamethasone_elrCNum ||
                  item.betamethasone_elrCNum === "")) ||
              (item.dexamethasone_elrC === true &&
                (!item.dexamethasone_elrCNum ||
                  item.dexamethasone_elrCNum === "")) ||
              (item.hydrocortisone_elrC === true &&
                (!item.hydrocortisone_elrCNum ||
                  item.hydrocortisone_elrCNum === "")) ||
              (item.methylprednisolone_elrC === true &&
                (!item.methylprednisolone_elrCNum ||
                  item.methylprednisolone_elrCNum === "")) ||
              (item.prednisolone_elrC === true &&
                (!item.prednisolone_elrCNum ||
                  item.prednisolone_elrCNum === "")) ||
              (item.prednisone_elrC === true &&
                (!item.prednisone_elrCNum || item.prednisone_elrCNum === "")) ||
              (item.triamcinolone_elrC === true &&
                (!item.triamcinolone_elrCNum ||
                  item.triamcinolone_elrCNum === ""))
            ) {
              steroids_elrC_txtEmpty = true;
            }

            // Range: >=0, no maximum
            if (
              (item.betamethasone_elrC === true &&
                item.betamethasone_elrCNum !== "" &&
                parseInt(item.betamethasone_elrCNum, 10) < 0) ||
              (item.dexamethasone_elrC === true &&
                item.dexamethasone_elrCNum !== "" &&
                parseInt(item.dexamethasone_elrCNum, 10) < 0) ||
              (item.hydrocortisone_elrC === true &&
                item.hydrocortisone_elrCNum !== "" &&
                parseInt(item.hydrocortisone_elrCNum, 10) < 0) ||
              (item.methylprednisolone_elrC === true &&
                item.methylprednisolone_elrCNum !== "" &&
                parseInt(item.methylprednisolone_elrCNum, 10) < 0) ||
              (item.prednisolone_elrC === true &&
                item.prednisolone_elrCNum !== "" &&
                parseInt(item.prednisolone_elrCNum, 10) < 0) ||
              (item.prednisone_elrC === true &&
                item.prednisone_elrCNum !== "" &&
                parseInt(item.prednisone_elrCNum, 10) < 0) ||
              (item.triamcinolone_elrC === true &&
                item.triamcinolone_elrCNum !== "" &&
                parseInt(item.triamcinolone_elrCNum, 10) < 0)
            ) {
              steroidsDoseRangeInvalid_elrC = true;
            }

            if (
              item.steroidReasonPretratment_elrC === false &&
              item.steroidReasonPrimarycrs_elrC === false &&
              item.steroidReasonSecondarycrs_elrC === false &&
              item.steroidReasonSupportivecrs_elrC === false &&
              item.steroidReasonPrimaryneuro_elrC === false &&
              item.steroidReasonSecondaryneuro_elrC === false &&
              item.steroidReasonSupportiveneuro_elrC === false &&
              item.steroidReasonUnk_elrC === false
            ) {
              Q392CReasonEmptyHard = true;
            }
          }

          if (item.granulocyte_elrC === true) {
            if (
              item.prophylactic_use_elrC === false &&
              item.secondary_prophylactic_use_elrC === false &&
              item.oth_use_elrC === false &&
              item.unknown_elrC === null
            ) {
              Q393CReasonEmptyHard = true;
            }
          }

          if (
            Q39CEmptyHard ||
            Q391CReasonEmptyHard ||
            tociliAdmsDateEmpty_elrC ||
            tociliAdmsDateInvalid_elrC ||
            tociliAdmsDateRangeInvalid_elrC ||
            tociliDoseEmptyHard_elrC ||
            tociliDoseMgkgEmptyHard_elrC ||
            tociliDoseOtherEmptyHard_elrC ||
            name_dose_steroidsHardEmpty_elrC ||
            steroids_elrC_txtEmpty ||
            steroidsDoseRangeInvalid_elrC ||
            Q392CReasonEmptyHard ||
            Q393CReasonEmptyHard
          ) {
            isValid = false;
          }
        }

        if (
          Q40DosingFreqEmptyHard ||
          Q40DosingFreqTxtEmptyHard ||
          Q40IgGEmptyHard ||
          Q40ReasonEmptyHard ||
          Q40Init_RdHard ||
          Q40Init_dtHard ||
          // Q37 retired
          // q37ElrexvayliEmpty ||
          Q39ElrEmptyHard ||
          // Q39.1 retired
          // Q39elrReasonHard ||
          Q39elrGranduloEmptyHard ||
          Q39elrSteroidsEmptyHard ||
          name_dose_steroidsHardEmpty ||
          steroids_elr_txtEmpty ||
          // q37_Elr_Dose_week_frquencyRd_Empty ||
          // q37_Elr_Dose_week_frquency_txt_Empty ||
          // q37_Elr_Dose_week_freq_range ||
          // q37SubsequentDoseConflict ||
          // q37ElrDosageRdEmpty ||
          // q37ElrDosageOtherTxtEmpty ||
          InvalidQ40init_dt ||
          init_dt_before_eof ||
          init_dt_full_chain_invalid ||
          ivigInitRdHard_elrC ||
          ivigInitDtHard_elrC ||
          ivigInitDtInvalid_elrC ||
          ivigInitDtBeforeEof_elrC ||
          ivigInitDtFullChainInvalid_elrC ||
          ivigDosingTypeEmptyHard_elrC ||
          ivigDosingFreqEmptyHard_elrC ||
          ivigDosingFreqTxtEmptyHard_elrC ||
          ivigDosingFreqRangeInvalid_elrC ||
          ivigReasonEmptyHard_elrC ||
          ivigReasonOtherTxtEmptyHard_elrC ||
          ivigDoseStrengthEmptyHard_elrC ||
          ivigDoseStrengthRangeInvalid_elrC ||
          ivigDoseStrengthMgKgRangeInvalid_elrC ||
          ivigDoseStrengthOtherTxtEmptyHard_elrC ||
          ivigBodyWeightEmptyHard_elrC ||
          ivigBodyWeightRangeInvalid_elrC ||
          ivigLastIggEmptyHard_elrC
        ) {
          isValid = false;
        }
        return {
          ...item,
          Q40DosingFreqEmptyHard,
          Q40DosingFreqTxtEmptyHard,
          Q40IgGEmptyHard,
          Q40ReasonEmptyHard,
          Q40Init_RdHard,
          Q40Init_dtHard,
          // Q37 retired
          q37ElrexvayliEmpty,
          Q39ElrEmptyHard,
          Q39elrReasonHard,
          Q39elrGranduloEmptyHard,
          Q39elrSteroidsEmptyHard,
          name_dose_steroidsHardEmpty,

          steroids_elr_txtEmpty,
          q37_Elr_Dose_week_frquencyRd_Empty,
          q37_Elr_Dose_week_frquency_txt_Empty,
          q37_Elr_Dose_week_freq_range,
          q37SubsequentDoseConflict,
          q37ElrDosageRdEmpty,
          q37ElrDosageOtherTxtEmpty,
          InvalidQ40init_dt,
          init_dt_before_eof,
          init_dt_full_chain_invalid,
          Q39CEmptyHard,
          Q391CReasonEmptyHard,
          tociliAdmsDateEmpty_elrC,
          tociliAdmsDateInvalid_elrC,
          tociliAdmsDateRangeInvalid_elrC,
          tociliDoseEmptyHard_elrC,
          tociliDoseMgkgEmptyHard_elrC,
          tociliDoseOtherEmptyHard_elrC,
          name_dose_steroidsHardEmpty_elrC,
          steroids_elrC_txtEmpty,
          steroidsDoseRangeInvalid_elrC,
          Q392CReasonEmptyHard,
          Q393CReasonEmptyHard,
          ivigInitRdHard_elrC,
          ivigInitDtHard_elrC,
          ivigInitDtInvalid_elrC,
          ivigInitDtBeforeEof_elrC,
          ivigInitDtFullChainInvalid_elrC,
          ivigDosingTypeEmptyHard_elrC,
          ivigDosingFreqEmptyHard_elrC,
          ivigDosingFreqTxtEmptyHard_elrC,
          ivigDosingFreqRangeInvalid_elrC,
          ivigReasonEmptyHard_elrC,
          ivigReasonOtherTxtEmptyHard_elrC,
          ivigDoseStrengthEmptyHard_elrC,
          ivigDoseStrengthRangeInvalid_elrC,
          ivigDoseStrengthMgKgRangeInvalid_elrC,
          ivigDoseStrengthOtherTxtEmptyHard_elrC,
          ivigBodyWeightEmptyHard_elrC,
          ivigBodyWeightRangeInvalid_elrC,
          ivigLastIggEmptyHard_elrC,
        };
      });
      setQ37ElrexDetails(updatedArray);
    }

    //37linvo
    if (isLinvoveySelected) {
      if (Q37LinvoDetails && Q37LinvoDetails.length > 0) {
        const updatedArray = Q37LinvoDetails.map((item) => {
          //Q40
          let Q40LinvoDosingFreqEmptyHard = false;
          let Q40LinvoDosingFreqTxtEmptyHard = false;

          let Q40LinvoIgGEmptyHard = false;
          let Q40LinvoReasonEmptyHard = false;
          let Q40LinvoInit_RdHard = false;
          let Q40LinvoInit_dtHard = false;
          let invalidQ40Init_dt_linvo = false;
          let init_dt_before_eof_linvo = false;
          let init_dt_full_chain_invalid_linvo = false;

          // Q40/Q40.C is never skipped based on the Q39/Q39.C answer - it is
          // always displayed (and therefore always validated) for a
          // selected cohort.
          const skipQ40Linvo = false;

          if (!isNewEcrfC && !skipQ40Linvo) {
            if (item.q40DosingFreqRd_linvo == null) {
              Q40LinvoDosingFreqEmptyHard = true;
            }

            if (
              item.q40DosingFreqRd_linvo === 5 &&
              item.dosing_Freq_Oth_Txt_linvo === ""
            ) {
              Q40LinvoDosingFreqTxtEmptyHard = true;
            }

            if (item.q40DosingFreqRd_linvo !== 1) {
              if (item.q40IgILevelRd_linvo === null) {
                Q40LinvoIgGEmptyHard = true;
              }

              if (
                item.primaryProf_linvo !== true &&
                item.secondaryProf_linvo !== true &&
                item.otherReson_linvo !== true
              ) {
                Q40LinvoReasonEmptyHard = true;
              }

              if (item.q40Init_Dt_Rd_linvo === null) {
                Q40LinvoInit_RdHard = true;
              }

              if (!Q40LinvoInit_RdHard && item.q40Init_Dt_Rd_linvo === 1) {
                if (isStringEmpty(item.q40Init_Dt_linvo)) {
                  Q40LinvoInit_dtHard = true;
                } else if (!IsDateValid(item.q40Init_Dt_Rd_linvo)) {
                  invalidQ40Init_dt_linvo = true;
                } else {
                  if (
                    !ValidateIndexDAndLOTDateChain(
                      item.q40Init_Dt_linvo,
                      RRMMCohortTreatment.Linvoseltamab,
                    )
                  ) {
                    init_dt_before_eof_linvo = true;
                  }
                  if (
                    !ValidateQ40FullDateChain(
                      item.q40Init_Dt_linvo,
                      RRMMCohortTreatment.Linvoseltamab,
                    )
                  ) {
                    init_dt_full_chain_invalid_linvo = true;
                  }
                }
              }
            }
          }

          //40.C - Linvoseltamab
          let ivigInitRdHard_linvoC = false;
          let ivigInitDtHard_linvoC = false;
          let ivigInitDtInvalid_linvoC = false;
          let ivigInitDtBeforeEof_linvoC = false;
          let ivigInitDtFullChainInvalid_linvoC = false;

          let ivigDosingTypeEmptyHard_linvoC = false;

          let ivigDosingFreqEmptyHard_linvoC = false;
          let ivigDosingFreqTxtEmptyHard_linvoC = false;
          let ivigDosingFreqRangeInvalid_linvoC = false;

          let ivigReasonEmptyHard_linvoC = false;
          let ivigReasonOtherTxtEmptyHard_linvoC = false;

          let ivigDoseStrengthEmptyHard_linvoC = false;
          let ivigDoseStrengthRangeInvalid_linvoC = false;
          let ivigDoseStrengthMgKgRangeInvalid_linvoC = false;
          let ivigDoseStrengthOtherTxtEmptyHard_linvoC = false;

          let ivigBodyWeightEmptyHard_linvoC = false;
          let ivigBodyWeightRangeInvalid_linvoC = false;

          let ivigLastIggEmptyHard_linvoC = false;

          if (isNewEcrfC && !skipQ40Linvo) {
            if (item.ivigInitRd_linvoC === null) {
              ivigInitRdHard_linvoC = true;
            }

            if (item.ivigInitRd_linvoC === 2) {
              if (isStringEmpty(item.ivigInitDt_linvoC)) {
                ivigInitDtHard_linvoC = true;
              } else if (!IsDateValid(item.ivigInitDt_linvoC)) {
                ivigInitDtInvalid_linvoC = true;
              } else {
                if (
                  !ValidateQ40CInitDtDateChain(
                    item.ivigInitDt_linvoC,
                    RRMMCohortTreatment.Linvoseltamab,
                  )
                ) {
                  ivigInitDtBeforeEof_linvoC = true;
                }
                if (
                  !ValidateQ40FullDateChain(
                    item.ivigInitDt_linvoC,
                    RRMMCohortTreatment.Linvoseltamab,
                  )
                ) {
                  ivigInitDtFullChainInvalid_linvoC = true;
                }
              }
            }

            if (item.ivigInitRd_linvoC !== 1) {
              if (item.ivigDosingType_linvoC === null) {
                ivigDosingTypeEmptyHard_linvoC = true;
              }

              if (item.ivigDosingFreqRd_linvoC === null) {
                ivigDosingFreqEmptyHard_linvoC = true;
              } else if (item.ivigDosingFreqRd_linvoC === 3) {
                if (isStringEmpty(item.ivigDosingFreqOtherTxt_linvoC)) {
                  ivigDosingFreqTxtEmptyHard_linvoC = true;
                }
              } else if (item.ivigDosingFreqRd_linvoC !== 99) {
                const freqTxt =
                  item.ivigDosingFreqRd_linvoC === 1
                    ? item.ivigDosingFreqTimesTxt_linvoC
                    : item.ivigDosingFreqWeeksTxt_linvoC;
                if (isStringEmpty(freqTxt)) {
                  ivigDosingFreqTxtEmptyHard_linvoC = true;
                } else {
                  const freqNum = parseInt(freqTxt, 10);
                  if (
                    isNaN(freqNum) ||
                    freqNum <= 0 ||
                    freqNum > 10 ||
                    !Number.isInteger(Number(freqTxt))
                  ) {
                    ivigDosingFreqRangeInvalid_linvoC = true;
                  }
                }
              }

              if (
                item.ivigReasonPrimary_linvoC === false &&
                item.ivigReasonSecondary_linvoC === false &&
                item.ivigReasonOther_linvoC === false &&
                item.ivigReasonUnk_linvoC === false
              ) {
                ivigReasonEmptyHard_linvoC = true;
              }
              if (
                item.ivigReasonOther_linvoC === true &&
                isStringEmpty(item.ivigReasonOtherTxt_linvoC)
              ) {
                ivigReasonOtherTxtEmptyHard_linvoC = true;
              }

              // "g" is only offered for Flat dosage and "mg/kg" only for
              // Weight-based dosage, so a stale selection counts as unanswered.
              if (
                item.ivigDoseStrengthRd_linvoC === null ||
                (item.ivigDoseStrengthRd_linvoC === 1 &&
                  item.ivigDosingType_linvoC !== 1) ||
                (item.ivigDoseStrengthRd_linvoC === 3 &&
                  item.ivigDosingType_linvoC !== 2)
              ) {
                ivigDoseStrengthEmptyHard_linvoC = true;
              } else if (item.ivigDoseStrengthRd_linvoC === 1) {
                if (isStringEmpty(item.ivigDoseStrengthGTxt_linvoC)) {
                  ivigDoseStrengthEmptyHard_linvoC = true;
                } else {
                  const gNum = parseFloat(item.ivigDoseStrengthGTxt_linvoC);
                  if (isNaN(gNum) || gNum <= 0 || gNum >= 200) {
                    ivigDoseStrengthRangeInvalid_linvoC = true;
                  }
                }
              } else if (item.ivigDoseStrengthRd_linvoC === 3) {
                if (isStringEmpty(item.ivigDoseStrengthMgKgTxt_linvoC)) {
                  ivigDoseStrengthEmptyHard_linvoC = true;
                } else {
                  const mgKgNum = parseFloat(
                    item.ivigDoseStrengthMgKgTxt_linvoC,
                  );
                  if (isNaN(mgKgNum) || mgKgNum <= 0 || mgKgNum >= 2000) {
                    ivigDoseStrengthMgKgRangeInvalid_linvoC = true;
                  }
                }
              } else if (item.ivigDoseStrengthRd_linvoC === 2) {
                if (isStringEmpty(item.ivigDoseStrengthOtherTxt_linvoC)) {
                  ivigDoseStrengthOtherTxtEmptyHard_linvoC = true;
                }
              }

              // [H] Body weight is only required for Weight-based dosage
              if (item.ivigDosingType_linvoC === 2) {
                if (item.ivigBodyWeightRd_linvoC === null) {
                  ivigBodyWeightEmptyHard_linvoC = true;
                } else if (item.ivigBodyWeightRd_linvoC === 1) {
                  if (isStringEmpty(item.ivigBodyWeightKgTxt_linvoC)) {
                    ivigBodyWeightEmptyHard_linvoC = true;
                  } else if (
                    IsBodyWeightInvalid(item.ivigBodyWeightKgTxt_linvoC, 1, 630)
                  ) {
                    ivigBodyWeightRangeInvalid_linvoC = true;
                  }
                } else if (item.ivigBodyWeightRd_linvoC === 2) {
                  if (isStringEmpty(item.ivigBodyWeightLbsTxt_linvoC)) {
                    ivigBodyWeightEmptyHard_linvoC = true;
                  } else if (
                    IsBodyWeightInvalid(
                      item.ivigBodyWeightLbsTxt_linvoC,
                      1,
                      1400,
                    )
                  ) {
                    ivigBodyWeightRangeInvalid_linvoC = true;
                  }
                }
              }

              if (item.ivigLastIgg_linvoC === null) {
                ivigLastIggEmptyHard_linvoC = true;
              }
            }
          }

          let Q39LinvoEmptyHard = false;

          let Q39linvoSteroidsEmptyHard = false;
          let name_dose_steroids_linvoHardEmpty = false;
          let steroids_linvo_txtEmpty = false;

          let Q39linvoReasonHard = false;

          let Q39linvoGranduloEmptyHard = false;
          // Q37 retired
          /*
          let q37LinvoveyEmpty = false;
          if (
            isNewEcrfA === false &&
            item.q37_Linvo_Dose_week_frquencyRd !== 1 &&
            item.Q37LinvoveyRd === null
          ) {
            q37LinvoveyEmpty = true;
          }

          if (q37LinvoveyEmpty) {
            isValid = false;
          }

          let q37_Linvo_Dose_week_frquencyRd_Empty = false;
          if (
            isNewEcrfA === false &&
            item.q37_Linvo_Dose_week_frquencyRd == null
          ) {
            q37_Linvo_Dose_week_frquencyRd_Empty = true;
          }

          let q37_Linvo_Dose_week_frquency_txt_Empty = false;
          let q37_Linvo_Dose_week_freq_range = false;
          if (
            isNewEcrfA === false &&
            item.q37_Linvo_Dose_week_frquencyRd == 2 &&
            (item.q37_Linvo_Dose_week_frquency_txt === "" ||
              item.q37_Linvo_Dose_week_frquency_txt === null)
          ) {
            q37_Linvo_Dose_week_frquency_txt_Empty = true;
          }

          if (
            isNewEcrfA === false &&
            item.q37_Linvo_Dose_week_frquencyRd == 2 &&
            (item.q37_Linvo_Dose_week_frquency_txt !== "" ||
              item.q37_Linvo_Dose_week_frquency_txt !== null)
          ) {
            const checkValue = isValidNumber(
              item.q37_Linvo_Dose_week_frquency_txt,
            );
            if (
              isNewEcrfA === false &&
              item.q37_Linvo_Dose_week_frquency_txt &&
              item.q37_Linvo_Dose_week_frquencyRd_Empty == false &&
              !checkValue
            ) {
              q37_Linvo_Dose_week_freq_range = true;
            }
          }

          let q37LinvoSubsequentDoseConflict = false;
          // Check if any dose in Q36 has "Subsequent treatment doses" (dosingphaselinvo === 5)
          const hasSubsequentDosesLinvo = linvoveyDetails.some(
            (item) => item.dosing_phase_linvo === 5,
          );

          // Check if Q37 answer is "Patient has not completed the step-up dosing phase"
          const q37LinvoNotCompleted =
            item.q37_Linvo_Dose_week_frquencyRd === 1;

          if (
            hasSubsequentDosesLinvo &&
            q37LinvoNotCompleted &&
            isNewEcrfA === false
          ) {
            isValid = false;
            q37LinvoSubsequentDoseConflict = true;
          }

          let q37_Linvo_Dosage_Rd_Empty = false;
          if (
            isNewEcrfA === false &&
            item.q37_Linvo_Dose_week_frquencyRd != 1 &&
            item.q37_Linvo_Dosage_Rd == null
          ) {
            q37_Linvo_Dosage_Rd_Empty = true;
          }

          let q37_Linvo_Dosage_OtherTxt_Empty = false;
          if (
            isNewEcrfA === false &&
            item.q37_Linvo_Dosage_Rd == 2 &&
            (item.q37_Linvo_Dosage_OtherTxt == "" ||
              item.q37_Linvo_Dosage_OtherTxt == null)
          ) {
            q37_Linvo_Dosage_OtherTxt_Empty = true;
          }
          */
          let q37LinvoveyEmpty = false;
          let q37_Linvo_Dose_week_frquencyRd_Empty = false;
          let q37_Linvo_Dose_week_frquency_txt_Empty = false;
          let q37_Linvo_Dose_week_freq_range = false;
          let q37LinvoSubsequentDoseConflict = false;
          let q37_Linvo_Dosage_Rd_Empty = false;
          let q37_Linvo_Dosage_OtherTxt_Empty = false;

          //39linvo
          if (!isNewEcrfC) {
            if (
              item.tocilizumab_linvo === false &&
              item.granulocyte_linvo === false &&
              item.Steroids === false &&
              item.noneoftheabove_linvo !== 99
            ) {
              Q39LinvoEmptyHard = true;
            }

            // Q39.1 retired
            /*
            if (item.tocilizumab_linvo === true && !isNewEcrfA) {
              if (
                item.cytokin_linvo === false &&
                item.secondary_prophylactic_crs_linvo === false &&
                item.crs_linvo === false &&
                item.neurotoxicity_linvo === false &&
                item.secondary_neurotoxicity_linvo === false &&
                item.tmt_neurotoxicity_linvo === false &&
                item.tmt_linvoseltamab_unknown === false
              ) {
                Q39linvoReasonHard = true;
              }
            }
            */

            if (item.granulocyte_linvo === true) {
              if (
                item.prophylactic_use_linvo === false &&
                item.secondary_prophylactic_use_linvo === false &&
                item.oth_use_linvo === false &&
                item.unknown_linvo === null
              ) {
                Q39linvoGranduloEmptyHard = true;
              }
            }

            if (item.Steroids === true) {
              if (
                item.cytokin_linvo_steroids === false &&
                item.secondary_prophylactic_linvo_steroids === false &&
                item.crs_linvo_steroids === false &&
                item.neurotoxicity_linvo_steroids === false &&
                item.secondary_neurotoxicity_linvo_steroids === false &&
                item.tmt_neurotoxicity_linvo_steroids === false &&
                item.tmt_linvo_unknown === false &&
                item.steroid_pre_tmt_linvo === false
              ) {
                Q39linvoSteroidsEmptyHard = true;
              }

              if (
                item.betamethasone_linvo === false &&
                item.dexamethasone_linvo === false &&
                item.hydrocortisone_linvo === false &&
                item.methylprednisolone_linvo === false &&
                item.prednisolone_linvo === false &&
                item.prednisone_linvo === false &&
                item.triamcinolone_linvo === false &&
                item.dose_name_linvo_unknown === false
              ) {
                name_dose_steroids_linvoHardEmpty = true;
              }

              if (
                (item.betamethasone_linvo === true &&
                  (!item.betamethasone_linvoNum ||
                    item.betamethasone_linvoNum === "")) ||
                (item.dexamethasone_linvo === true &&
                  (!item.dexamethasone_linvoNum ||
                    item.dexamethasone_linvoNum === "")) ||
                (item.hydrocortisone_linvo === true &&
                  (!item.hydrocortisone_linvoNum ||
                    item.hydrocortisone_linvoNum === "")) ||
                (item.methylprednisolone_linvo === true &&
                  (!item.methylprednisolone_linvoNum ||
                    item.methylprednisolone_linvoNum === "")) ||
                (item.prednisolone_linvo === true &&
                  (!item.prednisolone_linvoNum ||
                    item.prednisolone_linvoNum === "")) ||
                (item.prednisone_linvo === true &&
                  (!item.prednisone_linvoNum ||
                    item.prednisone_linvoNum === "")) ||
                (item.triamcinolone_linvo === true &&
                  (!item.triamcinolone_linvoNum ||
                    item.triamcinolone_linvoNum === ""))
              ) {
                steroids_linvo_txtEmpty = true;
              }
            }
          }

          //39.1.A Linvo
          if (
            isNewEcrfA &&
            !isNewEcrfC &&
            item.tocilizumab_linvo === true &&
            q391ALinvoTocilzumabList?.length > 0
          ) {
            let linvoIsValid = true;

            const updatedArray = q391ALinvoTocilzumabList.map((linvoRow) => {
              const hasAnyReason =
                linvoRow.primaryProphylacticCrs_linvo === true ||
                linvoRow.secondaryProphylacticCrs_linvo === true ||
                linvoRow.supportiveCareCrs_linvo === true ||
                linvoRow.primaryProphylacticNeurologic_linvo === true ||
                linvoRow.secondaryProphylacticNeurologic_linvo === true ||
                linvoRow.supportiveCareNeurologic_linvo === true ||
                linvoRow.medicationReasonUnk_linvo === true;

              const q39LinvoReasonEmpty = !hasAnyReason;

              const q39LinvoAdministrationRDEmpty =
                !linvoRow.administrationDateRD_linvo;

              let q39LinvoAdministrationDateEmpty = false;
              let q39LinvoInvalidAdministrationDate = false;

              if (linvoRow.administrationDateRD_linvo === 1) {
                if (!linvoRow.administrationDate_linvo) {
                  q39LinvoAdministrationDateEmpty = true;
                } else if (
                  linvoRow.administrationDate_linvo === "Invalid Date" ||
                  !DateInRange(
                    linvoRow.administrationDate_linvo,
                    dayjs("01-01-1920"),
                    dayjs(),
                  )
                ) {
                  q39LinvoInvalidAdministrationDate = true;
                }
              }

              // ---- Aggregate validity ----
              if (
                q39LinvoReasonEmpty ||
                q39LinvoAdministrationRDEmpty ||
                q39LinvoAdministrationDateEmpty ||
                q39LinvoInvalidAdministrationDate
              ) {
                linvoIsValid = false;
              }

              return {
                ...linvoRow,
                q39LinvoReasonEmpty,
                q39LinvoAdministrationRDEmpty,
                q39LinvoAdministrationDateEmpty,
                q39LinvoInvalidAdministrationDate,
              };
            });

            const adminDates = q391ALinvoTocilzumabList
              .filter(
                (row) =>
                  row.administrationDateRD_linvo === 1 &&
                  row.administrationDate_linvo &&
                  IsValidDateValue(row.administrationDate_linvo),
              )
              .map((row) => row.administrationDate_linvo);
            const earliestAdminDate =
              adminDates.length > 0
                ? adminDates.reduce((min, d) =>
                    IsLessOrEqual(d, min) ? d : min,
                  )
                : null;

            const latestAdminDate =
              adminDates.length > 0
                ? adminDates.reduce((max, d) =>
                    IsLessOrEqual(max, d) ? d : max,
                  )
                : null;

            // Bispecific/CAR-T index date ≤ initiation date ≤ earliest 1st
            // subsequent treatment dose in Q36 ≤ administration date ≤ last
            // administration in column [D] of Q26 (if known) ≤ end date of
            // LOT in Q26.2 ≤ start date of subsequent LOT if reported in
            // Q26/Q26.1/Q26.2/Q27/Q31 ≤ end of follow-up.
            const linvoFirstSubQ36DateForQ39 = GetQ36FirstSubsequentDoseDate(
              linvoveyDetails,
              Q381A_LINVO_AUTO_CONFIG,
            );
            const q391LinvoChainBounds = GetQ361ChainBoundaryDates(
              RRMMCohortTreatment.Linvoseltamab,
            );

            const linvoDateIsValid = IsQ361DateChainValid([
              linvoIndexDate,
              linvoInitiationDate,
              linvoFirstSubQ36DateForQ39,
              earliestAdminDate,
              latestAdminDate,
              q391LinvoChainBounds.q26LastAdminDt,
              q391LinvoChainBounds.q262EndDate,
              q391LinvoChainBounds.nextLotStartDate,
              endOfFollowUpDate,
            ]);

            setQ391ALinvoAdministrationDateOutOfRange(!linvoDateIsValid);

            if (!linvoIsValid || !linvoDateIsValid) {
              isValid = false;
            }

            setQ391ALinvoTocilzumabList(updatedArray);
          }

          //39.C Linvoseltamab
          let Q39CEmptyHard = false;
          let Q391CReasonEmptyHard = false;
          let tociliAdmsDateEmpty_linvoC = false;
          let tociliAdmsDateInvalid_linvoC = false;
          let tociliAdmsDateRangeInvalid_linvoC = false;
          let tociliDoseEmptyHard_linvoC = false;
          let tociliDoseOtherEmptyHard_linvoC = false;
          let tociliDoseMgkgEmptyHard_linvoC = false;

          //39.2.C Linvoseltamab
          let name_dose_steroidsHardEmpty_linvoC = false;
          let steroids_linvoC_txtEmpty = false;
          let steroidsDoseRangeInvalid_linvoC = false;
          let Q392CReasonEmptyHard = false;

          //39.3.C Linvoseltamab
          let Q393CReasonEmptyHard = false;

          if (isNewEcrfC) {
            if (
              item.tocilizumab_linvoC === false &&
              item.steroids_linvoC === false &&
              item.granulocyte_linvoC === false &&
              item.noneoftheabove_linvoC !== 99
            ) {
              Q39CEmptyHard = true;
            }

            if (item.tocilizumab_linvoC === true) {
              if (
                item.tociliReasonPretratment_linvoC === false &&
                item.tociliReasonPrimarycrs_linvoC === false &&
                item.tociliReasonSecondarycrs_linvoC === false &&
                item.tociliReasonSupportivecrs_linvoC === false &&
                item.tociliReasonPrimaryneuro_linvoC === false &&
                item.tociliReasonSecondaryneuro_linvoC === false &&
                item.tociliReasonSupportiveneuro_linvoC === false &&
                item.tociliReasonUnk_linvoC === false
              ) {
                Q391CReasonEmptyHard = true;
              }

              if (item.tociliAdmsDateRD_linvoC == null) {
                tociliAdmsDateEmpty_linvoC = true;
              } else if (item.tociliAdmsDateRD_linvoC === 1) {
                if (!item.tociliAdmsDate_linvoC) {
                  tociliAdmsDateEmpty_linvoC = true;
                } else if (item.tociliAdmsDate_linvoC === "Invalid Date") {
                  tociliAdmsDateInvalid_linvoC = true;
                } else if (
                  !IsDateInRangeInclusive(
                    item.tociliAdmsDate_linvoC,
                    linvoInitiationDate
                      ? dayjs(linvoInitiationDate).subtract(7, "day")
                      : dayjs("01-01-1920"),
                    linvoInitiationDate
                      ? dayjs(linvoInitiationDate).add(14, "day")
                      : dayjs(),
                  )
                ) {
                  tociliAdmsDateRangeInvalid_linvoC = true;
                }
              }

              if (item.tociliDoseRd_linvoC == null) {
                tociliDoseEmptyHard_linvoC = true;
              } else if (
                item.tociliDoseRd_linvoC === 1 &&
                (!item.tociliDoseMgkg_linvoC ||
                  item.tociliDoseMgkg_linvoC === "")
              ) {
                tociliDoseEmptyHard_linvoC = true;
              } else if (
                item.tociliDoseRd_linvoC === 1 &&
                item.tociliDoseMgkg_linvoC &&
                /^\d+(\.\d{0,2})?$/.test(item.tociliDoseMgkg_linvoC) &&
                (parseFloat(item.tociliDoseMgkg_linvoC) <= 0 ||
                  parseFloat(item.tociliDoseMgkg_linvoC) > 15)
              ) {
                tociliDoseMgkgEmptyHard_linvoC = true;
              } else if (
                item.tociliDoseRd_linvoC === 2 &&
                (!item.tociliDoseOther_linvoC ||
                  item.tociliDoseOther_linvoC === "")
              ) {
                tociliDoseOtherEmptyHard_linvoC = true;
              }
            }

            if (item.steroids_linvoC === true) {
              if (
                item.betamethasone_linvoC === false &&
                item.dexamethasone_linvoC === false &&
                item.hydrocortisone_linvoC === false &&
                item.methylprednisolone_linvoC === false &&
                item.prednisolone_linvoC === false &&
                item.prednisone_linvoC === false &&
                item.triamcinolone_linvoC === false &&
                item.dose_name_linvoC_unknown === false
              ) {
                name_dose_steroidsHardEmpty_linvoC = true;
              }

              if (
                (item.betamethasone_linvoC === true &&
                  (!item.betamethasone_linvoCNum ||
                    item.betamethasone_linvoCNum === "")) ||
                (item.dexamethasone_linvoC === true &&
                  (!item.dexamethasone_linvoCNum ||
                    item.dexamethasone_linvoCNum === "")) ||
                (item.hydrocortisone_linvoC === true &&
                  (!item.hydrocortisone_linvoCNum ||
                    item.hydrocortisone_linvoCNum === "")) ||
                (item.methylprednisolone_linvoC === true &&
                  (!item.methylprednisolone_linvoCNum ||
                    item.methylprednisolone_linvoCNum === "")) ||
                (item.prednisolone_linvoC === true &&
                  (!item.prednisolone_linvoCNum ||
                    item.prednisolone_linvoCNum === "")) ||
                (item.prednisone_linvoC === true &&
                  (!item.prednisone_linvoCNum ||
                    item.prednisone_linvoCNum === "")) ||
                (item.triamcinolone_linvoC === true &&
                  (!item.triamcinolone_linvoCNum ||
                    item.triamcinolone_linvoCNum === ""))
              ) {
                steroids_linvoC_txtEmpty = true;
              }

              // Range: >=0, no maximum
              if (
                (item.betamethasone_linvoC === true &&
                  item.betamethasone_linvoCNum !== "" &&
                  parseInt(item.betamethasone_linvoCNum, 10) < 0) ||
                (item.dexamethasone_linvoC === true &&
                  item.dexamethasone_linvoCNum !== "" &&
                  parseInt(item.dexamethasone_linvoCNum, 10) < 0) ||
                (item.hydrocortisone_linvoC === true &&
                  item.hydrocortisone_linvoCNum !== "" &&
                  parseInt(item.hydrocortisone_linvoCNum, 10) < 0) ||
                (item.methylprednisolone_linvoC === true &&
                  item.methylprednisolone_linvoCNum !== "" &&
                  parseInt(item.methylprednisolone_linvoCNum, 10) < 0) ||
                (item.prednisolone_linvoC === true &&
                  item.prednisolone_linvoCNum !== "" &&
                  parseInt(item.prednisolone_linvoCNum, 10) < 0) ||
                (item.prednisone_linvoC === true &&
                  item.prednisone_linvoCNum !== "" &&
                  parseInt(item.prednisone_linvoCNum, 10) < 0) ||
                (item.triamcinolone_linvoC === true &&
                  item.triamcinolone_linvoCNum !== "" &&
                  parseInt(item.triamcinolone_linvoCNum, 10) < 0)
              ) {
                steroidsDoseRangeInvalid_linvoC = true;
              }

              if (
                item.steroidReasonPretratment_linvoC === false &&
                item.steroidReasonPrimarycrs_linvoC === false &&
                item.steroidReasonSecondarycrs_linvoC === false &&
                item.steroidReasonSupportivecrs_linvoC === false &&
                item.steroidReasonPrimaryneuro_linvoC === false &&
                item.steroidReasonSecondaryneuro_linvoC === false &&
                item.steroidReasonSupportiveneuro_linvoC === false &&
                item.steroidReasonUnk_linvoC === false
              ) {
                Q392CReasonEmptyHard = true;
              }
            }

            if (item.granulocyte_linvoC === true) {
              if (
                item.prophylactic_use_linvoC === false &&
                item.secondary_prophylactic_use_linvoC === false &&
                item.oth_use_linvoC === false &&
                item.unknown_linvoC === null
              ) {
                Q393CReasonEmptyHard = true;
              }
            }

            if (
              Q39CEmptyHard ||
              Q391CReasonEmptyHard ||
              tociliAdmsDateEmpty_linvoC ||
              tociliAdmsDateInvalid_linvoC ||
              tociliAdmsDateRangeInvalid_linvoC ||
              tociliDoseEmptyHard_linvoC ||
              tociliDoseMgkgEmptyHard_linvoC ||
              tociliDoseOtherEmptyHard_linvoC ||
              name_dose_steroidsHardEmpty_linvoC ||
              steroids_linvoC_txtEmpty ||
              steroidsDoseRangeInvalid_linvoC ||
              Q392CReasonEmptyHard ||
              Q393CReasonEmptyHard
            ) {
              isValid = false;
            }
          }

          if (
            Q39LinvoEmptyHard ||
            // Q39.1 retired
            // Q39linvoReasonHard ||
            Q39linvoSteroidsEmptyHard ||
            name_dose_steroids_linvoHardEmpty ||
            steroids_linvo_txtEmpty ||
            // Q37 retired
            // q37LinvoveyEmpty ||
            // q37_Linvo_Dose_week_frquencyRd_Empty ||
            // q37_Linvo_Dose_week_frquency_txt_Empty ||
            // q37_Linvo_Dose_week_freq_range ||
            // q37LinvoSubsequentDoseConflict ||
            // q37_Linvo_Dosage_Rd_Empty ||
            // q37_Linvo_Dosage_OtherTxt_Empty ||
            Q39linvoGranduloEmptyHard ||
            Q40LinvoDosingFreqEmptyHard ||
            Q40LinvoDosingFreqTxtEmptyHard ||
            Q40LinvoIgGEmptyHard ||
            Q40LinvoReasonEmptyHard ||
            Q40LinvoInit_RdHard ||
            Q40LinvoInit_dtHard ||
            invalidQ40Init_dt_linvo ||
            init_dt_before_eof_linvo ||
            init_dt_full_chain_invalid_linvo ||
            ivigInitRdHard_linvoC ||
            ivigInitDtHard_linvoC ||
            ivigInitDtInvalid_linvoC ||
            ivigInitDtBeforeEof_linvoC ||
            ivigInitDtFullChainInvalid_linvoC ||
            ivigDosingTypeEmptyHard_linvoC ||
            ivigDosingFreqEmptyHard_linvoC ||
            ivigDosingFreqTxtEmptyHard_linvoC ||
            ivigDosingFreqRangeInvalid_linvoC ||
            ivigReasonEmptyHard_linvoC ||
            ivigReasonOtherTxtEmptyHard_linvoC ||
            ivigDoseStrengthEmptyHard_linvoC ||
            ivigDoseStrengthRangeInvalid_linvoC ||
            ivigDoseStrengthMgKgRangeInvalid_linvoC ||
            ivigDoseStrengthOtherTxtEmptyHard_linvoC ||
            ivigBodyWeightEmptyHard_linvoC ||
            ivigBodyWeightRangeInvalid_linvoC ||
            ivigLastIggEmptyHard_linvoC
          ) {
            isValid = false;
          }

          return {
            ...item,
            Q39LinvoEmptyHard,
            Q39linvoReasonHard,
            Q39linvoSteroidsEmptyHard,
            name_dose_steroids_linvoHardEmpty,
            steroids_linvo_txtEmpty,
            q37LinvoveyEmpty,
            q37_Linvo_Dosage_OtherTxt_Empty,
            q37LinvoSubsequentDoseConflict,
            q37_Linvo_Dosage_Rd_Empty,
            q37_Linvo_Dose_week_frquency_txt_Empty,
            q37_Linvo_Dose_week_freq_range,

            q37_Linvo_Dose_week_frquencyRd_Empty,
            Q39linvoGranduloEmptyHard,
            Q40LinvoDosingFreqEmptyHard,
            Q40LinvoDosingFreqTxtEmptyHard,
            Q40LinvoIgGEmptyHard,
            Q40LinvoReasonEmptyHard,
            Q40LinvoInit_RdHard,
            Q40LinvoInit_dtHard,
            invalidQ40Init_dt_linvo,
            init_dt_before_eof_linvo,
            init_dt_full_chain_invalid_linvo,
            Q39CEmptyHard,
            Q391CReasonEmptyHard,
            tociliAdmsDateEmpty_linvoC,
            tociliAdmsDateInvalid_linvoC,
            tociliAdmsDateRangeInvalid_linvoC,
            tociliDoseEmptyHard_linvoC,
            tociliDoseMgkgEmptyHard_linvoC,
            tociliDoseOtherEmptyHard_linvoC,
            name_dose_steroidsHardEmpty_linvoC,
            steroids_linvoC_txtEmpty,
            steroidsDoseRangeInvalid_linvoC,
            Q392CReasonEmptyHard,
            Q393CReasonEmptyHard,
            ivigInitRdHard_linvoC,
            ivigInitDtHard_linvoC,
            ivigInitDtInvalid_linvoC,
            ivigInitDtBeforeEof_linvoC,
            ivigInitDtFullChainInvalid_linvoC,
            ivigDosingTypeEmptyHard_linvoC,
            ivigDosingFreqEmptyHard_linvoC,
            ivigDosingFreqTxtEmptyHard_linvoC,
            ivigDosingFreqRangeInvalid_linvoC,
            ivigReasonEmptyHard_linvoC,
            ivigReasonOtherTxtEmptyHard_linvoC,
            ivigDoseStrengthEmptyHard_linvoC,
            ivigDoseStrengthRangeInvalid_linvoC,
            ivigDoseStrengthMgKgRangeInvalid_linvoC,
            ivigDoseStrengthOtherTxtEmptyHard_linvoC,
            ivigBodyWeightEmptyHard_linvoC,
            ivigBodyWeightRangeInvalid_linvoC,
            ivigLastIggEmptyHard_linvoC,
          };
        });
        setQ37LinvoDetails(updatedArray);
      }
    }

    // Q38 retired
    /*
    //38elr

    if (
      isElrexvayliSelected &&
      Q37ElrexDetails[0].Q37ElrexvayliRd === 1 &&
      Q38ElrexDetails &&
      Q38ElrexDetails.length > 0
    ) {
      const updatedArray = Q38ElrexDetails.map((item, index) => {
        let Q38ElrexEmptyHard = false;
        if (isNewEcrfA == false && item.Q38ElrexvayliRd === null) {
          Q38ElrexEmptyHard = true;
        }

        let Q38DateEmpty = false;
        if (
          isNewEcrfA == false &&
          item.Q38ElrexvayliRd === 1 &&
          item.dt_dosefreq_elr_dt === ""
        ) {
          Q38DateEmpty = true;
        }

        let Invalid_dt_dosefreq_elr_dt = false;
        if (
          isNewEcrfA == false &&
          item.Q38ElrexvayliRd === 1 &&
          item.dt_dosefreq_elr_dt &&
          item.dt_dosefreq_elr_dt === "Invalid Date"
        ) {
          Invalid_dt_dosefreq_elr_dt = true;
        }

        if (
          item.Q38ElrexvayliRd === 1 &&
          item.dt_dosefreq_elr_dt != "Invalid Date" &&
          item.dt_dosefreq_elr_dt &&
          !DateInRange(item.dt_dosefreq_elr_dt, dayjs("01-01-1920"), dayjs())
        ) {
          Invalid_dt_dosefreq_elr_dt = true;
        }

        const q36DoseAdminDates = elrexvayliDetails
          .filter(
            (row) =>
              row.dose_administration_elr === 1 &&
              IsValidDateValue(row.dose_administration_elr_dt),
          )
          .map((row) => row.dose_administration_elr_dt);

        const latestQ36DoseDate =
          q36DoseAdminDates.length > 0
            ? q36DoseAdminDates.reduce((max, d) =>
                IsLessOrEqual(max, d) ? d : max,
              )
            : null;

        let Q38ElrexDateInvalid = false;
        if (
          isNewEcrfA === false &&
          item.Q38ElrexvayliRd === 1 &&
          item.dt_dosefreq_elr_dt !== "" &&
          DateInRange(item.dt_dosefreq_elr_dt, dayjs("01-01-1920"), dayjs())
        ) {
          // Rule 1: Index Date ≤ Q38 Date
          if (elrexIndexDate !== "" && IsValidDateValue(elrexIndexDate)) {
            if (!IsLessOrEqual(elrexIndexDate, item.dt_dosefreq_elr_dt)) {
              Q38ElrexDateInvalid = true;
            }
          }

          // Rule 2: Latest Q36 Date ≤ Q38 Date
          if (
            latestQ36DoseDate &&
            !IsLessOrEqual(latestQ36DoseDate, item.dt_dosefreq_elr_dt)
          ) {
            Q38ElrexDateInvalid = true;
          }

          // Rule 3: Q38 Date ≤ LOT Start Date
          if (
            elrexfioLOTStartDate !== "" &&
            IsValidDateValue(elrexfioLOTStartDate)
          ) {
            if (!IsLessOrEqual(item.dt_dosefreq_elr_dt, elrexfioLOTStartDate)) {
              Q38ElrexDateInvalid = true;
            }
          }

          // Rule 4: LOT Start Date ≤ End of Follow-up (or Q38 ≤ EOF if no LOT)
          if (endOfFollowUpDate !== "" && IsValidDateValue(endOfFollowUpDate)) {
            if (
              elrexfioLOTStartDate !== "" &&
              IsValidDateValue(elrexfioLOTStartDate)
            ) {
              if (!IsLessOrEqual(elrexfioLOTStartDate, endOfFollowUpDate)) {
                Q38ElrexDateInvalid = true;
              }
            } else {
              // No LOT, so check Q38 ≤ EOF directly
              if (!IsLessOrEqual(item.dt_dosefreq_elr_dt, endOfFollowUpDate)) {
                Q38ElrexDateInvalid = true;
              }
            }
          }
        }

        //Q38Elr New Column Validation

        let q38ElrexfioEmpty = false;
        if (item.Q38ElrexvayliRd === null) {
          q38ElrexfioEmpty = true;
        }

        let q38_Elr_Dose_week_frquencyRd_Empty = false;
        if (isNewEcrfA == false && item.q38_Elr_Dose_week_frquencyRd == null) {
          q38_Elr_Dose_week_frquencyRd_Empty = true;
        }

        let q38_Elr_Dose_week_frquency_txt_Empty = false;
        if (
          isNewEcrfA == false &&
          item.q38_Elr_Dose_week_frquencyRd == 1 &&
          (item.q38_Elr_Dose_week_frquency_txt === "" ||
            item.q38_Elr_Dose_week_frquency_txt === null)
        ) {
          q38_Elr_Dose_week_frquency_txt_Empty = true;
        }

        //invalid Number
        if (
          item.q38_Elr_Dose_week_frquencyRd == 1 &&
          (item.q38_Elr_Dose_week_frquency_txt !== "" ||
            item.q38_Elr_Dose_week_frquency_txt !== null)
        ) {
          const checkValue = isValidNumber(item.q38_Elr_Dose_week_frquency_txt);
          if (item.q38_Elr_Dose_week_frquencyRd_Empty == false && !checkValue) {
            q38_Elr_Dose_week_frquency_txt_Empty = true;
          }
        }
        let q38ElrDoseFrequencyConflict = false;

        if (Q37ElrexDetails.length > 0) {
          const q37Frequency = Q37ElrexDetails[0].q37_Elr_Dose_week_frquencyRd;
          const q38Frequency = item.q38_Elr_Dose_week_frquencyRd;

          // Check if both are using custom frequency
          if (q37Frequency === 2 && q38Frequency === 1) {
            const q37FreqValue = parseFloat(
              Q37ElrexDetails[0].q37_Elr_Dose_week_frquency_txt,
            );
            const q38FreqValue = parseFloat(
              item.q38_Elr_Dose_week_frquency_txt,
            );

            // Q38 should be LESS than Q37 (reduction means lower frequency)
            if (
              !isNaN(q37FreqValue) &&
              !isNaN(q38FreqValue) &&
              q37FreqValue > q38FreqValue
            ) {
              q38ElrDoseFrequencyConflict = true;
              isValid = false;
            }
          }
        }

        if (
          Q38ElrexEmptyHard ||
          Q38DateEmpty ||
          Q38ElrexDateInvalid ||
          q38ElrexfioEmpty ||
          q38_Elr_Dose_week_frquencyRd_Empty ||
          q38_Elr_Dose_week_frquency_txt_Empty ||
          Invalid_dt_dosefreq_elr_dt ||
          q38ElrDoseFrequencyConflict
        ) {
          isValid = false;
        }

        return {
          ...item,

          Q38ElrexEmptyHard,
          Q38DateEmpty,
          Q38ElrexDateInvalid,
          q38ElrexfioEmpty,
          q38_Elr_Dose_week_frquencyRd_Empty,
          q38_Elr_Dose_week_frquency_txt_Empty,
          Invalid_dt_dosefreq_elr_dt,
          q38ElrDoseFrequencyConflict,
        };
      });

      setQ38ElrexDetails(updatedArray);
    }

    //38 linvo
    if (
      isLinvoveySelected &&
      Q37LinvoDetails[0].Q37LinvoveyRd === 1 &&
      Q38LinvoDetails &&
      Q38LinvoDetails.length > 0
    ) {
      const updatedArray = Q38LinvoDetails.map((item, index) => {
        let Q38LinvoEmptyHard = false;
        if (isNewEcrfA == false && item.Q38LinvoveyRd === null) {
          Q38LinvoEmptyHard = true;
        }

        let Q38LinvoDateEmpty = false;
        if (
          isNewEcrfA == false &&
          item.Q38LinvoveyRd === 1 &&
          item.dt_dosefreq_linvo_dt === ""
        ) {
          Q38LinvoDateEmpty = true;
        }

        let InvalidQ38Linvoveydate = false;

        if (
          item.Q38LinvoveyRd === 1 &&
          item.dt_dosefreq_linvo_dt &&
          item.dt_dosefreq_linvo_dt === "Invalid Date"
        ) {
          InvalidQ38Linvoveydate = true;
        }

        if (
          isNewEcrfA == false &&
          item.Q38LinvoveyRd === 1 &&
          item.dt_dosefreq_linvo_dt &&
          item.dt_dosefreq_linvo_dt != "Invalid Date" &&
          !DateInRange(item.dt_dosefreq_linvo_dt, dayjs("01-01-1920"), dayjs())
        ) {
          InvalidQ38Linvoveydate = true;
        }

        const q36DoseAdminDates = linvoveyDetails
          .filter(
            (row) =>
              row.dose_administration_linvo === 1 &&
              IsValidDateValue(row.dose_administration_linvo_dt),
          )
          .map((row) => row.dose_administration_linvo_dt);

        const latestQ36DoseDate =
          q36DoseAdminDates.length > 0
            ? q36DoseAdminDates.reduce((max, d) =>
                IsLessOrEqual(max, d) ? d : max,
              )
            : null;

        // ✅ NEW: Complete date chain validation
        let Q38LinvoDateInvalid = false;
        if (
          isNewEcrfA === false &&
          item.Q38LinvoveyRd === 1 &&
          item.dt_dosefreq_linvo_dt !== "" &&
          DateInRange(item.dt_dosefreq_linvo_dt, dayjs("01-01-1920"), dayjs())
        ) {
          // Rule 1: Index Date ≤ Q38 Date
          if (linvoIndexDate !== "" && IsValidDateValue(linvoIndexDate)) {
            if (!IsLessOrEqual(linvoIndexDate, item.dt_dosefreq_linvo_dt)) {
              Q38LinvoDateInvalid = true;
            }
          }

          // Rule 2: Latest Q36 Date ≤ Q38 Date
          if (
            latestQ36DoseDate &&
            !IsLessOrEqual(latestQ36DoseDate, item.dt_dosefreq_linvo_dt)
          ) {
            Q38LinvoDateInvalid = true;
          }

          // Rule 3: Q38 Date ≤ LOT Start Date
          if (
            linvoseltamabLOTStartDate !== "" &&
            IsValidDateValue(linvoseltamabLOTStartDate)
          ) {
            if (
              !IsLessOrEqual(
                item.dt_dosefreq_linvo_dt,
                linvoseltamabLOTStartDate,
              )
            ) {
              Q38LinvoDateInvalid = true;
            }
          }

          // Rule 4: LOT Start Date ≤ End of Follow-up (or Q38 ≤ EOF if no LOT)
          if (endOfFollowUpDate !== "" && IsValidDateValue(endOfFollowUpDate)) {
            if (
              linvoseltamabLOTStartDate !== "" &&
              IsValidDateValue(linvoseltamabLOTStartDate)
            ) {
              if (
                !IsLessOrEqual(linvoseltamabLOTStartDate, endOfFollowUpDate)
              ) {
                Q38LinvoDateInvalid = true;
              }
            } else {
              // No LOT, so check Q38 ≤ EOF directly
              if (
                !IsLessOrEqual(item.dt_dosefreq_linvo_dt, endOfFollowUpDate)
              ) {
                Q38LinvoDateInvalid = true;
              }
            }
          }
        }

        let q38_Linvo_Dose_week_frquencyRd_Empty = false;
        if (
          isNewEcrfA == false &&
          item.q38_Linvo_Dose_week_frquencyRd == null
        ) {
          q38_Linvo_Dose_week_frquencyRd_Empty = true;
        }

        let q38_Linvo_Dose_week_frquency_txt_Empty = false;
        if (
          isNewEcrfA == false &&
          item.q38_Linvo_Dose_week_frquencyRd == 1 &&
          (item.q38_Linvo_Dose_week_frquency_txt === "" ||
            item.q38_Linvo_Dose_week_frquency_txt === null)
        ) {
          q38_Linvo_Dose_week_frquency_txt_Empty = true;
        }

        if (
          item.q38_Linvo_Dose_week_frquencyRd == 1 &&
          (item.q38_Linvo_Dose_week_frquency_txt !== "" ||
            item.q38_Linvo_Dose_week_frquency_txt !== null)
        ) {
          const checkValue = isValidNumber(
            item.q38_Linvo_Dose_week_frquency_txt,
          );
          if (
            item.q38_Linvo_Dose_week_frquencyRd_Empty == false &&
            !checkValue
          ) {
            q38_Linvo_Dose_week_frquency_txt_Empty = true;
          }
        }

        let q38_Linvo_Dosage_Rd_Empty = false;

        let q38_Linvo_Dosage_OtherTxt_Empty = false;

        let q38LinvoDoseFrequencyConflict = false;

        if (Q37LinvoDetails.length > 0) {
          const q37Frequency =
            Q37LinvoDetails[0].q37_Linvo_Dose_week_frquencyRd;
          const q38Frequency = item.q38_Linvo_Dose_week_frquencyRd;

          // Check if both are using custom frequency
          if (q37Frequency === 2 && q38Frequency === 1) {
            const q37FreqValue = parseFloat(
              Q37LinvoDetails[0].q37_Linvo_Dose_week_frquency_txt,
            );
            const q38FreqValue = parseFloat(
              item.q38_Linvo_Dose_week_frquency_txt,
            );

            // Q38 should be LESS than Q37 (reduction means lower frequency)
            if (
              !isNaN(q37FreqValue) &&
              !isNaN(q38FreqValue) &&
              q37FreqValue > q38FreqValue
            ) {
              q38LinvoDoseFrequencyConflict = true;
              isValid = false;
            }
          }
        }

        if (
          Q38LinvoEmptyHard ||
          Q38LinvoDateEmpty ||
          Q38LinvoDateInvalid ||
          q38_Linvo_Dosage_OtherTxt_Empty ||
          q38_Linvo_Dosage_Rd_Empty ||
          q38_Linvo_Dose_week_frquency_txt_Empty ||
          q38_Linvo_Dose_week_frquencyRd_Empty ||
          InvalidQ38Linvoveydate ||
          q38LinvoDoseFrequencyConflict
        ) {
          isValid = false;
        }

        return {
          ...item,

          Q38LinvoEmptyHard,
          Q38LinvoDateEmpty,
          Q38LinvoDateInvalid,
          q38_Linvo_Dosage_Rd_Empty,
          q38_Linvo_Dose_week_frquency_txt_Empty,
          q38_Linvo_Dose_week_frquencyRd_Empty,
          q38_Linvo_Dosage_OtherTxt_Empty,
          InvalidQ38Linvoveydate,
          q38LinvoDoseFrequencyConflict,
        };
      });
      setQ38LinvoDetails(updatedArray);
    }
    */

    // ============================================
    // Q38.1.A ELREXFIO VALIDATION
    // ============================================

    if (isElrexvayliSelected && isNewEcrfA) {
      Q381AElrexDetails.forEach((row, index) => {
        let rowHasError = false;

        // ========================================
        // Column B: Type of change - HARD ERROR
        // ========================================
        if (row.changeType === null || row.changeType === undefined) {
          isValid = false;
          rowHasError = true;
          Q381AElrexDetails[index].changeTypeEmpty = true;
        }

        // If changeType is "Frequency change" (2), validate sub-radio
        if (
          row.changeType === 2 &&
          (row.changeTypeDoseFrequency === null ||
            row.changeTypeDoseFrequency === undefined)
        ) {
          isValid = false;
          rowHasError = true;
          Q381AElrexDetails[index].changeTypeDoseFrequencyEmpty = true;
        }

        // If changeType is "Strength change" (3), validate sub-radio
        if (
          row.changeType === 3 &&
          (row.changeTypeDoseStrength === null ||
            row.changeTypeDoseStrength === undefined)
        ) {
          isValid = false;
          rowHasError = true;
          Q381AElrexDetails[index].changeTypeDoseStrengthEmpty = true;
        }

        // Skip columns C-F validation if "No changes" or "Unknown"
        if (row.changeType === 1 || row.changeType === 99) {
          return; // Skip rest of validations for this row
        }

        // ========================================
        // Column C: Reasons - HARD ERROR
        // ========================================
        if (
          row.selectedReasons.length === 0 &&
          row.selectedAEReasons.length === 0 &&
          !row.reasonUnknown
        ) {
          isValid = false;
          rowHasError = true;
          Q381AElrexDetails[index].reasonEmpty = true;
        }

        // Patient death reason retired (hidden in UI, see
        // RetiredDiscontinuationReasons) - date-of-death validation no
        // longer reachable, commented out rather than left dead.
        /*
        if (
          row.selectedReasons.includes(1) &&
          (patientDeathDate == null || !isStringEmpty(patientDeathDate))
        ) {
          isValid = false;
          rowHasError = true;
          Q381AElrexDetails[index].deathDtWarning = true;
        }
        */

        // If "Other" (ID=14) is selected, validate other text field
        if (
          row.selectedReasons.includes(14) &&
          isStringEmpty(row.reasonOtherText)
        ) {
          isValid = false;
          rowHasError = true;
          Q381AElrexDetails[index].reasonOtherTextEmpty = true;
        }

        // After checking if reasonEmpty
        if (row.selectedReasons.includes(2)) {
          // 2 = Adverse Event ID
          const hasAESubReasons = row.selectedAEReasons.length > 0;
          const aeReasonItem = row.reasonList?.find(
            (r) => r.bispecifictreatmentdiscontinuationreasonid === 2,
          );
          const isAEUnknownChecked =
            aeReasonItem?.aeSubReasons?.some(
              (ae) =>
                ae.bispecifictreatmentdiscontinuationreasonaeid === 99 &&
                ae.isSelected,
            ) || false;

          if (!hasAESubReasons && !isAEUnknownChecked) {
            isValid = false;
            rowHasError = true;
            Q381AElrexDetails[index].aeSubReasonEmpty = true;
          } else {
            const selectedAEs =
              aeReasonItem?.aeSubReasons?.filter((ae) => ae.isSelected) || [];
            const isNeuroSelected = selectedAEs.some(
              (ae) => ae.reasonName === DiscontinuationReason.NeurologicalEvent,
            );

            if (isNeuroSelected) {
              const selectedNeuroReasons =
                row.neurologicAEReasonList?.filter((n) => n.isSelected) || [];

              if (selectedNeuroReasons.length === 0) {
                isValid = false;
                rowHasError = true;
                Q381AElrexDetails[index].neuroReasonEmpty = true;
              }

              selectedNeuroReasons.forEach((neuro) => {
                if (
                  neuro.reasonName?.includes("Other") &&
                  (!neuro.reasonText || !neuro.reasonText.trim())
                ) {
                  isValid = false;
                  rowHasError = true;
                  Q381AElrexDetails[index].neuroOtherTextEmpty = true;
                }
              });
            }
          }
        }

        // ========================================
        // Column D: Pattern BEFORE change - HARD ERROR
        // ========================================
        // If changeType is "Frequency change" (2)
        if (row.changeType === 2) {
          // Frequency type required
          if (
            row.patterBeforeChangeDoseFrequency === null ||
            row.patterBeforeChangeDoseFrequency === undefined
          ) {
            isValid = false;
            rowHasError = true;
            Q381AElrexDetails[index].patternBeforeFrequencyEmpty = true;
          }

          // Frequency value required if type is 1 (times/week) or 2 (every X weeks)
          if (
            (row.patterBeforeChangeDoseFrequency === 1 ||
              row.patterBeforeChangeDoseFrequency === 2) &&
            isStringEmpty(
              row.patterBeforeChangeDoseFrequency === 1
                ? row.patterBeforeChangeDoseFrequencyTimesPerWeek
                : row.patterBeforeChangeDoseFrequencyWeek,
            )
          ) {
            isValid = false;
            rowHasError = true;
            Q381AElrexDetails[index].patternBeforeFrequencyEmpty = true;
          }

          // Range validation: Integer, >0 and ≤10
          if (
            !isStringEmpty(
              row.patterBeforeChangeDoseFrequency === 1
                ? row.patterBeforeChangeDoseFrequencyTimesPerWeek
                : row.patterBeforeChangeDoseFrequencyWeek,
            ) &&
            row.patterBeforeChangeDoseFrequency !== 99
          ) {
            const weekValue = Number(
              row.patterBeforeChangeDoseFrequency === 1
                ? row.patterBeforeChangeDoseFrequencyTimesPerWeek
                : row.patterBeforeChangeDoseFrequencyWeek,
            );
            if (
              !Number.isInteger(weekValue) ||
              weekValue <= 0 ||
              weekValue > 20
            ) {
              isValid = false;
              rowHasError = true;
              Q381AElrexDetails[index].patternBeforeFrequencyRangeInvalid =
                true;
            }
          }
        }
        // If changeType is "Strength change" (3)
        if (row.changeType === 3) {
          // Strength type required
          if (
            row.patterBeforeChangeDoseStrength === null ||
            row.patterBeforeChangeDoseStrength === undefined
          ) {
            isValid = false;
            rowHasError = true;
            Q381AElrexDetails[index].patternBeforeStrengthEmpty = true;
          }

          // Strength value required if type is 1 (mg), 2 (mg/kg), or 3 (mg/m²)
          if (
            (row.patterBeforeChangeDoseStrength === 1 ||
              row.patterBeforeChangeDoseStrength === 2 ||
              row.patterBeforeChangeDoseStrength === 3) &&
            isStringEmpty(
              row.patterBeforeChangeDoseStrength === 1
                ? row.patterBeforeChangeDoseStrengthMg
                : row.patterBeforeChangeDoseStrength === 2
                  ? row.patterBeforeChangeDoseStrengthMgKg
                  : row.patterBeforeChangeDoseStrengthMgM2,
            )
          ) {
            isValid = false;
            rowHasError = true;
            Q381AElrexDetails[index].patternBeforeStrengthEmpty = true;
          }

          // Range validation: Decimal, >0.00 and ≤4000.00
          if (
            !isStringEmpty(
              row.patterBeforeChangeDoseStrength === 1
                ? row.patterBeforeChangeDoseStrengthMg
                : row.patterBeforeChangeDoseStrength === 2
                  ? row.patterBeforeChangeDoseStrengthMgKg
                  : row.patterBeforeChangeDoseStrengthMgM2,
            ) &&
            row.patterBeforeChangeDoseStrength !== 99
          ) {
            const strengthValue = Number(
              row.patterBeforeChangeDoseStrength == 1
                ? row.patterBeforeChangeDoseStrengthMg
                : row.patterBeforeChangeDoseStrength == 2
                  ? row.patterBeforeChangeDoseStrengthMgKg
                  : row.patterBeforeChangeDoseStrengthMgM2,
            );
            if (
              isNaN(strengthValue) ||
              strengthValue <= 0 ||
              strengthValue > 4000
            ) {
              isValid = false;
              rowHasError = true;
              Q381AElrexDetails[index].patternBeforeFrequencyRangeInvalid =
                true;
            }
          }
        }
        // ========================================
        // Column E: Pattern AFTER change - HARD ERROR
        // ========================================
        // If changeType is "Frequency change" (2)
        if (row.changeType === 2) {
          // Frequency type required
          if (
            row.patterAfterChangeDoseFrequency === null ||
            row.patterAfterChangeDoseFrequency === undefined
          ) {
            isValid = false;
            rowHasError = true;
            Q381AElrexDetails[index].patternAfterFrequencyEmpty = true;
          }

          // Frequency value required if type is 1 or 2
          if (
            (row.patterAfterChangeDoseFrequency === 1 ||
              row.patterAfterChangeDoseFrequency === 2) &&
            isStringEmpty(
              row.patterAfterChangeDoseFrequency === 1
                ? row.patterAfterChangeDoseFrequencyTimesPerWeek
                : row.patterAfterChangeDoseFrequencyWeek,
            )
          ) {
            isValid = false;
            rowHasError = true;
            Q381AElrexDetails[index].patternAfterFrequencyEmpty = true;
          }

          // Range validation: Integer, >0 and ≤10
          if (
            !isStringEmpty(
              row.patterAfterChangeDoseFrequency === 1
                ? row.patterAfterChangeDoseFrequencyTimesPerWeek
                : row.patterAfterChangeDoseFrequencyWeek,
            ) &&
            row.patterAfterChangeDoseFrequency !== 99
          ) {
            const weekValue = Number(
              row.patterAfterChangeDoseFrequency === 1
                ? row.patterAfterChangeDoseFrequencyTimesPerWeek
                : row.patterAfterChangeDoseFrequencyWeek,
            );
            if (
              !Number.isInteger(weekValue) ||
              weekValue <= 0 ||
              weekValue > 20
            ) {
              isValid = false;
              rowHasError = true;
              Q381AElrexDetails[index].patternAfterFrequencyRangeInvalid = true;
            }
          }
        }

        // If changeType is "Strength change" (3)
        if (row.changeType === 3) {
          // Strength type required
          if (
            row.patterAfterChangeDoseStrength === null ||
            row.patterAfterChangeDoseStrength === undefined
          ) {
            isValid = false;
            rowHasError = true;
            Q381AElrexDetails[index].patternAfterStrengthEmpty = true;
          }

          // Strength value required if type is 1, 2, or 3
          if (
            (row.patterAfterChangeDoseStrength === 1 ||
              row.patterAfterChangeDoseStrength === 2 ||
              row.patterAfterChangeDoseStrength === 3) &&
            isStringEmpty(
              row.patterAfterChangeDoseStrength === 1
                ? row.patterAfterChangeDoseStrengthMg
                : row.patterAfterChangeDoseStrength === 2
                  ? row.patterAfterChangeDoseStrengthMgKg
                  : row.patterAfterChangeDoseStrengthMgM2,
            )
          ) {
            isValid = false;
            rowHasError = true;
            Q381AElrexDetails[index].patternAfterStrengthEmpty = true;
          }

          // Range validation: Decimal, >0.00 and ≤4000.00
          if (
            !isStringEmpty(
              row.patterAfterChangeDoseStrength === 1
                ? row.patterAfterChangeDoseStrengthMg
                : row.patterAfterChangeDoseStrength === 2
                  ? row.patterAfterChangeDoseStrengthMgKg
                  : row.patterAfterChangeDoseStrengthMgM2,
            ) &&
            row.patterAfterChangeDoseStrength !== 99
          ) {
            const strengthValue = Number(
              row.patterAfterChangeDoseStrength === 1
                ? row.patterAfterChangeDoseStrengthMg
                : row.patterAfterChangeDoseStrength === 2
                  ? row.patterAfterChangeDoseStrengthMgKg
                  : row.patterAfterChangeDoseStrengthMgM2,
            );
            if (
              isNaN(strengthValue) ||
              strengthValue <= 0 ||
              strengthValue > 4000
            ) {
              isValid = false;
              rowHasError = true;
              Q381AElrexDetails[index].patternAfterStrengthRangeInvalid = true;
            }
          }
        }

        // ========================================
        // Conflict Validations: Before vs After
        // ========================================
        // Increase/decrease conflict (normalizes both sides to a doses-per-week
        // rate before comparing, since "once every N weeks" and "X times per
        // week" are on inverse scales) - mirrors Q40.1.C's approach, and this
        // also naturally forbids mixing "___ times per week" with "once every
        // ___ weeks" in the wrong direction, since that combination can never
        // satisfy the rate comparison below.
        Q381AElrexDetails[index].frequencyIncreaseConflict = false;
        Q381AElrexDetails[index].frequencyDecreaseConflict = false;
        if (row.changeType === 2) {
          const beforeNum =
            row.patterBeforeChangeDoseFrequency === 1
              ? row.patterBeforeChangeDoseFrequencyTimesPerWeek
              : row.patterBeforeChangeDoseFrequency === 2
                ? row.patterBeforeChangeDoseFrequencyWeek
                : null;
          const afterNum =
            row.patterAfterChangeDoseFrequency === 1
              ? row.patterAfterChangeDoseFrequencyTimesPerWeek
              : row.patterAfterChangeDoseFrequency === 2
                ? row.patterAfterChangeDoseFrequencyWeek
                : null;

          if (!isStringEmpty(beforeNum) && !isStringEmpty(afterNum)) {
            const beforeRate =
              row.patterBeforeChangeDoseFrequency === 2
                ? 1 / Number(beforeNum)
                : Number(beforeNum);
            const afterRate =
              row.patterAfterChangeDoseFrequency === 2
                ? 1 / Number(afterNum)
                : Number(afterNum);

            if (row.changeTypeDoseFrequency === 1 && afterRate <= beforeRate) {
              isValid = false;
              rowHasError = true;
              Q381AElrexDetails[index].frequencyIncreaseConflict = true;
            } else if (
              row.changeTypeDoseFrequency === 2 &&
              afterRate >= beforeRate
            ) {
              isValid = false;
              rowHasError = true;
              Q381AElrexDetails[index].frequencyDecreaseConflict = true;
            }
          }
        }

        // For STRENGTH INCREASE
        if (
          row.changeType === 3 &&
          row.changeTypeDoseStrength === 1 &&
          row.patterBeforeChangeDoseStrength !== 99 &&
          row.patterAfterChangeDoseStrength !== 99 &&
          row.patterBeforeChangeDoseStrength ===
            row.patterAfterChangeDoseStrength &&
          !isStringEmpty(
            row.patterBeforeChangeDoseStrength == 1
              ? row.patterBeforeChangeDoseStrengthMg
              : row.patterBeforeChangeDoseStrength == 2
                ? row.patterBeforeChangeDoseStrengthMgKg
                : row.patterBeforeChangeDoseStrengthMgM2,
          ) &&
          !isStringEmpty(
            row.patterAfterChangeDoseStrength == 1
              ? row.patterAfterChangeDoseStrengthMg
              : row.patterAfterChangeDoseStrength == 2
                ? row.patterAfterChangeDoseStrengthMgKg
                : row.patterAfterChangeDoseStrengthMgM2,
          )
        ) {
          const beforeValue = Number(
            row.patterBeforeChangeDoseStrength == 1
              ? row.patterBeforeChangeDoseStrengthMg
              : row.patterBeforeChangeDoseStrength == 2
                ? row.patterBeforeChangeDoseStrengthMgKg
                : row.patterBeforeChangeDoseStrengthMgM2,
          );
          const afterValue = Number(
            row.patterAfterChangeDoseStrength == 1
              ? row.patterAfterChangeDoseStrengthMg
              : row.patterAfterChangeDoseStrength == 2
                ? row.patterAfterChangeDoseStrengthMgKg
                : row.patterAfterChangeDoseStrengthMgM2,
          );

          // After should be > Before
          if (afterValue <= beforeValue) {
            isValid = false;
            rowHasError = true;
            Q381AElrexDetails[index].strengthIncreaseConflict = true;
          }
        }

        // For STRENGTH DECREASE
        if (
          row.changeType === 3 &&
          row.changeTypeDoseStrength === 2 &&
          row.patterBeforeChangeDoseStrength !== 99 &&
          row.patterAfterChangeDoseStrength !== 99 &&
          row.patterBeforeChangeDoseStrength ===
            row.patterAfterChangeDoseStrength &&
          !isStringEmpty(
            row.patterBeforeChangeDoseStrength == 1
              ? row.patterBeforeChangeDoseStrengthMg
              : row.patterBeforeChangeDoseStrength == 2
                ? row.patterBeforeChangeDoseStrengthMgKg
                : row.patterBeforeChangeDoseStrengthMgM2,
          ) &&
          !isStringEmpty(
            row.patterAfterChangeDoseStrength == 1
              ? row.patterAfterChangeDoseStrengthMg
              : row.patterAfterChangeDoseStrength == 2
                ? row.patterAfterChangeDoseStrengthMgKg
                : row.patterAfterChangeDoseStrengthMgM2,
          )
        ) {
          const beforeValue = Number(
            row.patterBeforeChangeDoseStrength == 1
              ? row.patterBeforeChangeDoseStrengthMg
              : row.patterBeforeChangeDoseStrength == 2
                ? row.patterBeforeChangeDoseStrengthMgKg
                : row.patterBeforeChangeDoseStrengthMgM2,
          );
          const afterValue = Number(
            row.patterAfterChangeDoseStrength == 1
              ? row.patterAfterChangeDoseStrengthMg
              : row.patterAfterChangeDoseStrength == 2
                ? row.patterAfterChangeDoseStrengthMgKg
                : row.patterAfterChangeDoseStrengthMgM2,
          );

          // After should be < Before
          if (afterValue >= beforeValue) {
            isValid = false;
            rowHasError = true;
            Q381AElrexDetails[index].strengthDecreaseConflict = true;
          }
        }

        // ========================================
        // WARNING: Both patterns "Unknown"
        // ========================================
        if (row.changeType === 2 || row.changeType === 3) {
          const beforeUnknowValue =
            row.changeType === 2
              ? row.patterBeforeChangeDoseFrequency
              : row.patterBeforeChangeDoseStrength;
          const afterUnknowValue =
            row.changeType === 2
              ? row.patterAfterChangeDoseFrequency
              : row.patterAfterChangeDoseStrength;

          Q381AElrexDetails[index].patternBeforeUnknownWarning =
            beforeUnknowValue === 99;
          Q381AElrexDetails[index].patternAfterUnknownWarning =
            afterUnknowValue === 99;
        }

        // ========================================
        // Column F: Date - HARD ERROR
        // ========================================
        if (row.dateChangeRd === null || row.dateChangeRd === undefined) {
          isValid = false;
          rowHasError = true;
          Q381AElrexDetails[index].dateChangeRdEmpty = true;
        }
        // If "Date provided" (1) is selected, validate the date

        if (row.dateChangeRd === 1) {
          if (isStringEmpty(row.dateChange)) {
            isValid = false;
            rowHasError = true;
            Q381AElrexDetails[index].dateChangeEmpty = true;
          } else {
            // Date format validation
            if (!DateInRange(row.dateChange, dayjs("01-01-1920"), dayjs())) {
              isValid = false;
              rowHasError = true;
              Q381AElrexDetails[index].invalidDateChange = true;
            } else {
              // ✅ Use the new date chain validation
              if (
                !ValidateQ381ADateChain(
                  row.dateChange,
                  RRMMCohortTreatment.ELREXFIO,
                )
              ) {
                isValid = false;
                rowHasError = true;
                Q381AElrexDetails[index].dateChangeOutOfBound = true;
              }
            }
          }
        }
      });

      // Chronological chain warning: after[i] should match before[i+1] once
      // rows of the same modification type are sorted by date
      const getFrequencyValueElr = (row, prefix) => {
        const unit = row[`patter${prefix}ChangeDoseFrequency`];
        if (unit !== 1 && unit !== 2) return null;
        const txt =
          unit === 1
            ? row[`patter${prefix}ChangeDoseFrequencyTimesPerWeek`]
            : row[`patter${prefix}ChangeDoseFrequencyWeek`];
        if (isStringEmpty(txt)) return null;
        return { unit, value: String(txt) };
      };
      const getStrengthValueElr = (row, prefix) => {
        const unit = row[`patter${prefix}ChangeDoseStrength`];
        if (unit !== 1 && unit !== 2 && unit !== 3) return null;
        const txt =
          unit === 1
            ? row[`patter${prefix}ChangeDoseStrengthMg`]
            : unit === 2
              ? row[`patter${prefix}ChangeDoseStrengthMgKg`]
              : row[`patter${prefix}ChangeDoseStrengthMgM2`];
        if (isStringEmpty(txt)) return null;
        return { unit, value: String(txt) };
      };

      // Sort by date when both rows being compared have a valid date;
      // otherwise (Array.prototype.sort is stable) fall back to the order
      // rows were entered in (i.e., 1st, 2nd, 3rd... as shown in the UI).
      const sortByDateOrEntryOrderElr = (rows) =>
        [...rows].sort((a, b) => {
          const aValid = a.dateChangeRd === 1 && IsValidDateValue(a.dateChange);
          const bValid = b.dateChangeRd === 1 && IsValidDateValue(b.dateChange);
          return aValid && bValid
            ? new Date(a.dateChange) - new Date(b.dateChange)
            : 0;
        });

      const chronRowsFreqElr = sortByDateOrEntryOrderElr(
        Q381AElrexDetails.filter((r) => r.changeType === 2),
      );
      const chronRowsStrengthElr = sortByDateOrEntryOrderElr(
        Q381AElrexDetails.filter((r) => r.changeType === 3),
      );

      let chainMismatchElrex = false;
      for (let i = 0; i < chronRowsFreqElr.length - 1; i++) {
        const after = getFrequencyValueElr(chronRowsFreqElr[i], "After");
        const before = getFrequencyValueElr(chronRowsFreqElr[i + 1], "Before");
        if (
          after != null &&
          before != null &&
          (after.unit !== before.unit || after.value !== before.value)
        ) {
          chainMismatchElrex = true;
          break;
        }
      }
      if (!chainMismatchElrex) {
        for (let i = 0; i < chronRowsStrengthElr.length - 1; i++) {
          const after = getStrengthValueElr(chronRowsStrengthElr[i], "After");
          const before = getStrengthValueElr(
            chronRowsStrengthElr[i + 1],
            "Before",
          );
          if (
            after != null &&
            before != null &&
            (after.unit !== before.unit || after.value !== before.value)
          ) {
            chainMismatchElrex = true;
            break;
          }
        }
      }
      setQ381AElrex_WarningHard(chainMismatchElrex);

      // AUTO-009 - when Q36 reports more than one 1st subsequent treatment
      // dose, the auto-populated column [D] must still match at least one of
      // them. Column [D] is seeded from the earliest such row, so this only
      // fails once Q36 has been edited away from the seeded value.
      const q36MismatchElrex = ValidateQ38PatternBeforeMatchesQ36(
        chronRowsFreqElr[0],
        chronRowsStrengthElr[0],
        elrexvayliDetails,
        Q381A_ELREX_AUTO_CONFIG,
        getFrequencyValueElr,
        getStrengthValueElr,
      );
      setQ381AElrexQ36Mismatch(q36MismatchElrex);
      if (q36MismatchElrex) {
        isValid = false;
      }

      setQ381AElrexDetails([...Q381AElrexDetails]);
    }

    // ============================================
    // Q38.1.A LINVOSELTAMAB VALIDATION
    // ============================================
    if (isLinvoveySelected && isNewEcrfA) {
      Q381ALinvoDetails.forEach((row, index) => {
        let rowHasError = false;

        // ========================================
        // Column B: Type of change - HARD ERROR
        // ========================================
        if (row.changeType === null || row.changeType === undefined) {
          isValid = false;
          rowHasError = true;
          Q381ALinvoDetails[index].changeTypeEmpty = true;
        }

        if (
          row.changeType === 2 &&
          (row.changeTypeDoseFrequency === null ||
            row.changeTypeDoseFrequency === undefined)
        ) {
          isValid = false;
          rowHasError = true;
          Q381ALinvoDetails[index].changeTypeDoseFrequencyEmpty = true;
        }

        if (
          row.changeType === 3 &&
          (row.changeTypeDoseStrength === null ||
            row.changeTypeDoseStrength === undefined)
        ) {
          isValid = false;
          rowHasError = true;
          Q381ALinvoDetails[index].changeTypeDoseStrengthEmpty = true;
        }

        if (row.changeType === 1 || row.changeType === 99) {
          return;
        }

        // ========================================
        // Column C: Reasons - HARD ERROR
        // ========================================
        if (
          row.selectedReasons.length === 0 &&
          row.selectedAEReasons.length === 0 &&
          !row.reasonUnknown
        ) {
          isValid = false;
          rowHasError = true;
          Q381ALinvoDetails[index].reasonEmpty = true;
        }

        // Patient death reason retired (hidden in UI, see
        // RetiredDiscontinuationReasons) - date-of-death validation no
        // longer reachable, commented out rather than left dead.
        /*
        if (
          row.selectedReasons.includes(1) &&
          (patientDeathDate == null || !isStringEmpty(patientDeathDate))
        ) {
          isValid = false;
          rowHasError = true;
          Q381ALinvoDetails[index].deathDtWarning = true;
        }
        */

        if (
          row.selectedReasons.includes(14) &&
          isStringEmpty(row.reasonOtherText)
        ) {
          isValid = false;
          rowHasError = true;
          Q381ALinvoDetails[index].reasonOtherTextEmpty = true;
        }

        // In Q38.1.A LINVOSELTAMAB validation section
        if (row.selectedReasons.includes(2)) {
          const hasAESubReasons = row.selectedAEReasons.length > 0;
          const aeReasonItem = row.reasonList?.find(
            (r) => r.bispecifictreatmentdiscontinuationreasonid === 2,
          );
          const isAEUnknownChecked =
            aeReasonItem?.aeSubReasons?.some(
              (ae) =>
                ae.bispecifictreatmentdiscontinuationreasonaeid === 99 &&
                ae.isSelected,
            ) || false;

          if (!hasAESubReasons && !isAEUnknownChecked) {
            isValid = false;
            rowHasError = true;
            Q381ALinvoDetails[index].aeSubReasonEmpty = true;
          } else {
            const selectedAEs =
              aeReasonItem?.aeSubReasons?.filter((ae) => ae.isSelected) || [];
            const isNeuroSelected = selectedAEs.some(
              (ae) => ae.reasonName === DiscontinuationReason.NeurologicalEvent,
            );

            if (isNeuroSelected) {
              const selectedNeuroReasons =
                row.neurologicAEReasonList?.filter((n) => n.isSelected) || [];

              if (selectedNeuroReasons.length === 0) {
                isValid = false;
                rowHasError = true;
                Q381ALinvoDetails[index].neuroReasonEmpty = true;
              }

              selectedNeuroReasons.forEach((neuro) => {
                if (
                  neuro.reasonName?.includes("Other") &&
                  (!neuro.reasonText || !neuro.reasonText.trim())
                ) {
                  isValid = false;
                  rowHasError = true;
                  Q381ALinvoDetails[index].neuroOtherTextEmpty = true;
                }
              });
            }
          }
        }

        // ========================================
        // Column D: Pattern BEFORE change - HARD ERROR
        // ========================================
        if (row.changeType === 2) {
          if (
            row.patterBeforeChangeDoseFrequency === null ||
            row.patterBeforeChangeDoseFrequency === undefined
          ) {
            isValid = false;
            rowHasError = true;
            Q381ALinvoDetails[index].patternBeforeFrequencyEmpty = true;
          }

          if (
            (row.patterBeforeChangeDoseFrequency === 1 ||
              row.patterBeforeChangeDoseFrequency === 2) &&
            isStringEmpty(
              row.patterBeforeChangeDoseFrequency === 1
                ? row.patterBeforeChangeDoseFrequencyTimesPerWeek
                : row.patterBeforeChangeDoseFrequencyWeek,
            )
          ) {
            isValid = false;
            rowHasError = true;
            Q381ALinvoDetails[index].patternBeforeFrequencyEmpty = true;
          }

          if (
            !isStringEmpty(
              row.patterBeforeChangeDoseFrequency === 1
                ? row.patterBeforeChangeDoseFrequencyTimesPerWeek
                : row.patterBeforeChangeDoseFrequencyWeek,
            ) &&
            row.patterBeforeChangeDoseFrequency !== 99
          ) {
            const weekValue = Number(
              row.patterBeforeChangeDoseFrequency === 1
                ? row.patterBeforeChangeDoseFrequencyTimesPerWeek
                : row.patterBeforeChangeDoseFrequencyWeek,
            );
            if (
              !Number.isInteger(weekValue) ||
              weekValue <= 0 ||
              weekValue > 20
            ) {
              isValid = false;
              rowHasError = true;
              Q381ALinvoDetails[index].patternBeforeFrequencyRangeInvalid =
                true;
            }
          }
        }

        if (row.changeType === 3) {
          if (
            row.patterBeforeChangeDoseStrength === null ||
            row.patterBeforeChangeDoseStrength === undefined
          ) {
            isValid = false;
            rowHasError = true;
            Q381ALinvoDetails[index].patternBeforeStrengthEmpty = true;
          }

          if (
            (row.patterBeforeChangeDoseStrength === 1 ||
              row.patterBeforeChangeDoseStrength === 2 ||
              row.patterBeforeChangeDoseStrength === 3) &&
            isStringEmpty(
              row.patterBeforeChangeDoseStrength === 1
                ? row.patterBeforeChangeDoseStrengthMg
                : row.patterBeforeChangeDoseStrength === 2
                  ? row.patterBeforeChangeDoseStrengthMgKg
                  : row.patterBeforeChangeDoseStrengthMgM2,
            )
          ) {
            isValid = false;
            rowHasError = true;
            Q381ALinvoDetails[index].patternBeforeStrengthEmpty = true;
          }

          if (
            !isStringEmpty(
              row.patterBeforeChangeDoseStrength === 1
                ? row.patterBeforeChangeDoseStrengthMg
                : row.patterBeforeChangeDoseStrength === 2
                  ? row.patterBeforeChangeDoseStrengthMgKg
                  : row.patterBeforeChangeDoseStrengthMgM2,
            ) &&
            row.patterBeforeChangeDoseStrength !== 99
          ) {
            const strengthValue = Number(
              row.patterBeforeChangeDoseStrength === 1
                ? row.patterBeforeChangeDoseStrengthMg
                : row.patterBeforeChangeDoseStrength === 2
                  ? row.patterBeforeChangeDoseStrengthMgKg
                  : row.patterBeforeChangeDoseStrengthMgM2,
            );
            if (
              isNaN(strengthValue) ||
              strengthValue <= 0 ||
              strengthValue > 4000
            ) {
              isValid = false;
              rowHasError = true;
              Q381ALinvoDetails[index].patternBeforeFrequencyRangeInvalid =
                true;
            }
          }
        }

        // ========================================
        // Column E: Pattern AFTER change - HARD ERROR
        // ========================================
        if (row.changeType === 2) {
          if (
            row.patterAfterChangeDoseFrequency === null ||
            row.patterAfterChangeDoseFrequency === undefined
          ) {
            isValid = false;
            rowHasError = true;
            Q381ALinvoDetails[index].patternAfterFrequencyEmpty = true;
          }

          if (
            (row.patterAfterChangeDoseFrequency === 1 ||
              row.patterAfterChangeDoseFrequency === 2) &&
            isStringEmpty(
              row.patterAfterChangeDoseFrequency === 1
                ? row.patterAfterChangeDoseFrequencyTimesPerWeek
                : row.patterAfterChangeDoseFrequencyWeek,
            )
          ) {
            isValid = false;
            rowHasError = true;
            Q381ALinvoDetails[index].patternAfterFrequencyEmpty = true;
          }

          if (
            !isStringEmpty(
              row.patterAfterChangeDoseFrequency === 1
                ? row.patterAfterChangeDoseFrequencyTimesPerWeek
                : row.patterAfterChangeDoseFrequencyWeek,
            ) &&
            row.patterAfterChangeDoseFrequency !== 99
          ) {
            const weekValue = Number(
              row.patterAfterChangeDoseFrequency === 1
                ? row.patterAfterChangeDoseFrequencyTimesPerWeek
                : row.patterAfterChangeDoseFrequencyWeek,
            );
            if (
              !Number.isInteger(weekValue) ||
              weekValue <= 0 ||
              weekValue > 20
            ) {
              isValid = false;
              rowHasError = true;
              Q381ALinvoDetails[index].patternAfterFrequencyRangeInvalid = true;
            }
          }
        }

        if (row.changeType === 3) {
          if (
            row.patterAfterChangeDoseStrength === null ||
            row.patterAfterChangeDoseStrength === undefined
          ) {
            isValid = false;
            rowHasError = true;
            Q381ALinvoDetails[index].patternAfterStrengthEmpty = true;
          }

          if (
            (row.patterAfterChangeDoseStrength === 1 ||
              row.patterAfterChangeDoseStrength === 2 ||
              row.patterAfterChangeDoseStrength === 3) &&
            isStringEmpty(
              row.patterAfterChangeDoseStrength === 1
                ? row.patterAfterChangeDoseStrengthMg
                : row.patterAfterChangeDoseStrength === 2
                  ? row.patterAfterChangeDoseStrengthMgKg
                  : row.patterAfterChangeDoseStrengthMgM2,
            )
          ) {
            isValid = false;
            rowHasError = true;
            Q381ALinvoDetails[index].patternAfterStrengthEmpty = true;
          }

          if (
            !isStringEmpty(
              row.patterAfterChangeDoseStrength === 1
                ? row.patterAfterChangeDoseStrengthMg
                : row.patterAfterChangeDoseStrength === 2
                  ? row.patterAfterChangeDoseStrengthMgKg
                  : row.patterAfterChangeDoseStrengthMgM2,
            ) &&
            row.patterAfterChangeDoseStrength !== 99
          ) {
            const strengthValue = Number(
              row.patterAfterChangeDoseStrength === 1
                ? row.patterAfterChangeDoseStrengthMg
                : row.patterAfterChangeDoseStrength === 2
                  ? row.patterAfterChangeDoseStrengthMgKg
                  : row.patterAfterChangeDoseStrengthMgM2,
            );
            if (
              isNaN(strengthValue) ||
              strengthValue <= 0 ||
              strengthValue > 4000
            ) {
              isValid = false;
              rowHasError = true;
              Q381ALinvoDetails[index].patternAfterStrengthRangeInvalid = true;
            }
          }
        }

        // ========================================
        // Conflict Validations: Before vs After
        // ========================================
        // Increase/decrease conflict (normalizes both sides to a doses-per-week
        // rate before comparing, since "once every N weeks" and "X times per
        // week" are on inverse scales) - mirrors Q40.1.C's approach, and this
        // also naturally forbids mixing "___ times per week" with "once every
        // ___ weeks" in the wrong direction, since that combination can never
        // satisfy the rate comparison below.
        Q381ALinvoDetails[index].frequencyIncreaseConflict = false;
        Q381ALinvoDetails[index].frequencyDecreaseConflict = false;
        if (row.changeType === 2) {
          const beforeNum =
            row.patterBeforeChangeDoseFrequency === 1
              ? row.patterBeforeChangeDoseFrequencyTimesPerWeek
              : row.patterBeforeChangeDoseFrequency === 2
                ? row.patterBeforeChangeDoseFrequencyWeek
                : null;
          const afterNum =
            row.patterAfterChangeDoseFrequency === 1
              ? row.patterAfterChangeDoseFrequencyTimesPerWeek
              : row.patterAfterChangeDoseFrequency === 2
                ? row.patterAfterChangeDoseFrequencyWeek
                : null;

          if (!isStringEmpty(beforeNum) && !isStringEmpty(afterNum)) {
            const beforeRate =
              row.patterBeforeChangeDoseFrequency === 2
                ? 1 / Number(beforeNum)
                : Number(beforeNum);
            const afterRate =
              row.patterAfterChangeDoseFrequency === 2
                ? 1 / Number(afterNum)
                : Number(afterNum);

            if (row.changeTypeDoseFrequency === 1 && afterRate <= beforeRate) {
              isValid = false;
              rowHasError = true;
              Q381ALinvoDetails[index].frequencyIncreaseConflict = true;
            } else if (
              row.changeTypeDoseFrequency === 2 &&
              afterRate >= beforeRate
            ) {
              isValid = false;
              rowHasError = true;
              Q381ALinvoDetails[index].frequencyDecreaseConflict = true;
            }
          }
        }

        if (
          row.changeType === 3 &&
          row.changeTypeDoseStrength === 1 &&
          row.patterBeforeChangeDoseStrength !== 99 &&
          row.patterAfterChangeDoseStrength !== 99 &&
          row.patterBeforeChangeDoseStrength ===
            row.patterAfterChangeDoseStrength &&
          !isStringEmpty(
            row.patterBeforeChangeDoseStrength == 1
              ? row.patterBeforeChangeDoseStrengthMg
              : row.patterBeforeChangeDoseStrength == 2
                ? row.patterBeforeChangeDoseStrengthMgKg
                : row.patterBeforeChangeDoseStrengthMgM2,
          ) &&
          !isStringEmpty(
            row.patterAfterChangeDoseStrength == 1
              ? row.patterAfterChangeDoseStrengthMg
              : row.patterAfterChangeDoseStrength == 2
                ? row.patterAfterChangeDoseStrengthMgKg
                : row.patterAfterChangeDoseStrengthMgM2,
          )
        ) {
          const beforeValue = Number(
            row.patterBeforeChangeDoseStrength == 1
              ? row.patterBeforeChangeDoseStrengthMg
              : row.patterBeforeChangeDoseStrength == 2
                ? row.patterBeforeChangeDoseStrengthMgKg
                : row.patterBeforeChangeDoseStrengthMgM2,
          );
          const afterValue = Number(
            row.patterAfterChangeDoseStrength == 1
              ? row.patterAfterChangeDoseStrengthMg
              : row.patterAfterChangeDoseStrength == 2
                ? row.patterAfterChangeDoseStrengthMgKg
                : row.patterAfterChangeDoseStrengthMgM2,
          );

          if (afterValue <= beforeValue) {
            isValid = false;
            rowHasError = true;
            Q381ALinvoDetails[index].strengthIncreaseConflict = true;
          }
        }

        if (
          row.changeType === 3 &&
          row.changeTypeDoseStrength === 2 &&
          row.patterBeforeChangeDoseStrength !== 99 &&
          row.patterAfterChangeDoseStrength !== 99 &&
          row.patterBeforeChangeDoseStrength ===
            row.patterAfterChangeDoseStrength &&
          !isStringEmpty(
            row.patterBeforeChangeDoseStrength == 1
              ? row.patterBeforeChangeDoseStrengthMg
              : row.patterBeforeChangeDoseStrength == 2
                ? row.patterBeforeChangeDoseStrengthMgKg
                : row.patterBeforeChangeDoseStrengthMgM2,
          ) &&
          !isStringEmpty(
            row.patterAfterChangeDoseStrength == 1
              ? row.patterAfterChangeDoseStrengthMg
              : row.patterAfterChangeDoseStrength == 2
                ? row.patterAfterChangeDoseStrengthMgKg
                : row.patterAfterChangeDoseStrengthMgM2,
          )
        ) {
          const beforeValue = Number(
            row.patterBeforeChangeDoseStrength == 1
              ? row.patterBeforeChangeDoseStrengthMg
              : row.patterBeforeChangeDoseStrength == 2
                ? row.patterBeforeChangeDoseStrengthMgKg
                : row.patterBeforeChangeDoseStrengthMgM2,
          );
          const afterValue = Number(
            row.patterAfterChangeDoseStrength == 1
              ? row.patterAfterChangeDoseStrengthMg
              : row.patterAfterChangeDoseStrength == 2
                ? row.patterAfterChangeDoseStrengthMgKg
                : row.patterAfterChangeDoseStrengthMgM2,
          );

          if (afterValue >= beforeValue) {
            isValid = false;
            rowHasError = true;
            Q381ALinvoDetails[index].strengthDecreaseConflict = true;
          }
        }

        // ========================================
        // WARNING: Pattern Unknown
        // ========================================
        if (row.changeType === 2 || row.changeType === 3) {
          const beforeUnknowValue =
            row.changeType === 2
              ? row.patterBeforeChangeDoseFrequency
              : row.patterBeforeChangeDoseStrength;
          const afterUnknowValue =
            row.changeType === 2
              ? row.patterAfterChangeDoseFrequency
              : row.patterAfterChangeDoseStrength;

          Q381ALinvoDetails[index].patternBeforeUnknownWarning =
            beforeUnknowValue === 99;
          Q381ALinvoDetails[index].patternAfterUnknownWarning =
            afterUnknowValue === 99;
        }

        // ========================================
        // Column F: Date - HARD ERROR
        // ========================================
        if (row.dateChangeRd === null || row.dateChangeRd === undefined) {
          isValid = false;
          rowHasError = true;
          Q381ALinvoDetails[index].dateChangeRdEmpty = true;
        }

        if (row.dateChangeRd === 1) {
          if (isStringEmpty(row.dateChange)) {
            isValid = false;
            rowHasError = true;
            Q381ALinvoDetails[index].dateChangeEmpty = true;
          } else {
            // Date format validation
            if (
              row.dateChange &&
              !DateInRange(row.dateChange, dayjs("01-01-1920"), dayjs())
            ) {
              isValid = false;
              rowHasError = true;
              Q381ALinvoDetails[index].invalidDateChange = true;
            } else {
              // ✅ Use the new date chain validation
              if (
                !ValidateQ381ADateChain(
                  row.dateChange,
                  RRMMCohortTreatment.Linvoseltamab,
                )
              ) {
                isValid = false;
                rowHasError = true;
                Q381ALinvoDetails[index].dateChangeOutOfBound = true;
              }
            }
          }
        }
      });

      // Chronological chain warning: after[i] should match before[i+1] once
      // rows of the same modification type are sorted by date
      const getFrequencyValueLinvo = (row, prefix) => {
        const unit = row[`patter${prefix}ChangeDoseFrequency`];
        if (unit !== 1 && unit !== 2) return null;
        const txt =
          unit === 1
            ? row[`patter${prefix}ChangeDoseFrequencyTimesPerWeek`]
            : row[`patter${prefix}ChangeDoseFrequencyWeek`];
        if (isStringEmpty(txt)) return null;
        return { unit, value: String(txt) };
      };
      const getStrengthValueLinvo = (row, prefix) => {
        const unit = row[`patter${prefix}ChangeDoseStrength`];
        if (unit !== 1 && unit !== 2 && unit !== 3) return null;
        const txt =
          unit === 1
            ? row[`patter${prefix}ChangeDoseStrengthMg`]
            : unit === 2
              ? row[`patter${prefix}ChangeDoseStrengthMgKg`]
              : row[`patter${prefix}ChangeDoseStrengthMgM2`];
        if (isStringEmpty(txt)) return null;
        return { unit, value: String(txt) };
      };

      // Sort by date when both rows being compared have a valid date;
      // otherwise (Array.prototype.sort is stable) fall back to the order
      // rows were entered in (i.e., 1st, 2nd, 3rd... as shown in the UI).
      const sortByDateOrEntryOrderLinvo = (rows) =>
        [...rows].sort((a, b) => {
          const aValid = a.dateChangeRd === 1 && IsValidDateValue(a.dateChange);
          const bValid = b.dateChangeRd === 1 && IsValidDateValue(b.dateChange);
          return aValid && bValid
            ? new Date(a.dateChange) - new Date(b.dateChange)
            : 0;
        });

      const chronRowsFreqLinvo = sortByDateOrEntryOrderLinvo(
        Q381ALinvoDetails.filter((r) => r.changeType === 2),
      );
      const chronRowsStrengthLinvo = sortByDateOrEntryOrderLinvo(
        Q381ALinvoDetails.filter((r) => r.changeType === 3),
      );

      let chainMismatchLinvo = false;
      for (let i = 0; i < chronRowsFreqLinvo.length - 1; i++) {
        const after = getFrequencyValueLinvo(chronRowsFreqLinvo[i], "After");
        const before = getFrequencyValueLinvo(
          chronRowsFreqLinvo[i + 1],
          "Before",
        );
        if (
          after != null &&
          before != null &&
          (after.unit !== before.unit || after.value !== before.value)
        ) {
          chainMismatchLinvo = true;
          break;
        }
      }
      if (!chainMismatchLinvo) {
        for (let i = 0; i < chronRowsStrengthLinvo.length - 1; i++) {
          const after = getStrengthValueLinvo(
            chronRowsStrengthLinvo[i],
            "After",
          );
          const before = getStrengthValueLinvo(
            chronRowsStrengthLinvo[i + 1],
            "Before",
          );
          if (
            after != null &&
            before != null &&
            (after.unit !== before.unit || after.value !== before.value)
          ) {
            chainMismatchLinvo = true;
            break;
          }
        }
      }
      setQ381ALinvo_WarningHard(chainMismatchLinvo);

      // AUTO-009 - see the Elrexfio block above.
      const q36MismatchLinvo = ValidateQ38PatternBeforeMatchesQ36(
        chronRowsFreqLinvo[0],
        chronRowsStrengthLinvo[0],
        linvoveyDetails,
        Q381A_LINVO_AUTO_CONFIG,
        getFrequencyValueLinvo,
        getStrengthValueLinvo,
      );
      setQ381ALinvoQ36Mismatch(q36MismatchLinvo);
      if (q36MismatchLinvo) {
        isValid = false;
      }

      setQ381ALinvoDetails([...Q381ALinvoDetails]);
    }

    // ==================== Q38.2.A ELREXFIO VALIDATION ====================
    if (isElrexvayliSelected && isNewEcrfA) {
      Q382AElrexDetails.forEach((row, index) => {
        let rowHasError = false;

        // Column B: Type of change - HARD ERROR
        if (row.changeType === null || row.changeType === undefined) {
          isValid = false;
          rowHasError = true;
          Q382AElrexDetails[index].changeTypeEmpty = true;
        }

        // Skip columns C-E validation if "No changes" (1) or "Unknown" (99)
        if (row.changeType === 1 || row.changeType === 99) {
          return; // Skip rest of validations for this row
        }

        // Column C: Reasons - HARD ERROR
        if (row.selectedReasons.length === 0 && !row.reasonUnknown) {
          isValid = false;
          rowHasError = true;
          Q382AElrexDetails[index].reasonEmpty = true;
        }

        // Patient death reason retired (hidden in UI, see
        // RetiredDiscontinuationReasons) - date-of-death validation no
        // longer reachable, commented out rather than left dead.
        /*
        // Check for patient death warning
        if (row.selectedReasons.includes(1)) {
          // ID=1 is "Patient death"
          if (patientDeathDate === null || isStringEmpty(patientDeathDate)) {
            isValid = false;
            rowHasError = true;
            Q382AElrexDetails[index].deathDtWarning = true;
          }
        }
        */

        // If "Other" (ID=14) is selected, validate other text field
        if (row.selectedReasons.includes(14)) {
          if (isStringEmpty(row.reasonOtherText)) {
            isValid = false;
            rowHasError = true;
            Q382AElrexDetails[index].reasonOtherTextEmpty = true;
          }
        }

        // If "Adverse Event" (ID=2) is selected, validate AE sub-reasons
        if (row.selectedReasons.includes(2)) {
          const hasAESubReasons = row.selectedAEReasons.length > 0;
          const aeReasonItem = row.reasonList?.find(
            (r) => r.bispecifictreatmentdiscontinuationreasonid === 2,
          );
          if (!hasAESubReasons) {
            isValid = false;
            rowHasError = true;
            Q382AElrexDetails[index].aeSubReasonEmpty = true;
          } else {
            const selectedAEs =
              aeReasonItem?.aeSubReasons?.filter((ae) => ae.isSelected) || [];
            const isNeuroSelected = selectedAEs.some(
              (ae) => ae.reasonName === DiscontinuationReason.NeurologicalEvent,
            );

            if (isNeuroSelected) {
              const selectedNeuroReasons =
                row.neurologicAEReasonList?.filter((n) => n.isSelected) || [];

              if (selectedNeuroReasons.length === 0) {
                isValid = false;
                rowHasError = true;
                Q382AElrexDetails[index].neuroReasonEmpty = true;
              }

              selectedNeuroReasons.forEach((neuro) => {
                if (
                  neuro.reasonName?.includes("Other") &&
                  (!neuro.reasonText || !neuro.reasonText.trim())
                ) {
                  isValid = false;
                  rowHasError = true;
                  Q382AElrexDetails[index].neuroOtherTextEmpty = true;
                }
              });
            }
          }
        }

        // Column D: Start date - HARD ERROR
        if (
          row.startDateChangeRd === null ||
          row.startDateChangeRd === undefined
        ) {
          isValid = false;
          rowHasError = true;
          Q382AElrexDetails[index].startDateChangeRdEmpty = true;
        }

        // If "Date provided" (1) is selected for start date
        if (row.startDateChangeRd === 1) {
          if (isStringEmpty(row.startDateChange)) {
            isValid = false;
            rowHasError = true;
            Q382AElrexDetails[index].startDateChangeEmpty = true;
          } else {
            // Date format validation
            if (
              !DateInRange(row.startDateChange, dayjs("01-01-1920"), dayjs())
            ) {
              isValid = false;
              rowHasError = true;
              Q382AElrexDetails[index].invalidStartDateChange = true;
            } else {
              // Date range validation (between Index Date and End of Follow-up)
              if (
                !ValidateQ382ADateChain(
                  row.startDateChange,
                  RRMMCohortTreatment.ELREXFIO,
                  "start",
                )
              ) {
                isValid = false;
                rowHasError = true;
                Q382AElrexDetails[index].startDateChangeOutOfBound = true;
              }
            }
          }
        }

        // Column E: End date - HARD ERROR
        if (row.endDateChangeRd === null || row.endDateChangeRd === undefined) {
          isValid = false;
          rowHasError = true;
          Q382AElrexDetails[index].endDateChangeRdEmpty = true;
        }

        // If "Date provided" (2) is selected for end date
        if (row.endDateChangeRd === 2) {
          if (isStringEmpty(row.endDateChange)) {
            isValid = false;
            rowHasError = true;
            Q382AElrexDetails[index].endDateChangeEmpty = true;
          } else {
            // Date format validation
            if (!DateInRange(row.endDateChange, dayjs("01-01-1920"), dayjs())) {
              isValid = false;
              rowHasError = true;
              Q382AElrexDetails[index].invalidEndDateChange = true;
            } else {
              // Date range validation
              if (
                !ValidateQ382ADateChain(
                  row.endDateChange,
                  RRMMCohortTreatment.ELREXFIO,
                  "end",
                )
              ) {
                isValid = false;
                rowHasError = true;
                Q382AElrexDetails[index].endDateChangeOutOfBound = true;
              }

              // End date must be >= Start date
              if (
                row.startDateChangeRd === 1 &&
                !isStringEmpty(row.startDateChange)
              ) {
                if (
                  dayjs(row.endDateChange).isBefore(dayjs(row.startDateChange))
                ) {
                  isValid = false;
                  rowHasError = true;
                  Q382AElrexDetails[index].invalidEndDateChange = true;
                }
              }
            }
          }
        }
      });

      setQ382AElrexDetails([...Q382AElrexDetails]);
    }

    // ==================== Q38.2.A LINVOSELTAMAB VALIDATION ====================
    if (isLinvoveySelected && isNewEcrfA) {
      Q382ALinvoDetails.forEach((row, index) => {
        let rowHasError = false;

        // Column B: Type of change - HARD ERROR
        if (row.changeType === null || row.changeType === undefined) {
          isValid = false;
          rowHasError = true;
          Q382ALinvoDetails[index].changeTypeEmpty = true;
        }

        // Skip columns C-E validation if "No changes" (1) or "Unknown" (99)
        if (row.changeType === 1 || row.changeType === 99) {
          return; // Skip rest of validations for this row
        }

        // Column C: Reasons - HARD ERROR
        if (row.selectedReasons.length === 0 && !row.reasonUnknown) {
          isValid = false;
          rowHasError = true;
          Q382ALinvoDetails[index].reasonEmpty = true;
        }

        // Patient death reason retired (hidden in UI, see
        // RetiredDiscontinuationReasons) - date-of-death validation no
        // longer reachable, commented out rather than left dead.
        /*
        // Check for patient death warning
        if (row.selectedReasons.includes(1)) {
          if (patientDeathDate === null || isStringEmpty(patientDeathDate)) {
            isValid = false;
            rowHasError = true;
            Q382ALinvoDetails[index].deathDtWarning = true;
          }
        }
        */

        // If "Other" (ID=14) is selected, validate other text field
        if (row.selectedReasons.includes(14)) {
          if (isStringEmpty(row.reasonOtherText)) {
            isValid = false;
            rowHasError = true;
            Q382ALinvoDetails[index].reasonOtherTextEmpty = true;
          }
        }

        // If "Adverse Event" (ID=2) is selected, validate AE sub-reasons
        if (row.selectedReasons.includes(2)) {
          const hasAESubReasons = row.selectedAEReasons.length > 0;
          const aeReasonItem = row.reasonList?.find(
            (r) => r.bispecifictreatmentdiscontinuationreasonid === 2,
          );
          if (!hasAESubReasons) {
            isValid = false;
            rowHasError = true;
            Q382ALinvoDetails[index].aeSubReasonEmpty = true;
          } else {
            const selectedAEs =
              aeReasonItem?.aeSubReasons?.filter((ae) => ae.isSelected) || [];
            const isNeuroSelected = selectedAEs.some(
              (ae) => ae.reasonName === DiscontinuationReason.NeurologicalEvent,
            );

            if (isNeuroSelected) {
              const selectedNeuroReasons =
                row.neurologicAEReasonList?.filter((n) => n.isSelected) || [];

              if (selectedNeuroReasons.length === 0) {
                isValid = false;
                rowHasError = true;
                Q382ALinvoDetails[index].neuroReasonEmpty = true;
              }

              selectedNeuroReasons.forEach((neuro) => {
                if (
                  neuro.reasonName?.includes("Other") &&
                  (!neuro.reasonText || !neuro.reasonText.trim())
                ) {
                  isValid = false;
                  rowHasError = true;
                  Q382ALinvoDetails[index].neuroOtherTextEmpty = true;
                }
              });
            }
          }
        }

        // Column D: Start date - HARD ERROR
        if (
          row.startDateChangeRd === null ||
          row.startDateChangeRd === undefined
        ) {
          isValid = false;
          rowHasError = true;
          Q382ALinvoDetails[index].startDateChangeRdEmpty = true;
        }

        // If "Date provided" (1) is selected for start date
        if (row.startDateChangeRd === 1) {
          if (isStringEmpty(row.startDateChange)) {
            isValid = false;
            rowHasError = true;
            Q382ALinvoDetails[index].startDateChangeEmpty = true;
          } else {
            // Date format validation
            if (
              !DateInRange(row.startDateChange, dayjs("01-01-1920"), dayjs())
            ) {
              isValid = false;
              rowHasError = true;
              Q382ALinvoDetails[index].invalidStartDateChange = true;
            } else {
              // Date range validation
              if (
                !ValidateQ382ADateChain(
                  row.startDateChange,
                  RRMMCohortTreatment.Linvoseltamab,
                  "start",
                )
              ) {
                isValid = false;
                rowHasError = true;
                Q382ALinvoDetails[index].startDateChangeOutOfBound = true;
              }
            }
          }
        }

        // Column E: End date - HARD ERROR
        if (row.endDateChangeRd === null || row.endDateChangeRd === undefined) {
          isValid = false;
          rowHasError = true;
          Q382ALinvoDetails[index].endDateChangeRdEmpty = true;
        }

        // If "Date provided" (2) is selected for end date
        if (row.endDateChangeRd === 2) {
          if (isStringEmpty(row.endDateChange)) {
            isValid = false;
            rowHasError = true;
            Q382ALinvoDetails[index].endDateChangeEmpty = true;
          } else {
            // Date format validation
            if (!DateInRange(row.endDateChange, dayjs("01-01-1920"), dayjs())) {
              isValid = false;
              rowHasError = true;
              Q382ALinvoDetails[index].invalidEndDateChange = true;
            } else {
              // Date range validation
              if (
                !ValidateQ382ADateChain(
                  row.endDateChange,
                  RRMMCohortTreatment.Linvoseltamab,
                  "end",
                )
              ) {
                isValid = false;
                rowHasError = true;
                Q382ALinvoDetails[index].endDateChangeOutOfBound = true;
              }

              // End date must be >= Start date
              if (
                row.startDateChangeRd === 1 &&
                !isStringEmpty(row.startDateChange)
              ) {
                if (
                  dayjs(row.endDateChange).isBefore(dayjs(row.startDateChange))
                ) {
                  isValid = false;
                  rowHasError = true;
                  Q382ALinvoDetails[index].invalidEndDateChange = true;
                }
              }
            }
          }
        }
      });

      setQ382ALinvoDetails([...Q382ALinvoDetails]);
    }

    //Q40.1.B Validate
    if (
      !isNewEcrfC &&
      isNewEcrfB &&
      showQ40Elr &&
      Q37ElrexDetails[0]?.Q40dosingfreqRd != null &&
      Q37ElrexDetails[0]?.Q40dosingfreqRd != 1
    ) {
      const updatedQ401aElrList = Q401aElrDetails.map((row, index) => {
        let Q401aInit_Elr_RdHard = false;
        let Q401aInit_Elr_dtHard = false;
        let Q401aInvalidQ401ainit_Elr_dt = false;
        let Q401aInit_Elr_dt_before_eof = false;
        let Q401aIntendedDosingEmpty_Elr = false;
        let Q401aIntendedDosingRangeInvalid_Elr = false;
        let Q401aReasonIvigEmptyHard_Elr = false;
        let Q401aDosingType_ElrEmptyHard = false;
        let Q401aDosageFrequencyEmpty_Elr = false;
        let Q401aDosageRangeInvalid_Elr = false;
        let Q401aWeightEmpty_Elr = false;
        let Q401aWeightRangeInvalid_Elr = false;
        let Q401aWeight_AssesEmptyHard_Elr = false;
        let Q401aWeight_AssesDtEmptyHard_Elr = false;
        let Q401aWeight_AssesDtInvalid_Elr = false;
        let Q401aWeight_Asses_dt_before_eof_Elr = false;

        /* ---------------- Column B ---------------- */
        if (row.Q401ainit_dt_Rd_Elr === null) {
          Q401aInit_Elr_RdHard = true;
        }

        if (!Q401aInit_Elr_RdHard && row.Q401ainit_dt_Rd_Elr === 1) {
          if (isStringEmpty(row.Q401ainit_Elr_dt)) {
            Q401aInit_Elr_dtHard = true;
          } else if (!IsDateValid(row.Q401ainit_Elr_dt)) {
            Q401aInvalidQ401ainit_Elr_dt = true;
          } else if (
            !ValidateIndexDAndLOTDateChain(
              row.Q401ainit_Elr_dt,
              RRMMCohortTreatment.ELREXFIO,
            )
          ) {
            Q401aInit_Elr_dt_before_eof = true;
          }
        }

        /* ---------------- Column C ---------------- */
        if (
          row.Q401aIntendedDosing_Elr === null &&
          isStringEmpty(row.Q401aIntendedDosing_Elr_WeekValue) &&
          isStringEmpty(row.Q401aIntendedDosing_Elr_MonthValue) &&
          isStringEmpty(row.Q401aIntendedDosing_Elr_OnceWeekValue) &&
          isStringEmpty(row.Q401aIntendedDosing_Elr_OnceMonthValue) &&
          isStringEmpty(row.Q401aIntendedDosing_Elr_OtherValue)
        ) {
          Q401aIntendedDosingEmpty_Elr = true;
        }

        if (
          (row.Q401aIntendedDosing_Elr === 1 &&
            isStringEmpty(row.Q401aIntendedDosing_Elr_WeekValue)) ||
          (row.Q401aIntendedDosing_Elr === 2 &&
            isStringEmpty(row.Q401aIntendedDosing_Elr_MonthValue)) ||
          (row.Q401aIntendedDosing_Elr === 3 &&
            isStringEmpty(row.Q401aIntendedDosing_Elr_OnceWeekValue)) ||
          (row.Q401aIntendedDosing_Elr === 4 &&
            isStringEmpty(row.Q401aIntendedDosing_Elr_OnceMonthValue))
        ) {
          Q401aIntendedDosingRangeInvalid_Elr = true;
        }

        /* ---------------- Column D ---------------- */
        if (
          row.Q401aPrimaryProf_Elr === false &&
          row.Q401aSecProf_Elr === false &&
          row.Q401aOtherProf_Elr === false &&
          row.Q401aUnknownProf_Elr === null
        ) {
          Q401aReasonIvigEmptyHard_Elr = true;
        }

        /* ---------------- Column E ---------------- */
        if (row.Q401aDosingType_Elr === null) {
          Q401aDosingType_ElrEmptyHard = true;
        }

        /* ---------------- Column F ---------------- */
        if (
          row.Q401aDosage_Elr === null &&
          isStringEmpty(row.Q401aDosage_Elr_GValue) &&
          isStringEmpty(row.Q401aDosage_Elr_MgValue)
        ) {
          Q401aDosageFrequencyEmpty_Elr = true;
        }

        if (
          (row.Q401aDosage_Elr === 1 &&
            isStringEmpty(row.Q401aDosage_Elr_GValue)) ||
          (row.Q401aDosage_Elr === 2 &&
            isStringEmpty(row.Q401aDosage_Elr_MgValue)) ||
          (!isStringEmpty(row.Q401aDosage_Elr_GValue) &&
            (parseInt(row.Q401aDosage_Elr_GValue) < 1 ||
              parseInt(row.Q401aDosage_Elr_GValue) > 200)) ||
          (!isStringEmpty(row.Q401aDosage_Elr_MgValue) &&
            (parseInt(row.Q401aDosage_Elr_MgValue) < 1 ||
              parseInt(row.Q401aDosage_Elr_MgValue) > 2000))
        ) {
          Q401aDosageRangeInvalid_Elr = true;
        }

        /* ---------------- Column G ---------------- */
        if (
          row.Q401aDosingType_Elr == 2 &&
          row.Q401aWeight_Elr === null &&
          isStringEmpty(row.Q401aWeight_Value_KgElr) &&
          isStringEmpty(row.Q401aWeight_Value_LbsElr)
        ) {
          Q401aWeightEmpty_Elr = true;
        }

        if (
          (row.Q401aDosingType_Elr == 2 &&
            row.Q401aWeight_Elr === 1 &&
            isStringEmpty(row.Q401aWeight_Value_KgElr)) ||
          (row.Q401aWeight_Elr === 2 &&
            isStringEmpty(row.Q401aWeight_Value_LbsElr)) ||
          (!isStringEmpty(row.Q401aWeight_Value_KgElr) &&
            (parseInt(row.Q401aWeight_Value_KgElr) < 2 ||
              parseInt(row.Q401aWeight_Value_KgElr) > 630)) ||
          (!isStringEmpty(row.Q401aWeight_Value_LbsElr) &&
            (parseInt(row.Q401aWeight_Value_LbsElr) < 2 ||
              parseInt(row.Q401aWeight_Value_LbsElr) > 1400))
        ) {
          Q401aWeightRangeInvalid_Elr = true;
        }

        /* ---------------- Column H ---------------- */
        if (
          row.Q401aDosingType_Elr == 2 &&
          row.Q401aWeight_Asses_Elr === null &&
          isStringEmpty(row.Q401aWeight_Asses_Elr_dt)
        ) {
          Q401aWeight_AssesEmptyHard_Elr = true;
        }

        if (row.Q401aDosingType_Elr == 2 && row.Q401aWeight_Asses_Elr === 1) {
          if (isStringEmpty(row.Q401aWeight_Asses_Elr_dt)) {
            Q401aWeight_AssesDtEmptyHard_Elr = true;
          } else if (!IsDateValid(row.Q401aWeight_Asses_Elr_dt)) {
            Q401aWeight_AssesDtInvalid_Elr = true;
          } else if (
            !ValidateIndexDAndLOTDateChain(
              row.Q401aWeight_Asses_Elr_dt,
              RRMMCohortTreatment.ELREXFIO,
            )
          ) {
            Q401aWeight_Asses_dt_before_eof_Elr = true;
          }
        }

        if (
          Q401aInit_Elr_RdHard ||
          Q401aInit_Elr_dtHard ||
          Q401aInvalidQ401ainit_Elr_dt ||
          Q401aInit_Elr_dt_before_eof ||
          Q401aIntendedDosingEmpty_Elr ||
          Q401aIntendedDosingRangeInvalid_Elr ||
          Q401aReasonIvigEmptyHard_Elr ||
          Q401aDosingType_ElrEmptyHard ||
          Q401aDosageFrequencyEmpty_Elr ||
          Q401aDosageRangeInvalid_Elr ||
          Q401aWeightEmpty_Elr ||
          Q401aWeightRangeInvalid_Elr ||
          Q401aWeight_AssesEmptyHard_Elr ||
          Q401aWeight_AssesDtEmptyHard_Elr ||
          Q401aWeight_AssesDtInvalid_Elr ||
          Q401aWeight_Asses_dt_before_eof_Elr
        ) {
          isValid = false;
        }

        return {
          ...row,
          Q401aInit_Elr_RdHard,
          Q401aInit_Elr_dtHard,
          Q401aInvalidQ401ainit_Elr_dt,
          Q401aInit_Elr_dt_before_eof,
          Q401aIntendedDosingEmpty_Elr,
          Q401aIntendedDosingRangeInvalid_Elr,
          Q401aReasonIvigEmptyHard_Elr,
          Q401aDosingType_ElrEmptyHard,
          Q401aDosageFrequencyEmpty_Elr,
          Q401aDosageRangeInvalid_Elr,
          Q401aWeightEmpty_Elr,
          Q401aWeightRangeInvalid_Elr,
          Q401aWeight_AssesEmptyHard_Elr,
          Q401aWeight_AssesDtEmptyHard_Elr,
          Q401aWeight_AssesDtInvalid_Elr,
          Q401aWeight_Asses_dt_before_eof_Elr,
        };
      });

      setQ401aElrDetails(updatedQ401aElrList);

      // Warning for Q40.1.B
      const priEnabled = Q37ElrexDetails[0]?.primaryProf;
      const secEnabled = Q37ElrexDetails[0]?.secondaryProf;
      const otherEnabled = Q37ElrexDetails[0]?.otherReson;

      // Check individually
      const priMatch =
        !priEnabled ||
        Q401aElrDetails?.some((x) => x.Q401aPrimaryProf_Elr === true);
      const secMatch =
        !secEnabled ||
        Q401aElrDetails?.some((x) => x.Q401aSecProf_Elr === true);
      const otherMatch =
        !otherEnabled ||
        Q401aElrDetails?.some((x) => x.Q401aOtherProf_Elr === true);

      // Final condition
      const isMatch = priMatch && secMatch && otherMatch;

      if (!isMatch) setQ40Elr_WarningHard(true);
      else setQ40Elr_WarningHard(false);
    }

    if (
      !isNewEcrfC &&
      isNewEcrfB &&
      showQ40Linvo &&
      Q37LinvoDetails[0]?.q40DosingFreqRd_linvo != null &&
      Q37LinvoDetails[0]?.q40DosingFreqRd_linvo != 1
    ) {
      const updatedQ401aLinvoList = Q401aLinvoDetails.map((row, index) => {
        let Q401aInit_Lin_RdHard = false;
        let Q401aInit_Lin_dtHard = false;
        let Q401aInvalidQ401ainit_Lin_dt = false;
        let Q401aInit_Lin_dt_before_eof = false;
        let Q401aIntendedDosingEmpty_Lin = false;
        let Q401aIntendedDosingRangeInvalid_Lin = false;
        let Q401aReasonIvigEmptyHard_Lin = false;
        let Q401aDosingType_LinEmptyHard = false;
        let Q401aDosageFrequencyEmpty_Lin = false;
        let Q401aDosageRangeInvalid_Lin = false;
        let Q401aWeightEmpty_Lin = false;
        let Q401aWeightRangeInvalid_Lin = false;
        let Q401aWeight_AssesEmptyHard_Lin = false;
        let Q401aWeight_AssesDtEmptyHard_Lin = false;
        let Q401aWeight_AssesDtInvalid_Lin = false;
        let Q401aWeight_Asses_dt_before_eof_Lin = false;

        /* ---------------- Column B ---------------- */
        if (row.Q401ainit_dt_Rd_Lin === null) {
          Q401aInit_Lin_RdHard = true;
        }

        if (!Q401aInit_Lin_RdHard && row.Q401ainit_dt_Rd_Lin === 1) {
          if (isStringEmpty(row.Q401ainit_Lin_dt)) {
            Q401aInit_Lin_dtHard = true;
          } else if (!IsDateValid(row.Q401ainit_Lin_dt)) {
            Q401aInvalidQ401ainit_Lin_dt = true;
          } else if (
            !ValidateIndexDAndLOTDateChain(
              row.Q401ainit_Lin_dt,
              RRMMCohortTreatment.Linvoseltamab,
            )
          ) {
            Q401aInit_Lin_dt_before_eof = true;
          }
        }

        /* ---------------- Column C ---------------- */
        if (
          row.Q401aIntendedDosing_Lin === null &&
          isStringEmpty(row.Q401aIntendedDosing_Lin_WeekValue) &&
          isStringEmpty(row.Q401aIntendedDosing_Lin_MonthValue) &&
          isStringEmpty(row.Q401aIntendedDosing_Lin_OnceWeekValue) &&
          isStringEmpty(row.Q401aIntendedDosing_Lin_OnceMonthValue) &&
          isStringEmpty(row.Q401aIntendedDosing_Lin_OtherValue)
        ) {
          Q401aIntendedDosingEmpty_Lin = true;
        }

        if (
          (row.Q401aIntendedDosing_Lin === 1 &&
            isStringEmpty(row.Q401aIntendedDosing_Lin_WeekValue)) ||
          (row.Q401aIntendedDosing_Lin === 2 &&
            isStringEmpty(row.Q401aIntendedDosing_Lin_MonthValue)) ||
          (row.Q401aIntendedDosing_Lin === 3 &&
            isStringEmpty(row.Q401aIntendedDosing_Lin_OnceWeekValue)) ||
          (row.Q401aIntendedDosing_Lin === 4 &&
            isStringEmpty(row.Q401aIntendedDosing_Lin_OnceMonthValue))
        ) {
          Q401aIntendedDosingRangeInvalid_Lin = true;
        }

        /* ---------------- Column D ---------------- */
        if (
          row.Q401aPrimaryProf_Lin === false &&
          row.Q401aSecProf_Lin === false &&
          row.Q401aOtherProf_Lin === false &&
          row.Q401aUnknownProf_Lin === null
        ) {
          Q401aReasonIvigEmptyHard_Lin = true;
        }

        /* ---------------- Column E ---------------- */
        if (row.Q401aDosingType_Lin === null) {
          Q401aDosingType_LinEmptyHard = true;
        }

        /* ---------------- Column F ---------------- */
        if (
          row.Q401aDosage_Lin === null &&
          isStringEmpty(row.Q401aDosage_Lin_GValue) &&
          isStringEmpty(row.Q401aDosage_Lin_MgValue)
        ) {
          Q401aDosageFrequencyEmpty_Lin = true;
        }

        if (
          (row.Q401aDosage_Lin === 1 &&
            isStringEmpty(row.Q401aDosage_Lin_GValue)) ||
          (row.Q401aDosage_Lin === 2 &&
            isStringEmpty(row.Q401aDosage_Lin_MgValue)) ||
          (!isStringEmpty(row.Q401aDosage_Lin_GValue) &&
            (parseInt(row.Q401aDosage_Lin_GValue) < 1 ||
              parseInt(row.Q401aDosage_Lin_GValue) > 200)) ||
          (!isStringEmpty(row.Q401aDosage_Lin_MgValue) &&
            (parseInt(row.Q401aDosage_Lin_MgValue) < 1 ||
              parseInt(row.Q401aDosage_Lin_MgValue) > 2000))
        ) {
          Q401aDosageRangeInvalid_Lin = true;
        }

        /* ---------------- Column G ---------------- */
        if (
          row.Q401aDosingType_Lin == 2 &&
          row.Q401aWeight_Lin === null &&
          isStringEmpty(row.Q401aWeight_Value_KgLin) &&
          isStringEmpty(row.Q401aWeight_Value_LbsLin)
        ) {
          Q401aWeightEmpty_Lin = true;
        }

        if (
          (row.Q401aDosingType_Lin == 2 &&
            row.Q401aWeight_Lin === 1 &&
            isStringEmpty(row.Q401aWeight_Value_KgLin)) ||
          (row.Q401aWeight_Lin === 2 &&
            isStringEmpty(row.Q401aWeight_Value_LbsLin)) ||
          (!isStringEmpty(row.Q401aWeight_Value_KgLin) &&
            (parseInt(row.Q401aWeight_Value_KgLin) < 2 ||
              parseInt(row.Q401aWeight_Value_KgLin) > 630)) ||
          (!isStringEmpty(row.Q401aWeight_Value_LbsLin) &&
            (parseInt(row.Q401aWeight_Value_LbsLin) < 2 ||
              parseInt(row.Q401aWeight_Value_LbsLin) > 1400))
        ) {
          Q401aWeightRangeInvalid_Lin = true;
        }

        /* ---------------- Column H ---------------- */
        if (
          row.Q401aDosingType_Lin == 2 &&
          row.Q401aWeight_Asses_Lin === null &&
          isStringEmpty(row.Q401aWeight_Asses_Lin_dt)
        ) {
          Q401aWeight_AssesEmptyHard_Lin = true;
        }

        if (row.Q401aDosingType_Lin === 2 && row.Q401aWeight_Asses_Lin === 1) {
          if (isStringEmpty(row.Q401aWeight_Asses_Lin_dt)) {
            Q401aWeight_AssesDtEmptyHard_Lin = true;
          } else if (!IsDateValid(row.Q401aWeight_Asses_Lin_dt)) {
            Q401aWeight_AssesDtInvalid_Lin = true;
          } else if (
            !ValidateIndexDAndLOTDateChain(
              row.Q401aWeight_Asses_Lin_dt,
              RRMMCohortTreatment.Linvoseltamab,
            )
          ) {
            Q401aWeight_Asses_dt_before_eof_Lin = true;
          }
        }

        if (
          Q401aInit_Lin_RdHard ||
          Q401aInit_Lin_dtHard ||
          Q401aInvalidQ401ainit_Lin_dt ||
          Q401aInit_Lin_dt_before_eof ||
          Q401aIntendedDosingEmpty_Lin ||
          Q401aIntendedDosingRangeInvalid_Lin ||
          Q401aReasonIvigEmptyHard_Lin ||
          Q401aDosingType_LinEmptyHard ||
          Q401aDosageFrequencyEmpty_Lin ||
          Q401aDosageRangeInvalid_Lin ||
          Q401aWeightEmpty_Lin ||
          Q401aWeightRangeInvalid_Lin ||
          Q401aWeight_AssesEmptyHard_Lin ||
          Q401aWeight_AssesDtEmptyHard_Lin ||
          Q401aWeight_AssesDtInvalid_Lin ||
          Q401aWeight_Asses_dt_before_eof_Lin
        ) {
          isValid = false;
        }

        return {
          ...row,
          Q401aInit_Lin_RdHard,
          Q401aInit_Lin_dtHard,
          Q401aInvalidQ401ainit_Lin_dt,
          Q401aInit_Lin_dt_before_eof,
          Q401aIntendedDosingEmpty_Lin,
          Q401aIntendedDosingRangeInvalid_Lin,
          Q401aReasonIvigEmptyHard_Lin,
          Q401aDosingType_LinEmptyHard,
          Q401aDosageFrequencyEmpty_Lin,
          Q401aDosageRangeInvalid_Lin,
          Q401aWeightEmpty_Lin,
          Q401aWeightRangeInvalid_Lin,
          Q401aWeight_AssesEmptyHard_Lin,
          Q401aWeight_AssesDtEmptyHard_Lin,
          Q401aWeight_AssesDtInvalid_Lin,
          Q401aWeight_Asses_dt_before_eof_Lin,
        };
      });

      setQ401aLinvoDetails(updatedQ401aLinvoList);

      // Warning for Q40.1.B
      const priEnabled = Q37LinvoDetails[0]?.primaryProf_Linvo;
      const secEnabled = Q37LinvoDetails[0]?.secondaryProf_Linvo;
      const otherEnabled = Q37LinvoDetails[0]?.otherReson_Linvo;

      // Check individually
      const priMatch =
        !priEnabled ||
        updatedQ401aLinvoList?.some((x) => x.Q401aPrimaryProf_Lin === true);
      const secMatch =
        !secEnabled ||
        updatedQ401aLinvoList?.some((x) => x.Q401aSecProf_Lin === true);
      const otherMatch =
        !otherEnabled ||
        updatedQ401aLinvoList?.some((x) => x.Q401aOtherProf_Lin === true);

      // Final condition
      const isMatch = priMatch && secMatch && otherMatch;
      if (!isMatch) setQ40Lin_WarningHard(true);
      else setQ40Lin_WarningHard(false);
    }

    //Q40.1.C Validate - Elrexfio
    if (
      isNewEcrfC &&
      showQ40Elr &&
      Q37ElrexDetails[0]?.ivigInitRd_elrC != null &&
      Q37ElrexDetails[0]?.ivigInitRd_elrC !== 1
    ) {
      const ivigInitDateElr =
        Q37ElrexDetails[0]?.ivigInitRd_elrC === 2
          ? Q37ElrexDetails[0]?.ivigInitDt_elrC
          : null;

      const updatedQ401cElrList = Q401cElrDetails.map((row) => {
        let Q401cChangeTypeEmptyHard_Elr = false;
        let Q401cFreqBeforeEmptyHard_Elr = false;
        let Q401cFreqBeforeTxtEmptyHard_Elr = false;
        let Q401cFreqBeforeRangeInvalid_Elr = false;
        let Q401cFreqAfterEmptyHard_Elr = false;
        let Q401cFreqAfterTxtEmptyHard_Elr = false;
        let Q401cFreqAfterRangeInvalid_Elr = false;
        let Q401cDateChangeRdHard_Elr = false;
        let Q401cDateChangeDtHard_Elr = false;
        let Q401cDateChangeInvalid_Elr = false;
        let Q401cDateChangeOutOfBound_Elr = false;
        let Q401cFrequencyIncreaseConflict_Elr = false;
        let Q401cFrequencyDecreaseConflict_Elr = false;
        let Q401cWarningUnk_Elr = false;

        if (row.Q401cChangeType_Elr === null) {
          Q401cChangeTypeEmptyHard_Elr = true;
        }

        const isRealChange =
          row.Q401cChangeType_Elr === 2 || row.Q401cChangeType_Elr === 3;

        if (isRealChange) {
          /* ---------------- Column C: before ---------------- */
          if (
            row.Q401cFreqBeforeRd_Elr === null &&
            isStringEmpty(row.Q401cFreqBeforeTimesTxt_Elr) &&
            isStringEmpty(row.Q401cFreqBeforeWeeksTxt_Elr) &&
            isStringEmpty(row.Q401cFreqBeforeOtherTxt_Elr)
          ) {
            Q401cFreqBeforeEmptyHard_Elr = true;
          } else if (row.Q401cFreqBeforeRd_Elr === 3) {
            if (isStringEmpty(row.Q401cFreqBeforeOtherTxt_Elr)) {
              Q401cFreqBeforeTxtEmptyHard_Elr = true;
            }
          } else if (row.Q401cFreqBeforeRd_Elr === 99) {
            Q401cWarningUnk_Elr = true;
          } else if (row.Q401cFreqBeforeRd_Elr !== null) {
            const freqTxt =
              row.Q401cFreqBeforeRd_Elr === 1
                ? row.Q401cFreqBeforeTimesTxt_Elr
                : row.Q401cFreqBeforeWeeksTxt_Elr;
            if (isStringEmpty(freqTxt)) {
              Q401cFreqBeforeTxtEmptyHard_Elr = true;
            } else {
              const freqNum = parseInt(freqTxt, 10);
              if (
                isNaN(freqNum) ||
                freqNum <= 0 ||
                freqNum > 10 ||
                !Number.isInteger(Number(freqTxt))
              ) {
                Q401cFreqBeforeRangeInvalid_Elr = true;
              }
            }
          }

          /* ---------------- Column D: after ---------------- */
          if (
            row.Q401cFreqAfterRd_Elr === null &&
            isStringEmpty(row.Q401cFreqAfterTimesTxt_Elr) &&
            isStringEmpty(row.Q401cFreqAfterWeeksTxt_Elr) &&
            isStringEmpty(row.Q401cFreqAfterOtherTxt_Elr)
          ) {
            Q401cFreqAfterEmptyHard_Elr = true;
          } else if (row.Q401cFreqAfterRd_Elr === 3) {
            if (isStringEmpty(row.Q401cFreqAfterOtherTxt_Elr)) {
              Q401cFreqAfterTxtEmptyHard_Elr = true;
            }
          } else if (row.Q401cFreqAfterRd_Elr === 99) {
            Q401cWarningUnk_Elr = true;
          } else if (row.Q401cFreqAfterRd_Elr !== null) {
            const freqTxt =
              row.Q401cFreqAfterRd_Elr === 1
                ? row.Q401cFreqAfterTimesTxt_Elr
                : row.Q401cFreqAfterWeeksTxt_Elr;
            if (isStringEmpty(freqTxt)) {
              Q401cFreqAfterTxtEmptyHard_Elr = true;
            } else {
              const freqNum = parseInt(freqTxt, 10);
              if (
                isNaN(freqNum) ||
                freqNum <= 0 ||
                freqNum > 10 ||
                !Number.isInteger(Number(freqTxt))
              ) {
                Q401cFreqAfterRangeInvalid_Elr = true;
              }
            }
          }

          /* ---------------- Column E: date of change ---------------- */
          if (row.Q401cDateChangeRd_Elr === null) {
            Q401cDateChangeRdHard_Elr = true;
          }

          if (!Q401cDateChangeRdHard_Elr && row.Q401cDateChangeRd_Elr === 1) {
            if (isStringEmpty(row.Q401cDateChange_Elr)) {
              Q401cDateChangeDtHard_Elr = true;
            } else if (!IsDateValid(row.Q401cDateChange_Elr)) {
              Q401cDateChangeInvalid_Elr = true;
            } else if (
              !ValidateQ401CDateChain(
                row.Q401cDateChange_Elr,
                RRMMCohortTreatment.ELREXFIO,
                ivigInitDateElr,
              )
            ) {
              Q401cDateChangeOutOfBound_Elr = true;
            }
          }

          /* ---------------- Increase/decrease conflict (normalizes both sides to a doses-per-week rate before comparing, since "once every N weeks" and "X times per week" are on inverse scales) ---------------- */
          const beforeNumElr =
            row.Q401cFreqBeforeRd_Elr === 1
              ? row.Q401cFreqBeforeTimesTxt_Elr
              : row.Q401cFreqBeforeRd_Elr === 2
                ? row.Q401cFreqBeforeWeeksTxt_Elr
                : null;
          const afterNumElr =
            row.Q401cFreqAfterRd_Elr === 1
              ? row.Q401cFreqAfterTimesTxt_Elr
              : row.Q401cFreqAfterRd_Elr === 2
                ? row.Q401cFreqAfterWeeksTxt_Elr
                : null;

          if (!isStringEmpty(beforeNumElr) && !isStringEmpty(afterNumElr)) {
            const beforeRateElr =
              row.Q401cFreqBeforeRd_Elr === 2
                ? 1 / Number(beforeNumElr)
                : Number(beforeNumElr);
            const afterRateElr =
              row.Q401cFreqAfterRd_Elr === 2
                ? 1 / Number(afterNumElr)
                : Number(afterNumElr);

            if (
              row.Q401cChangeType_Elr === 2 &&
              afterRateElr <= beforeRateElr
            ) {
              Q401cFrequencyIncreaseConflict_Elr = true;
            } else if (
              row.Q401cChangeType_Elr === 3 &&
              afterRateElr >= beforeRateElr
            ) {
              Q401cFrequencyDecreaseConflict_Elr = true;
            }
          }
        }

        if (
          Q401cChangeTypeEmptyHard_Elr ||
          Q401cFreqBeforeEmptyHard_Elr ||
          Q401cFreqBeforeTxtEmptyHard_Elr ||
          Q401cFreqBeforeRangeInvalid_Elr ||
          Q401cFreqAfterEmptyHard_Elr ||
          Q401cFreqAfterTxtEmptyHard_Elr ||
          Q401cFreqAfterRangeInvalid_Elr ||
          Q401cDateChangeRdHard_Elr ||
          Q401cDateChangeDtHard_Elr ||
          Q401cDateChangeInvalid_Elr ||
          Q401cDateChangeOutOfBound_Elr ||
          Q401cFrequencyIncreaseConflict_Elr ||
          Q401cFrequencyDecreaseConflict_Elr
        ) {
          isValid = false;
        }

        return {
          ...row,
          Q401cChangeTypeEmptyHard_Elr,
          Q401cFreqBeforeEmptyHard_Elr,
          Q401cFreqBeforeTxtEmptyHard_Elr,
          Q401cFreqBeforeRangeInvalid_Elr,
          Q401cFreqAfterEmptyHard_Elr,
          Q401cFreqAfterTxtEmptyHard_Elr,
          Q401cFreqAfterRangeInvalid_Elr,
          Q401cDateChangeRdHard_Elr,
          Q401cDateChangeDtHard_Elr,
          Q401cDateChangeInvalid_Elr,
          Q401cDateChangeOutOfBound_Elr,
          Q401cFrequencyIncreaseConflict_Elr,
          Q401cFrequencyDecreaseConflict_Elr,
          Q401cWarningUnk_Elr,
        };
      });

      setQ401cElrDetails(updatedQ401cElrList);

      // Chronological chain warning: after[i] should match before[i+1] once rows
      // are sorted by date; when a date isn't available for either row being
      // compared, fall back to the order rows were entered in (1st, 2nd, 3rd...
      // as shown in the UI), since Array.prototype.sort is stable.
      const chronRowsElr = updatedQ401cElrList
        .filter(
          (r) => r.Q401cChangeType_Elr === 2 || r.Q401cChangeType_Elr === 3,
        )
        .sort((a, b) => {
          const aValid = IsValidDateValue(a.Q401cDateChange_Elr);
          const bValid = IsValidDateValue(b.Q401cDateChange_Elr);
          return aValid && bValid
            ? new Date(a.Q401cDateChange_Elr) - new Date(b.Q401cDateChange_Elr)
            : 0;
        });

      let chainMismatchElr = false;
      for (let i = 0; i < chronRowsElr.length - 1; i++) {
        const current = chronRowsElr[i];
        const next = chronRowsElr[i + 1];
        // Compare the entered values directly, regardless of which unit
        // (times per week / every X weeks / other) was selected for each.
        const currentAfterVal =
          current.Q401cFreqAfterRd_Elr === 1
            ? current.Q401cFreqAfterTimesTxt_Elr
            : current.Q401cFreqAfterRd_Elr === 2
              ? current.Q401cFreqAfterWeeksTxt_Elr
              : current.Q401cFreqAfterOtherTxt_Elr;
        const nextBeforeVal =
          next.Q401cFreqBeforeRd_Elr === 1
            ? next.Q401cFreqBeforeTimesTxt_Elr
            : next.Q401cFreqBeforeRd_Elr === 2
              ? next.Q401cFreqBeforeWeeksTxt_Elr
              : next.Q401cFreqBeforeOtherTxt_Elr;

        if (
          isStringEmpty(currentAfterVal) ||
          isStringEmpty(nextBeforeVal) ||
          String(currentAfterVal) !== String(nextBeforeVal)
        ) {
          chainMismatchElr = true;
          break;
        }
      }
      setQ401cElr_WarningHard(chainMismatchElr);
    }

    //Q40.1.C Validate - Linvoseltamab
    if (
      isNewEcrfC &&
      showQ40Linvo &&
      Q37LinvoDetails[0]?.ivigInitRd_linvoC != null &&
      Q37LinvoDetails[0]?.ivigInitRd_linvoC !== 1
    ) {
      const ivigInitDateLin =
        Q37LinvoDetails[0]?.ivigInitRd_linvoC === 2
          ? Q37LinvoDetails[0]?.ivigInitDt_linvoC
          : null;

      const updatedQ401cLinvoList = Q401cLinvoDetails.map((row) => {
        let Q401cChangeTypeEmptyHard_Lin = false;
        let Q401cFreqBeforeEmptyHard_Lin = false;
        let Q401cFreqBeforeTxtEmptyHard_Lin = false;
        let Q401cFreqBeforeRangeInvalid_Lin = false;
        let Q401cFreqAfterEmptyHard_Lin = false;
        let Q401cFreqAfterTxtEmptyHard_Lin = false;
        let Q401cFreqAfterRangeInvalid_Lin = false;
        let Q401cDateChangeRdHard_Lin = false;
        let Q401cDateChangeDtHard_Lin = false;
        let Q401cDateChangeInvalid_Lin = false;
        let Q401cDateChangeOutOfBound_Lin = false;
        let Q401cFrequencyIncreaseConflict_Lin = false;
        let Q401cFrequencyDecreaseConflict_Lin = false;
        let Q401cWarningUnk_Lin = false;

        if (row.Q401cChangeType_Lin === null) {
          Q401cChangeTypeEmptyHard_Lin = true;
        }

        const isRealChange =
          row.Q401cChangeType_Lin === 2 || row.Q401cChangeType_Lin === 3;

        if (isRealChange) {
          /* ---------------- Column C: before ---------------- */
          if (
            row.Q401cFreqBeforeRd_Lin === null &&
            isStringEmpty(row.Q401cFreqBeforeTimesTxt_Lin) &&
            isStringEmpty(row.Q401cFreqBeforeWeeksTxt_Lin) &&
            isStringEmpty(row.Q401cFreqBeforeOtherTxt_Lin)
          ) {
            Q401cFreqBeforeEmptyHard_Lin = true;
          } else if (row.Q401cFreqBeforeRd_Lin === 3) {
            if (isStringEmpty(row.Q401cFreqBeforeOtherTxt_Lin)) {
              Q401cFreqBeforeTxtEmptyHard_Lin = true;
            }
          } else if (row.Q401cFreqBeforeRd_Lin === 99) {
            Q401cWarningUnk_Lin = true;
          } else if (row.Q401cFreqBeforeRd_Lin !== null) {
            const freqTxt =
              row.Q401cFreqBeforeRd_Lin === 1
                ? row.Q401cFreqBeforeTimesTxt_Lin
                : row.Q401cFreqBeforeWeeksTxt_Lin;
            if (isStringEmpty(freqTxt)) {
              Q401cFreqBeforeTxtEmptyHard_Lin = true;
            } else {
              const freqNum = parseInt(freqTxt, 10);
              if (
                isNaN(freqNum) ||
                freqNum <= 0 ||
                freqNum > 10 ||
                !Number.isInteger(Number(freqTxt))
              ) {
                Q401cFreqBeforeRangeInvalid_Lin = true;
              }
            }
          }

          /* ---------------- Column D: after ---------------- */
          if (
            row.Q401cFreqAfterRd_Lin === null &&
            isStringEmpty(row.Q401cFreqAfterTimesTxt_Lin) &&
            isStringEmpty(row.Q401cFreqAfterWeeksTxt_Lin) &&
            isStringEmpty(row.Q401cFreqAfterOtherTxt_Lin)
          ) {
            Q401cFreqAfterEmptyHard_Lin = true;
          } else if (row.Q401cFreqAfterRd_Lin === 3) {
            if (isStringEmpty(row.Q401cFreqAfterOtherTxt_Lin)) {
              Q401cFreqAfterTxtEmptyHard_Lin = true;
            }
          } else if (row.Q401cFreqAfterRd_Lin === 99) {
            Q401cWarningUnk_Lin = true;
          } else if (row.Q401cFreqAfterRd_Lin !== null) {
            const freqTxt =
              row.Q401cFreqAfterRd_Lin === 1
                ? row.Q401cFreqAfterTimesTxt_Lin
                : row.Q401cFreqAfterWeeksTxt_Lin;
            if (isStringEmpty(freqTxt)) {
              Q401cFreqAfterTxtEmptyHard_Lin = true;
            } else {
              const freqNum = parseInt(freqTxt, 10);
              if (
                isNaN(freqNum) ||
                freqNum <= 0 ||
                freqNum > 10 ||
                !Number.isInteger(Number(freqTxt))
              ) {
                Q401cFreqAfterRangeInvalid_Lin = true;
              }
            }
          }

          /* ---------------- Column E: date of change ---------------- */
          if (row.Q401cDateChangeRd_Lin === null) {
            Q401cDateChangeRdHard_Lin = true;
          }

          if (!Q401cDateChangeRdHard_Lin && row.Q401cDateChangeRd_Lin === 1) {
            if (isStringEmpty(row.Q401cDateChange_Lin)) {
              Q401cDateChangeDtHard_Lin = true;
            } else if (!IsDateValid(row.Q401cDateChange_Lin)) {
              Q401cDateChangeInvalid_Lin = true;
            } else if (
              !ValidateQ401CDateChain(
                row.Q401cDateChange_Lin,
                RRMMCohortTreatment.Linvoseltamab,
                ivigInitDateLin,
              )
            ) {
              Q401cDateChangeOutOfBound_Lin = true;
            }
          }

          /* ---------------- Increase/decrease conflict (normalizes both sides to a doses-per-week rate before comparing, since "once every N weeks" and "X times per week" are on inverse scales) ---------------- */
          const beforeNumLin =
            row.Q401cFreqBeforeRd_Lin === 1
              ? row.Q401cFreqBeforeTimesTxt_Lin
              : row.Q401cFreqBeforeRd_Lin === 2
                ? row.Q401cFreqBeforeWeeksTxt_Lin
                : null;
          const afterNumLin =
            row.Q401cFreqAfterRd_Lin === 1
              ? row.Q401cFreqAfterTimesTxt_Lin
              : row.Q401cFreqAfterRd_Lin === 2
                ? row.Q401cFreqAfterWeeksTxt_Lin
                : null;

          if (!isStringEmpty(beforeNumLin) && !isStringEmpty(afterNumLin)) {
            const beforeRateLin =
              row.Q401cFreqBeforeRd_Lin === 2
                ? 1 / Number(beforeNumLin)
                : Number(beforeNumLin);
            const afterRateLin =
              row.Q401cFreqAfterRd_Lin === 2
                ? 1 / Number(afterNumLin)
                : Number(afterNumLin);

            if (
              row.Q401cChangeType_Lin === 2 &&
              afterRateLin <= beforeRateLin
            ) {
              Q401cFrequencyIncreaseConflict_Lin = true;
            } else if (
              row.Q401cChangeType_Lin === 3 &&
              afterRateLin >= beforeRateLin
            ) {
              Q401cFrequencyDecreaseConflict_Lin = true;
            }
          }
        }

        if (
          Q401cChangeTypeEmptyHard_Lin ||
          Q401cFreqBeforeEmptyHard_Lin ||
          Q401cFreqBeforeTxtEmptyHard_Lin ||
          Q401cFreqBeforeRangeInvalid_Lin ||
          Q401cFreqAfterEmptyHard_Lin ||
          Q401cFreqAfterTxtEmptyHard_Lin ||
          Q401cFreqAfterRangeInvalid_Lin ||
          Q401cDateChangeRdHard_Lin ||
          Q401cDateChangeDtHard_Lin ||
          Q401cDateChangeInvalid_Lin ||
          Q401cDateChangeOutOfBound_Lin ||
          Q401cFrequencyIncreaseConflict_Lin ||
          Q401cFrequencyDecreaseConflict_Lin
        ) {
          isValid = false;
        }

        return {
          ...row,
          Q401cChangeTypeEmptyHard_Lin,
          Q401cFreqBeforeEmptyHard_Lin,
          Q401cFreqBeforeTxtEmptyHard_Lin,
          Q401cFreqBeforeRangeInvalid_Lin,
          Q401cFreqAfterEmptyHard_Lin,
          Q401cFreqAfterTxtEmptyHard_Lin,
          Q401cFreqAfterRangeInvalid_Lin,
          Q401cDateChangeRdHard_Lin,
          Q401cDateChangeDtHard_Lin,
          Q401cDateChangeInvalid_Lin,
          Q401cDateChangeOutOfBound_Lin,
          Q401cFrequencyIncreaseConflict_Lin,
          Q401cFrequencyDecreaseConflict_Lin,
          Q401cWarningUnk_Lin,
        };
      });

      setQ401cLinvoDetails(updatedQ401cLinvoList);

      // Sort by date when available for both rows; otherwise (stable sort)
      // fall back to entry order (1st, 2nd, 3rd... as shown in the UI).
      const chronRowsLin = updatedQ401cLinvoList
        .filter(
          (r) => r.Q401cChangeType_Lin === 2 || r.Q401cChangeType_Lin === 3,
        )
        .sort((a, b) => {
          const aValid = IsValidDateValue(a.Q401cDateChange_Lin);
          const bValid = IsValidDateValue(b.Q401cDateChange_Lin);
          return aValid && bValid
            ? new Date(a.Q401cDateChange_Lin) - new Date(b.Q401cDateChange_Lin)
            : 0;
        });

      let chainMismatchLin = false;
      for (let i = 0; i < chronRowsLin.length - 1; i++) {
        const current = chronRowsLin[i];
        const next = chronRowsLin[i + 1];
        // Compare the entered values directly, regardless of which unit
        // (times per week / every X weeks / other) was selected for each.
        const currentAfterVal =
          current.Q401cFreqAfterRd_Lin === 1
            ? current.Q401cFreqAfterTimesTxt_Lin
            : current.Q401cFreqAfterRd_Lin === 2
              ? current.Q401cFreqAfterWeeksTxt_Lin
              : current.Q401cFreqAfterOtherTxt_Lin;
        const nextBeforeVal =
          next.Q401cFreqBeforeRd_Lin === 1
            ? next.Q401cFreqBeforeTimesTxt_Lin
            : next.Q401cFreqBeforeRd_Lin === 2
              ? next.Q401cFreqBeforeWeeksTxt_Lin
              : next.Q401cFreqBeforeOtherTxt_Lin;

        if (
          isStringEmpty(currentAfterVal) ||
          isStringEmpty(nextBeforeVal) ||
          String(currentAfterVal) !== String(nextBeforeVal)
        ) {
          chainMismatchLin = true;
          break;
        }
      }
      setQ401cLinvo_WarningHard(chainMismatchLin);
    }

    //Q40.2.C Validate
    const validateQ402cList = (details, cohortType, suffix) => {
      const updatedList = details.map((row, idx) => {
        const updatedRow = { ...row };
        const assessment = row[`Q402cAssessment_${suffix}`];
        const dt = row[`Q402cAssessment_${suffix}_dt`];

        updatedRow[`Q402cAssessment_${suffix}_RdHard`] = false;
        updatedRow[`Q402cAssessment_${suffix}_dtHard`] = false;
        updatedRow[`Q402cAssessment_${suffix}_dtInvalid`] = false;
        updatedRow[`Q402cAssessment_${suffix}_dtOutOfRange`] = false;
        updatedRow.Q402cRow1NotEarliestHard = false;
        updatedRow[`Q402cLevelBucket_${suffix}_Empty`] = false;
        updatedRow[`Q402cLevelValue_${suffix}_Empty`] = false;
        updatedRow[`Q402cLevelValue_${suffix}_RangeInvalid`] = false;

        /* ---------------- Column [C] - Date of assessment ---------------- */
        if (assessment === null && isStringEmpty(dt)) {
          updatedRow[`Q402cAssessment_${suffix}_RdHard`] = true;
          isValid = false;
        }

        if (assessment === 2) {
          if (isStringEmpty(dt)) {
            updatedRow[`Q402cAssessment_${suffix}_dtHard`] = true;
            isValid = false;
          } else if (dt === "Invalid Date") {
            updatedRow[`Q402cAssessment_${suffix}_dtInvalid`] = true;
            isValid = false;
          } else if (!DateInRange(dt, dayjs("01-01-1920"), dayjs())) {
            updatedRow[`Q402cAssessment_${suffix}_dtInvalid`] = true;
            isValid = false;
          } else {
            let lower;
            let upper;
            if (row.rowType === 1) {
              lower = getQ402CIndexMinus365("validate", cohortType);
              upper = getQ402CPeriodEndDate("validate", cohortType);
            } else if (row.rowType === 2) {
              lower = IsValidDateValue(getQ402CIndexDate(cohortType))
                ? new Date(getQ402CIndexDate(cohortType))
                : null;
              upper = getQ402CPeriodEndDate("validate", cohortType);
            } else {
              // Row 1 & Row 2 are always index 0/1; periodic rows start at
              // index 2, so this row's own N = index - 1.
              const rowNumber = idx - 1;
              lower = getQ402CTargetMinus45("validate", cohortType, rowNumber);
              upper = getQ402CTargetPlus45Capped(
                "validate",
                cohortType,
                rowNumber,
              );
            }

            if (!ValidateQ402CDate(lower, dt, upper)) {
              updatedRow[`Q402cAssessment_${suffix}_dtOutOfRange`] = true;
              isValid = false;
            }
          }
        }

        /* ---------------- Column [D] - IgG level ---------------- */
        if (assessment !== 1) {
          if (row.rowType === 2) {
            const level = row[`Q402cLevelValue_${suffix}`];
            const mgVal = row[`Q402cLevelValue_${suffix}_MgValue`];
            const gVal = row[`Q402cLevelValue_${suffix}_GValue`];

            if (level === null && isStringEmpty(mgVal) && isStringEmpty(gVal)) {
              updatedRow[`Q402cLevelValue_${suffix}_Empty`] = true;
              isValid = false;
            }

            if (
              (level === 1 && isStringEmpty(mgVal)) ||
              (level === 2 && isStringEmpty(gVal)) ||
              (!isStringEmpty(mgVal) &&
                (parseFloat(mgVal) <= 0 || parseFloat(mgVal) >= 5000)) ||
              (!isStringEmpty(gVal) &&
                (parseFloat(gVal) <= 0 || parseFloat(gVal) >= 50))
            ) {
              updatedRow[`Q402cLevelValue_${suffix}_RangeInvalid`] = true;
              isValid = false;
            }
          } else {
            if (row[`Q402cLevelBucket_${suffix}`] === null) {
              updatedRow[`Q402cLevelBucket_${suffix}_Empty`] = true;
              isValid = false;
            }
          }
        }

        return updatedRow;
      });

      // Row 1 (baseline) date must be on or before every other reported
      // assessment date.
      const baselineDate = updatedList[0]?.[`Q402cAssessment_${suffix}_dt`];
      if (updatedList[0]?.[`Q402cAssessment_${suffix}`] === 2 && baselineDate) {
        const otherDates = updatedList
          .slice(1)
          .map((r) => r[`Q402cAssessment_${suffix}_dt`])
          .filter((d) => IsValidDateValue(d));
        const earliestOther = otherDates.reduce(
          (min, d) => (!min || IsLessOrEqual(d, min) ? d : min),
          null,
        );
        if (earliestOther && !IsLessOrEqual(baselineDate, earliestOther)) {
          updatedList[0][`Q402cRow1NotEarliestHard`] = true;
          isValid = false;
        }
      }

      // Chronological order + bucket/threshold cross-checks (soft warnings).
      let datesOutOfOrder = false;
      const chronoDates = updatedList
        .map((r) => r[`Q402cAssessment_${suffix}_dt`])
        .filter((d) => IsValidDateValue(d));
      for (let i = 0; i < chronoDates.length - 1; i++) {
        if (!IsLessOrEqual(chronoDates[i], chronoDates[i + 1])) {
          datesOutOfOrder = true;
          break;
        }
      }

      const has400to500 = updatedList.some(
        (r) => r[`Q402cLevelBucket_${suffix}`] === 2,
      );
      const hasOver500 = updatedList.some(
        (r) => r[`Q402cLevelBucket_${suffix}`] === 3,
      );
      const highestRow = updatedList[1];
      const highestValueMg =
        highestRow?.[`Q402cLevelValue_${suffix}`] === 1
          ? parseFloat(highestRow[`Q402cLevelValue_${suffix}_MgValue`])
          : null;
      const thresholdConflict =
        (has400to500 &&
          (highestValueMg === null ||
            isNaN(highestValueMg) ||
            highestValueMg < 400)) ||
        (hasOver500 &&
          (highestValueMg === null ||
            isNaN(highestValueMg) ||
            highestValueMg <= 500));

      return { updatedList, datesOutOfOrder, thresholdConflict };
    };

    if (isNewEcrfC && isElrexvayliSelected) {
      const { updatedList, datesOutOfOrder, thresholdConflict } =
        validateQ402cList(Q402cElrDetails, RRMMCohortTreatment.ELREXFIO, "Elr");
      setQ402cElrDetails(updatedList);
      setQ402cElrDatesWarning(datesOutOfOrder);
      setQ402cElrThresholdWarning(thresholdConflict);
    }

    if (isNewEcrfC && isLinvoveySelected) {
      const { updatedList, datesOutOfOrder, thresholdConflict } =
        validateQ402cList(
          Q402cLinvoDetails,
          RRMMCohortTreatment.Linvoseltamab,
          "Lin",
        );
      setQ402cLinvoDetails(updatedList);
      setQ402cLinvoDatesWarning(datesOutOfOrder);
      setQ402cLinvoThresholdWarning(thresholdConflict);
    }

    //Q40.2.B Validate
    if (!isNewEcrfC && isNewEcrfB && showQ40Elr) {
      const updatedQ402aElrList = Q402aElrDetails.map((row) => {
        const updatedRow = { ...row };

        // Reset all errors
        updatedRow.Q402aIgg_assessment_Elr_RdHard = false;
        updatedRow.Q402aIgg_assessment_Elr_dtHard = false;
        updatedRow.Q402aIgg_assessment_Elr_dtInvalid = false;
        updatedRow.Q402aIgg_assessment_Elr_dt_before_eof = false;
        updatedRow.Q402aIgg_level_Elr_Empty = false;
        updatedRow.Q402aIgg_level_Elr_RangeInvalid = false;

        /* ---------------- Column B ---------------- */
        if (
          row.Q402aIgg_assessment_Elr === null &&
          isStringEmpty(row.Q402aIgg_assessment_Elr_dt)
        ) {
          updatedRow.Q402aIgg_assessment_Elr_RdHard = true;
          isValid = false;
        }

        if (row.Q402aIgg_assessment_Elr === 2) {
          if (isStringEmpty(row.Q402aIgg_assessment_Elr_dt)) {
            updatedRow.Q402aIgg_assessment_Elr_dtHard = true;
            isValid = false;
          } else if (row.Q402aIgg_assessment_Elr_dt === "Invalid Date") {
            updatedRow.Q402aIgg_assessment_Elr_dtInvalid = true;
            isValid = false;
          } else if (
            !DateInRange(
              row.Q402aIgg_assessment_Elr_dt,
              dayjs("01-01-1920"),
              dayjs(),
            )
          ) {
            updatedRow.Q402aIgg_assessment_Elr_dtInvalid = true;
            isValid = false;
          } else {
            let indexMinus91 = showElrDate91("validate");
            let indexPlus7 = getElrLinoQ402BUpperLimitDate(
              "validate",
              RRMMCohortTreatment.ELREXFIO,
            );
            let dateValidationResult = ValidateQ402BDate(
              indexMinus91,
              row.Q402aIgg_assessment_Elr_dt,
              indexPlus7,
              elrNextLotStartDate,
              endOfFollowUpDate,
            );
            if (!dateValidationResult) {
              updatedRow.Q402aIgg_assessment_Elr_dt_before_eof = true;
              isValid = false;
            }
          }
        }

        /* ---------------- Column C ---------------- */
        if (
          row.Q402aIgg_assessment_Elr != 1 &&
          row.Q402aIgg_level_Elr === null &&
          isStringEmpty(row.Q402aIgg_level_Elr_MgValue) &&
          isStringEmpty(row.Q402aIgg_level_Elr_GValue)
        ) {
          updatedRow.Q402aIgg_level_Elr_Empty = true;
          isValid = false;
        }

        if (
          (row.Q402aIgg_assessment_Elr != 1 &&
            row.Q402aIgg_level_Elr === 1 &&
            isStringEmpty(row.Q402aIgg_level_Elr_MgValue)) ||
          (row.Q402aIgg_level_Elr === 2 &&
            isStringEmpty(row.Q402aIgg_level_Elr_GValue)) ||
          (!isStringEmpty(row.Q402aIgg_level_Elr_MgValue) &&
            (parseInt(row.Q402aIgg_level_Elr_MgValue) < 1 ||
              parseInt(row.Q402aIgg_level_Elr_MgValue) > 5000)) ||
          (!isStringEmpty(row.Q402aIgg_level_Elr_GValue) &&
            (parseInt(row.Q402aIgg_level_Elr_GValue) < 1 ||
              parseInt(row.Q402aIgg_level_Elr_GValue) > 50))
        ) {
          updatedRow.Q402aIgg_level_Elr_RangeInvalid = true;
          isValid = false;
        }

        return updatedRow;
      });

      setQ402aElrDetails(updatedQ402aElrList);
    }

    //Q40.2.B Validate
    if (!isNewEcrfC && isNewEcrfB && showQ40Linvo) {
      const updatedQ402aLinvoList = Q402aLinvoDetails.map((row) => {
        const updatedRow = { ...row };

        // Reset all errors
        updatedRow.Q402aIgg_assessment_Lin_RdHard = false;
        updatedRow.Q402aIgg_assessment_Lin_dtHard = false;
        updatedRow.Q402aIgg_assessment_Lin_dtInvalid = false;
        updatedRow.Q402aIgg_assessment_Lin_dt_before_eof = false;
        updatedRow.Q402aIgg_level_Lin_Empty = false;
        updatedRow.Q402aIgg_level_Lin_RangeInvalid = false;

        /* ---------------- Column B ---------------- */
        if (
          row.Q402aIgg_assessment_Lin === null &&
          isStringEmpty(row.Q402aIgg_assessment_Lin_dt)
        ) {
          updatedRow.Q402aIgg_assessment_Lin_RdHard = true;
          isValid = false;
        }

        if (row.Q402aIgg_assessment_Lin === 2) {
          if (isStringEmpty(row.Q402aIgg_assessment_Lin_dt)) {
            updatedRow.Q402aIgg_assessment_Lin_dtHard = true;
            isValid = false;
          } else if (row.Q402aIgg_assessment_Lin_dt === "Invalid Date") {
            updatedRow.Q402aIgg_assessment_Lin_dtInvalid = true;
            isValid = false;
          } else if (
            !DateInRange(
              row.Q402aIgg_assessment_Lin_dt,
              dayjs("01-01-1920"),
              dayjs(),
            )
          ) {
            updatedRow.Q402aIgg_assessment_Lin_dtInvalid = true;
            isValid = false;
          } else {
            let indexMinus91 = showLinDate91("validate");
            let indexPlus7 = getElrLinoQ402BUpperLimitDate(
              "validate",
              RRMMCohortTreatment.Linvoseltamab,
            );
            let dateValidationResult = ValidateQ402BDate(
              indexMinus91,
              row.Q402aIgg_assessment_Lin_dt,
              indexPlus7,
              linvoNextLotStartDate,
              endOfFollowUpDate,
            );
            if (!dateValidationResult) {
              updatedRow.Q402aIgg_assessment_Lin_dt_before_eof = true;
              isValid = false;
            }
          }
        }

        /* ---------------- Column C ---------------- */
        if (
          row.Q402aIgg_assessment_Lin != 1 &&
          row.Q402aIgg_level_Lin === null &&
          isStringEmpty(row.Q402aIgg_level_Lin_MgValue) &&
          isStringEmpty(row.Q402aIgg_level_Lin_GValue)
        ) {
          updatedRow.Q402aIgg_level_Lin_Empty = true;
          isValid = false;
        }

        if (
          (row.Q402aIgg_assessment_Lin != 1 &&
            row.Q402aIgg_level_Lin === 1 &&
            isStringEmpty(row.Q402aIgg_level_Lin_MgValue)) ||
          (row.Q402aIgg_level_Lin === 2 &&
            isStringEmpty(row.Q402aIgg_level_Lin_GValue)) ||
          (!isStringEmpty(row.Q402aIgg_level_Lin_MgValue) &&
            (parseInt(row.Q402aIgg_level_Lin_MgValue) < 1 ||
              parseInt(row.Q402aIgg_level_Lin_MgValue) > 5000)) ||
          (!isStringEmpty(row.Q402aIgg_level_Lin_GValue) &&
            (parseInt(row.Q402aIgg_level_Lin_GValue) < 1 ||
              parseInt(row.Q402aIgg_level_Lin_GValue) > 50))
        ) {
          updatedRow.Q402aIgg_level_Lin_RangeInvalid = true;
          isValid = false;
        }

        return updatedRow; // ✅ CRITICAL FIX
      });

      setQ402aLinvoDetails(updatedQ402aLinvoList);
    }

    //Q40.3.B Validate
    if (
      !isNewEcrfC &&
      isNewEcrfB &&
      showQ40Elr &&
      Q37ElrexDetails[0]?.Q40dosingfreqRd != null &&
      Q37ElrexDetails[0]?.Q40dosingfreqRd != 1
    ) {
      const updatedQ403aElrList = Q403aElrDetails.map((row) => {
        const updatedRow = { ...row };

        // Reset all errors
        updatedRow.Q403aIgg_assessment_Elr_RdHard = false;
        updatedRow.Q403aIgg_assessment_Elr_dtHard = false;
        updatedRow.Q403aIgg_assessment_Elr_dtInvalid = false;
        updatedRow.Q403aIgg_assessment_Elr_dt_before_eof = false;
        updatedRow.Q403aIgg_level_Elr_Empty = false;
        updatedRow.Q403aIgg_level_Elr_RangeInvalid = false;

        /* ---------------- Column B ---------------- */
        if (
          row.Q403aIgg_assessment_Elr === null &&
          isStringEmpty(row.Q403aIgg_assessment_Elr_dt)
        ) {
          updatedRow.Q403aIgg_assessment_Elr_RdHard = true;
          isValid = false;
        }

        if (row.Q403aIgg_assessment_Elr === 2) {
          if (isStringEmpty(row.Q403aIgg_assessment_Elr_dt)) {
            updatedRow.Q403aIgg_assessment_Elr_dtHard = true;
            isValid = false;
          } else if (row.Q403aIgg_assessment_Elr_dt === "Invalid Date") {
            updatedRow.Q403aIgg_assessment_Elr_dtInvalid = true;
            isValid = false;
          } else if (
            !DateInRange(
              row.Q403aIgg_assessment_Elr_dt,
              dayjs("01-01-1920"),
              dayjs(),
            )
          ) {
            updatedRow.Q403aIgg_assessment_Elr_dtInvalid = true;
            isValid = false;
          } else {
            let indexMinus91 = showElrDate91("validate");
            let Q40Minus91 = showElrQ40Date91("validate");
            let Q40Date = showElrQ40Date("validate");
            let dateValidationResult = ValidateQ403BDate(
              indexMinus91,
              Q40Minus91,
              row.Q403aIgg_assessment_Elr_dt,
              Q40Date,
              elrNextLotStartDate,
              endOfFollowUpDate,
            );

            if (!dateValidationResult) {
              updatedRow.Q403aIgg_assessment_Elr_dt_before_eof = true;
              isValid = false;
            }
          }
        }

        /* ---------------- Column C ---------------- */
        if (
          row.Q403aIgg_assessment_Elr != 1 &&
          row.Q403aIgg_level_Elr === null &&
          isStringEmpty(row.Q403aIgg_level_Elr_MgValue) &&
          isStringEmpty(row.Q403aIgg_level_Elr_GValue)
        ) {
          updatedRow.Q403aIgg_level_Elr_Empty = true;
          isValid = false;
        }

        if (
          (row.Q403aIgg_assessment_Elr != 1 &&
            row.Q403aIgg_level_Elr === 1 &&
            isStringEmpty(row.Q403aIgg_level_Elr_MgValue)) ||
          (row.Q403aIgg_level_Elr === 2 &&
            isStringEmpty(row.Q403aIgg_level_Elr_GValue)) ||
          (!isStringEmpty(row.Q403aIgg_level_Elr_MgValue) &&
            (parseInt(row.Q403aIgg_level_Elr_MgValue) < 1 ||
              parseInt(row.Q403aIgg_level_Elr_MgValue) > 5000)) ||
          (!isStringEmpty(row.Q403aIgg_level_Elr_GValue) &&
            (parseInt(row.Q403aIgg_level_Elr_GValue) < 1 ||
              parseInt(row.Q403aIgg_level_Elr_GValue) > 50))
        ) {
          updatedRow.Q403aIgg_level_Elr_RangeInvalid = true;
          isValid = false;
        }

        return updatedRow;
      });

      setQ403aElrDetails(updatedQ403aElrList);
    }

    //Q40.3.B Validate
    if (
      !isNewEcrfC &&
      isNewEcrfB &&
      showQ40Linvo &&
      Q37LinvoDetails[0]?.q40DosingFreqRd_linvo != null &&
      Q37LinvoDetails[0]?.q40DosingFreqRd_linvo != 1
    ) {
      const updatedQ403aTalList = Q403aLinvoDetails.map((row) => {
        const updatedRow = { ...row };

        // Reset all errors
        updatedRow.Q403aIgg_assessment_Lin_RdHard = false;
        updatedRow.Q403aIgg_assessment_Lin_dtHard = false;
        updatedRow.Q403aIgg_assessment_Lin_dtInvalid = false;
        updatedRow.Q403aIgg_assessment_Lin_dt_before_eof = false;
        updatedRow.Q403aIgg_level_Lin_Empty = false;
        updatedRow.Q403aIgg_level_Lin_RangeInvalid = false;

        /* ---------------- Column B ---------------- */
        if (
          row.Q403aIgg_assessment_Lin === null &&
          isStringEmpty(row.Q403aIgg_assessment_Lin_dt)
        ) {
          updatedRow.Q403aIgg_assessment_Lin_RdHard = true;
          isValid = false;
        }

        if (row.Q403aIgg_assessment_Lin === 2) {
          if (isStringEmpty(row.Q403aIgg_assessment_Lin_dt)) {
            updatedRow.Q403aIgg_assessment_Lin_dtHard = true;
            isValid = false;
          } else if (row.Q403aIgg_assessment_Lin_dt === "Invalid Date") {
            updatedRow.Q403aIgg_assessment_Lin_dtInvalid = true;
            isValid = false;
          } else if (
            !DateInRange(
              row.Q403aIgg_assessment_Lin_dt,
              dayjs("01-01-1920"),
              dayjs(),
            )
          ) {
            updatedRow.Q403aIgg_assessment_Lin_dtInvalid = true;
            isValid = false;
          } else {
            let indexMinus91 = showLinDate91("validate");
            let Q40Minus91 = showLinQ40Date91("validate");
            let Q40Date = showLinQ40Date("validate");
            let dateValidationResult = ValidateQ403BDate(
              indexMinus91,
              Q40Minus91,
              row.Q403aIgg_assessment_Lin_dt,
              Q40Date,
              linvoNextLotStartDate,
              endOfFollowUpDate,
            );
            if (!dateValidationResult) {
              updatedRow.Q403aIgg_assessment_Lin_dt_before_eof = true;
              isValid = false;
            }
          }
        }

        /* ---------------- Column C ---------------- */
        if (
          row.Q403aIgg_assessment_Lin != 1 &&
          row.Q403aIgg_level_Lin === null &&
          isStringEmpty(row.Q403aIgg_level_Lin_MgValue) &&
          isStringEmpty(row.Q403aIgg_level_Lin_GValue)
        ) {
          updatedRow.Q403aIgg_level_Lin_Empty = true;
          isValid = false;
        }

        if (
          (row.Q403aIgg_assessment_Lin != 1 &&
            row.Q403aIgg_level_Lin === 1 &&
            isStringEmpty(row.Q403aIgg_level_Lin_MgValue)) ||
          (row.Q403aIgg_level_Lin === 2 &&
            isStringEmpty(row.Q403aIgg_level_Lin_GValue)) ||
          (!isStringEmpty(row.Q403aIgg_level_Lin_MgValue) &&
            (parseInt(row.Q403aIgg_level_Lin_MgValue) < 1 ||
              parseInt(row.Q403aIgg_level_Lin_MgValue) > 5000)) ||
          (!isStringEmpty(row.Q403aIgg_level_Lin_GValue) &&
            (parseInt(row.Q403aIgg_level_Lin_GValue) < 1 ||
              parseInt(row.Q403aIgg_level_Lin_GValue) > 50))
        ) {
          updatedRow.Q403aIgg_level_Lin_RangeInvalid = true;
          isValid = false;
        }

        return updatedRow; // ✅ CRITICAL FIX
      });

      setQ403aLinvoDetails(updatedQ403aTalList);
    }

    // eslint-disable-next-line no-console

    return isValid;
  };

  const ValidateForDateCondition = (Q36ListData) => {
    let isValid = true;
    let cohortId = Q36ListData[0].cohorttreatmentid;

    const bispecificQ26DataForQ36 = bispecificQ26Data;
    const bispecificQ331ADataForQ36 = bispecificQ331AData;
    const bispecificQ31Q32DataForQ36 = (bispecificQ31Q32Data ?? []).filter(
      (x) => x?.cohortTreatmentId === cohortId,
    );

    //Date 1: Fetch earliest Date from Q26 & Q26.1
    const earliestQ26Dt = bispecificQ26DataForQ36
      .filter(
        (row) =>
          row.firstAdministrationDt !== null &&
          row.firstAdministrationDt !== "",
      )
      .reduce((earliest, row) => {
        const currentDate = new Date(row.firstAdministrationDt);

        if (!earliest || currentDate < earliest) {
          return currentDate;
        }
        return earliest;
      }, null);

    const formattedEarliestQ26Dt = earliestQ26Dt
      ? earliestQ26Dt.toLocaleDateString("en-US")
      : null;

    //Date 2: Fetch Latest Date of Dose Administration
    const latestQ36Dt = Q36ListData.filter(
      (row) =>
        row.dose_administration_Elrh_dt !== null &&
        row.dose_administration_Elrh_dt !== "",
    ).reduce((latest, row) => {
      const currentDate = new Date(row.dose_administration_Elrh_dt);
      return !latest || currentDate > new Date(latest)
        ? currentDate.toLocaleDateString("en-US")
        : latest;
    }, null);

    const hasValue = (v) =>
      v !== null && v !== undefined && v.toString().trim() !== "";
    const safeFind = (data, predicate) =>
      (data ?? []).find((x) => x && predicate(x)) ?? null;

    //Check which lot to consider while validating based on Q26 data
    if (bispecificQ26DataForQ36.length === 1) {
      const lot = Number(bispecificQ26DataForQ36[0].lotNumber);
      let nextLotNumber = lot + 1;

      if (lot === 1) {
        const firstAdminDtLot1 =
          (bispecificQ331ADataForQ36 ?? []).find(
            (x) => x && Number(x.lotNumber) === lot,
          )?.firstAdministrationDt ?? null;

        // Latest Date of Dose Administration <= 1L of Q33.1.A <=  end of followup
        isValid = CheckDateValidationQ36(
          latestQ36Dt,
          null,
          firstAdminDtLot1,
          endOfFollowUpDate,
        );
      } else if (lot === 2) {
        //Date 3: Fetch earliest Date from Q26 & Q26.1

        //Q28 2L Discontinuation Date
        const ndmmDiscontinuationLot =
          safeFind(ndmmLotData, (x) => Number(x.lotNumber) === lot)?.endLotDt ??
          null;

        //Q33.1.A 2L Discontinuation Date
        const q331ALastAdminDtLot =
          safeFind(
            bispecificQ331ADataForQ36,
            (x) => Number(x.lotNumber) === lot,
          )?.lastAdministrationDt ?? null;

        //Q31 3L Start Date
        const q31FirstAdminDtLot =
          safeFind(
            bispecificQ31Q32DataForQ36,
            (x) => Number(x.lotNumber) === nextLotNumber,
          )?.firstAdministrationDt ?? null;

        const dateToCompare =
          ndmmDiscontinuationLot != null
            ? ndmmDiscontinuationLot
            : q331ALastAdminDtLot != null
              ? q331ALastAdminDtLot
              : q31FirstAdminDtLot != null
                ? q31FirstAdminDtLot
                : null;

        //Earliest date of 12,12.1 or 26, 26.1 <= Latest Date of Dose Administration <= 2L Q28 discontinuation date or 2L Q33.1.A discontinuation date or 3L start date in Q31 <= end of follow up
        isValid = CheckDateValidationQ36(
          formattedEarliestQ26Dt,
          latestQ36Dt,
          dateToCompare,
          endOfFollowUpDate,
        );
      }
    } else if (bispecificQ26DataForQ36.length > 1) {
      const lotNumbers = bispecificQ26DataForQ36
        .map((x) => Number(x.lotNumber))
        .filter((x) => !isNaN(x))
        .sort((a, b) => a - b);

      for (let i = 0; i < lotNumbers.length; i++) {
        let isSequential = true;
        let lot = lotNumbers[i];
        let nextLotNumber = lot + 1;

        // Check if this is the last lot
        let isLastLot = i === lotNumbers.length - 1;

        // Check if not first and not last
        let isNotFirstOrLast = i !== 0 && i !== lotNumbers.length - 1;

        if (lotNumbers[i] !== lotNumbers[i - 1] + 1) {
          isSequential = false;
        }

        if (isSequential) {
          // LOTs are sequential

          //Q28, Q29 L Discontinuation Date
          const ndmmDiscontinuationLot =
            safeFind(ndmmLotData, (x) => Number(x.lotNumber) === lot)
              ?.endLotDt ?? null;

          //Q33.1.A L Discontinuation Date
          const q331ALastAdminDtLot =
            safeFind(
              bispecificQ331ADataForQ36,
              (x) => Number(x.lotNumber) === lot,
            )?.lastAdministrationDt ?? null;

          //Q26.1 L+1 Start Date
          const q26FirstAdminDtNextLot =
            safeFind(
              bispecificQ26DataForQ36,
              (x) => Number(x.lotNumber) === nextLotNumber,
            )?.firstAdministrationDt ?? null;

          //Q32 Discontinuation Date
          const q32LastAdminDtLot =
            safeFind(
              bispecificQ31Q32DataForQ36,
              (x) => Number(x.lotNumber) === lot,
            )?.lastAdministrationDt ?? null;

          //Q31 next Lot Start Date
          const q31FirstAdminDtNextLot =
            safeFind(
              bispecificQ31Q32DataForQ36,
              (x) => Number(x.lotNumber) === nextLotNumber,
            )?.firstAdministrationDt ?? null;

          const dateToCompare = hasValue(ndmmDiscontinuationLot)
            ? ndmmDiscontinuationLot
            : hasValue(q331ALastAdminDtLot)
              ? q331ALastAdminDtLot
              : hasValue(q26FirstAdminDtNextLot)
                ? q26FirstAdminDtNextLot
                : null;
          const dateToCompareLastLot = hasValue(q32LastAdminDtLot)
            ? q32LastAdminDtLot
            : hasValue(ndmmDiscontinuationLot)
              ? ndmmDiscontinuationLot
              : hasValue(q331ALastAdminDtLot)
                ? q331ALastAdminDtLot
                : hasValue(q31FirstAdminDtNextLot)
                  ? q31FirstAdminDtNextLot
                  : null;
          const dateToCompareMidlot = hasValue(q32LastAdminDtLot)
            ? q32LastAdminDtLot
            : lot === 3 && hasValue(ndmmDiscontinuationLot)
              ? ndmmDiscontinuationLot
              : hasValue(q331ALastAdminDtLot)
                ? q331ALastAdminDtLot
                : lot === 3 && hasValue(q26FirstAdminDtNextLot)
                  ? q26FirstAdminDtNextLot
                  : hasValue(q31FirstAdminDtNextLot)
                    ? q31FirstAdminDtNextLot
                    : null;

          if (isLastLot) {
            //if this is the last lotnumber of the list
            //Earliest of Q26, Q26.1 <= Latest Date of Dose Administration <= L end Q32 OR L end Q29 OR L end Q33.1.A OR L+1 Start Q31 <= end of follow up
            isValid = CheckDateValidationQ36(
              formattedEarliestQ26Dt,
              latestQ36Dt,
              dateToCompareLastLot,
              endOfFollowUpDate,
            );
          } else if (isNotFirstOrLast) {
            //if this is not the first or last lotnumber of the list
            isValid = CheckDateValidationQ36(
              formattedEarliestQ26Dt,
              latestQ36Dt,
              dateToCompareMidlot,
              endOfFollowUpDate,
            );
          } else {
            //if this is not the last lotnumber of the list
            //Earliest of Q26, Q26.1 <= Latest Date of Dose Administration <= L end Q28 OR L end Q33.1.A OR L+1 Start Q26, Q26.1 <= end of follow up
            isValid = CheckDateValidationQ36(
              formattedEarliestQ26Dt,
              latestQ36Dt,
              dateToCompare,
              endOfFollowUpDate,
            );
          }
        } else if (!isSequential) {
          // LOTs are sequential

          //Q28, Q29 L Discontinuation Date
          const ndmmDiscontinuationLot =
            safeFind(ndmmLotData, (x) => Number(x.lotNumber) === lot)
              ?.endLotDt ?? null;

          //Q33.1.A L Discontinuation Date
          const q331ALastAdminDtLot =
            safeFind(
              bispecificQ331ADataForQ36,
              (x) => Number(x.lotNumber) === lot,
            )?.lastAdministrationDt ?? null;

          //Q31 L+1 Start Date
          const q31StartDateLot =
            safeFind(
              bispecificQ31Q32DataForQ36,
              (x) => Number(x.lotNumber) === nextLotNumber,
            )?.firstAdministrationDt ?? null;

          //Q27 L+1 Start Date
          const q27StartDateLot =
            safeFind(ndmmLotData, (x) => Number(x.lotNumber) === nextLotNumber)
              ?.startLotDt ?? null;

          //Q33.1.A L+1 Start Date
          const q331AStartAdminDtNextLot =
            safeFind(
              bispecificQ331ADataForQ36,
              (x) => Number(x.lotNumber) === nextLotNumber,
            )?.firstAdministrationDt ?? null;

          const dateToCompare = hasValue(ndmmDiscontinuationLot)
            ? ndmmDiscontinuationLot
            : hasValue(q331ALastAdminDtLot)
              ? q331ALastAdminDtLot
              : hasValue(q31StartDateLot)
                ? q31StartDateLot
                : hasValue(q27StartDateLot)
                  ? q27StartDateLot
                  : hasValue(q331AStartAdminDtNextLot)
                    ? q331AStartAdminDtNextLot
                    : null;
          const dateToCompareLastLot = hasValue(q331ALastAdminDtLot)
            ? q331ALastAdminDtLot
            : hasValue(q31StartDateLot)
              ? q31StartDateLot
              : null;
          const dateToCompareMidlot = hasValue(q331ALastAdminDtLot)
            ? q331ALastAdminDtLot
            : hasValue(q31StartDateLot)
              ? q31StartDateLot
              : hasValue(q331AStartAdminDtNextLot)
                ? q331AStartAdminDtNextLot
                : null;

          if (isLastLot) {
            //if this is the last lotnumber of the list
            //Earliest of Q26, Q26.1 <= Latest Date of Dose Administration <= L Q33.1.A DD OR L+1 Q31 SD <= end of follow up
            isValid = CheckDateValidationQ36(
              formattedEarliestQ26Dt,
              latestQ36Dt,
              dateToCompareLastLot,
              endOfFollowUpDate,
            );
          } else if (isNotFirstOrLast) {
            //if this is not the first or last lotnumber of the list
            isValid = CheckDateValidationQ36(
              formattedEarliestQ26Dt,
              latestQ36Dt,
              dateToCompareMidlot,
              endOfFollowUpDate,
            );
          } else {
            //if this is not the last lotnumber of the list
            //Earliest of Q26, Q26.1 <= Latest Date of Dose Administration <= L end Q28 DD OR L end Q33.1.A DD OR L+1 Q31 SD OR L+1 Q27 SD OR L+1 Q33.1.A SD <= end of follow up
            isValid = CheckDateValidationQ36(
              formattedEarliestQ26Dt,
              latestQ36Dt,
              dateToCompare,
              endOfFollowUpDate,
            );
          }
          if (!isValid) return isValid;
        }
      }
    }

    return isValid;
  };

  const ValidateIndexDAndLOTDateChain = (date, cohortType) => {
    const indexDate =
      cohortType === RRMMCohortTreatment.ELREXFIO
        ? elrexIndexDate
        : linvoIndexDate;

  
    const periodEndDate =
      q40PeriodEndDateList?.get(cohortType) || endOfFollowUpDate || "";

    const idx = IsValidDateValue(indexDate) ? indexDate : null;
    const q40Date = IsValidDateValue(date) ? date : null;
    const upper = IsValidDateValue(periodEndDate) ? periodEndDate : null;

    // index date ≤ [D]
    if (idx && q40Date && !IsLessOrEqual(idx, q40Date)) {
      return false;
    }

    // [D] ≤ period end (MIN of next LOT start, end of follow-up)
    if (q40Date && upper && !IsLessOrEqual(q40Date, upper)) {
      return false;
    }

    return true;
  };

  // Q40/Q40.C full date chain (per explicit instruction): Bispecifics/CAR-T
  // index date <= Bispecifics/CAR-T initiation date <= [B] (IVIG initiation
  // date reported in Q40/Q40.C) <= date of last administration reported in
  // column [D] of Q26 (if known) <= end date of LOT in Q26.2 <= start date
  // of subsequent LOT if reported in Q26/Q26.1/Q26.2/Q27/Q31 <= end of
  // follow-up. Reuses the same Q26/Q26.2/next-LOT-start boundary resolution
  // as Q36.1's column [B] chain (GetQ361ChainBoundaryDates/
  // IsQ361DateChainValid) so it stays consistent with that logic.
  const ValidateQ40FullDateChain = (date, cohortType) => {
    const indexDate =
      cohortType === RRMMCohortTreatment.ELREXFIO
        ? elrexIndexDate
        : linvoIndexDate;

    const initiationDate =
      cohortType === RRMMCohortTreatment.ELREXFIO
        ? elrexInitiationDate
        : linvoInitiationDate;

    const chainBoundaries = GetQ361ChainBoundaryDates(cohortType);

    return IsQ361DateChainValid([
      indexDate,
      initiationDate,
      date,
      chainBoundaries.q26LastAdminDt,
      chainBoundaries.q262EndDate,
      chainBoundaries.nextLotStartDate,
      endOfFollowUpDate,
    ]);
  };

  // Q40.C column [B] (Initiation date of IVIG) - unlike Q40's own column [D]
  // above, the lower bound here is the Bispecifics/CAR-T *initiation* date
  // (S12) rather than the index date (index date <= initiation date always,
  // by construction, so it's never the binding constraint against user
  // input). The upper bound is q40PeriodEndDate, which is already
  // MIN(start date of subsequent LOT if reported in Q26/Q26.1/Q26.2/Q27/Q31,
  // end of follow-up) - computed server-side
  // (CommonService.CalculateQ40PeriodEndDate).
  const ValidateQ40CInitDtDateChain = (date, cohortType) => {
    const initiationDate =
      cohortType === RRMMCohortTreatment.ELREXFIO
        ? elrexInitiationDate
        : linvoInitiationDate;

    const periodEndDate = q40PeriodEndDateList?.get(cohortType) ?? "";

    const init = IsValidDateValue(initiationDate) ? initiationDate : null;
    const q40cDate = IsValidDateValue(date) ? date : null;
    const periodEnd = IsValidDateValue(periodEndDate) ? periodEndDate : null;

    // Bispecifics/CAR-T initiation date ≤ [B]
    if (init && q40cDate && !IsLessOrEqual(init, q40cDate)) {
      return false;
    }

    // [B] ≤ period end
    if (q40cDate && periodEnd && !IsLessOrEqual(q40cDate, periodEnd)) {
      return false;
    }

    return true;
  };

  // Q40.1.C column [E] (Date of dose frequency change) - lower bound is the
  // IVIG initiation date reported in Q40.C (if known), otherwise the
  // Bispecifics/CAR-T initiation date; upper bound is q40PeriodEndDate (same
  // MIN(...) value used by Q40.C's own date chain).
  const ValidateQ401CDateChain = (date, cohortType, ivigInitDate) => {
    const bispecificInitDate =
      cohortType === RRMMCohortTreatment.ELREXFIO
        ? elrexInitiationDate
        : linvoInitiationDate;

    const lowerBoundDate = IsValidDateValue(ivigInitDate)
      ? ivigInitDate
      : bispecificInitDate;

    const periodEndDate = q40PeriodEndDateList?.get(cohortType) ?? "";

    const lower = IsValidDateValue(lowerBoundDate) ? lowerBoundDate : null;
    const changeDate = IsValidDateValue(date) ? date : null;
    const upper = IsValidDateValue(periodEndDate) ? periodEndDate : null;

    // lower bound ≤ [E]
    if (lower && changeDate && !IsLessOrEqual(lower, changeDate)) {
      return false;
    }

    // [E] ≤ period end
    if (changeDate && upper && !IsLessOrEqual(changeDate, upper)) {
      return false;
    }

    return true;
  };

  // Full column [F] chain: Bispecific/CAR-T index date <= initiation date <=
  // earliest 1st subsequent treatment dose in Q36 <= [F] <= last
  // administration in column [D] of Q26 (if known) <= end date of LOT in
  // Q26.2 <= start date of subsequent LOT if reported in
  // Q26/Q26.1/Q26.2/Q27/Q31 <= end of follow-up.
  const ValidateQ381ADateChain = (date, cohortType) => {
    const indexDate =
      cohortType === RRMMCohortTreatment.ELREXFIO
        ? elrexIndexDate
        : linvoIndexDate;
    const initiationDate =
      cohortType === RRMMCohortTreatment.ELREXFIO
        ? elrexInitiationDate
        : linvoInitiationDate;
    const q36Rows =
      cohortType === RRMMCohortTreatment.ELREXFIO
        ? elrexvayliDetails
        : linvoveyDetails;
    const autoConfig =
      cohortType === RRMMCohortTreatment.ELREXFIO
        ? Q381A_ELREX_AUTO_CONFIG
        : Q381A_LINVO_AUTO_CONFIG;

    const firstSubQ36Date = GetQ36FirstSubsequentDoseDate(q36Rows, autoConfig);
    const { q26LastAdminDt, q262EndDate, nextLotStartDate } =
      GetQ361ChainBoundaryDates(cohortType);

    return IsQ361DateChainValid([
      indexDate,
      initiationDate,
      firstSubQ36Date,
      date,
      q26LastAdminDt,
      q262EndDate,
      nextLotStartDate,
      endOfFollowUpDate,
    ]);
  };

  // Q38.2.A columns [D]/[E] (start/end dose date) follow the same chain as
  // Q38.1.A's column [F].
  const ValidateQ382ADateChain = (date, cohortType) =>
    ValidateQ381ADateChain(date, cohortType);

  const getQ36UpperLimitDate = (cohortType) => {
    const subsequentLOTStartDate =
      cohortNextLotDatesList?.get(cohortType)?.get(QuestionType.Q40) ?? "";

    const q26Data =
      bispecificQ26Data?.find((x) => x.cohortTreatmentId === cohortType) ??
      null;

    const q262EndDate =
      bispecificQ331AData?.find((x) => x.lotNumber === q26Data?.lotNumber)
        ?.lastAdministrationDt ?? null;

    const earliestUpperBound = getEarliestDate(
      q26Data?.lastAdministrationDt,
      q262EndDate,
      subsequentLOTStartDate,
      endOfFollowUpDate,
    );

    return (
      earliestUpperBound ??
      "[the earliest of: Last administration date in Q26, End date of LOT in Q26.2, Start date of the subsequent LOT, End of follow-up date]"
    );
  };

  const showLinvoDate = () => {
    let varLinvoIndexDate = "";
    if (linvoIndexDate === null || "") {
      varLinvoIndexDate = "[Bispecifics/CAR-T index date]";
    } else {
      varLinvoIndexDate = linvoIndexDate;
    }

    return varLinvoIndexDate;
  };

  const showElrDate = () => {
    let varElrIndexDate = "";
    if (elrexIndexDate === null || "") {
      varElrIndexDate = "[Bispecifics/CAR-T index date]";
    } else {
      varElrIndexDate = elrexIndexDate;
    }

    return varElrIndexDate;
  };

  // Q40.C's "from" date uses the Bispecifics/CAR-T *initiation* date, not the
  // index date above - consistent with ValidateQ40CInitDtDateChain's lower
  // bound.
  const showElrInitiationDate = () => {
    if (!elrexInitiationDate) {
      return "[Bispecifics/CAR-T initiation date]";
    }
    return elrexInitiationDate;
  };

  const showLinvoInitiationDate = () => {
    if (!linvoInitiationDate) {
      return "[Bispecifics/CAR-T initiation date]";
    }
    return linvoInitiationDate;
  };

  // Q40.C period end = MIN(start date of subsequent LOT if reported in
  // Q26/Q26.1/Q26.2/Q27/Q31, end of follow-up) - computed server-side
  // (CommonService.CalculateQ40PeriodEndDate).
  const showElrQ40PeriodEndDate = () =>
    q40PeriodEndDateList.get(RRMMCohortTreatment.ELREXFIO) ||
    "[end of follow-up]";

  const showLinvoQ40PeriodEndDate = () =>
    q40PeriodEndDateList.get(RRMMCohortTreatment.Linvoseltamab) ||
    "[end of follow-up]";

  // Q40.1.C lower bound = IVIG initiation date reported in Q40.C (if known),
  // otherwise the Bispecifics/CAR-T initiation date.
  // Q40.2.C - shared per-drug (Elr/Lin) date helpers. mode: "validate"
  // returns a Date/null for cross-field comparison; otherwise a formatted
  // MM/DD/YYYY string (or a bracketed placeholder while the anchor date is
  // unknown). The anchor is the Bispecifics/CAR-T *index* date -
  // elrexIndexDate/linvoIndexDate - per the spec's "[Bispecifics/CAR-T index
  // date]" anchor (distinct from Q40.C's initiation-date anchor). The upper
  // bound reuses q40PeriodEndDateList - the same MIN(start of subsequent
  // LOT, end of follow-up) value computed server-side.
  const formatQ402CDate = (date) =>
    `${String(date.getMonth() + 1).padStart(2, "0")}/${String(
      date.getDate(),
    ).padStart(2, "0")}/${date.getFullYear()}`;

  const getQ402CIndexDate = (cohortType) =>
    cohortType === RRMMCohortTreatment.ELREXFIO
      ? elrexIndexDate
      : linvoIndexDate;

  const getQ402CBoundaryDate = (cohortType) =>
    q40PeriodEndDateList.get(cohortType) || endOfFollowUpDate || "";

  const getQ402CIndexDateDisplay = (cohortType) => {
    const indexDate = getQ402CIndexDate(cohortType);
    return indexDate
      ? formatQ402CDate(new Date(indexDate))
      : "[Bispecifics/CAR-T index date]";
  };

  // Row 1 lower bound = index date - 365 days
  const getQ402CIndexMinus365 = (mode, cohortType) => {
    const indexDate = getQ402CIndexDate(cohortType);
    if (!indexDate) {
      return mode === "validate"
        ? null
        : "[Bispecifics/CAR-T index date – 365 days]";
    }
    const date = new Date(indexDate);
    date.setDate(date.getDate() - 365);
    return mode === "validate" ? date : formatQ402CDate(date);
  };

  // Row 1 & Row 2 upper bound = Q40 period end date
  const getQ402CPeriodEndDate = (mode, cohortType) => {
    const boundary = getQ402CBoundaryDate(cohortType);
    if (!boundary || !IsValidDateValue(boundary)) {
      return mode === "validate" ? null : "[end of follow-up]";
    }
    return mode === "validate"
      ? new Date(boundary)
      : formatQ402CDate(new Date(boundary));
  };

  // Row 3+ target date = index date + 91 * rowNumber days (1-based)
  const getQ402CTargetDateDisplay = (mode, cohortType, rowNumber) => {
    const indexDate = getQ402CIndexDate(cohortType);
    const target = GetQ402CTargetDate(indexDate, rowNumber);
    if (!target) {
      return mode === "validate" ? null : "[Bispecifics/CAR-T index date]";
    }
    return mode === "validate" ? target : formatQ402CDate(target);
  };

  // Row 3+ lower bound = target date - 45 days
  const getQ402CTargetMinus45 = (mode, cohortType, rowNumber) => {
    const target = getQ402CTargetDateDisplay("validate", cohortType, rowNumber);
    if (!target) {
      return mode === "validate" ? null : "[target date – 45 days]";
    }
    const date = new Date(target);
    date.setDate(date.getDate() - 45);
    return mode === "validate" ? date : formatQ402CDate(date);
  };

  // Row 3+ upper bound = MIN(target date + 45 days, Q40 period end date)
  const getQ402CTargetPlus45Capped = (mode, cohortType, rowNumber) => {
    const target = getQ402CTargetDateDisplay("validate", cohortType, rowNumber);
    if (!target) {
      return mode === "validate"
        ? null
        : "[MIN(target date + 45 days, end of follow-up)]";
    }
    const boundary = getQ402CBoundaryDate(cohortType);
    const plus45 = new Date(target);
    plus45.setDate(plus45.getDate() + 45);
    const candidates = [plus45];
    if (boundary && IsValidDateValue(boundary)) {
      candidates.push(new Date(boundary));
    }
    const earliest = new Date(Math.min(...candidates.map((d) => d.getTime())));
    return mode === "validate" ? earliest : formatQ402CDate(earliest);
  };

  const showElrIvigInitDateQ401C = () => {
    const ivigInitDate =
      Q37ElrexDetails[0]?.ivigInitRd_elrC === 2
        ? Q37ElrexDetails[0]?.ivigInitDt_elrC
        : null;
    return (
      ivigInitDate ||
      elrexInitiationDate ||
      "[Bispecifics/CAR-T initiation date]"
    );
  };

  const showLinIvigInitDateQ401C = () => {
    const ivigInitDate =
      Q37LinvoDetails[0]?.ivigInitRd_linvoC === 2
        ? Q37LinvoDetails[0]?.ivigInitDt_linvoC
        : null;
    return (
      ivigInitDate ||
      linvoInitiationDate ||
      "[Bispecifics/CAR-T initiation date]"
    );
  };

  const CheckDuplicatePhasesRealtime = (doseDetails, type) => {
    const phaseField =
      type === "elrexfio" ? "dosing_phase_elr" : "dosing_phase_linvo";

    // Elrex does NOT have step-up 3, but DOES include 1st dose
    const phasesToCheck = type === "elrexfio" ? [1, 2, 3] : [1, 2, 3];

    let duplicates = [];

    phasesToCheck.forEach((phase) => {
      const phaseCount = doseDetails.filter(
        (dose) => Number(dose[phaseField]) === phase,
      ).length;

      if (phaseCount >= 2) {
        const phaseName =
          phase === 1
            ? "Step-up dose 1"
            : phase === 2
              ? "Step-up dose 2"
              : phase === 3
                ? "Step-up dose 3"
                : "1st treatment dose";

        duplicates.push({ phase, count: phaseCount, phaseName });
      }
    });

    return duplicates;
  };

  const CheckMissingStepUpDosesRealtime = (doseDetails, type) => {
    const phaseField =
      type === "elrexfio" ? "dosing_phase_elr" : "dosing_phase_linvo";
    // Collect all valid numeric phases
    const phases = doseDetails
      .map((d) => Number(d[phaseField]))
      .filter((v) => !isNaN(v) && v > 0);

    // Check if 1st treatment dose exists
    // Elrexfio: phase 3 = 1st treatment dose
    // Linvo: phase 4 = 1st treatment dose
    const firstTreatmentPhase = type === "elrexfio" ? 3 : 4;
    const hasFirstTreatment = phases.includes(firstTreatmentPhase);

    // If no 1st treatment dose selected, no warning needed
    if (!hasFirstTreatment) {
      return false;
    }

    // Check for required step-up doses (same for both)
    const hasStepUp1 = phases.includes(1); // Step-up dose 1
    const hasStepUp2 = phases.includes(2); // Step-up dose 2

    // Warning if EITHER step-up 1 OR step-up 2 is missing
    // when 1st treatment dose is present
    return !hasStepUp1 && !hasStepUp2;
  };

  const runElrexWarnings = (doseArray) => {
    const duplicates = CheckDuplicatePhasesRealtime(doseArray, "elrexfio");
    setElrexfioDuplicatePhaseWarning(duplicates.length > 0 ? duplicates : null);

    const missingStepUp = CheckMissingStepUpDosesRealtime(
      doseArray,
      "elrexfio",
    );
    setElrexfioMissingStepUpWarning(missingStepUp);
  };

  useEffect(() => {
    if (elrexvayliDetails && elrexvayliDetails.length > 0) {
      runElrexWarnings(elrexvayliDetails);
    }
  }, [elrexvayliDetails]);

  const runLinvoWarnings = (doseArray) => {
    const duplicates = CheckDuplicatePhasesRealtime(doseArray, "linvo");
    setLinvoDuplicatePhaseWarning(duplicates.length > 0 ? duplicates : null);

    const missingStepUp = CheckMissingStepUpDosesRealtime(doseArray, "linvo");
    setLinvoMissingStepUpWarning(missingStepUp);
  };

  useEffect(() => {
    if (linvoveyDetails?.length) {
      runLinvoWarnings(linvoveyDetails);
    }
  }, [linvoveyDetails]);

  const showElrDate91 = (validate) => {
    if (!elrexIndexDate) {
      return validate == "validate"
        ? null
        : "[Bispecifics/CAR-T index date – 91 days]";
    }

    const date = new Date(elrexIndexDate);

    // Subtract 91 days
    date.setDate(date.getDate() - 91);

    // Format to MM/DD/YYYY
    const formattedDate = `${String(date.getMonth() + 1).padStart(2, "0")}/${String(
      date.getDate(),
    ).padStart(2, "0")}/${date.getFullYear()}`;

    return formattedDate;
  };

  const getElrLinoQ402BUpperLimitDate = (type, cohortType) => {
    const indexDate =
      cohortType === RRMMCohortTreatment.ELREXFIO
        ? elrexIndexDate
        : RRMMCohortTreatment.Linvoseltamab
          ? linvoIndexDate
          : null;

    if (!indexDate) {
      return type === "validate"
        ? null
        : "[the earliest of: (Bispecifics/CAR-T initiation date + 7 days) or (start of next LOT) or (end of follow-up)]";
    }
    const upperLimitDates = [];
    const nextLotStartDate =
      cohortNextLotDatesList?.get(cohortType)?.get(QuestionType.Q40) ?? "";

    // Index Date + 7 days
    const indexPlus7 = new Date(indexDate);
    indexPlus7.setDate(indexPlus7.getDate() + 7);
    upperLimitDates.push(indexPlus7);

    if (type !== "validate") {
      // Start of next LOT
      if (nextLotStartDate) {
        upperLimitDates.push(new Date(nextLotStartDate));
      }

      // End of follow-up
      if (endOfFollowUpDate) {
        upperLimitDates.push(new Date(endOfFollowUpDate));
      }
    }
    // Get earliest date
    const earliestDate = new Date(
      Math.min(...upperLimitDates.map((d) => d.getTime())),
    );

    // Format MM/DD/YYYY
    return `${String(earliestDate.getMonth() + 1).padStart(2, "0")}/${String(
      earliestDate.getDate(),
    ).padStart(2, "0")}/${earliestDate.getFullYear()}`;
  };

  const showLinDate91 = (validate) => {
    if (!linvoIndexDate) {
      return validate == "validate"
        ? null
        : "[Bispecifics/CAR-T index date – 91 days]";
    }

    const date = new Date(linvoIndexDate);

    // Subtract 91 days
    date.setDate(date.getDate() - 91);

    // Format to MM/DD/YYYY
    const formattedDate = `${String(date.getMonth() + 1).padStart(2, "0")}/${String(
      date.getDate(),
    ).padStart(2, "0")}/${date.getFullYear()}`;

    return formattedDate;
  };

  const showLinQ40Date = (validate) => {
    let varLinIndexDate = "";
    if (isStringEmpty(Q37LinvoDetails[0].q40Init_Dt_linvo)) {
      varLinIndexDate =
        validate == "validate" ? null : "[Initiation date of IGRT]";
    } else {
      varLinIndexDate = Q37LinvoDetails[0].q40Init_Dt_linvo;
    }

    return varLinIndexDate;
  };

  const showLinQ40Date91 = (validate) => {
    if (isStringEmpty(Q37LinvoDetails[0].q40Init_Dt_linvo)) {
      return validate == "validate"
        ? null
        : "[Initiation date of IGRT – 91 days]";
    }

    const date = new Date(Q37LinvoDetails[0].q40Init_Dt_linvo);

    // Subtract 91 days
    date.setDate(date.getDate() - 91);

    // Format to MM/DD/YYYY
    const formattedDate = `${String(date.getMonth() + 1).padStart(2, "0")}/${String(
      date.getDate(),
    ).padStart(2, "0")}/${date.getFullYear()}`;

    return formattedDate;
  };

  const showElrQ40Date = (validate) => {
    let varElrIndexDate = "";
    if (isStringEmpty(Q37ElrexDetails[0].Q40init_dt)) {
      varElrIndexDate =
        validate == "validate" ? null : "[Initiation date of IGRT]";
    } else {
      varElrIndexDate = Q37ElrexDetails[0].Q40init_dt;
    }

    return varElrIndexDate;
  };

  const showElrQ40Date91 = (validate) => {
    if (isStringEmpty(Q37ElrexDetails[0].Q40init_dt)) {
      return validate == "validate"
        ? null
        : "[Initiation date of IGRT – 91 days]";
    }

    const date = new Date(Q37ElrexDetails[0].Q40init_dt);

    // Subtract 91 days
    date.setDate(date.getDate() - 91);

    // Format to MM/DD/YYYY
    const formattedDate = `${String(date.getMonth() + 1).padStart(2, "0")}/${String(
      date.getDate(),
    ).padStart(2, "0")}/${date.getFullYear()}`;

    return formattedDate;
  };

  // Q40 is hidden for a cohort if that cohort's Q39.C answer is "None of the above"
  // Q40 (or Q40.C, when Update C applies) is always displayed for a selected
  // cohort - it is independent of that cohort's Q39/Q39.C answer, including
  // "None of the above".
  const showQ40Elr = isElrexvayliSelected;
  const showQ40Linvo = isLinvoveySelected;

  return (
    // <form> scopes radio groups per LOT: every section reuses the same radio
    // names (e.g. "elr_dose_strength_rbl0"), which would otherwise be one
    // page-wide group.
    <form className="dependent-section" onSubmit={(e) => e.preventDefault()}>
      <CollapsibleSectionHeader
        title={lot.label}
        isExpanded={!isLotCollapsed(0)}
        onToggle={() => toggleLotBlock(0)}
        hasError={showTopErrMsg}
      />
      <Collapse in={!isLotCollapsed(0)}>
        <div>
              <div>
                <div>
                  <div className="question-answer">
                    {(isLinvoveySelected || isElrexvayliSelected) && (
                      <>
                        <div>
                          {isElrexvayliSelected && (
                            <>
                              <div className="question question-weight">
                                <span>Q36.</span>
                                <span className="quest-text-pad">
                                  Provide{" "}
                                  <i>
                                    dosing information for each
                                    administration{" "}
                                  </i>
                                  of
                                  <>
                                    {" "}
                                    {isElrexvayliSelected ? (
                                      <>
                                        <span
                                          style={{
                                            color:
                                              PageDesignConstants.fontColor,
                                            fontWeight: "bolder",
                                          }}
                                        >
                                          ELREXFIO<sup>TM</sup>{" "}
                                          (elranatamab-bcmm)
                                        </span>{" "}
                                      </>
                                    ) : (
                                      ""
                                    )}{" "}
                                    during the step-up{" "}
                                    <span
                                      data-tooltip-id="encounterTip"
                                      className="modal-popup"
                                      onClick={() =>
                                        setShowHideHyperlinkModalQ36Elrex(true)
                                      }
                                    >
                                      dosing phase
                                    </span>
                                    {showHideHyperlinkModalQ36Elrex && (
                                      <InfoPopUp
                                        description={hyperlinkTextQ36Elrexfio}
                                        OkClick={() =>
                                          setShowHideHyperlinkModalQ36Elrex(
                                            false,
                                          )
                                        }
                                      />
                                    )}{" "}
                                    and the 1<sup>st</sup> subsequent treatment
                                    dose.
                                  </>
                                  <br />
                                  <br />
                                  The table below includes rows for each dose
                                  expected to be received. For Tal, patients may
                                  or may not have Step-Up Dose 3 depending on
                                  whether they follow a weekly or biweekly
                                  dosing schedule. If Step-Up Dose 3 is not
                                  applicable, please select{" "}
                                  <i>Patient did not receive dose.</i>
                                  <br />
                                  <br />
                                  <b>Please add additional rows if: </b>
                                  <br />
                                  <>
                                    - For Tal patients initiating under a
                                    biweekly dosing schedule where Step-Up Dose
                                    3 was received
                                  </>
                                  <br />
                                  <>
                                    - A patient that repeated a step-up dose
                                    (i.e., due to toxicity or gaps between
                                    doses, the same step-up dose was received
                                    multiple times)
                                  </>
                                  <br />
                                  <>
                                    - A patient had multiple step-up dosing
                                    phases (including the respective 1
                                    <sup>st</sup> subsequent treatment dose
                                    after each step-up dosing phase, if
                                    received).
                                  </>
                                  <br />
                                  <b>
                                    As of 9/1/2026, the eMMpower study will no
                                    longer be collecting all subsequent
                                    maintenance doses. Only the 1<sup>st</sup>{" "}
                                    subsequent treatment dose after the step-up
                                    dosing phase is required.
                                  </b>
                                  <br />
                                  <br />
                                  <b>
                                    If the dosing phase is not documented,
                                    please infer the dosing phase using the
                                    relevant dosing label information provided
                                  </b>{" "}
                                  <i>
                                    (see Dosing Phase hyperlink for more
                                    information on dosing label).
                                  </i>
                                </span>
                              </div>
                              {/* <ErrorField
                                show={elrNumberofAdministratorMatching}
                                message={
                                  ELCharacteristics_MSGS.RRMMNotMatchingElr
                                }
                                style={{ paddingLeft: "0px" }}
                              /> */}

                              <ErrorField
                                show={indexDateRRMM_elrHard}
                                message={
                                  ELCharacteristics_MSGS.indexDateRRMMNotMatchingElr
                                }
                                style={{ paddingLeft: "50px" }}
                              />

                              <div className="dependent-section">
                                <ErrorField
                                  show={elrexDuplicateDates}
                                  message={
                                    "ELREXFIO cannot have duplicate date of dose administration."
                                  }
                                  style={{ paddingLeft: "0px" }}
                                />

                                <ErrorField
                                  show={!!setupElrDose3_WarningHard}
                                  message={setupElrDose3_WarningHard}
                                  style={{ paddingLeft: "0px" }}
                                />

                                <ErrorField
                                  show={!!setupElrDose1st_WarningHard}
                                  message={setupElrDose1st_WarningHard}
                                  style={{ paddingLeft: "0px" }}
                                />

                                <ErrorField
                                  show={!!setupElrDose1stSubs_WarningHard}
                                  message={setupElrDose1stSubs_WarningHard}
                                  style={{ paddingLeft: "0px" }}
                                />

                                <ErrorField
                                  show={setupElrQ26_WarningHard}
                                  message={ELCharacteristics_MSGS.Q26Warning}
                                  style={{ paddingLeft: "0px" }}
                                />

                                <ErrorField
                                  show={setupElr_WarningHard}
                                  message={
                                    ELCharacteristics_MSGS.DuplicateStepUpNotAllowed
                                  }
                                  style={{ paddingLeft: "0px" }}
                                />

                                <ErrorField
                                  show={elrexfioDuplicatePhaseWarning}
                                  message={
                                    <>
                                      WARNING: you have entered duplicate dose
                                      administrations with the same step-up
                                      dosing phase (i.e., at least two rows
                                      where{" "}
                                      <i>
                                        Step-up dose 1, Step-up dose 2, or 1st
                                        treatment dose
                                      </i>{" "}
                                      have been recorded). The likelihood of
                                      this occurrence is considered rare and
                                      should occur only if multiple step-up
                                      doses occurred. If the patient did not
                                      experience multiple step-up doses, please
                                      revise accordingly.
                                    </>
                                  }
                                  style={{ paddingLeft: "0px" }}
                                />
                                <ErrorField
                                  show={elrexfioMissingStepUpWarning}
                                  message={ELCharacteristics_MSGS.StepUpWarning}
                                  style={{ paddingLeft: "0px" }}
                                />

                                <ErrorField
                                  show={dt_elr_2L_3L_date_conditions}
                                  message={
                                    ELCharacteristics_MSGS.Q362L3LErrorMessage
                                  }
                                  style={{
                                    paddingLeft: "0px",
                                  }}
                                />

                                <ErrorField
                                  show={!isElrLastAdminDateValid}
                                  message={
                                    ELCharacteristics_MSGS.DoseMustMatchLastAdministrationDateElr
                                  }
                                  style={{
                                    paddingLeft: "0px",
                                    paddingBottom: "2px",
                                  }}
                                />

                                {elrexvayliDetails &&
                                  elrexvayliDetails.length > 0 &&
                                  elrexvayliDetails
                                    .map((item, idx) => ({ item, idx }))
                                    .filter(({ item }, index, arr) => {
                                      if (
                                        [1, 2, 3, 4, null].includes(
                                          item.dosing_phase_elr,
                                        )
                                      ) {
                                        return true;
                                      }

                                      return false;
                                    })
                                    .map(({ item: data, idx }, pos) => (
                                      <div
                                        className="loop-section-ul"
                                        key={idx}
                                      >
                                        <div className="loop-head">
                                          <span
                                            className="question-weight"
                                            style={{ float: "inline-start" }}
                                          >
                                            {GetOrdinal(idx + 1)} ELREXFIO®
                                            <sup>TM</sup> (elranatamab-bcmm)
                                          </span>
                                          {idx > 3 && (
                                            <div
                                              className="delete-btn-div"
                                              style={{ float: "inline-end" }}
                                            >
                                              <img
                                                width="16px"
                                                src="../Assets/images/Icon-material-delete.png"
                                                alt=""
                                                style={{ cursor: "pointer" }}
                                                onClick={() =>
                                                  DeleteElrexvayliRow(idx)
                                                }
                                              />
                                            </div>
                                          )}
                                        </div>

                                        {/* ELREXFIO® */}
                                        <div className="question-bot-sapce">
                                          <div className="sub-question grid-overflow">
                                            <table
                                              className="date-picker-ttchar"
                                              style={{
                                                border: "1px solid black",
                                                borderCollapse: "collapse",
                                                width: "100%",
                                              }}
                                            >
                                              <thead>
                                                <tr>
                                                  <th
                                                    style={{
                                                      border: "1px solid black",
                                                      padding: "8px",
                                                      textAlign: "center",
                                                    }}
                                                  >
                                                    Treatment
                                                  </th>
                                                  <th
                                                    style={{
                                                      border: "1px solid black",
                                                      padding: "8px",
                                                      textAlign: "center",
                                                      width: "20%",
                                                    }}
                                                  >
                                                    {" "}
                                                    <span
                                                      data-tooltip-id="encounterTip"
                                                      className="modal-popup"
                                                      onClick={() =>
                                                        setShowHideHyperlinkModalQ36Elrex(
                                                          true,
                                                        )
                                                      }
                                                    >
                                                      Dosing phase
                                                    </span>
                                                    {showHideHyperlinkModalQ36Elrex && (
                                                      <InfoPopUp
                                                        description={
                                                          hyperlinkTextQ36Elrexfio
                                                        }
                                                        OkClick={() =>
                                                          setShowHideHyperlinkModalQ36Elrex(
                                                            false,
                                                          )
                                                        }
                                                      />
                                                    )}{" "}
                                                  </th>
                                                  <th
                                                    style={{
                                                      border: "1px solid black",
                                                      padding: "8px",
                                                      textAlign: "center",
                                                    }}
                                                  >
                                                    Date of dose administration
                                                  </th>
                                                  <th
                                                    style={{
                                                      border: "1px solid black",
                                                      padding: "8px",
                                                      textAlign: "center",
                                                    }}
                                                  >
                                                    Site of care
                                                  </th>
                                                  <th
                                                    style={{
                                                      border: "1px solid black",
                                                      padding: "8px",
                                                      textAlign: "center",
                                                      minWidth: "280px",
                                                    }}
                                                  >
                                                    Dose strength
                                                  </th>
                                                </tr>
                                              </thead>

                                              <tbody>
                                                <tr>
                                                  <td
                                                    style={{
                                                      border: "1px solid black",
                                                      padding: "8px",
                                                    }}
                                                  >
                                                    <span className="quest-text-pad">
                                                      ELREXFIO<sup>TM</sup>{" "}
                                                      (elranatamab-bcmm)
                                                    </span>
                                                  </td>

                                                  <td
                                                    style={{
                                                      border: "1px solid black",
                                                      padding: "8px",
                                                    }}
                                                  >
                                                    <div>
                                                      {idx === 1 ||
                                                      idx === 2 ||
                                                      idx === 3 ? (
                                                        ""
                                                      ) : (
                                                        <div
                                                          className="option-without-input"
                                                          style={{
                                                            display: "flex",
                                                          }}
                                                        >
                                                          <label className="option-fbox">
                                                            <input
                                                              type="radio"
                                                              id={idx}
                                                              name={
                                                                "dosing_phase_elr" +
                                                                idx
                                                              }
                                                              value={1}
                                                              checked={
                                                                idx === 0
                                                                  ? true
                                                                  : data.dosing_phase_elr ===
                                                                    1
                                                              }
                                                              onChange={
                                                                HandleRepeaterFieldChange
                                                              }
                                                            />
                                                            <span
                                                              className="radio-text-padding"
                                                              style={{
                                                                paddingLeft:
                                                                  "10px",
                                                              }}
                                                            >
                                                              Step-up dose
                                                              1{" "}
                                                            </span>
                                                          </label>
                                                        </div>
                                                      )}

                                                      {idx === 0 ||
                                                      idx === 2 ||
                                                      idx === 3 ? (
                                                        ""
                                                      ) : (
                                                        <div
                                                          className="option-without-input"
                                                          style={{
                                                            display: "flex",
                                                            alignItems:
                                                              "flex-start",
                                                          }}
                                                        >
                                                          <label className="option-fbox">
                                                            <input
                                                              type="radio"
                                                              style={{
                                                                marginTop:
                                                                  "6px",
                                                              }}
                                                              id={idx}
                                                              name={
                                                                "dosing_phase_elr" +
                                                                idx
                                                              }
                                                              value={2}
                                                              checked={
                                                                idx === 1
                                                                  ? true
                                                                  : data.dosing_phase_elr ===
                                                                    2
                                                              }
                                                              onChange={
                                                                HandleRepeaterFieldChange
                                                              }
                                                            />

                                                            <span
                                                              // className="radio-text-padding"
                                                              style={{
                                                                paddingLeft:
                                                                  "10px",
                                                              }}
                                                            >
                                                              Step-up dose 2
                                                            </span>
                                                          </label>
                                                        </div>
                                                      )}

                                                      {idx === 0 ||
                                                      idx === 1 ||
                                                      idx === 3 ? (
                                                        ""
                                                      ) : (
                                                        <div
                                                          className="option-without-input"
                                                          style={{
                                                            display: "flex",
                                                            alignItems:
                                                              "flex-start",
                                                          }}
                                                        >
                                                          <label className="option-fbox">
                                                            <input
                                                              type="radio"
                                                              style={{
                                                                marginTop:
                                                                  "6px",
                                                              }}
                                                              id={idx}
                                                              name={
                                                                "dosing_phase_elr" +
                                                                idx
                                                              }
                                                              value={3}
                                                              checked={
                                                                idx === 2
                                                                  ? true
                                                                  : data.dosing_phase_elr ===
                                                                    3
                                                              }
                                                              onChange={
                                                                HandleRepeaterFieldChange
                                                              }
                                                            />

                                                            <span
                                                              style={{
                                                                paddingLeft:
                                                                  "10px",
                                                              }}
                                                            >
                                                              1<sup>st</sup>{" "}
                                                              treatment dose
                                                            </span>
                                                          </label>
                                                        </div>
                                                      )}

                                                      {idx === 0 ||
                                                      idx === 1 ||
                                                      idx === 2 ? (
                                                        ""
                                                      ) : (
                                                        <div
                                                          className="option-without-input"
                                                          style={{
                                                            display: "flex",
                                                            alignItems:
                                                              "flex-start",
                                                          }}
                                                        >
                                                          <label className="option-fbox">
                                                            <input
                                                              type="radio"
                                                              style={{
                                                                marginTop:
                                                                  "6px",
                                                              }}
                                                              id={idx}
                                                              name={
                                                                "dosing_phase_elr" +
                                                                idx
                                                              }
                                                              value={4}
                                                              checked={
                                                                idx === 3
                                                                  ? true
                                                                  : data.dosing_phase_elr ===
                                                                    4
                                                              }
                                                              onChange={
                                                                HandleRepeaterFieldChange
                                                              }
                                                            />

                                                            <span
                                                              style={{
                                                                paddingLeft:
                                                                  "10px",
                                                              }}
                                                            >
                                                              1<sup>st</sup>{" "}
                                                              subsequent
                                                              treatment dose
                                                            </span>
                                                          </label>
                                                        </div>
                                                      )}

                                                      <ErrorField
                                                        show={
                                                          data.dosing_phase_elrHard
                                                        }
                                                        message={
                                                          ELCharacteristics_MSGS.dosingphaseEmpty
                                                        }
                                                        style={{
                                                          paddingLeft: "0px",
                                                        }}
                                                      />

                                                      <ErrorField
                                                        show={
                                                          data.dosing_phase_elrHard_othEmpty
                                                        }
                                                        message={
                                                          "Please enter the text"
                                                        }
                                                        style={{
                                                          paddingLeft: "0px",
                                                        }}
                                                      />
                                                    </div>
                                                  </td>

                                                  <td
                                                    style={{
                                                      border: "1px solid black",
                                                      padding: "8px",
                                                    }}
                                                  >
                                                    {idx == 0 ? (
                                                      <>
                                                        {" "}
                                                        {
                                                          elrexInitiationDate
                                                        }{" "}
                                                      </>
                                                    ) : (
                                                      <div>
                                                        <div
                                                          className="option-without-input"
                                                          style={{
                                                            display: "flex",
                                                          }}
                                                        >
                                                          <input
                                                            type="radio"
                                                            id={idx}
                                                            name={
                                                              "dose_administration_elr" +
                                                              idx
                                                            }
                                                            value={1}
                                                            checked={
                                                              data.dose_administration_elr ===
                                                              1
                                                            }
                                                            onChange={
                                                              HandleRepeaterFieldChange
                                                            }
                                                          />
                                                          <span
                                                            className="radio-text-padding"
                                                            style={{
                                                              paddingLeft:
                                                                "10px",
                                                              marginLeft:
                                                                "-5px",
                                                            }}
                                                          >
                                                            <DatePickerComponent
                                                              ctrlId={
                                                                "dose_administration_elr_dt-" +
                                                                idx
                                                              }
                                                              HandleDateChange={
                                                                HandleRepeaterDateChange
                                                              }
                                                              // optionId={1}
                                                              minDate={dayjs(
                                                                "01-01-1920",
                                                              )}
                                                              maxDate={dayjs()}
                                                              locale="en" // current it is mm/dd/yyyy but for dd/mm/yyyy use "en-gb"
                                                              date={
                                                                data.dose_administration_elr_dt
                                                              }
                                                            />
                                                          </span>
                                                        </div>
                                                        <div
                                                          className="option-without-input"
                                                          style={{
                                                            display: "flex",
                                                            alignItems:
                                                              "flex-start",
                                                          }}
                                                        >
                                                          <label className="option-fbox">
                                                            <input
                                                              type="radio"
                                                              style={{
                                                                marginTop:
                                                                  "6px",
                                                              }}
                                                              id={idx}
                                                              name={
                                                                "dose_administration_elr" +
                                                                idx
                                                              }
                                                              value={99}
                                                              checked={
                                                                data.dose_administration_elr ===
                                                                99
                                                              }
                                                              onChange={
                                                                HandleRepeaterFieldChange
                                                              }
                                                            />

                                                            <span
                                                              // className="radio-text-padding"
                                                              style={{
                                                                paddingLeft:
                                                                  "10px",
                                                              }}
                                                            >
                                                              Date of dose
                                                              administration
                                                              unknown
                                                            </span>
                                                          </label>
                                                        </div>

                                                        {!data.isManuallyAdded && (
                                                          <div
                                                            className="option-without-input"
                                                            style={{
                                                              display: "flex",
                                                              alignItems:
                                                                "flex-start",
                                                            }}
                                                          >
                                                            <label className="option-fbox">
                                                              <input
                                                                type="radio"
                                                                style={{
                                                                  marginTop:
                                                                    "6px",
                                                                }}
                                                                id={idx}
                                                                name={
                                                                  "dose_administration_elr" +
                                                                  idx
                                                                }
                                                                value={2}
                                                                checked={
                                                                  data.dose_administration_elr ===
                                                                  2
                                                                }
                                                                onChange={
                                                                  HandleRepeaterFieldChange
                                                                }
                                                              />

                                                              <span
                                                                // className="radio-text-padding"
                                                                style={{
                                                                  paddingLeft:
                                                                    "10px",
                                                                }}
                                                              >
                                                                Patient did not
                                                                receive dose
                                                              </span>
                                                            </label>
                                                          </div>
                                                        )}

                                                        <ErrorField
                                                          show={
                                                            data.dose_administration_elrHard
                                                          }
                                                          message={
                                                            ELCharacteristics_MSGS.administrationEmpty
                                                          }
                                                          style={{
                                                            paddingLeft: "0px",
                                                          }}
                                                        />
                                                        <ErrorField
                                                          show={
                                                            data.dose_administration_elrHard_dt
                                                          }
                                                          message={
                                                            ELCharacteristics_MSGS.administrationEmptyDate
                                                          }
                                                          style={{
                                                            paddingLeft: "0px",
                                                          }}
                                                        />
                                                        <ErrorField
                                                          show={
                                                            data.dt_elr_date_out_of_bound
                                                          }
                                                          message={
                                                            ELCharacteristics_MSGS.Q36DateOutOfBound
                                                          }
                                                          style={{
                                                            paddingLeft: "0px",
                                                          }}
                                                        />

                                                        <ErrorField
                                                          show={
                                                            data.Invalid_dose_administration_elr_dt
                                                          }
                                                          message={
                                                            CommonError_Msgs.InvalidDate
                                                          }
                                                          style={{
                                                            paddingLeft: "0px",
                                                          }}
                                                        />
                                                        <ErrorField
                                                          show={
                                                            data.dose_administration_elr_dt_before_eof
                                                          }
                                                          message={
                                                            ELCharacteristics_MSGS.DateBeforeEOF
                                                          }
                                                          style={{
                                                            paddingLeft: "0px",
                                                          }}
                                                        />
                                                      </div>
                                                    )}
                                                  </td>

                                                  <td
                                                    style={{
                                                      border: "1px solid black",
                                                      padding: "8px",
                                                    }}
                                                  >
                                                    {data.dose_administration_elr ===
                                                    2 ? (
                                                      <b>Not applicable</b>
                                                    ) : (
                                                      <div>
                                                        <div
                                                          className="option-without-input"
                                                          style={{
                                                            display: "flex",
                                                          }}
                                                        >
                                                          <label className="option-fbox">
                                                            <input
                                                              type="radio"
                                                              id={idx}
                                                              name={
                                                                "siteofcare_elr" +
                                                                idx
                                                              }
                                                              value={1}
                                                              checked={
                                                                data.siteofcare_elr ===
                                                                1
                                                              }
                                                              onChange={
                                                                HandleRepeaterFieldChange
                                                              }
                                                            />
                                                            <span
                                                              // className="radio-text-padding"
                                                              style={{
                                                                paddingLeft:
                                                                  "10px",
                                                              }}
                                                            >
                                                              Inpatient{" "}
                                                            </span>
                                                          </label>
                                                        </div>
                                                        <div
                                                          className="option-without-input"
                                                          style={{
                                                            display: "flex",
                                                            alignItems:
                                                              "flex-start",
                                                          }}
                                                        >
                                                          <label className="option-fbox">
                                                            <input
                                                              type="radio"
                                                              style={{
                                                                marginTop:
                                                                  "6px",
                                                              }}
                                                              id={idx}
                                                              name={
                                                                "siteofcare_elr" +
                                                                idx
                                                              }
                                                              value={2}
                                                              checked={
                                                                data.siteofcare_elr ===
                                                                2
                                                              }
                                                              onChange={
                                                                HandleRepeaterFieldChange
                                                              }
                                                            />

                                                            <span
                                                              // className="radio-text-padding"
                                                              style={{
                                                                paddingLeft:
                                                                  "10px",
                                                              }}
                                                            >
                                                              Outpatient
                                                            </span>
                                                          </label>
                                                        </div>

                                                        {/* <div
                                                    className="option-without-input"
                                                    style={{
                                                      display: "flex",
                                                      alignItems: "flex-start",
                                                    }}
                                                  >
                                                    <label className="option-fbox">
                                                      <input
                                                        type="radio"
                                                        style={{
                                                          marginTop: "6px",
                                                        }}
                                                        id={idx}
                                                        name={
                                                          "siteofcare_elr" +
                                                          idx
                                                        }
                                                        value={3}
                                                        checked={
                                                          data.siteofcare_elr ===
                                                          3
                                                        }
                                                        onChange={
                                                          HandleRepeaterFieldChange
                                                        }
                                                      />

                                                      <span
                                                        // className="radio-text-padding"
                                                        style={{
                                                          paddingLeft: "10px",
                                                        }}
                                                      >
                                                        Both inpatient and
                                                        outpatient
                                                      </span>
                                                    </label>
                                                  </div> */}
                                                        <ErrorField
                                                          show={
                                                            data.siteofcare_elrHard
                                                          }
                                                          message={
                                                            ELCharacteristics_MSGS.siteofcareEmpty
                                                          }
                                                          style={{
                                                            paddingLeft: "0px",
                                                          }}
                                                        />
                                                      </div>
                                                    )}
                                                  </td>

                                                  <td
                                                    style={{
                                                      border: "1px solid black",
                                                      padding: "8px",
                                                    }}
                                                  >
                                                    {data.dose_administration_elr ===
                                                    2 ? (
                                                      <b>Not applicable</b>
                                                    ) : pos === 0 ? (
                                                      <> {doseStrength_Elr} </>
                                                    ) : (
                                                      <div>
                                                        {data.dosing_phase_elr ===
                                                          4 && (
                                                          <b>
                                                            Initial dose
                                                            strength immediately
                                                            following step-up
                                                            dosing phase:
                                                          </b>
                                                        )}
                                                        <div className="option-fbox">
                                                          <label>
                                                            <input
                                                              type="radio"
                                                              id={idx}
                                                              name={
                                                                "elr_dose_strength_rbl" +
                                                                idx
                                                              }
                                                              value={1}
                                                              checked={
                                                                data.elr_dose_strength_rbl ===
                                                                1
                                                              }
                                                              onChange={
                                                                HandleRepeaterFieldChange
                                                              }
                                                            />
                                                          </label>
                                                          <label>
                                                            <span className="radio-text-padding">
                                                              <input
                                                                className="input-dash-custom"
                                                                id={idx}
                                                                name={
                                                                  "elr_dose_strength_mg" +
                                                                  idx
                                                                }
                                                                value={
                                                                  data.elr_dose_strength_mg
                                                                }
                                                                type="text"
                                                                onKeyDown={(
                                                                  e,
                                                                ) => {
                                                                  // Allow backspace, arrow keys, and delete
                                                                  if (
                                                                    [
                                                                      "Backspace",
                                                                      "ArrowLeft",
                                                                      "ArrowRight",
                                                                      "Delete",
                                                                      "Tab",
                                                                    ].includes(
                                                                      e.key,
                                                                    )
                                                                  ) {
                                                                    return;
                                                                  }

                                                                  // Prevent non-numeric characters, except '.'
                                                                  if (
                                                                    !/[0-9.]/.test(
                                                                      e.key,
                                                                    )
                                                                  ) {
                                                                    e.preventDefault();
                                                                    return;
                                                                  }

                                                                  const value =
                                                                    e.target
                                                                      .value;
                                                                  const selectionStart =
                                                                    e.target
                                                                      .selectionStart;
                                                                  const newValue =
                                                                    value.slice(
                                                                      0,
                                                                      selectionStart,
                                                                    ) +
                                                                    e.key +
                                                                    value.slice(
                                                                      e.target
                                                                        .selectionEnd,
                                                                    );

                                                                  // Prevent multiple '.'
                                                                  if (
                                                                    e.key ===
                                                                      "." &&
                                                                    value.includes(
                                                                      ".",
                                                                    )
                                                                  ) {
                                                                    e.preventDefault();
                                                                    return;
                                                                  }

                                                                  // Prevent '.' as the first character
                                                                  if (
                                                                    e.key ===
                                                                      "." &&
                                                                    value === ""
                                                                  ) {
                                                                    e.preventDefault();
                                                                    return;
                                                                  }

                                                                  // Allow only one digit after decimal point
                                                                  if (
                                                                    newValue.includes(
                                                                      ".",
                                                                    ) &&
                                                                    newValue.split(
                                                                      ".",
                                                                    )[1]
                                                                      .length >
                                                                      2
                                                                  ) {
                                                                    e.preventDefault();
                                                                    return;
                                                                  }
                                                                }}
                                                                onChange={
                                                                  HandleRepeaterFieldChange
                                                                }
                                                              />
                                                            </span>
                                                            mg
                                                          </label>
                                                        </div>
                                                        <br />
                                                        <div className="option-fbox">
                                                          <label>
                                                            <input
                                                              type="radio"
                                                              id={idx}
                                                              name={
                                                                "elr_dose_strength_rbl" +
                                                                idx
                                                              }
                                                              value={2}
                                                              checked={
                                                                data.elr_dose_strength_rbl ===
                                                                2
                                                              }
                                                              onChange={
                                                                HandleRepeaterFieldChange
                                                              }
                                                            />
                                                          </label>
                                                          <label>
                                                            <span className="radio-text-padding">
                                                              <input
                                                                className="input-dash-custom"
                                                                id={idx}
                                                                name={
                                                                  "elr_dose_strength_mg_kg" +
                                                                  idx
                                                                }
                                                                value={
                                                                  data.elr_dose_strength_mg_kg
                                                                }
                                                                type="text"
                                                                onKeyDown={(
                                                                  e,
                                                                ) => {
                                                                  // Allow backspace, arrow keys, and delete
                                                                  if (
                                                                    [
                                                                      "Backspace",
                                                                      "ArrowLeft",
                                                                      "ArrowRight",
                                                                      "Delete",
                                                                      "Tab",
                                                                    ].includes(
                                                                      e.key,
                                                                    )
                                                                  ) {
                                                                    return;
                                                                  }

                                                                  // Prevent non-numeric characters, except '.'
                                                                  if (
                                                                    !/[0-9.]/.test(
                                                                      e.key,
                                                                    )
                                                                  ) {
                                                                    e.preventDefault();
                                                                    return;
                                                                  }

                                                                  const value =
                                                                    e.target
                                                                      .value;
                                                                  const selectionStart =
                                                                    e.target
                                                                      .selectionStart;
                                                                  const newValue =
                                                                    value.slice(
                                                                      0,
                                                                      selectionStart,
                                                                    ) +
                                                                    e.key +
                                                                    value.slice(
                                                                      e.target
                                                                        .selectionEnd,
                                                                    );

                                                                  // Prevent multiple '.'
                                                                  if (
                                                                    e.key ===
                                                                      "." &&
                                                                    value.includes(
                                                                      ".",
                                                                    )
                                                                  ) {
                                                                    e.preventDefault();
                                                                    return;
                                                                  }

                                                                  // Prevent '.' as the first character
                                                                  if (
                                                                    e.key ===
                                                                      "." &&
                                                                    value === ""
                                                                  ) {
                                                                    e.preventDefault();
                                                                    return;
                                                                  }

                                                                  // Allow only one digit after decimal point
                                                                  if (
                                                                    newValue.includes(
                                                                      ".",
                                                                    ) &&
                                                                    newValue.split(
                                                                      ".",
                                                                    )[1]
                                                                      .length >
                                                                      2
                                                                  ) {
                                                                    e.preventDefault();
                                                                    return;
                                                                  }
                                                                }}
                                                                onChange={
                                                                  HandleRepeaterFieldChange
                                                                }
                                                              />
                                                            </span>
                                                            mg/kg
                                                          </label>
                                                        </div>
                                                        <br />
                                                        <div className="option-fbox">
                                                          <label>
                                                            <input
                                                              type="radio"
                                                              id={idx}
                                                              name={
                                                                "elr_dose_strength_rbl" +
                                                                idx
                                                              }
                                                              value={3}
                                                              checked={
                                                                data.elr_dose_strength_rbl ===
                                                                3
                                                              }
                                                              onChange={
                                                                HandleRepeaterFieldChange
                                                              }
                                                            />
                                                          </label>
                                                          <label>
                                                            <span className="radio-text-padding">
                                                              <input
                                                                className="input-dash-custom"
                                                                id={idx}
                                                                name={
                                                                  "elr_dose_strength_mgm" +
                                                                  idx
                                                                }
                                                                value={
                                                                  data.elr_dose_strength_mgm
                                                                }
                                                                type="text"
                                                                onKeyDown={(
                                                                  e,
                                                                ) => {
                                                                  // Allow backspace, arrow keys, and delete
                                                                  if (
                                                                    [
                                                                      "Backspace",
                                                                      "ArrowLeft",
                                                                      "ArrowRight",
                                                                      "Delete",
                                                                      "Tab",
                                                                    ].includes(
                                                                      e.key,
                                                                    )
                                                                  ) {
                                                                    return;
                                                                  }

                                                                  // Prevent non-numeric characters, except '.'
                                                                  if (
                                                                    !/[0-9.]/.test(
                                                                      e.key,
                                                                    )
                                                                  ) {
                                                                    e.preventDefault();
                                                                    return;
                                                                  }

                                                                  const value =
                                                                    e.target
                                                                      .value;
                                                                  const selectionStart =
                                                                    e.target
                                                                      .selectionStart;
                                                                  const newValue =
                                                                    value.slice(
                                                                      0,
                                                                      selectionStart,
                                                                    ) +
                                                                    e.key +
                                                                    value.slice(
                                                                      e.target
                                                                        .selectionEnd,
                                                                    );

                                                                  // Prevent multiple '.'
                                                                  if (
                                                                    e.key ===
                                                                      "." &&
                                                                    value.includes(
                                                                      ".",
                                                                    )
                                                                  ) {
                                                                    e.preventDefault();
                                                                    return;
                                                                  }

                                                                  // Prevent '.' as the first character
                                                                  if (
                                                                    e.key ===
                                                                      "." &&
                                                                    value === ""
                                                                  ) {
                                                                    e.preventDefault();
                                                                    return;
                                                                  }

                                                                  // Allow only one digit after decimal point
                                                                  if (
                                                                    newValue.includes(
                                                                      ".",
                                                                    ) &&
                                                                    newValue.split(
                                                                      ".",
                                                                    )[1]
                                                                      .length >
                                                                      2
                                                                  ) {
                                                                    e.preventDefault();
                                                                    return;
                                                                  }
                                                                }}
                                                                onChange={
                                                                  HandleRepeaterFieldChange
                                                                }
                                                              />
                                                            </span>
                                                            &nbsp; mg/m
                                                            <sup>2</sup>
                                                          </label>
                                                          {/*<label><span className="" style={{ marginLeft: '12px', textAlign: 'start' }}>(Range: 1 to 550)</span></label>*/}
                                                        </div>
                                                        <div className="">
                                                          <div className="option-without-input">
                                                            <label className="option-fbox">
                                                              <input
                                                                type="radio"
                                                                id={idx}
                                                                name={
                                                                  "elr_dose_strength_rbl" +
                                                                  idx
                                                                }
                                                                value={99}
                                                                checked={
                                                                  data.elr_dose_strength_rbl ===
                                                                  99
                                                                }
                                                                onChange={
                                                                  HandleRepeaterFieldChange
                                                                }
                                                              />
                                                              <span className="radio-text-padding">
                                                                Unknown
                                                              </span>
                                                            </label>
                                                          </div>
                                                        </div>
                                                        <ErrorField
                                                          show={
                                                            data.elr_dose_freq_hard_error
                                                          }
                                                          message={
                                                            ELCharacteristics_MSGS.siteofcareEmpty
                                                          }
                                                          style={{
                                                            paddingLeft: "0px",
                                                          }}
                                                        />
                                                        <ErrorField
                                                          show={
                                                            data.elr_dose_strength_mg_kgHard
                                                          }
                                                          message={
                                                            ELCharacteristics_MSGS.DecimalRange
                                                          }
                                                          style={{
                                                            paddingLeft: "0px",
                                                          }}
                                                        />

                                                        <ErrorField
                                                          show={
                                                            data.elr_dose_strength_mg_kgRange
                                                          }
                                                          message={
                                                            ELCharacteristics_MSGS.DecimalRange
                                                          }
                                                          style={{
                                                            paddingLeft: "0px",
                                                          }}
                                                        />
                                                        <br />
                                                        <br />
                                                        {data.dosing_phase_elr ===
                                                          4 && (
                                                          <div>
                                                            <b>
                                                              Initial dose
                                                              frequency
                                                              immediately
                                                              following step-up
                                                              dosing phase:
                                                            </b>
                                                            <div className="option-fbox">
                                                              <label>
                                                                <input
                                                                  type="radio"
                                                                  id={idx}
                                                                  name={
                                                                    "elr_dose_freq_rbl" +
                                                                    idx
                                                                  }
                                                                  value={1}
                                                                  checked={
                                                                    data.elr_dose_freq_rbl ===
                                                                    1
                                                                  }
                                                                  onChange={
                                                                    HandleRepeaterFieldChange
                                                                  }
                                                                />
                                                              </label>
                                                              <label>
                                                                <span className="radio-text-padding">
                                                                  <input
                                                                    className="input-dash-custom"
                                                                    id={idx}
                                                                    name={
                                                                      "elr_dose_freq_week" +
                                                                      idx
                                                                    }
                                                                    value={
                                                                      data.elr_dose_freq_week
                                                                    }
                                                                    type="text"
                                                                    onKeyDown={(
                                                                      e,
                                                                    ) => {
                                                                      // Allow backspace, arrow keys, and delete
                                                                      if (
                                                                        [
                                                                          "Backspace",
                                                                          "ArrowLeft",
                                                                          "ArrowRight",
                                                                          "Delete",
                                                                          "Tab",
                                                                        ].includes(
                                                                          e.key,
                                                                        )
                                                                      ) {
                                                                        return;
                                                                      }

                                                                      // Prevent non-numeric characters, except '.'
                                                                      if (
                                                                        !/[0-9.]/.test(
                                                                          e.key,
                                                                        )
                                                                      ) {
                                                                        e.preventDefault();
                                                                        return;
                                                                      }

                                                                      const value =
                                                                        e.target
                                                                          .value;
                                                                      const selectionStart =
                                                                        e.target
                                                                          .selectionStart;
                                                                      const newValue =
                                                                        value.slice(
                                                                          0,
                                                                          selectionStart,
                                                                        ) +
                                                                        e.key +
                                                                        value.slice(
                                                                          e
                                                                            .target
                                                                            .selectionEnd,
                                                                        );

                                                                      // Prevent multiple '.'
                                                                      if (
                                                                        e.key ===
                                                                          "." &&
                                                                        value.includes(
                                                                          ".",
                                                                        )
                                                                      ) {
                                                                        e.preventDefault();
                                                                        return;
                                                                      }

                                                                      // Prevent '.' as the first character
                                                                      if (
                                                                        e.key ===
                                                                          "." &&
                                                                        value ===
                                                                          ""
                                                                      ) {
                                                                        e.preventDefault();
                                                                        return;
                                                                      }

                                                                      // Allow only one digit after decimal point
                                                                      if (
                                                                        newValue.includes(
                                                                          ".",
                                                                        ) &&
                                                                        newValue.split(
                                                                          ".",
                                                                        )[1]
                                                                          .length >
                                                                          2
                                                                      ) {
                                                                        e.preventDefault();
                                                                        return;
                                                                      }
                                                                    }}
                                                                    onChange={
                                                                      HandleRepeaterFieldChange
                                                                    }
                                                                  />
                                                                </span>
                                                                <span
                                                                  className="radio-text-padding"
                                                                  style={{
                                                                    display:
                                                                      "block",
                                                                  }}
                                                                >
                                                                  times per
                                                                  week
                                                                </span>
                                                              </label>
                                                            </div>
                                                            <div className="option-fbox">
                                                              <label>
                                                                <input
                                                                  type="radio"
                                                                  id={idx}
                                                                  name={
                                                                    "elr_dose_freq_rbl" +
                                                                    idx
                                                                  }
                                                                  value={2}
                                                                  checked={
                                                                    data.elr_dose_freq_rbl ===
                                                                    2
                                                                  }
                                                                  onChange={
                                                                    HandleRepeaterFieldChange
                                                                  }
                                                                />
                                                              </label>
                                                              <label>
                                                                <span className="radio-text-padding">
                                                                  Once every
                                                                  &nbsp;
                                                                  <input
                                                                    className="input-dash-custom"
                                                                    id={idx}
                                                                    name={
                                                                      "elr_dose_freq_onceweek" +
                                                                      idx
                                                                    }
                                                                    value={
                                                                      data.elr_dose_freq_onceweek
                                                                    }
                                                                    type="text"
                                                                    onKeyDown={(
                                                                      e,
                                                                    ) => {
                                                                      // Allow backspace, arrow keys, and delete
                                                                      if (
                                                                        [
                                                                          "Backspace",
                                                                          "ArrowLeft",
                                                                          "ArrowRight",
                                                                          "Delete",
                                                                          "Tab",
                                                                        ].includes(
                                                                          e.key,
                                                                        )
                                                                      ) {
                                                                        return;
                                                                      }

                                                                      // Prevent non-numeric characters, except '.'
                                                                      if (
                                                                        !/[0-9.]/.test(
                                                                          e.key,
                                                                        )
                                                                      ) {
                                                                        e.preventDefault();
                                                                        return;
                                                                      }

                                                                      const value =
                                                                        e.target
                                                                          .value;
                                                                      const selectionStart =
                                                                        e.target
                                                                          .selectionStart;
                                                                      const newValue =
                                                                        value.slice(
                                                                          0,
                                                                          selectionStart,
                                                                        ) +
                                                                        e.key +
                                                                        value.slice(
                                                                          e
                                                                            .target
                                                                            .selectionEnd,
                                                                        );

                                                                      // Prevent multiple '.'
                                                                      if (
                                                                        e.key ===
                                                                          "." &&
                                                                        value.includes(
                                                                          ".",
                                                                        )
                                                                      ) {
                                                                        e.preventDefault();
                                                                        return;
                                                                      }

                                                                      // Prevent '.' as the first character
                                                                      if (
                                                                        e.key ===
                                                                          "." &&
                                                                        value ===
                                                                          ""
                                                                      ) {
                                                                        e.preventDefault();
                                                                        return;
                                                                      }

                                                                      // Allow only one digit after decimal point
                                                                      if (
                                                                        newValue.includes(
                                                                          ".",
                                                                        ) &&
                                                                        newValue.split(
                                                                          ".",
                                                                        )[1]
                                                                          .length >
                                                                          2
                                                                      ) {
                                                                        e.preventDefault();
                                                                        return;
                                                                      }
                                                                    }}
                                                                    onChange={
                                                                      HandleRepeaterFieldChange
                                                                    }
                                                                  />
                                                                </span>
                                                                <span
                                                                  className="radio-text-padding"
                                                                  style={{
                                                                    display:
                                                                      "block",
                                                                  }}
                                                                >
                                                                  week(s)
                                                                </span>
                                                              </label>
                                                            </div>
                                                            <div className="">
                                                              <div className="option-without-input">
                                                                <label className="option-fbox">
                                                                  <input
                                                                    type="radio"
                                                                    id={idx}
                                                                    name={
                                                                      "elr_dose_freq_rbl" +
                                                                      idx
                                                                    }
                                                                    value={99}
                                                                    checked={
                                                                      data.elr_dose_freq_rbl ===
                                                                      99
                                                                    }
                                                                    onChange={
                                                                      HandleRepeaterFieldChange
                                                                    }
                                                                  />

                                                                  <span className="radio-text-padding">
                                                                    Unknown
                                                                  </span>
                                                                </label>
                                                              </div>
                                                            </div>
                                                          </div>
                                                        )}
                                                        <ErrorField
                                                          show={
                                                            data.elr_dose_weekly_freq_hard_error
                                                          }
                                                          message={
                                                            ELCharacteristics_MSGS.doseFrequencyEmpty
                                                          }
                                                          style={{
                                                            paddingLeft: "0px",
                                                          }}
                                                        />
                                                        <ErrorField
                                                          show={
                                                            data.elr_dose_weekly_strength_mg_kgHard
                                                          }
                                                          message={
                                                            ELCharacteristics_MSGS.doseFrequencyValueEmpty
                                                          }
                                                          style={{
                                                            paddingLeft: "0px",
                                                          }}
                                                        />
                                                        <ErrorField
                                                          show={
                                                            data.elr_dose_weekly_freq_RangeError
                                                          }
                                                          message={
                                                            ELCharacteristics_MSGS.DecimalRange
                                                          }
                                                          style={{
                                                            paddingLeft: "0px",
                                                          }}
                                                        />
                                                      </div>
                                                    )}
                                                  </td>
                                                </tr>
                                              </tbody>
                                            </table>
                                          </div>
                                        </div>
                                      </div>
                                    ))}

                                {elrexvayliDetails &&
                                  elrexvayliDetails.length > 0 &&
                                  elrexvayliDetails.length < 550 && (
                                    <div className="sub-question-bot-sapce-ul mt-2">
                                      <div
                                        className="answer-list-text"
                                        onClick={AddElrexvayliRow}
                                      >
                                        <a>
                                          <img
                                            width="18px"
                                            src="../Assets/images/plus.png"
                                            alt=""
                                          />
                                        </a>
                                        <a className="radio-text-padding">
                                          Click here to add another dose
                                          administration of elranatamab
                                        </a>
                                      </div>
                                    </div>
                                  )}
                              </div>
                              {/* 36.1 Elrex start */}
                              <div>
                                <div className="dependent-section sub-question marginleft15">
                                  <div
                                    className="question question-weight"
                                    style={{ marginBottom: "10px" }}
                                  >
                                    <span>Q36.1.</span>
                                    <span className="quest-text-pad">
                                      Please list all hospitalizations that
                                      occurred during the{" "}
                                      <b>step-up dosing phase</b> (i.e.,
                                      hospitalizations where the patient was
                                      admitted between the date of{" "}
                                      <i>Step-up Dose 1</i> and the date of the{" "}
                                      <i>
                                        1<sup>st</sup> Subsequent Treatment Dose
                                      </i>{" "}
                                      reported in Q36). Please specify the date
                                      of admission, date of discharge, and
                                      reason for hospitalization.{" "}
                                    </span>
                                  </div>
                                  <ErrorField
                                    show={elrInpatientHospMissing}
                                    message={
                                      ELCharacteristics_MSGS.ELREXFIO_INPATIENT_HOSP_MISSING
                                    }
                                    style={{ paddingLeft: "0px" }}
                                  />

                                  {elrHospDetails &&
                                    elrHospDetails.length > 0 &&
                                    elrHospDetails.map((data, idx) => (
                                      <div
                                        className="loop-section-ul"
                                        key={idx}
                                      >
                                        <div className="loop-head">
                                          <span
                                            className="question-weight"
                                            style={{ float: "inline-start" }}
                                          >
                                            {GetOrdinal(idx + 1)} ELREXFIO
                                            <sup>TM</sup> (elranatamab-bcmm)
                                            Hospitalization
                                          </span>

                                          {idx != 0 && (
                                            <div
                                              className="delete-btn-div"
                                              style={{ float: "inline-end" }}
                                            >
                                              <img
                                                width="16px"
                                                src="../Assets/images/Icon-material-delete.png"
                                                alt=""
                                                style={{ cursor: "pointer" }}
                                                onClick={() =>
                                                  DeleteElrHospRow(idx)
                                                }
                                              />
                                            </div>
                                          )}
                                        </div>

                                        {/* ELREXFIO® */}
                                        <div className="question-bot-sapce">
                                          <div className="sub-question grid-overflow">
                                            <table
                                              style={{
                                                border: "1px solid black",
                                                borderCollapse: "collapse",
                                                width: "100%",
                                              }}
                                            >
                                              <thead>
                                                <tr>
                                                  <th
                                                    style={{
                                                      border: "1px solid black",
                                                      padding: "8px",
                                                      textAlign: "center",
                                                      // width: "25%"
                                                    }}
                                                  >
                                                    Treatment
                                                  </th>
                                                  <th
                                                    style={{
                                                      border: "1px solid black",
                                                      padding: "8px",
                                                      textAlign: "center",
                                                      // width: "20%"
                                                    }}
                                                  >
                                                    Date of admission
                                                  </th>
                                                  {data.dt_admission != null &&
                                                    data.dt_admission !== 2 && (
                                                      <>
                                                        <th
                                                          style={{
                                                            border:
                                                              "1px solid black",
                                                            padding: "8px",
                                                            textAlign: "center",
                                                            // width: "20%"
                                                          }}
                                                        >
                                                          Date of discharge
                                                        </th>
                                                        <th
                                                          style={{
                                                            border:
                                                              "1px solid black",
                                                            padding: "8px",
                                                            textAlign: "center",
                                                            // width: "35%"
                                                          }}
                                                        >
                                                          Reason of
                                                          hospitalization
                                                        </th>
                                                      </>
                                                    )}
                                                </tr>
                                              </thead>

                                              <tbody>
                                                <tr>
                                                  <td
                                                    style={{
                                                      border: "1px solid black",
                                                      padding: "8px",
                                                    }}
                                                  >
                                                    <span className="quest-text-pad">
                                                      ELREXFIO<sup>TM</sup>{" "}
                                                      (elranatamab-bcmm)
                                                    </span>
                                                  </td>
                                                  <td
                                                    style={{
                                                      border: "1px solid black",
                                                      padding: "8px",
                                                    }}
                                                  >
                                                    <div>
                                                      <div
                                                        className="option-without-input"
                                                        style={{
                                                          display: "flex",
                                                        }}
                                                      >
                                                        <input
                                                          type="radio"
                                                          id={idx}
                                                          name={
                                                            "dt_admission_elr" +
                                                            idx
                                                          }
                                                          value={1}
                                                          checked={
                                                            data.dt_admission ===
                                                            1
                                                          }
                                                          onChange={
                                                            HandleRepeaterFieldChange
                                                          }
                                                        />
                                                        <span
                                                          className="radio-text-padding"
                                                          style={{
                                                            paddingLeft: "10px",
                                                            marginLeft: "-5px",
                                                          }}
                                                        >
                                                          <DatePickerComponent
                                                            ctrlId={
                                                              "dt_admission_dt_elr-" +
                                                              idx
                                                            }
                                                            HandleDateChange={
                                                              HandleRepeaterDateChange
                                                            }
                                                            // optionId={1}
                                                            minDate={dayjs(
                                                              "01-01-1920",
                                                            )}
                                                            maxDate={dayjs()}
                                                            locale="en" // current it is mm/dd/yyyy but for dd/mm/yyyy use "en-gb"
                                                            date={
                                                              data.dt_admission_dt
                                                            }
                                                          />
                                                        </span>
                                                      </div>
                                                      <div
                                                        className="option-without-input"
                                                        style={{
                                                          display: "flex",
                                                          alignItems:
                                                            "flex-start",
                                                        }}
                                                      >
                                                        <label className="option-fbox">
                                                          <input
                                                            type="radio"
                                                            style={{
                                                              marginTop: "6px",
                                                            }}
                                                            id={idx}
                                                            name={
                                                              "dt_admission_elr" +
                                                              idx
                                                            }
                                                            value={99}
                                                            checked={
                                                              data.dt_admission ===
                                                              99
                                                            }
                                                            onChange={
                                                              HandleRepeaterFieldChange
                                                            }
                                                          />
                                                          <span
                                                            className="radio-text-padding"
                                                            style={{
                                                              paddingLeft:
                                                                "10px",
                                                            }}
                                                          >
                                                            Date of admission
                                                            unknown
                                                          </span>
                                                        </label>
                                                      </div>
                                                      <div
                                                        className="option-without-input"
                                                        style={{
                                                          display: "flex",
                                                          alignItems:
                                                            "flex-start",
                                                        }}
                                                      >
                                                        <label className="option-fbox">
                                                          <input
                                                            type="radio"
                                                            style={{
                                                              marginTop: "6px",
                                                            }}
                                                            id={idx}
                                                            name={
                                                              "dt_admission_elr" +
                                                              idx
                                                            }
                                                            value={2}
                                                            checked={
                                                              data.dt_admission ===
                                                              2
                                                            }
                                                            onChange={
                                                              HandleRepeaterFieldChange
                                                            }
                                                          />
                                                          <span
                                                            className="radio-text-padding"
                                                            style={{
                                                              paddingLeft:
                                                                "10px",
                                                            }}
                                                          >
                                                            Patient had no
                                                            hospitalizations
                                                            during the step-up
                                                            dosing phase
                                                          </span>
                                                        </label>
                                                      </div>
                                                      <ErrorField
                                                        show={
                                                          data.dt_admission_Hard
                                                        }
                                                        message={
                                                          ELCharacteristics_MSGS.admissionEmpty_linvo
                                                        }
                                                        style={{
                                                          paddingLeft: "0px",
                                                        }}
                                                      />

                                                      <ErrorField
                                                        show={
                                                          data.dt_admission_Hard_dt
                                                        }
                                                        message={
                                                          ELCharacteristics_MSGS.admissionEmptyDate
                                                        }
                                                        style={{
                                                          paddingLeft: "0px",
                                                        }}
                                                      />

                                                      <ErrorField
                                                        show={
                                                          data.Invalid_admission_Hard_dt
                                                        }
                                                        message={
                                                          CommonError_Msgs.InvalidDate
                                                        }
                                                        style={{
                                                          paddingLeft: "0px",
                                                        }}
                                                      />
                                                      <ErrorField
                                                        show={
                                                          data.dt_admission_out_of_bound
                                                        }
                                                        message={
                                                          ELCharacteristics_MSGS.Q361DateOutOfBound
                                                        }
                                                        style={{
                                                          paddingLeft: "0px",
                                                        }}
                                                      />
                                                    </div>
                                                  </td>
                                                  {data.dt_admission != null &&
                                                    data.dt_admission !== 2 && (
                                                      <>
                                                        <td
                                                          style={{
                                                            border:
                                                              "1px solid black",
                                                            padding: "8px",
                                                          }}
                                                        >
                                                          <div>
                                                            <div
                                                              className="option-without-input"
                                                              style={{
                                                                display: "flex",
                                                              }}
                                                            >
                                                              <input
                                                                type="radio"
                                                                id={idx}
                                                                name={
                                                                  "dt_discharge_elr" +
                                                                  idx
                                                                }
                                                                value={1}
                                                                checked={
                                                                  data.dt_discharge ===
                                                                  1
                                                                }
                                                                onChange={
                                                                  HandleRepeaterFieldChange
                                                                }
                                                              />
                                                              <span
                                                                className="radio-text-padding"
                                                                style={{
                                                                  paddingLeft:
                                                                    "10px",
                                                                  marginLeft:
                                                                    "-5px",
                                                                }}
                                                              >
                                                                <DatePickerComponent
                                                                  ctrlId={
                                                                    "dt_discharge_dt_elr-" +
                                                                    idx
                                                                  }
                                                                  HandleDateChange={
                                                                    HandleRepeaterDateChange
                                                                  }
                                                                  // optionId={1}
                                                                  minDate={dayjs(
                                                                    "01-01-1920",
                                                                  )}
                                                                  maxDate={dayjs()}
                                                                  locale="en" // current it is mm/dd/yyyy but for dd/mm/yyyy use "en-gb"
                                                                  date={
                                                                    data.dt_discharge_dt
                                                                  }
                                                                />
                                                              </span>
                                                            </div>
                                                            <div
                                                              className="option-without-input"
                                                              style={{
                                                                display: "flex",
                                                                alignItems:
                                                                  "flex-start",
                                                              }}
                                                            >
                                                              <label className="option-fbox">
                                                                <input
                                                                  type="radio"
                                                                  style={{
                                                                    marginTop:
                                                                      "6px",
                                                                  }}
                                                                  id={idx}
                                                                  name={
                                                                    "dt_discharge_elr" +
                                                                    idx
                                                                  }
                                                                  value={99}
                                                                  checked={
                                                                    data.dt_discharge ===
                                                                    99
                                                                  }
                                                                  onChange={
                                                                    HandleRepeaterFieldChange
                                                                  }
                                                                />

                                                                <span
                                                                  className="radio-text-padding"
                                                                  style={{
                                                                    paddingLeft:
                                                                      "10px",
                                                                  }}
                                                                >
                                                                  Date of
                                                                  discharge
                                                                  unknown
                                                                </span>
                                                              </label>
                                                            </div>

                                                            <div
                                                              className="option-without-input"
                                                              style={{
                                                                display: "flex",
                                                                alignItems:
                                                                  "flex-start",
                                                              }}
                                                            >
                                                              <label className="option-fbox">
                                                                <input
                                                                  type="radio"
                                                                  style={{
                                                                    marginTop:
                                                                      "6px",
                                                                  }}
                                                                  id={idx}
                                                                  name={
                                                                    "dt_discharge_elr" +
                                                                    idx
                                                                  }
                                                                  value={2}
                                                                  checked={
                                                                    data.dt_discharge ===
                                                                    2
                                                                  }
                                                                  onChange={
                                                                    HandleRepeaterFieldChange
                                                                  }
                                                                />

                                                                <span
                                                                  className="radio-text-padding"
                                                                  style={{
                                                                    paddingLeft:
                                                                      "10px",
                                                                  }}
                                                                >
                                                                  Patient is
                                                                  still
                                                                  hospitalized
                                                                </span>
                                                              </label>
                                                            </div>
                                                            <ErrorField
                                                              show={
                                                                data.dt_discharge_Hard
                                                              }
                                                              message={
                                                                ELCharacteristics_MSGS.dischargeEmpty_linvo
                                                              }
                                                              style={{
                                                                paddingLeft:
                                                                  "0px",
                                                              }}
                                                            />
                                                            <ErrorField
                                                              show={
                                                                data.dt_discharge_Hard_dt
                                                              }
                                                              message={
                                                                ELCharacteristics_MSGS.dischargeEmptyDate
                                                              }
                                                              style={{
                                                                paddingLeft:
                                                                  "0px",
                                                              }}
                                                            />

                                                            <ErrorField
                                                              show={
                                                                data.dt_discharge_Hard_dt_check
                                                              }
                                                              message={
                                                                ELCharacteristics_MSGS.dischargeDateCheck
                                                              }
                                                              style={{
                                                                paddingLeft:
                                                                  "0px",
                                                              }}
                                                            />
                                                            <ErrorField
                                                              show={
                                                                data.dt_discharge_out_of_bound
                                                              }
                                                              message={
                                                                ELCharacteristics_MSGS.Q36DateOutOfBound
                                                              }
                                                              style={{
                                                                paddingLeft:
                                                                  "0px",
                                                              }}
                                                            />

                                                            <ErrorField
                                                              show={
                                                                data.dateRangeThirty
                                                              }
                                                              message={
                                                                ELCharacteristics_MSGS.DateRangeThirty
                                                              }
                                                              style={{
                                                                paddingLeft:
                                                                  "0px",
                                                              }}
                                                            />

                                                            <ErrorField
                                                              show={
                                                                data.Invalid_dt_discharge_Hard
                                                              }
                                                              message={
                                                                CommonError_Msgs.InvalidDate
                                                              }
                                                              style={{
                                                                paddingLeft:
                                                                  "0px",
                                                              }}
                                                            />
                                                            <ErrorField
                                                              show={
                                                                data.dt_discharge_before_eof
                                                              }
                                                              message={
                                                                ELCharacteristics_MSGS.DateBeforeEOF
                                                              }
                                                              style={{
                                                                paddingLeft:
                                                                  "0px",
                                                              }}
                                                            />
                                                          </div>
                                                        </td>
                                                        <td
                                                          style={{
                                                            border:
                                                              "1px solid black",
                                                            padding: "8px",
                                                          }}
                                                        >
                                                          <div>
                                                            <div className="">
                                                              <label className="long-text-option">
                                                                <input
                                                                  type="checkbox"
                                                                  id={idx}
                                                                  name={
                                                                    "hospReason1_elr" +
                                                                    idx
                                                                  }
                                                                  checked={
                                                                    data.hospReason1
                                                                  }
                                                                  onChange={
                                                                    HandleRepeaterFieldChange
                                                                  }
                                                                />
                                                                <span
                                                                  style={{
                                                                    marginLeft:
                                                                      "6px",
                                                                  }}
                                                                >
                                                                  Administration
                                                                  of{" "}
                                                                  {
                                                                    COHORT_TREATMENT_GENERIC_NAME_MAP[
                                                                      RRMMCohortTreatment
                                                                        .ELREXFIO
                                                                    ]
                                                                  }
                                                                </span>
                                                              </label>
                                                            </div>
                                                            <div className="">
                                                              <label className="long-text-option">
                                                                <input
                                                                  type="checkbox"
                                                                  id={idx}
                                                                  name={
                                                                    "hospReason2_elr" +
                                                                    idx
                                                                  }
                                                                  checked={
                                                                    data.hospReason2
                                                                  }
                                                                  onChange={
                                                                    HandleRepeaterFieldChange
                                                                  }
                                                                />
                                                                <span
                                                                  style={{
                                                                    marginLeft:
                                                                      "6px",
                                                                  }}
                                                                >
                                                                  Monitoring of
                                                                  adverse event
                                                                  (AE) as part
                                                                  of the
                                                                  treatment
                                                                  plan{" "}
                                                                </span>
                                                              </label>
                                                            </div>
                                                            <div className="">
                                                              <label className="long-text-option">
                                                                <input
                                                                  type="checkbox"
                                                                  id={idx}
                                                                  name={
                                                                    "hospReason3_elr" +
                                                                    idx
                                                                  }
                                                                  checked={
                                                                    data.hospReason3
                                                                  }
                                                                  onChange={
                                                                    HandleRepeaterFieldChange
                                                                  }
                                                                />
                                                                <span
                                                                  style={{
                                                                    marginLeft:
                                                                      "6px",
                                                                  }}
                                                                >
                                                                  Management of
                                                                  cytokine
                                                                  release
                                                                  syndrome (CRS)
                                                                </span>
                                                              </label>
                                                            </div>
                                                            <div className="">
                                                              <label className="long-text-option">
                                                                <input
                                                                  type="checkbox"
                                                                  id={idx}
                                                                  name={
                                                                    "hospReason4_elr" +
                                                                    idx
                                                                  }
                                                                  checked={
                                                                    data.hospReason4
                                                                  }
                                                                  onChange={
                                                                    HandleRepeaterFieldChange
                                                                  }
                                                                />
                                                                <span
                                                                  style={{
                                                                    marginLeft:
                                                                      "6px",
                                                                  }}
                                                                >
                                                                  Management of
                                                                  immune
                                                                  effector
                                                                  cell-associated
                                                                  neurotoxicity
                                                                  syndrome
                                                                  (ICANS)
                                                                </span>
                                                              </label>
                                                            </div>
                                                            <div className="">
                                                              <label className="long-text-option">
                                                                <input
                                                                  type="checkbox"
                                                                  id={idx}
                                                                  name={
                                                                    "hospReason5_elr" +
                                                                    idx
                                                                  }
                                                                  checked={
                                                                    data.hospReason5
                                                                  }
                                                                  onChange={
                                                                    HandleRepeaterFieldChange
                                                                  }
                                                                />
                                                                <span
                                                                  style={{
                                                                    marginLeft:
                                                                      "6px",
                                                                  }}
                                                                >
                                                                  Management of
                                                                  other AE
                                                                </span>
                                                              </label>
                                                            </div>
                                                            <div className="">
                                                              <label className="long-text-option">
                                                                <input
                                                                  type="checkbox"
                                                                  id={idx}
                                                                  name={
                                                                    "hospReason7_elr" +
                                                                    idx
                                                                  }
                                                                  checked={
                                                                    data.hospReason7
                                                                  }
                                                                  onChange={
                                                                    HandleRepeaterFieldChange
                                                                  }
                                                                />
                                                                <span
                                                                  style={{
                                                                    marginLeft:
                                                                      "6px",
                                                                  }}
                                                                >
                                                                  Administration
                                                                  of daratumumab
                                                                </span>
                                                              </label>
                                                            </div>
                                                            <div
                                                              className="option-without-input"
                                                              style={{
                                                                display: "flex",
                                                              }}
                                                            >
                                                              <label className="option-fbox">
                                                                <input
                                                                  type="checkbox"
                                                                  id={idx}
                                                                  name={
                                                                    "hospReason6_elr" +
                                                                    idx
                                                                  }
                                                                  checked={
                                                                    data.hospReason6
                                                                  }
                                                                  onChange={
                                                                    HandleRepeaterFieldChange
                                                                  }
                                                                />
                                                                <span
                                                                  style={{
                                                                    marginLeft:
                                                                      "4px",
                                                                    paddingRight:
                                                                      "15px",
                                                                    marginBottom:
                                                                      "0px",
                                                                    padding:
                                                                      "0",
                                                                  }}
                                                                >
                                                                  Other non-AE
                                                                  related
                                                                  reasons,
                                                                  please
                                                                  specify:
                                                                  <div
                                                                    style={{
                                                                      paddingLeft:
                                                                        "15px",
                                                                      marginBottom:
                                                                        "0px",
                                                                      padding:
                                                                        "0",
                                                                    }}
                                                                  >
                                                                    <textarea
                                                                      name={
                                                                        "hosp_reason_oth_txt_elr" +
                                                                        idx
                                                                      }
                                                                      className="input-dash"
                                                                      type="text"
                                                                      value={
                                                                        data.hosp_reason_oth_txt
                                                                      }
                                                                      onChange={
                                                                        HandleRepeaterFieldChange
                                                                      }
                                                                      maxLength={
                                                                        300
                                                                      }
                                                                      id={idx}
                                                                    />
                                                                  </div>
                                                                </span>
                                                              </label>
                                                            </div>
                                                            <div className="">
                                                              <label className="long-text-option">
                                                                <input
                                                                  type="radio"
                                                                  id={idx}
                                                                  name={
                                                                    "hospReasonUnk_elr" +
                                                                    idx
                                                                  }
                                                                  checked={
                                                                    data.hospReasonUnk
                                                                  }
                                                                  onChange={
                                                                    HandleRepeaterFieldChange
                                                                  }
                                                                />
                                                                <span
                                                                  style={{
                                                                    marginLeft:
                                                                      "6px",
                                                                  }}
                                                                >
                                                                  Unknown
                                                                </span>
                                                              </label>
                                                            </div>
                                                            <ErrorField
                                                              show={
                                                                data.hospReasonEmptyHard
                                                              }
                                                              message={
                                                                ELCharacteristics_MSGS.Q40ReasonEmptyHard
                                                              }
                                                              style={{
                                                                paddingLeft:
                                                                  "0px",
                                                              }}
                                                            />
                                                            <ErrorField
                                                              show={
                                                                data.hospReasonTxtEmpty
                                                              }
                                                              message={
                                                                ELCharacteristics_MSGS.Q36Point1ReasonTxtEmpty
                                                              }
                                                              style={{
                                                                paddingLeft:
                                                                  "0px",
                                                              }}
                                                            />
                                                          </div>
                                                        </td>
                                                      </>
                                                    )}
                                                </tr>
                                              </tbody>
                                            </table>
                                          </div>
                                        </div>
                                      </div>
                                    ))}

                                  {elrHospDetails &&
                                    elrHospDetails.length > 0 &&
                                    elrHospDetails.length < 550 &&
                                    elrHospDetails[elrHospDetails.length - 1]
                                      .dt_admission != null &&
                                    elrHospDetails[elrHospDetails.length - 1]
                                      .dt_admission !== 2 && (
                                      <div className="sub-question-bot-sapce-ul mt-2">
                                        <div
                                          className="answer-list-text"
                                          onClick={AddElrHospRow}
                                        >
                                          <a>
                                            <img
                                              width="18px"
                                              src="../Assets/images/plus.png"
                                              alt=""
                                            />
                                          </a>
                                          <a className="radio-text-padding">
                                            Click here to add another
                                            hospitalization of ELREXFIO
                                          </a>
                                        </div>
                                      </div>
                                    )}
                                </div>
                              </div>
                              {/* 36.1 ELREX end */}
                            </>
                          )}
                          {/* Table 2 Linvoseltamab */}

                          <div>
                            {isLinvoveySelected && (
                              <>
                                <div className="question question-weight">
                                  <span>Q36.</span>
                                  <span className="quest-text-pad">
                                    Provide{" "}
                                    <i>
                                      dosing information for each
                                      administration{" "}
                                    </i>
                                    of
                                    <>
                                      {" "}
                                      {isLinvoveySelected ? (
                                        <>
                                          <span
                                            style={{
                                              color:
                                                PageDesignConstants.fontColor,
                                              fontWeight: "bolder",
                                            }}
                                          >
                                            LYNOZYFIC
                                            <sup className="supscript">
                                              TM
                                            </sup>{" "}
                                            (linvoquetamab-gcpt)
                                          </span>{" "}
                                        </>
                                      ) : (
                                        ""
                                      )}{" "}
                                      during the step-up{" "}
                                      <span
                                        data-tooltip-id="encounterTip"
                                        className="modal-popup"
                                        onClick={() =>
                                          setShowHideHyperlinkModalQ36Linvoseltamab(
                                            true,
                                          )
                                        }
                                      >
                                        dosing phase
                                      </span>
                                      {showHideHyperlinkModalQ36Linvoseltamab && (
                                        <InfoPopUp
                                          description={
                                            hyperlinkTextQ36Linvoseltamab
                                          }
                                          OkClick={() =>
                                            setShowHideHyperlinkModalQ36Linvoseltamab(
                                              false,
                                            )
                                          }
                                        />
                                      )}{" "}
                                      and the 1<sup>st</sup> subsequent
                                      treatment dose.
                                    </>
                                    <br />
                                    <br />
                                    The table below includes rows for each dose
                                    expected to be received. For Tal, patients
                                    may or may not have Step-Up Dose 3 depending
                                    on whether they follow a weekly or biweekly
                                    dosing schedule. If Step-Up Dose 3 is not
                                    applicable, please select{" "}
                                    <i>Patient did not receive dose.</i>
                                    <br />
                                    <br />
                                    <b>Please add additional rows if: </b>
                                    <br />
                                    <>
                                      - For Tal patients initiating under a
                                      biweekly dosing schedule where Step-Up
                                      Dose 3 was received
                                    </>
                                    <br />
                                    <>
                                      - A patient that repeated a step-up dose
                                      (i.e., due to toxicity or gaps between
                                      doses, the same step-up dose was received
                                      multiple times)
                                    </>
                                    <br />
                                    <>
                                      - A patient had multiple step-up dosing
                                      phases (including the respective 1
                                      <sup>st</sup> subsequent treatment dose
                                      after each step-up dosing phase, if
                                      received).
                                    </>
                                    <br />
                                    <b>
                                      As of 9/1/2026, the eMMpower study will no
                                      longer be collecting all subsequent
                                      maintenance doses. Only the 1<sup>st</sup>{" "}
                                      subsequent treatment dose after the
                                      step-up dosing phase is required.
                                    </b>
                                    <br />
                                    <br />
                                    <b>
                                      If the dosing phase is not documented,
                                      please infer the dosing phase using the
                                      relevant dosing label information provided
                                    </b>{" "}
                                    <i>
                                      (see Dosing Phase hyperlink for more
                                      information on dosing label).
                                    </i>
                                  </span>
                                </div>
                                {/* <ErrorField
                                  show={linvoNumberofAdministratorMatching}
                                  message={
                                    ELCharacteristics_MSGS.RRMMNotMatchingLinvo
                                  }
                                  style={{ paddingLeft: "0px" }}
                                /> */}

                                <div className="dependent-section">
                                  <ErrorField
                                    show={linvoDuplicateDates}
                                    message={
                                      "LINVOSELTAMAB cannot have duplicate date of dose administration."
                                    }
                                    style={{ paddingLeft: "0px" }}
                                  />

                                  <ErrorField
                                    show={linvoDuplicatePhaseWarning}
                                    message={
                                      <>
                                        WARNING: you have entered duplicate dose
                                        administrations with the same step-up
                                        dosing phase (i.e., at least two rows
                                        where{" "}
                                        <i>
                                          Step-up dose 1, Step-up dose 2, or 1st
                                          treatment dose
                                        </i>{" "}
                                        have been recorded). The likelihood of
                                        this occurrence is considered rare and
                                        should occur only if multiple step-up
                                        doses occurred. If the patient did not
                                        experience multiple step-up doses,
                                        please revise accordingly.
                                      </>
                                    }
                                    style={{ paddingLeft: "0px" }}
                                  />
                                  <ErrorField
                                    show={linvoMissingStepUpWarning}
                                    message={
                                      ELCharacteristics_MSGS.StepUpWarning
                                    }
                                    style={{ paddingLeft: "0px" }}
                                  />

                                  <ErrorField
                                    show={!!setupLinvoDose3_WarningHard}
                                    message={setupLinvoDose3_WarningHard}
                                    style={{ paddingLeft: "0px" }}
                                  />

                                  <ErrorField
                                    show={!!setupLinvoDose1st_WarningHard}
                                    message={setupLinvoDose1st_WarningHard}
                                    style={{ paddingLeft: "0px" }}
                                  />

                                  <ErrorField
                                    show={!!setupLinvoDose1stSubs_WarningHard}
                                    message={setupLinvoDose1stSubs_WarningHard}
                                    style={{ paddingLeft: "0px" }}
                                  />

                                  <ErrorField
                                    show={setupLinvoQ26_WarningHard}
                                    message={ELCharacteristics_MSGS.Q26Warning}
                                    style={{ paddingLeft: "0px" }}
                                  />

                                  <ErrorField
                                    show={setupLinvo_WarningHard}
                                    message={
                                      ELCharacteristics_MSGS.DuplicateStepUpNotAllowed
                                    }
                                    style={{ paddingLeft: "0px" }}
                                  />

                                  <ErrorField
                                    show={dt_lino_2L_3L_date_conditions}
                                    message={
                                      ELCharacteristics_MSGS.Q362L3LErrorMessage
                                    }
                                    style={{
                                      paddingLeft: "0px",
                                    }}
                                  />

                                  <ErrorField
                                    show={indexDateRRMM_linvoHard}
                                    message={
                                      ELCharacteristics_MSGS.indexDateRRMMNotMatchingLinvo
                                    }
                                    style={{
                                      paddingLeft: "0px",
                                      paddingBottom: "2px",
                                    }}
                                  />
                                  <ErrorField
                                    show={!isLinvoLastAdminDateValid}
                                    message={
                                      ELCharacteristics_MSGS.DoseMustMatchLastAdministrationDateLinvo
                                    }
                                    style={{
                                      paddingLeft: "0px",
                                      paddingBottom: "2px",
                                    }}
                                  />

                                  {linvoveyDetails &&
                                    linvoveyDetails.length > 0 &&
                                    linvoveyDetails
                                      .map((item, idx) => ({ item, idx }))
                                      .filter(({ item }, index, arr) => {
                                        if (
                                          [1, 2, 3, 4, 5, null].includes(
                                            item.dosing_phase_linvo,
                                          )
                                        ) {
                                          return true;
                                        }

                                        return false;
                                      })
                                      .map(({ item: data, idx }, pos) => (
                                        <div
                                          className="loop-section-ul"
                                          key={idx}
                                        >
                                          <div className="loop-head">
                                            <span
                                              className="question-weight"
                                              style={{ float: "inline-start" }}
                                            >
                                              {GetOrdinal(idx + 1)} LYNOZYFIC{" "}
                                              <sup className="supscript">
                                                TM
                                              </sup>{" "}
                                              (linvoquetamab-gcpt)
                                            </span>

                                            {idx > 4 && (
                                              <div
                                                className="delete-btn-div"
                                                style={{ float: "inline-end" }}
                                              >
                                                <img
                                                  width="16px"
                                                  src="../Assets/images/Icon-material-delete.png"
                                                  alt=""
                                                  style={{ cursor: "pointer" }}
                                                  onClick={() =>
                                                    DeleteLinvoveyRow(idx)
                                                  }
                                                />
                                              </div>
                                            )}
                                          </div>

                                          {/* ELREXFIO® */}
                                          <div className="question-bot-sapce">
                                            <div className="sub-question grid-overflow">
                                              <table
                                                style={{
                                                  border: "1px solid black",
                                                  borderCollapse: "collapse",
                                                  width: "100%",
                                                }}
                                              >
                                                <thead>
                                                  <tr>
                                                    <th
                                                      style={{
                                                        border:
                                                          "1px solid black",
                                                        padding: "8px",
                                                        textAlign: "center",
                                                      }}
                                                    >
                                                      Treatment
                                                    </th>
                                                    <th
                                                      style={{
                                                        border:
                                                          "1px solid black",
                                                        padding: "8px",
                                                        textAlign: "center",
                                                        width: "20%",
                                                      }}
                                                    >
                                                      {" "}
                                                      <span
                                                        data-tooltip-id="encounterTip"
                                                        className="modal-popup"
                                                        onClick={() =>
                                                          setShowHideHyperlinkModalQ36Linvoseltamab(
                                                            true,
                                                          )
                                                        }
                                                      >
                                                        Dosing phase
                                                      </span>
                                                      {showHideHyperlinkModalQ36Linvoseltamab && (
                                                        <InfoPopUp
                                                          description={
                                                            hyperlinkTextQ36Linvoseltamab
                                                          }
                                                          OkClick={() =>
                                                            setShowHideHyperlinkModalQ36Linvoseltamab(
                                                              false,
                                                            )
                                                          }
                                                        />
                                                      )}{" "}
                                                    </th>
                                                    <th
                                                      style={{
                                                        border:
                                                          "1px solid black",
                                                        padding: "8px",
                                                        textAlign: "center",
                                                      }}
                                                    >
                                                      Date of dose
                                                      administration
                                                    </th>
                                                    <th
                                                      style={{
                                                        border:
                                                          "1px solid black",
                                                        padding: "8px",
                                                        textAlign: "center",
                                                      }}
                                                    >
                                                      Site of care
                                                    </th>

                                                    <th
                                                      style={{
                                                        border:
                                                          "1px solid black",
                                                        padding: "8px",
                                                        textAlign: "center",
                                                        minWidth: "280px",
                                                      }}
                                                    >
                                                      Dose strength
                                                    </th>
                                                    {/* {data.Q36TalConditionMet && (
                                                <>
                                                  <th
                                                    style={{
                                                      border: "1px solid black",
                                                      padding: "8px",
                                                      textAlign: "center",
                                                    }}
                                                  >
                                                    Date of inpatient admission
                                                    for this administration
                                                  </th>
                                                  <th
                                                    style={{
                                                      border: "1px solid black",
                                                      padding: "8px",
                                                      textAlign: "center",
                                                    }}
                                                  >
                                                    Date of inpatient discharge
                                                    for this administration
                                                  </th>
                                                </>
                                              )} */}
                                                  </tr>
                                                </thead>

                                                <tbody>
                                                  <tr>
                                                    <td
                                                      style={{
                                                        border:
                                                          "1px solid black",
                                                        padding: "8px",
                                                      }}
                                                    >
                                                      <span className="quest-text-pad">
                                                        LYNOZYFIC<sup>TM</sup>{" "}
                                                        (linvoquetamab-gcpt)
                                                      </span>
                                                    </td>

                                                    <td
                                                      style={{
                                                        border:
                                                          "1px solid black",
                                                        padding: "8px",
                                                      }}
                                                    >
                                                      <div>
                                                        {idx === 1 ||
                                                        idx === 2 ||
                                                        idx === 3 ||
                                                        idx === 4 ? (
                                                          ""
                                                        ) : (
                                                          <div
                                                            className="option-without-input"
                                                            style={{
                                                              display: "flex",
                                                            }}
                                                          >
                                                            <label className="option-fbox">
                                                              <input
                                                                type="radio"
                                                                id={idx}
                                                                name={
                                                                  idx === 0
                                                                    ? true
                                                                    : "dosing_phase_linvo" +
                                                                      idx
                                                                }
                                                                value={1}
                                                                checked={
                                                                  idx === 0
                                                                    ? true
                                                                    : data.dosing_phase_linvo ===
                                                                      1
                                                                }
                                                                onChange={
                                                                  HandleRepeaterFieldChange
                                                                }
                                                              />
                                                              <span
                                                                // className="radio-text-padding"
                                                                style={{
                                                                  paddingLeft:
                                                                    "10px",
                                                                }}
                                                              >
                                                                Step-up dose
                                                                1{" "}
                                                              </span>
                                                            </label>
                                                          </div>
                                                        )}

                                                        {idx === 0 ||
                                                        idx === 2 ||
                                                        idx === 3 ||
                                                        idx === 4 ? (
                                                          ""
                                                        ) : (
                                                          <div
                                                            className="option-without-input"
                                                            style={{
                                                              display: "flex",
                                                              alignItems:
                                                                "flex-start",
                                                            }}
                                                          >
                                                            <label className="option-fbox">
                                                              <input
                                                                type="radio"
                                                                style={{
                                                                  marginTop:
                                                                    "6px",
                                                                }}
                                                                id={idx}
                                                                name={
                                                                  idx === 1
                                                                    ? true
                                                                    : "dosing_phase_linvo" +
                                                                      idx
                                                                }
                                                                value={2}
                                                                checked={
                                                                  idx === 1
                                                                    ? true
                                                                    : data.dosing_phase_linvo ===
                                                                      2
                                                                }
                                                                onChange={
                                                                  HandleRepeaterFieldChange
                                                                }
                                                              />

                                                              <span
                                                                // className="radio-text-padding"
                                                                style={{
                                                                  paddingLeft:
                                                                    "10px",
                                                                }}
                                                              >
                                                                Step-up dose 2
                                                              </span>
                                                            </label>
                                                          </div>
                                                        )}

                                                        {idx === 0 ||
                                                        idx === 1 ||
                                                        idx === 3 ||
                                                        idx === 4 ? (
                                                          ""
                                                        ) : (
                                                          <div
                                                            className="option-without-input"
                                                            style={{
                                                              display: "flex",
                                                            }}
                                                          >
                                                            <label className="option-fbox">
                                                              <input
                                                                type="radio"
                                                                id={idx}
                                                                name={
                                                                  idx === 2
                                                                    ? true
                                                                    : "dosing_phase_linvo" +
                                                                      idx
                                                                }
                                                                value={3}
                                                                checked={
                                                                  idx === 2
                                                                    ? true
                                                                    : data.dosing_phase_linvo ===
                                                                      3
                                                                }
                                                                onChange={
                                                                  HandleRepeaterFieldChange
                                                                }
                                                              />
                                                              <span
                                                                className="radio-text-padding"
                                                                style={{
                                                                  paddingLeft:
                                                                    "10px",
                                                                }}
                                                              >
                                                                Step-up dose
                                                                3{" "}
                                                              </span>
                                                            </label>
                                                          </div>
                                                        )}
                                                        {idx === 0 ||
                                                        idx === 1 ||
                                                        idx === 2 ||
                                                        idx === 4 ? (
                                                          ""
                                                        ) : (
                                                          <div
                                                            className="option-without-input"
                                                            style={{
                                                              display: "flex",
                                                              alignItems:
                                                                "flex-start",
                                                            }}
                                                          >
                                                            <label className="option-fbox">
                                                              <input
                                                                type="radio"
                                                                style={{
                                                                  marginTop:
                                                                    "6px",
                                                                }}
                                                                id={idx}
                                                                name={
                                                                  idx === 3
                                                                    ? true
                                                                    : "dosing_phase_linvo" +
                                                                      idx
                                                                }
                                                                value={4}
                                                                checked={
                                                                  idx === 3
                                                                    ? true
                                                                    : data.dosing_phase_linvo ===
                                                                      4
                                                                }
                                                                onChange={
                                                                  HandleRepeaterFieldChange
                                                                }
                                                              />

                                                              <span
                                                                // className="radio-text-padding"
                                                                style={{
                                                                  paddingLeft:
                                                                    "10px",
                                                                }}
                                                              >
                                                                1<sup>st</sup>{" "}
                                                                treatment dose
                                                              </span>
                                                            </label>
                                                          </div>
                                                        )}
                                                        {idx === 0 ||
                                                        idx === 1 ||
                                                        idx === 2 ||
                                                        idx === 3 ? (
                                                          ""
                                                        ) : (
                                                          <div
                                                            className="option-without-input"
                                                            style={{
                                                              display: "flex",
                                                              alignItems:
                                                                "flex-start",
                                                            }}
                                                          >
                                                            <label className="option-fbox">
                                                              <input
                                                                type="radio"
                                                                style={{
                                                                  marginTop:
                                                                    "6px",
                                                                }}
                                                                id={idx}
                                                                name={
                                                                  idx === 4
                                                                    ? true
                                                                    : "dosing_phase_linvo" +
                                                                      idx
                                                                }
                                                                value={5}
                                                                checked={
                                                                  idx === 4
                                                                    ? true
                                                                    : data.dosing_phase_linvo ===
                                                                      5
                                                                }
                                                                onChange={
                                                                  HandleRepeaterFieldChange
                                                                }
                                                              />

                                                              <span
                                                                // className="radio-text-padding"
                                                                style={{
                                                                  paddingLeft:
                                                                    "10px",
                                                                }}
                                                              >
                                                                1<sup>st</sup>{" "}
                                                                subsequent
                                                                treatment dose
                                                              </span>
                                                            </label>
                                                          </div>
                                                        )}

                                                        <ErrorField
                                                          show={
                                                            data.dosing_phase_linvoHard
                                                          }
                                                          message={
                                                            ELCharacteristics_MSGS.dosingphaseEmpty_linvo
                                                          }
                                                          style={{
                                                            paddingLeft: "0px",
                                                          }}
                                                        />

                                                        <ErrorField
                                                          show={
                                                            data.dosing_phase_linvoHard_othEmpty
                                                          }
                                                          message={
                                                            "Please enter the text"
                                                          }
                                                          style={{
                                                            paddingLeft: "0px",
                                                          }}
                                                        />
                                                      </div>
                                                    </td>

                                                    <td
                                                      style={{
                                                        border:
                                                          "1px solid black",
                                                        padding: "8px",
                                                      }}
                                                    >
                                                      {idx == 0 ? (
                                                        <>
                                                          {" "}
                                                          {
                                                            linvoInitiationDate
                                                          }{" "}
                                                        </>
                                                      ) : (
                                                        <div>
                                                          <div
                                                            className="option-without-input"
                                                            style={{
                                                              display: "flex",
                                                            }}
                                                          >
                                                            <input
                                                              type="radio"
                                                              id={idx}
                                                              name={
                                                                "dose_administration_linvo" +
                                                                idx
                                                              }
                                                              value={1}
                                                              checked={
                                                                data.dose_administration_linvo ===
                                                                1
                                                              }
                                                              onChange={
                                                                HandleRepeaterFieldChange
                                                              }
                                                            />
                                                            <span
                                                              className="radio-text-padding"
                                                              style={{
                                                                paddingLeft:
                                                                  "10px",
                                                                marginLeft:
                                                                  "-5px",
                                                              }}
                                                            >
                                                              <DatePickerComponent
                                                                ctrlId={
                                                                  "dose_administration_linvo_dt-" +
                                                                  idx
                                                                }
                                                                HandleDateChange={
                                                                  HandleRepeaterDateChange
                                                                }
                                                                // optionId={1}
                                                                minDate={dayjs(
                                                                  "01-01-1920",
                                                                )}
                                                                maxDate={dayjs()}
                                                                locale="en" // current it is mm/dd/yyyy but for dd/mm/yyyy use "en-gb"
                                                                date={
                                                                  data.dose_administration_linvo_dt
                                                                }
                                                              />
                                                            </span>
                                                          </div>
                                                          <div
                                                            className="option-without-input"
                                                            style={{
                                                              display: "flex",
                                                              alignItems:
                                                                "flex-start",
                                                            }}
                                                          >
                                                            <label className="option-fbox">
                                                              <input
                                                                type="radio"
                                                                style={{
                                                                  marginTop:
                                                                    "6px",
                                                                }}
                                                                id={idx}
                                                                name={
                                                                  "dose_administration_linvo" +
                                                                  idx
                                                                }
                                                                value={99}
                                                                checked={
                                                                  data.dose_administration_linvo ===
                                                                  99
                                                                }
                                                                onChange={
                                                                  HandleRepeaterFieldChange
                                                                }
                                                              />
                                                              <span
                                                                // className="radio-text-padding"
                                                                style={{
                                                                  paddingLeft:
                                                                    "10px",
                                                                }}
                                                              >
                                                                Date of dose
                                                                administration
                                                                unknown
                                                              </span>
                                                            </label>
                                                          </div>
                                                          {!data.isManuallyAdded && (
                                                            <div
                                                              className="option-without-input"
                                                              style={{
                                                                display: "flex",
                                                                alignItems:
                                                                  "flex-start",
                                                              }}
                                                            >
                                                              <label className="option-fbox">
                                                                <input
                                                                  type="radio"
                                                                  style={{
                                                                    marginTop:
                                                                      "6px",
                                                                  }}
                                                                  id={idx}
                                                                  name={
                                                                    "dose_administration_linvo" +
                                                                    idx
                                                                  }
                                                                  value={2}
                                                                  checked={
                                                                    data.dose_administration_linvo ===
                                                                    2
                                                                  }
                                                                  onChange={
                                                                    HandleRepeaterFieldChange
                                                                  }
                                                                />
                                                                <span
                                                                  // className="radio-text-padding"
                                                                  style={{
                                                                    paddingLeft:
                                                                      "10px",
                                                                  }}
                                                                >
                                                                  Patient did
                                                                  not receive
                                                                  dose
                                                                </span>
                                                              </label>
                                                            </div>
                                                          )}
                                                          <ErrorField
                                                            show={
                                                              data.dose_administration_linvoHard
                                                            }
                                                            message={
                                                              ELCharacteristics_MSGS.administrationEmpty_linvo
                                                            }
                                                            style={{
                                                              paddingLeft:
                                                                "0px",
                                                            }}
                                                          />
                                                          <ErrorField
                                                            show={
                                                              data.dose_administration_linvoHard_dt
                                                            }
                                                            message={
                                                              ELCharacteristics_MSGS.administrationEmptyDate
                                                            }
                                                            style={{
                                                              paddingLeft:
                                                                "0px",
                                                            }}
                                                          />

                                                          <ErrorField
                                                            show={
                                                              data.dt_linvo_date_out_of_bound
                                                            }
                                                            message={
                                                              ELCharacteristics_MSGS.Q36DateOutOfBound
                                                            }
                                                            style={{
                                                              paddingLeft:
                                                                "0px",
                                                            }}
                                                          />

                                                          <ErrorField
                                                            show={
                                                              data.Invalid_dose_administration_linvo_dt
                                                            }
                                                            message={
                                                              CommonError_Msgs.InvalidDate
                                                            }
                                                            style={{
                                                              paddingLeft:
                                                                "0px",
                                                            }}
                                                          />
                                                        </div>
                                                      )}
                                                    </td>
                                                    <td
                                                      style={{
                                                        border:
                                                          "1px solid black",
                                                        padding: "8px",
                                                      }}
                                                    >
                                                      {data.dose_administration_linvo ===
                                                      2 ? (
                                                        <b>Not applicable</b>
                                                      ) : (
                                                        <div>
                                                          <div
                                                            className="option-without-input"
                                                            style={{
                                                              display: "flex",
                                                            }}
                                                          >
                                                            <label className="option-fbox">
                                                              <input
                                                                type="radio"
                                                                id={idx}
                                                                name={
                                                                  "siteofcare_linvo" +
                                                                  idx
                                                                }
                                                                value={1}
                                                                checked={
                                                                  data.siteofcare_linvo ===
                                                                  1
                                                                }
                                                                onChange={
                                                                  HandleRepeaterFieldChange
                                                                }
                                                              />
                                                              <span
                                                                // className="radio-text-padding"
                                                                style={{
                                                                  paddingLeft:
                                                                    "10px",
                                                                }}
                                                              >
                                                                Inpatient{" "}
                                                              </span>
                                                            </label>
                                                          </div>
                                                          <div
                                                            className="option-without-input"
                                                            style={{
                                                              display: "flex",
                                                              alignItems:
                                                                "flex-start",
                                                            }}
                                                          >
                                                            <label className="option-fbox">
                                                              <input
                                                                type="radio"
                                                                style={{
                                                                  marginTop:
                                                                    "6px",
                                                                }}
                                                                id={idx}
                                                                name={
                                                                  "siteofcare_linvo" +
                                                                  idx
                                                                }
                                                                value={2}
                                                                checked={
                                                                  data.siteofcare_linvo ===
                                                                  2
                                                                }
                                                                onChange={
                                                                  HandleRepeaterFieldChange
                                                                }
                                                              />

                                                              <span
                                                                // className="radio-text-padding"
                                                                style={{
                                                                  paddingLeft:
                                                                    "10px",
                                                                }}
                                                              >
                                                                Outpatient
                                                              </span>
                                                            </label>
                                                          </div>

                                                          <ErrorField
                                                            show={
                                                              data.siteofcare_linvoHard
                                                            }
                                                            message={
                                                              ELCharacteristics_MSGS.siteofcareEmpty_linvo
                                                            }
                                                            style={{
                                                              paddingLeft:
                                                                "0px",
                                                            }}
                                                          />
                                                        </div>
                                                      )}
                                                    </td>

                                                    <td
                                                      style={{
                                                        border:
                                                          "1px solid black",
                                                        padding: "8px",
                                                      }}
                                                    >
                                                      {data.dose_administration_linvo ===
                                                      2 ? (
                                                        <b>Not applicable</b>
                                                      ) : pos === 0 ? (
                                                        <>
                                                          {" "}
                                                          {
                                                            doseStrength_Linvo
                                                          }{" "}
                                                        </>
                                                      ) : (
                                                        <div>
                                                          {data.dosing_phase_linvo ===
                                                            5 && (
                                                            <b>
                                                              Initial dose
                                                              strength
                                                              immediately
                                                              following step-up
                                                              dosing phase:
                                                            </b>
                                                          )}
                                                          <div className="option-fbox">
                                                            <label>
                                                              <input
                                                                type="radio"
                                                                id={idx}
                                                                name={
                                                                  "linvo_dose_strength_rbl" +
                                                                  idx
                                                                }
                                                                value={1}
                                                                checked={
                                                                  data.linvo_dose_strength_rbl ===
                                                                  1
                                                                }
                                                                onChange={
                                                                  HandleRepeaterFieldChange
                                                                }
                                                              />
                                                            </label>
                                                            <label>
                                                              <span className="radio-text-padding">
                                                                <input
                                                                  className="input-dash-custom"
                                                                  id={idx}
                                                                  name={
                                                                    "linvo_dose_strength_mg" +
                                                                    idx
                                                                  }
                                                                  value={
                                                                    data.linvo_dose_strength_mg
                                                                  }
                                                                  type="text"
                                                                  onKeyDown={(
                                                                    e,
                                                                  ) => {
                                                                    // Allow backspace, arrow keys, and delete
                                                                    if (
                                                                      [
                                                                        "Backspace",
                                                                        "ArrowLeft",
                                                                        "ArrowRight",
                                                                        "Delete",
                                                                        "Tab",
                                                                      ].includes(
                                                                        e.key,
                                                                      )
                                                                    ) {
                                                                      return;
                                                                    }

                                                                    // Prevent non-numeric characters, except '.'
                                                                    if (
                                                                      !/[0-9.]/.test(
                                                                        e.key,
                                                                      )
                                                                    ) {
                                                                      e.preventDefault();
                                                                      return;
                                                                    }

                                                                    const value =
                                                                      e.target
                                                                        .value;
                                                                    const selectionStart =
                                                                      e.target
                                                                        .selectionStart;
                                                                    const newValue =
                                                                      value.slice(
                                                                        0,
                                                                        selectionStart,
                                                                      ) +
                                                                      e.key +
                                                                      value.slice(
                                                                        e.target
                                                                          .selectionEnd,
                                                                      );

                                                                    // Prevent multiple '.'
                                                                    if (
                                                                      e.key ===
                                                                        "." &&
                                                                      value.includes(
                                                                        ".",
                                                                      )
                                                                    ) {
                                                                      e.preventDefault();
                                                                      return;
                                                                    }

                                                                    // Prevent '.' as the first character
                                                                    if (
                                                                      e.key ===
                                                                        "." &&
                                                                      value ===
                                                                        ""
                                                                    ) {
                                                                      e.preventDefault();
                                                                      return;
                                                                    }

                                                                    // Allow only one digit after decimal point
                                                                    if (
                                                                      newValue.includes(
                                                                        ".",
                                                                      ) &&
                                                                      newValue.split(
                                                                        ".",
                                                                      )[1]
                                                                        .length >
                                                                        2
                                                                    ) {
                                                                      e.preventDefault();
                                                                      return;
                                                                    }
                                                                  }}
                                                                  onChange={
                                                                    HandleRepeaterFieldChange
                                                                  }
                                                                />
                                                              </span>
                                                              mg
                                                            </label>
                                                          </div>
                                                          <br />
                                                          <div className="option-fbox">
                                                            <label>
                                                              <input
                                                                type="radio"
                                                                id={idx}
                                                                name={
                                                                  "linvo_dose_strength_rbl" +
                                                                  idx
                                                                }
                                                                value={2}
                                                                checked={
                                                                  data.linvo_dose_strength_rbl ===
                                                                  2
                                                                }
                                                                onChange={
                                                                  HandleRepeaterFieldChange
                                                                }
                                                              />
                                                            </label>
                                                            <label>
                                                              <span className="radio-text-padding">
                                                                <input
                                                                  className="input-dash-custom"
                                                                  id={idx}
                                                                  name={
                                                                    "linvo_dose_strength_mg_kg" +
                                                                    idx
                                                                  }
                                                                  value={
                                                                    data.linvo_dose_strength_mg_kg
                                                                  }
                                                                  type="text"
                                                                  onKeyDown={(
                                                                    e,
                                                                  ) => {
                                                                    // Allow backspace, arrow keys, and delete
                                                                    if (
                                                                      [
                                                                        "Backspace",
                                                                        "ArrowLeft",
                                                                        "ArrowRight",
                                                                        "Delete",
                                                                        "Tab",
                                                                      ].includes(
                                                                        e.key,
                                                                      )
                                                                    ) {
                                                                      return;
                                                                    }

                                                                    // Prevent non-numeric characters, except '.'
                                                                    if (
                                                                      !/[0-9.]/.test(
                                                                        e.key,
                                                                      )
                                                                    ) {
                                                                      e.preventDefault();
                                                                      return;
                                                                    }

                                                                    const value =
                                                                      e.target
                                                                        .value;
                                                                    const selectionStart =
                                                                      e.target
                                                                        .selectionStart;
                                                                    const newValue =
                                                                      value.slice(
                                                                        0,
                                                                        selectionStart,
                                                                      ) +
                                                                      e.key +
                                                                      value.slice(
                                                                        e.target
                                                                          .selectionEnd,
                                                                      );

                                                                    // Prevent multiple '.'
                                                                    if (
                                                                      e.key ===
                                                                        "." &&
                                                                      value.includes(
                                                                        ".",
                                                                      )
                                                                    ) {
                                                                      e.preventDefault();
                                                                      return;
                                                                    }

                                                                    // Prevent '.' as the first character
                                                                    if (
                                                                      e.key ===
                                                                        "." &&
                                                                      value ===
                                                                        ""
                                                                    ) {
                                                                      e.preventDefault();
                                                                      return;
                                                                    }

                                                                    // Allow only one digit after decimal point
                                                                    if (
                                                                      newValue.includes(
                                                                        ".",
                                                                      ) &&
                                                                      newValue.split(
                                                                        ".",
                                                                      )[1]
                                                                        .length >
                                                                        2
                                                                    ) {
                                                                      e.preventDefault();
                                                                      return;
                                                                    }
                                                                  }}
                                                                  onChange={
                                                                    HandleRepeaterFieldChange
                                                                  }
                                                                />
                                                              </span>
                                                              mg/kg
                                                            </label>
                                                          </div>
                                                          <br />
                                                          <div className="option-fbox">
                                                            <label>
                                                              <input
                                                                type="radio"
                                                                id={idx}
                                                                name={
                                                                  "linvo_dose_strength_rbl" +
                                                                  idx
                                                                }
                                                                value={3}
                                                                checked={
                                                                  data.linvo_dose_strength_rbl ===
                                                                  3
                                                                }
                                                                onChange={
                                                                  HandleRepeaterFieldChange
                                                                }
                                                              />
                                                            </label>
                                                            <label>
                                                              <span className="radio-text-padding">
                                                                <input
                                                                  className="input-dash-custom"
                                                                  id={idx}
                                                                  name={
                                                                    "linvo_dose_strength_mgm" +
                                                                    idx
                                                                  }
                                                                  value={
                                                                    data.linvo_dose_strength_mgm
                                                                  }
                                                                  type="text"
                                                                  onKeyDown={(
                                                                    e,
                                                                  ) => {
                                                                    // Allow backspace, arrow keys, and delete
                                                                    if (
                                                                      [
                                                                        "Backspace",
                                                                        "ArrowLeft",
                                                                        "ArrowRight",
                                                                        "Delete",
                                                                        "Tab",
                                                                      ].includes(
                                                                        e.key,
                                                                      )
                                                                    ) {
                                                                      return;
                                                                    }

                                                                    // Prevent non-numeric characters, except '.'
                                                                    if (
                                                                      !/[0-9.]/.test(
                                                                        e.key,
                                                                      )
                                                                    ) {
                                                                      e.preventDefault();
                                                                      return;
                                                                    }

                                                                    const value =
                                                                      e.target
                                                                        .value;
                                                                    const selectionStart =
                                                                      e.target
                                                                        .selectionStart;
                                                                    const newValue =
                                                                      value.slice(
                                                                        0,
                                                                        selectionStart,
                                                                      ) +
                                                                      e.key +
                                                                      value.slice(
                                                                        e.target
                                                                          .selectionEnd,
                                                                      );

                                                                    // Prevent multiple '.'
                                                                    if (
                                                                      e.key ===
                                                                        "." &&
                                                                      value.includes(
                                                                        ".",
                                                                      )
                                                                    ) {
                                                                      e.preventDefault();
                                                                      return;
                                                                    }

                                                                    // Prevent '.' as the first character
                                                                    if (
                                                                      e.key ===
                                                                        "." &&
                                                                      value ===
                                                                        ""
                                                                    ) {
                                                                      e.preventDefault();
                                                                      return;
                                                                    }

                                                                    // Allow only one digit after decimal point
                                                                    if (
                                                                      newValue.includes(
                                                                        ".",
                                                                      ) &&
                                                                      newValue.split(
                                                                        ".",
                                                                      )[1]
                                                                        .length >
                                                                        2
                                                                    ) {
                                                                      e.preventDefault();
                                                                      return;
                                                                    }
                                                                  }}
                                                                  onChange={
                                                                    HandleRepeaterFieldChange
                                                                  }
                                                                />
                                                              </span>
                                                              &nbsp; mg/m
                                                              <sup>2</sup>
                                                            </label>
                                                            {/*<label><span className="" style={{ marginLeft: '12px', textAlign: 'start' }}>(Range: 1 to 550)</span></label>*/}
                                                          </div>
                                                          <br />
                                                          <div className="">
                                                            <div className="option-without-input">
                                                              <label className="option-fbox">
                                                                <input
                                                                  type="radio"
                                                                  id={idx}
                                                                  name={
                                                                    "linvo_dose_strength_rbl" +
                                                                    idx
                                                                  }
                                                                  value={99}
                                                                  checked={
                                                                    data.linvo_dose_strength_rbl ===
                                                                    99
                                                                  }
                                                                  onChange={
                                                                    HandleRepeaterFieldChange
                                                                  }
                                                                />

                                                                <span className="radio-text-padding">
                                                                  Unknown
                                                                </span>
                                                              </label>
                                                            </div>
                                                          </div>
                                                          <ErrorField
                                                            show={
                                                              data.linvo_dose_freq_hard_error
                                                            }
                                                            message={
                                                              ELCharacteristics_MSGS.siteofcareEmpty
                                                            }
                                                            style={{
                                                              paddingLeft:
                                                                "0px",
                                                            }}
                                                          />

                                                          <ErrorField
                                                            show={
                                                              data.linvo_dose_strength_mg_kgHard
                                                            }
                                                            message={
                                                              ELCharacteristics_MSGS.DecimalRange
                                                            }
                                                            style={{
                                                              paddingLeft:
                                                                "0px",
                                                            }}
                                                          />

                                                          <ErrorField
                                                            show={
                                                              data.linvo_dose_strength_mg_kgRange
                                                            }
                                                            message={
                                                              ELCharacteristics_MSGS.DecimalRange
                                                            }
                                                            style={{
                                                              paddingLeft:
                                                                "0px",
                                                            }}
                                                          />

                                                          <br />
                                                          <br />
                                                          {data.dosing_phase_linvo ===
                                                            5 && (
                                                            <div>
                                                              <b>
                                                                Initial dose
                                                                frequency
                                                                immediately
                                                                following
                                                                step-up dosing
                                                                phase:
                                                              </b>

                                                              <div className="option-fbox">
                                                                <label>
                                                                  <input
                                                                    type="radio"
                                                                    id={idx}
                                                                    name={
                                                                      "linvo_dose_freq_rbl" +
                                                                      idx
                                                                    }
                                                                    value={1}
                                                                    checked={
                                                                      data.linvo_dose_freq_rbl ===
                                                                      1
                                                                    }
                                                                    onChange={
                                                                      HandleRepeaterFieldChange
                                                                    }
                                                                  />
                                                                </label>
                                                                <label>
                                                                  <span className="radio-text-padding">
                                                                    <input
                                                                      className="input-dash-custom"
                                                                      id={idx}
                                                                      name={
                                                                        "linvo_dose_freq_week" +
                                                                        idx
                                                                      }
                                                                      value={
                                                                        data.linvo_dose_freq_week
                                                                      }
                                                                      type="text"
                                                                      inputMode="numeric"
                                                                      onKeyDown={(
                                                                        e,
                                                                      ) => {
                                                                        // Allow control/navigation keys
                                                                        if (
                                                                          [
                                                                            "Backspace",
                                                                            "ArrowLeft",
                                                                            "ArrowRight",
                                                                            "Delete",
                                                                            "Tab",
                                                                            "Home",
                                                                            "End",
                                                                          ].includes(
                                                                            e.key,
                                                                          )
                                                                        ) {
                                                                          return;
                                                                        }

                                                                        // Allow only digits 0-9
                                                                        if (
                                                                          !/^[0-9]$/.test(
                                                                            e.key,
                                                                          )
                                                                        ) {
                                                                          e.preventDefault();
                                                                        }
                                                                      }}
                                                                      onChange={
                                                                        HandleRepeaterFieldChange
                                                                      }
                                                                    />
                                                                  </span>
                                                                  <span
                                                                    className="radio-text-padding"
                                                                    style={{
                                                                      display:
                                                                        "block",
                                                                    }}
                                                                  >
                                                                    times per
                                                                    week
                                                                  </span>
                                                                </label>
                                                              </div>
                                                              <div className="option-fbox">
                                                                <label>
                                                                  <input
                                                                    type="radio"
                                                                    id={idx}
                                                                    name={
                                                                      "linvo_dose_freq_rbl" +
                                                                      idx
                                                                    }
                                                                    value={2}
                                                                    checked={
                                                                      data.linvo_dose_freq_rbl ===
                                                                      2
                                                                    }
                                                                    onChange={
                                                                      HandleRepeaterFieldChange
                                                                    }
                                                                  />
                                                                </label>
                                                                <label>
                                                                  <span className="radio-text-padding">
                                                                    Once every
                                                                    &nbsp;
                                                                    <input
                                                                      className="input-dash-custom"
                                                                      id={idx}
                                                                      name={
                                                                        "linvo_dose_freq_onceweek" +
                                                                        idx
                                                                      }
                                                                      value={
                                                                        data.linvo_dose_freq_onceweek
                                                                      }
                                                                      type="text"
                                                                      inputMode="numeric"
                                                                      onKeyDown={(
                                                                        e,
                                                                      ) => {
                                                                        // Allow control/navigation keys
                                                                        if (
                                                                          [
                                                                            "Backspace",
                                                                            "ArrowLeft",
                                                                            "ArrowRight",
                                                                            "Delete",
                                                                            "Tab",
                                                                            "Home",
                                                                            "End",
                                                                          ].includes(
                                                                            e.key,
                                                                          )
                                                                        ) {
                                                                          return;
                                                                        }

                                                                        // Allow only integers (0-9)
                                                                        if (
                                                                          !/^[0-9]$/.test(
                                                                            e.key,
                                                                          )
                                                                        ) {
                                                                          e.preventDefault();
                                                                        }
                                                                      }}
                                                                      onChange={
                                                                        HandleRepeaterFieldChange
                                                                      }
                                                                    />
                                                                  </span>
                                                                  <span
                                                                    className="radio-text-padding"
                                                                    style={{
                                                                      display:
                                                                        "block",
                                                                    }}
                                                                  >
                                                                    week(s)
                                                                  </span>
                                                                </label>
                                                              </div>
                                                              <div className="">
                                                                <div className="option-without-input">
                                                                  <label className="option-fbox">
                                                                    <input
                                                                      type="radio"
                                                                      id={idx}
                                                                      name={
                                                                        "linvo_dose_freq_rbl" +
                                                                        idx
                                                                      }
                                                                      value={99}
                                                                      checked={
                                                                        data.linvo_dose_freq_rbl ===
                                                                        99
                                                                      }
                                                                      onChange={
                                                                        HandleRepeaterFieldChange
                                                                      }
                                                                    />

                                                                    <span className="radio-text-padding">
                                                                      Unknown
                                                                    </span>
                                                                  </label>
                                                                </div>
                                                              </div>
                                                              <ErrorField
                                                                show={
                                                                  data.linvo_dose_weekly_freq_hard_error
                                                                }
                                                                message={
                                                                  ELCharacteristics_MSGS.doseFrequencyEmpty
                                                                }
                                                                style={{
                                                                  paddingLeft:
                                                                    "0px",
                                                                }}
                                                              />

                                                              <ErrorField
                                                                show={
                                                                  data.linvo_dose_weekly_strength_mg_kgHard
                                                                }
                                                                message={
                                                                  ELCharacteristics_MSGS.doseFrequencyValueEmpty
                                                                }
                                                                style={{
                                                                  paddingLeft:
                                                                    "0px",
                                                                }}
                                                              />
                                                              <ErrorField
                                                                show={
                                                                  data.linvo_dose_weekly_freq_RangeError
                                                                }
                                                                message={
                                                                  ELCharacteristics_MSGS.DecimalRange
                                                                }
                                                                style={{
                                                                  paddingLeft:
                                                                    "0px",
                                                                }}
                                                              />
                                                            </div>
                                                          )}
                                                        </div>
                                                      )}
                                                    </td>

                                                    {/* {data.Q36TalConditionMet && (
                                                <>
                                                  <td
                                                    style={{
                                                      border: "1px solid black",
                                                      padding: "8px",
                                                    }}
                                                  >
                                                    <div>
                                                      <div
                                                        className="option-without-input"
                                                        style={{
                                                          display: "flex",
                                                        }}
                                                      >
                                                        <input
                                                          type="radio"
                                                          id={idx}
                                                          name={
                                                            "dt_admission_linvo" +
                                                            idx
                                                          }
                                                          value={1}
                                                          checked={
                                                            data.dt_admission_linvo ===
                                                            1
                                                          }
                                                          onChange={
                                                            HandleRepeaterFieldChange
                                                          }
                                                        />
                                                        <span
                                                          className="radio-text-padding"
                                                          style={{
                                                            paddingLeft: "10px",
                                                            marginLeft: "-5px",
                                                          }}
                                                        >
                                                          <DateControl
                                                            ctrlId={
                                                              "dt_admission_linvo_dt-" +
                                                              idx
                                                            }
                                                            HandleDateChange={
                                                              HandleRepeaterDateChange
                                                            }
                                                            date={
                                                              data.dt_admission_linvo_dt
                                                            }
                                                          />
                                                        </span>
                                                      </div>
                                                      <div
                                                        className="option-without-input"
                                                        style={{
                                                          display: "flex",
                                                          alignItems:
                                                            "flex-start",
                                                        }}
                                                      >
                                                        <input
                                                          type="radio"
                                                          style={{
                                                            marginTop: "6px",
                                                          }}
                                                          id={idx}
                                                          name={
                                                            "dt_admission_linvo" +
                                                            idx
                                                          }
                                                          value={99}
                                                          checked={
                                                            data.dt_admission_linvo ===
                                                            99
                                                          }
                                                          onChange={
                                                            HandleRepeaterFieldChange
                                                          }
                                                        />
                                                        <label className="option-fbox">
                                                          <span
                                                            className="radio-text-padding"
                                                            style={{
                                                              paddingLeft:
                                                                "10px",
                                                            }}
                                                          >
                                                            Date of admission
                                                            unknown
                                                          </span>
                                                        </label>
                                                      </div>
                                                      <ErrorField
                                                        show={
                                                          data.dt_admission_linvoHard
                                                        }
                                                        message={
                                                          ELCharacteristics_MSGS.admissionEmpty_linvo
                                                        }
                                                        style={{
                                                          paddingLeft: "0px",
                                                        }}
                                                      />

                                                      <ErrorField
                                                        show={
                                                          data.dt_admission_linvoHard_dt
                                                        }
                                                        message={
                                                          ELCharacteristics_MSGS.admissionEmptyDate
                                                        }
                                                        style={{
                                                          paddingLeft: "0px",
                                                        }}
                                                      />
                                                      <ErrorField
                                                        show={
                                                          data.dt_admission_linvoHard_dt_check
                                                        }
                                                        message={
                                                          ELCharacteristics_MSGS.admissionDateCheck
                                                        }
                                                        style={{
                                                          paddingLeft: "0px",
                                                        }}
                                                      />
                                                    </div>
                                                  </td>
                                                  <td
                                                    style={{
                                                      border: "1px solid black",
                                                      padding: "8px",
                                                    }}
                                                  >
                                                    <div>
                                                      <div
                                                        className="option-without-input"
                                                        style={{
                                                          display: "flex",
                                                        }}
                                                      >
                                                        <input
                                                          type="radio"
                                                          id={idx}
                                                          name={
                                                            "dt_discharge_linvo" +
                                                            idx
                                                          }
                                                          value={1}
                                                          checked={
                                                            data.dt_discharge_linvo ===
                                                            1
                                                          }
                                                          onChange={
                                                            HandleRepeaterFieldChange
                                                          }
                                                        />
                                                        <span
                                                          className="radio-text-padding"
                                                          style={{
                                                            paddingLeft: "10px",
                                                            marginLeft: "-5px",
                                                          }}
                                                        >
                                                          <DateControl
                                                            ctrlId={
                                                              "dt_discharge_linvo_dt-" +
                                                              idx
                                                            }
                                                            HandleDateChange={
                                                              HandleRepeaterDateChange
                                                            }
                                                            date={
                                                              data.dt_discharge_linvo_dt
                                                            }
                                                          />
                                                        </span>
                                                      </div>
                                                      <div
                                                        className="option-without-input"
                                                        style={{
                                                          display: "flex",
                                                          alignItems:
                                                            "flex-start",
                                                        }}
                                                      >
                                                        <input
                                                          type="radio"
                                                          style={{
                                                            marginTop: "6px",
                                                          }}
                                                          id={idx}
                                                          name={
                                                            "dt_discharge_linvo" +
                                                            idx
                                                          }
                                                          value={2}
                                                          checked={
                                                            data.dt_discharge_linvo ===
                                                            2
                                                          }
                                                          onChange={
                                                            HandleRepeaterFieldChange
                                                          }
                                                        />
                                                        <label className="option-fbox">
                                                          <span
                                                            className="radio-text-padding"
                                                            style={{
                                                              paddingLeft:
                                                                "10px",
                                                            }}
                                                          >
                                                            Date of discharge
                                                            unknown
                                                          </span>
                                                        </label>
                                                      </div>

                                                      <div
                                                        className="option-without-input"
                                                        style={{
                                                          display: "flex",
                                                          alignItems:
                                                            "flex-start",
                                                        }}
                                                      >
                                                        <input
                                                          type="radio"
                                                          style={{
                                                            marginTop: "6px",
                                                          }}
                                                          id={idx}
                                                          name={
                                                            "dt_discharge_linvo" +
                                                            idx
                                                          }
                                                          value={3}
                                                          checked={
                                                            data.dt_discharge_linvo ===
                                                            3
                                                          }
                                                          onChange={
                                                            HandleRepeaterFieldChange
                                                          }
                                                        />
                                                        <label>
                                                          <span
                                                            className="radio-text-padding"
                                                            style={{
                                                              paddingLeft:
                                                                "10px",
                                                            }}
                                                          >
                                                            Patient is still
                                                            hospitalized
                                                          </span>
                                                        </label>
                                                      </div>
                                                      <ErrorField
                                                        show={
                                                          data.dt_discharge_linvoHard
                                                        }
                                                        message={
                                                          ELCharacteristics_MSGS.dischargeEmpty_linvo
                                                        }
                                                        style={{
                                                          paddingLeft: "0px",
                                                        }}
                                                      />
                                                      <ErrorField
                                                        show={
                                                          data.dt_discharge_linvoHard_dt
                                                        }
                                                        message={
                                                          ELCharacteristics_MSGS.dischargeEmptyDate
                                                        }
                                                        style={{
                                                          paddingLeft: "0px",
                                                        }}
                                                      />

                                                      <ErrorField
                                                        show={
                                                          data.dt_discharge_linvoHard_dt_check
                                                        }
                                                        message={
                                                          ELCharacteristics_MSGS.dischargeDateCheck
                                                        }
                                                        style={{
                                                          paddingLeft: "0px",
                                                        }}
                                                      />
                                                    </div>
                                                  </td>
                                                </>
                                              )} */}
                                                  </tr>
                                                </tbody>
                                              </table>
                                            </div>
                                          </div>
                                        </div>
                                      ))}

                                  {linvoveyDetails &&
                                    linvoveyDetails.length > 0 &&
                                    linvoveyDetails.length < 550 && (
                                      <div className="sub-question-bot-sapce-ul mt-2">
                                        <div
                                          className="answer-list-text"
                                          onClick={AddLinvoveyRow}
                                        >
                                          <a>
                                            <img
                                              width="18px"
                                              src="../Assets/images/plus.png"
                                              alt=""
                                            />
                                          </a>
                                          <a className="radio-text-padding">
                                            Click here to add another dose
                                            administration of Linvoseltamab
                                          </a>
                                        </div>
                                      </div>
                                    )}
                                </div>
                                {/* 36.1 Linvoseltamab start */}
                                <div>
                                  <div className="dependent-section sub-question marginleft15">
                                    <div
                                      className="question question-weight"
                                      style={{ marginBottom: "10px" }}
                                    >
                                      <span>Q36.1.</span>
                                      <span className="quest-text-pad">
                                        Please list all hospitalizations that
                                        occurred during the{" "}
                                        <b>step-up dosing phase</b> (i.e.,
                                        hospitalizations where the patient was
                                        admitted between the date of{" "}
                                        <i>Step-up Dose 1</i> and the date of
                                        the{" "}
                                        <i>
                                          1<sup>st</sup> Subsequent Treatment
                                          Dose
                                        </i>{" "}
                                        reported in Q36). Please specify the
                                        date of admission, date of discharge,
                                        and reason for hospitalization.{" "}
                                      </span>
                                    </div>
                                    <ErrorField
                                      show={linvoInpatientHospMissing}
                                      message={
                                        ELCharacteristics_MSGS.LINVO_INPATIENT_HOSP_MISSING
                                      }
                                      style={{ paddingLeft: "0px" }}
                                    />
                                    {linvoHospDetails &&
                                      linvoHospDetails.length > 0 &&
                                      linvoHospDetails.map((data, idx) => (
                                        <div
                                          className="loop-section-ul"
                                          key={idx}
                                        >
                                          <div className="loop-head">
                                            <span
                                              className="question-weight"
                                              style={{ float: "inline-start" }}
                                            >
                                              {GetOrdinal(idx + 1)} LYNOZYFIC{" "}
                                              <sup className="supscript">
                                                TM
                                              </sup>{" "}
                                              (linvoquetamab-gcpt)
                                              Hospitalizations
                                            </span>

                                            {idx != 0 && (
                                              <div
                                                className="delete-btn-div"
                                                style={{ float: "inline-end" }}
                                              >
                                                <img
                                                  width="16px"
                                                  src="../Assets/images/Icon-material-delete.png"
                                                  alt=""
                                                  style={{ cursor: "pointer" }}
                                                  onClick={() =>
                                                    DeleteLinvoHospRow(idx)
                                                  }
                                                />
                                              </div>
                                            )}
                                          </div>

                                          {/* ELREXFIO® */}
                                          <div className="question-bot-sapce">
                                            <div className="sub-question grid-overflow">
                                              <table
                                                style={{
                                                  border: "1px solid black",
                                                  borderCollapse: "collapse",
                                                  width: "100%",
                                                }}
                                              >
                                                <thead>
                                                  <tr>
                                                    <th
                                                      style={{
                                                        border:
                                                          "1px solid black",
                                                        padding: "8px",
                                                        textAlign: "center",
                                                        // width: "25%"
                                                      }}
                                                    >
                                                      Treatment
                                                    </th>
                                                    <th
                                                      style={{
                                                        border:
                                                          "1px solid black",
                                                        padding: "8px",
                                                        textAlign: "center",
                                                        // width: "20%"
                                                      }}
                                                    >
                                                      Date of admission
                                                    </th>
                                                    {data.dt_admission !=
                                                      null &&
                                                      data.dt_admission !==
                                                        2 && (
                                                        <>
                                                          <th
                                                            style={{
                                                              border:
                                                                "1px solid black",
                                                              padding: "8px",
                                                              textAlign:
                                                                "center",
                                                              // width: "20%"
                                                            }}
                                                          >
                                                            Date of discharge
                                                          </th>
                                                          <th
                                                            style={{
                                                              border:
                                                                "1px solid black",
                                                              padding: "8px",
                                                              textAlign:
                                                                "center",
                                                              // width: "35%"
                                                            }}
                                                          >
                                                            Reason of
                                                            hospitalization
                                                          </th>
                                                        </>
                                                      )}
                                                  </tr>
                                                </thead>

                                                <tbody>
                                                  <tr>
                                                    <td
                                                      style={{
                                                        border:
                                                          "1px solid black",
                                                        padding: "8px",
                                                      }}
                                                    >
                                                      <span className="quest-text-pad">
                                                        LYNOZYFIC<sup>TM</sup>{" "}
                                                        (linvoquetamab-gcpt)
                                                      </span>
                                                    </td>
                                                    <td
                                                      style={{
                                                        border:
                                                          "1px solid black",
                                                        padding: "8px",
                                                      }}
                                                    >
                                                      <div>
                                                        <div
                                                          className="option-without-input"
                                                          style={{
                                                            display: "flex",
                                                          }}
                                                        >
                                                          <input
                                                            type="radio"
                                                            id={idx}
                                                            name={
                                                              "dt_admission_linvo" +
                                                              idx
                                                            }
                                                            value={1}
                                                            checked={
                                                              data.dt_admission ===
                                                              1
                                                            }
                                                            onChange={
                                                              HandleRepeaterFieldChange
                                                            }
                                                          />
                                                          <span
                                                            className="radio-text-padding"
                                                            style={{
                                                              paddingLeft:
                                                                "10px",
                                                              marginLeft:
                                                                "-5px",
                                                            }}
                                                          >
                                                            <DatePickerComponent
                                                              ctrlId={
                                                                "dt_admission_dt_linvo-" +
                                                                idx
                                                              }
                                                              HandleDateChange={
                                                                HandleRepeaterDateChange
                                                              }
                                                              // optionId={1}
                                                              minDate={dayjs(
                                                                "01-01-1920",
                                                              )}
                                                              maxDate={dayjs()}
                                                              locale="en" // current it is mm/dd/yyyy but for dd/mm/yyyy use "en-gb"
                                                              date={
                                                                data.dt_admission_dt
                                                              }
                                                            />
                                                          </span>
                                                        </div>
                                                        <div
                                                          className="option-without-input"
                                                          style={{
                                                            display: "flex",
                                                            alignItems:
                                                              "flex-start",
                                                          }}
                                                        >
                                                          <label className="option-fbox">
                                                            <input
                                                              type="radio"
                                                              style={{
                                                                marginTop:
                                                                  "6px",
                                                              }}
                                                              id={idx}
                                                              name={
                                                                "dt_admission_linvo" +
                                                                idx
                                                              }
                                                              value={99}
                                                              checked={
                                                                data.dt_admission ===
                                                                99
                                                              }
                                                              onChange={
                                                                HandleRepeaterFieldChange
                                                              }
                                                            />
                                                            <span
                                                              className="radio-text-padding"
                                                              style={{
                                                                paddingLeft:
                                                                  "10px",
                                                              }}
                                                            >
                                                              Date of admission
                                                              unknown
                                                            </span>
                                                          </label>
                                                        </div>
                                                        <div
                                                          className="option-without-input"
                                                          style={{
                                                            display: "flex",
                                                            alignItems:
                                                              "flex-start",
                                                          }}
                                                        >
                                                          <label className="option-fbox">
                                                            <input
                                                              type="radio"
                                                              style={{
                                                                marginTop:
                                                                  "6px",
                                                              }}
                                                              id={idx}
                                                              name={
                                                                "dt_admission_linvo" +
                                                                idx
                                                              }
                                                              value={2}
                                                              checked={
                                                                data.dt_admission ===
                                                                2
                                                              }
                                                              onChange={
                                                                HandleRepeaterFieldChange
                                                              }
                                                            />
                                                            <span
                                                              className="radio-text-padding"
                                                              style={{
                                                                paddingLeft:
                                                                  "10px",
                                                              }}
                                                            >
                                                              Patient had no
                                                              hospitalizations
                                                              during the step-up
                                                              dosing phase
                                                            </span>
                                                          </label>
                                                        </div>
                                                        <ErrorField
                                                          show={
                                                            data.dt_admission_Hard
                                                          }
                                                          message={
                                                            ELCharacteristics_MSGS.admissionEmpty_linvo
                                                          }
                                                          style={{
                                                            paddingLeft: "0px",
                                                          }}
                                                        />

                                                        <ErrorField
                                                          show={
                                                            data.dt_admission_Hard_dt
                                                          }
                                                          message={
                                                            ELCharacteristics_MSGS.admissionEmptyDate
                                                          }
                                                          style={{
                                                            paddingLeft: "0px",
                                                          }}
                                                        />

                                                        <ErrorField
                                                          show={
                                                            data.Invalid_admission_Hard_dt
                                                          }
                                                          message={
                                                            CommonError_Msgs.InvalidDate
                                                          }
                                                          style={{
                                                            paddingLeft: "0px",
                                                          }}
                                                        />
                                                        <ErrorField
                                                          show={
                                                            data.dt_admission_before_eof
                                                          }
                                                          message={
                                                            ELCharacteristics_MSGS.DateBeforeEOF
                                                          }
                                                          style={{
                                                            paddingLeft: "0px",
                                                          }}
                                                        />

                                                        <ErrorField
                                                          show={
                                                            data.dt_admission_out_of_bound
                                                          }
                                                          message={
                                                            ELCharacteristics_MSGS.Q361DateOutOfBound
                                                          }
                                                          style={{
                                                            paddingLeft: "0px",
                                                          }}
                                                        />
                                                      </div>
                                                    </td>
                                                    {data.dt_admission !=
                                                      null &&
                                                      data.dt_admission !==
                                                        2 && (
                                                        <>
                                                          <td
                                                            style={{
                                                              border:
                                                                "1px solid black",
                                                              padding: "8px",
                                                            }}
                                                          >
                                                            <div>
                                                              <div
                                                                className="option-without-input"
                                                                style={{
                                                                  display:
                                                                    "flex",
                                                                }}
                                                              >
                                                                <input
                                                                  type="radio"
                                                                  id={idx}
                                                                  name={
                                                                    "dt_discharge_linvo" +
                                                                    idx
                                                                  }
                                                                  value={1}
                                                                  checked={
                                                                    data.dt_discharge ===
                                                                    1
                                                                  }
                                                                  onChange={
                                                                    HandleRepeaterFieldChange
                                                                  }
                                                                />
                                                                <span
                                                                  className="radio-text-padding"
                                                                  style={{
                                                                    paddingLeft:
                                                                      "10px",
                                                                    marginLeft:
                                                                      "-5px",
                                                                  }}
                                                                >
                                                                  {/* <DateControl
                                                                  ctrlId={
                                                                    "dt_discharge_dt_linvo-" +
                                                                    idx
                                                                  }
                                                                  HandleDateChange={
                                                                    HandleRepeaterDateChange
                                                                  }
                                                                  date={
                                                                    data.dt_discharge_dt
                                                                  }
                                                                /> */}

                                                                  <DatePickerComponent
                                                                    ctrlId={
                                                                      "dt_discharge_dt_linvo-" +
                                                                      idx
                                                                    }
                                                                    // selectedDate={selectedDate}
                                                                    //onDateChange={(newDate) => setSelectedDate(newDate)}
                                                                    HandleDateChange={
                                                                      HandleRepeaterDateChange
                                                                    }
                                                                    // optionId={1}
                                                                    minDate={dayjs(
                                                                      "01-01-1920",
                                                                    )}
                                                                    maxDate={dayjs()}
                                                                    locale="en" // current it is mm/dd/yyyy but for dd/mm/yyyy use "en-gb"
                                                                    date={
                                                                      data.dt_discharge_dt
                                                                    }
                                                                  />
                                                                </span>
                                                              </div>
                                                              <div
                                                                className="option-without-input"
                                                                style={{
                                                                  display:
                                                                    "flex",
                                                                  alignItems:
                                                                    "flex-start",
                                                                }}
                                                              >
                                                                <label className="option-fbox">
                                                                  <input
                                                                    type="radio"
                                                                    style={{
                                                                      marginTop:
                                                                        "6px",
                                                                    }}
                                                                    id={idx}
                                                                    name={
                                                                      "dt_discharge_linvo" +
                                                                      idx
                                                                    }
                                                                    value={99}
                                                                    checked={
                                                                      data.dt_discharge ===
                                                                      99
                                                                    }
                                                                    onChange={
                                                                      HandleRepeaterFieldChange
                                                                    }
                                                                  />

                                                                  <span
                                                                    className="radio-text-padding"
                                                                    style={{
                                                                      paddingLeft:
                                                                        "10px",
                                                                    }}
                                                                  >
                                                                    Date of
                                                                    discharge
                                                                    unknown
                                                                  </span>
                                                                </label>
                                                              </div>

                                                              <div
                                                                className="option-without-input"
                                                                style={{
                                                                  display:
                                                                    "flex",
                                                                  alignItems:
                                                                    "flex-start",
                                                                }}
                                                              >
                                                                <label className="option-fbox">
                                                                  <input
                                                                    type="radio"
                                                                    style={{
                                                                      marginTop:
                                                                        "6px",
                                                                    }}
                                                                    id={idx}
                                                                    name={
                                                                      "dt_discharge_linvo" +
                                                                      idx
                                                                    }
                                                                    value={2}
                                                                    checked={
                                                                      data.dt_discharge ===
                                                                      2
                                                                    }
                                                                    onChange={
                                                                      HandleRepeaterFieldChange
                                                                    }
                                                                  />

                                                                  <span
                                                                    className="radio-text-padding"
                                                                    style={{
                                                                      paddingLeft:
                                                                        "10px",
                                                                    }}
                                                                  >
                                                                    Patient is
                                                                    still
                                                                    hospitalized
                                                                  </span>
                                                                </label>
                                                              </div>
                                                              <ErrorField
                                                                show={
                                                                  data.dt_discharge_Hard
                                                                }
                                                                message={
                                                                  ELCharacteristics_MSGS.dischargeEmpty_linvo
                                                                }
                                                                style={{
                                                                  paddingLeft:
                                                                    "0px",
                                                                }}
                                                              />
                                                              <ErrorField
                                                                show={
                                                                  data.dt_discharge_Hard_dt
                                                                }
                                                                message={
                                                                  ELCharacteristics_MSGS.dischargeEmptyDate
                                                                }
                                                                style={{
                                                                  paddingLeft:
                                                                    "0px",
                                                                }}
                                                              />

                                                              <ErrorField
                                                                show={
                                                                  data.dt_discharge_Hard_dt_check
                                                                }
                                                                message={
                                                                  ELCharacteristics_MSGS.dischargeDateCheck
                                                                }
                                                                style={{
                                                                  paddingLeft:
                                                                    "0px",
                                                                }}
                                                              />
                                                              <ErrorField
                                                                show={
                                                                  data.dt_discharge_out_of_bound
                                                                }
                                                                message={
                                                                  ELCharacteristics_MSGS.Q36DateOutOfBound
                                                                }
                                                                style={{
                                                                  paddingLeft:
                                                                    "0px",
                                                                }}
                                                              />

                                                              <ErrorField
                                                                show={
                                                                  data.dateRangeThirty
                                                                }
                                                                message={
                                                                  ELCharacteristics_MSGS.DateRangeThirty
                                                                }
                                                                style={{
                                                                  paddingLeft:
                                                                    "0px",
                                                                }}
                                                              />

                                                              <ErrorField
                                                                show={
                                                                  data.Invalid_dt_discharge_Hard
                                                                }
                                                                message={
                                                                  CommonError_Msgs.InvalidDate
                                                                }
                                                                style={{
                                                                  paddingLeft:
                                                                    "0px",
                                                                }}
                                                              />
                                                              <ErrorField
                                                                show={
                                                                  data.dt_discharge_before_eof
                                                                }
                                                                message={
                                                                  ELCharacteristics_MSGS.DateBeforeEOF
                                                                }
                                                                style={{
                                                                  paddingLeft:
                                                                    "0px",
                                                                }}
                                                              />
                                                            </div>
                                                          </td>
                                                          <td
                                                            style={{
                                                              border:
                                                                "1px solid black",
                                                              padding: "8px",
                                                            }}
                                                          >
                                                            <div>
                                                              <div className="">
                                                                <label className="long-text-option">
                                                                  <input
                                                                    type="checkbox"
                                                                    id={idx}
                                                                    name={
                                                                      "hospReason1_linvo" +
                                                                      idx
                                                                    }
                                                                    checked={
                                                                      data.hospReason1
                                                                    }
                                                                    onChange={
                                                                      HandleRepeaterFieldChange
                                                                    }
                                                                  />
                                                                  <span
                                                                    style={{
                                                                      marginLeft:
                                                                        "6px",
                                                                    }}
                                                                  >
                                                                    Administration
                                                                    of{" "}
                                                                    {
                                                                      COHORT_TREATMENT_GENERIC_NAME_MAP[
                                                                        RRMMCohortTreatment
                                                                          .Linvoseltamab
                                                                      ]
                                                                    }
                                                                  </span>
                                                                </label>
                                                              </div>
                                                              <div className="">
                                                                <label className="long-text-option">
                                                                  <input
                                                                    type="checkbox"
                                                                    id={idx}
                                                                    name={
                                                                      "hospReason2_linvo" +
                                                                      idx
                                                                    }
                                                                    checked={
                                                                      data.hospReason2
                                                                    }
                                                                    onChange={
                                                                      HandleRepeaterFieldChange
                                                                    }
                                                                  />
                                                                  <span
                                                                    style={{
                                                                      marginLeft:
                                                                        "6px",
                                                                    }}
                                                                  >
                                                                    Monitoring
                                                                    of adverse
                                                                    event (AE)
                                                                    as part of
                                                                    the
                                                                    treatment
                                                                    plan{" "}
                                                                  </span>
                                                                </label>
                                                              </div>
                                                              <div className="">
                                                                <label className="long-text-option">
                                                                  <input
                                                                    type="checkbox"
                                                                    id={idx}
                                                                    name={
                                                                      "hospReason3_linvo" +
                                                                      idx
                                                                    }
                                                                    checked={
                                                                      data.hospReason3
                                                                    }
                                                                    onChange={
                                                                      HandleRepeaterFieldChange
                                                                    }
                                                                  />
                                                                  <span
                                                                    style={{
                                                                      marginLeft:
                                                                        "6px",
                                                                    }}
                                                                  >
                                                                    Management
                                                                    of cytokine
                                                                    release
                                                                    syndrome
                                                                    (CRS)
                                                                  </span>
                                                                </label>
                                                              </div>
                                                              <div className="">
                                                                <label className="long-text-option">
                                                                  <input
                                                                    type="checkbox"
                                                                    id={idx}
                                                                    name={
                                                                      "hospReason4_linvo" +
                                                                      idx
                                                                    }
                                                                    checked={
                                                                      data.hospReason4
                                                                    }
                                                                    onChange={
                                                                      HandleRepeaterFieldChange
                                                                    }
                                                                  />
                                                                  <span
                                                                    style={{
                                                                      marginLeft:
                                                                        "6px",
                                                                    }}
                                                                  >
                                                                    Management
                                                                    of immune
                                                                    effector
                                                                    cell-associated
                                                                    neurotoxicity
                                                                    syndrome
                                                                    (ICANS)
                                                                  </span>
                                                                </label>
                                                              </div>
                                                              <div className="">
                                                                <label className="long-text-option">
                                                                  <input
                                                                    type="checkbox"
                                                                    id={idx}
                                                                    name={
                                                                      "hospReason5_linvo" +
                                                                      idx
                                                                    }
                                                                    checked={
                                                                      data.hospReason5
                                                                    }
                                                                    onChange={
                                                                      HandleRepeaterFieldChange
                                                                    }
                                                                  />
                                                                  <span
                                                                    style={{
                                                                      marginLeft:
                                                                        "6px",
                                                                    }}
                                                                  >
                                                                    Management
                                                                    of other AE
                                                                  </span>
                                                                </label>
                                                              </div>
                                                              <div className="">
                                                                <label className="long-text-option">
                                                                  <input
                                                                    type="checkbox"
                                                                    id={idx}
                                                                    name={
                                                                      "hospReason7_linvo" +
                                                                      idx
                                                                    }
                                                                    checked={
                                                                      data.hospReason7
                                                                    }
                                                                    onChange={
                                                                      HandleRepeaterFieldChange
                                                                    }
                                                                  />
                                                                  <span
                                                                    style={{
                                                                      marginLeft:
                                                                        "6px",
                                                                    }}
                                                                  >
                                                                    Administration
                                                                    of
                                                                    daratumumab
                                                                  </span>
                                                                </label>
                                                              </div>
                                                              <div
                                                                className="option-without-input"
                                                                style={{
                                                                  display:
                                                                    "flex",
                                                                }}
                                                              >
                                                                <label className="option-fbox">
                                                                  <input
                                                                    type="checkbox"
                                                                    id={idx}
                                                                    name={
                                                                      "hospReason6_linvo" +
                                                                      idx
                                                                    }
                                                                    checked={
                                                                      data.hospReason6
                                                                    }
                                                                    onChange={
                                                                      HandleRepeaterFieldChange
                                                                    }
                                                                  />
                                                                  <span
                                                                    style={{
                                                                      marginLeft:
                                                                        "4px",
                                                                      paddingRight:
                                                                        "15px",
                                                                      marginBottom:
                                                                        "0px",
                                                                      padding:
                                                                        "0",
                                                                    }}
                                                                  >
                                                                    Other non-AE
                                                                    related
                                                                    reasons,
                                                                    please
                                                                    specify:
                                                                    <div
                                                                      style={{
                                                                        paddingLeft:
                                                                          "15px",
                                                                        marginBottom:
                                                                          "0px",
                                                                        padding:
                                                                          "0",
                                                                      }}
                                                                    >
                                                                      <textarea
                                                                        name={
                                                                          "hosp_reason_oth_txt_linvo" +
                                                                          idx
                                                                        }
                                                                        className="input-dash"
                                                                        type="text"
                                                                        value={
                                                                          data.hosp_reason_oth_txt
                                                                        }
                                                                        onChange={
                                                                          HandleRepeaterFieldChange
                                                                        }
                                                                        maxLength={
                                                                          300
                                                                        }
                                                                        id={idx}
                                                                      />
                                                                    </div>
                                                                  </span>
                                                                </label>
                                                              </div>
                                                              <div className="">
                                                                <label className="long-text-option">
                                                                  <input
                                                                    type="radio"
                                                                    id={idx}
                                                                    name={
                                                                      "hospReasonUnk_linvo" +
                                                                      idx
                                                                    }
                                                                    checked={
                                                                      data.hospReasonUnk
                                                                    }
                                                                    onChange={
                                                                      HandleRepeaterFieldChange
                                                                    }
                                                                  />
                                                                  <span
                                                                    style={{
                                                                      marginLeft:
                                                                        "6px",
                                                                    }}
                                                                  >
                                                                    Unknown
                                                                  </span>
                                                                </label>
                                                              </div>
                                                              <ErrorField
                                                                show={
                                                                  data.hospReasonEmptyHard
                                                                }
                                                                message={
                                                                  ELCharacteristics_MSGS.Q40ReasonEmptyHard
                                                                }
                                                                style={{
                                                                  paddingLeft:
                                                                    "0px",
                                                                }}
                                                              />
                                                              <ErrorField
                                                                show={
                                                                  data.hospReasonTxtEmpty
                                                                }
                                                                message={
                                                                  ELCharacteristics_MSGS.Q36Point1ReasonTxtEmpty
                                                                }
                                                                style={{
                                                                  paddingLeft:
                                                                    "0px",
                                                                }}
                                                              />
                                                            </div>
                                                          </td>
                                                        </>
                                                      )}
                                                  </tr>
                                                </tbody>
                                              </table>
                                            </div>
                                          </div>
                                        </div>
                                      ))}
                                    {linvoHospDetails &&
                                      linvoHospDetails.length > 0 &&
                                      linvoHospDetails.length < 550 &&
                                      linvoHospDetails[
                                        linvoHospDetails.length - 1
                                      ].dt_admission != null &&
                                      linvoHospDetails[
                                        linvoHospDetails.length - 1
                                      ].dt_admission !== 2 && (
                                        <div className="sub-question-bot-sapce-ul mt-2">
                                          <div
                                            className="answer-list-text"
                                            onClick={AddLinvoHospRow}
                                          >
                                            <a>
                                              <img
                                                width="18px"
                                                src="../Assets/images/plus.png"
                                                alt=""
                                              />
                                            </a>
                                            <a className="radio-text-padding">
                                              Click here to add another
                                              hospitalization of Linvoseltamab
                                            </a>
                                          </div>
                                        </div>
                                      )}
                                  </div>
                                </div>
                                {/* 36.1 Linvoseltamab end */}
                              </>
                            )}
                          </div>
                        </div>
                        {/* Q37 start --------------------------------------- */}
                        {/* Q37 retired - render disabled via `false &&` guard below; JSX left intact */}
                        {false && isNewEcrfA === false && (
                          <>
                            <div>
                              <div className="question question-weight">
                                <span>Q37.</span>
                                <span className="quest-text-pad">
                                  Please indicate if any medication(s) had dose
                                  frequency reductions during treatment with{" "}
                                  <span
                                    style={{
                                      color: PageDesignConstants.fontColor,
                                      fontWeight: "bolder",
                                    }}
                                  >
                                    {isElrexvayliSelected &&
                                    isLinvoveySelected ? (
                                      <>
                                        ELREXFIO<sup>TM</sup> (elranatamab-bcmm)
                                        and LYNOZYFIC
                                        <sup className="supscript">TM</sup>{" "}
                                        (linvoquetamab-gcpt).
                                      </>
                                    ) : isElrexvayliSelected ? (
                                      "ELREXFIO<sup>TM</sup> (elranatamab-bcmm)."
                                    ) : isLinvoveySelected ? (
                                      <>
                                        LYNOZYFIC
                                        <sup className="supscript">TM</sup>{" "}
                                        (linvoquetamab-gcpt).
                                      </>
                                    ) : (
                                      ""
                                    )}
                                  </span>{" "}
                                </span>
                              </div>
                              {isElrexvayliSelected &&
                                Q37ElrexDetails &&
                                Q37ElrexDetails.length > 0 && (
                                  <ErrorField
                                    show={
                                      Q37ElrexDetails[0]
                                        .q37SubsequentDoseConflict
                                    }
                                    message={
                                      ELCharacteristics_MSGS.q37SubsequentDoseConflict
                                    }
                                    style={{
                                      paddingLeft: "0px",
                                      marginBottom: "10px",
                                    }}
                                  />
                                )}

                              {isLinvoveySelected &&
                                Q37LinvoDetails &&
                                Q37LinvoDetails.length > 0 && (
                                  <ErrorField
                                    show={
                                      Q37LinvoDetails[0]
                                        ?.q37LinvoSubsequentDoseConflict
                                    }
                                    message={
                                      ELCharacteristics_MSGS.q37SubsequentDoseConflict
                                    }
                                    style={{
                                      paddingLeft: "0px",
                                      marginBottom: "10px",
                                    }}
                                  />
                                )}

                              <div className="question-bot-sapce">
                                <div className="sub-question grid-overflow">
                                  <table
                                    style={{
                                      border: "1px solid black",
                                      borderCollapse: "collapse",
                                      width: "100%",
                                    }}
                                  >
                                    <thead>
                                      <tr>
                                        <th
                                          style={{
                                            border: "1px solid black",
                                            padding: "8px",
                                            textAlign: "center",
                                          }}
                                        >
                                          Treatment
                                        </th>
                                        <th
                                          style={{
                                            border: "1px solid black",
                                            padding: "8px",
                                            textAlign: "center",
                                            width: "25%",
                                          }}
                                        >
                                          Initial dosing frequency after the
                                          step-up dose is completed
                                        </th>
                                        <th
                                          style={{
                                            border: "1px solid black",
                                            padding: "8px",
                                            textAlign: "center",
                                            width: "25%",
                                          }}
                                        >
                                          Was the dosing frequency reduced for
                                          this medication at any point during
                                          the
                                          {/*                                   
                                   line of
                                  therapy */}
                                          <span
                                            data-tooltip-id="encounterTip"
                                            className="modal-popup"
                                            onClick={() =>
                                              setShowLineOfTherapyModal(true)
                                            }
                                          >
                                            {" "}
                                            {""} line of therapy
                                          </span>
                                          {showLineOfTherapyModal && (
                                            <InfoPopUp
                                              description={
                                                hyperlinkTextLineOfTherapy
                                              }
                                              OkClick={() =>
                                                setShowLineOfTherapyModal(false)
                                              }
                                            />
                                          )}{" "}
                                          ?
                                        </th>

                                        <th
                                          style={{
                                            border: "1px solid black",
                                            padding: "8px",
                                            textAlign: "center",
                                          }}
                                        >
                                          Initial dosing strength after step-up
                                          dose phase was completed
                                        </th>
                                      </tr>
                                    </thead>

                                    <tbody>
                                      {/* Elrex */}
                                      {isElrexvayliSelected && (
                                        <tr>
                                          {Q37ElrexDetails.length > 0 &&
                                            Q37ElrexDetails.map((data, idx) => (
                                              <React.Fragment key={idx}>
                                                <td
                                                  style={{
                                                    border: "1px solid black",
                                                    padding: "8px",
                                                  }}
                                                >
                                                  <span className="quest-text-pad">
                                                    ELREXFIO<sup>TM</sup>{" "}
                                                    (elranatamab-bcmm)
                                                  </span>
                                                </td>

                                                <td
                                                  style={{
                                                    border: "1px solid black",
                                                    padding: "8px",
                                                  }}
                                                >
                                                  <div
                                                    className="option-without-input"
                                                    style={{ display: "flex" }}
                                                  >
                                                    <label className="option-fbox">
                                                      <input
                                                        type="radio"
                                                        id={idx}
                                                        name={
                                                          "q37_Elr_Dose_week_frquencyRd" +
                                                          idx
                                                        }
                                                        value={1}
                                                        checked={
                                                          data.q37_Elr_Dose_week_frquencyRd ===
                                                          1
                                                        }
                                                        onChange={
                                                          HandleFieldChange
                                                        }
                                                      />
                                                      <span
                                                        style={{
                                                          paddingLeft: "10px",
                                                          display: "flex",
                                                        }}
                                                      >
                                                        The patient has not
                                                        completed the step-up
                                                        dosing phase
                                                      </span>
                                                    </label>
                                                  </div>

                                                  {elrexvayliDetails &&
                                                    elrexvayliDetails.length >
                                                      0 && (
                                                      <div
                                                        className="option-without-input"
                                                        style={{
                                                          display: "flex",
                                                        }}
                                                      >
                                                        <label className="option-fbox">
                                                          <input
                                                            type="radio"
                                                            id={idx}
                                                            name={
                                                              "q37_Elr_Dose_week_frquencyRd" +
                                                              idx
                                                            }
                                                            value={2}
                                                            checked={
                                                              data.q37_Elr_Dose_week_frquencyRd ===
                                                              2
                                                            }
                                                            onChange={
                                                              HandleFieldChange
                                                            }
                                                          />
                                                          <span
                                                            style={{
                                                              paddingLeft:
                                                                "10px",
                                                              display:
                                                                "inline-flex",
                                                            }}
                                                          >
                                                            <span>
                                                              Once every &nbsp;
                                                            </span>
                                                            <span>
                                                              <input
                                                                className="input-dash-sm"
                                                                id={idx}
                                                                name={
                                                                  "q37_Elr_Dose_week_frquency_txt" +
                                                                  idx
                                                                }
                                                                value={
                                                                  data.q37_Elr_Dose_week_frquency_txt
                                                                }
                                                                type="text"
                                                                onKeyDown={(
                                                                  e,
                                                                ) => {
                                                                  // Allow backspace, arrow keys, and delete
                                                                  if (
                                                                    [
                                                                      "Backspace",
                                                                      "ArrowLeft",
                                                                      "ArrowRight",
                                                                      "Delete",
                                                                      "Tab",
                                                                    ].includes(
                                                                      e.key,
                                                                    )
                                                                  ) {
                                                                    return;
                                                                  }

                                                                  // Prevent non-numeric characters, except '.'
                                                                  if (
                                                                    !/[0-9.]/.test(
                                                                      e.key,
                                                                    )
                                                                  ) {
                                                                    e.preventDefault();
                                                                    return;
                                                                  }

                                                                  const value =
                                                                    e.target
                                                                      .value;
                                                                  const selectionStart =
                                                                    e.target
                                                                      .selectionStart;
                                                                  const newValue =
                                                                    value.slice(
                                                                      0,
                                                                      selectionStart,
                                                                    ) +
                                                                    e.key +
                                                                    value.slice(
                                                                      e.target
                                                                        .selectionEnd,
                                                                    );

                                                                  // Prevent multiple '.'
                                                                  if (
                                                                    e.key ===
                                                                      "." &&
                                                                    value.includes(
                                                                      ".",
                                                                    )
                                                                  ) {
                                                                    e.preventDefault();
                                                                    return;
                                                                  }

                                                                  // Prevent '.' as the first character
                                                                  if (
                                                                    e.key ===
                                                                      "." &&
                                                                    value === ""
                                                                  ) {
                                                                    e.preventDefault();
                                                                    return;
                                                                  }

                                                                  // Allow only one digit after decimal point
                                                                  if (
                                                                    newValue.includes(
                                                                      ".",
                                                                    ) &&
                                                                    newValue.split(
                                                                      ".",
                                                                    )[1]
                                                                      .length >
                                                                      2
                                                                  ) {
                                                                    e.preventDefault();
                                                                    return;
                                                                  }
                                                                }}
                                                                onChange={
                                                                  HandleFieldChange
                                                                }
                                                              />
                                                            </span>
                                                            week
                                                          </span>
                                                        </label>
                                                      </div>
                                                    )}
                                                  {/* new Unknown */}
                                                  <div
                                                    className="option-without-input"
                                                    style={{ display: "flex" }}
                                                  >
                                                    <label className="option-fbox">
                                                      <input
                                                        type="radio"
                                                        id={idx}
                                                        name={
                                                          "q37_Elr_Dose_week_frquencyRd" +
                                                          idx
                                                        }
                                                        value={99}
                                                        checked={
                                                          data.q37_Elr_Dose_week_frquencyRd ===
                                                          99
                                                        }
                                                        onChange={
                                                          HandleFieldChange
                                                        }
                                                      />

                                                      <span
                                                        style={{
                                                          paddingLeft: "10px",
                                                          display: "flex",
                                                        }}
                                                      >
                                                        Unknown
                                                      </span>
                                                    </label>
                                                  </div>

                                                  <ErrorField
                                                    show={
                                                      data.q37_Elr_Dose_week_frquencyRd_Empty
                                                    }
                                                    message={
                                                      ELCharacteristics_MSGS.admissionEmpty
                                                    }
                                                    style={{
                                                      paddingLeft: "0px",
                                                    }}
                                                  />

                                                  <ErrorField
                                                    show={
                                                      data.q37_Elr_Dose_week_frquency_txt_Empty
                                                    }
                                                    message={
                                                      ELCharacteristics_MSGS.specifyDosageFrequency
                                                    }
                                                    style={{
                                                      paddingLeft: "0px",
                                                    }}
                                                  />

                                                  <ErrorField
                                                    show={
                                                      data.q37_Elr_Dose_week_freq_range
                                                    }
                                                    message={
                                                      ELCharacteristics_MSGS.allowedRange
                                                    }
                                                    style={{
                                                      paddingLeft: "0px",
                                                    }}
                                                  />
                                                </td>

                                                {data.q37_Elr_Dose_week_frquencyRd !==
                                                1 ? (
                                                  <>
                                                    <td
                                                      style={{
                                                        border:
                                                          "1px solid black",
                                                        padding: "8px",
                                                      }}
                                                    >
                                                      <div>
                                                        <div>
                                                          <div
                                                            className="option-without-input"
                                                            style={{
                                                              display: "flex",
                                                            }}
                                                          >
                                                            <label className="option-fbox">
                                                              <input
                                                                type="radio"
                                                                // id="Q37Elrexfio_select_1"

                                                                id={idx}
                                                                //name="Q37ElrexvayliRd"
                                                                name={
                                                                  "Q37ElrexvayliRd" +
                                                                  idx
                                                                }
                                                                value={1}
                                                                checked={
                                                                  data.Q37ElrexvayliRd ===
                                                                  1
                                                                }
                                                                onChange={
                                                                  HandleFieldChange
                                                                }
                                                              />
                                                              <span
                                                                style={{
                                                                  paddingLeft:
                                                                    "10px",
                                                                  marginLeft:
                                                                    "-5px",
                                                                }}
                                                              >
                                                                Yes
                                                              </span>
                                                            </label>
                                                          </div>
                                                          <div
                                                            className="option-without-input"
                                                            style={{
                                                              display: "flex",
                                                              alignItems:
                                                                "flex-start",
                                                            }}
                                                          >
                                                            <label className="option-fbox">
                                                              <input
                                                                type="radio"
                                                                style={{
                                                                  marginTop:
                                                                    "6px",
                                                                }}
                                                                id={idx}
                                                                name={
                                                                  "Q37ElrexvayliRd" +
                                                                  idx
                                                                }
                                                                value={2}
                                                                checked={
                                                                  data.Q37ElrexvayliRd ===
                                                                  2
                                                                }
                                                                onChange={
                                                                  HandleFieldChange
                                                                }
                                                              />

                                                              <span
                                                                style={{
                                                                  paddingLeft:
                                                                    "10px",
                                                                }}
                                                              >
                                                                No
                                                              </span>
                                                            </label>
                                                          </div>
                                                          <div
                                                            className="option-without-input"
                                                            style={{
                                                              display: "flex",
                                                              alignItems:
                                                                "flex-start",
                                                            }}
                                                          >
                                                            <label className="option-fbox">
                                                              <input
                                                                type="radio"
                                                                style={{
                                                                  marginTop:
                                                                    "6px",
                                                                }}
                                                                id={idx}
                                                                name={
                                                                  "Q37ElrexvayliRd" +
                                                                  idx
                                                                }
                                                                value={99}
                                                                checked={
                                                                  data.Q37ElrexvayliRd ===
                                                                  99
                                                                }
                                                                onChange={
                                                                  HandleFieldChange
                                                                }
                                                              />

                                                              <span
                                                                style={{
                                                                  paddingLeft:
                                                                    "10px",
                                                                }}
                                                              >
                                                                Unknown
                                                              </span>
                                                            </label>
                                                          </div>
                                                          <ErrorField
                                                            show={
                                                              data.q37ElrexvayliEmpty
                                                            }
                                                            message={
                                                              ELCharacteristics_MSGS.administrationEmpty
                                                            }
                                                            style={{
                                                              paddingLeft:
                                                                "0px",
                                                            }}
                                                          />
                                                        </div>
                                                      </div>
                                                    </td>
                                                  </>
                                                ) : (
                                                  <td
                                                    style={{
                                                      border: "1px solid black",
                                                      //alignContent: "flex-start",
                                                      textAlign: "center",
                                                      padding: "8px",
                                                      //textAlign: 'start'
                                                    }}
                                                  >
                                                    <span
                                                      style={{
                                                        textAlign: "center",
                                                        verticalAlign: "center",
                                                      }}
                                                      className="not-applicable"
                                                    >
                                                      <b>Not applicable</b>
                                                    </span>
                                                  </td>
                                                )}

                                                {/* third column here */}
                                                {data.q37_Elr_Dose_week_frquencyRd !==
                                                1 ? (
                                                  <td
                                                    style={{
                                                      border: "1px solid black",
                                                      padding: "8px",
                                                    }}
                                                  >
                                                    <>
                                                      <div>
                                                        <label>
                                                          <input
                                                            type="radio"
                                                            id={idx}
                                                            name={
                                                              "q37ElrDosageRd" +
                                                              idx
                                                            }
                                                            value={1}
                                                            checked={
                                                              data.q37ElrDosageRd ===
                                                              1
                                                            }
                                                            onChange={
                                                              HandleRepeaterFieldChange
                                                            }
                                                          />
                                                          <span
                                                            style={{
                                                              paddingLeft:
                                                                "10px",
                                                            }}
                                                          >
                                                            76 mg
                                                          </span>
                                                        </label>
                                                      </div>

                                                      <div>
                                                        <label>
                                                          <input
                                                            type="radio"
                                                            id={idx}
                                                            name={
                                                              "q37ElrDosageRd" +
                                                              idx
                                                            }
                                                            value={2}
                                                            checked={
                                                              data.q37ElrDosageRd ===
                                                              2
                                                            }
                                                            onChange={
                                                              HandleRepeaterFieldChange
                                                            }
                                                          />
                                                          <span
                                                            style={{
                                                              paddingLeft:
                                                                "10px",
                                                            }}
                                                          >
                                                            Other, please
                                                            specify{" "}
                                                            <textarea
                                                              className="input-dash"
                                                              style={{
                                                                marginLeft:
                                                                  "23px",
                                                              }}
                                                              id={idx}
                                                              name={
                                                                "q37ElrDosageOtherTxt" +
                                                                idx
                                                              }
                                                              value={
                                                                data.q37ElrDosageOtherTxt
                                                              }
                                                              type="text"
                                                              onChange={
                                                                HandleRepeaterFieldChange
                                                              }
                                                              maxLength={300}
                                                            />
                                                          </span>
                                                        </label>
                                                      </div>

                                                      <div>
                                                        <label>
                                                          <input
                                                            type="radio"
                                                            id={idx}
                                                            name={
                                                              "q37ElrDosageRd" +
                                                              idx
                                                            }
                                                            value={99}
                                                            checked={
                                                              data.q37ElrDosageRd ===
                                                              99
                                                            }
                                                            onChange={
                                                              HandleRepeaterFieldChange
                                                            }
                                                          />
                                                          <span
                                                            style={{
                                                              paddingLeft:
                                                                "10px",
                                                            }}
                                                          >
                                                            Unknown
                                                          </span>
                                                        </label>
                                                      </div>

                                                      {/* Error Fields */}
                                                      <ErrorField
                                                        show={
                                                          Q37ElrexDetails[idx]
                                                            .q37ElrDosageRdEmpty
                                                        }
                                                        message={
                                                          ELCharacteristics_MSGS.administrationEmpty
                                                        }
                                                        style={{
                                                          paddingLeft: "0px",
                                                        }}
                                                      />
                                                      <ErrorField
                                                        show={
                                                          Q37ElrexDetails[0]
                                                            .q37ElrDosageOtherTxtEmpty
                                                        }
                                                        message={
                                                          ELCharacteristics_MSGS.specifyOtherText
                                                        }
                                                        style={{
                                                          paddingLeft: "0px",
                                                        }}
                                                      />
                                                    </>
                                                  </td>
                                                ) : (
                                                  <td
                                                    style={{
                                                      border: "1px solid black",
                                                      //alignContent: "flex-start",
                                                      textAlign: "center",
                                                      padding: "8px",
                                                      //textAlign: 'start'
                                                    }}
                                                  >
                                                    <span
                                                      style={{
                                                        textAlign: "center",
                                                        verticalAlign: "center",
                                                      }}
                                                      className="not-applicable"
                                                    >
                                                      <b>Not applicable</b>
                                                    </span>
                                                  </td>
                                                )}
                                              </React.Fragment>
                                            ))}
                                        </tr>
                                      )}

                                      {/* Linvoseltamab */}
                                      {isLinvoveySelected && (
                                        <tr>
                                          {Q37LinvoDetails.length > 0 &&
                                            Q37LinvoDetails.map((data, idx) => (
                                              <React.Fragment key={idx}>
                                                <td
                                                  style={{
                                                    border: "1px solid black",
                                                    padding: "8px",
                                                  }}
                                                >
                                                  <span className="quest-text-pad">
                                                    LYNOZYFIC
                                                    <sup className="supscript">
                                                      TM
                                                    </sup>{" "}
                                                    (linvoquetamab-gcpt)
                                                  </span>
                                                </td>
                                                <td
                                                  style={{
                                                    border: "1px solid black",
                                                    padding: "8px",
                                                  }}
                                                >
                                                  <div
                                                    className="option-without-input"
                                                    style={{ display: "flex" }}
                                                  >
                                                    <label className="option-fbox">
                                                      <input
                                                        type="radio"
                                                        id={idx}
                                                        name={
                                                          "q37_Linvo_Dose_week_frquencyRd" +
                                                          idx
                                                        }
                                                        value={1}
                                                        checked={
                                                          data.q37_Linvo_Dose_week_frquencyRd ===
                                                          1
                                                        }
                                                        onChange={
                                                          HandleFieldChange
                                                        }
                                                      />

                                                      <span
                                                        style={{
                                                          paddingLeft: "10px",
                                                          display: "flex",
                                                        }}
                                                      >
                                                        The patient has not
                                                        completed the step-up
                                                        dosing phase
                                                      </span>
                                                    </label>
                                                  </div>
                                                  {linvoveyDetails &&
                                                    linvoveyDetails.length >
                                                      0 && (
                                                      <div
                                                        className="option-without-input"
                                                        style={{
                                                          display: "flex",
                                                        }}
                                                      >
                                                        <label className="option-fbox">
                                                          <input
                                                            type="radio"
                                                            id={idx}
                                                            name={
                                                              "q37_Linvo_Dose_week_frquencyRd" +
                                                              idx
                                                            }
                                                            value={2}
                                                            checked={
                                                              data.q37_Linvo_Dose_week_frquencyRd ===
                                                              2
                                                            }
                                                            onChange={
                                                              HandleFieldChange
                                                            }
                                                          />
                                                          <span
                                                            style={{
                                                              paddingLeft:
                                                                "10px",
                                                              display:
                                                                "inline-flex",
                                                            }}
                                                          >
                                                            <span>
                                                              Once every &nbsp;
                                                            </span>
                                                            <span>
                                                              <input
                                                                className="input-dash-sm"
                                                                id={idx}
                                                                name={
                                                                  "q37_Linvo_Dose_week_frquency_txt" +
                                                                  idx
                                                                }
                                                                type="text"
                                                                onChange={
                                                                  HandleFieldChange
                                                                }
                                                                value={
                                                                  data.q37_Linvo_Dose_week_frquency_txt
                                                                }
                                                                onKeyDown={(
                                                                  e,
                                                                ) => {
                                                                  // Allow backspace, arrow keys, and delete
                                                                  if (
                                                                    [
                                                                      "Backspace",
                                                                      "ArrowLeft",
                                                                      "ArrowRight",
                                                                      "Delete",
                                                                      "Tab",
                                                                    ].includes(
                                                                      e.key,
                                                                    )
                                                                  ) {
                                                                    return;
                                                                  }

                                                                  // Prevent non-numeric characters, except '.'
                                                                  if (
                                                                    !/[0-9.]/.test(
                                                                      e.key,
                                                                    )
                                                                  ) {
                                                                    e.preventDefault();
                                                                    return;
                                                                  }

                                                                  const value =
                                                                    e.target
                                                                      .value;
                                                                  const selectionStart =
                                                                    e.target
                                                                      .selectionStart;
                                                                  const newValue =
                                                                    value.slice(
                                                                      0,
                                                                      selectionStart,
                                                                    ) +
                                                                    e.key +
                                                                    value.slice(
                                                                      e.target
                                                                        .selectionEnd,
                                                                    );

                                                                  // Prevent multiple '.'
                                                                  if (
                                                                    e.key ===
                                                                      "." &&
                                                                    value.includes(
                                                                      ".",
                                                                    )
                                                                  ) {
                                                                    e.preventDefault();
                                                                    return;
                                                                  }

                                                                  // Prevent '.' as the first character
                                                                  if (
                                                                    e.key ===
                                                                      "." &&
                                                                    value === ""
                                                                  ) {
                                                                    e.preventDefault();
                                                                    return;
                                                                  }

                                                                  // Allow only one digit after decimal point
                                                                  if (
                                                                    newValue.includes(
                                                                      ".",
                                                                    ) &&
                                                                    newValue.split(
                                                                      ".",
                                                                    )[1]
                                                                      .length >
                                                                      2
                                                                  ) {
                                                                    e.preventDefault();
                                                                    return;
                                                                  }
                                                                }}
                                                              />
                                                            </span>
                                                            week
                                                          </span>
                                                        </label>
                                                      </div>
                                                    )}
                                                  {/* new Unknown */}
                                                  <div
                                                    className="option-without-input"
                                                    style={{ display: "flex" }}
                                                  >
                                                    <label className="option-fbox">
                                                      <input
                                                        type="radio"
                                                        id={idx}
                                                        name={
                                                          "q37_Linvo_Dose_week_frquencyRd" +
                                                          idx
                                                        }
                                                        value={99}
                                                        checked={
                                                          data.q37_Linvo_Dose_week_frquencyRd ===
                                                          99
                                                        }
                                                        onChange={
                                                          HandleFieldChange
                                                        }
                                                      />

                                                      <span
                                                        style={{
                                                          paddingLeft: "10px",
                                                          display: "flex",
                                                        }}
                                                      >
                                                        Unknown
                                                      </span>
                                                    </label>
                                                  </div>
                                                  <div>
                                                    <ErrorField
                                                      show={
                                                        data.q37_Linvo_Dose_week_frquencyRd_Empty
                                                      }
                                                      message={
                                                        ELCharacteristics_MSGS.admissionEmpty
                                                      }
                                                      style={{
                                                        paddingLeft: "0px",
                                                      }}
                                                    />
                                                    <ErrorField
                                                      show={
                                                        data.q37_Linvo_Dose_week_frquency_txt_Empty
                                                      }
                                                      message={
                                                        ELCharacteristics_MSGS.specifyDosageFrequency
                                                      }
                                                      style={{
                                                        paddingLeft: "0px",
                                                      }}
                                                    />
                                                    <ErrorField
                                                      show={
                                                        data.q37_Linvo_Dose_week_freq_range
                                                      }
                                                      message={
                                                        ELCharacteristics_MSGS.allowedRange
                                                      }
                                                      style={{
                                                        paddingLeft: "0px",
                                                      }}
                                                    />
                                                  </div>
                                                </td>
                                                {data.q37_Linvo_Dose_week_frquencyRd !==
                                                1 ? (
                                                  <>
                                                    <td
                                                      style={{
                                                        border:
                                                          "1px solid black",
                                                        padding: "8px",
                                                      }}
                                                    >
                                                      <div>
                                                        <div>
                                                          <div
                                                            className="option-without-input"
                                                            style={{
                                                              display: "flex",
                                                            }}
                                                          >
                                                            <label className="option-fbox">
                                                              <input
                                                                type="radio"
                                                                // id="Q37Linvoseltamab_select_1"
                                                                // name="Q37LinvoveyRd"

                                                                id={idx}
                                                                name={
                                                                  "Q37LinvoveyRd" +
                                                                  idx
                                                                }
                                                                value={1}
                                                                checked={
                                                                  data.Q37LinvoveyRd ===
                                                                  1
                                                                }
                                                                onChange={
                                                                  HandleFieldChange
                                                                }
                                                              />
                                                              <span
                                                                // className="radio-text-padding"
                                                                style={{
                                                                  paddingLeft:
                                                                    "10px",
                                                                  marginLeft:
                                                                    "-5px",
                                                                }}
                                                              >
                                                                Yes
                                                              </span>
                                                            </label>
                                                          </div>
                                                          <div
                                                            className="option-without-input"
                                                            style={{
                                                              display: "flex",
                                                              alignItems:
                                                                "flex-start",
                                                            }}
                                                          >
                                                            <label className="option-fbox">
                                                              <input
                                                                type="radio"
                                                                style={{
                                                                  marginTop:
                                                                    "6px",
                                                                }}
                                                                // id="Q37Linvoseltamab_select_0"
                                                                // name="Q37LinvoveyRd"

                                                                id={idx}
                                                                name={
                                                                  "Q37LinvoveyRd" +
                                                                  idx
                                                                }
                                                                value={2}
                                                                checked={
                                                                  data.Q37LinvoveyRd ===
                                                                  2
                                                                }
                                                                onChange={
                                                                  HandleFieldChange
                                                                }
                                                              />

                                                              <span
                                                                // className="radio-text-padding"
                                                                style={{
                                                                  paddingLeft:
                                                                    "10px",
                                                                }}
                                                              >
                                                                No
                                                              </span>
                                                            </label>
                                                          </div>

                                                          <div
                                                            className="option-without-input"
                                                            style={{
                                                              display: "flex",
                                                              alignItems:
                                                                "flex-start",
                                                            }}
                                                          >
                                                            <label className="option-fbox">
                                                              <input
                                                                type="radio"
                                                                style={{
                                                                  marginTop:
                                                                    "6px",
                                                                }}
                                                                // id="Q37Elrexfio_select_0"
                                                                // name="Q37ElrexvayliRd"
                                                                id={idx}
                                                                name={
                                                                  "Q37LinvoveyRd" +
                                                                  idx
                                                                }
                                                                value={99}
                                                                checked={
                                                                  data.Q37LinvoveyRd ===
                                                                  99
                                                                }
                                                                onChange={
                                                                  HandleFieldChange
                                                                }
                                                              />

                                                              <span
                                                                // className="radio-text-padding"
                                                                style={{
                                                                  paddingLeft:
                                                                    "10px",
                                                                }}
                                                              >
                                                                Unknown
                                                              </span>
                                                            </label>
                                                          </div>
                                                          <ErrorField
                                                            show={
                                                              data.q37LinvoveyEmpty
                                                            }
                                                            message={
                                                              ELCharacteristics_MSGS.administrationEmpty
                                                            }
                                                            style={{
                                                              paddingLeft:
                                                                "0px",
                                                            }}
                                                          />
                                                        </div>
                                                      </div>
                                                    </td>

                                                    {/* end */}
                                                  </>
                                                ) : (
                                                  <td
                                                    style={{
                                                      border: "1px solid black",
                                                      //alignContent: "flex-start",
                                                      textAlign: "center",
                                                      padding: "8px",
                                                      //textAlign: 'start'
                                                    }}
                                                  >
                                                    <span
                                                      style={{
                                                        textAlign: "center",
                                                      }}
                                                      className="not-applicable"
                                                    >
                                                      <b>Not applicable</b>
                                                    </span>
                                                  </td>
                                                )}
                                                {/* third column here */}
                                                {data.q37_Linvo_Dose_week_frquencyRd !==
                                                1 ? (
                                                  <div className="option-without-input">
                                                    <div
                                                      style={{
                                                        paddingLeft: "15px",
                                                      }}
                                                    >
                                                      <div
                                                        className="option-without-input"
                                                        //style={{ display: "flex" }}
                                                      >
                                                        <label className="option-fbox">
                                                          <input
                                                            type="radio"
                                                            id={idx}
                                                            name={
                                                              "q37_Linvo_Dosage_Rd" +
                                                              idx
                                                            }
                                                            value={1}
                                                            checked={
                                                              data.q37_Linvo_Dosage_Rd ===
                                                              1
                                                            }
                                                            onChange={
                                                              HandleFieldChange
                                                            }
                                                          />
                                                          <span
                                                            className="radio-text-padding"
                                                            style={{
                                                              paddingLeft:
                                                                "10px",
                                                            }}
                                                          >
                                                            200 mg{" "}
                                                          </span>
                                                        </label>
                                                      </div>
                                                      <div className="option-without-input">
                                                        <label className="option-fbox">
                                                          <input
                                                            type="radio"
                                                            style={{
                                                              marginTop: "6px",
                                                            }}
                                                            id={idx}
                                                            name={
                                                              "q37_Linvo_Dosage_Rd" +
                                                              idx
                                                            }
                                                            value={2}
                                                            checked={
                                                              data.q37_Linvo_Dosage_Rd ===
                                                              2
                                                            }
                                                            onChange={
                                                              HandleFieldChange
                                                            }
                                                          />

                                                          <span
                                                            style={{
                                                              paddingLeft:
                                                                "10px",
                                                            }}
                                                          >
                                                            Other, please
                                                            specify
                                                            <span>
                                                              <textarea
                                                                className="input-dash"
                                                                id={idx}
                                                                name={
                                                                  "q37_Linvo_Dosage_OtherTxt" +
                                                                  idx
                                                                }
                                                                type="text"
                                                                onChange={
                                                                  HandleFieldChange
                                                                }
                                                                value={
                                                                  data.q37_Linvo_Dosage_OtherTxt
                                                                }
                                                              />
                                                            </span>
                                                          </span>
                                                        </label>
                                                      </div>

                                                      <div className="option-without-input">
                                                        <label className="option-fbox">
                                                          <input
                                                            type="radio"
                                                            style={{
                                                              marginTop: "6px",
                                                            }}
                                                            id={idx}
                                                            name={
                                                              "q37_Linvo_Dosage_Rd" +
                                                              idx
                                                            }
                                                            value={99}
                                                            checked={
                                                              data.q37_Linvo_Dosage_Rd ===
                                                              99
                                                            }
                                                            onChange={
                                                              HandleFieldChange
                                                            }
                                                          />

                                                          <span
                                                            style={{
                                                              paddingLeft:
                                                                "10px",
                                                            }}
                                                          >
                                                            Unknown
                                                          </span>
                                                        </label>
                                                      </div>

                                                      <ErrorField
                                                        show={
                                                          data.q37_Linvo_Dosage_Rd_Empty
                                                        }
                                                        message={
                                                          ELCharacteristics_MSGS.admissionEmpty
                                                        }
                                                        style={{
                                                          paddingLeft: "0px",
                                                        }}
                                                      />

                                                      <ErrorField
                                                        show={
                                                          data.q37_Linvo_Dosage_OtherTxt_Empty
                                                        }
                                                        message={
                                                          ELCharacteristics_MSGS.specifyOtherText
                                                        }
                                                        style={{
                                                          paddingLeft: "0px",
                                                        }}
                                                      />
                                                    </div>
                                                  </div>
                                                ) : (
                                                  <td
                                                    style={{
                                                      border: "1px solid black",
                                                      //alignContent: "flex-start",
                                                      textAlign: "center",
                                                      padding: "8px",
                                                      //textAlign: 'start'
                                                    }}
                                                  >
                                                    <span
                                                      style={{
                                                        textAlign: "center",
                                                      }}
                                                      className="not-applicable"
                                                    >
                                                      <b>Not applicable</b>
                                                    </span>
                                                  </td>
                                                )}
                                              </React.Fragment>
                                            ))}
                                        </tr>
                                      )}
                                      {/* Linvoseltamab */}
                                    </tbody>
                                  </table>
                                </div>
                              </div>
                            </div>
                          </>
                        )}
                        {/* Q37 end---------------------------------------------------------- */}
                        {/* Q38 start */}
                        {/* Q38 retired - render disabled via `false &&` guard below; JSX left intact (includes both Elrexfio and Linvoseltamab sub-blocks through the true closing boundary near "Q38Linvoseltamab" trailing marker, NOT the mislabeled "Q38 end" comment mid-block) */}
                        {false &&
                          (Q37ElrexDetails[0].Q37ElrexvayliRd === 1 ||
                            Q37LinvoDetails[0].Q37LinvoveyRd === 1) &&
                          !isNewEcrfA && (
                            <>
                              <div className="question question-weight">
                                <span>Q38.</span>
                                <span className="quest-text-pad">
                                  Please provide all dates of dose frequency
                                  reductions during treatment with{" "}
                                  <span
                                    style={{
                                      color: PageDesignConstants.fontColor,
                                      fontWeight: "bolder",
                                    }}
                                  >
                                    {isElrexvayliSelected &&
                                    Q37ElrexDetails[0].Q37ElrexvayliRd === 1 &&
                                    isLinvoveySelected &&
                                    Q37LinvoDetails[0].Q37LinvoveyRd === 1 ? (
                                      <>
                                        ELREXFIO<sup>TM</sup> (elranatamab-bcmm)
                                        and LYNOZYFIC
                                        <sup className="supscript">TM</sup>{" "}
                                        (linvoquetamab-gcpt).
                                      </>
                                    ) : isElrexvayliSelected &&
                                      Q37ElrexDetails[0].Q37ElrexvayliRd ===
                                        1 ? (
                                      "ELREXFIO<sup>TM</sup> (elranatamab-bcmm)."
                                    ) : isLinvoveySelected &&
                                      Q37LinvoDetails[0].Q37LinvoveyRd === 1 ? (
                                      <>
                                        LYNOZYFIC
                                        <sup className="supscript">TM</sup>{" "}
                                        (linvoquetamab-gcpt).
                                      </>
                                    ) : (
                                      ""
                                    )}
                                  </span>{" "}
                                  <br />
                                  <span>
                                    Note that dose frequency reductions refers
                                    only to the frequency of administrations
                                    (i.e., weekly, biweekly, monthly, etc.) and
                                    note changes in dose strength.
                                  </span>
                                </span>
                              </div>
                            </>
                          )}
                        {isElrexvayliSelected &&
                          Q37ElrexDetails[0].showDateRedFreqElr &&
                          !isNewEcrfA && (
                            <div className="dependent-section">
                              {Q38ElrexDetails &&
                                Q38ElrexDetails.length > 0 &&
                                Q38ElrexDetails.map((data, idx) => (
                                  <div className="loop-section-ul" key={idx}>
                                    <div className="loop-head">
                                      <span
                                        className="question-weight"
                                        style={{ float: "inline-start" }}
                                      >
                                        {GetOrdinal(idx + 1)} Dose frequency
                                        reductions
                                      </span>
                                      {idx != 0 && (
                                        <div
                                          className="delete-btn-div"
                                          style={{ float: "inline-end" }}
                                        >
                                          <img
                                            width="16px"
                                            src="../Assets/images/Icon-material-delete.png"
                                            alt=""
                                            style={{ cursor: "pointer" }}
                                            onClick={() =>
                                              DeleteQ38ElrexvayliRow(idx)
                                            }
                                          />
                                        </div>
                                      )}
                                    </div>

                                    {/* ELREXFIO® */}
                                    <div className="question-bot-sapce">
                                      <div className="sub-question grid-overflow">
                                        <table
                                          style={{
                                            border: "1px solid black",
                                            borderCollapse: "collapse",
                                            width: "100%",
                                          }}
                                        >
                                          <thead>
                                            <tr>
                                              <th
                                                style={{
                                                  border: "1px solid black",
                                                  padding: "8px",
                                                  textAlign: "center",
                                                }}
                                              >
                                                Treatment
                                              </th>
                                              <th
                                                style={{
                                                  border: "1px solid black",
                                                  padding: "8px",
                                                  textAlign: "center",
                                                  width: "15%",
                                                }}
                                              >
                                                Date of dose frequency reduction
                                              </th>
                                              <th
                                                style={{
                                                  border: "1px solid black",
                                                  padding: "8px",
                                                  textAlign: "center",
                                                }}
                                              >
                                                Dose frequency after reduction
                                              </th>
                                            </tr>
                                          </thead>

                                          <tbody>
                                            <tr>
                                              <td
                                                style={{
                                                  border: "1px solid black",
                                                  padding: "8px",
                                                }}
                                              >
                                                <span className="quest-text-pad">
                                                  ELREXFIO<sup>TM</sup>{" "}
                                                  (elranatamab-bcmm)
                                                </span>
                                              </td>

                                              <td
                                                style={{
                                                  border: "1px solid black",
                                                  padding: "8px",
                                                }}
                                              >
                                                <div>
                                                  <div>
                                                    <div
                                                      className="option-without-input"
                                                      style={{
                                                        display: "flex",
                                                      }}
                                                    >
                                                      <input
                                                        type="radio"
                                                        id={idx}
                                                        name={
                                                          "Q38ElrexvayliRd" +
                                                          idx
                                                        }
                                                        value={1}
                                                        checked={
                                                          data.Q38ElrexvayliRd ===
                                                          1
                                                        }
                                                        onChange={
                                                          HandleRepeaterFieldChange
                                                        }
                                                      />
                                                      <span
                                                        className="radio-text-padding"
                                                        style={{
                                                          paddingLeft: "10px",
                                                          marginLeft: "-5px",
                                                        }}
                                                      >
                                                        <DatePickerComponent
                                                          ctrlId={
                                                            "dt_dosefreq_elr_dt-" +
                                                            idx
                                                          }
                                                          HandleDateChange={
                                                            HandleRepeaterDateChange
                                                          }
                                                          minDate={dayjs(
                                                            "01-01-1920",
                                                          )}
                                                          maxDate={dayjs()}
                                                          locale="en" // current it is mm/dd/yyyy but for dd/mm/yyyy use "en-gb"
                                                          date={
                                                            data.dt_dosefreq_elr_dt
                                                          }
                                                        />
                                                      </span>
                                                    </div>
                                                    <div
                                                      className="option-without-input"
                                                      style={{
                                                        display: "flex",
                                                        alignItems:
                                                          "flex-start",
                                                      }}
                                                    >
                                                      <label className="option-fbox">
                                                        <input
                                                          type="radio"
                                                          style={{
                                                            marginTop: "6px",
                                                          }}
                                                          id={idx}
                                                          name={
                                                            "Q38ElrexvayliRd" +
                                                            idx
                                                          }
                                                          value={99}
                                                          checked={
                                                            data.Q38ElrexvayliRd ===
                                                            99
                                                          }
                                                          onChange={
                                                            HandleRepeaterFieldChange
                                                          }
                                                        />

                                                        <span
                                                          style={{
                                                            paddingLeft: "10px",
                                                          }}
                                                        >
                                                          Date of dose frequency
                                                          reduction unknown
                                                        </span>
                                                      </label>
                                                    </div>

                                                    <ErrorField
                                                      show={
                                                        data.Q38ElrexEmptyHard
                                                      }
                                                      message={
                                                        ELCharacteristics_MSGS.administrationEmpty
                                                      }
                                                      style={{
                                                        paddingLeft: "0px",
                                                      }}
                                                    />
                                                    <ErrorField
                                                      show={
                                                        data.Q38ElrexDateInvalid
                                                      }
                                                      message={
                                                        ELCharacteristics_MSGS.Q38ElrexDateInvalid
                                                      }
                                                      style={{
                                                        paddingLeft: "0px",
                                                      }}
                                                    />
                                                    <ErrorField
                                                      show={data.Q38DateEmpty}
                                                      message={
                                                        ELCharacteristics_MSGS.Q38DateEmpty
                                                      }
                                                      style={{
                                                        paddingLeft: "0px",
                                                      }}
                                                    />

                                                    <ErrorField
                                                      show={
                                                        data.Invalid_dt_dosefreq_elr_dt
                                                      }
                                                      message={
                                                        CommonError_Msgs.InvalidDate
                                                      }
                                                      style={{
                                                        paddingLeft: "0px",
                                                      }}
                                                    />
                                                  </div>
                                                </div>
                                              </td>

                                              <td
                                                style={{
                                                  border: "1px solid black",
                                                  padding: "8px",
                                                }}
                                              >
                                                <div
                                                  className="option-without-input"
                                                  style={{ display: "flex" }}
                                                >
                                                  <label className="option-fbox">
                                                    <input
                                                      type="radio"
                                                      id={idx}
                                                      name={
                                                        "q38_Elr_Dose_week_frquencyRd" +
                                                        idx
                                                      }
                                                      value={1}
                                                      checked={
                                                        data.q38_Elr_Dose_week_frquencyRd ===
                                                        1
                                                      }
                                                      onChange={
                                                        HandleRepeaterFieldChange
                                                      }
                                                    />
                                                    <span
                                                      style={{
                                                        paddingLeft: "10px",
                                                        display: "inline-flex",
                                                      }}
                                                    >
                                                      <span>
                                                        Once every &nbsp;
                                                      </span>
                                                      <span>
                                                        <input
                                                          className="input-dash-sm"
                                                          id={idx}
                                                          name={
                                                            "q38_Elr_Dose_week_frquency_txt" +
                                                            idx
                                                          }
                                                          value={
                                                            data.q38_Elr_Dose_week_frquency_txt
                                                          }
                                                          type="text"
                                                          onChange={
                                                            HandleRepeaterFieldChange
                                                          }
                                                          onKeyDown={(e) => {
                                                            // Allow backspace, arrow keys, and delete
                                                            if (
                                                              [
                                                                "Backspace",
                                                                "ArrowLeft",
                                                                "ArrowRight",
                                                                "Delete",
                                                                "Tab",
                                                              ].includes(e.key)
                                                            ) {
                                                              return;
                                                            }

                                                            // Prevent non-numeric characters, except '.'
                                                            if (
                                                              !/[0-9.]/.test(
                                                                e.key,
                                                              )
                                                            ) {
                                                              e.preventDefault();
                                                              return;
                                                            }

                                                            const value =
                                                              e.target.value;
                                                            const selectionStart =
                                                              e.target
                                                                .selectionStart;
                                                            const newValue =
                                                              value.slice(
                                                                0,
                                                                selectionStart,
                                                              ) +
                                                              e.key +
                                                              value.slice(
                                                                e.target
                                                                  .selectionEnd,
                                                              );

                                                            // Prevent multiple '.'
                                                            if (
                                                              e.key === "." &&
                                                              value.includes(
                                                                ".",
                                                              )
                                                            ) {
                                                              e.preventDefault();
                                                              return;
                                                            }

                                                            // Prevent '.' as the first character
                                                            if (
                                                              e.key === "." &&
                                                              value === ""
                                                            ) {
                                                              e.preventDefault();
                                                              return;
                                                            }

                                                            // Allow only one digit after decimal point
                                                            if (
                                                              newValue.includes(
                                                                ".",
                                                              ) &&
                                                              newValue.split(
                                                                ".",
                                                              )[1].length > 2
                                                            ) {
                                                              e.preventDefault();
                                                              return;
                                                            }
                                                          }}
                                                        />
                                                      </span>
                                                      week
                                                    </span>
                                                  </label>
                                                </div>
                                                {/* new Unknown */}
                                                <div
                                                  className="option-without-input"
                                                  style={{ display: "flex" }}
                                                >
                                                  <label className="option-fbox">
                                                    <input
                                                      type="radio"
                                                      id={idx}
                                                      name={
                                                        "q38_Elr_Dose_week_frquencyRd" +
                                                        idx
                                                      }
                                                      value={99}
                                                      checked={
                                                        data.q38_Elr_Dose_week_frquencyRd ===
                                                        99
                                                      }
                                                      onChange={
                                                        HandleRepeaterFieldChange
                                                      }
                                                    />

                                                    <span
                                                      style={{
                                                        paddingLeft: "10px",
                                                        display: "flex",
                                                      }}
                                                    >
                                                      Unknown
                                                    </span>
                                                  </label>
                                                </div>
                                                <ErrorField
                                                  show={
                                                    data.q38_Elr_Dose_week_frquencyRd_Empty
                                                  }
                                                  message={
                                                    ELCharacteristics_MSGS.administrationEmpty
                                                  }
                                                  style={{
                                                    paddingLeft: "0px",
                                                  }}
                                                />
                                                <ErrorField
                                                  show={
                                                    data.q38_Elr_Dose_week_frquency_txt_Empty
                                                  }
                                                  message={
                                                    ELCharacteristics_MSGS.specifyDosageFrequencyAndRange
                                                  }
                                                  style={{
                                                    paddingLeft: "0px",
                                                  }}
                                                />

                                                <ErrorField
                                                  show={
                                                    data.q38ElrDoseFrequencyConflict
                                                  }
                                                  message={
                                                    ELCharacteristics_MSGS.Q38FrequencyConflict
                                                  }
                                                  style={{
                                                    paddingLeft: "0px",
                                                  }}
                                                />
                                              </td>
                                            </tr>
                                          </tbody>
                                        </table>
                                      </div>
                                    </div>
                                  </div>
                                ))}

                              {Q38ElrexDetails &&
                                Q38ElrexDetails.length > 0 &&
                                Q38ElrexDetails.length < 550 && (
                                  <div className="sub-question-bot-sapce-ul mt-2">
                                    <div
                                      className="answer-list-text"
                                      onClick={AddQ38ElrexvayliRow}
                                    >
                                      <a>
                                        <img
                                          width="18px"
                                          src="../Assets/images/plus.png"
                                          alt=""
                                        />
                                      </a>
                                      <a className="radio-text-padding">
                                        Click here to add another date of dose
                                        frequency reduction
                                      </a>
                                    </div>
                                  </div>
                                )}
                            </div>
                          )}
                        {/* Q38 end  */}
                        {/* Q38 Linvoseltamab */}
                        {isLinvoveySelected &&
                          Q37LinvoDetails[0].showDateRedFreqLinvo &&
                          !isNewEcrfA && (
                            <div className="dependent-section">
                              {Q38LinvoDetails &&
                                Q38LinvoDetails.length > 0 &&
                                Q38LinvoDetails.map((data, idx) => (
                                  <div className="loop-section-ul" key={idx}>
                                    <div className="loop-head">
                                      <span
                                        className="question-weight"
                                        style={{ float: "inline-start" }}
                                      >
                                        {GetOrdinal(idx + 1)} Dose frequency
                                        reductions
                                      </span>
                                      {idx != 0 && (
                                        <div
                                          className="delete-btn-div"
                                          style={{ float: "inline-end" }}
                                        >
                                          <img
                                            width="16px"
                                            src="../Assets/images/Icon-material-delete.png"
                                            alt=""
                                            style={{ cursor: "pointer" }}
                                            onClick={() =>
                                              DeleteQ38LinvoveyRow(idx)
                                            }
                                          />
                                        </div>
                                      )}
                                    </div>

                                    {/* LINVOSELTAMAB */}
                                    <div className="question-bot-sapce">
                                      <div className="sub-question grid-overflow">
                                        <table
                                          style={{
                                            border: "1px solid black",
                                            borderCollapse: "collapse",
                                            width: "100%",
                                          }}
                                        >
                                          <thead>
                                            <tr>
                                              <th
                                                style={{
                                                  border: "1px solid black",
                                                  padding: "8px",
                                                  textAlign: "center",
                                                }}
                                              >
                                                Treatment
                                              </th>
                                              <th
                                                style={{
                                                  border: "1px solid black",
                                                  padding: "8px",
                                                  textAlign: "center",
                                                  width: "15%",
                                                }}
                                              >
                                                Date of dose frequency reduction
                                              </th>
                                              <th
                                                style={{
                                                  border: "1px solid black",
                                                  padding: "8px",
                                                  textAlign: "center",
                                                }}
                                              >
                                                Dose frequency after reduction
                                              </th>
                                            </tr>
                                          </thead>

                                          <tbody>
                                            <tr>
                                              <td
                                                style={{
                                                  border: "1px solid black",
                                                  padding: "8px",
                                                }}
                                              >
                                                <span className="quest-text-pad">
                                                  LYNOZYFIC
                                                  <sup className="supscript">
                                                    TM
                                                  </sup>{" "}
                                                  (linvoquetamab-gcpt)
                                                </span>
                                              </td>

                                              <td
                                                style={{
                                                  border: "1px solid black",
                                                  padding: "8px",
                                                }}
                                              >
                                                <div>
                                                  <div>
                                                    <div
                                                      className="option-without-input"
                                                      style={{
                                                        display: "flex",
                                                      }}
                                                    >
                                                      <input
                                                        type="radio"
                                                        id={idx}
                                                        name={
                                                          "Q38LinvoveyRd" + idx
                                                        }
                                                        value={1}
                                                        checked={
                                                          data.Q38LinvoveyRd ===
                                                          1
                                                        }
                                                        onChange={
                                                          HandleRepeaterFieldChange
                                                        }
                                                      />
                                                      <span
                                                        className="radio-text-padding"
                                                        style={{
                                                          paddingLeft: "10px",
                                                          marginLeft: "-5px",
                                                        }}
                                                      >
                                                        <DatePickerComponent
                                                          ctrlId={
                                                            "dt_dosefreq_linvo_dt-" +
                                                            idx
                                                          }
                                                          HandleDateChange={
                                                            HandleRepeaterDateChange
                                                          }
                                                          minDate={dayjs(
                                                            "01-01-1920",
                                                          )}
                                                          maxDate={dayjs()}
                                                          locale="en" // current it is mm/dd/yyyy but for dd/mm/yyyy use "en-gb"
                                                          date={
                                                            data.dt_dosefreq_linvo_dt
                                                          }
                                                        />
                                                      </span>
                                                    </div>
                                                    <div
                                                      className="option-without-input"
                                                      style={{
                                                        display: "flex",
                                                        alignItems:
                                                          "flex-start",
                                                      }}
                                                    >
                                                      <label className="option-fbox">
                                                        <input
                                                          type="radio"
                                                          style={{
                                                            marginTop: "6px",
                                                          }}
                                                          id={idx}
                                                          name={
                                                            "Q38LinvoveyRd" +
                                                            idx
                                                          }
                                                          value={99}
                                                          checked={
                                                            data.Q38LinvoveyRd ===
                                                            99
                                                          }
                                                          onChange={
                                                            HandleRepeaterFieldChange
                                                          }
                                                        />

                                                        <span
                                                          className="radio-text-padding"
                                                          style={{
                                                            paddingLeft: "10px",
                                                          }}
                                                        >
                                                          Date of dose frequency
                                                          reduction unknown
                                                        </span>
                                                      </label>
                                                    </div>

                                                    <ErrorField
                                                      show={
                                                        data.Q38LinvoEmptyHard
                                                      }
                                                      message={
                                                        ELCharacteristics_MSGS.Q38LinvoEmptyHard
                                                      }
                                                      style={{
                                                        paddingLeft: "0px",
                                                      }}
                                                    />
                                                    <ErrorField
                                                      show={
                                                        data.Q38LinvoDateEmpty
                                                      }
                                                      message={
                                                        ELCharacteristics_MSGS.Q38LinvoDateEmpty
                                                      }
                                                      style={{
                                                        paddingLeft: "0px",
                                                      }}
                                                    />
                                                    <ErrorField
                                                      show={
                                                        data.Q38LinvoDateInvalid
                                                      }
                                                      message={
                                                        ELCharacteristics_MSGS.Q38LinvoDateInvalid
                                                      }
                                                      style={{
                                                        paddingLeft: "0px",
                                                      }}
                                                    />

                                                    <ErrorField
                                                      show={
                                                        data.InvalidQ38Linvoveydate
                                                      }
                                                      message={
                                                        CommonError_Msgs.InvalidDate
                                                      }
                                                      style={{
                                                        paddingLeft: "0px",
                                                      }}
                                                    />
                                                  </div>
                                                </div>
                                              </td>
                                              {/* </> */}
                                              {/* ))} */}

                                              <td
                                                style={{
                                                  border: "1px solid black",
                                                  padding: "8px",
                                                }}
                                              >
                                                <div
                                                  className="option-without-input"
                                                  style={{ display: "flex" }}
                                                >
                                                  <label className="option-fbox">
                                                    <input
                                                      type="radio"
                                                      id={idx}
                                                      name={
                                                        "q38_Linvo_Dose_week_frquencyRd" +
                                                        idx
                                                      }
                                                      value={1}
                                                      checked={
                                                        data.q38_Linvo_Dose_week_frquencyRd ===
                                                        1
                                                      }
                                                      onChange={
                                                        HandleRepeaterFieldChange
                                                      }
                                                    />
                                                    <span
                                                      style={{
                                                        paddingLeft: "10px",
                                                        display: "inline-flex",
                                                      }}
                                                    >
                                                      <span>
                                                        Once every &nbsp;
                                                      </span>
                                                      <span>
                                                        <input
                                                          className="input-dash-sm"
                                                          id={idx}
                                                          name={
                                                            "q38_Linvo_Dose_week_frquency_txt" +
                                                            idx
                                                          }
                                                          type="text"
                                                          onChange={
                                                            HandleRepeaterFieldChange
                                                          }
                                                          value={
                                                            data.q38_Linvo_Dose_week_frquency_txt
                                                          }
                                                          onKeyDown={(e) => {
                                                            // Allow backspace, arrow keys, and delete
                                                            if (
                                                              [
                                                                "Backspace",
                                                                "ArrowLeft",
                                                                "ArrowRight",
                                                                "Delete",
                                                                "Tab",
                                                              ].includes(e.key)
                                                            ) {
                                                              return;
                                                            }

                                                            // Prevent non-numeric characters, except '.'
                                                            if (
                                                              !/[0-9.]/.test(
                                                                e.key,
                                                              )
                                                            ) {
                                                              e.preventDefault();
                                                              return;
                                                            }

                                                            const value =
                                                              e.target.value;
                                                            const selectionStart =
                                                              e.target
                                                                .selectionStart;
                                                            const newValue =
                                                              value.slice(
                                                                0,
                                                                selectionStart,
                                                              ) +
                                                              e.key +
                                                              value.slice(
                                                                e.target
                                                                  .selectionEnd,
                                                              );

                                                            // Prevent multiple '.'
                                                            if (
                                                              e.key === "." &&
                                                              value.includes(
                                                                ".",
                                                              )
                                                            ) {
                                                              e.preventDefault();
                                                              return;
                                                            }

                                                            // Prevent '.' as the first character
                                                            if (
                                                              e.key === "." &&
                                                              value === ""
                                                            ) {
                                                              e.preventDefault();
                                                              return;
                                                            }

                                                            // Allow only one digit after decimal point
                                                            if (
                                                              newValue.includes(
                                                                ".",
                                                              ) &&
                                                              newValue.split(
                                                                ".",
                                                              )[1].length > 2
                                                            ) {
                                                              e.preventDefault();
                                                              return;
                                                            }
                                                          }}
                                                        />
                                                      </span>
                                                      week
                                                    </span>
                                                  </label>
                                                </div>
                                                {/* new Unknown */}
                                                <div
                                                  className="option-without-input"
                                                  style={{ display: "flex" }}
                                                >
                                                  <label className="option-fbox">
                                                    <input
                                                      type="radio"
                                                      id={idx}
                                                      name={
                                                        "q38_Linvo_Dose_week_frquencyRd" +
                                                        idx
                                                      }
                                                      value={99}
                                                      checked={
                                                        data.q38_Linvo_Dose_week_frquencyRd ===
                                                        99
                                                      }
                                                      onChange={
                                                        HandleRepeaterFieldChange
                                                      }
                                                    />

                                                    <span
                                                      style={{
                                                        paddingLeft: "10px",
                                                        display: "flex",
                                                      }}
                                                    >
                                                      Unknown
                                                    </span>
                                                  </label>
                                                </div>
                                                <div>
                                                  <ErrorField
                                                    show={
                                                      data.q38_Linvo_Dose_week_frquencyRd_Empty
                                                    }
                                                    message={
                                                      ELCharacteristics_MSGS.admissionEmpty
                                                    }
                                                    style={{
                                                      paddingLeft: "0px",
                                                    }}
                                                  />

                                                  <ErrorField
                                                    show={
                                                      data.q38_Linvo_Dose_week_frquency_txt_Empty
                                                    }
                                                    message={
                                                      ELCharacteristics_MSGS.specifyDosageFrequencyAndRange
                                                    }
                                                    style={{
                                                      paddingLeft: "0px",
                                                    }}
                                                  />

                                                  <ErrorField
                                                    show={
                                                      data.q38LinvoDoseFrequencyConflict
                                                    }
                                                    message={
                                                      ELCharacteristics_MSGS.Q38FrequencyConflict
                                                    }
                                                    style={{
                                                      paddingLeft: "0px",
                                                    }}
                                                  />
                                                </div>
                                              </td>
                                            </tr>

                                            {/* Linvoseltamab */}
                                          </tbody>
                                        </table>
                                      </div>
                                    </div>
                                  </div>
                                ))}

                              {Q38LinvoDetails &&
                                Q38LinvoDetails.length > 0 &&
                                Q38LinvoDetails.length < 550 && (
                                  <div className="sub-question-bot-sapce-ul mt-2">
                                    <div
                                      className="answer-list-text"
                                      onClick={AddQ38LinvoveyRow}
                                    >
                                      <a>
                                        <img
                                          width="18px"
                                          src="../Assets/images/plus.png"
                                          alt=""
                                        />
                                      </a>
                                      <a className="radio-text-padding">
                                        Click here to add another date of dose
                                        frequency reduction
                                      </a>
                                    </div>
                                  </div>
                                )}
                            </div>
                          )}
                        <br></br>
                        {/* Q38Linvoseltamab */}
                        {/* Q38.1.A start Elrex */}
                        {isElrexvayliSelected && isNewEcrfA == true && (
                          <div className="question question-weight">
                            <span>Q38.1.A</span>
                            <span className="quest-text-pad">
                              Please provide information on any{" "}
                              <i>dose frequency, or dose strength</i>, changes
                              that occurred from completion of the step-up
                              dosing phase until the end of the line of therapy
                              with{" "}
                              <span
                                style={{
                                  color: PageDesignConstants.fontColor,
                                  fontWeight: "bolder",
                                }}
                              >
                                {isElrexvayliSelected && (
                                  <>
                                    ELREXFIO<sup>TM</sup> (elranatamab-bcmm)
                                  </>
                                )}
                              </span>{" "}
                              Add additional rows for each type of change that
                              occurred.
                              <br />
                            </span>
                          </div>
                        )}
                        {/* Q38.1.A ELREXFIO */}
                        {isElrexvayliSelected && isNewEcrfA && (
                          <div className="dependent-section">
                            <ErrorField
                              show={q381AElrex_WarningHard}
                              message={
                                ELCharacteristics_MSGS.Q381AChainMismatchWarning
                              }
                              style={{ paddingLeft: "0px" }}
                            />
                            <ErrorField
                              show={q381AElrexQ36Mismatch}
                              message={
                                ELCharacteristics_MSGS.Q381AQ36PatternBeforeMismatch
                              }
                              style={{ paddingLeft: "0px" }}
                            />
                            {Q381AElrexDetails &&
                              Q381AElrexDetails.length > 0 &&
                              Q381AElrexDetails.map((data, idx) => (
                                <div className="loop-section-ul" key={idx}>
                                  <div className="loop-head">
                                    <span
                                      className="question-weight"
                                      style={{ float: "inline-start" }}
                                    >
                                      {GetOrdinal(idx + 1)} Dose/frequency
                                      change
                                    </span>
                                    {idx !== 0 && (
                                      <div
                                        className="delete-btn-div"
                                        style={{ float: "inline-end" }}
                                      >
                                        <img
                                          width="16px"
                                          src="../Assets/images/Icon-material-delete.png"
                                          alt=""
                                          style={{ cursor: "pointer" }}
                                          onClick={() =>
                                            DeleteQ381AElrexRow(idx)
                                          }
                                        />
                                      </div>
                                    )}
                                  </div>

                                  {/* ELREXFIO Table */}
                                  <div className="question-bot-sapce">
                                    <div className="sub-question grid-overflow">
                                      <table
                                        style={{
                                          border: "1px solid black",
                                          borderCollapse: "collapse",
                                          width: "100%",
                                        }}
                                      >
                                        <thead>
                                          <tr>
                                            <th
                                              style={{
                                                border: "1px solid black",
                                                padding: "8px",
                                                textAlign: "center",
                                              }}
                                            >
                                              Treatment
                                            </th>
                                            <th
                                              style={{
                                                border: "1px solid black",
                                                padding: "8px",
                                                textAlign: "center",
                                                minWidth: "150px",
                                              }}
                                            >
                                              Type of change
                                            </th>
                                            <th
                                              style={{
                                                border: "1px solid black",
                                                padding: "8px",
                                                textAlign: "center",
                                                minWidth: "280px",
                                              }}
                                            >
                                              Reason for change
                                            </th>
                                            <th
                                              style={{
                                                border: "1px solid black",
                                                padding: "8px",
                                                textAlign: "center",
                                              }}
                                            >
                                              Pattern <i>before</i> change
                                            </th>
                                            <th
                                              style={{
                                                border: "1px solid black",
                                                padding: "8px",
                                                textAlign: "center",
                                              }}
                                            >
                                              Pattern <i>after</i> change
                                            </th>
                                            <th
                                              style={{
                                                border: "1px solid black",
                                                padding: "8px",
                                                textAlign: "center",
                                              }}
                                            >
                                              Date of change
                                            </th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          <tr>
                                            {/* Column A: Treatment */}
                                            <td
                                              style={{
                                                border: "1px solid black",
                                                padding: "8px",
                                              }}
                                            >
                                              <span className="quest-text-pad">
                                                ELREXFIO<sup>TM</sup>{" "}
                                                (elranatamab-bcmm)
                                              </span>
                                            </td>

                                            <td
                                              style={{
                                                border: "1px solid black",
                                                padding: "8px",
                                              }}
                                            >
                                              {/* Radio 1 - No changes */}

                                              <div className="option-without-input">
                                                <label className="option-fbox">
                                                  <input
                                                    type="radio"
                                                    id={idx}
                                                    name={`changeTypeElr${idx}`}
                                                    value="1"
                                                    checked={
                                                      Number(
                                                        data.changeType,
                                                      ) === 1
                                                    }
                                                    onChange={
                                                      HandleRepeaterFieldChange
                                                    }
                                                  />
                                                  <span
                                                    className="radio-text-padding"
                                                    style={{
                                                      paddingLeft: "10px",
                                                      marginLeft: "-5px",
                                                    }}
                                                  >
                                                    No dose frequency or dose
                                                    strength changes occurred
                                                  </span>
                                                </label>
                                              </div>

                                              {/* Radio 2 - Frequency change */}
                                              <div className="option-without-input">
                                                <label className="option-fbox">
                                                  <input
                                                    style={{ marginTop: "6px" }}
                                                    type="radio"
                                                    id={idx}
                                                    name={`changeTypeElr${idx}`}
                                                    value="2"
                                                    checked={
                                                      Number(
                                                        data.changeType,
                                                      ) === 2
                                                    }
                                                    onChange={
                                                      HandleRepeaterFieldChange
                                                    }
                                                  />
                                                  <span
                                                    className="radio-text-padding"
                                                    style={{
                                                      paddingLeft: "10px",
                                                      marginLeft: "-5px",
                                                    }}
                                                  >
                                                    Dose frequency change
                                                  </span>
                                                </label>
                                              </div>

                                              {/* Sub-radio for Frequency Increase/Decrease */}
                                              {data.changeType === 2 && (
                                                <div
                                                  style={{
                                                    paddingLeft: "15px",
                                                  }}
                                                >
                                                  <div
                                                    className="option-without-input"
                                                    style={{ display: "flex" }}
                                                  >
                                                    <label className="option-fbox">
                                                      <input
                                                        type="radio"
                                                        id={idx}
                                                        name={`changeTypeDoseFrequencyElr${idx}`}
                                                        value="1"
                                                        checked={
                                                          data.changeTypeDoseFrequency ===
                                                          1
                                                        }
                                                        onChange={
                                                          HandleRepeaterFieldChange
                                                        }
                                                      />
                                                      <span
                                                        style={{
                                                          paddingLeft: "10px",
                                                        }}
                                                      >
                                                        Frequency increase
                                                      </span>
                                                    </label>
                                                  </div>
                                                  <div className="option-without-input">
                                                    <label className="option-fbox">
                                                      <input
                                                        type="radio"
                                                        style={{
                                                          marginTop: "6px",
                                                        }}
                                                        id={idx}
                                                        name={`changeTypeDoseFrequencyElr${idx}`}
                                                        value="2"
                                                        checked={
                                                          data.changeTypeDoseFrequency ===
                                                          2
                                                        }
                                                        onChange={
                                                          HandleRepeaterFieldChange
                                                        }
                                                      />
                                                      <span
                                                        style={{
                                                          paddingLeft: "10px",
                                                        }}
                                                      >
                                                        Frequency decrease
                                                      </span>
                                                    </label>
                                                  </div>

                                                  <ErrorField
                                                    show={
                                                      data.changeTypeDoseFrequencyEmpty
                                                    }
                                                    message={
                                                      ELCharacteristics_MSGS.ChangeTypeDoseFrequencyEmpty
                                                    }
                                                    style={{
                                                      paddingLeft: "0px",
                                                    }}
                                                  />
                                                </div>
                                              )}

                                              {/* Radio 3 - Strength change */}
                                              <div className="option-without-input">
                                                <label className="option-fbox">
                                                  <input
                                                    type="radio"
                                                    id={idx}
                                                    name={`changeTypeElr${idx}`}
                                                    value="3"
                                                    checked={
                                                      Number(
                                                        data.changeType,
                                                      ) === 3
                                                    }
                                                    onChange={
                                                      HandleRepeaterFieldChange
                                                    }
                                                  />
                                                  <span
                                                    className="radio-text-padding"
                                                    style={{
                                                      paddingLeft: "10px",
                                                      marginLeft: "-5px",
                                                    }}
                                                  >
                                                    Dose strength change
                                                  </span>
                                                </label>
                                              </div>

                                              {/* Sub-radio for Strength Increase/Decrease */}
                                              {data.changeType === 3 && (
                                                <div
                                                  style={{
                                                    paddingLeft: "15px",
                                                  }}
                                                >
                                                  <div
                                                    className="option-without-input"
                                                    style={{ display: "flex" }}
                                                  >
                                                    <label className="option-fbox">
                                                      <input
                                                        type="radio"
                                                        id={idx}
                                                        name={`changeTypeDoseStrengthElr${idx}`}
                                                        value="1"
                                                        checked={
                                                          data.changeTypeDoseStrength ===
                                                          1
                                                        }
                                                        onChange={
                                                          HandleRepeaterFieldChange
                                                        }
                                                      />
                                                      <span
                                                        style={{
                                                          paddingLeft: "10px",
                                                        }}
                                                      >
                                                        Strength increase
                                                      </span>
                                                    </label>
                                                  </div>
                                                  <div className="option-without-input">
                                                    <label className="option-fbox">
                                                      <input
                                                        type="radio"
                                                        style={{
                                                          marginTop: "6px",
                                                        }}
                                                        id={idx}
                                                        name={`changeTypeDoseStrengthElr${idx}`}
                                                        value="2"
                                                        checked={
                                                          data.changeTypeDoseStrength ===
                                                          2
                                                        }
                                                        onChange={
                                                          HandleRepeaterFieldChange
                                                        }
                                                      />
                                                      <span
                                                        style={{
                                                          paddingLeft: "10px",
                                                        }}
                                                      >
                                                        Strength decrease
                                                      </span>
                                                    </label>
                                                  </div>
                                                  <ErrorField
                                                    show={
                                                      data.changeTypeDoseStrengthEmpty
                                                    }
                                                    message={
                                                      ELCharacteristics_MSGS.ChangeTypeDoseStrengthEmpty
                                                    }
                                                    style={{
                                                      paddingLeft: "0px",
                                                    }}
                                                  />
                                                </div>
                                              )}

                                              {/* Radio 4 - Unknown */}
                                              <div
                                                className="option-without-input"
                                                style={{
                                                  display: "flex",
                                                  alignItems: "flex-start",
                                                }}
                                              >
                                                <label className="option-fbox">
                                                  <input
                                                    type="radio"
                                                    style={{ marginTop: "6px" }}
                                                    id={idx}
                                                    name={`changeTypeElr${idx}`}
                                                    value={99}
                                                    checked={
                                                      data.changeType === 99
                                                    }
                                                    onChange={
                                                      HandleRepeaterFieldChange
                                                    }
                                                  />
                                                  <span
                                                    style={{
                                                      paddingLeft: "10px",
                                                    }}
                                                  >
                                                    Unknown if any dose changes
                                                    occurred
                                                  </span>
                                                </label>
                                              </div>

                                              <ErrorField
                                                show={data.changeTypeEmpty}
                                                message={
                                                  ELCharacteristics_MSGS.ChangeTypeEmpty
                                                }
                                                style={{ paddingLeft: "0px" }}
                                              />
                                            </td>

                                            {/* Column C: Reasons for change */}
                                            <td
                                              style={{
                                                border: "1px solid black",
                                                padding: "8px",
                                                textAlign: "start",
                                              }}
                                            >
                                              {data.changeType !== 1 &&
                                              data.changeType !== 99 ? (
                                                <div>
                                                  {/* Primary Reasons */}
                                                  {data.reasonList?.map(
                                                    (reason, itemidx) => (
                                                      <div
                                                        key={
                                                          reason.bispecifictreatmentdiscontinuationreasonid
                                                        }
                                                        className="option-without-input"
                                                        style={{
                                                          display: "flex",
                                                          flexDirection:
                                                            "column",
                                                        }}
                                                      >
                                                        <label className="option-fbox">
                                                          <input
                                                            type="checkbox"
                                                            id={`reasonElr_${idx}_${itemidx}`}
                                                            name={`reasonElr_${idx}_${itemidx}`}
                                                            idx={idx}
                                                            itemidx={itemidx}
                                                            value={
                                                              reason.bispecifictreatmentdiscontinuationreasonid
                                                            }
                                                            checked={
                                                              reason.isSelected ||
                                                              false
                                                            }
                                                            onChange={(e) =>
                                                              HandleQ381ARepeaterFieldChange(
                                                                e,
                                                                "Elrex",
                                                                reason.reasonName,
                                                                idx,
                                                                itemidx,
                                                                null,
                                                              )
                                                            }
                                                          />
                                                          <span
                                                            style={{
                                                              paddingLeft:
                                                                "10px",
                                                            }}
                                                          >
                                                            {reason.reasonName}
                                                            {reason.superscripttext && (
                                                              <sup>
                                                                {
                                                                  reason.superscripttext
                                                                }
                                                              </sup>
                                                            )}
                                                          </span>
                                                        </label>

                                                        {/* "Other" text field - Show only if this reason contains "Other" and is selected */}
                                                        {reason.reasonName?.includes(
                                                          "Other",
                                                        ) && (
                                                          <div
                                                            style={{
                                                              paddingLeft:
                                                                "25px",
                                                              marginTop: "5px",
                                                            }}
                                                          >
                                                            <textarea
                                                              className="input-dash"
                                                              id={`reasonTextElr_${idx}_${itemidx}`}
                                                              name={`reasonTextElr_${idx}_${itemidx}`}
                                                              idx={idx}
                                                              itemidx={itemidx}
                                                              value={
                                                                Q381AElrexDetails[
                                                                  idx
                                                                ]
                                                                  ?.reasonOtherText ||
                                                                ""
                                                              }
                                                              onChange={(e) =>
                                                                HandleQ381ARepeaterFieldChange(
                                                                  e,
                                                                  "Elrex",
                                                                  reason.reasonName,
                                                                  idx,
                                                                  itemidx,
                                                                  null,
                                                                )
                                                              }
                                                              maxLength={300}
                                                              style={{
                                                                width: "100%",
                                                                minHeight:
                                                                  "60px",
                                                                resize:
                                                                  "vertical",
                                                              }}
                                                            />
                                                            <div
                                                              style={{
                                                                fontSize:
                                                                  "11px",
                                                                color: "#666",
                                                                marginTop:
                                                                  "2px",
                                                              }}
                                                            ></div>
                                                            <ErrorField
                                                              show={
                                                                data.reasonOtherTextEmpty
                                                              }
                                                              message={
                                                                ELCharacteristics_MSGS.ReasonOtherTextEmpty
                                                              }
                                                              style={{
                                                                paddingLeft:
                                                                  "0px",
                                                              }}
                                                            />
                                                          </div>
                                                        )}

                                                        {/* AE Sub-reasons - Only show if this is "Adverse event" (ID=2) and it's selected */}
                                                        {reason.bispecifictreatmentdiscontinuationreasonid ===
                                                          2 &&
                                                          reason.isSelected &&
                                                          reason.aeSubReasons
                                                            ?.length > 0 && (
                                                            <div
                                                              style={{
                                                                paddingLeft:
                                                                  "25px",
                                                                marginTop:
                                                                  "5px",
                                                              }}
                                                            >
                                                              {reason.aeSubReasons.map(
                                                                (ae, aeIdx) => {
                                                                  // ✅ Check if this is "AE type unknown" - check BOTH possible property names
                                                                  const aeName =
                                                                    ae.reasonName ||
                                                                    ae.bispecifictreatmentdiscontinuationreasonae ||
                                                                    "";
                                                                  const isAEUnknown =
                                                                    aeName
                                                                      .toLowerCase()
                                                                      .includes(
                                                                        "unknown",
                                                                      );

                                                                  return (
                                                                    <div
                                                                      key={
                                                                        ae.bispecifictreatmentdiscontinuationreasonaeid
                                                                      }
                                                                      className="option-without-input"
                                                                    >
                                                                      <label className="option-fbox">
                                                                        <input
                                                                          type={
                                                                            isAEUnknown
                                                                              ? "radio"
                                                                              : "checkbox"
                                                                          } // ✅ Radio for unknown, checkbox for others
                                                                          id={`aeReasonElr_${idx}_${itemidx}_${aeIdx}`}
                                                                          name={
                                                                            isAEUnknown
                                                                              ? `aeUnknownElr_${idx}_${itemidx}`
                                                                              : `aeReasonElr_${idx}_${itemidx}_${aeIdx}`
                                                                          }
                                                                          idx={
                                                                            idx
                                                                          }
                                                                          itemidx={
                                                                            itemidx
                                                                          }
                                                                          aeidx={
                                                                            aeIdx
                                                                          }
                                                                          value={
                                                                            ae.bispecifictreatmentdiscontinuationreasonaeid
                                                                          }
                                                                          checked={
                                                                            ae.isSelected ||
                                                                            false
                                                                          }
                                                                          onChange={(
                                                                            e,
                                                                          ) =>
                                                                            HandleQ381ARepeaterFieldChange(
                                                                              e,
                                                                              "Elrex",
                                                                              reason.reasonName,
                                                                              idx,
                                                                              itemidx,
                                                                              aeIdx,
                                                                            )
                                                                          }
                                                                        />
                                                                        <span
                                                                          style={{
                                                                            paddingLeft:
                                                                              "10px",
                                                                          }}
                                                                        >
                                                                          {
                                                                            ae.reasonName
                                                                          }{" "}
                                                                          {/* ✅ Using the correct property name from your original code */}
                                                                          {ae.superscripttext && (
                                                                            <sup>
                                                                              {
                                                                                ae.superscripttext
                                                                              }
                                                                            </sup>
                                                                          )}
                                                                        </span>
                                                                      </label>
                                                                      {ae.reasonName ===
                                                                        DiscontinuationReason.NeurologicalEvent &&
                                                                        ae.isSelected && (
                                                                          <div
                                                                            style={{
                                                                              marginLeft:
                                                                                "28px",
                                                                              marginTop:
                                                                                "8px",
                                                                            }}
                                                                          >
                                                                            {data
                                                                              .neurologicAEReasonList
                                                                              ?.length >
                                                                              0 &&
                                                                              data.neurologicAEReasonList.map(
                                                                                (
                                                                                  aeNeuro,
                                                                                  aeNeuroIdx,
                                                                                ) => {
                                                                                  return (
                                                                                    <div
                                                                                      key={
                                                                                        aeNeuroIdx
                                                                                      }
                                                                                    >
                                                                                      <label
                                                                                        className="d-flex align-items-start"
                                                                                        htmlFor={`elr_adverse_neuro_${itemidx}_${aeIdx}_${aeNeuroIdx}`}
                                                                                      >
                                                                                        <input
                                                                                          type="checkbox"
                                                                                          id={`elr_adverse_neuro_${itemidx}_${aeIdx}_${aeNeuroIdx}`}
                                                                                          name={`elr_adverse_neuro_${itemidx}_${aeIdx}_${aeNeuroIdx}`}
                                                                                          checked={
                                                                                            aeNeuro.isSelected
                                                                                          }
                                                                                          onChange={(
                                                                                            e,
                                                                                          ) =>
                                                                                            HandleNeuroRepeaterFieldChange(
                                                                                              e,
                                                                                              "381A",
                                                                                              RRMMCohortTreatment.ELREXFIO,
                                                                                              aeNeuroIdx,
                                                                                              idx,
                                                                                            )
                                                                                          }
                                                                                          style={{
                                                                                            marginTop:
                                                                                              "6px",
                                                                                          }}
                                                                                        />

                                                                                        <span
                                                                                          style={{
                                                                                            textAlign:
                                                                                              "left",
                                                                                          }}
                                                                                          className="radio-text-padding"
                                                                                        >
                                                                                          {
                                                                                            aeNeuro.reasonName
                                                                                          }
                                                                                        </span>
                                                                                      </label>

                                                                                      {aeNeuro.reasonName.includes(
                                                                                        "Other",
                                                                                      ) && (
                                                                                        <div>
                                                                                          <label
                                                                                            htmlFor={`elr_neuro_reasontxt_${itemidx}_${aeNeuroIdx}`}
                                                                                          >
                                                                                            <span
                                                                                              style={{
                                                                                                paddingLeft:
                                                                                                  "28px",
                                                                                              }}
                                                                                              className="radio-text-padding"
                                                                                            >
                                                                                              <textarea
                                                                                                style={{
                                                                                                  width:
                                                                                                    "264px",
                                                                                                }}
                                                                                                id={`elr_neuro_reasontxt_${itemidx}_${aeNeuroIdx}`}
                                                                                                name={`elr_neuro_reasontxt_${itemidx}_${aeNeuroIdx}`}
                                                                                                type="text"
                                                                                                value={
                                                                                                  aeNeuro.reasonText
                                                                                                }
                                                                                                onChange={(
                                                                                                  e,
                                                                                                ) =>
                                                                                                  HandleNeuroRepeaterFieldChange(
                                                                                                    e,
                                                                                                    "381A",
                                                                                                    RRMMCohortTreatment.ELREXFIO,
                                                                                                    aeNeuroIdx,
                                                                                                    idx,
                                                                                                  )
                                                                                                }
                                                                                                maxLength={
                                                                                                  300
                                                                                                }
                                                                                              />
                                                                                            </span>
                                                                                          </label>
                                                                                          <ErrorField
                                                                                            show={
                                                                                              data.neuroOtherTextEmpty
                                                                                            }
                                                                                            message={
                                                                                              ELCharacteristics_MSGS.Q38AENeurologicOther
                                                                                            }
                                                                                          />
                                                                                        </div>
                                                                                      )}
                                                                                    </div>
                                                                                  );
                                                                                },
                                                                              )}
                                                                            <ErrorField
                                                                              show={
                                                                                data.neuroReasonEmpty
                                                                              }
                                                                              message={
                                                                                ELCharacteristics_MSGS.Q38AENeurologicEmpty
                                                                              }
                                                                            />
                                                                          </div>
                                                                        )}
                                                                    </div>
                                                                  );
                                                                },
                                                              )}
                                                              <ErrorField
                                                                show={
                                                                  data.aeSubReasonEmpty
                                                                }
                                                                message={
                                                                  ELCharacteristics_MSGS.Q38AEEmpty
                                                                }
                                                                style={{
                                                                  paddingLeft:
                                                                    "0px",
                                                                }}
                                                              />
                                                            </div>
                                                          )}
                                                      </div>
                                                    ),
                                                  )}

                                                  {/* ELREXFIO - Unknown Option */}
                                                  <div
                                                    className="option-without-input"
                                                    style={{
                                                      display: "flex",
                                                      marginTop: "10px",
                                                    }}
                                                  >
                                                    <label className="option-fbox">
                                                      <input
                                                        type="radio"
                                                        id={idx}
                                                        name={
                                                          "reasonUnknownElr" +
                                                          idx
                                                        }
                                                        checked={
                                                          data.reasonUnknown ||
                                                          false
                                                        }
                                                        value={true}
                                                        onChange={
                                                          HandleRepeaterFieldChange
                                                        }
                                                      />
                                                      <span
                                                        style={{
                                                          paddingLeft: "10px",
                                                        }}
                                                      >
                                                        Unknown
                                                      </span>
                                                    </label>
                                                  </div>

                                                  <ErrorField
                                                    show={data.reasonEmpty}
                                                    message={
                                                      ELCharacteristics_MSGS.Q38ReasonEmpty
                                                    }
                                                    style={{
                                                      paddingLeft: "0px",
                                                    }}
                                                  />

                                                  <ErrorField
                                                    show={data.deathDtWarning}
                                                    message={
                                                      ELCharacteristics_MSGS.deathWarningQ20
                                                    }
                                                    style={{
                                                      paddingLeft: "0px",
                                                    }}
                                                  />
                                                </div>
                                              ) : (
                                                <span
                                                  style={{
                                                    textAlign: "center",
                                                  }}
                                                  className="not-applicable"
                                                >
                                                  <b>Not applicable</b>
                                                </span>
                                              )}
                                            </td>

                                            {/* Column D - Pattern BEFORE change */}
                                            <td
                                              style={{
                                                border: "1px solid black",
                                                padding: "8px",
                                                textAlign: "start",
                                              }}
                                            >
                                              {data.changeType === null ||
                                              data.changeType === 99 ||
                                              data.changeType === 1 ? (
                                                <span
                                                  style={{
                                                    textAlign: "center",
                                                  }}
                                                  className="not-applicable"
                                                >
                                                  <b>Not applicable</b>
                                                </span>
                                              ) : (
                                                <div>
                                                  {/* Frequency Pattern - only show if changeType is 2 (Frequency) */}
                                                  {data.changeType === 2 && (
                                                    <div>
                                                      {/* ✅ Option 1: Times per week WITH TEXT INPUT */}
                                                      <div
                                                        className="option-without-input"
                                                        style={{
                                                          display: "flex",
                                                        }}
                                                      >
                                                        <label className="option-fbox">
                                                          <input
                                                            type="radio"
                                                            id={idx}
                                                            name={`patterBeforeChangeDoseFrequencyElr${idx}`}
                                                            value={1}
                                                            checked={
                                                              data.patterBeforeChangeDoseFrequency ===
                                                              1
                                                            }
                                                            onChange={
                                                              HandleRepeaterFieldChange
                                                            }
                                                          />
                                                          <span
                                                            style={{
                                                              paddingLeft:
                                                                "10px",
                                                              display: "flex",
                                                              alignItems:
                                                                "center",
                                                            }}
                                                          >
                                                            <input
                                                              className="input-dash-sm"
                                                              id={idx}
                                                              name={`patterBeforeChangeDoseFrequencyTimesPerWeekElr${idx}`}
                                                              value={
                                                                data.patterBeforeChangeDoseFrequencyTimesPerWeek ||
                                                                ""
                                                              }
                                                              type="text"
                                                              style={{
                                                                width: "50px",
                                                                margin: "0 5px",
                                                              }}
                                                              onChange={
                                                                HandleRepeaterFieldChange
                                                              }
                                                              onKeyDown={(
                                                                e,
                                                              ) => {
                                                                if (
                                                                  [
                                                                    "Backspace",
                                                                    "ArrowLeft",
                                                                    "ArrowRight",
                                                                    "Delete",
                                                                    "Tab",
                                                                  ].includes(
                                                                    e.key,
                                                                  )
                                                                )
                                                                  return;
                                                                if (
                                                                  !/[0-9]/.test(
                                                                    e.key,
                                                                  )
                                                                )
                                                                  e.preventDefault();
                                                              }}
                                                            />
                                                            times per week
                                                          </span>
                                                        </label>
                                                      </div>
                                                      {/* Option 2: Every X weeks */}
                                                      <div
                                                        className="option-without-input"
                                                        style={{
                                                          display: "flex",
                                                        }}
                                                      >
                                                        <label className="option-fbox">
                                                          <input
                                                            type="radio"
                                                            id={idx}
                                                            name={`patterBeforeChangeDoseFrequencyElr${idx}`}
                                                            value={2}
                                                            checked={
                                                              data.patterBeforeChangeDoseFrequency ===
                                                              2
                                                            }
                                                            onChange={
                                                              HandleRepeaterFieldChange
                                                            }
                                                          />
                                                          <span
                                                            style={{
                                                              paddingLeft:
                                                                "10px",
                                                              display: "flex",
                                                              alignItems:
                                                                "center",
                                                            }}
                                                          >
                                                            Once every
                                                            <input
                                                              className="input-dash-sm"
                                                              id={idx}
                                                              name={`patterBeforeChangeDoseFrequencyWeekElr${idx}`}
                                                              value={
                                                                data.patterBeforeChangeDoseFrequencyWeek ||
                                                                ""
                                                              }
                                                              type="text"
                                                              style={{
                                                                width: "50px",
                                                                margin: "0 5px",
                                                              }}
                                                              onChange={
                                                                HandleRepeaterFieldChange
                                                              }
                                                              onKeyDown={(
                                                                e,
                                                              ) => {
                                                                if (
                                                                  [
                                                                    "Backspace",
                                                                    "ArrowLeft",
                                                                    "ArrowRight",
                                                                    "Delete",
                                                                    "Tab",
                                                                  ].includes(
                                                                    e.key,
                                                                  )
                                                                )
                                                                  return;
                                                                if (
                                                                  !/[0-9]/.test(
                                                                    e.key,
                                                                  )
                                                                )
                                                                  e.preventDefault();
                                                              }}
                                                            />
                                                            week(s)
                                                          </span>
                                                        </label>
                                                      </div>
                                                      {/* Unknown */}
                                                      <div className="option-without-input">
                                                        <label className="option-fbox">
                                                          <input
                                                            type="radio"
                                                            id={idx}
                                                            name={`patterBeforeChangeDoseFrequencyElr${idx}`}
                                                            value={99}
                                                            checked={
                                                              data.patterBeforeChangeDoseFrequency ===
                                                              99
                                                            }
                                                            onChange={
                                                              HandleRepeaterFieldChange
                                                            }
                                                          />
                                                          <span
                                                            style={{
                                                              paddingLeft:
                                                                "10px",
                                                            }}
                                                          >
                                                            Unknown
                                                          </span>
                                                        </label>
                                                      </div>
                                                      <ErrorField
                                                        show={
                                                          data.patternBeforeFrequencyEmpty
                                                        }
                                                        message={
                                                          ELCharacteristics_MSGS.frequencyPatternEmpty
                                                        }
                                                        style={{
                                                          paddingLeft: "0px",
                                                        }}
                                                      />
                                                      <ErrorField
                                                        show={
                                                          data.patternBeforeFrequencyRangeInvalid
                                                        }
                                                        message={
                                                          ELCharacteristics_MSGS.frequencyRange
                                                        }
                                                        style={{
                                                          paddingLeft: "0px",
                                                        }}
                                                      />

                                                      <ErrorField
                                                        show={
                                                          data.patternBeforeUnknownWarning
                                                        }
                                                        message={
                                                          ELCharacteristics_MSGS.Q381AWarningUnk
                                                        }
                                                        style={{
                                                          paddingLeft: "0px",
                                                        }}
                                                      />
                                                    </div>
                                                  )}

                                                  {/* Strength Pattern - only show if changeType is 3 (Strength) */}
                                                  {data.changeType === 3 && (
                                                    <div
                                                      style={{
                                                        marginTop:
                                                          data.changeType === 2
                                                            ? "15px"
                                                            : "0",
                                                      }}
                                                    >
                                                      {/* mg option */}
                                                      <div
                                                        className="option-without-input"
                                                        style={{
                                                          display: "flex",
                                                        }}
                                                      >
                                                        <label className="option-fbox">
                                                          <input
                                                            type="radio"
                                                            id={idx}
                                                            name={`patterBeforeChangeDoseStrengthElr${idx}`}
                                                            value={1}
                                                            checked={
                                                              data.patterBeforeChangeDoseStrength ===
                                                              1
                                                            }
                                                            onChange={
                                                              HandleRepeaterFieldChange
                                                            }
                                                          />
                                                          <span
                                                            style={{
                                                              paddingLeft:
                                                                "10px",
                                                              display: "flex",
                                                            }}
                                                          >
                                                            <input
                                                              className="input-dash-sm"
                                                              id={idx}
                                                              name={`patterBeforeChangeDoseStrengthMgElr${idx}`}
                                                              value={
                                                                data.patterBeforeChangeDoseStrengthMg ||
                                                                ""
                                                              }
                                                              type="text"
                                                              onChange={
                                                                HandleRepeaterFieldChange
                                                              }
                                                              onKeyDown={
                                                                AllowNumericWithDecimal
                                                              }
                                                            />
                                                            <span>
                                                              &nbsp;mg
                                                            </span>
                                                          </span>
                                                        </label>
                                                      </div>

                                                      {/* mg/kg option */}
                                                      <div
                                                        className="option-without-input"
                                                        style={{
                                                          display: "flex",
                                                        }}
                                                      >
                                                        <label className="option-fbox">
                                                          <input
                                                            type="radio"
                                                            id={idx}
                                                            name={`patterBeforeChangeDoseStrengthElr${idx}`}
                                                            value={2}
                                                            checked={
                                                              data.patterBeforeChangeDoseStrength ===
                                                              2
                                                            }
                                                            onChange={
                                                              HandleRepeaterFieldChange
                                                            }
                                                          />
                                                          <span
                                                            style={{
                                                              paddingLeft:
                                                                "10px",
                                                              display: "flex",
                                                            }}
                                                          >
                                                            <input
                                                              className="input-dash-sm"
                                                              id={idx}
                                                              name={`patterBeforeChangeDoseStrengthMgKgElr${idx}`}
                                                              value={
                                                                data.patterBeforeChangeDoseStrengthMgKg ||
                                                                ""
                                                              }
                                                              type="text"
                                                              onChange={
                                                                HandleRepeaterFieldChange
                                                              }
                                                              onKeyDown={
                                                                AllowNumericWithDecimal
                                                              }
                                                            />
                                                            <span>
                                                              &nbsp;mg/kg
                                                            </span>
                                                          </span>
                                                        </label>
                                                      </div>

                                                      {/* mg/m² option */}
                                                      <div
                                                        className="option-without-input"
                                                        style={{
                                                          display: "flex",
                                                        }}
                                                      >
                                                        <label className="option-fbox">
                                                          <input
                                                            type="radio"
                                                            id={idx}
                                                            name={`patterBeforeChangeDoseStrengthElr${idx}`}
                                                            value={3}
                                                            checked={
                                                              data.patterBeforeChangeDoseStrength ===
                                                              3
                                                            }
                                                            onChange={
                                                              HandleRepeaterFieldChange
                                                            }
                                                          />
                                                          <span
                                                            style={{
                                                              paddingLeft:
                                                                "10px",
                                                              display: "flex",
                                                            }}
                                                          >
                                                            <input
                                                              className="input-dash-sm"
                                                              id={idx}
                                                              name={`patterBeforeChangeDoseStrengthMgM2Elr${idx}`}
                                                              value={
                                                                data.patterBeforeChangeDoseStrengthMgM2 ||
                                                                ""
                                                              }
                                                              type="text"
                                                              onChange={
                                                                HandleRepeaterFieldChange
                                                              }
                                                              onKeyDown={
                                                                AllowNumericWithDecimal
                                                              }
                                                            />
                                                            <span>
                                                              &nbsp;mg/m²
                                                            </span>
                                                          </span>
                                                        </label>
                                                      </div>

                                                      {/* Unknown option */}
                                                      <div className="option-without-input">
                                                        <label className="option-fbox">
                                                          <input
                                                            type="radio"
                                                            id={idx}
                                                            name={`patterBeforeChangeDoseStrengthElr${idx}`}
                                                            value={99}
                                                            checked={
                                                              data.patterBeforeChangeDoseStrength ===
                                                              99
                                                            }
                                                            onChange={
                                                              HandleRepeaterFieldChange
                                                            }
                                                          />
                                                          <span
                                                            style={{
                                                              paddingLeft:
                                                                "10px",
                                                            }}
                                                          >
                                                            Unknown
                                                          </span>
                                                        </label>
                                                      </div>

                                                      <ErrorField
                                                        show={
                                                          data.patternBeforeStrengthEmpty
                                                        }
                                                        message={
                                                          ELCharacteristics_MSGS.strengthPatternEmpty
                                                        }
                                                        style={{
                                                          paddingLeft: "0px",
                                                        }}
                                                      />

                                                      <ErrorField
                                                        show={
                                                          data.patternBeforeFrequencyRangeInvalid
                                                        }
                                                        message={
                                                          ELCharacteristics_MSGS.DecimalRange
                                                        }
                                                        style={{
                                                          paddingLeft: "0px",
                                                        }}
                                                      />

                                                      <ErrorField
                                                        show={
                                                          data.patternBeforeUnknownWarning
                                                        }
                                                        message={
                                                          ELCharacteristics_MSGS.Q381AWarningUnk
                                                        }
                                                        style={{
                                                          paddingLeft: "0px",
                                                        }}
                                                      />
                                                    </div>
                                                  )}
                                                </div>
                                              )}
                                            </td>

                                            {/* Column E: Pattern AFTER change */}
                                            <td
                                              style={{
                                                border: "1px solid black",
                                                padding: "8px",
                                                textAlign: "start",
                                              }}
                                            >
                                              {data.changeType === null ||
                                              data.changeType === 99 ||
                                              data.changeType === 1 ? (
                                                <span
                                                  style={{
                                                    textAlign: "center",
                                                  }}
                                                  className="not-applicable"
                                                >
                                                  <b>Not applicable</b>
                                                </span>
                                              ) : (
                                                <div>
                                                  {/* Frequency Pattern - only show if changeType is 2 (Frequency) */}
                                                  {data.changeType === 2 && (
                                                    <div>
                                                      {/* Times per week */}
                                                      <div className="option-without-input">
                                                        <label className="option-fbox">
                                                          <input
                                                            type="radio"
                                                            id={idx}
                                                            name={`patterAfterChangeDoseFrequencyElr${idx}`}
                                                            value={1}
                                                            checked={
                                                              data.patterAfterChangeDoseFrequency ===
                                                              1
                                                            }
                                                            onChange={
                                                              HandleRepeaterFieldChange
                                                            }
                                                          />
                                                          <input
                                                            className="input-dash-sm"
                                                            id={idx}
                                                            name={`patterAfterChangeDoseFrequencyTimesPerWeekElr${idx}`}
                                                            value={
                                                              data.patterAfterChangeDoseFrequencyTimesPerWeek ||
                                                              ""
                                                            }
                                                            type="text"
                                                            style={{
                                                              width: "60px",
                                                              margin: "0 5px",
                                                            }}
                                                            onChange={
                                                              HandleRepeaterFieldChange
                                                            }
                                                            onKeyDown={(e) => {
                                                              if (
                                                                [
                                                                  "Backspace",
                                                                  "ArrowLeft",
                                                                  "ArrowRight",
                                                                  "Delete",
                                                                  "Tab",
                                                                ].includes(
                                                                  e.key,
                                                                )
                                                              )
                                                                return;
                                                              if (
                                                                !/[0-9]/.test(
                                                                  e.key,
                                                                )
                                                              )
                                                                e.preventDefault();
                                                            }}
                                                          />
                                                          <span
                                                            style={{
                                                              paddingLeft:
                                                                "10px",
                                                            }}
                                                          >
                                                            times per week
                                                          </span>
                                                        </label>
                                                      </div>
                                                      {/* Every X weeks */}
                                                      <div
                                                        className="option-without-input"
                                                        style={{
                                                          display: "flex",
                                                        }}
                                                      >
                                                        <label className="option-fbox">
                                                          <input
                                                            type="radio"
                                                            id={idx}
                                                            name={`patterAfterChangeDoseFrequencyElr${idx}`}
                                                            value={2}
                                                            checked={
                                                              data.patterAfterChangeDoseFrequency ===
                                                              2
                                                            }
                                                            onChange={
                                                              HandleRepeaterFieldChange
                                                            }
                                                          />
                                                          <span
                                                            style={{
                                                              paddingLeft:
                                                                "10px",
                                                              display: "flex",
                                                            }}
                                                          >
                                                            Every
                                                            <input
                                                              className="input-dash-sm"
                                                              id={idx}
                                                              name={`patterAfterChangeDoseFrequencyWeekElr${idx}`}
                                                              value={
                                                                data.patterAfterChangeDoseFrequencyWeek
                                                              }
                                                              type="text"
                                                              style={{
                                                                width: "50px",
                                                                margin: "0 5px",
                                                              }}
                                                              onChange={
                                                                HandleRepeaterFieldChange
                                                              }
                                                              onKeyDown={(
                                                                e,
                                                              ) => {
                                                                if (
                                                                  [
                                                                    "Backspace",
                                                                    "ArrowLeft",
                                                                    "ArrowRight",
                                                                    "Delete",
                                                                    "Tab",
                                                                  ].includes(
                                                                    e.key,
                                                                  )
                                                                )
                                                                  return;
                                                                if (
                                                                  !/[0-9]/.test(
                                                                    e.key,
                                                                  )
                                                                )
                                                                  e.preventDefault();
                                                              }}
                                                            />
                                                            week(s)
                                                          </span>
                                                        </label>
                                                      </div>
                                                      {/* Unknown */}
                                                      <div className="option-without-input">
                                                        <label className="option-fbox">
                                                          <input
                                                            type="radio"
                                                            id={idx}
                                                            name={`patterAfterChangeDoseFrequencyElr${idx}`}
                                                            value={99}
                                                            checked={
                                                              data.patterAfterChangeDoseFrequency ===
                                                              99
                                                            }
                                                            onChange={
                                                              HandleRepeaterFieldChange
                                                            }
                                                          />
                                                          <span
                                                            style={{
                                                              paddingLeft:
                                                                "10px",
                                                            }}
                                                          >
                                                            Unknown
                                                          </span>
                                                        </label>
                                                      </div>
                                                      <ErrorField
                                                        show={
                                                          data.patternAfterFrequencyEmpty
                                                        }
                                                        message={
                                                          ELCharacteristics_MSGS.frequencyPatternEmpty
                                                        }
                                                        style={{
                                                          paddingLeft: "0px",
                                                        }}
                                                      />
                                                      <ErrorField
                                                        show={
                                                          data.patternAfterFrequencyRangeInvalid
                                                        }
                                                        message={
                                                          ELCharacteristics_MSGS.frequencyRange
                                                        }
                                                        style={{
                                                          paddingLeft: "0px",
                                                        }}
                                                      />

                                                      <ErrorField
                                                        show={
                                                          data.patternAfterUnknownWarning
                                                        }
                                                        message={
                                                          ELCharacteristics_MSGS.Q381AWarningUnk
                                                        }
                                                        style={{
                                                          paddingLeft: "0px",
                                                        }}
                                                      />
                                                    </div>
                                                  )}

                                                  {/* Strength Pattern - only show if changeType is 3 (Strength) */}
                                                  {data.changeType === 3 && (
                                                    <div
                                                      style={{
                                                        marginTop:
                                                          data.changeType === 2
                                                            ? "15px"
                                                            : "0",
                                                      }}
                                                    >
                                                      {/* ✅ Option 1: mg */}
                                                      <div
                                                        className="option-without-input"
                                                        style={{
                                                          display: "flex",
                                                        }}
                                                      >
                                                        <label className="option-fbox">
                                                          <input
                                                            type="radio"
                                                            id={idx}
                                                            name={`patterAfterChangeDoseStrengthElr${idx}`}
                                                            value={1}
                                                            checked={
                                                              data.patterAfterChangeDoseStrength ===
                                                              1
                                                            }
                                                            onChange={
                                                              HandleRepeaterFieldChange
                                                            }
                                                          />
                                                          <span
                                                            style={{
                                                              paddingLeft:
                                                                "10px",
                                                              display: "flex",
                                                            }}
                                                          >
                                                            <input
                                                              className="input-dash-sm"
                                                              id={idx}
                                                              name={`patterAfterChangeDoseStrengthMgElr${idx}`}
                                                              value={
                                                                data.patterAfterChangeDoseStrengthMg
                                                              }
                                                              type="text"
                                                              onChange={
                                                                HandleRepeaterFieldChange
                                                              }
                                                              onKeyDown={
                                                                AllowNumericWithDecimal
                                                              }
                                                            />
                                                            <span>
                                                              &nbsp;mg
                                                            </span>
                                                          </span>
                                                        </label>
                                                      </div>

                                                      {/* ✅ Option 2: mg/kg */}
                                                      <div
                                                        className="option-without-input"
                                                        style={{
                                                          display: "flex",
                                                        }}
                                                      >
                                                        <label className="option-fbox">
                                                          <input
                                                            type="radio"
                                                            id={idx}
                                                            name={`patterAfterChangeDoseStrengthElr${idx}`}
                                                            value={2}
                                                            checked={
                                                              data.patterAfterChangeDoseStrength ===
                                                              2
                                                            }
                                                            onChange={
                                                              HandleRepeaterFieldChange
                                                            }
                                                          />
                                                          <span
                                                            style={{
                                                              paddingLeft:
                                                                "10px",
                                                              display: "flex",
                                                            }}
                                                          >
                                                            <input
                                                              className="input-dash-sm"
                                                              id={idx}
                                                              name={`patterAfterChangeDoseStrengthMgKgElr${idx}`}
                                                              value={
                                                                data.patterAfterChangeDoseStrengthMgKg
                                                              }
                                                              type="text"
                                                              onChange={
                                                                HandleRepeaterFieldChange
                                                              }
                                                              onKeyDown={
                                                                AllowNumericWithDecimal
                                                              }
                                                            />
                                                            <span>
                                                              &nbsp;mg/kg
                                                            </span>
                                                          </span>
                                                        </label>
                                                      </div>

                                                      {/* ✅ Option 3: mg/m² */}
                                                      <div
                                                        className="option-without-input"
                                                        style={{
                                                          display: "flex",
                                                        }}
                                                      >
                                                        <label className="option-fbox">
                                                          <input
                                                            type="radio"
                                                            id={idx}
                                                            name={`patterAfterChangeDoseStrengthElr${idx}`}
                                                            value={3}
                                                            checked={
                                                              data.patterAfterChangeDoseStrength ===
                                                              3
                                                            }
                                                            onChange={
                                                              HandleRepeaterFieldChange
                                                            }
                                                          />
                                                          <span
                                                            style={{
                                                              paddingLeft:
                                                                "10px",
                                                              display: "flex",
                                                            }}
                                                          >
                                                            <input
                                                              className="input-dash-sm"
                                                              id={idx}
                                                              name={`patterAfterChangeDoseStrengthMgM2Elr${idx}`}
                                                              value={
                                                                data.patterAfterChangeDoseStrengthMgM2
                                                              }
                                                              type="text"
                                                              onChange={
                                                                HandleRepeaterFieldChange
                                                              }
                                                              onKeyDown={
                                                                AllowNumericWithDecimal
                                                              }
                                                            />
                                                            <span>
                                                              &nbsp;mg/m²
                                                            </span>
                                                          </span>
                                                        </label>
                                                      </div>

                                                      {/* Unknown option */}
                                                      <div className="option-without-input">
                                                        <label className="option-fbox">
                                                          <input
                                                            type="radio"
                                                            id={idx}
                                                            name={`patterAfterChangeDoseStrengthElr${idx}`}
                                                            value={99}
                                                            checked={
                                                              data.patterAfterChangeDoseStrength ===
                                                              99
                                                            }
                                                            onChange={
                                                              HandleRepeaterFieldChange
                                                            }
                                                          />
                                                          <span
                                                            style={{
                                                              paddingLeft:
                                                                "10px",
                                                            }}
                                                          >
                                                            Unknown
                                                          </span>
                                                        </label>
                                                      </div>

                                                      <ErrorField
                                                        show={
                                                          data.patternAfterStrengthEmpty
                                                        }
                                                        message={
                                                          ELCharacteristics_MSGS.strengthPatternEmpty
                                                        }
                                                        style={{
                                                          paddingLeft: "0px",
                                                        }}
                                                      />

                                                      <ErrorField
                                                        show={
                                                          data.patternAfterStrengthRangeInvalid
                                                        }
                                                        message={
                                                          ELCharacteristics_MSGS.DecimalRange
                                                        }
                                                        style={{
                                                          paddingLeft: "0px",
                                                        }}
                                                      />

                                                      <ErrorField
                                                        show={
                                                          data.patternAfterUnknownWarning
                                                        }
                                                        message={
                                                          ELCharacteristics_MSGS.Q381AWarningUnk
                                                        }
                                                        style={{
                                                          paddingLeft: "0px",
                                                        }}
                                                      />
                                                    </div>
                                                  )}

                                                  {/* Conflict validation errors */}
                                                  <ErrorField
                                                    show={
                                                      data.frequencyIncreaseConflict
                                                    }
                                                    message={
                                                      data.patterBeforeChangeDoseFrequency ===
                                                      1
                                                        ? ELCharacteristics_MSGS.FrequencyIncreaseConflict
                                                        : ELCharacteristics_MSGS.FrequencyDecreaseConflict
                                                    }
                                                    style={{
                                                      paddingLeft: "0px",
                                                    }}
                                                  />
                                                  <ErrorField
                                                    show={
                                                      data.frequencyDecreaseConflict
                                                    }
                                                    message={
                                                      data.patterBeforeChangeDoseFrequency ===
                                                      2
                                                        ? ELCharacteristics_MSGS.FrequencyIncreaseConflict
                                                        : ELCharacteristics_MSGS.FrequencyDecreaseConflict
                                                    }
                                                    style={{
                                                      paddingLeft: "0px",
                                                    }}
                                                  />
                                                  <ErrorField
                                                    show={
                                                      data.strengthIncreaseConflict
                                                    }
                                                    message={
                                                      ELCharacteristics_MSGS.StrengthIncreaseConflict
                                                    }
                                                    style={{
                                                      paddingLeft: "0px",
                                                    }}
                                                  />
                                                  <ErrorField
                                                    show={
                                                      data.strengthDecreaseConflict
                                                    }
                                                    message={
                                                      ELCharacteristics_MSGS.StrengthDecreaseConflict
                                                    }
                                                    style={{
                                                      paddingLeft: "0px",
                                                    }}
                                                  />
                                                </div>
                                              )}
                                            </td>

                                            {/* Column F: Date of change */}
                                            <td
                                              style={{
                                                border: "1px solid black",
                                                padding: "8px",
                                              }}
                                            >
                                              {data.changeType !== 1 &&
                                              data.changeType !== 99 ? (
                                                <div>
                                                  <div
                                                    className="option-without-input"
                                                    style={{ display: "flex" }}
                                                  >
                                                    <input
                                                      type="radio"
                                                      id={idx}
                                                      name={
                                                        "dateChangeRdElr" + idx
                                                      }
                                                      value={1}
                                                      checked={
                                                        data.dateChangeRd === 1
                                                      }
                                                      onChange={
                                                        HandleRepeaterFieldChange
                                                      }
                                                    />
                                                    <span
                                                      className="radio-text-padding"
                                                      style={{
                                                        paddingLeft: "10px",
                                                        marginLeft: "-5px",
                                                      }}
                                                    >
                                                      <DatePickerComponent
                                                        ctrlId={`dateChangeElr-${idx}`}
                                                        HandleDateChange={
                                                          HandleRepeaterDateChange
                                                        }
                                                        minDate={dayjs(
                                                          "01-01-1920",
                                                        )}
                                                        maxDate={dayjs()}
                                                        locale="en"
                                                        date={data.dateChange}
                                                      />
                                                    </span>
                                                  </div>
                                                  <div
                                                    className="option-without-input"
                                                    style={{
                                                      display: "flex",
                                                      alignItems: "flex-start",
                                                    }}
                                                  >
                                                    <label className="option-fbox">
                                                      <input
                                                        type="radio"
                                                        style={{
                                                          marginTop: "6px",
                                                        }}
                                                        id={idx}
                                                        name={
                                                          "dateChangeRdElr" +
                                                          idx
                                                        }
                                                        value={99}
                                                        checked={
                                                          data.dateChangeRd ===
                                                          99
                                                        }
                                                        onChange={
                                                          HandleRepeaterFieldChange
                                                        }
                                                      />
                                                      <span
                                                        style={{
                                                          paddingLeft: "10px",
                                                        }}
                                                      >
                                                        Date of change unknown
                                                      </span>
                                                    </label>
                                                  </div>
                                                  <ErrorField
                                                    show={
                                                      data.dateChangeRdEmpty
                                                    }
                                                    message={
                                                      ELCharacteristics_MSGS.General
                                                    }
                                                    style={{
                                                      paddingLeft: "0px",
                                                    }}
                                                  />
                                                  <ErrorField
                                                    show={data.dateChangeEmpty}
                                                    message={
                                                      ELCharacteristics_MSGS.GeneralDateEmpty
                                                    }
                                                    style={{
                                                      paddingLeft: "0px",
                                                    }}
                                                  />
                                                  <ErrorField
                                                    show={
                                                      data.invalidDateChange
                                                    }
                                                    message={
                                                      CommonError_Msgs.InvalidDate
                                                    }
                                                    style={{
                                                      paddingLeft: "0px",
                                                    }}
                                                  />
                                                  <ErrorField
                                                    show={
                                                      data.dateChangeOutOfBound
                                                    }
                                                    message={
                                                      ELCharacteristics_MSGS.DateChangeOutOfBound
                                                    }
                                                    style={{
                                                      paddingLeft: "0px",
                                                    }}
                                                  />
                                                </div>
                                              ) : (
                                                <span
                                                  style={{
                                                    textAlign: "center",
                                                  }}
                                                  className="not-applicable"
                                                >
                                                  <b>Not applicable</b>
                                                </span>
                                              )}
                                            </td>
                                          </tr>
                                        </tbody>
                                      </table>
                                    </div>
                                  </div>
                                </div>
                              ))}

                            {/* Add button */}
                            {Q381AElrexDetails &&
                              Q381AElrexDetails.length > 0 &&
                              Number(Q381AElrexDetails[0].changeType) !== 1 &&
                              Number(Q381AElrexDetails[0].changeType) !==
                                99 && (
                                <div className="sub-question-bot-sapce-ul mt-2">
                                  <div
                                    className="answer-list-text"
                                    onClick={AddQ381AElrexRow}
                                  >
                                    <a>
                                      <img
                                        width="18px"
                                        src="../Assets/images/plus.png"
                                        alt=""
                                      />
                                    </a>
                                    <a className="radio-text-padding">
                                      Click here to add another dose/frequency
                                      change
                                    </a>
                                  </div>
                                </div>
                              )}
                          </div>
                        )}
                        {/* Q38.1.A End */}
                        {isLinvoveySelected && isNewEcrfA == true && (
                          <div className="question question-weight">
                            <span>Q38.1.A</span>
                            <span className="quest-text-pad">
                              Please provide information on any{" "}
                              <i>dose frequency, or dose strength</i>, changes
                              that occurred from completion of the step-up
                              dosing phase until the end of the line of therapy
                              with{" "}
                              <span
                                style={{
                                  color: PageDesignConstants.fontColor,
                                  fontWeight: "bolder",
                                }}
                              >
                                {isLinvoveySelected && (
                                  <>
                                    LYNOZYFIC<sup>TM</sup> (linvoseltamab-gcpt)
                                  </>
                                )}
                              </span>{" "}
                              Add additional rows for each type of change that
                              occurred.
                              <br />
                            </span>
                          </div>
                        )}
                        {/* Q38.1.A LINVOSELTAMAB */}
                        {isLinvoveySelected && isNewEcrfA && (
                          <div className="dependent-section">
                            <ErrorField
                              show={q381ALinvo_WarningHard}
                              message={
                                ELCharacteristics_MSGS.Q381AChainMismatchWarning
                              }
                              style={{ paddingLeft: "0px" }}
                            />
                            <ErrorField
                              show={q381ALinvoQ36Mismatch}
                              message={
                                ELCharacteristics_MSGS.Q381AQ36PatternBeforeMismatch
                              }
                              style={{ paddingLeft: "0px" }}
                            />
                            {Q381ALinvoDetails &&
                              Q381ALinvoDetails.length > 0 &&
                              Q381ALinvoDetails.map((data, idx) => (
                                <div className="loop-section-ul" key={idx}>
                                  <div className="loop-head">
                                    <span
                                      className="question-weight"
                                      style={{ float: "inline-start" }}
                                    >
                                      {GetOrdinal(idx + 1)} Dose/frequency
                                      change
                                    </span>
                                    {idx !== 0 && (
                                      <div
                                        className="delete-btn-div"
                                        style={{ float: "inline-end" }}
                                      >
                                        <img
                                          width="16px"
                                          src="../Assets/images/Icon-material-delete.png"
                                          alt=""
                                          style={{ cursor: "pointer" }}
                                          onClick={() =>
                                            DeleteQ381ALinvoRow(idx)
                                          }
                                        />
                                      </div>
                                    )}
                                  </div>

                                  {/* LINVOSELTAMAB Table */}
                                  <div className="question-bot-sapce">
                                    <div className="sub-question grid-overflow">
                                      <table
                                        style={{
                                          border: "1px solid black",
                                          borderCollapse: "collapse",
                                          width: "100%",
                                        }}
                                      >
                                        <thead>
                                          <tr>
                                            <th
                                              style={{
                                                border: "1px solid black",
                                                padding: "8px",
                                                textAlign: "center",
                                                minWidth: "150px",
                                              }}
                                            >
                                              Treatment
                                            </th>
                                            <th
                                              style={{
                                                border: "1px solid black",
                                                padding: "8px",
                                                textAlign: "center",
                                                minWidth: "250px",
                                              }}
                                            >
                                              Type of change
                                            </th>
                                            <th
                                              style={{
                                                border: "1px solid black",
                                                padding: "8px",
                                                textAlign: "center",
                                                minWidth: "280px",
                                              }}
                                            >
                                              Reason for change
                                            </th>
                                            <th
                                              style={{
                                                border: "1px solid black",
                                                padding: "8px",
                                                textAlign: "center",
                                              }}
                                            >
                                              Pattern <i>before</i> change
                                            </th>
                                            <th
                                              style={{
                                                border: "1px solid black",
                                                padding: "8px",
                                                textAlign: "center",
                                              }}
                                            >
                                              Pattern <i>after</i> change
                                            </th>
                                            <th
                                              style={{
                                                border: "1px solid black",
                                                padding: "8px",
                                                textAlign: "center",
                                              }}
                                            >
                                              Date of change
                                            </th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          <tr>
                                            {/* Column A: Treatment */}
                                            <td
                                              style={{
                                                border: "1px solid black",
                                                padding: "8px",
                                              }}
                                            >
                                              <span className="quest-text-pad">
                                                LYNOZYFIC
                                                <sup className="superscript">
                                                  TM
                                                </sup>{" "}
                                                (linvoquetamab-gcpt)
                                              </span>
                                            </td>

                                            {/* Column B: Type of change */}
                                            <td
                                              style={{
                                                border: "1px solid black",
                                                padding: "8px",
                                              }}
                                            >
                                              <div className="option-without-input">
                                                <label className="option-fbox">
                                                  <input
                                                    type="radio"
                                                    id={idx}
                                                    name={
                                                      "changeTypeLinvo" + idx
                                                    }
                                                    value={1}
                                                    checked={
                                                      data.changeType === 1
                                                    }
                                                    onChange={
                                                      HandleRepeaterFieldChange
                                                    }
                                                  />
                                                  <span
                                                    className="radio-text-padding"
                                                    style={{
                                                      paddingLeft: "10px",
                                                      marginLeft: "-5px",
                                                    }}
                                                  >
                                                    No dose frequency or dose
                                                    strength changes occurred
                                                  </span>
                                                </label>
                                              </div>
                                              <div className="option-without-input">
                                                <label className="option-fbox">
                                                  <input
                                                    type="radio"
                                                    id={idx}
                                                    name={
                                                      "changeTypeLinvo" + idx
                                                    }
                                                    value={2}
                                                    checked={
                                                      data.changeType === 2
                                                    }
                                                    onChange={
                                                      HandleRepeaterFieldChange
                                                    }
                                                  />
                                                  <span
                                                    className="radio-text-padding"
                                                    style={{
                                                      paddingLeft: "10px",
                                                      marginLeft: "-5px",
                                                    }}
                                                  >
                                                    Dose frequency change
                                                  </span>
                                                </label>
                                              </div>

                                              {/* Sub-radio for Frequency Increase/Decrease */}
                                              {data.changeType === 2 && (
                                                <div
                                                  style={{
                                                    paddingLeft: "15px",
                                                  }}
                                                >
                                                  <div
                                                    className="option-without-input"
                                                    style={{ display: "flex" }}
                                                  >
                                                    <label className="option-fbox">
                                                      <input
                                                        type="radio"
                                                        id={idx}
                                                        name={
                                                          "changeTypeDoseFrequencyLinvo" +
                                                          idx
                                                        }
                                                        value={1}
                                                        checked={
                                                          data.changeTypeDoseFrequency ===
                                                          1
                                                        }
                                                        onChange={
                                                          HandleRepeaterFieldChange
                                                        }
                                                      />
                                                      <span
                                                        style={{
                                                          paddingLeft: "10px",
                                                        }}
                                                      >
                                                        Frequency increase
                                                      </span>
                                                    </label>
                                                  </div>
                                                  <div className="option-without-input">
                                                    <label className="option-fbox">
                                                      <input
                                                        type="radio"
                                                        style={{
                                                          marginTop: "6px",
                                                        }}
                                                        id={idx}
                                                        name={
                                                          "changeTypeDoseFrequencyLinvo" +
                                                          idx
                                                        }
                                                        value={2}
                                                        checked={
                                                          data.changeTypeDoseFrequency ===
                                                          2
                                                        }
                                                        onChange={
                                                          HandleRepeaterFieldChange
                                                        }
                                                      />
                                                      <span
                                                        style={{
                                                          paddingLeft: "10px",
                                                        }}
                                                      >
                                                        Frequency decrease
                                                      </span>
                                                    </label>
                                                  </div>
                                                  <ErrorField
                                                    show={
                                                      data.changeTypeDoseFrequencyEmpty
                                                    }
                                                    message={
                                                      ELCharacteristics_MSGS.ChangeTypeDoseFrequencyEmpty
                                                    }
                                                    style={{
                                                      paddingLeft: "0px",
                                                    }}
                                                  />
                                                </div>
                                              )}

                                              <div className="option-without-input">
                                                <label className="option-fbox">
                                                  <input
                                                    type="radio"
                                                    id={idx}
                                                    name={
                                                      "changeTypeLinvo" + idx
                                                    }
                                                    value={3}
                                                    checked={
                                                      data.changeType === 3
                                                    }
                                                    onChange={
                                                      HandleRepeaterFieldChange
                                                    }
                                                  />
                                                  <span
                                                    className="radio-text-padding"
                                                    style={{
                                                      paddingLeft: "10px",
                                                      marginLeft: "-5px",
                                                    }}
                                                  >
                                                    Dose strength change
                                                  </span>
                                                </label>
                                              </div>

                                              {/* Sub-radio for Strength Increase/Decrease */}
                                              {data.changeType === 3 && (
                                                <div
                                                  style={{
                                                    paddingLeft: "15px",
                                                  }}
                                                >
                                                  <div
                                                    className="option-without-input"
                                                    style={{ display: "flex" }}
                                                  >
                                                    <label className="option-fbox">
                                                      <input
                                                        type="radio"
                                                        id={idx}
                                                        name={
                                                          "changeTypeDoseStrengthLinvo" +
                                                          idx
                                                        }
                                                        value={1}
                                                        checked={
                                                          data.changeTypeDoseStrength ===
                                                          1
                                                        }
                                                        onChange={
                                                          HandleRepeaterFieldChange
                                                        }
                                                      />
                                                      <span
                                                        style={{
                                                          paddingLeft: "10px",
                                                        }}
                                                      >
                                                        Strength increase
                                                      </span>
                                                    </label>
                                                  </div>
                                                  <div className="option-without-input">
                                                    <label className="option-fbox">
                                                      <input
                                                        type="radio"
                                                        style={{
                                                          marginTop: "6px",
                                                        }}
                                                        id={idx}
                                                        name={
                                                          "changeTypeDoseStrengthLinvo" +
                                                          idx
                                                        }
                                                        value={2}
                                                        checked={
                                                          data.changeTypeDoseStrength ===
                                                          2
                                                        }
                                                        onChange={
                                                          HandleRepeaterFieldChange
                                                        }
                                                      />
                                                      <span
                                                        style={{
                                                          paddingLeft: "10px",
                                                        }}
                                                      >
                                                        Strength decrease
                                                      </span>
                                                    </label>
                                                  </div>
                                                  <ErrorField
                                                    show={
                                                      data.changeTypeDoseStrengthEmpty
                                                    }
                                                    message={
                                                      ELCharacteristics_MSGS.ChangeTypeDoseStrengthEmpty
                                                    }
                                                    style={{
                                                      paddingLeft: "0px",
                                                    }}
                                                  />
                                                </div>
                                              )}

                                              <div
                                                className="option-without-input"
                                                style={{
                                                  display: "flex",
                                                  alignItems: "flex-start",
                                                }}
                                              >
                                                <label className="option-fbox">
                                                  <input
                                                    type="radio"
                                                    style={{ marginTop: "6px" }}
                                                    id={idx}
                                                    name={
                                                      "changeTypeLinvo" + idx
                                                    }
                                                    value={99}
                                                    checked={
                                                      data.changeType === 99
                                                    }
                                                    onChange={
                                                      HandleRepeaterFieldChange
                                                    }
                                                  />
                                                  <span
                                                    style={{
                                                      paddingLeft: "10px",
                                                    }}
                                                  >
                                                    Unknown if any dose changes
                                                    occurred
                                                  </span>
                                                </label>
                                              </div>
                                              <ErrorField
                                                show={data.changeTypeEmpty}
                                                message={
                                                  ELCharacteristics_MSGS.ChangeTypeEmpty
                                                }
                                                style={{ paddingLeft: "0px" }}
                                              />
                                            </td>

                                            {/* Column C: Reasons for change */}
                                            {/* Q38.1.A LINVOSELTAMAB - Column C - Reasons for change */}
                                            <td
                                              style={{
                                                border: "1px solid black",
                                                padding: "8px",
                                                textAlign: "start",
                                              }}
                                            >
                                              {data.changeType !== 1 &&
                                              data.changeType !== 99 ? (
                                                <div>
                                                  {/* Primary Reasons */}
                                                  {data.reasonList?.map(
                                                    (reason, itemidx) => (
                                                      <div
                                                        key={
                                                          reason.bispecifictreatmentdiscontinuationreasonid
                                                        }
                                                        className="option-without-input"
                                                        style={{
                                                          display: "flex",
                                                          flexDirection:
                                                            "column",
                                                        }}
                                                      >
                                                        <label className="option-fbox">
                                                          <input
                                                            type="checkbox"
                                                            id={`reasonLinvo_${idx}_${itemidx}`}
                                                            name={`reasonLinvo_${idx}_${itemidx}`}
                                                            idx={idx}
                                                            itemidx={itemidx}
                                                            value={
                                                              reason.bispecifictreatmentdiscontinuationreasonid
                                                            }
                                                            checked={
                                                              reason.isSelected ||
                                                              false
                                                            }
                                                            onChange={(e) =>
                                                              HandleQ381ALinvoRepeaterFieldChange(
                                                                e,
                                                                "Linvo",
                                                                reason.reasonName,
                                                                idx,
                                                                itemidx,
                                                                null,
                                                              )
                                                            }
                                                          />
                                                          <span
                                                            style={{
                                                              paddingLeft:
                                                                "10px",
                                                            }}
                                                          >
                                                            {reason.reasonName}
                                                            {reason.superscripttext && (
                                                              <sup>
                                                                {
                                                                  reason.superscripttext
                                                                }
                                                              </sup>
                                                            )}
                                                          </span>
                                                        </label>

                                                        {/* "Other" text field - Show only if this reason contains "Other" and is selected */}
                                                        {reason.reasonName?.includes(
                                                          "Other",
                                                        ) && (
                                                          <div
                                                            style={{
                                                              paddingLeft:
                                                                "25px",
                                                              marginTop: "5px",
                                                            }}
                                                          >
                                                            <textarea
                                                              className="input-dash"
                                                              id={`reasonTextLinvo_${idx}_${itemidx}`}
                                                              name={`reasonTextLinvo_${idx}_${itemidx}`}
                                                              idx={idx}
                                                              itemidx={itemidx}
                                                              value={
                                                                Q381ALinvoDetails[
                                                                  idx
                                                                ]
                                                                  ?.reasonOtherText ||
                                                                ""
                                                              }
                                                              onChange={(e) =>
                                                                HandleQ381ALinvoRepeaterFieldChange(
                                                                  e,
                                                                  "Linvo",
                                                                  reason.reasonName,
                                                                  idx,
                                                                  itemidx,
                                                                  null,
                                                                )
                                                              }
                                                              maxLength={300}
                                                              style={{
                                                                width: "100%",
                                                                minHeight:
                                                                  "60px",
                                                                resize:
                                                                  "vertical",
                                                              }}
                                                            />
                                                            <div
                                                              style={{
                                                                fontSize:
                                                                  "11px",
                                                                color: "#666",
                                                                marginTop:
                                                                  "2px",
                                                              }}
                                                            ></div>
                                                            <ErrorField
                                                              show={
                                                                data.reasonOtherTextEmpty
                                                              }
                                                              message={
                                                                ELCharacteristics_MSGS.ReasonOtherTextEmpty
                                                              }
                                                              style={{
                                                                paddingLeft:
                                                                  "0px",
                                                              }}
                                                            />
                                                          </div>
                                                        )}

                                                        {/* AE Sub-reasons - Only show if this is "Adverse event" (ID=2) and it's selected */}
                                                        {reason.bispecifictreatmentdiscontinuationreasonid ===
                                                          2 &&
                                                          reason.isSelected &&
                                                          reason.aeSubReasons
                                                            ?.length > 0 && (
                                                            <div
                                                              style={{
                                                                paddingLeft:
                                                                  "25px",
                                                                marginTop:
                                                                  "5px",
                                                              }}
                                                            >
                                                              {reason.aeSubReasons.map(
                                                                (ae, aeIdx) => {
                                                                  const aeName =
                                                                    ae.reasonName ||
                                                                    ae.bispecifictreatmentdiscontinuationreasonae ||
                                                                    "";
                                                                  const isAEUnknown =
                                                                    aeName
                                                                      .toLowerCase()
                                                                      .includes(
                                                                        "unknown",
                                                                      );

                                                                  return (
                                                                    <div
                                                                      key={
                                                                        ae.bispecifictreatmentdiscontinuationreasonaeid
                                                                      }
                                                                      className="option-without-input"
                                                                    >
                                                                      <label className="option-fbox">
                                                                        <input
                                                                          type={
                                                                            isAEUnknown
                                                                              ? "radio"
                                                                              : "checkbox"
                                                                          }
                                                                          id={`aeReasonLinvo_${idx}_${itemidx}_${aeIdx}`}
                                                                          name={
                                                                            isAEUnknown
                                                                              ? `aeUnknownLinvo_${idx}_${itemidx}`
                                                                              : `aeReasonLinvo_${idx}_${itemidx}_${aeIdx}`
                                                                          }
                                                                          idx={
                                                                            idx
                                                                          }
                                                                          itemidx={
                                                                            itemidx
                                                                          }
                                                                          aeidx={
                                                                            aeIdx
                                                                          }
                                                                          value={
                                                                            ae.bispecifictreatmentdiscontinuationreasonaeid
                                                                          }
                                                                          checked={
                                                                            ae.isSelected ||
                                                                            false
                                                                          }
                                                                          onChange={(
                                                                            e,
                                                                          ) =>
                                                                            HandleQ381ALinvoRepeaterFieldChange(
                                                                              e,
                                                                              "Linvo",
                                                                              reason.reasonName,
                                                                              idx,
                                                                              itemidx,
                                                                              aeIdx,
                                                                            )
                                                                          }
                                                                        />
                                                                        <span
                                                                          style={{
                                                                            paddingLeft:
                                                                              "10px",
                                                                          }}
                                                                        >
                                                                          {
                                                                            ae.reasonName
                                                                          }
                                                                          {ae.superscripttext && (
                                                                            <sup>
                                                                              {
                                                                                ae.superscripttext
                                                                              }
                                                                            </sup>
                                                                          )}
                                                                        </span>
                                                                      </label>
                                                                      {ae.reasonName ===
                                                                        DiscontinuationReason.NeurologicalEvent &&
                                                                        ae.isSelected && (
                                                                          <div
                                                                            style={{
                                                                              marginLeft:
                                                                                "28px",
                                                                              marginTop:
                                                                                "8px",
                                                                            }}
                                                                          >
                                                                            {data
                                                                              .neurologicAEReasonList
                                                                              ?.length >
                                                                              0 &&
                                                                              data.neurologicAEReasonList.map(
                                                                                (
                                                                                  aeNeuro,
                                                                                  aeNeuroIdx,
                                                                                ) => {
                                                                                  return (
                                                                                    <div
                                                                                      key={
                                                                                        aeNeuroIdx
                                                                                      }
                                                                                    >
                                                                                      <label
                                                                                        className="d-flex align-items-start"
                                                                                        htmlFor={`linvo_adverse_neuro_${itemidx}_${aeIdx}_${aeNeuroIdx}`}
                                                                                      >
                                                                                        <input
                                                                                          type="checkbox"
                                                                                          id={`linvo_adverse_neuro_${itemidx}_${aeIdx}_${aeNeuroIdx}`}
                                                                                          name={`linvo_adverse_neuro_${itemidx}_${aeIdx}_${aeNeuroIdx}`}
                                                                                          checked={
                                                                                            aeNeuro.isSelected
                                                                                          }
                                                                                          onChange={(
                                                                                            e,
                                                                                          ) =>
                                                                                            HandleNeuroRepeaterFieldChange(
                                                                                              e,
                                                                                              "381A",
                                                                                              RRMMCohortTreatment.Linvoseltamab,
                                                                                              aeNeuroIdx,
                                                                                              idx,
                                                                                            )
                                                                                          }
                                                                                          style={{
                                                                                            marginTop:
                                                                                              "6px",
                                                                                          }}
                                                                                        />

                                                                                        <span
                                                                                          style={{
                                                                                            textAlign:
                                                                                              "left",
                                                                                          }}
                                                                                          className="radio-text-padding"
                                                                                        >
                                                                                          {
                                                                                            aeNeuro.reasonName
                                                                                          }
                                                                                        </span>
                                                                                      </label>

                                                                                      {aeNeuro.reasonName.includes(
                                                                                        "Other",
                                                                                      ) && (
                                                                                        <div>
                                                                                          <label
                                                                                            htmlFor={`linvo_neuro_reasontxt_${itemidx}_${aeNeuroIdx}`}
                                                                                          >
                                                                                            <span
                                                                                              style={{
                                                                                                paddingLeft:
                                                                                                  "28px",
                                                                                              }}
                                                                                              className="radio-text-padding"
                                                                                            >
                                                                                              <textarea
                                                                                                style={{
                                                                                                  width:
                                                                                                    "264px",
                                                                                                }}
                                                                                                id={`linvo_neuro_reasontxt_${itemidx}_${aeNeuroIdx}`}
                                                                                                name={`linvo_neuro_reasontxt_${itemidx}_${aeNeuroIdx}`}
                                                                                                type="text"
                                                                                                value={
                                                                                                  aeNeuro.reasonText
                                                                                                }
                                                                                                onChange={(
                                                                                                  e,
                                                                                                ) =>
                                                                                                  HandleNeuroRepeaterFieldChange(
                                                                                                    e,
                                                                                                    "381A",
                                                                                                    RRMMCohortTreatment.Linvoseltamab,
                                                                                                    aeNeuroIdx,
                                                                                                    idx,
                                                                                                  )
                                                                                                }
                                                                                                maxLength={
                                                                                                  300
                                                                                                }
                                                                                              />
                                                                                            </span>
                                                                                          </label>
                                                                                          <ErrorField
                                                                                            show={
                                                                                              data.neuroOtherTextEmpty
                                                                                            }
                                                                                            message={
                                                                                              ELCharacteristics_MSGS.Q38AENeurologicOther
                                                                                            }
                                                                                          />
                                                                                        </div>
                                                                                      )}
                                                                                    </div>
                                                                                  );
                                                                                },
                                                                              )}
                                                                            <ErrorField
                                                                              show={
                                                                                data.neuroReasonEmpty
                                                                              }
                                                                              message={
                                                                                ELCharacteristics_MSGS.Q38AENeurologicEmpty
                                                                              }
                                                                            />
                                                                          </div>
                                                                        )}
                                                                    </div>
                                                                  );
                                                                },
                                                              )}
                                                              <ErrorField
                                                                show={
                                                                  data.aeSubReasonEmpty
                                                                }
                                                                message={
                                                                  ELCharacteristics_MSGS.Q38AEEmpty
                                                                }
                                                                style={{
                                                                  paddingLeft:
                                                                    "0px",
                                                                }}
                                                              />
                                                            </div>
                                                          )}
                                                      </div>
                                                    ),
                                                  )}

                                                  {/* LINVOSELTAMAB - Unknown Option */}
                                                  <div
                                                    className="option-without-input"
                                                    style={{
                                                      display: "flex",
                                                      marginTop: "5px",
                                                    }}
                                                  >
                                                    <label className="option-fbox">
                                                      <input
                                                        type="radio"
                                                        id={idx}
                                                        name={
                                                          "reasonUnknownLinvo" +
                                                          idx
                                                        }
                                                        checked={
                                                          data.reasonUnknown ||
                                                          false
                                                        }
                                                        value={true}
                                                        onChange={
                                                          HandleRepeaterFieldChange
                                                        }
                                                      />
                                                      <span
                                                        style={{
                                                          paddingLeft: "10px",
                                                        }}
                                                      >
                                                        Unknown
                                                      </span>
                                                    </label>
                                                  </div>

                                                  <ErrorField
                                                    show={data.reasonEmpty}
                                                    message={
                                                      ELCharacteristics_MSGS.Q38ReasonEmpty
                                                    }
                                                    style={{
                                                      paddingLeft: "0px",
                                                    }}
                                                  />
                                                  <ErrorField
                                                    show={data.deathDtWarning}
                                                    message={
                                                      ELCharacteristics_MSGS.deathWarningQ20
                                                    }
                                                    style={{
                                                      paddingLeft: "0px",
                                                    }}
                                                  />
                                                </div>
                                              ) : (
                                                <span
                                                  style={{
                                                    textAlign: "center",
                                                  }}
                                                  className="not-applicable"
                                                >
                                                  <b>Not applicable</b>
                                                </span>
                                              )}
                                            </td>

                                            {/* Column D - Pattern BEFORE change - LINVOSELTAMAB */}
                                            <td
                                              style={{
                                                border: "1px solid black",
                                                padding: "8px",
                                                textAlign: "start",
                                              }}
                                            >
                                              {data.changeType === null ||
                                              data.changeType === 99 ||
                                              data.changeType === 1 ? (
                                                <span
                                                  style={{
                                                    textAlign: "center",
                                                  }}
                                                  className="not-applicable"
                                                >
                                                  <b>Not applicable</b>
                                                </span>
                                              ) : (
                                                <div>
                                                  {/* Frequency Pattern */}
                                                  {data.changeType === 2 && (
                                                    <div>
                                                      {/* ✅ Option 1: Times per week WITH INPUT */}
                                                      <div
                                                        className="option-without-input"
                                                        style={{
                                                          display: "flex",
                                                          alignItems: "center",
                                                        }}
                                                      >
                                                        <label
                                                          className="option-fbox"
                                                          style={{
                                                            display: "flex",
                                                            alignItems:
                                                              "center",
                                                          }}
                                                        >
                                                          <input
                                                            type="radio"
                                                            id={idx}
                                                            name={`patterBeforeChangeDoseFrequencyLinvo${idx}`}
                                                            value={1}
                                                            checked={
                                                              data.patterBeforeChangeDoseFrequency ===
                                                              1
                                                            }
                                                            onChange={
                                                              HandleRepeaterFieldChange
                                                            }
                                                          />
                                                          <span
                                                            style={{
                                                              paddingLeft:
                                                                "10px",
                                                              display: "flex",
                                                              alignItems:
                                                                "center",
                                                            }}
                                                          >
                                                            <input
                                                              className="input-dash-sm"
                                                              id={idx}
                                                              name={`patterBeforeChangeDoseFrequencyTimesPerWeekLinvo${idx}`}
                                                              value={
                                                                data.patterBeforeChangeDoseFrequencyTimesPerWeek ||
                                                                ""
                                                              }
                                                              type="text"
                                                              style={{
                                                                width: "60px",
                                                                margin: "0 5px",
                                                              }}
                                                              onChange={
                                                                HandleRepeaterFieldChange
                                                              }
                                                              onKeyDown={(
                                                                e,
                                                              ) => {
                                                                if (
                                                                  [
                                                                    "Backspace",
                                                                    "ArrowLeft",
                                                                    "ArrowRight",
                                                                    "Delete",
                                                                    "Tab",
                                                                  ].includes(
                                                                    e.key,
                                                                  )
                                                                )
                                                                  return;
                                                                if (
                                                                  !/[0-9]/.test(
                                                                    e.key,
                                                                  )
                                                                )
                                                                  e.preventDefault();
                                                              }}
                                                            />
                                                            times per week
                                                          </span>
                                                        </label>
                                                      </div>

                                                      {/* Option 2: Once every X weeks */}
                                                      <div
                                                        className="option-without-input"
                                                        style={{
                                                          display: "flex",
                                                          alignItems: "center",
                                                        }}
                                                      >
                                                        <label
                                                          className="option-fbox"
                                                          style={{
                                                            display: "flex",
                                                            alignItems:
                                                              "center",
                                                          }}
                                                        >
                                                          <input
                                                            type="radio"
                                                            id={idx}
                                                            name={`patterBeforeChangeDoseFrequencyLinvo${idx}`}
                                                            value={2}
                                                            checked={
                                                              data.patterBeforeChangeDoseFrequency ===
                                                              2
                                                            }
                                                            onChange={
                                                              HandleRepeaterFieldChange
                                                            }
                                                          />
                                                          <span
                                                            style={{
                                                              paddingLeft:
                                                                "10px",
                                                              display: "flex",
                                                              alignItems:
                                                                "center",
                                                            }}
                                                          >
                                                            Once every
                                                            <input
                                                              className="input-dash-sm"
                                                              id={idx}
                                                              name={`patterBeforeChangeDoseFrequencyWeekLinvo${idx}`}
                                                              value={
                                                                data.patterBeforeChangeDoseFrequencyWeek ||
                                                                ""
                                                              }
                                                              type="text"
                                                              style={{
                                                                width: "60px",
                                                                margin: "0 5px",
                                                              }}
                                                              onChange={
                                                                HandleRepeaterFieldChange
                                                              }
                                                              onKeyDown={(
                                                                e,
                                                              ) => {
                                                                if (
                                                                  [
                                                                    "Backspace",
                                                                    "ArrowLeft",
                                                                    "ArrowRight",
                                                                    "Delete",
                                                                    "Tab",
                                                                  ].includes(
                                                                    e.key,
                                                                  )
                                                                )
                                                                  return;
                                                                if (
                                                                  !/[0-9]/.test(
                                                                    e.key,
                                                                  )
                                                                )
                                                                  e.preventDefault();
                                                              }}
                                                            />
                                                            week(s)
                                                          </span>
                                                        </label>
                                                      </div>

                                                      {/* Unknown */}
                                                      <div className="option-without-input">
                                                        <label className="option-fbox">
                                                          <input
                                                            type="radio"
                                                            id={idx}
                                                            name={`patterBeforeChangeDoseFrequencyLinvo${idx}`}
                                                            value={99}
                                                            checked={
                                                              data.patterBeforeChangeDoseFrequency ===
                                                              99
                                                            }
                                                            onChange={
                                                              HandleRepeaterFieldChange
                                                            }
                                                          />
                                                          <span
                                                            style={{
                                                              paddingLeft:
                                                                "10px",
                                                            }}
                                                          >
                                                            Unknown
                                                          </span>
                                                        </label>
                                                      </div>

                                                      <ErrorField
                                                        show={
                                                          data.patternBeforeFrequencyEmpty
                                                        }
                                                        message={
                                                          ELCharacteristics_MSGS.frequencyPatternEmpty
                                                        }
                                                        style={{
                                                          paddingLeft: "0px",
                                                        }}
                                                      />
                                                      <ErrorField
                                                        show={
                                                          data.patternBeforeFrequencyRangeInvalid
                                                        }
                                                        message={
                                                          ELCharacteristics_MSGS.frequencyRange
                                                        }
                                                        style={{
                                                          paddingLeft: "0px",
                                                        }}
                                                      />
                                                      <ErrorField
                                                        show={
                                                          data.patternBeforeUnknownWarning
                                                        }
                                                        message={
                                                          ELCharacteristics_MSGS.Q381AWarningUnk
                                                        }
                                                        style={{
                                                          paddingLeft: "0px",
                                                        }}
                                                      />
                                                    </div>
                                                  )}

                                                  {/* Strength Pattern */}
                                                  {data.changeType === 3 && (
                                                    <div
                                                      style={{
                                                        marginTop:
                                                          data.changeType === 2
                                                            ? "15px"
                                                            : "0",
                                                      }}
                                                    >
                                                      {/* ✅ mg option - SEPARATE FIELD */}
                                                      <div
                                                        className="option-without-input"
                                                        style={{
                                                          display: "flex",
                                                          alignItems: "center",
                                                        }}
                                                      >
                                                        <label
                                                          className="option-fbox"
                                                          style={{
                                                            display: "flex",
                                                            alignItems:
                                                              "center",
                                                          }}
                                                        >
                                                          <input
                                                            type="radio"
                                                            id={idx}
                                                            name={`patterBeforeChangeDoseStrengthLinvo${idx}`}
                                                            value={1}
                                                            checked={
                                                              data.patterBeforeChangeDoseStrength ===
                                                              1
                                                            }
                                                            onChange={
                                                              HandleRepeaterFieldChange
                                                            }
                                                          />
                                                          <span
                                                            style={{
                                                              paddingLeft:
                                                                "10px",
                                                              display: "flex",
                                                              alignItems:
                                                                "center",
                                                            }}
                                                          >
                                                            <input
                                                              className="input-dash-sm"
                                                              id={idx}
                                                              name={`patterBeforeChangeDoseStrengthMgLinvo${idx}`}
                                                              value={
                                                                data.patterBeforeChangeDoseStrengthMg ||
                                                                ""
                                                              }
                                                              type="text"
                                                              style={{
                                                                width: "80px",
                                                              }}
                                                              onChange={
                                                                HandleRepeaterFieldChange
                                                              }
                                                              onKeyDown={
                                                                AllowNumericWithDecimal
                                                              }
                                                            />
                                                            <span>
                                                              &nbsp;mg
                                                            </span>
                                                          </span>
                                                        </label>
                                                      </div>

                                                      {/* ✅ mg/kg option - SEPARATE FIELD */}
                                                      <div
                                                        className="option-without-input"
                                                        style={{
                                                          display: "flex",
                                                          alignItems: "center",
                                                        }}
                                                      >
                                                        <label
                                                          className="option-fbox"
                                                          style={{
                                                            display: "flex",
                                                            alignItems:
                                                              "center",
                                                          }}
                                                        >
                                                          <input
                                                            type="radio"
                                                            id={idx}
                                                            name={`patterBeforeChangeDoseStrengthLinvo${idx}`}
                                                            value={2}
                                                            checked={
                                                              data.patterBeforeChangeDoseStrength ===
                                                              2
                                                            }
                                                            onChange={
                                                              HandleRepeaterFieldChange
                                                            }
                                                          />
                                                          <span
                                                            style={{
                                                              paddingLeft:
                                                                "10px",
                                                              display: "flex",
                                                              alignItems:
                                                                "center",
                                                            }}
                                                          >
                                                            <input
                                                              className="input-dash-sm"
                                                              id={idx}
                                                              name={`patterBeforeChangeDoseStrengthMgKgLinvo${idx}`}
                                                              value={
                                                                data.patterBeforeChangeDoseStrengthMgKg ||
                                                                ""
                                                              }
                                                              type="text"
                                                              style={{
                                                                width: "80px",
                                                              }}
                                                              onChange={
                                                                HandleRepeaterFieldChange
                                                              }
                                                              onKeyDown={
                                                                AllowNumericWithDecimal
                                                              }
                                                            />
                                                            <span>
                                                              &nbsp;mg/kg
                                                            </span>
                                                          </span>
                                                        </label>
                                                      </div>

                                                      {/* ✅ mg/m² option - SEPARATE FIELD */}
                                                      <div
                                                        className="option-without-input"
                                                        style={{
                                                          display: "flex",
                                                          alignItems: "center",
                                                        }}
                                                      >
                                                        <label
                                                          className="option-fbox"
                                                          style={{
                                                            display: "flex",
                                                            alignItems:
                                                              "center",
                                                          }}
                                                        >
                                                          <input
                                                            type="radio"
                                                            id={idx}
                                                            name={`patterBeforeChangeDoseStrengthLinvo${idx}`}
                                                            value={3}
                                                            checked={
                                                              data.patterBeforeChangeDoseStrength ===
                                                              3
                                                            }
                                                            onChange={
                                                              HandleRepeaterFieldChange
                                                            }
                                                          />
                                                          <span
                                                            style={{
                                                              paddingLeft:
                                                                "10px",
                                                              display: "flex",
                                                              alignItems:
                                                                "center",
                                                            }}
                                                          >
                                                            <input
                                                              className="input-dash-sm"
                                                              id={idx}
                                                              name={`patterBeforeChangeDoseStrengthMgM2Linvo${idx}`}
                                                              value={
                                                                data.patterBeforeChangeDoseStrengthMgM2 ||
                                                                ""
                                                              }
                                                              type="text"
                                                              style={{
                                                                width: "80px",
                                                              }}
                                                              onChange={
                                                                HandleRepeaterFieldChange
                                                              }
                                                              onKeyDown={
                                                                AllowNumericWithDecimal
                                                              }
                                                            />
                                                            <span>
                                                              &nbsp;mg/m²
                                                            </span>
                                                          </span>
                                                        </label>
                                                      </div>

                                                      {/* Unknown */}
                                                      <div className="option-without-input">
                                                        <label className="option-fbox">
                                                          <input
                                                            type="radio"
                                                            id={idx}
                                                            name={`patterBeforeChangeDoseStrengthLinvo${idx}`}
                                                            value={99}
                                                            checked={
                                                              data.patterBeforeChangeDoseStrength ===
                                                              99
                                                            }
                                                            onChange={
                                                              HandleRepeaterFieldChange
                                                            }
                                                          />
                                                          <span
                                                            style={{
                                                              paddingLeft:
                                                                "10px",
                                                            }}
                                                          >
                                                            Unknown
                                                          </span>
                                                        </label>
                                                      </div>

                                                      <ErrorField
                                                        show={
                                                          data.patternBeforeStrengthEmpty
                                                        }
                                                        message={
                                                          ELCharacteristics_MSGS.strengthPatternEmpty
                                                        }
                                                        style={{
                                                          paddingLeft: "0px",
                                                        }}
                                                      />

                                                      <ErrorField
                                                        show={
                                                          data.patternBeforeFrequencyRangeInvalid
                                                        }
                                                        message={
                                                          ELCharacteristics_MSGS.DecimalRange
                                                        }
                                                        style={{
                                                          paddingLeft: "0px",
                                                        }}
                                                      />
                                                      <ErrorField
                                                        show={
                                                          data.patternBeforeUnknownWarning
                                                        }
                                                        message={
                                                          ELCharacteristics_MSGS.Q381AWarningUnk
                                                        }
                                                        style={{
                                                          paddingLeft: "0px",
                                                        }}
                                                      />
                                                    </div>
                                                  )}
                                                </div>
                                              )}
                                            </td>

                                            {/* Column E: Pattern AFTER change */}
                                            {/* Column E: Pattern AFTER change - LINVOSELTAMAB */}
                                            <td
                                              style={{
                                                border: "1px solid black",
                                                padding: "8px",
                                                textAlign: "start",
                                              }}
                                            >
                                              {data.changeType === 2 ? (
                                                <div>
                                                  {/* Frequency Pattern AFTER */}

                                                  {/* ✅ Option 1: Times per week - SEPARATE FIELD */}
                                                  <div
                                                    className="option-without-input"
                                                    style={{
                                                      display: "flex",
                                                      alignItems: "center",
                                                    }}
                                                  >
                                                    <label
                                                      className="option-fbox"
                                                      style={{
                                                        display: "flex",
                                                        alignItems: "center",
                                                      }}
                                                    >
                                                      <input
                                                        type="radio"
                                                        id={idx}
                                                        name={`patterAfterChangeDoseFrequencyLinvo${idx}`}
                                                        value={1}
                                                        checked={
                                                          data.patterAfterChangeDoseFrequency ===
                                                          1
                                                        }
                                                        onChange={
                                                          HandleRepeaterFieldChange
                                                        }
                                                      />
                                                      <span
                                                        style={{
                                                          paddingLeft: "10px",
                                                          display: "flex",
                                                          alignItems: "center",
                                                        }}
                                                      >
                                                        <input
                                                          className="input-dash-sm"
                                                          id={idx}
                                                          name={`patterAfterChangeDoseFrequencyTimesPerWeekLinvo${idx}`}
                                                          value={
                                                            data.patterAfterChangeDoseFrequencyTimesPerWeek ||
                                                            ""
                                                          }
                                                          type="text"
                                                          style={{
                                                            width: "60px",
                                                            margin: "0 5px",
                                                          }}
                                                          onChange={
                                                            HandleRepeaterFieldChange
                                                          }
                                                          onKeyDown={(e) => {
                                                            if (
                                                              [
                                                                "Backspace",
                                                                "ArrowLeft",
                                                                "ArrowRight",
                                                                "Delete",
                                                                "Tab",
                                                              ].includes(e.key)
                                                            )
                                                              return;
                                                            if (
                                                              !/[0-9]/.test(
                                                                e.key,
                                                              )
                                                            )
                                                              e.preventDefault();
                                                          }}
                                                        />
                                                        times per week
                                                      </span>
                                                    </label>
                                                  </div>

                                                  {/* ✅ Option 2: Once every X weeks - SEPARATE FIELD */}
                                                  <div
                                                    className="option-without-input"
                                                    style={{
                                                      display: "flex",
                                                      alignItems: "center",
                                                    }}
                                                  >
                                                    <label
                                                      className="option-fbox"
                                                      style={{
                                                        display: "flex",
                                                        alignItems: "center",
                                                      }}
                                                    >
                                                      <input
                                                        type="radio"
                                                        id={idx}
                                                        name={`patterAfterChangeDoseFrequencyLinvo${idx}`}
                                                        value={2}
                                                        checked={
                                                          data.patterAfterChangeDoseFrequency ===
                                                          2
                                                        }
                                                        onChange={
                                                          HandleRepeaterFieldChange
                                                        }
                                                      />
                                                      <span
                                                        style={{
                                                          paddingLeft: "10px",
                                                          display: "flex",
                                                          alignItems: "center",
                                                        }}
                                                      >
                                                        Once every
                                                        <input
                                                          className="input-dash-sm"
                                                          id={idx}
                                                          name={`patterAfterChangeDoseFrequencyWeekLinvo${idx}`}
                                                          value={
                                                            data.patterAfterChangeDoseFrequencyWeek ||
                                                            ""
                                                          }
                                                          type="text"
                                                          style={{
                                                            width: "60px",
                                                            margin: "0 5px",
                                                          }}
                                                          onChange={
                                                            HandleRepeaterFieldChange
                                                          }
                                                          onKeyDown={(e) => {
                                                            if (
                                                              [
                                                                "Backspace",
                                                                "ArrowLeft",
                                                                "ArrowRight",
                                                                "Delete",
                                                                "Tab",
                                                              ].includes(e.key)
                                                            )
                                                              return;
                                                            if (
                                                              !/[0-9]/.test(
                                                                e.key,
                                                              )
                                                            )
                                                              e.preventDefault();
                                                          }}
                                                        />
                                                        week(s)
                                                      </span>
                                                    </label>
                                                  </div>

                                                  {/* Unknown */}
                                                  <div
                                                    className="option-without-input"
                                                    style={{ display: "flex" }}
                                                  >
                                                    <label className="option-fbox">
                                                      <input
                                                        type="radio"
                                                        id={idx}
                                                        name={`patterAfterChangeDoseFrequencyLinvo${idx}`}
                                                        value={99}
                                                        checked={
                                                          data.patterAfterChangeDoseFrequency ===
                                                          99
                                                        }
                                                        onChange={
                                                          HandleRepeaterFieldChange
                                                        }
                                                      />
                                                      <span
                                                        style={{
                                                          paddingLeft: "10px",
                                                          display: "flex",
                                                        }}
                                                      >
                                                        Unknown
                                                      </span>
                                                    </label>
                                                  </div>

                                                  <ErrorField
                                                    show={
                                                      data.patternAfterFrequencyEmpty
                                                    }
                                                    message={
                                                      ELCharacteristics_MSGS.frequencyPatternEmpty
                                                    }
                                                    style={{
                                                      paddingLeft: "0px",
                                                    }}
                                                  />
                                                  <ErrorField
                                                    show={
                                                      data.patternAfterFrequencyRangeInvalid
                                                    }
                                                    message={
                                                      ELCharacteristics_MSGS.frequencyRange
                                                    }
                                                    style={{
                                                      paddingLeft: "0px",
                                                    }}
                                                  />

                                                  <ErrorField
                                                    show={
                                                      data.frequencyIncreaseConflict
                                                    }
                                                    message={
                                                      data.patterBeforeChangeDoseFrequency ===
                                                      1
                                                        ? ELCharacteristics_MSGS.FrequencyIncreaseConflict
                                                        : ELCharacteristics_MSGS.FrequencyDecreaseConflict
                                                    }
                                                    style={{
                                                      paddingLeft: "0px",
                                                    }}
                                                  />
                                                  <ErrorField
                                                    show={
                                                      data.frequencyDecreaseConflict
                                                    }
                                                    message={
                                                      data.patterBeforeChangeDoseFrequency ===
                                                      2
                                                        ? ELCharacteristics_MSGS.FrequencyIncreaseConflict
                                                        : ELCharacteristics_MSGS.FrequencyDecreaseConflict
                                                    }
                                                    style={{
                                                      paddingLeft: "0px",
                                                    }}
                                                  />
                                                  <ErrorField
                                                    show={
                                                      data.patternAfterUnknownWarning
                                                    }
                                                    message={
                                                      ELCharacteristics_MSGS.Q381AWarningUnk
                                                    }
                                                    style={{
                                                      paddingLeft: "0px",
                                                    }}
                                                  />
                                                </div>
                                              ) : data.changeType === 3 ? (
                                                <div>
                                                  {/* Strength Pattern AFTER */}

                                                  {/* ✅ mg option - SEPARATE FIELD */}
                                                  <div
                                                    className="option-without-input"
                                                    style={{
                                                      display: "flex",
                                                      alignItems: "center",
                                                    }}
                                                  >
                                                    <label
                                                      className="option-fbox"
                                                      style={{
                                                        display: "flex",
                                                        alignItems: "center",
                                                      }}
                                                    >
                                                      <input
                                                        type="radio"
                                                        id={idx}
                                                        name={`patterAfterChangeDoseStrengthLinvo${idx}`}
                                                        value={1}
                                                        checked={
                                                          data.patterAfterChangeDoseStrength ===
                                                          1
                                                        }
                                                        onChange={
                                                          HandleRepeaterFieldChange
                                                        }
                                                      />
                                                      <span
                                                        style={{
                                                          paddingLeft: "10px",
                                                          display: "flex",
                                                          alignItems: "center",
                                                        }}
                                                      >
                                                        <input
                                                          className="input-dash-sm"
                                                          id={idx}
                                                          name={`patterAfterChangeDoseStrengthMgLinvo${idx}`}
                                                          value={
                                                            data.patterAfterChangeDoseStrengthMg ||
                                                            ""
                                                          }
                                                          type="text"
                                                          style={{
                                                            width: "80px",
                                                          }}
                                                          onChange={
                                                            HandleRepeaterFieldChange
                                                          }
                                                          onKeyDown={
                                                            AllowNumericWithDecimal
                                                          }
                                                        />
                                                        <span>&nbsp;mg</span>
                                                      </span>
                                                    </label>
                                                  </div>

                                                  {/* ✅ mg/kg option - SEPARATE FIELD */}
                                                  <div
                                                    className="option-without-input"
                                                    style={{
                                                      display: "flex",
                                                      alignItems: "center",
                                                    }}
                                                  >
                                                    <label
                                                      className="option-fbox"
                                                      style={{
                                                        display: "flex",
                                                        alignItems: "center",
                                                      }}
                                                    >
                                                      <input
                                                        type="radio"
                                                        id={idx}
                                                        name={`patterAfterChangeDoseStrengthLinvo${idx}`}
                                                        value={2}
                                                        checked={
                                                          data.patterAfterChangeDoseStrength ===
                                                          2
                                                        }
                                                        onChange={
                                                          HandleRepeaterFieldChange
                                                        }
                                                      />
                                                      <span
                                                        style={{
                                                          paddingLeft: "10px",
                                                          display: "flex",
                                                          alignItems: "center",
                                                        }}
                                                      >
                                                        <input
                                                          className="input-dash-sm"
                                                          id={idx}
                                                          name={`patterAfterChangeDoseStrengthMgKgLinvo${idx}`}
                                                          value={
                                                            data.patterAfterChangeDoseStrengthMgKg ||
                                                            ""
                                                          }
                                                          type="text"
                                                          style={{
                                                            width: "80px",
                                                          }}
                                                          onChange={
                                                            HandleRepeaterFieldChange
                                                          }
                                                          onKeyDown={
                                                            AllowNumericWithDecimal
                                                          }
                                                        />
                                                        <span>&nbsp;mg/kg</span>
                                                      </span>
                                                    </label>
                                                  </div>

                                                  {/* ✅ mg/m² option - SEPARATE FIELD */}
                                                  <div
                                                    className="option-without-input"
                                                    style={{
                                                      display: "flex",
                                                      alignItems: "center",
                                                    }}
                                                  >
                                                    <label
                                                      className="option-fbox"
                                                      style={{
                                                        display: "flex",
                                                        alignItems: "center",
                                                      }}
                                                    >
                                                      <input
                                                        type="radio"
                                                        id={idx}
                                                        name={`patterAfterChangeDoseStrengthLinvo${idx}`}
                                                        value={3}
                                                        checked={
                                                          data.patterAfterChangeDoseStrength ===
                                                          3
                                                        }
                                                        onChange={
                                                          HandleRepeaterFieldChange
                                                        }
                                                      />
                                                      <span
                                                        style={{
                                                          paddingLeft: "10px",
                                                          display: "flex",
                                                          alignItems: "center",
                                                        }}
                                                      >
                                                        <input
                                                          className="input-dash-sm"
                                                          id={idx}
                                                          name={`patterAfterChangeDoseStrengthMgM2Linvo${idx}`}
                                                          value={
                                                            data.patterAfterChangeDoseStrengthMgM2 ||
                                                            ""
                                                          }
                                                          type="text"
                                                          style={{
                                                            width: "80px",
                                                          }}
                                                          onChange={
                                                            HandleRepeaterFieldChange
                                                          }
                                                          onKeyDown={
                                                            AllowNumericWithDecimal
                                                          }
                                                        />
                                                        <span>&nbsp;mg/m²</span>
                                                      </span>
                                                    </label>
                                                  </div>

                                                  {/* Unknown */}
                                                  <div
                                                    className="option-without-input"
                                                    style={{ display: "flex" }}
                                                  >
                                                    <label className="option-fbox">
                                                      <input
                                                        type="radio"
                                                        id={idx}
                                                        name={`patterAfterChangeDoseStrengthLinvo${idx}`}
                                                        value={99}
                                                        checked={
                                                          data.patterAfterChangeDoseStrength ===
                                                          99
                                                        }
                                                        onChange={
                                                          HandleRepeaterFieldChange
                                                        }
                                                      />
                                                      <span
                                                        style={{
                                                          paddingLeft: "10px",
                                                          display: "flex",
                                                        }}
                                                      >
                                                        Unknown
                                                      </span>
                                                    </label>
                                                  </div>

                                                  <ErrorField
                                                    show={
                                                      data.patternAfterStrengthEmpty
                                                    }
                                                    message={
                                                      ELCharacteristics_MSGS.strengthPatternEmpty
                                                    }
                                                    style={{
                                                      paddingLeft: "0px",
                                                    }}
                                                  />

                                                  <ErrorField
                                                    show={
                                                      data.patternAfterStrengthRangeInvalid
                                                    }
                                                    message={
                                                      ELCharacteristics_MSGS.DecimalRange
                                                    }
                                                    style={{
                                                      paddingLeft: "0px",
                                                    }}
                                                  />
                                                  <ErrorField
                                                    show={
                                                      data.strengthIncreaseConflict
                                                    }
                                                    message={
                                                      ELCharacteristics_MSGS.StrengthIncreaseConflict
                                                    }
                                                    style={{
                                                      paddingLeft: "0px",
                                                    }}
                                                  />
                                                  <ErrorField
                                                    show={
                                                      data.strengthDecreaseConflict
                                                    }
                                                    message={
                                                      ELCharacteristics_MSGS.StrengthDecreaseConflict
                                                    }
                                                    style={{
                                                      paddingLeft: "0px",
                                                    }}
                                                  />
                                                  <ErrorField
                                                    show={
                                                      data.patternAfterUnknownWarning
                                                    }
                                                    message={
                                                      ELCharacteristics_MSGS.Q381AWarningUnk
                                                    }
                                                    style={{
                                                      paddingLeft: "0px",
                                                    }}
                                                  />
                                                </div>
                                              ) : (
                                                <span
                                                  style={{
                                                    textAlign: "center",
                                                  }}
                                                  className="not-applicable"
                                                >
                                                  <b>Not applicable</b>
                                                </span>
                                              )}
                                            </td>

                                            {/* Column F: Date of change */}
                                            <td
                                              style={{
                                                border: "1px solid black",
                                                padding: "8px",
                                              }}
                                            >
                                              {data.changeType !== 1 &&
                                              data.changeType !== 99 ? (
                                                <div>
                                                  <div
                                                    className="option-without-input"
                                                    style={{ display: "flex" }}
                                                  >
                                                    <input
                                                      type="radio"
                                                      id={idx}
                                                      name={
                                                        "dateChangeRdLinvo" +
                                                        idx
                                                      }
                                                      value={1}
                                                      checked={
                                                        data.dateChangeRd === 1
                                                      }
                                                      onChange={
                                                        HandleRepeaterFieldChange
                                                      }
                                                    />
                                                    <span
                                                      className="radio-text-padding"
                                                      style={{
                                                        paddingLeft: "10px",
                                                        marginLeft: "-5px",
                                                      }}
                                                    >
                                                      <DatePickerComponent
                                                        ctrlId={`dateChangeLinvo-${idx}`}
                                                        HandleDateChange={
                                                          HandleRepeaterDateChange
                                                        }
                                                        minDate={dayjs(
                                                          "01-01-1920",
                                                        )}
                                                        maxDate={dayjs()}
                                                        locale="en"
                                                        date={data.dateChange}
                                                      />
                                                    </span>
                                                  </div>
                                                  <div
                                                    className="option-without-input"
                                                    style={{
                                                      display: "flex",
                                                      alignItems: "flex-start",
                                                    }}
                                                  >
                                                    <label className="option-fbox">
                                                      <input
                                                        type="radio"
                                                        style={{
                                                          marginTop: "6px",
                                                        }}
                                                        id={idx}
                                                        name={
                                                          "dateChangeRdLinvo" +
                                                          idx
                                                        }
                                                        value={99}
                                                        checked={
                                                          data.dateChangeRd ===
                                                          99
                                                        }
                                                        onChange={
                                                          HandleRepeaterFieldChange
                                                        }
                                                      />
                                                      <span
                                                        style={{
                                                          paddingLeft: "10px",
                                                        }}
                                                      >
                                                        Date of change unknown
                                                      </span>
                                                    </label>
                                                  </div>
                                                  <ErrorField
                                                    show={
                                                      data.dateChangeRdEmpty
                                                    }
                                                    message={
                                                      ELCharacteristics_MSGS.General
                                                    }
                                                    style={{
                                                      paddingLeft: "0px",
                                                    }}
                                                  />
                                                  <ErrorField
                                                    show={data.dateChangeEmpty}
                                                    message={
                                                      ELCharacteristics_MSGS.DateChangeEmpty
                                                    }
                                                    style={{
                                                      paddingLeft: "0px",
                                                    }}
                                                  />
                                                  <ErrorField
                                                    show={
                                                      data.invalidDateChange
                                                    }
                                                    message={
                                                      CommonError_Msgs.InvalidDate
                                                    }
                                                    style={{
                                                      paddingLeft: "0px",
                                                    }}
                                                  />
                                                  <ErrorField
                                                    show={
                                                      data.dateChangeOutOfBound
                                                    }
                                                    message={
                                                      ELCharacteristics_MSGS.DateChangeOutOfBound
                                                    }
                                                    style={{
                                                      paddingLeft: "0px",
                                                    }}
                                                  />
                                                </div>
                                              ) : (
                                                <span
                                                  style={{
                                                    textAlign: "center",
                                                  }}
                                                  className="not-applicable"
                                                >
                                                  <b>Not applicable</b>
                                                </span>
                                              )}
                                            </td>
                                          </tr>
                                        </tbody>
                                      </table>
                                    </div>
                                  </div>
                                </div>
                              ))}

                            {/* Add button */}
                            {Q381ALinvoDetails &&
                              Q381ALinvoDetails.length > 0 &&
                              Number(Q381ALinvoDetails[0].changeType) !== 1 &&
                              Number(Q381ALinvoDetails[0].changeType) !==
                                99 && (
                                <div className="sub-question-bot-sapce-ul mt-2">
                                  <div
                                    className="answer-list-text"
                                    onClick={AddQ381ALinvoRow}
                                  >
                                    <a>
                                      <img
                                        width="18px"
                                        src="../Assets/images/plus.png"
                                        alt=""
                                      />
                                    </a>
                                    <a className="radio-text-padding">
                                      Click here to add another dose/frequency
                                      change
                                    </a>
                                  </div>
                                </div>
                              )}
                          </div>
                        )}
                        {/* Q38.1.A End */}
                        <br></br> <br></br>
                        {/* Q38.2 NEw changes */}
                        {isElrexvayliSelected && isNewEcrfA == true && (
                          <div className="question question-weight">
                            <span>Q38.2.A</span>
                            <span className="quest-text-pad">
                              Please provide information on any{" "}
                              <i>dose interruptions or dose delays</i>, that
                              occurred from completion of the step-up dosing
                              phase until the end of the line of therapy with{" "}
                              <span
                                style={{
                                  color: PageDesignConstants.fontColor,
                                  fontWeight: "bolder",
                                }}
                              >
                                {isElrexvayliSelected && (
                                  <>
                                    ELREXFIO<sup>TM</sup> (elranatamab-bcmm)
                                  </>
                                )}
                              </span>{" "}
                              Add additional rows for each interruption/delay
                              that occurred.
                              <br />
                            </span>
                          </div>
                        )}
                        {/* Q38.2.A ELREXFIO */}
                        {isElrexvayliSelected && isNewEcrfA && (
                          <div className="dependent-section">
                            {Q382AElrexDetails &&
                              Q382AElrexDetails.length > 0 &&
                              Q382AElrexDetails.map((data, idx) => (
                                <div className="loop-section-ul" key={idx}>
                                  <div className="loop-head">
                                    <span
                                      className="question-weight"
                                      style={{ float: "inline-start" }}
                                    >
                                      {GetOrdinal(idx + 1)} Dose interruptions/
                                      Dose delay
                                    </span>
                                    {idx !== 0 && (
                                      <div
                                        className="delete-btn-div"
                                        style={{ float: "inline-end" }}
                                      >
                                        <img
                                          width="16px"
                                          src="../Assets/images/Icon-material-delete.png"
                                          alt=""
                                          style={{ cursor: "pointer" }}
                                          onClick={() =>
                                            DeleteQ382AElrexRow(idx)
                                          }
                                        />
                                      </div>
                                    )}
                                  </div>

                                  {/* ELREXFIO Table */}
                                  <div className="question-bot-sapce">
                                    <div className="sub-question grid-overflow">
                                      <table
                                        style={{
                                          border: "1px solid black",
                                          borderCollapse: "collapse",
                                          width: "100%",
                                        }}
                                      >
                                        <thead>
                                          <tr>
                                            <th
                                              style={{
                                                border: "1px solid black",
                                                padding: "8px",
                                                textAlign: "center",
                                              }}
                                            >
                                              Treatment
                                            </th>
                                            <th
                                              style={{
                                                border: "1px solid black",
                                                padding: "8px",
                                                textAlign: "center",
                                              }}
                                            >
                                              Type of change
                                            </th>
                                            <th
                                              style={{
                                                border: "1px solid black",
                                                padding: "8px",
                                                textAlign: "center",
                                                minWidth: "280px",
                                              }}
                                            >
                                              Reason for change
                                            </th>
                                            <th
                                              style={{
                                                border: "1px solid black",
                                                padding: "8px",
                                                textAlign: "center",
                                              }}
                                            >
                                              Start of dose interruption/delay
                                            </th>
                                            <th
                                              style={{
                                                border: "1px solid black",
                                                padding: "8px",
                                                textAlign: "center",
                                              }}
                                            >
                                              End of dose interruption/delay
                                            </th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          <tr>
                                            {/* Column A: Treatment */}
                                            <td
                                              style={{
                                                border: "1px solid black",
                                                padding: "8px",
                                              }}
                                            >
                                              <span className="quest-text-pad">
                                                ELREXFIO<sup>TM</sup>{" "}
                                                (elranatamab-bcmm)
                                              </span>
                                            </td>

                                            <td
                                              style={{
                                                border: "1px solid black",
                                                padding: "8px",
                                              }}
                                            >
                                              {/* Radio 1 - No interruption/delay */}
                                              <div className="option-without-input">
                                                <label className="d-flex align-items-start">
                                                  <input
                                                    style={{ marginTop: "6px" }}
                                                    type="radio"
                                                    id={idx}
                                                    name={`changeTypeElr382A${idx}`}
                                                    value="1"
                                                    checked={
                                                      Number(
                                                        data.changeType,
                                                      ) === 1
                                                    }
                                                    onChange={(e) =>
                                                      HandleQ382RepeaterFieldChange(
                                                        e,
                                                        "Elrexfio",
                                                        idx,
                                                      )
                                                    }
                                                  />
                                                  <span
                                                    className="radio-text-padding"
                                                    style={{
                                                      paddingLeft: "10px",
                                                      marginLeft: "-5px",
                                                    }}
                                                  >
                                                    No dose interruption or
                                                    delay occurred
                                                  </span>
                                                </label>
                                              </div>

                                              {/* Radio 2 - Interruption/delay */}
                                              <div className="option-without-input">
                                                <label className="option-fbox">
                                                  <input
                                                    type="radio"
                                                    id={idx}
                                                    name={`changeTypeElr382A${idx}`}
                                                    value="2"
                                                    checked={
                                                      Number(
                                                        data.changeType,
                                                      ) === 2
                                                    }
                                                    onChange={(e) =>
                                                      HandleQ382RepeaterFieldChange(
                                                        e,
                                                        "Elrexfio",
                                                        idx,
                                                      )
                                                    }
                                                  />
                                                  <span
                                                    className="radio-text-padding"
                                                    style={{
                                                      paddingLeft: "10px",
                                                      marginLeft: "-5px",
                                                    }}
                                                  >
                                                    Dose interruption or delay
                                                  </span>
                                                </label>
                                              </div>

                                              {/* Radio 3 - Unknown */}
                                              <div
                                                className="option-without-input"
                                                style={{
                                                  display: "flex",
                                                  alignItems: "flex-start",
                                                }}
                                              >
                                                <label className="option-fbox">
                                                  <input
                                                    type="radio"
                                                    style={{ marginTop: "6px" }}
                                                    id={idx}
                                                    name={`changeTypeElr382A${idx}`}
                                                    value="99"
                                                    checked={
                                                      Number(
                                                        data.changeType,
                                                      ) === 99
                                                    }
                                                    onChange={(e) =>
                                                      HandleQ382RepeaterFieldChange(
                                                        e,
                                                        "Elrexfio",
                                                        idx,
                                                      )
                                                    }
                                                  />
                                                  <span
                                                    style={{
                                                      paddingLeft: "10px",
                                                    }}
                                                  >
                                                    Unknown if dose interruption
                                                    or delay occurred
                                                  </span>
                                                </label>
                                              </div>

                                              <ErrorField
                                                show={data.changeTypeEmpty}
                                                message={
                                                  ELCharacteristics_MSGS.ChangeTypeEmpty
                                                }
                                                style={{ paddingLeft: "0px" }}
                                              />
                                            </td>

                                            {/* Column C: Reasons for change */}
                                            <td
                                              style={{
                                                border: "1px solid black",
                                                padding: "8px",
                                                textAlign: "start",
                                              }}
                                            >
                                              {data.changeType !== 1 &&
                                              data.changeType !== 99 ? (
                                                <div>
                                                  {/* Primary Reasons */}
                                                  {data.reasonList?.map(
                                                    (reason, itemidx) => (
                                                      <div
                                                        key={
                                                          reason.bispecifictreatmentdiscontinuationreasonid
                                                        }
                                                        className="option-without-input"
                                                        style={{
                                                          display: "flex",
                                                          flexDirection:
                                                            "column",
                                                        }}
                                                      >
                                                        <label className="option-fbox">
                                                          <input
                                                            type="checkbox"
                                                            id={`reasonElr_${idx}_${itemidx}`}
                                                            name={`reasonElr_${idx}_${itemidx}`}
                                                            idx={idx}
                                                            itemidx={itemidx}
                                                            value={
                                                              reason.bispecifictreatmentdiscontinuationreasonid
                                                            }
                                                            checked={
                                                              reason.isSelected ||
                                                              false
                                                            }
                                                            onChange={(e) =>
                                                              HandleQ382ARepeaterChange(
                                                                e,
                                                                "Elrex",
                                                                reason.reasonName,
                                                                idx,
                                                                itemidx,
                                                                null,
                                                              )
                                                            }
                                                          />
                                                          <span
                                                            style={{
                                                              paddingLeft:
                                                                "10px",
                                                            }}
                                                          >
                                                            {reason.reasonName}
                                                            {reason.superscripttext && (
                                                              <sup>
                                                                {
                                                                  reason.superscripttext
                                                                }
                                                              </sup>
                                                            )}
                                                          </span>
                                                        </label>

                                                        {/* "Other" text field - Show only if this reason contains "Other" and is selected */}
                                                        {reason.reasonName?.includes(
                                                          "Other",
                                                        ) && (
                                                          <div
                                                            style={{
                                                              paddingLeft:
                                                                "25px",
                                                              marginTop: "5px",
                                                            }}
                                                          >
                                                            <textarea
                                                              className="input-dash"
                                                              id={`reasonTextElr_${idx}_${itemidx}`}
                                                              name={`reasonTextElr_${idx}_${itemidx}`}
                                                              idx={idx}
                                                              itemidx={itemidx}
                                                              value={
                                                                Q382AElrexDetails[
                                                                  idx
                                                                ]
                                                                  ?.reasonOtherText ||
                                                                ""
                                                              }
                                                              onChange={(e) =>
                                                                HandleQ382ARepeaterChange(
                                                                  e,
                                                                  "Elrex",
                                                                  reason.reasonName,
                                                                  idx,
                                                                  itemidx,
                                                                  null,
                                                                )
                                                              }
                                                              maxLength={300}
                                                              style={{
                                                                width: "100%",
                                                                minHeight:
                                                                  "60px",
                                                                resize:
                                                                  "vertical",
                                                              }}
                                                            />
                                                            <div
                                                              style={{
                                                                fontSize:
                                                                  "11px",
                                                                color: "#666",
                                                                marginTop:
                                                                  "2px",
                                                              }}
                                                            ></div>
                                                            <ErrorField
                                                              show={
                                                                data.reasonOtherTextEmpty
                                                              }
                                                              message={
                                                                ELCharacteristics_MSGS.ReasonOtherTextEmpty
                                                              }
                                                              style={{
                                                                paddingLeft:
                                                                  "0px",
                                                              }}
                                                            />
                                                          </div>
                                                        )}

                                                        {/* AE Sub-reasons - Only show if this is "Adverse event" (ID=2) and it's selected */}
                                                        {reason.bispecifictreatmentdiscontinuationreasonid ===
                                                          2 &&
                                                          reason.isSelected &&
                                                          reason.aeSubReasons
                                                            ?.length > 0 && (
                                                            <div
                                                              style={{
                                                                paddingLeft:
                                                                  "25px",
                                                                marginTop:
                                                                  "5px",
                                                              }}
                                                            >
                                                              {reason.aeSubReasons.map(
                                                                (ae, aeIdx) => {
                                                                  // ✅ Check if this is "AE type unknown" - check BOTH possible property names
                                                                  const aeName =
                                                                    ae.reasonName ||
                                                                    ae.bispecifictreatmentdiscontinuationreasonae ||
                                                                    "";
                                                                  const isAEUnknown =
                                                                    aeName
                                                                      .toLowerCase()
                                                                      .includes(
                                                                        "unknown",
                                                                      );

                                                                  return (
                                                                    <div
                                                                      key={
                                                                        ae.bispecifictreatmentdiscontinuationreasonaeid
                                                                      }
                                                                      className="option-without-input"
                                                                    >
                                                                      <label className="option-fbox">
                                                                        <input
                                                                          type={
                                                                            isAEUnknown
                                                                              ? "radio"
                                                                              : "checkbox"
                                                                          } // ✅ Radio for unknown, checkbox for others
                                                                          id={`aeReasonElr_${idx}_${itemidx}_${aeIdx}`}
                                                                          name={
                                                                            isAEUnknown
                                                                              ? `aeUnknownElr_${idx}_${itemidx}`
                                                                              : `aeReasonElr_${idx}_${itemidx}_${aeIdx}`
                                                                          }
                                                                          idx={
                                                                            idx
                                                                          }
                                                                          itemidx={
                                                                            itemidx
                                                                          }
                                                                          aeidx={
                                                                            aeIdx
                                                                          }
                                                                          value={
                                                                            ae.bispecifictreatmentdiscontinuationreasonaeid
                                                                          }
                                                                          checked={
                                                                            ae.isSelected ||
                                                                            false
                                                                          }
                                                                          onChange={(
                                                                            e,
                                                                          ) =>
                                                                            HandleQ382ARepeaterChange(
                                                                              e,
                                                                              "Elrex",
                                                                              reason.reasonName,
                                                                              idx,
                                                                              itemidx,
                                                                              aeIdx,
                                                                            )
                                                                          }
                                                                        />
                                                                        <span
                                                                          style={{
                                                                            paddingLeft:
                                                                              "10px",
                                                                          }}
                                                                        >
                                                                          {
                                                                            ae.reasonName
                                                                          }{" "}
                                                                          {/* ✅ Using the correct property name from your original code */}
                                                                          {ae.superscripttext && (
                                                                            <sup>
                                                                              {
                                                                                ae.superscripttext
                                                                              }
                                                                            </sup>
                                                                          )}
                                                                        </span>
                                                                      </label>
                                                                      {ae.reasonName ===
                                                                        DiscontinuationReason.NeurologicalEvent &&
                                                                        ae.isSelected && (
                                                                          <div
                                                                            style={{
                                                                              marginLeft:
                                                                                "28px",
                                                                              marginTop:
                                                                                "8px",
                                                                            }}
                                                                          >
                                                                            {data
                                                                              .neurologicAEReasonList
                                                                              ?.length >
                                                                              0 &&
                                                                              data.neurologicAEReasonList.map(
                                                                                (
                                                                                  aeNeuro,
                                                                                  aeNeuroIdx,
                                                                                ) => {
                                                                                  return (
                                                                                    <div
                                                                                      key={
                                                                                        aeNeuroIdx
                                                                                      }
                                                                                    >
                                                                                      <label
                                                                                        className="d-flex align-items-start"
                                                                                        htmlFor={`adverse_neuro_elr_${itemidx}_${aeIdx}_${aeNeuroIdx}`}
                                                                                      >
                                                                                        <input
                                                                                          type="checkbox"
                                                                                          id={`adverse_neuro_elr_${itemidx}_${aeIdx}_${aeNeuroIdx}`}
                                                                                          name={`adverse_neuro_elr_${itemidx}_${aeIdx}_${aeNeuroIdx}`}
                                                                                          checked={
                                                                                            aeNeuro.isSelected
                                                                                          }
                                                                                          onChange={(
                                                                                            e,
                                                                                          ) =>
                                                                                            HandleNeuroRepeaterFieldChange(
                                                                                              e,
                                                                                              "382A",
                                                                                              RRMMCohortTreatment.ELREXFIO,
                                                                                              aeNeuroIdx,
                                                                                              idx,
                                                                                            )
                                                                                          }
                                                                                          style={{
                                                                                            marginTop:
                                                                                              "6px",
                                                                                          }}
                                                                                        />

                                                                                        <span
                                                                                          style={{
                                                                                            textAlign:
                                                                                              "left",
                                                                                          }}
                                                                                          className="radio-text-padding"
                                                                                        >
                                                                                          {
                                                                                            aeNeuro.reasonName
                                                                                          }
                                                                                        </span>
                                                                                      </label>

                                                                                      {aeNeuro.reasonName.includes(
                                                                                        "Other",
                                                                                      ) && (
                                                                                        <div>
                                                                                          <label
                                                                                            htmlFor={`neuro_reasontxt_elr_${itemidx}_${aeNeuroIdx}`}
                                                                                          >
                                                                                            <span
                                                                                              style={{
                                                                                                paddingLeft:
                                                                                                  "28px",
                                                                                              }}
                                                                                              className="radio-text-padding"
                                                                                            >
                                                                                              <textarea
                                                                                                style={{
                                                                                                  width:
                                                                                                    "264px",
                                                                                                }}
                                                                                                id={`neuro_reasontxt_elr_${itemidx}_${aeNeuroIdx}`}
                                                                                                name={`neuro_reasontxt_elr_${itemidx}_${aeNeuroIdx}`}
                                                                                                type="text"
                                                                                                value={
                                                                                                  aeNeuro.reasonText
                                                                                                }
                                                                                                onChange={(
                                                                                                  e,
                                                                                                ) =>
                                                                                                  HandleNeuroRepeaterFieldChange(
                                                                                                    e,
                                                                                                    "382A",
                                                                                                    RRMMCohortTreatment.ELREXFIO,
                                                                                                    aeNeuroIdx,
                                                                                                    idx,
                                                                                                  )
                                                                                                }
                                                                                                maxLength={
                                                                                                  300
                                                                                                }
                                                                                              />
                                                                                            </span>
                                                                                          </label>
                                                                                          <ErrorField
                                                                                            show={
                                                                                              data.neuroOtherTextEmpty
                                                                                            }
                                                                                            message={
                                                                                              ELCharacteristics_MSGS.Q38AENeurologicOther
                                                                                            }
                                                                                          />
                                                                                        </div>
                                                                                      )}
                                                                                    </div>
                                                                                  );
                                                                                },
                                                                              )}
                                                                            <ErrorField
                                                                              show={
                                                                                data.neuroReasonEmpty
                                                                              }
                                                                              message={
                                                                                ELCharacteristics_MSGS.Q38AENeurologicEmpty
                                                                              }
                                                                            />
                                                                          </div>
                                                                        )}
                                                                    </div>
                                                                  );
                                                                },
                                                              )}
                                                              <ErrorField
                                                                show={
                                                                  data.aeSubReasonEmpty
                                                                }
                                                                message={
                                                                  ELCharacteristics_MSGS.Q38AEEmpty
                                                                }
                                                                style={{
                                                                  paddingLeft:
                                                                    "0px",
                                                                }}
                                                              />
                                                            </div>
                                                          )}
                                                      </div>
                                                    ),
                                                  )}

                                                  {/* ELREXFIO - Unknown Option */}
                                                  <div
                                                    className="option-without-input"
                                                    style={{
                                                      display: "flex",
                                                      marginTop: "10px",
                                                    }}
                                                  >
                                                    <label className="option-fbox">
                                                      <input
                                                        type="radio"
                                                        id={idx}
                                                        name={
                                                          "reasonUnknownElr382A" +
                                                          idx
                                                        }
                                                        checked={
                                                          data.reasonUnknown ||
                                                          false
                                                        }
                                                        value={true}
                                                        onChange={(e) =>
                                                          HandleQ382RepeaterFieldChange(
                                                            e,
                                                            "Elrexfio",
                                                            idx,
                                                          )
                                                        }
                                                      />
                                                      <span
                                                        style={{
                                                          paddingLeft: "10px",
                                                        }}
                                                      >
                                                        Unknown
                                                      </span>
                                                    </label>
                                                  </div>
                                                  <ErrorField
                                                    show={data.reasonEmpty}
                                                    message={
                                                      ELCharacteristics_MSGS.Q38ReasonEmpty
                                                    }
                                                    style={{
                                                      paddingLeft: "0px",
                                                    }}
                                                  />

                                                  <ErrorField
                                                    show={data.deathDtWarning}
                                                    message={
                                                      ELCharacteristics_MSGS.deathWarningQ20
                                                    }
                                                    style={{
                                                      paddingLeft: "0px",
                                                    }}
                                                  />
                                                </div>
                                              ) : (
                                                <span
                                                  style={{
                                                    textAlign: "center",
                                                  }}
                                                  className="not-applicable"
                                                >
                                                  <b>Not applicable</b>
                                                </span>
                                              )}
                                            </td>

                                            {/* Column D: Date of change */}
                                            <td
                                              style={{
                                                border: "1px solid black",
                                                padding: "8px",
                                              }}
                                            >
                                              {data.changeType !== 1 &&
                                              data.changeType !== 99 ? (
                                                <div>
                                                  <div
                                                    className="option-without-input"
                                                    style={{ display: "flex" }}
                                                  >
                                                    <input
                                                      type="radio"
                                                      id={idx}
                                                      name={
                                                        "startDateChangeRdElr" +
                                                        idx
                                                      }
                                                      value={1}
                                                      checked={
                                                        data.startDateChangeRd ===
                                                        1
                                                      }
                                                      onChange={(e) =>
                                                        HandleQ382RepeaterFieldChange(
                                                          e,
                                                          "Elrexfio",
                                                          idx,
                                                        )
                                                      }
                                                    />
                                                    <span
                                                      className="radio-text-padding"
                                                      style={{
                                                        paddingLeft: "10px",
                                                        marginLeft: "-5px",
                                                      }}
                                                    >
                                                      <DatePickerComponent
                                                        ctrlId={`startDateChangeElr-${idx}`}
                                                        HandleDateChange={
                                                          HandleRepeaterDateChange
                                                        }
                                                        minDate={dayjs(
                                                          "01-01-1920",
                                                        )}
                                                        maxDate={dayjs()}
                                                        locale="en"
                                                        date={
                                                          data.startDateChange
                                                        }
                                                      />
                                                    </span>
                                                  </div>
                                                  <div
                                                    className="option-without-input"
                                                    style={{
                                                      display: "flex",
                                                      alignItems: "flex-start",
                                                    }}
                                                  >
                                                    <label className="option-fbox">
                                                      <input
                                                        type="radio"
                                                        style={{
                                                          marginTop: "6px",
                                                        }}
                                                        id={idx}
                                                        name={
                                                          "startDateChangeRdElr" +
                                                          idx
                                                        }
                                                        value={99}
                                                        checked={
                                                          data.startDateChangeRd ===
                                                          99
                                                        }
                                                        onChange={(e) =>
                                                          HandleQ382RepeaterFieldChange(
                                                            e,
                                                            "Elrexfio",
                                                            idx,
                                                          )
                                                        }
                                                      />
                                                      <span
                                                        style={{
                                                          paddingLeft: "10px",
                                                        }}
                                                      >
                                                        Start date of dose
                                                        interruption or delay
                                                        unknown
                                                      </span>
                                                    </label>
                                                  </div>
                                                  <ErrorField
                                                    show={
                                                      data.startDateChangeRdEmpty
                                                    }
                                                    message={
                                                      ELCharacteristics_MSGS.General
                                                    }
                                                    style={{
                                                      paddingLeft: "0px",
                                                    }}
                                                  />
                                                  <ErrorField
                                                    show={
                                                      data.startDateChangeEmpty
                                                    }
                                                    message={
                                                      ELCharacteristics_MSGS.DateChangeEmpty
                                                    }
                                                    style={{
                                                      paddingLeft: "0px",
                                                    }}
                                                  />
                                                  <ErrorField
                                                    show={
                                                      data.invalidStartDateChange
                                                    }
                                                    message={
                                                      CommonError_Msgs.InvalidDate
                                                    }
                                                    style={{
                                                      paddingLeft: "0px",
                                                    }}
                                                  />
                                                  <ErrorField
                                                    show={
                                                      data.startDateChangeOutOfBound
                                                    }
                                                    message={
                                                      ELCharacteristics_MSGS.DateChangeOutOfBound
                                                    }
                                                    style={{
                                                      paddingLeft: "0px",
                                                    }}
                                                  />
                                                  <ErrorField
                                                    show={
                                                      data.interruptionStartDateUnknownWarning
                                                    }
                                                    message={
                                                      ELCharacteristics_MSGS.Q382AWarningUnk
                                                    }
                                                    style={{
                                                      paddingLeft: "0px",
                                                    }}
                                                  />
                                                </div>
                                              ) : (
                                                <span
                                                  style={{
                                                    textAlign: "center",
                                                  }}
                                                  className="not-applicable"
                                                >
                                                  <b>Not applicable</b>
                                                </span>
                                              )}
                                            </td>

                                            <td
                                              style={{
                                                border: "1px solid black",
                                                padding: "8px",
                                              }}
                                            >
                                              {data.changeType !== 1 &&
                                              data.changeType !== 99 ? (
                                                <div>
                                                  <div
                                                    className="option-without-input"
                                                    style={{
                                                      display: "flex",
                                                      alignItems: "flex-start",
                                                    }}
                                                  >
                                                    <label className="option-fbox">
                                                      <input
                                                        type="radio"
                                                        style={{
                                                          marginTop: "6px",
                                                        }}
                                                        id={idx}
                                                        name={
                                                          "endDateChangeRdElr" +
                                                          idx
                                                        }
                                                        value={1}
                                                        checked={
                                                          data.endDateChangeRd ===
                                                          1
                                                        }
                                                        onChange={(e) =>
                                                          HandleQ382RepeaterFieldChange(
                                                            e,
                                                            "Elrexfio",
                                                            idx,
                                                          )
                                                        }
                                                      />
                                                      <span
                                                        style={{
                                                          paddingLeft: "10px",
                                                        }}
                                                      >
                                                        Dose interruption or
                                                        delay ongoing
                                                      </span>
                                                    </label>
                                                  </div>

                                                  <div
                                                    className="option-without-input"
                                                    style={{ display: "flex" }}
                                                  >
                                                    <input
                                                      type="radio"
                                                      id={idx}
                                                      name={
                                                        "endDateChangeRdElr" +
                                                        idx
                                                      }
                                                      value={2}
                                                      checked={
                                                        data.endDateChangeRd ===
                                                        2
                                                      }
                                                      onChange={(e) =>
                                                        HandleQ382RepeaterFieldChange(
                                                          e,
                                                          "Elrexfio",
                                                          idx,
                                                        )
                                                      }
                                                    />
                                                    <span
                                                      className="radio-text-padding"
                                                      style={{
                                                        paddingLeft: "10px",
                                                        marginLeft: "-5px",
                                                      }}
                                                    >
                                                      <DatePickerComponent
                                                        ctrlId={`endDateChangeElr-${idx}`}
                                                        HandleDateChange={
                                                          HandleRepeaterDateChange
                                                        }
                                                        minDate={dayjs(
                                                          "01-01-1920",
                                                        )}
                                                        maxDate={dayjs()}
                                                        locale="en"
                                                        date={
                                                          data.endDateChange
                                                        }
                                                      />
                                                    </span>
                                                  </div>
                                                  <div
                                                    className="option-without-input"
                                                    style={{
                                                      display: "flex",
                                                      alignItems: "flex-start",
                                                    }}
                                                  >
                                                    <label className="option-fbox">
                                                      <input
                                                        type="radio"
                                                        style={{
                                                          marginTop: "6px",
                                                        }}
                                                        id={idx}
                                                        name={
                                                          "endDateChangeRdElr" +
                                                          idx
                                                        }
                                                        value={99}
                                                        checked={
                                                          data.endDateChangeRd ===
                                                          99
                                                        }
                                                        onChange={(e) =>
                                                          HandleQ382RepeaterFieldChange(
                                                            e,
                                                            "Elrexfio",
                                                            idx,
                                                          )
                                                        }
                                                      />
                                                      <span
                                                        style={{
                                                          paddingLeft: "10px",
                                                        }}
                                                      >
                                                        End date of dose
                                                        interruption or delay
                                                        unknown
                                                      </span>
                                                    </label>
                                                  </div>
                                                  <ErrorField
                                                    show={
                                                      data.endDateChangeRdEmpty
                                                    }
                                                    message={
                                                      ELCharacteristics_MSGS.General
                                                    }
                                                    style={{
                                                      paddingLeft: "0px",
                                                    }}
                                                  />
                                                  <ErrorField
                                                    show={
                                                      data.endDateChangeEmpty
                                                    }
                                                    message={
                                                      ELCharacteristics_MSGS.DateChangeEmpty
                                                    }
                                                    style={{
                                                      paddingLeft: "0px",
                                                    }}
                                                  />
                                                  <ErrorField
                                                    show={
                                                      data.invalidEndDateChange
                                                    }
                                                    message={
                                                      CommonError_Msgs.InvalidDate
                                                    }
                                                    style={{
                                                      paddingLeft: "0px",
                                                    }}
                                                  />
                                                  <ErrorField
                                                    show={
                                                      data.endDateChangeOutOfBound
                                                    }
                                                    message={
                                                      ELCharacteristics_MSGS.DateChangeOutOfBound
                                                    }
                                                    style={{
                                                      paddingLeft: "0px",
                                                    }}
                                                  />

                                                  <ErrorField
                                                    show={
                                                      data.interruptionEndDateUnknownWarning
                                                    }
                                                    message={
                                                      ELCharacteristics_MSGS.Q382AWarningUnk
                                                    }
                                                    style={{
                                                      paddingLeft: "0px",
                                                    }}
                                                  />
                                                </div>
                                              ) : (
                                                <span
                                                  style={{
                                                    textAlign: "center",
                                                  }}
                                                  className="not-applicable"
                                                >
                                                  <b>Not applicable</b>
                                                </span>
                                              )}
                                            </td>
                                          </tr>
                                        </tbody>
                                      </table>
                                    </div>
                                  </div>
                                </div>
                              ))}

                            {/* Add button */}
                            {Q382AElrexDetails &&
                              Q382AElrexDetails.length > 0 &&
                              Number(Q382AElrexDetails[0].changeType) !== 1 &&
                              Number(Q382AElrexDetails[0].changeType) !==
                                99 && (
                                <div className="sub-question-bot-sapce-ul mt-2">
                                  <div
                                    className="answer-list-text"
                                    onClick={AddQ382AElrexRow}
                                  >
                                    <a>
                                      <img
                                        width="18px"
                                        src="../Assets/images/plus.png"
                                        alt=""
                                      />
                                    </a>
                                    <a className="radio-text-padding">
                                      Click here to add another dose
                                      interruption/delay
                                    </a>
                                  </div>
                                </div>
                              )}
                          </div>
                        )}
                        {/* Q38.2.A End */}
                        {/* Q38.2 NEw changes */}
                        {isLinvoveySelected && isNewEcrfA == true && (
                          <div className="question question-weight">
                            <span>Q38.2.A</span>
                            <span className="quest-text-pad">
                              Please provide information on any{" "}
                              <i>dose interruptions or dose delays</i>, that
                              occurred from completion of the step-up dosing
                              phase until the end of the line of therapy with{" "}
                              <span
                                style={{
                                  color: PageDesignConstants.fontColor,
                                  fontWeight: "bolder",
                                }}
                              >
                                {isLinvoveySelected && (
                                  <>
                                    LYNOZYFIC<sup>TM</sup> (linvoseltamab-gcpt)
                                  </>
                                )}
                              </span>{" "}
                              Add additional rows for each interruption/delay
                              that occurred.
                              <br />
                            </span>
                          </div>
                        )}
                        {/* Q38.2.A LINVOSELTAMAB */}
                        {isLinvoveySelected && isNewEcrfA && (
                          <div className="dependent-section">
                            {Q382ALinvoDetails &&
                              Q382ALinvoDetails.length > 0 &&
                              Q382ALinvoDetails.map((data, idx) => (
                                <div className="loop-section-ul" key={idx}>
                                  <div className="loop-head">
                                    <span
                                      className="question-weight"
                                      style={{ float: "inline-start" }}
                                    >
                                      {GetOrdinal(idx + 1)} Dose interruptions/
                                      Dose delay
                                    </span>
                                    {idx !== 0 && (
                                      <div
                                        className="delete-btn-div"
                                        style={{ float: "inline-end" }}
                                      >
                                        <img
                                          width="16px"
                                          src="../Assets/images/Icon-material-delete.png"
                                          alt=""
                                          style={{ cursor: "pointer" }}
                                          onClick={() =>
                                            DeleteQ382ALinvoRow(idx)
                                          }
                                        />
                                      </div>
                                    )}
                                  </div>

                                  {/* LINVOSELTAMAB Table */}
                                  <div className="question-bot-sapce">
                                    <div className="sub-question grid-overflow">
                                      <table
                                        style={{
                                          border: "1px solid black",
                                          borderCollapse: "collapse",
                                          width: "100%",
                                        }}
                                      >
                                        <thead>
                                          <tr>
                                            <th
                                              style={{
                                                border: "1px solid black",
                                                padding: "8px",
                                                textAlign: "center",
                                              }}
                                            >
                                              Treatment
                                            </th>
                                            <th
                                              style={{
                                                border: "1px solid black",
                                                padding: "8px",
                                                textAlign: "center",
                                              }}
                                            >
                                              Type of change
                                            </th>
                                            <th
                                              style={{
                                                border: "1px solid black",
                                                padding: "8px",
                                                textAlign: "center",
                                                minWidth: "280px",
                                              }}
                                            >
                                              Reason for change
                                            </th>
                                            <th
                                              style={{
                                                border: "1px solid black",
                                                padding: "8px",
                                                textAlign: "center",
                                              }}
                                            >
                                              Start of dose interruption/delay
                                            </th>
                                            <th
                                              style={{
                                                border: "1px solid black",
                                                padding: "8px",
                                                textAlign: "center",
                                              }}
                                            >
                                              End of dose interruption/delay
                                            </th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          <tr>
                                            {/* Column A: Treatment */}
                                            <td
                                              style={{
                                                border: "1px solid black",
                                                padding: "8px",
                                              }}
                                            >
                                              <span className="quest-text-pad">
                                                LYNOZYFIC
                                                <sup className="superscript">
                                                  TM
                                                </sup>{" "}
                                                (linvoquetamab-gcpt)
                                              </span>
                                            </td>

                                            <td
                                              style={{
                                                border: "1px solid black",
                                                padding: "8px",
                                              }}
                                            >
                                              <div className="option-without-input">
                                                <label className="option-fbox">
                                                  <input
                                                    type="radio"
                                                    id={idx}
                                                    name={`changeTypeLinvo382A${idx}`}
                                                    value="1"
                                                    checked={
                                                      Number(
                                                        data.changeType,
                                                      ) === 1
                                                    }
                                                    onChange={(e) =>
                                                      HandleQ382RepeaterFieldChange(
                                                        e,
                                                        "Linvo",
                                                        idx,
                                                      )
                                                    }
                                                  />
                                                  <span
                                                    className="radio-text-padding"
                                                    style={{
                                                      paddingLeft: "10px",
                                                      marginLeft: "-5px",
                                                    }}
                                                  >
                                                    No dose interruption or
                                                    delay occurred
                                                  </span>
                                                </label>
                                              </div>

                                              <div className="option-without-input">
                                                <label className="option-fbox">
                                                  <input
                                                    type="radio"
                                                    id={idx}
                                                    name={`changeTypeLinvo382A${idx}`}
                                                    value="2"
                                                    checked={
                                                      Number(
                                                        data.changeType,
                                                      ) === 2
                                                    }
                                                    onChange={(e) =>
                                                      HandleQ382RepeaterFieldChange(
                                                        e,
                                                        "Linvo",
                                                        idx,
                                                      )
                                                    }
                                                  />
                                                  <span
                                                    className="radio-text-padding"
                                                    style={{
                                                      paddingLeft: "10px",
                                                      marginLeft: "-5px",
                                                    }}
                                                  >
                                                    Dose interruption or delay
                                                  </span>
                                                </label>
                                              </div>

                                              <div
                                                className="option-without-input"
                                                style={{
                                                  display: "flex",
                                                  alignItems: "flex-start",
                                                }}
                                              >
                                                <label className="option-fbox">
                                                  <input
                                                    type="radio"
                                                    style={{ marginTop: "6px" }}
                                                    id={idx}
                                                    name={`changeTypeLinvo382A${idx}`}
                                                    value="99"
                                                    checked={
                                                      Number(
                                                        data.changeType,
                                                      ) === 99
                                                    }
                                                    onChange={(e) =>
                                                      HandleQ382RepeaterFieldChange(
                                                        e,
                                                        "Linvo",
                                                        idx,
                                                      )
                                                    }
                                                  />
                                                  <span
                                                    style={{
                                                      paddingLeft: "10px",
                                                    }}
                                                  >
                                                    Unknown if dose interruption
                                                    or delay occurred
                                                  </span>
                                                </label>
                                              </div>

                                              <ErrorField
                                                show={data.changeTypeEmpty}
                                                message={
                                                  ELCharacteristics_MSGS.ChangeTypeEmpty
                                                }
                                                style={{ paddingLeft: "0px" }}
                                              />
                                            </td>

                                            {/* Column C: Reasons for change */}
                                            <td
                                              style={{
                                                border: "1px solid black",
                                                padding: "8px",
                                                textAlign: "start",
                                              }}
                                            >
                                              {data.changeType !== 1 &&
                                              data.changeType !== 99 ? (
                                                <div>
                                                  {/* Primary Reasons */}
                                                  {data.reasonList?.map(
                                                    (reason, itemidx) => (
                                                      <div
                                                        key={
                                                          reason.bispecifictreatmentdiscontinuationreasonid
                                                        }
                                                        className="option-without-input"
                                                        style={{
                                                          display: "flex",
                                                          flexDirection:
                                                            "column",
                                                        }}
                                                      >
                                                        <label className="option-fbox">
                                                          <input
                                                            type="checkbox"
                                                            id={`reasonLinvo_${idx}_${itemidx}`}
                                                            name={`reasonLinvo_${idx}_${itemidx}`}
                                                            idx={idx}
                                                            itemidx={itemidx}
                                                            value={
                                                              reason.bispecifictreatmentdiscontinuationreasonid
                                                            }
                                                            checked={
                                                              reason.isSelected ||
                                                              false
                                                            }
                                                            onChange={(e) =>
                                                              HandleQ382ARepeaterChange(
                                                                e,
                                                                "Linvo",
                                                                reason.reasonName,
                                                                idx,
                                                                itemidx,
                                                                null,
                                                              )
                                                            }
                                                          />
                                                          <span
                                                            style={{
                                                              paddingLeft:
                                                                "10px",
                                                            }}
                                                          >
                                                            {reason.reasonName}
                                                            {reason.superscripttext && (
                                                              <sup>
                                                                {
                                                                  reason.superscripttext
                                                                }
                                                              </sup>
                                                            )}
                                                          </span>
                                                        </label>

                                                        {/* "Other" text field - Show only if this reason contains "Other" and is selected */}
                                                        {reason.reasonName?.includes(
                                                          "Other",
                                                        ) && (
                                                          <div
                                                            style={{
                                                              paddingLeft:
                                                                "25px",
                                                              marginTop: "5px",
                                                            }}
                                                          >
                                                            <textarea
                                                              className="input-dash"
                                                              id={`reasonTextLinvo_${idx}_${itemidx}`}
                                                              name={`reasonTextLinvo_${idx}_${itemidx}`}
                                                              idx={idx}
                                                              itemidx={itemidx}
                                                              value={
                                                                Q382ALinvoDetails[
                                                                  idx
                                                                ]
                                                                  ?.reasonOtherText ||
                                                                ""
                                                              }
                                                              onChange={(e) =>
                                                                HandleQ382ARepeaterChange(
                                                                  e,
                                                                  "Linvo",
                                                                  reason.reasonName,
                                                                  idx,
                                                                  itemidx,
                                                                  null,
                                                                )
                                                              }
                                                              maxLength={300}
                                                              style={{
                                                                width: "100%",
                                                                minHeight:
                                                                  "60px",
                                                                resize:
                                                                  "vertical",
                                                              }}
                                                            />
                                                            <div
                                                              style={{
                                                                fontSize:
                                                                  "11px",
                                                                color: "#666",
                                                                marginTop:
                                                                  "2px",
                                                              }}
                                                            ></div>
                                                            <ErrorField
                                                              show={
                                                                data.reasonOtherTextEmpty
                                                              }
                                                              message={
                                                                ELCharacteristics_MSGS.ReasonOtherTextEmpty
                                                              }
                                                              style={{
                                                                paddingLeft:
                                                                  "0px",
                                                              }}
                                                            />
                                                          </div>
                                                        )}

                                                        {/* AE Sub-reasons - Only show if this is "Adverse event" (ID=2) and it's selected */}
                                                        {reason.bispecifictreatmentdiscontinuationreasonid ===
                                                          2 &&
                                                          reason.isSelected &&
                                                          reason.aeSubReasons
                                                            ?.length > 0 && (
                                                            <div
                                                              style={{
                                                                paddingLeft:
                                                                  "25px",
                                                                marginTop:
                                                                  "5px",
                                                              }}
                                                            >
                                                              {reason.aeSubReasons.map(
                                                                (ae, aeIdx) => {
                                                                  const aeName =
                                                                    ae.reasonName ||
                                                                    ae.bispecifictreatmentdiscontinuationreasonae ||
                                                                    "";
                                                                  const isAEUnknown =
                                                                    aeName
                                                                      .toLowerCase()
                                                                      .includes(
                                                                        "unknown",
                                                                      );

                                                                  return (
                                                                    <div
                                                                      key={
                                                                        ae.bispecifictreatmentdiscontinuationreasonaeid
                                                                      }
                                                                      className="option-without-input"
                                                                    >
                                                                      <label className="option-fbox">
                                                                        <input
                                                                          type={
                                                                            isAEUnknown
                                                                              ? "radio"
                                                                              : "checkbox"
                                                                          }
                                                                          id={`aeReasonLinvo_${idx}_${itemidx}_${aeIdx}`}
                                                                          name={
                                                                            isAEUnknown
                                                                              ? `aeUnknownLinvo_${idx}_${itemidx}`
                                                                              : `aeReasonLinvo_${idx}_${itemidx}_${aeIdx}`
                                                                          }
                                                                          idx={
                                                                            idx
                                                                          }
                                                                          itemidx={
                                                                            itemidx
                                                                          }
                                                                          aeidx={
                                                                            aeIdx
                                                                          }
                                                                          value={
                                                                            ae.bispecifictreatmentdiscontinuationreasonaeid
                                                                          }
                                                                          checked={
                                                                            ae.isSelected ||
                                                                            false
                                                                          }
                                                                          onChange={(
                                                                            e,
                                                                          ) =>
                                                                            HandleQ382ARepeaterChange(
                                                                              e,
                                                                              "Linvo",
                                                                              reason.reasonName,
                                                                              idx,
                                                                              itemidx,
                                                                              aeIdx,
                                                                            )
                                                                          }
                                                                        />
                                                                        <span
                                                                          style={{
                                                                            paddingLeft:
                                                                              "10px",
                                                                          }}
                                                                        >
                                                                          {
                                                                            ae.reasonName
                                                                          }
                                                                          {ae.superscripttext && (
                                                                            <sup>
                                                                              {
                                                                                ae.superscripttext
                                                                              }
                                                                            </sup>
                                                                          )}
                                                                        </span>
                                                                      </label>
                                                                      {ae.reasonName ===
                                                                        DiscontinuationReason.NeurologicalEvent &&
                                                                        ae.isSelected && (
                                                                          <div
                                                                            style={{
                                                                              marginLeft:
                                                                                "28px",
                                                                              marginTop:
                                                                                "8px",
                                                                            }}
                                                                          >
                                                                            {data
                                                                              .neurologicAEReasonList
                                                                              ?.length >
                                                                              0 &&
                                                                              data.neurologicAEReasonList.map(
                                                                                (
                                                                                  aeNeuro,
                                                                                  aeNeuroIdx,
                                                                                ) => {
                                                                                  return (
                                                                                    <div
                                                                                      key={
                                                                                        aeNeuroIdx
                                                                                      }
                                                                                    >
                                                                                      <label
                                                                                        className="d-flex align-items-start"
                                                                                        htmlFor={`adverse_neuro_linvo_${itemidx}_${aeIdx}_${aeNeuroIdx}`}
                                                                                      >
                                                                                        <input
                                                                                          type="checkbox"
                                                                                          id={`adverse_neuro_linvo_${itemidx}_${aeIdx}_${aeNeuroIdx}`}
                                                                                          name={`adverse_neuro_linvo_${itemidx}_${aeIdx}_${aeNeuroIdx}`}
                                                                                          checked={
                                                                                            aeNeuro.isSelected
                                                                                          }
                                                                                          onChange={(
                                                                                            e,
                                                                                          ) =>
                                                                                            HandleNeuroRepeaterFieldChange(
                                                                                              e,
                                                                                              "382A",
                                                                                              RRMMCohortTreatment.Linvoseltamab,
                                                                                              aeNeuroIdx,
                                                                                              idx,
                                                                                            )
                                                                                          }
                                                                                          style={{
                                                                                            marginTop:
                                                                                              "6px",
                                                                                          }}
                                                                                        />

                                                                                        <span
                                                                                          style={{
                                                                                            textAlign:
                                                                                              "left",
                                                                                          }}
                                                                                          className="radio-text-padding"
                                                                                        >
                                                                                          {
                                                                                            aeNeuro.reasonName
                                                                                          }
                                                                                        </span>
                                                                                      </label>

                                                                                      {aeNeuro.reasonName.includes(
                                                                                        "Other",
                                                                                      ) && (
                                                                                        <div>
                                                                                          <label
                                                                                            htmlFor={`neuro_reasontxt_linvo_${itemidx}_${aeNeuroIdx}`}
                                                                                          >
                                                                                            <span
                                                                                              style={{
                                                                                                paddingLeft:
                                                                                                  "28px",
                                                                                              }}
                                                                                              className="radio-text-padding"
                                                                                            >
                                                                                              <textarea
                                                                                                style={{
                                                                                                  width:
                                                                                                    "264px",
                                                                                                }}
                                                                                                id={`neuro_reasontxt_linvo_${itemidx}_${aeNeuroIdx}`}
                                                                                                name={`neuro_reasontxt_linvo_${itemidx}_${aeNeuroIdx}`}
                                                                                                type="text"
                                                                                                value={
                                                                                                  aeNeuro.reasonText
                                                                                                }
                                                                                                onChange={(
                                                                                                  e,
                                                                                                ) =>
                                                                                                  HandleNeuroRepeaterFieldChange(
                                                                                                    e,
                                                                                                    "382A",
                                                                                                    RRMMCohortTreatment.Linvoseltamab,
                                                                                                    aeNeuroIdx,
                                                                                                    idx,
                                                                                                  )
                                                                                                }
                                                                                                maxLength={
                                                                                                  300
                                                                                                }
                                                                                              />
                                                                                            </span>
                                                                                          </label>
                                                                                          <ErrorField
                                                                                            show={
                                                                                              data.neuroOtherTextEmpty
                                                                                            }
                                                                                            message={
                                                                                              ELCharacteristics_MSGS.Q38AENeurologicOther
                                                                                            }
                                                                                          />
                                                                                        </div>
                                                                                      )}
                                                                                    </div>
                                                                                  );
                                                                                },
                                                                              )}
                                                                            <ErrorField
                                                                              show={
                                                                                data.neuroReasonEmpty
                                                                              }
                                                                              message={
                                                                                ELCharacteristics_MSGS.Q38AENeurologicEmpty
                                                                              }
                                                                            />
                                                                          </div>
                                                                        )}
                                                                    </div>
                                                                  );
                                                                },
                                                              )}
                                                              <ErrorField
                                                                show={
                                                                  data.aeSubReasonEmpty
                                                                }
                                                                message={
                                                                  ELCharacteristics_MSGS.Q38AEEmpty
                                                                }
                                                                style={{
                                                                  paddingLeft:
                                                                    "0px",
                                                                }}
                                                              />
                                                            </div>
                                                          )}
                                                      </div>
                                                    ),
                                                  )}

                                                  {/* LINVOSELTAMAB - Unknown Option */}
                                                  <div
                                                    className="option-without-input"
                                                    style={{
                                                      display: "flex",
                                                      marginTop: "5px",
                                                    }}
                                                  >
                                                    <label className="option-fbox">
                                                      <input
                                                        type="radio"
                                                        id={idx}
                                                        name={
                                                          "reasonUnknownLinvo382A" +
                                                          idx
                                                        }
                                                        checked={
                                                          data.reasonUnknown ||
                                                          false
                                                        }
                                                        value={true}
                                                        onChange={(e) =>
                                                          HandleQ382RepeaterFieldChange(
                                                            e,
                                                            "Linvo",
                                                            idx,
                                                          )
                                                        }
                                                      />
                                                      <span
                                                        style={{
                                                          paddingLeft: "10px",
                                                        }}
                                                      >
                                                        Unknown
                                                      </span>
                                                    </label>
                                                  </div>

                                                  <ErrorField
                                                    show={data.reasonEmpty}
                                                    message={
                                                      ELCharacteristics_MSGS.Q38ReasonEmpty
                                                    }
                                                    style={{
                                                      paddingLeft: "0px",
                                                    }}
                                                  />
                                                  <ErrorField
                                                    show={data.deathDtWarning}
                                                    message={
                                                      ELCharacteristics_MSGS.deathWarningQ20
                                                    }
                                                    style={{
                                                      paddingLeft: "0px",
                                                    }}
                                                  />
                                                </div>
                                              ) : (
                                                <span
                                                  style={{
                                                    textAlign: "center",
                                                  }}
                                                  className="not-applicable"
                                                >
                                                  <b>Not applicable</b>
                                                </span>
                                              )}
                                            </td>

                                            {/* Column D: start Date of change */}
                                            <td
                                              style={{
                                                border: "1px solid black",
                                                padding: "8px",
                                              }}
                                            >
                                              {data.changeType !== 1 &&
                                              data.changeType !== 99 ? (
                                                <div>
                                                  <div
                                                    className="option-without-input"
                                                    style={{ display: "flex" }}
                                                  >
                                                    <input
                                                      type="radio"
                                                      id={idx}
                                                      name={
                                                        "startDateChangeRdLinvo" +
                                                        idx
                                                      }
                                                      value={1}
                                                      checked={
                                                        data.startDateChangeRd ===
                                                        1
                                                      }
                                                      onChange={(e) =>
                                                        HandleQ382RepeaterFieldChange(
                                                          e,
                                                          "Linvo",
                                                          idx,
                                                        )
                                                      }
                                                    />
                                                    <span
                                                      className="radio-text-padding"
                                                      style={{
                                                        paddingLeft: "10px",
                                                        marginLeft: "-5px",
                                                      }}
                                                    >
                                                      <DatePickerComponent
                                                        ctrlId={`startDateChangeLinvo-${idx}`}
                                                        HandleDateChange={
                                                          HandleRepeaterDateChange
                                                        }
                                                        minDate={dayjs(
                                                          "01-01-1920",
                                                        )}
                                                        maxDate={dayjs()}
                                                        locale="en"
                                                        date={
                                                          data.startDateChange
                                                        }
                                                      />
                                                    </span>
                                                  </div>
                                                  <div
                                                    className="option-without-input"
                                                    style={{
                                                      display: "flex",
                                                      alignItems: "flex-start",
                                                    }}
                                                  >
                                                    <label className="option-fbox">
                                                      <input
                                                        type="radio"
                                                        style={{
                                                          marginTop: "6px",
                                                        }}
                                                        id={idx}
                                                        name={
                                                          "startDateChangeRdLinvo" +
                                                          idx
                                                        }
                                                        value={99}
                                                        checked={
                                                          data.startDateChangeRd ===
                                                          99
                                                        }
                                                        onChange={(e) =>
                                                          HandleQ382RepeaterFieldChange(
                                                            e,
                                                            "Linvo",
                                                            idx,
                                                          )
                                                        }
                                                      />
                                                      <span
                                                        style={{
                                                          paddingLeft: "10px",
                                                        }}
                                                      >
                                                        Start date of dose
                                                        interruption or delay
                                                        unknown
                                                      </span>
                                                    </label>
                                                  </div>
                                                  <ErrorField
                                                    show={
                                                      data.startDateChangeRdEmpty
                                                    }
                                                    message={
                                                      ELCharacteristics_MSGS.General
                                                    }
                                                    style={{
                                                      paddingLeft: "0px",
                                                    }}
                                                  />
                                                  <ErrorField
                                                    show={
                                                      data.startDateChangeEmpty
                                                    }
                                                    message={
                                                      ELCharacteristics_MSGS.DateChangeEmpty
                                                    }
                                                    style={{
                                                      paddingLeft: "0px",
                                                    }}
                                                  />
                                                  <ErrorField
                                                    show={
                                                      data.invalidStartDateChange
                                                    }
                                                    message={
                                                      CommonError_Msgs.InvalidDate
                                                    }
                                                    style={{
                                                      paddingLeft: "0px",
                                                    }}
                                                  />
                                                  <ErrorField
                                                    show={
                                                      data.startDateChangeOutOfBound
                                                    }
                                                    message={
                                                      ELCharacteristics_MSGS.DateChangeOutOfBound
                                                    }
                                                    style={{
                                                      paddingLeft: "0px",
                                                    }}
                                                  />
                                                  <ErrorField
                                                    show={
                                                      data.interruptionStartDateUnknownWarning
                                                    }
                                                    message={
                                                      ELCharacteristics_MSGS.Q382AWarningUnk
                                                    }
                                                    style={{
                                                      paddingLeft: "0px",
                                                    }}
                                                  />
                                                </div>
                                              ) : (
                                                <span
                                                  style={{
                                                    textAlign: "center",
                                                  }}
                                                  className="not-applicable"
                                                >
                                                  <b>Not applicable</b>
                                                </span>
                                              )}
                                            </td>

                                            {/* Column E: End Date of change */}
                                            <td
                                              style={{
                                                border: "1px solid black",
                                                padding: "8px",
                                              }}
                                            >
                                              {data.changeType !== 1 &&
                                              data.changeType !== 99 ? (
                                                <div>
                                                  <div
                                                    className="option-without-input"
                                                    style={{
                                                      display: "flex",
                                                      alignItems: "flex-start",
                                                    }}
                                                  >
                                                    <label className="option-fbox">
                                                      <input
                                                        type="radio"
                                                        style={{
                                                          marginTop: "6px",
                                                        }}
                                                        id={idx}
                                                        name={
                                                          "endDateChangeRdLinvo" +
                                                          idx
                                                        }
                                                        value={1}
                                                        checked={
                                                          data.endDateChangeRd ===
                                                          1
                                                        }
                                                        onChange={(e) =>
                                                          HandleQ382RepeaterFieldChange(
                                                            e,
                                                            "Linvo",
                                                            idx,
                                                          )
                                                        }
                                                      />
                                                      <span
                                                        style={{
                                                          paddingLeft: "10px",
                                                        }}
                                                      >
                                                        Dose interruption or
                                                        delay ongoing
                                                      </span>
                                                    </label>
                                                  </div>

                                                  <div
                                                    className="option-without-input"
                                                    style={{ display: "flex" }}
                                                  >
                                                    <input
                                                      type="radio"
                                                      id={idx}
                                                      name={
                                                        "endDateChangeRdLinvo" +
                                                        idx
                                                      }
                                                      value={2}
                                                      checked={
                                                        data.endDateChangeRd ===
                                                        2
                                                      }
                                                      onChange={(e) =>
                                                        HandleQ382RepeaterFieldChange(
                                                          e,
                                                          "Linvo",
                                                          idx,
                                                        )
                                                      }
                                                    />
                                                    <span
                                                      className="radio-text-padding"
                                                      style={{
                                                        paddingLeft: "10px",
                                                        marginLeft: "-5px",
                                                      }}
                                                    >
                                                      <DatePickerComponent
                                                        ctrlId={`endDateChangeLinvo-${idx}`}
                                                        HandleDateChange={
                                                          HandleRepeaterDateChange
                                                        }
                                                        minDate={dayjs(
                                                          "01-01-1920",
                                                        )}
                                                        maxDate={dayjs()}
                                                        locale="en"
                                                        date={
                                                          data.endDateChange
                                                        }
                                                      />
                                                    </span>
                                                  </div>
                                                  <div
                                                    className="option-without-input"
                                                    style={{
                                                      display: "flex",
                                                      alignItems: "flex-start",
                                                    }}
                                                  >
                                                    <label className="option-fbox">
                                                      <input
                                                        type="radio"
                                                        style={{
                                                          marginTop: "6px",
                                                        }}
                                                        id={idx}
                                                        name={
                                                          "endDateChangeRdLinvo" +
                                                          idx
                                                        }
                                                        value={99}
                                                        checked={
                                                          data.endDateChangeRd ===
                                                          99
                                                        }
                                                        onChange={(e) =>
                                                          HandleQ382RepeaterFieldChange(
                                                            e,
                                                            "Linvo",
                                                            idx,
                                                          )
                                                        }
                                                      />
                                                      <span
                                                        style={{
                                                          paddingLeft: "10px",
                                                        }}
                                                      >
                                                        End date of dose
                                                        interruption or delay
                                                        unknown
                                                      </span>
                                                    </label>
                                                  </div>
                                                  <ErrorField
                                                    show={
                                                      data.endDateChangeRdEmpty
                                                    }
                                                    message={
                                                      ELCharacteristics_MSGS.General
                                                    }
                                                    style={{
                                                      paddingLeft: "0px",
                                                    }}
                                                  />
                                                  <ErrorField
                                                    show={
                                                      data.endDateChangeEmpty
                                                    }
                                                    message={
                                                      ELCharacteristics_MSGS.DateChangeEmpty
                                                    }
                                                    style={{
                                                      paddingLeft: "0px",
                                                    }}
                                                  />
                                                  <ErrorField
                                                    show={
                                                      data.invalidEndDateChange
                                                    }
                                                    message={
                                                      CommonError_Msgs.InvalidDate
                                                    }
                                                    style={{
                                                      paddingLeft: "0px",
                                                    }}
                                                  />
                                                  <ErrorField
                                                    show={
                                                      data.endDateChangeOutOfBound
                                                    }
                                                    message={
                                                      ELCharacteristics_MSGS.DateChangeOutOfBound
                                                    }
                                                    style={{
                                                      paddingLeft: "0px",
                                                    }}
                                                  />

                                                  <ErrorField
                                                    show={
                                                      data.interruptionEndDateUnknownWarning
                                                    }
                                                    message={
                                                      ELCharacteristics_MSGS.Q382AWarningUnk
                                                    }
                                                    style={{
                                                      paddingLeft: "0px",
                                                    }}
                                                  />
                                                </div>
                                              ) : (
                                                <span
                                                  style={{
                                                    textAlign: "center",
                                                  }}
                                                  className="not-applicable"
                                                >
                                                  <b>Not applicable</b>
                                                </span>
                                              )}
                                            </td>
                                          </tr>
                                        </tbody>
                                      </table>
                                    </div>
                                  </div>
                                </div>
                              ))}

                            {/* Add button */}
                            {Q382ALinvoDetails &&
                              Q382ALinvoDetails.length > 0 &&
                              Number(Q382ALinvoDetails[0].changeType) !== 1 &&
                              Number(Q382ALinvoDetails[0].changeType) !==
                                99 && (
                                <div className="sub-question-bot-sapce-ul mt-2">
                                  <div
                                    className="answer-list-text"
                                    onClick={AddQ382ALinvoRow}
                                  >
                                    <a>
                                      <img
                                        width="18px"
                                        src="../Assets/images/plus.png"
                                        alt=""
                                      />
                                    </a>
                                    <a className="radio-text-padding">
                                      Click here to add another dose
                                      interruption/delay
                                    </a>
                                  </div>
                                </div>
                              )}
                          </div>
                        )}
                        {/* Q38.2.A End */}
                        {/* Question 39  start*/}
                        {/* show if Elrexfio selected */}
                        {!isNewEcrfC && (
                          <div className="">
                            {isElrexvayliSelected &&
                              Q37ElrexDetails &&
                              Q37ElrexDetails.length > 0 &&
                              Q37ElrexDetails.map((data, idx) => (
                                <React.Fragment key={idx}>
                                  <div className="sub-question-bot-sapce ">
                                    <div className="question question-weight">
                                      <span>Q39.</span>
                                      <span className="quest-text-pad">
                                        Which medications were used, if any,
                                        during the <b>step-up dosing phase</b>{" "}
                                        of{" "}
                                        <span
                                          style={{
                                            color:
                                              PageDesignConstants.fontColor,
                                            fontWeight: "bolder",
                                          }}
                                        >
                                          ELREXFIO<sup>TM</sup>{" "}
                                          (elranatamab-bcmm)
                                        </span>{" "}
                                        i.e from
                                        <span
                                          style={{
                                            color:
                                              PageDesignConstants.fontColor,
                                            fontWeight: "bolder",
                                          }}
                                        >
                                          {" "}
                                          {getQ402CIndexDateDisplay(
                                            RRMMCohortTreatment.ELREXFIO,
                                          )}{" "}
                                        </span>
                                        until the end of step-up dosing?{" "}
                                        <i>Select all that apply.</i>
                                        <br />
                                        If you cannot determine if an
                                        administration is part of the{" "}
                                        <strong>Step-Up Dosing Phase, </strong>
                                        please infer the dosing phase using the
                                        ELREXFIO
                                        <sup>TM</sup> (elranatamab-bcmm) dosing
                                        label information provided.
                                      </span>
                                    </div>
                                    <div className="radio-text-padding">
                                      <div className="double-dig-answer">
                                        <div className="">
                                          <label>
                                            <input
                                              type="checkbox"
                                              id={idx}
                                              name={"tocilizumab_elr" + idx}
                                              checked={data.tocilizumab_elr}
                                              onChange={HandleFieldChange}
                                            />
                                            <span className="radio-text-padding">
                                              Tocilizumab
                                            </span>
                                          </label>
                                        </div>

                                        <div className="">
                                          <label>
                                            <input
                                              type="checkbox"
                                              id={idx}
                                              name={"Granulocyte" + idx}
                                              checked={data.Granulocyte}
                                              onChange={HandleFieldChange}
                                            />
                                            <span className="radio-text-padding">
                                              Granulocyte colony-stimulating
                                              factor (G-CSF)
                                            </span>
                                          </label>
                                        </div>
                                        {/* Phase 2  */}
                                        <div className="">
                                          <label>
                                            <input
                                              type="checkbox"
                                              id={idx}
                                              name={"SteroidsElr" + idx}
                                              checked={data.SteroidsElr}
                                              onChange={HandleFieldChange}
                                            />
                                            <span className="radio-text-padding">
                                              Steroids
                                            </span>
                                          </label>
                                        </div>

                                        <div className="option-without-input">
                                          <label className="option-fbox">
                                            <input
                                              type="radio"
                                              id={idx}
                                              name={"noneoftheabove_elr" + idx}
                                              value={99}
                                              checked={
                                                data.noneoftheabove_elr === 99
                                              }
                                              onChange={HandleFieldChange}
                                            />

                                            <span className="radio-text-padding">
                                              None of the above{" "}
                                            </span>
                                          </label>
                                        </div>
                                        <ErrorField
                                          show={data.Q39ElrEmptyHard}
                                          message={
                                            ELCharacteristics_MSGS.Q39ElrEmptyHard
                                          }
                                          style={{
                                            paddingLeft: "0px",
                                          }}
                                        />
                                      </div>
                                    </div>
                                  </div>

                                  {/* Q39.1. */}
                                  {/* Q39.1 retired - render disabled via `false &&` guard below; JSX left intact */}

                                  {false &&
                                    data.tocilizumab_elr &&
                                    !isNewEcrfA && (
                                      <div
                                        style={{
                                          marginLeft: "28px",
                                          marginBlock: "6px",
                                          padding: "5px",
                                        }}
                                      >
                                        <div className="question question-weight">
                                          <span>Q39.1.</span>
                                          <span className="quest-text-pad">
                                            Summarize the reasons why
                                            Tocilizumab was used and dosing
                                            information while receiving
                                            treatment with{" "}
                                            <span
                                              style={{
                                                color:
                                                  PageDesignConstants.fontColor,
                                                fontWeight: "bolder",
                                              }}
                                            >
                                              ELREXFIO<sup>TM</sup>
                                              {"  "}
                                              (elranatamab-bcmm)
                                            </span>
                                            . <i>Select all that apply.</i>
                                          </span>
                                        </div>

                                        <div className="dependent-sub-section">
                                          {/* ELREXFIO® */}
                                          <div className="question-bot-sapce">
                                            <div className="sub-question grid-overflow">
                                              <table
                                                style={{
                                                  border: "1px solid black",
                                                  borderCollapse: "collapse",
                                                  width: "100%",
                                                }}
                                              >
                                                <thead>
                                                  <tr>
                                                    <th
                                                      style={{
                                                        border:
                                                          "1px solid black",
                                                        padding: "8px",
                                                        textAlign: "center",
                                                        width: "20%",
                                                      }}
                                                    >
                                                      Treatment
                                                    </th>
                                                    <th
                                                      style={{
                                                        border:
                                                          "1px solid black",
                                                        padding: "8px",
                                                        textAlign: "center",
                                                        width: "15%",
                                                      }}
                                                    >
                                                      Medication
                                                    </th>
                                                    <th
                                                      style={{
                                                        border:
                                                          "1px solid black",
                                                        padding: "8px",
                                                        textAlign: "center",
                                                        width: "45%",
                                                      }}
                                                    >
                                                      Reason for receiving
                                                      medication
                                                    </th>
                                                  </tr>
                                                </thead>

                                                <tbody>
                                                  <tr>
                                                    <td
                                                      style={{
                                                        border:
                                                          "1px solid black",
                                                        padding: "8px",
                                                      }}
                                                    >
                                                      <span className="quest-text-pad">
                                                        ELREXFIO<sup>TM</sup>{" "}
                                                        (elranatamab-bcmm)
                                                      </span>
                                                    </td>

                                                    <td
                                                      style={{
                                                        border:
                                                          "1px solid black",
                                                        padding: "8px",
                                                      }}
                                                    >
                                                      <div>
                                                        <div>
                                                          <div
                                                            className="option-without-input"
                                                            style={{
                                                              display: "flex",
                                                              alignItems:
                                                                "flex-start",
                                                            }}
                                                          >
                                                            <label className="option-fbox">
                                                              <span
                                                                className="radio-text-padding"
                                                                style={{
                                                                  paddingLeft:
                                                                    "10px",
                                                                }}
                                                              >
                                                                Tocilizumab
                                                              </span>
                                                            </label>
                                                          </div>
                                                        </div>
                                                      </div>
                                                    </td>
                                                    <td
                                                      style={{
                                                        border:
                                                          "1px solid black",
                                                        padding: "8px",
                                                      }}
                                                    >
                                                      <div>
                                                        <div className="">
                                                          <label className="checkbox-option">
                                                            <input
                                                              type="checkbox"
                                                              id={idx}
                                                              name={
                                                                "cytokin_elr" +
                                                                idx
                                                              }
                                                              checked={
                                                                data.cytokin_elr
                                                              }
                                                              onChange={
                                                                HandleFieldChange
                                                              }
                                                            />
                                                            <span className="radio-text-padding">
                                                              Primary
                                                              prophylactic for
                                                              cytokine release
                                                              syndrome (CRS)
                                                            </span>
                                                          </label>
                                                        </div>

                                                        <div className="">
                                                          <label className="checkbox-option">
                                                            <input
                                                              type="checkbox"
                                                              id={idx}
                                                              name={
                                                                "secondary_prophylactic_crs_elr" +
                                                                idx
                                                              }
                                                              checked={
                                                                data.secondary_prophylactic_crs_elr
                                                              }
                                                              onChange={
                                                                HandleFieldChange
                                                              }
                                                            />
                                                            <span className="radio-text-padding">
                                                              Secondary
                                                              prophylactic for
                                                              CRS
                                                            </span>
                                                          </label>
                                                        </div>

                                                        <div className="">
                                                          <label className="checkbox-option">
                                                            <input
                                                              type="checkbox"
                                                              id={idx}
                                                              name={
                                                                "crs_elr" + idx
                                                              }
                                                              checked={
                                                                data.crs_elr
                                                              }
                                                              onChange={
                                                                HandleFieldChange
                                                              }
                                                            />
                                                            <span className="radio-text-padding">
                                                              Treatment or
                                                              supportive care
                                                              for CRS
                                                            </span>
                                                          </label>
                                                        </div>
                                                        <div className="">
                                                          <label className="checkbox-option">
                                                            <input
                                                              type="checkbox"
                                                              id={idx}
                                                              name={
                                                                "neurotoxicity_elr" +
                                                                idx
                                                              }
                                                              checked={
                                                                data.neurotoxicity_elr
                                                              }
                                                              onChange={
                                                                HandleFieldChange
                                                              }
                                                            />
                                                            <span className="radio-text-padding">
                                                              Primary
                                                              prophylactic for
                                                              neurologic events
                                                            </span>
                                                          </label>
                                                        </div>
                                                        <div className="">
                                                          <label className="checkbox-option">
                                                            <input
                                                              type="checkbox"
                                                              id={idx}
                                                              name={
                                                                "secondary_neurotoxicity_elr" +
                                                                idx
                                                              }
                                                              checked={
                                                                data.secondary_neurotoxicity_elr
                                                              }
                                                              onChange={
                                                                HandleFieldChange
                                                              }
                                                            />
                                                            <span className="radio-text-padding">
                                                              Secondary
                                                              prophylactic for
                                                              neurologic events
                                                            </span>
                                                          </label>
                                                        </div>

                                                        <div className="">
                                                          <label className="checkbox-option">
                                                            <input
                                                              type="checkbox"
                                                              id={idx}
                                                              name={
                                                                "tmt_neurotoxicity_elr" +
                                                                idx
                                                              }
                                                              checked={
                                                                data.tmt_neurotoxicity_elr
                                                              }
                                                              onChange={
                                                                HandleFieldChange
                                                              }
                                                            />
                                                            <span className="radio-text-padding">
                                                              Treatment or
                                                              supportive care
                                                              for neurologic
                                                              events
                                                            </span>
                                                          </label>
                                                        </div>

                                                        <div className="">
                                                          <label className="checkbox-option">
                                                            <input
                                                              type="radio"
                                                              id={idx}
                                                              name={
                                                                "tmt_elr_unknown" +
                                                                idx
                                                              }
                                                              checked={
                                                                data.tmt_elr_unknown
                                                              }
                                                              onChange={
                                                                HandleFieldChange
                                                              }
                                                            />
                                                            <span className="radio-text-padding">
                                                              Unknown
                                                            </span>
                                                          </label>
                                                        </div>

                                                        <ErrorField
                                                          show={
                                                            data.Q39elrReasonHard
                                                          }
                                                          message={
                                                            ELCharacteristics_MSGS.Q39elrReasonHard
                                                          }
                                                          style={{
                                                            paddingLeft: "0px",
                                                          }}
                                                        />
                                                      </div>
                                                    </td>
                                                  </tr>
                                                </tbody>
                                              </table>
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    )}

                                  {/* Q39.1.A */}
                                  {data.tocilizumab_elr && isNewEcrfA && (
                                    <div
                                      style={{
                                        marginLeft: "28px",
                                        marginBlock: "6px",
                                        padding: "5px",
                                      }}
                                    >
                                      <div className="question question-weight">
                                        <span>Q39.1.A.</span>
                                        <span className="quest-text-pad">
                                          Summarize the reasons why tocilizumab
                                          was used and dosing information while
                                          receiving treatment with{" "}
                                          <span
                                            style={{
                                              color:
                                                PageDesignConstants.fontColor,
                                              fontWeight: "bolder",
                                            }}
                                          >
                                            ELREXFIO<sup>TM</sup>
                                            {"  "}
                                            (elranatamab-bcmm)
                                          </span>
                                          . <i>Select all that apply.</i>
                                        </span>
                                      </div>

                                      <div className="dependent-section">
                                        {/* ELREXFIO® */}
                                        {q391AElrTocilzumabList?.length > 0 &&
                                          q391AElrTocilzumabList.map(
                                            (itm, index) => (
                                              <div
                                                className="loop-section-ul"
                                                key={index}
                                              >
                                                <div className="loop-head">
                                                  <span
                                                    className="question-weight"
                                                    style={{
                                                      float: "inline-start",
                                                    }}
                                                  >
                                                    {GetOrdinal(index + 1)}{" "}
                                                    ELREXFIO
                                                    <sup>TM</sup>{" "}
                                                    (elranatamab-bcmm)
                                                  </span>
                                                  {index !== 0 && (
                                                    <div
                                                      className="delete-btn-div"
                                                      style={{
                                                        float: "inline-end",
                                                      }}
                                                    >
                                                      <img
                                                        width="16px"
                                                        src="../Assets/images/Icon-material-delete.png"
                                                        alt=""
                                                        style={{
                                                          cursor: "pointer",
                                                        }}
                                                        onClick={() =>
                                                          DeleteQ391ARow(
                                                            index,
                                                            RRMMCohortTreatment.ELREXFIO,
                                                          )
                                                        }
                                                      />
                                                    </div>
                                                  )}
                                                </div>
                                                <div className="question-bot-sapce">
                                                  <div className="sub-question grid-overflow">
                                                    <table
                                                      style={{
                                                        border:
                                                          "1px solid black",
                                                        borderCollapse:
                                                          "collapse",
                                                        width: "100%",
                                                      }}
                                                    >
                                                      <thead>
                                                        <tr>
                                                          <th
                                                            style={{
                                                              border:
                                                                "1px solid black",
                                                              padding: "8px",
                                                              textAlign:
                                                                "center",
                                                              width: "20%",
                                                            }}
                                                          >
                                                            Treatment
                                                          </th>
                                                          <th
                                                            style={{
                                                              border:
                                                                "1px solid black",
                                                              padding: "8px",
                                                              textAlign:
                                                                "center",
                                                              width: "15%",
                                                            }}
                                                          >
                                                            Medication
                                                          </th>
                                                          <th
                                                            style={{
                                                              border:
                                                                "1px solid black",
                                                              padding: "8px",
                                                              textAlign:
                                                                "center",
                                                              width: "15%",
                                                            }}
                                                          >
                                                            Date of tocilizumab
                                                            administration
                                                          </th>
                                                          <th
                                                            style={{
                                                              border:
                                                                "1px solid black",
                                                              padding: "8px",
                                                              textAlign:
                                                                "center",
                                                              width: "45%",
                                                            }}
                                                          >
                                                            Reason for receiving
                                                            medication
                                                          </th>
                                                        </tr>
                                                      </thead>

                                                      <tbody>
                                                        <tr>
                                                          <td
                                                            style={{
                                                              border:
                                                                "1px solid black",
                                                              padding: "8px",
                                                            }}
                                                          >
                                                            <span className="quest-text-pad">
                                                              ELREXFIO
                                                              <sup>TM</sup>{" "}
                                                              (elranatamab-bcmm)
                                                            </span>
                                                          </td>

                                                          <td
                                                            style={{
                                                              border:
                                                                "1px solid black",
                                                              padding: "8px",
                                                            }}
                                                          >
                                                            <div>
                                                              <div>
                                                                <div
                                                                  className="option-without-input"
                                                                  style={{
                                                                    display:
                                                                      "flex",
                                                                    alignItems:
                                                                      "flex-start",
                                                                  }}
                                                                >
                                                                  <label className="option-fbox">
                                                                    <span
                                                                      className="radio-text-padding"
                                                                      style={{
                                                                        paddingLeft:
                                                                          "10px",
                                                                      }}
                                                                    >
                                                                      Tocilizumab
                                                                    </span>
                                                                  </label>
                                                                </div>
                                                              </div>
                                                            </div>
                                                          </td>
                                                          <td
                                                            style={{
                                                              border:
                                                                "1px solid black",
                                                              padding: "8px",
                                                            }}
                                                          >
                                                            <div>
                                                              <div
                                                                className="option-without-input"
                                                                style={{
                                                                  display:
                                                                    "flex",
                                                                }}
                                                              >
                                                                <input
                                                                  type="radio"
                                                                  id={index}
                                                                  name={
                                                                    "administrationDateRD_elr" +
                                                                    index
                                                                  }
                                                                  value={1}
                                                                  checked={
                                                                    itm.administrationDateRD_elr ===
                                                                    1
                                                                  }
                                                                  onChange={
                                                                    HandleRepeaterFieldChange
                                                                  }
                                                                />
                                                                <span
                                                                  className="radio-text-padding"
                                                                  style={{
                                                                    paddingLeft:
                                                                      "10px",
                                                                    marginLeft:
                                                                      "-5px",
                                                                  }}
                                                                >
                                                                  <DatePickerComponent
                                                                    ctrlId={
                                                                      "administrationDate_elr-" +
                                                                      index
                                                                    }
                                                                    HandleDateChange={
                                                                      HandleRepeaterDateChange
                                                                    }
                                                                    optionId={1}
                                                                    minDate={dayjs(
                                                                      "01-01-1920",
                                                                    )}
                                                                    maxDate={dayjs()}
                                                                    locale="en"
                                                                    date={
                                                                      itm.administrationDate_elr
                                                                    }
                                                                  />
                                                                </span>
                                                              </div>

                                                              <div
                                                                className="option-without-input"
                                                                style={{
                                                                  display:
                                                                    "flex",
                                                                }}
                                                              >
                                                                <label className="option-fbox">
                                                                  <input
                                                                    type="radio"
                                                                    id={index}
                                                                    name={
                                                                      "administrationDateRD_elr" +
                                                                      index
                                                                    }
                                                                    value={99}
                                                                    checked={
                                                                      itm.administrationDateRD_elr ===
                                                                      99
                                                                    }
                                                                    onChange={
                                                                      HandleRepeaterFieldChange
                                                                    }
                                                                  />
                                                                  <span
                                                                    style={{
                                                                      paddingLeft:
                                                                        "10px",
                                                                      marginLeft:
                                                                        "-5px",
                                                                    }}
                                                                  >
                                                                    Date of
                                                                    administration
                                                                    unknown
                                                                  </span>
                                                                </label>
                                                              </div>

                                                              <ErrorField
                                                                show={
                                                                  itm.q39ElrAdministrationRDEmpty
                                                                }
                                                                message={
                                                                  ELCharacteristics_MSGS.Q40Init_RdHard
                                                                }
                                                                style={{
                                                                  paddingLeft:
                                                                    "0px",
                                                                }}
                                                              />

                                                              <ErrorField
                                                                show={
                                                                  itm.q39ElrAdministrationDateEmpty
                                                                }
                                                                message={
                                                                  ELCharacteristics_MSGS.Q391ATocilizumabAdministrationDateEmty
                                                                }
                                                                style={{
                                                                  paddingLeft:
                                                                    "0px",
                                                                }}
                                                              />
                                                              <ErrorField
                                                                show={
                                                                  itm.q39ElrInvalidAdministrationDate
                                                                }
                                                                message={
                                                                  CommonError_Msgs.InvalidDate
                                                                }
                                                                style={{
                                                                  paddingLeft:
                                                                    "0px",
                                                                }}
                                                              />
                                                              <ErrorField
                                                                show={
                                                                  q391AElrAdministrationDateOutOfRange
                                                                }
                                                                message={
                                                                  ELCharacteristics_MSGS.Q391ATocilizumabDateChainOutOfBound
                                                                }
                                                                style={{
                                                                  paddingLeft:
                                                                    "0px",
                                                                }}
                                                              />
                                                            </div>
                                                          </td>
                                                          <td
                                                            style={{
                                                              border:
                                                                "1px solid black",
                                                              padding: "8px",
                                                            }}
                                                          >
                                                            <div>
                                                              <div className="">
                                                                <label className="checkbox-option">
                                                                  <input
                                                                    type="checkbox"
                                                                    id={index}
                                                                    name={
                                                                      "primaryProphylacticCrs_elr" +
                                                                      index
                                                                    }
                                                                    checked={
                                                                      itm.primaryProphylacticCrs_elr
                                                                    }
                                                                    onChange={
                                                                      HandleRepeaterFieldChange
                                                                    }
                                                                  />
                                                                  <span className="radio-text-padding">
                                                                    Primary
                                                                    prophylactic
                                                                    for cytokine
                                                                    release
                                                                    syndrome
                                                                    (CRS)
                                                                  </span>
                                                                </label>
                                                              </div>

                                                              <div className="">
                                                                <label className="checkbox-option">
                                                                  <input
                                                                    type="checkbox"
                                                                    id={index}
                                                                    name={
                                                                      "secondaryProphylacticCrs_elr" +
                                                                      index
                                                                    }
                                                                    checked={
                                                                      itm.secondaryProphylacticCrs_elr
                                                                    }
                                                                    onChange={
                                                                      HandleRepeaterFieldChange
                                                                    }
                                                                  />
                                                                  <span className="radio-text-padding">
                                                                    Secondary
                                                                    prophylactic
                                                                    for CRS
                                                                  </span>
                                                                </label>
                                                              </div>

                                                              <div className="">
                                                                <label className="checkbox-option">
                                                                  <input
                                                                    type="checkbox"
                                                                    id={index}
                                                                    name={
                                                                      "supportiveCareCrs_elr" +
                                                                      index
                                                                    }
                                                                    checked={
                                                                      itm.supportiveCareCrs_elr
                                                                    }
                                                                    onChange={
                                                                      HandleRepeaterFieldChange
                                                                    }
                                                                  />
                                                                  <span className="radio-text-padding">
                                                                    Treatment or
                                                                    supportive
                                                                    care for CRS
                                                                  </span>
                                                                </label>
                                                              </div>
                                                              <div className="">
                                                                <label className="checkbox-option">
                                                                  <input
                                                                    type="checkbox"
                                                                    id={index}
                                                                    name={
                                                                      "primaryProphylacticNeurologic_elr" +
                                                                      index
                                                                    }
                                                                    checked={
                                                                      itm.primaryProphylacticNeurologic_elr
                                                                    }
                                                                    onChange={
                                                                      HandleRepeaterFieldChange
                                                                    }
                                                                  />
                                                                  <span className="radio-text-padding">
                                                                    Primary
                                                                    prophylactic
                                                                    for
                                                                    neurologic
                                                                    events
                                                                  </span>
                                                                </label>
                                                              </div>
                                                              <div className="">
                                                                <label className="checkbox-option">
                                                                  <input
                                                                    type="checkbox"
                                                                    id={index}
                                                                    name={
                                                                      "secondaryProphylacticNeurologic_elr" +
                                                                      index
                                                                    }
                                                                    checked={
                                                                      itm.secondaryProphylacticNeurologic_elr
                                                                    }
                                                                    onChange={
                                                                      HandleRepeaterFieldChange
                                                                    }
                                                                  />
                                                                  <span className="radio-text-padding">
                                                                    Secondary
                                                                    prophylactic
                                                                    for
                                                                    neurologic
                                                                    events
                                                                  </span>
                                                                </label>
                                                              </div>

                                                              <div className="">
                                                                <label className="checkbox-option">
                                                                  <input
                                                                    type="checkbox"
                                                                    id={index}
                                                                    name={
                                                                      "supportiveCareNeurologic_elr" +
                                                                      index
                                                                    }
                                                                    checked={
                                                                      itm.supportiveCareNeurologic_elr
                                                                    }
                                                                    onChange={
                                                                      HandleRepeaterFieldChange
                                                                    }
                                                                  />
                                                                  <span className="radio-text-padding">
                                                                    Treatment or
                                                                    supportive
                                                                    care for
                                                                    neurologic
                                                                    events
                                                                  </span>
                                                                </label>
                                                              </div>

                                                              <div className="">
                                                                <label className="checkbox-option">
                                                                  <input
                                                                    type="radio"
                                                                    id={index}
                                                                    name={
                                                                      "medicationReasonUnk_elr" +
                                                                      index
                                                                    }
                                                                    checked={
                                                                      itm.medicationReasonUnk_elr
                                                                    }
                                                                    onChange={
                                                                      HandleRepeaterFieldChange
                                                                    }
                                                                  />
                                                                  <span className="radio-text-padding">
                                                                    Unknown
                                                                  </span>
                                                                </label>
                                                              </div>

                                                              <ErrorField
                                                                show={
                                                                  itm.q39ElrReasonEmpty
                                                                }
                                                                message={
                                                                  ELCharacteristics_MSGS.Q39elrReasonHard
                                                                }
                                                                style={{
                                                                  paddingLeft:
                                                                    "0px",
                                                                }}
                                                              />
                                                            </div>
                                                          </td>
                                                        </tr>
                                                      </tbody>
                                                    </table>
                                                  </div>
                                                </div>
                                              </div>
                                            ),
                                          )}

                                        {q391AElrTocilzumabList?.length > 0 &&
                                          q391AElrTocilzumabList?.length <
                                            550 && (
                                            <div className="sub-question-bot-sapce-ul mt-2">
                                              <div
                                                className="answer-list-text"
                                                onClick={() =>
                                                  AddQ391ARow(
                                                    RRMMCohortTreatment.ELREXFIO,
                                                  )
                                                }
                                              >
                                                <a>
                                                  <img
                                                    width="18px"
                                                    src="../Assets/images/plus.png"
                                                    alt=""
                                                  />
                                                </a>
                                                <a className="radio-text-padding">
                                                  Click here to add another
                                                  tocilizumab administration of
                                                  elranatamab
                                                </a>
                                              </div>
                                            </div>
                                          )}
                                      </div>
                                    </div>
                                  )}

                                  {/* New Phase 39.2  */}
                                  {data.SteroidsElr && (
                                    <div
                                      style={{
                                        marginLeft: "28px",
                                        marginBlock: "6px",
                                        padding: "5px",
                                      }}
                                    >
                                      <div className="question question-weight">
                                        <span>Q39.2.</span>
                                        <span className="quest-text-pad">
                                          Summarize the reasons why steroids
                                          were used and dosing information while
                                          receiving treatment with{" "}
                                          <span
                                            style={{
                                              color:
                                                PageDesignConstants.fontColor,
                                              fontWeight: "bolder",
                                            }}
                                          >
                                            ELREXFIO<sup>TM</sup>{" "}
                                            (elranatamab-bcmm)
                                          </span>
                                          . <i>Select all that apply.</i>
                                        </span>
                                      </div>

                                      <div className="dependent-sub-section">
                                        <div className="question-bot-sapce">
                                          <div className="sub-question grid-overflow">
                                            <table
                                              style={{
                                                border: "1px solid black",
                                                borderCollapse: "collapse",
                                                width: "100%",
                                              }}
                                            >
                                              <thead>
                                                <tr>
                                                  <th
                                                    style={{
                                                      border: "1px solid black",
                                                      padding: "8px",
                                                      textAlign: "center",
                                                      width: "5%",
                                                    }}
                                                  >
                                                    Treatment
                                                  </th>
                                                  <th
                                                    style={{
                                                      border: "1px solid black",
                                                      padding: "8px",
                                                      textAlign: "center",
                                                      width: "5%",
                                                    }}
                                                  >
                                                    Medication
                                                  </th>

                                                  <th
                                                    style={{
                                                      border: "1px solid black",
                                                      padding: "8px",
                                                      textAlign: "center",
                                                      width: "35%",
                                                    }}
                                                  >
                                                    Name and dose of the
                                                    steroid(s) used
                                                  </th>

                                                  <th
                                                    style={{
                                                      border: "1px solid black",
                                                      padding: "8px",
                                                      textAlign: "center",
                                                      width: "50%",
                                                    }}
                                                  >
                                                    Reason for receiving
                                                    medication
                                                  </th>
                                                </tr>
                                              </thead>

                                              <tbody>
                                                <tr>
                                                  <td
                                                    style={{
                                                      border: "1px solid black",
                                                      padding: "8px",
                                                    }}
                                                  >
                                                    <span className="quest-text-pad">
                                                      ELREXFIO<sup>TM</sup>{" "}
                                                      (elranatamab-bcmm)
                                                    </span>
                                                  </td>

                                                  <td
                                                    style={{
                                                      border: "1px solid black",
                                                      padding: "8px",
                                                    }}
                                                  >
                                                    <div>
                                                      <div>
                                                        <div
                                                          className="option-without-input"
                                                          style={{
                                                            display: "flex",
                                                            alignItems:
                                                              "flex-start",
                                                          }}
                                                        >
                                                          <label>
                                                            <span
                                                              className="radio-text-padding"
                                                              style={{
                                                                paddingLeft:
                                                                  "10px",
                                                              }}
                                                            >
                                                              Steroids
                                                            </span>
                                                          </label>
                                                        </div>
                                                      </div>
                                                    </div>
                                                  </td>

                                                  {/* Start */}

                                                  <td
                                                    style={{
                                                      border: "1px solid black",
                                                      padding: "8px",
                                                    }}
                                                  >
                                                    <div>
                                                      <div>
                                                        <div>
                                                          <label className="option-fbox">
                                                            <input
                                                              style={{
                                                                marginTop:
                                                                  "6px",
                                                              }}
                                                              type="checkbox"
                                                              id={idx}
                                                              name={
                                                                "betamethasone_elr" +
                                                                idx
                                                              }
                                                              checked={
                                                                data.betamethasone_elr
                                                              }
                                                              onChange={
                                                                HandleFieldChange
                                                              }
                                                            />
                                                            <span
                                                              style={{
                                                                paddingLeft:
                                                                  "15px",
                                                              }}
                                                            >
                                                              Betamethasone
                                                              (total dose:
                                                              <input
                                                                className="sub-input-dash-num"
                                                                style={{
                                                                  border:
                                                                    "none",
                                                                  borderBottom:
                                                                    "1px solid #727272",
                                                                  background:
                                                                    "transparent",
                                                                  outline:
                                                                    "none",
                                                                }}
                                                                id={idx}
                                                                name={
                                                                  "betamethasone_elrNum" +
                                                                  idx
                                                                }
                                                                type="text"
                                                                value={
                                                                  data.betamethasone_elrNum
                                                                }
                                                                onKeyDown={
                                                                  AllowOnlyNonZeroNumeric
                                                                }
                                                                onChange={
                                                                  HandleFieldChange
                                                                }
                                                                maxLength={10}
                                                              />
                                                              mg)
                                                            </span>
                                                          </label>
                                                        </div>

                                                        <div className="">
                                                          <label className="option-fbox">
                                                            <input
                                                              style={{
                                                                marginTop:
                                                                  "6px",
                                                              }}
                                                              type="checkbox"
                                                              id={idx}
                                                              name={
                                                                "dexamethasone_elr" +
                                                                idx
                                                              }
                                                              checked={
                                                                data.dexamethasone_elr
                                                              }
                                                              onChange={
                                                                HandleFieldChange
                                                              }
                                                            />
                                                            <span
                                                              style={{
                                                                paddingLeft:
                                                                  "15px",
                                                              }}
                                                            >
                                                              Dexamethasone
                                                              (total dose:
                                                              <input
                                                                className="sub-input-dash-num"
                                                                style={{
                                                                  border:
                                                                    "none",
                                                                  borderBottom:
                                                                    "1px solid #727272",
                                                                  background:
                                                                    "transparent",
                                                                  outline:
                                                                    "none",
                                                                }}
                                                                id={idx}
                                                                name={
                                                                  "dexamethasone_elrNum" +
                                                                  idx
                                                                }
                                                                type="text"
                                                                value={
                                                                  data.dexamethasone_elrNum
                                                                }
                                                                onKeyDown={
                                                                  AllowOnlyNonZeroNumeric
                                                                }
                                                                onChange={
                                                                  HandleFieldChange
                                                                }
                                                                maxLength={10}
                                                              />
                                                              mg)
                                                            </span>
                                                          </label>
                                                        </div>
                                                        <div className="">
                                                          <label className="option-fbox">
                                                            <input
                                                              type="checkbox"
                                                              style={{
                                                                marginTop:
                                                                  "6px",
                                                              }}
                                                              id={idx}
                                                              name={
                                                                "hydrocortisone_elr" +
                                                                idx
                                                              }
                                                              checked={
                                                                data.hydrocortisone_elr
                                                              }
                                                              onChange={
                                                                HandleFieldChange
                                                              }
                                                            />
                                                            <span
                                                              style={{
                                                                paddingLeft:
                                                                  "15px",
                                                              }}
                                                            >
                                                              Hydrocortisone
                                                              (oral or
                                                              intravenous only)
                                                              <br></br>(total
                                                              dose:
                                                              <input
                                                                className="sub-input-dash-num"
                                                                style={{
                                                                  border:
                                                                    "none",
                                                                  borderBottom:
                                                                    "1px solid #727272",
                                                                  background:
                                                                    "transparent",
                                                                  outline:
                                                                    "none",
                                                                }}
                                                                id={idx}
                                                                name={
                                                                  "hydrocortisone_elrNum" +
                                                                  idx
                                                                }
                                                                type="text"
                                                                value={
                                                                  data.hydrocortisone_elrNum
                                                                }
                                                                onKeyDown={
                                                                  AllowOnlyNonZeroNumeric
                                                                }
                                                                onChange={
                                                                  HandleFieldChange
                                                                }
                                                                maxLength={10}
                                                              />
                                                              mg)
                                                            </span>
                                                          </label>
                                                        </div>

                                                        <div className="">
                                                          <label className="option-fbox">
                                                            <input
                                                              style={{
                                                                marginTop:
                                                                  "6px",
                                                              }}
                                                              type="checkbox"
                                                              id={idx}
                                                              name={
                                                                "methylprednisolone" +
                                                                idx
                                                              }
                                                              checked={
                                                                data.methylprednisolone
                                                              }
                                                              onChange={
                                                                HandleFieldChange
                                                              }
                                                            />
                                                            <span
                                                              style={{
                                                                paddingLeft:
                                                                  "15px",
                                                              }}
                                                            >
                                                              Methylprednisolone
                                                              (total dose:
                                                              <input
                                                                className="sub-input-dash-num"
                                                                style={{
                                                                  border:
                                                                    "none",
                                                                  borderBottom:
                                                                    "1px solid #727272",
                                                                  background:
                                                                    "transparent",
                                                                  outline:
                                                                    "none",
                                                                }}
                                                                id={idx}
                                                                name={
                                                                  "methylprednisoloneNum" +
                                                                  idx
                                                                }
                                                                type="text"
                                                                value={
                                                                  data.methylprednisoloneNum
                                                                }
                                                                onKeyDown={
                                                                  AllowOnlyNonZeroNumeric
                                                                }
                                                                onChange={
                                                                  HandleFieldChange
                                                                }
                                                                maxLength={10}
                                                              />
                                                              mg)
                                                            </span>
                                                          </label>
                                                        </div>

                                                        <div className="">
                                                          <label className="option-fbox">
                                                            <input
                                                              style={{
                                                                marginTop:
                                                                  "6px",
                                                              }}
                                                              type="checkbox"
                                                              id={idx}
                                                              name={
                                                                "prednisolone_elr" +
                                                                idx
                                                              }
                                                              checked={
                                                                data.prednisolone_elr
                                                              }
                                                              onChange={
                                                                HandleFieldChange
                                                              }
                                                            />
                                                            <span
                                                              style={{
                                                                paddingLeft:
                                                                  "15px",
                                                              }}
                                                            >
                                                              Prednisolone
                                                              (total dose:
                                                              <input
                                                                className="sub-input-dash-num"
                                                                style={{
                                                                  border:
                                                                    "none",
                                                                  borderBottom:
                                                                    "1px solid #727272",
                                                                  background:
                                                                    "transparent",
                                                                  outline:
                                                                    "none",
                                                                }}
                                                                id={idx}
                                                                name={
                                                                  "prednisolone_elrNum" +
                                                                  idx
                                                                }
                                                                type="text"
                                                                value={
                                                                  data.prednisolone_elrNum
                                                                }
                                                                onKeyDown={
                                                                  AllowOnlyNonZeroNumeric
                                                                }
                                                                onChange={
                                                                  HandleFieldChange
                                                                }
                                                                maxLength={10}
                                                              />
                                                              mg)
                                                            </span>
                                                          </label>
                                                        </div>

                                                        <div className="">
                                                          <label className="option-fbox">
                                                            <input
                                                              style={{
                                                                marginTop:
                                                                  "6px",
                                                              }}
                                                              type="checkbox"
                                                              id={idx}
                                                              name={
                                                                "prednisone_elr" +
                                                                idx
                                                              }
                                                              checked={
                                                                data.prednisone_elr
                                                              }
                                                              onChange={
                                                                HandleFieldChange
                                                              }
                                                            />
                                                            <span
                                                              style={{
                                                                paddingLeft:
                                                                  "15px",
                                                              }}
                                                            >
                                                              Prednisone (total
                                                              dose:
                                                              <input
                                                                className="sub-input-dash-num"
                                                                style={{
                                                                  border:
                                                                    "none",
                                                                  borderBottom:
                                                                    "1px solid #727272",
                                                                  background:
                                                                    "transparent",
                                                                  outline:
                                                                    "none",
                                                                }}
                                                                id={idx}
                                                                name={
                                                                  "prednisone_elrNum" +
                                                                  idx
                                                                }
                                                                type="text"
                                                                value={
                                                                  data.prednisone_elrNum
                                                                }
                                                                onKeyDown={
                                                                  AllowOnlyNonZeroNumeric
                                                                }
                                                                onChange={
                                                                  HandleFieldChange
                                                                }
                                                                maxLength={10}
                                                              />
                                                              mg)
                                                            </span>
                                                          </label>
                                                        </div>

                                                        <div className="">
                                                          <label className="option-fbox">
                                                            <input
                                                              style={{
                                                                marginTop:
                                                                  "6px",
                                                              }}
                                                              type="checkbox"
                                                              id={idx}
                                                              name={
                                                                "triamcinolone_elr" +
                                                                idx
                                                              }
                                                              checked={
                                                                data.triamcinolone_elr
                                                              }
                                                              onChange={
                                                                HandleFieldChange
                                                              }
                                                            />
                                                            <span
                                                              style={{
                                                                paddingLeft:
                                                                  "15px",
                                                              }}
                                                            >
                                                              Triamcinolone
                                                              (total dose:
                                                              <input
                                                                className="sub-input-dash-num"
                                                                style={{
                                                                  border:
                                                                    "none",
                                                                  borderBottom:
                                                                    "1px solid #727272",
                                                                  background:
                                                                    "transparent",
                                                                  outline:
                                                                    "none",
                                                                }}
                                                                id={idx}
                                                                name={
                                                                  "triamcinolone_elrNum" +
                                                                  idx
                                                                }
                                                                type="text"
                                                                value={
                                                                  data.triamcinolone_elrNum
                                                                }
                                                                onKeyDown={
                                                                  AllowOnlyNonZeroNumeric
                                                                }
                                                                onChange={
                                                                  HandleFieldChange
                                                                }
                                                                maxLength={10}
                                                              />
                                                              mg)
                                                            </span>
                                                          </label>
                                                        </div>

                                                        <div className="">
                                                          <label className="option-fbox">
                                                            <input
                                                              style={{
                                                                marginTop:
                                                                  "6px",
                                                              }}
                                                              type="radio"
                                                              id={idx}
                                                              name={
                                                                "dose_name_elr_unknown" +
                                                                idx
                                                              }
                                                              checked={
                                                                data.dose_name_elr_unknown
                                                              }
                                                              onChange={
                                                                HandleFieldChange
                                                              }
                                                            />
                                                            <span className="radio-text-padding">
                                                              Unknown
                                                            </span>
                                                          </label>
                                                        </div>

                                                        <ErrorField
                                                          show={
                                                            data.name_dose_steroidsHardEmpty
                                                          }
                                                          message={
                                                            ELCharacteristics_MSGS.name_dose_steroidsHardEmpty
                                                          }
                                                          style={{
                                                            paddingLeft: "0px",
                                                          }}
                                                        />

                                                        <ErrorField
                                                          show={
                                                            data.steroids_elr_txtEmpty
                                                          }
                                                          message={
                                                            ELCharacteristics_MSGS.steroids_elr_txtEmpty
                                                          }
                                                          style={{
                                                            paddingLeft: "0px",
                                                          }}
                                                        />
                                                      </div>
                                                    </div>
                                                  </td>

                                                  {/* End */}

                                                  <td
                                                    style={{
                                                      border: "1px solid black",
                                                      padding: "8px",
                                                    }}
                                                  >
                                                    <div>
                                                      <div className="">
                                                        <label className="checkbox-option">
                                                          <input
                                                            type="checkbox"
                                                            id={idx}
                                                            name={
                                                              "steroid_pre_tmt_elr" +
                                                              idx
                                                            }
                                                            checked={
                                                              data.steroid_pre_tmt_elr
                                                            }
                                                            onChange={
                                                              HandleFieldChange
                                                            }
                                                          />
                                                          <span className="radio-text-padding">
                                                            As a pre-treatment
                                                            per label
                                                            recommendation
                                                          </span>
                                                        </label>
                                                      </div>

                                                      <div className="">
                                                        <label className="checkbox-option">
                                                          <input
                                                            type="checkbox"
                                                            id={idx}
                                                            name={
                                                              "cytokin_elr_steroids" +
                                                              idx
                                                            }
                                                            checked={
                                                              data.cytokin_elr_steroids
                                                            }
                                                            onChange={
                                                              HandleFieldChange
                                                            }
                                                          />
                                                          <span
                                                            style={{
                                                              paddingLeft:
                                                                "15px",
                                                            }}
                                                          >
                                                            Primary prophylactic
                                                            for cytokine release
                                                            syndrome (CRS)
                                                          </span>
                                                        </label>
                                                      </div>

                                                      <div className="">
                                                        <label className="checkbox-option">
                                                          <input
                                                            type="checkbox"
                                                            id={idx}
                                                            name={
                                                              "secondary_prophylactic_elr_steroids" +
                                                              idx
                                                            }
                                                            checked={
                                                              data.secondary_prophylactic_elr_steroids
                                                            }
                                                            onChange={
                                                              HandleFieldChange
                                                            }
                                                          />
                                                          <span
                                                            style={{
                                                              paddingLeft:
                                                                "15px",
                                                            }}
                                                          >
                                                            Secondary
                                                            prophylactic for CRS
                                                          </span>
                                                        </label>
                                                      </div>

                                                      <div className="">
                                                        <label className="checkbox-option">
                                                          <input
                                                            type="checkbox"
                                                            id={idx}
                                                            name={
                                                              "crs_elr_steroids" +
                                                              idx
                                                            }
                                                            checked={
                                                              data.crs_elr_steroids
                                                            }
                                                            onChange={
                                                              HandleFieldChange
                                                            }
                                                          />
                                                          <span className="radio-text-padding">
                                                            Treatment or
                                                            supportive care for
                                                            CRS
                                                          </span>
                                                        </label>
                                                      </div>
                                                      <div className="">
                                                        <label className="checkbox-option">
                                                          <input
                                                            type="checkbox"
                                                            id={idx}
                                                            name={
                                                              "neurotoxicity_elr_steroids" +
                                                              idx
                                                            }
                                                            checked={
                                                              data.neurotoxicity_elr_steroids
                                                            }
                                                            onChange={
                                                              HandleFieldChange
                                                            }
                                                          />
                                                          <span
                                                            style={{
                                                              paddingLeft:
                                                                "15px",
                                                            }}
                                                          >
                                                            Primary prophylactic
                                                            for neurologic
                                                            events
                                                          </span>
                                                        </label>
                                                      </div>

                                                      <div className="">
                                                        <label className="checkbox-option">
                                                          <input
                                                            type="checkbox"
                                                            id={idx}
                                                            name={
                                                              "secondary_neurotoxicity_elr_steroids" +
                                                              idx
                                                            }
                                                            checked={
                                                              data.secondary_neurotoxicity_elr_steroids
                                                            }
                                                            onChange={
                                                              HandleFieldChange
                                                            }
                                                          />
                                                          <span
                                                            style={{
                                                              paddingLeft:
                                                                "15px",
                                                            }}
                                                          >
                                                            Secondary
                                                            prophylactic for
                                                            neurologic events
                                                          </span>
                                                        </label>
                                                      </div>

                                                      <div className="">
                                                        <label className="checkbox-option">
                                                          <input
                                                            type="checkbox"
                                                            id={idx}
                                                            name={
                                                              "tmt_neurotoxicity_elr_steroids" +
                                                              idx
                                                            }
                                                            checked={
                                                              data.tmt_neurotoxicity_elr_steroids
                                                            }
                                                            onChange={
                                                              HandleFieldChange
                                                            }
                                                          />
                                                          <span
                                                            style={{
                                                              paddingLeft:
                                                                "15px",
                                                            }}
                                                          >
                                                            Treatment or
                                                            supportive care for
                                                            neurologic events
                                                          </span>
                                                        </label>
                                                      </div>

                                                      <div className="">
                                                        <label>
                                                          <input
                                                            type="radio"
                                                            id={idx}
                                                            name={
                                                              "tmt_steroids_elr_unknown" +
                                                              idx
                                                            }
                                                            checked={
                                                              data.tmt_steroids_elr_unknown
                                                            }
                                                            onChange={
                                                              HandleFieldChange
                                                            }
                                                          />
                                                          <span className="radio-text-padding">
                                                            Unknown
                                                          </span>
                                                        </label>
                                                      </div>

                                                      <ErrorField
                                                        show={
                                                          data.Q39elrSteroidsEmptyHard
                                                        }
                                                        message={
                                                          ELCharacteristics_MSGS.Q39elrSteroidsEmptyHard
                                                        }
                                                        style={{
                                                          paddingLeft: "0px",
                                                        }}
                                                      />
                                                    </div>
                                                  </td>
                                                </tr>
                                              </tbody>
                                            </table>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  )}
                                  {/* 39.3 */}

                                  {data.Granulocyte && (
                                    <div
                                      style={{
                                        marginLeft: "28px",
                                        marginBlock: "6px",
                                        padding: "5px",
                                      }}
                                    >
                                      <div className="question question-weight">
                                        <span>Q39.3.</span>
                                        <span className="quest-text-pad">
                                          Why was G-CSF used while receiving
                                          treatment with{" "}
                                          <span
                                            style={{
                                              color:
                                                PageDesignConstants.fontColor,
                                              fontWeight: "bolder",
                                            }}
                                          >
                                            ELREXFIO<sup>TM</sup>{" "}
                                            (elranatamab-bcmm)
                                          </span>
                                          ? <i>Select all that apply.</i>
                                        </span>
                                      </div>

                                      <div className="dependent-sub-section">
                                        {/* ELREXFIO® */}
                                        <div className="question-bot-sapce">
                                          <div className="sub-question grid-overflow">
                                            <table
                                              style={{
                                                border: "1px solid black",
                                                borderCollapse: "collapse",
                                                width: "100%",
                                              }}
                                            >
                                              <thead>
                                                <tr>
                                                  <th
                                                    style={{
                                                      border: "1px solid black",
                                                      padding: "8px",
                                                      textAlign: "center",
                                                      width: "25%",
                                                    }}
                                                  >
                                                    Treatment
                                                  </th>
                                                  <th
                                                    style={{
                                                      border: "1px solid black",
                                                      padding: "8px",
                                                      textAlign: "center",
                                                      width: "25%",
                                                    }}
                                                  >
                                                    Medication
                                                  </th>

                                                  <th
                                                    style={{
                                                      border: "1px solid black",
                                                      padding: "8px",
                                                      textAlign: "center",
                                                      width: "45%",
                                                    }}
                                                  >
                                                    Reason for receiving
                                                    medication
                                                  </th>
                                                </tr>
                                              </thead>

                                              <tbody>
                                                <tr>
                                                  <td
                                                    style={{
                                                      border: "1px solid black",
                                                      padding: "8px",
                                                    }}
                                                  >
                                                    <span className="quest-text-pad">
                                                      ELREXFIO<sup>TM</sup>{" "}
                                                      (elranatamab-bcmm)
                                                    </span>
                                                  </td>

                                                  <td
                                                    style={{
                                                      border: "1px solid black",
                                                      padding: "8px",
                                                    }}
                                                  >
                                                    <div>
                                                      <div>
                                                        <div
                                                          className="option-without-input"
                                                          style={{
                                                            display: "flex",
                                                            alignItems:
                                                              "flex-start",
                                                          }}
                                                        >
                                                          <label>
                                                            <span
                                                              className="radio-text-padding"
                                                              style={{
                                                                paddingLeft:
                                                                  "10px",
                                                              }}
                                                            >
                                                              Granulocyte
                                                              colony-stimulating
                                                              factor (G-CSF)
                                                            </span>
                                                          </label>
                                                        </div>
                                                      </div>
                                                    </div>
                                                  </td>

                                                  <td
                                                    style={{
                                                      border: "1px solid black",
                                                      padding: "8px",
                                                    }}
                                                  >
                                                    <div>
                                                      <div>
                                                        <div className="">
                                                          <label>
                                                            <input
                                                              type="checkbox"
                                                              id={idx}
                                                              name={
                                                                "prophylactic_use_elr" +
                                                                idx
                                                              }
                                                              checked={
                                                                data.prophylactic_use_elr
                                                              }
                                                              onChange={
                                                                HandleFieldChange
                                                              }
                                                            />
                                                            <span className="radio-text-padding">
                                                              Primary
                                                              Prophylactic use
                                                            </span>
                                                          </label>
                                                        </div>
                                                        <div className="">
                                                          <label>
                                                            <input
                                                              type="checkbox"
                                                              id={idx}
                                                              name={
                                                                "secondary_prophylactic_use_elr" +
                                                                idx
                                                              }
                                                              checked={
                                                                data.secondary_prophylactic_use_elr
                                                              }
                                                              onChange={
                                                                HandleFieldChange
                                                              }
                                                            />
                                                            <span className="radio-text-padding">
                                                              Secondary
                                                              Prophylactic use
                                                            </span>
                                                          </label>
                                                        </div>

                                                        <div className="">
                                                          <label>
                                                            <input
                                                              type="checkbox"
                                                              id={idx}
                                                              name={
                                                                "oth_use_elr" +
                                                                idx
                                                              }
                                                              checked={
                                                                data.oth_use_elr
                                                              }
                                                              onChange={
                                                                HandleFieldChange
                                                              }
                                                            />
                                                            <span className="radio-text-padding">
                                                              Other use
                                                            </span>
                                                          </label>
                                                        </div>
                                                        <div className="">
                                                          <label>
                                                            <input
                                                              type="radio"
                                                              id={idx}
                                                              value={99}
                                                              name={
                                                                "unknown_elr" +
                                                                idx
                                                              }
                                                              checked={
                                                                data.unknown_elr ===
                                                                99
                                                              }
                                                              onChange={
                                                                HandleFieldChange
                                                              }
                                                            />
                                                            <span className="radio-text-padding">
                                                              Unknown
                                                            </span>
                                                          </label>
                                                        </div>

                                                        <ErrorField
                                                          show={
                                                            data.Q39elrGranduloEmptyHard
                                                          }
                                                          message={
                                                            ELCharacteristics_MSGS.Q39elrGranduloEmptyHard
                                                          }
                                                          style={{
                                                            paddingLeft: "0px",
                                                          }}
                                                        />
                                                      </div>
                                                    </div>
                                                  </td>
                                                </tr>
                                              </tbody>
                                            </table>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  )}
                                </React.Fragment>
                              ))}
                          </div>
                        )}
                        {/* Question 39.C start (eCRF New Elements C) */}
                        {isNewEcrfC && (
                          <div className="">
                            {isElrexvayliSelected &&
                              Q37ElrexDetails &&
                              Q37ElrexDetails.length > 0 &&
                              Q37ElrexDetails.map((data, idx) => (
                                <React.Fragment key={idx}>
                                  <div className="sub-question-bot-sapce ">
                                    <div className="question question-weight">
                                      <span>Q39.C.</span>
                                      <span className="quest-text-pad">
                                        Which medications were used, if any,
                                        <b>
                                          {" "}
                                          within {""}
                                          <u>
                                            7 days before to 14 days after
                                          </u>{" "}
                                          initiating treatment{" "}
                                        </b>{" "}
                                        with{" "}
                                        <span
                                          style={{
                                            color:
                                              PageDesignConstants.fontColor,
                                            fontWeight: "bolder",
                                          }}
                                        >
                                          ELREXFIO<sup>TM</sup>{" "}
                                          (elranatamab-bcmm)
                                        </span>{" "}
                                        (i.e.,{" "}
                                        <span
                                          style={{
                                            color:
                                              PageDesignConstants.fontColor,
                                          }}
                                        >
                                          [
                                          {formatDateMinus7Days(
                                            elrexInitiationDate,
                                          )}
                                          ]
                                        </span>{" "}
                                        through{" "}
                                        <span
                                          style={{
                                            color:
                                              PageDesignConstants.fontColor,
                                          }}
                                        >
                                          [
                                          {formatDatePlus14Days(
                                            elrexInitiationDate,
                                          )}
                                          ]
                                        </span>
                                        )? <i>Select all that apply.</i>
                                      </span>
                                    </div>
                                    <div className="radio-text-padding">
                                      <div className="double-dig-answer">
                                        <div className="">
                                          <label>
                                            <input
                                              type="checkbox"
                                              id={idx}
                                              name={"tocilizumab_elrC" + idx}
                                              checked={data.tocilizumab_elrC}
                                              onChange={HandleFieldChange}
                                            />
                                            <span className="radio-text-padding">
                                              Tocilizumab
                                            </span>
                                          </label>
                                        </div>
                                        <div className="">
                                          <label>
                                            <input
                                              type="checkbox"
                                              id={idx}
                                              name={"steroids_elrC" + idx}
                                              checked={data.steroids_elrC}
                                              onChange={HandleFieldChange}
                                            />
                                            <span className="radio-text-padding">
                                              Steroids
                                            </span>
                                          </label>
                                        </div>
                                        <div className="">
                                          <label>
                                            <input
                                              type="checkbox"
                                              id={idx}
                                              name={"granulocyte_elrC" + idx}
                                              checked={data.granulocyte_elrC}
                                              onChange={HandleFieldChange}
                                            />
                                            <span className="radio-text-padding">
                                              Granulocyte colony-stimulating
                                              factor (G-CSF)
                                            </span>
                                          </label>
                                        </div>
                                        <div className="option-without-input">
                                          <label className="option-fbox">
                                            <input
                                              type="radio"
                                              id={idx}
                                              name={"noneoftheabove_elrC" + idx}
                                              value={99}
                                              checked={
                                                data.noneoftheabove_elrC === 99
                                              }
                                              onChange={HandleFieldChange}
                                            />
                                            <span className="radio-text-padding">
                                              None of the above{" "}
                                            </span>
                                          </label>
                                        </div>
                                        <ErrorField
                                          show={data.Q39CEmptyHard}
                                          message={
                                            ELCharacteristics_MSGS.Q39CEmptyHard
                                          }
                                          style={{
                                            paddingLeft: "0px",
                                          }}
                                        />
                                      </div>
                                    </div>
                                  </div>

                                  {data.tocilizumab_elrC && (
                                    <div
                                      style={{
                                        marginLeft: "28px",
                                        marginBlock: "6px",
                                        padding: "5px",
                                      }}
                                    >
                                      <div className="question question-weight">
                                        <span>Q39.1.C.</span>
                                        <span className="quest-text-pad">
                                          Summarize the reasons why tocilizumab
                                          was used and dosing information for
                                          tocilizumab{" "}
                                          <b>
                                            <u>
                                              within 7 days before to 14 days
                                              after
                                            </u>{" "}
                                            initiating treatment
                                          </b>{" "}
                                          with{" "}
                                          <span
                                            style={{
                                              color:
                                                PageDesignConstants.fontColor,
                                              fontWeight: "bolder",
                                            }}
                                          >
                                            ELREXFIO<sup>TM</sup>
                                            {"  "}
                                            (elranatamab-bcmm)
                                          </span>{" "}
                                          (i.e.,{" "}
                                          <span
                                            style={{
                                              color:
                                                PageDesignConstants.fontColor,
                                            }}
                                          >
                                            [
                                            {formatDateMinus7Days(
                                              elrexInitiationDate,
                                            )}
                                            ]
                                          </span>{" "}
                                          through{" "}
                                          <span
                                            style={{
                                              color:
                                                PageDesignConstants.fontColor,
                                            }}
                                          >
                                            [
                                            {formatDatePlus14Days(
                                              elrexInitiationDate,
                                            )}
                                            ]
                                          </span>
                                          ). <i>Select all that apply.</i>
                                        </span>
                                      </div>
                                      <div
                                        className="dependent-section grid-overflow"
                                        style={{ backgroundColor: "#ffffff" }}
                                      >
                                        <table
                                          style={{
                                            border: "1px solid black",
                                            borderCollapse: "collapse",
                                            width: "100%",
                                            backgroundColor: "#ffffff",
                                            cursor: "default",
                                          }}
                                        >
                                          <thead>
                                            <tr>
                                              <th
                                                style={{
                                                  border: "1px solid black",
                                                  padding: "6px",
                                                  textAlign: "center",
                                                  backgroundColor: "#ffffff",
                                                }}
                                              >
                                                Treatment
                                              </th>
                                              <th
                                                style={{
                                                  border: "1px solid black",
                                                  padding: "6px",
                                                  textAlign: "center",
                                                  backgroundColor: "#ffffff",
                                                }}
                                              >
                                                Medication
                                              </th>
                                              <th
                                                style={{
                                                  border: "1px solid black",
                                                  padding: "6px",
                                                  textAlign: "center",
                                                  backgroundColor: "#ffffff",
                                                }}
                                              >
                                                Date of tocilizumab
                                                administration
                                              </th>
                                              <th
                                                style={{
                                                  border: "1px solid black",
                                                  padding: "6px",
                                                  textAlign: "center",
                                                  backgroundColor: "#ffffff",
                                                }}
                                              >
                                                Dose Strength
                                              </th>
                                              <th
                                                style={{
                                                  border: "1px solid black",
                                                  padding: "6px",
                                                  textAlign: "center",
                                                  backgroundColor: "#ffffff",
                                                }}
                                              >
                                                Reason for receiving medication
                                              </th>
                                            </tr>
                                          </thead>
                                          <tbody>
                                            <tr>
                                              <td
                                                style={{
                                                  border: "1px solid black",
                                                  padding: "8px",
                                                  verticalAlign: "top",
                                                }}
                                              >
                                                ELREXFIO<sup>TM</sup>{" "}
                                                (elranatamab-bcmm)
                                              </td>
                                              <td
                                                style={{
                                                  border: "1px solid black",
                                                  padding: "8px",
                                                  verticalAlign: "top",
                                                }}
                                              >
                                                Tocilizumab
                                              </td>
                                              <td
                                                style={{
                                                  border: "1px solid black",
                                                  padding: "8px",
                                                  verticalAlign: "top",
                                                }}
                                              >
                                                <div className="sub-question">
                                                  <div
                                                    className="option-without-input"
                                                    style={{ display: "flex" }}
                                                  >
                                                    <input
                                                      type="radio"
                                                      id={idx}
                                                      name={
                                                        "tociliAdmsDateRD_elrC" +
                                                        idx
                                                      }
                                                      value={1}
                                                      checked={
                                                        data.tociliAdmsDateRD_elrC ===
                                                        1
                                                      }
                                                      onChange={
                                                        HandleFieldChange
                                                      }
                                                    />
                                                    <span
                                                      className="radio-text-padding"
                                                      style={{
                                                        paddingLeft: "10px",
                                                        marginLeft: "-5px",
                                                      }}
                                                    >
                                                      <DatePickerComponent
                                                        ctrlId={
                                                          "tociliAdmsDate_elrC-" +
                                                          idx
                                                        }
                                                        HandleDateChange={
                                                          HandleRepeaterDateChange
                                                        }
                                                        optionId={1}
                                                        minDate={dayjs(
                                                          "01-01-1920",
                                                        )}
                                                        maxDate={dayjs()}
                                                        locale="en"
                                                        date={
                                                          data.tociliAdmsDate_elrC
                                                        }
                                                      />
                                                    </span>
                                                  </div>
                                                  <div
                                                    className="option-without-input"
                                                    style={{ display: "flex" }}
                                                  >
                                                    <label className="option-fbox">
                                                      <input
                                                        type="radio"
                                                        id={idx}
                                                        name={
                                                          "tociliAdmsDateRD_elrC" +
                                                          idx
                                                        }
                                                        value={99}
                                                        checked={
                                                          data.tociliAdmsDateRD_elrC ===
                                                          99
                                                        }
                                                        onChange={
                                                          HandleFieldChange
                                                        }
                                                      />
                                                      <span
                                                        style={{
                                                          paddingLeft: "10px",
                                                          marginLeft: "-5px",
                                                        }}
                                                      >
                                                        Date of administration
                                                        unknown
                                                      </span>
                                                    </label>
                                                  </div>
                                                  <ErrorField
                                                    show={
                                                      data.tociliAdmsDateEmpty_elrC
                                                    }
                                                    message={
                                                      ELCharacteristics_MSGS.Q391CDateEmpty
                                                    }
                                                    style={{
                                                      paddingLeft: "0px",
                                                    }}
                                                  />
                                                  <ErrorField
                                                    show={
                                                      data.tociliAdmsDateInvalid_elrC
                                                    }
                                                    message={
                                                      CommonError_Msgs.InvalidDate
                                                    }
                                                    style={{
                                                      paddingLeft: "0px",
                                                    }}
                                                  />
                                                  <ErrorField
                                                    show={
                                                      data.tociliAdmsDateRangeInvalid_elrC
                                                    }
                                                    message={
                                                      elrexInitiationDate
                                                        ? `Please enter a date between ${dayjs(
                                                            elrexInitiationDate,
                                                          )
                                                            .subtract(7, "day")
                                                            .format(
                                                              "MM/DD/YYYY",
                                                            )} and ${dayjs(
                                                            elrexInitiationDate,
                                                          )
                                                            .add(14, "day")
                                                            .format(
                                                              "MM/DD/YYYY",
                                                            )}`
                                                        : ELCharacteristics_MSGS.Q391CDateInvalid
                                                    }
                                                    style={{
                                                      paddingLeft: "0px",
                                                    }}
                                                  />
                                                </div>
                                              </td>
                                              <td
                                                style={{
                                                  border: "1px solid black",
                                                  padding: "8px",
                                                  verticalAlign: "top",
                                                }}
                                              >
                                                <div className="sub-question">
                                                  <div
                                                    className="option-without-input"
                                                    style={{ display: "flex" }}
                                                  >
                                                    <input
                                                      type="radio"
                                                      id={idx}
                                                      name={
                                                        "tociliDoseRd_elrC" +
                                                        idx
                                                      }
                                                      value={1}
                                                      checked={
                                                        data.tociliDoseRd_elrC ===
                                                        1
                                                      }
                                                      onChange={
                                                        HandleFieldChange
                                                      }
                                                    />
                                                    <span
                                                      className="radio-text-padding"
                                                      style={{
                                                        paddingLeft: "10px",
                                                        marginLeft: "-5px",
                                                      }}
                                                    >
                                                      <input
                                                        type="text"
                                                        className="answer-textbox"
                                                        style={{
                                                          width: "80px",
                                                          border: "none",
                                                          borderBottom:
                                                            "1px solid #000",
                                                          borderRadius: 0,
                                                          outline: "none",
                                                          backgroundColor:
                                                            "transparent",
                                                        }}
                                                        id={idx}
                                                        name={
                                                          "tociliDoseMgkg_elrC" +
                                                          idx
                                                        }
                                                        value={
                                                          data.tociliDoseMgkg_elrC
                                                        }
                                                        onChange={
                                                          HandleFieldChange
                                                        }
                                                        onKeyDown={
                                                          AllowNumericWithDecimal
                                                        }
                                                      />{" "}
                                                      mg/kg
                                                    </span>
                                                  </div>
                                                  <div>
                                                    <div
                                                      style={{
                                                        display: "flex",
                                                        alignItems:
                                                          "flex-start",
                                                      }}
                                                    >
                                                      <input
                                                        type="checkbox"
                                                        style={{
                                                          marginTop: "6px",
                                                        }}
                                                        id={idx}
                                                        name={
                                                          "tociliDoseOtherToggle_elrC" +
                                                          idx
                                                        }
                                                        checked={
                                                          data.tociliDoseRd_elrC ===
                                                          2
                                                        }
                                                        onChange={
                                                          HandleFieldChange
                                                        }
                                                      />
                                                      <span className="radio-text-padding">
                                                        Other, please specify:
                                                      </span>
                                                    </div>
                                                    <textarea
                                                      className="input-dash"
                                                      id={idx}
                                                      name={
                                                        "tociliDoseOther_elrC" +
                                                        idx
                                                      }
                                                      value={
                                                        data.tociliDoseOther_elrC
                                                      }
                                                      onChange={
                                                        HandleFieldChange
                                                      }
                                                      placeholder="Please specify"
                                                    />
                                                  </div>
                                                  <div
                                                    className="option-without-input"
                                                    style={{ display: "flex" }}
                                                  >
                                                    <label className="option-fbox">
                                                      <input
                                                        type="radio"
                                                        id={idx}
                                                        name={
                                                          "tociliDoseRd_elrC" +
                                                          idx
                                                        }
                                                        value={99}
                                                        checked={
                                                          data.tociliDoseRd_elrC ===
                                                          99
                                                        }
                                                        onChange={
                                                          HandleFieldChange
                                                        }
                                                      />
                                                      <span
                                                        style={{
                                                          paddingLeft: "10px",
                                                          marginLeft: "-5px",
                                                        }}
                                                      >
                                                        Unknown
                                                      </span>
                                                    </label>
                                                  </div>
                                                  <ErrorField
                                                    show={
                                                      data.tociliDoseEmptyHard_elrC
                                                    }
                                                    message={
                                                      ELCharacteristics_MSGS.Q391CDoseEmptyHard
                                                    }
                                                    style={{
                                                      paddingLeft: "0px",
                                                    }}
                                                  />
                                                  <ErrorField
                                                    show={
                                                      data.tociliDoseMgkgEmptyHard_elrC
                                                    }
                                                    message={
                                                      ELCharacteristics_MSGS.Q391CDoseRangeInvalid
                                                    }
                                                    style={{
                                                      paddingLeft: "0px",
                                                    }}
                                                  />
                                                  <ErrorField
                                                    show={
                                                      data.tociliDoseOtherEmptyHard_elrC
                                                    }
                                                    message={
                                                      ELCharacteristics_MSGS.Q391CDoseOtherEmptyHard
                                                    }
                                                    style={{
                                                      paddingLeft: "0px",
                                                    }}
                                                  />
                                                </div>
                                              </td>
                                              <td
                                                style={{
                                                  border: "1px solid black",
                                                  padding: "8px",
                                                  verticalAlign: "top",
                                                }}
                                              >
                                                <div className="sub-question">
                                                  <div
                                                    style={{
                                                      display: "flex",
                                                      alignItems: "flex-start",
                                                    }}
                                                    onClick={(e) => {
                                                      if (
                                                        e.target.tagName ===
                                                        "INPUT"
                                                      )
                                                        return;
                                                      HandleFieldChange({
                                                        target: {
                                                          name:
                                                            "tociliReasonPretratment_elrC" +
                                                            idx,
                                                          id: `${idx}`,
                                                        },
                                                      });
                                                    }}
                                                  >
                                                    <input
                                                      type="checkbox"
                                                      style={{
                                                        marginTop: "6px",
                                                      }}
                                                      id={idx}
                                                      name={
                                                        "tociliReasonPretratment_elrC" +
                                                        idx
                                                      }
                                                      checked={
                                                        data.tociliReasonPretratment_elrC
                                                      }
                                                      onChange={
                                                        HandleFieldChange
                                                      }
                                                    />
                                                    <span className="radio-text-padding">
                                                      As a pre-treatment per
                                                      label recommendation
                                                    </span>
                                                  </div>
                                                  <div
                                                    style={{
                                                      display: "flex",
                                                      alignItems: "flex-start",
                                                    }}
                                                    onClick={(e) => {
                                                      if (
                                                        e.target.tagName ===
                                                        "INPUT"
                                                      )
                                                        return;
                                                      HandleFieldChange({
                                                        target: {
                                                          name:
                                                            "tociliReasonPrimarycrs_elrC" +
                                                            idx,
                                                          id: `${idx}`,
                                                        },
                                                      });
                                                    }}
                                                  >
                                                    <input
                                                      type="checkbox"
                                                      style={{
                                                        marginTop: "6px",
                                                      }}
                                                      id={idx}
                                                      name={
                                                        "tociliReasonPrimarycrs_elrC" +
                                                        idx
                                                      }
                                                      checked={
                                                        data.tociliReasonPrimarycrs_elrC
                                                      }
                                                      onChange={
                                                        HandleFieldChange
                                                      }
                                                    />
                                                    <span className="radio-text-padding">
                                                      Primary prophylactic for
                                                      cytokine release syndrome
                                                      (CRS)
                                                    </span>
                                                  </div>
                                                  <div
                                                    style={{
                                                      display: "flex",
                                                      alignItems: "flex-start",
                                                    }}
                                                    onClick={(e) => {
                                                      if (
                                                        e.target.tagName ===
                                                        "INPUT"
                                                      )
                                                        return;
                                                      HandleFieldChange({
                                                        target: {
                                                          name:
                                                            "tociliReasonSecondarycrs_elrC" +
                                                            idx,
                                                          id: `${idx}`,
                                                        },
                                                      });
                                                    }}
                                                  >
                                                    <input
                                                      type="checkbox"
                                                      style={{
                                                        marginTop: "6px",
                                                      }}
                                                      id={idx}
                                                      name={
                                                        "tociliReasonSecondarycrs_elrC" +
                                                        idx
                                                      }
                                                      checked={
                                                        data.tociliReasonSecondarycrs_elrC
                                                      }
                                                      onChange={
                                                        HandleFieldChange
                                                      }
                                                    />
                                                    <span className="radio-text-padding">
                                                      Secondary prophylactic for
                                                      CRS
                                                    </span>
                                                  </div>
                                                  <div
                                                    style={{
                                                      display: "flex",
                                                      alignItems: "flex-start",
                                                    }}
                                                    onClick={(e) => {
                                                      if (
                                                        e.target.tagName ===
                                                        "INPUT"
                                                      )
                                                        return;
                                                      HandleFieldChange({
                                                        target: {
                                                          name:
                                                            "tociliReasonSupportivecrs_elrC" +
                                                            idx,
                                                          id: `${idx}`,
                                                        },
                                                      });
                                                    }}
                                                  >
                                                    <input
                                                      type="checkbox"
                                                      style={{
                                                        marginTop: "6px",
                                                      }}
                                                      id={idx}
                                                      name={
                                                        "tociliReasonSupportivecrs_elrC" +
                                                        idx
                                                      }
                                                      checked={
                                                        data.tociliReasonSupportivecrs_elrC
                                                      }
                                                      onChange={
                                                        HandleFieldChange
                                                      }
                                                    />
                                                    <span className="radio-text-padding">
                                                      Treatment or supportive
                                                      care for CRS
                                                    </span>
                                                  </div>
                                                  <div
                                                    style={{
                                                      display: "flex",
                                                      alignItems: "flex-start",
                                                    }}
                                                    onClick={(e) => {
                                                      if (
                                                        e.target.tagName ===
                                                        "INPUT"
                                                      )
                                                        return;
                                                      HandleFieldChange({
                                                        target: {
                                                          name:
                                                            "tociliReasonPrimaryneuro_elrC" +
                                                            idx,
                                                          id: `${idx}`,
                                                        },
                                                      });
                                                    }}
                                                  >
                                                    <input
                                                      type="checkbox"
                                                      style={{
                                                        marginTop: "6px",
                                                      }}
                                                      id={idx}
                                                      name={
                                                        "tociliReasonPrimaryneuro_elrC" +
                                                        idx
                                                      }
                                                      checked={
                                                        data.tociliReasonPrimaryneuro_elrC
                                                      }
                                                      onChange={
                                                        HandleFieldChange
                                                      }
                                                    />
                                                    <span className="radio-text-padding">
                                                      Primary prophylactic for
                                                      neurologic events
                                                    </span>
                                                  </div>
                                                  <div
                                                    style={{
                                                      display: "flex",
                                                      alignItems: "flex-start",
                                                    }}
                                                    onClick={(e) => {
                                                      if (
                                                        e.target.tagName ===
                                                        "INPUT"
                                                      )
                                                        return;
                                                      HandleFieldChange({
                                                        target: {
                                                          name:
                                                            "tociliReasonSecondaryneuro_elrC" +
                                                            idx,
                                                          id: `${idx}`,
                                                        },
                                                      });
                                                    }}
                                                  >
                                                    <input
                                                      type="checkbox"
                                                      style={{
                                                        marginTop: "6px",
                                                      }}
                                                      id={idx}
                                                      name={
                                                        "tociliReasonSecondaryneuro_elrC" +
                                                        idx
                                                      }
                                                      checked={
                                                        data.tociliReasonSecondaryneuro_elrC
                                                      }
                                                      onChange={
                                                        HandleFieldChange
                                                      }
                                                    />
                                                    <span className="radio-text-padding">
                                                      Secondary prophylactic for
                                                      neurologic events
                                                    </span>
                                                  </div>
                                                  <div
                                                    style={{
                                                      display: "flex",
                                                      alignItems: "flex-start",
                                                    }}
                                                    onClick={(e) => {
                                                      if (
                                                        e.target.tagName ===
                                                        "INPUT"
                                                      )
                                                        return;
                                                      HandleFieldChange({
                                                        target: {
                                                          name:
                                                            "tociliReasonSupportiveneuro_elrC" +
                                                            idx,
                                                          id: `${idx}`,
                                                        },
                                                      });
                                                    }}
                                                  >
                                                    <input
                                                      type="checkbox"
                                                      style={{
                                                        marginTop: "6px",
                                                      }}
                                                      id={idx}
                                                      name={
                                                        "tociliReasonSupportiveneuro_elrC" +
                                                        idx
                                                      }
                                                      checked={
                                                        data.tociliReasonSupportiveneuro_elrC
                                                      }
                                                      onChange={
                                                        HandleFieldChange
                                                      }
                                                    />
                                                    <span className="radio-text-padding">
                                                      Treatment or supportive
                                                      care for neurologic events
                                                    </span>
                                                  </div>
                                                  <div className="">
                                                    <label className="checkbox-option">
                                                      <input
                                                        type="radio"
                                                        id={idx}
                                                        name={
                                                          "tociliReasonUnk_elrC" +
                                                          idx
                                                        }
                                                        checked={
                                                          data.tociliReasonUnk_elrC
                                                        }
                                                        onChange={
                                                          HandleFieldChange
                                                        }
                                                      />
                                                      <span className="radio-text-padding">
                                                        Unknown
                                                      </span>
                                                    </label>
                                                  </div>
                                                  <ErrorField
                                                    show={
                                                      data.Q391CReasonEmptyHard
                                                    }
                                                    message={
                                                      ELCharacteristics_MSGS.Q391CReasonEmptyHard
                                                    }
                                                    style={{
                                                      paddingLeft: "0px",
                                                    }}
                                                  />
                                                </div>
                                              </td>
                                            </tr>
                                          </tbody>
                                        </table>
                                      </div>
                                    </div>
                                  )}

                                  {data.steroids_elrC && (
                                    <div
                                      style={{
                                        marginLeft: "28px",
                                        marginBlock: "6px",
                                        padding: "5px",
                                      }}
                                    >
                                      <div className="question question-weight">
                                        <span>Q39.2.C.</span>
                                        <span className="quest-text-pad">
                                          Summarize the reasons why steroids
                                          were used and dosing information for
                                          steroids used{" "}
                                          <b>
                                            within{" "}
                                            <u>
                                              7 days before to 14 days after
                                            </u>{" "}
                                            initiating treatment
                                          </b>{" "}
                                          with{" "}
                                          <span
                                            style={{
                                              color:
                                                PageDesignConstants.fontColor,
                                              fontWeight: "bolder",
                                            }}
                                          >
                                            ELREXFIO<sup>TM</sup>{" "}
                                            (elranatamab-bcmm)
                                          </span>{" "}
                                          (i.e.,{" "}
                                          <span
                                            style={{
                                              color:
                                                PageDesignConstants.fontColor,
                                            }}
                                          >
                                            [
                                            {formatDateMinus7Days(
                                              elrexInitiationDate,
                                            )}
                                            ]
                                          </span>{" "}
                                          through{" "}
                                          <span
                                            style={{
                                              color:
                                                PageDesignConstants.fontColor,
                                            }}
                                          >
                                            [
                                            {formatDatePlus14Days(
                                              elrexInitiationDate,
                                            )}
                                            ]
                                          </span>
                                          ). <i>Select all that apply.</i>
                                        </span>
                                      </div>
                                      <div
                                        className="dependent-section grid-overflow"
                                        style={{ backgroundColor: "#ffffff" }}
                                      >
                                        <table
                                          style={{
                                            border: "1px solid black",
                                            borderCollapse: "collapse",
                                            width: "100%",
                                            backgroundColor: "#ffffff",
                                          }}
                                        >
                                          <thead>
                                            <tr>
                                              <th
                                                style={{
                                                  border: "1px solid black",
                                                  padding: "6px",
                                                  textAlign: "center",
                                                  backgroundColor: "#ffffff",
                                                }}
                                              >
                                                Treatment
                                              </th>
                                              <th
                                                style={{
                                                  border: "1px solid black",
                                                  padding: "6px",
                                                  textAlign: "center",
                                                  backgroundColor: "#ffffff",
                                                }}
                                              >
                                                Medication type
                                              </th>
                                              <th
                                                style={{
                                                  border: "1px solid black",
                                                  padding: "6px",
                                                  textAlign: "center",
                                                  backgroundColor: "#ffffff",
                                                }}
                                              >
                                                Name and dose per day of the
                                                steroid(s) used
                                              </th>
                                              <th
                                                style={{
                                                  border: "1px solid black",
                                                  padding: "6px",
                                                  textAlign: "center",
                                                  backgroundColor: "#ffffff",
                                                }}
                                              >
                                                Reason for receiving medication
                                              </th>
                                            </tr>
                                          </thead>
                                          <tbody>
                                            <tr>
                                              <td
                                                style={{
                                                  border: "1px solid black",
                                                  padding: "8px",
                                                  verticalAlign: "top",
                                                }}
                                              >
                                                ELREXFIO<sup>TM</sup>{" "}
                                                (elranatamab-bcmm)
                                              </td>
                                              <td
                                                style={{
                                                  border: "1px solid black",
                                                  padding: "8px",
                                                  verticalAlign: "top",
                                                }}
                                              >
                                                Steroids
                                              </td>
                                              <td
                                                style={{
                                                  border: "1px solid black",
                                                  padding: "8px",
                                                  verticalAlign: "top",
                                                }}
                                              >
                                                <div className="sub-question">
                                                  <div
                                                    style={{
                                                      display: "flex",
                                                      alignItems: "flex-start",
                                                    }}
                                                    onClick={(e) => {
                                                      if (
                                                        e.target.tagName ===
                                                        "INPUT"
                                                      )
                                                        return;
                                                      HandleFieldChange({
                                                        target: {
                                                          name:
                                                            "betamethasone_elrC" +
                                                            idx,
                                                          id: `${idx}`,
                                                        },
                                                      });
                                                    }}
                                                  >
                                                    <input
                                                      type="checkbox"
                                                      style={{
                                                        marginTop: "6px",
                                                      }}
                                                      id={idx}
                                                      name={
                                                        "betamethasone_elrC" +
                                                        idx
                                                      }
                                                      checked={
                                                        data.betamethasone_elrC
                                                      }
                                                      onChange={
                                                        HandleFieldChange
                                                      }
                                                    />
                                                    <span
                                                      style={{
                                                        paddingLeft: "15px",
                                                      }}
                                                    >
                                                      Betamethasone (dose:
                                                      <input
                                                        className="sub-input-dash-num"
                                                        style={{
                                                          border: "none",
                                                          borderBottom:
                                                            "1px solid #727272",
                                                          background:
                                                            "transparent",
                                                          outline: "none",
                                                        }}
                                                        id={idx}
                                                        name={
                                                          "betamethasone_elrCNum" +
                                                          idx
                                                        }
                                                        type="text"
                                                        value={
                                                          data.betamethasone_elrCNum
                                                        }
                                                        onKeyDown={
                                                          AllowOnlyNonZeroNumeric
                                                        }
                                                        onChange={
                                                          HandleFieldChange
                                                        }
                                                        maxLength={10}
                                                      />
                                                      mg/day)
                                                    </span>
                                                  </div>

                                                  <div
                                                    style={{
                                                      display: "flex",
                                                      alignItems: "flex-start",
                                                    }}
                                                    onClick={(e) => {
                                                      if (
                                                        e.target.tagName ===
                                                        "INPUT"
                                                      )
                                                        return;
                                                      HandleFieldChange({
                                                        target: {
                                                          name:
                                                            "dexamethasone_elrC" +
                                                            idx,
                                                          id: `${idx}`,
                                                        },
                                                      });
                                                    }}
                                                  >
                                                    <input
                                                      type="checkbox"
                                                      style={{
                                                        marginTop: "6px",
                                                      }}
                                                      id={idx}
                                                      name={
                                                        "dexamethasone_elrC" +
                                                        idx
                                                      }
                                                      checked={
                                                        data.dexamethasone_elrC
                                                      }
                                                      onChange={
                                                        HandleFieldChange
                                                      }
                                                    />
                                                    <span
                                                      style={{
                                                        paddingLeft: "15px",
                                                      }}
                                                    >
                                                      Dexamethasone (dose:
                                                      <input
                                                        className="sub-input-dash-num"
                                                        style={{
                                                          border: "none",
                                                          borderBottom:
                                                            "1px solid #727272",
                                                          background:
                                                            "transparent",
                                                          outline: "none",
                                                        }}
                                                        id={idx}
                                                        name={
                                                          "dexamethasone_elrCNum" +
                                                          idx
                                                        }
                                                        type="text"
                                                        value={
                                                          data.dexamethasone_elrCNum
                                                        }
                                                        onKeyDown={
                                                          AllowOnlyNonZeroNumeric
                                                        }
                                                        onChange={
                                                          HandleFieldChange
                                                        }
                                                        maxLength={10}
                                                      />
                                                      mg/day)
                                                    </span>
                                                  </div>

                                                  <div
                                                    style={{
                                                      display: "flex",
                                                      alignItems: "flex-start",
                                                    }}
                                                    onClick={(e) => {
                                                      if (
                                                        e.target.tagName ===
                                                        "INPUT"
                                                      )
                                                        return;
                                                      HandleFieldChange({
                                                        target: {
                                                          name:
                                                            "hydrocortisone_elrC" +
                                                            idx,
                                                          id: `${idx}`,
                                                        },
                                                      });
                                                    }}
                                                  >
                                                    <input
                                                      type="checkbox"
                                                      style={{
                                                        marginTop: "6px",
                                                      }}
                                                      id={idx}
                                                      name={
                                                        "hydrocortisone_elrC" +
                                                        idx
                                                      }
                                                      checked={
                                                        data.hydrocortisone_elrC
                                                      }
                                                      onChange={
                                                        HandleFieldChange
                                                      }
                                                    />
                                                    <span
                                                      style={{
                                                        paddingLeft: "15px",
                                                      }}
                                                    >
                                                      Hydrocortisone (oral or
                                                      intravenous only)
                                                      <br></br>(dose:
                                                      <input
                                                        className="sub-input-dash-num"
                                                        style={{
                                                          border: "none",
                                                          borderBottom:
                                                            "1px solid #727272",
                                                          background:
                                                            "transparent",
                                                          outline: "none",
                                                        }}
                                                        id={idx}
                                                        name={
                                                          "hydrocortisone_elrCNum" +
                                                          idx
                                                        }
                                                        type="text"
                                                        value={
                                                          data.hydrocortisone_elrCNum
                                                        }
                                                        onKeyDown={
                                                          AllowOnlyNonZeroNumeric
                                                        }
                                                        onChange={
                                                          HandleFieldChange
                                                        }
                                                        maxLength={10}
                                                      />
                                                      mg/day)
                                                    </span>
                                                  </div>

                                                  <div
                                                    style={{
                                                      display: "flex",
                                                      alignItems: "flex-start",
                                                    }}
                                                    onClick={(e) => {
                                                      if (
                                                        e.target.tagName ===
                                                        "INPUT"
                                                      )
                                                        return;
                                                      HandleFieldChange({
                                                        target: {
                                                          name:
                                                            "methylprednisolone_elrC" +
                                                            idx,
                                                          id: `${idx}`,
                                                        },
                                                      });
                                                    }}
                                                  >
                                                    <input
                                                      type="checkbox"
                                                      style={{
                                                        marginTop: "6px",
                                                      }}
                                                      id={idx}
                                                      name={
                                                        "methylprednisolone_elrC" +
                                                        idx
                                                      }
                                                      checked={
                                                        data.methylprednisolone_elrC
                                                      }
                                                      onChange={
                                                        HandleFieldChange
                                                      }
                                                    />
                                                    <span
                                                      style={{
                                                        paddingLeft: "15px",
                                                      }}
                                                    >
                                                      Methylprednisolone (dose:
                                                      <input
                                                        className="sub-input-dash-num"
                                                        style={{
                                                          border: "none",
                                                          borderBottom:
                                                            "1px solid #727272",
                                                          background:
                                                            "transparent",
                                                          outline: "none",
                                                        }}
                                                        id={idx}
                                                        name={
                                                          "methylprednisolone_elrCNum" +
                                                          idx
                                                        }
                                                        type="text"
                                                        value={
                                                          data.methylprednisolone_elrCNum
                                                        }
                                                        onKeyDown={
                                                          AllowOnlyNonZeroNumeric
                                                        }
                                                        onChange={
                                                          HandleFieldChange
                                                        }
                                                        maxLength={10}
                                                      />
                                                      mg/day)
                                                    </span>
                                                  </div>

                                                  <div
                                                    style={{
                                                      display: "flex",
                                                      alignItems: "flex-start",
                                                    }}
                                                    onClick={(e) => {
                                                      if (
                                                        e.target.tagName ===
                                                        "INPUT"
                                                      )
                                                        return;
                                                      HandleFieldChange({
                                                        target: {
                                                          name:
                                                            "prednisolone_elrC" +
                                                            idx,
                                                          id: `${idx}`,
                                                        },
                                                      });
                                                    }}
                                                  >
                                                    <input
                                                      type="checkbox"
                                                      style={{
                                                        marginTop: "6px",
                                                      }}
                                                      id={idx}
                                                      name={
                                                        "prednisolone_elrC" +
                                                        idx
                                                      }
                                                      checked={
                                                        data.prednisolone_elrC
                                                      }
                                                      onChange={
                                                        HandleFieldChange
                                                      }
                                                    />
                                                    <span
                                                      style={{
                                                        paddingLeft: "15px",
                                                      }}
                                                    >
                                                      Prednisolone (dose:
                                                      <input
                                                        className="sub-input-dash-num"
                                                        style={{
                                                          border: "none",
                                                          borderBottom:
                                                            "1px solid #727272",
                                                          background:
                                                            "transparent",
                                                          outline: "none",
                                                        }}
                                                        id={idx}
                                                        name={
                                                          "prednisolone_elrCNum" +
                                                          idx
                                                        }
                                                        type="text"
                                                        value={
                                                          data.prednisolone_elrCNum
                                                        }
                                                        onKeyDown={
                                                          AllowOnlyNonZeroNumeric
                                                        }
                                                        onChange={
                                                          HandleFieldChange
                                                        }
                                                        maxLength={10}
                                                      />
                                                      mg/day)
                                                    </span>
                                                  </div>

                                                  <div
                                                    style={{
                                                      display: "flex",
                                                      alignItems: "flex-start",
                                                    }}
                                                    onClick={(e) => {
                                                      if (
                                                        e.target.tagName ===
                                                        "INPUT"
                                                      )
                                                        return;
                                                      HandleFieldChange({
                                                        target: {
                                                          name:
                                                            "prednisone_elrC" +
                                                            idx,
                                                          id: `${idx}`,
                                                        },
                                                      });
                                                    }}
                                                  >
                                                    <input
                                                      type="checkbox"
                                                      style={{
                                                        marginTop: "6px",
                                                      }}
                                                      id={idx}
                                                      name={
                                                        "prednisone_elrC" + idx
                                                      }
                                                      checked={
                                                        data.prednisone_elrC
                                                      }
                                                      onChange={
                                                        HandleFieldChange
                                                      }
                                                    />
                                                    <span
                                                      style={{
                                                        paddingLeft: "15px",
                                                      }}
                                                    >
                                                      Prednisone (dose:
                                                      <input
                                                        className="sub-input-dash-num"
                                                        style={{
                                                          border: "none",
                                                          borderBottom:
                                                            "1px solid #727272",
                                                          background:
                                                            "transparent",
                                                          outline: "none",
                                                        }}
                                                        id={idx}
                                                        name={
                                                          "prednisone_elrCNum" +
                                                          idx
                                                        }
                                                        type="text"
                                                        value={
                                                          data.prednisone_elrCNum
                                                        }
                                                        onKeyDown={
                                                          AllowOnlyNonZeroNumeric
                                                        }
                                                        onChange={
                                                          HandleFieldChange
                                                        }
                                                        maxLength={10}
                                                      />
                                                      mg/day)
                                                    </span>
                                                  </div>

                                                  <div
                                                    style={{
                                                      display: "flex",
                                                      alignItems: "flex-start",
                                                    }}
                                                    onClick={(e) => {
                                                      if (
                                                        e.target.tagName ===
                                                        "INPUT"
                                                      )
                                                        return;
                                                      HandleFieldChange({
                                                        target: {
                                                          name:
                                                            "triamcinolone_elrC" +
                                                            idx,
                                                          id: `${idx}`,
                                                        },
                                                      });
                                                    }}
                                                  >
                                                    <input
                                                      type="checkbox"
                                                      style={{
                                                        marginTop: "6px",
                                                      }}
                                                      id={idx}
                                                      name={
                                                        "triamcinolone_elrC" +
                                                        idx
                                                      }
                                                      checked={
                                                        data.triamcinolone_elrC
                                                      }
                                                      onChange={
                                                        HandleFieldChange
                                                      }
                                                    />
                                                    <span
                                                      style={{
                                                        paddingLeft: "15px",
                                                      }}
                                                    >
                                                      Triamcinolone (dose:
                                                      <input
                                                        className="sub-input-dash-num"
                                                        style={{
                                                          border: "none",
                                                          borderBottom:
                                                            "1px solid #727272",
                                                          background:
                                                            "transparent",
                                                          outline: "none",
                                                        }}
                                                        id={idx}
                                                        name={
                                                          "triamcinolone_elrCNum" +
                                                          idx
                                                        }
                                                        type="text"
                                                        value={
                                                          data.triamcinolone_elrCNum
                                                        }
                                                        onKeyDown={
                                                          AllowOnlyNonZeroNumeric
                                                        }
                                                        onChange={
                                                          HandleFieldChange
                                                        }
                                                        maxLength={10}
                                                      />
                                                      mg/day)
                                                    </span>
                                                  </div>

                                                  <div className="">
                                                    <label>
                                                      <input
                                                        type="radio"
                                                        id={idx}
                                                        name={
                                                          "dose_name_elrC_unknown" +
                                                          idx
                                                        }
                                                        checked={
                                                          data.dose_name_elrC_unknown
                                                        }
                                                        onChange={
                                                          HandleFieldChange
                                                        }
                                                      />
                                                      <span className="radio-text-padding">
                                                        Unknown
                                                      </span>
                                                    </label>
                                                  </div>

                                                  <ErrorField
                                                    show={
                                                      data.name_dose_steroidsHardEmpty_elrC
                                                    }
                                                    message={
                                                      ELCharacteristics_MSGS.Q392CDoseEmptyHard
                                                    }
                                                    style={{
                                                      paddingLeft: "0px",
                                                    }}
                                                  />

                                                  <ErrorField
                                                    show={
                                                      data.steroids_elrC_txtEmpty
                                                    }
                                                    message={
                                                      ELCharacteristics_MSGS.Q392CDoseTxtEmptyHard
                                                    }
                                                    style={{
                                                      paddingLeft: "0px",
                                                    }}
                                                  />

                                                  <ErrorField
                                                    show={
                                                      data.steroidsDoseRangeInvalid_elrC
                                                    }
                                                    message={
                                                      ELCharacteristics_MSGS.Q392CDoseRangeInvalid
                                                    }
                                                    style={{
                                                      paddingLeft: "0px",
                                                    }}
                                                  />
                                                </div>
                                              </td>
                                              <td
                                                style={{
                                                  border: "1px solid black",
                                                  padding: "8px",
                                                  verticalAlign: "top",
                                                }}
                                              >
                                                <div className="sub-question">
                                                  <div
                                                    style={{
                                                      display: "flex",
                                                      alignItems: "flex-start",
                                                    }}
                                                    onClick={(e) => {
                                                      if (
                                                        e.target.tagName ===
                                                        "INPUT"
                                                      )
                                                        return;
                                                      HandleFieldChange({
                                                        target: {
                                                          name:
                                                            "steroidReasonPretratment_elrC" +
                                                            idx,
                                                          id: `${idx}`,
                                                        },
                                                      });
                                                    }}
                                                  >
                                                    <input
                                                      type="checkbox"
                                                      style={{
                                                        marginTop: "6px",
                                                      }}
                                                      id={idx}
                                                      name={
                                                        "steroidReasonPretratment_elrC" +
                                                        idx
                                                      }
                                                      checked={
                                                        data.steroidReasonPretratment_elrC
                                                      }
                                                      onChange={
                                                        HandleFieldChange
                                                      }
                                                    />
                                                    <span className="radio-text-padding">
                                                      As a pre-treatment per
                                                      label recommendation
                                                    </span>
                                                  </div>
                                                  <div
                                                    style={{
                                                      display: "flex",
                                                      alignItems: "flex-start",
                                                    }}
                                                    onClick={(e) => {
                                                      if (
                                                        e.target.tagName ===
                                                        "INPUT"
                                                      )
                                                        return;
                                                      HandleFieldChange({
                                                        target: {
                                                          name:
                                                            "steroidReasonPrimarycrs_elrC" +
                                                            idx,
                                                          id: `${idx}`,
                                                        },
                                                      });
                                                    }}
                                                  >
                                                    <input
                                                      type="checkbox"
                                                      style={{
                                                        marginTop: "6px",
                                                      }}
                                                      id={idx}
                                                      name={
                                                        "steroidReasonPrimarycrs_elrC" +
                                                        idx
                                                      }
                                                      checked={
                                                        data.steroidReasonPrimarycrs_elrC
                                                      }
                                                      onChange={
                                                        HandleFieldChange
                                                      }
                                                    />
                                                    <span className="radio-text-padding">
                                                      Primary prophylactic for
                                                      cytokine release syndrome
                                                      (CRS)
                                                    </span>
                                                  </div>
                                                  <div
                                                    style={{
                                                      display: "flex",
                                                      alignItems: "flex-start",
                                                    }}
                                                    onClick={(e) => {
                                                      if (
                                                        e.target.tagName ===
                                                        "INPUT"
                                                      )
                                                        return;
                                                      HandleFieldChange({
                                                        target: {
                                                          name:
                                                            "steroidReasonSecondarycrs_elrC" +
                                                            idx,
                                                          id: `${idx}`,
                                                        },
                                                      });
                                                    }}
                                                  >
                                                    <input
                                                      type="checkbox"
                                                      style={{
                                                        marginTop: "6px",
                                                      }}
                                                      id={idx}
                                                      name={
                                                        "steroidReasonSecondarycrs_elrC" +
                                                        idx
                                                      }
                                                      checked={
                                                        data.steroidReasonSecondarycrs_elrC
                                                      }
                                                      onChange={
                                                        HandleFieldChange
                                                      }
                                                    />
                                                    <span className="radio-text-padding">
                                                      Secondary prophylactic for
                                                      CRS
                                                    </span>
                                                  </div>
                                                  <div
                                                    style={{
                                                      display: "flex",
                                                      alignItems: "flex-start",
                                                    }}
                                                    onClick={(e) => {
                                                      if (
                                                        e.target.tagName ===
                                                        "INPUT"
                                                      )
                                                        return;
                                                      HandleFieldChange({
                                                        target: {
                                                          name:
                                                            "steroidReasonSupportivecrs_elrC" +
                                                            idx,
                                                          id: `${idx}`,
                                                        },
                                                      });
                                                    }}
                                                  >
                                                    <input
                                                      type="checkbox"
                                                      style={{
                                                        marginTop: "6px",
                                                      }}
                                                      id={idx}
                                                      name={
                                                        "steroidReasonSupportivecrs_elrC" +
                                                        idx
                                                      }
                                                      checked={
                                                        data.steroidReasonSupportivecrs_elrC
                                                      }
                                                      onChange={
                                                        HandleFieldChange
                                                      }
                                                    />
                                                    <span className="radio-text-padding">
                                                      Treatment or supportive
                                                      care for CRS
                                                    </span>
                                                  </div>
                                                  <div
                                                    style={{
                                                      display: "flex",
                                                      alignItems: "flex-start",
                                                    }}
                                                    onClick={(e) => {
                                                      if (
                                                        e.target.tagName ===
                                                        "INPUT"
                                                      )
                                                        return;
                                                      HandleFieldChange({
                                                        target: {
                                                          name:
                                                            "steroidReasonPrimaryneuro_elrC" +
                                                            idx,
                                                          id: `${idx}`,
                                                        },
                                                      });
                                                    }}
                                                  >
                                                    <input
                                                      type="checkbox"
                                                      style={{
                                                        marginTop: "6px",
                                                      }}
                                                      id={idx}
                                                      name={
                                                        "steroidReasonPrimaryneuro_elrC" +
                                                        idx
                                                      }
                                                      checked={
                                                        data.steroidReasonPrimaryneuro_elrC
                                                      }
                                                      onChange={
                                                        HandleFieldChange
                                                      }
                                                    />
                                                    <span className="radio-text-padding">
                                                      Primary prophylactic for
                                                      neurologic events
                                                    </span>
                                                  </div>
                                                  <div
                                                    style={{
                                                      display: "flex",
                                                      alignItems: "flex-start",
                                                    }}
                                                    onClick={(e) => {
                                                      if (
                                                        e.target.tagName ===
                                                        "INPUT"
                                                      )
                                                        return;
                                                      HandleFieldChange({
                                                        target: {
                                                          name:
                                                            "steroidReasonSecondaryneuro_elrC" +
                                                            idx,
                                                          id: `${idx}`,
                                                        },
                                                      });
                                                    }}
                                                  >
                                                    <input
                                                      type="checkbox"
                                                      style={{
                                                        marginTop: "6px",
                                                      }}
                                                      id={idx}
                                                      name={
                                                        "steroidReasonSecondaryneuro_elrC" +
                                                        idx
                                                      }
                                                      checked={
                                                        data.steroidReasonSecondaryneuro_elrC
                                                      }
                                                      onChange={
                                                        HandleFieldChange
                                                      }
                                                    />
                                                    <span className="radio-text-padding">
                                                      Secondary prophylactic for
                                                      neurologic events
                                                    </span>
                                                  </div>
                                                  <div
                                                    style={{
                                                      display: "flex",
                                                      alignItems: "flex-start",
                                                    }}
                                                    onClick={(e) => {
                                                      if (
                                                        e.target.tagName ===
                                                        "INPUT"
                                                      )
                                                        return;
                                                      HandleFieldChange({
                                                        target: {
                                                          name:
                                                            "steroidReasonSupportiveneuro_elrC" +
                                                            idx,
                                                          id: `${idx}`,
                                                        },
                                                      });
                                                    }}
                                                  >
                                                    <input
                                                      type="checkbox"
                                                      style={{
                                                        marginTop: "6px",
                                                      }}
                                                      id={idx}
                                                      name={
                                                        "steroidReasonSupportiveneuro_elrC" +
                                                        idx
                                                      }
                                                      checked={
                                                        data.steroidReasonSupportiveneuro_elrC
                                                      }
                                                      onChange={
                                                        HandleFieldChange
                                                      }
                                                    />
                                                    <span className="radio-text-padding">
                                                      Treatment or supportive
                                                      care for neurologic events
                                                    </span>
                                                  </div>
                                                  <div className="">
                                                    <label className="checkbox-option">
                                                      <input
                                                        type="radio"
                                                        id={idx}
                                                        name={
                                                          "steroidReasonUnk_elrC" +
                                                          idx
                                                        }
                                                        checked={
                                                          data.steroidReasonUnk_elrC
                                                        }
                                                        onChange={
                                                          HandleFieldChange
                                                        }
                                                      />
                                                      <span className="radio-text-padding">
                                                        Unknown
                                                      </span>
                                                    </label>
                                                  </div>
                                                  <ErrorField
                                                    show={
                                                      data.Q392CReasonEmptyHard
                                                    }
                                                    message={
                                                      ELCharacteristics_MSGS.Q392CReasonEmptyHard
                                                    }
                                                    style={{
                                                      paddingLeft: "0px",
                                                    }}
                                                  />
                                                </div>
                                              </td>
                                            </tr>
                                          </tbody>
                                        </table>
                                      </div>
                                    </div>
                                  )}

                                  {/* 39.3.C */}
                                  {data.granulocyte_elrC && (
                                    <div
                                      style={{
                                        marginLeft: "28px",
                                        marginBlock: "6px",
                                        padding: "5px",
                                      }}
                                    >
                                      <div className="question question-weight">
                                        <span>Q39.3.C.</span>
                                        <span className="quest-text-pad">
                                          Why was G-CSF used{" "}
                                          <b>
                                            within{" "}
                                            <u>
                                              7 days before to 14 days after
                                            </u>{" "}
                                            initiating treatment
                                          </b>{" "}
                                          with{" "}
                                          <span
                                            style={{
                                              color:
                                                PageDesignConstants.fontColor,
                                              fontWeight: "bolder",
                                            }}
                                          >
                                            ELREXFIO<sup>TM</sup>{" "}
                                            (elranatamab-bcmm)
                                          </span>{" "}
                                          (i.e.,{" "}
                                          <span
                                            style={{
                                              color:
                                                PageDesignConstants.fontColor,
                                            }}
                                          >
                                            [
                                            {formatDateMinus7Days(
                                              elrexInitiationDate,
                                            )}
                                            ]
                                          </span>{" "}
                                          through{" "}
                                          <span
                                            style={{
                                              color:
                                                PageDesignConstants.fontColor,
                                            }}
                                          >
                                            [
                                            {formatDatePlus14Days(
                                              elrexInitiationDate,
                                            )}
                                            ]
                                          </span>
                                          ). <i>Select all that apply.</i>
                                        </span>
                                      </div>

                                      <div
                                        className="dependent-sub-section"
                                        style={{ backgroundColor: "#ffffff" }}
                                      >
                                        <div className="question-bot-sapce">
                                          <div className="sub-question grid-overflow">
                                            <table
                                              style={{
                                                border: "1px solid black",
                                                borderCollapse: "collapse",
                                                width: "100%",
                                              }}
                                            >
                                              <thead>
                                                <tr>
                                                  <th
                                                    style={{
                                                      border: "1px solid black",
                                                      padding: "8px",
                                                      textAlign: "center",
                                                      width: "25%",
                                                    }}
                                                  >
                                                    Treatment
                                                  </th>
                                                  <th
                                                    style={{
                                                      border: "1px solid black",
                                                      padding: "8px",
                                                      textAlign: "center",
                                                      width: "25%",
                                                    }}
                                                  >
                                                    Medication
                                                  </th>

                                                  <th
                                                    style={{
                                                      border: "1px solid black",
                                                      padding: "8px",
                                                      textAlign: "center",
                                                      width: "45%",
                                                    }}
                                                  >
                                                    Reason for receiving
                                                    medication
                                                  </th>
                                                </tr>
                                              </thead>

                                              <tbody>
                                                <tr>
                                                  <td
                                                    style={{
                                                      border: "1px solid black",
                                                      padding: "8px",
                                                    }}
                                                  >
                                                    <span className="quest-text-pad">
                                                      ELREXFIO<sup>TM</sup>{" "}
                                                      (elranatamab-bcmm)
                                                    </span>
                                                  </td>

                                                  <td
                                                    style={{
                                                      border: "1px solid black",
                                                      padding: "8px",
                                                    }}
                                                  >
                                                    <div>
                                                      <div>
                                                        <div
                                                          className="option-without-input"
                                                          style={{
                                                            display: "flex",
                                                            alignItems:
                                                              "flex-start",
                                                          }}
                                                        >
                                                          <label>
                                                            <span
                                                              className="radio-text-padding"
                                                              style={{
                                                                paddingLeft:
                                                                  "10px",
                                                              }}
                                                            >
                                                              Granulocyte
                                                              colony-stimulating
                                                              factor (G-CSF)
                                                            </span>
                                                          </label>
                                                        </div>
                                                      </div>
                                                    </div>
                                                  </td>

                                                  <td
                                                    style={{
                                                      border: "1px solid black",
                                                      padding: "8px",
                                                    }}
                                                  >
                                                    <div>
                                                      <div>
                                                        <div className="">
                                                          <label>
                                                            <input
                                                              type="checkbox"
                                                              id={idx}
                                                              name={
                                                                "prophylactic_use_elrC" +
                                                                idx
                                                              }
                                                              checked={
                                                                data.prophylactic_use_elrC
                                                              }
                                                              onChange={
                                                                HandleFieldChange
                                                              }
                                                            />
                                                            <span className="radio-text-padding">
                                                              Primary
                                                              Prophylactic use
                                                            </span>
                                                          </label>
                                                        </div>
                                                        <div className="">
                                                          <label>
                                                            <input
                                                              type="checkbox"
                                                              id={idx}
                                                              name={
                                                                "secondary_prophylactic_use_elrC" +
                                                                idx
                                                              }
                                                              checked={
                                                                data.secondary_prophylactic_use_elrC
                                                              }
                                                              onChange={
                                                                HandleFieldChange
                                                              }
                                                            />
                                                            <span className="radio-text-padding">
                                                              Secondary
                                                              Prophylactic use
                                                            </span>
                                                          </label>
                                                        </div>

                                                        <div className="">
                                                          <label>
                                                            <input
                                                              type="checkbox"
                                                              id={idx}
                                                              name={
                                                                "oth_use_elrC" +
                                                                idx
                                                              }
                                                              checked={
                                                                data.oth_use_elrC
                                                              }
                                                              onChange={
                                                                HandleFieldChange
                                                              }
                                                            />
                                                            <span className="radio-text-padding">
                                                              Other use
                                                            </span>
                                                          </label>
                                                        </div>
                                                        <div className="">
                                                          <label>
                                                            <input
                                                              type="radio"
                                                              id={idx}
                                                              value={99}
                                                              name={
                                                                "unknown_elrC" +
                                                                idx
                                                              }
                                                              checked={
                                                                data.unknown_elrC ===
                                                                99
                                                              }
                                                              onChange={
                                                                HandleFieldChange
                                                              }
                                                            />
                                                            <span className="radio-text-padding">
                                                              Unknown
                                                            </span>
                                                          </label>
                                                        </div>

                                                        <ErrorField
                                                          show={
                                                            data.Q393CReasonEmptyHard
                                                          }
                                                          message={
                                                            ELCharacteristics_MSGS.Q393CReasonEmptyHard
                                                          }
                                                          style={{
                                                            paddingLeft: "0px",
                                                          }}
                                                        />
                                                      </div>
                                                    </div>
                                                  </td>
                                                </tr>
                                              </tbody>
                                            </table>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  )}
                                </React.Fragment>
                              ))}
                          </div>
                        )}
                        {/* -------------------- */}
                        {/*Show if Linvoseltamab selected  */}
                        {!isNewEcrfC && (
                          <div className="">
                            {isLinvoveySelected &&
                              Q37LinvoDetails &&
                              Q37LinvoDetails.length > 0 &&
                              Q37LinvoDetails.map((data, idx) => (
                                <React.Fragment key={idx}>
                                  <div className="sub-question-bot-sapce">
                                    <div className="question question-weight">
                                      <span>Q39.</span>
                                      <span className="quest-text-pad">
                                        Which medications were used, if any,
                                        during the <b>step-up dosing phase</b>{" "}
                                        of{" "}
                                        <span
                                          style={{
                                            color:
                                              PageDesignConstants.fontColor,
                                            fontWeight: "bolder",
                                          }}
                                        >
                                          LYNOZYFIC<sup>TM</sup>{" "}
                                          (linvoquetamab-gcpt)
                                        </span>{" "}
                                        i.e from
                                        <span
                                          style={{
                                            color:
                                              PageDesignConstants.fontColor,
                                            fontWeight: "bolder",
                                          }}
                                        >
                                          {" "}
                                          {getQ402CIndexDateDisplay(
                                            RRMMCohortTreatment.Linvoseltamab,
                                          )}{" "}
                                        </span>
                                        <span>
                                          until the end of step-up dosing?
                                        </span>{" "}
                                        <i>Select all that apply.</i>
                                        <br />
                                        If you cannot determine if an
                                        administration is part of the{" "}
                                        <strong>Step-Up Dosing Phase, </strong>
                                        please infer the dosing phase using the
                                        LYNOZYFIC
                                        <sup>TM</sup> (linvoquetamab-gcpt)
                                        dosing label information provided.
                                      </span>
                                    </div>
                                    <div className="radio-text-padding">
                                      <div className="double-dig-answer">
                                        <div className="">
                                          <label>
                                            <input
                                              type="checkbox"
                                              id={idx}
                                              name={"tocilizumab_linvo" + idx}
                                              checked={data.tocilizumab_linvo}
                                              onChange={HandleFieldChange}
                                            />
                                            <span className="radio-text-padding">
                                              Tocilizumab
                                            </span>
                                          </label>
                                        </div>
                                        <div className="">
                                          <label>
                                            <input
                                              type="checkbox"
                                              id={idx}
                                              name={"granulocyte_linvo" + idx}
                                              checked={!!data.granulocyte_linvo}
                                              onChange={HandleFieldChange}
                                            />
                                            <span className="radio-text-padding">
                                              Granulocyte colony-stimulating
                                              factor (G-CSF)
                                            </span>
                                          </label>
                                        </div>
                                        {/* Phase 2  */}

                                        <div className="">
                                          <label>
                                            <input
                                              type="checkbox"
                                              id={idx}
                                              name={"Steroids" + idx}
                                              checked={data.Steroids}
                                              onChange={HandleFieldChange}
                                            />
                                            <span className="radio-text-padding">
                                              Steroids
                                            </span>
                                          </label>
                                        </div>

                                        <div className="option-without-input">
                                          <label className="option-fbox">
                                            <input
                                              type="radio"
                                              id={idx}
                                              name={
                                                "noneoftheabove_linvo" + idx
                                              }
                                              value={99}
                                              checked={
                                                data.noneoftheabove_linvo === 99
                                              }
                                              onChange={HandleFieldChange}
                                            />

                                            <span className="radio-text-padding">
                                              None of the above{" "}
                                            </span>
                                          </label>
                                        </div>
                                        <ErrorField
                                          show={data.Q39LinvoEmptyHard}
                                          message={
                                            ELCharacteristics_MSGS.Q39LinvoEmptyHard
                                          }
                                          style={{
                                            paddingLeft: "0px",
                                          }}
                                        />
                                      </div>
                                    </div>
                                  </div>

                                  {/* Q39.1 */}
                                  {/* Q39.1 retired - render disabled via `false &&` guard below; JSX left intact */}
                                  {false &&
                                    data.tocilizumab_linvo &&
                                    !isNewEcrfA && (
                                      <div
                                        style={{
                                          marginLeft: "28px",
                                          marginBlock: "6px",
                                          padding: "5px",
                                        }}
                                      >
                                        <div className="question question-weight">
                                          <span>Q39.1.</span>
                                          <span className="quest-text-pad">
                                            Summarize the reasons why
                                            Tocilizumab was used and dosing
                                            information while receiving
                                            treatment with{" "}
                                            <span
                                              style={{
                                                color:
                                                  PageDesignConstants.fontColor,
                                                fontWeight: "bolder",
                                              }}
                                            >
                                              LYNOZYFIC
                                              <sup className="supscript">
                                                TM
                                              </sup>{" "}
                                              (linvoseltamab-gcpt)
                                            </span>
                                            . <i>Select all that apply.</i>
                                          </span>
                                        </div>

                                        <div className="dependent-sub-section">
                                          <div className="question-bot-sapce">
                                            <div className="sub-question grid-overflow">
                                              <table
                                                style={{
                                                  border: "1px solid black",
                                                  borderCollapse: "collapse",
                                                  width: "100%",
                                                }}
                                              >
                                                <thead>
                                                  <tr>
                                                    <th
                                                      style={{
                                                        border:
                                                          "1px solid black",
                                                        padding: "8px",
                                                        textAlign: "center",
                                                        width: "20%",
                                                      }}
                                                    >
                                                      Treatment
                                                    </th>
                                                    <th
                                                      style={{
                                                        border:
                                                          "1px solid black",
                                                        padding: "8px",
                                                        textAlign: "center",
                                                        width: "15%",
                                                      }}
                                                    >
                                                      Medication
                                                    </th>

                                                    <th
                                                      style={{
                                                        border:
                                                          "1px solid black",
                                                        padding: "8px",
                                                        textAlign: "center",
                                                        width: "45%",
                                                      }}
                                                    >
                                                      Reason for receiving
                                                      medication
                                                    </th>
                                                  </tr>
                                                </thead>

                                                <tbody>
                                                  <tr>
                                                    <td
                                                      style={{
                                                        border:
                                                          "1px solid black",
                                                        padding: "8px",
                                                      }}
                                                    >
                                                      <span className="quest-text-pad">
                                                        LYNOZYFIC
                                                        <sup>TM</sup>{" "}
                                                        (linvoquetamab-gcpt)
                                                      </span>
                                                    </td>

                                                    <td
                                                      style={{
                                                        border:
                                                          "1px solid black",
                                                        padding: "8px",
                                                      }}
                                                    >
                                                      <div>
                                                        <div>
                                                          <div
                                                            className="option-without-input"
                                                            style={{
                                                              display: "flex",
                                                              alignItems:
                                                                "flex-start",
                                                            }}
                                                          >
                                                            <label>
                                                              <span
                                                                className="radio-text-padding"
                                                                style={{
                                                                  paddingLeft:
                                                                    "10px",
                                                                }}
                                                              >
                                                                Tocilizumab
                                                              </span>
                                                            </label>
                                                          </div>
                                                        </div>
                                                      </div>
                                                    </td>

                                                    <td
                                                      style={{
                                                        border:
                                                          "1px solid black",
                                                        padding: "8px",
                                                      }}
                                                    >
                                                      <div>
                                                        <div className="">
                                                          <label className="checkbox-option">
                                                            <input
                                                              type="checkbox"
                                                              id={idx}
                                                              name={
                                                                "cytokin_linvo" +
                                                                idx
                                                              }
                                                              checked={
                                                                data.cytokin_linvo
                                                              }
                                                              onChange={
                                                                HandleFieldChange
                                                              }
                                                            />
                                                            <span className="radio-text-padding">
                                                              Primary
                                                              prophylactic for
                                                              cytokine release
                                                              syndrome (CRS)
                                                            </span>
                                                          </label>
                                                        </div>
                                                        <div className="">
                                                          <label className="checkbox-option">
                                                            <input
                                                              type="checkbox"
                                                              id={idx}
                                                              name={
                                                                "secondary_prophylactic_crs_linvo" +
                                                                idx
                                                              }
                                                              checked={
                                                                data.secondary_prophylactic_crs_linvo
                                                              }
                                                              onChange={
                                                                HandleFieldChange
                                                              }
                                                            />
                                                            <span className="radio-text-padding">
                                                              Secondary
                                                              prophylactic for
                                                              CRS
                                                            </span>
                                                          </label>
                                                        </div>

                                                        <div className="">
                                                          <label className="checkbox-option">
                                                            <input
                                                              type="checkbox"
                                                              id={idx}
                                                              name={
                                                                "crs_linvo" +
                                                                idx
                                                              }
                                                              checked={
                                                                data.crs_linvo
                                                              }
                                                              onChange={
                                                                HandleFieldChange
                                                              }
                                                            />
                                                            <span className="radio-text-padding">
                                                              Treatment or
                                                              supportive care
                                                              for CRS
                                                            </span>
                                                          </label>
                                                        </div>
                                                        <div className="">
                                                          <label className="checkbox-option">
                                                            <input
                                                              type="checkbox"
                                                              id={idx}
                                                              name={
                                                                "neurotoxicity_linvo" +
                                                                idx
                                                              }
                                                              checked={
                                                                data.neurotoxicity_linvo
                                                              }
                                                              onChange={
                                                                HandleFieldChange
                                                              }
                                                            />
                                                            <span className="radio-text-padding">
                                                              Primary
                                                              prophylactic for
                                                              neurologic events
                                                            </span>
                                                          </label>
                                                        </div>

                                                        <div className="">
                                                          <label className="checkbox-option">
                                                            <input
                                                              type="checkbox"
                                                              id={idx}
                                                              name={
                                                                "secondary_neurotoxicity_linvo" +
                                                                idx
                                                              }
                                                              checked={
                                                                data.secondary_neurotoxicity_linvo
                                                              }
                                                              onChange={
                                                                HandleFieldChange
                                                              }
                                                            />
                                                            <span className="radio-text-padding">
                                                              Secondary
                                                              prophylactic for
                                                              neurologic events
                                                            </span>
                                                          </label>
                                                        </div>

                                                        <div className="">
                                                          <label className="checkbox-option">
                                                            <input
                                                              type="checkbox"
                                                              id={idx}
                                                              name={
                                                                "tmt_neurotoxicity_linvo" +
                                                                idx
                                                              }
                                                              checked={
                                                                data.tmt_neurotoxicity_linvo
                                                              }
                                                              onChange={
                                                                HandleFieldChange
                                                              }
                                                            />
                                                            <span className="radio-text-padding">
                                                              Treatment or
                                                              supportive care
                                                              for neurologic
                                                              events
                                                            </span>
                                                          </label>
                                                        </div>

                                                        <div className="">
                                                          <label className="checkbox-option">
                                                            <input
                                                              type="radio"
                                                              id={idx}
                                                              name={
                                                                "tmt_linvoseltamab_unknown" +
                                                                idx
                                                              }
                                                              checked={
                                                                data.tmt_linvoseltamab_unknown
                                                              }
                                                              onChange={
                                                                HandleFieldChange
                                                              }
                                                            />
                                                            <span className="radio-text-padding">
                                                              Unknown
                                                            </span>
                                                          </label>
                                                        </div>

                                                        <ErrorField
                                                          show={
                                                            data.Q39linvoReasonHard
                                                          }
                                                          message={
                                                            ELCharacteristics_MSGS.Q39linvoReasonHard
                                                          }
                                                          style={{
                                                            paddingLeft: "0px",
                                                          }}
                                                        />
                                                      </div>
                                                    </td>
                                                  </tr>
                                                </tbody>
                                              </table>
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    )}

                                  {/* Q39.1.A */}
                                  {data.tocilizumab_linvo && isNewEcrfA && (
                                    <div
                                      style={{
                                        marginLeft: "28px",
                                        marginBlock: "6px",
                                        padding: "5px",
                                      }}
                                    >
                                      <div className="question question-weight">
                                        <span>Q39.1.A.</span>
                                        <span className="quest-text-pad">
                                          Summarize the reasons why tocilizumab
                                          was used and dosing information while
                                          receiving treatment with{" "}
                                          <span
                                            style={{
                                              color:
                                                PageDesignConstants.fontColor,
                                              fontWeight: "bolder",
                                            }}
                                          >
                                            LYNOZYFIC
                                            <sup className="supscript">TM</sup>
                                            {"  "}
                                            (linvoquetamab-gcpt).
                                          </span>{" "}
                                          <i>Select all that apply.</i>
                                        </span>
                                      </div>

                                      <div className="dependent-section">
                                        {/* ELREXFIO® */}
                                        {q391ALinvoTocilzumabList?.length > 0 &&
                                          q391ALinvoTocilzumabList.map(
                                            (itm, index) => (
                                              <div
                                                className="loop-section-ul"
                                                key={index}
                                              >
                                                <div className="loop-head">
                                                  <span
                                                    className="question-weight"
                                                    style={{
                                                      float: "inline-start",
                                                    }}
                                                  >
                                                    {GetOrdinal(index + 1)}{" "}
                                                    LYNOZYFIC
                                                    <sup className="supscript">
                                                      TM
                                                    </sup>
                                                    {"  "}
                                                    (linvoquetamab-gcpt)
                                                  </span>
                                                  {index !== 0 && (
                                                    <div
                                                      className="delete-btn-div"
                                                      style={{
                                                        float: "inline-end",
                                                      }}
                                                    >
                                                      <img
                                                        width="16px"
                                                        src="../Assets/images/Icon-material-delete.png"
                                                        alt=""
                                                        style={{
                                                          cursor: "pointer",
                                                        }}
                                                        onClick={() =>
                                                          DeleteQ391ARow(
                                                            index,
                                                            RRMMCohortTreatment.Linvoseltamab,
                                                          )
                                                        }
                                                      />
                                                    </div>
                                                  )}
                                                </div>
                                                <div className="question-bot-sapce">
                                                  <div className="sub-question grid-overflow">
                                                    <table
                                                      style={{
                                                        border:
                                                          "1px solid black",
                                                        borderCollapse:
                                                          "collapse",
                                                        width: "100%",
                                                      }}
                                                    >
                                                      <thead>
                                                        <tr>
                                                          <th
                                                            style={{
                                                              border:
                                                                "1px solid black",
                                                              padding: "8px",
                                                              textAlign:
                                                                "center",
                                                              width: "20%",
                                                            }}
                                                          >
                                                            Treatment
                                                          </th>
                                                          <th
                                                            style={{
                                                              border:
                                                                "1px solid black",
                                                              padding: "8px",
                                                              textAlign:
                                                                "center",
                                                              width: "15%",
                                                            }}
                                                          >
                                                            Medication
                                                          </th>
                                                          <th
                                                            style={{
                                                              border:
                                                                "1px solid black",
                                                              padding: "8px",
                                                              textAlign:
                                                                "center",
                                                              width: "15%",
                                                            }}
                                                          >
                                                            Date of tocilizumab
                                                            administration
                                                          </th>
                                                          <th
                                                            style={{
                                                              border:
                                                                "1px solid black",
                                                              padding: "8px",
                                                              textAlign:
                                                                "center",
                                                              width: "45%",
                                                            }}
                                                          >
                                                            Reason for receiving
                                                            medication
                                                          </th>
                                                        </tr>
                                                      </thead>

                                                      <tbody>
                                                        <tr>
                                                          <td
                                                            style={{
                                                              border:
                                                                "1px solid black",
                                                              padding: "8px",
                                                            }}
                                                          >
                                                            <span className="quest-text-pad">
                                                              LYNOZYFIC
                                                              <sup className="supscript">
                                                                TM
                                                              </sup>
                                                              {"  "}
                                                              (linvoquetamab-gcpt)
                                                            </span>
                                                          </td>

                                                          <td
                                                            style={{
                                                              border:
                                                                "1px solid black",
                                                              padding: "8px",
                                                            }}
                                                          >
                                                            <div>
                                                              <div>
                                                                <div
                                                                  className="option-without-input"
                                                                  style={{
                                                                    display:
                                                                      "flex",
                                                                    alignItems:
                                                                      "flex-start",
                                                                  }}
                                                                >
                                                                  <label className="option-fbox">
                                                                    <span
                                                                      className="radio-text-padding"
                                                                      style={{
                                                                        paddingLeft:
                                                                          "10px",
                                                                      }}
                                                                    >
                                                                      Tocilizumab
                                                                    </span>
                                                                  </label>
                                                                </div>
                                                              </div>
                                                            </div>
                                                          </td>
                                                          <td
                                                            style={{
                                                              border:
                                                                "1px solid black",
                                                              padding: "8px",
                                                            }}
                                                          >
                                                            <div>
                                                              <div
                                                                className="option-without-input"
                                                                style={{
                                                                  display:
                                                                    "flex",
                                                                }}
                                                              >
                                                                <input
                                                                  type="radio"
                                                                  id={index}
                                                                  name={
                                                                    "administrationDateRD_linvo" +
                                                                    index
                                                                  }
                                                                  value={1}
                                                                  checked={
                                                                    itm.administrationDateRD_linvo ===
                                                                    1
                                                                  }
                                                                  onChange={
                                                                    HandleRepeaterFieldChange
                                                                  }
                                                                />
                                                                <span
                                                                  className="radio-text-padding"
                                                                  style={{
                                                                    paddingLeft:
                                                                      "10px",
                                                                    marginLeft:
                                                                      "-5px",
                                                                  }}
                                                                >
                                                                  <DatePickerComponent
                                                                    ctrlId={
                                                                      "administrationDate_linvo-" +
                                                                      index
                                                                    }
                                                                    HandleDateChange={
                                                                      HandleRepeaterDateChange
                                                                    }
                                                                    optionId={1}
                                                                    minDate={dayjs(
                                                                      "01-01-1920",
                                                                    )}
                                                                    maxDate={dayjs()}
                                                                    locale="en"
                                                                    date={
                                                                      itm.administrationDate_linvo
                                                                    }
                                                                  />
                                                                </span>
                                                              </div>

                                                              <div
                                                                className="option-without-input"
                                                                style={{
                                                                  display:
                                                                    "flex",
                                                                }}
                                                              >
                                                                <label className="option-fbox">
                                                                  <input
                                                                    type="radio"
                                                                    id={index}
                                                                    name={
                                                                      "administrationDateRD_linvo" +
                                                                      index
                                                                    }
                                                                    value={99}
                                                                    checked={
                                                                      itm.administrationDateRD_linvo ===
                                                                      99
                                                                    }
                                                                    onChange={
                                                                      HandleRepeaterFieldChange
                                                                    }
                                                                  />
                                                                  <span
                                                                    style={{
                                                                      paddingLeft:
                                                                        "10px",
                                                                      marginLeft:
                                                                        "-5px",
                                                                    }}
                                                                  >
                                                                    Date of
                                                                    administration
                                                                    unknown
                                                                  </span>
                                                                </label>
                                                              </div>

                                                              <ErrorField
                                                                show={
                                                                  itm.q39LinvoAdministrationRDEmpty
                                                                }
                                                                message={
                                                                  ELCharacteristics_MSGS.Q40Init_RdHard
                                                                }
                                                                style={{
                                                                  paddingLeft:
                                                                    "0px",
                                                                }}
                                                              />

                                                              <ErrorField
                                                                show={
                                                                  itm.q39LinvoAdministrationDateEmpty
                                                                }
                                                                message={
                                                                  ELCharacteristics_MSGS.Q391ATocilizumabAdministrationDateEmty
                                                                }
                                                                style={{
                                                                  paddingLeft:
                                                                    "0px",
                                                                }}
                                                              />
                                                              <ErrorField
                                                                show={
                                                                  itm.q39LinvoInvalidAdministrationDate
                                                                }
                                                                message={
                                                                  CommonError_Msgs.InvalidDate
                                                                }
                                                                style={{
                                                                  paddingLeft:
                                                                    "0px",
                                                                }}
                                                              />
                                                              <ErrorField
                                                                show={
                                                                  q391ALinvoAdministrationDateOutOfRange
                                                                }
                                                                message={
                                                                  ELCharacteristics_MSGS.Q391ATocilizumabDateChainOutOfBound
                                                                }
                                                                style={{
                                                                  paddingLeft:
                                                                    "0px",
                                                                }}
                                                              />
                                                            </div>
                                                          </td>
                                                          <td
                                                            style={{
                                                              border:
                                                                "1px solid black",
                                                              padding: "8px",
                                                            }}
                                                          >
                                                            <div>
                                                              <div className="">
                                                                <label className="checkbox-option">
                                                                  <input
                                                                    type="checkbox"
                                                                    id={index}
                                                                    name={
                                                                      "primaryProphylacticCrs_linvo" +
                                                                      index
                                                                    }
                                                                    checked={
                                                                      itm.primaryProphylacticCrs_linvo
                                                                    }
                                                                    onChange={
                                                                      HandleRepeaterFieldChange
                                                                    }
                                                                  />
                                                                  <span className="radio-text-padding">
                                                                    Primary
                                                                    prophylactic
                                                                    for cytokine
                                                                    release
                                                                    syndrome
                                                                    (CRS)
                                                                  </span>
                                                                </label>
                                                              </div>

                                                              <div className="">
                                                                <label className="checkbox-option">
                                                                  <input
                                                                    type="checkbox"
                                                                    id={index}
                                                                    name={
                                                                      "secondaryProphylacticCrs_linvo" +
                                                                      index
                                                                    }
                                                                    checked={
                                                                      itm.secondaryProphylacticCrs_linvo
                                                                    }
                                                                    onChange={
                                                                      HandleRepeaterFieldChange
                                                                    }
                                                                  />
                                                                  <span className="radio-text-padding">
                                                                    Secondary
                                                                    prophylactic
                                                                    for CRS
                                                                  </span>
                                                                </label>
                                                              </div>

                                                              <div className="">
                                                                <label className="checkbox-option">
                                                                  <input
                                                                    type="checkbox"
                                                                    id={index}
                                                                    name={
                                                                      "supportiveCareCrs_linvo" +
                                                                      index
                                                                    }
                                                                    checked={
                                                                      itm.supportiveCareCrs_linvo
                                                                    }
                                                                    onChange={
                                                                      HandleRepeaterFieldChange
                                                                    }
                                                                  />
                                                                  <span className="radio-text-padding">
                                                                    Treatment or
                                                                    supportive
                                                                    care for CRS
                                                                  </span>
                                                                </label>
                                                              </div>
                                                              <div className="">
                                                                <label className="checkbox-option">
                                                                  <input
                                                                    type="checkbox"
                                                                    id={index}
                                                                    name={
                                                                      "primaryProphylacticNeurologic_linvo" +
                                                                      index
                                                                    }
                                                                    checked={
                                                                      itm.primaryProphylacticNeurologic_linvo
                                                                    }
                                                                    onChange={
                                                                      HandleRepeaterFieldChange
                                                                    }
                                                                  />
                                                                  <span className="radio-text-padding">
                                                                    Primary
                                                                    prophylactic
                                                                    for
                                                                    neurologic
                                                                    events
                                                                  </span>
                                                                </label>
                                                              </div>
                                                              <div className="">
                                                                <label className="checkbox-option">
                                                                  <input
                                                                    type="checkbox"
                                                                    id={index}
                                                                    name={
                                                                      "secondaryProphylacticNeurologic_linvo" +
                                                                      index
                                                                    }
                                                                    checked={
                                                                      itm.secondaryProphylacticNeurologic_linvo
                                                                    }
                                                                    onChange={
                                                                      HandleRepeaterFieldChange
                                                                    }
                                                                  />
                                                                  <span className="radio-text-padding">
                                                                    Secondary
                                                                    prophylactic
                                                                    for
                                                                    neurologic
                                                                    events
                                                                  </span>
                                                                </label>
                                                              </div>

                                                              <div className="">
                                                                <label className="checkbox-option">
                                                                  <input
                                                                    type="checkbox"
                                                                    id={index}
                                                                    name={
                                                                      "supportiveCareNeurologic_linvo" +
                                                                      index
                                                                    }
                                                                    checked={
                                                                      itm.supportiveCareNeurologic_linvo
                                                                    }
                                                                    onChange={
                                                                      HandleRepeaterFieldChange
                                                                    }
                                                                  />
                                                                  <span className="radio-text-padding">
                                                                    Treatment or
                                                                    supportive
                                                                    care for
                                                                    neurologic
                                                                    events
                                                                  </span>
                                                                </label>
                                                              </div>

                                                              <div className="">
                                                                <label className="checkbox-option">
                                                                  <input
                                                                    type="radio"
                                                                    id={index}
                                                                    name={
                                                                      "medicationReasonUnk_linvo" +
                                                                      index
                                                                    }
                                                                    checked={
                                                                      itm.medicationReasonUnk_linvo
                                                                    }
                                                                    onChange={
                                                                      HandleRepeaterFieldChange
                                                                    }
                                                                  />
                                                                  <span className="radio-text-padding">
                                                                    Unknown
                                                                  </span>
                                                                </label>
                                                              </div>

                                                              <ErrorField
                                                                show={
                                                                  itm.q39LinvoReasonEmpty
                                                                }
                                                                message={
                                                                  ELCharacteristics_MSGS.Q39linvoReasonHard
                                                                }
                                                                style={{
                                                                  paddingLeft:
                                                                    "0px",
                                                                }}
                                                              />
                                                            </div>
                                                          </td>
                                                        </tr>
                                                      </tbody>
                                                    </table>
                                                  </div>
                                                </div>
                                              </div>
                                            ),
                                          )}

                                        {q391ALinvoTocilzumabList?.length > 0 &&
                                          q391ALinvoTocilzumabList?.length <
                                            550 && (
                                            <div className="sub-question-bot-sapce-ul mt-2">
                                              <div
                                                className="answer-list-text"
                                                onClick={() =>
                                                  AddQ391ARow(
                                                    RRMMCohortTreatment.Linvoseltamab,
                                                  )
                                                }
                                              >
                                                <a>
                                                  <img
                                                    width="18px"
                                                    src="../Assets/images/plus.png"
                                                    alt=""
                                                  />
                                                </a>
                                                <a className="radio-text-padding">
                                                  Click here to add another
                                                  tocilizumab administration of
                                                  linvoseltamab
                                                </a>
                                              </div>
                                            </div>
                                          )}
                                      </div>
                                    </div>
                                  )}

                                  {/* Q39.2 */}

                                  {data.Steroids && (
                                    <div
                                      style={{
                                        marginLeft: "28px",
                                        marginBlock: "6px",
                                        padding: "5px",
                                      }}
                                    >
                                      <div className="question question-weight">
                                        <span>Q39.2.</span>
                                        <span className="quest-text-pad">
                                          Summarize the reasons why steroids
                                          were used and dosing information while
                                          receiving treatment with{" "}
                                          <span
                                            style={{
                                              color:
                                                PageDesignConstants.fontColor,
                                              fontWeight: "bolder",
                                            }}
                                          >
                                            LYNOZYFIC
                                            <sup className="supscript">
                                              TM
                                            </sup>{" "}
                                            (linvoquetamab-gcpt)
                                          </span>
                                          . <i>Select all that apply.</i>
                                        </span>
                                      </div>

                                      <div className="dependent-sub-section">
                                        {/* ELREXFIO® */}
                                        <div className="question-bot-sapce">
                                          <div className="sub-question grid-overflow">
                                            <table
                                              style={{
                                                border: "1px solid black",
                                                borderCollapse: "collapse",
                                                width: "100%",
                                              }}
                                            >
                                              <thead>
                                                <tr>
                                                  <th
                                                    style={{
                                                      border: "1px solid black",
                                                      padding: "8px",
                                                      textAlign: "center",
                                                      width: "5%",
                                                    }}
                                                  >
                                                    Treatment
                                                  </th>
                                                  <th
                                                    style={{
                                                      border: "1px solid black",
                                                      padding: "8px",
                                                      textAlign: "center",
                                                      width: "5%",
                                                    }}
                                                  >
                                                    Medication
                                                  </th>

                                                  <th
                                                    style={{
                                                      border: "1px solid black",
                                                      padding: "8px",
                                                      textAlign: "center",
                                                      width: "35%",
                                                    }}
                                                  >
                                                    Name and dose of the
                                                    steroid(s) used
                                                  </th>

                                                  <th
                                                    style={{
                                                      border: "1px solid black",
                                                      padding: "8px",
                                                      textAlign: "center",
                                                      width: "50%",
                                                    }}
                                                  >
                                                    Reason for receiving
                                                    medication
                                                  </th>
                                                </tr>
                                              </thead>

                                              <tbody>
                                                <tr>
                                                  <td
                                                    style={{
                                                      border: "1px solid black",
                                                      padding: "8px",
                                                    }}
                                                  >
                                                    <span className="quest-text-pad">
                                                      LYNOZYFIC
                                                      <sup>TM</sup>{" "}
                                                      (linvoquetamab-gcpt)
                                                    </span>
                                                  </td>

                                                  <td
                                                    style={{
                                                      border: "1px solid black",
                                                      padding: "8px",
                                                    }}
                                                  >
                                                    <div>
                                                      <div>
                                                        <div
                                                          className="option-without-input"
                                                          style={{
                                                            display: "flex",
                                                            alignItems:
                                                              "flex-start",
                                                          }}
                                                        >
                                                          <label>
                                                            <span
                                                              className="radio-text-padding"
                                                              style={{
                                                                paddingLeft:
                                                                  "10px",
                                                              }}
                                                            >
                                                              Steroids
                                                            </span>
                                                          </label>
                                                        </div>
                                                      </div>
                                                    </div>
                                                  </td>

                                                  {/* Start */}

                                                  <td
                                                    style={{
                                                      border: "1px solid black",
                                                      padding: "8px",
                                                    }}
                                                  >
                                                    <div>
                                                      <div>
                                                        <div className="option-fbox">
                                                          <label>
                                                            <input
                                                              type="checkbox"
                                                              id={idx}
                                                              name={
                                                                "betamethasone_linvo" +
                                                                idx
                                                              }
                                                              checked={
                                                                data.betamethasone_linvo
                                                              }
                                                              onChange={
                                                                HandleFieldChange
                                                              }
                                                            />
                                                            <span
                                                              style={{
                                                                paddingLeft:
                                                                  "15px",
                                                              }}
                                                            >
                                                              Betamethasone
                                                              (total dose:
                                                              <input
                                                                className="sub-input-dash-num"
                                                                style={{
                                                                  border:
                                                                    "none",
                                                                  borderBottom:
                                                                    "1px solid #727272",
                                                                  background:
                                                                    "transparent",
                                                                  outline:
                                                                    "none",
                                                                }}
                                                                id={idx}
                                                                name={
                                                                  "betamethasone_linvoNum" +
                                                                  idx
                                                                }
                                                                type="text"
                                                                value={
                                                                  data.betamethasone_linvoNum
                                                                }
                                                                onKeyDown={
                                                                  AllowOnlyNonZeroNumeric
                                                                }
                                                                onChange={
                                                                  HandleFieldChange
                                                                }
                                                                maxLength={10}
                                                              />
                                                              mg)
                                                            </span>
                                                          </label>
                                                        </div>

                                                        <div className="">
                                                          <label>
                                                            <input
                                                              type="checkbox"
                                                              id={idx}
                                                              name={
                                                                "dexamethasone_linvo" +
                                                                idx
                                                              }
                                                              checked={
                                                                data.dexamethasone_linvo
                                                              }
                                                              onChange={
                                                                HandleFieldChange
                                                              }
                                                            />
                                                            <span
                                                              style={{
                                                                paddingLeft:
                                                                  "15px",
                                                              }}
                                                            >
                                                              Dexamethasone
                                                              (total dose:
                                                              <input
                                                                className="sub-input-dash-num"
                                                                style={{
                                                                  border:
                                                                    "none",
                                                                  borderBottom:
                                                                    "1px solid #727272",
                                                                  background:
                                                                    "transparent",
                                                                  outline:
                                                                    "none",
                                                                }}
                                                                id={idx}
                                                                name={
                                                                  "dexamethasone_linvoNum" +
                                                                  idx
                                                                }
                                                                type="text"
                                                                value={
                                                                  data.dexamethasone_linvoNum
                                                                }
                                                                onKeyDown={
                                                                  AllowOnlyNonZeroNumeric
                                                                }
                                                                onChange={
                                                                  HandleFieldChange
                                                                }
                                                                maxLength={10}
                                                              />
                                                              mg)
                                                            </span>
                                                          </label>
                                                        </div>
                                                        <div className="">
                                                          <label className="option-fbox">
                                                            <input
                                                              style={{
                                                                marginTop:
                                                                  "6px",
                                                              }}
                                                              type="checkbox"
                                                              id={idx}
                                                              name={
                                                                "hydrocortisone_linvo" +
                                                                idx
                                                              }
                                                              checked={
                                                                data.hydrocortisone_linvo
                                                              }
                                                              onChange={
                                                                HandleFieldChange
                                                              }
                                                            />
                                                            <span
                                                              style={{
                                                                paddingLeft:
                                                                  "15px",
                                                              }}
                                                            >
                                                              Hydrocortisone
                                                              (oral or
                                                              intravenous only)
                                                              <br></br>
                                                              (total dose:
                                                              <input
                                                                className="sub-input-dash-num"
                                                                style={{
                                                                  border:
                                                                    "none",
                                                                  borderBottom:
                                                                    "1px solid #727272",
                                                                  background:
                                                                    "transparent",
                                                                  outline:
                                                                    "none",
                                                                }}
                                                                id={idx}
                                                                name={
                                                                  "hydrocortisone_linvoNum" +
                                                                  idx
                                                                }
                                                                type="text"
                                                                value={
                                                                  data.hydrocortisone_linvoNum
                                                                }
                                                                onKeyDown={
                                                                  AllowNumericWithDecimal
                                                                }
                                                                onChange={
                                                                  HandleFieldChange
                                                                }
                                                                maxLength={10}
                                                              />
                                                              mg)
                                                            </span>
                                                          </label>
                                                        </div>

                                                        <div className="">
                                                          <label>
                                                            <input
                                                              type="checkbox"
                                                              id={idx}
                                                              name={
                                                                "methylprednisolone_linvo" +
                                                                idx
                                                              }
                                                              checked={
                                                                data.methylprednisolone_linvo
                                                              }
                                                              onChange={
                                                                HandleFieldChange
                                                              }
                                                            />
                                                            <span
                                                              style={{
                                                                paddingLeft:
                                                                  "15px",
                                                              }}
                                                            >
                                                              Methylprednisolone
                                                              (total dose:
                                                              <input
                                                                className="sub-input-dash-num"
                                                                style={{
                                                                  border:
                                                                    "none",
                                                                  borderBottom:
                                                                    "1px solid #727272",
                                                                  background:
                                                                    "transparent",
                                                                  outline:
                                                                    "none",
                                                                }}
                                                                id={idx}
                                                                name={
                                                                  "methylprednisolone_linvoNum" +
                                                                  idx
                                                                }
                                                                type="text"
                                                                value={
                                                                  data.methylprednisolone_linvoNum
                                                                }
                                                                onKeyDown={
                                                                  AllowOnlyNonZeroNumeric
                                                                }
                                                                onChange={
                                                                  HandleFieldChange
                                                                }
                                                                maxLength={10}
                                                              />
                                                              mg)
                                                            </span>
                                                          </label>
                                                        </div>

                                                        <div className="">
                                                          <label>
                                                            <input
                                                              type="checkbox"
                                                              id={idx}
                                                              name={
                                                                "prednisolone_linvo" +
                                                                idx
                                                              }
                                                              checked={
                                                                data.prednisolone_linvo
                                                              }
                                                              onChange={
                                                                HandleFieldChange
                                                              }
                                                            />
                                                            <span
                                                              style={{
                                                                paddingLeft:
                                                                  "15px",
                                                              }}
                                                            >
                                                              Prednisolone
                                                              (total dose:
                                                              <input
                                                                className="sub-input-dash-num"
                                                                style={{
                                                                  border:
                                                                    "none",
                                                                  borderBottom:
                                                                    "1px solid #727272",
                                                                  background:
                                                                    "transparent",
                                                                  outline:
                                                                    "none",
                                                                }}
                                                                id={idx}
                                                                name={
                                                                  "prednisolone_linvoNum" +
                                                                  idx
                                                                }
                                                                type="text"
                                                                value={
                                                                  data.prednisolone_linvoNum
                                                                }
                                                                onKeyDown={
                                                                  AllowOnlyNonZeroNumeric
                                                                }
                                                                onChange={
                                                                  HandleFieldChange
                                                                }
                                                                maxLength={10}
                                                              />
                                                              mg)
                                                            </span>
                                                          </label>
                                                        </div>

                                                        <div className="">
                                                          <label>
                                                            <input
                                                              type="checkbox"
                                                              id={idx}
                                                              name={
                                                                "prednisone_linvo" +
                                                                idx
                                                              }
                                                              checked={
                                                                data.prednisone_linvo
                                                              }
                                                              onChange={
                                                                HandleFieldChange
                                                              }
                                                            />
                                                            <span
                                                              style={{
                                                                paddingLeft:
                                                                  "15px",
                                                              }}
                                                            >
                                                              Prednisone (total
                                                              dose:
                                                              <input
                                                                className="sub-input-dash-num"
                                                                style={{
                                                                  border:
                                                                    "none",
                                                                  borderBottom:
                                                                    "1px solid #727272",
                                                                  background:
                                                                    "transparent",
                                                                  outline:
                                                                    "none",
                                                                }}
                                                                id={idx}
                                                                name={
                                                                  "prednisone_linvoNum" +
                                                                  idx
                                                                }
                                                                type="text"
                                                                value={
                                                                  data.prednisone_linvoNum
                                                                }
                                                                onKeyDown={
                                                                  AllowOnlyNonZeroNumeric
                                                                }
                                                                onChange={
                                                                  HandleFieldChange
                                                                }
                                                                maxLength={10}
                                                              />
                                                              mg)
                                                            </span>
                                                          </label>
                                                        </div>

                                                        <div className="">
                                                          <label>
                                                            <input
                                                              type="checkbox"
                                                              id={idx}
                                                              name={
                                                                "triamcinolone_linvo" +
                                                                idx
                                                              }
                                                              checked={
                                                                data.triamcinolone_linvo
                                                              }
                                                              onChange={
                                                                HandleFieldChange
                                                              }
                                                            />
                                                            <span
                                                              style={{
                                                                paddingLeft:
                                                                  "15px",
                                                              }}
                                                            >
                                                              Triamcinolone
                                                              (total dose:
                                                              <input
                                                                className="sub-input-dash-num"
                                                                style={{
                                                                  border:
                                                                    "none",
                                                                  borderBottom:
                                                                    "1px solid #727272",
                                                                  background:
                                                                    "transparent",
                                                                  outline:
                                                                    "none",
                                                                }}
                                                                id={idx}
                                                                name={
                                                                  "triamcinolone_linvoNum" +
                                                                  idx
                                                                }
                                                                type="text"
                                                                value={
                                                                  data.triamcinolone_linvoNum
                                                                }
                                                                onKeyDown={
                                                                  AllowOnlyNonZeroNumeric
                                                                }
                                                                onChange={
                                                                  HandleFieldChange
                                                                }
                                                                maxLength={10}
                                                              />
                                                              mg)
                                                            </span>
                                                          </label>
                                                        </div>

                                                        <div className="">
                                                          <label>
                                                            <input
                                                              type="radio"
                                                              id={idx}
                                                              name={
                                                                "dose_name_linvo_unknown" +
                                                                idx
                                                              }
                                                              checked={
                                                                data.dose_name_linvo_unknown
                                                              }
                                                              onChange={
                                                                HandleFieldChange
                                                              }
                                                            />
                                                            <span className="radio-text-padding">
                                                              Unknown
                                                            </span>
                                                          </label>
                                                        </div>

                                                        <ErrorField
                                                          show={
                                                            data.name_dose_steroids_linvoHardEmpty
                                                          }
                                                          message={
                                                            ELCharacteristics_MSGS.name_dose_steroidslinvoHardEmpty
                                                          }
                                                          style={{
                                                            paddingLeft: "0px",
                                                          }}
                                                        />

                                                        <ErrorField
                                                          show={
                                                            data.steroids_linvo_txtEmpty
                                                          }
                                                          message={
                                                            ELCharacteristics_MSGS.steroids_linvo_txtEmpty
                                                          }
                                                          style={{
                                                            paddingLeft: "0px",
                                                          }}
                                                        />
                                                      </div>
                                                    </div>
                                                  </td>

                                                  {/* End */}

                                                  <td
                                                    style={{
                                                      border: "1px solid black",
                                                      padding: "8px",
                                                    }}
                                                  >
                                                    <div>
                                                      <div className="">
                                                        <label className="checkbox-option">
                                                          <input
                                                            type="checkbox"
                                                            id={idx}
                                                            name={
                                                              "steroid_pre_tmt_linvo" +
                                                              idx
                                                            }
                                                            checked={
                                                              data.steroid_pre_tmt_linvo
                                                            }
                                                            onChange={
                                                              HandleFieldChange
                                                            }
                                                          />
                                                          <span
                                                            style={{
                                                              paddingLeft:
                                                                "15px",
                                                            }}
                                                          >
                                                            As a pre-treatment
                                                            per label
                                                            recommendation
                                                          </span>
                                                        </label>
                                                      </div>

                                                      <div className="">
                                                        <label className="checkbox-option">
                                                          <input
                                                            type="checkbox"
                                                            id={idx}
                                                            name={
                                                              "cytokin_linvo_steroids" +
                                                              idx
                                                            }
                                                            checked={
                                                              data.cytokin_linvo_steroids
                                                            }
                                                            onChange={
                                                              HandleFieldChange
                                                            }
                                                          />
                                                          <span
                                                            style={{
                                                              paddingLeft:
                                                                "15px",
                                                            }}
                                                          >
                                                            Primary prophylactic
                                                            for cytokine release
                                                            syndrome (CRS)
                                                          </span>
                                                        </label>
                                                      </div>

                                                      <div className="">
                                                        <label className="checkbox-option">
                                                          <input
                                                            type="checkbox"
                                                            id={idx}
                                                            name={
                                                              "secondary_prophylactic_linvo_steroids" +
                                                              idx
                                                            }
                                                            checked={
                                                              data.secondary_prophylactic_linvo_steroids
                                                            }
                                                            onChange={
                                                              HandleFieldChange
                                                            }
                                                          />
                                                          <span
                                                            style={{
                                                              paddingLeft:
                                                                "15px",
                                                            }}
                                                          >
                                                            Secondary
                                                            prophylactic for CRS
                                                          </span>
                                                        </label>
                                                      </div>

                                                      <div className="">
                                                        <label className="checkbox-option">
                                                          <input
                                                            type="checkbox"
                                                            id={idx}
                                                            name={
                                                              "crs_linvo_steroids" +
                                                              idx
                                                            }
                                                            checked={
                                                              data.crs_linvo_steroids
                                                            }
                                                            onChange={
                                                              HandleFieldChange
                                                            }
                                                          />
                                                          <span
                                                            style={{
                                                              paddingLeft:
                                                                "15px",
                                                            }}
                                                          >
                                                            Treatment or
                                                            supportive care for
                                                            CRS
                                                          </span>
                                                        </label>
                                                      </div>
                                                      <div className="">
                                                        <label className="checkbox-option">
                                                          <input
                                                            type="checkbox"
                                                            id={idx}
                                                            name={
                                                              "neurotoxicity_linvo_steroids" +
                                                              idx
                                                            }
                                                            checked={
                                                              data.neurotoxicity_linvo_steroids
                                                            }
                                                            onChange={
                                                              HandleFieldChange
                                                            }
                                                          />
                                                          <span
                                                            style={{
                                                              paddingLeft:
                                                                "15px",
                                                            }}
                                                          >
                                                            Primary prophylactic
                                                            for neurologic
                                                            events
                                                          </span>
                                                        </label>
                                                      </div>

                                                      <div className="">
                                                        <label className="checkbox-option">
                                                          <input
                                                            type="checkbox"
                                                            id={idx}
                                                            name={
                                                              "secondary_neurotoxicity_linvo_steroids" +
                                                              idx
                                                            }
                                                            checked={
                                                              data.secondary_neurotoxicity_linvo_steroids
                                                            }
                                                            onChange={
                                                              HandleFieldChange
                                                            }
                                                          />
                                                          <span
                                                            style={{
                                                              paddingLeft:
                                                                "15px",
                                                            }}
                                                          >
                                                            Secondary
                                                            prophylactic for
                                                            neurologic events
                                                          </span>
                                                        </label>
                                                      </div>
                                                      <div className="">
                                                        <label className="checkbox-option">
                                                          <input
                                                            type="checkbox"
                                                            id={idx}
                                                            name={
                                                              "tmt_neurotoxicity_linvo_steroids" +
                                                              idx
                                                            }
                                                            checked={
                                                              data.tmt_neurotoxicity_linvo_steroids
                                                            }
                                                            onChange={
                                                              HandleFieldChange
                                                            }
                                                          />
                                                          <span
                                                            style={{
                                                              paddingLeft:
                                                                "15px",
                                                            }}
                                                          >
                                                            Treatment or
                                                            supportive care for
                                                            neurologic events
                                                          </span>
                                                        </label>
                                                      </div>

                                                      <div className="">
                                                        <label>
                                                          <input
                                                            type="radio"
                                                            id={idx}
                                                            name={
                                                              "tmt_linvo_unknown" +
                                                              idx
                                                            }
                                                            checked={
                                                              data.tmt_linvo_unknown
                                                            }
                                                            onChange={
                                                              HandleFieldChange
                                                            }
                                                          />
                                                          <span className="radio-text-padding">
                                                            Unknown
                                                          </span>
                                                        </label>
                                                      </div>

                                                      <ErrorField
                                                        show={
                                                          data.Q39linvoSteroidsEmptyHard
                                                        }
                                                        message={
                                                          ELCharacteristics_MSGS.Q39linvoSteroidsEmptyHard
                                                        }
                                                        style={{
                                                          paddingLeft: "0px",
                                                        }}
                                                      />
                                                    </div>
                                                  </td>
                                                </tr>
                                              </tbody>
                                            </table>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  )}

                                  {data.granulocyte_linvo && (
                                    <div
                                      style={{
                                        marginLeft: "28px",
                                        marginBlock: "6px",
                                        padding: "5px",
                                      }}
                                    >
                                      <div className="question question-weight">
                                        <span>Q39.3.</span>
                                        <span className="quest-text-pad">
                                          Why was G-CSF used while receiving
                                          treatment with{" "}
                                          <span
                                            style={{
                                              color:
                                                PageDesignConstants.fontColor,
                                              fontWeight: "bolder",
                                            }}
                                          >
                                            LYNOZYFIC<sup>TM</sup>{" "}
                                            (linvoseltamab-gcpt)
                                          </span>
                                          ? <i>Select all that apply.</i>
                                        </span>
                                      </div>

                                      <div className="dependent-sub-section">
                                        <div className="question-bot-sapce">
                                          <div className="sub-question grid-overflow">
                                            <table
                                              style={{
                                                border: "1px solid black",
                                                borderCollapse: "collapse",
                                                width: "100%",
                                              }}
                                            >
                                              <thead>
                                                <tr>
                                                  <th
                                                    style={{
                                                      border: "1px solid black",
                                                      padding: "8px",
                                                      textAlign: "center",
                                                      width: "5%",
                                                    }}
                                                  >
                                                    Treatment
                                                  </th>
                                                  <th
                                                    style={{
                                                      border: "1px solid black",
                                                      padding: "8px",
                                                      textAlign: "center",
                                                      width: "5%",
                                                    }}
                                                  >
                                                    Medication
                                                  </th>
                                                  <th
                                                    style={{
                                                      border: "1px solid black",
                                                      padding: "8px",
                                                      textAlign: "center",
                                                      width: "50%",
                                                    }}
                                                  >
                                                    Reason for receiving
                                                    medication
                                                  </th>
                                                </tr>
                                              </thead>

                                              <tbody>
                                                <tr>
                                                  <td
                                                    style={{
                                                      border: "1px solid black",
                                                      padding: "8px",
                                                    }}
                                                  >
                                                    <span className="quest-text-pad">
                                                      LYNOZYFIC<sup>TM</sup>{" "}
                                                      (linvoseltamab-gcpt)
                                                    </span>
                                                  </td>

                                                  <td
                                                    style={{
                                                      border: "1px solid black",
                                                      padding: "8px",
                                                    }}
                                                  >
                                                    <div
                                                      className="option-without-input"
                                                      style={{
                                                        display: "flex",
                                                        alignItems:
                                                          "flex-start",
                                                      }}
                                                    >
                                                      <label>
                                                        <span
                                                          className="radio-text-padding"
                                                          style={{
                                                            paddingLeft: "10px",
                                                          }}
                                                        >
                                                          Granulocyte
                                                          colony-stimulating
                                                          factor (G-CSF)
                                                        </span>
                                                      </label>
                                                    </div>
                                                  </td>

                                                  <td
                                                    style={{
                                                      border: "1px solid black",
                                                      padding: "8px",
                                                    }}
                                                  >
                                                    <div>
                                                      <div className="">
                                                        <label>
                                                          <input
                                                            type="checkbox"
                                                            id={idx}
                                                            name={
                                                              "prophylactic_use_linvo" +
                                                              idx
                                                            }
                                                            checked={
                                                              data.prophylactic_use_linvo
                                                            }
                                                            onChange={
                                                              HandleFieldChange
                                                            }
                                                          />
                                                          <span className="radio-text-padding">
                                                            Primary Prophylactic
                                                            use
                                                          </span>
                                                        </label>
                                                      </div>
                                                      <div className="">
                                                        <label>
                                                          <input
                                                            type="checkbox"
                                                            id={idx}
                                                            name={
                                                              "secondary_prophylactic_use_linvo" +
                                                              idx
                                                            }
                                                            checked={
                                                              data.secondary_prophylactic_use_linvo
                                                            }
                                                            onChange={
                                                              HandleFieldChange
                                                            }
                                                          />
                                                          <span className="radio-text-padding">
                                                            Secondary
                                                            Prophylactic use
                                                          </span>
                                                        </label>
                                                      </div>

                                                      <div className="">
                                                        <label>
                                                          <input
                                                            type="checkbox"
                                                            id={idx}
                                                            name={
                                                              "oth_use_linvo" +
                                                              idx
                                                            }
                                                            checked={
                                                              data.oth_use_linvo
                                                            }
                                                            onChange={
                                                              HandleFieldChange
                                                            }
                                                          />
                                                          <span className="radio-text-padding">
                                                            Other use
                                                          </span>
                                                        </label>
                                                      </div>
                                                      <div className="">
                                                        <label>
                                                          <input
                                                            type="radio"
                                                            id={idx}
                                                            value={99}
                                                            name={
                                                              "unknown_linvo" +
                                                              idx
                                                            }
                                                            checked={
                                                              data.unknown_linvo ===
                                                              99
                                                            }
                                                            onChange={
                                                              HandleFieldChange
                                                            }
                                                          />
                                                          <span className="radio-text-padding">
                                                            Unknown
                                                          </span>
                                                        </label>
                                                      </div>

                                                      <ErrorField
                                                        show={
                                                          data.Q39linvoGranduloEmptyHard
                                                        }
                                                        message={
                                                          ELCharacteristics_MSGS.Q39linvoGranduloEmptyHard
                                                        }
                                                        style={{
                                                          paddingLeft: "0px",
                                                        }}
                                                      />
                                                    </div>
                                                  </td>
                                                </tr>
                                              </tbody>
                                            </table>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  )}
                                </React.Fragment>
                              ))}
                          </div>
                        )}
                        {/* Question 39.C start - Linvoseltamab (eCRF New Elements C) */}
                        {isNewEcrfC && (
                          <div className="">
                            {isLinvoveySelected &&
                              Q37LinvoDetails &&
                              Q37LinvoDetails.length > 0 &&
                              Q37LinvoDetails.map((data, idx) => (
                                <React.Fragment key={idx}>
                                  <div className="sub-question-bot-sapce">
                                    <div className="question question-weight">
                                      <span>Q39.C.</span>
                                      <span className="quest-text-pad">
                                        Which medications were used, if any,{" "}
                                        <b>
                                          within{" "}
                                          <u> 7 days before to 14 days after</u>{" "}
                                          initiating treatment
                                        </b>{" "}
                                        with{" "}
                                        <span
                                          style={{
                                            color:
                                              PageDesignConstants.fontColor,
                                            fontWeight: "bolder",
                                          }}
                                        >
                                          LYNOZYFIC<sup>TM</sup>{" "}
                                          (linvoseltamab-gcpt)
                                        </span>{" "}
                                        (i.e.,{" "}
                                        <span
                                          style={{
                                            color:
                                              PageDesignConstants.fontColor,
                                          }}
                                        >
                                          [
                                          {formatDateMinus7Days(
                                            linvoInitiationDate,
                                          )}
                                          ]
                                        </span>{" "}
                                        through{" "}
                                        <span
                                          style={{
                                            color:
                                              PageDesignConstants.fontColor,
                                          }}
                                        >
                                          [
                                          {formatDatePlus14Days(
                                            linvoInitiationDate,
                                          )}
                                          ]
                                        </span>
                                        )? <i>Select all that apply.</i>
                                      </span>
                                    </div>
                                    <div className="radio-text-padding">
                                      <div className="double-dig-answer">
                                        <div className="">
                                          <label>
                                            <input
                                              type="checkbox"
                                              id={idx}
                                              name={"tocilizumab_linvoC" + idx}
                                              checked={data.tocilizumab_linvoC}
                                              onChange={HandleFieldChange}
                                            />
                                            <span className="radio-text-padding">
                                              Tocilizumab
                                            </span>
                                          </label>
                                        </div>
                                        <div className="">
                                          <label>
                                            <input
                                              type="checkbox"
                                              id={idx}
                                              name={"steroids_linvoC" + idx}
                                              checked={data.steroids_linvoC}
                                              onChange={HandleFieldChange}
                                            />
                                            <span className="radio-text-padding">
                                              Steroids
                                            </span>
                                          </label>
                                        </div>
                                        <div className="">
                                          <label>
                                            <input
                                              type="checkbox"
                                              id={idx}
                                              name={"granulocyte_linvoC" + idx}
                                              checked={data.granulocyte_linvoC}
                                              onChange={HandleFieldChange}
                                            />
                                            <span className="radio-text-padding">
                                              Granulocyte colony-stimulating
                                              factor (G-CSF)
                                            </span>
                                          </label>
                                        </div>
                                        <div className="option-without-input">
                                          <label className="option-fbox">
                                            <input
                                              type="radio"
                                              id={idx}
                                              name={
                                                "noneoftheabove_linvoC" + idx
                                              }
                                              value={99}
                                              checked={
                                                data.noneoftheabove_linvoC ===
                                                99
                                              }
                                              onChange={HandleFieldChange}
                                            />
                                            <span className="radio-text-padding">
                                              None of the above{" "}
                                            </span>
                                          </label>
                                        </div>
                                        <ErrorField
                                          show={data.Q39CEmptyHard}
                                          message={
                                            ELCharacteristics_MSGS.Q39CEmptyHard
                                          }
                                          style={{
                                            paddingLeft: "0px",
                                          }}
                                        />
                                      </div>
                                    </div>
                                  </div>

                                  {data.tocilizumab_linvoC && (
                                    <div
                                      style={{
                                        marginLeft: "28px",
                                        marginBlock: "6px",
                                        padding: "5px",
                                      }}
                                    >
                                      <div className="question question-weight">
                                        <span>Q39.1.C.</span>
                                        <span className="quest-text-pad">
                                          Summarize the reasons why tocilizumab
                                          was used and dosing information for
                                          tocilizumab{" "}
                                          <b>
                                            <u>
                                              within 7 days before to 14 days
                                              after
                                            </u>{" "}
                                            initiating treatment
                                          </b>{" "}
                                          with{" "}
                                          <span
                                            style={{
                                              color:
                                                PageDesignConstants.fontColor,
                                              fontWeight: "bolder",
                                            }}
                                          >
                                            LYNOZYFIC<sup>TM</sup>{" "}
                                            (linvoseltamab-gcpt)
                                          </span>{" "}
                                          (i.e.,{" "}
                                          <span
                                            style={{
                                              color:
                                                PageDesignConstants.fontColor,
                                            }}
                                          >
                                            [
                                            {formatDateMinus7Days(
                                              linvoInitiationDate,
                                            )}
                                            ]
                                          </span>{" "}
                                          through{" "}
                                          <span
                                            style={{
                                              color:
                                                PageDesignConstants.fontColor,
                                            }}
                                          >
                                            [
                                            {formatDatePlus14Days(
                                              linvoInitiationDate,
                                            )}
                                            ]
                                          </span>
                                          ). <i>Select all that apply.</i>
                                        </span>
                                      </div>
                                      <div
                                        className="dependent-section grid-overflow"
                                        style={{ backgroundColor: "#ffffff" }}
                                      >
                                        <table
                                          style={{
                                            border: "1px solid black",
                                            borderCollapse: "collapse",
                                            width: "100%",
                                            backgroundColor: "#ffffff",
                                            cursor: "default",
                                          }}
                                        >
                                          <thead>
                                            <tr>
                                              <th
                                                style={{
                                                  border: "1px solid black",
                                                  padding: "6px",
                                                  textAlign: "center",
                                                  backgroundColor: "#ffffff",
                                                }}
                                              >
                                                Treatment
                                              </th>
                                              <th
                                                style={{
                                                  border: "1px solid black",
                                                  padding: "6px",
                                                  textAlign: "center",
                                                  backgroundColor: "#ffffff",
                                                }}
                                              >
                                                Medication
                                              </th>
                                              <th
                                                style={{
                                                  border: "1px solid black",
                                                  padding: "6px",
                                                  textAlign: "center",
                                                  backgroundColor: "#ffffff",
                                                }}
                                              >
                                                Date of tocilizumab
                                                administration
                                              </th>
                                              <th
                                                style={{
                                                  border: "1px solid black",
                                                  padding: "6px",
                                                  textAlign: "center",
                                                  backgroundColor: "#ffffff",
                                                }}
                                              >
                                                Dose Strength
                                              </th>
                                              <th
                                                style={{
                                                  border: "1px solid black",
                                                  padding: "6px",
                                                  textAlign: "center",
                                                  backgroundColor: "#ffffff",
                                                }}
                                              >
                                                Reason for receiving medication
                                              </th>
                                            </tr>
                                          </thead>
                                          <tbody>
                                            <tr>
                                              <td
                                                style={{
                                                  border: "1px solid black",
                                                  padding: "8px",
                                                  verticalAlign: "top",
                                                }}
                                              >
                                                LYNOZYFIC<sup>TM</sup>{" "}
                                                (linvoseltamab-gcpt)
                                              </td>
                                              <td
                                                style={{
                                                  border: "1px solid black",
                                                  padding: "8px",
                                                  verticalAlign: "top",
                                                }}
                                              >
                                                Tocilizumab
                                              </td>
                                              <td
                                                style={{
                                                  border: "1px solid black",
                                                  padding: "8px",
                                                  verticalAlign: "top",
                                                }}
                                              >
                                                <div className="sub-question">
                                                  <div
                                                    className="option-without-input"
                                                    style={{ display: "flex" }}
                                                  >
                                                    <input
                                                      type="radio"
                                                      id={idx}
                                                      name={
                                                        "tociliAdmsDateRD_linvoC" +
                                                        idx
                                                      }
                                                      value={1}
                                                      checked={
                                                        data.tociliAdmsDateRD_linvoC ===
                                                        1
                                                      }
                                                      onChange={
                                                        HandleFieldChange
                                                      }
                                                    />
                                                    <span
                                                      className="radio-text-padding"
                                                      style={{
                                                        paddingLeft: "10px",
                                                        marginLeft: "-5px",
                                                      }}
                                                    >
                                                      <DatePickerComponent
                                                        ctrlId={
                                                          "tociliAdmsDate_linvoC-" +
                                                          idx
                                                        }
                                                        HandleDateChange={
                                                          HandleRepeaterDateChange
                                                        }
                                                        optionId={1}
                                                        minDate={dayjs(
                                                          "01-01-1920",
                                                        )}
                                                        maxDate={dayjs()}
                                                        locale="en"
                                                        date={
                                                          data.tociliAdmsDate_linvoC
                                                        }
                                                      />
                                                    </span>
                                                  </div>
                                                  <div
                                                    className="option-without-input"
                                                    style={{ display: "flex" }}
                                                  >
                                                    <label className="option-fbox">
                                                      <input
                                                        type="radio"
                                                        id={idx}
                                                        name={
                                                          "tociliAdmsDateRD_linvoC" +
                                                          idx
                                                        }
                                                        value={99}
                                                        checked={
                                                          data.tociliAdmsDateRD_linvoC ===
                                                          99
                                                        }
                                                        onChange={
                                                          HandleFieldChange
                                                        }
                                                      />
                                                      <span
                                                        style={{
                                                          paddingLeft: "10px",
                                                          marginLeft: "-5px",
                                                        }}
                                                      >
                                                        Date of administration
                                                        unknown
                                                      </span>
                                                    </label>
                                                  </div>
                                                  <ErrorField
                                                    show={
                                                      data.tociliAdmsDateEmpty_linvoC
                                                    }
                                                    message={
                                                      ELCharacteristics_MSGS.Q391CDateEmpty
                                                    }
                                                    style={{
                                                      paddingLeft: "0px",
                                                    }}
                                                  />
                                                  <ErrorField
                                                    show={
                                                      data.tociliAdmsDateInvalid_linvoC
                                                    }
                                                    message={
                                                      CommonError_Msgs.InvalidDate
                                                    }
                                                    style={{
                                                      paddingLeft: "0px",
                                                    }}
                                                  />
                                                  <ErrorField
                                                    show={
                                                      data.tociliAdmsDateRangeInvalid_linvoC
                                                    }
                                                    message={
                                                      linvoInitiationDate
                                                        ? `Please enter a date between ${dayjs(
                                                            linvoInitiationDate,
                                                          )
                                                            .subtract(7, "day")
                                                            .format(
                                                              "MM/DD/YYYY",
                                                            )} and ${dayjs(
                                                            linvoInitiationDate,
                                                          )
                                                            .add(14, "day")
                                                            .format(
                                                              "MM/DD/YYYY",
                                                            )}`
                                                        : ELCharacteristics_MSGS.Q391CDateInvalid
                                                    }
                                                    style={{
                                                      paddingLeft: "0px",
                                                    }}
                                                  />
                                                </div>
                                              </td>
                                              <td
                                                style={{
                                                  border: "1px solid black",
                                                  padding: "8px",
                                                  verticalAlign: "top",
                                                }}
                                              >
                                                <div className="sub-question">
                                                  <div
                                                    className="option-without-input"
                                                    style={{ display: "flex" }}
                                                  >
                                                    <input
                                                      type="radio"
                                                      id={idx}
                                                      name={
                                                        "tociliDoseRd_linvoC" +
                                                        idx
                                                      }
                                                      value={1}
                                                      checked={
                                                        data.tociliDoseRd_linvoC ===
                                                        1
                                                      }
                                                      onChange={
                                                        HandleFieldChange
                                                      }
                                                    />
                                                    <span
                                                      className="radio-text-padding"
                                                      style={{
                                                        paddingLeft: "10px",
                                                        marginLeft: "-5px",
                                                      }}
                                                    >
                                                      <input
                                                        type="text"
                                                        className="answer-textbox"
                                                        style={{
                                                          width: "80px",
                                                          border: "none",
                                                          borderBottom:
                                                            "1px solid #000",
                                                          borderRadius: 0,
                                                          outline: "none",
                                                          backgroundColor:
                                                            "transparent",
                                                        }}
                                                        id={idx}
                                                        name={
                                                          "tociliDoseMgkg_linvoC" +
                                                          idx
                                                        }
                                                        value={
                                                          data.tociliDoseMgkg_linvoC
                                                        }
                                                        onChange={
                                                          HandleFieldChange
                                                        }
                                                        onKeyDown={
                                                          AllowNumericWithDecimal
                                                        }
                                                      />{" "}
                                                      mg/kg
                                                    </span>
                                                  </div>
                                                  <div>
                                                    <div
                                                      style={{
                                                        display: "flex",
                                                        alignItems:
                                                          "flex-start",
                                                      }}
                                                    >
                                                      <input
                                                        type="checkbox"
                                                        style={{
                                                          marginTop: "6px",
                                                        }}
                                                        id={idx}
                                                        name={
                                                          "tociliDoseOtherToggle_linvoC" +
                                                          idx
                                                        }
                                                        checked={
                                                          data.tociliDoseRd_linvoC ===
                                                          2
                                                        }
                                                        onChange={
                                                          HandleFieldChange
                                                        }
                                                      />
                                                      <span className="radio-text-padding">
                                                        Other, please specify:
                                                      </span>
                                                    </div>
                                                    <textarea
                                                      className="input-dash"
                                                      id={idx}
                                                      name={
                                                        "tociliDoseOther_linvoC" +
                                                        idx
                                                      }
                                                      value={
                                                        data.tociliDoseOther_linvoC
                                                      }
                                                      onChange={
                                                        HandleFieldChange
                                                      }
                                                      placeholder="Please specify"
                                                    />
                                                  </div>
                                                  <div
                                                    className="option-without-input"
                                                    style={{ display: "flex" }}
                                                  >
                                                    <label className="option-fbox">
                                                      <input
                                                        type="radio"
                                                        id={idx}
                                                        name={
                                                          "tociliDoseRd_linvoC" +
                                                          idx
                                                        }
                                                        value={99}
                                                        checked={
                                                          data.tociliDoseRd_linvoC ===
                                                          99
                                                        }
                                                        onChange={
                                                          HandleFieldChange
                                                        }
                                                      />
                                                      <span
                                                        style={{
                                                          paddingLeft: "10px",
                                                          marginLeft: "-5px",
                                                        }}
                                                      >
                                                        Unknown
                                                      </span>
                                                    </label>
                                                  </div>
                                                  <ErrorField
                                                    show={
                                                      data.tociliDoseEmptyHard_linvoC
                                                    }
                                                    message={
                                                      ELCharacteristics_MSGS.Q391CDoseEmptyHard
                                                    }
                                                    style={{
                                                      paddingLeft: "0px",
                                                    }}
                                                  />
                                                  <ErrorField
                                                    show={
                                                      data.tociliDoseMgkgEmptyHard_linvoC
                                                    }
                                                    message={
                                                      ELCharacteristics_MSGS.Q391CDoseRangeInvalid
                                                    }
                                                    style={{
                                                      paddingLeft: "0px",
                                                    }}
                                                  />
                                                  <ErrorField
                                                    show={
                                                      data.tociliDoseOtherEmptyHard_linvoC
                                                    }
                                                    message={
                                                      ELCharacteristics_MSGS.Q391CDoseOtherEmptyHard
                                                    }
                                                    style={{
                                                      paddingLeft: "0px",
                                                    }}
                                                  />
                                                </div>
                                              </td>
                                              <td
                                                style={{
                                                  border: "1px solid black",
                                                  padding: "8px",
                                                  verticalAlign: "top",
                                                }}
                                              >
                                                <div className="sub-question">
                                                  <div
                                                    style={{
                                                      display: "flex",
                                                      alignItems: "flex-start",
                                                    }}
                                                    onClick={(e) => {
                                                      if (
                                                        e.target.tagName ===
                                                        "INPUT"
                                                      )
                                                        return;
                                                      HandleFieldChange({
                                                        target: {
                                                          name:
                                                            "tociliReasonPretratment_linvoC" +
                                                            idx,
                                                          id: `${idx}`,
                                                        },
                                                      });
                                                    }}
                                                  >
                                                    <input
                                                      type="checkbox"
                                                      style={{
                                                        marginTop: "6px",
                                                      }}
                                                      id={idx}
                                                      name={
                                                        "tociliReasonPretratment_linvoC" +
                                                        idx
                                                      }
                                                      checked={
                                                        data.tociliReasonPretratment_linvoC
                                                      }
                                                      onChange={
                                                        HandleFieldChange
                                                      }
                                                    />
                                                    <span className="radio-text-padding">
                                                      As a pre-treatment per
                                                      label recommendation
                                                    </span>
                                                  </div>
                                                  <div
                                                    style={{
                                                      display: "flex",
                                                      alignItems: "flex-start",
                                                    }}
                                                    onClick={(e) => {
                                                      if (
                                                        e.target.tagName ===
                                                        "INPUT"
                                                      )
                                                        return;
                                                      HandleFieldChange({
                                                        target: {
                                                          name:
                                                            "tociliReasonPrimarycrs_linvoC" +
                                                            idx,
                                                          id: `${idx}`,
                                                        },
                                                      });
                                                    }}
                                                  >
                                                    <input
                                                      type="checkbox"
                                                      style={{
                                                        marginTop: "6px",
                                                      }}
                                                      id={idx}
                                                      name={
                                                        "tociliReasonPrimarycrs_linvoC" +
                                                        idx
                                                      }
                                                      checked={
                                                        data.tociliReasonPrimarycrs_linvoC
                                                      }
                                                      onChange={
                                                        HandleFieldChange
                                                      }
                                                    />
                                                    <span className="radio-text-padding">
                                                      Primary prophylactic for
                                                      cytokine release syndrome
                                                      (CRS)
                                                    </span>
                                                  </div>
                                                  <div
                                                    style={{
                                                      display: "flex",
                                                      alignItems: "flex-start",
                                                    }}
                                                    onClick={(e) => {
                                                      if (
                                                        e.target.tagName ===
                                                        "INPUT"
                                                      )
                                                        return;
                                                      HandleFieldChange({
                                                        target: {
                                                          name:
                                                            "tociliReasonSecondarycrs_linvoC" +
                                                            idx,
                                                          id: `${idx}`,
                                                        },
                                                      });
                                                    }}
                                                  >
                                                    <input
                                                      type="checkbox"
                                                      style={{
                                                        marginTop: "6px",
                                                      }}
                                                      id={idx}
                                                      name={
                                                        "tociliReasonSecondarycrs_linvoC" +
                                                        idx
                                                      }
                                                      checked={
                                                        data.tociliReasonSecondarycrs_linvoC
                                                      }
                                                      onChange={
                                                        HandleFieldChange
                                                      }
                                                    />
                                                    <span className="radio-text-padding">
                                                      Secondary prophylactic for
                                                      CRS
                                                    </span>
                                                  </div>
                                                  <div
                                                    style={{
                                                      display: "flex",
                                                      alignItems: "flex-start",
                                                    }}
                                                    onClick={(e) => {
                                                      if (
                                                        e.target.tagName ===
                                                        "INPUT"
                                                      )
                                                        return;
                                                      HandleFieldChange({
                                                        target: {
                                                          name:
                                                            "tociliReasonSupportivecrs_linvoC" +
                                                            idx,
                                                          id: `${idx}`,
                                                        },
                                                      });
                                                    }}
                                                  >
                                                    <input
                                                      type="checkbox"
                                                      style={{
                                                        marginTop: "6px",
                                                      }}
                                                      id={idx}
                                                      name={
                                                        "tociliReasonSupportivecrs_linvoC" +
                                                        idx
                                                      }
                                                      checked={
                                                        data.tociliReasonSupportivecrs_linvoC
                                                      }
                                                      onChange={
                                                        HandleFieldChange
                                                      }
                                                    />
                                                    <span className="radio-text-padding">
                                                      Treatment or supportive
                                                      care for CRS
                                                    </span>
                                                  </div>
                                                  <div
                                                    style={{
                                                      display: "flex",
                                                      alignItems: "flex-start",
                                                    }}
                                                    onClick={(e) => {
                                                      if (
                                                        e.target.tagName ===
                                                        "INPUT"
                                                      )
                                                        return;
                                                      HandleFieldChange({
                                                        target: {
                                                          name:
                                                            "tociliReasonPrimaryneuro_linvoC" +
                                                            idx,
                                                          id: `${idx}`,
                                                        },
                                                      });
                                                    }}
                                                  >
                                                    <input
                                                      type="checkbox"
                                                      style={{
                                                        marginTop: "6px",
                                                      }}
                                                      id={idx}
                                                      name={
                                                        "tociliReasonPrimaryneuro_linvoC" +
                                                        idx
                                                      }
                                                      checked={
                                                        data.tociliReasonPrimaryneuro_linvoC
                                                      }
                                                      onChange={
                                                        HandleFieldChange
                                                      }
                                                    />
                                                    <span className="radio-text-padding">
                                                      Primary prophylactic for
                                                      neurologic events
                                                    </span>
                                                  </div>
                                                  <div
                                                    style={{
                                                      display: "flex",
                                                      alignItems: "flex-start",
                                                    }}
                                                    onClick={(e) => {
                                                      if (
                                                        e.target.tagName ===
                                                        "INPUT"
                                                      )
                                                        return;
                                                      HandleFieldChange({
                                                        target: {
                                                          name:
                                                            "tociliReasonSecondaryneuro_linvoC" +
                                                            idx,
                                                          id: `${idx}`,
                                                        },
                                                      });
                                                    }}
                                                  >
                                                    <input
                                                      type="checkbox"
                                                      style={{
                                                        marginTop: "6px",
                                                      }}
                                                      id={idx}
                                                      name={
                                                        "tociliReasonSecondaryneuro_linvoC" +
                                                        idx
                                                      }
                                                      checked={
                                                        data.tociliReasonSecondaryneuro_linvoC
                                                      }
                                                      onChange={
                                                        HandleFieldChange
                                                      }
                                                    />
                                                    <span className="radio-text-padding">
                                                      Secondary prophylactic for
                                                      neurologic events
                                                    </span>
                                                  </div>
                                                  <div
                                                    style={{
                                                      display: "flex",
                                                      alignItems: "flex-start",
                                                    }}
                                                    onClick={(e) => {
                                                      if (
                                                        e.target.tagName ===
                                                        "INPUT"
                                                      )
                                                        return;
                                                      HandleFieldChange({
                                                        target: {
                                                          name:
                                                            "tociliReasonSupportiveneuro_linvoC" +
                                                            idx,
                                                          id: `${idx}`,
                                                        },
                                                      });
                                                    }}
                                                  >
                                                    <input
                                                      type="checkbox"
                                                      style={{
                                                        marginTop: "6px",
                                                      }}
                                                      id={idx}
                                                      name={
                                                        "tociliReasonSupportiveneuro_linvoC" +
                                                        idx
                                                      }
                                                      checked={
                                                        data.tociliReasonSupportiveneuro_linvoC
                                                      }
                                                      onChange={
                                                        HandleFieldChange
                                                      }
                                                    />
                                                    <span className="radio-text-padding">
                                                      Treatment or supportive
                                                      care for neurologic events
                                                    </span>
                                                  </div>
                                                  <div className="">
                                                    <label className="checkbox-option">
                                                      <input
                                                        type="radio"
                                                        id={idx}
                                                        name={
                                                          "tociliReasonUnk_linvoC" +
                                                          idx
                                                        }
                                                        checked={
                                                          data.tociliReasonUnk_linvoC
                                                        }
                                                        onChange={
                                                          HandleFieldChange
                                                        }
                                                      />
                                                      <span className="radio-text-padding">
                                                        Unknown
                                                      </span>
                                                    </label>
                                                  </div>
                                                  <ErrorField
                                                    show={
                                                      data.Q391CReasonEmptyHard
                                                    }
                                                    message={
                                                      ELCharacteristics_MSGS.Q391CReasonEmptyHard
                                                    }
                                                    style={{
                                                      paddingLeft: "0px",
                                                    }}
                                                  />
                                                </div>
                                              </td>
                                            </tr>
                                          </tbody>
                                        </table>
                                      </div>
                                    </div>
                                  )}

                                  {data.steroids_linvoC && (
                                    <div
                                      style={{
                                        marginLeft: "28px",
                                        marginBlock: "6px",
                                        padding: "5px",
                                      }}
                                    >
                                      <div className="question question-weight">
                                        <span>Q39.2.C.</span>
                                        <span className="quest-text-pad">
                                          Summarize the reasons why steroids
                                          were used and dosing information for
                                          steroids used{" "}
                                          <b>
                                            within{" "}
                                            <u>
                                              7 days before to 14 days after
                                            </u>{" "}
                                            initiating treatment
                                          </b>{" "}
                                          with{" "}
                                          <span
                                            style={{
                                              color:
                                                PageDesignConstants.fontColor,
                                              fontWeight: "bolder",
                                            }}
                                          >
                                            LYNOZYFIC<sup>TM</sup>{" "}
                                            (linvoseltamab-gcpt)
                                          </span>{" "}
                                          (i.e.,{" "}
                                          <span
                                            style={{
                                              color:
                                                PageDesignConstants.fontColor,
                                            }}
                                          >
                                            [
                                            {formatDateMinus7Days(
                                              linvoInitiationDate,
                                            )}
                                            ]
                                          </span>{" "}
                                          through{" "}
                                          <span
                                            style={{
                                              color:
                                                PageDesignConstants.fontColor,
                                            }}
                                          >
                                            [
                                            {formatDatePlus14Days(
                                              linvoInitiationDate,
                                            )}
                                            ]
                                          </span>
                                          ). <i>Select all that apply.</i>
                                        </span>
                                      </div>
                                      <div
                                        className="dependent-section grid-overflow"
                                        style={{ backgroundColor: "#ffffff" }}
                                      >
                                        <table
                                          style={{
                                            border: "1px solid black",
                                            borderCollapse: "collapse",
                                            width: "100%",
                                            backgroundColor: "#ffffff",
                                          }}
                                        >
                                          <thead>
                                            <tr>
                                              <th
                                                style={{
                                                  border: "1px solid black",
                                                  padding: "6px",
                                                  textAlign: "center",
                                                  backgroundColor: "#ffffff",
                                                }}
                                              >
                                                Treatment
                                              </th>
                                              <th
                                                style={{
                                                  border: "1px solid black",
                                                  padding: "6px",
                                                  textAlign: "center",
                                                  backgroundColor: "#ffffff",
                                                }}
                                              >
                                                Medication type
                                              </th>
                                              <th
                                                style={{
                                                  border: "1px solid black",
                                                  padding: "6px",
                                                  textAlign: "center",
                                                  backgroundColor: "#ffffff",
                                                }}
                                              >
                                                Name and dose per day of the
                                                steroid(s) used
                                              </th>
                                              <th
                                                style={{
                                                  border: "1px solid black",
                                                  padding: "6px",
                                                  textAlign: "center",
                                                  backgroundColor: "#ffffff",
                                                }}
                                              >
                                                Reason for receiving medication
                                              </th>
                                            </tr>
                                          </thead>
                                          <tbody>
                                            <tr>
                                              <td
                                                style={{
                                                  border: "1px solid black",
                                                  padding: "8px",
                                                  verticalAlign: "top",
                                                }}
                                              >
                                                LYNOZYFIC<sup>TM</sup>{" "}
                                                (linvoseltamab-gcpt)
                                              </td>
                                              <td
                                                style={{
                                                  border: "1px solid black",
                                                  padding: "8px",
                                                  verticalAlign: "top",
                                                }}
                                              >
                                                Steroids
                                              </td>
                                              <td
                                                style={{
                                                  border: "1px solid black",
                                                  padding: "8px",
                                                  verticalAlign: "top",
                                                }}
                                              >
                                                <div className="sub-question">
                                                  <div
                                                    style={{
                                                      display: "flex",
                                                      alignItems: "flex-start",
                                                    }}
                                                    onClick={(e) => {
                                                      if (
                                                        e.target.tagName ===
                                                        "INPUT"
                                                      )
                                                        return;
                                                      HandleFieldChange({
                                                        target: {
                                                          name:
                                                            "betamethasone_linvoC" +
                                                            idx,
                                                          id: `${idx}`,
                                                        },
                                                      });
                                                    }}
                                                  >
                                                    <input
                                                      type="checkbox"
                                                      style={{
                                                        marginTop: "6px",
                                                      }}
                                                      id={idx}
                                                      name={
                                                        "betamethasone_linvoC" +
                                                        idx
                                                      }
                                                      checked={
                                                        data.betamethasone_linvoC
                                                      }
                                                      onChange={
                                                        HandleFieldChange
                                                      }
                                                    />
                                                    <span
                                                      style={{
                                                        paddingLeft: "15px",
                                                      }}
                                                    >
                                                      Betamethasone (dose:
                                                      <input
                                                        className="sub-input-dash-num"
                                                        style={{
                                                          border: "none",
                                                          borderBottom:
                                                            "1px solid #727272",
                                                          background:
                                                            "transparent",
                                                          outline: "none",
                                                        }}
                                                        id={idx}
                                                        name={
                                                          "betamethasone_linvoCNum" +
                                                          idx
                                                        }
                                                        type="text"
                                                        value={
                                                          data.betamethasone_linvoCNum
                                                        }
                                                        onKeyDown={
                                                          AllowOnlyNonZeroNumeric
                                                        }
                                                        onChange={
                                                          HandleFieldChange
                                                        }
                                                        maxLength={10}
                                                      />
                                                      mg/day)
                                                    </span>
                                                  </div>

                                                  <div
                                                    style={{
                                                      display: "flex",
                                                      alignItems: "flex-start",
                                                    }}
                                                    onClick={(e) => {
                                                      if (
                                                        e.target.tagName ===
                                                        "INPUT"
                                                      )
                                                        return;
                                                      HandleFieldChange({
                                                        target: {
                                                          name:
                                                            "dexamethasone_linvoC" +
                                                            idx,
                                                          id: `${idx}`,
                                                        },
                                                      });
                                                    }}
                                                  >
                                                    <input
                                                      type="checkbox"
                                                      style={{
                                                        marginTop: "6px",
                                                      }}
                                                      id={idx}
                                                      name={
                                                        "dexamethasone_linvoC" +
                                                        idx
                                                      }
                                                      checked={
                                                        data.dexamethasone_linvoC
                                                      }
                                                      onChange={
                                                        HandleFieldChange
                                                      }
                                                    />
                                                    <span
                                                      style={{
                                                        paddingLeft: "15px",
                                                      }}
                                                    >
                                                      Dexamethasone (dose:
                                                      <input
                                                        className="sub-input-dash-num"
                                                        style={{
                                                          border: "none",
                                                          borderBottom:
                                                            "1px solid #727272",
                                                          background:
                                                            "transparent",
                                                          outline: "none",
                                                        }}
                                                        id={idx}
                                                        name={
                                                          "dexamethasone_linvoCNum" +
                                                          idx
                                                        }
                                                        type="text"
                                                        value={
                                                          data.dexamethasone_linvoCNum
                                                        }
                                                        onKeyDown={
                                                          AllowOnlyNonZeroNumeric
                                                        }
                                                        onChange={
                                                          HandleFieldChange
                                                        }
                                                        maxLength={10}
                                                      />
                                                      mg/day)
                                                    </span>
                                                  </div>

                                                  <div
                                                    style={{
                                                      display: "flex",
                                                      alignItems: "flex-start",
                                                    }}
                                                    onClick={(e) => {
                                                      if (
                                                        e.target.tagName ===
                                                        "INPUT"
                                                      )
                                                        return;
                                                      HandleFieldChange({
                                                        target: {
                                                          name:
                                                            "hydrocortisone_linvoC" +
                                                            idx,
                                                          id: `${idx}`,
                                                        },
                                                      });
                                                    }}
                                                  >
                                                    <input
                                                      type="checkbox"
                                                      style={{
                                                        marginTop: "6px",
                                                      }}
                                                      id={idx}
                                                      name={
                                                        "hydrocortisone_linvoC" +
                                                        idx
                                                      }
                                                      checked={
                                                        data.hydrocortisone_linvoC
                                                      }
                                                      onChange={
                                                        HandleFieldChange
                                                      }
                                                    />
                                                    <span
                                                      style={{
                                                        paddingLeft: "15px",
                                                      }}
                                                    >
                                                      Hydrocortisone (oral or
                                                      intravenous only)
                                                      <br></br>(dose:
                                                      <input
                                                        className="sub-input-dash-num"
                                                        style={{
                                                          border: "none",
                                                          borderBottom:
                                                            "1px solid #727272",
                                                          background:
                                                            "transparent",
                                                          outline: "none",
                                                        }}
                                                        id={idx}
                                                        name={
                                                          "hydrocortisone_linvoCNum" +
                                                          idx
                                                        }
                                                        type="text"
                                                        value={
                                                          data.hydrocortisone_linvoCNum
                                                        }
                                                        onKeyDown={
                                                          AllowOnlyNonZeroNumeric
                                                        }
                                                        onChange={
                                                          HandleFieldChange
                                                        }
                                                        maxLength={10}
                                                      />
                                                      mg/day)
                                                    </span>
                                                  </div>

                                                  <div
                                                    style={{
                                                      display: "flex",
                                                      alignItems: "flex-start",
                                                    }}
                                                    onClick={(e) => {
                                                      if (
                                                        e.target.tagName ===
                                                        "INPUT"
                                                      )
                                                        return;
                                                      HandleFieldChange({
                                                        target: {
                                                          name:
                                                            "methylprednisolone_linvoC" +
                                                            idx,
                                                          id: `${idx}`,
                                                        },
                                                      });
                                                    }}
                                                  >
                                                    <input
                                                      type="checkbox"
                                                      style={{
                                                        marginTop: "6px",
                                                      }}
                                                      id={idx}
                                                      name={
                                                        "methylprednisolone_linvoC" +
                                                        idx
                                                      }
                                                      checked={
                                                        data.methylprednisolone_linvoC
                                                      }
                                                      onChange={
                                                        HandleFieldChange
                                                      }
                                                    />
                                                    <span
                                                      style={{
                                                        paddingLeft: "15px",
                                                      }}
                                                    >
                                                      Methylprednisolone (dose:
                                                      <input
                                                        className="sub-input-dash-num"
                                                        style={{
                                                          border: "none",
                                                          borderBottom:
                                                            "1px solid #727272",
                                                          background:
                                                            "transparent",
                                                          outline: "none",
                                                        }}
                                                        id={idx}
                                                        name={
                                                          "methylprednisolone_linvoCNum" +
                                                          idx
                                                        }
                                                        type="text"
                                                        value={
                                                          data.methylprednisolone_linvoCNum
                                                        }
                                                        onKeyDown={
                                                          AllowOnlyNonZeroNumeric
                                                        }
                                                        onChange={
                                                          HandleFieldChange
                                                        }
                                                        maxLength={10}
                                                      />
                                                      mg/day)
                                                    </span>
                                                  </div>

                                                  <div
                                                    style={{
                                                      display: "flex",
                                                      alignItems: "flex-start",
                                                    }}
                                                    onClick={(e) => {
                                                      if (
                                                        e.target.tagName ===
                                                        "INPUT"
                                                      )
                                                        return;
                                                      HandleFieldChange({
                                                        target: {
                                                          name:
                                                            "prednisolone_linvoC" +
                                                            idx,
                                                          id: `${idx}`,
                                                        },
                                                      });
                                                    }}
                                                  >
                                                    <input
                                                      type="checkbox"
                                                      style={{
                                                        marginTop: "6px",
                                                      }}
                                                      id={idx}
                                                      name={
                                                        "prednisolone_linvoC" +
                                                        idx
                                                      }
                                                      checked={
                                                        data.prednisolone_linvoC
                                                      }
                                                      onChange={
                                                        HandleFieldChange
                                                      }
                                                    />
                                                    <span
                                                      style={{
                                                        paddingLeft: "15px",
                                                      }}
                                                    >
                                                      Prednisolone (dose:
                                                      <input
                                                        className="sub-input-dash-num"
                                                        style={{
                                                          border: "none",
                                                          borderBottom:
                                                            "1px solid #727272",
                                                          background:
                                                            "transparent",
                                                          outline: "none",
                                                        }}
                                                        id={idx}
                                                        name={
                                                          "prednisolone_linvoCNum" +
                                                          idx
                                                        }
                                                        type="text"
                                                        value={
                                                          data.prednisolone_linvoCNum
                                                        }
                                                        onKeyDown={
                                                          AllowOnlyNonZeroNumeric
                                                        }
                                                        onChange={
                                                          HandleFieldChange
                                                        }
                                                        maxLength={10}
                                                      />
                                                      mg/day)
                                                    </span>
                                                  </div>

                                                  <div
                                                    style={{
                                                      display: "flex",
                                                      alignItems: "flex-start",
                                                    }}
                                                    onClick={(e) => {
                                                      if (
                                                        e.target.tagName ===
                                                        "INPUT"
                                                      )
                                                        return;
                                                      HandleFieldChange({
                                                        target: {
                                                          name:
                                                            "prednisone_linvoC" +
                                                            idx,
                                                          id: `${idx}`,
                                                        },
                                                      });
                                                    }}
                                                  >
                                                    <input
                                                      type="checkbox"
                                                      style={{
                                                        marginTop: "6px",
                                                      }}
                                                      id={idx}
                                                      name={
                                                        "prednisone_linvoC" +
                                                        idx
                                                      }
                                                      checked={
                                                        data.prednisone_linvoC
                                                      }
                                                      onChange={
                                                        HandleFieldChange
                                                      }
                                                    />
                                                    <span
                                                      style={{
                                                        paddingLeft: "15px",
                                                      }}
                                                    >
                                                      Prednisone (dose:
                                                      <input
                                                        className="sub-input-dash-num"
                                                        style={{
                                                          border: "none",
                                                          borderBottom:
                                                            "1px solid #727272",
                                                          background:
                                                            "transparent",
                                                          outline: "none",
                                                        }}
                                                        id={idx}
                                                        name={
                                                          "prednisone_linvoCNum" +
                                                          idx
                                                        }
                                                        type="text"
                                                        value={
                                                          data.prednisone_linvoCNum
                                                        }
                                                        onKeyDown={
                                                          AllowOnlyNonZeroNumeric
                                                        }
                                                        onChange={
                                                          HandleFieldChange
                                                        }
                                                        maxLength={10}
                                                      />
                                                      mg/day)
                                                    </span>
                                                  </div>

                                                  <div
                                                    style={{
                                                      display: "flex",
                                                      alignItems: "flex-start",
                                                    }}
                                                    onClick={(e) => {
                                                      if (
                                                        e.target.tagName ===
                                                        "INPUT"
                                                      )
                                                        return;
                                                      HandleFieldChange({
                                                        target: {
                                                          name:
                                                            "triamcinolone_linvoC" +
                                                            idx,
                                                          id: `${idx}`,
                                                        },
                                                      });
                                                    }}
                                                  >
                                                    <input
                                                      type="checkbox"
                                                      style={{
                                                        marginTop: "6px",
                                                      }}
                                                      id={idx}
                                                      name={
                                                        "triamcinolone_linvoC" +
                                                        idx
                                                      }
                                                      checked={
                                                        data.triamcinolone_linvoC
                                                      }
                                                      onChange={
                                                        HandleFieldChange
                                                      }
                                                    />
                                                    <span
                                                      style={{
                                                        paddingLeft: "15px",
                                                      }}
                                                    >
                                                      Triamcinolone (dose:
                                                      <input
                                                        className="sub-input-dash-num"
                                                        style={{
                                                          border: "none",
                                                          borderBottom:
                                                            "1px solid #727272",
                                                          background:
                                                            "transparent",
                                                          outline: "none",
                                                        }}
                                                        id={idx}
                                                        name={
                                                          "triamcinolone_linvoCNum" +
                                                          idx
                                                        }
                                                        type="text"
                                                        value={
                                                          data.triamcinolone_linvoCNum
                                                        }
                                                        onKeyDown={
                                                          AllowOnlyNonZeroNumeric
                                                        }
                                                        onChange={
                                                          HandleFieldChange
                                                        }
                                                        maxLength={10}
                                                      />
                                                      mg/day)
                                                    </span>
                                                  </div>

                                                  <div className="">
                                                    <label>
                                                      <input
                                                        type="radio"
                                                        id={idx}
                                                        name={
                                                          "dose_name_linvoC_unknown" +
                                                          idx
                                                        }
                                                        checked={
                                                          data.dose_name_linvoC_unknown
                                                        }
                                                        onChange={
                                                          HandleFieldChange
                                                        }
                                                      />
                                                      <span className="radio-text-padding">
                                                        Unknown
                                                      </span>
                                                    </label>
                                                  </div>

                                                  <ErrorField
                                                    show={
                                                      data.name_dose_steroidsHardEmpty_linvoC
                                                    }
                                                    message={
                                                      ELCharacteristics_MSGS.Q392CDoseEmptyHard
                                                    }
                                                    style={{
                                                      paddingLeft: "0px",
                                                    }}
                                                  />

                                                  <ErrorField
                                                    show={
                                                      data.steroids_linvoC_txtEmpty
                                                    }
                                                    message={
                                                      ELCharacteristics_MSGS.Q392CDoseTxtEmptyHard
                                                    }
                                                    style={{
                                                      paddingLeft: "0px",
                                                    }}
                                                  />

                                                  <ErrorField
                                                    show={
                                                      data.steroidsDoseRangeInvalid_linvoC
                                                    }
                                                    message={
                                                      ELCharacteristics_MSGS.Q392CDoseRangeInvalid
                                                    }
                                                    style={{
                                                      paddingLeft: "0px",
                                                    }}
                                                  />
                                                </div>
                                              </td>
                                              <td
                                                style={{
                                                  border: "1px solid black",
                                                  padding: "8px",
                                                  verticalAlign: "top",
                                                }}
                                              >
                                                <div className="sub-question">
                                                  <div
                                                    style={{
                                                      display: "flex",
                                                      alignItems: "flex-start",
                                                    }}
                                                    onClick={(e) => {
                                                      if (
                                                        e.target.tagName ===
                                                        "INPUT"
                                                      )
                                                        return;
                                                      HandleFieldChange({
                                                        target: {
                                                          name:
                                                            "steroidReasonPretratment_linvoC" +
                                                            idx,
                                                          id: `${idx}`,
                                                        },
                                                      });
                                                    }}
                                                  >
                                                    <input
                                                      type="checkbox"
                                                      style={{
                                                        marginTop: "6px",
                                                      }}
                                                      id={idx}
                                                      name={
                                                        "steroidReasonPretratment_linvoC" +
                                                        idx
                                                      }
                                                      checked={
                                                        data.steroidReasonPretratment_linvoC
                                                      }
                                                      onChange={
                                                        HandleFieldChange
                                                      }
                                                    />
                                                    <span className="radio-text-padding">
                                                      As a pre-treatment per
                                                      label recommendation
                                                    </span>
                                                  </div>
                                                  <div
                                                    style={{
                                                      display: "flex",
                                                      alignItems: "flex-start",
                                                    }}
                                                    onClick={(e) => {
                                                      if (
                                                        e.target.tagName ===
                                                        "INPUT"
                                                      )
                                                        return;
                                                      HandleFieldChange({
                                                        target: {
                                                          name:
                                                            "steroidReasonPrimarycrs_linvoC" +
                                                            idx,
                                                          id: `${idx}`,
                                                        },
                                                      });
                                                    }}
                                                  >
                                                    <input
                                                      type="checkbox"
                                                      style={{
                                                        marginTop: "6px",
                                                      }}
                                                      id={idx}
                                                      name={
                                                        "steroidReasonPrimarycrs_linvoC" +
                                                        idx
                                                      }
                                                      checked={
                                                        data.steroidReasonPrimarycrs_linvoC
                                                      }
                                                      onChange={
                                                        HandleFieldChange
                                                      }
                                                    />
                                                    <span className="radio-text-padding">
                                                      Primary prophylactic for
                                                      cytokine release syndrome
                                                      (CRS)
                                                    </span>
                                                  </div>
                                                  <div
                                                    style={{
                                                      display: "flex",
                                                      alignItems: "flex-start",
                                                    }}
                                                    onClick={(e) => {
                                                      if (
                                                        e.target.tagName ===
                                                        "INPUT"
                                                      )
                                                        return;
                                                      HandleFieldChange({
                                                        target: {
                                                          name:
                                                            "steroidReasonSecondarycrs_linvoC" +
                                                            idx,
                                                          id: `${idx}`,
                                                        },
                                                      });
                                                    }}
                                                  >
                                                    <input
                                                      type="checkbox"
                                                      style={{
                                                        marginTop: "6px",
                                                      }}
                                                      id={idx}
                                                      name={
                                                        "steroidReasonSecondarycrs_linvoC" +
                                                        idx
                                                      }
                                                      checked={
                                                        data.steroidReasonSecondarycrs_linvoC
                                                      }
                                                      onChange={
                                                        HandleFieldChange
                                                      }
                                                    />
                                                    <span className="radio-text-padding">
                                                      Secondary prophylactic for
                                                      CRS
                                                    </span>
                                                  </div>
                                                  <div
                                                    style={{
                                                      display: "flex",
                                                      alignItems: "flex-start",
                                                    }}
                                                    onClick={(e) => {
                                                      if (
                                                        e.target.tagName ===
                                                        "INPUT"
                                                      )
                                                        return;
                                                      HandleFieldChange({
                                                        target: {
                                                          name:
                                                            "steroidReasonSupportivecrs_linvoC" +
                                                            idx,
                                                          id: `${idx}`,
                                                        },
                                                      });
                                                    }}
                                                  >
                                                    <input
                                                      type="checkbox"
                                                      style={{
                                                        marginTop: "6px",
                                                      }}
                                                      id={idx}
                                                      name={
                                                        "steroidReasonSupportivecrs_linvoC" +
                                                        idx
                                                      }
                                                      checked={
                                                        data.steroidReasonSupportivecrs_linvoC
                                                      }
                                                      onChange={
                                                        HandleFieldChange
                                                      }
                                                    />
                                                    <span className="radio-text-padding">
                                                      Treatment or supportive
                                                      care for CRS
                                                    </span>
                                                  </div>
                                                  <div
                                                    style={{
                                                      display: "flex",
                                                      alignItems: "flex-start",
                                                    }}
                                                    onClick={(e) => {
                                                      if (
                                                        e.target.tagName ===
                                                        "INPUT"
                                                      )
                                                        return;
                                                      HandleFieldChange({
                                                        target: {
                                                          name:
                                                            "steroidReasonPrimaryneuro_linvoC" +
                                                            idx,
                                                          id: `${idx}`,
                                                        },
                                                      });
                                                    }}
                                                  >
                                                    <input
                                                      type="checkbox"
                                                      style={{
                                                        marginTop: "6px",
                                                      }}
                                                      id={idx}
                                                      name={
                                                        "steroidReasonPrimaryneuro_linvoC" +
                                                        idx
                                                      }
                                                      checked={
                                                        data.steroidReasonPrimaryneuro_linvoC
                                                      }
                                                      onChange={
                                                        HandleFieldChange
                                                      }
                                                    />
                                                    <span className="radio-text-padding">
                                                      Primary prophylactic for
                                                      neurologic events
                                                    </span>
                                                  </div>
                                                  <div
                                                    style={{
                                                      display: "flex",
                                                      alignItems: "flex-start",
                                                    }}
                                                    onClick={(e) => {
                                                      if (
                                                        e.target.tagName ===
                                                        "INPUT"
                                                      )
                                                        return;
                                                      HandleFieldChange({
                                                        target: {
                                                          name:
                                                            "steroidReasonSecondaryneuro_linvoC" +
                                                            idx,
                                                          id: `${idx}`,
                                                        },
                                                      });
                                                    }}
                                                  >
                                                    <input
                                                      type="checkbox"
                                                      style={{
                                                        marginTop: "6px",
                                                      }}
                                                      id={idx}
                                                      name={
                                                        "steroidReasonSecondaryneuro_linvoC" +
                                                        idx
                                                      }
                                                      checked={
                                                        data.steroidReasonSecondaryneuro_linvoC
                                                      }
                                                      onChange={
                                                        HandleFieldChange
                                                      }
                                                    />
                                                    <span className="radio-text-padding">
                                                      Secondary prophylactic for
                                                      neurologic events
                                                    </span>
                                                  </div>
                                                  <div
                                                    style={{
                                                      display: "flex",
                                                      alignItems: "flex-start",
                                                    }}
                                                    onClick={(e) => {
                                                      if (
                                                        e.target.tagName ===
                                                        "INPUT"
                                                      )
                                                        return;
                                                      HandleFieldChange({
                                                        target: {
                                                          name:
                                                            "steroidReasonSupportiveneuro_linvoC" +
                                                            idx,
                                                          id: `${idx}`,
                                                        },
                                                      });
                                                    }}
                                                  >
                                                    <input
                                                      type="checkbox"
                                                      style={{
                                                        marginTop: "6px",
                                                      }}
                                                      id={idx}
                                                      name={
                                                        "steroidReasonSupportiveneuro_linvoC" +
                                                        idx
                                                      }
                                                      checked={
                                                        data.steroidReasonSupportiveneuro_linvoC
                                                      }
                                                      onChange={
                                                        HandleFieldChange
                                                      }
                                                    />
                                                    <span className="radio-text-padding">
                                                      Treatment or supportive
                                                      care for neurologic events
                                                    </span>
                                                  </div>
                                                  <div className="">
                                                    <label className="checkbox-option">
                                                      <input
                                                        type="radio"
                                                        id={idx}
                                                        name={
                                                          "steroidReasonUnk_linvoC" +
                                                          idx
                                                        }
                                                        checked={
                                                          data.steroidReasonUnk_linvoC
                                                        }
                                                        onChange={
                                                          HandleFieldChange
                                                        }
                                                      />
                                                      <span className="radio-text-padding">
                                                        Unknown
                                                      </span>
                                                    </label>
                                                  </div>
                                                  <ErrorField
                                                    show={
                                                      data.Q392CReasonEmptyHard
                                                    }
                                                    message={
                                                      ELCharacteristics_MSGS.Q392CReasonEmptyHard
                                                    }
                                                    style={{
                                                      paddingLeft: "0px",
                                                    }}
                                                  />
                                                </div>
                                              </td>
                                            </tr>
                                          </tbody>
                                        </table>
                                      </div>
                                    </div>
                                  )}

                                  {/* 39.3.C */}
                                  {data.granulocyte_linvoC && (
                                    <div
                                      style={{
                                        marginLeft: "28px",
                                        marginBlock: "6px",
                                        padding: "5px",
                                      }}
                                    >
                                      <div className="question question-weight">
                                        <span>Q39.3.C.</span>
                                        <span className="quest-text-pad">
                                          Why was G-CSF used{" "}
                                          <b>
                                            within{" "}
                                            <u>
                                              7 days before to 14 days after
                                            </u>{" "}
                                            initiating treatment
                                          </b>{" "}
                                          with{" "}
                                          <span
                                            style={{
                                              color:
                                                PageDesignConstants.fontColor,
                                              fontWeight: "bolder",
                                            }}
                                          >
                                            LYNOZYFIC<sup>TM</sup>{" "}
                                            (linvoseltamab-gcpt)
                                          </span>{" "}
                                          (i.e.,{" "}
                                          <span
                                            style={{
                                              color:
                                                PageDesignConstants.fontColor,
                                            }}
                                          >
                                            [
                                            {formatDateMinus7Days(
                                              linvoInitiationDate,
                                            )}
                                            ]
                                          </span>{" "}
                                          through{" "}
                                          <span
                                            style={{
                                              color:
                                                PageDesignConstants.fontColor,
                                            }}
                                          >
                                            [
                                            {formatDatePlus14Days(
                                              linvoInitiationDate,
                                            )}
                                            ]
                                          </span>
                                          ). <i>Select all that apply.</i>
                                        </span>
                                      </div>

                                      <div
                                        className="dependent-sub-section"
                                        style={{ backgroundColor: "#ffffff" }}
                                      >
                                        <div className="question-bot-sapce">
                                          <div className="sub-question grid-overflow">
                                            <table
                                              style={{
                                                border: "1px solid black",
                                                borderCollapse: "collapse",
                                                width: "100%",
                                              }}
                                            >
                                              <thead>
                                                <tr>
                                                  <th
                                                    style={{
                                                      border: "1px solid black",
                                                      padding: "8px",
                                                      textAlign: "center",
                                                      width: "5%",
                                                    }}
                                                  >
                                                    Treatment
                                                  </th>
                                                  <th
                                                    style={{
                                                      border: "1px solid black",
                                                      padding: "8px",
                                                      textAlign: "center",
                                                      width: "5%",
                                                    }}
                                                  >
                                                    Medication
                                                  </th>
                                                  <th
                                                    style={{
                                                      border: "1px solid black",
                                                      padding: "8px",
                                                      textAlign: "center",
                                                      width: "50%",
                                                    }}
                                                  >
                                                    Reason for receiving
                                                    medication
                                                  </th>
                                                </tr>
                                              </thead>

                                              <tbody>
                                                <tr>
                                                  <td
                                                    style={{
                                                      border: "1px solid black",
                                                      padding: "8px",
                                                    }}
                                                  >
                                                    <span className="quest-text-pad">
                                                      LYNOZYFIC<sup>TM</sup>{" "}
                                                      (linvoseltamab-gcpt)
                                                    </span>
                                                  </td>

                                                  <td
                                                    style={{
                                                      border: "1px solid black",
                                                      padding: "8px",
                                                    }}
                                                  >
                                                    <div
                                                      className="option-without-input"
                                                      style={{
                                                        display: "flex",
                                                        alignItems:
                                                          "flex-start",
                                                      }}
                                                    >
                                                      <label>
                                                        <span
                                                          className="radio-text-padding"
                                                          style={{
                                                            paddingLeft: "10px",
                                                          }}
                                                        >
                                                          Granulocyte
                                                          colony-stimulating
                                                          factor (G-CSF)
                                                        </span>
                                                      </label>
                                                    </div>
                                                  </td>

                                                  <td
                                                    style={{
                                                      border: "1px solid black",
                                                      padding: "8px",
                                                    }}
                                                  >
                                                    <div>
                                                      <div className="">
                                                        <label>
                                                          <input
                                                            type="checkbox"
                                                            id={idx}
                                                            name={
                                                              "prophylactic_use_linvoC" +
                                                              idx
                                                            }
                                                            checked={
                                                              data.prophylactic_use_linvoC
                                                            }
                                                            onChange={
                                                              HandleFieldChange
                                                            }
                                                          />
                                                          <span className="radio-text-padding">
                                                            Primary Prophylactic
                                                            use
                                                          </span>
                                                        </label>
                                                      </div>
                                                      <div className="">
                                                        <label>
                                                          <input
                                                            type="checkbox"
                                                            id={idx}
                                                            name={
                                                              "secondary_prophylactic_use_linvoC" +
                                                              idx
                                                            }
                                                            checked={
                                                              data.secondary_prophylactic_use_linvoC
                                                            }
                                                            onChange={
                                                              HandleFieldChange
                                                            }
                                                          />
                                                          <span className="radio-text-padding">
                                                            Secondary
                                                            Prophylactic use
                                                          </span>
                                                        </label>
                                                      </div>

                                                      <div className="">
                                                        <label>
                                                          <input
                                                            type="checkbox"
                                                            id={idx}
                                                            name={
                                                              "oth_use_linvoC" +
                                                              idx
                                                            }
                                                            checked={
                                                              data.oth_use_linvoC
                                                            }
                                                            onChange={
                                                              HandleFieldChange
                                                            }
                                                          />
                                                          <span className="radio-text-padding">
                                                            Other use
                                                          </span>
                                                        </label>
                                                      </div>
                                                      <div className="">
                                                        <label>
                                                          <input
                                                            type="radio"
                                                            id={idx}
                                                            value={99}
                                                            name={
                                                              "unknown_linvoC" +
                                                              idx
                                                            }
                                                            checked={
                                                              data.unknown_linvoC ===
                                                              99
                                                            }
                                                            onChange={
                                                              HandleFieldChange
                                                            }
                                                          />
                                                          <span className="radio-text-padding">
                                                            Unknown
                                                          </span>
                                                        </label>
                                                      </div>

                                                      <ErrorField
                                                        show={
                                                          data.Q393CReasonEmptyHard
                                                        }
                                                        message={
                                                          ELCharacteristics_MSGS.Q393CReasonEmptyHard
                                                        }
                                                        style={{
                                                          paddingLeft: "0px",
                                                        }}
                                                      />
                                                    </div>
                                                  </td>
                                                </tr>
                                              </tbody>
                                            </table>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  )}
                                </React.Fragment>
                              ))}
                          </div>
                        )}
                        {/* Question 39 end */}
                      </>
                    )}

                    {/* 40 start */}
                    {!isNewEcrfC && (showQ40Elr || showQ40Linvo) && (
                      <div>
                        <div className="question question-weight">
                          <span>Q40.</span>
                          <span className="quest-text-pad">
                            Summarize information related to the usage of
                            immunoglobulin replacement therapy (IGRT):
                            intravenous immune globulin (IVIG) or
                            subcutaneous immunoglobulin (SCIG) during the
                            patient’s treatment with{" "}
                            <span
                              style={{
                                color: PageDesignConstants.fontColor,
                                fontWeight: "bolder",
                              }}
                            >
                              {showQ40Elr && (
                                <>
                                  ELREXFIO<sup>TM</sup> (elranatamab-bcmm)
                                </>
                              )}

                              {showQ40Elr && showQ40Linvo && " and "}

                              {showQ40Linvo && (
                                <>
                                  LYNOZYFIC<sup>TM</sup> (linvoseltamab-gcpt)
                                </>
                              )}
                            </span>
                            .
                          </span>
                        </div>

                        <div className="question-bot-sapce">
                          <div className="sub-question grid-overflow">
                            <table
                              style={{
                                border: "1px solid black",
                                borderCollapse: "collapse",
                                width: "100%",
                              }}
                            >
                              <thead>
                                <tr>
                                  <th
                                    style={{
                                      border: "1px solid black",
                                      padding: "8px",
                                      textAlign: "center",
                                    }}
                                  >
                                    Treatment
                                  </th>
                                  <th
                                    style={{
                                      border: "1px solid black",
                                      padding: "8px",
                                      textAlign: "center",
                                      width: "33%",
                                    }}
                                  >
                                    Intended dosing frequency
                                  </th>

                                  <th
                                    style={{
                                      border: "1px solid black",
                                      padding: "8px",
                                      textAlign: "center",
                                      width: "16%",
                                    }}
                                  >
                                    Last IgG level prior to most
                                    <br /> recent IGRT administration
                                  </th>

                                  <th
                                    style={{
                                      border: "1px solid black",
                                      padding: "8px",
                                      textAlign: "center",
                                      width: "15%",
                                    }}
                                  >
                                    Initiation date of IGRT
                                  </th>

                                  <th
                                    style={{
                                      border: "1px solid black",
                                      padding: "8px",
                                      textAlign: "center",
                                      width: "45%",
                                    }}
                                  >
                                    Reason for receiving medication Select all
                                    that apply.
                                  </th>
                                </tr>
                              </thead>

                              <tbody>
                                {showQ40Elr &&
                                  Q37ElrexDetails.length > 0 &&
                                  Q37ElrexDetails.map((data, idx) => (
                                    <tr key={idx}>
                                      <td
                                        style={{
                                          border: "1px solid black",
                                          padding: "8px",
                                        }}
                                      >
                                        <span className="quest-text-pad">
                                          ELREXFIO<sup>TM</sup>{" "}
                                          (elranatamab-bcmm)
                                        </span>
                                      </td>

                                      {/* Dosing freq column */}
                                      <td
                                        style={{
                                          border: "1px solid black",
                                          padding: "8px",
                                        }}
                                      >
                                        <div>
                                          <div
                                            className="option-without-input"
                                            style={{ display: "flex" }}
                                          >
                                            <label className="option-fbox">
                                              <input
                                                type="radio"
                                                id={idx}
                                                name={"Q40dosingfreqRd" + idx}
                                                value={1}
                                                checked={
                                                  data.Q40dosingfreqRd === 1
                                                }
                                                onChange={HandleFieldChange}
                                              />
                                              <span
                                                className="radio-text-padding"
                                                style={{
                                                  paddingLeft: "10px",
                                                  marginLeft: "-5px",
                                                }}
                                              >
                                                Patient never received IGRT
                                                during treatment
                                              </span>
                                            </label>
                                          </div>
                                          <div
                                            className="option-without-input"
                                            style={{ display: "flex" }}
                                          >
                                            <label className="option-fbox">
                                              <input
                                                type="radio"
                                                id={idx}
                                                name={"Q40dosingfreqRd" + idx}
                                                value={2}
                                                checked={
                                                  data.Q40dosingfreqRd === 2
                                                }
                                                onChange={HandleFieldChange}
                                              />
                                              <span
                                                className="radio-text-padding"
                                                style={{
                                                  paddingLeft: "10px",
                                                  marginLeft: "-5px",
                                                }}
                                              >
                                                Biweekly (every 2 weeks)
                                              </span>
                                            </label>
                                          </div>
                                          <div
                                            className="option-without-input"
                                            style={{
                                              display: "flex",
                                              alignItems: "flex-start",
                                            }}
                                          >
                                            <label className="option-fbox">
                                              <input
                                                type="radio"
                                                style={{ marginTop: "6px" }}
                                                id={idx}
                                                name={"Q40dosingfreqRd" + idx}
                                                value={3}
                                                checked={
                                                  data.Q40dosingfreqRd === 3
                                                }
                                                onChange={HandleFieldChange}
                                              />

                                              <span
                                                className="radio-text-padding"
                                                style={{
                                                  paddingLeft: "10px",
                                                  marginLeft: "-5px",
                                                }}
                                              >
                                                Monthly
                                              </span>
                                            </label>
                                          </div>

                                          <div
                                            className="option-without-input"
                                            style={{ display: "flex" }}
                                          >
                                            <label className="option-fbox">
                                              <input
                                                type="radio"
                                                id={idx}
                                                name={"Q40dosingfreqRd" + idx}
                                                value={4}
                                                checked={
                                                  data.Q40dosingfreqRd === 4
                                                }
                                                onChange={HandleFieldChange}
                                              />
                                              <span
                                                className="radio-text-padding"
                                                style={{
                                                  paddingLeft: "10px",
                                                  marginLeft: "-5px",
                                                }}
                                              >
                                                Bimonthly (every 2 months)
                                              </span>
                                            </label>
                                          </div>

                                          <div
                                            className="option-without-input"
                                            style={{ display: "flex" }}
                                          >
                                            <label className="option-fbox">
                                              <input
                                                type="radio"
                                                id={idx}
                                                name={"Q40dosingfreqRd" + idx}
                                                value={5}
                                                checked={
                                                  data.Q40dosingfreqRd === 5
                                                }
                                                onChange={HandleFieldChange}
                                              />

                                              <span
                                                style={{
                                                  marginLeft: "4px",
                                                  paddingRight: "15px",
                                                  marginBottom: "0px",
                                                  padding: "0",
                                                }}
                                              >
                                                Other frequency, please specify:
                                                <div
                                                  style={{
                                                    paddingLeft: "15px",
                                                    marginBottom: "0px",
                                                    padding: "0",
                                                  }}
                                                >
                                                  <textarea
                                                    name={
                                                      "dosing_freq_oth_txt" +
                                                      idx
                                                    }
                                                    className="input-dash"
                                                    type="text"
                                                    value={
                                                      data.dosing_freq_oth_txt
                                                    }
                                                    onChange={HandleFieldChange}
                                                    maxLength={300}
                                                    id={idx}
                                                  />
                                                </div>
                                              </span>
                                            </label>
                                          </div>

                                          <div
                                            className="option-without-input"
                                            style={{ display: "flex" }}
                                          >
                                            <label className="option-fbox">
                                              <input
                                                type="radio"
                                                id={idx}
                                                name={"Q40dosingfreqRd" + idx}
                                                value={99}
                                                checked={
                                                  data.Q40dosingfreqRd === 99
                                                }
                                                onChange={HandleFieldChange}
                                              />
                                              <span
                                                className="radio-text-padding"
                                                style={{
                                                  paddingLeft: "10px",
                                                  marginLeft: "-5px",
                                                }}
                                              >
                                                Unknown
                                              </span>
                                            </label>
                                          </div>

                                          <ErrorField
                                            show={data.Q40DosingFreqEmptyHard}
                                            message={
                                              ELCharacteristics_MSGS.Q40DosingFreqEmptyHard
                                            }
                                            style={{ paddingLeft: "0px" }}
                                          />

                                          <ErrorField
                                            show={
                                              data.Q40DosingFreqTxtEmptyHard
                                            }
                                            message={
                                              ELCharacteristics_MSGS.Q40DosingFreqTxtEmptyHard
                                            }
                                            style={{ paddingLeft: "0px" }}
                                          />
                                        </div>
                                      </td>

                                      {/* Last  IgG Level */}
                                      {data.Q40dosingfreqRd !== 1 ? (
                                        <td
                                          style={{
                                            border: "1px solid black",
                                            padding: "8px",
                                          }}
                                        >
                                          <div>
                                            <div
                                              className="option-without-input"
                                              style={{
                                                display: "flex",
                                                alignItems: "flex-start",
                                              }}
                                            >
                                              <label className="option-fbox">
                                                <input
                                                  type="radio"
                                                  style={{ marginTop: "6px" }}
                                                  id={idx}
                                                  name={"Q40IgILevelRd" + idx}
                                                  value={1}
                                                  checked={
                                                    data.Q40IgILevelRd === 1
                                                  }
                                                  onChange={HandleFieldChange}
                                                />
                                                <span
                                                  className="radio-text-padding"
                                                  style={{
                                                    paddingLeft: "10px",
                                                    marginLeft: "-5px",
                                                  }}
                                                >
                                                  {"<"} 400 mg/dL
                                                </span>
                                              </label>
                                            </div>

                                            <div
                                              className="option-without-input"
                                              style={{ display: "flex" }}
                                            >
                                              <label className="option-fbox">
                                                <input
                                                  type="radio"
                                                  id={idx}
                                                  name={"Q40IgILevelRd" + idx}
                                                  value={2}
                                                  checked={
                                                    data.Q40IgILevelRd === 2
                                                  }
                                                  onChange={HandleFieldChange}
                                                />
                                                <span
                                                  className="radio-text-padding"
                                                  style={{
                                                    paddingLeft: "10px",
                                                    marginLeft: "-5px",
                                                  }}
                                                >
                                                  400-500 mg/dL
                                                </span>
                                              </label>
                                            </div>

                                            <div
                                              className="option-without-input"
                                              style={{ display: "flex" }}
                                            >
                                              <label className="option-fbox">
                                                <input
                                                  type="radio"
                                                  id={idx}
                                                  name={"Q40IgILevelRd" + idx}
                                                  value={3}
                                                  checked={
                                                    data.Q40IgILevelRd === 3
                                                  }
                                                  onChange={HandleFieldChange}
                                                />
                                                <span
                                                  className="radio-text-padding"
                                                  style={{
                                                    paddingLeft: "10px",
                                                    marginLeft: "-5px",
                                                  }}
                                                >
                                                  {">"} 500 mg/dL
                                                </span>
                                              </label>
                                            </div>

                                            <div
                                              className="option-without-input"
                                              style={{ display: "flex" }}
                                            >
                                              <label className="option-fbox">
                                                <input
                                                  type="radio"
                                                  id={idx}
                                                  name={"Q40IgILevelRd" + idx}
                                                  value={99}
                                                  checked={
                                                    data.Q40IgILevelRd === 99
                                                  }
                                                  onChange={HandleFieldChange}
                                                />
                                                <span
                                                  className="radio-text-padding"
                                                  style={{
                                                    paddingLeft: "10px",
                                                    marginLeft: "-5px",
                                                  }}
                                                >
                                                  Unknown
                                                </span>
                                              </label>
                                            </div>

                                            <ErrorField
                                              show={data.Q40IgGEmptyHard}
                                              message={
                                                ELCharacteristics_MSGS.Q40IgGEmptyHard
                                              }
                                              style={{ paddingLeft: "0px" }}
                                            />
                                          </div>
                                        </td>
                                      ) : (
                                        <td
                                          style={{
                                            border: "1px solid black",
                                            textAlign: "center",
                                            padding: "8px",
                                          }}
                                        >
                                          <span
                                            style={{
                                              textAlign: "center",
                                              verticalAlign: "center",
                                            }}
                                            className="not-applicable"
                                          >
                                            <b>Not applicable</b>
                                          </span>
                                        </td>
                                      )}

                                      {/* IVIG */}
                                      {data.Q40dosingfreqRd !== 1 ? (
                                        <td
                                          style={{
                                            border: "1px solid black",
                                            padding: "8px",
                                          }}
                                        >
                                          <div>
                                            <div
                                              className="option-without-input"
                                              style={{ display: "flex" }}
                                            >
                                              <input
                                                type="radio"
                                                id={idx}
                                                name={"Q40init_dt_Rd" + idx}
                                                value={1}
                                                checked={
                                                  data.Q40init_dt_Rd === 1
                                                }
                                                onChange={HandleFieldChange}
                                              />
                                              <span
                                                className="radio-text-padding"
                                                style={{
                                                  paddingLeft: "10px",
                                                  marginLeft: "-5px",
                                                }}
                                              >
                                                <DatePickerComponent
                                                  ctrlId={"Q40init_dt-" + idx}
                                                  HandleDateChange={
                                                    HandleRepeaterDateChange
                                                  }
                                                  optionId={1}
                                                  minDate={dayjs("01-01-1920")}
                                                  maxDate={dayjs()}
                                                  locale="en" // current it is mm/dd/yyyy but for dd/mm/yyyy use "en-gb"
                                                  date={data.Q40init_dt}
                                                />
                                              </span>
                                            </div>

                                            <div
                                              className="option-without-input"
                                              style={{ display: "flex" }}
                                            >
                                              <label className="option-fbox">
                                                <input
                                                  type="radio"
                                                  id={idx}
                                                  name={"Q40init_dt_Rd" + idx}
                                                  value={99}
                                                  checked={
                                                    data.Q40init_dt_Rd === 99
                                                  }
                                                  onChange={HandleFieldChange}
                                                />
                                                <span
                                                  // className="radio-text-padding"
                                                  style={{
                                                    paddingLeft: "10px",
                                                    marginLeft: "-5px",
                                                  }}
                                                >
                                                  Date of initiation unknown
                                                </span>
                                              </label>
                                            </div>

                                            <ErrorField
                                              show={data.Q40Init_RdHard}
                                              message={
                                                ELCharacteristics_MSGS.Q40Init_RdHard
                                              }
                                              style={{ paddingLeft: "0px" }}
                                            />

                                            <ErrorField
                                              show={data.Q40Init_dtHard}
                                              message={
                                                ELCharacteristics_MSGS.Q40Init_dtHard
                                              }
                                              style={{ paddingLeft: "0px" }}
                                            />
                                            <ErrorField
                                              show={data.InvalidQ40init_dt}
                                              message={
                                                CommonError_Msgs.InvalidDate
                                              }
                                              style={{ paddingLeft: "0px" }}
                                            />
                                            <ErrorField
                                              show={data.init_dt_before_eof}
                                              message={
                                                ELCharacteristics_MSGS.Q40DateOutOfRange
                                              }
                                              style={{ paddingLeft: "0px" }}
                                            />
                                            <ErrorField
                                              show={
                                                data.init_dt_full_chain_invalid
                                              }
                                              message={
                                                ELCharacteristics_MSGS.Q40FullChainOutOfRange
                                              }
                                              style={{ paddingLeft: "0px" }}
                                            />
                                          </div>
                                        </td>
                                      ) : (
                                        <td
                                          style={{
                                            border: "1px solid black",
                                            //alignContent: "flex-start",
                                            textAlign: "center",
                                            padding: "8px",
                                            //textAlign: 'start'
                                          }}
                                        >
                                          <span
                                            style={{
                                              textAlign: "center",
                                              verticalAlign: "center",
                                            }}
                                            className="not-applicable"
                                          >
                                            <b>Not applicable</b>
                                          </span>
                                        </td>
                                      )}

                                      {/* Medication Reason */}
                                      {data.Q40dosingfreqRd !== 1 ? (
                                        <td
                                          style={{
                                            border: "1px solid black",
                                            padding: "8px",
                                          }}
                                        >
                                          <div>
                                            <div className="">
                                              <label
                                                className="option-fbox"
                                                style={{
                                                  alignItems: "baseline",
                                                }}
                                              >
                                                <input
                                                  type="checkbox"
                                                  id={idx}
                                                  name={"primaryProf" + idx}
                                                  checked={data.primaryProf}
                                                  onChange={HandleFieldChange}
                                                />
                                                <span
                                                  style={{
                                                    marginLeft: "6px",
                                                  }}
                                                >
                                                  Primary prophylactic for
                                                  infections
                                                </span>
                                              </label>
                                            </div>

                                            <div className="">
                                              <label
                                                className="option-fbox"
                                                style={{
                                                  alignItems: "baseline",
                                                }}
                                              >
                                                <input
                                                  type="checkbox"
                                                  id={idx}
                                                  name={"secondaryProf" + idx}
                                                  checked={data.secondaryProf}
                                                  onChange={HandleFieldChange}
                                                />
                                                <span
                                                  style={{
                                                    marginLeft: "6px",
                                                  }}
                                                >
                                                  Secondary prophylactic for
                                                  infections
                                                </span>
                                              </label>
                                            </div>

                                            <div className="">
                                              <label>
                                                <input
                                                  type="checkbox"
                                                  id={idx}
                                                  name={"otherReson" + idx}
                                                  checked={data.otherReson}
                                                  onChange={HandleFieldChange}
                                                />
                                                <span
                                                  style={{
                                                    marginLeft: "6px",
                                                  }}
                                                >
                                                  Other reason
                                                </span>
                                              </label>
                                            </div>

                                            <ErrorField
                                              show={data.Q40ReasonEmptyHard}
                                              message={
                                                ELCharacteristics_MSGS.Q40ReasonEmptyHard
                                              }
                                              style={{ paddingLeft: "0px" }}
                                            />
                                          </div>
                                        </td>
                                      ) : (
                                        <td
                                          style={{
                                            border: "1px solid black",
                                            //alignContent: "flex-start",
                                            textAlign: "center",
                                            padding: "8px",
                                            //textAlign: 'start'
                                          }}
                                        >
                                          <span
                                            style={{
                                              textAlign: "center",
                                              verticalAlign: "center",
                                            }}
                                            className="not-applicable"
                                          >
                                            <b>Not applicable</b>
                                          </span>
                                        </td>
                                      )}
                                    </tr>
                                  ))}
                                {showQ40Linvo &&
                                  Q37LinvoDetails.length > 0 &&
                                  Q37LinvoDetails.map((data, idx) => (
                                    <tr key={idx}>
                                      <td
                                        style={{
                                          border: "1px solid black",
                                          padding: "8px",
                                        }}
                                      >
                                        <span className="quest-text-pad">
                                          LYNOZYFIC<sup>TM</sup>{" "}
                                          (linvoquetamab-gcpt)
                                        </span>
                                      </td>
                                      {/* Dosing freq column */}
                                      <td
                                        style={{
                                          border: "1px solid black",
                                          padding: "8px",
                                        }}
                                      >
                                        <div>
                                          <div
                                            className="option-without-input"
                                            style={{ display: "flex" }}
                                          >
                                            <label className="option-fbox">
                                              <input
                                                type="radio"
                                                id={idx}
                                                name={
                                                  "q40DosingFreqRd_linvo" + idx
                                                }
                                                value={1}
                                                checked={
                                                  data.q40DosingFreqRd_linvo ===
                                                  1
                                                }
                                                onChange={HandleFieldChange}
                                              />
                                              <span
                                                className="radio-text-padding"
                                                style={{
                                                  paddingLeft: "10px",
                                                  marginLeft: "-5px",
                                                }}
                                              >
                                                Patient never received IGRT
                                                during treatment
                                              </span>
                                            </label>
                                          </div>
                                          <div
                                            className="option-without-input"
                                            style={{ display: "flex" }}
                                          >
                                            <label className="option-fbox">
                                              <input
                                                type="radio"
                                                id={idx}
                                                name={
                                                  "q40DosingFreqRd_linvo" + idx
                                                }
                                                value={2}
                                                checked={
                                                  data.q40DosingFreqRd_linvo ===
                                                  2
                                                }
                                                onChange={HandleFieldChange}
                                              />
                                              <span
                                                className="radio-text-padding"
                                                style={{
                                                  paddingLeft: "10px",
                                                  marginLeft: "-5px",
                                                }}
                                              >
                                                Biweekly (every 2 weeks)
                                              </span>
                                            </label>
                                          </div>
                                          <div
                                            className="option-without-input"
                                            style={{
                                              display: "flex",
                                              alignItems: "flex-start",
                                            }}
                                          >
                                            <label className="option-fbox">
                                              <input
                                                type="radio"
                                                style={{ marginTop: "6px" }}
                                                id={idx}
                                                name={
                                                  "q40DosingFreqRd_linvo" + idx
                                                }
                                                value={3}
                                                checked={
                                                  data.q40DosingFreqRd_linvo ===
                                                  3
                                                }
                                                onChange={HandleFieldChange}
                                              />

                                              <span
                                                className="radio-text-padding"
                                                style={{
                                                  paddingLeft: "10px",
                                                  marginLeft: "-5px",
                                                }}
                                              >
                                                Monthly
                                              </span>
                                            </label>
                                          </div>

                                          <div
                                            className="option-without-input"
                                            style={{ display: "flex" }}
                                          >
                                            <label className="option-fbox">
                                              <input
                                                type="radio"
                                                id={idx}
                                                name={
                                                  "q40DosingFreqRd_linvo" + idx
                                                }
                                                value={4}
                                                checked={
                                                  data.q40DosingFreqRd_linvo ===
                                                  4
                                                }
                                                onChange={HandleFieldChange}
                                              />
                                              <span
                                                className="radio-text-padding"
                                                style={{
                                                  paddingLeft: "10px",
                                                  marginLeft: "-5px",
                                                }}
                                              >
                                                Bimonthly (every 2 months)
                                              </span>
                                            </label>
                                          </div>

                                          <div
                                            className="option-without-input"
                                            style={{ display: "flex" }}
                                          >
                                            <label className="option-fbox">
                                              <input
                                                type="radio"
                                                id={idx}
                                                name={
                                                  "q40DosingFreqRd_linvo" + idx
                                                }
                                                value={5}
                                                checked={
                                                  data.q40DosingFreqRd_linvo ===
                                                  5
                                                }
                                                onChange={HandleFieldChange}
                                              />

                                              <span
                                                style={{
                                                  marginLeft: "4px",
                                                  paddingRight: "15px",
                                                  marginBottom: "0px",
                                                  padding: "0",
                                                }}
                                              >
                                                Other frequency, please specify:
                                                <div
                                                  style={{
                                                    paddingLeft: "15px",
                                                    marginBottom: "0px",
                                                    padding: "0",
                                                  }}
                                                >
                                                  <textarea
                                                    name={
                                                      "dosing_Freq_Oth_Txt_linvo" +
                                                      idx
                                                    }
                                                    className="input-dash"
                                                    type="text"
                                                    value={
                                                      data.dosing_Freq_Oth_Txt_linvo
                                                    }
                                                    onChange={HandleFieldChange}
                                                    maxLength={300}
                                                    id={idx}
                                                  />
                                                </div>
                                              </span>
                                            </label>
                                          </div>

                                          <div
                                            className="option-without-input"
                                            style={{ display: "flex" }}
                                          >
                                            <label className="option-fbox">
                                              <input
                                                type="radio"
                                                id={idx}
                                                name={
                                                  "q40DosingFreqRd_linvo" + idx
                                                }
                                                value={99}
                                                checked={
                                                  data.q40DosingFreqRd_linvo ===
                                                  99
                                                }
                                                onChange={HandleFieldChange}
                                              />
                                              <span
                                                className="radio-text-padding"
                                                style={{
                                                  paddingLeft: "10px",
                                                  marginLeft: "-5px",
                                                }}
                                              >
                                                Unknown
                                              </span>
                                            </label>
                                          </div>

                                          <ErrorField
                                            show={
                                              data.Q40LinvoDosingFreqEmptyHard
                                            }
                                            message={
                                              ELCharacteristics_MSGS.Q40DosingFreqEmptyHard
                                            }
                                            style={{ paddingLeft: "0px" }}
                                          />

                                          <ErrorField
                                            show={
                                              data.Q40LinvoDosingFreqTxtEmptyHard
                                            }
                                            message={
                                              ELCharacteristics_MSGS.Q40DosingFreqTxtEmptyHard
                                            }
                                            style={{ paddingLeft: "0px" }}
                                          />
                                        </div>
                                      </td>
                                      {/* Last  IgG Level */}
                                      {data.q40DosingFreqRd_linvo !== 1 ? (
                                        <td
                                          style={{
                                            border: "1px solid black",
                                            padding: "8px",
                                          }}
                                        >
                                          <div>
                                            <div
                                              className="option-without-input"
                                              style={{
                                                display: "flex",
                                                alignItems: "flex-start",
                                              }}
                                            >
                                              <label className="option-fbox">
                                                <input
                                                  type="radio"
                                                  style={{ marginTop: "6px" }}
                                                  id={idx}
                                                  name={
                                                    "q40IgILevelRd_linvo" + idx
                                                  }
                                                  value={1}
                                                  checked={
                                                    data.q40IgILevelRd_linvo ===
                                                    1
                                                  }
                                                  onChange={HandleFieldChange}
                                                />
                                                <span
                                                  className="radio-text-padding"
                                                  style={{
                                                    paddingLeft: "10px",
                                                    marginLeft: "-5px",
                                                  }}
                                                >
                                                  {"<"} 400 mg/dL
                                                </span>
                                              </label>
                                            </div>

                                            <div
                                              className="option-without-input"
                                              style={{ display: "flex" }}
                                            >
                                              <label className="option-fbox">
                                                <input
                                                  type="radio"
                                                  id={idx}
                                                  name={
                                                    "q40IgILevelRd_linvo" + idx
                                                  }
                                                  value={2}
                                                  checked={
                                                    data.q40IgILevelRd_linvo ===
                                                    2
                                                  }
                                                  onChange={HandleFieldChange}
                                                />
                                                <span
                                                  className="radio-text-padding"
                                                  style={{
                                                    paddingLeft: "10px",
                                                    marginLeft: "-5px",
                                                  }}
                                                >
                                                  400-500 mg/dL
                                                </span>
                                              </label>
                                            </div>

                                            <div
                                              className="option-without-input"
                                              style={{ display: "flex" }}
                                            >
                                              <label className="option-fbox">
                                                <input
                                                  type="radio"
                                                  id={idx}
                                                  name={
                                                    "q40IgILevelRd_linvo" + idx
                                                  }
                                                  value={3}
                                                  checked={
                                                    data.q40IgILevelRd_linvo ===
                                                    3
                                                  }
                                                  onChange={HandleFieldChange}
                                                />
                                                <span
                                                  className="radio-text-padding"
                                                  style={{
                                                    paddingLeft: "10px",
                                                    marginLeft: "-5px",
                                                  }}
                                                >
                                                  {">"} 500 mg/dL
                                                </span>
                                              </label>
                                            </div>

                                            <div
                                              className="option-without-input"
                                              style={{ display: "flex" }}
                                            >
                                              <label className="option-fbox">
                                                <input
                                                  type="radio"
                                                  id={idx}
                                                  name={
                                                    "q40IgILevelRd_linvo" + idx
                                                  }
                                                  value={99}
                                                  checked={
                                                    data.q40IgILevelRd_linvo ===
                                                    99
                                                  }
                                                  onChange={HandleFieldChange}
                                                />
                                                <span
                                                  className="radio-text-padding"
                                                  style={{
                                                    paddingLeft: "10px",
                                                    marginLeft: "-5px",
                                                  }}
                                                >
                                                  Unknown
                                                </span>
                                              </label>
                                            </div>

                                            <ErrorField
                                              show={data.Q40LinvoIgGEmptyHard}
                                              message={
                                                ELCharacteristics_MSGS.Q40IgGEmptyHard
                                              }
                                              style={{ paddingLeft: "0px" }}
                                            />
                                          </div>
                                        </td>
                                      ) : (
                                        <td
                                          style={{
                                            border: "1px solid black",
                                            textAlign: "center",
                                            padding: "8px",
                                          }}
                                        >
                                          <span
                                            style={{
                                              textAlign: "center",
                                              verticalAlign: "center",
                                            }}
                                            className="not-applicable"
                                          >
                                            <b>Not applicable</b>
                                          </span>
                                        </td>
                                      )}
                                      {/* IVIG */}
                                      {data.q40DosingFreqRd_linvo !== 1 ? (
                                        <td
                                          style={{
                                            border: "1px solid black",
                                            padding: "8px",
                                          }}
                                        >
                                          <div>
                                            <div
                                              className="option-without-input"
                                              style={{ display: "flex" }}
                                            >
                                              <input
                                                type="radio"
                                                id={idx}
                                                name={
                                                  "q40Init_Dt_Rd_linvo" + idx
                                                }
                                                value={1}
                                                checked={
                                                  data.q40Init_Dt_Rd_linvo === 1
                                                }
                                                onChange={HandleFieldChange}
                                              />
                                              <span
                                                className="radio-text-padding"
                                                style={{
                                                  paddingLeft: "10px",
                                                  marginLeft: "-5px",
                                                }}
                                              >
                                                <DatePickerComponent
                                                  ctrlId={
                                                    "q40Init_Dt_linvo-" + idx
                                                  }
                                                  HandleDateChange={
                                                    HandleRepeaterDateChange
                                                  }
                                                  optionId={1}
                                                  minDate={dayjs("01-01-1920")}
                                                  maxDate={dayjs()}
                                                  locale="en" // current it is mm/dd/yyyy but for dd/mm/yyyy use "en-gb"
                                                  date={data.q40Init_Dt_linvo}
                                                />
                                              </span>
                                            </div>

                                            <div
                                              className="option-without-input"
                                              style={{ display: "flex" }}
                                            >
                                              <label className="option-fbox">
                                                <input
                                                  type="radio"
                                                  id={idx}
                                                  name={
                                                    "q40Init_Dt_Rd_linvo" + idx
                                                  }
                                                  value={99}
                                                  checked={
                                                    data.q40Init_Dt_Rd_linvo ===
                                                    99
                                                  }
                                                  onChange={HandleFieldChange}
                                                />
                                                <span
                                                  style={{
                                                    paddingLeft: "10px",
                                                    marginLeft: "-5px",
                                                  }}
                                                >
                                                  Date of initiation unknown
                                                </span>
                                              </label>
                                            </div>

                                            <ErrorField
                                              show={data.Q40LinvoInit_RdHard}
                                              message={
                                                ELCharacteristics_MSGS.Q40Init_RdHard
                                              }
                                              style={{ paddingLeft: "0px" }}
                                            />

                                            <ErrorField
                                              show={data.Q40LinvoInit_dtHard}
                                              message={
                                                ELCharacteristics_MSGS.Q40Init_dtHard
                                              }
                                              style={{ paddingLeft: "0px" }}
                                            />
                                            <ErrorField
                                              show={
                                                data.invalidQ40Init_dt_linvo
                                              }
                                              message={
                                                CommonError_Msgs.InvalidDate
                                              }
                                              style={{ paddingLeft: "0px" }}
                                            />
                                            <ErrorField
                                              show={
                                                data.init_dt_before_eof_linvo
                                              }
                                              message={
                                                ELCharacteristics_MSGS.Q40DateOutOfRange
                                              }
                                              style={{ paddingLeft: "0px" }}
                                            />
                                            <ErrorField
                                              show={
                                                data.init_dt_full_chain_invalid_linvo
                                              }
                                              message={
                                                ELCharacteristics_MSGS.Q40FullChainOutOfRange
                                              }
                                              style={{ paddingLeft: "0px" }}
                                            />
                                          </div>
                                        </td>
                                      ) : (
                                        <td
                                          style={{
                                            border: "1px solid black",
                                            textAlign: "center",
                                            padding: "8px",
                                          }}
                                        >
                                          <span
                                            style={{
                                              textAlign: "center",
                                              verticalAlign: "center",
                                            }}
                                            className="not-applicable"
                                          >
                                            <b>Not applicable</b>
                                          </span>
                                        </td>
                                      )}
                                      {/* Medication Reason */}
                                      {data.q40DosingFreqRd_linvo !== 1 ? (
                                        <td
                                          style={{
                                            border: "1px solid black",
                                            padding: "8px",
                                          }}
                                        >
                                          <div>
                                            <div className="">
                                              <label
                                                className="option-fbox"
                                                style={{
                                                  alignItems: "baseline",
                                                }}
                                              >
                                                <input
                                                  type="checkbox"
                                                  id={idx}
                                                  name={
                                                    "primaryProf_linvo" + idx
                                                  }
                                                  checked={
                                                    data.primaryProf_linvo
                                                  }
                                                  onChange={HandleFieldChange}
                                                />
                                                <span
                                                  style={{
                                                    marginLeft: "6px",
                                                  }}
                                                >
                                                  Primary prophylactic for
                                                  infections
                                                </span>
                                              </label>
                                            </div>

                                            <div className="">
                                              <label
                                                className="option-fbox"
                                                style={{
                                                  alignItems: "baseline",
                                                }}
                                              >
                                                <input
                                                  type="checkbox"
                                                  id={idx}
                                                  name={
                                                    "secondaryProf_linvo" + idx
                                                  }
                                                  checked={
                                                    data.secondaryProf_linvo
                                                  }
                                                  onChange={HandleFieldChange}
                                                />
                                                <span
                                                  style={{
                                                    marginLeft: "6px",
                                                  }}
                                                >
                                                  Secondary prophylactic for
                                                  infections
                                                </span>
                                              </label>
                                            </div>

                                            <div className="">
                                              <label>
                                                <input
                                                  type="checkbox"
                                                  id={idx}
                                                  name={
                                                    "otherReson_linvo" + idx
                                                  }
                                                  checked={
                                                    data.otherReson_linvo
                                                  }
                                                  onChange={HandleFieldChange}
                                                />
                                                <span
                                                  style={{
                                                    marginLeft: "6px",
                                                  }}
                                                >
                                                  Other reason
                                                </span>
                                              </label>
                                            </div>

                                            <ErrorField
                                              show={
                                                data.Q40LinvoReasonEmptyHard
                                              }
                                              message={
                                                ELCharacteristics_MSGS.Q40ReasonEmptyHard
                                              }
                                              style={{ paddingLeft: "0px" }}
                                            />
                                          </div>
                                        </td>
                                      ) : (
                                        <td
                                          style={{
                                            border: "1px solid black",
                                            textAlign: "center",
                                            padding: "8px",
                                          }}
                                        >
                                          <span
                                            style={{
                                              textAlign: "center",
                                              verticalAlign: "center",
                                            }}
                                            className="not-applicable"
                                          >
                                            <b>Not applicable</b>
                                          </span>
                                        </td>
                                      )}
                                    </tr>
                                  ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </div>
                    )}
                    {/* 40 end */}

                    {/* 40.C start - Elrexfio */}
                    {isNewEcrfC && showQ40Elr && (
                      <div>
                        <div className="question question-weight">
                          <span>Q40.C.</span>
                          <span className="quest-text-pad">
                            Summarize information related to the usage of
                            intravenous immune globulin (IVIG) during the
                            patient’s treatment with{" "}
                            <span
                              style={{
                                color: PageDesignConstants.fontColor,
                                fontWeight: "bolder",
                              }}
                            >
                              ELREXFIO<sup>TM</sup> (elranatamab-bcmm)
                            </span>{" "}
                            (i.e., from{" "}
                            <span
                              style={{ color: PageDesignConstants.fontColor }}
                            >
                              {`[${showElrInitiationDate()}]`}
                            </span>{" "}
                            to{" "}
                            <span
                              style={{ color: PageDesignConstants.fontColor }}
                            >
                              {showElrQ40PeriodEndDate()}
                            </span>
                            ).
                          </span>
                        </div>

                        <div className="question-bot-sapce">
                          <div className="sub-question grid-overflow">
                            <table
                              style={{
                                border: "1px solid black",
                                borderCollapse: "collapse",
                                width: "100%",
                              }}
                            >
                              <thead>
                                <tr>
                                  <th
                                    style={{
                                      border: "1px solid black",
                                      padding: "8px",
                                      textAlign: "center",
                                    }}
                                  >
                                    Treatment
                                  </th>
                                  <th
                                    style={{
                                      border: "1px solid black",
                                      padding: "8px",
                                      textAlign: "center",
                                      width: "14%",
                                    }}
                                  >
                                    Initiation date of IGRT
                                  </th>
                                  <th
                                    style={{
                                      border: "1px solid black",
                                      padding: "8px",
                                      textAlign: "center",
                                      width: "13%",
                                    }}
                                  >
                                    Intended dosing type at treatment initiation
                                  </th>
                                  <th
                                    style={{
                                      border: "1px solid black",
                                      padding: "8px",
                                      textAlign: "center",
                                      width: "16%",
                                    }}
                                  >
                                    Intended dosing frequency
                                  </th>
                                  <th
                                    style={{
                                      border: "1px solid black",
                                      padding: "8px",
                                      textAlign: "center",
                                      width: "20%",
                                    }}
                                  >
                                    Reason for receiving medication.{" "}
                                    <span
                                      style={{
                                        fontWeight: "normal",
                                        fontStyle: "italic",
                                      }}
                                    >
                                      Select all that apply.
                                    </span>
                                  </th>
                                  <th
                                    style={{
                                      border: "1px solid black",
                                      padding: "8px",
                                      textAlign: "center",
                                      width: "13%",
                                    }}
                                  >
                                    Initial dose strength of IGRT
                                  </th>
                                  <th
                                    style={{
                                      border: "1px solid black",
                                      padding: "8px",
                                      textAlign: "center",
                                      width: "14%",
                                    }}
                                  >
                                    Last IgG level prior to first IGRT
                                    administration
                                  </th>
                                  <th
                                    style={{
                                      border: "1px solid black",
                                      padding: "8px",
                                      textAlign: "center",
                                      width: "13%",
                                    }}
                                  >
                                    Body weight ±1 month of initiation date
                                  </th>
                                </tr>
                              </thead>

                              <tbody>
                                {/* Elrexfio */}
                                {showQ40Elr &&
                                  Q37ElrexDetails.length > 0 &&
                                  Q37ElrexDetails.map((data, idx) => (
                                    <tr key={idx}>
                                      <td
                                        style={{
                                          border: "1px solid black",
                                          padding: "8px",
                                        }}
                                      >
                                        <span className="quest-text-pad">
                                          ELREXFIO<sup>TM</sup>{" "}
                                          (elranatamab-bcmm)
                                        </span>
                                      </td>

                                      {/* Initiation date of IVIG */}
                                      <td
                                        style={{
                                          border: "1px solid black",
                                          padding: "8px",
                                        }}
                                      >
                                        <div>
                                          <div
                                            className="option-without-input"
                                            style={{ display: "flex" }}
                                          >
                                            <label className="option-fbox">
                                              <input
                                                type="radio"
                                                id={idx}
                                                name={"ivigInitRd_elrC" + idx}
                                                value={1}
                                                checked={
                                                  data.ivigInitRd_elrC === 1
                                                }
                                                onChange={HandleFieldChange}
                                              />
                                              <span
                                                className="radio-text-padding"
                                                style={{
                                                  paddingLeft: "10px",
                                                  marginLeft: "-5px",
                                                }}
                                              >
                                                Patient never received IGRT
                                                during treatment
                                              </span>
                                            </label>
                                          </div>

                                          <div
                                            className="option-without-input"
                                            style={{ display: "flex" }}
                                          >
                                            <label className="option-fbox">
                                              <input
                                                type="radio"
                                                id={idx}
                                                name={"ivigInitRd_elrC" + idx}
                                                value={2}
                                                checked={
                                                  data.ivigInitRd_elrC === 2
                                                }
                                                onChange={HandleFieldChange}
                                              />
                                              <span
                                                className="radio-text-padding"
                                                style={{
                                                  paddingLeft: "10px",
                                                  marginLeft: "-5px",
                                                }}
                                              >
                                                <DatePickerComponent
                                                  ctrlId={
                                                    "ivigInitDt_elrC-" + idx
                                                  }
                                                  HandleDateChange={
                                                    HandleRepeaterDateChange
                                                  }
                                                  optionId={2}
                                                  minDate={dayjs("01-01-1920")}
                                                  maxDate={dayjs()}
                                                  locale="en"
                                                  date={data.ivigInitDt_elrC}
                                                />
                                              </span>
                                            </label>
                                          </div>

                                          <div
                                            className="option-without-input"
                                            style={{ display: "flex" }}
                                          >
                                            <label className="option-fbox">
                                              <input
                                                type="radio"
                                                id={idx}
                                                name={"ivigInitRd_elrC" + idx}
                                                value={99}
                                                checked={
                                                  data.ivigInitRd_elrC === 99
                                                }
                                                onChange={HandleFieldChange}
                                              />
                                              <span
                                                className="radio-text-padding"
                                                style={{
                                                  paddingLeft: "10px",
                                                  marginLeft: "-5px",
                                                }}
                                              >
                                                Date of initiation unknown
                                              </span>
                                            </label>
                                          </div>

                                          <ErrorField
                                            show={data.ivigInitRdHard_elrC}
                                            message={
                                              ELCharacteristics_MSGS.ivigInitRdHardC
                                            }
                                            style={{ paddingLeft: "0px" }}
                                          />
                                          <ErrorField
                                            show={data.ivigInitDtHard_elrC}
                                            message={
                                              ELCharacteristics_MSGS.ivigInitDtHardC
                                            }
                                            style={{ paddingLeft: "0px" }}
                                          />
                                          <ErrorField
                                            show={data.ivigInitDtInvalid_elrC}
                                            message={
                                              ELCharacteristics_MSGS.ivigInitDtInvalidC
                                            }
                                            style={{ paddingLeft: "0px" }}
                                          />
                                          <ErrorField
                                            show={data.ivigInitDtBeforeEof_elrC}
                                            message={
                                              ELCharacteristics_MSGS.ivigInitDtBeforeEofC
                                            }
                                            style={{ paddingLeft: "0px" }}
                                          />
                                          <ErrorField
                                            show={
                                              data.ivigInitDtFullChainInvalid_elrC
                                            }
                                            message={
                                              ELCharacteristics_MSGS.ivigInitDtFullChainOutOfRangeC
                                            }
                                            style={{ paddingLeft: "0px" }}
                                          />
                                        </div>
                                      </td>

                                      {data.ivigInitRd_elrC !== 1 ? (
                                        <>
                                          {/* Intended dosing type */}
                                          <td
                                            style={{
                                              border: "1px solid black",
                                              padding: "8px",
                                            }}
                                          >
                                            <div>
                                              <div className="option-without-input">
                                                <label className="option-fbox">
                                                  <input
                                                    type="radio"
                                                    id={idx}
                                                    name={
                                                      "ivigDosingType_elrC" +
                                                      idx
                                                    }
                                                    value={1}
                                                    checked={
                                                      data.ivigDosingType_elrC ===
                                                      1
                                                    }
                                                    onChange={HandleFieldChange}
                                                  />
                                                  <span className="radio-text-padding">
                                                    Flat dosage
                                                  </span>
                                                </label>
                                              </div>
                                              <div className="option-without-input">
                                                <label className="option-fbox">
                                                  <input
                                                    type="radio"
                                                    id={idx}
                                                    name={
                                                      "ivigDosingType_elrC" +
                                                      idx
                                                    }
                                                    value={2}
                                                    checked={
                                                      data.ivigDosingType_elrC ===
                                                      2
                                                    }
                                                    onChange={HandleFieldChange}
                                                  />
                                                  <span className="radio-text-padding">
                                                    Weight-based dosage
                                                  </span>
                                                </label>
                                              </div>
                                              <div className="option-without-input">
                                                <label className="option-fbox">
                                                  <input
                                                    type="radio"
                                                    id={idx}
                                                    name={
                                                      "ivigDosingType_elrC" +
                                                      idx
                                                    }
                                                    value={99}
                                                    checked={
                                                      data.ivigDosingType_elrC ===
                                                      99
                                                    }
                                                    onChange={HandleFieldChange}
                                                  />
                                                  <span className="radio-text-padding">
                                                    Unknown
                                                  </span>
                                                </label>
                                              </div>
                                              <ErrorField
                                                show={
                                                  data.ivigDosingTypeEmptyHard_elrC
                                                }
                                                message={
                                                  ELCharacteristics_MSGS.ivigDosingTypeEmptyHardC
                                                }
                                                style={{
                                                  paddingLeft: "0px",
                                                }}
                                              />
                                            </div>
                                          </td>

                                          {/* Intended dosing frequency */}
                                          <td
                                            style={{
                                              border: "1px solid black",
                                              padding: "8px",
                                            }}
                                          >
                                            <div>
                                              <div
                                                className="option-without-input"
                                                style={{ display: "flex" }}
                                              >
                                                <label className="option-fbox">
                                                  <input
                                                    type="radio"
                                                    id={idx}
                                                    name={
                                                      "ivigDosingFreqRd_elrC" +
                                                      idx
                                                    }
                                                    value={1}
                                                    checked={
                                                      data.ivigDosingFreqRd_elrC ===
                                                      1
                                                    }
                                                    onChange={HandleFieldChange}
                                                  />
                                                  <span
                                                    className="radio-text-padding"
                                                    style={{
                                                      paddingLeft: "10px",
                                                      marginLeft: "-5px",
                                                    }}
                                                  >
                                                    <input
                                                      type="text"
                                                      className="input-dash-ttcustom"
                                                      name={
                                                        "ivigDosingFreqTimesTxt_elrC" +
                                                        idx
                                                      }
                                                      id={idx}
                                                      value={
                                                        data.ivigDosingFreqTimesTxt_elrC
                                                      }
                                                      onChange={
                                                        HandleFieldChange
                                                      }
                                                      maxLength={2}
                                                    />{" "}
                                                    times per week
                                                  </span>
                                                </label>
                                              </div>

                                              <div
                                                className="option-without-input"
                                                style={{ display: "flex" }}
                                              >
                                                <label className="option-fbox">
                                                  <input
                                                    type="radio"
                                                    id={idx}
                                                    name={
                                                      "ivigDosingFreqRd_elrC" +
                                                      idx
                                                    }
                                                    value={2}
                                                    checked={
                                                      data.ivigDosingFreqRd_elrC ===
                                                      2
                                                    }
                                                    onChange={HandleFieldChange}
                                                  />
                                                  <span
                                                    className="radio-text-padding"
                                                    style={{
                                                      paddingLeft: "10px",
                                                      marginLeft: "-5px",
                                                    }}
                                                  >
                                                    Once every{" "}
                                                    <input
                                                      type="text"
                                                      className="input-dash-ttcustom"
                                                      name={
                                                        "ivigDosingFreqWeeksTxt_elrC" +
                                                        idx
                                                      }
                                                      id={idx}
                                                      value={
                                                        data.ivigDosingFreqWeeksTxt_elrC
                                                      }
                                                      onChange={
                                                        HandleFieldChange
                                                      }
                                                      maxLength={2}
                                                    />{" "}
                                                    weeks
                                                  </span>
                                                </label>
                                              </div>

                                              <div
                                                className="option-without-input"
                                                style={{ display: "flex" }}
                                              >
                                                <label className="option-fbox">
                                                  <input
                                                    type="radio"
                                                    id={idx}
                                                    name={
                                                      "ivigDosingFreqRd_elrC" +
                                                      idx
                                                    }
                                                    value={3}
                                                    checked={
                                                      data.ivigDosingFreqRd_elrC ===
                                                      3
                                                    }
                                                    onChange={HandleFieldChange}
                                                  />
                                                  <span
                                                    className="radio-text-padding"
                                                    style={{
                                                      paddingLeft: "10px",
                                                      marginLeft: "-5px",
                                                    }}
                                                  >
                                                    Other, please specify:
                                                    <div>
                                                      <textarea
                                                        className="input-dash"
                                                        name={
                                                          "ivigDosingFreqOtherTxt_elrC" +
                                                          idx
                                                        }
                                                        id={idx}
                                                        value={
                                                          data.ivigDosingFreqOtherTxt_elrC
                                                        }
                                                        onChange={
                                                          HandleFieldChange
                                                        }
                                                        maxLength={300}
                                                      />
                                                    </div>
                                                  </span>
                                                </label>
                                              </div>

                                              <div
                                                className="option-without-input"
                                                style={{ display: "flex" }}
                                              >
                                                <label className="option-fbox">
                                                  <input
                                                    type="radio"
                                                    id={idx}
                                                    name={
                                                      "ivigDosingFreqRd_elrC" +
                                                      idx
                                                    }
                                                    value={99}
                                                    checked={
                                                      data.ivigDosingFreqRd_elrC ===
                                                      99
                                                    }
                                                    onChange={HandleFieldChange}
                                                  />
                                                  <span
                                                    className="radio-text-padding"
                                                    style={{
                                                      paddingLeft: "10px",
                                                      marginLeft: "-5px",
                                                    }}
                                                  >
                                                    Unknown
                                                  </span>
                                                </label>
                                              </div>

                                              <ErrorField
                                                show={
                                                  data.ivigDosingFreqEmptyHard_elrC
                                                }
                                                message={
                                                  ELCharacteristics_MSGS.ivigDosingFreqEmptyHardC
                                                }
                                                style={{
                                                  paddingLeft: "0px",
                                                }}
                                              />
                                              <ErrorField
                                                show={
                                                  data.ivigDosingFreqTxtEmptyHard_elrC
                                                }
                                                message={
                                                  ELCharacteristics_MSGS.ivigDosingFreqTxtEmptyHardC
                                                }
                                                style={{
                                                  paddingLeft: "0px",
                                                }}
                                              />
                                              <ErrorField
                                                show={
                                                  data.ivigDosingFreqRangeInvalid_elrC
                                                }
                                                message={
                                                  ELCharacteristics_MSGS.ivigDosingFreqRangeInvalidC
                                                }
                                                style={{
                                                  paddingLeft: "0px",
                                                }}
                                              />
                                            </div>
                                          </td>

                                          {/* Reason for receiving medication */}
                                          <td
                                            style={{
                                              border: "1px solid black",
                                              padding: "8px",
                                            }}
                                          >
                                            <div>
                                              <div className="">
                                                <label
                                                  className="checkbox-option"
                                                  style={{
                                                    alignItems: "flex-start",
                                                  }}
                                                >
                                                  <input
                                                    type="checkbox"
                                                    style={{ marginTop: "6px" }}
                                                    id={idx}
                                                    name={
                                                      "ivigReasonPrimary_elrC" +
                                                      idx
                                                    }
                                                    checked={
                                                      data.ivigReasonPrimary_elrC
                                                    }
                                                    onChange={HandleFieldChange}
                                                  />
                                                  <span className="radio-text-padding">
                                                    Primary prophylactic for
                                                    infections
                                                  </span>
                                                </label>
                                              </div>
                                              <div className="">
                                                <label
                                                  className="checkbox-option"
                                                  style={{
                                                    alignItems: "flex-start",
                                                  }}
                                                >
                                                  <input
                                                    type="checkbox"
                                                    style={{ marginTop: "6px" }}
                                                    id={idx}
                                                    name={
                                                      "ivigReasonSecondary_elrC" +
                                                      idx
                                                    }
                                                    checked={
                                                      data.ivigReasonSecondary_elrC
                                                    }
                                                    onChange={HandleFieldChange}
                                                  />
                                                  <span className="radio-text-padding">
                                                    Secondary prophylactic for
                                                    infections
                                                  </span>
                                                </label>
                                              </div>
                                              <div className="">
                                                <label
                                                  className="checkbox-option"
                                                  style={{
                                                    alignItems: "flex-start",
                                                  }}
                                                >
                                                  <input
                                                    type="checkbox"
                                                    style={{ marginTop: "6px" }}
                                                    id={idx}
                                                    name={
                                                      "ivigReasonOther_elrC" +
                                                      idx
                                                    }
                                                    checked={
                                                      data.ivigReasonOther_elrC
                                                    }
                                                    onChange={HandleFieldChange}
                                                  />
                                                  <span className="radio-text-padding">
                                                    Other reason, please
                                                    specify:
                                                  </span>
                                                </label>
                                                <div>
                                                  <textarea
                                                    className="input-dash"
                                                    name={
                                                      "ivigReasonOtherTxt_elrC" +
                                                      idx
                                                    }
                                                    id={idx}
                                                    value={
                                                      data.ivigReasonOtherTxt_elrC
                                                    }
                                                    onChange={HandleFieldChange}
                                                    maxLength={300}
                                                  />
                                                </div>
                                                <ErrorField
                                                  show={
                                                    data.ivigReasonOtherTxtEmptyHard_elrC
                                                  }
                                                  message={
                                                    ELCharacteristics_MSGS.ivigReasonOtherTxtEmptyHardC
                                                  }
                                                  style={{
                                                    paddingLeft: "0px",
                                                  }}
                                                />
                                              </div>
                                              <div className="">
                                                <label
                                                  className="checkbox-option"
                                                  style={{
                                                    alignItems: "flex-start",
                                                  }}
                                                >
                                                  <input
                                                    type="radio"
                                                    style={{ marginTop: "6px" }}
                                                    id={idx}
                                                    name={
                                                      "ivigReasonUnk_elrC" + idx
                                                    }
                                                    checked={
                                                      data.ivigReasonUnk_elrC
                                                    }
                                                    onChange={HandleFieldChange}
                                                  />
                                                  <span className="radio-text-padding">
                                                    Unknown
                                                  </span>
                                                </label>
                                              </div>
                                              <ErrorField
                                                show={
                                                  data.ivigReasonEmptyHard_elrC
                                                }
                                                message={
                                                  ELCharacteristics_MSGS.ivigReasonEmptyHardC
                                                }
                                                style={{
                                                  paddingLeft: "0px",
                                                }}
                                              />
                                            </div>
                                          </td>

                                          {/* Initial dose strength */}
                                          <td
                                            style={{
                                              border: "1px solid black",
                                              padding: "8px",
                                            }}
                                          >
                                            <div>
                                              {data.ivigDosingType_elrC ===
                                                1 && (
                                                <div
                                                  className="option-without-input"
                                                  style={{ display: "flex" }}
                                                >
                                                  <label className="option-fbox">
                                                    <input
                                                      type="radio"
                                                      id={idx}
                                                      name={
                                                        "ivigDoseStrengthRd_elrC" +
                                                        idx
                                                      }
                                                      value={1}
                                                      checked={
                                                        data.ivigDoseStrengthRd_elrC ===
                                                        1
                                                      }
                                                      onChange={
                                                        HandleFieldChange
                                                      }
                                                    />
                                                    <span
                                                      className="radio-text-padding"
                                                      style={{
                                                        paddingLeft: "10px",
                                                        marginLeft: "-5px",
                                                      }}
                                                    >
                                                      <input
                                                        type="text"
                                                        className="input-dash-ttcustom"
                                                        name={
                                                          "ivigDoseStrengthGTxt_elrC" +
                                                          idx
                                                        }
                                                        id={idx}
                                                        value={
                                                          data.ivigDoseStrengthGTxt_elrC
                                                        }
                                                        onChange={
                                                          HandleFieldChange
                                                        }
                                                        maxLength={6}
                                                      />{" "}
                                                      g
                                                    </span>
                                                  </label>
                                                </div>
                                              )}

                                              {data.ivigDosingType_elrC ===
                                                2 && (
                                                <div
                                                  className="option-without-input"
                                                  style={{ display: "flex" }}
                                                >
                                                  <label className="option-fbox">
                                                    <input
                                                      type="radio"
                                                      id={idx}
                                                      name={
                                                        "ivigDoseStrengthRd_elrC" +
                                                        idx
                                                      }
                                                      value={3}
                                                      checked={
                                                        data.ivigDoseStrengthRd_elrC ===
                                                        3
                                                      }
                                                      onChange={
                                                        HandleFieldChange
                                                      }
                                                    />
                                                    <span
                                                      className="radio-text-padding"
                                                      style={{
                                                        paddingLeft: "10px",
                                                        marginLeft: "-5px",
                                                      }}
                                                    >
                                                      <input
                                                        type="text"
                                                        className="input-dash-ttcustom"
                                                        name={
                                                          "ivigDoseStrengthMgKgTxt_elrC" +
                                                          idx
                                                        }
                                                        id={idx}
                                                        value={
                                                          data.ivigDoseStrengthMgKgTxt_elrC
                                                        }
                                                        onChange={
                                                          HandleFieldChange
                                                        }
                                                        onKeyDown={
                                                          AllowNumericWithDecimal
                                                        }
                                                        maxLength={6}
                                                      />{" "}
                                                      mg/kg
                                                    </span>
                                                  </label>
                                                </div>
                                              )}

                                              <div
                                                className="option-without-input"
                                                style={{ display: "flex" }}
                                              >
                                                <label className="option-fbox">
                                                  <input
                                                    type="radio"
                                                    id={idx}
                                                    name={
                                                      "ivigDoseStrengthRd_elrC" +
                                                      idx
                                                    }
                                                    value={2}
                                                    checked={
                                                      data.ivigDoseStrengthRd_elrC ===
                                                      2
                                                    }
                                                    onChange={HandleFieldChange}
                                                  />
                                                  <span
                                                    className="radio-text-padding"
                                                    style={{
                                                      paddingLeft: "10px",
                                                      marginLeft: "-5px",
                                                    }}
                                                  >
                                                    Other, please specify:
                                                    <div>
                                                      <textarea
                                                        className="input-dash"
                                                        name={
                                                          "ivigDoseStrengthOtherTxt_elrC" +
                                                          idx
                                                        }
                                                        id={idx}
                                                        value={
                                                          data.ivigDoseStrengthOtherTxt_elrC
                                                        }
                                                        onChange={
                                                          HandleFieldChange
                                                        }
                                                        maxLength={300}
                                                      />
                                                    </div>
                                                  </span>
                                                </label>
                                              </div>

                                              <div
                                                className="option-without-input"
                                                style={{ display: "flex" }}
                                              >
                                                <label className="option-fbox">
                                                  <input
                                                    type="radio"
                                                    id={idx}
                                                    name={
                                                      "ivigDoseStrengthRd_elrC" +
                                                      idx
                                                    }
                                                    value={99}
                                                    checked={
                                                      data.ivigDoseStrengthRd_elrC ===
                                                      99
                                                    }
                                                    onChange={HandleFieldChange}
                                                  />
                                                  <span
                                                    className="radio-text-padding"
                                                    style={{
                                                      paddingLeft: "10px",
                                                      marginLeft: "-5px",
                                                    }}
                                                  >
                                                    Unknown
                                                  </span>
                                                </label>
                                              </div>

                                              <ErrorField
                                                show={
                                                  data.ivigDoseStrengthEmptyHard_elrC
                                                }
                                                message={
                                                  ELCharacteristics_MSGS.ivigDoseStrengthEmptyHardC
                                                }
                                                style={{
                                                  paddingLeft: "0px",
                                                }}
                                              />
                                              <ErrorField
                                                show={
                                                  data.ivigDoseStrengthRangeInvalid_elrC
                                                }
                                                message={
                                                  ELCharacteristics_MSGS.ivigDoseStrengthRangeInvalidC
                                                }
                                                style={{
                                                  paddingLeft: "0px",
                                                }}
                                              />
                                              <ErrorField
                                                show={
                                                  data.ivigDoseStrengthMgKgRangeInvalid_elrC
                                                }
                                                message={
                                                  ELCharacteristics_MSGS.ivigDoseStrengthMgKgRangeInvalidC
                                                }
                                                style={{
                                                  paddingLeft: "0px",
                                                }}
                                              />
                                              <ErrorField
                                                show={
                                                  data.ivigDoseStrengthOtherTxtEmptyHard_elrC
                                                }
                                                message={
                                                  ELCharacteristics_MSGS.ivigDoseStrengthOtherTxtEmptyHardC
                                                }
                                                style={{
                                                  paddingLeft: "0px",
                                                }}
                                              />
                                            </div>
                                          </td>

                                          {/* Last IgG level */}
                                          <td
                                            style={{
                                              border: "1px solid black",
                                              padding: "8px",
                                            }}
                                          >
                                            <div>
                                              <div className="option-without-input">
                                                <label className="option-fbox">
                                                  <input
                                                    type="radio"
                                                    id={idx}
                                                    name={
                                                      "ivigLastIgg_elrC" + idx
                                                    }
                                                    value={1}
                                                    checked={
                                                      data.ivigLastIgg_elrC ===
                                                      1
                                                    }
                                                    onChange={HandleFieldChange}
                                                  />
                                                  <span className="radio-text-padding">
                                                    {"<"} 400 mg/dL
                                                  </span>
                                                </label>
                                              </div>
                                              <div className="option-without-input">
                                                <label className="option-fbox">
                                                  <input
                                                    type="radio"
                                                    id={idx}
                                                    name={
                                                      "ivigLastIgg_elrC" + idx
                                                    }
                                                    value={2}
                                                    checked={
                                                      data.ivigLastIgg_elrC ===
                                                      2
                                                    }
                                                    onChange={HandleFieldChange}
                                                  />
                                                  <span className="radio-text-padding">
                                                    400-500 mg/dL
                                                  </span>
                                                </label>
                                              </div>
                                              <div className="option-without-input">
                                                <label className="option-fbox">
                                                  <input
                                                    type="radio"
                                                    id={idx}
                                                    name={
                                                      "ivigLastIgg_elrC" + idx
                                                    }
                                                    value={3}
                                                    checked={
                                                      data.ivigLastIgg_elrC ===
                                                      3
                                                    }
                                                    onChange={HandleFieldChange}
                                                  />
                                                  <span className="radio-text-padding">
                                                    {">"} 500 mg/dL
                                                  </span>
                                                </label>
                                              </div>
                                              <div className="option-without-input">
                                                <label className="option-fbox">
                                                  <input
                                                    type="radio"
                                                    id={idx}
                                                    name={
                                                      "ivigLastIgg_elrC" + idx
                                                    }
                                                    value={99}
                                                    checked={
                                                      data.ivigLastIgg_elrC ===
                                                      99
                                                    }
                                                    onChange={HandleFieldChange}
                                                  />
                                                  <span className="radio-text-padding">
                                                    Unknown
                                                  </span>
                                                </label>
                                              </div>
                                              <ErrorField
                                                show={
                                                  data.ivigLastIggEmptyHard_elrC
                                                }
                                                message={
                                                  ELCharacteristics_MSGS.ivigLastIggEmptyHardC
                                                }
                                                style={{
                                                  paddingLeft: "0px",
                                                }}
                                              />
                                            </div>
                                          </td>

                                          {/* Body weight ±1 month of initiation date */}
                                          {data.ivigDosingType_elrC === 2 ? (
                                            <td
                                              style={{
                                                border: "1px solid black",
                                                padding: "8px",
                                              }}
                                            >
                                              <div>
                                                <div
                                                  className="option-without-input"
                                                  style={{ display: "flex" }}
                                                >
                                                  <label className="option-fbox">
                                                    <input
                                                      type="radio"
                                                      id={idx}
                                                      name={
                                                        "ivigBodyWeightRd_elrC" +
                                                        idx
                                                      }
                                                      value={1}
                                                      checked={
                                                        data.ivigBodyWeightRd_elrC ===
                                                        1
                                                      }
                                                      onChange={
                                                        HandleFieldChange
                                                      }
                                                    />
                                                    <span
                                                      className="radio-text-padding"
                                                      style={{
                                                        paddingLeft: "10px",
                                                        marginLeft: "-5px",
                                                      }}
                                                    >
                                                      <input
                                                        type="text"
                                                        className="input-dash-ttcustom"
                                                        name={
                                                          "ivigBodyWeightKgTxt_elrC" +
                                                          idx
                                                        }
                                                        id={idx}
                                                        value={
                                                          data.ivigBodyWeightKgTxt_elrC
                                                        }
                                                        onChange={
                                                          HandleFieldChange
                                                        }
                                                        onKeyDown={
                                                          AllowNumericWithDecimal
                                                        }
                                                        maxLength={10}
                                                      />{" "}
                                                      kg
                                                    </span>
                                                  </label>
                                                </div>

                                                <div
                                                  className="option-without-input"
                                                  style={{ display: "flex" }}
                                                >
                                                  <label className="option-fbox">
                                                    <input
                                                      type="radio"
                                                      id={idx}
                                                      name={
                                                        "ivigBodyWeightRd_elrC" +
                                                        idx
                                                      }
                                                      value={2}
                                                      checked={
                                                        data.ivigBodyWeightRd_elrC ===
                                                        2
                                                      }
                                                      onChange={
                                                        HandleFieldChange
                                                      }
                                                    />
                                                    <span
                                                      className="radio-text-padding"
                                                      style={{
                                                        paddingLeft: "10px",
                                                        marginLeft: "-5px",
                                                      }}
                                                    >
                                                      <input
                                                        type="text"
                                                        className="input-dash-ttcustom"
                                                        name={
                                                          "ivigBodyWeightLbsTxt_elrC" +
                                                          idx
                                                        }
                                                        id={idx}
                                                        value={
                                                          data.ivigBodyWeightLbsTxt_elrC
                                                        }
                                                        onChange={
                                                          HandleFieldChange
                                                        }
                                                        onKeyDown={
                                                          AllowNumericWithDecimal
                                                        }
                                                        maxLength={10}
                                                      />{" "}
                                                      lbs
                                                    </span>
                                                  </label>
                                                </div>

                                                <div
                                                  className="option-without-input"
                                                  style={{ display: "flex" }}
                                                >
                                                  <label className="option-fbox">
                                                    <input
                                                      type="radio"
                                                      id={idx}
                                                      name={
                                                        "ivigBodyWeightRd_elrC" +
                                                        idx
                                                      }
                                                      value={99}
                                                      checked={
                                                        data.ivigBodyWeightRd_elrC ===
                                                        99
                                                      }
                                                      onChange={
                                                        HandleFieldChange
                                                      }
                                                    />
                                                    <span
                                                      className="radio-text-padding"
                                                      style={{
                                                        paddingLeft: "10px",
                                                        marginLeft: "-5px",
                                                      }}
                                                    >
                                                      Unknown
                                                    </span>
                                                  </label>
                                                </div>

                                                <ErrorField
                                                  show={
                                                    data.ivigBodyWeightEmptyHard_elrC
                                                  }
                                                  message={
                                                    ELCharacteristics_MSGS.ivigBodyWeightEmptyHardC
                                                  }
                                                  style={{
                                                    paddingLeft: "0px",
                                                  }}
                                                />
                                                <ErrorField
                                                  show={
                                                    data.ivigBodyWeightRangeInvalid_elrC
                                                  }
                                                  message={
                                                    data.ivigBodyWeightRd_elrC ===
                                                    1
                                                      ? ELCharacteristics_MSGS.ivigBodyWeightKgRangeInvalidC
                                                      : ELCharacteristics_MSGS.ivigBodyWeightLbsRangeInvalidC
                                                  }
                                                  style={{
                                                    paddingLeft: "0px",
                                                  }}
                                                />
                                              </div>
                                            </td>
                                          ) : (
                                            <td
                                              style={{
                                                border: "1px solid black",
                                                textAlign: "center",
                                                padding: "8px",
                                              }}
                                            >
                                              <span className="not-applicable">
                                                <b>Not applicable</b>
                                              </span>
                                            </td>
                                          )}
                                        </>
                                      ) : (
                                        <td
                                          colSpan={6}
                                          style={{
                                            border: "1px solid black",
                                            textAlign: "center",
                                            padding: "8px",
                                          }}
                                        >
                                          <span className="not-applicable">
                                            <b>Not applicable</b>
                                          </span>
                                        </td>
                                      )}
                                    </tr>
                                  ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </div>
                    )}
                    {/* 40.C end - Elrexfio */}

                    {/* 40.C start - Linvoseltamab */}
                    {isNewEcrfC && showQ40Linvo && (
                      <div>
                        <div className="question question-weight">
                          <span>Q40.C.</span>
                          <span className="quest-text-pad">
                            Summarize information related to the usage of
                            intravenous immune globulin (IVIG) during the
                            patient’s treatment with{" "}
                            <span
                              style={{
                                color: PageDesignConstants.fontColor,
                                fontWeight: "bolder",
                              }}
                            >
                              LYNOZYFIC<sup>TM</sup> (linvoseltamab-gcpt)
                            </span>{" "}
                            (i.e., from{" "}
                            <span
                              style={{ color: PageDesignConstants.fontColor }}
                            >
                              {`[${showLinvoInitiationDate()}]`}
                            </span>{" "}
                            to{" "}
                            <span
                              style={{ color: PageDesignConstants.fontColor }}
                            >
                              {showLinvoQ40PeriodEndDate()}
                            </span>
                            ).
                          </span>
                        </div>

                        <div className="question-bot-sapce">
                          <div className="sub-question grid-overflow">
                            <table
                              style={{
                                border: "1px solid black",
                                borderCollapse: "collapse",
                                width: "100%",
                              }}
                            >
                              <thead>
                                <tr>
                                  <th
                                    style={{
                                      border: "1px solid black",
                                      padding: "8px",
                                      textAlign: "center",
                                    }}
                                  >
                                    Treatment
                                  </th>
                                  <th
                                    style={{
                                      border: "1px solid black",
                                      padding: "8px",
                                      textAlign: "center",
                                      width: "14%",
                                    }}
                                  >
                                    Initiation date of IGRT
                                  </th>
                                  <th
                                    style={{
                                      border: "1px solid black",
                                      padding: "8px",
                                      textAlign: "center",
                                      width: "13%",
                                    }}
                                  >
                                    Intended dosing type at treatment initiation
                                  </th>
                                  <th
                                    style={{
                                      border: "1px solid black",
                                      padding: "8px",
                                      textAlign: "center",
                                      width: "16%",
                                    }}
                                  >
                                    Intended dosing frequency
                                  </th>
                                  <th
                                    style={{
                                      border: "1px solid black",
                                      padding: "8px",
                                      textAlign: "center",
                                      width: "20%",
                                    }}
                                  >
                                    Reason for receiving medication.{" "}
                                    <span
                                      style={{
                                        fontWeight: "normal",
                                        fontStyle: "italic",
                                      }}
                                    >
                                      Select all that apply.
                                    </span>
                                  </th>
                                  <th
                                    style={{
                                      border: "1px solid black",
                                      padding: "8px",
                                      textAlign: "center",
                                      width: "13%",
                                    }}
                                  >
                                    Initial dose strength of IGRT
                                  </th>
                                  <th
                                    style={{
                                      border: "1px solid black",
                                      padding: "8px",
                                      textAlign: "center",
                                      width: "14%",
                                    }}
                                  >
                                    Last IgG level prior to first IGRT
                                    administration
                                  </th>
                                  <th
                                    style={{
                                      border: "1px solid black",
                                      padding: "8px",
                                      textAlign: "center",
                                      width: "13%",
                                    }}
                                  >
                                    Body weight ±1 month of initiation date
                                  </th>
                                </tr>
                              </thead>

                              <tbody>
                                {/* Linvoseltamab */}
                                {showQ40Linvo &&
                                  Q37LinvoDetails.length > 0 &&
                                  Q37LinvoDetails.map((data, idx) => (
                                    <tr key={idx}>
                                      <td
                                        style={{
                                          border: "1px solid black",
                                          padding: "8px",
                                        }}
                                      >
                                        <span className="quest-text-pad">
                                          LYNOZYFIC<sup>TM</sup>{" "}
                                          (linvoseltamab-gcpt)
                                        </span>
                                      </td>

                                      {/* Initiation date of IVIG */}
                                      <td
                                        style={{
                                          border: "1px solid black",
                                          padding: "8px",
                                        }}
                                      >
                                        <div>
                                          <div
                                            className="option-without-input"
                                            style={{ display: "flex" }}
                                          >
                                            <label className="option-fbox">
                                              <input
                                                type="radio"
                                                id={idx}
                                                name={"ivigInitRd_linvoC" + idx}
                                                value={1}
                                                checked={
                                                  data.ivigInitRd_linvoC === 1
                                                }
                                                onChange={HandleFieldChange}
                                              />
                                              <span
                                                className="radio-text-padding"
                                                style={{
                                                  paddingLeft: "10px",
                                                  marginLeft: "-5px",
                                                }}
                                              >
                                                Patient never received IGRT
                                                during treatment
                                              </span>
                                            </label>
                                          </div>

                                          <div
                                            className="option-without-input"
                                            style={{ display: "flex" }}
                                          >
                                            <label className="option-fbox">
                                              <input
                                                type="radio"
                                                id={idx}
                                                name={"ivigInitRd_linvoC" + idx}
                                                value={2}
                                                checked={
                                                  data.ivigInitRd_linvoC === 2
                                                }
                                                onChange={HandleFieldChange}
                                              />
                                              <span
                                                className="radio-text-padding"
                                                style={{
                                                  paddingLeft: "10px",
                                                  marginLeft: "-5px",
                                                }}
                                              >
                                                <DatePickerComponent
                                                  ctrlId={
                                                    "ivigInitDt_linvoC-" + idx
                                                  }
                                                  HandleDateChange={
                                                    HandleRepeaterDateChange
                                                  }
                                                  optionId={2}
                                                  minDate={dayjs("01-01-1920")}
                                                  maxDate={dayjs()}
                                                  locale="en"
                                                  date={data.ivigInitDt_linvoC}
                                                />
                                              </span>
                                            </label>
                                          </div>

                                          <div
                                            className="option-without-input"
                                            style={{ display: "flex" }}
                                          >
                                            <label className="option-fbox">
                                              <input
                                                type="radio"
                                                id={idx}
                                                name={"ivigInitRd_linvoC" + idx}
                                                value={99}
                                                checked={
                                                  data.ivigInitRd_linvoC === 99
                                                }
                                                onChange={HandleFieldChange}
                                              />
                                              <span
                                                className="radio-text-padding"
                                                style={{
                                                  paddingLeft: "10px",
                                                  marginLeft: "-5px",
                                                }}
                                              >
                                                Date of initiation unknown
                                              </span>
                                            </label>
                                          </div>

                                          <ErrorField
                                            show={data.ivigInitRdHard_linvoC}
                                            message={
                                              ELCharacteristics_MSGS.ivigInitRdHardC
                                            }
                                            style={{ paddingLeft: "0px" }}
                                          />
                                          <ErrorField
                                            show={data.ivigInitDtHard_linvoC}
                                            message={
                                              ELCharacteristics_MSGS.ivigInitDtHardC
                                            }
                                            style={{ paddingLeft: "0px" }}
                                          />
                                          <ErrorField
                                            show={data.ivigInitDtInvalid_linvoC}
                                            message={
                                              ELCharacteristics_MSGS.ivigInitDtInvalidC
                                            }
                                            style={{ paddingLeft: "0px" }}
                                          />
                                          <ErrorField
                                            show={
                                              data.ivigInitDtBeforeEof_linvoC
                                            }
                                            message={
                                              ELCharacteristics_MSGS.ivigInitDtBeforeEofC
                                            }
                                            style={{ paddingLeft: "0px" }}
                                          />
                                          <ErrorField
                                            show={
                                              data.ivigInitDtFullChainInvalid_linvoC
                                            }
                                            message={
                                              ELCharacteristics_MSGS.ivigInitDtFullChainOutOfRangeC
                                            }
                                            style={{ paddingLeft: "0px" }}
                                          />
                                        </div>
                                      </td>

                                      {data.ivigInitRd_linvoC !== 1 ? (
                                        <>
                                          {/* Intended dosing type */}
                                          <td
                                            style={{
                                              border: "1px solid black",
                                              padding: "8px",
                                            }}
                                          >
                                            <div>
                                              <div className="option-without-input">
                                                <label className="option-fbox">
                                                  <input
                                                    type="radio"
                                                    id={idx}
                                                    name={
                                                      "ivigDosingType_linvoC" +
                                                      idx
                                                    }
                                                    value={1}
                                                    checked={
                                                      data.ivigDosingType_linvoC ===
                                                      1
                                                    }
                                                    onChange={HandleFieldChange}
                                                  />
                                                  <span className="radio-text-padding">
                                                    Flat dosage
                                                  </span>
                                                </label>
                                              </div>
                                              <div className="option-without-input">
                                                <label className="option-fbox">
                                                  <input
                                                    type="radio"
                                                    id={idx}
                                                    name={
                                                      "ivigDosingType_linvoC" +
                                                      idx
                                                    }
                                                    value={2}
                                                    checked={
                                                      data.ivigDosingType_linvoC ===
                                                      2
                                                    }
                                                    onChange={HandleFieldChange}
                                                  />
                                                  <span className="radio-text-padding">
                                                    Weight-based dosage
                                                  </span>
                                                </label>
                                              </div>
                                              <div className="option-without-input">
                                                <label className="option-fbox">
                                                  <input
                                                    type="radio"
                                                    id={idx}
                                                    name={
                                                      "ivigDosingType_linvoC" +
                                                      idx
                                                    }
                                                    value={99}
                                                    checked={
                                                      data.ivigDosingType_linvoC ===
                                                      99
                                                    }
                                                    onChange={HandleFieldChange}
                                                  />
                                                  <span className="radio-text-padding">
                                                    Unknown
                                                  </span>
                                                </label>
                                              </div>
                                              <ErrorField
                                                show={
                                                  data.ivigDosingTypeEmptyHard_linvoC
                                                }
                                                message={
                                                  ELCharacteristics_MSGS.ivigDosingTypeEmptyHardC
                                                }
                                                style={{
                                                  paddingLeft: "0px",
                                                }}
                                              />
                                            </div>
                                          </td>

                                          {/* Intended dosing frequency */}
                                          <td
                                            style={{
                                              border: "1px solid black",
                                              padding: "8px",
                                            }}
                                          >
                                            <div>
                                              <div
                                                className="option-without-input"
                                                style={{ display: "flex" }}
                                              >
                                                <label className="option-fbox">
                                                  <input
                                                    type="radio"
                                                    id={idx}
                                                    name={
                                                      "ivigDosingFreqRd_linvoC" +
                                                      idx
                                                    }
                                                    value={1}
                                                    checked={
                                                      data.ivigDosingFreqRd_linvoC ===
                                                      1
                                                    }
                                                    onChange={HandleFieldChange}
                                                  />
                                                  <span
                                                    className="radio-text-padding"
                                                    style={{
                                                      paddingLeft: "10px",
                                                      marginLeft: "-5px",
                                                    }}
                                                  >
                                                    <input
                                                      type="text"
                                                      className="input-dash-ttcustom"
                                                      name={
                                                        "ivigDosingFreqTimesTxt_linvoC" +
                                                        idx
                                                      }
                                                      id={idx}
                                                      value={
                                                        data.ivigDosingFreqTimesTxt_linvoC
                                                      }
                                                      onChange={
                                                        HandleFieldChange
                                                      }
                                                      maxLength={2}
                                                    />{" "}
                                                    times per week
                                                  </span>
                                                </label>
                                              </div>

                                              <div
                                                className="option-without-input"
                                                style={{ display: "flex" }}
                                              >
                                                <label className="option-fbox">
                                                  <input
                                                    type="radio"
                                                    id={idx}
                                                    name={
                                                      "ivigDosingFreqRd_linvoC" +
                                                      idx
                                                    }
                                                    value={2}
                                                    checked={
                                                      data.ivigDosingFreqRd_linvoC ===
                                                      2
                                                    }
                                                    onChange={HandleFieldChange}
                                                  />
                                                  <span
                                                    className="radio-text-padding"
                                                    style={{
                                                      paddingLeft: "10px",
                                                      marginLeft: "-5px",
                                                    }}
                                                  >
                                                    Once every{" "}
                                                    <input
                                                      type="text"
                                                      className="input-dash-ttcustom"
                                                      name={
                                                        "ivigDosingFreqWeeksTxt_linvoC" +
                                                        idx
                                                      }
                                                      id={idx}
                                                      value={
                                                        data.ivigDosingFreqWeeksTxt_linvoC
                                                      }
                                                      onChange={
                                                        HandleFieldChange
                                                      }
                                                      maxLength={2}
                                                    />{" "}
                                                    weeks
                                                  </span>
                                                </label>
                                              </div>

                                              <div
                                                className="option-without-input"
                                                style={{ display: "flex" }}
                                              >
                                                <label className="option-fbox">
                                                  <input
                                                    type="radio"
                                                    id={idx}
                                                    name={
                                                      "ivigDosingFreqRd_linvoC" +
                                                      idx
                                                    }
                                                    value={3}
                                                    checked={
                                                      data.ivigDosingFreqRd_linvoC ===
                                                      3
                                                    }
                                                    onChange={HandleFieldChange}
                                                  />
                                                  <span
                                                    className="radio-text-padding"
                                                    style={{
                                                      paddingLeft: "10px",
                                                      marginLeft: "-5px",
                                                    }}
                                                  >
                                                    Other, please specify:
                                                    <div>
                                                      <textarea
                                                        className="input-dash"
                                                        name={
                                                          "ivigDosingFreqOtherTxt_linvoC" +
                                                          idx
                                                        }
                                                        id={idx}
                                                        value={
                                                          data.ivigDosingFreqOtherTxt_linvoC
                                                        }
                                                        onChange={
                                                          HandleFieldChange
                                                        }
                                                        maxLength={300}
                                                      />
                                                    </div>
                                                  </span>
                                                </label>
                                              </div>

                                              <div
                                                className="option-without-input"
                                                style={{ display: "flex" }}
                                              >
                                                <label className="option-fbox">
                                                  <input
                                                    type="radio"
                                                    id={idx}
                                                    name={
                                                      "ivigDosingFreqRd_linvoC" +
                                                      idx
                                                    }
                                                    value={99}
                                                    checked={
                                                      data.ivigDosingFreqRd_linvoC ===
                                                      99
                                                    }
                                                    onChange={HandleFieldChange}
                                                  />
                                                  <span
                                                    className="radio-text-padding"
                                                    style={{
                                                      paddingLeft: "10px",
                                                      marginLeft: "-5px",
                                                    }}
                                                  >
                                                    Unknown
                                                  </span>
                                                </label>
                                              </div>

                                              <ErrorField
                                                show={
                                                  data.ivigDosingFreqEmptyHard_linvoC
                                                }
                                                message={
                                                  ELCharacteristics_MSGS.ivigDosingFreqEmptyHardC
                                                }
                                                style={{
                                                  paddingLeft: "0px",
                                                }}
                                              />
                                              <ErrorField
                                                show={
                                                  data.ivigDosingFreqTxtEmptyHard_linvoC
                                                }
                                                message={
                                                  ELCharacteristics_MSGS.ivigDosingFreqTxtEmptyHardC
                                                }
                                                style={{
                                                  paddingLeft: "0px",
                                                }}
                                              />
                                              <ErrorField
                                                show={
                                                  data.ivigDosingFreqRangeInvalid_linvoC
                                                }
                                                message={
                                                  ELCharacteristics_MSGS.ivigDosingFreqRangeInvalidC
                                                }
                                                style={{
                                                  paddingLeft: "0px",
                                                }}
                                              />
                                            </div>
                                          </td>

                                          {/* Reason for receiving medication */}
                                          <td
                                            style={{
                                              border: "1px solid black",
                                              padding: "8px",
                                            }}
                                          >
                                            <div>
                                              <div className="">
                                                <label
                                                  className="checkbox-option"
                                                  style={{
                                                    alignItems: "flex-start",
                                                  }}
                                                >
                                                  <input
                                                    type="checkbox"
                                                    style={{ marginTop: "6px" }}
                                                    id={idx}
                                                    name={
                                                      "ivigReasonPrimary_linvoC" +
                                                      idx
                                                    }
                                                    checked={
                                                      data.ivigReasonPrimary_linvoC
                                                    }
                                                    onChange={HandleFieldChange}
                                                  />
                                                  <span className="radio-text-padding">
                                                    Primary prophylactic for
                                                    infections
                                                  </span>
                                                </label>
                                              </div>
                                              <div className="">
                                                <label
                                                  className="checkbox-option"
                                                  style={{
                                                    alignItems: "flex-start",
                                                  }}
                                                >
                                                  <input
                                                    type="checkbox"
                                                    style={{ marginTop: "6px" }}
                                                    id={idx}
                                                    name={
                                                      "ivigReasonSecondary_linvoC" +
                                                      idx
                                                    }
                                                    checked={
                                                      data.ivigReasonSecondary_linvoC
                                                    }
                                                    onChange={HandleFieldChange}
                                                  />
                                                  <span className="radio-text-padding">
                                                    Secondary prophylactic for
                                                    infections
                                                  </span>
                                                </label>
                                              </div>
                                              <div className="">
                                                <label
                                                  className="checkbox-option"
                                                  style={{
                                                    alignItems: "flex-start",
                                                  }}
                                                >
                                                  <input
                                                    type="checkbox"
                                                    style={{ marginTop: "6px" }}
                                                    id={idx}
                                                    name={
                                                      "ivigReasonOther_linvoC" +
                                                      idx
                                                    }
                                                    checked={
                                                      data.ivigReasonOther_linvoC
                                                    }
                                                    onChange={HandleFieldChange}
                                                  />
                                                  <span className="radio-text-padding">
                                                    Other reason, please
                                                    specify:
                                                  </span>
                                                </label>
                                                <div>
                                                  <textarea
                                                    className="input-dash"
                                                    name={
                                                      "ivigReasonOtherTxt_linvoC" +
                                                      idx
                                                    }
                                                    id={idx}
                                                    value={
                                                      data.ivigReasonOtherTxt_linvoC
                                                    }
                                                    onChange={HandleFieldChange}
                                                    maxLength={300}
                                                  />
                                                </div>
                                                <ErrorField
                                                  show={
                                                    data.ivigReasonOtherTxtEmptyHard_linvoC
                                                  }
                                                  message={
                                                    ELCharacteristics_MSGS.ivigReasonOtherTxtEmptyHardC
                                                  }
                                                  style={{
                                                    paddingLeft: "0px",
                                                  }}
                                                />
                                              </div>
                                              <div className="">
                                                <label
                                                  className="checkbox-option"
                                                  style={{
                                                    alignItems: "flex-start",
                                                  }}
                                                >
                                                  <input
                                                    type="radio"
                                                    style={{ marginTop: "6px" }}
                                                    id={idx}
                                                    name={
                                                      "ivigReasonUnk_linvoC" +
                                                      idx
                                                    }
                                                    checked={
                                                      data.ivigReasonUnk_linvoC
                                                    }
                                                    onChange={HandleFieldChange}
                                                  />
                                                  <span className="radio-text-padding">
                                                    Unknown
                                                  </span>
                                                </label>
                                              </div>
                                              <ErrorField
                                                show={
                                                  data.ivigReasonEmptyHard_linvoC
                                                }
                                                message={
                                                  ELCharacteristics_MSGS.ivigReasonEmptyHardC
                                                }
                                                style={{
                                                  paddingLeft: "0px",
                                                }}
                                              />
                                            </div>
                                          </td>

                                          {/* Initial dose strength */}
                                          <td
                                            style={{
                                              border: "1px solid black",
                                              padding: "8px",
                                            }}
                                          >
                                            <div>
                                              {data.ivigDosingType_linvoC ===
                                                1 && (
                                                <div
                                                  className="option-without-input"
                                                  style={{ display: "flex" }}
                                                >
                                                  <label className="option-fbox">
                                                    <input
                                                      type="radio"
                                                      id={idx}
                                                      name={
                                                        "ivigDoseStrengthRd_linvoC" +
                                                        idx
                                                      }
                                                      value={1}
                                                      checked={
                                                        data.ivigDoseStrengthRd_linvoC ===
                                                        1
                                                      }
                                                      onChange={
                                                        HandleFieldChange
                                                      }
                                                    />
                                                    <span
                                                      className="radio-text-padding"
                                                      style={{
                                                        paddingLeft: "10px",
                                                        marginLeft: "-5px",
                                                      }}
                                                    >
                                                      <input
                                                        type="text"
                                                        className="input-dash-ttcustom"
                                                        name={
                                                          "ivigDoseStrengthGTxt_linvoC" +
                                                          idx
                                                        }
                                                        id={idx}
                                                        value={
                                                          data.ivigDoseStrengthGTxt_linvoC
                                                        }
                                                        onChange={
                                                          HandleFieldChange
                                                        }
                                                        maxLength={6}
                                                      />{" "}
                                                      g
                                                    </span>
                                                  </label>
                                                </div>
                                              )}

                                              {data.ivigDosingType_linvoC ===
                                                2 && (
                                                <div
                                                  className="option-without-input"
                                                  style={{ display: "flex" }}
                                                >
                                                  <label className="option-fbox">
                                                    <input
                                                      type="radio"
                                                      id={idx}
                                                      name={
                                                        "ivigDoseStrengthRd_linvoC" +
                                                        idx
                                                      }
                                                      value={3}
                                                      checked={
                                                        data.ivigDoseStrengthRd_linvoC ===
                                                        3
                                                      }
                                                      onChange={
                                                        HandleFieldChange
                                                      }
                                                    />
                                                    <span
                                                      className="radio-text-padding"
                                                      style={{
                                                        paddingLeft: "10px",
                                                        marginLeft: "-5px",
                                                      }}
                                                    >
                                                      <input
                                                        type="text"
                                                        className="input-dash-ttcustom"
                                                        name={
                                                          "ivigDoseStrengthMgKgTxt_linvoC" +
                                                          idx
                                                        }
                                                        id={idx}
                                                        value={
                                                          data.ivigDoseStrengthMgKgTxt_linvoC
                                                        }
                                                        onChange={
                                                          HandleFieldChange
                                                        }
                                                        onKeyDown={
                                                          AllowNumericWithDecimal
                                                        }
                                                        maxLength={6}
                                                      />{" "}
                                                      mg/kg
                                                    </span>
                                                  </label>
                                                </div>
                                              )}

                                              <div
                                                className="option-without-input"
                                                style={{ display: "flex" }}
                                              >
                                                <label className="option-fbox">
                                                  <input
                                                    type="radio"
                                                    id={idx}
                                                    name={
                                                      "ivigDoseStrengthRd_linvoC" +
                                                      idx
                                                    }
                                                    value={2}
                                                    checked={
                                                      data.ivigDoseStrengthRd_linvoC ===
                                                      2
                                                    }
                                                    onChange={HandleFieldChange}
                                                  />
                                                  <span
                                                    className="radio-text-padding"
                                                    style={{
                                                      paddingLeft: "10px",
                                                      marginLeft: "-5px",
                                                    }}
                                                  >
                                                    Other, please specify:
                                                    <div>
                                                      <textarea
                                                        className="input-dash"
                                                        name={
                                                          "ivigDoseStrengthOtherTxt_linvoC" +
                                                          idx
                                                        }
                                                        id={idx}
                                                        value={
                                                          data.ivigDoseStrengthOtherTxt_linvoC
                                                        }
                                                        onChange={
                                                          HandleFieldChange
                                                        }
                                                        maxLength={300}
                                                      />
                                                    </div>
                                                  </span>
                                                </label>
                                              </div>

                                              <div
                                                className="option-without-input"
                                                style={{ display: "flex" }}
                                              >
                                                <label className="option-fbox">
                                                  <input
                                                    type="radio"
                                                    id={idx}
                                                    name={
                                                      "ivigDoseStrengthRd_linvoC" +
                                                      idx
                                                    }
                                                    value={99}
                                                    checked={
                                                      data.ivigDoseStrengthRd_linvoC ===
                                                      99
                                                    }
                                                    onChange={HandleFieldChange}
                                                  />
                                                  <span
                                                    className="radio-text-padding"
                                                    style={{
                                                      paddingLeft: "10px",
                                                      marginLeft: "-5px",
                                                    }}
                                                  >
                                                    Unknown
                                                  </span>
                                                </label>
                                              </div>

                                              <ErrorField
                                                show={
                                                  data.ivigDoseStrengthEmptyHard_linvoC
                                                }
                                                message={
                                                  ELCharacteristics_MSGS.ivigDoseStrengthEmptyHardC
                                                }
                                                style={{
                                                  paddingLeft: "0px",
                                                }}
                                              />
                                              <ErrorField
                                                show={
                                                  data.ivigDoseStrengthRangeInvalid_linvoC
                                                }
                                                message={
                                                  ELCharacteristics_MSGS.ivigDoseStrengthRangeInvalidC
                                                }
                                                style={{
                                                  paddingLeft: "0px",
                                                }}
                                              />
                                              <ErrorField
                                                show={
                                                  data.ivigDoseStrengthMgKgRangeInvalid_linvoC
                                                }
                                                message={
                                                  ELCharacteristics_MSGS.ivigDoseStrengthMgKgRangeInvalidC
                                                }
                                                style={{
                                                  paddingLeft: "0px",
                                                }}
                                              />
                                              <ErrorField
                                                show={
                                                  data.ivigDoseStrengthOtherTxtEmptyHard_linvoC
                                                }
                                                message={
                                                  ELCharacteristics_MSGS.ivigDoseStrengthOtherTxtEmptyHardC
                                                }
                                                style={{
                                                  paddingLeft: "0px",
                                                }}
                                              />
                                            </div>
                                          </td>

                                          {/* Last IgG level */}
                                          <td
                                            style={{
                                              border: "1px solid black",
                                              padding: "8px",
                                            }}
                                          >
                                            <div>
                                              <div className="option-without-input">
                                                <label className="option-fbox">
                                                  <input
                                                    type="radio"
                                                    id={idx}
                                                    name={
                                                      "ivigLastIgg_linvoC" + idx
                                                    }
                                                    value={1}
                                                    checked={
                                                      data.ivigLastIgg_linvoC ===
                                                      1
                                                    }
                                                    onChange={HandleFieldChange}
                                                  />
                                                  <span className="radio-text-padding">
                                                    {"<"} 400 mg/dL
                                                  </span>
                                                </label>
                                              </div>
                                              <div className="option-without-input">
                                                <label className="option-fbox">
                                                  <input
                                                    type="radio"
                                                    id={idx}
                                                    name={
                                                      "ivigLastIgg_linvoC" + idx
                                                    }
                                                    value={2}
                                                    checked={
                                                      data.ivigLastIgg_linvoC ===
                                                      2
                                                    }
                                                    onChange={HandleFieldChange}
                                                  />
                                                  <span className="radio-text-padding">
                                                    400-500 mg/dL
                                                  </span>
                                                </label>
                                              </div>
                                              <div className="option-without-input">
                                                <label className="option-fbox">
                                                  <input
                                                    type="radio"
                                                    id={idx}
                                                    name={
                                                      "ivigLastIgg_linvoC" + idx
                                                    }
                                                    value={3}
                                                    checked={
                                                      data.ivigLastIgg_linvoC ===
                                                      3
                                                    }
                                                    onChange={HandleFieldChange}
                                                  />
                                                  <span className="radio-text-padding">
                                                    {">"} 500 mg/dL
                                                  </span>
                                                </label>
                                              </div>
                                              <div className="option-without-input">
                                                <label className="option-fbox">
                                                  <input
                                                    type="radio"
                                                    id={idx}
                                                    name={
                                                      "ivigLastIgg_linvoC" + idx
                                                    }
                                                    value={99}
                                                    checked={
                                                      data.ivigLastIgg_linvoC ===
                                                      99
                                                    }
                                                    onChange={HandleFieldChange}
                                                  />
                                                  <span className="radio-text-padding">
                                                    Unknown
                                                  </span>
                                                </label>
                                              </div>
                                              <ErrorField
                                                show={
                                                  data.ivigLastIggEmptyHard_linvoC
                                                }
                                                message={
                                                  ELCharacteristics_MSGS.ivigLastIggEmptyHardC
                                                }
                                                style={{
                                                  paddingLeft: "0px",
                                                }}
                                              />
                                            </div>
                                          </td>

                                          {/* Body weight ±1 month of initiation date */}
                                          {data.ivigDosingType_linvoC === 2 ? (
                                            <td
                                              style={{
                                                border: "1px solid black",
                                                padding: "8px",
                                              }}
                                            >
                                              <div>
                                                <div
                                                  className="option-without-input"
                                                  style={{ display: "flex" }}
                                                >
                                                  <label className="option-fbox">
                                                    <input
                                                      type="radio"
                                                      id={idx}
                                                      name={
                                                        "ivigBodyWeightRd_linvoC" +
                                                        idx
                                                      }
                                                      value={1}
                                                      checked={
                                                        data.ivigBodyWeightRd_linvoC ===
                                                        1
                                                      }
                                                      onChange={
                                                        HandleFieldChange
                                                      }
                                                    />
                                                    <span
                                                      className="radio-text-padding"
                                                      style={{
                                                        paddingLeft: "10px",
                                                        marginLeft: "-5px",
                                                      }}
                                                    >
                                                      <input
                                                        type="text"
                                                        className="input-dash-ttcustom"
                                                        name={
                                                          "ivigBodyWeightKgTxt_linvoC" +
                                                          idx
                                                        }
                                                        id={idx}
                                                        value={
                                                          data.ivigBodyWeightKgTxt_linvoC
                                                        }
                                                        onChange={
                                                          HandleFieldChange
                                                        }
                                                        onKeyDown={
                                                          AllowNumericWithDecimal
                                                        }
                                                        maxLength={10}
                                                      />{" "}
                                                      kg
                                                    </span>
                                                  </label>
                                                </div>

                                                <div
                                                  className="option-without-input"
                                                  style={{ display: "flex" }}
                                                >
                                                  <label className="option-fbox">
                                                    <input
                                                      type="radio"
                                                      id={idx}
                                                      name={
                                                        "ivigBodyWeightRd_linvoC" +
                                                        idx
                                                      }
                                                      value={2}
                                                      checked={
                                                        data.ivigBodyWeightRd_linvoC ===
                                                        2
                                                      }
                                                      onChange={
                                                        HandleFieldChange
                                                      }
                                                    />
                                                    <span
                                                      className="radio-text-padding"
                                                      style={{
                                                        paddingLeft: "10px",
                                                        marginLeft: "-5px",
                                                      }}
                                                    >
                                                      <input
                                                        type="text"
                                                        className="input-dash-ttcustom"
                                                        name={
                                                          "ivigBodyWeightLbsTxt_linvoC" +
                                                          idx
                                                        }
                                                        id={idx}
                                                        value={
                                                          data.ivigBodyWeightLbsTxt_linvoC
                                                        }
                                                        onChange={
                                                          HandleFieldChange
                                                        }
                                                        onKeyDown={
                                                          AllowNumericWithDecimal
                                                        }
                                                        maxLength={10}
                                                      />{" "}
                                                      lbs
                                                    </span>
                                                  </label>
                                                </div>

                                                <div
                                                  className="option-without-input"
                                                  style={{ display: "flex" }}
                                                >
                                                  <label className="option-fbox">
                                                    <input
                                                      type="radio"
                                                      id={idx}
                                                      name={
                                                        "ivigBodyWeightRd_linvoC" +
                                                        idx
                                                      }
                                                      value={99}
                                                      checked={
                                                        data.ivigBodyWeightRd_linvoC ===
                                                        99
                                                      }
                                                      onChange={
                                                        HandleFieldChange
                                                      }
                                                    />
                                                    <span
                                                      className="radio-text-padding"
                                                      style={{
                                                        paddingLeft: "10px",
                                                        marginLeft: "-5px",
                                                      }}
                                                    >
                                                      Unknown
                                                    </span>
                                                  </label>
                                                </div>

                                                <ErrorField
                                                  show={
                                                    data.ivigBodyWeightEmptyHard_linvoC
                                                  }
                                                  message={
                                                    ELCharacteristics_MSGS.ivigBodyWeightEmptyHardC
                                                  }
                                                  style={{
                                                    paddingLeft: "0px",
                                                  }}
                                                />
                                                <ErrorField
                                                  show={
                                                    data.ivigBodyWeightRangeInvalid_linvoC
                                                  }
                                                  message={
                                                    data.ivigBodyWeightRd_linvoC ===
                                                    1
                                                      ? ELCharacteristics_MSGS.ivigBodyWeightKgRangeInvalidC
                                                      : ELCharacteristics_MSGS.ivigBodyWeightLbsRangeInvalidC
                                                  }
                                                  style={{
                                                    paddingLeft: "0px",
                                                  }}
                                                />
                                              </div>
                                            </td>
                                          ) : (
                                            <td
                                              style={{
                                                border: "1px solid black",
                                                textAlign: "center",
                                                padding: "8px",
                                              }}
                                            >
                                              <span className="not-applicable">
                                                <b>Not applicable</b>
                                              </span>
                                            </td>
                                          )}
                                        </>
                                      ) : (
                                        <td
                                          colSpan={6}
                                          style={{
                                            border: "1px solid black",
                                            textAlign: "center",
                                            padding: "8px",
                                          }}
                                        >
                                          <span className="not-applicable">
                                            <b>Not applicable</b>
                                          </span>
                                        </td>
                                      )}
                                    </tr>
                                  ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </div>
                    )}
                    {/* 40.C end - Linvoseltamab */}

                    {/* 40.1.A start -- */}
                    {!isNewEcrfC &&
                      isNewEcrfB &&
                      showQ40Elr &&
                      Q37ElrexDetails[0]?.Q40dosingfreqRd != null &&
                      Q37ElrexDetails[0]?.Q40dosingfreqRd != 1 && (
                        <div className="question question-weight">
                          <span>Q40.1.B</span>
                          <span className="quest-text-pad">
                            Please provide{" "}
                            <i>information on each IGRT administration</i>{" "}
                            received during the patient’s treatment with{" "}
                            <span
                              style={{
                                color: PageDesignConstants.fontColor,
                                fontWeight: "bolder",
                              }}
                            >
                              {showQ40Elr && (
                                <>
                                  ELREXFIO<sup>TM</sup> (elranatamab-bcmm).
                                </>
                              )}
                            </span>{" "}
                            Add additional rows for each assessment, as needed.
                          </span>
                        </div>
                      )}

                    {!isNewEcrfC &&
                      isNewEcrfB &&
                      showQ40Elr &&
                      Q37ElrexDetails[0]?.Q40dosingfreqRd != null &&
                      Q37ElrexDetails[0]?.Q40dosingfreqRd != 1 && (
                        <div className="dependent-section">
                          <ErrorField
                            show={q40Elr_WarningHard}
                            message={ELCharacteristics_MSGS.WarningForIVIGQ401a}
                            style={{ paddingLeft: "0px" }}
                          />
                          {/* Elrex */}
                          {isNewEcrfB &&
                            showQ40Elr &&
                            Q401aElrDetails.length > 0 &&
                            Q401aElrDetails.map((data, idx) => (
                              <div className="loop-section-ul" key={idx}>
                                <div className="loop-head">
                                  <span
                                    className="question-weight"
                                    style={{ float: "inline-start" }}
                                  >
                                    {GetOrdinal(idx + 1)} IGRT Administration
                                  </span>
                                  {idx !== 0 && (
                                    <div
                                      className="delete-btn-div"
                                      style={{ float: "inline-end" }}
                                    >
                                      <img
                                        width="16px"
                                        src="../Assets/images/Icon-material-delete.png"
                                        alt=""
                                        style={{ cursor: "pointer" }}
                                        onClick={() => DeleteQ401aElrRow(idx)}
                                      />
                                    </div>
                                  )}
                                </div>

                                <div className="question-bot-sapce">
                                  <div
                                    className="sub-question"
                                    style={{ overflowX: "auto" }}
                                  >
                                    <table
                                      style={{
                                        border: "1px solid black",
                                        borderCollapse: "collapse",
                                        width: "100%",
                                      }}
                                    >
                                      <thead>
                                        <tr>
                                          <th
                                            style={{
                                              border: "1px solid black",
                                              padding: "8px",
                                              textAlign: "center",
                                            }}
                                          >
                                            Treatment
                                          </th>
                                          <th
                                            style={{
                                              border: "1px solid black",
                                              padding: "8px",
                                              textAlign: "center",
                                              width: "33%",
                                            }}
                                          >
                                            Date of IGRT administration
                                          </th>

                                          <th
                                            style={{
                                              border: "1px solid black",
                                              padding: "8px",
                                              textAlign: "center",
                                              width: "16%",
                                            }}
                                          >
                                            Intended dosing frequency
                                          </th>

                                          <th
                                            style={{
                                              border: "1px solid black",
                                              padding: "8px",
                                              textAlign: "center",
                                              width: "15%",
                                            }}
                                          >
                                            Reason for receiving IGRT
                                          </th>

                                          <th
                                            style={{
                                              border: "1px solid black",
                                              padding: "8px",
                                              textAlign: "center",
                                              width: "45%",
                                            }}
                                          >
                                            Dosing type
                                          </th>

                                          <th
                                            style={{
                                              border: "1px solid black",
                                              padding: "8px",
                                              textAlign: "center",
                                              width: "45%",
                                            }}
                                          >
                                            Dosage
                                          </th>

                                          <th
                                            style={{
                                              border: "1px solid black",
                                              padding: "8px",
                                              textAlign: "center",
                                              width: "45%",
                                            }}
                                          >
                                            Body Weight
                                          </th>

                                          <th
                                            style={{
                                              border: "1px solid black",
                                              padding: "8px",
                                              textAlign: "center",
                                              width: "45%",
                                            }}
                                          >
                                            Date of weight assessment
                                          </th>
                                        </tr>
                                      </thead>

                                      <tbody>
                                        <tr key={idx}>
                                          <td
                                            style={{
                                              border: "1px solid black",
                                              padding: "8px",
                                            }}
                                          >
                                            <span className="quest-text-pad">
                                              <>
                                                ELREXFIO<sup>TM</sup>{" "}
                                                (elranatamab-bcmm).
                                              </>
                                            </span>
                                          </td>

                                          {/* IVIG admission date*/}
                                          <td
                                            style={{
                                              border: "1px solid black",
                                              padding: "8px",
                                            }}
                                          >
                                            <div>
                                              <div
                                                className="option-without-input"
                                                style={{ display: "flex" }}
                                              >
                                                <input
                                                  type="radio"
                                                  id={idx}
                                                  name={
                                                    "Q401ainit_dt_Rd_Elr" + idx
                                                  }
                                                  value={1}
                                                  checked={
                                                    data.Q401ainit_dt_Rd_Elr ===
                                                    1
                                                  }
                                                  onChange={
                                                    HandleQ401aFieldChange
                                                  }
                                                />
                                                <span
                                                  className="radio-text-padding"
                                                  style={{
                                                    paddingLeft: "10px",
                                                    marginLeft: "-5px",
                                                  }}
                                                >
                                                  <DatePickerComponent
                                                    ctrlId={
                                                      "Q401ainit_Elr_dt-" + idx
                                                    }
                                                    HandleDateChange={
                                                      HandleQ401aRepeaterDateChange
                                                    }
                                                    optionId={1}
                                                    minDate={dayjs(
                                                      "01-01-1920",
                                                    )}
                                                    maxDate={dayjs()}
                                                    locale="en" // current it is mm/dd/yyyy but for dd/mm/yyyy use "en-gb"
                                                    date={data.Q401ainit_Elr_dt}
                                                  />
                                                </span>
                                              </div>

                                              <div
                                                className="option-without-input"
                                                style={{ display: "flex" }}
                                              >
                                                <label className="option-fbox">
                                                  <input
                                                    type="radio"
                                                    id={idx}
                                                    name={
                                                      "Q401ainit_dt_Rd_Elr" +
                                                      idx
                                                    }
                                                    value={99}
                                                    checked={
                                                      data.Q401ainit_dt_Rd_Elr ===
                                                      99
                                                    }
                                                    onChange={
                                                      HandleQ401aFieldChange
                                                    }
                                                  />
                                                  <span
                                                    style={{
                                                      paddingLeft: "10px",
                                                      marginLeft: "-5px",
                                                    }}
                                                  >
                                                    Date of initiation unknown
                                                  </span>
                                                </label>
                                              </div>

                                              <ErrorField
                                                show={data.Q401aInit_Elr_RdHard}
                                                message={
                                                  ELCharacteristics_MSGS.Q401aSelect_RdHard
                                                }
                                                style={{ paddingLeft: "0px" }}
                                              />

                                              <ErrorField
                                                show={data.Q401aInit_Elr_dtHard}
                                                message={
                                                  ELCharacteristics_MSGS.Q401aDateAdm_Hard
                                                }
                                                style={{ paddingLeft: "0px" }}
                                              />
                                              <ErrorField
                                                show={
                                                  data.Q401aInvalidQ401ainit_Elr_dt
                                                }
                                                message={
                                                  CommonError_Msgs.InvalidDate
                                                }
                                                style={{ paddingLeft: "0px" }}
                                              />
                                              <ErrorField
                                                show={
                                                  data.Q401aInit_Elr_dt_before_eof
                                                }
                                                message={
                                                  ELCharacteristics_MSGS.DateBeforeEOFAfterIndex
                                                }
                                                style={{ paddingLeft: "0px" }}
                                              />
                                            </div>
                                          </td>

                                          {/* Intended Dosing */}
                                          <td
                                            style={{
                                              border: "1px solid black",
                                              padding: "8px",
                                              textAlign: "start",
                                            }}
                                          >
                                            <div>
                                              <div
                                                className="option-without-input"
                                                style={{
                                                  display: "flex",
                                                }}
                                              >
                                                <label className="option-fbox">
                                                  <input
                                                    type="radio"
                                                    id={idx}
                                                    name={
                                                      "Q401aIntendedDosing_Elr" +
                                                      idx
                                                    }
                                                    value={1}
                                                    checked={
                                                      data.Q401aIntendedDosing_Elr ===
                                                      1
                                                    }
                                                    onChange={
                                                      HandleQ401aFieldChange
                                                    }
                                                  />
                                                  <span
                                                    style={{
                                                      paddingLeft: "10px",
                                                      display: "flex",
                                                    }}
                                                  >
                                                    <span>
                                                      <input
                                                        className="input-dash-ttcustom"
                                                        id={idx}
                                                        name={
                                                          "Q401aIntendedDosing_Elr_WeekValue" +
                                                          idx
                                                        }
                                                        value={
                                                          data.Q401aIntendedDosing_Elr_WeekValue
                                                        }
                                                        type="text"
                                                        onChange={
                                                          HandleQ401aFieldChange
                                                        }
                                                        onKeyDown={(e) => {
                                                          if (
                                                            [
                                                              "Backspace",
                                                              "ArrowLeft",
                                                              "ArrowRight",
                                                              "Delete",
                                                              "Tab",
                                                            ].includes(e.key)
                                                          )
                                                            return;
                                                          if (
                                                            !/[0-9]/.test(e.key)
                                                          )
                                                            e.preventDefault();
                                                        }}
                                                      />
                                                    </span>
                                                    <span>
                                                      &nbsp;times per week
                                                    </span>
                                                  </span>
                                                </label>
                                              </div>

                                              <div
                                                className="option-without-input"
                                                style={{
                                                  display: "flex",
                                                }}
                                              >
                                                <label className="option-fbox">
                                                  <input
                                                    type="radio"
                                                    id={idx}
                                                    name={
                                                      "Q401aIntendedDosing_Elr" +
                                                      idx
                                                    }
                                                    value={2}
                                                    checked={
                                                      data.Q401aIntendedDosing_Elr ===
                                                      2
                                                    }
                                                    onChange={
                                                      HandleQ401aFieldChange
                                                    }
                                                  />
                                                  <span
                                                    style={{
                                                      paddingLeft: "10px",
                                                      display: "flex",
                                                    }}
                                                  >
                                                    <span>
                                                      <input
                                                        className="input-dash-ttcustom"
                                                        id={idx}
                                                        name={
                                                          "Q401aIntendedDosing_Elr_MonthValue" +
                                                          idx
                                                        }
                                                        value={
                                                          data.Q401aIntendedDosing_Elr_MonthValue
                                                        }
                                                        type="text"
                                                        onChange={
                                                          HandleQ401aFieldChange
                                                        }
                                                        onKeyDown={(e) => {
                                                          if (
                                                            [
                                                              "Backspace",
                                                              "ArrowLeft",
                                                              "ArrowRight",
                                                              "Delete",
                                                              "Tab",
                                                            ].includes(e.key)
                                                          )
                                                            return;
                                                          if (
                                                            !/[0-9]/.test(e.key)
                                                          )
                                                            e.preventDefault();
                                                        }}
                                                      />
                                                    </span>
                                                    <span>
                                                      &nbsp;times per month{" "}
                                                    </span>
                                                  </span>
                                                </label>
                                              </div>

                                              <div
                                                className="option-without-input"
                                                style={{
                                                  display: "flex",
                                                }}
                                              >
                                                <label
                                                  className="option-fbox"
                                                  style={{
                                                    display: "inline-flex",
                                                    alignItems: "center",
                                                    whiteSpace: "nowrap",
                                                  }}
                                                >
                                                  <input
                                                    type="radio"
                                                    id={idx}
                                                    name={
                                                      "Q401aIntendedDosing_Elr" +
                                                      idx
                                                    }
                                                    value={3}
                                                    checked={
                                                      data.Q401aIntendedDosing_Elr ===
                                                      3
                                                    }
                                                    onChange={
                                                      HandleQ401aFieldChange
                                                    }
                                                  />
                                                  <span
                                                    style={{
                                                      paddingLeft: "10px",
                                                      display: "inline-flex",
                                                      alignItems: "center",
                                                      whiteSpace: "nowrap",
                                                      gap: "4px",
                                                    }}
                                                  >
                                                    <span>
                                                      Once every &nbsp;
                                                    </span>
                                                    <span>
                                                      <input
                                                        className="input-dash-ttcustom"
                                                        id={idx}
                                                        name={
                                                          "Q401aIntendedDosing_Elr_OnceWeekValue" +
                                                          idx
                                                        }
                                                        value={
                                                          data.Q401aIntendedDosing_Elr_OnceWeekValue
                                                        }
                                                        type="text"
                                                        onChange={
                                                          HandleQ401aFieldChange
                                                        }
                                                        onKeyDown={(e) => {
                                                          if (
                                                            [
                                                              "Backspace",
                                                              "ArrowLeft",
                                                              "ArrowRight",
                                                              "Delete",
                                                              "Tab",
                                                            ].includes(e.key)
                                                          )
                                                            return;
                                                          if (
                                                            !/[0-9]/.test(e.key)
                                                          )
                                                            e.preventDefault();
                                                        }}
                                                      />
                                                    </span>
                                                    <span>&nbsp;weeks </span>
                                                  </span>
                                                </label>
                                              </div>

                                              <div
                                                className="option-without-input"
                                                style={{
                                                  display: "flex",
                                                }}
                                              >
                                                <label
                                                  className="option-fbox"
                                                  style={{
                                                    display: "inline-flex",
                                                    alignItems: "center",
                                                    whiteSpace: "nowrap",
                                                  }}
                                                >
                                                  <input
                                                    type="radio"
                                                    id={idx}
                                                    name={
                                                      "Q401aIntendedDosing_Elr" +
                                                      idx
                                                    }
                                                    value={4}
                                                    checked={
                                                      data.Q401aIntendedDosing_Elr ===
                                                      4
                                                    }
                                                    onChange={
                                                      HandleQ401aFieldChange
                                                    }
                                                  />
                                                  <span
                                                    style={{
                                                      paddingLeft: "10px",
                                                      display: "inline-flex",
                                                      alignItems: "center",
                                                      whiteSpace: "nowrap",
                                                      gap: "4px",
                                                    }}
                                                  >
                                                    <span>
                                                      Once every &nbsp;
                                                    </span>
                                                    <span>
                                                      <input
                                                        className="input-dash-ttcustom"
                                                        id={idx}
                                                        name={
                                                          "Q401aIntendedDosing_Elr_OnceMonthValue" +
                                                          idx
                                                        }
                                                        value={
                                                          data.Q401aIntendedDosing_Elr_OnceMonthValue
                                                        }
                                                        type="text"
                                                        onChange={
                                                          HandleQ401aFieldChange
                                                        }
                                                        onKeyDown={(e) => {
                                                          if (
                                                            [
                                                              "Backspace",
                                                              "ArrowLeft",
                                                              "ArrowRight",
                                                              "Delete",
                                                              "Tab",
                                                            ].includes(e.key)
                                                          )
                                                            return;
                                                          if (
                                                            !/[0-9]/.test(e.key)
                                                          )
                                                            e.preventDefault();
                                                        }}
                                                      />
                                                    </span>
                                                    <span>&nbsp;months </span>
                                                  </span>
                                                </label>
                                              </div>

                                              <div
                                                className="option-without-input"
                                                style={{
                                                  display: "flex",
                                                }}
                                              >
                                                <label className="option-fbox">
                                                  <input
                                                    type="radio"
                                                    id={idx}
                                                    name={
                                                      "Q401aIntendedDosing_Elr" +
                                                      idx
                                                    }
                                                    value={5}
                                                    checked={
                                                      data.Q401aIntendedDosing_Elr ===
                                                      5
                                                    }
                                                    onChange={
                                                      HandleQ401aFieldChange
                                                    }
                                                  />
                                                  <span
                                                    style={{
                                                      paddingLeft: "10px",
                                                      display: "flex",
                                                    }}
                                                  >
                                                    <span>
                                                      Other, please specify{" "}
                                                    </span>
                                                    <span>
                                                      <input
                                                        className="input-dash-ttcustom"
                                                        id={idx}
                                                        name={
                                                          "Q401aIntendedDosing_Elr_OtherValue" +
                                                          idx
                                                        }
                                                        value={
                                                          data.Q401aIntendedDosing_Elr_OtherValue
                                                        }
                                                        type="text"
                                                        onChange={
                                                          HandleQ401aFieldChange
                                                        }
                                                      />
                                                    </span>
                                                  </span>
                                                </label>
                                              </div>

                                              <div
                                                className="option-without-input"
                                                style={{
                                                  display: "flex",
                                                }}
                                              >
                                                <label className="option-fbox">
                                                  <input
                                                    type="radio"
                                                    id={idx}
                                                    name={
                                                      "Q401aIntendedDosing_Elr" +
                                                      idx
                                                    }
                                                    value={99}
                                                    checked={
                                                      data.Q401aIntendedDosing_Elr ===
                                                      99
                                                    }
                                                    onChange={
                                                      HandleQ401aFieldChange
                                                    }
                                                  />
                                                  <span
                                                    style={{
                                                      paddingLeft: "10px",
                                                      display: "flex",
                                                    }}
                                                  >
                                                    Unknown
                                                  </span>
                                                </label>
                                              </div>

                                              <ErrorField
                                                show={
                                                  data.Q401aIntendedDosingEmpty_Elr
                                                }
                                                message={
                                                  ELCharacteristics_MSGS.Q401aSelect_RdHard
                                                }
                                                style={{
                                                  paddingLeft: "0px",
                                                }}
                                              />
                                              <ErrorField
                                                show={
                                                  data.Q401aIntendedDosingRangeInvalid_Elr
                                                }
                                                message={
                                                  ELCharacteristics_MSGS.Q401aFrequencyEmptyHard
                                                }
                                                style={{
                                                  paddingLeft: "0px",
                                                }}
                                              />
                                            </div>
                                          </td>

                                          {/* Reason of IVIG */}
                                          <td
                                            style={{
                                              border: "1px solid black",
                                              padding: "8px",
                                            }}
                                          >
                                            <div>
                                              <div
                                                className="option-without-input"
                                                style={{ display: "flex" }}
                                              >
                                                <label className="option-fbox">
                                                  <input
                                                    type="checkbox"
                                                    id={idx}
                                                    name={
                                                      "Q401aPrimaryProf_Elr" +
                                                      idx
                                                    }
                                                    checked={
                                                      data.Q401aPrimaryProf_Elr
                                                    }
                                                    onChange={
                                                      HandleQ401aFieldChange
                                                    }
                                                  />
                                                  <span
                                                    className="radio-text-padding"
                                                    style={{
                                                      paddingLeft: "10px",
                                                      marginLeft: "-5px",
                                                    }}
                                                  >
                                                    Primary prophylactic for
                                                    infections
                                                  </span>
                                                </label>
                                              </div>
                                              <div
                                                className="option-without-input"
                                                style={{ display: "flex" }}
                                              >
                                                <label className="option-fbox">
                                                  <input
                                                    type="checkbox"
                                                    id={idx}
                                                    name={
                                                      "Q401aSecProf_Elr" + idx
                                                    }
                                                    checked={
                                                      data.Q401aSecProf_Elr
                                                    }
                                                    onChange={
                                                      HandleQ401aFieldChange
                                                    }
                                                  />
                                                  <span
                                                    className="radio-text-padding"
                                                    style={{
                                                      paddingLeft: "10px",
                                                      marginLeft: "-5px",
                                                    }}
                                                  >
                                                    Secondary prophylactic for
                                                    infections
                                                  </span>
                                                </label>
                                              </div>
                                              <div
                                                className="option-without-input"
                                                style={{
                                                  display: "flex",
                                                  alignItems: "flex-start",
                                                }}
                                              >
                                                <label className="option-fbox">
                                                  <input
                                                    type="checkbox"
                                                    id={idx}
                                                    name={
                                                      "Q401aOtherProf_Elr" + idx
                                                    }
                                                    checked={
                                                      data.Q401aOtherProf_Elr
                                                    }
                                                    onChange={
                                                      HandleQ401aFieldChange
                                                    }
                                                  />
                                                  <span
                                                    className="radio-text-padding"
                                                    style={{
                                                      paddingLeft: "10px",
                                                      marginLeft: "-5px",
                                                    }}
                                                  >
                                                    Other reason
                                                  </span>
                                                </label>
                                              </div>
                                              <div
                                                className="option-without-input"
                                                style={{ display: "flex" }}
                                              >
                                                <label className="option-fbox">
                                                  <input
                                                    type="radio"
                                                    id={idx}
                                                    name={
                                                      "Q401aUnknownProf_Elr" +
                                                      idx
                                                    }
                                                    value={99}
                                                    checked={
                                                      data.Q401aUnknownProf_Elr ===
                                                      99
                                                    }
                                                    onChange={
                                                      HandleQ401aFieldChange
                                                    }
                                                  />
                                                  <span
                                                    className="radio-text-padding"
                                                    style={{
                                                      paddingLeft: "10px",
                                                      marginLeft: "-5px",
                                                    }}
                                                  >
                                                    Unknown
                                                  </span>
                                                </label>
                                              </div>

                                              <ErrorField
                                                show={
                                                  data.Q401aReasonIvigEmptyHard_Elr
                                                }
                                                message={
                                                  ELCharacteristics_MSGS.Q401aSelectCheckbox_RdHard
                                                }
                                                style={{ paddingLeft: "0px" }}
                                              />
                                            </div>
                                          </td>

                                          {/* Dosing Type */}
                                          <td
                                            style={{
                                              border: "1px solid black",
                                              padding: "8px",
                                            }}
                                          >
                                            <div>
                                              <div
                                                className="option-without-input"
                                                style={{ display: "flex" }}
                                              >
                                                <label className="option-fbox">
                                                  <input
                                                    type="radio"
                                                    id={idx}
                                                    name={
                                                      "Q401aDosingType_Elr" +
                                                      idx
                                                    }
                                                    value={1}
                                                    checked={
                                                      data.Q401aDosingType_Elr ===
                                                      1
                                                    }
                                                    onChange={
                                                      HandleQ401aFieldChange
                                                    }
                                                  />
                                                  <span
                                                    className="radio-text-padding"
                                                    style={{
                                                      paddingLeft: "10px",
                                                      marginLeft: "-5px",
                                                    }}
                                                  >
                                                    Flat dosage
                                                  </span>
                                                </label>
                                              </div>
                                              <div
                                                className="option-without-input"
                                                style={{ display: "flex" }}
                                              >
                                                <label className="option-fbox">
                                                  <input
                                                    type="radio"
                                                    id={idx}
                                                    name={
                                                      "Q401aDosingType_Elr" +
                                                      idx
                                                    }
                                                    value={2}
                                                    checked={
                                                      data.Q401aDosingType_Elr ===
                                                      2
                                                    }
                                                    onChange={
                                                      HandleQ401aFieldChange
                                                    }
                                                  />
                                                  <span
                                                    className="radio-text-padding"
                                                    style={{
                                                      paddingLeft: "10px",
                                                      marginLeft: "-5px",
                                                    }}
                                                  >
                                                    Weight-based dosage
                                                  </span>
                                                </label>
                                              </div>

                                              <ErrorField
                                                show={
                                                  data.Q401aDosingType_ElrEmptyHard
                                                }
                                                message={
                                                  ELCharacteristics_MSGS.Q401aSelect_RdHard
                                                }
                                                style={{ paddingLeft: "0px" }}
                                              />
                                            </div>
                                          </td>

                                          {/* Dosage*/}
                                          <td
                                            style={{
                                              border: "1px solid black",
                                              padding: "8px",
                                              textAlign: "start",
                                            }}
                                          >
                                            <div>
                                              {data.Q401aDosingType_Elr ===
                                                1 && (
                                                <div
                                                  className="option-without-input"
                                                  style={{
                                                    display: "flex",
                                                  }}
                                                >
                                                  <label className="option-fbox">
                                                    <input
                                                      type="radio"
                                                      id={idx}
                                                      name={
                                                        "Q401aDosage_Elr" + idx
                                                      }
                                                      value={1}
                                                      checked={
                                                        data.Q401aDosage_Elr ===
                                                        1
                                                      }
                                                      onChange={
                                                        HandleQ401aFieldChange
                                                      }
                                                    />
                                                    <span
                                                      style={{
                                                        paddingLeft: "10px",
                                                        display: "flex",
                                                      }}
                                                    >
                                                      <input
                                                        className="input-dash-custom"
                                                        id={idx}
                                                        name={
                                                          "Q401aDosage_Elr_GValue" +
                                                          idx
                                                        }
                                                        value={
                                                          data.Q401aDosage_Elr_GValue
                                                        }
                                                        type="text"
                                                        onChange={
                                                          HandleQ401aFieldChange
                                                        }
                                                        onKeyDown={
                                                          AllowNumericWithDecimal
                                                        }
                                                        maxLength={10}
                                                      />
                                                      <span>&nbsp;g</span>
                                                    </span>
                                                  </label>
                                                </div>
                                              )}

                                              {data.Q401aDosingType_Elr ===
                                                2 && (
                                                <div
                                                  className="option-without-input"
                                                  style={{
                                                    display: "flex",
                                                  }}
                                                >
                                                  <label className="option-fbox">
                                                    <input
                                                      type="radio"
                                                      id={idx}
                                                      name={
                                                        "Q401aDosage_Elr" + idx
                                                      }
                                                      value={2}
                                                      checked={
                                                        data.Q401aDosage_Elr ===
                                                        2
                                                      }
                                                      onChange={
                                                        HandleQ401aFieldChange
                                                      }
                                                    />
                                                    <span
                                                      style={{
                                                        paddingLeft: "10px",
                                                        display: "flex",
                                                      }}
                                                    >
                                                      <span>
                                                        <input
                                                          className="input-dash-ttcustom"
                                                          id={idx}
                                                          name={
                                                            "Q401aDosage_Elr_MgValue" +
                                                            idx
                                                          }
                                                          value={
                                                            data.Q401aDosage_Elr_MgValue
                                                          }
                                                          type="text"
                                                          onChange={
                                                            HandleQ401aFieldChange
                                                          }
                                                          onKeyDown={
                                                            AllowNumericWithDecimal
                                                          }
                                                          maxLength={10}
                                                        />
                                                      </span>
                                                      <span>&nbsp;mg/kg </span>
                                                    </span>
                                                  </label>
                                                </div>
                                              )}

                                              <div
                                                className="option-without-input"
                                                style={{
                                                  display: "flex",
                                                }}
                                              >
                                                <label className="option-fbox">
                                                  <input
                                                    type="radio"
                                                    id={idx}
                                                    name={
                                                      "Q401aDosage_Elr" + idx
                                                    }
                                                    value={99}
                                                    checked={
                                                      data.Q401aDosage_Elr ===
                                                      99
                                                    }
                                                    onChange={
                                                      HandleQ401aFieldChange
                                                    }
                                                  />
                                                  <span
                                                    style={{
                                                      paddingLeft: "10px",
                                                      display: "flex",
                                                    }}
                                                  >
                                                    Unknown
                                                  </span>
                                                </label>
                                              </div>

                                              <ErrorField
                                                show={
                                                  data.Q401aDosageFrequencyEmpty_Elr
                                                }
                                                message={
                                                  ELCharacteristics_MSGS.Q401aSelect_RdHard
                                                }
                                                style={{
                                                  paddingLeft: "0px",
                                                }}
                                              />
                                              <ErrorField
                                                show={
                                                  data.Q401aDosageRangeInvalid_Elr
                                                }
                                                message={
                                                  data.Q401aDosage_Elr === 1
                                                    ? ELCharacteristics_MSGS.Q401aDosageEmptyHardG
                                                    : ELCharacteristics_MSGS.Q401aDosageEmptyHardMG
                                                }
                                                style={{
                                                  paddingLeft: "0px",
                                                }}
                                              />
                                            </div>
                                          </td>

                                          {/* Body Weight*/}
                                          {data.Q401aDosingType_Elr === 2 ? (
                                            <td
                                              style={{
                                                border: "1px solid black",
                                                padding: "8px",
                                                textAlign: "start",
                                              }}
                                            >
                                              <div>
                                                <div
                                                  className="option-without-input"
                                                  style={{
                                                    display: "flex",
                                                  }}
                                                >
                                                  <label className="option-fbox">
                                                    <input
                                                      type="radio"
                                                      id={idx}
                                                      name={
                                                        "Q401aWeight_Elr" + idx
                                                      }
                                                      value={1}
                                                      checked={
                                                        data.Q401aWeight_Elr ===
                                                        1
                                                      }
                                                      onChange={
                                                        HandleQ401aFieldChange
                                                      }
                                                    />
                                                    <span
                                                      style={{
                                                        paddingLeft: "10px",
                                                        display: "flex",
                                                      }}
                                                    >
                                                      <span>
                                                        <input
                                                          className="input-dash-ttcustom"
                                                          id={idx}
                                                          name={
                                                            "Q401aWeight_Value_KgElr" +
                                                            idx
                                                          }
                                                          value={
                                                            data.Q401aWeight_Value_KgElr
                                                          }
                                                          type="text"
                                                          onChange={
                                                            HandleQ401aFieldChange
                                                          }
                                                          onKeyDown={
                                                            AllowNumericWithDecimal
                                                          }
                                                          maxLength={10}
                                                        />
                                                      </span>
                                                      <span>&nbsp;kg</span>
                                                    </span>
                                                  </label>
                                                </div>

                                                <div
                                                  className="option-without-input"
                                                  style={{
                                                    display: "flex",
                                                  }}
                                                >
                                                  <label className="option-fbox">
                                                    <input
                                                      type="radio"
                                                      id={idx}
                                                      name={
                                                        "Q401aWeight_Elr" + idx
                                                      }
                                                      value={2}
                                                      checked={
                                                        data.Q401aWeight_Elr ===
                                                        2
                                                      }
                                                      onChange={
                                                        HandleQ401aFieldChange
                                                      }
                                                    />
                                                    <span
                                                      style={{
                                                        paddingLeft: "10px",
                                                        display: "flex",
                                                      }}
                                                    >
                                                      <span>
                                                        <input
                                                          className="input-dash-ttcustom"
                                                          id={idx}
                                                          name={
                                                            "Q401aWeight_Value_LbsElr" +
                                                            idx
                                                          }
                                                          value={
                                                            data.Q401aWeight_Value_LbsElr
                                                          }
                                                          type="text"
                                                          onChange={
                                                            HandleQ401aFieldChange
                                                          }
                                                          onKeyDown={
                                                            AllowNumericWithDecimal
                                                          }
                                                          maxLength={10}
                                                        />
                                                      </span>
                                                      <span>&nbsp;lbs </span>
                                                    </span>
                                                  </label>
                                                </div>

                                                <div
                                                  className="option-without-input"
                                                  style={{
                                                    display: "flex",
                                                  }}
                                                >
                                                  <label className="option-fbox">
                                                    <input
                                                      type="radio"
                                                      id={idx}
                                                      name={
                                                        "Q401aWeight_Elr" + idx
                                                      }
                                                      value={99}
                                                      checked={
                                                        data.Q401aWeight_Elr ===
                                                        99
                                                      }
                                                      onChange={
                                                        HandleQ401aFieldChange
                                                      }
                                                    />
                                                    <span
                                                      style={{
                                                        paddingLeft: "10px",
                                                        display: "flex",
                                                      }}
                                                    >
                                                      Unknown
                                                    </span>
                                                  </label>
                                                </div>

                                                <ErrorField
                                                  show={
                                                    data.Q401aWeightEmpty_Elr
                                                  }
                                                  message={
                                                    ELCharacteristics_MSGS.Q401aSelect_RdHard
                                                  }
                                                  style={{
                                                    paddingLeft: "0px",
                                                  }}
                                                />
                                                <ErrorField
                                                  show={
                                                    data.Q401aWeightRangeInvalid_Elr
                                                  }
                                                  message={
                                                    data.Q401aWeight_Elr === 1
                                                      ? ELCharacteristics_MSGS.Q401aBodyWeightEmptyHardKg
                                                      : ELCharacteristics_MSGS.Q401aBodyWeightEmptyHardLbs
                                                  }
                                                  style={{
                                                    paddingLeft: "0px",
                                                  }}
                                                />
                                              </div>
                                            </td>
                                          ) : (
                                            <td
                                              style={{
                                                border: "1px solid black",
                                                //alignContent: "flex-start",
                                                textAlign: "center",
                                                padding: "8px",
                                                //textAlign: 'start'
                                              }}
                                            >
                                              <span
                                                style={{
                                                  textAlign: "center",
                                                  verticalAlign: "center",
                                                }}
                                                className="not-applicable"
                                              >
                                                <b>Not applicable</b>
                                              </span>
                                            </td>
                                          )}

                                          {/* Weight assessment date */}

                                          {data.Q401aDosingType_Elr === 2 ? (
                                            <td
                                              style={{
                                                border: "1px solid black",
                                                padding: "8px",
                                              }}
                                            >
                                              <div>
                                                <div
                                                  className="option-without-input"
                                                  style={{ display: "flex" }}
                                                >
                                                  <input
                                                    type="radio"
                                                    id={idx}
                                                    name={
                                                      "Q401aWeight_Asses_Elr" +
                                                      idx
                                                    }
                                                    value={1}
                                                    checked={
                                                      data.Q401aWeight_Asses_Elr ===
                                                      1
                                                    }
                                                    onChange={
                                                      HandleQ401aFieldChange
                                                    }
                                                  />
                                                  <span
                                                    className="radio-text-padding"
                                                    style={{
                                                      paddingLeft: "10px",
                                                      marginLeft: "-5px",
                                                    }}
                                                  >
                                                    <DatePickerComponent
                                                      ctrlId={
                                                        "Q401aWeight_Asses_Elr_dt-" +
                                                        idx
                                                      }
                                                      HandleDateChange={
                                                        HandleQ401aRepeaterDateChange
                                                      }
                                                      optionId={1}
                                                      minDate={dayjs(
                                                        "01-01-1920",
                                                      )}
                                                      maxDate={dayjs()}
                                                      locale="en" // current it is mm/dd/yyyy but for dd/mm/yyyy use "en-gb"
                                                      date={
                                                        data.Q401aWeight_Asses_Elr_dt
                                                      }
                                                    />
                                                  </span>
                                                </div>

                                                <div
                                                  className="option-without-input"
                                                  style={{ display: "flex" }}
                                                >
                                                  <label className="option-fbox">
                                                    <input
                                                      type="radio"
                                                      id={idx}
                                                      name={
                                                        "Q401aWeight_Asses_Elr" +
                                                        idx
                                                      }
                                                      value={99}
                                                      checked={
                                                        data.Q401aWeight_Asses_Elr ===
                                                        99
                                                      }
                                                      onChange={
                                                        HandleQ401aFieldChange
                                                      }
                                                    />
                                                    <span
                                                      style={{
                                                        paddingLeft: "10px",
                                                        marginLeft: "-5px",
                                                      }}
                                                    >
                                                      Date of initiation unknown
                                                    </span>
                                                  </label>
                                                </div>

                                                <ErrorField
                                                  show={
                                                    data.Q401aWeight_AssesEmptyHard_Elr
                                                  }
                                                  message={
                                                    ELCharacteristics_MSGS.Q401aSelect_RdHard
                                                  }
                                                  style={{ paddingLeft: "0px" }}
                                                />

                                                <ErrorField
                                                  show={
                                                    data.Q401aWeight_AssesDtEmptyHard_Elr
                                                  }
                                                  message={
                                                    ELCharacteristics_MSGS.Q401aDateWeight_Hard
                                                  }
                                                  style={{ paddingLeft: "0px" }}
                                                />
                                                <ErrorField
                                                  show={
                                                    data.Q401aWeight_AssesDtInvalid_Elr
                                                  }
                                                  message={
                                                    CommonError_Msgs.InvalidDate
                                                  }
                                                  style={{ paddingLeft: "0px" }}
                                                />
                                                <ErrorField
                                                  show={
                                                    data.Q401aWeight_Asses_dt_before_eof_Elr
                                                  }
                                                  message={
                                                    ELCharacteristics_MSGS.DateBeforeEOFAfterIndex
                                                  }
                                                  style={{ paddingLeft: "0px" }}
                                                />
                                              </div>
                                            </td>
                                          ) : (
                                            <td
                                              style={{
                                                border: "1px solid black",
                                                textAlign: "center",
                                                padding: "8px",
                                              }}
                                            >
                                              <span
                                                style={{
                                                  textAlign: "center",
                                                  verticalAlign: "center",
                                                }}
                                                className="not-applicable"
                                              >
                                                <b>Not applicable</b>
                                              </span>
                                            </td>
                                          )}
                                        </tr>
                                      </tbody>
                                    </table>
                                  </div>
                                </div>
                              </div>
                            ))}
                          {/* Add button */}
                          {Q401aElrDetails && (
                            <div className="sub-question-bot-sapce-ul mt-2">
                              <div
                                className="answer-list-text"
                                onClick={AddQ401aElrRow}
                              >
                                <a>
                                  <img
                                    width="18px"
                                    src="../Assets/images/plus.png"
                                    alt=""
                                  />
                                </a>
                                <a className="radio-text-padding">
                                  Click here to add IVIG administration received
                                </a>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    {/* 40.1.A  end */}

                    {/* 40.1.A start -- && data.Q40dosingfreqRd_Lin != 1*/}
                    {!isNewEcrfC &&
                      isNewEcrfB &&
                      showQ40Linvo &&
                      Q37LinvoDetails[0]?.q40DosingFreqRd_linvo != null &&
                      Q37LinvoDetails[0]?.q40DosingFreqRd_linvo != 1 && (
                        <div>
                          <div className="question question-weight">
                            <span>Q40.1.B</span>
                            <span className="quest-text-pad">
                              Please provide{" "}
                              <i>information on each IGRT administration</i>{" "}
                              received during the patient’s treatment with{" "}
                              <span
                                style={{
                                  color: PageDesignConstants.fontColor,
                                  fontWeight: "bolder",
                                }}
                              >
                                {showQ40Linvo && (
                                  <>
                                    LYNOZYFIC<sup>TM</sup> (linvoseltamab-gcpt).
                                  </>
                                )}{" "}
                              </span>
                              Add additional rows for each assessment, as
                              needed.
                            </span>
                          </div>
                        </div>
                      )}

                    {!isNewEcrfC &&
                      isNewEcrfB &&
                      showQ40Linvo &&
                      Q37LinvoDetails[0]?.q40DosingFreqRd_linvo != null &&
                      Q37LinvoDetails[0]?.q40DosingFreqRd_linvo != 1 && (
                        <div className="dependent-section">
                          <ErrorField
                            show={q40Lin_WarningHard}
                            message={ELCharacteristics_MSGS.WarningForIVIGQ401a}
                            style={{ paddingLeft: "0px" }}
                          />

                          {/* Talvey */}
                          {showQ40Linvo &&
                            Q401aLinvoDetails.length > 0 &&
                            Q401aLinvoDetails.map((data, idx) => (
                              <div className="loop-section-ul" key={idx}>
                                <div className="loop-head">
                                  <span
                                    className="question-weight"
                                    style={{ float: "inline-start" }}
                                  >
                                    {GetOrdinal(idx + 1)} IGRT Administration
                                  </span>
                                  {idx !== 0 && (
                                    <div
                                      className="delete-btn-div"
                                      style={{ float: "inline-end" }}
                                    >
                                      <img
                                        width="16px"
                                        src="../Assets/images/Icon-material-delete.png"
                                        alt=""
                                        style={{ cursor: "pointer" }}
                                        onClick={() => DeleteQ401aLinvoRow(idx)}
                                      />
                                    </div>
                                  )}
                                </div>

                                <div className="question-bot-sapce">
                                  <div
                                    className="sub-question"
                                    style={{ overflowX: "auto" }}
                                  >
                                    <table
                                      style={{
                                        border: "1px solid black",
                                        borderCollapse: "collapse",
                                        width: "100%",
                                      }}
                                    >
                                      <thead>
                                        <tr>
                                          <th
                                            style={{
                                              border: "1px solid black",
                                              padding: "8px",
                                              textAlign: "center",
                                            }}
                                          >
                                            Treatment
                                          </th>
                                          <th
                                            style={{
                                              border: "1px solid black",
                                              padding: "8px",
                                              textAlign: "center",
                                              width: "33%",
                                            }}
                                          >
                                            Date of IGRT administration
                                          </th>

                                          <th
                                            style={{
                                              border: "1px solid black",
                                              padding: "8px",
                                              textAlign: "center",
                                              width: "16%",
                                            }}
                                          >
                                            Intended dosing frequency
                                          </th>

                                          <th
                                            style={{
                                              border: "1px solid black",
                                              padding: "8px",
                                              textAlign: "center",
                                              width: "15%",
                                            }}
                                          >
                                            Reason for receiving IGRT
                                          </th>

                                          <th
                                            style={{
                                              border: "1px solid black",
                                              padding: "8px",
                                              textAlign: "center",
                                              width: "45%",
                                            }}
                                          >
                                            Dosing type
                                          </th>

                                          <th
                                            style={{
                                              border: "1px solid black",
                                              padding: "8px",
                                              textAlign: "center",
                                              width: "45%",
                                            }}
                                          >
                                            Dosage
                                          </th>

                                          <th
                                            style={{
                                              border: "1px solid black",
                                              padding: "8px",
                                              textAlign: "center",
                                              width: "45%",
                                            }}
                                          >
                                            Body Weight
                                          </th>

                                          <th
                                            style={{
                                              border: "1px solid black",
                                              padding: "8px",
                                              textAlign: "center",
                                              width: "45%",
                                            }}
                                          >
                                            Date of weight assessment
                                          </th>
                                        </tr>
                                      </thead>

                                      <tbody>
                                        <tr key={idx}>
                                          <td
                                            style={{
                                              border: "1px solid black",
                                              padding: "8px",
                                            }}
                                          >
                                            <span className="quest-text-pad">
                                              <>
                                                LYNOZYFIC<sup>TM</sup>{" "}
                                                (linvoseltamab-gcpt)
                                              </>
                                            </span>
                                          </td>

                                          {/* IVIG admission date*/}
                                          <td
                                            style={{
                                              border: "1px solid black",
                                              padding: "8px",
                                            }}
                                          >
                                            <div>
                                              <div
                                                className="option-without-input"
                                                style={{
                                                  display: "flex",
                                                }}
                                              >
                                                <input
                                                  type="radio"
                                                  id={idx}
                                                  name={
                                                    "Q401ainit_dt_Rd_Lin" + idx
                                                  }
                                                  value={1}
                                                  checked={
                                                    data.Q401ainit_dt_Rd_Lin ===
                                                    1
                                                  }
                                                  onChange={
                                                    HandleQ401aFieldChange
                                                  }
                                                />
                                                <span
                                                  className="radio-text-padding"
                                                  style={{
                                                    paddingLeft: "10px",
                                                    marginLeft: "-5px",
                                                  }}
                                                >
                                                  <DatePickerComponent
                                                    ctrlId={
                                                      "Q401ainit_Lin_dt-" + idx
                                                    }
                                                    HandleDateChange={
                                                      HandleQ401aRepeaterDateChange
                                                    }
                                                    optionId={1}
                                                    minDate={dayjs(
                                                      "01-01-1920",
                                                    )}
                                                    maxDate={dayjs()}
                                                    locale="en" // current it is mm/dd/yyyy but for dd/mm/yyyy use "en-gb"
                                                    date={data.Q401ainit_Lin_dt}
                                                  />
                                                </span>
                                              </div>

                                              <div
                                                className="option-without-input"
                                                style={{
                                                  display: "flex",
                                                }}
                                              >
                                                <label className="option-fbox">
                                                  <input
                                                    type="radio"
                                                    id={idx}
                                                    name={
                                                      "Q401ainit_dt_Rd_Lin" +
                                                      idx
                                                    }
                                                    value={99}
                                                    checked={
                                                      data.Q401ainit_dt_Rd_Lin ===
                                                      99
                                                    }
                                                    onChange={
                                                      HandleQ401aFieldChange
                                                    }
                                                  />
                                                  <span
                                                    style={{
                                                      paddingLeft: "10px",
                                                      marginLeft: "-5px",
                                                    }}
                                                  >
                                                    Date of initiation unknown
                                                  </span>
                                                </label>
                                              </div>

                                              <ErrorField
                                                show={data.Q401aInit_Lin_RdHard}
                                                message={
                                                  ELCharacteristics_MSGS.Q401aSelect_RdHard
                                                }
                                                style={{
                                                  paddingLeft: "0px",
                                                }}
                                              />

                                              <ErrorField
                                                show={data.Q401aInit_Lin_dtHard}
                                                message={
                                                  ELCharacteristics_MSGS.Q401aDateAdm_Hard
                                                }
                                                style={{
                                                  paddingLeft: "0px",
                                                }}
                                              />
                                              <ErrorField
                                                show={
                                                  data.Q401aInvalidQ401ainit_Lin_dt
                                                }
                                                message={
                                                  CommonError_Msgs.InvalidDate
                                                }
                                                style={{
                                                  paddingLeft: "0px",
                                                }}
                                              />
                                              <ErrorField
                                                show={
                                                  data.Q401aInit_Lin_dt_before_eof
                                                }
                                                message={
                                                  ELCharacteristics_MSGS.DateBeforeEOFAfterIndex
                                                }
                                                style={{
                                                  paddingLeft: "0px",
                                                }}
                                              />
                                            </div>
                                          </td>

                                          {/* Intended Dosing */}
                                          <td
                                            style={{
                                              border: "1px solid black",
                                              padding: "8px",
                                              textAlign: "start",
                                            }}
                                          >
                                            <div>
                                              <div
                                                className="option-without-input"
                                                style={{
                                                  display: "flex",
                                                }}
                                              >
                                                <label className="option-fbox">
                                                  <input
                                                    type="radio"
                                                    id={idx}
                                                    name={
                                                      "Q401aIntendedDosing_Lin" +
                                                      idx
                                                    }
                                                    value={1}
                                                    checked={
                                                      data.Q401aIntendedDosing_Lin ===
                                                      1
                                                    }
                                                    onChange={
                                                      HandleQ401aFieldChange
                                                    }
                                                  />
                                                  <span
                                                    style={{
                                                      paddingLeft: "10px",
                                                      display: "flex",
                                                    }}
                                                  >
                                                    <span>
                                                      <input
                                                        className="input-dash-ttcustom"
                                                        id={idx}
                                                        name={
                                                          "Q401aIntendedDosing_Lin_WeekValue" +
                                                          idx
                                                        }
                                                        value={
                                                          data.Q401aIntendedDosing_Lin_WeekValue
                                                        }
                                                        type="text"
                                                        onChange={
                                                          HandleQ401aFieldChange
                                                        }
                                                        onKeyDown={(e) => {
                                                          if (
                                                            [
                                                              "Backspace",
                                                              "ArrowLeft",
                                                              "ArrowRight",
                                                              "Delete",
                                                              "Tab",
                                                            ].includes(e.key)
                                                          )
                                                            return;
                                                          if (
                                                            !/[0-9]/.test(e.key)
                                                          )
                                                            e.preventDefault();
                                                        }}
                                                      />
                                                    </span>
                                                    <span>
                                                      &nbsp;times per week
                                                    </span>
                                                  </span>
                                                </label>
                                              </div>

                                              <div
                                                className="option-without-input"
                                                style={{
                                                  display: "flex",
                                                }}
                                              >
                                                <label className="option-fbox">
                                                  <input
                                                    type="radio"
                                                    id={idx}
                                                    name={
                                                      "Q401aIntendedDosing_Lin" +
                                                      idx
                                                    }
                                                    value={2}
                                                    checked={
                                                      data.Q401aIntendedDosing_Lin ===
                                                      2
                                                    }
                                                    onChange={
                                                      HandleQ401aFieldChange
                                                    }
                                                  />
                                                  <span
                                                    style={{
                                                      paddingLeft: "10px",
                                                      display: "flex",
                                                    }}
                                                  >
                                                    <span>
                                                      <input
                                                        className="input-dash-ttcustom"
                                                        id={idx}
                                                        name={
                                                          "Q401aIntendedDosing_Lin_MonthValue" +
                                                          idx
                                                        }
                                                        value={
                                                          data.Q401aIntendedDosing_Lin_MonthValue
                                                        }
                                                        type="text"
                                                        onChange={
                                                          HandleQ401aFieldChange
                                                        }
                                                        onKeyDown={(e) => {
                                                          if (
                                                            [
                                                              "Backspace",
                                                              "ArrowLeft",
                                                              "ArrowRight",
                                                              "Delete",
                                                              "Tab",
                                                            ].includes(e.key)
                                                          )
                                                            return;
                                                          if (
                                                            !/[0-9]/.test(e.key)
                                                          )
                                                            e.preventDefault();
                                                        }}
                                                      />
                                                    </span>
                                                    <span>
                                                      &nbsp;times per month{" "}
                                                    </span>
                                                  </span>
                                                </label>
                                              </div>

                                              <div
                                                className="option-without-input"
                                                style={{
                                                  display: "flex",
                                                }}
                                              >
                                                <label
                                                  className="option-fbox"
                                                  style={{
                                                    display: "inline-flex",
                                                    alignItems: "center",
                                                    whiteSpace: "nowrap",
                                                  }}
                                                >
                                                  <input
                                                    type="radio"
                                                    id={idx}
                                                    name={
                                                      "Q401aIntendedDosing_Lin" +
                                                      idx
                                                    }
                                                    value={3}
                                                    checked={
                                                      data.Q401aIntendedDosing_Lin ===
                                                      3
                                                    }
                                                    onChange={
                                                      HandleQ401aFieldChange
                                                    }
                                                  />
                                                  <span
                                                    style={{
                                                      paddingLeft: "10px",
                                                      display: "inline-flex",
                                                      alignItems: "center",
                                                      whiteSpace: "nowrap",
                                                      gap: "4px",
                                                    }}
                                                  >
                                                    <span>
                                                      Once every &nbsp;
                                                    </span>
                                                    <span>
                                                      <input
                                                        className="input-dash-ttcustom"
                                                        id={idx}
                                                        name={
                                                          "Q401aIntendedDosing_Lin_OnceWeekValue" +
                                                          idx
                                                        }
                                                        value={
                                                          data.Q401aIntendedDosing_Lin_OnceWeekValue
                                                        }
                                                        type="text"
                                                        onChange={
                                                          HandleQ401aFieldChange
                                                        }
                                                        onKeyDown={(e) => {
                                                          if (
                                                            [
                                                              "Backspace",
                                                              "ArrowLeft",
                                                              "ArrowRight",
                                                              "Delete",
                                                              "Tab",
                                                            ].includes(e.key)
                                                          )
                                                            return;
                                                          if (
                                                            !/[0-9]/.test(e.key)
                                                          )
                                                            e.preventDefault();
                                                        }}
                                                      />
                                                    </span>
                                                    <span>&nbsp;weeks </span>
                                                  </span>
                                                </label>
                                              </div>

                                              <div
                                                className="option-without-input"
                                                style={{
                                                  display: "flex",
                                                }}
                                              >
                                                <label
                                                  className="option-fbox"
                                                  style={{
                                                    display: "inline-flex",
                                                    alignItems: "center",
                                                    whiteSpace: "nowrap",
                                                  }}
                                                >
                                                  <input
                                                    type="radio"
                                                    id={idx}
                                                    name={
                                                      "Q401aIntendedDosing_Lin" +
                                                      idx
                                                    }
                                                    value={4}
                                                    checked={
                                                      data.Q401aIntendedDosing_Lin ===
                                                      4
                                                    }
                                                    onChange={
                                                      HandleQ401aFieldChange
                                                    }
                                                  />
                                                  <span
                                                    style={{
                                                      paddingLeft: "10px",
                                                      display: "inline-flex",
                                                      alignItems: "center",
                                                      whiteSpace: "nowrap",
                                                      gap: "4px",
                                                    }}
                                                  >
                                                    <span>
                                                      Once every &nbsp;
                                                    </span>
                                                    <span>
                                                      <input
                                                        className="input-dash-ttcustom"
                                                        id={idx}
                                                        name={
                                                          "Q401aIntendedDosing_Lin_OnceMonthValue" +
                                                          idx
                                                        }
                                                        value={
                                                          data.Q401aIntendedDosing_Lin_OnceMonthValue
                                                        }
                                                        type="text"
                                                        onChange={
                                                          HandleQ401aFieldChange
                                                        }
                                                        onKeyDown={(e) => {
                                                          if (
                                                            [
                                                              "Backspace",
                                                              "ArrowLeft",
                                                              "ArrowRight",
                                                              "Delete",
                                                              "Tab",
                                                            ].includes(e.key)
                                                          )
                                                            return;
                                                          if (
                                                            !/[0-9]/.test(e.key)
                                                          )
                                                            e.preventDefault();
                                                        }}
                                                      />
                                                    </span>
                                                    <span>&nbsp;months </span>
                                                  </span>
                                                </label>
                                              </div>

                                              <div
                                                className="option-without-input"
                                                style={{
                                                  display: "flex",
                                                }}
                                              >
                                                <label
                                                  className="option-fbox"
                                                  style={{
                                                    display: "inline-flex",
                                                    alignItems: "center",
                                                    whiteSpace: "nowrap",
                                                  }}
                                                >
                                                  <input
                                                    type="radio"
                                                    id={idx}
                                                    name={
                                                      "Q401aIntendedDosing_Lin" +
                                                      idx
                                                    }
                                                    value={5}
                                                    checked={
                                                      data.Q401aIntendedDosing_Lin ===
                                                      5
                                                    }
                                                    onChange={
                                                      HandleQ401aFieldChange
                                                    }
                                                  />
                                                  <span
                                                    style={{
                                                      paddingLeft: "10px",
                                                      display: "inline-flex",
                                                      alignItems: "center",
                                                      whiteSpace: "nowrap",
                                                      gap: "4px",
                                                    }}
                                                  >
                                                    <span>
                                                      Other, please
                                                      specify:{" "}
                                                    </span>
                                                    <span>
                                                      <input
                                                        className="input-dash-ttcustom"
                                                        id={idx}
                                                        name={
                                                          "Q401aIntendedDosing_Lin_OtherValue" +
                                                          idx
                                                        }
                                                        value={
                                                          data.Q401aIntendedDosing_Lin_OtherValue
                                                        }
                                                        type="text"
                                                        onChange={
                                                          HandleQ401aFieldChange
                                                        }
                                                      />
                                                    </span>
                                                  </span>
                                                </label>
                                              </div>

                                              <div
                                                className="option-without-input"
                                                style={{
                                                  display: "flex",
                                                }}
                                              >
                                                <label className="option-fbox">
                                                  <input
                                                    type="radio"
                                                    id={idx}
                                                    name={
                                                      "Q401aIntendedDosing_Lin" +
                                                      idx
                                                    }
                                                    value={99}
                                                    checked={
                                                      data.Q401aIntendedDosing_Lin ===
                                                      99
                                                    }
                                                    onChange={
                                                      HandleQ401aFieldChange
                                                    }
                                                  />
                                                  <span
                                                    style={{
                                                      paddingLeft: "10px",
                                                      display: "flex",
                                                    }}
                                                  >
                                                    Unknown
                                                  </span>
                                                </label>
                                              </div>

                                              <ErrorField
                                                show={
                                                  data.Q401aIntendedDosingEmpty_Lin
                                                }
                                                message={
                                                  ELCharacteristics_MSGS.Q401aSelect_RdHard
                                                }
                                                style={{
                                                  paddingLeft: "0px",
                                                }}
                                              />
                                              <ErrorField
                                                show={
                                                  data.Q401aIntendedDosingRangeInvalid_Lin
                                                }
                                                message={
                                                  ELCharacteristics_MSGS.Q401aFrequencyEmptyHard
                                                }
                                                style={{
                                                  paddingLeft: "0px",
                                                }}
                                              />
                                            </div>
                                          </td>

                                          {/* Reason of IVIG */}
                                          <td
                                            style={{
                                              border: "1px solid black",
                                              padding: "8px",
                                            }}
                                          >
                                            <div>
                                              <div
                                                className="option-without-input"
                                                style={{
                                                  display: "flex",
                                                }}
                                              >
                                                <label className="option-fbox">
                                                  <input
                                                    type="checkbox"
                                                    id={idx}
                                                    name={
                                                      "Q401aPrimaryProf_Lin" +
                                                      idx
                                                    }
                                                    checked={
                                                      data.Q401aPrimaryProf_Lin
                                                    }
                                                    onChange={
                                                      HandleQ401aFieldChange
                                                    }
                                                  />
                                                  <span
                                                    className="radio-text-padding"
                                                    style={{
                                                      paddingLeft: "10px",
                                                      marginLeft: "-5px",
                                                    }}
                                                  >
                                                    Primary prophylactic for
                                                    infections
                                                  </span>
                                                </label>
                                              </div>
                                              <div
                                                className="option-without-input"
                                                style={{
                                                  display: "flex",
                                                }}
                                              >
                                                <label className="option-fbox">
                                                  <input
                                                    type="checkbox"
                                                    id={idx}
                                                    name={
                                                      "Q401aSecProf_Lin" + idx
                                                    }
                                                    checked={
                                                      data.Q401aSecProf_Lin
                                                    }
                                                    onChange={
                                                      HandleQ401aFieldChange
                                                    }
                                                  />
                                                  <span
                                                    className="radio-text-padding"
                                                    style={{
                                                      paddingLeft: "10px",
                                                      marginLeft: "-5px",
                                                    }}
                                                  >
                                                    Secondary prophylactic for
                                                    infections
                                                  </span>
                                                </label>
                                              </div>
                                              <div
                                                className="option-without-input"
                                                style={{
                                                  display: "flex",
                                                  alignItems: "flex-start",
                                                }}
                                              >
                                                <label className="option-fbox">
                                                  <input
                                                    type="checkbox"
                                                    id={idx}
                                                    name={
                                                      "Q401aOtherProf_Lin" + idx
                                                    }
                                                    checked={
                                                      data.Q401aOtherProf_Lin
                                                    }
                                                    onChange={
                                                      HandleQ401aFieldChange
                                                    }
                                                  />
                                                  <span
                                                    className="radio-text-padding"
                                                    style={{
                                                      paddingLeft: "10px",
                                                      marginLeft: "-5px",
                                                    }}
                                                  >
                                                    Other reason
                                                  </span>
                                                </label>
                                              </div>
                                              <div
                                                className="option-without-input"
                                                style={{
                                                  display: "flex",
                                                }}
                                              >
                                                <label className="option-fbox">
                                                  <input
                                                    type="radio"
                                                    id={idx}
                                                    name={
                                                      "Q401aUnknownProf_Lin" +
                                                      idx
                                                    }
                                                    value={99}
                                                    checked={
                                                      data.Q401aUnknownProf_Lin ===
                                                      99
                                                    }
                                                    onChange={
                                                      HandleQ401aFieldChange
                                                    }
                                                  />
                                                  <span
                                                    className="radio-text-padding"
                                                    style={{
                                                      paddingLeft: "10px",
                                                      marginLeft: "-5px",
                                                    }}
                                                  >
                                                    Unknown
                                                  </span>
                                                </label>
                                              </div>

                                              <ErrorField
                                                show={
                                                  data.Q401aReasonIvigEmptyHard_Lin
                                                }
                                                message={
                                                  ELCharacteristics_MSGS.Q401aSelectCheckbox_RdHard
                                                }
                                                style={{
                                                  paddingLeft: "0px",
                                                }}
                                              />
                                            </div>
                                          </td>

                                          {/* Dosing Type */}
                                          <td
                                            style={{
                                              border: "1px solid black",
                                              padding: "8px",
                                            }}
                                          >
                                            <div>
                                              <div
                                                className="option-without-input"
                                                style={{
                                                  display: "flex",
                                                }}
                                              >
                                                <label className="option-fbox">
                                                  <input
                                                    type="radio"
                                                    id={idx}
                                                    name={
                                                      "Q401aDosingType_Lin" +
                                                      idx
                                                    }
                                                    value={1}
                                                    checked={
                                                      data.Q401aDosingType_Lin ===
                                                      1
                                                    }
                                                    onChange={
                                                      HandleQ401aFieldChange
                                                    }
                                                  />
                                                  <span
                                                    className="radio-text-padding"
                                                    style={{
                                                      paddingLeft: "10px",
                                                      marginLeft: "-5px",
                                                    }}
                                                  >
                                                    Flat dosage
                                                  </span>
                                                </label>
                                              </div>
                                              <div
                                                className="option-without-input"
                                                style={{
                                                  display: "flex",
                                                }}
                                              >
                                                <label className="option-fbox">
                                                  <input
                                                    type="radio"
                                                    id={idx}
                                                    name={
                                                      "Q401aDosingType_Lin" +
                                                      idx
                                                    }
                                                    value={2}
                                                    checked={
                                                      data.Q401aDosingType_Lin ===
                                                      2
                                                    }
                                                    onChange={
                                                      HandleQ401aFieldChange
                                                    }
                                                  />
                                                  <span
                                                    className="radio-text-padding"
                                                    style={{
                                                      paddingLeft: "10px",
                                                      marginLeft: "-5px",
                                                    }}
                                                  >
                                                    Weight-based dosage
                                                  </span>
                                                </label>
                                              </div>

                                              <ErrorField
                                                show={
                                                  data.Q401aDosingType_LinEmptyHard
                                                }
                                                message={
                                                  ELCharacteristics_MSGS.Q401aSelect_RdHard
                                                }
                                                style={{
                                                  paddingLeft: "0px",
                                                }}
                                              />
                                            </div>
                                          </td>

                                          {/* Dosage*/}
                                          <td
                                            style={{
                                              border: "1px solid black",
                                              padding: "8px",
                                              textAlign: "start",
                                            }}
                                          >
                                            <div>
                                              {data.Q401aDosingType_Lin ===
                                                1 && (
                                                <div
                                                  className="option-without-input"
                                                  style={{
                                                    display: "flex",
                                                  }}
                                                >
                                                  <label className="option-fbox">
                                                    <input
                                                      type="radio"
                                                      id={idx}
                                                      name={
                                                        "Q401aDosage_Lin" + idx
                                                      }
                                                      value={1}
                                                      checked={
                                                        data.Q401aDosage_Lin ===
                                                        1
                                                      }
                                                      onChange={
                                                        HandleQ401aFieldChange
                                                      }
                                                    />
                                                    <span
                                                      style={{
                                                        paddingLeft: "10px",
                                                        display: "flex",
                                                      }}
                                                    >
                                                      <span>
                                                        <input
                                                          className="input-dash-ttcustom"
                                                          id={idx}
                                                          name={
                                                            "Q401aDosage_Lin_GValue" +
                                                            idx
                                                          }
                                                          value={
                                                            data.Q401aDosage_Lin_GValue
                                                          }
                                                          type="text"
                                                          onChange={
                                                            HandleQ401aFieldChange
                                                          }
                                                          onKeyDown={
                                                            AllowNumericWithDecimal
                                                          }
                                                          maxLength={10}
                                                        />
                                                      </span>
                                                      <span>&nbsp;g</span>
                                                    </span>
                                                  </label>
                                                </div>
                                              )}

                                              {data.Q401aDosingType_Lin ===
                                                2 && (
                                                <div
                                                  className="option-without-input"
                                                  style={{
                                                    display: "flex",
                                                  }}
                                                >
                                                  <label className="option-fbox">
                                                    <input
                                                      type="radio"
                                                      id={idx}
                                                      name={
                                                        "Q401aDosage_Lin" + idx
                                                      }
                                                      value={2}
                                                      checked={
                                                        data.Q401aDosage_Lin ===
                                                        2
                                                      }
                                                      onChange={
                                                        HandleQ401aFieldChange
                                                      }
                                                    />
                                                    <span
                                                      style={{
                                                        paddingLeft: "10px",
                                                        display: "flex",
                                                      }}
                                                    >
                                                      <span>
                                                        <input
                                                          className="input-dash-ttcustom"
                                                          id={idx}
                                                          name={
                                                            "Q401aDosage_Lin_MgValue" +
                                                            idx
                                                          }
                                                          value={
                                                            data.Q401aDosage_Lin_MgValue
                                                          }
                                                          type="text"
                                                          onChange={
                                                            HandleQ401aFieldChange
                                                          }
                                                          onKeyDown={
                                                            AllowNumericWithDecimal
                                                          }
                                                          maxLength={10}
                                                        />
                                                      </span>
                                                      <span>&nbsp;mg/kg </span>
                                                    </span>
                                                  </label>
                                                </div>
                                              )}

                                              <div
                                                className="option-without-input"
                                                style={{
                                                  display: "flex",
                                                }}
                                              >
                                                <label className="option-fbox">
                                                  <input
                                                    type="radio"
                                                    id={idx}
                                                    name={
                                                      "Q401aDosage_Lin" + idx
                                                    }
                                                    value={99}
                                                    checked={
                                                      data.Q401aDosage_Lin ===
                                                      99
                                                    }
                                                    onChange={
                                                      HandleQ401aFieldChange
                                                    }
                                                  />
                                                  <span
                                                    style={{
                                                      paddingLeft: "10px",
                                                      display: "flex",
                                                    }}
                                                  >
                                                    Unknown
                                                  </span>
                                                </label>
                                              </div>

                                              <ErrorField
                                                show={
                                                  data.Q401aDosageFrequencyEmpty_Lin
                                                }
                                                message={
                                                  ELCharacteristics_MSGS.Q401aSelect_RdHard
                                                }
                                                style={{
                                                  paddingLeft: "0px",
                                                }}
                                              />
                                              <ErrorField
                                                show={
                                                  data.Q401aDosageRangeInvalid_Lin
                                                }
                                                message={
                                                  data.Q401aDosage_Lin === 1
                                                    ? ELCharacteristics_MSGS.Q401aDosageEmptyHardG
                                                    : ELCharacteristics_MSGS.Q401aDosageEmptyHardMG
                                                }
                                                style={{
                                                  paddingLeft: "0px",
                                                }}
                                              />
                                            </div>
                                          </td>

                                          {/* Body Weight*/}
                                          {data.Q401aDosingType_Lin === 2 ? (
                                            <td
                                              style={{
                                                border: "1px solid black",
                                                padding: "8px",
                                                textAlign: "start",
                                              }}
                                            >
                                              <div>
                                                <div
                                                  className="option-without-input"
                                                  style={{
                                                    display: "flex",
                                                  }}
                                                >
                                                  <label className="option-fbox">
                                                    <input
                                                      type="radio"
                                                      id={idx}
                                                      name={
                                                        "Q401aWeight_Lin" + idx
                                                      }
                                                      value={1}
                                                      checked={
                                                        data.Q401aWeight_Lin ===
                                                        1
                                                      }
                                                      onChange={
                                                        HandleQ401aFieldChange
                                                      }
                                                    />
                                                    <span
                                                      style={{
                                                        paddingLeft: "10px",
                                                        display: "flex",
                                                      }}
                                                    >
                                                      <span>
                                                        <input
                                                          className="input-dash-ttcustom"
                                                          id={idx}
                                                          name={
                                                            "Q401aWeight_Value_KgLin" +
                                                            idx
                                                          }
                                                          value={
                                                            data.Q401aWeight_Value_KgLin
                                                          }
                                                          type="text"
                                                          onChange={
                                                            HandleQ401aFieldChange
                                                          }
                                                          onKeyDown={
                                                            AllowNumericWithDecimal
                                                          }
                                                          maxLength={10}
                                                        />
                                                      </span>
                                                      <span>&nbsp;kg</span>
                                                    </span>
                                                  </label>
                                                </div>

                                                <div
                                                  className="option-without-input"
                                                  style={{
                                                    display: "flex",
                                                  }}
                                                >
                                                  <label className="option-fbox">
                                                    <input
                                                      type="radio"
                                                      id={idx}
                                                      name={
                                                        "Q401aWeight_Lin" + idx
                                                      }
                                                      value={2}
                                                      checked={
                                                        data.Q401aWeight_Lin ===
                                                        2
                                                      }
                                                      onChange={
                                                        HandleQ401aFieldChange
                                                      }
                                                    />
                                                    <span
                                                      style={{
                                                        paddingLeft: "10px",
                                                        display: "flex",
                                                      }}
                                                    >
                                                      <span>
                                                        <input
                                                          className="input-dash-ttcustom"
                                                          id={idx}
                                                          name={
                                                            "Q401aWeight_Value_LbsLin" +
                                                            idx
                                                          }
                                                          value={
                                                            data.Q401aWeight_Value_LbsLin
                                                          }
                                                          type="text"
                                                          onChange={
                                                            HandleQ401aFieldChange
                                                          }
                                                          onKeyDown={
                                                            AllowNumericWithDecimal
                                                          }
                                                          maxLength={10}
                                                        />
                                                      </span>
                                                      <span>&nbsp;lbs </span>
                                                    </span>
                                                  </label>
                                                </div>

                                                <div
                                                  className="option-without-input"
                                                  style={{
                                                    display: "flex",
                                                  }}
                                                >
                                                  <label className="option-fbox">
                                                    <input
                                                      type="radio"
                                                      id={idx}
                                                      name={
                                                        "Q401aWeight_Lin" + idx
                                                      }
                                                      value={99}
                                                      checked={
                                                        data.Q401aWeight_Lin ===
                                                        99
                                                      }
                                                      onChange={
                                                        HandleQ401aFieldChange
                                                      }
                                                    />
                                                    <span
                                                      style={{
                                                        paddingLeft: "10px",
                                                        display: "flex",
                                                      }}
                                                    >
                                                      Unknown
                                                    </span>
                                                  </label>
                                                </div>

                                                <ErrorField
                                                  show={
                                                    data.Q401aWeightEmpty_Lin
                                                  }
                                                  message={
                                                    ELCharacteristics_MSGS.Q401aSelect_RdHard
                                                  }
                                                  style={{
                                                    paddingLeft: "0px",
                                                  }}
                                                />
                                                <ErrorField
                                                  show={
                                                    data.Q401aWeightRangeInvalid_Lin
                                                  }
                                                  message={
                                                    data.Q401aWeight_Lin === 1
                                                      ? ELCharacteristics_MSGS.Q401aBodyWeightEmptyHardKg
                                                      : ELCharacteristics_MSGS.Q401aBodyWeightEmptyHardLbs
                                                  }
                                                  style={{
                                                    paddingLeft: "0px",
                                                  }}
                                                />
                                              </div>
                                            </td>
                                          ) : (
                                            <td
                                              style={{
                                                border: "1px solid black",
                                                //alignContent: "flex-start",
                                                textAlign: "center",
                                                padding: "8px",
                                                //textAlign: 'start'
                                              }}
                                            >
                                              <span
                                                style={{
                                                  textAlign: "center",
                                                  verticalAlign: "center",
                                                }}
                                                className="not-applicable"
                                              >
                                                <b>Not applicable</b>
                                              </span>
                                            </td>
                                          )}

                                          {/* Weight assessment date */}
                                          {data.Q401aDosingType_Lin === 2 ? (
                                            <td
                                              style={{
                                                border: "1px solid black",
                                                padding: "8px",
                                              }}
                                            >
                                              <div>
                                                <div
                                                  className="option-without-input"
                                                  style={{
                                                    display: "flex",
                                                  }}
                                                >
                                                  <input
                                                    type="radio"
                                                    id={idx}
                                                    name={
                                                      "Q401aWeight_Asses_Lin" +
                                                      idx
                                                    }
                                                    value={1}
                                                    checked={
                                                      data.Q401aWeight_Asses_Lin ===
                                                      1
                                                    }
                                                    onChange={
                                                      HandleQ401aFieldChange
                                                    }
                                                  />
                                                  <span
                                                    className="radio-text-padding"
                                                    style={{
                                                      paddingLeft: "10px",
                                                      marginLeft: "-5px",
                                                    }}
                                                  >
                                                    <DatePickerComponent
                                                      ctrlId={
                                                        "Q401aWeight_Asses_Lin_dt-" +
                                                        idx
                                                      }
                                                      HandleDateChange={
                                                        HandleQ401aRepeaterDateChange
                                                      }
                                                      optionId={1}
                                                      minDate={dayjs(
                                                        "01-01-1920",
                                                      )}
                                                      maxDate={dayjs()}
                                                      locale="en" // current it is mm/dd/yyyy but for dd/mm/yyyy use "en-gb"
                                                      date={
                                                        data.Q401aWeight_Asses_Lin_dt
                                                      }
                                                    />
                                                  </span>
                                                </div>

                                                <div
                                                  className="option-without-input"
                                                  style={{
                                                    display: "flex",
                                                  }}
                                                >
                                                  <label className="option-fbox">
                                                    <input
                                                      type="radio"
                                                      id={idx}
                                                      name={
                                                        "Q401aWeight_Asses_Lin" +
                                                        idx
                                                      }
                                                      value={99}
                                                      checked={
                                                        data.Q401aWeight_Asses_Lin ===
                                                        99
                                                      }
                                                      onChange={
                                                        HandleQ401aFieldChange
                                                      }
                                                    />
                                                    <span
                                                      style={{
                                                        paddingLeft: "10px",
                                                        marginLeft: "-5px",
                                                      }}
                                                    >
                                                      Date of initiation unknown
                                                    </span>
                                                  </label>
                                                </div>

                                                <ErrorField
                                                  show={
                                                    data.Q401aWeight_AssesEmptyHard_Lin
                                                  }
                                                  message={
                                                    ELCharacteristics_MSGS.Q401aSelect_RdHard
                                                  }
                                                  style={{
                                                    paddingLeft: "0px",
                                                  }}
                                                />

                                                <ErrorField
                                                  show={
                                                    data.Q401aWeight_AssesDtEmptyHard_Lin
                                                  }
                                                  message={
                                                    ELCharacteristics_MSGS.Q401aDateWeight_Hard
                                                  }
                                                  style={{
                                                    paddingLeft: "0px",
                                                  }}
                                                />
                                                <ErrorField
                                                  show={
                                                    data.Q401aWeight_AssesDtInvalid_Lin
                                                  }
                                                  message={
                                                    CommonError_Msgs.InvalidDate
                                                  }
                                                  style={{
                                                    paddingLeft: "0px",
                                                  }}
                                                />
                                                <ErrorField
                                                  show={
                                                    data.Q401aWeight_Asses_dt_before_eof_Lin
                                                  }
                                                  message={
                                                    ELCharacteristics_MSGS.DateBeforeEOFAfterIndex
                                                  }
                                                  style={{
                                                    paddingLeft: "0px",
                                                  }}
                                                />
                                              </div>
                                            </td>
                                          ) : (
                                            <td
                                              style={{
                                                border: "1px solid black",
                                                textAlign: "center",
                                                padding: "8px",
                                              }}
                                            >
                                              <span
                                                style={{
                                                  textAlign: "center",
                                                  verticalAlign: "center",
                                                }}
                                                className="not-applicable"
                                              >
                                                <b>Not applicable</b>
                                              </span>
                                            </td>
                                          )}
                                        </tr>
                                      </tbody>
                                    </table>
                                  </div>
                                </div>
                              </div>
                            ))}
                          {/* Add button */}
                          {Q401aLinvoDetails && (
                            <div className="sub-question-bot-sapce-ul mt-2">
                              <div
                                className="answer-list-text"
                                onClick={AddQ401aLinvoRow}
                              >
                                <a>
                                  <img
                                    width="18px"
                                    src="../Assets/images/plus.png"
                                    alt=""
                                  />
                                </a>
                                <a className="radio-text-padding">
                                  Click here to add IVIG administration received
                                </a>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    {/* 40.1.A  end */}

                    {/* 40.1.C start - Elrexfio */}
                    {isNewEcrfC &&
                      showQ40Elr &&
                      Q37ElrexDetails[0]?.ivigInitRd_elrC != null &&
                      Q37ElrexDetails[0]?.ivigInitRd_elrC !== 1 && (
                        <div className="question question-weight">
                          <span>Q40.1.C</span>
                          <span className="quest-text-pad">
                            Please provide information on{" "}
                            <b>
                              <i>
                                <u>any IGRT dose frequency changes</u>
                              </i>
                            </b>{" "}
                            that occurred after initiation of IGRT during the
                            patient’s treatment with{" "}
                            <span
                              style={{
                                color: PageDesignConstants.fontColor,
                                fontWeight: "bolder",
                              }}
                            >
                              ELREXFIO<sup>TM</sup> (elranatamab-bcmm)
                            </span>{" "}
                            (i.e., from{" "}
                            <span
                              style={{ color: PageDesignConstants.fontColor }}
                            >
                              {showElrIvigInitDateQ401C()}
                            </span>{" "}
                            to{" "}
                            <span
                              style={{ color: PageDesignConstants.fontColor }}
                            >
                              {showElrQ40PeriodEndDate()}
                            </span>
                            ).
                            <br />
                            Please enter dose change frequency changes in
                            chronological order from earliest to most recent
                            after IGRT initiation. Note that, for the earliest
                            reported dose frequency change, the{" "}
                            <i>Pattern before change</i> has been auto-populated
                            with information from the{" "}
                            <i>Intended dose frequency</i> in Q40.C.
                            <br />
                            <br />
                            Add additional rows for each change, as needed.
                          </span>
                        </div>
                      )}

                    {isNewEcrfC &&
                      showQ40Elr &&
                      Q37ElrexDetails[0]?.ivigInitRd_elrC != null &&
                      Q37ElrexDetails[0]?.ivigInitRd_elrC !== 1 && (
                        <div className="dependent-section">
                          <ErrorField
                            show={q401cElr_WarningHard}
                            message={
                              ELCharacteristics_MSGS.Q401cChainMismatchWarning
                            }
                            style={{ paddingLeft: "0px" }}
                          />
                          {Q401cElrDetails.length > 0 &&
                            Q401cElrDetails.map((data, idx) => (
                              <div className="loop-section-ul" key={idx}>
                                <div className="loop-head">
                                  <span
                                    className="question-weight"
                                    style={{ float: "inline-start" }}
                                  >
                                    {GetOrdinal(idx + 1)} Dose frequency change
                                  </span>
                                  {idx !== 0 && (
                                    <div
                                      className="delete-btn-div"
                                      style={{ float: "inline-end" }}
                                    >
                                      <img
                                        width="16px"
                                        src="../Assets/images/Icon-material-delete.png"
                                        alt=""
                                        style={{ cursor: "pointer" }}
                                        onClick={() => DeleteQ401cElrRow(idx)}
                                      />
                                    </div>
                                  )}
                                </div>

                                <div className="question-bot-sapce">
                                  <div
                                    className="sub-question"
                                    style={{ overflowX: "auto" }}
                                  >
                                    <table
                                      style={{
                                        border: "1px solid black",
                                        borderCollapse: "collapse",
                                        width: "100%",
                                      }}
                                    >
                                      <thead>
                                        <tr>
                                          <th
                                            style={{
                                              border: "1px solid black",
                                              padding: "8px",
                                              textAlign: "center",
                                            }}
                                          >
                                            Treatment
                                          </th>
                                          <th
                                            style={{
                                              border: "1px solid black",
                                              padding: "8px",
                                              textAlign: "center",
                                              minWidth: "200px",
                                            }}
                                          >
                                            Type of dose frequency change
                                          </th>
                                          <th
                                            style={{
                                              border: "1px solid black",
                                              padding: "8px",
                                              textAlign: "center",
                                            }}
                                          >
                                            Dose frequency <i>before</i> change
                                          </th>
                                          <th
                                            style={{
                                              border: "1px solid black",
                                              padding: "8px",
                                              textAlign: "center",
                                            }}
                                          >
                                            Dose frequency <i>after</i> change
                                          </th>
                                          <th
                                            style={{
                                              border: "1px solid black",
                                              padding: "8px",
                                              textAlign: "center",
                                            }}
                                          >
                                            Date of change
                                          </th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        <tr>
                                          <td
                                            style={{
                                              border: "1px solid black",
                                              padding: "8px",
                                            }}
                                          >
                                            <span className="quest-text-pad">
                                              ELREXFIO<sup>TM</sup>{" "}
                                              (elranatamab-bcmm)
                                            </span>
                                          </td>

                                          {/* Column B - Type of dose frequency change */}
                                          <td
                                            style={{
                                              border: "1px solid black",
                                              padding: "8px",
                                            }}
                                          >
                                            <div>
                                              {idx === 0 && (
                                                <div className="option-without-input">
                                                  <label className="option-fbox">
                                                    <input
                                                      type="radio"
                                                      id={idx}
                                                      name={
                                                        "Q401cChangeType_Elr" +
                                                        idx
                                                      }
                                                      value={1}
                                                      checked={
                                                        data.Q401cChangeType_Elr ===
                                                        1
                                                      }
                                                      onChange={
                                                        HandleQ401cFieldChange
                                                      }
                                                    />
                                                    <span className="radio-text-padding">
                                                      No dose frequency changes
                                                      occurred
                                                    </span>
                                                  </label>
                                                </div>
                                              )}
                                              <div className="option-without-input">
                                                <label className="option-fbox">
                                                  <input
                                                    type="radio"
                                                    id={idx}
                                                    name={
                                                      "Q401cChangeType_Elr" +
                                                      idx
                                                    }
                                                    value={2}
                                                    checked={
                                                      data.Q401cChangeType_Elr ===
                                                      2
                                                    }
                                                    onChange={
                                                      HandleQ401cFieldChange
                                                    }
                                                  />
                                                  <span className="radio-text-padding">
                                                    Dose frequency increase
                                                  </span>
                                                </label>
                                              </div>
                                              <div className="option-without-input">
                                                <label className="option-fbox">
                                                  <input
                                                    type="radio"
                                                    id={idx}
                                                    name={
                                                      "Q401cChangeType_Elr" +
                                                      idx
                                                    }
                                                    value={3}
                                                    checked={
                                                      data.Q401cChangeType_Elr ===
                                                      3
                                                    }
                                                    onChange={
                                                      HandleQ401cFieldChange
                                                    }
                                                  />
                                                  <span className="radio-text-padding">
                                                    Dose frequency decrease
                                                  </span>
                                                </label>
                                              </div>
                                              {idx === 0 && (
                                                <div className="option-without-input">
                                                  <label className="option-fbox">
                                                    <input
                                                      type="radio"
                                                      id={idx}
                                                      name={
                                                        "Q401cChangeType_Elr" +
                                                        idx
                                                      }
                                                      value={99}
                                                      checked={
                                                        data.Q401cChangeType_Elr ===
                                                        99
                                                      }
                                                      onChange={
                                                        HandleQ401cFieldChange
                                                      }
                                                    />
                                                    <span className="radio-text-padding">
                                                      Unknown if any dose
                                                      frequency changes occurred
                                                    </span>
                                                  </label>
                                                </div>
                                              )}
                                              <ErrorField
                                                show={
                                                  data.Q401cChangeTypeEmptyHard_Elr
                                                }
                                                message={
                                                  ELCharacteristics_MSGS.Q401cChangeTypeEmptyHard
                                                }
                                                style={{ paddingLeft: "0px" }}
                                              />
                                            </div>
                                          </td>

                                          {data.Q401cChangeType_Elr === 2 ||
                                          data.Q401cChangeType_Elr === 3 ? (
                                            <>
                                              {/* Column C - Dose frequency before change */}
                                              <td
                                                style={{
                                                  border: "1px solid black",
                                                  padding: "8px",
                                                }}
                                              >
                                                <div>
                                                  <label
                                                    className="option-without-input"
                                                    style={{
                                                      display: "flex",
                                                    }}
                                                  >
                                                    <input
                                                      type="radio"
                                                      id={idx}
                                                      name={
                                                        "Q401cFreqBeforeRd_Elr" +
                                                        idx
                                                      }
                                                      value={1}
                                                      checked={
                                                        data.Q401cFreqBeforeRd_Elr ===
                                                        1
                                                      }
                                                      onChange={
                                                        HandleQ401cFieldChange
                                                      }
                                                    />
                                                    <span
                                                      className="radio-text-padding"
                                                      style={{
                                                        paddingLeft: "10px",
                                                      }}
                                                    >
                                                      <input
                                                        type="text"
                                                        className="input-dash-ttcustom"
                                                        name={
                                                          "Q401cFreqBeforeTimesTxt_Elr" +
                                                          idx
                                                        }
                                                        id={idx}
                                                        value={
                                                          data.Q401cFreqBeforeTimesTxt_Elr
                                                        }
                                                        onChange={
                                                          HandleQ401cFieldChange
                                                        }
                                                        maxLength={2}
                                                      />{" "}
                                                      times per week
                                                    </span>
                                                  </label>
                                                  <label
                                                    className="option-without-input"
                                                    style={{
                                                      display: "flex",
                                                    }}
                                                  >
                                                    <input
                                                      type="radio"
                                                      id={idx}
                                                      name={
                                                        "Q401cFreqBeforeRd_Elr" +
                                                        idx
                                                      }
                                                      value={2}
                                                      checked={
                                                        data.Q401cFreqBeforeRd_Elr ===
                                                        2
                                                      }
                                                      onChange={
                                                        HandleQ401cFieldChange
                                                      }
                                                    />
                                                    <span
                                                      className="radio-text-padding"
                                                      style={{
                                                        paddingLeft: "10px",
                                                      }}
                                                    >
                                                      Once every{" "}
                                                      <input
                                                        type="text"
                                                        className="input-dash-ttcustom"
                                                        name={
                                                          "Q401cFreqBeforeWeeksTxt_Elr" +
                                                          idx
                                                        }
                                                        id={idx}
                                                        value={
                                                          data.Q401cFreqBeforeWeeksTxt_Elr
                                                        }
                                                        onChange={
                                                          HandleQ401cFieldChange
                                                        }
                                                        maxLength={2}
                                                      />{" "}
                                                      weeks
                                                    </span>
                                                  </label>
                                                  <label
                                                    className="option-without-input"
                                                    style={{
                                                      display: "flex",
                                                    }}
                                                  >
                                                    <input
                                                      type="radio"
                                                      id={idx}
                                                      name={
                                                        "Q401cFreqBeforeRd_Elr" +
                                                        idx
                                                      }
                                                      value={3}
                                                      checked={
                                                        data.Q401cFreqBeforeRd_Elr ===
                                                        3
                                                      }
                                                      onChange={
                                                        HandleQ401cFieldChange
                                                      }
                                                    />
                                                    <span
                                                      className="radio-text-padding"
                                                      style={{
                                                        paddingLeft: "10px",
                                                      }}
                                                    >
                                                      Other, please specify:
                                                      <div>
                                                        <textarea
                                                          className="input-dash"
                                                          name={
                                                            "Q401cFreqBeforeOtherTxt_Elr" +
                                                            idx
                                                          }
                                                          id={idx}
                                                          value={
                                                            data.Q401cFreqBeforeOtherTxt_Elr
                                                          }
                                                          onChange={
                                                            HandleQ401cFieldChange
                                                          }
                                                          maxLength={300}
                                                        />
                                                      </div>
                                                    </span>
                                                  </label>
                                                  <label
                                                    className="option-without-input"
                                                    style={{
                                                      display: "flex",
                                                    }}
                                                  >
                                                    <input
                                                      type="radio"
                                                      id={idx}
                                                      name={
                                                        "Q401cFreqBeforeRd_Elr" +
                                                        idx
                                                      }
                                                      value={99}
                                                      checked={
                                                        data.Q401cFreqBeforeRd_Elr ===
                                                        99
                                                      }
                                                      onChange={
                                                        HandleQ401cFieldChange
                                                      }
                                                    />
                                                    <span
                                                      className="radio-text-padding"
                                                      style={{
                                                        paddingLeft: "10px",
                                                      }}
                                                    >
                                                      Unknown
                                                    </span>
                                                  </label>
                                                  <ErrorField
                                                    show={
                                                      data.Q401cFreqBeforeEmptyHard_Elr
                                                    }
                                                    message={
                                                      ELCharacteristics_MSGS.Q401cFreqBeforeEmptyHard
                                                    }
                                                    style={{
                                                      paddingLeft: "0px",
                                                    }}
                                                  />
                                                  <ErrorField
                                                    show={
                                                      data.Q401cFreqBeforeTxtEmptyHard_Elr
                                                    }
                                                    message={
                                                      ELCharacteristics_MSGS.Q401cFreqBeforeTxtEmptyHard
                                                    }
                                                    style={{
                                                      paddingLeft: "0px",
                                                    }}
                                                  />
                                                  <ErrorField
                                                    show={
                                                      data.Q401cFreqBeforeRangeInvalid_Elr
                                                    }
                                                    message={
                                                      ELCharacteristics_MSGS.Q401cFreqBeforeRangeInvalid
                                                    }
                                                    style={{
                                                      paddingLeft: "0px",
                                                    }}
                                                  />
                                                  <ErrorField
                                                    show={
                                                      data.Q401cFreqBeforeRd_Elr ===
                                                      99
                                                    }
                                                    message={Q401cWarningUnkMsg}
                                                    style={{
                                                      paddingLeft: "0px",
                                                    }}
                                                  />
                                                </div>
                                              </td>

                                              {/* Column D - Dose frequency after change */}
                                              <td
                                                style={{
                                                  border: "1px solid black",
                                                  padding: "8px",
                                                }}
                                              >
                                                <div>
                                                  <label
                                                    className="option-without-input"
                                                    style={{ display: "flex" }}
                                                  >
                                                    <input
                                                      type="radio"
                                                      id={idx}
                                                      name={
                                                        "Q401cFreqAfterRd_Elr" +
                                                        idx
                                                      }
                                                      value={1}
                                                      checked={
                                                        data.Q401cFreqAfterRd_Elr ===
                                                        1
                                                      }
                                                      onChange={
                                                        HandleQ401cFieldChange
                                                      }
                                                    />
                                                    <span
                                                      className="radio-text-padding"
                                                      style={{
                                                        paddingLeft: "10px",
                                                      }}
                                                    >
                                                      <input
                                                        type="text"
                                                        className="input-dash-ttcustom"
                                                        name={
                                                          "Q401cFreqAfterTimesTxt_Elr" +
                                                          idx
                                                        }
                                                        id={idx}
                                                        value={
                                                          data.Q401cFreqAfterTimesTxt_Elr
                                                        }
                                                        onChange={
                                                          HandleQ401cFieldChange
                                                        }
                                                        maxLength={2}
                                                      />{" "}
                                                      times per week
                                                    </span>
                                                  </label>
                                                  <label
                                                    className="option-without-input"
                                                    style={{ display: "flex" }}
                                                  >
                                                    <input
                                                      type="radio"
                                                      id={idx}
                                                      name={
                                                        "Q401cFreqAfterRd_Elr" +
                                                        idx
                                                      }
                                                      value={2}
                                                      checked={
                                                        data.Q401cFreqAfterRd_Elr ===
                                                        2
                                                      }
                                                      onChange={
                                                        HandleQ401cFieldChange
                                                      }
                                                    />
                                                    <span
                                                      className="radio-text-padding"
                                                      style={{
                                                        paddingLeft: "10px",
                                                      }}
                                                    >
                                                      Once every{" "}
                                                      <input
                                                        type="text"
                                                        className="input-dash-ttcustom"
                                                        name={
                                                          "Q401cFreqAfterWeeksTxt_Elr" +
                                                          idx
                                                        }
                                                        id={idx}
                                                        value={
                                                          data.Q401cFreqAfterWeeksTxt_Elr
                                                        }
                                                        onChange={
                                                          HandleQ401cFieldChange
                                                        }
                                                        maxLength={2}
                                                      />{" "}
                                                      weeks
                                                    </span>
                                                  </label>
                                                  <label
                                                    className="option-without-input"
                                                    style={{ display: "flex" }}
                                                  >
                                                    <input
                                                      type="radio"
                                                      id={idx}
                                                      name={
                                                        "Q401cFreqAfterRd_Elr" +
                                                        idx
                                                      }
                                                      value={3}
                                                      checked={
                                                        data.Q401cFreqAfterRd_Elr ===
                                                        3
                                                      }
                                                      onChange={
                                                        HandleQ401cFieldChange
                                                      }
                                                    />
                                                    <span
                                                      className="radio-text-padding"
                                                      style={{
                                                        paddingLeft: "10px",
                                                      }}
                                                    >
                                                      Other, please specify:
                                                      <div>
                                                        <textarea
                                                          className="input-dash"
                                                          name={
                                                            "Q401cFreqAfterOtherTxt_Elr" +
                                                            idx
                                                          }
                                                          id={idx}
                                                          value={
                                                            data.Q401cFreqAfterOtherTxt_Elr
                                                          }
                                                          onChange={
                                                            HandleQ401cFieldChange
                                                          }
                                                          maxLength={300}
                                                        />
                                                      </div>
                                                    </span>
                                                  </label>
                                                  <label
                                                    className="option-without-input"
                                                    style={{ display: "flex" }}
                                                  >
                                                    <input
                                                      type="radio"
                                                      id={idx}
                                                      name={
                                                        "Q401cFreqAfterRd_Elr" +
                                                        idx
                                                      }
                                                      value={99}
                                                      checked={
                                                        data.Q401cFreqAfterRd_Elr ===
                                                        99
                                                      }
                                                      onChange={
                                                        HandleQ401cFieldChange
                                                      }
                                                    />
                                                    <span
                                                      className="radio-text-padding"
                                                      style={{
                                                        paddingLeft: "10px",
                                                      }}
                                                    >
                                                      Unknown
                                                    </span>
                                                  </label>
                                                  <ErrorField
                                                    show={
                                                      data.Q401cFreqAfterEmptyHard_Elr
                                                    }
                                                    message={
                                                      ELCharacteristics_MSGS.Q401cFreqAfterEmptyHard
                                                    }
                                                    style={{
                                                      paddingLeft: "0px",
                                                    }}
                                                  />
                                                  <ErrorField
                                                    show={
                                                      data.Q401cFreqAfterTxtEmptyHard_Elr
                                                    }
                                                    message={
                                                      ELCharacteristics_MSGS.Q401cFreqAfterTxtEmptyHard
                                                    }
                                                    style={{
                                                      paddingLeft: "0px",
                                                    }}
                                                  />
                                                  <ErrorField
                                                    show={
                                                      data.Q401cFreqAfterRangeInvalid_Elr
                                                    }
                                                    message={
                                                      ELCharacteristics_MSGS.Q401cFreqAfterRangeInvalid
                                                    }
                                                    style={{
                                                      paddingLeft: "0px",
                                                    }}
                                                  />
                                                  <ErrorField
                                                    show={
                                                      data.Q401cFrequencyIncreaseConflict_Elr
                                                    }
                                                    message={
                                                      ELCharacteristics_MSGS.Q401cFrequencyIncreaseConflict
                                                    }
                                                    style={{
                                                      paddingLeft: "0px",
                                                    }}
                                                  />
                                                  <ErrorField
                                                    show={
                                                      data.Q401cFrequencyDecreaseConflict_Elr
                                                    }
                                                    message={
                                                      ELCharacteristics_MSGS.Q401cFrequencyDecreaseConflict
                                                    }
                                                    style={{
                                                      paddingLeft: "0px",
                                                    }}
                                                  />
                                                  <ErrorField
                                                    show={
                                                      data.Q401cFreqAfterRd_Elr ===
                                                      99
                                                    }
                                                    message={Q401cWarningUnkMsg}
                                                    style={{
                                                      paddingLeft: "0px",
                                                    }}
                                                  />
                                                </div>
                                              </td>

                                              {/* Column E - Date of change */}
                                              <td
                                                style={{
                                                  border: "1px solid black",
                                                  padding: "8px",
                                                }}
                                              >
                                                <div>
                                                  <div
                                                    className="option-without-input"
                                                    style={{ display: "flex" }}
                                                  >
                                                    <label className="option-fbox">
                                                      <input
                                                        type="radio"
                                                        id={idx}
                                                        name={
                                                          "Q401cDateChangeRd_Elr" +
                                                          idx
                                                        }
                                                        value={1}
                                                        checked={
                                                          data.Q401cDateChangeRd_Elr ===
                                                          1
                                                        }
                                                        onChange={
                                                          HandleQ401cFieldChange
                                                        }
                                                      />
                                                      <span
                                                        className="radio-text-padding"
                                                        style={{
                                                          paddingLeft: "10px",
                                                          marginLeft: "-5px",
                                                        }}
                                                      >
                                                        <DatePickerComponent
                                                          ctrlId={
                                                            "Q401cDateChange_Elr-" +
                                                            idx
                                                          }
                                                          HandleDateChange={
                                                            HandleQ401cRepeaterDateChange
                                                          }
                                                          optionId={1}
                                                          minDate={dayjs(
                                                            "01-01-1920",
                                                          )}
                                                          maxDate={dayjs()}
                                                          locale="en"
                                                          date={
                                                            data.Q401cDateChange_Elr
                                                          }
                                                        />
                                                      </span>
                                                    </label>
                                                  </div>
                                                  <div
                                                    className="option-without-input"
                                                    style={{ display: "flex" }}
                                                  >
                                                    <label className="option-fbox">
                                                      <input
                                                        type="radio"
                                                        id={idx}
                                                        name={
                                                          "Q401cDateChangeRd_Elr" +
                                                          idx
                                                        }
                                                        value={99}
                                                        checked={
                                                          data.Q401cDateChangeRd_Elr ===
                                                          99
                                                        }
                                                        onChange={
                                                          HandleQ401cFieldChange
                                                        }
                                                      />
                                                      <span
                                                        className="radio-text-padding"
                                                        style={{
                                                          paddingLeft: "10px",
                                                          marginLeft: "-5px",
                                                        }}
                                                      >
                                                        Date unknown
                                                      </span>
                                                    </label>
                                                  </div>
                                                  <ErrorField
                                                    show={
                                                      data.Q401cDateChangeRdHard_Elr
                                                    }
                                                    message={
                                                      ELCharacteristics_MSGS.Q401cDateChangeRdHard
                                                    }
                                                    style={{
                                                      paddingLeft: "0px",
                                                    }}
                                                  />
                                                  <ErrorField
                                                    show={
                                                      data.Q401cDateChangeDtHard_Elr
                                                    }
                                                    message={
                                                      ELCharacteristics_MSGS.Q401cDateChangeDtHard
                                                    }
                                                    style={{
                                                      paddingLeft: "0px",
                                                    }}
                                                  />
                                                  <ErrorField
                                                    show={
                                                      data.Q401cDateChangeInvalid_Elr
                                                    }
                                                    message={
                                                      ELCharacteristics_MSGS.Q401cDateChangeInvalid
                                                    }
                                                    style={{
                                                      paddingLeft: "0px",
                                                    }}
                                                  />
                                                  <ErrorField
                                                    show={
                                                      data.Q401cDateChangeOutOfBound_Elr
                                                    }
                                                    message={
                                                      ELCharacteristics_MSGS.Q401cDateChangeOutOfBound
                                                    }
                                                    style={{
                                                      paddingLeft: "0px",
                                                    }}
                                                  />
                                                </div>
                                              </td>
                                            </>
                                          ) : (
                                            <td
                                              colSpan={3}
                                              style={{
                                                border: "1px solid black",
                                                padding: "8px",
                                                textAlign: "center",
                                              }}
                                            >
                                              <span
                                                style={{
                                                  textAlign: "center",
                                                }}
                                                className="not-applicable"
                                              >
                                                <b>Not applicable</b>
                                              </span>
                                            </td>
                                          )}
                                        </tr>
                                      </tbody>
                                    </table>
                                  </div>
                                </div>
                              </div>
                            ))}

                          {Q401cElrDetails &&
                            Q401cElrDetails.length > 0 &&
                            Number(Q401cElrDetails[0].Q401cChangeType_Elr) !==
                              1 &&
                            Number(Q401cElrDetails[0].Q401cChangeType_Elr) !==
                              99 && (
                              <div className="sub-question-bot-sapce-ul mt-2">
                                <div
                                  className="answer-list-text"
                                  onClick={AddQ401cElrRow}
                                >
                                  <a>
                                    <img
                                      width="18px"
                                      src="../Assets/images/plus.png"
                                      alt=""
                                    />
                                  </a>
                                  <a className="radio-text-padding">
                                    Click here to add another dose frequency
                                    change
                                  </a>
                                </div>
                              </div>
                            )}
                        </div>
                      )}
                    {/* 40.1.C end - Elrexfio */}

                    {/* 40.1.C start - Linvoseltamab */}
                    {isNewEcrfC &&
                      showQ40Linvo &&
                      Q37LinvoDetails[0]?.ivigInitRd_linvoC != null &&
                      Q37LinvoDetails[0]?.ivigInitRd_linvoC !== 1 && (
                        <div className="question question-weight">
                          <span>Q40.1.C</span>
                          <span className="quest-text-pad">
                            Please provide information on{" "}
                            <b>
                              <i>
                                <u>any IGRT dose frequency changes</u>
                              </i>
                            </b>{" "}
                            that occurred after initiation of IGRT during the
                            patient’s treatment with{" "}
                            <span
                              style={{
                                color: PageDesignConstants.fontColor,
                                fontWeight: "bolder",
                              }}
                            >
                              LYNOZYFIC<sup>TM</sup> (linvoseltamab-gcpt)
                            </span>{" "}
                            (i.e., from{" "}
                            <span
                              style={{ color: PageDesignConstants.fontColor }}
                            >
                              {showLinIvigInitDateQ401C()}
                            </span>{" "}
                            to{" "}
                            <span
                              style={{ color: PageDesignConstants.fontColor }}
                            >
                              {showLinvoQ40PeriodEndDate()}
                            </span>
                            ).
                            <br />
                            Please enter dose change frequency changes in
                            chronological order from earliest to most recent
                            after IGRT initiation. Note that, for the earliest
                            reported dose frequency change, the{" "}
                            <i>Pattern before change</i> has been auto-populated
                            with information from the{" "}
                            <i>Intended dose frequency</i> in Q40.C.
                            <br />
                            <br />
                            Add additional rows for each change, as needed.
                          </span>
                        </div>
                      )}

                    {isNewEcrfC &&
                      showQ40Linvo &&
                      Q37LinvoDetails[0]?.ivigInitRd_linvoC != null &&
                      Q37LinvoDetails[0]?.ivigInitRd_linvoC !== 1 && (
                        <div className="dependent-section">
                          <ErrorField
                            show={q401cLinvo_WarningHard}
                            message={
                              ELCharacteristics_MSGS.Q401cChainMismatchWarning
                            }
                            style={{ paddingLeft: "0px" }}
                          />
                          {Q401cLinvoDetails.length > 0 &&
                            Q401cLinvoDetails.map((data, idx) => (
                              <div className="loop-section-ul" key={idx}>
                                <div className="loop-head">
                                  <span
                                    className="question-weight"
                                    style={{ float: "inline-start" }}
                                  >
                                    {GetOrdinal(idx + 1)} Dose frequency change
                                  </span>
                                  {idx !== 0 && (
                                    <div
                                      className="delete-btn-div"
                                      style={{ float: "inline-end" }}
                                    >
                                      <img
                                        width="16px"
                                        src="../Assets/images/Icon-material-delete.png"
                                        alt=""
                                        style={{ cursor: "pointer" }}
                                        onClick={() => DeleteQ401cLinvoRow(idx)}
                                      />
                                    </div>
                                  )}
                                </div>

                                <div className="question-bot-sapce">
                                  <div
                                    className="sub-question"
                                    style={{ overflowX: "auto" }}
                                  >
                                    <table
                                      style={{
                                        border: "1px solid black",
                                        borderCollapse: "collapse",
                                        width: "100%",
                                      }}
                                    >
                                      <thead>
                                        <tr>
                                          <th
                                            style={{
                                              border: "1px solid black",
                                              padding: "8px",
                                              textAlign: "center",
                                            }}
                                          >
                                            Treatment
                                          </th>
                                          <th
                                            style={{
                                              border: "1px solid black",
                                              padding: "8px",
                                              textAlign: "center",
                                              minWidth: "200px",
                                            }}
                                          >
                                            Type of dose frequency change
                                          </th>
                                          <th
                                            style={{
                                              border: "1px solid black",
                                              padding: "8px",
                                              textAlign: "center",
                                            }}
                                          >
                                            Dose frequency <i>before</i> change
                                          </th>
                                          <th
                                            style={{
                                              border: "1px solid black",
                                              padding: "8px",
                                              textAlign: "center",
                                            }}
                                          >
                                            Dose frequency <i>after</i> change
                                          </th>
                                          <th
                                            style={{
                                              border: "1px solid black",
                                              padding: "8px",
                                              textAlign: "center",
                                            }}
                                          >
                                            Date of change
                                          </th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        <tr>
                                          <td
                                            style={{
                                              border: "1px solid black",
                                              padding: "8px",
                                            }}
                                          >
                                            <span className="quest-text-pad">
                                              LYNOZYFIC<sup>TM</sup>{" "}
                                              (linvoseltamab-gcpt)
                                            </span>
                                          </td>

                                          {/* Column B - Type of dose frequency change */}
                                          <td
                                            style={{
                                              border: "1px solid black",
                                              padding: "8px",
                                            }}
                                          >
                                            <div>
                                              {idx === 0 && (
                                                <div className="option-without-input">
                                                  <label className="option-fbox">
                                                    <input
                                                      type="radio"
                                                      id={idx}
                                                      name={
                                                        "Q401cChangeType_Lin" +
                                                        idx
                                                      }
                                                      value={1}
                                                      checked={
                                                        data.Q401cChangeType_Lin ===
                                                        1
                                                      }
                                                      onChange={
                                                        HandleQ401cFieldChange
                                                      }
                                                    />
                                                    <span className="radio-text-padding">
                                                      No dose frequency changes
                                                      occurred
                                                    </span>
                                                  </label>
                                                </div>
                                              )}
                                              <div className="option-without-input">
                                                <label className="option-fbox">
                                                  <input
                                                    type="radio"
                                                    id={idx}
                                                    name={
                                                      "Q401cChangeType_Lin" +
                                                      idx
                                                    }
                                                    value={2}
                                                    checked={
                                                      data.Q401cChangeType_Lin ===
                                                      2
                                                    }
                                                    onChange={
                                                      HandleQ401cFieldChange
                                                    }
                                                  />
                                                  <span className="radio-text-padding">
                                                    Dose frequency increase
                                                  </span>
                                                </label>
                                              </div>
                                              <div className="option-without-input">
                                                <label className="option-fbox">
                                                  <input
                                                    type="radio"
                                                    id={idx}
                                                    name={
                                                      "Q401cChangeType_Lin" +
                                                      idx
                                                    }
                                                    value={3}
                                                    checked={
                                                      data.Q401cChangeType_Lin ===
                                                      3
                                                    }
                                                    onChange={
                                                      HandleQ401cFieldChange
                                                    }
                                                  />
                                                  <span className="radio-text-padding">
                                                    Dose frequency decrease
                                                  </span>
                                                </label>
                                              </div>
                                              {idx === 0 && (
                                                <div className="option-without-input">
                                                  <label className="option-fbox">
                                                    <input
                                                      type="radio"
                                                      id={idx}
                                                      name={
                                                        "Q401cChangeType_Lin" +
                                                        idx
                                                      }
                                                      value={99}
                                                      checked={
                                                        data.Q401cChangeType_Lin ===
                                                        99
                                                      }
                                                      onChange={
                                                        HandleQ401cFieldChange
                                                      }
                                                    />
                                                    <span className="radio-text-padding">
                                                      Unknown if any dose
                                                      frequency changes occurred
                                                    </span>
                                                  </label>
                                                </div>
                                              )}
                                              <ErrorField
                                                show={
                                                  data.Q401cChangeTypeEmptyHard_Lin
                                                }
                                                message={
                                                  ELCharacteristics_MSGS.Q401cChangeTypeEmptyHard
                                                }
                                                style={{ paddingLeft: "0px" }}
                                              />
                                            </div>
                                          </td>

                                          {data.Q401cChangeType_Lin === 2 ||
                                          data.Q401cChangeType_Lin === 3 ? (
                                            <>
                                              {/* Column C - Dose frequency before change */}
                                              <td
                                                style={{
                                                  border: "1px solid black",
                                                  padding: "8px",
                                                }}
                                              >
                                                <div>
                                                  <label
                                                    className="option-without-input"
                                                    style={{
                                                      display: "flex",
                                                    }}
                                                  >
                                                    <input
                                                      type="radio"
                                                      id={idx}
                                                      name={
                                                        "Q401cFreqBeforeRd_Lin" +
                                                        idx
                                                      }
                                                      value={1}
                                                      checked={
                                                        data.Q401cFreqBeforeRd_Lin ===
                                                        1
                                                      }
                                                      onChange={
                                                        HandleQ401cFieldChange
                                                      }
                                                    />
                                                    <span
                                                      className="radio-text-padding"
                                                      style={{
                                                        paddingLeft: "10px",
                                                      }}
                                                    >
                                                      <input
                                                        type="text"
                                                        className="input-dash-ttcustom"
                                                        name={
                                                          "Q401cFreqBeforeTimesTxt_Lin" +
                                                          idx
                                                        }
                                                        id={idx}
                                                        value={
                                                          data.Q401cFreqBeforeTimesTxt_Lin
                                                        }
                                                        onChange={
                                                          HandleQ401cFieldChange
                                                        }
                                                        maxLength={2}
                                                      />{" "}
                                                      times per week
                                                    </span>
                                                  </label>
                                                  <label
                                                    className="option-without-input"
                                                    style={{
                                                      display: "flex",
                                                    }}
                                                  >
                                                    <input
                                                      type="radio"
                                                      id={idx}
                                                      name={
                                                        "Q401cFreqBeforeRd_Lin" +
                                                        idx
                                                      }
                                                      value={2}
                                                      checked={
                                                        data.Q401cFreqBeforeRd_Lin ===
                                                        2
                                                      }
                                                      onChange={
                                                        HandleQ401cFieldChange
                                                      }
                                                    />
                                                    <span
                                                      className="radio-text-padding"
                                                      style={{
                                                        paddingLeft: "10px",
                                                      }}
                                                    >
                                                      Once every{" "}
                                                      <input
                                                        type="text"
                                                        className="input-dash-ttcustom"
                                                        name={
                                                          "Q401cFreqBeforeWeeksTxt_Lin" +
                                                          idx
                                                        }
                                                        id={idx}
                                                        value={
                                                          data.Q401cFreqBeforeWeeksTxt_Lin
                                                        }
                                                        onChange={
                                                          HandleQ401cFieldChange
                                                        }
                                                        maxLength={2}
                                                      />{" "}
                                                      weeks
                                                    </span>
                                                  </label>
                                                  <label
                                                    className="option-without-input"
                                                    style={{
                                                      display: "flex",
                                                    }}
                                                  >
                                                    <input
                                                      type="radio"
                                                      id={idx}
                                                      name={
                                                        "Q401cFreqBeforeRd_Lin" +
                                                        idx
                                                      }
                                                      value={3}
                                                      checked={
                                                        data.Q401cFreqBeforeRd_Lin ===
                                                        3
                                                      }
                                                      onChange={
                                                        HandleQ401cFieldChange
                                                      }
                                                    />
                                                    <span
                                                      className="radio-text-padding"
                                                      style={{
                                                        paddingLeft: "10px",
                                                      }}
                                                    >
                                                      Other, please specify:
                                                      <div>
                                                        <textarea
                                                          className="input-dash"
                                                          name={
                                                            "Q401cFreqBeforeOtherTxt_Lin" +
                                                            idx
                                                          }
                                                          id={idx}
                                                          value={
                                                            data.Q401cFreqBeforeOtherTxt_Lin
                                                          }
                                                          onChange={
                                                            HandleQ401cFieldChange
                                                          }
                                                          maxLength={300}
                                                        />
                                                      </div>
                                                    </span>
                                                  </label>
                                                  <label
                                                    className="option-without-input"
                                                    style={{
                                                      display: "flex",
                                                    }}
                                                  >
                                                    <input
                                                      type="radio"
                                                      id={idx}
                                                      name={
                                                        "Q401cFreqBeforeRd_Lin" +
                                                        idx
                                                      }
                                                      value={99}
                                                      checked={
                                                        data.Q401cFreqBeforeRd_Lin ===
                                                        99
                                                      }
                                                      onChange={
                                                        HandleQ401cFieldChange
                                                      }
                                                    />
                                                    <span
                                                      className="radio-text-padding"
                                                      style={{
                                                        paddingLeft: "10px",
                                                      }}
                                                    >
                                                      Unknown
                                                    </span>
                                                  </label>
                                                  <ErrorField
                                                    show={
                                                      data.Q401cFreqBeforeEmptyHard_Lin
                                                    }
                                                    message={
                                                      ELCharacteristics_MSGS.Q401cFreqBeforeEmptyHard
                                                    }
                                                    style={{
                                                      paddingLeft: "0px",
                                                    }}
                                                  />
                                                  <ErrorField
                                                    show={
                                                      data.Q401cFreqBeforeTxtEmptyHard_Lin
                                                    }
                                                    message={
                                                      ELCharacteristics_MSGS.Q401cFreqBeforeTxtEmptyHard
                                                    }
                                                    style={{
                                                      paddingLeft: "0px",
                                                    }}
                                                  />
                                                  <ErrorField
                                                    show={
                                                      data.Q401cFreqBeforeRangeInvalid_Lin
                                                    }
                                                    message={
                                                      ELCharacteristics_MSGS.Q401cFreqBeforeRangeInvalid
                                                    }
                                                    style={{
                                                      paddingLeft: "0px",
                                                    }}
                                                  />
                                                  <ErrorField
                                                    show={
                                                      data.Q401cFreqBeforeRd_Lin ===
                                                      99
                                                    }
                                                    message={Q401cWarningUnkMsg}
                                                    style={{
                                                      paddingLeft: "0px",
                                                    }}
                                                  />
                                                </div>
                                              </td>

                                              {/* Column D - Dose frequency after change */}
                                              <td
                                                style={{
                                                  border: "1px solid black",
                                                  padding: "8px",
                                                }}
                                              >
                                                <div>
                                                  <label
                                                    className="option-without-input"
                                                    style={{ display: "flex" }}
                                                  >
                                                    <input
                                                      type="radio"
                                                      id={idx}
                                                      name={
                                                        "Q401cFreqAfterRd_Lin" +
                                                        idx
                                                      }
                                                      value={1}
                                                      checked={
                                                        data.Q401cFreqAfterRd_Lin ===
                                                        1
                                                      }
                                                      onChange={
                                                        HandleQ401cFieldChange
                                                      }
                                                    />
                                                    <span
                                                      className="radio-text-padding"
                                                      style={{
                                                        paddingLeft: "10px",
                                                      }}
                                                    >
                                                      <input
                                                        type="text"
                                                        className="input-dash-ttcustom"
                                                        name={
                                                          "Q401cFreqAfterTimesTxt_Lin" +
                                                          idx
                                                        }
                                                        id={idx}
                                                        value={
                                                          data.Q401cFreqAfterTimesTxt_Lin
                                                        }
                                                        onChange={
                                                          HandleQ401cFieldChange
                                                        }
                                                        maxLength={2}
                                                      />{" "}
                                                      times per week
                                                    </span>
                                                  </label>
                                                  <label
                                                    className="option-without-input"
                                                    style={{ display: "flex" }}
                                                  >
                                                    <input
                                                      type="radio"
                                                      id={idx}
                                                      name={
                                                        "Q401cFreqAfterRd_Lin" +
                                                        idx
                                                      }
                                                      value={2}
                                                      checked={
                                                        data.Q401cFreqAfterRd_Lin ===
                                                        2
                                                      }
                                                      onChange={
                                                        HandleQ401cFieldChange
                                                      }
                                                    />
                                                    <span
                                                      className="radio-text-padding"
                                                      style={{
                                                        paddingLeft: "10px",
                                                      }}
                                                    >
                                                      Once every{" "}
                                                      <input
                                                        type="text"
                                                        className="input-dash-ttcustom"
                                                        name={
                                                          "Q401cFreqAfterWeeksTxt_Lin" +
                                                          idx
                                                        }
                                                        id={idx}
                                                        value={
                                                          data.Q401cFreqAfterWeeksTxt_Lin
                                                        }
                                                        onChange={
                                                          HandleQ401cFieldChange
                                                        }
                                                        maxLength={2}
                                                      />{" "}
                                                      weeks
                                                    </span>
                                                  </label>
                                                  <label
                                                    className="option-without-input"
                                                    style={{ display: "flex" }}
                                                  >
                                                    <input
                                                      type="radio"
                                                      id={idx}
                                                      name={
                                                        "Q401cFreqAfterRd_Lin" +
                                                        idx
                                                      }
                                                      value={3}
                                                      checked={
                                                        data.Q401cFreqAfterRd_Lin ===
                                                        3
                                                      }
                                                      onChange={
                                                        HandleQ401cFieldChange
                                                      }
                                                    />
                                                    <span
                                                      className="radio-text-padding"
                                                      style={{
                                                        paddingLeft: "10px",
                                                      }}
                                                    >
                                                      Other, please specify:
                                                      <div>
                                                        <textarea
                                                          className="input-dash"
                                                          name={
                                                            "Q401cFreqAfterOtherTxt_Lin" +
                                                            idx
                                                          }
                                                          id={idx}
                                                          value={
                                                            data.Q401cFreqAfterOtherTxt_Lin
                                                          }
                                                          onChange={
                                                            HandleQ401cFieldChange
                                                          }
                                                          maxLength={300}
                                                        />
                                                      </div>
                                                    </span>
                                                  </label>
                                                  <label
                                                    className="option-without-input"
                                                    style={{ display: "flex" }}
                                                  >
                                                    <input
                                                      type="radio"
                                                      id={idx}
                                                      name={
                                                        "Q401cFreqAfterRd_Lin" +
                                                        idx
                                                      }
                                                      value={99}
                                                      checked={
                                                        data.Q401cFreqAfterRd_Lin ===
                                                        99
                                                      }
                                                      onChange={
                                                        HandleQ401cFieldChange
                                                      }
                                                    />
                                                    <span
                                                      className="radio-text-padding"
                                                      style={{
                                                        paddingLeft: "10px",
                                                      }}
                                                    >
                                                      Unknown
                                                    </span>
                                                  </label>
                                                  <ErrorField
                                                    show={
                                                      data.Q401cFreqAfterEmptyHard_Lin
                                                    }
                                                    message={
                                                      ELCharacteristics_MSGS.Q401cFreqAfterEmptyHard
                                                    }
                                                    style={{
                                                      paddingLeft: "0px",
                                                    }}
                                                  />
                                                  <ErrorField
                                                    show={
                                                      data.Q401cFreqAfterTxtEmptyHard_Lin
                                                    }
                                                    message={
                                                      ELCharacteristics_MSGS.Q401cFreqAfterTxtEmptyHard
                                                    }
                                                    style={{
                                                      paddingLeft: "0px",
                                                    }}
                                                  />
                                                  <ErrorField
                                                    show={
                                                      data.Q401cFreqAfterRangeInvalid_Lin
                                                    }
                                                    message={
                                                      ELCharacteristics_MSGS.Q401cFreqAfterRangeInvalid
                                                    }
                                                    style={{
                                                      paddingLeft: "0px",
                                                    }}
                                                  />
                                                  <ErrorField
                                                    show={
                                                      data.Q401cFrequencyIncreaseConflict_Lin
                                                    }
                                                    message={
                                                      ELCharacteristics_MSGS.Q401cFrequencyIncreaseConflict
                                                    }
                                                    style={{
                                                      paddingLeft: "0px",
                                                    }}
                                                  />
                                                  <ErrorField
                                                    show={
                                                      data.Q401cFrequencyDecreaseConflict_Lin
                                                    }
                                                    message={
                                                      ELCharacteristics_MSGS.Q401cFrequencyDecreaseConflict
                                                    }
                                                    style={{
                                                      paddingLeft: "0px",
                                                    }}
                                                  />
                                                  <ErrorField
                                                    show={
                                                      data.Q401cFreqAfterRd_Lin ===
                                                      99
                                                    }
                                                    message={Q401cWarningUnkMsg}
                                                    style={{
                                                      paddingLeft: "0px",
                                                    }}
                                                  />
                                                </div>
                                              </td>

                                              {/* Column E - Date of change */}
                                              <td
                                                style={{
                                                  border: "1px solid black",
                                                  padding: "8px",
                                                }}
                                              >
                                                <div>
                                                  <div
                                                    className="option-without-input"
                                                    style={{ display: "flex" }}
                                                  >
                                                    <label className="option-fbox">
                                                      <input
                                                        type="radio"
                                                        id={idx}
                                                        name={
                                                          "Q401cDateChangeRd_Lin" +
                                                          idx
                                                        }
                                                        value={1}
                                                        checked={
                                                          data.Q401cDateChangeRd_Lin ===
                                                          1
                                                        }
                                                        onChange={
                                                          HandleQ401cFieldChange
                                                        }
                                                      />
                                                      <span
                                                        className="radio-text-padding"
                                                        style={{
                                                          paddingLeft: "10px",
                                                          marginLeft: "-5px",
                                                        }}
                                                      >
                                                        <DatePickerComponent
                                                          ctrlId={
                                                            "Q401cDateChange_Lin-" +
                                                            idx
                                                          }
                                                          HandleDateChange={
                                                            HandleQ401cRepeaterDateChange
                                                          }
                                                          optionId={1}
                                                          minDate={dayjs(
                                                            "01-01-1920",
                                                          )}
                                                          maxDate={dayjs()}
                                                          locale="en"
                                                          date={
                                                            data.Q401cDateChange_Lin
                                                          }
                                                        />
                                                      </span>
                                                    </label>
                                                  </div>
                                                  <div
                                                    className="option-without-input"
                                                    style={{ display: "flex" }}
                                                  >
                                                    <label className="option-fbox">
                                                      <input
                                                        type="radio"
                                                        id={idx}
                                                        name={
                                                          "Q401cDateChangeRd_Lin" +
                                                          idx
                                                        }
                                                        value={99}
                                                        checked={
                                                          data.Q401cDateChangeRd_Lin ===
                                                          99
                                                        }
                                                        onChange={
                                                          HandleQ401cFieldChange
                                                        }
                                                      />
                                                      <span
                                                        className="radio-text-padding"
                                                        style={{
                                                          paddingLeft: "10px",
                                                          marginLeft: "-5px",
                                                        }}
                                                      >
                                                        Date unknown
                                                      </span>
                                                    </label>
                                                  </div>
                                                  <ErrorField
                                                    show={
                                                      data.Q401cDateChangeRdHard_Lin
                                                    }
                                                    message={
                                                      ELCharacteristics_MSGS.Q401cDateChangeRdHard
                                                    }
                                                    style={{
                                                      paddingLeft: "0px",
                                                    }}
                                                  />
                                                  <ErrorField
                                                    show={
                                                      data.Q401cDateChangeDtHard_Lin
                                                    }
                                                    message={
                                                      ELCharacteristics_MSGS.Q401cDateChangeDtHard
                                                    }
                                                    style={{
                                                      paddingLeft: "0px",
                                                    }}
                                                  />
                                                  <ErrorField
                                                    show={
                                                      data.Q401cDateChangeInvalid_Lin
                                                    }
                                                    message={
                                                      ELCharacteristics_MSGS.Q401cDateChangeInvalid
                                                    }
                                                    style={{
                                                      paddingLeft: "0px",
                                                    }}
                                                  />
                                                  <ErrorField
                                                    show={
                                                      data.Q401cDateChangeOutOfBound_Lin
                                                    }
                                                    message={
                                                      ELCharacteristics_MSGS.Q401cDateChangeOutOfBound
                                                    }
                                                    style={{
                                                      paddingLeft: "0px",
                                                    }}
                                                  />
                                                </div>
                                              </td>
                                            </>
                                          ) : (
                                            <td
                                              colSpan={3}
                                              style={{
                                                border: "1px solid black",
                                                padding: "8px",
                                                textAlign: "center",
                                              }}
                                            >
                                              <span
                                                style={{
                                                  textAlign: "center",
                                                }}
                                                className="not-applicable"
                                              >
                                                <b>Not applicable</b>
                                              </span>
                                            </td>
                                          )}
                                        </tr>
                                      </tbody>
                                    </table>
                                  </div>
                                </div>
                              </div>
                            ))}

                          {Q401cLinvoDetails &&
                            Q401cLinvoDetails.length > 0 &&
                            Number(Q401cLinvoDetails[0].Q401cChangeType_Lin) !==
                              1 &&
                            Number(Q401cLinvoDetails[0].Q401cChangeType_Lin) !==
                              99 && (
                              <div className="sub-question-bot-sapce-ul mt-2">
                                <div
                                  className="answer-list-text"
                                  onClick={AddQ401cLinvoRow}
                                >
                                  <a>
                                    <img
                                      width="18px"
                                      src="../Assets/images/plus.png"
                                      alt=""
                                    />
                                  </a>
                                  <a className="radio-text-padding">
                                    Click here to add another dose frequency
                                    change
                                  </a>
                                </div>
                              </div>
                            )}
                        </div>
                      )}
                    {/* 40.1.C end - Linvoseltamab */}

                    {/* 40.2.C start - Elrexfio */}
                    {isNewEcrfC && isElrexvayliSelected && (
                      <div className="question question-weight">
                        <span>Q40.2.C</span>
                        <span className="quest-text-pad">
                          Please provide information on{" "}
                          <b>
                            <i>
                              <u>immunoglobulin G (IgG) levels</u>
                            </i>
                          </b>{" "}
                          closest to each target date and within each target
                          window defined around treatment with{" "}
                          <span
                            style={{
                              color: PageDesignConstants.fontColor,
                              fontWeight: "bolder",
                            }}
                          >
                            ELREXFIO<sup>TM</sup> (elranatamab-bcmm)
                          </span>
                          .
                        </span>
                      </div>
                    )}

                    {isNewEcrfC && isElrexvayliSelected && (
                      <div className="dependent-section">
                        <ErrorField
                          show={q402cElrDatesWarning}
                          message={
                            ELCharacteristics_MSGS.Q402cDatesNotChronological
                          }
                          style={{ paddingLeft: "0px" }}
                        />
                        <ErrorField
                          show={q402cElrThresholdWarning}
                          message={
                            ELCharacteristics_MSGS.Q402cRow2ThresholdConflict
                          }
                          style={{ paddingLeft: "0px" }}
                        />
                        <div className="question-bot-sapce">
                          <div
                            className="sub-question grid-overflow"
                            style={{ backgroundColor: "#ffffff" }}
                          >
                            <table
                              style={{
                                border: "1px solid black",
                                borderCollapse: "collapse",
                                width: "100%",
                                backgroundColor: "#ffffff",
                              }}
                            >
                              <thead>
                                <tr>
                                  <th
                                    style={{
                                      border: "1px solid black",
                                      padding: "8px",
                                      textAlign: "center",
                                      backgroundColor: "#ffffff",
                                    }}
                                  >
                                    Treatment
                                  </th>
                                  <th
                                    style={{
                                      border: "1px solid black",
                                      padding: "8px",
                                      textAlign: "center",
                                      width: "27%",
                                      backgroundColor: "#ffffff",
                                    }}
                                  >
                                    Target assessment
                                  </th>
                                  <th
                                    style={{
                                      border: "1px solid black",
                                      padding: "8px",
                                      textAlign: "center",
                                      width: "27%",
                                      backgroundColor: "#ffffff",
                                    }}
                                  >
                                    Date of assessment
                                  </th>
                                  <th
                                    style={{
                                      border: "1px solid black",
                                      padding: "8px",
                                      textAlign: "center",
                                      width: "16%",
                                      backgroundColor: "#ffffff",
                                    }}
                                  >
                                    IgG level
                                  </th>
                                </tr>
                              </thead>
                              <tbody>
                                {Q402cElrDetails.map((data, idx) => {
                                  // Row 1 & Row 2 are always index 0/1; periodic
                                  // rows start at index 2, so N = index - 1.
                                  const rowNumber = idx - 1;
                                  const rowLabel =
                                    data.rowType === 1 ? (
                                      <>
                                        IgG level{" "}
                                        <u>
                                          closest to start of bispecific
                                          treatment
                                        </u>{" "}
                                        on{" "}
                                        <span
                                          style={{
                                            color:
                                              PageDesignConstants.fontColor,
                                          }}
                                        >
                                          {getQ402CIndexDateDisplay(
                                            RRMMCohortTreatment.ELREXFIO,
                                          )}
                                        </span>{" "}
                                        (and within{" "}
                                        <span
                                          style={{
                                            color:
                                              PageDesignConstants.fontColor,
                                          }}
                                        >
                                          {getQ402CIndexMinus365(
                                            null,
                                            RRMMCohortTreatment.ELREXFIO,
                                          )}
                                        </span>{" "}
                                        to{" "}
                                        <span
                                          style={{
                                            color:
                                              PageDesignConstants.fontColor,
                                          }}
                                        >
                                          {getQ402CPeriodEndDate(
                                            null,
                                            RRMMCohortTreatment.ELREXFIO,
                                          )}
                                        </span>
                                        )
                                      </>
                                    ) : data.rowType === 2 ? (
                                      <>
                                        <u>Highest IgG level</u> after treatment
                                        initiation (within{" "}
                                        <span
                                          style={{
                                            color:
                                              PageDesignConstants.fontColor,
                                          }}
                                        >
                                          {getQ402CIndexDateDisplay(
                                            RRMMCohortTreatment.ELREXFIO,
                                          )}
                                        </span>{" "}
                                        to{" "}
                                        <span
                                          style={{
                                            color:
                                              PageDesignConstants.fontColor,
                                          }}
                                        >
                                          {getQ402CPeriodEndDate(
                                            null,
                                            RRMMCohortTreatment.ELREXFIO,
                                          )}
                                        </span>
                                        )
                                      </>
                                    ) : (
                                      <>
                                        IgG <u>level closest to target date</u>{" "}
                                        of{" "}
                                        <span
                                          style={{
                                            color:
                                              PageDesignConstants.fontColor,
                                          }}
                                        >
                                          {getQ402CTargetDateDisplay(
                                            null,
                                            RRMMCohortTreatment.ELREXFIO,
                                            rowNumber,
                                          )}
                                        </span>{" "}
                                        (and within{" "}
                                        <span
                                          style={{
                                            color:
                                              PageDesignConstants.fontColor,
                                          }}
                                        >
                                          {getQ402CTargetMinus45(
                                            null,
                                            RRMMCohortTreatment.ELREXFIO,
                                            rowNumber,
                                          )}
                                        </span>{" "}
                                        to{" "}
                                        <span
                                          style={{
                                            color:
                                              PageDesignConstants.fontColor,
                                          }}
                                        >
                                          {getQ402CTargetPlus45Capped(
                                            null,
                                            RRMMCohortTreatment.ELREXFIO,
                                            rowNumber,
                                          )}
                                        </span>
                                        )
                                      </>
                                    );

                                  return (
                                    <tr key={idx}>
                                      <td
                                        style={{
                                          border: "1px solid black",
                                          padding: "8px",
                                          backgroundColor: "#ffffff",
                                        }}
                                      >
                                        <span className="quest-text-pad">
                                          ELREXFIO<sup>TM</sup>{" "}
                                          (elranatamab-bcmm)
                                        </span>
                                      </td>

                                      <td
                                        style={{
                                          border: "1px solid black",
                                          padding: "8px",
                                          backgroundColor: "#ffffff",
                                        }}
                                      >
                                        <span className="quest-text-pad">
                                          {rowLabel}
                                        </span>
                                      </td>

                                      {/* Date of assessment */}
                                      <td
                                        style={{
                                          border: "1px solid black",
                                          padding: "8px",
                                          backgroundColor: "#ffffff",
                                        }}
                                      >
                                        <div>
                                          <div
                                            className="option-without-input"
                                            style={{ display: "flex" }}
                                          >
                                            <label className="option-fbox">
                                              <input
                                                type="radio"
                                                id={idx}
                                                name={
                                                  "Q402cAssessment_Elr" + idx
                                                }
                                                value={1}
                                                checked={
                                                  data.Q402cAssessment_Elr === 1
                                                }
                                                onChange={
                                                  HandleQ402cFieldChange
                                                }
                                              />
                                              <span
                                                style={{
                                                  paddingLeft: "10px",
                                                  marginLeft: "-5px",
                                                }}
                                              >
                                                No IgG assessment available
                                              </span>
                                            </label>
                                          </div>

                                          <div
                                            className="option-without-input"
                                            style={{ display: "flex" }}
                                          >
                                            <input
                                              type="radio"
                                              id={idx}
                                              name={"Q402cAssessment_Elr" + idx}
                                              value={2}
                                              checked={
                                                data.Q402cAssessment_Elr === 2
                                              }
                                              onChange={HandleQ402cFieldChange}
                                            />
                                            <span
                                              className="radio-text-padding"
                                              style={{
                                                paddingLeft: "10px",
                                                marginLeft: "-5px",
                                              }}
                                            >
                                              <DatePickerComponent
                                                ctrlId={
                                                  "Q402cAssessment_Elr_dt-" +
                                                  idx
                                                }
                                                HandleDateChange={
                                                  HandleQ402cRepeaterDateChange
                                                }
                                                optionId={1}
                                                minDate={dayjs("01-01-1920")}
                                                maxDate={dayjs()}
                                                locale="en"
                                                date={
                                                  data.Q402cAssessment_Elr_dt
                                                }
                                              />
                                            </span>
                                          </div>

                                          <div
                                            className="option-without-input"
                                            style={{ display: "flex" }}
                                          >
                                            <label className="option-fbox">
                                              <input
                                                type="radio"
                                                id={idx}
                                                name={
                                                  "Q402cAssessment_Elr" + idx
                                                }
                                                value={99}
                                                checked={
                                                  data.Q402cAssessment_Elr ===
                                                  99
                                                }
                                                onChange={
                                                  HandleQ402cFieldChange
                                                }
                                              />
                                              <span
                                                style={{
                                                  paddingLeft: "10px",
                                                  marginLeft: "-5px",
                                                }}
                                              >
                                                Date unknown
                                              </span>
                                            </label>
                                          </div>

                                          <ErrorField
                                            show={
                                              data.Q402cAssessment_Elr_RdHard
                                            }
                                            message={
                                              ELCharacteristics_MSGS.Q402cSelect_RdHard
                                            }
                                            style={{ paddingLeft: "0px" }}
                                          />
                                          <ErrorField
                                            show={
                                              data.Q402cAssessment_Elr_dtHard
                                            }
                                            message={
                                              ELCharacteristics_MSGS.Q402cIggDateAdm_Hard
                                            }
                                            style={{ paddingLeft: "0px" }}
                                          />
                                          <ErrorField
                                            show={
                                              data.Q402cAssessment_Elr_dtInvalid
                                            }
                                            message={
                                              CommonError_Msgs.InvalidDate
                                            }
                                            style={{ paddingLeft: "0px" }}
                                          />
                                          <ErrorField
                                            show={
                                              data.Q402cAssessment_Elr_dtOutOfRange
                                            }
                                            message={
                                              ELCharacteristics_MSGS.Q402cDateOutOfRange
                                            }
                                            style={{ paddingLeft: "0px" }}
                                          />
                                          <ErrorField
                                            show={data.Q402cRow1NotEarliestHard}
                                            message={
                                              ELCharacteristics_MSGS.Q402cRow1NotEarliest
                                            }
                                            style={{ paddingLeft: "0px" }}
                                          />
                                        </div>
                                      </td>

                                      {/* IgG level */}
                                      {data.Q402cAssessment_Elr !== 1 ? (
                                        <td
                                          style={{
                                            border: "1px solid black",
                                            padding: "8px",
                                            textAlign: "start",
                                            backgroundColor: "#ffffff",
                                          }}
                                        >
                                          {data.rowType === 2 ? (
                                            <div>
                                              <div
                                                className="option-without-input"
                                                style={{ display: "flex" }}
                                              >
                                                <label className="option-fbox">
                                                  <input
                                                    type="radio"
                                                    id={idx}
                                                    name={
                                                      "Q402cLevelValue_Elr" +
                                                      idx
                                                    }
                                                    value={1}
                                                    checked={
                                                      data.Q402cLevelValue_Elr ===
                                                      1
                                                    }
                                                    onChange={
                                                      HandleQ402cFieldChange
                                                    }
                                                  />
                                                  <span
                                                    style={{
                                                      paddingLeft: "10px",
                                                      display: "flex",
                                                    }}
                                                  >
                                                    <span>
                                                      <input
                                                        className="input-dash-ttcustom"
                                                        id={idx}
                                                        name={
                                                          "Q402cLevelValue_Elr_MgValue" +
                                                          idx
                                                        }
                                                        value={
                                                          data.Q402cLevelValue_Elr_MgValue
                                                        }
                                                        type="text"
                                                        onChange={
                                                          HandleQ402cFieldChange
                                                        }
                                                        onKeyDown={
                                                          AllowNumericWithDecimal
                                                        }
                                                      />
                                                    </span>
                                                    <span>&nbsp;mg/dL</span>
                                                  </span>
                                                </label>
                                              </div>

                                              <div className="option-without-input">
                                                <label className="option-fbox">
                                                  <input
                                                    type="radio"
                                                    id={idx}
                                                    name={
                                                      "Q402cLevelValue_Elr" +
                                                      idx
                                                    }
                                                    value={99}
                                                    checked={
                                                      data.Q402cLevelValue_Elr ===
                                                      99
                                                    }
                                                    onChange={
                                                      HandleQ402cFieldChange
                                                    }
                                                  />
                                                  <span className="radio-text-padding">
                                                    Unknown
                                                  </span>
                                                </label>
                                              </div>

                                              <ErrorField
                                                show={
                                                  data.Q402cLevelValue_Elr_Empty
                                                }
                                                message={
                                                  ELCharacteristics_MSGS.Q402cSelect_RdHard
                                                }
                                                style={{ paddingLeft: "0px" }}
                                              />
                                              <ErrorField
                                                show={
                                                  data.Q402cLevelValue_Elr_RangeInvalid
                                                }
                                                message={
                                                  data.Q402cLevelValue_Elr === 2
                                                    ? ELCharacteristics_MSGS.Q402cLevelRangeHardGL
                                                    : ELCharacteristics_MSGS.Q402cLevelRangeHardMG
                                                }
                                                style={{ paddingLeft: "0px" }}
                                              />
                                            </div>
                                          ) : (
                                            <div>
                                              {[
                                                { val: 1, label: "<400 mg/dL" },
                                                {
                                                  val: 2,
                                                  label: "400-500 mg/dL",
                                                },
                                                { val: 3, label: ">500 mg/dL" },
                                                { val: 99, label: "Unknown" },
                                              ].map((opt) => (
                                                <div
                                                  key={opt.val}
                                                  className="option-without-input"
                                                >
                                                  <label className="option-fbox">
                                                    <input
                                                      type="radio"
                                                      id={idx}
                                                      name={
                                                        "Q402cLevelBucket_Elr" +
                                                        idx
                                                      }
                                                      value={opt.val}
                                                      checked={
                                                        data.Q402cLevelBucket_Elr ===
                                                        opt.val
                                                      }
                                                      onChange={
                                                        HandleQ402cFieldChange
                                                      }
                                                    />
                                                    <span className="radio-text-padding">
                                                      {opt.label}
                                                    </span>
                                                  </label>
                                                </div>
                                              ))}
                                              <ErrorField
                                                show={
                                                  data.Q402cLevelBucket_Elr_Empty
                                                }
                                                message={
                                                  ELCharacteristics_MSGS.Q402cLevelBucketEmptyHard
                                                }
                                                style={{ paddingLeft: "0px" }}
                                              />
                                            </div>
                                          )}
                                        </td>
                                      ) : (
                                        <td
                                          style={{
                                            border: "1px solid black",
                                            padding: "8px",
                                            textAlign: "center",
                                            backgroundColor: "#ffffff",
                                          }}
                                        >
                                          <span className="not-applicable">
                                            <b>Not applicable</b>
                                          </span>
                                        </td>
                                      )}
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </div>
                    )}
                    {/* 40.2.C end - Elrexfio */}

                    {/* 40.2.C start - Linvoseltamab */}
                    {isNewEcrfC && isLinvoveySelected && (
                      <div className="question question-weight">
                        <span>Q40.2.C</span>
                        <span className="quest-text-pad">
                          Please provide information on{" "}
                          <b>
                            <i>
                              <u>immunoglobulin G (IgG) levels</u>
                            </i>
                          </b>{" "}
                          closest to each target date and within each target
                          window defined around treatment with{" "}
                          <span
                            style={{
                              color: PageDesignConstants.fontColor,
                              fontWeight: "bolder",
                            }}
                          >
                            LYNOZYFIC<sup>TM</sup> (linvoseltamab-gcpt)
                          </span>
                          .
                        </span>
                      </div>
                    )}

                    {isNewEcrfC && isLinvoveySelected && (
                      <div className="dependent-section">
                        <ErrorField
                          show={q402cLinvoDatesWarning}
                          message={
                            ELCharacteristics_MSGS.Q402cDatesNotChronological
                          }
                          style={{ paddingLeft: "0px" }}
                        />
                        <ErrorField
                          show={q402cLinvoThresholdWarning}
                          message={
                            ELCharacteristics_MSGS.Q402cRow2ThresholdConflict
                          }
                          style={{ paddingLeft: "0px" }}
                        />
                        <div className="question-bot-sapce">
                          <div
                            className="sub-question grid-overflow"
                            style={{ backgroundColor: "#ffffff" }}
                          >
                            <table
                              style={{
                                border: "1px solid black",
                                borderCollapse: "collapse",
                                width: "100%",
                                backgroundColor: "#ffffff",
                              }}
                            >
                              <thead>
                                <tr>
                                  <th
                                    style={{
                                      border: "1px solid black",
                                      padding: "8px",
                                      textAlign: "center",
                                      backgroundColor: "#ffffff",
                                    }}
                                  >
                                    Treatment
                                  </th>
                                  <th
                                    style={{
                                      border: "1px solid black",
                                      padding: "8px",
                                      textAlign: "center",
                                      width: "27%",
                                      backgroundColor: "#ffffff",
                                    }}
                                  >
                                    Target assessment
                                  </th>
                                  <th
                                    style={{
                                      border: "1px solid black",
                                      padding: "8px",
                                      textAlign: "center",
                                      width: "27%",
                                      backgroundColor: "#ffffff",
                                    }}
                                  >
                                    Date of assessment
                                  </th>
                                  <th
                                    style={{
                                      border: "1px solid black",
                                      padding: "8px",
                                      textAlign: "center",
                                      width: "16%",
                                      backgroundColor: "#ffffff",
                                    }}
                                  >
                                    IgG level
                                  </th>
                                </tr>
                              </thead>
                              <tbody>
                                {Q402cLinvoDetails.map((data, idx) => {
                                  // Row 1 & Row 2 are always index 0/1; periodic
                                  // rows start at index 2, so N = index - 1.
                                  const rowNumber = idx - 1;
                                  const rowLabel =
                                    data.rowType === 1 ? (
                                      <>
                                        IgG level{" "}
                                        <u>
                                          closest to start of bispecific
                                          treatment
                                        </u>{" "}
                                        on{" "}
                                        <span
                                          style={{
                                            color:
                                              PageDesignConstants.fontColor,
                                          }}
                                        >
                                          {getQ402CIndexDateDisplay(
                                            RRMMCohortTreatment.Linvoseltamab,
                                          )}
                                        </span>{" "}
                                        (and within{" "}
                                        <span
                                          style={{
                                            color:
                                              PageDesignConstants.fontColor,
                                          }}
                                        >
                                          {getQ402CIndexMinus365(
                                            null,
                                            RRMMCohortTreatment.Linvoseltamab,
                                          )}
                                        </span>{" "}
                                        to{" "}
                                        <span
                                          style={{
                                            color:
                                              PageDesignConstants.fontColor,
                                          }}
                                        >
                                          {getQ402CPeriodEndDate(
                                            null,
                                            RRMMCohortTreatment.Linvoseltamab,
                                          )}
                                        </span>
                                        )
                                      </>
                                    ) : data.rowType === 2 ? (
                                      <>
                                        <u>Highest IgG level</u> after treatment
                                        initiation (within{" "}
                                        <span
                                          style={{
                                            color:
                                              PageDesignConstants.fontColor,
                                          }}
                                        >
                                          {getQ402CIndexDateDisplay(
                                            RRMMCohortTreatment.Linvoseltamab,
                                          )}
                                        </span>{" "}
                                        to{" "}
                                        <span
                                          style={{
                                            color:
                                              PageDesignConstants.fontColor,
                                          }}
                                        >
                                          {getQ402CPeriodEndDate(
                                            null,
                                            RRMMCohortTreatment.Linvoseltamab,
                                          )}
                                        </span>
                                        )
                                      </>
                                    ) : (
                                      <>
                                        IgG <u>level closest to target date</u>{" "}
                                        of{" "}
                                        <span
                                          style={{
                                            color:
                                              PageDesignConstants.fontColor,
                                          }}
                                        >
                                          {getQ402CTargetDateDisplay(
                                            null,
                                            RRMMCohortTreatment.Linvoseltamab,
                                            rowNumber,
                                          )}
                                        </span>{" "}
                                        (and within{" "}
                                        <span
                                          style={{
                                            color:
                                              PageDesignConstants.fontColor,
                                          }}
                                        >
                                          {getQ402CTargetMinus45(
                                            null,
                                            RRMMCohortTreatment.Linvoseltamab,
                                            rowNumber,
                                          )}
                                        </span>{" "}
                                        to{" "}
                                        <span
                                          style={{
                                            color:
                                              PageDesignConstants.fontColor,
                                          }}
                                        >
                                          {getQ402CTargetPlus45Capped(
                                            null,
                                            RRMMCohortTreatment.Linvoseltamab,
                                            rowNumber,
                                          )}
                                        </span>
                                        )
                                      </>
                                    );

                                  return (
                                    <tr key={idx}>
                                      <td
                                        style={{
                                          border: "1px solid black",
                                          padding: "8px",
                                          backgroundColor: "#ffffff",
                                        }}
                                      >
                                        <span className="quest-text-pad">
                                          LYNOZYFIC<sup>TM</sup>{" "}
                                          (linvoseltamab-gcpt)
                                        </span>
                                      </td>

                                      <td
                                        style={{
                                          border: "1px solid black",
                                          padding: "8px",
                                          backgroundColor: "#ffffff",
                                        }}
                                      >
                                        <span className="quest-text-pad">
                                          {rowLabel}
                                        </span>
                                      </td>

                                      {/* Date of assessment */}
                                      <td
                                        style={{
                                          border: "1px solid black",
                                          padding: "8px",
                                          backgroundColor: "#ffffff",
                                        }}
                                      >
                                        <div>
                                          <div
                                            className="option-without-input"
                                            style={{ display: "flex" }}
                                          >
                                            <label className="option-fbox">
                                              <input
                                                type="radio"
                                                id={idx}
                                                name={
                                                  "Q402cAssessment_Lin" + idx
                                                }
                                                value={1}
                                                checked={
                                                  data.Q402cAssessment_Lin === 1
                                                }
                                                onChange={
                                                  HandleQ402cFieldChange
                                                }
                                              />
                                              <span
                                                style={{
                                                  paddingLeft: "10px",
                                                  marginLeft: "-5px",
                                                }}
                                              >
                                                No IgG assessment available
                                              </span>
                                            </label>
                                          </div>

                                          <div
                                            className="option-without-input"
                                            style={{ display: "flex" }}
                                          >
                                            <input
                                              type="radio"
                                              id={idx}
                                              name={"Q402cAssessment_Lin" + idx}
                                              value={2}
                                              checked={
                                                data.Q402cAssessment_Lin === 2
                                              }
                                              onChange={HandleQ402cFieldChange}
                                            />
                                            <span
                                              className="radio-text-padding"
                                              style={{
                                                paddingLeft: "10px",
                                                marginLeft: "-5px",
                                              }}
                                            >
                                              <DatePickerComponent
                                                ctrlId={
                                                  "Q402cAssessment_Lin_dt-" +
                                                  idx
                                                }
                                                HandleDateChange={
                                                  HandleQ402cRepeaterDateChange
                                                }
                                                optionId={1}
                                                minDate={dayjs("01-01-1920")}
                                                maxDate={dayjs()}
                                                locale="en"
                                                date={
                                                  data.Q402cAssessment_Lin_dt
                                                }
                                              />
                                            </span>
                                          </div>

                                          <div
                                            className="option-without-input"
                                            style={{ display: "flex" }}
                                          >
                                            <label className="option-fbox">
                                              <input
                                                type="radio"
                                                id={idx}
                                                name={
                                                  "Q402cAssessment_Lin" + idx
                                                }
                                                value={99}
                                                checked={
                                                  data.Q402cAssessment_Lin ===
                                                  99
                                                }
                                                onChange={
                                                  HandleQ402cFieldChange
                                                }
                                              />
                                              <span
                                                style={{
                                                  paddingLeft: "10px",
                                                  marginLeft: "-5px",
                                                }}
                                              >
                                                Date unknown
                                              </span>
                                            </label>
                                          </div>

                                          <ErrorField
                                            show={
                                              data.Q402cAssessment_Lin_RdHard
                                            }
                                            message={
                                              ELCharacteristics_MSGS.Q402cSelect_RdHard
                                            }
                                            style={{ paddingLeft: "0px" }}
                                          />
                                          <ErrorField
                                            show={
                                              data.Q402cAssessment_Lin_dtHard
                                            }
                                            message={
                                              ELCharacteristics_MSGS.Q402cIggDateAdm_Hard
                                            }
                                            style={{ paddingLeft: "0px" }}
                                          />
                                          <ErrorField
                                            show={
                                              data.Q402cAssessment_Lin_dtInvalid
                                            }
                                            message={
                                              CommonError_Msgs.InvalidDate
                                            }
                                            style={{ paddingLeft: "0px" }}
                                          />
                                          <ErrorField
                                            show={
                                              data.Q402cAssessment_Lin_dtOutOfRange
                                            }
                                            message={
                                              ELCharacteristics_MSGS.Q402cDateOutOfRange
                                            }
                                            style={{ paddingLeft: "0px" }}
                                          />
                                          <ErrorField
                                            show={data.Q402cRow1NotEarliestHard}
                                            message={
                                              ELCharacteristics_MSGS.Q402cRow1NotEarliest
                                            }
                                            style={{ paddingLeft: "0px" }}
                                          />
                                        </div>
                                      </td>

                                      {/* IgG level */}
                                      {data.Q402cAssessment_Lin !== 1 ? (
                                        <td
                                          style={{
                                            border: "1px solid black",
                                            padding: "8px",
                                            textAlign: "start",
                                            backgroundColor: "#ffffff",
                                          }}
                                        >
                                          {data.rowType === 2 ? (
                                            <div>
                                              <div
                                                className="option-without-input"
                                                style={{ display: "flex" }}
                                              >
                                                <label className="option-fbox">
                                                  <input
                                                    type="radio"
                                                    id={idx}
                                                    name={
                                                      "Q402cLevelValue_Lin" +
                                                      idx
                                                    }
                                                    value={1}
                                                    checked={
                                                      data.Q402cLevelValue_Lin ===
                                                      1
                                                    }
                                                    onChange={
                                                      HandleQ402cFieldChange
                                                    }
                                                  />
                                                  <span
                                                    style={{
                                                      paddingLeft: "10px",
                                                      display: "flex",
                                                    }}
                                                  >
                                                    <span>
                                                      <input
                                                        className="input-dash-ttcustom"
                                                        id={idx}
                                                        name={
                                                          "Q402cLevelValue_Lin_MgValue" +
                                                          idx
                                                        }
                                                        value={
                                                          data.Q402cLevelValue_Lin_MgValue
                                                        }
                                                        type="text"
                                                        onChange={
                                                          HandleQ402cFieldChange
                                                        }
                                                        onKeyDown={
                                                          AllowNumericWithDecimal
                                                        }
                                                      />
                                                    </span>
                                                    <span>&nbsp;mg/dL</span>
                                                  </span>
                                                </label>
                                              </div>

                                              <div className="option-without-input">
                                                <label className="option-fbox">
                                                  <input
                                                    type="radio"
                                                    id={idx}
                                                    name={
                                                      "Q402cLevelValue_Lin" +
                                                      idx
                                                    }
                                                    value={99}
                                                    checked={
                                                      data.Q402cLevelValue_Lin ===
                                                      99
                                                    }
                                                    onChange={
                                                      HandleQ402cFieldChange
                                                    }
                                                  />
                                                  <span className="radio-text-padding">
                                                    Unknown
                                                  </span>
                                                </label>
                                              </div>

                                              <ErrorField
                                                show={
                                                  data.Q402cLevelValue_Lin_Empty
                                                }
                                                message={
                                                  ELCharacteristics_MSGS.Q402cSelect_RdHard
                                                }
                                                style={{ paddingLeft: "0px" }}
                                              />
                                              <ErrorField
                                                show={
                                                  data.Q402cLevelValue_Lin_RangeInvalid
                                                }
                                                message={
                                                  data.Q402cLevelValue_Lin === 2
                                                    ? ELCharacteristics_MSGS.Q402cLevelRangeHardGL
                                                    : ELCharacteristics_MSGS.Q402cLevelRangeHardMG
                                                }
                                                style={{ paddingLeft: "0px" }}
                                              />
                                            </div>
                                          ) : (
                                            <div>
                                              {[
                                                { val: 1, label: "<400 mg/dL" },
                                                {
                                                  val: 2,
                                                  label: "400-500 mg/dL",
                                                },
                                                { val: 3, label: ">500 mg/dL" },
                                                { val: 99, label: "Unknown" },
                                              ].map((opt) => (
                                                <div
                                                  key={opt.val}
                                                  className="option-without-input"
                                                >
                                                  <label className="option-fbox">
                                                    <input
                                                      type="radio"
                                                      id={idx}
                                                      name={
                                                        "Q402cLevelBucket_Lin" +
                                                        idx
                                                      }
                                                      value={opt.val}
                                                      checked={
                                                        data.Q402cLevelBucket_Lin ===
                                                        opt.val
                                                      }
                                                      onChange={
                                                        HandleQ402cFieldChange
                                                      }
                                                    />
                                                    <span className="radio-text-padding">
                                                      {opt.label}
                                                    </span>
                                                  </label>
                                                </div>
                                              ))}
                                              <ErrorField
                                                show={
                                                  data.Q402cLevelBucket_Lin_Empty
                                                }
                                                message={
                                                  ELCharacteristics_MSGS.Q402cLevelBucketEmptyHard
                                                }
                                                style={{ paddingLeft: "0px" }}
                                              />
                                            </div>
                                          )}
                                        </td>
                                      ) : (
                                        <td
                                          style={{
                                            border: "1px solid black",
                                            padding: "8px",
                                            textAlign: "center",
                                            backgroundColor: "#ffffff",
                                          }}
                                        >
                                          <span className="not-applicable">
                                            <b>Not applicable</b>
                                          </span>
                                        </td>
                                      )}
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </div>
                    )}
                    {/* 40.2.C end - Linvoseltamab */}

                    {/* 40.2.A start */}
                    {!isNewEcrfC &&
                      isNewEcrfB &&
                      (showQ40Elr || showQ40Linvo) && (
                        <div>
                          <div className="question question-weight">
                            <span>Q40.2.B</span>
                            <span className="quest-text-pad">
                              Please provide the immunoglobulin G (IgG) level{" "}
                              <i>at the start of bispecific treatment </i>{" "}
                              {showQ40Elr && (
                                <>
                                  (ranging from
                                  <span
                                    style={{
                                      color: PageDesignConstants.fontColor,
                                      fontWeight: "bolder",
                                    }}
                                  >
                                    {" "}
                                    {showElrDate91()}{" "}
                                  </span>
                                  to{" "}
                                  <span
                                    style={{
                                      color: PageDesignConstants.fontColor,
                                      fontWeight: "bolder",
                                    }}
                                  >
                                    {" "}
                                    {getElrLinoQ402BUpperLimitDate(
                                      null,
                                      RRMMCohortTreatment.ELREXFIO,
                                    )}{" "}
                                  </span>
                                  ) with{" "}
                                </>
                              )}
                              <span
                                style={{
                                  color: PageDesignConstants.fontColor,
                                  fontWeight: "bolder",
                                }}
                              >
                                {showQ40Elr && (
                                  <>
                                    ELREXFIO<sup>TM</sup> (elranatamab-bcmm)
                                  </>
                                )}

                                {showQ40Elr && showQ40Linvo
                                  ? " and "
                                  : showQ40Elr
                                    ? "."
                                    : ""}
                              </span>
                              <span>
                                {showQ40Linvo && (
                                  <>
                                    (ranging from
                                    <span
                                      style={{
                                        color: PageDesignConstants.fontColor,
                                        fontWeight: "bolder",
                                      }}
                                    >
                                      {" "}
                                      {showLinDate91()}{" "}
                                    </span>
                                    to{" "}
                                    <span
                                      style={{
                                        color: PageDesignConstants.fontColor,
                                        fontWeight: "bolder",
                                      }}
                                    >
                                      {" "}
                                      {getElrLinoQ402BUpperLimitDate(
                                        null,
                                        RRMMCohortTreatment.Linvoseltamab,
                                      )}{" "}
                                    </span>
                                    ) with{" "}
                                    <span
                                      style={{
                                        color: PageDesignConstants.fontColor,
                                        fontWeight: "bolder",
                                      }}
                                    >
                                      LYNOZYFIC<sup>TM</sup>{" "}
                                      (linvoseltamab-gcpt)
                                    </span>
                                    .
                                  </>
                                )}
                              </span>
                            </span>
                          </div>

                          <div className="question-bot-sapce">
                            <div className="sub-question grid-overflow">
                              <table
                                style={{
                                  border: "1px solid black",
                                  borderCollapse: "collapse",
                                  width: "100%",
                                }}
                              >
                                <thead>
                                  <tr>
                                    <th
                                      style={{
                                        border: "1px solid black",
                                        padding: "8px",
                                        textAlign: "center",
                                      }}
                                    >
                                      Treatment
                                    </th>
                                    <th
                                      style={{
                                        border: "1px solid black",
                                        padding: "8px",
                                        textAlign: "center",
                                        width: "33%",
                                      }}
                                    >
                                      Date of IgG assessment at the start of
                                      bispecific treatment
                                    </th>

                                    <th
                                      style={{
                                        border: "1px solid black",
                                        padding: "8px",
                                        textAlign: "center",
                                        width: "16%",
                                      }}
                                    >
                                      IgG level at the start of bispecific
                                      treatment
                                    </th>
                                  </tr>
                                </thead>

                                <tbody>
                                  {/* ELREX */}
                                  {showQ40Elr &&
                                    Q402aElrDetails.length > 0 &&
                                    Q402aElrDetails.map((data, idx) => (
                                      <tr key={idx}>
                                        <td
                                          style={{
                                            border: "1px solid black",
                                            padding: "8px",
                                          }}
                                        >
                                          <span className="quest-text-pad">
                                            <>
                                              ELREXFIO<sup>TM</sup>{" "}
                                              (elranatamab-bcmm).
                                            </>
                                          </span>
                                        </td>

                                        {/* IgG assessment */}
                                        <td
                                          style={{
                                            border: "1px solid black",
                                            padding: "8px",
                                          }}
                                        >
                                          <div>
                                            <div
                                              className="option-without-input"
                                              style={{
                                                display: "flex",
                                              }}
                                            >
                                              <label className="option-fbox">
                                                <input
                                                  type="radio"
                                                  id={idx}
                                                  name={
                                                    "Q402aIgg_assessment_Elr" +
                                                    idx
                                                  }
                                                  value={1}
                                                  checked={
                                                    data.Q402aIgg_assessment_Elr ===
                                                    1
                                                  }
                                                  onChange={
                                                    HandleQ402aFieldChange
                                                  }
                                                />
                                                <span
                                                  style={{
                                                    paddingLeft: "10px",
                                                    marginLeft: "-5px",
                                                  }}
                                                >
                                                  No IgG assessment performed at
                                                  treatment initiation
                                                </span>
                                              </label>
                                            </div>

                                            <div
                                              className="option-without-input"
                                              style={{
                                                display: "flex",
                                              }}
                                            >
                                              <input
                                                type="radio"
                                                id={idx}
                                                name={
                                                  "Q402aIgg_assessment_Elr" +
                                                  idx
                                                }
                                                value={2}
                                                checked={
                                                  data.Q402aIgg_assessment_Elr ===
                                                  2
                                                }
                                                onChange={
                                                  HandleQ402aFieldChange
                                                }
                                              />
                                              <span
                                                className="radio-text-padding"
                                                style={{
                                                  paddingLeft: "10px",
                                                  marginLeft: "-5px",
                                                }}
                                              >
                                                <DatePickerComponent
                                                  ctrlId={
                                                    "Q402aIgg_assessment_Elr_dt-" +
                                                    idx
                                                  }
                                                  HandleDateChange={
                                                    HandleQ402aRepeaterDateChange
                                                  }
                                                  optionId={1}
                                                  minDate={dayjs("01-01-1920")}
                                                  maxDate={dayjs()}
                                                  locale="en" // current it is mm/dd/yyyy but for dd/mm/yyyy use "en-gb"
                                                  date={
                                                    data.Q402aIgg_assessment_Elr_dt
                                                  }
                                                />
                                              </span>
                                            </div>

                                            <div
                                              className="option-without-input"
                                              style={{
                                                display: "flex",
                                              }}
                                            >
                                              <label className="option-fbox">
                                                <input
                                                  type="radio"
                                                  id={idx}
                                                  name={
                                                    "Q402aIgg_assessment_Elr" +
                                                    idx
                                                  }
                                                  value={99}
                                                  checked={
                                                    data.Q402aIgg_assessment_Elr ===
                                                    99
                                                  }
                                                  onChange={
                                                    HandleQ402aFieldChange
                                                  }
                                                />
                                                <span
                                                  style={{
                                                    paddingLeft: "10px",
                                                    marginLeft: "-5px",
                                                  }}
                                                >
                                                  Date of initiation unknown
                                                </span>
                                              </label>
                                            </div>

                                            <ErrorField
                                              show={
                                                data.Q402aIgg_assessment_Elr_RdHard
                                              }
                                              message={
                                                ELCharacteristics_MSGS.Q402aSelect_RdHard
                                              }
                                              style={{
                                                paddingLeft: "0px",
                                              }}
                                            />

                                            <ErrorField
                                              show={
                                                data.Q402aIgg_assessment_Elr_dtHard
                                              }
                                              message={
                                                ELCharacteristics_MSGS.Q402aIggDateAdm_Hard
                                              }
                                              style={{
                                                paddingLeft: "0px",
                                              }}
                                            />
                                            <ErrorField
                                              show={
                                                data.Q402aIgg_assessment_Elr_dtInvalid
                                              }
                                              message={
                                                CommonError_Msgs.InvalidDate
                                              }
                                              style={{
                                                paddingLeft: "0px",
                                              }}
                                            />
                                            <ErrorField
                                              show={
                                                data.Q402aIgg_assessment_Elr_dt_before_eof
                                              }
                                              message={
                                                ELCharacteristics_MSGS.Q402BDateBeforeEOFAfterIndex91
                                              }
                                              style={{
                                                paddingLeft: "0px",
                                              }}
                                            />
                                          </div>
                                        </td>

                                        {/* IgG level */}
                                        {data.Q402aIgg_assessment_Elr != 1 ? (
                                          <td
                                            style={{
                                              border: "1px solid black",
                                              padding: "8px",
                                              textAlign: "start",
                                            }}
                                          >
                                            <div>
                                              <div
                                                className="option-without-input"
                                                style={{
                                                  display: "flex",
                                                }}
                                              >
                                                <label className="option-fbox">
                                                  <input
                                                    type="radio"
                                                    id={idx}
                                                    name={
                                                      "Q402aIgg_level_Elr" + idx
                                                    }
                                                    value={1}
                                                    checked={
                                                      data.Q402aIgg_level_Elr ===
                                                      1
                                                    }
                                                    onChange={
                                                      HandleQ402aFieldChange
                                                    }
                                                  />
                                                  <span
                                                    style={{
                                                      paddingLeft: "10px",
                                                      display: "flex",
                                                    }}
                                                  >
                                                    <span>
                                                      <input
                                                        className="input-dash-ttcustom"
                                                        id={idx}
                                                        name={
                                                          "Q402aIgg_level_Elr_MgValue" +
                                                          idx
                                                        }
                                                        value={
                                                          data.Q402aIgg_level_Elr_MgValue
                                                        }
                                                        type="text"
                                                        onChange={
                                                          HandleQ402aFieldChange
                                                        }
                                                        onKeyDown={
                                                          AllowNumericWithDecimal
                                                        }
                                                      />
                                                    </span>
                                                    <span>&nbsp;mg/dL</span>
                                                  </span>
                                                </label>
                                              </div>

                                              <div
                                                className="option-without-input"
                                                style={{
                                                  display: "flex",
                                                }}
                                              >
                                                <label className="option-fbox">
                                                  <input
                                                    type="radio"
                                                    id={idx}
                                                    name={
                                                      "Q402aIgg_level_Elr" + idx
                                                    }
                                                    value={2}
                                                    checked={
                                                      data.Q402aIgg_level_Elr ===
                                                      2
                                                    }
                                                    onChange={
                                                      HandleQ402aFieldChange
                                                    }
                                                  />
                                                  <span
                                                    style={{
                                                      paddingLeft: "10px",
                                                      display: "flex",
                                                    }}
                                                  >
                                                    <span>
                                                      <input
                                                        className="input-dash-ttcustom"
                                                        id={idx}
                                                        name={
                                                          "Q402aIgg_level_Elr_GValue" +
                                                          idx
                                                        }
                                                        value={
                                                          data.Q402aIgg_level_Elr_GValue
                                                        }
                                                        type="text"
                                                        onChange={
                                                          HandleQ402aFieldChange
                                                        }
                                                        onKeyDown={
                                                          AllowNumericWithDecimal
                                                        }
                                                      />
                                                    </span>
                                                    <span>&nbsp;g/L</span>
                                                  </span>
                                                </label>
                                              </div>

                                              <div
                                                className="option-without-input"
                                                style={{
                                                  display: "flex",
                                                }}
                                              >
                                                <label className="option-fbox">
                                                  <input
                                                    type="radio"
                                                    id={idx}
                                                    name={
                                                      "Q402aIgg_level_Elr" + idx
                                                    }
                                                    value={99}
                                                    checked={
                                                      data.Q402aIgg_level_Elr ===
                                                      99
                                                    }
                                                    onChange={
                                                      HandleQ402aFieldChange
                                                    }
                                                  />
                                                  <span
                                                    style={{
                                                      paddingLeft: "10px",
                                                      display: "flex",
                                                    }}
                                                  >
                                                    Unknown
                                                  </span>
                                                </label>
                                              </div>

                                              <ErrorField
                                                show={
                                                  data.Q402aIgg_level_Elr_Empty
                                                }
                                                message={
                                                  ELCharacteristics_MSGS.Q402aSelect_RdHard
                                                }
                                                style={{
                                                  paddingLeft: "0px",
                                                }}
                                              />
                                              <ErrorField
                                                show={
                                                  data.Q402aIgg_level_Elr_RangeInvalid
                                                }
                                                message={
                                                  data.Q402aIgg_level_Elr === 1
                                                    ? ELCharacteristics_MSGS.Q402aLevelRangeHardMG
                                                    : data.Q402aIgg_level_Elr ===
                                                        2
                                                      ? ELCharacteristics_MSGS.Q402aLevelRangeHardGL
                                                      : ""
                                                }
                                                style={{
                                                  paddingLeft: "0px",
                                                }}
                                              />
                                            </div>
                                          </td>
                                        ) : (
                                          <td
                                            style={{
                                              border: "1px solid black",
                                              //alignContent: "flex-start",
                                              textAlign: "center",
                                              padding: "8px",
                                              //textAlign: 'start'
                                            }}
                                          >
                                            <span
                                              style={{
                                                textAlign: "center",
                                                verticalAlign: "center",
                                              }}
                                              className="not-applicable"
                                            >
                                              <b>Not applicable</b>
                                            </span>
                                          </td>
                                        )}
                                      </tr>
                                    ))}

                                  {showQ40Linvo &&
                                    Q402aLinvoDetails.length > 0 &&
                                    Q402aLinvoDetails.map((data, idx) => (
                                      <tr key={idx}>
                                        <>
                                          <td
                                            style={{
                                              border: "1px solid black",
                                              padding: "8px",
                                            }}
                                          >
                                            <span className="quest-text-pad">
                                              <>
                                                LYNOZYFIC<sup>TM</sup>{" "}
                                                (linvoseltamab-gcpt)
                                              </>
                                            </span>
                                          </td>

                                          {/* IgG assessment */}
                                          <td
                                            style={{
                                              border: "1px solid black",
                                              padding: "8px",
                                            }}
                                          >
                                            <div>
                                              <div
                                                className="option-without-input"
                                                style={{
                                                  display: "flex",
                                                }}
                                              >
                                                <label className="option-fbox">
                                                  <input
                                                    type="radio"
                                                    id={idx}
                                                    name={
                                                      "Q402aIgg_assessment_Lin" +
                                                      idx
                                                    }
                                                    value={1}
                                                    checked={
                                                      data.Q402aIgg_assessment_Lin ===
                                                      1
                                                    }
                                                    onChange={
                                                      HandleQ402aFieldChange
                                                    }
                                                  />
                                                  <span
                                                    style={{
                                                      paddingLeft: "10px",
                                                      marginLeft: "-5px",
                                                    }}
                                                  >
                                                    No IgG assessment performed
                                                    at treatment initiation
                                                  </span>
                                                </label>
                                              </div>

                                              <div
                                                className="option-without-input"
                                                style={{
                                                  display: "flex",
                                                }}
                                              >
                                                <input
                                                  type="radio"
                                                  id={idx}
                                                  name={
                                                    "Q402aIgg_assessment_Lin" +
                                                    idx
                                                  }
                                                  value={2}
                                                  checked={
                                                    data.Q402aIgg_assessment_Lin ===
                                                    2
                                                  }
                                                  onChange={
                                                    HandleQ402aFieldChange
                                                  }
                                                />
                                                <span
                                                  className="radio-text-padding"
                                                  style={{
                                                    paddingLeft: "10px",
                                                    marginLeft: "-5px",
                                                  }}
                                                >
                                                  <DatePickerComponent
                                                    ctrlId={
                                                      "Q402aIgg_assessment_Lin_dt-" +
                                                      idx
                                                    }
                                                    HandleDateChange={
                                                      HandleQ402aRepeaterDateChange
                                                    }
                                                    optionId={1}
                                                    minDate={dayjs(
                                                      "01-01-1920",
                                                    )}
                                                    maxDate={dayjs()}
                                                    locale="en" // current it is mm/dd/yyyy but for dd/mm/yyyy use "en-gb"
                                                    date={
                                                      data.Q402aIgg_assessment_Lin_dt
                                                    }
                                                  />
                                                </span>
                                              </div>

                                              <div
                                                className="option-without-input"
                                                style={{
                                                  display: "flex",
                                                }}
                                              >
                                                <label className="option-fbox">
                                                  <input
                                                    type="radio"
                                                    id={idx}
                                                    name={
                                                      "Q402aIgg_assessment_Lin" +
                                                      idx
                                                    }
                                                    value={99}
                                                    checked={
                                                      data.Q402aIgg_assessment_Lin ===
                                                      99
                                                    }
                                                    onChange={
                                                      HandleQ402aFieldChange
                                                    }
                                                  />
                                                  <span
                                                    style={{
                                                      paddingLeft: "10px",
                                                      marginLeft: "-5px",
                                                    }}
                                                  >
                                                    Date of initiation unknown
                                                  </span>
                                                </label>
                                              </div>

                                              <ErrorField
                                                show={
                                                  data.Q402aIgg_assessment_Lin_RdHard
                                                }
                                                message={
                                                  ELCharacteristics_MSGS.Q402aSelect_RdHard
                                                }
                                                style={{
                                                  paddingLeft: "0px",
                                                }}
                                              />

                                              <ErrorField
                                                show={
                                                  data.Q402aIgg_assessment_Lin_dtHard
                                                }
                                                message={
                                                  ELCharacteristics_MSGS.Q402aIggDateAdm_Hard
                                                }
                                                style={{
                                                  paddingLeft: "0px",
                                                }}
                                              />
                                              <ErrorField
                                                show={
                                                  data.Q402aIgg_assessment_Lin_dtInvalid
                                                }
                                                message={
                                                  CommonError_Msgs.InvalidDate
                                                }
                                                style={{
                                                  paddingLeft: "0px",
                                                }}
                                              />
                                              <ErrorField
                                                show={
                                                  data.Q402aIgg_assessment_Lin_dt_before_eof
                                                }
                                                message={
                                                  ELCharacteristics_MSGS.Q402BDateBeforeEOFAfterIndex91
                                                }
                                                style={{
                                                  paddingLeft: "0px",
                                                }}
                                              />
                                            </div>
                                          </td>

                                          {/* IgG level */}
                                          {data.Q402aIgg_assessment_Lin != 1 ? (
                                            <td
                                              style={{
                                                border: "1px solid black",
                                                padding: "8px",
                                                textAlign: "start",
                                              }}
                                            >
                                              <div>
                                                <div
                                                  className="option-without-input"
                                                  style={{
                                                    display: "flex",
                                                  }}
                                                >
                                                  <label className="option-fbox">
                                                    <input
                                                      type="radio"
                                                      id={idx}
                                                      name={
                                                        "Q402aIgg_level_Lin" +
                                                        idx
                                                      }
                                                      value={1}
                                                      checked={
                                                        data.Q402aIgg_level_Lin ===
                                                        1
                                                      }
                                                      onChange={
                                                        HandleQ402aFieldChange
                                                      }
                                                    />
                                                    <span
                                                      style={{
                                                        paddingLeft: "10px",
                                                        display: "flex",
                                                      }}
                                                    >
                                                      <span>
                                                        <input
                                                          className="input-dash-ttcustom"
                                                          id={idx}
                                                          name={
                                                            "Q402aIgg_level_Lin_MgValue" +
                                                            idx
                                                          }
                                                          value={
                                                            data.Q402aIgg_level_Lin_MgValue
                                                          }
                                                          type="text"
                                                          onChange={
                                                            HandleQ402aFieldChange
                                                          }
                                                          onKeyDown={
                                                            AllowNumericWithDecimal
                                                          }
                                                        />
                                                      </span>
                                                      <span>&nbsp;mg/dL</span>
                                                    </span>
                                                  </label>
                                                </div>

                                                <div
                                                  className="option-without-input"
                                                  style={{
                                                    display: "flex",
                                                  }}
                                                >
                                                  <label className="option-fbox">
                                                    <input
                                                      type="radio"
                                                      id={idx}
                                                      name={
                                                        "Q402aIgg_level_Lin" +
                                                        idx
                                                      }
                                                      value={2}
                                                      checked={
                                                        data.Q402aIgg_level_Lin ===
                                                        2
                                                      }
                                                      onChange={
                                                        HandleQ402aFieldChange
                                                      }
                                                    />
                                                    <span
                                                      style={{
                                                        paddingLeft: "10px",
                                                        display: "flex",
                                                      }}
                                                    >
                                                      <span>
                                                        <input
                                                          className="input-dash-ttcustom"
                                                          id={idx}
                                                          name={
                                                            "Q402aIgg_level_Lin_GValue" +
                                                            idx
                                                          }
                                                          value={
                                                            data.Q402aIgg_level_Lin_GValue
                                                          }
                                                          type="text"
                                                          onChange={
                                                            HandleQ402aFieldChange
                                                          }
                                                          onKeyDown={
                                                            AllowNumericWithDecimal
                                                          }
                                                        />
                                                      </span>
                                                      <span>&nbsp;g/L</span>
                                                    </span>
                                                  </label>
                                                </div>

                                                <div
                                                  className="option-without-input"
                                                  style={{
                                                    display: "flex",
                                                  }}
                                                >
                                                  <label className="option-fbox">
                                                    <input
                                                      type="radio"
                                                      id={idx}
                                                      name={
                                                        "Q402aIgg_level_Lin" +
                                                        idx
                                                      }
                                                      value={99}
                                                      checked={
                                                        data.Q402aIgg_level_Lin ===
                                                        99
                                                      }
                                                      onChange={
                                                        HandleQ402aFieldChange
                                                      }
                                                    />
                                                    <span
                                                      style={{
                                                        paddingLeft: "10px",
                                                        display: "flex",
                                                      }}
                                                    >
                                                      Unknown
                                                    </span>
                                                  </label>
                                                </div>

                                                <ErrorField
                                                  show={
                                                    data.Q402aIgg_level_Lin_Empty
                                                  }
                                                  message={
                                                    ELCharacteristics_MSGS.Q402aSelect_RdHard
                                                  }
                                                  style={{
                                                    paddingLeft: "0px",
                                                  }}
                                                />
                                                <ErrorField
                                                  show={
                                                    data.Q402aIgg_level_Lin_RangeInvalid
                                                  }
                                                  message={
                                                    data.Q402aIgg_level_Lin ===
                                                    1
                                                      ? ELCharacteristics_MSGS.Q402aLevelRangeHardMG
                                                      : data.Q402aIgg_level_Lin ===
                                                          2
                                                        ? ELCharacteristics_MSGS.Q402aLevelRangeHardGL
                                                        : ""
                                                  }
                                                  style={{
                                                    paddingLeft: "0px",
                                                  }}
                                                />
                                              </div>
                                            </td>
                                          ) : (
                                            <td
                                              style={{
                                                border: "1px solid black",
                                                //alignContent: "flex-start",
                                                textAlign: "center",
                                                padding: "8px",
                                                //textAlign: 'start'
                                              }}
                                            >
                                              <span
                                                style={{
                                                  textAlign: "center",
                                                  verticalAlign: "center",
                                                }}
                                                className="not-applicable"
                                              >
                                                <b>Not applicable</b>
                                              </span>
                                            </td>
                                          )}
                                        </>
                                      </tr>
                                    ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        </div>
                      )}
                    {/* 40.2.A end */}

                    {/* 40.3.A start */}
                    {!isNewEcrfC &&
                      isNewEcrfB &&
                      (showQ40Elr || showQ40Linvo) &&
                      ((Q37ElrexDetails[0]?.Q40dosingfreqRd != null &&
                        Q37ElrexDetails[0]?.Q40dosingfreqRd != 1) ||
                        (Q37LinvoDetails[0]?.q40DosingFreqRd_linvo != null &&
                          Q37LinvoDetails[0]?.q40DosingFreqRd_linvo != 1)) && (
                        <div>
                          <div className="question question-weight">
                            <span>Q40.3.B</span>
                            <span className="quest-text-pad">
                              Please provide the{" "}
                              <i>
                                last immunoglobulin G (IgG) level before the
                                start of IGRT{" "}
                              </i>{" "}
                              {showQ40Elr && (
                                <>
                                  (ranging from
                                  <span
                                    style={{
                                      color: PageDesignConstants.fontColor,
                                      fontWeight: "bolder",
                                    }}
                                  >
                                    {" "}
                                    {showElrQ40Date91()}{" "}
                                  </span>
                                  to{" "}
                                  <span
                                    style={{
                                      color: PageDesignConstants.fontColor,
                                      fontWeight: "bolder",
                                    }}
                                  >
                                    {" "}
                                    {showElrQ40Date()}{" "}
                                  </span>
                                  ) while on treatment with{" "}
                                </>
                              )}
                              <span
                                style={{
                                  color: PageDesignConstants.fontColor,
                                  fontWeight: "bolder",
                                }}
                              >
                                {showQ40Elr && (
                                  <>
                                    ELREXFIO<sup>TM</sup> (elranatamab-bcmm)
                                  </>
                                )}

                                {showQ40Elr && showQ40Linvo
                                  ? " and "
                                  : showQ40Elr
                                    ? "."
                                    : ""}
                              </span>
                              <span>
                                {showQ40Linvo && (
                                  <>
                                    (ranging from
                                    <span
                                      style={{
                                        color: PageDesignConstants.fontColor,
                                        fontWeight: "bolder",
                                      }}
                                    >
                                      {" "}
                                      {showLinQ40Date91()}{" "}
                                    </span>
                                    to{" "}
                                    <span
                                      style={{
                                        color: PageDesignConstants.fontColor,
                                        fontWeight: "bolder",
                                      }}
                                    >
                                      {" "}
                                      {showLinQ40Date()}{" "}
                                    </span>
                                    ) while on treatment with{" "}
                                    <span
                                      style={{
                                        color: PageDesignConstants.fontColor,
                                        fontWeight: "bolder",
                                      }}
                                    >
                                      {" "}
                                      LYNOZYFIC
                                      <sup>TM</sup> (linvoseltamab-gcpt)
                                    </span>
                                    .
                                  </>
                                )}
                              </span>
                            </span>
                          </div>

                          <div className="question-bot-sapce">
                            <div className="sub-question grid-overflow">
                              <table
                                style={{
                                  border: "1px solid black",
                                  borderCollapse: "collapse",
                                  width: "100%",
                                }}
                              >
                                <thead>
                                  <tr>
                                    <th
                                      style={{
                                        border: "1px solid black",
                                        padding: "8px",
                                        textAlign: "center",
                                      }}
                                    >
                                      Treatment
                                    </th>
                                    <th
                                      style={{
                                        border: "1px solid black",
                                        padding: "8px",
                                        textAlign: "center",
                                        width: "33%",
                                      }}
                                    >
                                      Date of last IgG assessment before start
                                      of IGRT
                                    </th>

                                    <th
                                      style={{
                                        border: "1px solid black",
                                        padding: "8px",
                                        textAlign: "center",
                                        width: "16%",
                                      }}
                                    >
                                      Last IgG level before start of IGRT
                                    </th>
                                  </tr>
                                </thead>

                                <tbody>
                                  {/* ELREX */}
                                  {showQ40Elr &&
                                    Q403aElrDetails.length > 0 &&
                                    Q37ElrexDetails[0]?.Q40dosingfreqRd !=
                                      null &&
                                    Q37ElrexDetails[0]?.Q40dosingfreqRd != 1 &&
                                    Q403aElrDetails.map((data, idx) => (
                                      <tr key={idx}>
                                        <td
                                          style={{
                                            border: "1px solid black",
                                            padding: "8px",
                                          }}
                                        >
                                          <span className="quest-text-pad">
                                            <>
                                              ELREXFIO<sup>TM</sup>{" "}
                                              (elranatamab-bcmm).
                                            </>
                                          </span>
                                        </td>

                                        {/* IgG assessment */}
                                        <td
                                          style={{
                                            border: "1px solid black",
                                            padding: "8px",
                                          }}
                                        >
                                          <div>
                                            <div
                                              className="option-without-input"
                                              style={{
                                                display: "flex",
                                              }}
                                            >
                                              <label className="option-fbox">
                                                <input
                                                  type="radio"
                                                  id={idx}
                                                  name={
                                                    "Q403aIgg_assessment_Elr" +
                                                    idx
                                                  }
                                                  value={1}
                                                  checked={
                                                    data.Q403aIgg_assessment_Elr ===
                                                    1
                                                  }
                                                  onChange={
                                                    HandleQ403aFieldChange
                                                  }
                                                />
                                                <span
                                                  style={{
                                                    paddingLeft: "10px",
                                                    marginLeft: "-5px",
                                                  }}
                                                >
                                                  No IgG assessment performed at
                                                  IGRT initiation
                                                </span>
                                              </label>
                                            </div>

                                            <div
                                              className="option-without-input"
                                              style={{
                                                display: "flex",
                                              }}
                                            >
                                              <input
                                                type="radio"
                                                id={idx}
                                                name={
                                                  "Q403aIgg_assessment_Elr" +
                                                  idx
                                                }
                                                value={2}
                                                checked={
                                                  data.Q403aIgg_assessment_Elr ===
                                                  2
                                                }
                                                onChange={
                                                  HandleQ403aFieldChange
                                                }
                                              />
                                              <span
                                                className="radio-text-padding"
                                                style={{
                                                  paddingLeft: "10px",
                                                  marginLeft: "-5px",
                                                }}
                                              >
                                                <DatePickerComponent
                                                  ctrlId={
                                                    "Q403aIgg_assessment_Elr_dt-" +
                                                    idx
                                                  }
                                                  HandleDateChange={
                                                    HandleQ403aRepeaterDateChange
                                                  }
                                                  optionId={1}
                                                  minDate={dayjs("01-01-1920")}
                                                  maxDate={dayjs()}
                                                  locale="en" // current it is mm/dd/yyyy but for dd/mm/yyyy use "en-gb"
                                                  date={
                                                    data.Q403aIgg_assessment_Elr_dt
                                                  }
                                                />
                                              </span>
                                            </div>

                                            <div
                                              className="option-without-input"
                                              style={{
                                                display: "flex",
                                              }}
                                            >
                                              <label className="option-fbox">
                                                <input
                                                  type="radio"
                                                  id={idx}
                                                  name={
                                                    "Q403aIgg_assessment_Elr" +
                                                    idx
                                                  }
                                                  value={99}
                                                  checked={
                                                    data.Q403aIgg_assessment_Elr ===
                                                    99
                                                  }
                                                  onChange={
                                                    HandleQ403aFieldChange
                                                  }
                                                />
                                                <span
                                                  style={{
                                                    paddingLeft: "10px",
                                                    marginLeft: "-5px",
                                                  }}
                                                >
                                                  Date of initiation unknown
                                                </span>
                                              </label>
                                            </div>

                                            <ErrorField
                                              show={
                                                data.Q403aIgg_assessment_Elr_RdHard
                                              }
                                              message={
                                                ELCharacteristics_MSGS.Q402aSelect_RdHard
                                              }
                                              style={{
                                                paddingLeft: "0px",
                                              }}
                                            />

                                            <ErrorField
                                              show={
                                                data.Q403aIgg_assessment_Elr_dtHard
                                              }
                                              message={
                                                ELCharacteristics_MSGS.Q402aIggDateAdm_Hard
                                              }
                                              style={{
                                                paddingLeft: "0px",
                                              }}
                                            />
                                            <ErrorField
                                              show={
                                                data.Q403aIgg_assessment_Elr_dtInvalid
                                              }
                                              message={
                                                CommonError_Msgs.InvalidDate
                                              }
                                              style={{
                                                paddingLeft: "0px",
                                              }}
                                            />
                                            <ErrorField
                                              show={
                                                data.Q403aIgg_assessment_Elr_dt_before_eof
                                              }
                                              message={
                                                ELCharacteristics_MSGS.Q403BDateRangeError
                                              }
                                              style={{
                                                paddingLeft: "0px",
                                              }}
                                            />
                                          </div>
                                        </td>

                                        {/* IgG level */}
                                        {data.Q403aIgg_assessment_Elr != 1 ? (
                                          <td
                                            style={{
                                              border: "1px solid black",
                                              padding: "8px",
                                              textAlign: "start",
                                            }}
                                          >
                                            <div>
                                              <div
                                                className="option-without-input"
                                                style={{
                                                  display: "flex",
                                                }}
                                              >
                                                <label className="option-fbox">
                                                  <input
                                                    type="radio"
                                                    id={idx}
                                                    name={
                                                      "Q403aIgg_level_Elr" + idx
                                                    }
                                                    value={1}
                                                    checked={
                                                      data.Q403aIgg_level_Elr ===
                                                      1
                                                    }
                                                    onChange={
                                                      HandleQ403aFieldChange
                                                    }
                                                  />
                                                  <span
                                                    style={{
                                                      paddingLeft: "10px",
                                                      display: "flex",
                                                    }}
                                                  >
                                                    <span>
                                                      <input
                                                        className="input-dash-ttcustom"
                                                        id={idx}
                                                        name={
                                                          "Q403aIgg_level_Elr_MgValue" +
                                                          idx
                                                        }
                                                        value={
                                                          data.Q403aIgg_level_Elr_MgValue
                                                        }
                                                        type="text"
                                                        onChange={
                                                          HandleQ403aFieldChange
                                                        }
                                                        onKeyDown={
                                                          AllowNumericWithDecimal
                                                        }
                                                      />
                                                    </span>
                                                    <span>&nbsp;mg/dL</span>
                                                  </span>
                                                </label>
                                              </div>

                                              <div
                                                className="option-without-input"
                                                style={{
                                                  display: "flex",
                                                }}
                                              >
                                                <label className="option-fbox">
                                                  <input
                                                    type="radio"
                                                    id={idx}
                                                    name={
                                                      "Q403aIgg_level_Elr" + idx
                                                    }
                                                    value={2}
                                                    checked={
                                                      data.Q403aIgg_level_Elr ===
                                                      2
                                                    }
                                                    onChange={
                                                      HandleQ403aFieldChange
                                                    }
                                                  />
                                                  <span
                                                    style={{
                                                      paddingLeft: "10px",
                                                      display: "flex",
                                                    }}
                                                  >
                                                    <span>
                                                      <input
                                                        className="input-dash-ttcustom"
                                                        id={idx}
                                                        name={
                                                          "Q403aIgg_level_Elr_GValue" +
                                                          idx
                                                        }
                                                        value={
                                                          data.Q403aIgg_level_Elr_GValue
                                                        }
                                                        type="text"
                                                        onChange={
                                                          HandleQ403aFieldChange
                                                        }
                                                        onKeyDown={
                                                          AllowNumericWithDecimal
                                                        }
                                                      />
                                                    </span>
                                                    <span>&nbsp;g/L</span>
                                                  </span>
                                                </label>
                                              </div>

                                              <div
                                                className="option-without-input"
                                                style={{
                                                  display: "flex",
                                                }}
                                              >
                                                <label className="option-fbox">
                                                  <input
                                                    type="radio"
                                                    id={idx}
                                                    name={
                                                      "Q403aIgg_level_Elr" + idx
                                                    }
                                                    value={99}
                                                    checked={
                                                      data.Q403aIgg_level_Elr ===
                                                      99
                                                    }
                                                    onChange={
                                                      HandleQ403aFieldChange
                                                    }
                                                  />
                                                  <span
                                                    style={{
                                                      paddingLeft: "10px",
                                                      display: "flex",
                                                    }}
                                                  >
                                                    Unknown
                                                  </span>
                                                </label>
                                              </div>

                                              <ErrorField
                                                show={
                                                  data.Q403aIgg_level_Elr_Empty
                                                }
                                                message={
                                                  ELCharacteristics_MSGS.Q402aSelect_RdHard
                                                }
                                                style={{
                                                  paddingLeft: "0px",
                                                }}
                                              />
                                              <ErrorField
                                                show={
                                                  data.Q403aIgg_level_Elr_RangeInvalid
                                                }
                                                message={
                                                  data.Q403aIgg_level_Elr === 1
                                                    ? ELCharacteristics_MSGS.Q402aLevelRangeHardMG
                                                    : data.Q403aIgg_level_Elr ===
                                                        2
                                                      ? ELCharacteristics_MSGS.Q402aLevelRangeHardGL
                                                      : ""
                                                }
                                                style={{
                                                  paddingLeft: "0px",
                                                }}
                                              />
                                            </div>
                                          </td>
                                        ) : (
                                          <td
                                            style={{
                                              border: "1px solid black",
                                              //alignContent: "flex-start",
                                              textAlign: "center",
                                              padding: "8px",
                                              //textAlign: 'start'
                                            }}
                                          >
                                            <span
                                              style={{
                                                textAlign: "center",
                                                verticalAlign: "center",
                                              }}
                                              className="not-applicable"
                                            >
                                              <b>Not applicable</b>
                                            </span>
                                          </td>
                                        )}
                                      </tr>
                                    ))}

                                  {showQ40Linvo &&
                                    Q403aLinvoDetails.length > 0 &&
                                    Q37LinvoDetails[0]?.q40DosingFreqRd_linvo !=
                                      null &&
                                    Q37LinvoDetails[0]?.q40DosingFreqRd_linvo !=
                                      1 &&
                                    Q403aLinvoDetails.map((data, idx) => (
                                      <tr key={idx}>
                                        <>
                                          <td
                                            style={{
                                              border: "1px solid black",
                                              padding: "8px",
                                            }}
                                          >
                                            <span className="quest-text-pad">
                                              <>
                                                LYNOZYFIC<sup>TM</sup>{" "}
                                                (linvoseltamab-gcpt)
                                              </>
                                            </span>
                                          </td>

                                          {/* IgG assessment */}
                                          <td
                                            style={{
                                              border: "1px solid black",
                                              padding: "8px",
                                            }}
                                          >
                                            <div>
                                              <div
                                                className="option-without-input"
                                                style={{
                                                  display: "flex",
                                                }}
                                              >
                                                <label className="option-fbox">
                                                  <input
                                                    type="radio"
                                                    id={idx}
                                                    name={
                                                      "Q403aIgg_assessment_Lin" +
                                                      idx
                                                    }
                                                    value={1}
                                                    checked={
                                                      data.Q403aIgg_assessment_Lin ===
                                                      1
                                                    }
                                                    onChange={
                                                      HandleQ403aFieldChange
                                                    }
                                                  />
                                                  <span
                                                    style={{
                                                      paddingLeft: "10px",
                                                      marginLeft: "-5px",
                                                    }}
                                                  >
                                                    No IgG assessment performed
                                                    at IGRT initiation
                                                  </span>
                                                </label>
                                              </div>

                                              <div
                                                className="option-without-input"
                                                style={{
                                                  display: "flex",
                                                }}
                                              >
                                                <input
                                                  type="radio"
                                                  id={idx}
                                                  name={
                                                    "Q403aIgg_assessment_Lin" +
                                                    idx
                                                  }
                                                  value={2}
                                                  checked={
                                                    data.Q403aIgg_assessment_Lin ===
                                                    2
                                                  }
                                                  onChange={
                                                    HandleQ403aFieldChange
                                                  }
                                                />
                                                <span
                                                  className="radio-text-padding"
                                                  style={{
                                                    paddingLeft: "10px",
                                                    marginLeft: "-5px",
                                                  }}
                                                >
                                                  <DatePickerComponent
                                                    ctrlId={
                                                      "Q403aIgg_assessment_Lin_dt-" +
                                                      idx
                                                    }
                                                    HandleDateChange={
                                                      HandleQ403aRepeaterDateChange
                                                    }
                                                    optionId={1}
                                                    minDate={dayjs(
                                                      "01-01-1920",
                                                    )}
                                                    maxDate={dayjs()}
                                                    locale="en" // current it is mm/dd/yyyy but for dd/mm/yyyy use "en-gb"
                                                    date={
                                                      data.Q403aIgg_assessment_Lin_dt
                                                    }
                                                  />
                                                </span>
                                              </div>

                                              <div
                                                className="option-without-input"
                                                style={{
                                                  display: "flex",
                                                }}
                                              >
                                                <label className="option-fbox">
                                                  <input
                                                    type="radio"
                                                    id={idx}
                                                    name={
                                                      "Q403aIgg_assessment_Lin" +
                                                      idx
                                                    }
                                                    value={99}
                                                    checked={
                                                      data.Q403aIgg_assessment_Lin ===
                                                      99
                                                    }
                                                    onChange={
                                                      HandleQ403aFieldChange
                                                    }
                                                  />
                                                  <span
                                                    style={{
                                                      paddingLeft: "10px",
                                                      marginLeft: "-5px",
                                                    }}
                                                  >
                                                    Date of administration
                                                    unknown
                                                  </span>
                                                </label>
                                              </div>

                                              <ErrorField
                                                show={
                                                  data.Q403aIgg_assessment_Lin_RdHard
                                                }
                                                message={
                                                  ELCharacteristics_MSGS.Q402aSelect_RdHard
                                                }
                                                style={{
                                                  paddingLeft: "0px",
                                                }}
                                              />

                                              <ErrorField
                                                show={
                                                  data.Q403aIgg_assessment_Lin_dtHard
                                                }
                                                message={
                                                  ELCharacteristics_MSGS.Q402aIggDateAdm_Hard
                                                }
                                                style={{
                                                  paddingLeft: "0px",
                                                }}
                                              />
                                              <ErrorField
                                                show={
                                                  data.Q403aIgg_assessment_Lin_dtInvalid
                                                }
                                                message={
                                                  CommonError_Msgs.InvalidDate
                                                }
                                                style={{
                                                  paddingLeft: "0px",
                                                }}
                                              />
                                              <ErrorField
                                                show={
                                                  data.Q403aIgg_assessment_Lin_dt_before_eof
                                                }
                                                message={
                                                  ELCharacteristics_MSGS.Q403BDateRangeError
                                                }
                                                style={{
                                                  paddingLeft: "0px",
                                                }}
                                              />
                                            </div>
                                          </td>

                                          {/* IgG level */}
                                          {data.Q403aIgg_assessment_Lin != 1 ? (
                                            <td
                                              style={{
                                                border: "1px solid black",
                                                padding: "8px",
                                                textAlign: "start",
                                              }}
                                            >
                                              <div>
                                                <div
                                                  className="option-without-input"
                                                  style={{
                                                    display: "flex",
                                                  }}
                                                >
                                                  <label className="option-fbox">
                                                    <input
                                                      type="radio"
                                                      id={idx}
                                                      name={
                                                        "Q403aIgg_level_Lin" +
                                                        idx
                                                      }
                                                      value={1}
                                                      checked={
                                                        data.Q403aIgg_level_Lin ===
                                                        1
                                                      }
                                                      onChange={
                                                        HandleQ403aFieldChange
                                                      }
                                                    />
                                                    <span
                                                      style={{
                                                        paddingLeft: "10px",
                                                        display: "flex",
                                                      }}
                                                    >
                                                      <span>
                                                        <input
                                                          className="input-dash-ttcustom"
                                                          id={idx}
                                                          name={
                                                            "Q403aIgg_level_Lin_MgValue" +
                                                            idx
                                                          }
                                                          value={
                                                            data.Q403aIgg_level_Lin_MgValue
                                                          }
                                                          type="text"
                                                          onChange={
                                                            HandleQ403aFieldChange
                                                          }
                                                          onKeyDown={
                                                            AllowNumericWithDecimal
                                                          }
                                                        />
                                                      </span>
                                                      <span>&nbsp;mg/dL</span>
                                                    </span>
                                                  </label>
                                                </div>

                                                <div
                                                  className="option-without-input"
                                                  style={{
                                                    display: "flex",
                                                  }}
                                                >
                                                  <label className="option-fbox">
                                                    <input
                                                      type="radio"
                                                      id={idx}
                                                      name={
                                                        "Q403aIgg_level_Lin" +
                                                        idx
                                                      }
                                                      value={2}
                                                      checked={
                                                        data.Q403aIgg_level_Lin ===
                                                        2
                                                      }
                                                      onChange={
                                                        HandleQ403aFieldChange
                                                      }
                                                    />
                                                    <span
                                                      style={{
                                                        paddingLeft: "10px",
                                                        display: "flex",
                                                      }}
                                                    >
                                                      <span>
                                                        <input
                                                          className="input-dash-ttcustom"
                                                          id={idx}
                                                          name={
                                                            "Q403aIgg_level_Lin_GValue" +
                                                            idx
                                                          }
                                                          value={
                                                            data.Q403aIgg_level_Lin_GValue
                                                          }
                                                          type="text"
                                                          onChange={
                                                            HandleQ403aFieldChange
                                                          }
                                                          onKeyDown={
                                                            AllowNumericWithDecimal
                                                          }
                                                        />
                                                      </span>
                                                      <span>&nbsp;g/L</span>
                                                    </span>
                                                  </label>
                                                </div>

                                                <div
                                                  className="option-without-input"
                                                  style={{
                                                    display: "flex",
                                                  }}
                                                >
                                                  <label className="option-fbox">
                                                    <input
                                                      type="radio"
                                                      id={idx}
                                                      name={
                                                        "Q403aIgg_level_Lin" +
                                                        idx
                                                      }
                                                      value={99}
                                                      checked={
                                                        data.Q403aIgg_level_Lin ===
                                                        99
                                                      }
                                                      onChange={
                                                        HandleQ403aFieldChange
                                                      }
                                                    />
                                                    <span
                                                      style={{
                                                        paddingLeft: "10px",
                                                        display: "flex",
                                                      }}
                                                    >
                                                      Unknown
                                                    </span>
                                                  </label>
                                                </div>

                                                <ErrorField
                                                  show={
                                                    data.Q403aIgg_level_Lin_Empty
                                                  }
                                                  message={
                                                    ELCharacteristics_MSGS.Q402aSelect_RdHard
                                                  }
                                                  style={{
                                                    paddingLeft: "0px",
                                                  }}
                                                />
                                                <ErrorField
                                                  show={
                                                    data.Q403aIgg_level_Lin_RangeInvalid
                                                  }
                                                  message={
                                                    data.Q403aIgg_level_Lin ===
                                                    1
                                                      ? ELCharacteristics_MSGS.Q402aLevelRangeHardMG
                                                      : data.Q403aIgg_level_Lin ===
                                                          2
                                                        ? ELCharacteristics_MSGS.Q402aLevelRangeHardGL
                                                        : ""
                                                  }
                                                  style={{
                                                    paddingLeft: "0px",
                                                  }}
                                                />
                                              </div>
                                            </td>
                                          ) : (
                                            <td
                                              style={{
                                                border: "1px solid black",
                                                //alignContent: "flex-start",
                                                textAlign: "center",
                                                padding: "8px",
                                                //textAlign: 'start'
                                              }}
                                            >
                                              <span
                                                style={{
                                                  textAlign: "center",
                                                  verticalAlign: "center",
                                                }}
                                                className="not-applicable"
                                              >
                                                <b>Not applicable</b>
                                              </span>
                                            </td>
                                          )}
                                        </>
                                      </tr>
                                    ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        </div>
                      )}
                    {/* 40.3.A end */}
                  </div>
                </div>
              </div>

        </div>
      </Collapse>
    </form>
  );
});

// Page: one ELLotSection per LOT cohort (Line_CohortID, e.g. "2L Elrexfio") of every
// eligible Elrexfio / Linvoseltamab cohort. Validation runs on every section (so every
// error is shown), then the per-LOT payloads are merged into a single save.
const ELCharacteristics = () => {
  const storedData = GetLocalStorageData("CohortTreatment");
  const pid = GetLocalStorageData("patientId") ?? 0;
  const [TTIsPresent, setTTIsPresent] = useState(false);
  const [ndmmAvailable, setNdmmAvailable] = useState(false);
  const [rrmmAvailable, setRRMMAvailable] = useState(false);
  const [lotEntries, setLotEntries] = useState([]);
  const [showTopErrMsg, setShowTopErrMsg] = useState(false);
  const sectionRefs = useRef([]);

  const {
    ShowToast,
    ToggleLoader,
    HandleSessionTimeout,
    isSideMenuActive,
    setIsSideMenuActive,
  } = useContext(AppContext);

  const navigatePaths = {
    prevPage: TTIsPresent
      ? "/TTCharacteristics"
      : ndmmAvailable
        ? "/NDMMSCTCharacteristics"
        : "/NonCohortDefiningTreatment",
    currPage: "/ELCharacteristics",
    nextPage: rrmmAvailable ? "/CarTCharacteristics" : "/IMWGReporting",
  };

  useEffect(() => {
    const cohorts = storedData ? JSON.parse(storedData) : [];
    setNdmmAvailable(cohorts.some((c) => c.ndmm === true && c.isEligible));
    setTTIsPresent(
      cohorts.some(
        (c) =>
          (c.cohortTreatmentID === 9 || c.cohortTreatmentID === 10) &&
          c.isEligible === true,
      ),
    );
    const rrmm = cohorts.filter((c) => c.ndmm === false && c.isEligible);
    setRRMMAvailable(
      rrmm.some(
        (c) =>
          c.cohortTreatmentID === RRMMCohortTreatment.ABECMA ||
          c.cohortTreatmentID === RRMMCohortTreatment.CARVYKTI,
      ),
    );

    // One entry per received line of therapy of each Elrexfio / Linvoseltamab cohort,
    // or a single entry (no LOT) when the cohort has no received lines.
    const entries = [];
    rrmm
      .filter(
        (c) =>
          c.cohortTreatmentID === RRMMCohortTreatment.ELREXFIO ||
          c.cohortTreatmentID === RRMMCohortTreatment.Linvoseltamab,
      )
      .forEach((c) => {
        const name =
          c.cohortTreatmentID === RRMMCohortTreatment.ELREXFIO
            ? "Elrexfio"
            : "Linvoseltamab";
        const lots = (c.receivedLinesOfTherapy ?? []).filter(
          (l) => l.lineOfTherapy > 0,
        );
        const lotNumbers =
          lots.length > 0 ? lots.map((l) => l.lineOfTherapy) : [null];
        lotNumbers.forEach((lineOfTherapy, idx) =>
          entries.push({
            cohortTreatmentId: c.cohortTreatmentID,
            lineOfTherapy,
            isFirstLot: idx === 0,
            label: lineOfTherapy ? `${lineOfTherapy}L – ${name}` : name,
          }),
        );
      });
    setLotEntries(entries);
  }, []);

  const toggleNav = () => {
    const sidebar = document.getElementById("mySidebar");
    const main = document.getElementById("main");
    if (isSideMenuActive === true) {
      sidebar.style.width = "0";
      main.style.marginLeft = "0";
      setIsSideMenuActive(false);
    } else if (isSideMenuActive === false) {
      sidebar.style.width = "350px";
      main.style.marginLeft = "350px";
      setIsSideMenuActive(true);
    }
  };

  useEffect(() => {
    const sidebar = document.getElementById("mySidebar");
    const main = document.getElementById("main");
    if (isSideMenuActive === true) {
      sidebar.style.width = "350px";
      main.style.marginLeft = "350px";
    } else {
      sidebar.style.width = "0";
      main.style.marginLeft = "0";
    }
  });

  const SavePage = async (validate) => {
    try {
      ToggleLoader(true);
      const sections = sectionRefs.current.filter(Boolean);

      // run every section's validation so all errors are shown
      let isValid = true;
      if (validate)
        sections.forEach((section) => {
          if (!section.validate()) isValid = false;
        });

      if (!isValid) {
        ToggleLoader(false);
        setShowTopErrMsg(true);
        return false;
      }
      setShowTopErrMsg(false);

      // Merge the per-LOT payloads: drug-specific lists come from that drug's
      // sections only; shared lists (Q39.2.C / Q39.3.C) from all.
      const ELCharModel = {};
      sections.forEach((section) => {
        const payload = section.buildPayload(validate);
        const isElr =
          section.lot.cohortTreatmentId === RRMMCohortTreatment.ELREXFIO;
        Object.entries(payload).forEach(([key, value]) => {
          if (!Array.isArray(value)) {
            ELCharModel[key] = value;
            return;
          }
          const isElrKey = /Elr/.test(key);
          const isLinvoKey = /Linvo/.test(key);
          if ((isElrKey && !isElr) || (isLinvoKey && isElr)) return;
          ELCharModel[key] = [...(ELCharModel[key] ?? []), ...value];
        });
      });

      if (pid > 0) {
        const response =
          await AdditionalTreatmentService.SaveELCharacteristicData(
            ELCharModel,
          );

        if (response.status !== HTTPResponse.OK) {
          if (response?.status === HTTPResponse.Unauthorized) {
            HandleSessionTimeout();
          } else throw response.error;
        } else {
          ShowToast(ToastMessages.Save_Success, ToastMessageType.Success);
          await Promise.all(sections.map((section) => section.reload()));
        }
      }
      ToggleLoader(false);
      return true;
    } catch (error) {
      console.error("SavePage error:", error);
      ToggleLoader(false);
      ShowToast(CommonError_Msgs.SAVE_ERR, ToastMessageType.Failed);
    }
  };

  return (
    <>
      <section className="survey-patient-details-section">
        <div className="row">
          <PatientDetails />
          <div className="col-12 col-sm-5 col-md-5 col-lg-6 col-content-alignment">
            <SaveAndNavigate paths={navigatePaths} Save={SavePage} />
          </div>
        </div>
      </section>
      <section className="main-survey-section">
        <div>
          <SideBarMenu activePageId={SideMenuItems.ELCharacteristics} />
          <div id="main" className="col-pad-left">
            <button
              className={isSideMenuActive ? "openbtn" : "openbtn active"}
              onClick={() => toggleNav()}
            >
              <i
                id="arrowNavigation"
                className="fa fa-arrow-left"
                aria-hidden="true"
              ></i>
            </button>

            <div className="survey-section-content">
              <div className="survey-section-content-header">
                <h4>ELRANATAMAB AND LINVOSELTAMAB CHARACTERISTICS </h4>
              </div>
              {showTopErrMsg && (
                <div className="clean-error ">
                  <ErrorField
                    show={showTopErrMsg}
                    message={CommonError_Msgs.Hard_Empty}
                  />
                </div>
              )}

              <div className="survey-question-section">
                <div className="survey-question-content">
                  <div className="question-answer">
                    {lotEntries.map((lot, index) => (
                      <ELLotSection
                        key={`${lot.cohortTreatmentId}-${lot.lineOfTherapy ?? "x"}`}
                        lot={lot}
                        ref={(el) => (sectionRefs.current[index] = el)}
                      />
                    ))}
                  </div>
                </div>
              </div>

              <div className="survey-section-content-foot">
                <SaveAndNavigate paths={navigatePaths} Save={SavePage} />
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};
export default ELCharacteristics;
